import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  collection,
  addDoc,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import type { User } from "firebase/auth";
import {
  GOOGLE_AUTH_REDIRECT_URIS,
  auth,
  clearGoogleAuthStorage,
  clearGoogleRedirectPending,
  completeGoogleRedirectSignIn,
  db,
  ensureAnonymousUser,
  isGoogleRedirectPending,
  listenForForegroundMessages,
  requestPushToken,
  signInWithGoogle,
  signOutUser,
} from "./firebase";
// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
type Gender = "female" | "male";
const normalizeSpiritGender = (value: unknown): Gender | undefined => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "male" || normalized === "female"
    ? (normalized as Gender)
    : undefined;
};
const profileGenderOrFallback = (value: unknown): Gender =>
  normalizeSpiritGender(value) || "female";
function isPlaceholderFriendName(value: unknown) {
  const cleaned = String(value || "").trim().toLowerCase();
  return !cleaned || cleaned === "spirit friend" || cleaned === "a spirit friend";
}

function friendlyNameFromEmail(email?: string) {
  const local = email?.split("@")[0]?.replace(/[._-]+/g, " ").trim();
  return local
    ? local
        .split(/\s+/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "";
}

const isPlainFirestoreObject = (value: unknown) => {
  if (!value || typeof value !== "object" || value instanceof Date) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const stripUndefinedForFirestore = <T,>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefinedForFirestore(item)) as T;
  }
  if (isPlainFirestoreObject(value)) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entryValue]) => entryValue !== undefined)
        .map(([key, entryValue]) => [key, stripUndefinedForFirestore(entryValue)])
    ) as T;
  }
  return value;
};
type Slot =
  | "robe"
  | "crown"
  | "wings"
  | "halo"
  | "hand"
  | "shield"
  | "feet"
  | "belt"
  | "sash"
  | "hat"
  | "set";
type ClothingItem = {
  id: string;
  name: string;
  icon: string;
  cost: number;
  desc: string;
  slot: Slot;
  color: string;
  bodyColor?: string;
  feetColor?: string;
  wingColor?: string;
  beltColor?: string;
  sashColor?: string;
  handColor?: string;
  haloColor?: string;
  crownColor?: string;
  hatColor?: string;
};
type Task = { id: string; label: string; icon: string; optional?: boolean };
type Spark = { id: number; x: number; y: number };
type JournalEntry = { date: string; text: string };
type PreachEntry = { date: string; text: string; prompt: string };
type HomeBloom = { date: string; kind: "walk" };
type SpiritVisitorKind =
  | "dove"
  | "golden_dove"
  | "lamb"
  | "butterfly"
  | "bluebird"
  | "deer"
  | "fox"
  | "rabbit";
type SpiritVisitor = {
  id: string;
  kind: SpiritVisitorKind;
  reason: string;
  createdAt: number;
  expiresAt: number;
};
type PrayerChallengeDays = 7 | 21 | 40;
type PrayerDurationChoice = PrayerChallengeDays | null;
type PrayerFriend = {
  id: string;
  kind: "mine" | "other";
  name?: string;
  request: string;
  righteousnessGiven: number;
  prayedDates: string[];
  challengeDays?: PrayerChallengeDays;
  challengeStarted?: string;
  answered?: boolean;
  answeredDate?: string;
  answeredNote?: string;
  sharePublic?: boolean;
  publicDisplayName?: string;
  publicSubmittedAt?: string;
};
type FaithJourneyStats = {
  journeyDay: number;
  prayerCount: number;
  answeredPrayerCount: number;
  testimonyCount: number;
  previousStageName: string;
  currentStageName: string;
};
type PushNotificationKind =
  | "friend_request"
  | "prayer_note"
  | "blessing"
  | "shared_prayer"
  | "partner_request"
  | "partner_focus"
  | "testimony_alert"
  | "daily_prayer"
  | "grow_spirit"
  | "keep_growing";
type SpiritFriendProfile = {
  uid: string;
  displayName: string;
  email: string;
  inviteCode: string;
  gender?: Gender;
  stageKey?: string;
};
type SpiritFriendRequest = {
  id: string;
  fromUid: string;
  toUid: string;
  fromName: string;
  toName: string;
  fromEmail?: string;
  toEmail?: string;
  fromGender?: Gender;
  toGender?: Gender;
  fromStageKey?: string;
  toStageKey?: string;
  status: "pending" | "accepted" | "declined" | "cancelled" | "removed";
  requestType?: "friend" | "accountability";
  respondedAt?: any;
};
type SpiritFriend = {
  id: string;
  uid: string;
  displayName: string;
  email?: string;
  gender?: Gender;
  stageKey?: string;
};
type SharedPrayer = {
  id: string;
  prayerId: string;
  ownerUid: string;
  ownerName: string;
  request: string;
  friendUids: string[];
  createdAt?: string;
};
type PrayerSignal = {
  id: string;
  fromUid: string;
  toUid: string;
  fromName: string;
  toName?: string;
  type: "prayed" | "blessing" | "accountability";
  message?: string;
  blessingLabel?: string;
  checkInKind?: "prayer" | "word" | "not-yet";
  createdAt?: string;
};
type PublicTestimony = {
  id: string;
  testimony: string;
  prayerTitle: string;
  displayName: string;
  createdAt?: string;
  submittedAt?: string;
  userId?: string;
  approved?: boolean;
  rejected?: boolean;
};
type PrayerLight = {
  id: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
};
type AngelMessage = { id: number; text: string };
type MoodEntry = { mood: string; label: string; verse: string; ref: string };
type TestimonyStoneKind =
  | "prayer"
  | "kindness"
  | "courage"
  | "gratitude"
  | "faithfulness"
  | "forgiveness"
  | "worship"
  | "answered"
  | "white";
type TestimonyStone = {
  id: string;
  kind: TestimonyStoneKind;
  name: string;
  icon: string;
  desc: string;
  date: string;
  source: string;
  ultimate?: boolean;
};
type CheckedMap = Record<string, boolean>;
type DoneMap = Record<number, boolean>;
type SamaritanMap = Record<string, boolean>;
type ButtonEvent = React.MouseEvent<HTMLElement>;

type HapticKind = "light" | "medium" | "success" | "warning";

function spiritHapticPattern(pattern: number | number[]) {
  if (typeof navigator === "undefined") return;
  const haptics = navigator as Navigator & {
    vibrate?: (pattern: number | number[]) => boolean;
  };
  if (!haptics.vibrate) return;
  try {
    haptics.vibrate(pattern);
  } catch {
    // Haptics are optional and unsupported on some browsers.
  }
}

function spiritHaptic(kind: HapticKind = "light") {
  const patterns: Record<HapticKind, number | number[]> = {
    light: 12,
    medium: [18, 24, 18],
    success: [14, 24, 34],
    warning: [24, 34, 18],
  };
  spiritHapticPattern(patterns[kind]);
}

type HomeSlot =
  | "wallpaper"
  | "floor"
  | "furniture"
  | "pet"
  | "decor"
  | "window"
  | "garden"
  | "effect";
type HomeItem = {
  id: string;
  name: string;
  icon: string;
  cost: number;
  desc: string;
  slot: HomeSlot;
  bg?: string;
  accent?: string;
  color?: string;
};
type PlacedHomeMap = Partial<Record<HomeSlot, string>>;
type HomePositionMap = Record<string, { x: number; y: number }>;
type HomeGrowthStage = {
  level: number;
  name: string;
  icon: string;
  desc: string;
  minItems: number;
};
type HomeSpiritPreview = {
  stageKey: string;
  xp: number;
  xpMax: number;
  stageAura: string;
  stageGlow: string;
  animated: boolean;
  equipped: ClothingItem[];
  gender: Gender;
  skinTone: string;
  hairColor: string;
};
type HomeCareMood = "peaceful" | "glowing" | "blooming" | "restoring" | "radiant";
type HomeCareState = {
  mood: HomeCareMood;
  title: string;
  message: string;
  completedWalk: boolean;
  prayedToday: boolean;
  journaledToday: boolean;
  answeredToday: boolean;
  testimonySharedToday: boolean;
  blessedToday?: boolean;
};

// ─────────────────────────────────────────────────────────────────────────────
// PALETTE
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  pageBg: "#FFFDF8",
  cardBg: "rgba(255,254,249,0.92)",
  cardBorder: "rgba(91,126,148,0.20)",
  gold: "#C99A3D",
  goldLight: "#E8C66F",
  goldPale: "#F4E6B8",
  goldShine: "#FFF7DC",
  goldDeep: "#6E5322",
  textDark: "#2F3028",
  textMid: "#45483E",
  textSoft: "#5A5D52",
  textFaint: "#6D7166",
  green: "#557A5B",
  greenPale: "rgba(194,222,199,0.32)",
  red: "#A04444",
  redPale: "rgba(254,202,202,0.30)",
};
const GOLD_GRAD = `linear-gradient(135deg, #F4D989 0%, ${C.gold} 68%, #A77A2C 100%)`;
const GOLD_GRAD_S = `linear-gradient(135deg, ${C.goldShine} 0%, ${C.goldLight} 100%)`;
const PAGE_GRAD = C.pageBg;

// ─────────────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────────────
const PRESET_TASKS: Task[] = [
  {
    id: "morning_prayer",
    label: "Morning prayer",
    icon: "🌅",
    optional: false,
  },
  { id: "night_prayer", label: "Night prayer", icon: "🌙", optional: false },
  { id: "praise", label: "Sang praise", icon: "🎵", optional: true },
  { id: "bible", label: "Read the Bible", icon: "📖", optional: false },
  { id: "repentance", label: "Repentance", icon: "🕊️", optional: false },
];

const VERSE_QUIZ = [
  {
    verse: "The Lord is my shepherd; I shall not ___.",
    answer: "want",
    options: ["fear", "want", "fall", "fail"],
    ref: "Psalm 23:1",
    reward: 20,
  },
  {
    verse: "I can do all things through ___ who strengthens me.",
    answer: "Christ",
    options: ["God", "faith", "Christ", "prayer"],
    ref: "Phil. 4:13",
    reward: 20,
  },
  {
    verse: "Be still, and know that I am ___.",
    answer: "God",
    options: ["Lord", "God", "King", "Light"],
    ref: "Psalm 46:10",
    reward: 20,
  },
  {
    verse: "The joy of the Lord is your ___.",
    answer: "strength",
    options: ["peace", "strength", "hope", "shield"],
    ref: "Neh. 8:10",
    reward: 20,
  },
  {
    verse: "Trust in the Lord with all your ___.",
    answer: "heart",
    options: ["soul", "mind", "heart", "life"],
    ref: "Proverbs 3:5",
    reward: 20,
  },
  {
    verse: "Your word is a lamp to my feet and a light to my ___.",
    answer: "path",
    options: ["soul", "way", "path", "heart"],
    ref: "Psalm 119:105",
    reward: 20,
  },
  {
    verse: "Greater is He who is in you than he who is in the ___.",
    answer: "world",
    options: ["darkness", "world", "flesh", "enemy"],
    ref: "1 John 4:4",
    reward: 20,
  },
  {
    verse: "Let all that you do be done in ___.",
    answer: "love",
    options: ["truth", "love", "peace", "joy"],
    ref: "1 Cor. 16:14",
    reward: 20,
  },
  {
    verse: "Cast all your anxiety on Him because He ___ for you.",
    answer: "cares",
    options: ["waits", "cares", "speaks", "fights"],
    ref: "1 Peter 5:7",
    reward: 20,
  },
  {
    verse: "God is our refuge and ___.",
    answer: "strength",
    options: ["song", "strength", "shield", "peace"],
    ref: "Psalm 46:1",
    reward: 20,
  },
  {
    verse:
      "Come to me, all who are weary and burdened, and I will give you ___.",
    answer: "rest",
    options: ["riches", "rest", "power", "answers"],
    ref: "Matt. 11:28",
    reward: 20,
  },
  {
    verse: "For we walk by faith, not by ___.",
    answer: "sight",
    options: ["fear", "sight", "works", "dreams"],
    ref: "2 Cor. 5:7",
    reward: 20,
  },
  {
    verse: "The Lord is my light and my ___.",
    answer: "salvation",
    options: ["salvation", "crown", "answer", "wisdom"],
    ref: "Psalm 27:1",
    reward: 20,
  },
  {
    verse: "Create in me a clean ___, O God.",
    answer: "heart",
    options: ["spirit", "mind", "heart", "song"],
    ref: "Psalm 51:10",
    reward: 20,
  },
  {
    verse:
      "Blessed are the peacemakers, for they shall be called children of ___.",
    answer: "God",
    options: ["light", "God", "peace", "heaven"],
    ref: "Matt. 5:9",
    reward: 20,
  },
  {
    verse: "The fruit of the Spirit is love, joy, ___.",
    answer: "peace",
    options: ["wisdom", "peace", "power", "mercy"],
    ref: "Gal. 5:22",
    reward: 20,
  },
  {
    verse: "Ask, and it will be given to ___.",
    answer: "you",
    options: ["all", "you", "them", "heaven"],
    ref: "Matt. 7:7",
    reward: 20,
  },
  {
    verse:
      "Faith is the substance of things hoped for, the evidence of things not ___.",
    answer: "seen",
    options: ["known", "seen", "heard", "spoken"],
    ref: "Heb. 11:1",
    reward: 20,
  },
  {
    verse: "Love is patient, love is ___.",
    answer: "kind",
    options: ["strong", "kind", "bright", "loud"],
    ref: "1 Cor. 13:4",
    reward: 20,
  },
  {
    verse: "The Lord will fight for you; you need only to be ___.",
    answer: "still",
    options: ["strong", "ready", "still", "brave"],
    ref: "Ex. 14:14",
    reward: 20,
  },
  {
    verse: "Do not be anxious about anything, but in every situation, by prayer and ___.",
    answer: "petition",
    options: ["petition", "worry", "waiting", "silence"],
    ref: "Phil. 4:6",
    reward: 20,
  },
  {
    verse: "The peace of God, which transcends all understanding, will guard your hearts and your ___.",
    answer: "minds",
    options: ["homes", "minds", "steps", "days"],
    ref: "Phil. 4:7",
    reward: 20,
  },
  {
    verse: "Seek first His kingdom and His ___.",
    answer: "righteousness",
    options: ["glory", "righteousness", "promise", "wisdom"],
    ref: "Matt. 6:33",
    reward: 20,
  },
  {
    verse: "The name of the Lord is a fortified ___.",
    answer: "tower",
    options: ["garden", "tower", "river", "gate"],
    ref: "Prov. 18:10",
    reward: 20,
  },
  {
    verse: "We love because He first loved ___.",
    answer: "us",
    options: ["us", "them", "Israel", "heaven"],
    ref: "1 John 4:19",
    reward: 20,
  },
  {
    verse: "The Lord bless you and keep ___.",
    answer: "you",
    options: ["faith", "peace", "you", "watch"],
    ref: "Num. 6:24",
    reward: 20,
  },
  {
    verse: "Your faith has made you ___.",
    answer: "well",
    options: ["strong", "well", "known", "ready"],
    ref: "Luke 8:48",
    reward: 20,
  },
  {
    verse: "The prayer of a righteous person is powerful and ___.",
    answer: "effective",
    options: ["effective", "quiet", "hidden", "simple"],
    ref: "James 5:16",
    reward: 20,
  },
];

const BIBLE_TRIVIA = [
  {
    q: "How many books are in the Bible?",
    options: ["60", "64", "66", "68"],
    answer: "66",
    reward: 25,
  },
  {
    q: "Who built the ark?",
    options: ["Moses", "Noah", "Abraham", "David"],
    answer: "Noah",
    reward: 15,
  },
  {
    q: "Which book is first in the New Testament?",
    options: ["Mark", "John", "Luke", "Matthew"],
    answer: "Matthew",
    reward: 20,
  },
  {
    q: "How many disciples did Jesus have?",
    options: ["10", "12", "7", "14"],
    answer: "12",
    reward: 15,
  },
  {
    q: "What was Jesus's first miracle?",
    options: [
      "Healing a blind man",
      "Walking on water",
      "Turning water to wine",
      "Raising Lazarus",
    ],
    answer: "Turning water to wine",
    reward: 20,
  },
  {
    q: "In which city was Jesus born?",
    options: ["Nazareth", "Jerusalem", "Bethlehem", "Jericho"],
    answer: "Bethlehem",
    reward: 15,
  },
  {
    q: "Who was swallowed by a great fish?",
    options: ["Elijah", "Jonah", "Daniel", "Ezra"],
    answer: "Jonah",
    reward: 15,
  },
  {
    q: "Who wrote most of the New Testament letters?",
    options: ["Peter", "John", "James", "Paul"],
    answer: "Paul",
    reward: 20,
  },
  {
    q: "How many days did Jesus fast in the wilderness?",
    options: ["30", "40", "50", "7"],
    answer: "40",
    reward: 20,
  },
  {
    q: "Where was Jesus arrested?",
    options: ["Eden", "Gethsemane", "Nazareth", "Bethany"],
    answer: "Gethsemane",
    reward: 25,
  },
  {
    q: "Who denied Jesus three times?",
    options: ["Judas", "Thomas", "Peter", "John"],
    answer: "Peter",
    reward: 15,
  },
  {
    q: "What river was Jesus baptized in?",
    options: ["Nile", "Jordan", "Euphrates", "Galilee"],
    answer: "Jordan",
    reward: 15,
  },
  {
    q: "Who was the first man?",
    options: ["Noah", "Adam", "Moses", "Jacob"],
    answer: "Adam",
    reward: 15,
  },
  {
    q: "Who received the Ten Commandments?",
    options: ["Moses", "David", "Samuel", "Joseph"],
    answer: "Moses",
    reward: 20,
  },
  {
    q: "What did David use to defeat Goliath?",
    options: ["A sword", "A spear", "A sling and stone", "A trumpet"],
    answer: "A sling and stone",
    reward: 20,
  },
  {
    q: "Who was thrown into the lions' den?",
    options: ["Daniel", "Joseph", "Gideon", "Solomon"],
    answer: "Daniel",
    reward: 20,
  },
  {
    q: "Who interpreted Pharaoh's dreams in Egypt?",
    options: ["Aaron", "Joseph", "Joshua", "Elisha"],
    answer: "Joseph",
    reward: 20,
  },
  {
    q: "Who led Israel after Moses?",
    options: ["Joshua", "Caleb", "Samuel", "David"],
    answer: "Joshua",
    reward: 20,
  },
  {
    q: "What sea did God part for Israel?",
    options: ["Dead Sea", "Red Sea", "Sea of Galilee", "Mediterranean Sea"],
    answer: "Red Sea",
    reward: 20,
  },
  {
    q: "Who was the wisest king of Israel?",
    options: ["Saul", "Solomon", "Ahab", "Hezekiah"],
    answer: "Solomon",
    reward: 20,
  },
  {
    q: "Who was Jesus's mother?",
    options: ["Martha", "Mary", "Elizabeth", "Ruth"],
    answer: "Mary",
    reward: 15,
  },
  {
    q: "Who baptized Jesus?",
    options: ["Peter", "John the Baptist", "Paul", "James"],
    answer: "John the Baptist",
    reward: 20,
  },
  {
    q: "Which disciple walked on water toward Jesus?",
    options: ["John", "Peter", "Andrew", "Philip"],
    answer: "Peter",
    reward: 20,
  },
  {
    q: "Which disciple betrayed Jesus?",
    options: ["Judas", "Thomas", "Matthew", "Peter"],
    answer: "Judas",
    reward: 15,
  },
  {
    q: "What did Jesus feed to the 5,000?",
    options: [
      "Bread and fish",
      "Manna and quail",
      "Figs and honey",
      "Grapes and bread",
    ],
    answer: "Bread and fish",
    reward: 20,
  },
  {
    q: "Who climbed a tree to see Jesus?",
    options: ["Zacchaeus", "Nicodemus", "Bartimaeus", "Lazarus"],
    answer: "Zacchaeus",
    reward: 20,
  },
  {
    q: "Who was raised from the dead after four days?",
    options: ["Lazarus", "Stephen", "John", "Malchus"],
    answer: "Lazarus",
    reward: 20,
  },
  {
    q: "In Jesus's parable, who helped the wounded man?",
    options: ["A priest", "A Levite", "A Samaritan", "A soldier"],
    answer: "A Samaritan",
    reward: 20,
  },
  {
    q: "What is the last book of the Bible?",
    options: ["Jude", "Revelation", "Acts", "Romans"],
    answer: "Revelation",
    reward: 20,
  },
  {
    q: "What is the first book of the Bible?",
    options: ["Exodus", "Genesis", "Matthew", "Psalms"],
    answer: "Genesis",
    reward: 15,
  },
  {
    q: "Which book has many songs and prayers?",
    options: ["Psalms", "Numbers", "Acts", "Titus"],
    answer: "Psalms",
    reward: 15,
  },
  {
    q: "Who wrote many Psalms?",
    options: ["David", "Noah", "Luke", "Timothy"],
    answer: "David",
    reward: 20,
  },
  {
    q: "Who was known as the father of many nations?",
    options: ["Abraham", "Isaac", "Jacob", "Moses"],
    answer: "Abraham",
    reward: 20,
  },
  {
    q: "What did God send to guide Israel by night?",
    options: ["A rainbow", "A pillar of fire", "A dove", "A star"],
    answer: "A pillar of fire",
    reward: 20,
  },
  {
    q: "Who saw a burning bush?",
    options: ["Moses", "Elijah", "Daniel", "Peter"],
    answer: "Moses",
    reward: 15,
  },
  {
    q: "Who was queen and helped save her people?",
    options: ["Ruth", "Esther", "Deborah", "Mary"],
    answer: "Esther",
    reward: 20,
  },
  {
    q: "Who said, 'Your people shall be my people'?",
    options: ["Ruth", "Naomi", "Hannah", "Miriam"],
    answer: "Ruth",
    reward: 20,
  },
  {
    q: "On what day did Jesus rise from the dead?",
    options: [
      "The first day",
      "The third day",
      "The seventh day",
      "The fortieth day",
    ],
    answer: "The third day",
    reward: 20,
  },
  {
    q: "Who prayed in the garden before Jesus was arrested?",
    options: ["Jesus", "Peter", "John", "Pilate"],
    answer: "Jesus",
    reward: 20,
  },
  {
    q: "Who prayed and received a son named Samuel?",
    options: ["Hannah", "Sarah", "Rachel", "Rebekah"],
    answer: "Hannah",
    reward: 20,
  },
  {
    q: "Which prophet called fire down on Mount Carmel?",
    options: ["Elijah", "Isaiah", "Jonah", "Jeremiah"],
    answer: "Elijah",
    reward: 25,
  },
  {
    q: "Who became queen of Persia and helped save the Jewish people?",
    options: ["Esther", "Ruth", "Miriam", "Deborah"],
    answer: "Esther",
    reward: 20,
  },
  {
    q: "Who was the first king of Israel?",
    options: ["Saul", "David", "Solomon", "Samuel"],
    answer: "Saul",
    reward: 20,
  },
  {
    q: "What did God provide from heaven for Israel in the wilderness?",
    options: ["Manna", "Olives", "Grapes", "Figs"],
    answer: "Manna",
    reward: 20,
  },
  {
    q: "Who wrote the Gospel of Luke and the book of Acts?",
    options: ["Luke", "Paul", "Peter", "Matthew"],
    answer: "Luke",
    reward: 25,
  },
  {
    q: "Where did the Holy Spirit come upon believers at Pentecost?",
    options: ["Jerusalem", "Bethlehem", "Nazareth", "Rome"],
    answer: "Jerusalem",
    reward: 25,
  },
  {
    q: "Who was blinded on the road to Damascus?",
    options: ["Paul", "Peter", "Stephen", "Barnabas"],
    answer: "Paul",
    reward: 20,
  },
  {
    q: "In the armor of God, what is the shield called?",
    options: ["Faith", "Truth", "Peace", "Salvation"],
    answer: "Faith",
    reward: 20,
  },
];

const SAMARITAN_ACTS = [
  { id: "sa1", label: "Helped a stranger today", icon: "🤝", reward: 30 },
  { id: "sa2", label: "Donated to those in need", icon: "💝", reward: 35 },
  {
    id: "sa3",
    label: "Visited or called someone lonely",
    icon: "📞",
    reward: 25,
  },
  { id: "sa4", label: "Fed someone who was hungry", icon: "🍞", reward: 30 },
  { id: "sa5", label: "Forgave someone who hurt me", icon: "🕊️", reward: 40 },
  {
    id: "sa6",
    label: "Encouraged a struggling friend",
    icon: "💬",
    reward: 25,
  },
  { id: "sa7", label: "Volunteered my time", icon: "⏰", reward: 35 },
  { id: "sa8", label: "Prayed for an enemy", icon: "🙏", reward: 40 },
];

const PREACH_PROMPTS = [
  "Pick a verse first — then explain why it speaks to you today.",
  "Write this as a letter to a friend going through hardship.",
  "What has God been teaching you this week? Tell someone.",
  "Describe a time you felt God show up unexpectedly.",
  "Write a word of encouragement to a stranger who is doubting.",
  "What does grace look like in your daily life? Give an example.",
  "Write a short prayer someone can pray when they feel alone.",
  "Share how you've seen God work in your community recently.",
];

const PUSH_NOTIFICATION_COPY: Record<
  PushNotificationKind,
  { title: string; body: string }
> = {
  friend_request: {
    title: "New friend request",
    body: "Someone wants to pray with you in Spirit Garden.",
  },
  prayer_note: {
    title: "💧 Grow Your Spirit",
    body: "Prayer and thanksgiving help your spirit grow.",
  },
  blessing: {
    title: "Blessing received",
    body: "Someone sent you encouragement in Spirit Garden.",
  },
  shared_prayer: {
    title: "🌱 Time to Pray",
    body: "Spend a few moments with God today.",
  },
  partner_request: {
    title: "Accountability partner request",
    body: "Someone invited you to walk together in Spirit Garden.",
  },
  partner_focus: {
    title: "Shared focus completed",
    body: "Your accountability partner completed your shared focus today.",
  },
  testimony_alert: {
    title: "New testimony alert",
    body: "See what God has done.",
  },
  daily_prayer: {
    title: "🌱 Time to Pray",
    body: "Spend a few moments with God today.",
  },
  grow_spirit: {
    title: "💧 Grow Your Spirit",
    body: "Prayer and thanksgiving help your spirit grow.",
  },
  keep_growing: {
    title: "🔥 Keep Growing",
    body: "Come back today and care for your spirit.",
  },
};

const APP_TESTING_MODE = false; // Launch mode: normal item costs and long-term stone pacing.

const CLOTHES: ClothingItem[] = [
  {
    id: "wings_grace",
    name: "Wings of Grace",
    icon: "🪽",
    cost: 75,
    desc: "Soft heavenly wings that make your spirit feel lifted and protected.",
    slot: "wings",
    color: "#f8fafc",
    wingColor: "#dbeafe",
  },
  {
    id: "wings_pearl",
    name: "Pearl Wings",
    icon: "🪽",
    cost: 60,
    desc: "Bright pearl wings with a soft, peaceful glow.",
    slot: "wings",
    color: "#fffaf0",
    wingColor: "#f8fafc",
  },
  {
    id: "wings_sky",
    name: "Sky Blue Wings",
    icon: "🪽",
    cost: 65,
    desc: "Gentle blue wings that feel calm, airy, and light.",
    slot: "wings",
    color: "#eff6ff",
    wingColor: "#93c5fd",
  },
  {
    id: "wings_rose",
    name: "Blush Rose Wings",
    icon: "🌸",
    cost: 70,
    desc: "Soft pink wings with a sweet bloom of grace.",
    slot: "wings",
    color: "#fff1f2",
    wingColor: "#fda4af",
  },
  {
    id: "wings_lavender",
    name: "Lavender Wings",
    icon: "💜",
    cost: 72,
    desc: "Dreamy lavender wings for a gentle, royal glow.",
    slot: "wings",
    color: "#f5f3ff",
    wingColor: "#c4b5fd",
  },
  {
    id: "wings_emerald",
    name: "Garden Wings",
    icon: "🌿",
    cost: 68,
    desc: "Fresh green wings inspired by growth, healing, and new life.",
    slot: "wings",
    color: "#ecfdf5",
    wingColor: "#86efac",
  },
  {
    id: "wings_angel",
    name: "Golden Angel Wings",
    icon: "✨",
    cost: 90,
    desc: "Layered golden wings that shine with warm heavenly light.",
    slot: "wings",
    color: "#fef9c3",
    wingColor: "#fde047",
  },
  {
    id: "wings_fire",
    name: "Seraph Fire Wings",
    icon: "🔥",
    cost: 95,
    desc: "Radiant fire-colored wings for a bold, worshipful spirit.",
    slot: "wings",
    color: "#fff7ed",
    wingColor: "#fb923c",
  },
  {
    id: "halo_grace",
    name: "Halo of Grace",
    icon: "⭕",
    cost: 70,
    desc: "A gentle golden glow resting above your spirit.",
    slot: "halo",
    color: "#fefce8",
    haloColor: "#fcd34d",
  },
];
const ARMOR_CLOSET_TEST_MODE = APP_TESTING_MODE;

const SLOT_LABELS: Record<string, string> = {
  robe: "Righteousness",
  hat: "Helmet",
  shield: "Shield",
  hand: "Sword",
  feet: "Shoes",
  belt: "Belt",
  wings: "Wings",
  halo: "Halo",
  set: "Full Armor ✦",
};
const SLOT_ORDER: Slot[] = [
  "set",
  "belt",
  "robe",
  "feet",
  "shield",
  "hat",
  "hand",
  "wings",
  "halo",
];

const HOME_ITEMS: HomeItem[] = [
  {
    id: "wp_radiant_city",
    name: "Cotton City",
    icon: "🌤️",
    cost: 63,
    desc: "Pink-gold clouds over a tiny shining city.",
    slot: "wallpaper",
    bg: "linear-gradient(180deg,#bfe8ff 0%,#fff7d6 42%,#ffe5a3 78%,#fff1c9 100%)",
    accent: "#f0c75e",
  },
  {
    id: "wp_clouds",
    name: "Marshmallow Clouds",
    icon: "☁️",
    cost: 42,
    desc: "Soft puffy clouds with sleepy golden light.",
    slot: "wallpaper",
    bg: "linear-gradient(180deg,#e8f6ff 0%,#fff8df 72%,#ffe7ae 100%)",
    accent: "#7dd3fc",
  },
  {
    id: "wp_sunburst",
    name: "Sun Hug",
    icon: "☀️",
    cost: 77,
    desc: "Warm rays wrapping the home like a morning hug.",
    slot: "wallpaper",
    bg: "radial-gradient(circle at 50% 5%,#fff7cc 0%,#ffe08a 20%,#dbeafe 48%,#fff8df 100%)",
    accent: "#f59e0b",
  },
  {
    id: "wp_roses",
    name: "Blush Rose",
    icon: "🌹",
    cost: 56,
    desc: "A sweet rose-colored sky with gentle garden light.",
    slot: "wallpaper",
    bg: "linear-gradient(180deg,#fff1f2 0%,#fce7f3 45%,#fff7d6 100%)",
    accent: "#f472b6",
  },
  {
    id: "fl_gold_path",
    name: "Honey Path",
    icon: "🟨",
    cost: 77,
    desc: "A cute golden path lined with tiny blossoms.",
    slot: "floor",
    color: "#f7d778",
    accent: "#d6a21a",
  },
  {
    id: "fl_marble_steps",
    name: "Pearl Path",
    icon: "⬜",
    cost: 53,
    desc: "Soft ivory steps that glow like little pearls.",
    slot: "floor",
    color: "#fffaf0",
    accent: "#d6c7a1",
  },
  {
    id: "fl_cloud_walk",
    name: "Cloud Walk",
    icon: "🌤️",
    cost: 63,
    desc: "A dreamy walkway floating over soft clouds.",
    slot: "floor",
    color: "#e0f2fe",
    accent: "#93c5fd",
  },
  {
    id: "fl_pasture",
    name: "Psalm Meadow",
    icon: "🌱",
    cost: 31,
    desc: "A cozy green pasture for peaceful rest.",
    slot: "floor",
    color: "#dcfce7",
    accent: "#86efac",
  },
  {
    id: "furn_waterfall",
    name: "Crystal Falls",
    icon: "💧",
    cost: 88,
    desc: "A soft waterfall flowing beside the golden path.",
    slot: "furniture",
    accent: "#60a5fa",
  },
  {
    id: "furn_lily_pond",
    name: "Lily Pond",
    icon: "🪷",
    cost: 72,
    desc: "A quiet pond with lilies and gentle ripples.",
    slot: "furniture",
    accent: "#67e8f9",
  },
  {
    id: "dec_gates",
    name: "Golden Gates",
    icon: "🚪",
    cost: 84,
    desc: "Small shining gates opening into the garden.",
    slot: "decor",
    accent: "#c99012",
  },
  {
    id: "dec_lanterns",
    name: "Glow Lanterns",
    icon: "🏮",
    cost: 53,
    desc: "Sweet lanterns twinkling along the path.",
    slot: "decor",
    accent: "#f59e0b",
  },
  {
    id: "dec_rainbow",
    name: "Promise Bow",
    icon: "🌈",
    cost: 59,
    desc: "A soft rainbow of God's faithful promises that stretches across your heavenly sky.",
    slot: "decor",
    accent: "#e879f9",
  },
  {
    id: "pet_dove",
    name: "Peace Dove",
    icon: "🕊️",
    cost: 74,
    desc: "A gentle dove fluttering over your home.",
    slot: "pet",
    accent: "#bae6fd",
  },
  {
    id: "pet_butterfly",
    name: "Promise Butterfly",
    icon: "🦋",
    cost: 52,
    desc: "A tiny butterfly that flutters across your heavenly garden.",
    slot: "pet",
    accent: "#67e8f9",
  },
  {
    id: "eff_glory_rays",
    name: "Glory Rays",
    icon: "✨",
    cost: 62,
    desc: "Soft rays shine behind your spirit.",
    slot: "effect",
    accent: "#facc15",
  },
  {
    id: "eff_covenant_rainbow",
    name: "Covenant Rainbow",
    icon: "🌈",
    cost: 70,
    desc: "A gentle rainbow rests in the sky.",
    slot: "effect",
    accent: "#c084fc",
  },
  {
    id: "eff_shooting_stars",
    name: "Shooting Stars",
    icon: "💫",
    cost: 78,
    desc: "Stars quietly streak across the sky.",
    slot: "effect",
    accent: "#93c5fd",
  },
  {
    id: "eff_night_aura",
    name: "Night Aura",
    icon: "🌌",
    cost: 86,
    desc: "Tiny constellations glow around your spirit.",
    slot: "effect",
    accent: "#818cf8",
  },
];
const HIDDEN_HOME_SHOP_ITEM_IDS = new Set([
  "furn_waterfall",
  "furn_lily_pond",
  "dec_gates",
  "dec_lanterns",
  "dec_rainbow",
]);
const HOME_SHOP_ITEMS = HOME_ITEMS.filter(
  (item) => !HIDDEN_HOME_SHOP_ITEM_IDS.has(item.id)
);
const HOME_SHOP_TEST_MODE = APP_TESTING_MODE;

const HOME_SLOT_LABELS: Record<HomeSlot, string> = {
  wallpaper: "Sky",
  floor: "Path",
  window: "Palace",
  furniture: "Feature",
  decor: "Decor",
  pet: "Companion",
  garden: "Garden",
  effect: "Heavenly Effects",
};
const HOME_SLOT_ORDER: HomeSlot[] = [
  "wallpaper",
  "floor",
  "furniture",
  "effect",
  "pet",
];
const HOME_SLOT_HELP: Record<HomeSlot, string> = {
  wallpaper: "soft background",
  floor: "golden walkway",
  window: "main home look",
  decor: "rainbows and lights",
  garden: "quiet flowers",
  furniture: "home features",
  pet: "gentle garden life",
  effect: "heavenly atmosphere",
};
const HOME_ROLE_LABELS: Record<HomeSlot, string> = {
  wallpaper: "Sky",
  floor: "Path",
  window: "Palace",
  furniture: "Feature",
  decor: "Decor",
  pet: "Companion",
  garden: "Garden",
  effect: "Heavenly Effect",
};
const HOME_EFFECT_LABELS: Record<HomeSlot, string> = {
  wallpaper: "Sets the mood",
  floor: "Holds your stones",
  window: "Grows with faith",
  furniture: "Adds a feature",
  decor: "Adds soft detail",
  pet: "Adds gentle life",
  garden: "Softens the scene",
  effect: "Adds holy atmosphere",
};
const HOME_GROWTH_STAGES: HomeGrowthStage[] = [
  {
    level: 0,
    name: "Humble Shelter",
    icon: "🕯️",
    minItems: 0,
    desc: "A quiet place for faith to begin.",
  },
  {
    level: 1,
    name: "Peace Cottage",
    icon: "🏡",
    minItems: 3,
    desc: "A warm cottage for a growing spirit.",
  },
  {
    level: 2,
    name: "Growing Heavenly Home",
    icon: "🌿",
    minItems: 6,
    desc: "Your faithful habits are helping your home grow.",
  },
  {
    level: 3,
    name: "Sanctuary Courtyard",
    icon: "🕊️",
    minItems: 10,
    desc: "Prayer and gratitude are shaping this peaceful place.",
  },
  {
    level: 4,
    name: "Palace Courtyard",
    icon: "✨",
    minItems: 14,
    desc: "Answered prayers bring peace and beauty here.",
  },
  {
    level: 5,
    name: "Sanctuary of Light",
    icon: "✦",
    minItems: 18,
    desc: "A peaceful sanctuary, steady and bright.",
  },
  {
    level: 6,
    name: "Heavenly Realm",
    icon: "✦",
    minItems: 24,
    desc: "Grace, prayer, and testimony shine together.",
  },
];
const getHomeGrowthStage = (ownedCount: number): HomeGrowthStage =>
  [...HOME_GROWTH_STAGES]
    .reverse()
    .find((stage) => ownedCount >= stage.minItems) || HOME_GROWTH_STAGES[0];

const HOME_STAGE_SIGNATURES: Record<number, { title: string; line: string; vessel: string }> = {
  0: {
    title: "A gentle home is beginning.",
    line: "Every faithful habit helps your home begin.",
    vessel: "seed",
  },
  1: {
    title: "Your home is growing warmer.",
    line: "Keep tending it, one day at a time.",
    vessel: "warm",
  },
  2: {
    title: "Your home is growing in faith.",
    line: "Every faithful habit helps your home grow.",
    vessel: "word",
  },
  3: {
    title: "Your home feels peaceful.",
    line: "Prayer and thanks are making it steady.",
    vessel: "sanctuary",
  },
  4: {
    title: "Your home is flourishing.",
    line: "Answered prayers are bringing new peace.",
    vessel: "radiant",
  },
  5: {
    title: "Grace is filling your home.",
    line: "Your faithful habits are becoming beauty.",
    vessel: "radiant",
  },
  6: {
    title: "Your home is radiant.",
    line: "Prayer, gratitude, and grace shine here.",
    vessel: "realm",
  },
};

const HOME_STAGE_BY_SPIRIT_STAGE: Record<string, number> = {
  seed: 0,
  baby: 1,
  teen: 2,
  adult: 3,
  radiant: 4,
};
const getHomeGrowthStageForSpiritStage = (stageKey?: string): HomeGrowthStage => {
  const level = HOME_STAGE_BY_SPIRIT_STAGE[stageKey || "seed"] ?? 0;
  return HOME_GROWTH_STAGES.find((homeStage) => homeStage.level === level) || HOME_GROWTH_STAGES[0];
};
const getNextHomeGrowthStageForSpiritStage = (stageKey?: string): HomeGrowthStage | null => {
  const level = HOME_STAGE_BY_SPIRIT_STAGE[stageKey || "seed"] ?? 0;
  return HOME_GROWTH_STAGES.find((homeStage) => homeStage.level > level) || null;
};

const SPIRIT_STAGES = [
  {
    name: "Seed of Faith",
    xpMin: 0,
    xpMax: 199,
    desc: "A tiny seed of faith, quietly beginning to glow.",
    key: "seed",
    aura: "#fde68a",
    glow: "#d99a12",
  },
  {
    name: "Baby Spirit",
    xpMin: 200,
    xpMax: 999,
    desc: "Your spirit is newly born — soft, precious, and growing.",
    key: "baby",
    aura: "#fbcfe8",
    glow: "#f472b6",
  },
  {
    name: "Growing Spirit",
    xpMin: 1000,
    xpMax: 2999,
    desc: "Your faith is stretching upward, bright and full of new strength.",
    key: "teen",
    aura: "#bfdbfe",
    glow: "#3b82f6",
  },
  {
    name: "Mature Spirit",
    xpMin: 3000,
    xpMax: 7999,
    desc: "Your spirit feels rooted, steady, and crowned with quiet wisdom.",
    key: "adult",
    aura: "#ddd6fe",
    glow: "#8b5cf6",
  },
  {
    name: "Radiant Spirit",
    xpMin: 8000,
    xpMax: Infinity,
    desc: "Your light shines before others. Glorify God.",
    key: "radiant",
    aura: "#fecdd3",
    glow: "#f43f5e",
  },
];

const TESTIMONY_STONE_LIBRARY: Record<
  TestimonyStoneKind,
  Omit<TestimonyStone, "id" | "date" | "source">
> = {
  prayer: {
    kind: "prayer",
    name: "Prayer Stone",
    icon: "🙏",
    desc: "A soft blue stone earned by returning faithfully to prayer.",
  },
  kindness: {
    kind: "kindness",
    name: "Kindness Stone",
    icon: "🤝",
    desc: "A warm stone for love shown in action.",
  },
  courage: {
    kind: "courage",
    name: "Courage Stone",
    icon: "🛡️",
    desc: "A bright stone for choosing faith when it is difficult.",
  },
  gratitude: {
    kind: "gratitude",
    name: "Gratitude Stone",
    icon: "🌻",
    desc: "A golden stone for thanksgiving and remembrance.",
  },
  faithfulness: {
    kind: "faithfulness",
    name: "Faithfulness Stone",
    icon: "🌱",
    desc: "A green stone for steady days and small faithful steps.",
  },
  forgiveness: {
    kind: "forgiveness",
    name: "Forgiveness Stone",
    icon: "🕊️",
    desc: "A gentle stone for mercy, release, and grace.",
  },
  worship: {
    kind: "worship",
    name: "Worship Stone",
    icon: "🎵",
    desc: "A shining stone for praise lifted to God.",
  },
  answered: {
    kind: "answered",
    name: "Answered Prayer Stone",
    icon: "🌤️",
    desc: "A heavenly stone for a prayer God answered.",
  },
  white: {
    kind: "white",
    name: "White Stone",
    icon: "🤍",
    desc: "The ultimate heavenly stone, given when your garden holds every major witness.",
    ultimate: true,
  },
};
const QUEST_STONE_KINDS: TestimonyStoneKind[] = [
  "prayer",
  "kindness",
  "courage",
  "gratitude",
  "faithfulness",
  "forgiveness",
  "worship",
];
const TESTIMONY_STONE_FAST_MODE = APP_TESTING_MODE;

const DEFAULT_HAIR_COLOR = "#F5DEB3";
const ONBOARDING_HAIR_COLOR = "#B95A3A";

const PRAYER_FOCUS = [
  {
    title: "Peace for the anxious",
    prompt: "Pray for someone carrying anxiety today.",
    verse: "Present your requests to God.",
    ref: "Philippians 4:6",
  },
  {
    title: "Strength for the weary",
    prompt: "Pray for someone who feels tired or discouraged.",
    verse: "He gives strength to the weary.",
    ref: "Isaiah 40:29",
  },
  {
    title: "Comfort for the lonely",
    prompt: "Pray for someone who needs to feel remembered.",
    verse: "I will not leave you comfortless.",
    ref: "John 14:18",
  },
  {
    title: "Wisdom for decisions",
    prompt: "Pray for someone making a hard choice.",
    verse: "If any of you lacks wisdom, ask God.",
    ref: "James 1:5",
  },
  {
    title: "Healing and restoration",
    prompt: "Pray for someone who needs healing in body, heart, or family.",
    verse: "I am the Lord who heals you.",
    ref: "Exodus 15:26",
  },
  {
    title: "Protection and rescue",
    prompt: "Pray for someone facing difficulty or danger.",
    verse: "God is our refuge and strength.",
    ref: "Psalm 46:1",
  },
  {
    title: "Gratitude and joy",
    prompt: "Pray with thanksgiving for what God has already done.",
    verse: "Give thanks in all circumstances.",
    ref: "1 Thessalonians 5:18",
  },
];

const QUICK_PRAYER_TOPICS = [
  "Healing",
  "Family",
  "Guidance",
  "Wisdom",
  "Relationships",
  "Work",
  "Gratitude",
  "Faith",
  "Forgiveness",
  "Other",
];

const MOOD_OPTIONS = [
  {
    mood: "anxious",
    label: "Anxious",
    icon: "🌧️",
    verse:
      "Do not be anxious about anything; by prayer present your requests to God.",
    ref: "Philippians 4:6",
  },
  {
    mood: "tired",
    label: "Tired",
    icon: "🌙",
    verse:
      "Come to me, all who are weary and burdened, and I will give you rest.",
    ref: "Matthew 11:28",
  },
  {
    mood: "grateful",
    label: "Grateful",
    icon: "🌤️",
    verse: "Give thanks to the Lord, for He is good.",
    ref: "Psalm 107:1",
  },
  {
    mood: "hopeful",
    label: "Hopeful",
    icon: "🌱",
    verse: "Those who hope in the Lord will renew their strength.",
    ref: "Isaiah 40:31",
  },
  {
    mood: "peaceful",
    label: "Peaceful",
    icon: "🕊️",
    verse: "My peace I give you.",
    ref: "John 14:27",
  },
];

// ─── DAILY QUEST POOL ────────────────────────────────────────────────────────
type QuestItem =
  | { kind: "verse"; idx: number }
  | { kind: "trivia"; idx: number }
  | { kind: "samaritan"; idx: number }
  | { kind: "preach" };

function buildDailyQuests(dateKey: string): QuestItem[] {
  const seed = dateKey
    .split("-")
    .map(Number)
    .reduce((a, b) => a * 31 + b, 7);
  const rng = (() => {
    let s = seed;
    return () => {
      s = (s * 1664525 + 1013904223) & 0xffffffff;
      return (s >>> 0) / 0xffffffff;
    };
  })();
  const pool: QuestItem[] = [
    ...VERSE_QUIZ.map((_, i) => ({ kind: "verse" as const, idx: i })),
    ...BIBLE_TRIVIA.map((_, i) => ({ kind: "trivia" as const, idx: i })),
    ...SAMARITAN_ACTS.map((_, i) => ({ kind: "samaritan" as const, idx: i })),
    { kind: "preach" as const },
  ];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, 7);
}

// ─────────────────────────────────────────────────────────────────────────────
// NEGLECT TRACKING — how dark is the home based on missed days
// ─────────────────────────────────────────────────────────────────────────────
function computeNeglectDarkness(activityLog: Record<string, boolean>): number {
  // Returns 0 (bright) to 1 (very dark) based on consecutive missed days.
  // Brand-new users and anyone active today should start with a bright home.
  const today = new Date();
  const todayId = dateKey(today);
  const activeDays = Object.keys(activityLog).filter((key) => activityLog[key]);

  if (activityLog[todayId] || activeDays.length === 0) return 0;

  let missedStreak = 0;
  for (let i = 1; i <= 7; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = dateKey(d);
    if (activityLog[key]) break;
    missedStreak++;
  }
  return Math.min(missedStreak / 5, 1);
}

function computeWalkBloomStreak(homeBlooms: HomeBloom[]): number {
  const completedDays = new Set(
    homeBlooms.filter((bloom) => bloom.kind === "walk").map((bloom) => bloom.date)
  );
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    if (!completedDays.has(dateKey(d))) break;
    streak++;
  }
  return streak;
}

const SPIRIT_VISITOR_COPY: Record<SpiritVisitorKind, { name: string; article: string }> = {
  dove: { name: "Dove", article: "A" },
  golden_dove: { name: "Golden Dove", article: "A" },
  lamb: { name: "Lamb", article: "A" },
  butterfly: { name: "Butterfly", article: "A" },
  bluebird: { name: "Bluebird", article: "A" },
  deer: { name: "Deer", article: "A" },
  fox: { name: "Fox", article: "A" },
  rabbit: { name: "Rabbit", article: "A" },
};

function activeSpiritVisitors(visitors: SpiritVisitor[], now = Date.now()) {
  return visitors
    .filter((visitor) => visitor.expiresAt > now)
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 3);
}

// ─────────────────────────────────────────────────────────────────────────────
// PERSISTENCE
// ─────────────────────────────────────────────────────────────────────────────
const dateKey = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayKey = () => dateKey(new Date());
const isDateKey = (value?: string): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value);

function parseDateKey(value?: string) {
  if (!isDateKey(value)) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function daysSinceDateKey(startDate: string, endDate: string) {
  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);
  if (!start || !end) return 1;
  const diff = end.getTime() - start.getTime();
  return Math.max(1, Math.floor(diff / 86400000) + 1);
}

function earliestJourneyDate({
  activityLog,
  entries,
  prayerFriends,
  lastActive,
  today,
}: {
  activityLog: Record<string, boolean>;
  entries: JournalEntry[];
  prayerFriends: PrayerFriend[];
  lastActive?: string;
  today: string;
}) {
  const candidates = [
    today,
    lastActive,
    ...Object.keys(activityLog || {}),
    ...entries.map((entry) => entry.date),
    ...prayerFriends.flatMap((prayer) => [
      prayer.challengeStarted,
      prayer.answeredDate,
      prayer.publicSubmittedAt,
      ...(Array.isArray(prayer.prayedDates) ? prayer.prayedDates : []),
    ]),
  ].filter(isDateKey);

  return candidates.sort()[0] || today;
}
function stoneForKind(
  kind: TestimonyStoneKind,
  date: string,
  source: string,
  idSuffix: string
): TestimonyStone {
  const base = TESTIMONY_STONE_LIBRARY[kind];
  return { ...base, id: `${kind}_${idSuffix}`, date, source };
}

function testimonyGemColors(
  kind: TestimonyStoneKind
): [string, string, string] {
  const colors: Record<TestimonyStoneKind, [string, string, string]> = {
    prayer: ["#e0f2fe", "#60c5f8", "#1d72d2"],       // sapphire blue
    kindness: ["#ffe4ec", "#fb8fb8", "#d9467b"],     // rose pink
    courage: ["#ede9fe", "#9f7aea", "#5b21b6"],      // royal violet
    gratitude: ["#fff0bf", "#f7c84f", "#b87912"],    // thanksgiving gold
    faithfulness: ["#e3f8d8", "#8dd86f", "#2f8f46"], // garden green
    forgiveness: ["#ecfeff", "#8ee8df", "#0891b2"],  // aqua mercy
    worship: ["#fae8ff", "#d77cff", "#9333ea"],      // praise amethyst
    answered: ["#fff7d6", "#f5b66b", "#d66b2d"],     // sunrise amber
    white: ["#ffffff", "#edf5ff", "#b8c7e6"],        // pearl white
  };
  return colors[kind];
}

function prayerStreak(dates: string[], today: string): number {
  const prayed = new Set(dates);
  let streak = 0;
  const d = new Date(`${today}T00:00:00`);
  for (let i = 0; i < 30; i++) {
    const key = dateKey(d);
    if (!prayed.has(key)) break;
    streak++;
    d.setDate(d.getDate() - 1);
  }
  return streak;
}

type MonthGroup<T> = { key: string; label: string; items: T[] };
function monthKey(date?: string): string {
  return date && /^\d{4}-\d{2}/.test(date) ? date.slice(0, 7) : "undated";
}
function monthLabel(date?: string): string {
  if (!date) return "Undated";
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "Undated";
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
function groupByMonth<T extends { date?: string }>(
  items: T[]
): MonthGroup<T>[] {
  const order: string[] = [];
  const groups: Record<string, MonthGroup<T>> = {};
  items.forEach((item) => {
    const key = monthKey(item.date);
    if (!groups[key]) {
      order.push(key);
      groups[key] = { key, label: monthLabel(item.date), items: [] };
    }
    groups[key].items.push(item);
  });
  return order.map((key) => groups[key]);
}
const LS = {
  get: <T,>(k: string, fb: T): T => {
    try {
      const r = localStorage.getItem(k);
      return r != null ? (JSON.parse(r) as T) : fb;
    } catch {
      return fb;
    }
  },
  set: (k: string, v: unknown): void => {
    try {
      localStorage.setItem(k, JSON.stringify(v));
    } catch {}
  },
};

function formatSpiritName(value: string) {
  return value.replace(
    /(^|\s)([a-z])/g,
    (_match, space, letter) => space + letter.toUpperCase()
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL CSS
// ─────────────────────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600&family=IM+Fell+English:ital@0;1&family=Nunito:wght@400;600;700;800;900&display=swap');
  :root {
    --gold:#C99A3D; --gold-light:#E8C66F; --gold-pale:#F4E6B8; --gold-shine:#FFF7DC;
    --gold-deep:#6E5322; --text-dark:#2F3028; --text-mid:#45483E; --text-soft:#5A5D52;
    --text-faint:#6D7166; --green:#557A5B; --red:#A04444;
    --sky:#6F91A9; --sky-soft:#DCEAF0; --sage:#78947D; --sage-soft:#E3EEE5;
    --card-bg:rgba(255,254,249,0.92); --card-border:rgba(91,126,148,0.20);
    --gold-grad:linear-gradient(135deg,#F4D989 0%,#C99A3D 68%,#A77A2C 100%);
    --gold-grad-s:linear-gradient(135deg,#FFF7DC 0%,#E8C66F 100%);
    --page-grad:#FFFDF8;
  }
  *,*::before,*::after{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}
  html,body,#root{min-height:100%;background:#FFFDF8;}
  body{font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:16px;color:var(--text-dark);}
  .app-shell,.app-shell .app-header,.app-shell .current-stage-card,.app-shell .prayer-vessels-section{background:#FFFDF8!important;}
  button{touch-action:manipulation;font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
  .serif{font-family:'Cormorant Garamond',Georgia,serif;font-weight:500;}
  @keyframes av_pulse{0%,100%{opacity:.55;transform:scale(1)}50%{opacity:1;transform:scale(1.08)}}
  @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
  @keyframes twinkle{0%,100%{opacity:.08;transform:scale(.8)}50%{opacity:.4;transform:scale(1.4)}}
  @keyframes toastPop{from{transform:translateX(-50%) translateY(16px);opacity:0}to{transform:translateX(-50%) translateY(0);opacity:1}}
  @keyframes popIn{from{transform:scale(.96);opacity:0}to{transform:scale(1);opacity:1}}
  @keyframes stoneRewardFloat{0%,100%{transform:translateY(0) rotate(-1deg)}50%{transform:translateY(-5px) rotate(1deg)}}
  @keyframes questSlide{from{transform:translateX(18px);opacity:0}to{transform:translateX(0);opacity:1}}
  @keyframes sp0{to{transform:translate(0,-34px);opacity:0}}
  @keyframes sp1{to{transform:translate(24px,-22px);opacity:0}}
  @keyframes sp2{to{transform:translate(30px,10px);opacity:0}}
  @keyframes sp3{to{transform:translate(0,32px);opacity:0}}
  @keyframes sp4{to{transform:translate(-30px,10px);opacity:0}}
  @keyframes sp5{to{transform:translate(-24px,-22px);opacity:0}}
  @keyframes modalIn{from{transform:scale(.94) translateY(20px);opacity:0}to{transform:scale(1) translateY(0);opacity:1}}
  @keyframes stageRevealIn{from{opacity:0;transform:translateY(18px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}
  @keyframes stageRevealFloat{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-10px) scale(1.015)}}
  @keyframes stageRevealGlow{0%,100%{opacity:.48;transform:scale(.92)}50%{opacity:.86;transform:scale(1.08)}}
  .stage-reveal-content{animation:stageRevealIn .55s cubic-bezier(.2,.8,.2,1) both;}
  .stage-reveal-kicker{font-size:12px;letter-spacing:1.7px;text-transform:uppercase;color:var(--gold-deep);font-weight:900;margin-bottom:4px;}
  .stage-reveal-avatar-wrap{width:min(76vw,360px);height:min(76vw,360px);max-height:360px;margin:0 auto 4px;position:relative;display:flex;align-items:center;justify-content:center;}
  .stage-reveal-radiance{position:absolute;inset:10%;border-radius:50%;background:radial-gradient(circle,rgba(255,255,255,.98) 0%,rgba(255,225,121,.62) 35%,rgba(240,203,118,.18) 62%,transparent 76%);filter:blur(2px);animation:stageRevealGlow 3.2s ease-in-out infinite;}
  .stage-reveal-avatar{position:relative;z-index:2;animation:stageRevealFloat 3.5s ease-in-out infinite;filter:drop-shadow(0 18px 24px rgba(116,79,22,.18));}
  .stage-reveal-spark{position:absolute;z-index:1;color:#d3a64b;font-size:22px;text-shadow:0 0 12px rgba(255,225,121,.95);animation:twinkle 2.4s ease-in-out infinite;}
  @keyframes prayerTravel{0%{transform:translate(0,0) scale(.7);opacity:0}15%{opacity:1}70%{opacity:1}100%{transform:translate(var(--dx),var(--dy)) scale(.25);opacity:0}}
  @keyframes angelFloat{0%{transform:translate(-50%,12px) scale(.96);opacity:0}18%{opacity:1}78%{opacity:1}100%{transform:translate(-50%,-10px) scale(1);opacity:0}}
  @keyframes homeRestore{0%{opacity:0;transform:scale(.96)}25%{opacity:.95}100%{opacity:0;transform:scale(1.08)}}
  @keyframes homeBuildLight{0%{opacity:0;transform:translateY(22px) scaleY(.4)}25%{opacity:.8}100%{opacity:0;transform:translateY(-24px) scaleY(1.12)}}
  @keyframes homeTransformReveal{0%{opacity:0;transform:scale(.96) translateY(10px)}24%{opacity:.9;transform:scale(1.005) translateY(0)}78%{opacity:.72;transform:scale(1)}100%{opacity:0;transform:scale(1.025) translateY(-5px)}}
  @keyframes homeTransformRing{0%{opacity:0;transform:translate(-50%,-50%) scale(.55)}36%{opacity:.38}100%{opacity:0;transform:translate(-50%,-50%) scale(1.55)}}
  @keyframes homeTransformSpark{0%{opacity:0;transform:translateY(10px) scale(.86)}32%{opacity:.58}100%{opacity:0;transform:translateY(-24px) scale(1.04)}}
  @keyframes homeBuildRise{0%{opacity:.18;clip-path:inset(100% 0 0 0);transform:translateY(22px) scale(.96)}22%{opacity:.75}68%{clip-path:inset(18% 0 0 0);transform:translateY(2px) scale(1)}100%{opacity:1;clip-path:inset(0 0 0 0);transform:translateY(0) scale(1)}}
  @keyframes homeScaffoldDraw{0%{opacity:0;transform:scaleX(.35)}20%{opacity:.65}72%{opacity:.48;transform:scaleX(1)}100%{opacity:0;transform:scaleX(1.05)}}
  @keyframes angelBuildFloat{0%{opacity:0;transform:translate(var(--ax,0),18px) scale(.82)}16%{opacity:.92}50%{transform:translate(calc(var(--ax,0) + var(--drift,8px)),-8px) scale(1)}84%{opacity:.82}100%{opacity:0;transform:translate(calc(var(--ax,0) + var(--drift,8px)),-28px) scale(.9)}}
  @keyframes angelBuildSpark{0%{opacity:0;transform:translateY(10px) scale(.8)}25%{opacity:.7}100%{opacity:0;transform:translateY(-28px) scale(1.08)}}
  @keyframes buildBeamLift{0%{opacity:0;transform:translateY(28px) rotate(var(--rot,0deg)) scaleX(.65)}24%{opacity:.72}70%{opacity:.52;transform:translateY(0) rotate(var(--rot,0deg)) scaleX(1)}100%{opacity:0;transform:translateY(-18px) rotate(var(--rot,0deg)) scaleX(1.04)}}
  @keyframes praiseGlow{0%,100%{filter:drop-shadow(0 0 8px rgba(240,199,94,.35));transform:scale(1)}50%{filter:drop-shadow(0 0 24px rgba(240,199,94,.85));transform:scale(1.03)}}
  .card{background:linear-gradient(145deg,rgba(255,254,250,.95),rgba(249,251,249,.90));border:1px solid var(--card-border);border-radius:20px;padding:18px;backdrop-filter:blur(12px);box-shadow:0 5px 20px rgba(65,82,77,.055);}
  .focus-card{background:linear-gradient(135deg,rgba(255,253,246,.99),rgba(255,248,223,.66));border-color:rgba(211,166,75,.34);box-shadow:0 4px 18px rgba(138,90,8,.07);}
  .soft-guide-card{background:linear-gradient(135deg,rgba(255,253,246,.96),rgba(255,249,236,.72));border-color:rgba(184,147,75,.14);box-shadow:0 2px 12px rgba(138,90,8,.045);}
  .sacred-card{background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(246,250,255,.72));border-color:rgba(125,174,212,.22);box-shadow:0 2px 14px rgba(65,95,125,.06);}
  .answered-altar-card{position:relative;overflow:hidden;background:linear-gradient(160deg,rgba(255,253,246,.99),rgba(246,250,255,.76) 48%,rgba(255,248,223,.56));border-color:rgba(125,174,212,.28);box-shadow:0 5px 20px rgba(65,95,125,.08);}
  .answered-altar-card::before{content:"";position:absolute;left:14%;right:14%;top:10px;height:1px;background:linear-gradient(90deg,transparent,rgba(184,147,75,.34),transparent);}
  .answered-altar-header{display:flex;align-items:center;gap:9px;}
  .answered-altar-icon{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle,#fff,#dbeafe 54%,rgba(240,203,118,.34));border:1px solid rgba(125,174,212,.28);box-shadow:0 6px 16px rgba(65,95,125,.08);}
  .gold-btn{border:none;border-radius:14px;padding:12px 22px;background:var(--gold-grad);color:var(--text-dark);font-weight:800;cursor:pointer;font-size:14px;font-family:'Nunito',sans-serif;transition:all 0.25s;box-shadow:0 4px 14px rgba(138,90,8,.28);min-height:44px;}
  .gold-btn:disabled{background:rgba(255,226,122,.38);color:var(--text-faint);box-shadow:none;cursor:default;}
  .gold-btn:not(:disabled):active{filter:brightness(1.08);transform:translateY(-1px);}
  .bottom-tabs{position:fixed;bottom:0;left:0;right:0;z-index:100;background:rgba(248,251,249,.90);border-top:1px solid rgba(91,126,148,.18);padding:6px 4px env(safe-area-inset-bottom,8px);display:flex;justify-content:space-around;backdrop-filter:blur(20px);box-shadow:0 -5px 24px rgba(65,82,77,.07);}
  .tab-btn{display:flex;flex-direction:column;align-items:center;gap:2px;background:none;border:none;cursor:pointer;padding:6px 8px;border-radius:14px;transition:all 0.18s;min-width:52px;flex:1;max-width:72px;}
  .tab-btn.active{background:var(--gold-grad);box-shadow:0 2px 10px rgba(138,90,8,.22);}
  .tab-btn:active{transform:scale(.93);}
  .tab-label{font-size:12px;font-weight:700;color:var(--text-mid);}
  .tab-btn.active .tab-label{font-weight:800;color:var(--text-dark);}
  .task-row{display:flex;align-items:center;border:1.5px solid rgba(91,126,148,.24);background:rgba(251,253,250,.78);border-radius:16px;padding:14px 16px;cursor:pointer;transition:all 0.2s;text-align:left;color:inherit;font-family:inherit;min-height:56px;flex:1;}
  .task-row.done{border-color:rgba(201,154,61,.46);background:linear-gradient(120deg,rgba(255,249,227,.88),rgba(244,230,184,.42));}
  .task-row.disabled-task{opacity:0.62;cursor:default;background:rgba(240,240,230,.4);}
  .quest-card{background:rgba(255,253,240,.75);border:1px solid var(--card-border);border-radius:18px;padding:22px 18px;animation:questSlide .3s ease-out;}
  .quest-progress-dots{display:flex;gap:6px;justify-content:center;margin-bottom:20px;}
  .quest-dot{width:10px;height:10px;min-width:10px;min-height:10px;max-width:10px;max-height:10px;border-radius:50%;background:rgba(212,160,23,.25);transition:all .25s;border:0;padding:0;cursor:pointer;flex:0 0 10px;appearance:none;}
  .quest-dot:focus-visible{outline:2px solid var(--gold);outline-offset:3px;}
  .quest-dot.done-dot{background:var(--green);}
  .quest-dot.active-dot{background:var(--gold);transform:scale(1.3);}
  .quest-nav-row{display:grid;grid-template-columns:1fr 1fr;gap:9px;}
  .quest-nav-btn{border:1.5px solid rgba(91,143,184,.34);border-radius:14px;padding:11px 12px;background:rgba(255,253,246,.72);color:var(--text-mid);font-size:13px;font-weight:900;font-family:'Nunito',sans-serif;cursor:pointer;min-height:42px;}
  .quest-nav-btn.primary{border-color:rgba(211,166,75,.44);background:linear-gradient(135deg,rgba(255,252,223,.92),rgba(255,247,210,.72));color:var(--gold-deep);}
  .quest-nav-btn:disabled{opacity:.45;cursor:not-allowed;}
  @media (max-width:520px){
    .quest-progress-dots{
      flex-wrap:wrap;
      gap:7px;
      margin-bottom:14px;
      padding:0 8px;
    }
    .quest-dot{
      width:10px;
      height:10px;
      min-width:10px;
      min-height:10px;
      max-width:10px;
      max-height:10px;
      box-shadow:none;
      flex-basis:10px;
    }
    .quest-dot.active-dot{
      transform:scale(1.25);
      box-shadow:0 0 0 2px rgba(211,166,75,.18);
    }
    .quest-nav-row{
      gap:8px;
    }
    .quest-nav-btn{
      min-height:46px;
      padding:12px 10px;
      font-size:13.5px;
    }
    .quest-card .opt-btn{
      flex:1 1 100%;
      width:100%;
      min-height:46px;
      font-size:14px;
    }
  }
  @media (prefers-reduced-motion:reduce){
    .stage-reveal-content,.stage-reveal-avatar,.stage-reveal-radiance,.stage-reveal-spark{animation:none!important;}
  }
  .opt-btn{background:rgba(255,253,240,.7);border:1.5px solid var(--card-border);border-radius:12px;padding:12px 14px;color:var(--text-dark);cursor:pointer;font-size:14px;font-family:'Nunito',sans-serif;text-align:left;min-height:44px;transition:all .15s;flex:1 1 auto;}
  .opt-btn:not(:disabled):hover{background:rgba(255,248,223,.92);border-color:var(--gold);}
  .opt-btn.correct{background:rgba(134,239,172,.35);border-color:var(--green);color:var(--green);font-weight:800;}
  .opt-btn.wrong{background:rgba(252,165,165,.35);border-color:var(--red);color:var(--red);}
  .shop-category-card{position:relative;overflow:hidden;background:rgba(255,253,246,.86);border:1.5px solid var(--card-border);border-radius:20px;padding:14px 13px;cursor:pointer;min-width:122px;min-height:112px;flex:0 0 122px;text-align:left;color:var(--text-mid);transition:all .24s ease;box-shadow:0 2px 14px rgba(138,90,8,.06);}
  .shop-category-card::after{content:"";position:absolute;right:-24px;bottom:-28px;width:78px;height:78px;border-radius:50%;background:rgba(255,248,223,.45);pointer-events:none;}
  .shop-category-card.active{background:linear-gradient(145deg,rgba(255,246,207,.98),rgba(240,199,94,.72));border-color:var(--gold);color:var(--text-dark);box-shadow:0 8px 26px rgba(138,90,8,.18);transform:translateY(-2px);}
  .shop-category-card:active{transform:translateY(-1px) scale(.98);}
  .shop-category-icon{width:42px;height:42px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:rgba(255,252,230,.9);border:1px solid rgba(201,144,18,.22);font-size:23px;box-shadow:0 3px 14px rgba(138,90,8,.08);margin-bottom:8px;position:relative;z-index:1;}
  .shop-card{position:relative;overflow:hidden;background:rgba(255,253,246,.82);border:1.5px solid var(--card-border);border-radius:22px;padding:14px 12px;transition:all .24s ease;display:flex;flex-direction:column;box-shadow:0 2px 18px rgba(138,90,8,.07);}
  .shop-card::before{content:"";position:absolute;inset:0;background:radial-gradient(circle at top left,rgba(255,248,223,.62),transparent 48%);opacity:.7;pointer-events:none;}
  .shop-card.owned{border-color:rgba(201,144,18,.62);}
  .shop-card.expanded{box-shadow:0 8px 28px rgba(138,90,8,.14);border-color:rgba(201,144,18,.74);}
  .shop-game-icon{--item-color:#f8e4a3;position:relative;width:58px;height:62px;margin:0 auto 9px;display:flex;align-items:flex-end;justify-content:center;transform-origin:50% 90%;animation:itemBreathe 4.4s ease-in-out infinite;transition:transform .2s ease,filter .2s ease;}
  .shop-card:hover .shop-game-icon{transform:translateY(-3px) scale(1.06);filter:drop-shadow(0 0 14px rgba(240,203,118,.42));}
  .shop-card:active .shop-game-icon{animation:itemPop .24s ease-out;}
  .shop-game-shadow{position:absolute;left:12%;right:12%;bottom:0;height:12px;border-radius:50%;background:radial-gradient(ellipse,rgba(82,55,15,.20),rgba(82,55,15,0) 72%);filter:blur(2px);z-index:0;}
  .shop-game-glow{position:absolute;left:8%;right:8%;top:10%;bottom:10%;border-radius:999px;background:radial-gradient(circle,rgba(255,255,255,.56),var(--item-color) 52%,transparent 78%);opacity:.44;filter:blur(5px);z-index:0;}
  .shop-game-body{position:absolute;left:9%;right:9%;bottom:7px;height:70%;border-radius:22px 22px 17px 17px;background:linear-gradient(150deg,rgba(255,255,255,.98),rgba(255,248,223,.84) 42%,var(--item-color));border:1px solid rgba(184,147,75,.28);box-shadow:inset 0 8px 12px rgba(255,255,255,.72),inset 0 -10px 14px rgba(95,60,8,.08),0 8px 16px rgba(82,55,15,.15);z-index:1;overflow:hidden;}
  .shop-game-body::before{content:"";position:absolute;left:14%;top:12%;width:48%;height:24%;border-radius:999px;background:rgba(255,255,255,.65);}
  .shop-game-symbol{position:relative;z-index:3;font-size:29px;line-height:1;margin-bottom:19px;filter:drop-shadow(0 3px 3px rgba(70,45,8,.18));}
  .shop-game-sparkles{position:absolute;inset:-3px;z-index:4;opacity:0;pointer-events:none;transition:opacity .2s ease;}
  .shop-card:hover .shop-game-sparkles{opacity:1;}
  .shop-game-sparkles span{position:absolute;color:#fff7c7;text-shadow:0 0 8px var(--item-color);font-size:11px;animation:itemSparkle 1.3s ease-in-out infinite;}
  .shop-game-sparkles span:nth-child(1){left:8%;top:22%;}
  .shop-game-sparkles span:nth-child(2){right:6%;top:36%;animation-delay:.2s;}
  .shop-game-sparkles span:nth-child(3){left:24%;bottom:18%;animation-delay:.4s;}
  .shop-game-icon.slot-wings,.shop-game-icon.slot-halo,.shop-game-icon.slot-hat{animation:itemFlutterBody 3.4s ease-in-out infinite;}
  .shop-game-icon.slot-feet{animation:itemStep 1.9s ease-in-out infinite;}
  .shop-game-icon.slot-hand{animation:itemSway 4.2s ease-in-out infinite;}
  .shop-game-icon.slot-set{width:64px;height:66px;}
  .shop-game-icon.slot-set .shop-game-glow,.shop-game-icon.slot-halo .shop-game-glow{opacity:.72;}
  .closet-item-btn .shop-game-icon{width:50px;height:54px;margin-bottom:2px;}
  .closet-item-btn .shop-game-symbol{font-size:25px;margin-bottom:17px;}
  .closet-item-btn:hover .shop-game-icon{transform:translateY(-2px) scale(1.04);filter:drop-shadow(0 0 12px rgba(240,203,118,.36));}
  .testimony-stone-toggle{width:100%;background:transparent;border:none;padding:0;display:flex;justify-content:space-between;align-items:center;gap:10px;text-align:left;cursor:pointer;font-family:'Nunito',sans-serif;color:inherit;}
  .testimony-stone-chevron{width:34px;height:34px;border-radius:999px;background:rgba(255,253,246,.82);border:1px solid var(--card-border);display:flex;align-items:center;justify-content:center;color:var(--gold-deep);font-weight:900;transition:transform .24s ease,background .24s ease;}
  .testimony-stone-chevron.open{transform:rotate(180deg);background:var(--gold-grad-s);}
  .testimony-stone-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:14px;animation:popIn .22s ease-out;}
  .testimony-stone-card{text-align:center;background:linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,248,223,.72));border:1px solid var(--gold-pale);border-radius:16px;padding:12px 8px;transition:transform .2s ease,box-shadow .2s ease,border-color .2s ease;}
  .testimony-stone-card:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(138,90,8,.13);border-color:var(--gold);}
  .testimony-stone-card.locked{background:rgba(255,253,246,.42);border-color:var(--card-border);opacity:.62;}
  .testimony-gem-wrap{position:relative;width:58px;height:50px;margin:0 auto 8px;display:flex;align-items:flex-end;justify-content:center;}
  .testimony-gem-shadow{position:absolute;left:14%;right:14%;bottom:0;height:10px;border-radius:50%;background:radial-gradient(ellipse,rgba(82,55,15,.22),rgba(82,55,15,0) 72%);filter:blur(2px);}
  .testimony-gem{position:relative;width:46px;height:40px;margin-bottom:5px;clip-path:polygon(50% 0,86% 16%,100% 52%,70% 100%,30% 100%,0 52%,14% 16%);background:linear-gradient(145deg,var(--gem-a),var(--gem-b) 48%,var(--gem-c));border:1px solid rgba(118,82,24,.20);box-shadow:inset 0 8px 10px rgba(255,255,255,.62),inset 0 -9px 14px rgba(45,25,6,.15),0 7px 14px rgba(82,55,15,.18);animation:itemBreathe 4.2s ease-in-out infinite;}
  .testimony-gem::before{content:"";position:absolute;left:10%;top:12%;width:42%;height:23%;border-radius:999px;background:rgba(255,255,255,.7);transform:rotate(-12deg);}
  .testimony-gem::after{content:"";position:absolute;inset:0;background:linear-gradient(62deg,transparent 0 31%,rgba(255,255,255,.48) 32% 34%,transparent 35% 62%,rgba(255,255,255,.28) 63% 65%,transparent 66%),linear-gradient(118deg,transparent 0 45%,rgba(80,44,7,.12) 46% 48%,transparent 49%);opacity:.8;}
  .testimony-gem-core{position:absolute;left:50%;top:52%;width:16px;height:15px;transform:translate(-50%,-50%) rotate(45deg);border-radius:4px;background:rgba(255,255,255,.28);border:1px solid rgba(255,255,255,.35);box-shadow:0 0 14px rgba(255,255,255,.38);z-index:2;}
  .testimony-gem-white{background:radial-gradient(circle at 32% 18%,#fff 0,#fffdf2 42%,#dbeafe 78%,#b8c7e6 100%);box-shadow:inset 0 8px 12px rgba(255,255,255,.9),inset 0 -9px 14px rgba(148,163,184,.22),0 0 18px rgba(255,255,255,.82),0 8px 18px rgba(125,140,180,.22);}
  .testimony-gem-locked{filter:grayscale(.72);opacity:.58;background:linear-gradient(145deg,#fffdf2,#e7dcc2,#b6a989);}
  .testimony-gem-name{font-size:12.5px;color:var(--text-dark);font-weight:900;line-height:1.25;}
  .testimony-stone-card.locked .testimony-gem-name{color:var(--text-faint);}
  .stone-reward-overlay{position:fixed;inset:0;z-index:2400;display:flex;align-items:center;justify-content:center;padding:18px;background:rgba(80,50,0,.18);backdrop-filter:blur(8px);animation:popIn .18s ease-out;}
  .stone-reward-modal{width:min(380px,100%);text-align:center;background:linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,248,223,.96));border:1.5px solid rgba(211,166,75,.56);border-radius:24px;padding:24px 20px 20px;box-shadow:0 22px 70px rgba(95,60,8,.24);}
  .stone-reward-modal .testimony-gem-wrap{width:92px;height:76px;margin-bottom:12px;}
  .stone-reward-modal .testimony-gem{width:72px;height:62px;animation:stoneRewardFloat 2.8s ease-in-out infinite;}
  .stone-reward-title{font-size:12px;letter-spacing:1.8px;text-transform:uppercase;color:var(--text-faint);font-weight:900;margin-bottom:6px;}
  .stone-reward-name{font-family:'Cormorant Garamond',Georgia,serif;font-size:27px;font-weight:700;color:var(--gold-deep);line-height:1.1;margin:0 0 8px;}
  .stone-reward-copy{font-size:14px;line-height:1.65;color:var(--text-soft);margin:0 0 16px;}
  .stone-reward-actions{display:flex;gap:8px;}
  .stat-pill{background:rgba(255,250,210,.8);border:1px solid var(--gold-pale);border-radius:8px;padding:4px 8px;text-align:center;}
  .section-divider{display:flex;align-items:center;gap:8px;margin:4px 0 10px;}
  .section-divider-line{flex:1;height:1px;background:rgba(255,226,122,.6);}
  .section-divider-label{font-size:12px;font-weight:800;color:var(--gold-deep);letter-spacing:.8px;text-transform:uppercase;}
  .sam-btn{background:rgba(255,253,240,.55);border:1.5px solid var(--card-border);border-radius:14px;padding:14px 16px;cursor:pointer;text-align:left;font-family:'Nunito',sans-serif;display:flex;align-items:center;gap:12px;min-height:56px;transition:all .2s;width:100%;}
  .sam-btn.logged{background:rgba(255,239,160,.45);border-color:rgba(212,160,23,.5);cursor:default;}
  .accordion-trigger{width:100%;background:transparent;border:none;padding:18px 18px;display:flex;align-items:center;justify-content:space-between;cursor:pointer;font-family:'Nunito',sans-serif;min-height:60px;}
  .accordion-chevron{font-size:18px;color:var(--text-soft);transition:transform .25s;}
  .accordion-chevron.open{transform:rotate(180deg);}
  .sg-input{width:100%;background:rgba(255,254,249,.9);border:1.25px solid rgba(91,126,148,.30);border-radius:12px;padding:13px 14px;color:var(--text-dark);font-size:16px;outline:none;font-family:'Nunito',sans-serif;}
  .sg-input:focus{border-color:var(--gold);box-shadow:0 0 0 3px rgba(244,230,184,.44);}
  .sg-textarea{width:100%;resize:vertical;border-radius:14px;border:1.25px solid rgba(91,126,148,.28);padding:14px;font-size:15px;outline:none;font-family:'Cormorant Garamond',Georgia,serif;font-weight:500;color:var(--text-dark);background:rgba(255,254,249,.88);line-height:1.7;}
  .sg-textarea:focus{border-color:var(--gold);box-shadow:0 0 0 3px rgba(244,230,184,.38);}
  .closet-overlay{position:fixed;inset:0;background:rgba(80,50,0,.38);backdrop-filter:blur(8px);z-index:1000;display:flex;align-items:flex-end;justify-content:center;}
  .closet-sheet{background:linear-gradient(160deg,#FFFCF0,#FFF5CC);border:2px solid var(--gold);border-radius:28px 28px 0 0;padding:0 0 24px;width:100%;max-width:680px;max-height:90vh;overflow-y:auto;box-shadow:0 -8px 36px rgba(138,90,8,.22);}
  .closet-header{padding:14px 18px 12px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid var(--card-border);position:sticky;top:0;background:rgba(255,252,230,.97);backdrop-filter:blur(8px);z-index:2;}
  .home-room{position:relative;overflow:hidden;border-radius:22px;min-height:360px;border:1.5px solid rgba(201,144,18,.24);box-shadow:inset 0 -28px 42px rgba(138,90,8,.08),0 5px 20px rgba(180,130,20,.12);}
  .home-room-complete{box-shadow:inset 0 -28px 42px rgba(138,90,8,.07),0 0 0 1px rgba(255,248,223,.42),0 8px 26px rgba(240,203,118,.18);}
  .home-room-complete .home-rays{opacity:.92;filter:blur(.2px) brightness(1.08);}
  .home-room-transforming .home-cardboard,.home-room-transforming .home-little-shelter,.home-room-transforming .home-apartment,.home-room-transforming .home-house,.home-room-transforming .home-mansion,.home-room-transforming .home-palace,.home-room-transforming .home-chapel{animation:homeBuildRise 5.8s cubic-bezier(.2,.72,.2,1) forwards;transform-origin:50% 100%;}
  .home-room-transforming .home-rays{opacity:.92;filter:brightness(1.08);}
  .home-room-transforming .home-path{box-shadow:inset 0 0 28px rgba(255,255,255,.72),0 0 22px rgba(240,203,118,.24);}
  .home-room-blessed{box-shadow:inset 0 -24px 38px rgba(138,90,8,.06),0 0 0 1px rgba(236,253,245,.48),0 10px 30px rgba(46,125,50,.12);}
  .home-room-blessed .home-sky{filter:saturate(1.04) brightness(1.02);}
  .home-room-blessed .home-palace,.home-room-blessed .home-house,.home-room-blessed .home-chapel{filter:drop-shadow(0 16px 26px rgba(240,203,118,.34)) drop-shadow(0 0 16px rgba(236,253,245,.42));}
  .home-care-aura{position:absolute;inset:0;z-index:1;pointer-events:none;opacity:.42;transition:opacity .35s;background:radial-gradient(circle at 50% 38%,rgba(255,255,255,.34),transparent 44%);}
  .home-care-aura.glowing{background:radial-gradient(circle at 50% 35%,rgba(255,248,223,.56),rgba(240,203,118,.18) 32%,transparent 64%);animation:homeCarePulse 5.2s ease-in-out infinite;}
  .home-care-aura.peaceful{background:radial-gradient(circle at 50% 38%,rgba(219,234,254,.34),rgba(236,253,245,.20) 38%,transparent 68%);}
  .home-care-aura.blooming{background:radial-gradient(circle at 26% 78%,rgba(249,168,212,.25),transparent 34%),radial-gradient(circle at 76% 76%,rgba(134,239,172,.22),transparent 34%),radial-gradient(circle at 50% 38%,rgba(255,248,223,.34),transparent 62%);}
  .home-care-aura.restoring{background:radial-gradient(circle at 50% 32%,rgba(236,253,245,.36),rgba(255,248,223,.18) 36%,transparent 68%);animation:homeCareRestore 5.8s ease-in-out infinite;}
  .home-care-aura.radiant{background:radial-gradient(circle at 50% 34%,rgba(255,255,255,.64),rgba(255,248,223,.42) 28%,rgba(240,203,118,.18) 52%,transparent 72%);animation:homeCarePulse 4.8s ease-in-out infinite;}
  @keyframes homeCarePulse{0%,100%{opacity:.34;transform:scale(.98)}50%{opacity:.62;transform:scale(1.015)}}
  @keyframes homeCareRestore{0%,100%{opacity:.24;filter:saturate(.94)}50%{opacity:.52;filter:saturate(1.04)}}
  .home-care-card{background:linear-gradient(145deg,rgba(255,253,246,.95),rgba(236,253,245,.42))!important;border-color:rgba(125,174,212,.24)!important;box-shadow:0 3px 14px rgba(65,95,125,.045)!important;}
  .home-bless-btn{border:1.5px solid rgba(125,174,212,.28);background:linear-gradient(135deg,rgba(255,253,246,.98),rgba(219,234,254,.62));border-radius:14px;color:#497093;font-family:'Nunito',sans-serif;font-weight:900;font-size:13px;padding:10px 12px;min-height:42px;cursor:pointer;white-space:nowrap;box-shadow:0 2px 10px rgba(65,95,125,.045);}
  .home-care-signal-row{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;margin-top:12px;}
  .home-care-signal{display:flex;align-items:center;justify-content:center;min-height:32px;border-radius:999px;border:1px solid rgba(125,174,212,.18);background:rgba(255,255,255,.54);color:#6f8aa2;font-size:11.5px;font-weight:900;white-space:nowrap;}
  .home-care-signal.active{background:linear-gradient(135deg,rgba(255,253,246,.96),rgba(236,253,245,.72));border-color:rgba(134,190,155,.34);color:#2f6f4a;}
  .home-care-action-row{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:7px;margin-top:10px;}
  .home-care-action{border:1px solid rgba(91,143,184,.28);background:rgba(255,253,246,.76);border-radius:12px;padding:9px 8px;color:#497093;font-family:'Nunito',sans-serif;font-size:12.5px;font-weight:900;line-height:1.2;cursor:pointer;min-height:40px;}
  .home-care-complete{grid-column:1 / -1;display:flex;align-items:center;justify-content:center;min-height:38px;border-radius:12px;background:rgba(236,253,245,.62);border:1px solid rgba(134,190,155,.26);color:#2f6f4a;font-size:12.5px;font-weight:900;}
  .home-midground-glow{position:absolute;left:8%;right:8%;bottom:34%;height:72px;border-radius:50%;z-index:2;pointer-events:none;background:radial-gradient(ellipse,rgba(255,248,223,.46),rgba(255,248,223,0) 70%);}
  .home-stage-0 .home-midground-glow{opacity:.34;}
  .home-stage-1 .home-midground-glow{opacity:.46;}
  .home-stage-3 .home-midground-glow,.home-stage-4 .home-midground-glow{height:82px;opacity:.7;}
  .home-stage-5 .home-midground-glow,.home-stage-6 .home-midground-glow{left:3%;right:3%;height:96px;opacity:.82;}
  .home-foreground-grass{position:absolute;left:-2%;right:-2%;bottom:0;height:58px;z-index:7;pointer-events:none;background:radial-gradient(circle at 9% 86%,rgba(255,255,255,.55) 0 3px,transparent 4px),radial-gradient(circle at 82% 78%,rgba(255,255,255,.5) 0 3px,transparent 4px),linear-gradient(180deg,rgba(255,248,223,0),rgba(134,160,82,.22));opacity:.72;}
  .home-sky{position:absolute;inset:0 0 30%;overflow:hidden;}
  .home-rays{position:absolute;inset:-30% -20% 25%;opacity:.75;background:conic-gradient(from 205deg at 50% 8%,rgba(255,255,255,.9) 0 8deg,transparent 8deg 18deg,rgba(255,236,163,.65) 18deg 26deg,transparent 26deg 42deg,rgba(255,255,255,.75) 42deg 50deg,transparent 50deg 360deg);filter:blur(.3px);}
  .home-cloud{position:absolute;background:rgba(255,255,255,.68);border-radius:999px;filter:blur(.2px);box-shadow:0 0 24px rgba(255,255,255,.38);}
  .home-cloud.c1{width:170px;height:48px;left:-28px;top:70px;}
  .home-cloud.c2{width:210px;height:58px;right:-46px;top:90px;}
  .home-cloud.c3{width:150px;height:42px;left:36%;top:42px;opacity:.45;}
  .home-distant-city{position:absolute;left:2%;right:2%;bottom:35%;height:92px;opacity:.18;background:linear-gradient(to top,rgba(255,240,175,.9),rgba(255,255,255,0)),repeating-linear-gradient(90deg,transparent 0 14px,rgba(255,255,255,.88) 14px 21px,transparent 21px 36px);clip-path:polygon(0 100%,0 65%,4% 65%,5% 25%,7% 65%,10% 65%,11% 44%,12% 65%,17% 65%,18% 20%,20% 65%,25% 65%,27% 35%,29% 65%,36% 65%,38% 12%,41% 65%,49% 65%,50% 25%,52% 65%,60% 65%,62% 18%,65% 65%,72% 65%,74% 35%,76% 65%,84% 65%,85% 22%,88% 65%,95% 65%,96% 40%,98% 65%,100% 65%,100% 100%);}
  .home-stage-0 .home-distant-city,.home-stage-1 .home-distant-city{opacity:.08;}
  .home-stage-2 .home-distant-city{opacity:.18;}
  .home-stage-3 .home-distant-city{opacity:.28;}
  .home-stage-4 .home-distant-city{opacity:.38;}
  .home-stage-5 .home-distant-city{opacity:.58;height:104px;bottom:34%;}
  .home-stage-6 .home-distant-city{opacity:.68;height:112px;bottom:34%;filter:drop-shadow(0 0 16px rgba(255,248,223,.66));}
  .home-background-rainbow{position:absolute;left:50%;top:20px;width:min(118%,620px);height:185px;transform:translateX(-50%);z-index:1;pointer-events:none;opacity:.78;filter:drop-shadow(0 0 18px rgba(255,248,223,.75));animation:rainbowBreathe 6.5s ease-in-out infinite;}
  .home-background-rainbow::before{content:"";position:absolute;inset:0;border-radius:999px 999px 0 0;background:conic-gradient(from 210deg at 50% 100%,transparent 0 14deg,rgba(248,113,113,.72) 14deg 19deg,rgba(251,191,36,.70) 19deg 24deg,rgba(134,239,172,.68) 24deg 29deg,rgba(125,211,252,.72) 29deg 34deg,rgba(196,181,253,.68) 34deg 39deg,rgba(249,168,212,.64) 39deg 44deg,transparent 44deg 360deg);mask:radial-gradient(ellipse at 50% 100%,transparent 0 47%,#000 48% 62%,transparent 63%);-webkit-mask:radial-gradient(ellipse at 50% 100%,transparent 0 47%,#000 48% 62%,transparent 63%);}
  .home-background-rainbow::after{content:"✦";position:absolute;left:18%;top:54%;font-size:18px;color:#fff8df;text-shadow:0 0 12px rgba(240,203,118,.9);box-shadow:130px -28px 0 -5px rgba(255,248,223,.72),260px 5px 0 -6px rgba(255,248,223,.66);animation:itemSparkle 2.4s ease-in-out infinite;}
  .home-floor{position:absolute;left:0;right:0;bottom:0;height:44%;background:linear-gradient(180deg,#fff7d6 0%,#f7d778 100%);border-top:1px solid rgba(138,90,8,.14);}
  .home-path{position:absolute;left:50%;bottom:-6%;transform:translateX(-50%);width:44%;height:54%;background:linear-gradient(180deg,rgba(255,255,255,.78),rgba(240,199,94,.78));clip-path:polygon(43% 0,57% 0,100% 100%,0 100%);border-left:1px solid rgba(138,90,8,.16);border-right:1px solid rgba(138,90,8,.16);box-shadow:inset 0 0 22px rgba(255,255,255,.55);}
  .home-stage-0 .home-path{width:34%;opacity:.72;}
  .home-stage-1 .home-path{width:38%;opacity:.82;}
  .home-stage-5 .home-path,.home-stage-6 .home-path{width:50%;box-shadow:inset 0 0 26px rgba(255,255,255,.68),0 0 18px rgba(240,203,118,.22);}
  .home-path::before{content:"";position:absolute;inset:10% 14% 0;background:radial-gradient(circle at 50% 30%,rgba(255,255,255,.66) 0 2px,transparent 3px),radial-gradient(circle at 44% 63%,rgba(249,168,212,.38) 0 2px,transparent 3px),radial-gradient(circle at 58% 78%,rgba(255,243,196,.48) 0 2px,transparent 3px);opacity:.42;animation:gemSparkle 8s ease-in-out infinite;z-index:1;}
  .home-path::after{content:"";position:absolute;inset:0;background:repeating-linear-gradient(180deg,transparent 0 28px,rgba(138,90,8,.14) 29px 30px),repeating-linear-gradient(90deg,transparent 0 38px,rgba(138,90,8,.09) 39px 40px);opacity:.65;}
  .home-flowerbed{position:absolute;bottom:4%;height:31%;width:31%;border-radius:60% 60% 18px 18px;background:radial-gradient(circle at 30% 70%,#fff 0 4px,transparent 5px),radial-gradient(circle at 50% 55%,#f9a8d4 0 4px,transparent 5px),radial-gradient(circle at 65% 75%,#fff 0 4px,transparent 5px),linear-gradient(180deg,#b7d68e,#6b8f3a);opacity:.92;}
  .home-flowerbed.left{left:-5%;transform:rotate(-7deg);}
  .home-flowerbed.right{right:-5%;transform:rotate(7deg);}
  .daily-bloom-patch{position:absolute;left:11%;right:11%;bottom:6%;height:28%;z-index:4;pointer-events:none;}
  .daily-bloom{position:absolute;width:13px;height:13px;border-radius:50%;background:radial-gradient(circle,#fff 0 18%,#fff8df 20% 36%,#f0c75e 38% 58%,transparent 60%);filter:drop-shadow(0 2px 3px rgba(82,55,15,.10));transform-origin:50% 100%;animation:dailyBloomOpen .72s ease-out both,dailyBloomSway 5.4s ease-in-out var(--bloom-delay,0s) infinite;}
  .daily-bloom::before{content:"";position:absolute;left:50%;top:11px;width:2px;height:18px;border-radius:999px;background:linear-gradient(180deg,#86efac,#4f8c38);transform:translateX(-50%);z-index:-1;}
  .daily-bloom::after{content:"";position:absolute;left:-4px;top:4px;width:21px;height:13px;border-radius:50%;background:radial-gradient(circle at 50% 50%,#fff 0 14%,#f9a8d4 16% 42%,transparent 44%);opacity:.96;}
  .daily-bloom:nth-child(1){left:16%;bottom:12%;}
  .daily-bloom:nth-child(2){left:78%;bottom:15%;animation-delay:.08s,var(--bloom-delay,0s);}
  .daily-bloom:nth-child(3){left:27%;bottom:26%;}
  .daily-bloom:nth-child(4){left:67%;bottom:29%;animation-delay:.14s,var(--bloom-delay,0s);}
  .daily-bloom:nth-child(5){left:8%;bottom:35%;}
  .daily-bloom:nth-child(6){left:87%;bottom:38%;animation-delay:.18s,var(--bloom-delay,0s);}
  .daily-bloom:nth-child(7){left:38%;bottom:9%;}
  .daily-bloom:nth-child(8){left:56%;bottom:11%;animation-delay:.22s,var(--bloom-delay,0s);}
  .daily-bloom:nth-child(9){left:20%;bottom:50%;}
  .daily-bloom:nth-child(10){left:73%;bottom:52%;animation-delay:.28s,var(--bloom-delay,0s);}
  .daily-bloom:nth-child(11){left:44%;bottom:40%;}
  .daily-bloom:nth-child(12){left:52%;bottom:55%;animation-delay:.34s,var(--bloom-delay,0s);}
  .daily-bloom:nth-child(3n)::after{background:radial-gradient(circle at 50% 50%,#fff 0 14%,#bae6fd 16% 42%,transparent 44%);}
  .daily-bloom:nth-child(4n)::after{background:radial-gradient(circle at 50% 50%,#fff 0 14%,#fbcfe8 16% 42%,transparent 44%);}
  @keyframes dailyBloomOpen{0%{opacity:0;transform:translateY(9px) scale(.25)}70%{opacity:1;transform:translateY(-1px) scale(1.08)}100%{opacity:1;transform:translateY(0) scale(1)}}
  @keyframes dailyBloomSway{0%,100%{rotate:-1deg}50%{rotate:2deg}}
  .home-palace{position:absolute;left:50%;top:21%;transform:translateX(-50%);width:228px;height:160px;z-index:5;pointer-events:none;filter:drop-shadow(0 14px 18px rgba(184,147,75,.18));}
  .home-palace-body{position:absolute;left:34px;right:34px;bottom:0;height:94px;border-radius:18px 18px 12px 12px;background:linear-gradient(180deg,#fffdf5 0%,#fff1c9 58%,#e9b949 100%);border:2px solid rgba(201,144,18,.45);box-shadow:inset 0 0 20px rgba(255,255,255,.78),0 0 26px rgba(240,199,94,.52);}
  .home-palace-roof{position:absolute;left:40px;right:40px;top:30px;height:42px;background:linear-gradient(180deg,#fff7d6,#f0c75e 58%,#c99012 100%);clip-path:polygon(0 100%,10% 45%,24% 100%,38% 36%,50% 0,62% 36%,76% 100%,90% 45%,100% 100%);border-bottom:2px solid rgba(138,90,8,.28);filter:drop-shadow(0 0 18px rgba(240,199,94,.55));}
  .home-palace-star{position:absolute;top:2px;left:50%;transform:translateX(-50%);color:#8a5a08;font-size:18px;text-shadow:0 0 12px rgba(255,248,223,.9);}
  .home-palace-balcony{position:absolute;left:64px;right:64px;bottom:50px;height:13px;border-radius:999px;background:linear-gradient(180deg,#fffdf5,#e9b949);border:1px solid rgba(138,90,8,.24);box-shadow:inset 0 3px 4px rgba(255,255,255,.72),0 3px 7px rgba(82,55,15,.08);z-index:6;}
  .home-palace-balcony::after{content:"";position:absolute;left:8px;right:8px;bottom:-10px;height:10px;background:repeating-linear-gradient(90deg,rgba(138,90,8,.32) 0 2px,transparent 2px 10px);}
  .home-palace-column{position:absolute;bottom:5px;width:12px;height:63px;border-radius:999px;background:linear-gradient(90deg,#e0b85a,#fff8df 42%,#f0cb76 70%,#a96f14);border:1px solid rgba(138,90,8,.22);box-shadow:inset 3px 0 4px rgba(255,255,255,.55);z-index:6;}
  .home-palace-column.c1{left:52px;}.home-palace-column.c2{right:52px;}
  .home-palace-steps{position:absolute;left:73px;right:73px;bottom:-12px;height:19px;border-radius:4px 4px 9px 9px;background:repeating-linear-gradient(180deg,#fff8df 0 5px,#e9b949 5px 7px);border:1px solid rgba(138,90,8,.18);box-shadow:0 7px 10px rgba(82,55,15,.12);z-index:4;}
  .home-palace-aura{position:absolute;left:18%;right:18%;bottom:-9px;height:42px;border-radius:50%;background:radial-gradient(ellipse,rgba(255,248,223,.58),rgba(240,203,118,.15) 48%,transparent 72%);z-index:3;pointer-events:none;}
  .home-path-gem{position:absolute;z-index:4;pointer-events:none;width:8px;height:8px;transform:rotate(45deg);opacity:.54;border:1px solid rgba(255,255,255,.55);background:linear-gradient(135deg,#fff 0%,#dff7ff 36%,#7dd3fc 72%,#8b5cf6 100%);box-shadow:0 0 5px rgba(125,211,252,.28),0 3px 5px rgba(82,55,15,.08);animation:pathGemShine 7.5s ease-in-out infinite;}
  .home-path-gem::after{content:"";position:absolute;left:1.5px;top:1.5px;width:3px;height:3px;background:rgba(255,255,255,.66);clip-path:polygon(0 0,100% 0,25% 100%);}
  .home-path-gem.pg1{left:49%;bottom:29%;width:7px;height:7px;animation-delay:.2s;}
  .home-path-gem.pg2{left:54%;bottom:20%;background:linear-gradient(135deg,#fff,#fff3c4 42%,#f0c75e 76%,#c99012);animation-delay:2.2s;}
  .home-path-gem.pg3{left:44%;bottom:13%;background:linear-gradient(135deg,#fff,#fce7f3 42%,#f9a8d4 76%,#e879f9);animation-delay:4s;}
  .home-tower{position:absolute;bottom:0;width:42px;height:118px;border-radius:14px 14px 10px 10px;background:linear-gradient(180deg,#fffdf5,#ffeec2 68%,#e9b949);border:2px solid rgba(201,144,18,.38);}
  .home-tower.left{left:0;}.home-tower.right{right:0;}
  .home-tower::before{content:"";position:absolute;left:50%;top:-34px;transform:translateX(-50%);width:34px;height:36px;background:linear-gradient(180deg,#fff7d6,#e9b949);clip-path:polygon(50% 0,100% 100%,0 100%);border:1.5px solid rgba(138,90,8,.3);}
  .home-door{position:absolute;left:50%;bottom:0;transform:translateX(-50%);width:42px;height:62px;border-radius:22px 22px 6px 6px;background:radial-gradient(circle at 50% 15%,#fff 0,#fff3c4 38%,#c99012 100%);border:2px solid rgba(138,90,8,.35);box-shadow:0 0 26px rgba(255,248,223,.9);}
  .home-window-dot{position:absolute;width:18px;height:28px;border-radius:10px 10px 4px 4px;background:rgba(255,255,255,.72);border:1px solid rgba(201,144,18,.35);top:54px;}
  .home-window-dot.left{left:56px;}.home-window-dot.right{right:56px;}
  .home-window-dot.center{left:101px;top:48px;}
  .home-style-win_chapel .home-palace-roof,.home-style-win_chapel .home-house-roof,.home-apartment.home-style-win_chapel .home-apartment-roof,.home-cardboard.home-style-win_chapel .home-cardboard-roof{clip-path:polygon(50% 0,96% 100%,4% 100%);left:24px;right:24px;top:12px;height:70px;background:linear-gradient(180deg,#fffaf0,#f0cb76 62%,#b98724 100%)!important;}
  .home-palace.home-style-win_chapel .home-palace-body,.home-house.home-style-win_chapel .home-house-body,.home-apartment.home-style-win_chapel .home-apartment-body,.home-cardboard.home-style-win_chapel .home-cardboard-body{border-radius:24px 24px 12px 12px;background:linear-gradient(180deg,#fffdf8 0%,#fff2cf 54%,#d8a647 100%);box-shadow:inset 0 0 22px rgba(255,255,255,.82),0 0 26px rgba(240,203,118,.44);}
  .home-style-win_chapel .home-palace-star{font-size:0;}
  .home-style-win_chapel .home-palace-star::after{content:"✝";font-size:24px;color:#7c5a1e;text-shadow:0 0 14px rgba(255,248,223,.95);}
  .home-apartment.home-style-win_chapel::before,.home-cardboard.home-style-win_chapel::before,.home-house.home-style-win_chapel::before{content:"✝";position:absolute;left:50%;top:-10px;transform:translateX(-50%);z-index:5;color:#7c5a1e;font-size:22px;text-shadow:0 0 14px rgba(255,248,223,.95);}
  .home-style-win_chapel .home-door,.home-style-win_chapel .home-house-door{background:radial-gradient(circle at 50% 18%,#fff,#fff8df 38%,#9a6b16 100%);width:36px;}
  .home-style-win_sunrise .home-palace-body,.home-style-win_sunrise .home-house-body,.home-apartment.home-style-win_sunrise .home-apartment-body,.home-cardboard.home-style-win_sunrise .home-cardboard-body{background:radial-gradient(circle at 50% 8%,#fff 0,#fde68a 28%,#fff1c9 54%,#e9a23a 100%);}
  .home-style-win_sunrise .home-palace-roof,.home-style-win_sunrise .home-house-roof,.home-apartment.home-style-win_sunrise .home-apartment-roof,.home-cardboard.home-style-win_sunrise .home-cardboard-roof{background:linear-gradient(180deg,#fff7d6,#f59e0b 58%,#c26a1b 100%)!important;}
  .home-style-win_palace .home-palace-body,.home-style-win_palace .home-house-body,.home-apartment.home-style-win_palace .home-apartment-body,.home-cardboard.home-style-win_palace .home-cardboard-body{background:linear-gradient(180deg,#fffdf5 0%,#fff1c9 48%,#e9b949 100%);box-shadow:inset 0 0 22px rgba(255,255,255,.82),0 0 32px rgba(240,203,118,.56);}
  .home-chapel{position:absolute;left:50%;top:27%;transform:translateX(-50%);width:218px;height:150px;z-index:4;pointer-events:none;filter:drop-shadow(0 14px 24px rgba(124,90,30,.22));}
  .home-chapel-body{position:absolute;left:32px;right:32px;bottom:0;height:92px;border-radius:30px 30px 12px 12px;background:linear-gradient(180deg,#fffdf8 0%,#fff1cf 48%,#d8a647 100%);border:2px solid rgba(138,90,8,.34);box-shadow:inset 0 8px 14px rgba(255,255,255,.82),0 0 24px rgba(240,203,118,.42);}
  .home-chapel-roof{position:absolute;left:18px;right:18px;top:34px;height:58px;background:linear-gradient(180deg,#fff8df,#d9a948 70%,#9a6b16);clip-path:polygon(50% 0,100% 100%,0 100%);filter:drop-shadow(0 0 16px rgba(240,203,118,.46));}
  .home-chapel-steeple{position:absolute;left:50%;top:0;transform:translateX(-50%);width:58px;height:62px;background:linear-gradient(180deg,#fffdf8,#f0cb76 72%,#b98724);clip-path:polygon(50% 0,100% 100%,0 100%);border:1px solid rgba(138,90,8,.22);}
  .home-chapel-cross{position:absolute;left:50%;top:-14px;transform:translateX(-50%);width:9px;height:32px;border-radius:999px;background:linear-gradient(180deg,#fff8df,#7c5a1e);box-shadow:0 0 16px rgba(255,248,223,.95);}
  .home-chapel-cross::after{content:"";position:absolute;left:-11px;top:10px;width:31px;height:7px;border-radius:999px;background:inherit;}
  .home-chapel-door{position:absolute;left:50%;bottom:0;transform:translateX(-50%);width:38px;height:58px;border-radius:24px 24px 6px 6px;background:radial-gradient(circle at 50% 18%,#fff,#fff8df 40%,#9a6b16 100%);border:2px solid rgba(124,90,30,.3);box-shadow:0 0 20px rgba(255,248,223,.76);}
  .home-chapel-window{position:absolute;top:61px;width:17px;height:27px;border-radius:12px 12px 5px 5px;background:rgba(255,255,255,.72);border:1px solid rgba(138,90,8,.22);box-shadow:inset 0 0 8px rgba(255,248,223,.76);}
  .home-chapel-window.left{left:54px;}.home-chapel-window.right{right:54px;}
  .home-sunrise-house{position:absolute;left:50%;top:30%;transform:translateX(-50%);width:208px;height:132px;z-index:4;pointer-events:none;filter:drop-shadow(0 12px 22px rgba(180,95,18,.2));}
  .home-sunrise-house .home-house-body{left:28px;right:28px;height:88px;background:radial-gradient(circle at 50% 4%,#fff 0,#fde68a 25%,#fff1c9 55%,#e9a23a 100%);}
  .home-sunrise-house .home-house-roof{left:6px;right:6px;height:64px;background:linear-gradient(180deg,#fff7d6,#f59e0b 58%,#c26a1b 100%);}
  .home-sunrise-rays{position:absolute;left:50%;top:-12px;width:100px;height:58px;transform:translateX(-50%);background:conic-gradient(from 210deg at 50% 100%,rgba(255,248,223,.9) 0 12deg,transparent 12deg 24deg,rgba(251,191,36,.58) 24deg 36deg,transparent 36deg 54deg,rgba(255,248,223,.8) 54deg 66deg,transparent 66deg 360deg);opacity:.8;}
  .home-cardboard{position:absolute;left:50%;top:42%;transform:translateX(-50%);width:150px;height:88px;z-index:4;pointer-events:none;filter:drop-shadow(0 10px 16px rgba(100,70,20,.2));}
  .home-cardboard-body{position:absolute;left:16px;right:16px;bottom:0;height:62px;border-radius:10px;background:linear-gradient(180deg,#f4d28d,#c98d43);border:2px solid rgba(138,90,8,.32);box-shadow:inset 0 0 14px rgba(255,248,223,.45);}
  .home-cardboard-roof{position:absolute;left:4px;right:4px;top:0;height:42px;background:linear-gradient(180deg,#f8e4a3,#d6a21a);clip-path:polygon(50% 0,100% 100%,0 100%);}
  .home-cardboard-door{position:absolute;left:58px;bottom:0;width:34px;height:43px;border-radius:16px 16px 4px 4px;background:rgba(255,248,223,.75);border:2px solid rgba(138,90,8,.26);}
  .home-little-shelter{position:absolute;left:50%;top:38%;transform:translateX(-50%);width:166px;height:104px;z-index:4;pointer-events:none;filter:drop-shadow(0 12px 18px rgba(100,70,20,.18));}
  .home-little-shelter-body{position:absolute;left:22px;right:22px;bottom:0;height:72px;border-radius:18px 18px 9px 9px;background:linear-gradient(180deg,#fff8df,#f0cb76 58%,#c99012);border:2px solid rgba(138,90,8,.28);box-shadow:inset 0 0 16px rgba(255,255,255,.62);}
  .home-little-shelter-roof{position:absolute;left:5px;right:5px;top:0;height:58px;background:linear-gradient(180deg,#fffdf5,#e0b85a 62%,#9a6b16);clip-path:polygon(50% 0,100% 100%,0 100%);filter:drop-shadow(0 0 12px rgba(240,203,118,.42));}
  .home-little-shelter-door{position:absolute;left:66px;bottom:0;width:34px;height:48px;border-radius:18px 18px 5px 5px;background:radial-gradient(circle at 50% 18%,#fff,#fff3c4 42%,#b7791f);border:2px solid rgba(138,90,8,.28);box-shadow:0 0 16px rgba(255,248,223,.58);}
  .home-little-shelter-window{position:absolute;top:55px;width:16px;height:21px;border-radius:8px 8px 4px 4px;background:rgba(255,255,255,.7);border:1px solid rgba(138,90,8,.24);}
  .home-little-shelter-window.left{left:42px;}.home-little-shelter-window.right{right:42px;}
  .home-cardboard::after,.home-little-shelter::after,.home-apartment::after,.home-house::after,.home-palace::after{content:"";position:absolute;left:8%;right:8%;bottom:-9px;height:15px;border-radius:50%;background:radial-gradient(ellipse,rgba(82,55,15,.20),rgba(82,55,15,0) 70%);z-index:-1;}
  .home-cardboard:not(.home-style-win_chapel)::before{content:"";position:absolute;left:24px;right:24px;top:33px;height:2px;background:rgba(138,90,8,.24);box-shadow:0 14px 0 rgba(255,248,223,.18);z-index:5;}
  .home-apartment{position:absolute;left:50%;top:31%;transform:translateX(-50%);width:166px;height:130px;z-index:4;pointer-events:none;filter:drop-shadow(0 12px 20px rgba(100,70,20,.18));}
  .home-apartment-body{position:absolute;left:28px;right:28px;bottom:0;height:112px;border-radius:16px 16px 8px 8px;background:linear-gradient(180deg,#fffdf5,#ffeec2 68%,#e9b949);border:2px solid rgba(201,144,18,.36);box-shadow:inset 0 0 16px rgba(255,255,255,.7);}
  .home-apartment-roof{position:absolute;left:22px;right:22px;top:0;height:30px;border-radius:16px 16px 6px 6px;background:linear-gradient(180deg,#fff7d6,#f0c75e);}
  .home-apartment-window{position:absolute;width:18px;height:20px;border-radius:7px 7px 3px 3px;background:rgba(255,255,255,.72);border:1px solid rgba(201,144,18,.32);}
  .home-apartment:not(.home-style-win_chapel)::before{content:"";position:absolute;left:48px;right:48px;bottom:0;height:38px;border-radius:14px 14px 4px 4px;background:radial-gradient(circle at 50% 20%,#fff,#fff3c4 46%,#c99012);border:1px solid rgba(138,90,8,.28);z-index:5;}
  .home-house{position:absolute;left:50%;top:33%;transform:translateX(-50%);width:184px;height:124px;z-index:4;pointer-events:none;filter:drop-shadow(0 12px 22px rgba(100,70,20,.18));}
  .home-house-body{position:absolute;left:30px;right:30px;bottom:0;height:82px;border-radius:18px 18px 9px 9px;background:linear-gradient(180deg,#fffdf5,#fff1c9 62%,#e9b949);border:2px solid rgba(201,144,18,.38);box-shadow:inset 0 0 18px rgba(255,255,255,.72);}
  .home-house-roof{position:absolute;left:12px;right:12px;top:0;height:62px;background:linear-gradient(180deg,#fff7d6,#f0c75e 58%,#c99012);clip-path:polygon(50% 0,100% 100%,0 100%);filter:drop-shadow(0 0 14px rgba(240,199,94,.5));}
  .home-house-door{position:absolute;left:76px;bottom:0;width:34px;height:50px;border-radius:18px 18px 5px 5px;background:radial-gradient(circle at 50% 18%,#fff,#fff3c4 45%,#c99012);border:2px solid rgba(138,90,8,.32);}
  .home-house:not(.home-style-win_chapel)::before{content:"";position:absolute;left:34px;right:34px;bottom:82px;height:8px;border-radius:999px;background:linear-gradient(90deg,transparent,rgba(255,248,223,.9),transparent);z-index:5;}
  .home-mansion{position:absolute;left:50%;top:29%;transform:translateX(-50%);width:216px;height:142px;z-index:4;pointer-events:none;filter:drop-shadow(0 14px 23px rgba(100,70,20,.18));}
  .home-mansion-body{position:absolute;left:24px;right:24px;bottom:0;height:94px;border-radius:16px 16px 10px 10px;background:linear-gradient(180deg,#fffdf8,#fff1c9 58%,#e0b85a);border:2px solid rgba(201,144,18,.38);box-shadow:inset 0 0 20px rgba(255,255,255,.78),0 0 18px rgba(240,203,118,.25);}
  .home-mansion-roof{position:absolute;left:13px;right:13px;top:12px;height:50px;background:linear-gradient(180deg,#fffaf0,#f0cb76 62%,#b7791f);clip-path:polygon(0 100%,10% 56%,22% 100%,34% 48%,50% 0,66% 48%,78% 100%,90% 56%,100% 100%);filter:drop-shadow(0 0 14px rgba(240,203,118,.46));}
  .home-mansion-column{position:absolute;bottom:7px;width:10px;height:65px;border-radius:999px;background:linear-gradient(90deg,#d3a64b,#fff8df 48%,#b7791f);border:1px solid rgba(138,90,8,.20);z-index:6;}
  .home-mansion-column.c1{left:55px;}.home-mansion-column.c2{right:55px;}
  .home-mansion-door{position:absolute;left:50%;bottom:0;transform:translateX(-50%);width:38px;height:56px;border-radius:20px 20px 5px 5px;background:radial-gradient(circle at 50% 18%,#fff,#fff3c4 42%,#b7791f);border:2px solid rgba(138,90,8,.30);box-shadow:0 0 18px rgba(255,248,223,.72);z-index:7;}
  .home-mansion-window{position:absolute;top:62px;width:18px;height:28px;border-radius:10px 10px 4px 4px;background:rgba(255,255,255,.76);border:1px solid rgba(201,144,18,.32);box-shadow:inset 0 0 8px rgba(255,248,223,.76);z-index:7;}
  .home-mansion-window.left{left:40px;}.home-mansion-window.right{right:40px;}
  .home-mansion::after{content:"";position:absolute;left:8%;right:8%;bottom:-9px;height:15px;border-radius:50%;background:radial-gradient(ellipse,rgba(82,55,15,.20),rgba(82,55,15,0) 70%);z-index:-1;}
  .home-realm-country{position:absolute;left:5%;right:5%;bottom:36%;height:56px;z-index:2;pointer-events:none;opacity:.78;background:radial-gradient(circle at 18% 70%,#86efac 0 9px,transparent 10px),radial-gradient(circle at 36% 62%,#f9a8d4 0 7px,transparent 8px),radial-gradient(circle at 70% 68%,#bae6fd 0 8px,transparent 9px),linear-gradient(180deg,rgba(255,248,223,.2),rgba(134,239,172,.45));border-radius:50% 50% 18px 18px;}
  .home-stage-5 .home-realm-country{height:68px;opacity:.88;background:radial-gradient(circle at 12% 72%,#86efac 0 9px,transparent 10px),radial-gradient(circle at 28% 62%,#f9a8d4 0 7px,transparent 8px),radial-gradient(circle at 49% 70%,#fde68a 0 8px,transparent 9px),radial-gradient(circle at 74% 66%,#bae6fd 0 8px,transparent 9px),linear-gradient(180deg,rgba(255,248,223,.24),rgba(134,239,172,.50));}
  .home-stage-6 .home-realm-country{left:2%;right:2%;bottom:34%;height:82px;opacity:.92;background:radial-gradient(circle at 12% 72%,#86efac 0 8px,transparent 9px),radial-gradient(circle at 28% 62%,#f9a8d4 0 7px,transparent 8px),radial-gradient(circle at 45% 64%,#fff3c4 0 9px,transparent 10px),radial-gradient(circle at 64% 68%,#bae6fd 0 8px,transparent 9px),radial-gradient(circle at 82% 58%,#ddd6fe 0 8px,transparent 9px),linear-gradient(180deg,rgba(255,248,223,.32),rgba(134,239,172,.42));filter:drop-shadow(0 0 14px rgba(255,248,223,.6));}
  .home-realm-planet{position:absolute;right:7%;top:15%;width:70px;height:70px;border-radius:50%;z-index:2;pointer-events:none;background:radial-gradient(circle at 32% 24%,#fff,#fff3c4 28%,#bae6fd 54%,#ddd6fe 82%,#f9a8d4 100%);box-shadow:0 0 34px rgba(255,248,223,.75),0 0 42px rgba(125,211,252,.38);opacity:.88;}
  .home-realm-planet::before{content:"";position:absolute;inset:-22px;border-radius:50%;border:1px solid rgba(255,248,223,.52);box-shadow:0 0 28px rgba(255,248,223,.34);}
  .home-realm-planet::after{content:"";position:absolute;left:-16px;right:-16px;top:31px;height:10px;border-radius:50%;border:2px solid rgba(255,248,223,.72);transform:rotate(-16deg);}
  .home-heavenly-veil{position:absolute;inset:0;z-index:2;pointer-events:none;background:radial-gradient(circle at 50% 6%,rgba(255,255,255,.55),transparent 24%),radial-gradient(circle at 18% 32%,rgba(186,230,253,.26),transparent 22%),radial-gradient(circle at 82% 38%,rgba(249,168,212,.22),transparent 20%);opacity:.84;}
  .home-heavenly-veil::after{content:"✦";position:absolute;left:18%;top:22%;font-size:14px;color:rgba(255,248,223,.9);text-shadow:0 0 12px rgba(240,203,118,.8);box-shadow:96px -18px 0 -4px rgba(255,248,223,.62),212px 24px 0 -5px rgba(255,248,223,.55),302px -8px 0 -5px rgba(255,248,223,.6);animation:itemSparkle 3.4s ease-in-out infinite;}
  .home-gate{position:absolute;bottom:20%;width:70px;height:130px;border:3px solid rgba(201,144,18,.75);border-bottom:none;z-index:3;opacity:.95;pointer-events:none;}
  .home-gate.left{left:8px;border-radius:48px 18px 0 0;transform:rotate(-4deg);}
  .home-gate.right{right:8px;border-radius:18px 48px 0 0;transform:rotate(4deg);}
  .home-gate::after{content:"";position:absolute;inset:12px 17px 0;border-left:2px solid rgba(201,144,18,.65);border-right:2px solid rgba(201,144,18,.65);}
  .home-lantern{position:absolute;bottom:18%;width:34px;height:42px;border-radius:12px;background:radial-gradient(circle,#fff7d6,#f0c75e 55%,#8a5a08);box-shadow:0 0 22px rgba(240,199,94,.8);z-index:3;pointer-events:none;}
  .home-lantern.left{left:22px;}.home-lantern.right{right:22px;}
  .home-slot{position:absolute;display:flex;align-items:flex-end;justify-content:center;user-select:none;z-index:5;transform-origin:50% 92%;transition:filter .22s ease;}
  .home-slot:hover{filter:drop-shadow(0 10px 18px rgba(95,60,8,.16));}
  .home-pet-track{justify-content:center;overflow:visible;}
  .home-pet-track.lamb-track{align-items:flex-end;min-height:96px;}
  .home-pet-track.butterfly-track{align-items:center;min-height:128px;}
  .home-pet-track.dove-track{align-items:center;min-height:112px;}
  .home-pet-track.lamb-track .animated-shop-item{min-width:clamp(220px,58vw,340px);animation-name:motionPastureWalk!important;animation-duration:var(--motion-duration,14s)!important;}
  .home-pet-track.butterfly-track .animated-shop-item{min-width:clamp(230px,60vw,360px);animation-name:motionGardenFlutter!important;animation-duration:var(--motion-duration,9s)!important;}
  .home-pet-track.dove-track .animated-shop-item{min-width:clamp(180px,52vw,300px);animation-name:motionDoveCircle!important;animation-duration:var(--motion-duration,10s)!important;}
  .home-pet-track.lamb-track .home-game-item.pet_lamb{width:clamp(92px,22vw,118px);height:clamp(74px,18vw,98px);}
  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{width:clamp(78px,19vw,104px);height:clamp(74px,18vw,98px);}
  .animated-shop-item{position:relative;display:flex;align-items:flex-end;justify-content:center;transform-origin:50% 92%;animation:motionIdle var(--motion-duration,5s) ease-in-out var(--motion-delay,0s) infinite;}
  .animated-shop-item.motion-walk{animation-name:motionWalk;animation-timing-function:ease-in-out;min-width:clamp(104px,27vw,142px);}
  .animated-shop-item.motion-flutter{animation-name:motionFlutter;animation-timing-function:ease-in-out;min-width:clamp(94px,24vw,126px);min-height:clamp(88px,22vw,118px);}
  .animated-shop-item.motion-hop{animation-name:motionHop;animation-timing-function:ease-in-out;}
  .animated-shop-item.motion-glow{animation-name:motionGlow;animation-timing-function:ease-in-out;}
  .animated-shop-item.motion-sway{animation-name:motionSway;animation-timing-function:ease-in-out;}
  .animated-shop-item.motion-walk .home-game-item{animation:itemStep 1.45s ease-in-out var(--motion-delay,0s) infinite;}
  .animated-shop-item.motion-flutter .home-game-item{animation:itemFlutterBody 1.9s ease-in-out var(--motion-delay,0s) infinite;}
  .animated-shop-item.motion-hop .home-game-item{animation:itemPerchHop 2.7s ease-in-out var(--motion-delay,0s) infinite;}
  .animated-shop-item.motion-sway .home-game-item{animation:itemSway 4.8s ease-in-out var(--motion-delay,0s) infinite;}
  .animated-shop-item.motion-glow .home-game-glow{animation:itemGlow 2.8s ease-in-out var(--motion-delay,0s) infinite;}
  .animated-shop-item.motion-flutter .home-game-detail.wing-left{animation:wingFlutterLeft .42s ease-in-out infinite;}
  .animated-shop-item.motion-flutter .home-game-detail.wing-right{animation:wingFlutterRight .42s ease-in-out infinite;}
  .animated-shop-item.motion-walk .home-game-shadow,.animated-shop-item.motion-hop .home-game-shadow{opacity:.95;}
  .animated-shop-item.motion-flutter .home-game-shadow{opacity:.42;transform:scaleX(.74);}
  .animated-shop-item.motion-walk .home-game-symbol{animation:lambStepBounce 1.1s ease-in-out infinite;}
  .home-game-item{--accent:#e9b949;--accent-soft:rgba(240,203,118,.38);position:relative;width:clamp(54px,15vw,82px);height:clamp(56px,15vw,84px);display:flex;align-items:flex-end;justify-content:center;transform-origin:50% 92%;animation:itemBreathe 4.8s ease-in-out infinite;transition:transform .2s ease,filter .2s ease;}
  .home-game-item:hover{transform:translateY(-3px) scale(1.06);filter:drop-shadow(0 0 16px var(--accent-soft));}
  .home-game-item:active{animation:itemPop .24s ease-out;}
  .home-game-shadow{position:absolute;left:12%;right:12%;bottom:-2px;height:15px;border-radius:50%;background:radial-gradient(ellipse,rgba(82,55,15,.23),rgba(82,55,15,0) 72%);filter:blur(2px);transform:scaleX(1.08);z-index:0;}
  .home-game-glow{position:absolute;left:12%;right:12%;top:18%;bottom:10%;border-radius:999px;background:radial-gradient(circle,rgba(255,255,255,.44),var(--accent-soft) 42%,transparent 72%);opacity:.46;filter:blur(1.6px);z-index:0;animation:itemGlow 3.8s ease-in-out infinite;}
  .home-game-body{position:absolute;left:12%;right:12%;bottom:8px;height:68%;border-radius:24px 24px 18px 18px;background:linear-gradient(155deg,rgba(255,255,255,.96),rgba(255,248,223,.86) 42%,var(--accent-soft));border:1.5px solid rgba(184,147,75,.32);box-shadow:inset 0 8px 12px rgba(255,255,255,.72),inset 0 -12px 14px rgba(95,60,8,.08),0 9px 18px rgba(82,55,15,.18);z-index:1;overflow:hidden;}
  .home-game-body::before{content:"";position:absolute;left:13%;top:10%;width:46%;height:24%;border-radius:999px;background:rgba(255,255,255,.64);filter:blur(.2px);}
  .home-game-body::after{content:"";position:absolute;right:10%;bottom:12%;width:34%;height:30%;border-radius:50%;background:rgba(255,255,255,.18);}
  .home-game-icon{position:relative;z-index:3;font-size:clamp(27px,7vw,38px);line-height:1;margin-bottom:18px;filter:drop-shadow(0 3px 3px rgba(70,45,8,.18));transform-origin:50% 85%;}
  .home-game-detail{position:absolute;z-index:2;pointer-events:none;}
  .home-game-sparkles{position:absolute;inset:-4px;z-index:4;pointer-events:none;opacity:0;transition:opacity .18s ease;}
  .home-game-item:hover .home-game-sparkles{opacity:.45;}
  .home-game-sparkles span{position:absolute;color:#fff7c7;text-shadow:0 0 8px var(--accent);font-size:12px;animation:itemSparkle 1.35s ease-in-out infinite;}
  .home-game-sparkles span:nth-child(1){left:8%;top:18%;animation-delay:0s;}
  .home-game-sparkles span:nth-child(2){right:4%;top:34%;animation-delay:.22s;}
  .home-game-sparkles span:nth-child(3){left:20%;bottom:25%;animation-delay:.44s;}
  .home-game-item.garden{width:clamp(72px,19vw,102px);height:clamp(58px,15vw,76px);animation:itemSway 5.4s ease-in-out infinite;}
  .home-game-item.garden .home-game-body{left:4%;right:4%;height:48%;border-radius:46% 46% 19px 19px;background:linear-gradient(180deg,#fff8df 0%,#b8dc96 50%,#6f9b4a 100%);}
  .home-game-item.garden .home-game-detail.flower-a{left:14%;top:23%;width:17px;height:17px;border-radius:50%;background:radial-gradient(circle,#fff 0 22%,#f9a8d4 24% 58%,transparent 60%);box-shadow:20px -7px 0 -2px #fbcfe8,43px 2px 0 -3px #fff;}
  .home-game-item.garden .home-game-detail.leaf-a{left:8%;right:10%;bottom:18px;height:19px;border-radius:50%;background:radial-gradient(circle at 22% 70%,rgba(255,255,255,.65) 0 4px,transparent 5px),linear-gradient(180deg,#9fd27b,#5f8d39);}
  .home-game-item.furniture{width:clamp(78px,19vw,108px);height:clamp(74px,18vw,102px);animation:itemBreathe 4.6s ease-in-out infinite;}
  .home-game-item.furniture .home-game-glow{opacity:.32;filter:blur(1px);}
  .home-game-item.furniture .home-game-body{border-radius:22px 22px 14px 14px;background:linear-gradient(160deg,rgba(255,255,255,.98),rgba(255,248,223,.9) 46%,var(--accent-soft));border-color:rgba(138,90,8,.28);box-shadow:inset 0 7px 8px rgba(255,255,255,.7),inset 0 -10px 10px rgba(95,60,8,.08),0 8px 13px rgba(82,55,15,.16);}
  .home-game-item.furniture .home-game-detail.water{left:24%;right:24%;top:17%;height:23%;border-radius:50%;background:radial-gradient(circle,#fff,rgba(125,211,252,.76));box-shadow:0 9px 0 -3px rgba(56,189,248,.42);animation:itemBounce 2.8s ease-in-out infinite;}
  .home-game-item.furniture .home-game-detail.base{left:24%;right:24%;bottom:9px;height:14px;border-radius:8px;background:linear-gradient(180deg,rgba(255,255,255,.7),rgba(184,147,75,.28));}
  .home-game-item.decor{animation:itemSway 4.2s ease-in-out infinite;}
  .home-game-item.decor .home-game-body{border-radius:18px;background:linear-gradient(155deg,rgba(255,255,255,.96),rgba(255,248,223,.88),var(--accent-soft));}
  .home-game-item.decor .home-game-detail.base{left:26%;right:26%;bottom:12px;height:10px;border-radius:999px;background:linear-gradient(180deg,rgba(255,255,255,.78),rgba(184,147,75,.30));box-shadow:0 0 10px var(--accent-soft);}
  .home-game-item.decor .home-game-detail.cross{left:45%;top:18%;width:8px;height:38px;border-radius:999px;background:linear-gradient(180deg,#fff7d6,var(--accent));box-shadow:0 0 12px var(--accent-soft);}
  .home-game-item.decor .home-game-detail.cross::after{content:"";position:absolute;left:-12px;top:10px;width:32px;height:7px;border-radius:999px;background:inherit;}
  .home-game-item.dec_rainbow .home-game-body{background:radial-gradient(circle at 50% 18%,rgba(255,255,255,.96),rgba(252,231,243,.78) 42%,rgba(186,230,253,.56));}
  .home-game-item.dec_lanterns .home-game-glow{opacity:.58;filter:blur(1.8px);}
  .home-game-item.furn_fountain .home-game-body{background:radial-gradient(circle at 50% 18%,#fff,rgba(219,242,255,.95) 40%,rgba(56,189,248,.34));}
  .home-game-item.furn_waterfall .home-game-body{background:linear-gradient(145deg,rgba(240,249,255,.96),rgba(147,197,253,.48),rgba(125,211,252,.28));}
  .home-game-item.furn_lily_pond .home-game-body{background:radial-gradient(ellipse at 50% 68%,rgba(255,255,255,.94),rgba(186,230,253,.72) 38%,rgba(45,145,170,.38) 78%);}
  .home-game-item.pet{width:clamp(58px,15vw,78px);height:clamp(58px,15vw,78px);animation:itemBounce 3.6s ease-in-out infinite;}
  .home-game-item.pet .home-game-body{left:14%;right:14%;height:64%;border-radius:50%;background:radial-gradient(circle at 30% 22%,#fff,rgba(224,242,254,.92) 56%,var(--accent-soft));}
  .home-game-item.pet .home-game-detail.wing-left,.home-game-item.pet .home-game-detail.wing-right{top:26%;width:24px;height:17px;border-radius:70% 18%;background:rgba(255,255,255,.74);border:1px solid rgba(184,147,75,.18);}
  .home-game-item.pet .home-game-detail.wing-left{left:3%;transform:rotate(-22deg);}
  .home-game-item.pet .home-game-detail.wing-right{right:3%;transform:rotate(22deg);}
  .home-game-item.pet .home-game-icon{animation:itemSway 3.1s ease-in-out infinite;}
  .home-game-item.pet_lamb{width:clamp(76px,18vw,98px);height:clamp(62px,16vw,84px);}
  .home-game-item.pet_lamb .home-game-glow{opacity:.22;filter:blur(1px);}
  .home-game-item.pet_lamb .home-game-body{left:8%;right:18%;bottom:15px;height:45%;border-radius:52% 48% 42% 44%;background:radial-gradient(circle at 18% 28%,#fff 0 9%,transparent 10%),radial-gradient(circle at 36% 18%,#fff 0 10%,transparent 11%),radial-gradient(circle at 56% 26%,#fff 0 9%,transparent 10%),radial-gradient(circle at 76% 34%,#fff 0 8%,transparent 9%),linear-gradient(180deg,#fff,#f5f1ea 58%,#cfc3b3);border:1px solid rgba(145,111,68,.18);box-shadow:inset 0 5px 8px rgba(255,255,255,.86),0 8px 11px rgba(82,55,15,.14);}
  .home-game-item.pet_lamb .home-game-body::before{content:"";position:absolute;left:9%;right:5%;top:-8px;height:24px;border-radius:50%;background:radial-gradient(circle at 11% 70%,#fff 0 7px,transparent 8px),radial-gradient(circle at 31% 45%,#fff 0 8px,transparent 9px),radial-gradient(circle at 54% 68%,#fff 0 7px,transparent 8px),radial-gradient(circle at 78% 48%,#fff 0 7px,transparent 8px);}
  .home-game-item.pet_lamb .home-game-body::after{content:"";position:absolute;left:-7px;top:22px;width:13px;height:12px;border-radius:50%;background:#fbfaf7;box-shadow:0 0 0 1px rgba(184,147,75,.12),50px -5px 0 -4px rgba(255,255,255,.9);}
  .home-game-item.pet_lamb .home-game-detail.wing-left{left:auto;right:7%;top:35%;width:22px;height:20px;border-radius:48% 52% 46% 42%;background:linear-gradient(180deg,#fff,#eadfce);transform:none;box-shadow:inset 0 4px 5px rgba(255,255,255,.65),0 0 0 1px rgba(184,147,75,.16);}
  .home-game-item.pet_lamb .home-game-detail.wing-left::after{content:"";position:absolute;right:5px;top:7px;width:3px;height:3px;border-radius:50%;background:#3d2800;box-shadow:-1px -1px 0 rgba(255,255,255,.75);}
  .home-game-item.pet_lamb .home-game-detail.wing-left::before{content:"";position:absolute;right:2px;top:-7px;width:8px;height:12px;border-radius:70% 40%;background:#d8c6b2;transform:rotate(24deg);box-shadow:-12px 2px 0 -1px #d8c6b2;}
  .home-game-item.pet_lamb .home-game-detail.wing-right{display:block;left:auto;right:22%;top:47%;width:9px;height:7px;border-radius:50%;background:#d7c5b1;border:none;transform:rotate(-12deg);}
  .home-game-item.pet_lamb .home-game-icon{display:block;position:relative;width:42px;height:10px;margin-bottom:8px;font-size:0;}
  .home-game-item.pet_lamb .home-game-icon::before,.home-game-item.pet_lamb .home-game-icon::after{content:"";position:absolute;bottom:0;width:5px;height:13px;border-radius:999px;background:#b9aa95;box-shadow:10px 0 0 #cbbdac;}
  .home-game-item.pet_lamb .home-game-icon::before{left:7px;}
  .home-game-item.pet_lamb .home-game-icon::after{right:7px;}
  .home-game-item.pet_dove{width:clamp(74px,18vw,96px);height:clamp(66px,17vw,88px);}
  .home-game-item.pet_dove .home-game-glow{opacity:.26;filter:blur(1px);}
  .home-game-item.pet_dove .home-game-body{left:25%;right:18%;bottom:18px;height:37%;border-radius:58% 70% 58% 52%;background:linear-gradient(145deg,#fff,#edf8ff 58%,#c8e7fa);border:1px solid rgba(105,140,165,.16);box-shadow:inset 0 6px 9px rgba(255,255,255,.88),0 8px 12px rgba(82,55,15,.12);}
  .home-game-item.pet_dove .home-game-body::before{content:"";position:absolute;right:4px;top:8px;width:4px;height:4px;border-radius:50%;background:#3d2800;box-shadow:-2px -2px 0 rgba(255,255,255,.75);}
  .home-game-item.pet_dove .home-game-body::after{content:"";position:absolute;right:-10px;top:9px;width:14px;height:10px;clip-path:polygon(0 0,100% 50%,0 100%);background:linear-gradient(90deg,#fef3c7,#d99a12);}
  .home-game-item.pet_dove .home-game-detail.wing-left,.home-game-item.pet_dove .home-game-detail.wing-right{top:18%;width:34px;height:24px;border-radius:80% 18%;background:linear-gradient(145deg,#fff,rgba(219,234,254,.94));border:1px solid rgba(105,140,165,.14);box-shadow:inset 3px 3px 4px rgba(255,255,255,.72);}
  .home-game-item.pet_dove .home-game-detail.wing-left{left:4%;transform:rotate(-20deg);transform-origin:90% 80%;}
  .home-game-item.pet_dove .home-game-detail.wing-right{right:9%;transform:rotate(17deg);transform-origin:10% 80%;}
  .home-game-item.pet_dove .home-game-icon{display:block;position:absolute;left:14%;bottom:23px;width:20px;height:14px;margin:0;font-size:0;clip-path:polygon(100% 0,0 48%,100% 100%,78% 50%);background:linear-gradient(90deg,#dbeafe,#fff);z-index:1;}
  .home-game-item.wallpaper .home-game-body{border-radius:18px;background:var(--wallpaper-bg,linear-gradient(180deg,#e8f6ff,#fff8df));}
  .home-game-item.wallpaper .home-game-detail.cloud-a{left:15%;top:24%;width:34px;height:12px;border-radius:999px;background:rgba(255,255,255,.78);box-shadow:17px 6px 0 -4px rgba(255,255,255,.72);}
  .home-game-item.floor .home-game-body{height:42%;border-radius:42% 42% 16px 16px;background:linear-gradient(180deg,rgba(255,255,255,.76),var(--accent));}
  .home-game-item.floor .home-game-detail.base{left:16%;right:16%;bottom:17px;height:12px;border-radius:50%;background:repeating-linear-gradient(90deg,rgba(138,90,8,.12) 0 5px,transparent 5px 10px);}
  .home-game-item.window .home-game-body{left:11%;right:11%;height:64%;border-radius:28px 28px 14px 14px;background:linear-gradient(180deg,#fffdf5,#fff1c9 55%,var(--accent-soft));}
  .home-game-item.window .home-game-detail.cross{left:46%;top:21%;width:8px;height:32px;border-radius:999px;background:linear-gradient(180deg,#fff7d6,var(--accent));box-shadow:0 0 12px var(--accent-soft);}
  .home-game-item.window .home-game-detail.cross::after{content:"";position:absolute;left:-11px;top:9px;width:30px;height:7px;border-radius:999px;background:inherit;}
  .home-game-item.wp_sunburst .home-game-body::after{content:"";position:absolute;left:31%;top:15%;width:24px;height:24px;border-radius:50%;background:radial-gradient(circle,#fff 0 22%,#fde68a 24% 58%,#f59e0b 100%);box-shadow:0 0 16px rgba(245,158,11,.55);}
  .home-game-item.wp_roses .home-game-body::after{content:"";position:absolute;left:17%;right:15%;bottom:12%;height:18px;background:radial-gradient(circle at 18% 60%,#fb7185 0 5px,transparent 6px),radial-gradient(circle at 48% 36%,#f9a8d4 0 5px,transparent 6px),radial-gradient(circle at 76% 62%,#fff 0 5px,transparent 6px);}
  .home-game-item.fl_gold_path .home-game-body::after,.home-game-item.fl_marble_steps .home-game-body::after,.home-game-item.fl_cloud_walk .home-game-body::after,.home-game-item.fl_pasture .home-game-body::after{content:"";position:absolute;left:12%;right:12%;bottom:20%;height:15px;border-radius:50%;background:repeating-linear-gradient(90deg,rgba(255,255,255,.42) 0 8px,rgba(138,90,8,.09) 8px 10px);}
  .home-game-item.win_palace .home-game-body::before,.home-game-item.win_chapel .home-game-body::before,.home-game-item.win_sunrise .home-game-body::before{content:"";position:absolute;left:34%;top:-18%;width:32%;height:32%;border-radius:999px 999px 12px 12px;background:linear-gradient(180deg,#fff8df,var(--accent));box-shadow:-21px 19px 0 -9px rgba(255,248,223,.88),21px 19px 0 -9px rgba(255,248,223,.88);}
  .home-game-item.win_palace .home-game-body::after{content:"";position:absolute;left:17%;right:17%;bottom:10%;height:38%;background:radial-gradient(circle at 20% 35%,rgba(255,255,255,.8) 0 5px,transparent 6px),radial-gradient(circle at 50% 20%,rgba(255,255,255,.85) 0 6px,transparent 7px),radial-gradient(circle at 80% 35%,rgba(255,255,255,.8) 0 5px,transparent 6px);border-radius:12px;}
  .home-game-item.win_chapel .home-game-body::after{content:"";position:absolute;left:38%;bottom:10%;width:24%;height:34%;border-radius:999px 999px 4px 4px;background:radial-gradient(circle at 50% 20%,#fff,#fff3c4 45%,#c99012);}
  .home-game-item.win_chapel .home-game-detail.cross{height:28px;top:14%;}
  .home-game-item.win_sunrise .home-game-body{background:radial-gradient(circle at 50% 12%,#fff,#fde68a 26%,rgba(255,241,201,.96) 45%,var(--accent-soft));}
  .home-game-item.win_sunrise .home-game-body::after{content:"";position:absolute;left:22%;right:22%;bottom:12%;height:28%;border-radius:999px 999px 5px 5px;background:linear-gradient(180deg,#fff7d6,#d97706);box-shadow:0 0 13px rgba(245,158,11,.45);}
  .home-game-item.furn_fountain .home-game-body{left:14%;right:14%;bottom:10px;height:50%;border-radius:14px 14px 22px 22px;background:linear-gradient(180deg,#f7fbff 0%,#d8f3ff 35%,#8ecae6 68%,#6aa6c8 100%);border:1.5px solid rgba(37,99,135,.24);}
  .home-game-item.furn_fountain .home-game-body::before{content:"";position:absolute;left:12%;right:12%;top:6px;height:11px;border-radius:50%;background:radial-gradient(ellipse,#fff,rgba(125,211,252,.78));box-shadow:0 18px 0 3px rgba(255,255,255,.22),0 20px 0 4px rgba(37,99,135,.14);}
  .home-game-item.furn_fountain .home-game-body::after{content:"";position:absolute;left:28%;right:28%;bottom:-9px;height:12px;border-radius:6px;background:linear-gradient(180deg,#d8f3ff,#6aa6c8);}
  .home-game-item.furn_fountain .home-game-detail.water{left:22%;right:22%;top:2%;height:32%;background:radial-gradient(circle,#fff 0 18%,rgba(125,211,252,.92) 20% 52%,rgba(14,165,233,.4));box-shadow:-13px 19px 0 -7px rgba(125,211,252,.62),13px 19px 0 -7px rgba(125,211,252,.62),0 27px 0 -9px rgba(56,189,248,.45);animation:fountainBubble 1.8s ease-in-out infinite;}
  .home-game-item.furn_fountain .home-game-detail.water::before{content:"";position:absolute;left:47%;top:-15px;width:5px;height:26px;border-radius:999px;background:linear-gradient(180deg,rgba(255,255,255,.98),rgba(125,211,252,.62));box-shadow:-12px 8px 0 -1px rgba(125,211,252,.46),12px 8px 0 -1px rgba(125,211,252,.46);}
  .home-game-item.dec_gates .home-game-body{left:17%;right:17%;height:73%;border-radius:999px 999px 6px 6px;background:linear-gradient(90deg,rgba(255,248,223,.9) 0 10%,transparent 10% 20%,rgba(240,203,118,.75) 20% 30%,transparent 30% 45%,rgba(255,248,223,.9) 45% 55%,transparent 55% 70%,rgba(240,203,118,.75) 70% 80%,transparent 80% 90%,rgba(255,248,223,.9) 90%);border:3px solid rgba(201,144,18,.72);border-bottom:none;}
  .home-game-item.dec_lanterns .home-game-body{left:34%;right:34%;height:52%;border-radius:13px;background:radial-gradient(circle at 50% 35%,#fff,#fde68a 45%,#c99012 100%);box-shadow:0 0 22px rgba(240,203,118,.85),inset 0 5px 7px rgba(255,255,255,.55);}
  .home-game-item.dec_lanterns .home-game-body::before{content:"";position:absolute;left:25%;right:25%;top:-10px;height:10px;border-radius:999px;background:#8a5a08;}
  .home-game-item.dec_rainbow .home-game-body{left:7%;right:7%;height:54%;border-radius:999px 999px 16px 16px;background:radial-gradient(ellipse at 50% 100%,transparent 0 35%,#fda4af 36% 44%,#fde047 45% 53%,#86efac 54% 62%,#7dd3fc 63% 71%,#c084fc 72% 80%,transparent 81%);}
  .home-game-item.garden_roses .home-game-detail.flower-a{box-shadow:18px -7px 0 -2px #fb7185,35px 2px 0 -3px #fda4af,51px -5px 0 -4px #fff;}
  .home-game-item.garden_lilies .home-game-detail.flower-a{background:radial-gradient(circle,#fff 0 30%,#f9a8d4 32% 54%,transparent 56%);box-shadow:20px -7px 0 -2px #fff,43px 2px 0 -3px #fce7f3;}
  .home-game-item.garden_vines .home-game-body{height:68%;border-radius:999px 999px 18px 18px;background:linear-gradient(90deg,transparent 0 26%,#5f8d39 27% 34%,transparent 35% 65%,#5f8d39 66% 73%,transparent 74%);}
  .home-game-item.garden_vines .home-game-detail.flower-a{top:18%;box-shadow:17px 10px 0 -2px #fb7185,36px -2px 0 -3px #f9a8d4,52px 12px 0 -4px #fff;}
  .home-game-item.garden .home-game-shadow{left:4%;right:4%;bottom:0;height:13px;background:radial-gradient(ellipse,rgba(65,93,45,.28),rgba(65,93,45,0) 72%);}
  .home-game-item.garden .home-game-glow{opacity:.16;filter:blur(.7px);}
  .home-game-item.garden .home-game-body{bottom:6px;border:1px solid rgba(66,112,53,.22);box-shadow:inset 0 6px 7px rgba(255,255,255,.34),inset 0 -10px 10px rgba(45,82,36,.18),0 7px 10px rgba(82,55,15,.12);}
  .home-game-item.garden .home-game-body::before{left:8%;right:8%;top:10%;width:auto;height:58%;border-radius:50%;background:radial-gradient(circle at 15% 82%,rgba(255,255,255,.62) 0 3px,transparent 4px),radial-gradient(circle at 42% 62%,rgba(255,255,255,.55) 0 4px,transparent 5px),radial-gradient(circle at 72% 78%,rgba(255,255,255,.50) 0 3px,transparent 4px),linear-gradient(180deg,rgba(154,205,119,.45),rgba(78,134,63,.42));filter:none;}
  .home-game-item.garden .home-game-detail.leaf-a{bottom:20px;height:24px;background:radial-gradient(circle at 17% 80%,rgba(255,255,255,.72) 0 4px,transparent 5px),radial-gradient(circle at 52% 54%,rgba(255,255,255,.55) 0 4px,transparent 5px),linear-gradient(180deg,#a8d979,#5b8f3c);animation:leafRipple 4.2s ease-in-out infinite;}
  .home-game-item.garden .home-game-detail.flower-a{animation:flowerNod 3.8s ease-in-out infinite;transform-origin:50% 100%;}
  .home-game-item.garden_roses .home-game-detail.flower-a{background:radial-gradient(circle at 50% 50%,#fff 0 16%,#fb7185 17% 56%,transparent 58%);box-shadow:17px -7px 0 -2px #fb7185,35px 2px 0 -3px #fda4af,51px -5px 0 -4px #fff,7px 17px 0 -5px #f9a8d4;}
  .home-game-item.garden_lilies .home-game-detail.flower-a{background:radial-gradient(ellipse at 50% 50%,#fff 0 35%,#f9a8d4 36% 54%,transparent 56%);box-shadow:19px -8px 0 -2px #fff,42px 2px 0 -3px #fce7f3,7px 16px 0 -5px #fff;}
  .home-game-item.garden_vines .home-game-body{left:12%;right:12%;bottom:5px;height:78%;border-radius:999px 999px 18px 18px;background:linear-gradient(90deg,transparent 0 22%,#477a33 23% 30%,transparent 31% 68%,#477a33 69% 76%,transparent 77%),radial-gradient(circle at 28% 42%,#83bd62 0 5px,transparent 6px),radial-gradient(circle at 69% 55%,#83bd62 0 5px,transparent 6px);}
  .home-game-item.garden_vines .home-game-body::after{content:"";position:absolute;left:14%;right:14%;top:12%;height:70%;border:3px solid #5f8d39;border-bottom:none;border-radius:999px 999px 0 0;background:transparent;}
  .home-game-item.garden_vines .home-game-detail.flower-a{top:17%;background:radial-gradient(circle at 50% 50%,#fff 0 17%,#fb7185 18% 55%,transparent 57%);box-shadow:17px 10px 0 -2px #fb7185,36px -2px 0 -3px #f9a8d4,52px 12px 0 -4px #fff,4px 30px 0 -5px #fda4af;}
  .home-game-item.furniture .home-game-shadow{left:9%;right:9%;bottom:0;height:16px;background:radial-gradient(ellipse,rgba(82,55,15,.25),rgba(82,55,15,0) 74%);}
  .home-game-item.furn_fountain{animation:fountainSettle 4.4s ease-in-out infinite;}
  .home-game-item.furn_fountain .home-game-glow{opacity:.22;filter:blur(.8px);}
  .home-game-item.furn_fountain .home-game-body{left:12%;right:12%;bottom:8px;height:52%;border-radius:14px 14px 24px 24px;background:linear-gradient(180deg,#fafdff 0%,#dff5ff 28%,#91cee9 64%,#5f9fbd 100%);box-shadow:inset 0 6px 8px rgba(255,255,255,.8),inset 0 -10px 12px rgba(24,90,125,.18),0 9px 12px rgba(82,55,15,.13);}
  .home-game-item.furn_fountain .home-game-detail.water{top:-7%;left:18%;right:18%;height:42%;animation:fountainBubble 1.35s ease-in-out infinite;box-shadow:-14px 25px 0 -7px rgba(125,211,252,.62),14px 25px 0 -7px rgba(125,211,252,.62),0 33px 0 -8px rgba(56,189,248,.46);}
  .home-game-item.furn_fountain .home-game-detail.water::before{content:"";position:absolute;left:47%;top:-18px;width:6px;height:36px;border-radius:999px;background:linear-gradient(180deg,rgba(255,255,255,.96),rgba(125,211,252,.68) 58%,rgba(14,165,233,.28));box-shadow:-13px 11px 0 -1px rgba(125,211,252,.52),13px 11px 0 -1px rgba(125,211,252,.52);animation:fountainStream 1.15s ease-in-out infinite;}
  .home-game-item.furn_fountain .home-game-detail.water::after{content:"";position:absolute;left:50%;top:-25px;width:8px;height:8px;border-radius:50%;background:rgba(255,255,255,.9);box-shadow:-16px 17px 0 -2px rgba(125,211,252,.76),15px 14px 0 -3px rgba(125,211,252,.78),-7px 39px 0 -3px rgba(255,255,255,.82),8px 43px 0 -4px rgba(125,211,252,.72);animation:waterDroplets 1.35s ease-in-out infinite;}
  .home-game-item.furn_waterfall{animation:waterfallSettle 5.6s ease-in-out infinite;}
  .home-game-item.furn_waterfall .home-game-glow{opacity:.2;filter:blur(.8px);}
  .home-game-item.furn_waterfall .home-game-body{left:9%;right:9%;bottom:7px;height:70%;border-radius:24px 24px 22px 22px;background:linear-gradient(145deg,#f8fbff 0%,#dbeafe 25%,#93c5fd 56%,#7dd3fc 100%);box-shadow:inset 7px 8px 9px rgba(255,255,255,.74),inset -7px -10px 12px rgba(37,99,135,.14),0 9px 12px rgba(82,55,15,.12);overflow:hidden;}
  .home-game-item.furn_waterfall .home-game-body::before{content:"";position:absolute;left:7%;right:7%;top:8%;height:28%;border-radius:50%;background:radial-gradient(ellipse,#fff,rgba(186,230,253,.78));opacity:.76;}
  .home-game-item.furn_waterfall .home-game-body::after{content:"";position:absolute;left:31%;right:31%;top:15%;bottom:8%;border-radius:999px;background:linear-gradient(180deg,rgba(255,255,255,.96),rgba(125,211,252,.82),rgba(14,165,233,.32));box-shadow:-13px 13px 0 -4px rgba(255,255,255,.54),13px 16px 0 -5px rgba(255,255,255,.46);animation:waterfallFlow 1.75s ease-in-out infinite;}
  .home-game-item.furn_waterfall .home-game-detail.water{left:18%;right:18%;bottom:3px;top:auto;height:23px;border-radius:50%;background:radial-gradient(ellipse,rgba(255,255,255,.88),rgba(125,211,252,.55) 56%,rgba(14,165,233,.2));animation:pondRipple 2.6s ease-in-out infinite;}
  .home-game-item.furn_waterfall .home-game-detail.water::before{content:"";position:absolute;left:18%;right:18%;top:5px;height:6px;border-radius:50%;border-top:1px solid rgba(255,255,255,.72);opacity:.8;animation:waterfallMist 2.8s ease-in-out infinite;}
  .home-game-item.furn_waterfall .home-game-detail.base{left:10%;right:10%;bottom:0;height:12px;border-radius:50%;background:radial-gradient(ellipse,rgba(82,55,15,.16),transparent 70%);}
  .home-game-item.furn_lily_pond{animation:pondBreathe 5.8s ease-in-out infinite;}
  .home-game-item.furn_lily_pond .home-game-glow{opacity:.16;filter:blur(.8px);}
  .home-game-item.furn_lily_pond .home-game-body{left:6%;right:6%;bottom:8px;height:46%;border-radius:50%;background:radial-gradient(ellipse at 50% 44%,rgba(255,255,255,.92),rgba(186,230,253,.76) 34%,rgba(14,165,233,.36) 72%,rgba(40,95,120,.2));box-shadow:inset 0 6px 8px rgba(255,255,255,.64),0 7px 11px rgba(82,55,15,.12);}
  .home-game-item.furn_lily_pond .home-game-body::before{content:"";position:absolute;left:18%;top:30%;width:18px;height:11px;border-radius:70% 20% 70% 20%;background:linear-gradient(145deg,#dcfce7,#65a30d);box-shadow:34px 3px 0 -1px #bbf7d0,18px -10px 0 -3px #86efac;}
  .home-game-item.furn_lily_pond .home-game-body::after{content:"";position:absolute;left:43%;top:18%;width:14px;height:14px;border-radius:50%;background:radial-gradient(circle,#fff 0 18%,#f9a8d4 20% 54%,transparent 56%);box-shadow:20px 11px 0 -4px #fce7f3;animation:flowerNod 3.9s ease-in-out infinite;}
  .home-game-item.furn_lily_pond .home-game-detail.water{left:12%;right:12%;bottom:13px;top:auto;height:16px;border-radius:50%;background:transparent;border-top:1px solid rgba(255,255,255,.62);border-bottom:1px solid rgba(125,211,252,.30);animation:pondRipple 2.8s ease-in-out infinite;}
  .home-game-item.furn_lily_pond .home-game-detail.base{left:10%;right:10%;bottom:2px;height:9px;border-radius:50%;background:radial-gradient(ellipse,rgba(82,55,15,.14),transparent 70%);}
  .home-slot.feature-slot .home-game-item.furniture{width:clamp(86px,20vw,112px);height:clamp(78px,18vw,102px);}
  .home-slot.feature-slot .home-game-item.furn_fountain .home-game-detail.water{top:-10%;height:46%;}
  .home-slot.feature-slot .home-game-item.furn_fountain .home-game-detail.water::before{height:34px;top:-17px;}
  .home-game-item.decor .home-game-shadow{left:16%;right:16%;height:13px;}
  .home-game-item.dec_lanterns{animation:lanternSwing 3.6s ease-in-out infinite;}
  .home-game-item.dec_lanterns .home-game-body{box-shadow:0 0 14px rgba(240,203,118,.62),inset 0 5px 7px rgba(255,255,255,.6);animation:lampGlow 2.4s ease-in-out infinite;}
  .home-game-item.dec_lanterns .home-game-detail.base{left:32%;right:32%;bottom:6px;height:9px;background:linear-gradient(180deg,#fff8df,#7c4a12);}
  .home-game-item.dec_gates .home-game-body{left:12%;right:12%;height:75%;background:linear-gradient(90deg,rgba(255,248,223,.96) 0 9%,transparent 9% 18%,rgba(240,203,118,.88) 18% 28%,transparent 28% 43%,rgba(255,248,223,.96) 43% 57%,transparent 57% 72%,rgba(240,203,118,.88) 72% 82%,transparent 82% 91%,rgba(255,248,223,.96) 91%);box-shadow:inset 0 5px 8px rgba(255,255,255,.56),0 8px 12px rgba(82,55,15,.12);}
  .home-game-item.dec_gates .home-game-detail.base{left:18%;right:18%;bottom:7px;height:8px;background:linear-gradient(90deg,#8a5a08,#f0cb76,#8a5a08);}
  .home-game-item.pet_lamb{animation:lambBodyIdle 2.9s ease-in-out infinite;}
  .home-game-item.pet_lamb .home-game-shadow{left:5%;right:18%;bottom:1px;height:12px;filter:blur(1.3px);}
  .home-game-item.pet_lamb .home-game-detail.wing-left{animation:lambHeadBob 2.4s ease-in-out infinite;}
  .home-game-item.pet_lamb .home-game-icon::before,.home-game-item.pet_lamb .home-game-icon::after{animation:lambLegs 1.1s ease-in-out infinite;}
  .home-game-item.pet_dove{animation:dovePerch 3.2s ease-in-out infinite;}
  .home-game-item.pet_dove .home-game-shadow{opacity:.36;left:18%;right:18%;bottom:2px;}
  .home-game-item.pet_dove .home-game-body::before{animation:doveBlink 4.4s ease-in-out infinite;}
  .home-game-item.pet_dove .home-game-icon{animation:doveTail 1.2s ease-in-out infinite;}
  .home-game-item.pet_butterfly{width:clamp(66px,17vw,90px);height:clamp(62px,16vw,84px);animation:butterflyBody 1.55s ease-in-out infinite;}
  .home-game-item.pet_butterfly .home-game-shadow{opacity:.24;left:22%;right:22%;bottom:0;height:10px;filter:blur(1.5px);}
  .home-game-item.pet_butterfly .home-game-glow{opacity:.24;filter:blur(.9px);}
  .home-game-item.pet_butterfly .home-game-body{left:45%;right:45%;bottom:24px;height:31%;border-radius:999px;background:linear-gradient(180deg,#3b2b58,#8b5cf6);border:none;box-shadow:0 4px 8px rgba(82,55,15,.12);overflow:visible;}
  .home-game-item.pet_butterfly .home-game-body::before,.home-game-item.pet_butterfly .home-game-body::after{content:"";position:absolute;top:-13px;width:28px;height:31px;border-radius:70% 45% 70% 45%;background:radial-gradient(circle at 34% 28%,rgba(255,255,255,.86) 0 5px,transparent 6px),linear-gradient(145deg,#fbcfe8,#67e8f9 62%,#8b5cf6);border:1px solid rgba(255,255,255,.44);box-shadow:inset 0 6px 8px rgba(255,255,255,.42),0 5px 8px rgba(82,55,15,.12);}
  .home-game-item.pet_butterfly .home-game-body::before{right:4px;transform-origin:100% 60%;animation:butterflyWingLeft .38s ease-in-out infinite;}
  .home-game-item.pet_butterfly .home-game-body::after{left:4px;transform-origin:0 60%;animation:butterflyWingRight .38s ease-in-out infinite;}
  .home-game-item.pet_butterfly .home-game-detail.wing-left,.home-game-item.pet_butterfly .home-game-detail.wing-right{top:15%;width:20px;height:24px;border-radius:60% 40%;background:linear-gradient(145deg,#fff,#f9a8d4 54%,#67e8f9);opacity:.92;}
  .home-game-item.pet_butterfly .home-game-detail.wing-left{left:12%;transform-origin:100% 80%;animation:butterflySmallWingLeft .38s ease-in-out infinite;}
  .home-game-item.pet_butterfly .home-game-detail.wing-right{right:12%;transform-origin:0 80%;animation:butterflySmallWingRight .38s ease-in-out infinite;}
  .home-game-item.pet_butterfly .home-game-icon{display:block;position:absolute;left:50%;top:24%;width:3px;height:16px;margin:0;font-size:0;background:#3b2b58;border-radius:999px;transform:translateX(-50%);}
  .home-game-item.pet_butterfly .home-game-icon::before,.home-game-item.pet_butterfly .home-game-icon::after{content:"";position:absolute;top:-4px;width:12px;height:8px;border-top:1.5px solid #3b2b58;border-radius:50%;}
  .home-game-item.pet_butterfly .home-game-icon::before{right:1px;transform:rotate(22deg);}
  .home-game-item.pet_butterfly .home-game-icon::after{left:1px;transform:rotate(-22deg);}
  .home-shop-preview{display:flex;justify-content:center;margin:0 auto 10px;min-height:78px;align-items:flex-end;overflow:visible;}
  .home-shop-preview .home-game-item{width:76px;height:76px;}
  .home-shop-preview .home-game-icon{display:none;}
  .home-shop-preview .home-game-item.pet_lamb .home-game-icon,.home-shop-preview .home-game-item.pet_dove .home-game-icon,.home-shop-preview .home-game-item.pet_butterfly .home-game-icon{display:block;}
  .home-shop-preview .animated-shop-item{display:flex;justify-content:center;min-width:82px;}
  .home-shop-preview.preview-window .home-game-item{width:88px;height:82px;}
  .home-shop-preview.preview-furniture .home-game-item{width:104px;height:94px;}
  .home-shop-preview.preview-window .animated-shop-item{min-width:94px;}
  .home-shop-preview.preview-furniture .animated-shop-item{min-width:112px;}
  .home-shop-preview.preview-garden .home-game-item{width:92px;height:70px;}
  .home-category-card .home-shop-preview{min-height:54px;margin-bottom:6px;}
  .home-category-card .home-shop-preview .home-game-item{width:52px;height:52px;}
  .home-category-card .home-shop-preview.preview-window .home-game-item{width:58px;height:54px;}
  .home-category-card .home-shop-preview.preview-furniture .home-game-item{width:70px;height:62px;}
  .home-category-card .home-shop-preview .home-game-glow{filter:blur(1px);opacity:.34;}
  .home-shop-preview.preview-furniture .home-game-glow{filter:blur(.8px);opacity:.28;}
  @keyframes itemBreathe{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-2px) scale(1.018)}}
  @keyframes itemGlow{0%,100%{opacity:.42;filter:blur(1.4px)}50%{opacity:.7;filter:blur(2px)}}
  @keyframes itemSway{0%,100%{transform:rotate(-1deg) translateY(0)}50%{transform:rotate(1.4deg) translateY(-2px)}}
  @keyframes itemBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
  @keyframes fountainBubble{0%,100%{transform:translateY(0) scale(1);opacity:.86}50%{transform:translateY(-4px) scale(1.08);opacity:1}}
  @keyframes fountainStream{0%,100%{transform:translateY(0) scaleY(.92);opacity:.68}50%{transform:translateY(-5px) scaleY(1.18);opacity:1}}
  @keyframes itemSparkle{0%,100%{transform:scale(.65);opacity:.2}45%{transform:scale(1.25);opacity:.95}}
  @keyframes itemPop{0%{transform:scale(1)}55%{transform:scale(1.14)}100%{transform:scale(1)}}
  @keyframes motionIdle{0%,100%{transform:translate(0,0)}50%{transform:translate(0,-2px)}}
  @keyframes motionSway{0%,100%{transform:translateX(0) rotate(-1deg)}50%{transform:translateX(4px) rotate(1.5deg)}}
  @keyframes motionGlow{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-1px) scale(1.025)}}
  @keyframes motionWalk{0%{transform:translateX(calc(var(--drift,24px) * -1)) scaleX(1)}10%{transform:translateX(calc(var(--drift,24px) * -.72)) translateY(-1px) scaleX(1)}24%{transform:translateX(calc(var(--drift,24px) * -.18)) translateY(0) scaleX(1)}34%{transform:translateX(calc(var(--drift,24px) * .16)) translateY(-1px) scaleX(1)}45%{transform:translateX(var(--drift,24px)) scaleX(1)}55%{transform:translateX(var(--drift,24px)) scaleX(1)}58%{transform:translateX(var(--drift,24px)) scaleX(-1)}70%{transform:translateX(calc(var(--drift,24px) * .52)) translateY(-1px) scaleX(-1)}86%{transform:translateX(calc(var(--drift,24px) * -.52)) translateY(0) scaleX(-1)}96%{transform:translateX(calc(var(--drift,24px) * -1)) scaleX(-1)}100%{transform:translateX(calc(var(--drift,24px) * -1)) scaleX(1)}}
  @keyframes motionFlutter{0%,100%{transform:translate(0,0) rotate(-2deg)}12%{transform:translate(calc(var(--drift,20px) * .22),-18px) rotate(7deg)}27%{transform:translate(calc(var(--drift,20px) * .74),-7px) rotate(-5deg)}43%{transform:translate(calc(var(--drift,20px) * .44),-28px) rotate(6deg)}59%{transform:translate(calc(var(--drift,20px) * -.62),-12px) rotate(-7deg)}76%{transform:translate(calc(var(--drift,20px) * -.24),-24px) rotate(4deg)}88%{transform:translate(calc(var(--drift,20px) * .08),-10px) rotate(-2deg)}}
  @keyframes motionPastureWalk{0%{transform:translateX(calc(var(--drift,94px) * -1)) scaleX(1)}8%{transform:translateX(calc(var(--drift,94px) * -.86)) translateY(-1px) scaleX(1)}18%{transform:translateX(calc(var(--drift,94px) * -.54)) scaleX(1)}31%{transform:translateX(calc(var(--drift,94px) * -.16)) translateY(-1px) scaleX(1)}43%{transform:translateX(calc(var(--drift,94px) * .26)) scaleX(1)}53%{transform:translateX(var(--drift,94px)) scaleX(1)}60%{transform:translateX(var(--drift,94px)) scaleX(-1)}73%{transform:translateX(calc(var(--drift,94px) * .38)) translateY(-1px) scaleX(-1)}88%{transform:translateX(calc(var(--drift,94px) * -.58)) scaleX(-1)}97%{transform:translateX(calc(var(--drift,94px) * -1)) scaleX(-1)}100%{transform:translateX(calc(var(--drift,94px) * -1)) scaleX(1)}}
  @keyframes motionGardenFlutter{0%,100%{transform:translate(calc(var(--drift,110px) * -.75),6px) rotate(-6deg)}14%{transform:translate(calc(var(--drift,110px) * -.30),-28px) rotate(9deg)}28%{transform:translate(calc(var(--drift,110px) * .32),-10px) rotate(-8deg)}44%{transform:translate(calc(var(--drift,110px) * .70),-36px) rotate(7deg)}61%{transform:translate(calc(var(--drift,110px) * .15),-18px) rotate(-5deg)}78%{transform:translate(calc(var(--drift,110px) * -.55),-32px) rotate(8deg)}}
  @keyframes motionDoveCircle{0%,100%{transform:translate(calc(var(--drift,70px) * -.35),0) rotate(-2deg)}18%{transform:translate(calc(var(--drift,70px) * .25),-18px) rotate(5deg)}38%{transform:translate(calc(var(--drift,70px) * .74),-5px) rotate(-3deg)}58%{transform:translate(calc(var(--drift,70px) * .16),12px) rotate(3deg)}78%{transform:translate(calc(var(--drift,70px) * -.54),-8px) rotate(-4deg)}}
  @keyframes lambStepBounce{0%,100%{transform:translateY(0) rotate(0)}30%{transform:translateY(-2px) rotate(-2deg)}65%{transform:translateY(-1px) rotate(2deg)}}
  @keyframes rainbowBreathe{0%,100%{opacity:.72;transform:translateX(-50%) translateY(0) scale(1)}50%{opacity:.86;transform:translateX(-50%) translateY(-2px) scale(1.018)}}
  @keyframes motionHop{0%,100%{transform:translate(0,0)}12%{transform:translate(0,-8px)}22%{transform:translate(8px,0)}52%{transform:translate(8px,0)}64%{transform:translate(8px,-7px)}76%{transform:translate(-6px,0)}}
  @keyframes itemStep{0%,100%{transform:translateY(0) rotate(0)}25%{transform:translateY(-2px) rotate(-1deg)}50%{transform:translateY(0) rotate(0)}75%{transform:translateY(-2px) rotate(1deg)}}
  @keyframes itemFlutterBody{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-3px) scale(1.03)}}
  @keyframes itemPerchHop{0%,100%{transform:translateY(0)}10%{transform:translateY(-5px)}18%{transform:translateY(0)}}
  @keyframes wingFlutterLeft{0%,100%{transform:rotate(-22deg) scaleX(1)}50%{transform:rotate(-45deg) scaleX(.82)}}
  @keyframes wingFlutterRight{0%,100%{transform:rotate(22deg) scaleX(1)}50%{transform:rotate(45deg) scaleX(.82)}}
  @keyframes doveWingLeft{0%,100%{transform:rotate(-20deg) scaleY(1)}50%{transform:rotate(-45deg) scaleY(.88)}}
  @keyframes doveWingRight{0%,100%{transform:rotate(17deg) scaleY(1)}50%{transform:rotate(42deg) scaleY(.88)}}
  @keyframes leafRipple{0%,100%{transform:translateY(0) scaleX(1)}50%{transform:translateY(-1px) scaleX(1.03)}}
  @keyframes flowerNod{0%,100%{transform:rotate(-1deg)}50%{transform:rotate(2deg) translateY(-1px)}}
  @keyframes fountainSettle{0%,100%{transform:translateY(0)}50%{transform:translateY(-1px)}}
  @keyframes waterDroplets{0%,100%{transform:translateY(0) scale(.9);opacity:.65}50%{transform:translateY(-5px) scale(1.08);opacity:1}}
  @keyframes waterfallSettle{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-1px) scale(1.012)}}
  @keyframes waterfallFlow{0%{transform:translateY(-9px) scaleY(1.02);background-position:0 0;opacity:.82}50%{transform:translateY(1px) scaleY(1.08);background-position:0 18px;opacity:1}100%{transform:translateY(8px) scaleY(1.02);background-position:0 36px;opacity:.82}}
  @keyframes waterfallMist{0%,100%{transform:scaleX(.78) translateY(0);opacity:.28}50%{transform:scaleX(1.15) translateY(-2px);opacity:.62}}
  @keyframes pondRipple{0%,100%{transform:scaleX(.88);opacity:.38}50%{transform:scaleX(1.08);opacity:.68}}
  @keyframes pondBreathe{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-.8px) scale(1.012)}}
  @keyframes gemSparkle{0%,100%{opacity:.28;filter:brightness(1)}50%{opacity:.56;filter:brightness(1.08)}}
  @keyframes pathGemShine{0%,100%{transform:rotate(45deg) translateY(0) scale(1);filter:brightness(1)}50%{transform:rotate(45deg) translateY(-1px) scale(1.05);filter:brightness(1.12)}}
  @keyframes lampGlow{0%,100%{opacity:.42;filter:blur(1px)}50%{opacity:.72;filter:blur(1.8px)}}
  @keyframes lanternSwing{0%,100%{transform:rotate(-1deg) translateY(0)}50%{transform:rotate(1.5deg) translateY(-1px)}}
  @keyframes lambBodyIdle{0%,100%{transform:translateY(0)}45%{transform:translateY(-1px)}}
  @keyframes lambHeadBob{0%,100%{transform:translateY(0) rotate(0)}50%{transform:translateY(1.5px) rotate(2deg)}}
  @keyframes lambLegs{0%,100%{transform:translateY(0)}50%{transform:translateY(-1px)}}
  @keyframes dovePerch{0%,100%{transform:translateY(0) rotate(-1deg)}50%{transform:translateY(-3px) rotate(1deg)}}
  @keyframes doveBlink{0%,92%,100%{transform:scaleY(1)}95%{transform:scaleY(.18)}}
  @keyframes doveTail{0%,100%{transform:rotate(-2deg)}50%{transform:rotate(7deg)}}
  @keyframes butterflyBody{0%,100%{transform:translateY(0) rotate(-2deg)}50%{transform:translateY(-5px) rotate(3deg)}}
  @keyframes butterflyWingLeft{0%,100%{transform:rotate(-36deg) scaleX(.78)}50%{transform:rotate(-8deg) scaleX(1)}}
  @keyframes butterflyWingRight{0%,100%{transform:rotate(36deg) scaleX(.78)}50%{transform:rotate(8deg) scaleX(1)}}
  @keyframes butterflySmallWingLeft{0%,100%{transform:rotate(-28deg) scaleX(.82)}50%{transform:rotate(-6deg) scaleX(1)}}
  @keyframes butterflySmallWingRight{0%,100%{transform:rotate(28deg) scaleX(.82)}50%{transform:rotate(6deg) scaleX(1)}}
  .animated-shop-item.motion-flutter .home-game-item.pet_dove .home-game-detail.wing-left{animation:doveWingLeft .7s ease-in-out infinite;}
  .animated-shop-item.motion-flutter .home-game-item.pet_dove .home-game-detail.wing-right{animation:doveWingRight .7s ease-in-out infinite;}
  .animated-shop-item.motion-walk .home-game-item.pet_lamb{animation:lambBodyIdle 2.9s ease-in-out infinite;}
  .animated-shop-item.motion-flutter .home-game-item.pet_dove{animation:dovePerch 3.2s ease-in-out infinite;}
  .animated-shop-item.motion-flutter .home-game-item.pet_butterfly{animation:butterflyBody 1.55s ease-in-out infinite;}
  .home-game-item:not(.pet_lamb):not(.pet_butterfly){animation-duration:7s!important;}
  .home-game-item:not(.pet_lamb):not(.pet_butterfly) .home-game-detail{animation-duration:5.5s!important;}
  .home-game-item:not(.pet_lamb):not(.pet_butterfly) .home-game-glow{opacity:.18;}
  .home-gem{position:absolute;z-index:2;pointer-events:none;width:14px;height:14px;transform:rotate(45deg);opacity:.38;background:linear-gradient(135deg,#fff 0%,#dff7ff 28%,#7dd3fc 55%,#8b5cf6 100%);border:1px solid rgba(255,255,255,.58);box-shadow:0 0 5px rgba(125,211,252,.24),0 3px 6px rgba(82,55,15,.06);animation:gemTwinkle 8s ease-in-out infinite;}
  .home-gem::before{content:"";position:absolute;inset:3px;background:linear-gradient(135deg,rgba(255,255,255,.9),rgba(255,255,255,0));clip-path:polygon(0 0,100% 0,45% 46%);}
  .home-gem.g1{left:15%;bottom:22%;width:15px;height:15px;animation-delay:.2s;}
  .home-gem.g2{right:18%;bottom:25%;width:20px;height:20px;background:linear-gradient(135deg,#fff 0%,#fff3c4 30%,#f0c75e 64%,#c99012 100%);animation-delay:1.1s;}
  .home-gem.g3{left:37%;bottom:14%;width:13px;height:13px;background:linear-gradient(135deg,#fff 0%,#fce7f3 34%,#f9a8d4 70%,#e879f9 100%);animation-delay:1.8s;}
  .home-gem.g4{right:34%;bottom:12%;width:14px;height:14px;background:linear-gradient(135deg,#fff 0%,#dcfce7 32%,#86efac 70%,#22c55e 100%);animation-delay:2.4s;}
  .home-room .home-gem{opacity:.26;filter:saturate(.82);}
  .home-room .home-path-gem{opacity:.34;}
  .home-palace-body::before,.home-house-body::before,.home-apartment-body::before{content:"";position:absolute;left:12%;right:12%;top:10px;height:9px;border-radius:999px;background:linear-gradient(90deg,transparent,rgba(255,255,255,.72),transparent);box-shadow:0 23px 0 -2px rgba(255,255,255,.28);}
  .home-palace-body::after{content:"";position:absolute;left:16%;right:16%;bottom:13px;height:39px;border-radius:10px;background:radial-gradient(circle at 20% 36%,rgba(255,255,255,.82) 0 5px,transparent 6px),radial-gradient(circle at 50% 20%,rgba(255,255,255,.9) 0 6px,transparent 7px),radial-gradient(circle at 80% 36%,rgba(255,255,255,.82) 0 5px,transparent 6px),linear-gradient(180deg,rgba(255,248,223,.42),rgba(201,144,18,.12));}
  .home-tower::after{content:"";position:absolute;left:10px;right:10px;top:31px;height:54px;border-radius:999px;background:repeating-linear-gradient(180deg,rgba(255,255,255,.54) 0 8px,transparent 8px 17px);}
  .home-door::after,.home-house-door::after,.home-chapel-door::after{content:"";position:absolute;right:8px;top:30px;width:4px;height:4px;border-radius:50%;background:#fff8df;box-shadow:0 0 7px rgba(255,248,223,.9);}
  .home-game-item.pet_lamb .home-game-body{background:radial-gradient(circle at 16% 20%,#fff 0 8%,transparent 9%),radial-gradient(circle at 34% 12%,#fff 0 10%,transparent 11%),radial-gradient(circle at 55% 22%,#fff 0 9%,transparent 10%),radial-gradient(circle at 74% 32%,#fff 0 8%,transparent 9%),linear-gradient(180deg,#fffdf8,#efe6d9 58%,#c7b7a0)!important;}
  .home-game-item.pet_lamb .home-game-detail.wing-left{width:25px;height:22px;background:radial-gradient(circle at 35% 35%,#fff 0 28%,#efe6d9 30% 68%,#c7b7a0 100%)!important;}
  .home-game-item.pet_lamb .home-game-detail.wing-left::before{width:10px;height:14px;background:#cbb7a1!important;box-shadow:-14px 3px 0 -1px #cbb7a1!important;}
  .home-game-item.pet_lamb .home-game-icon::before,.home-game-item.pet_lamb .home-game-icon::after{height:16px;background:#7a6652;box-shadow:11px 0 0 #8b7862!important;}
  .home-game-item.pet_butterfly .home-game-body::before,.home-game-item.pet_butterfly .home-game-body::after{background:radial-gradient(circle at 34% 28%,rgba(255,255,255,.94) 0 5px,transparent 6px),radial-gradient(circle at 68% 72%,rgba(255,255,255,.82) 0 4px,transparent 5px),linear-gradient(145deg,#fbcfe8,#67e8f9 55%,#7c3aed)!important;box-shadow:inset 0 7px 9px rgba(255,255,255,.48),0 6px 10px rgba(82,55,15,.14);}
  .home-game-item.pet_dove .home-game-body{background:linear-gradient(145deg,#fff,#f6fbff 56%,#c7e5f7)!important;}
  .home-game-item.pet_dove .home-game-detail.wing-left,.home-game-item.pet_dove .home-game-detail.wing-right{background:linear-gradient(145deg,#fff,#eaf6ff 70%,#c7e5f7)!important;}
  @keyframes gemTwinkle{0%,100%{transform:rotate(45deg) translateY(0) scale(1);filter:brightness(1)}50%{transform:rotate(45deg) translateY(-3px) scale(1.08);filter:brightness(1.18)}}
  .home-slot.feature-slot{filter:drop-shadow(0 6px 8px rgba(82,55,15,.12));}
  .home-slot.feature-slot .animated-shop-item{min-width:clamp(58px,15vw,82px)!important;min-height:clamp(48px,13vw,72px)!important;}
  .home-slot.feature-slot .home-game-item.furniture{width:clamp(58px,15vw,78px)!important;height:clamp(50px,13vw,72px)!important;animation:fountainSettle 6s ease-in-out infinite;}
  .home-slot.feature-slot .home-game-shadow{height:10px;left:8%;right:8%;bottom:1px;filter:blur(1px);opacity:.72;}
  .home-slot.feature-slot .home-game-glow{opacity:.12!important;filter:blur(.7px)!important;}
  .home-slot.feature-slot .home-game-item.furn_fountain{width:clamp(62px,15vw,82px)!important;height:clamp(54px,13vw,74px)!important;}
  .home-slot.feature-slot .home-game-item.furn_fountain .home-game-body{left:13%;right:13%;bottom:7px;height:48%;border-radius:12px 12px 20px 20px;box-shadow:inset 0 5px 6px rgba(255,255,255,.72),inset 0 -8px 10px rgba(24,90,125,.16),0 5px 8px rgba(82,55,15,.10);}
  .home-slot.feature-slot .home-game-item.furn_fountain .home-game-detail.water{top:-10%;height:46%;box-shadow:-13px 24px 0 -7px rgba(125,211,252,.62),13px 24px 0 -7px rgba(125,211,252,.62),0 33px 0 -8px rgba(56,189,248,.46);}
  .home-slot.feature-slot .home-game-item.furn_fountain .home-game-detail.water::before{height:34px!important;top:-17px;}
  .home-slot.feature-slot .home-game-item.furn_fountain .home-game-detail.water::after{top:-24px;width:8px;height:8px;box-shadow:-15px 17px 0 -2px rgba(125,211,252,.76),14px 14px 0 -3px rgba(125,211,252,.78),-7px 39px 0 -3px rgba(255,255,255,.82),8px 43px 0 -4px rgba(125,211,252,.72);}
  .home-slot.feature-slot .home-game-item.furn_waterfall{width:clamp(72px,18vw,96px)!important;height:clamp(76px,19vw,106px)!important;}
  .home-slot.feature-slot .home-game-item.furn_waterfall .home-game-body{left:10%;right:10%;bottom:6px;height:68%;}
  .home-slot.feature-slot .home-game-item.furn_lily_pond{width:clamp(66px,16vw,88px)!important;height:clamp(42px,11vw,58px)!important;}
  .home-slot.feature-slot .home-game-item.furn_lily_pond .home-game-body{bottom:6px;height:42%;}
  .home-pet-track.lamb-track .animated-shop-item{min-width:clamp(180px,50vw,280px)!important;animation-name:motionPastureWalk!important;}
  .home-pet-track.butterfly-track .animated-shop-item{min-width:clamp(170px,48vw,270px)!important;animation-name:motionGardenFlutter!important;}
  .home-pet-track.dove-track .animated-shop-item{min-width:clamp(170px,48vw,270px)!important;animation-name:motionDoveCircle!important;}
  .home-pet-track.lamb-track .home-game-item.pet_lamb{width:clamp(74px,18vw,96px)!important;height:clamp(58px,14vw,78px)!important;}
  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{width:clamp(58px,15vw,78px)!important;height:clamp(54px,14vw,72px)!important;}
  .home-game-item.pet_lamb .home-game-body{left:9%;right:20%;bottom:13px;height:42%;border-radius:55% 46% 42% 45%;background:radial-gradient(circle at 14% 25%,#fff 0 8%,transparent 9%),radial-gradient(circle at 32% 13%,#fff 0 10%,transparent 11%),radial-gradient(circle at 54% 22%,#fff 0 9%,transparent 10%),radial-gradient(circle at 75% 34%,#fff 0 8%,transparent 9%),linear-gradient(180deg,#fffdf8,#eee3d3 60%,#bfae97)!important;}
  .home-game-item.pet_lamb .home-game-body::after{left:-8px;top:18px;width:14px;height:13px;background:#fbfaf7;box-shadow:0 0 0 1px rgba(116,88,55,.14),48px -6px 0 -4px rgba(255,255,255,.92),54px 7px 0 -6px #d8c6b2;}
  .home-game-item.pet_lamb .home-game-detail.wing-left{right:6%;top:32%;width:24px;height:20px;border-radius:50% 48% 44% 42%;background:radial-gradient(circle at 34% 30%,#fff 0 26%,#eee3d3 28% 68%,#c3af97 100%)!important;}
  .home-game-item.pet_lamb .home-game-detail.wing-left::after{right:6px;top:7px;width:3.5px;height:3.5px;}
  .home-game-item.pet_lamb .home-game-detail.wing-right{right:22%;top:45%;width:10px;height:7px;background:#c3af97;}
  .home-game-item.pet_lamb .home-game-icon{width:39px;height:9px;margin-bottom:7px;}
  .home-game-item.pet_lamb .home-game-icon::before,.home-game-item.pet_lamb .home-game-icon::after{width:4px;height:14px;background:#6f5a47;box-shadow:10px 0 0 #836e58!important;}
  .home-game-item.pet_butterfly .home-game-body{left:47%;right:47%;bottom:22px;height:30%;background:linear-gradient(180deg,#2f254d,#6d48c5)!important;}
  .home-game-item.pet_butterfly .home-game-body::before,.home-game-item.pet_butterfly .home-game-body::after{top:-12px;width:24px;height:29px;border-radius:72% 42% 70% 42%;background:radial-gradient(circle at 34% 28%,rgba(255,255,255,.94) 0 4px,transparent 5px),radial-gradient(circle at 68% 72%,rgba(255,255,255,.82) 0 3px,transparent 4px),linear-gradient(145deg,#ffd6ea,#7dd3fc 55%,#7c3aed)!important;}
  .home-game-item.pet_butterfly .home-game-detail.wing-left,.home-game-item.pet_butterfly .home-game-detail.wing-right{width:17px;height:21px;top:17%;opacity:.86;}
  .home-game-item.pet_butterfly .home-game-icon{top:26%;height:15px;background:#2f254d;}
  .home-game-item.pet_dove{width:clamp(58px,15vw,78px)!important;height:clamp(52px,14vw,70px)!important;}
  .home-game-item.pet_dove .home-game-body{left:26%;right:18%;bottom:15px;height:36%;border-radius:60% 70% 56% 52%;background:linear-gradient(145deg,#fff,#f7fcff 58%,#c8e7fa)!important;}
  .home-game-item.pet_dove .home-game-detail.wing-left,.home-game-item.pet_dove .home-game-detail.wing-right{top:20%;width:28px;height:20px;}
  .home-game-item.pet_dove .home-game-icon{bottom:20px;width:17px;height:12px;}
  .home-slot.decor-slot{z-index:4;filter:drop-shadow(0 5px 8px rgba(82,55,15,.10));}
  .home-slot.decor-slot .home-game-item.decor{width:clamp(46px,12vw,64px)!important;height:clamp(48px,12vw,66px)!important;}
  .home-slot.garden-slot{z-index:4;max-width:24%;}
  .home-slot.garden-slot .home-game-item.garden{width:clamp(54px,14vw,74px)!important;height:clamp(42px,11vw,58px)!important;}
  .home-slot.feature-slot{z-index:6;filter:drop-shadow(0 6px 8px rgba(82,55,15,.12));}
  .home-slot.feature-slot .animated-shop-item{min-width:clamp(44px,12vw,66px)!important;min-height:clamp(38px,10vw,62px)!important;}
  .home-slot.feature-slot .home-game-item.furn_fountain{width:clamp(46px,12vw,64px)!important;height:clamp(42px,11vw,58px)!important;}
  .home-slot.feature-slot .home-game-item.furn_waterfall{width:clamp(70px,18vw,96px)!important;height:clamp(76px,19vw,106px)!important;}
  .home-slot.feature-slot .home-game-item.furn_lily_pond{width:clamp(52px,13vw,70px)!important;height:clamp(34px,9vw,48px)!important;}
  .home-pet-track{z-index:5!important;pointer-events:none;}
  .home-pet-track.lamb-track{left:5%!important;right:58%!important;bottom:15%!important;}
  .home-pet-track.butterfly-track{left:60%!important;right:4%!important;top:33%!important;}
  .home-pet-track.dove-track{left:5%!important;right:60%!important;top:38%!important;}
  .home-pet-track.lamb-track .animated-shop-item{min-width:clamp(88px,27vw,142px)!important;}
  .home-pet-track.butterfly-track .animated-shop-item{min-width:clamp(84px,25vw,132px)!important;}
  .home-pet-track.dove-track .animated-shop-item{min-width:clamp(82px,24vw,128px)!important;}
  .home-pet-track.lamb-track .home-game-item.pet_lamb{width:clamp(50px,13vw,68px)!important;height:clamp(40px,10vw,56px)!important;}
  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{width:clamp(38px,10vw,54px)!important;height:clamp(36px,10vw,50px)!important;}
  .home-pet-track.dove-track .home-game-item.pet_dove{width:clamp(40px,11vw,56px)!important;height:clamp(36px,10vw,50px)!important;}
  .next-action-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:6px;margin-top:10px;}
  .next-action-btn{background:rgba(255,253,246,.78);border:1px solid var(--card-border);border-radius:12px;padding:8px 6px;color:var(--text-mid);font-family:'Nunito',sans-serif;font-size:12px;font-weight:900;cursor:pointer;min-height:38px;}
  .next-action-btn:disabled{opacity:.42;cursor:default;}
  .next-action-btn:not(:disabled):active{transform:translateY(1px);}
  .scroll-list{display:flex;flex-direction:column;gap:10px;max-height:340px;overflow-y:auto;padding-right:4px;scrollbar-width:thin;scrollbar-color:rgba(201,144,18,.45) rgba(255,248,223,.35);}
  .scroll-list::-webkit-scrollbar{width:7px;}
  .scroll-list::-webkit-scrollbar-track{background:rgba(255,248,223,.35);border-radius:999px;}
  .scroll-list::-webkit-scrollbar-thumb{background:rgba(201,144,18,.45);border-radius:999px;}
  .scroll-list.compact{gap:9px;max-height:300px;}
  .daily-walk-scroll{display:flex;flex-direction:column;gap:8px;max-height:330px;overflow-y:auto;padding-right:4px;scrollbar-width:thin;scrollbar-color:rgba(201,144,18,.45) rgba(255,248,223,.35);}
  .daily-walk-scroll::-webkit-scrollbar{width:7px;}
  .daily-walk-scroll::-webkit-scrollbar-track{background:rgba(255,248,223,.35);border-radius:999px;}
  .daily-walk-scroll::-webkit-scrollbar-thumb{background:rgba(201,144,18,.45);border-radius:999px;}
  .home-floating-label{position:absolute;left:14px;right:14px;top:14px;display:flex;justify-content:space-between;gap:8px;z-index:10;}
  .home-shop-category-row{display:flex;gap:10px;overflow-x:auto;padding:2px 0 10px;}
  .home-category-card{min-width:112px;border:1.5px solid var(--card-border);border-radius:18px;padding:12px 10px;background:linear-gradient(160deg,rgba(255,253,246,.94),rgba(255,248,223,.48));cursor:pointer;box-shadow:0 3px 14px rgba(180,130,20,.08);transition:all .2s;text-align:center;}
  .home-category-card.active{border-color:var(--gold);background:linear-gradient(160deg,rgba(255,253,246,1),rgba(240,199,94,.35));box-shadow:0 6px 20px rgba(180,130,20,.18);transform:translateY(-1px);}
  .home-shop-card{background:rgba(255,253,240,.58);border:1.5px solid var(--card-border);border-radius:20px;padding:12px 10px;min-height:136px;transition:all .22s;display:flex;flex-direction:column;box-shadow:0 3px 14px rgba(180,130,20,.07);}
  .home-shop-card.owned{border-color:rgba(201,144,18,.52);}
  .home-shop-card.expanded{min-height:188px;box-shadow:0 8px 26px rgba(180,130,20,.18);}
  .placed-chip{background:rgba(255,253,246,.86);border:1px solid rgba(184,147,75,.24);border-radius:999px;padding:6px 10px;font-size:12px;font-weight:800;color:var(--text-mid);backdrop-filter:blur(6px);}
  .sticky-preview-card{position:relative;top:auto;z-index:1;backdrop-filter:blur(10px);box-shadow:0 4px 18px rgba(138,90,8,.10);}
  .shop-card,.home-shop-card,.home-category-card,.shop-category-card{border-radius:16px;box-shadow:0 2px 10px rgba(138,90,8,.05);}
  .shop-card.expanded,.home-shop-card.expanded{box-shadow:0 5px 18px rgba(138,90,8,.10);}
  .sticky-preview-label{display:inline-flex;align-items:center;gap:5px;background:rgba(255,248,223,.9);border:1px solid rgba(184,147,75,.26);border-radius:999px;padding:5px 10px;color:var(--gold-deep);font-size:12px;font-weight:900;white-space:nowrap;}
  .sticky-preview-card .home-room{min-height:300px;}
  .unlock-note{background:rgba(255,248,223,.58);border:1px solid rgba(184,147,75,.20);border-radius:14px;padding:10px 12px;color:var(--text-mid);font-size:13px;line-height:1.6;}
  .closet-pieces-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;align-items:start;}
  .closet-pieces-grid > div{min-width:0;}
  .closet-pieces-grid .closet-item-btn{width:100%;min-width:0;}
  .closet-item-btn{background:var(--card-bg);border:2px solid var(--card-border);border-radius:16px;padding:12px 14px;cursor:pointer;text-align:center;transition:all .2s;min-width:90px;min-height:80px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;}
  .closet-item-btn.equipped{border-color:var(--gold);box-shadow:0 4px 16px rgba(180,130,20,.28);}

  /* Low-funds modal */
  .funds-modal-overlay{position:fixed;inset:0;background:rgba(60,40,0,.42);backdrop-filter:blur(10px);z-index:2000;display:flex;align-items:center;justify-content:center;padding:18px;}
  .funds-modal{background:linear-gradient(160deg,#fffdf2,#fff3c4);border:2px solid var(--gold);border-radius:28px;padding:28px 22px;max-width:360px;width:100%;text-align:center;box-shadow:0 20px 60px rgba(138,90,8,.32);animation:modalIn .3s ease-out;}

  /* Sell confirm modal */
  .sell-modal-overlay{position:fixed;inset:0;background:rgba(60,40,0,.42);backdrop-filter:blur(10px);z-index:2000;display:flex;align-items:center;justify-content:center;padding:18px;}
  .sell-modal{background:linear-gradient(160deg,#fffdf2,#fff3c4);border:2px solid var(--gold);border-radius:28px;padding:28px 22px;max-width:360px;width:100%;text-align:center;box-shadow:0 20px 60px rgba(138,90,8,.32);animation:modalIn .3s ease-out;}

  /* Neglect darkness overlay */
  .home-neglect-overlay{position:absolute;inset:0;border-radius:28px;pointer-events:none;z-index:20;transition:opacity 1s;}
  .home-restore-overlay{position:absolute;inset:0;border-radius:22px;pointer-events:none;z-index:22;background:radial-gradient(circle at 50% 40%,rgba(255,255,255,.86),rgba(255,248,223,.55) 28%,transparent 68%);animation:homeRestore 2.4s ease-out forwards;}
  .home-restore-overlay::before{content:"";position:absolute;left:38%;right:38%;bottom:8%;height:58%;border-radius:999px;background:linear-gradient(180deg,rgba(255,255,255,.78),rgba(240,203,118,.24),transparent);filter:blur(1px);animation:homeBuildLight 2.4s ease-out forwards;}
  .home-restore-overlay::after{content:"";position:absolute;left:24%;right:24%;bottom:28%;height:46%;border-radius:999px;background:repeating-linear-gradient(90deg,transparent 0 16px,rgba(255,248,223,.42) 16px 18px,transparent 18px 34px);filter:blur(.4px);animation:homeBuildLight 2.4s ease-out .12s forwards;}
  .home-transform-overlay{position:absolute;inset:0;border-radius:22px;pointer-events:none;z-index:23;background:radial-gradient(circle at 50% 42%,rgba(255,255,255,.56),rgba(255,248,223,.26) 28%,transparent 68%);animation:homeTransformReveal 5.8s ease-in-out forwards;overflow:hidden;}
  .home-transform-overlay::before{content:"";position:absolute;left:50%;top:45%;width:210px;height:98px;border:1.5px solid rgba(240,203,118,.34);border-radius:50%;animation:homeTransformRing 5.4s ease-in-out forwards;box-shadow:0 0 16px rgba(255,248,223,.44);}
  .home-transform-overlay::after{content:"";position:absolute;left:23%;right:23%;top:26%;bottom:31%;border-radius:20px;background:linear-gradient(180deg,rgba(255,253,246,.42),rgba(240,203,118,.13));border:1px solid rgba(240,203,118,.24);clip-path:inset(100% 0 0 0);animation:homeBuildRise 5.6s cubic-bezier(.2,.72,.2,1) forwards;box-shadow:inset 0 0 18px rgba(255,255,255,.54),0 8px 20px rgba(138,90,8,.08);}
  .build-scaffold{position:absolute;left:18%;right:18%;top:28%;height:38%;z-index:2;opacity:.78;transform-origin:50% 50%;animation:homeScaffoldDraw 5.6s ease-in-out forwards;}
  .build-scaffold::before,.build-scaffold::after{content:"";position:absolute;inset:0;border-top:2px solid rgba(201,144,18,.30);border-bottom:2px solid rgba(201,144,18,.20);background:repeating-linear-gradient(90deg,rgba(201,144,18,.24) 0 2px,transparent 2px 34px),repeating-linear-gradient(180deg,transparent 0 22px,rgba(255,248,223,.46) 22px 24px,transparent 24px 45px);}
  .build-scaffold::after{transform:skewY(-10deg);opacity:.54;background:linear-gradient(36deg,transparent 0 48%,rgba(201,144,18,.28) 49% 51%,transparent 52% 100%),linear-gradient(-36deg,transparent 0 48%,rgba(201,144,18,.20) 49% 51%,transparent 52% 100%);}
  .build-beam{position:absolute;left:26%;right:26%;height:6px;border-radius:999px;background:linear-gradient(90deg,rgba(255,248,223,.2),rgba(240,203,118,.74),rgba(138,90,8,.24));box-shadow:0 0 10px rgba(240,203,118,.26);z-index:3;animation:buildBeamLift 5.2s ease-in-out forwards;}
  .build-beam.b1{top:34%;--rot:-8deg;animation-delay:.15s}.build-beam.b2{top:43%;left:20%;right:20%;--rot:6deg;animation-delay:.48s}.build-beam.b3{top:53%;left:30%;right:30%;--rot:-3deg;animation-delay:.82s}
  .builder-angel{position:absolute;z-index:4;font-size:20px;line-height:1;filter:drop-shadow(0 3px 7px rgba(138,90,8,.14));animation:angelBuildFloat 5.7s ease-in-out forwards;}
  .builder-angel.a1{left:18%;top:42%;--ax:0px;--drift:12px}.builder-angel.a2{right:18%;top:32%;--ax:0px;--drift:-12px;animation-delay:.35s}.builder-angel.a3{left:49%;top:25%;--ax:-8px;--drift:8px;animation-delay:.72s;font-size:18px}
  .builder-angel::after{content:"✦";position:absolute;left:18px;top:-8px;font-size:12px;color:#fff8df;text-shadow:0 0 9px rgba(240,203,118,.7);animation:angelBuildSpark 1.6s ease-in-out infinite;}
  .build-spark{position:absolute;z-index:4;color:#fff8df;text-shadow:0 0 10px rgba(240,203,118,.75);font-size:13px;animation:angelBuildSpark 2.1s ease-in-out infinite;}
  .build-spark.s1{left:30%;top:38%;animation-delay:.2s}.build-spark.s2{left:58%;top:33%;animation-delay:.75s}.build-spark.s3{left:48%;top:52%;animation-delay:1.15s}
  .home-transform-mini{width:88px;height:88px;margin:0 auto 10px;border-radius:28px;background:radial-gradient(circle at 50% 36%,#fff,rgba(255,248,223,.68) 38%,rgba(240,203,118,.18) 72%);display:flex;align-items:center;justify-content:center;position:relative;animation:homeTransformReveal 3.6s ease-in-out both;box-shadow:0 10px 22px rgba(138,90,8,.1);}
  .home-transform-mini::before{content:"";position:absolute;left:50%;top:52%;width:72px;height:36px;border:1.5px solid rgba(240,203,118,.36);border-radius:50%;animation:homeTransformRing 3.8s ease-in-out infinite;}
  .home-transform-mini::after{content:"✦";position:absolute;left:18px;top:20px;color:#fff8df;text-shadow:0 0 9px rgba(240,203,118,.65);box-shadow:42px 28px 0 -5px rgba(255,248,223,.58),50px -8px 0 -6px rgba(240,203,118,.52);animation:homeTransformSpark 3.4s ease-in-out infinite;}
  .home-blessing-overlay{position:absolute;inset:0;border-radius:22px;pointer-events:none;z-index:21;background:radial-gradient(circle at 50% 18%,rgba(255,255,255,.82),rgba(236,253,245,.38) 24%,transparent 58%);animation:homeRestore 2.35s ease-out forwards;}
  .home-blessing-overlay::before{content:"";position:absolute;left:16%;right:16%;top:12%;height:58%;border-radius:999px;background:linear-gradient(180deg,rgba(255,255,255,.7),rgba(236,253,245,.24),transparent);filter:blur(1.5px);animation:homeBuildLight 2.35s ease-out forwards;}
  .answered-praise{animation:praiseGlow 2.2s ease-in-out infinite;}
  .home-stage-0 .home-cardboard{top:45%;width:138px;height:80px;filter:drop-shadow(0 8px 12px rgba(100,70,20,.16));}
  .home-stage-0 .home-cardboard-body{height:54px;border-radius:8px;background:linear-gradient(180deg,#e8c27b,#b97838);box-shadow:inset 0 0 10px rgba(255,248,223,.34);}
  .home-stage-0 .home-cardboard-roof{height:34px;background:linear-gradient(180deg,#f3d28b,#b7791f);}
  .home-stage-1 .home-little-shelter{top:39%;width:178px;height:112px;}
  .home-stage-1 .home-little-shelter-roof{height:64px;filter:drop-shadow(0 0 10px rgba(240,203,118,.34));}
  .home-stage-1 .home-little-shelter-body{left:18px;right:18px;height:76px;border-radius:22px 22px 10px 10px;}
  .home-stage-2 .home-house{top:32%;width:196px;height:134px;}
  .home-stage-2 .home-house-roof{left:6px;right:6px;height:68px;}
  .home-stage-2 .home-house-body{left:24px;right:24px;height:88px;border-radius:20px 20px 10px 10px;}
  .home-stage-3 .home-mansion{top:27%;width:232px;height:154px;}
  .home-stage-3 .home-mansion-roof{height:58px;top:8px;clip-path:polygon(0 100%,8% 58%,18% 100%,30% 50%,42% 100%,50% 0,58% 100%,70% 50%,82% 100%,92% 58%,100% 100%);}
  .home-stage-3 .home-mansion-body{height:104px;left:18px;right:18px;}
  .home-stage-4 .home-palace{top:20%;width:236px;height:166px;}
  .home-stage-4 .home-tower{height:122px;}
  .home-stage-5 .home-palace{top:18%;width:260px;height:174px;filter:drop-shadow(0 16px 24px rgba(184,147,75,.22))!important;}
  .home-stage-5 .home-palace-body{left:30px;right:30px;height:102px;border-radius:20px 20px 12px 12px;}
  .home-stage-5 .home-palace-roof{left:26px;right:26px;top:24px;height:54px;clip-path:polygon(0 100%,8% 46%,18% 100%,30% 38%,42% 100%,50% 0,58% 100%,70% 38%,82% 100%,92% 46%,100% 100%);}
  .home-stage-5 .home-tower{width:48px;height:134px;}
  .home-stage-5 .home-tower.left{left:-5px}.home-stage-5 .home-tower.right{right:-5px}
  .home-stage-6 .home-palace{top:15%;width:282px;height:188px;filter:drop-shadow(0 18px 26px rgba(240,203,118,.32)) drop-shadow(0 0 22px rgba(255,248,223,.38))!important;}
  .home-stage-6 .home-palace-body{left:26px;right:26px;height:112px;border-radius:24px 24px 13px 13px;background:linear-gradient(180deg,#fffdf8 0%,#fff3c4 44%,#f0c75e 76%,#d99a12 100%);}
  .home-stage-6 .home-palace-roof{left:16px;right:16px;top:18px;height:64px;clip-path:polygon(0 100%,7% 45%,16% 100%,27% 34%,38% 100%,50% 0,62% 100%,73% 34%,84% 100%,93% 45%,100% 100%);}
  .home-stage-6 .home-tower{width:52px;height:146px;}
  .home-stage-6 .home-tower.left{left:-10px}.home-stage-6 .home-tower.right{right:-10px}
  .home-water-stream{position:absolute;z-index:4;pointer-events:none;border-radius:999px;opacity:.64;filter:blur(.15px);}
  .home-water-stream::before{content:"";position:absolute;inset:0;border-radius:inherit;background:linear-gradient(180deg,rgba(255,255,255,.72),rgba(125,211,252,.38),rgba(14,165,233,.12));animation:homeStreamFlow 2.8s ease-in-out infinite;}
  .home-water-stream.furn_fountain{right:18%;bottom:9%;width:21%;height:8%;transform:rotate(-10deg);}
  .home-water-stream.furn_waterfall{right:12%;bottom:8%;width:29%;height:10%;transform:rotate(-12deg);}
  .home-water-stream.furn_lily_pond{right:16%;bottom:8%;width:18%;height:6%;opacity:.42;}
  .home-room .home-gem{opacity:.16;width:10px;height:10px;filter:saturate(.74);box-shadow:0 0 4px rgba(125,211,252,.18),0 2px 4px rgba(82,55,15,.04);}
  .home-room .home-gem.g3,.home-room .home-gem.g4{display:none;}
  .home-room .home-path-gem{opacity:.26;width:6px;height:6px;box-shadow:0 0 4px rgba(255,248,223,.18);}
  .home-path::before{opacity:.2;}
  .home-background-rainbow::after{opacity:.42;box-shadow:130px -28px 0 -7px rgba(255,248,223,.46),260px 5px 0 -8px rgba(255,248,223,.38);}
  .home-room .home-game-sparkles{opacity:.18;}
  .home-room .home-game-sparkles span:nth-child(2),.home-room .home-game-sparkles span:nth-child(3){display:none;}
  .home-slot.garden-slot{left:5%!important;right:auto!important;bottom:6%!important;z-index:4!important;max-width:24%;}
  .home-slot.decor-slot{right:4%!important;bottom:28%!important;z-index:4!important;}
  .home-slot.feature-slot{right:5%!important;bottom:7%!important;z-index:6!important;}
  .home-slot.feature-slot.furn_waterfall-slot{right:4%!important;bottom:8%!important;z-index:5!important;}
  .home-slot.feature-slot .animated-shop-item{min-width:clamp(56px,15vw,88px)!important;min-height:clamp(48px,13vw,74px)!important;}
  .home-slot.feature-slot .home-game-item.furn_fountain{width:clamp(58px,15vw,82px)!important;height:clamp(54px,14vw,76px)!important;}
  .home-slot.feature-slot .home-game-item.furn_waterfall{width:clamp(82px,21vw,116px)!important;height:clamp(92px,23vw,128px)!important;}
  .home-slot.feature-slot .home-game-item.furn_waterfall .home-game-body{left:7%;right:7%;height:74%;bottom:8px;}
  .home-slot.feature-slot .home-game-item.furn_lily_pond{width:clamp(56px,14vw,78px)!important;height:clamp(36px,10vw,52px)!important;}
  .home-pet-track{z-index:5!important;pointer-events:none;opacity:.92;}
  .home-pet-track.lamb-track{left:3%!important;right:64%!important;bottom:13%!important;}
  .home-pet-track.butterfly-track{left:64%!important;right:4%!important;top:34%!important;}
  .home-pet-track.dove-track{left:5%!important;right:65%!important;top:39%!important;}
  .home-pet-track.lamb-track .animated-shop-item{min-width:clamp(78px,23vw,122px)!important;animation-duration:18s!important;}
  .home-pet-track.butterfly-track .animated-shop-item{min-width:clamp(72px,22vw,116px)!important;animation-duration:13s!important;}
  .home-pet-track.dove-track .animated-shop-item{min-width:clamp(72px,22vw,112px)!important;animation-duration:14s!important;}
  .home-pet-track.lamb-track .home-game-item.pet_lamb{width:clamp(42px,11vw,58px)!important;height:clamp(34px,9vw,48px)!important;}
  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{width:clamp(32px,9vw,46px)!important;height:clamp(30px,9vw,42px)!important;}
  .home-pet-track.dove-track .home-game-item.pet_dove{width:clamp(34px,9vw,48px)!important;height:clamp(30px,8vw,42px)!important;}
  .home-game-item.furn_waterfall .home-game-shadow{left:4%;right:4%;height:15px;opacity:.74;}
  .home-game-item.furn_waterfall .home-game-glow{opacity:.15!important;}
  .home-game-item.furn_waterfall .home-game-body{background:linear-gradient(145deg,#f8fbff 0%,#dbeafe 18%,#8fc7ef 48%,#4baed5 100%);border:1.5px solid rgba(37,99,135,.18);}
  .home-game-item.furn_waterfall .home-game-body::before{left:7%;right:7%;top:8%;height:30%;background:radial-gradient(ellipse at 50% 45%,#fff,rgba(186,230,253,.84) 52%,rgba(96,165,250,.32));box-shadow:-14px 30px 0 -8px rgba(255,255,255,.5),14px 34px 0 -9px rgba(255,255,255,.42);}
  .home-game-item.furn_waterfall .home-game-body::after{left:25%;right:25%;top:11%;bottom:2%;background:repeating-linear-gradient(180deg,rgba(255,255,255,.98) 0 8px,rgba(186,230,253,.9) 8px 17px,rgba(96,165,250,.62) 17px 28px,rgba(255,255,255,.7) 28px 34px);background-size:100% 36px;box-shadow:-16px 17px 0 -6px rgba(255,255,255,.58),16px 21px 0 -7px rgba(255,255,255,.48),0 31px 0 -8px rgba(255,255,255,.4);animation:waterfallFlow 1.2s linear infinite;}
  .home-game-item.furn_waterfall .home-game-detail.water{left:12%;right:12%;bottom:0;height:30px;background:radial-gradient(ellipse,rgba(255,255,255,.94),rgba(125,211,252,.58) 52%,rgba(14,165,233,.18));}
  .home-game-item.furn_waterfall .home-game-detail.water::after{content:"";position:absolute;left:12%;right:12%;top:9px;height:8px;border-radius:50%;border-top:1px solid rgba(255,255,255,.72);border-bottom:1px solid rgba(125,211,252,.28);animation:waterfallMist 2s ease-in-out infinite;}
  .home-game-item.furn_fountain .home-game-detail.water::before{animation:fountainStream 1.45s ease-in-out infinite;}
  .home-game-item.furn_fountain .home-game-detail.water::after{animation:waterDroplets 1.75s ease-in-out infinite;opacity:.72;}
  .home-game-item.furn_fountain .home-game-shadow{left:10%;right:10%;height:13px;opacity:.52;}
  .home-game-item.furn_fountain .home-game-body{left:12%;right:12%;bottom:7px;height:46%;border-radius:13px 13px 22px 22px;background:linear-gradient(180deg,#fbfdff 0%,#d8eef9 34%,#8bbdd4 68%,#5f90a8 100%);border:1.5px solid rgba(47,93,118,.25);box-shadow:inset 0 5px 7px rgba(255,255,255,.86),inset 0 -9px 12px rgba(24,74,105,.22),0 7px 10px rgba(82,55,15,.12);}
  .home-game-item.furn_fountain .home-game-body::before{left:8%;right:8%;top:7px;height:13px;border-radius:50%;background:radial-gradient(ellipse at 50% 40%,#fff,rgba(165,219,242,.92) 48%,rgba(50,120,155,.28));box-shadow:0 19px 0 2px rgba(255,255,255,.22),0 21px 0 4px rgba(48,93,120,.14);}
  .home-game-item.furn_fountain .home-game-body::after{left:25%;right:25%;bottom:-8px;height:12px;border-radius:8px 8px 5px 5px;background:linear-gradient(180deg,#d8eef9,#5f90a8);box-shadow:inset 0 2px 4px rgba(255,255,255,.56);}
  .home-game-item.furn_fountain .home-game-detail.water{left:17%;right:17%;top:-12%;height:47%;border-radius:50% 50% 48% 48%;background:radial-gradient(circle at 50% 14%,#fff 0 14%,rgba(190,233,249,.98) 16% 44%,rgba(74,176,214,.58) 45% 72%,rgba(14,116,144,.16));box-shadow:-16px 26px 0 -8px rgba(125,211,252,.62),16px 26px 0 -8px rgba(125,211,252,.58),0 35px 0 -8px rgba(45,139,175,.42);animation:fountainBubble 1.65s ease-in-out infinite;}
  .home-game-item.furn_fountain .home-game-detail.water::before{left:49%;top:-22px;width:5px;height:41px;border-radius:999px;background:linear-gradient(180deg,rgba(255,255,255,.98),rgba(165,219,242,.7) 58%,rgba(14,116,144,.24));box-shadow:-13px 13px 0 -1px rgba(125,211,252,.56),13px 13px 0 -1px rgba(125,211,252,.5);animation:fountainStream 1.18s ease-in-out infinite;}
  .home-game-item.furn_fountain .home-game-detail.water::after{left:50%;top:-28px;width:7px;height:7px;border-radius:50%;background:rgba(255,255,255,.92);box-shadow:-16px 18px 0 -2px rgba(125,211,252,.7),16px 16px 0 -3px rgba(125,211,252,.74),-8px 42px 0 -3px rgba(255,255,255,.78),8px 45px 0 -4px rgba(125,211,252,.68);animation:waterDroplets 1.55s ease-in-out infinite;opacity:.7;}
  .home-game-item.furn_waterfall .home-game-shadow{left:3%;right:3%;height:16px;opacity:.56;}
  .home-game-item.furn_waterfall .home-game-body{left:6%;right:6%;bottom:8px;height:76%;border-radius:28px 28px 20px 20px;background:linear-gradient(145deg,#eef4f7 0%,#aeb9bb 28%,#79939d 58%,#526b72 100%);border:1.5px solid rgba(48,71,78,.24);box-shadow:inset 7px 8px 10px rgba(255,255,255,.45),inset -8px -10px 13px rgba(35,60,70,.22),0 8px 12px rgba(82,55,15,.12);overflow:hidden;}
  .home-game-item.furn_waterfall .home-game-body::before{left:7%;right:7%;top:8%;height:32%;border-radius:50%;background:radial-gradient(ellipse at 50% 42%,#ffffff,rgba(208,235,246,.9) 47%,rgba(85,145,170,.32));box-shadow:-18px 35px 0 -10px rgba(255,255,255,.5),18px 38px 0 -10px rgba(255,255,255,.42),0 55px 0 -15px rgba(255,255,255,.35);}
  .home-game-item.furn_waterfall .home-game-body::after{left:22%;right:22%;top:12%;bottom:3%;border-radius:999px;background:repeating-linear-gradient(180deg,rgba(255,255,255,.98) 0 7px,rgba(190,233,249,.92) 7px 15px,rgba(81,178,216,.64) 15px 26px,rgba(255,255,255,.72) 26px 33px);background-size:100% 34px;box-shadow:-17px 18px 0 -7px rgba(255,255,255,.56),17px 21px 0 -8px rgba(255,255,255,.48),0 38px 0 -9px rgba(255,255,255,.38);animation:waterfallFlow 1.05s linear infinite;}
  .home-game-item.furn_waterfall .home-game-detail.water{left:8%;right:8%;bottom:0;height:31px;border-radius:50%;background:radial-gradient(ellipse at 50% 44%,rgba(255,255,255,.96),rgba(125,211,252,.58) 54%,rgba(14,116,144,.20));box-shadow:0 -5px 12px rgba(255,255,255,.34);animation:pondRipple 2.25s ease-in-out infinite;}
  .home-game-item.furn_waterfall .home-game-detail.water::before{content:"";position:absolute;left:20%;right:20%;top:8px;height:7px;border-radius:50%;border-top:1px solid rgba(255,255,255,.78);opacity:.9;animation:waterfallMist 2.2s ease-in-out infinite;}
  .home-game-item.furn_waterfall .home-game-detail.water::after{content:"";position:absolute;left:30%;right:30%;top:-5px;height:9px;border-radius:50%;background:radial-gradient(ellipse,rgba(255,255,255,.88),rgba(255,255,255,0) 70%);animation:waterfallMist 1.6s ease-in-out infinite;}
  .home-water-stream{opacity:.72;filter:blur(.05px);}
  .home-water-stream::before{background:linear-gradient(90deg,rgba(255,255,255,.7),rgba(125,211,252,.46),rgba(14,116,144,.12));box-shadow:inset 0 1px 2px rgba(255,255,255,.55),0 2px 5px rgba(14,116,144,.08);}
  .home-water-stream.furn_fountain{right:17%;bottom:8%;width:19%;height:7%;transform:rotate(-9deg);}
  .home-water-stream.furn_waterfall{right:8%;bottom:7%;width:34%;height:11%;transform:rotate(-13deg);}
  .home-game-item.furn_waterfall{filter:drop-shadow(0 7px 8px rgba(49,65,70,.16));}
  .home-game-item.furn_waterfall .home-game-icon{display:none!important;}
  .home-game-item.furn_waterfall .home-game-glow{opacity:.08!important;}
  .home-game-item.furn_waterfall .home-game-shadow{left:6%;right:6%;bottom:-3px;height:17px;opacity:.42;filter:blur(2.4px);}
  .home-game-item.furn_waterfall .home-game-body{left:3%;right:3%;bottom:9px;height:77%;border:none;border-radius:42% 50% 34% 38% / 30% 34% 28% 30%;background:radial-gradient(circle at 21% 78%,#5f7377 0 9px,transparent 10px),radial-gradient(circle at 80% 76%,#6b7f83 0 10px,transparent 11px),linear-gradient(145deg,#e7edf0 0%,#9aa7aa 30%,#687b80 62%,#4d646b 100%);box-shadow:inset 8px 8px 12px rgba(255,255,255,.36),inset -9px -11px 14px rgba(31,51,57,.22),0 8px 10px rgba(82,55,15,.11);overflow:hidden;clip-path:polygon(13% 11%,38% 1%,62% 8%,88% 22%,82% 87%,63% 96%,33% 92%,8% 78%,5% 34%);}
  .home-game-item.furn_waterfall .home-game-body::before{left:3%;right:3%;top:7%;height:85%;border-radius:42% 48% 32% 36%;background:radial-gradient(ellipse at 52% 12%,rgba(255,255,255,.96),rgba(212,239,249,.72) 24%,transparent 38%),radial-gradient(ellipse at 34% 65%,rgba(255,255,255,.36),transparent 46%);box-shadow:-19px 37px 0 -13px rgba(255,255,255,.36),21px 41px 0 -14px rgba(255,255,255,.32);opacity:.92;}
  .home-game-item.furn_waterfall .home-game-body::after{left:30%;right:28%;top:8%;bottom:2%;border-radius:999px;background:repeating-linear-gradient(180deg,rgba(255,255,255,.98) 0 6px,rgba(191,233,247,.9) 6px 14px,rgba(68,174,211,.65) 14px 24px,rgba(255,255,255,.72) 24px 30px);background-size:100% 30px;box-shadow:-14px 17px 0 -8px rgba(255,255,255,.55),14px 24px 0 -9px rgba(255,255,255,.45),0 42px 0 -11px rgba(255,255,255,.35);animation:waterfallFlow .95s linear infinite;}
  .home-game-item.furn_waterfall .home-game-detail.base{display:block;left:3%;right:3%;bottom:0;height:18px;border-radius:50%;background:radial-gradient(ellipse at 50% 40%,rgba(84,101,91,.34),rgba(62,83,77,.18) 52%,transparent 74%);}
  .home-game-item.furn_waterfall .home-game-detail.water{left:4%;right:4%;bottom:1px;height:34px;border-radius:50%;background:radial-gradient(ellipse at 50% 38%,rgba(255,255,255,.96),rgba(136,214,239,.58) 48%,rgba(14,116,144,.18) 76%,transparent 80%);box-shadow:0 -4px 10px rgba(255,255,255,.36);animation:pondRipple 2.1s ease-in-out infinite;}
  .home-game-item.furn_waterfall .home-game-detail.water::before{left:15%;right:15%;top:10px;height:7px;border-top:1px solid rgba(255,255,255,.82);border-bottom:1px solid rgba(125,211,252,.22);opacity:.85;animation:waterfallMist 1.9s ease-in-out infinite;}
  .home-game-item.furn_waterfall .home-game-detail.water::after{left:24%;right:24%;top:-7px;height:11px;background:radial-gradient(ellipse,rgba(255,255,255,.86),rgba(255,255,255,0) 70%);animation:waterfallMist 1.35s ease-in-out infinite;}
  .home-slot.feature-slot.furn_waterfall-slot{right:2%!important;bottom:7%!important;}
  .home-slot.feature-slot .home-game-item.furn_waterfall{width:clamp(88px,23vw,124px)!important;height:clamp(92px,24vw,132px)!important;}
  /* Final home polish: smoother level-up, grand water, calmer realistic companions */
  @keyframes homeSanctuaryBuild{0%{opacity:.18;clip-path:inset(84% 0 0 0);transform:translateY(24px) scale(.985);filter:blur(.45px) brightness(1.05)}30%{opacity:.62;clip-path:inset(48% 0 0 0);transform:translateY(10px) scale(1)}68%{opacity:.92;clip-path:inset(10% 0 0 0);transform:translateY(1px) scale(1.006);filter:blur(.05px) brightness(1.06)}100%{opacity:1;clip-path:inset(0 0 0 0);transform:translateY(0) scale(1);filter:none}}
  @keyframes homeVeilBlessing{0%{opacity:0;transform:scale(.92) translateY(16px)}22%{opacity:.72}74%{opacity:.48;transform:scale(1.02) translateY(0)}100%{opacity:0;transform:scale(1.05) translateY(-8px)}}
  @keyframes homeStoneSet{0%{opacity:0;transform:translateY(20px) scaleX(.45)}26%{opacity:.72}72%{opacity:.5;transform:translateY(0) scaleX(1)}100%{opacity:0;transform:translateY(-10px) scaleX(1.06)}}
  @keyframes homeAngelLight{0%{opacity:0;transform:translateY(18px) scale(.82)}22%{opacity:.68}64%{opacity:.58;transform:translateY(-8px) scale(1)}100%{opacity:0;transform:translateY(-26px) scale(.92)}}
  .home-room-transforming .home-cardboard,.home-room-transforming .home-little-shelter,.home-room-transforming .home-apartment,.home-room-transforming .home-house,.home-room-transforming .home-mansion,.home-room-transforming .home-palace,.home-room-transforming .home-chapel{animation:homeSanctuaryBuild 6.4s cubic-bezier(.18,.72,.18,1) forwards!important;transform-origin:50% 100%;}
  .home-transform-overlay{background:radial-gradient(circle at 50% 38%,rgba(255,255,255,.64),rgba(255,248,223,.24) 30%,transparent 68%)!important;animation:homeVeilBlessing 6.4s ease-in-out forwards!important;}
  .home-transform-overlay::before{left:50%!important;top:52%!important;width:230px!important;height:116px!important;border-color:rgba(240,203,118,.24)!important;box-shadow:0 0 18px rgba(255,248,223,.34)!important;}
  .home-transform-overlay::after{left:25%!important;right:25%!important;top:30%!important;bottom:30%!important;border-radius:22px!important;background:linear-gradient(180deg,rgba(255,253,246,.30),rgba(240,203,118,.10))!important;border-color:rgba(240,203,118,.18)!important;animation:homeSanctuaryBuild 6.1s cubic-bezier(.18,.72,.18,1) forwards!important;}
  .build-scaffold{left:22%!important;right:22%!important;top:31%!important;height:34%!important;opacity:.38!important;animation:homeScaffoldDraw 5.8s ease-in-out forwards!important;}
  .build-scaffold::before{border-color:rgba(240,203,118,.18)!important;background:repeating-linear-gradient(90deg,rgba(240,203,118,.14) 0 1px,transparent 1px 42px),repeating-linear-gradient(180deg,transparent 0 28px,rgba(255,248,223,.30) 28px 30px,transparent 30px 56px)!important;}
  .build-scaffold::after{opacity:.18!important;}
  .build-beam{height:5px!important;background:linear-gradient(90deg,transparent,rgba(255,248,223,.74),rgba(240,203,118,.46),transparent)!important;box-shadow:0 0 12px rgba(255,248,223,.35)!important;animation:homeStoneSet 5.8s ease-in-out forwards!important;}
  .builder-angel{font-size:0!important;width:24px;height:28px;border-radius:999px;background:radial-gradient(circle at 50% 28%,#fff 0 20%,rgba(255,248,223,.82) 21% 42%,rgba(240,203,118,.18) 64%,transparent 78%);filter:drop-shadow(0 0 10px rgba(255,248,223,.62));animation:homeAngelLight 5.8s ease-in-out forwards!important;}
  .builder-angel::before{content:"";position:absolute;left:-8px;right:-8px;top:8px;height:17px;border-radius:50%;background:radial-gradient(ellipse at 50% 50%,rgba(255,255,255,.58),rgba(255,248,223,.22) 54%,transparent 70%);}
  .builder-angel::after{left:19px!important;top:-5px!important;font-size:11px!important;}

  .home-slot.feature-slot.furn_waterfall-slot{right:1%!important;bottom:6%!important;}
  .home-slot.feature-slot .home-game-item.furn_waterfall{width:clamp(104px,27vw,148px)!important;height:clamp(116px,30vw,166px)!important;}
  .home-game-item.furn_waterfall{filter:drop-shadow(0 12px 15px rgba(35,55,60,.18))!important;animation:waterfallSettle 6.8s ease-in-out infinite!important;}
  .home-game-item.furn_waterfall .home-game-glow{opacity:.16!important;background:radial-gradient(ellipse,rgba(219,242,255,.78),rgba(96,165,250,.16),transparent 72%)!important;}
  .home-game-item.furn_waterfall .home-game-shadow{left:5%!important;right:5%!important;bottom:-4px!important;height:20px!important;opacity:.48!important;}
  .home-game-item.furn_waterfall .home-game-body{left:1%!important;right:1%!important;bottom:14px!important;height:79%!important;border-radius:48% 54% 33% 38% / 27% 33% 29% 31%!important;background:radial-gradient(circle at 18% 80%,#53686d 0 10px,transparent 11px),radial-gradient(circle at 82% 78%,#667b7f 0 12px,transparent 13px),radial-gradient(circle at 22% 28%,#dfe8eb 0 12%,transparent 13%),linear-gradient(145deg,#eef3f4 0%,#a7b1b3 26%,#6f858a 58%,#405b62 100%)!important;box-shadow:inset 9px 10px 14px rgba(255,255,255,.42),inset -13px -14px 17px rgba(24,44,50,.24),0 12px 16px rgba(82,55,15,.12)!important;clip-path:polygon(10% 17%,29% 4%,52% 0,78% 13%,92% 33%,86% 88%,64% 98%,34% 94%,7% 78%,2% 39%)!important;}
  .home-game-item.furn_waterfall .home-game-body::before{left:4%!important;right:4%!important;top:5%!important;height:90%!important;background:radial-gradient(ellipse at 52% 8%,rgba(255,255,255,.98),rgba(220,244,252,.74) 20%,transparent 34%),radial-gradient(ellipse at 39% 62%,rgba(255,255,255,.45),transparent 42%),radial-gradient(ellipse at 64% 72%,rgba(255,255,255,.34),transparent 44%)!important;box-shadow:-25px 42px 0 -16px rgba(255,255,255,.38),26px 49px 0 -18px rgba(255,255,255,.30)!important;}
  .home-game-item.furn_waterfall .home-game-body::after{left:27%!important;right:24%!important;top:6%!important;bottom:1%!important;border-radius:999px 999px 44% 44%!important;background:repeating-linear-gradient(180deg,rgba(255,255,255,1) 0 5px,rgba(208,244,255,.94) 5px 12px,rgba(76,184,220,.72) 12px 21px,rgba(255,255,255,.75) 21px 27px)!important;background-size:100% 28px!important;box-shadow:-18px 24px 0 -10px rgba(255,255,255,.6),17px 30px 0 -11px rgba(255,255,255,.46),0 58px 0 -14px rgba(255,255,255,.42)!important;animation:waterfallFlow .72s linear infinite!important;}
  .home-game-item.furn_waterfall .home-game-detail.base{left:0!important;right:0!important;bottom:1px!important;height:24px!important;background:radial-gradient(ellipse at 50% 42%,rgba(84,101,91,.42),rgba(62,83,77,.22) 48%,transparent 76%)!important;}
  .home-game-item.furn_waterfall .home-game-detail.water{left:-5%!important;right:-5%!important;bottom:0!important;height:42px!important;background:radial-gradient(ellipse at 50% 38%,rgba(255,255,255,.98),rgba(144,222,246,.66) 42%,rgba(14,116,144,.22) 72%,transparent 82%)!important;box-shadow:0 -6px 14px rgba(255,255,255,.42),inset 0 2px 7px rgba(255,255,255,.58)!important;}
  .home-game-item.furn_waterfall .home-game-detail.water::before{left:9%!important;right:9%!important;top:12px!important;height:9px!important;border-top:1.5px solid rgba(255,255,255,.92)!important;border-bottom:1px solid rgba(125,211,252,.28)!important;}
  .home-game-item.furn_waterfall .home-game-detail.water::after{left:18%!important;right:18%!important;top:-9px!important;height:15px!important;background:radial-gradient(ellipse,rgba(255,255,255,.92),rgba(255,255,255,0) 72%)!important;}
  .home-water-stream.furn_waterfall{right:4%!important;bottom:6%!important;width:42%!important;height:13%!important;opacity:.78!important;transform:rotate(-12deg)!important;}

  .home-slot.feature-slot .home-game-item.furn_fountain{width:clamp(78px,20vw,112px)!important;height:clamp(78px,20vw,116px)!important;}
  .home-game-item.furn_fountain{filter:drop-shadow(0 9px 12px rgba(38,80,105,.15))!important;}
  .home-game-item.furn_fountain .home-game-shadow{left:6%!important;right:6%!important;height:15px!important;opacity:.5!important;}
  .home-game-item.furn_fountain .home-game-body{left:7%!important;right:7%!important;bottom:8px!important;height:49%!important;border-radius:18px 18px 28px 28px!important;background:linear-gradient(180deg,#ffffff 0%,#dff6ff 28%,#91cfe8 62%,#467f9e 100%)!important;border:1.5px solid rgba(38,89,116,.24)!important;box-shadow:inset 0 7px 9px rgba(255,255,255,.9),inset 0 -12px 16px rgba(20,76,105,.24),0 8px 12px rgba(82,55,15,.10)!important;}
  .home-game-item.furn_fountain .home-game-body::before{left:3%!important;right:3%!important;top:4px!important;height:16px!important;background:radial-gradient(ellipse at 50% 40%,#fff,rgba(180,229,246,.98) 45%,rgba(30,111,150,.26))!important;box-shadow:0 23px 0 4px rgba(255,255,255,.26),0 26px 0 6px rgba(48,93,120,.16)!important;}
  .home-game-item.furn_fountain .home-game-body::after{left:20%!important;right:20%!important;bottom:-10px!important;height:14px!important;background:linear-gradient(180deg,#dff6ff,#467f9e)!important;}
  .home-game-item.furn_fountain .home-game-detail.water{left:8%!important;right:8%!important;top:-23%!important;height:62%!important;background:radial-gradient(circle at 50% 10%,#fff 0 10%,rgba(206,242,255,.98) 12% 36%,rgba(74,176,214,.62) 37% 68%,rgba(14,116,144,.15))!important;box-shadow:-21px 35px 0 -12px rgba(125,211,252,.66),21px 35px 0 -12px rgba(125,211,252,.6),0 47px 0 -12px rgba(45,139,175,.42)!important;animation:fountainBubble 1.25s ease-in-out infinite!important;}
  .home-game-item.furn_fountain .home-game-detail.water::before{left:49%!important;top:-31px!important;width:6px!important;height:56px!important;box-shadow:-18px 18px 0 -1px rgba(125,211,252,.54),18px 18px 0 -1px rgba(125,211,252,.5),0 8px 0 1px rgba(255,255,255,.5)!important;}
  .home-game-item.furn_fountain .home-game-detail.water::after{top:-36px!important;width:8px!important;height:8px!important;box-shadow:-22px 24px 0 -2px rgba(125,211,252,.75),22px 22px 0 -3px rgba(125,211,252,.74),-10px 54px 0 -3px rgba(255,255,255,.8),10px 58px 0 -4px rgba(125,211,252,.68)!important;}
  .home-water-stream.furn_fountain{right:14%!important;bottom:7%!important;width:24%!important;height:8%!important;opacity:.68!important;}

  .home-pet-track{opacity:.96!important;z-index:5!important;}
  .home-pet-track.lamb-track{left:3%!important;right:56%!important;bottom:12%!important;}
  .home-pet-track.butterfly-track{left:58%!important;right:3%!important;top:32%!important;}
  .home-pet-track.dove-track{left:4%!important;right:61%!important;top:36%!important;}
  .home-pet-track.lamb-track .animated-shop-item{min-width:clamp(110px,32vw,166px)!important;animation-duration:20s!important;}
  .home-pet-track.butterfly-track .animated-shop-item{min-width:clamp(104px,30vw,158px)!important;animation-duration:15s!important;}
  .home-pet-track.dove-track .animated-shop-item{min-width:clamp(96px,28vw,148px)!important;animation-duration:16s!important;}
  .home-pet-track.lamb-track .home-game-item.pet_lamb{width:clamp(62px,16vw,84px)!important;height:clamp(48px,13vw,66px)!important;}
  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{width:clamp(46px,13vw,66px)!important;height:clamp(44px,12vw,62px)!important;}
  .home-pet-track.dove-track .home-game-item.pet_dove{width:clamp(50px,14vw,70px)!important;height:clamp(44px,12vw,62px)!important;}
  .home-game-item.pet_lamb .home-game-body{left:7%!important;right:18%!important;bottom:14px!important;height:45%!important;border-radius:58% 46% 40% 44%!important;background:radial-gradient(circle at 16% 20%,#fff 0 8%,transparent 9%),radial-gradient(circle at 33% 11%,#fff 0 10%,transparent 11%),radial-gradient(circle at 54% 21%,#fff 0 9%,transparent 10%),radial-gradient(circle at 75% 31%,#fff 0 8%,transparent 9%),linear-gradient(180deg,#fffdf9,#eee2d2 58%,#bba991)!important;box-shadow:inset 0 5px 8px rgba(255,255,255,.86),0 7px 10px rgba(82,55,15,.12)!important;}
  .home-game-item.pet_lamb .home-game-body::after{left:-9px!important;top:16px!important;width:16px!important;height:14px!important;border-radius:56% 46% 50% 42%!important;background:#fbfaf7!important;box-shadow:0 0 0 1px rgba(116,88,55,.14),52px -6px 0 -4px rgba(255,255,255,.92),58px 7px 0 -6px #d8c6b2!important;}
  .home-game-item.pet_lamb .home-game-detail.wing-left{right:5%!important;top:30%!important;width:27px!important;height:22px!important;border-radius:54% 48% 44% 42%!important;background:radial-gradient(circle at 34% 30%,#fff 0 25%,#eee3d3 27% 68%,#c3af97 100%)!important;}
  .home-game-item.pet_lamb .home-game-detail.wing-left::before{right:2px!important;top:-8px!important;width:10px!important;height:15px!important;border-radius:70% 38%!important;background:#cab59c!important;box-shadow:-15px 4px 0 -1px #cab59c!important;}
  .home-game-item.pet_lamb .home-game-icon::before,.home-game-item.pet_lamb .home-game-icon::after{height:16px!important;background:#6f5a47!important;box-shadow:11px 0 0 #836e58!important;}
  .home-game-item.pet_butterfly .home-game-body::before,.home-game-item.pet_butterfly .home-game-body::after{width:28px!important;height:32px!important;background:radial-gradient(circle at 34% 28%,rgba(255,255,255,.96) 0 4px,transparent 5px),radial-gradient(circle at 70% 72%,rgba(255,255,255,.82) 0 3px,transparent 4px),linear-gradient(145deg,#f9a8d4,#7dd3fc 54%,#7c3aed)!important;box-shadow:inset 0 7px 10px rgba(255,255,255,.5),0 6px 10px rgba(82,55,15,.14)!important;}
  .home-game-item.pet_butterfly .home-game-detail.wing-left,.home-game-item.pet_butterfly .home-game-detail.wing-right{width:20px!important;height:24px!important;opacity:.9!important;}
  .home-game-item.pet_dove .home-game-body{left:23%!important;right:16%!important;bottom:16px!important;height:38%!important;border-radius:62% 72% 56% 52%!important;background:linear-gradient(145deg,#fff,#f8fdff 56%,#c7e5f7)!important;}
  .home-game-item.pet_dove .home-game-detail.wing-left,.home-game-item.pet_dove .home-game-detail.wing-right{width:31px!important;height:22px!important;background:linear-gradient(145deg,#fff,#edf8ff 70%,#c7e5f7)!important;}
  /* Home rebalance: calmer transformation, proportional water, refined gates */
  @keyframes homeGentleReveal{0%{opacity:.66;transform:translateY(10px) scale(.985);filter:blur(.35px) brightness(1.04)}42%{opacity:.9;transform:translateY(2px) scale(1.005);filter:blur(.08px) brightness(1.05)}100%{opacity:1;transform:translateY(0) scale(1);filter:none}}
  @keyframes homeSoftBlessing{0%{opacity:0;transform:scale(.96)}28%{opacity:.58}76%{opacity:.34;transform:scale(1.015)}100%{opacity:0;transform:scale(1.04)}}
  @keyframes waterFallReal{0%{background-position:0 0;transform:translateY(-8px);opacity:.92}100%{background-position:0 28px;transform:translateY(8px);opacity:1}}
  @keyframes gateSoftShimmer{0%,100%{filter:drop-shadow(0 4px 8px rgba(138,90,8,.12))}50%{filter:drop-shadow(0 5px 12px rgba(240,203,118,.25))}}
  .home-room-transforming .home-cardboard,.home-room-transforming .home-little-shelter,.home-room-transforming .home-apartment,.home-room-transforming .home-house,.home-room-transforming .home-mansion,.home-room-transforming .home-palace,.home-room-transforming .home-chapel{animation:homeGentleReveal 4.15s cubic-bezier(.22,.7,.22,1) forwards!important;clip-path:none!important;transform-origin:50% 100%;}
  .home-transform-overlay{background:radial-gradient(circle at 50% 38%,rgba(255,255,255,.58),rgba(255,248,223,.20) 30%,transparent 68%)!important;animation:homeSoftBlessing 4.15s ease-in-out forwards!important;}
  .home-transform-overlay::before{left:50%!important;top:52%!important;width:190px!important;height:90px!important;border-color:rgba(240,203,118,.18)!important;box-shadow:0 0 14px rgba(255,248,223,.28)!important;}
  .home-transform-overlay::after,.build-scaffold,.build-beam,.builder-angel,.build-spark{display:none!important;}

  .home-gate{bottom:19%!important;width:76px!important;height:128px!important;border:2px solid rgba(154,107,22,.42)!important;border-bottom:none!important;background:linear-gradient(90deg,rgba(255,248,223,.16),rgba(240,203,118,.22),rgba(255,248,223,.08));box-shadow:inset 0 0 10px rgba(255,248,223,.32),0 8px 16px rgba(82,55,15,.08);animation:gateSoftShimmer 5.5s ease-in-out infinite;}
  .home-gate.left{left:4px!important;border-radius:54px 20px 0 0!important;transform:rotate(-3deg)!important;}
  .home-gate.right{right:4px!important;border-radius:20px 54px 0 0!important;transform:rotate(3deg)!important;}
  .home-gate::before{content:"";position:absolute;left:50%;top:-9px;width:14px;height:14px;border-radius:50%;transform:translateX(-50%);background:radial-gradient(circle,#fff8df,#f0c75e 62%,#9a6b16);box-shadow:0 0 10px rgba(240,203,118,.55);}
  .home-gate::after{inset:13px 14px 0!important;border-left:2px solid rgba(154,107,22,.38)!important;border-right:2px solid rgba(154,107,22,.38)!important;background:repeating-linear-gradient(90deg,transparent 0 10px,rgba(201,144,18,.42) 10px 12px,transparent 12px 22px),linear-gradient(180deg,rgba(255,248,223,.22),transparent 54%);box-shadow:inset 0 12px 0 -10px rgba(255,248,223,.72),inset 0 42px 0 -40px rgba(201,144,18,.42);}

  .home-slot.feature-slot.furn_waterfall-slot{right:6%!important;bottom:8%!important;z-index:5!important;}
  .home-slot.feature-slot .home-game-item.furn_waterfall{width:clamp(78px,20vw,108px)!important;height:clamp(88px,22vw,122px)!important;}
  .home-game-item.furn_waterfall{filter:drop-shadow(0 8px 10px rgba(35,55,60,.16))!important;animation:waterfallSettle 7s ease-in-out infinite!important;}
  .home-game-item.furn_waterfall .home-game-glow{opacity:.08!important;}
  .home-game-item.furn_waterfall .home-game-shadow{left:5%!important;right:5%!important;bottom:-3px!important;height:16px!important;opacity:.42!important;}
  .home-game-item.furn_waterfall .home-game-body{left:3%!important;right:3%!important;bottom:11px!important;height:76%!important;border-radius:46% 52% 34% 38% / 28% 33% 30% 32%!important;background:radial-gradient(circle at 20% 76%,#52686d 0 7px,transparent 8px),radial-gradient(circle at 82% 78%,#61777c 0 8px,transparent 9px),radial-gradient(circle at 22% 28%,#dfe8eb 0 10%,transparent 11%),linear-gradient(145deg,#eef3f4 0%,#a5b0b2 28%,#71888d 58%,#435f66 100%)!important;box-shadow:inset 7px 8px 12px rgba(255,255,255,.38),inset -9px -10px 13px rgba(24,44,50,.22),0 8px 10px rgba(82,55,15,.10)!important;clip-path:polygon(12% 15%,34% 4%,58% 2%,84% 18%,90% 38%,83% 86%,62% 97%,33% 92%,8% 78%,4% 38%)!important;}
  .home-game-item.furn_waterfall .home-game-body::before{left:3%!important;right:3%!important;top:7%!important;height:85%!important;background:radial-gradient(ellipse at 52% 10%,rgba(255,255,255,.96),rgba(212,239,249,.72) 24%,transparent 38%),radial-gradient(ellipse at 34% 65%,rgba(255,255,255,.34),transparent 46%)!important;box-shadow:-18px 35px 0 -13px rgba(255,255,255,.32),20px 39px 0 -14px rgba(255,255,255,.28)!important;opacity:.9!important;}
  .home-game-item.furn_waterfall .home-game-body::after{left:30%!important;right:27%!important;top:8%!important;bottom:2%!important;border-radius:999px!important;background:repeating-linear-gradient(180deg,rgba(255,255,255,.98) 0 5px,rgba(208,244,255,.92) 5px 11px,rgba(74,176,214,.66) 11px 19px,rgba(255,255,255,.68) 19px 24px)!important;background-size:100% 24px!important;box-shadow:-14px 17px 0 -8px rgba(255,255,255,.55),14px 24px 0 -9px rgba(255,255,255,.45),0 42px 0 -11px rgba(255,255,255,.35)!important;animation:waterFallReal .85s linear infinite!important;}
  .home-game-item.furn_waterfall .home-game-detail.base{left:3%!important;right:3%!important;bottom:0!important;height:17px!important;background:radial-gradient(ellipse at 50% 42%,rgba(84,101,91,.34),rgba(62,83,77,.18) 52%,transparent 74%)!important;}
  .home-game-item.furn_waterfall .home-game-detail.water{left:1%!important;right:1%!important;bottom:0!important;height:30px!important;background:radial-gradient(ellipse at 50% 38%,rgba(255,255,255,.95),rgba(144,222,246,.55) 45%,rgba(14,116,144,.18) 72%,transparent 82%)!important;box-shadow:0 -4px 10px rgba(255,255,255,.34),inset 0 2px 6px rgba(255,255,255,.42)!important;}
  .home-water-stream.furn_waterfall{right:8%!important;bottom:7%!important;width:30%!important;height:9%!important;opacity:.58!important;transform:rotate(-12deg)!important;}

  .home-slot.feature-slot .home-game-item.furn_fountain{width:clamp(62px,16vw,88px)!important;height:clamp(62px,16vw,92px)!important;}
  .home-game-item.furn_fountain{filter:drop-shadow(0 7px 10px rgba(38,80,105,.13))!important;}
  .home-game-item.furn_fountain .home-game-shadow{left:9%!important;right:9%!important;height:12px!important;opacity:.45!important;}
  .home-game-item.furn_fountain .home-game-body{left:9%!important;right:9%!important;bottom:7px!important;height:48%!important;border-radius:16px 16px 24px 24px!important;background:linear-gradient(180deg,#fff 0%,#dff6ff 30%,#8fcbe6 64%,#4e86a4 100%)!important;border:1.5px solid rgba(38,89,116,.22)!important;box-shadow:inset 0 6px 8px rgba(255,255,255,.86),inset 0 -10px 13px rgba(20,76,105,.20),0 7px 10px rgba(82,55,15,.09)!important;}
  .home-game-item.furn_fountain .home-game-body::before{left:6%!important;right:6%!important;top:5px!important;height:13px!important;background:radial-gradient(ellipse at 50% 40%,#fff,rgba(180,229,246,.96) 48%,rgba(30,111,150,.24))!important;box-shadow:0 20px 0 3px rgba(255,255,255,.24),0 22px 0 5px rgba(48,93,120,.14)!important;}
  .home-game-item.furn_fountain .home-game-body::after{left:24%!important;right:24%!important;bottom:-8px!important;height:12px!important;background:linear-gradient(180deg,#dff6ff,#467f9e)!important;}
  .home-game-item.furn_fountain .home-game-detail.water{left:13%!important;right:13%!important;top:-16%!important;height:54%!important;background:radial-gradient(circle at 50% 12%,#fff 0 12%,rgba(206,242,255,.94) 14% 38%,rgba(74,176,214,.56) 39% 68%,rgba(14,116,144,.13))!important;box-shadow:-16px 28px 0 -10px rgba(125,211,252,.58),16px 28px 0 -10px rgba(125,211,252,.54),0 39px 0 -11px rgba(45,139,175,.34)!important;animation:fountainBubble 1.45s ease-in-out infinite!important;}
  .home-game-item.furn_fountain .home-game-detail.water::before{left:49%!important;top:-23px!important;width:5px!important;height:42px!important;box-shadow:-13px 13px 0 -1px rgba(125,211,252,.5),13px 13px 0 -1px rgba(125,211,252,.46),0 7px 0 1px rgba(255,255,255,.45)!important;}
  .home-game-item.furn_fountain .home-game-detail.water::after{top:-28px!important;width:7px!important;height:7px!important;box-shadow:-18px 20px 0 -3px rgba(125,211,252,.68),18px 18px 0 -3px rgba(125,211,252,.68),-8px 44px 0 -4px rgba(255,255,255,.75),8px 47px 0 -4px rgba(125,211,252,.6)!important;}
  .home-water-stream.furn_fountain{right:16%!important;bottom:8%!important;width:18%!important;height:6%!important;opacity:.52!important;}

  @keyframes homeStreamFlow{0%,100%{transform:translateX(-2px) scaleX(.94);opacity:.42}50%{transform:translateX(3px) scaleX(1.04);opacity:.7}}
  .prayer-page-calm{position:relative;}
  .prayer-page-calm::before{content:"";position:absolute;left:10%;right:10%;top:8px;height:120px;border-radius:999px;background:radial-gradient(ellipse,rgba(255,248,223,.46),rgba(255,255,255,0) 70%);pointer-events:none;}
  .prayer-light{position:fixed;width:22px;height:22px;border-radius:999px;z-index:9997;pointer-events:none;background:radial-gradient(circle,#fff 0%,#fff8df 44%,rgba(240,203,118,.62) 76%,rgba(240,203,118,0) 100%);box-shadow:0 0 12px rgba(240,203,118,.42),0 0 22px rgba(255,248,223,.46);animation:prayerTravel 1.15s ease-in-out forwards;}
  .prayer-light::after{content:"";position:absolute;left:50%;top:50%;width:8px;height:8px;border-radius:50%;background:rgba(255,255,255,.9);transform:translate(-50%,-50%);}
  .angel-message{position:fixed;left:50%;top:74px;bottom:auto;z-index:10050;pointer-events:none;transform:translateX(-50%);background:rgba(255,253,246,.98);border:1px solid rgba(184,147,75,.22);border-radius:999px;padding:9px 15px;color:var(--gold-deep);font-weight:900;font-size:13.5px;box-shadow:0 6px 18px rgba(138,90,8,.10);animation:angelFloat 2.2s ease-in-out forwards;white-space:nowrap;max-width:calc(100vw - 28px);overflow:hidden;text-overflow:ellipsis;}
  .prayer-section-tabs{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;}
  .prayer-tab-card{background:rgba(255,253,246,.84);border:1.5px solid var(--card-border);border-radius:16px;padding:12px 9px;color:var(--text-mid);font-family:'Nunito',sans-serif;cursor:pointer;text-align:center;box-shadow:0 2px 10px rgba(138,90,8,.04);transition:background .18s,border-color .18s,box-shadow .18s;}
  .prayer-tab-card.active{background:linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,248,223,.74));border-color:rgba(184,147,75,.45);box-shadow:0 5px 16px rgba(138,90,8,.08);}
  .prayer-status-chip{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;background:rgba(246,250,255,.76);border:1px solid rgba(125,174,212,.20);padding:4px 9px;color:#497093;font-size:11.5px;font-weight:900;}
  .pray-now-btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;background:var(--gold-grad);border:1px solid var(--gold);border-radius:13px;padding:10px 12px;color:var(--text-dark);font-family:'Nunito',sans-serif;font-weight:900;cursor:pointer;min-height:42px;box-shadow:0 5px 16px rgba(180,130,20,.12);transition:transform .16s ease,background .18s ease,border-color .18s ease,box-shadow .18s ease;}
  .pray-now-btn:active{transform:scale(.97);}
  .pray-now-btn.prayed{background:linear-gradient(135deg,rgba(246,250,255,.96),rgba(219,234,254,.80));border-color:rgba(91,143,184,.48);color:#315b78;box-shadow:0 4px 14px rgba(65,95,125,.10);}
  .pray-check-icon{width:20px;height:20px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;background:rgba(255,253,246,.92);border:1.5px solid rgba(201,144,18,.42);color:var(--gold-deep);font-size:12px;line-height:1;flex-shrink:0;}
  .pray-now-btn.prayed .pray-check-icon{background:#f8fbff;border-color:rgba(91,143,184,.50);color:#315b78;}
  .friend-card{background:rgba(255,253,246,.90);border:1.5px solid rgba(184,147,75,.16);border-radius:16px;padding:14px;box-shadow:0 2px 10px rgba(138,90,8,.04);}
  .friend-card.prayed{border-color:rgba(125,174,212,.25);background:linear-gradient(145deg,rgba(255,253,246,.96),rgba(246,250,255,.54));}
	  .gold-outline-card{background:linear-gradient(135deg,rgba(255,252,210,.94),rgba(255,248,223,.88),rgba(255,253,246,.94))!important;border:2px solid var(--gold)!important;box-shadow:0 4px 18px rgba(180,130,20,.16)!important;}
	  .prayer-friends-card{background:linear-gradient(135deg,rgba(255,252,210,.94),rgba(255,248,223,.88),rgba(255,253,246,.94))!important;border:2.5px solid var(--gold)!important;box-shadow:0 4px 18px rgba(180,130,20,.16)!important;}
  .prayer-friend-panel{background:rgba(255,253,246,.66);border:1px solid rgba(184,147,75,.16);border-radius:15px;padding:11px;display:flex;flex-direction:column;gap:8px;}
  .prayer-friend-panel-title{font-size:12px;text-transform:uppercase;letter-spacing:1.2px;color:var(--text-faint);font-weight:900;}
  .prayer-friend-row{display:flex;align-items:center;justify-content:space-between;gap:10px;background:rgba(255,255,255,.58);border:1px solid rgba(125,174,212,.15);border-radius:13px;padding:10px;}
  .prayer-friend-row.compact-friend-row{align-items:center;flex-direction:row;gap:8px;padding:8px 9px;}
  .compact-friend-identity{display:flex;align-items:center;gap:7px;min-width:0;flex:1;}
  .compact-friend-identity .prayer-friend-name{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .compact-friend-actions{display:flex;align-items:center;justify-content:flex-end;gap:6px;flex-shrink:0;}
  .accountability-partner-scroll{display:flex;gap:9px;overflow-x:auto;scroll-snap-type:x mandatory;overscroll-behavior-x:contain;padding:2px 1px 8px;scrollbar-width:thin;scrollbar-color:rgba(125,174,212,.32) transparent;-webkit-overflow-scrolling:touch;}
  .accountability-partner-slide{min-width:min(84%,280px);scroll-snap-align:start;background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(236,253,245,.62));border:1.5px solid rgba(125,174,212,.24);border-radius:15px;padding:11px;box-shadow:0 4px 12px rgba(65,95,125,.06);}
  .partner-focus-label{display:block;margin-top:10px;font-size:11px;font-weight:900;color:var(--text-faint);text-transform:uppercase;letter-spacing:.5px;}
  .partner-focus-select{margin-top:5px;min-height:38px!important;padding:8px 10px!important;font-size:13px!important;color:var(--text-dark)!important;background:rgba(255,255,255,.72)!important;}
  .partner-custom-focus-input{margin-top:7px;min-height:40px!important;padding:9px 10px!important;font-size:13px!important;text-transform:none!important;letter-spacing:0!important;}
  .partner-focus-complete-btn{width:100%;margin-top:9px;min-height:40px;border-radius:12px;border:1.5px solid rgba(91,143,184,.54);background:linear-gradient(135deg,rgba(219,234,254,.9),rgba(246,250,255,.96));color:#315b78;font-family:'Nunito',sans-serif;font-size:12.5px;font-weight:900;cursor:pointer;box-shadow:0 4px 12px rgba(65,95,125,.08);transition:transform .16s ease,background .18s ease,border-color .18s ease,box-shadow .18s ease;}
  .partner-focus-complete-btn:active{transform:scale(.98);}
  .partner-focus-complete-btn.completed{background:linear-gradient(135deg,rgba(255,248,223,.98),rgba(246,218,146,.78));border-color:rgba(201,144,18,.66);color:var(--gold-deep);box-shadow:0 5px 16px rgba(180,130,20,.16);cursor:default;}
  .partner-focus-complete-btn:disabled:not(.completed){opacity:.55;cursor:default;}
  .partner-focus-undo-btn{width:100%;margin-top:6px;border:none;background:transparent;color:var(--text-soft);font-family:'Nunito',sans-serif;font-size:12px;font-weight:800;cursor:pointer;padding:6px 8px;border-radius:10px;}
  .partner-focus-undo-btn:active{background:rgba(255,248,223,.62);transform:scale(.99);}
  .prayer-friend-name{font-size:13.5px;font-weight:900;color:var(--text-dark);line-height:1.25;overflow:hidden;text-overflow:ellipsis;}
  .prayer-friend-copy{font-size:12.5px;color:var(--text-soft);line-height:1.35;overflow:hidden;text-overflow:ellipsis;}
  .soft-action-btn{border:1px solid rgba(184,147,75,.24);background:rgba(255,248,223,.78);border-radius:12px;color:var(--gold-deep);font-family:'Nunito',sans-serif;font-weight:900;font-size:12.5px;padding:8px 10px;min-height:36px;cursor:pointer;white-space:nowrap;}
  .soft-action-btn.muted{background:rgba(255,253,246,.78);color:var(--text-faint);border-color:rgba(184,147,75,.14);}
  .soft-action-btn.friend-alert-btn{background:linear-gradient(135deg,rgba(255,248,223,.98),rgba(246,218,146,.72))!important;border-color:rgba(201,144,18,.62)!important;color:var(--gold-deep)!important;box-shadow:0 5px 16px rgba(180,130,20,.16)!important;}
  .friend-alert-badge{display:inline-flex;align-items:center;justify-content:center;min-width:19px;height:19px;border-radius:999px;background:rgba(255,253,246,.94);border:1px solid rgba(201,144,18,.42);color:var(--gold-deep);font-size:11px;font-weight:900;line-height:1;margin-left:4px;}
  .blessing-options-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(76px,1fr));gap:7px;}
  .soft-action-btn.blessing-option-btn{position:relative;overflow:hidden;min-height:52px;padding:8px 6px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;font-size:11.5px;line-height:1.15;white-space:normal;text-align:center;overflow-wrap:anywhere;transition:transform .16s ease,background .18s ease,border-color .18s ease,box-shadow .18s ease;}
  .soft-action-btn.blessing-option-btn:active{transform:scale(.97);}
  .soft-action-btn.blessing-option-btn.friend-alert-btn::after{content:"";position:absolute;inset:auto 8px 6px;height:2px;border-radius:999px;background:rgba(201,144,18,.36);}
  .blessing-option-btn span:last-child{display:block;max-width:100%;}
  .spirit-friends-dashboard{position:relative;gap:14px!important;}
  .spirit-friends-dashboard::before{content:"";position:absolute;inset:-18px -10px auto;height:190px;border-radius:0 0 999px 999px;background:linear-gradient(180deg,rgba(255,248,223,.34),rgba(255,253,246,.18),rgba(255,255,255,0));pointer-events:none;z-index:0;}
  .spirit-friends-dashboard::after{content:"";position:absolute;top:18px;right:18px;width:72px;height:72px;border-radius:50%;background:radial-gradient(circle,rgba(255,226,156,.22),rgba(255,226,156,0) 68%);pointer-events:none;z-index:0;}
  .spirit-friends-dashboard > *{position:relative;z-index:1;}
  .spirit-friends-dashboard > .sacred-card{border-color:rgba(231,207,159,.42)!important;outline:none!important;background:linear-gradient(145deg,rgba(255,253,246,.96),rgba(255,250,236,.72))!important;box-shadow:0 12px 26px rgba(91,67,28,.06),inset 0 1px 0 rgba(255,255,255,.78)!important;}
  .spirit-friends-hero-card{order:1;border-radius:28px!important;padding:20px!important;overflow:hidden;}
  .spirit-friends-hero-card::before{content:"";position:absolute;right:-34px;top:-38px;width:145px;height:145px;border-radius:50%;background:radial-gradient(circle,rgba(255,226,156,.26),rgba(255,226,156,0) 70%);pointer-events:none;}
  .spirit-friends-hero-card::after{content:"";position:absolute;left:18px;bottom:16px;width:54px;height:54px;border-radius:50%;background:radial-gradient(circle,rgba(226,244,232,.34),rgba(226,244,232,0) 72%);pointer-events:none;}
  .accountability-primary-card{order:3;border-radius:26px!important;background:linear-gradient(145deg,rgba(255,253,246,.97),rgba(251,247,235,.76))!important;box-shadow:0 14px 30px rgba(82,105,63,.07),inset 0 1px 0 rgba(255,255,255,.78)!important;overflow:hidden;}
  .accountability-primary-card::before{content:"";position:absolute;right:-34px;top:20px;width:138px;height:138px;border-radius:50%;background:radial-gradient(circle,rgba(188,222,161,.28),rgba(188,222,161,0) 68%);pointer-events:none;}
  .community-feed-card{order:4;border-radius:24px!important;}
  .spirit-friends-tools-card{order:2;border-radius:24px!important;}
  .blessing-community-card{order:5;border-radius:24px!important;}
  .spirit-friends-dashboard .prayer-friend-panel{border:1px solid rgba(231,207,159,.28)!important;background:rgba(255,253,246,.72)!important;border-radius:20px!important;box-shadow:inset 0 1px 0 rgba(255,255,255,.76)!important;}
  .friends-garden-panel{padding:13px!important;}
  .friends-garden-panel .scroll-list.compact{display:grid!important;grid-template-columns:repeat(auto-fit,minmax(210px,1fr))!important;max-height:420px!important;gap:10px!important;}
  .spirit-friends-dashboard .prayer-friend-row{border:1px solid rgba(231,207,159,.22)!important;background:rgba(255,255,255,.66)!important;border-radius:18px!important;box-shadow:0 7px 18px rgba(91,67,28,.045),inset 0 1px 0 rgba(255,255,255,.78)!important;}
  .spirit-friends-dashboard .compact-friend-row{min-height:74px;padding:12px!important;background:linear-gradient(145deg,rgba(255,253,246,.96),rgba(248,251,245,.72))!important;}
  .spirit-friends-dashboard .compact-friend-identity > div:first-child{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:linear-gradient(145deg,rgba(255,248,223,.96),rgba(226,244,232,.86));box-shadow:0 5px 14px rgba(82,105,63,.10);font-size:18px!important;}
  .spirit-friends-dashboard .compact-friend-actions{gap:7px!important;}
  .community-feed-glow-line{height:1px;border-radius:999px;background:linear-gradient(90deg,transparent,rgba(201,144,18,.32),rgba(218,180,255,.28),transparent);}
  .community-feed-card .scroll-list.compact{max-height:310px!important;}
  .community-feed-card .prayer-friend-row{align-items:flex-start!important;}
  .accountability-primary-card .accountability-partner-scroll{gap:12px!important;padding:4px 2px 10px!important;}
  .accountability-primary-card .accountability-partner-slide{min-width:min(88%,330px)!important;border:none!important;border-radius:22px!important;background:linear-gradient(150deg,rgba(255,253,246,.98),rgba(236,253,245,.75),rgba(255,248,223,.55))!important;box-shadow:0 12px 26px rgba(82,105,63,.10),inset 0 1px 0 rgba(255,255,255,.80)!important;padding:10px!important;}
  .accountability-primary-card .partner-focus-complete-btn:not(.completed){background:linear-gradient(135deg,rgba(246,250,255,.96),rgba(219,234,254,.76))!important;border-color:rgba(91,143,184,.42)!important;color:#315b78!important;}
  .blessing-community-card .blessing-options-grid{grid-template-columns:repeat(auto-fit,minmax(96px,1fr))!important;gap:9px!important;}
  .blessing-community-card .blessing-option-btn{border:1px solid rgba(231,207,159,.28)!important;border-radius:18px!important;background:rgba(255,253,246,.78)!important;box-shadow:0 7px 16px rgba(91,67,28,.045),inset 0 1px 0 rgba(255,255,255,.76)!important;}
  .spirit-friends-dashboard .soft-action-btn{border-color:rgba(184,147,75,.18)!important;box-shadow:0 6px 14px rgba(91,67,28,.05);transition:transform .18s ease,box-shadow .18s ease,background .18s ease;}
  .spirit-friends-dashboard .soft-action-btn:hover{transform:translateY(-1px);box-shadow:0 10px 20px rgba(91,67,28,.08);}
  .friend-notification-bell{display:inline-flex!important;align-items:center!important;gap:5px!important;}
  .friend-notification-bell.has-new{background:linear-gradient(135deg,rgba(255,248,223,.98),rgba(246,218,146,.80))!important;color:var(--gold-deep)!important;box-shadow:0 0 0 1px rgba(201,144,18,.24),0 8px 22px rgba(201,144,18,.16)!important;animation:softGoldPulse 2.8s ease-in-out infinite!important;}
  .friend-notification-center-card{order:1.5;border-radius:24px!important;background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(255,250,236,.72))!important;}
  .friend-notification-list{display:flex;flex-direction:column;gap:9px;max-height:330px;overflow-y:auto;-webkit-overflow-scrolling:touch;}
  .friend-notification-row{display:flex;align-items:flex-start;gap:10px;padding:11px;border-radius:18px;background:rgba(255,255,255,.58);box-shadow:inset 0 1px 0 rgba(255,255,255,.72);transition:background .18s ease,box-shadow .18s ease,transform .18s ease;}
  .friend-notification-row.unread{background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(255,248,223,.74));box-shadow:0 10px 22px rgba(201,144,18,.11),0 0 0 1px rgba(201,144,18,.18),inset 0 1px 0 rgba(255,255,255,.82);}
  .friend-notification-icon{width:32px;height:32px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;background:linear-gradient(145deg,rgba(255,248,223,.98),rgba(255,253,246,.82));box-shadow:0 5px 14px rgba(138,90,8,.08);flex-shrink:0;}
  .friend-notification-title{font-size:13.5px;font-weight:850;color:var(--text-dark);line-height:1.25;}
  .friend-notification-copy{font-size:12.5px;color:var(--text-soft);line-height:1.35;margin-top:2px;}
  .friend-notification-time{font-size:11.5px;font-weight:850;color:var(--gold-deep);white-space:nowrap;flex-shrink:0;}
  .community-feed-collapsed{width:100%;margin-top:12px;border:none;border-radius:18px;padding:12px 14px;display:flex;align-items:center;justify-content:space-between;gap:10px;background:linear-gradient(145deg,rgba(255,255,255,.70),rgba(255,253,246,.54));color:var(--text-dark);font-family:'Nunito',sans-serif;font-size:13.5px;font-weight:850;cursor:pointer;box-shadow:0 8px 18px rgba(91,67,28,.05),inset 0 1px 0 rgba(255,255,255,.76);}
  .community-feed-collapsed.unread{background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(255,248,223,.76));box-shadow:0 0 0 1px rgba(201,144,18,.18),0 10px 24px rgba(201,144,18,.12),inset 0 1px 0 rgba(255,255,255,.84);}
  .unread-friend-activity{box-shadow:0 0 0 1px rgba(201,144,18,.20),0 10px 24px rgba(201,144,18,.12),inset 0 1px 0 rgba(255,255,255,.82)!important;background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(255,248,223,.70))!important;}
  .has-friend-activity{box-shadow:0 0 0 1px rgba(201,144,18,.22),0 14px 30px rgba(201,144,18,.13),inset 0 1px 0 rgba(255,255,255,.86)!important;}
  .friend-activity-pill{display:inline-flex;align-items:center;margin-top:4px;padding:3px 8px;border-radius:999px;background:rgba(255,248,223,.86);color:var(--gold-deep);font-size:10.8px;font-weight:900;line-height:1.15;box-shadow:0 0 0 1px rgba(201,144,18,.16);}
  .accountability-update-card{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:10px;align-items:center;margin-bottom:12px;padding:12px;border-radius:20px;background:linear-gradient(145deg,rgba(236,253,245,.82),rgba(255,248,223,.66));box-shadow:0 10px 24px rgba(82,105,63,.10),inset 0 1px 0 rgba(255,255,255,.78);}
  .accountability-update-title{font-size:13.5px;font-weight:850;color:var(--text-dark);line-height:1.3;}
  @keyframes softGoldPulse{0%,100%{filter:brightness(1);transform:translateY(0)}50%{filter:brightness(1.04);transform:translateY(-1px)}}
  @keyframes friendGardenDrift{0%,100%{transform:translate3d(0,0,0);opacity:.52}50%{transform:translate3d(-10px,8px,0);opacity:.82}}
  /* Prayer Circle visual refinement: partner first, soft inbox, friend avatars. */
  .prayer-circle-subtitle{margin:0;color:var(--text-soft);font-size:13.5px;line-height:1.45;font-weight:500;}
  .spirit-friends-dashboard > .sacred-card{border-color:rgba(231,207,159,.22)!important;box-shadow:0 16px 34px rgba(91,67,28,.055),inset 0 1px 0 rgba(255,255,255,.80)!important;}
  .spirit-friends-tools-card{order:4!important;}
  .accountability-primary-card{order:2!important;}
  .community-feed-card{order:3!important;}
  .blessing-community-card{order:5!important;}
  .community-feed-card{background:radial-gradient(circle at 12% 0%,rgba(255,248,223,.45),transparent 32%),linear-gradient(145deg,rgba(255,253,246,.97),rgba(255,250,236,.72))!important;}
  .community-feed-card h3::before{content:"💌 ";font-family:'Nunito',sans-serif;font-size:15px;}
  .community-feed-card .prayer-friend-row{position:relative;padding-left:13px!important;}
  .community-feed-card .prayer-friend-row::before{content:"";position:absolute;left:0;top:12px;bottom:12px;width:3px;border-radius:999px;background:linear-gradient(180deg,rgba(201,144,18,.58),rgba(218,180,255,.22));}
  .friend-spirit-avatar{width:38px;height:38px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;overflow:hidden;flex:0 0 38px;background:radial-gradient(circle,rgba(255,248,223,.96),rgba(236,253,245,.72));box-shadow:0 7px 18px rgba(82,105,63,.10),inset 0 1px 0 rgba(255,255,255,.86);}
  .friend-spirit-avatar img{width:120%;height:120%;object-fit:contain;filter:drop-shadow(0 5px 8px rgba(91,67,28,.10));}
  .friend-spirit-avatar.partner{width:44px;height:44px;flex-basis:44px;background:radial-gradient(circle,rgba(255,248,223,.98),rgba(236,253,245,.86));}
  .friend-spirit-avatar.small{width:32px;height:32px;flex-basis:32px;}
  .friends-garden-panel .compact-friend-row{background:radial-gradient(circle at 0% 0%,rgba(255,248,223,.45),transparent 34%),linear-gradient(145deg,rgba(255,253,246,.96),rgba(248,251,245,.78))!important;}
  .accountability-primary-card{background:radial-gradient(circle at 90% 5%,rgba(188,222,161,.28),transparent 33%),linear-gradient(145deg,rgba(255,253,246,.98),rgba(251,247,235,.78))!important;}
  .accountability-primary-card h3{font-weight:600!important;}
  .blessing-community-card h3{font-weight:600!important;}
  .spirit-friends-dashboard .prayer-friend-panel-title{font-weight:650!important;letter-spacing:1px!important;color:#6e5322!important;}
  .spirit-friends-dashboard .prayer-friend-name{font-weight:700!important;}
  .spirit-friends-dashboard .soft-action-btn{font-weight:700!important;}
  @media (max-width:560px){
    .spirit-friends-hero-card{padding:18px!important;}
    .friends-garden-panel .scroll-list.compact{grid-template-columns:1fr!important;max-height:360px!important;}
    .spirit-friends-dashboard .compact-friend-row{grid-template-columns:1fr!important;}
    .spirit-friends-dashboard .compact-friend-actions{width:100%;justify-content:center!important;}
    .blessing-community-card .blessing-options-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
    .accountability-update-card{grid-template-columns:1fr!important;}
    .accountability-update-card .soft-action-btn{width:100%!important;}
    .friend-notification-row{padding:10px!important;}
  }
  .friend-checkbox-list{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:7px;}
  .friend-checkbox-row{display:flex;align-items:center;gap:8px;background:rgba(255,255,255,.54);border:1px solid rgba(125,174,212,.14);border-radius:12px;padding:9px 10px;color:var(--text-mid);font-size:13px;font-weight:800;cursor:pointer;min-width:0;}
  .friend-checkbox-row input{width:17px;height:17px;accent-color:var(--gold);flex-shrink:0;}
  .friend-checkbox-row span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}




  /* Prayer Circle: clearer Add Friend / Friends tools and cleaner Add Friend form. */
  .spirit-friends-dashboard .prayer-circle-tool-grid{
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:10px!important;
    width:100%!important;
    max-width:430px!important;
    margin:0 auto!important;
  }
  .spirit-friends-dashboard .prayer-circle-tool-grid .soft-action-btn{
    width:100%!important;
    min-width:0!important;
    min-height:54px!important;
    justify-content:center!important;
    border-radius:18px!important;
    white-space:nowrap!important;
    text-align:center!important;
    padding:12px 10px!important;
  }
  .spirit-friends-dashboard .spirit-friends-tools-card{
    padding:14px!important;
  }
  .spirit-friends-dashboard .add-friend-panel{
    padding:16px!important;
    gap:12px!important;
    border:1px solid rgba(184,147,75,.22)!important;
    border-radius:22px!important;
    background:linear-gradient(145deg,rgba(255,253,246,.92),rgba(255,255,255,.72))!important;
  }
  .spirit-friends-dashboard .add-friend-panel .prayer-friend-panel-title{
    margin:0 0 2px!important;
    padding:0!important;
    line-height:1.2!important;
    letter-spacing:.08em!important;
  }
  .spirit-friends-dashboard .add-friend-panel .friend-code-chip,
  .app-shell .add-friend-panel .friend-code-chip{
    width:100%!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    min-height:44px!important;
    padding:10px 12px!important;
    border-radius:15px!important;
    border:2px solid #c99012!important;
    background:linear-gradient(180deg,#f3cf78,#d7a73d)!important;
    color:#35281d!important;
    font-weight:900!important;
    line-height:1.25!important;
    white-space:normal!important;
    text-align:center!important;
    box-shadow:0 7px 16px rgba(184,134,31,.16),inset 0 1px 0 rgba(255,255,255,.58)!important;
  }
  .spirit-friends-dashboard .add-friend-panel .sg-input,
  .app-shell .add-friend-panel .sg-input{
    min-height:46px!important;
    border:2px solid #c99012!important;
    border-radius:14px!important;
    background:#fffdf8!important;
    color:#35281d!important;
    padding:12px 13px!important;
    box-shadow:none!important;
  }
  .spirit-friends-dashboard .add-friend-panel .sg-input:focus,
  .app-shell .add-friend-panel .sg-input:focus{
    border-color:#c99012!important;
    box-shadow:0 0 0 3px rgba(201,144,18,.20)!important;
  }
  .spirit-friends-dashboard .add-friend-panel .gold-btn{
    min-height:46px!important;
    border-radius:14px!important;
    padding-left:16px!important;
    padding-right:16px!important;
  }
  @media(max-width:390px){
    .spirit-friends-dashboard .prayer-circle-tool-grid{gap:8px!important;}
    .spirit-friends-dashboard .prayer-circle-tool-grid .soft-action-btn{min-height:50px!important;font-size:13px!important;}
  }

  /* Final stage reveal and Spirit Friends layout fixes. */
  .stage-reveal-grown{
    font-family:'Cormorant Garamond',Georgia,serif!important;
    letter-spacing:.01em!important;
    font-weight:700!important;
  }
  .stage-reveal-line,
  .stage-reveal-desc{
    text-wrap:balance;
  }
  .spirit-friends-dashboard .spirit-friends-hero-card{order:1!important;}
  .spirit-friends-dashboard .spirit-friends-tools-card{order:2!important;}
  .spirit-friends-dashboard .community-feed-card{order:3!important;}
  .spirit-friends-dashboard .accountability-primary-card{order:4!important;}
  .spirit-friends-dashboard .blessing-community-card{order:5!important;}
  .spirit-friends-dashboard .friends-garden-card{order:6!important;}
  @media (max-width:560px){
    .stage-reveal-grown{
      font-size:20px!important;
      line-height:1.18!important;
      margin-bottom:8px!important;
    }
    .stage-reveal-line,
    .stage-reveal-desc{
      max-width:calc(100vw - 30px)!important;
      white-space:nowrap!important;
      text-wrap:normal!important;
      overflow-wrap:normal!important;
      word-break:normal!important;
      letter-spacing:-.01em!important;
    }
    .stage-reveal-line{
      font-size:clamp(12px,3.32vw,13.4px)!important;
      line-height:1.35!important;
    }
    .stage-reveal-desc{
      font-size:clamp(11.5px,3.16vw,12.8px)!important;
      line-height:1.35!important;
    }
  }

  /* Spirit Friends calm premium pass */
  .spirit-friends-dashboard{gap:13px!important;}
  .spirit-friends-dashboard::before{opacity:.42!important;height:160px!important;}
  .spirit-friends-dashboard::after{opacity:.55!important;}
  .spirit-friends-dashboard > .sacred-card{border:1px solid rgba(231,207,159,.34)!important;box-shadow:0 10px 24px rgba(91,67,28,.045),inset 0 1px 0 rgba(255,255,255,.78)!important;}
  .spirit-friends-hero-card{border-color:rgba(201,144,18,.36)!important;background:linear-gradient(150deg,rgba(255,253,246,.98),rgba(255,249,229,.74),rgba(246,250,255,.36))!important;}
  .spirit-friends-hero-card .soft-action-btn{min-width:104px;justify-content:center;}
  .spirit-friends-tools-card{background:rgba(255,253,246,.76)!important;padding:13px!important;}
  .spirit-friends-tools-card .prayer-friend-panel{margin-top:0!important;}
  .spirit-friends-dashboard .prayer-friend-panel{padding:13px!important;border-color:rgba(231,207,159,.20)!important;background:linear-gradient(145deg,rgba(255,253,246,.78),rgba(255,255,255,.54))!important;}
  .spirit-friends-dashboard .prayer-friend-panel-title{font-size:13px!important;letter-spacing:.02em;color:var(--text-dark)!important;margin-bottom:9px!important;}
  .spirit-friends-dashboard .prayer-friend-row{border-color:rgba(231,207,159,.16)!important;background:rgba(255,255,255,.58)!important;box-shadow:0 6px 16px rgba(91,67,28,.035),inset 0 1px 0 rgba(255,255,255,.78)!important;}
  .spirit-friends-dashboard .compact-friend-row{min-height:68px!important;}
  .community-feed-card{background:linear-gradient(145deg,rgba(255,253,246,.94),rgba(246,250,255,.42))!important;}
  .community-feed-glow-line{opacity:.55!important;}
  .accountability-primary-card{border-color:rgba(188,222,161,.30)!important;}
  .blessing-community-card{background:linear-gradient(145deg,rgba(255,253,246,.94),rgba(255,248,253,.42))!important;}
  .blessing-community-card .blessing-option-btn{min-height:58px!important;}
  @media (max-width:560px){
    .spirit-friends-hero-card .soft-action-btn{min-width:0;flex:1 1 96px;}
    .spirit-friends-dashboard > .sacred-card{border-radius:22px!important;}
    .spirit-friends-dashboard .prayer-friend-panel{padding:12px!important;}
  }
	  .prayer-filter-btn{background:rgba(255,253,246,.82);border:1px solid var(--card-border);border-radius:999px;padding:9px 12px;color:var(--text-mid);font-weight:900;cursor:pointer;font-family:'Nunito',sans-serif;font-size:13px;white-space:nowrap;transition:all .18s;}
	  .prayer-filter-btn.active{background:var(--gold-grad-s);border-color:rgba(201,144,18,.52);color:var(--gold-deep);box-shadow:0 4px 14px rgba(138,90,8,.08);}
	  .quick-prayer-box{background:linear-gradient(160deg,rgba(246,250,255,.72),rgba(255,253,246,.88));border:1px solid rgba(125,174,212,.20);border-radius:15px;padding:8px;display:flex;flex-direction:column;gap:8px;}
	  .quick-prayer-toggle{width:100%;display:grid;grid-template-columns:1fr auto auto;gap:8px;align-items:center;border:none;background:transparent;color:#4F3510;font-family:'Nunito',sans-serif;cursor:pointer;text-align:left;padding:4px 3px;}
	  .quick-prayer-toggle span:first-child{font-size:13px;font-weight:900;color:#4F3510;line-height:1.25;}
	  .quick-prayer-count{font-size:11.5px;color:#5f7f98;font-weight:900;background:rgba(219,234,254,.58);border:1px solid rgba(125,174,212,.18);border-radius:999px;padding:3px 7px;white-space:nowrap;}
	  .quick-prayer-chevron{font-size:16px;color:#5f7f98;transition:transform .2s;line-height:1;}
	  .quick-prayer-chevron.open{transform:rotate(180deg);}
	  .quick-prayer-summary{font-size:12.5px;color:#5f7f98;line-height:1.35;padding:0 3px 2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
	  .quick-prayer-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:7px;}
	  .quick-prayer-chip{display:flex;align-items:center;gap:7px;min-width:0;border:1px solid rgba(125,174,212,.20);border-radius:999px;background:rgba(255,255,255,.66);padding:8px 10px;color:#4F3510;font-size:13px;font-weight:800;line-height:1.15;cursor:pointer;transition:background .18s,border-color .18s,box-shadow .18s;}
	  .quick-prayer-chip.selected{background:linear-gradient(135deg,rgba(255,253,246,.96),rgba(219,234,254,.82));border-color:rgba(125,174,212,.42);box-shadow:0 3px 10px rgba(65,95,125,.06);}
	  .quick-prayer-chip input{width:15px;height:15px;accent-color:#5f7f98;flex-shrink:0;}
	  .quick-prayer-chip span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
	  .empty-state{background:linear-gradient(145deg,rgba(255,253,246,.92),rgba(255,248,223,.44));border:1px dashed rgba(184,147,75,.32);border-radius:18px;padding:18px 14px;text-align:center;color:var(--text-soft);}
  .empty-state-title{margin:0 0 5px;font-size:14px;font-weight:900;color:var(--gold-deep);}
	  .meaning-chip{display:inline-flex;align-items:center;justify-content:center;background:rgba(255,248,223,.82);border:1px solid rgba(184,147,75,.24);border-radius:999px;padding:3px 8px;color:var(--gold-deep);font-size:11.5px;font-weight:900;line-height:1.2;}
	  .armor-shop-card{min-width:0;}
	  .armor-item-name{max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
	  .armor-shop-card .meaning-chip{max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
	  .armor-action-row{width:100%;min-width:0;}
	  .armor-action-row button{min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
	  .armor-shop-hero{min-width:0;}
	  .armor-righteousness-count{flex-shrink:0;min-width:72px;}
	  .closet-item-name{max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;word-break:normal;overflow-wrap:normal;}
	  .testimony-entry{position:relative;overflow:hidden;background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(246,250,255,.62) 58%,rgba(255,248,223,.38));border:1.5px solid rgba(125,174,212,.26);border-radius:18px;padding:15px;box-shadow:0 4px 16px rgba(65,95,125,.07);}
  .testimony-entry.public-open{max-height:min(72vh,680px);overflow-y:auto;overscroll-behavior:contain;padding-right:14px;scrollbar-width:thin;scrollbar-color:rgba(125,174,212,.45) rgba(246,250,255,.42);}
  .testimony-entry.public-open::-webkit-scrollbar{width:7px;}
  .testimony-entry.public-open::-webkit-scrollbar-track{background:rgba(246,250,255,.42);border-radius:999px;}
  .testimony-entry.public-open::-webkit-scrollbar-thumb{background:rgba(125,174,212,.45);border-radius:999px;}
  .testimony-entry::before{content:"";position:absolute;inset:0;background:radial-gradient(circle at top right,rgba(255,248,223,.66),transparent 42%),linear-gradient(90deg,rgba(125,174,212,.10),transparent 22%,transparent 78%,rgba(184,147,75,.10));pointer-events:none;}
  .testimony-entry > *{position:relative;z-index:1;}
  .testimony-label{display:inline-flex;border-radius:999px;background:rgba(255,255,255,.72);border:1px solid rgba(125,174,212,.22);padding:4px 9px;color:#497093;font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.6px;}
  .public-share-box{clear:both;margin-top:16px;background:linear-gradient(160deg,rgba(255,253,246,.96),rgba(246,250,255,.78));border:1.5px solid rgba(125,174,212,.28);border-radius:18px;padding:10px;box-shadow:0 5px 16px rgba(65,95,125,.07);width:100%;}
  .public-share-toggle{width:100%;display:flex;align-items:center;justify-content:space-between;gap:10px;background:rgba(255,255,255,.54);border:1px solid rgba(125,174,212,.18);border-radius:14px;padding:10px 11px;color:var(--text-dark);font-family:'Nunito',sans-serif;cursor:pointer;text-align:left;}
  .public-share-toggle-title{font-size:13.5px;font-weight:900;color:var(--text-dark);line-height:1.25;}
  .public-share-toggle-copy{font-size:12px;color:var(--text-soft);line-height:1.35;margin-top:2px;}
  .public-share-chevron{width:26px;height:26px;border-radius:999px;display:flex;align-items:center;justify-content:center;background:rgba(219,234,254,.58);border:1px solid rgba(125,174,212,.20);color:#497093;font-weight:900;transition:transform .22s ease;flex-shrink:0;}
  .public-share-chevron.open{transform:rotate(180deg);}
  .public-share-content{margin-top:10px;animation:popIn .18s ease-out;}
  .testimony-entry.public-open .public-share-content > div[id^="submit-public-"]{position:sticky;bottom:0;z-index:2;padding:8px 0 4px;background:linear-gradient(180deg,rgba(255,253,248,0),rgba(255,253,248,.96) 32%,rgba(255,253,248,.98));}
  .public-share-row{display:grid;grid-template-columns:1fr auto;gap:14px;align-items:start;cursor:pointer;}
  .public-share-title{font-size:14.5px;font-weight:900;color:var(--text-dark);margin-bottom:4px;}
  .public-share-copy{font-size:13px;color:var(--text-soft);line-height:1.5;}
  .public-share-check{width:24px;height:24px;accent-color:var(--gold);margin-top:2px;flex-shrink:0;}
  .public-preview-card{margin-top:13px;background:linear-gradient(160deg,rgba(255,255,255,.96),rgba(246,250,255,.78));border:1px solid rgba(125,174,212,.30);border-radius:16px;padding:14px;box-shadow:0 4px 12px rgba(65,95,125,.06);}
  .public-preview-label{display:inline-flex;border-radius:999px;background:rgba(219,234,254,.76);border:1px solid rgba(125,174,212,.28);padding:4px 9px;color:#497093;font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.6px;}
  /* Calm pass: Home stays magical but quieter; Prayer Vessels feels more sacred */
  .home-room{box-shadow:inset 0 -24px 36px rgba(138,90,8,.055),0 5px 18px rgba(180,130,20,.09)!important;}
  .home-rays{opacity:.48!important;filter:blur(.3px)!important;}
  .home-cloud{opacity:.5!important;}
  .home-distant-city{opacity:.26!important;}
  .home-midground-glow{opacity:.28!important;}
  .home-floating-label{opacity:.82!important;top:12px!important;}
  .home-floating-label .placed-chip{background:rgba(255,253,246,.72)!important;border-color:rgba(184,147,75,.18)!important;color:rgba(115,80,20,.86)!important;box-shadow:none!important;}
  .home-room .home-gem{opacity:.11!important;width:8px!important;height:8px!important;animation-duration:11s!important;box-shadow:0 0 3px rgba(125,211,252,.12)!important;}
  .home-room .home-gem.g2{opacity:.14!important;width:10px!important;height:10px!important;}
  .home-room .home-gem.g3,.home-room .home-gem.g4{display:none!important;}
  .home-room .home-path-gem{opacity:.17!important;width:5px!important;height:5px!important;animation-duration:12s!important;}
  .home-path::before{opacity:.22!important;animation-duration:12s!important;}
  .home-room .home-game-sparkles{display:none!important;}
  .home-room .home-game-item:hover{transform:none!important;}
  .home-water-stream{opacity:.38!important;filter:blur(.25px)!important;}
  .home-water-stream::before{animation-duration:4.2s!important;}
  .home-game-item.furn_fountain,.home-game-item.furn_waterfall{animation-duration:8s!important;}
  .home-game-item.furn_fountain .home-game-detail.water,.home-game-item.furn_waterfall .home-game-detail.water{opacity:.86!important;}
  .home-pet-track{opacity:.78!important;z-index:4!important;}
  .home-pet-track.lamb-track{left:4%!important;right:67%!important;bottom:12%!important;}
  .home-pet-track.butterfly-track{left:67%!important;right:5%!important;top:36%!important;}
  .home-pet-track.dove-track{left:5%!important;right:69%!important;top:40%!important;}
  .home-pet-track.lamb-track .animated-shop-item{min-width:clamp(62px,19vw,104px)!important;animation-duration:26s!important;}
  .home-pet-track.butterfly-track .animated-shop-item{min-width:clamp(58px,18vw,96px)!important;animation-duration:21s!important;}
  .home-pet-track.dove-track .animated-shop-item{min-width:clamp(58px,18vw,92px)!important;animation-duration:22s!important;}
  .home-pet-track.lamb-track .home-game-item.pet_lamb{width:clamp(38px,10vw,54px)!important;height:clamp(31px,8vw,44px)!important;}
  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{width:clamp(29px,8vw,42px)!important;height:clamp(28px,8vw,39px)!important;}
  .home-pet-track.dove-track .home-game-item.pet_dove{width:clamp(30px,8vw,43px)!important;height:clamp(27px,8vw,38px)!important;}

  .prayer-page-calm{gap:10px!important;}
  .prayer-page-calm::before{left:18%!important;right:18%!important;top:4px!important;height:92px!important;background:radial-gradient(ellipse,rgba(255,248,223,.30),rgba(255,255,255,0) 72%)!important;}
  .prayer-page-calm .card{background:rgba(255,253,246,.88)!important;border-color:rgba(184,147,75,.15)!important;box-shadow:0 2px 12px rgba(138,90,8,.045)!important;}
  .prayer-page-calm .sacred-card{background:linear-gradient(160deg,rgba(255,253,246,.95),rgba(246,250,255,.46))!important;border-color:rgba(125,174,212,.18)!important;}
  .prayer-section-tabs{grid-template-columns:1fr!important;background:rgba(255,253,246,.64);border:1px solid rgba(184,147,75,.12);border-radius:18px;padding:6px;}
  .prayer-tab-card{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:10px!important;text-align:left!important;border-radius:13px!important;padding:11px 12px!important;background:transparent!important;border-color:transparent!important;box-shadow:none!important;}
  .prayer-tab-card.active{background:rgba(255,248,223,.64)!important;border-color:rgba(184,147,75,.20)!important;box-shadow:none!important;}
  .prayer-tab-card > div:first-child{font-size:17px!important;margin-bottom:0!important;opacity:.78;}
  .prayer-status-chip{background:rgba(246,250,255,.74)!important;border-color:rgba(125,174,212,.16)!important;color:#557895!important;}
  .friend-card{background:rgba(255,253,246,.82)!important;border-color:rgba(184,147,75,.13)!important;box-shadow:none!important;}
  .friend-card.prayed{background:linear-gradient(145deg,rgba(255,253,246,.88),rgba(246,250,255,.48))!important;border-color:rgba(125,174,212,.20)!important;}
  .answered-altar-card{background:linear-gradient(160deg,rgba(255,253,246,.96),rgba(246,250,255,.56),rgba(255,248,223,.32))!important;border-color:rgba(125,174,212,.18)!important;}
  .answered-altar-icon{background:rgba(255,255,255,.58)!important;border-color:rgba(125,174,212,.16)!important;box-shadow:none!important;}
  .testimony-entry{background:linear-gradient(145deg,rgba(255,253,246,.94),rgba(246,250,255,.52))!important;border-color:rgba(125,174,212,.18)!important;box-shadow:0 2px 10px rgba(65,95,125,.045)!important;}
  .testimony-entry::before{opacity:.42!important;}
  .public-share-box{background:rgba(255,253,246,.70)!important;border-color:rgba(125,174,212,.18)!important;box-shadow:none!important;}
  .empty-state{background:rgba(255,253,246,.66)!important;border-color:rgba(184,147,75,.20)!important;}

  /* Living garden pass: more realistic companions and clearer heavenly water */
  @keyframes lambGardenWalk{0%{transform:translateX(-18px) scaleX(1)}15%{transform:translateX(-7px) translateY(-1px) scaleX(1)}32%{transform:translateX(16px) scaleX(1)}46%{transform:translateX(23px) scaleX(1)}53%{transform:translateX(23px) scaleX(-1)}70%{transform:translateX(3px) translateY(-1px) scaleX(-1)}88%{transform:translateX(-20px) scaleX(-1)}100%{transform:translateX(-18px) scaleX(1)}}
  @keyframes butterflyGardenDrift{0%,100%{transform:translate(-8px,6px) rotate(-4deg)}20%{transform:translate(8px,-10px) rotate(7deg)}42%{transform:translate(18px,-2px) rotate(-5deg)}64%{transform:translate(4px,-16px) rotate(5deg)}82%{transform:translate(-14px,-5px) rotate(-6deg)}}
  @keyframes doveGardenHop{0%,100%{transform:translate(-8px,2px) rotate(-1deg)}22%{transform:translate(4px,-5px) rotate(2deg)}44%{transform:translate(15px,1px) rotate(-1deg)}62%{transform:translate(9px,-8px) rotate(3deg)}82%{transform:translate(-11px,0) rotate(-2deg)}}
  @keyframes visibleWaterFall{0%{background-position:0 -4px;transform:translateY(-7px)}100%{background-position:0 24px;transform:translateY(7px)}}
  @keyframes fountainClearRise{0%,100%{transform:translateY(0) scaleY(.98);opacity:.92}50%{transform:translateY(-3px) scaleY(1.06);opacity:1}}
  .home-pet-track{opacity:.92!important;z-index:4!important;}
  .home-pet-track.lamb-track{left:6%!important;right:63%!important;bottom:11%!important;}
  .home-pet-track.butterfly-track{left:63%!important;right:6%!important;top:34%!important;}
  .home-pet-track.dove-track{left:7%!important;right:65%!important;top:39%!important;}
  .home-pet-track.lamb-track .animated-shop-item{min-width:clamp(82px,24vw,128px)!important;animation-name:lambGardenWalk!important;animation-duration:23s!important;animation-timing-function:ease-in-out!important;}
  .home-pet-track.butterfly-track .animated-shop-item{min-width:clamp(86px,25vw,132px)!important;animation-name:butterflyGardenDrift!important;animation-duration:18s!important;animation-timing-function:ease-in-out!important;}
  .home-pet-track.dove-track .animated-shop-item{min-width:clamp(74px,22vw,112px)!important;animation-name:doveGardenHop!important;animation-duration:19s!important;animation-timing-function:ease-in-out!important;}
  .home-pet-track.lamb-track .home-game-item.pet_lamb{width:clamp(50px,13vw,70px)!important;height:clamp(39px,10vw,56px)!important;}
  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{width:clamp(40px,11vw,58px)!important;height:clamp(38px,10vw,54px)!important;}
  .home-pet-track.dove-track .home-game-item.pet_dove{width:clamp(42px,11vw,60px)!important;height:clamp(36px,10vw,52px)!important;}
  .home-room .home-game-item.pet_lamb .home-game-shadow{left:8%!important;right:16%!important;bottom:0!important;height:10px!important;opacity:.38!important;filter:blur(1.5px)!important;}
  .home-room .home-game-item.pet_lamb .home-game-body{left:7%!important;right:17%!important;bottom:11px!important;height:48%!important;border-radius:58% 46% 42% 44%!important;background:radial-gradient(circle at 16% 20%,#fff 0 7%,transparent 8%),radial-gradient(circle at 34% 10%,#fff 0 10%,transparent 11%),radial-gradient(circle at 55% 22%,#fff 0 8%,transparent 9%),radial-gradient(circle at 75% 32%,#fff 0 7%,transparent 8%),linear-gradient(180deg,#fffdf8,#efe5d7 58%,#bba58d)!important;border:1px solid rgba(116,88,55,.16)!important;box-shadow:inset 0 5px 8px rgba(255,255,255,.9),0 6px 8px rgba(82,55,15,.09)!important;}
  .home-room .home-game-item.pet_lamb .home-game-body::after{left:-7px!important;top:13px!important;width:14px!important;height:12px!important;border-radius:58% 48% 50% 44%!important;background:#fbfaf7!important;box-shadow:0 0 0 1px rgba(116,88,55,.14),4px 3px 0 -2px #3d2800,47px -5px 0 -5px rgba(255,255,255,.9),52px 6px 0 -6px #d1bfa7!important;}
  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left{right:6%!important;top:29%!important;width:23px!important;height:19px!important;background:radial-gradient(circle at 32% 28%,#fff 0 25%,#efe4d4 27% 68%,#c1aa91 100%)!important;}
  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left::before{right:2px!important;top:-7px!important;width:9px!important;height:13px!important;background:#c8b298!important;box-shadow:-13px 3px 0 -1px #c8b298!important;}
  .home-room .home-game-item.pet_lamb .home-game-icon::before,.home-room .home-game-item.pet_lamb .home-game-icon::after{height:13px!important;background:#6f5a47!important;box-shadow:10px 0 0 #836e58!important;}
  .home-room .home-game-item.pet_butterfly .home-game-body{bottom:18px!important;background:linear-gradient(180deg,#3b2b58,#6d4ed8)!important;}
  .home-room .home-game-item.pet_butterfly .home-game-body::before,.home-room .home-game-item.pet_butterfly .home-game-body::after{width:22px!important;height:27px!important;background:radial-gradient(circle at 35% 28%,rgba(255,255,255,.94) 0 4px,transparent 5px),radial-gradient(circle at 68% 73%,rgba(255,255,255,.8) 0 3px,transparent 4px),linear-gradient(145deg,#f9a8d4,#9ee7f0 54%,#8b5cf6)!important;box-shadow:inset 0 6px 8px rgba(255,255,255,.45),0 4px 7px rgba(82,55,15,.10)!important;}
  .home-room .home-game-item.pet_butterfly .home-game-detail.wing-left,.home-room .home-game-item.pet_butterfly .home-game-detail.wing-right{width:16px!important;height:20px!important;opacity:.86!important;}
  .home-room .home-game-item.pet_dove .home-game-body{left:22%!important;right:14%!important;bottom:13px!important;height:40%!important;background:linear-gradient(145deg,#fff,#f8fdff 56%,#c7e5f7)!important;border:1px solid rgba(105,140,165,.13)!important;}
  .home-room .home-game-item.pet_dove .home-game-body::after{right:-8px!important;top:8px!important;width:12px!important;height:9px!important;background:linear-gradient(90deg,#fff8df,#d99a12)!important;}
  .home-room .home-game-item.pet_dove .home-game-detail.wing-left,.home-room .home-game-item.pet_dove .home-game-detail.wing-right{width:25px!important;height:18px!important;background:linear-gradient(145deg,#fff,#edf8ff 72%,#c7e5f7)!important;opacity:.9!important;}

  .home-water-stream{opacity:.62!important;filter:blur(.08px)!important;}
  .home-water-stream::before{background:linear-gradient(90deg,rgba(255,255,255,.82),rgba(150,226,248,.58),rgba(62,171,211,.24),rgba(255,255,255,.48))!important;box-shadow:inset 0 1px 3px rgba(255,255,255,.68),0 2px 7px rgba(14,116,144,.10)!important;animation-duration:3.2s!important;}
  .home-water-stream.furn_waterfall{right:7%!important;bottom:7%!important;width:34%!important;height:10%!important;opacity:.66!important;}
  .home-water-stream.furn_fountain{right:15%!important;bottom:8%!important;width:22%!important;height:7%!important;opacity:.62!important;}
  .home-slot.feature-slot.furn_waterfall-slot{right:5%!important;bottom:8%!important;}
  .home-slot.feature-slot .home-game-item.furn_waterfall{width:clamp(86px,22vw,118px)!important;height:clamp(94px,24vw,132px)!important;}
  .home-game-item.furn_waterfall .home-game-glow{opacity:.13!important;background:radial-gradient(ellipse,rgba(219,242,255,.62),rgba(96,165,250,.12),transparent 72%)!important;}
  .home-game-item.furn_waterfall .home-game-body::after{left:26%!important;right:22%!important;top:6%!important;bottom:1%!important;background:repeating-linear-gradient(180deg,rgba(255,255,255,1) 0 6px,rgba(207,244,255,.96) 6px 13px,rgba(67,181,218,.78) 13px 22px,rgba(255,255,255,.78) 22px 28px)!important;background-size:100% 28px!important;box-shadow:-18px 22px 0 -9px rgba(255,255,255,.62),17px 30px 0 -10px rgba(255,255,255,.52),0 48px 0 -11px rgba(255,255,255,.44)!important;animation:visibleWaterFall .82s linear infinite!important;}
  .home-game-item.furn_waterfall .home-game-detail.water{left:-2%!important;right:-2%!important;height:36px!important;background:radial-gradient(ellipse at 50% 38%,rgba(255,255,255,.98),rgba(147,223,247,.68) 44%,rgba(14,116,144,.24) 74%,transparent 84%)!important;}
  .home-slot.feature-slot .home-game-item.furn_fountain{width:clamp(68px,17vw,96px)!important;height:clamp(68px,17vw,98px)!important;}
  .home-game-item.furn_fountain .home-game-glow{opacity:.18!important;background:radial-gradient(ellipse,rgba(219,242,255,.58),rgba(56,189,248,.12),transparent 74%)!important;}
  .home-game-item.furn_fountain .home-game-detail.water{left:10%!important;right:10%!important;top:-20%!important;height:62%!important;background:radial-gradient(circle at 50% 10%,#fff 0 11%,rgba(211,245,255,.98) 12% 38%,rgba(83,190,225,.68) 39% 70%,rgba(14,116,144,.18))!important;box-shadow:-18px 32px 0 -10px rgba(125,211,252,.68),18px 32px 0 -10px rgba(125,211,252,.62),0 43px 0 -10px rgba(45,139,175,.42)!important;animation:fountainClearRise 1.38s ease-in-out infinite!important;}
  .home-game-item.furn_fountain .home-game-detail.water::before{top:-28px!important;height:52px!important;background:linear-gradient(180deg,rgba(255,255,255,1),rgba(153,224,247,.76) 58%,rgba(14,116,144,.28))!important;box-shadow:-17px 16px 0 -1px rgba(125,211,252,.56),17px 16px 0 -1px rgba(125,211,252,.52),0 9px 0 1px rgba(255,255,255,.5)!important;}
  .home-game-item.furn_fountain .home-game-detail.water::after{top:-33px!important;box-shadow:-21px 23px 0 -2px rgba(125,211,252,.74),21px 22px 0 -3px rgba(125,211,252,.72),-9px 51px 0 -3px rgba(255,255,255,.78),9px 55px 0 -4px rgba(125,211,252,.66)!important;}

  /* Heavenly water and gate pass: clearer water, softer integration, ornate entrance */
  @keyframes waterfallSilkFlow{0%{background-position:0 -8px;transform:translateY(-6px) scaleY(1)}100%{background-position:0 30px;transform:translateY(6px) scaleY(1.02)}}
  @keyframes streamGlimmer{0%,100%{opacity:.58;transform:translateX(-2px) scaleX(.96)}50%{opacity:.82;transform:translateX(3px) scaleX(1.04)}}
  @keyframes fountainCrownRise{0%,100%{transform:translateY(0) scale(.98);opacity:.9}45%{transform:translateY(-4px) scale(1.04);opacity:1}}
  @keyframes gateBlessedGlow{0%,100%{filter:drop-shadow(0 5px 10px rgba(138,90,8,.12))}50%{filter:drop-shadow(0 7px 16px rgba(240,203,118,.32))}}
  .home-water-stream{overflow:visible!important;opacity:.72!important;filter:blur(.02px)!important;}
  .home-water-stream::before{background:linear-gradient(90deg,rgba(255,255,255,.88),rgba(170,234,251,.68) 32%,rgba(57,174,217,.30) 58%,rgba(255,255,255,.55))!important;box-shadow:inset 0 1px 3px rgba(255,255,255,.78),0 2px 8px rgba(14,116,144,.12),0 0 16px rgba(186,230,253,.20)!important;animation:streamGlimmer 3.1s ease-in-out infinite!important;}
  .home-water-stream::after{content:"";position:absolute;left:8%;right:8%;top:42%;height:42%;border-radius:999px;background:radial-gradient(ellipse at 50% 40%,rgba(255,255,255,.72),rgba(125,211,252,.28) 52%,transparent 76%);opacity:.72;animation:pondRipple 3.4s ease-in-out infinite;}
  .home-water-stream.furn_waterfall{right:6%!important;bottom:6.5%!important;width:36%!important;height:11%!important;transform:rotate(-11deg)!important;}
  .home-water-stream.furn_fountain{right:14%!important;bottom:7.5%!important;width:24%!important;height:8%!important;transform:rotate(-8deg)!important;}
  .home-slot.feature-slot.furn_waterfall-slot{right:4.5%!important;bottom:8%!important;}
  .home-slot.feature-slot .home-game-item.furn_waterfall{width:clamp(92px,23vw,126px)!important;height:clamp(100px,25vw,138px)!important;}
  .home-game-item.furn_waterfall .home-game-glow{opacity:.18!important;background:radial-gradient(ellipse,rgba(219,242,255,.72),rgba(96,165,250,.16),transparent 72%)!important;}
  .home-game-item.furn_waterfall .home-game-body{background:radial-gradient(circle at 17% 79%,#4f666c 0 9px,transparent 10px),radial-gradient(circle at 84% 78%,#637a80 0 10px,transparent 11px),radial-gradient(circle at 22% 26%,#edf5f6 0 12%,transparent 13%),linear-gradient(145deg,#edf4f5 0%,#a9b5b8 24%,#6e858b 56%,#3e5961 100%)!important;}
  .home-game-item.furn_waterfall .home-game-body::before{background:radial-gradient(ellipse at 48% 8%,rgba(255,255,255,.98),rgba(220,246,255,.78) 21%,transparent 35%),radial-gradient(ellipse at 36% 62%,rgba(255,255,255,.48),transparent 44%),radial-gradient(ellipse at 64% 78%,rgba(186,230,253,.36),transparent 45%)!important;opacity:.94!important;}
  .home-game-item.furn_waterfall .home-game-body::after{left:23%!important;right:19%!important;top:5%!important;bottom:0!important;border-radius:999px 999px 44% 44%!important;background:repeating-linear-gradient(180deg,rgba(255,255,255,1) 0 6px,rgba(218,248,255,.98) 6px 13px,rgba(77,190,225,.82) 13px 23px,rgba(255,255,255,.78) 23px 30px)!important;background-size:100% 30px!important;box-shadow:-20px 24px 0 -10px rgba(255,255,255,.64),20px 32px 0 -11px rgba(255,255,255,.54),0 54px 0 -12px rgba(255,255,255,.46),0 0 18px rgba(186,230,253,.28)!important;animation:waterfallSilkFlow .78s linear infinite!important;}
  .home-game-item.furn_waterfall .home-game-detail.water{left:-7%!important;right:-7%!important;bottom:-1px!important;height:42px!important;background:radial-gradient(ellipse at 50% 36%,rgba(255,255,255,.98),rgba(150,226,248,.72) 42%,rgba(14,116,144,.26) 72%,transparent 84%)!important;box-shadow:0 -5px 14px rgba(255,255,255,.45),inset 0 2px 8px rgba(255,255,255,.58),0 0 18px rgba(186,230,253,.18)!important;}
  .home-game-item.furn_waterfall .home-game-detail.water::before{left:11%!important;right:11%!important;top:13px!important;height:10px!important;border-top:1.5px solid rgba(255,255,255,.94)!important;border-bottom:1px solid rgba(125,211,252,.34)!important;}
  .home-slot.feature-slot .home-game-item.furn_fountain{width:clamp(72px,18vw,102px)!important;height:clamp(72px,18vw,104px)!important;}
  .home-game-item.furn_fountain .home-game-glow{opacity:.22!important;background:radial-gradient(ellipse,rgba(219,242,255,.70),rgba(56,189,248,.15),transparent 74%)!important;}
  .home-game-item.furn_fountain .home-game-body{background:linear-gradient(180deg,#ffffff 0%,#e4f8ff 28%,#9ed8ee 63%,#4b86a5 100%)!important;box-shadow:inset 0 7px 10px rgba(255,255,255,.9),inset 0 -11px 15px rgba(20,76,105,.22),0 8px 12px rgba(82,55,15,.10)!important;}
  .home-game-item.furn_fountain .home-game-detail.water{left:6%!important;right:6%!important;top:-25%!important;height:70%!important;background:radial-gradient(circle at 50% 8%,#fff 0 11%,rgba(220,249,255,.98) 12% 38%,rgba(83,190,225,.72) 39% 70%,rgba(14,116,144,.18))!important;box-shadow:-21px 36px 0 -11px rgba(125,211,252,.72),21px 36px 0 -11px rgba(125,211,252,.66),0 50px 0 -11px rgba(45,139,175,.42),0 0 16px rgba(186,230,253,.25)!important;animation:fountainCrownRise 1.28s ease-in-out infinite!important;}
  .home-game-item.furn_fountain .home-game-detail.water::before{top:-31px!important;height:58px!important;width:6px!important;background:linear-gradient(180deg,rgba(255,255,255,1),rgba(159,228,249,.82) 58%,rgba(14,116,144,.30))!important;box-shadow:-19px 18px 0 -1px rgba(125,211,252,.60),19px 18px 0 -1px rgba(125,211,252,.56),0 9px 0 1px rgba(255,255,255,.56)!important;}
  .home-game-item.furn_fountain .home-game-detail.water::after{top:-37px!important;width:8px!important;height:8px!important;box-shadow:-24px 26px 0 -2px rgba(125,211,252,.78),24px 24px 0 -3px rgba(125,211,252,.76),-11px 58px 0 -3px rgba(255,255,255,.82),11px 62px 0 -4px rgba(125,211,252,.68)!important;}

  .home-gate{bottom:18%!important;width:82px!important;height:134px!important;border:2px solid rgba(154,107,22,.46)!important;border-bottom:none!important;background:linear-gradient(90deg,rgba(255,253,246,.10),rgba(255,248,223,.28) 28%,rgba(240,203,118,.28) 50%,rgba(255,248,223,.16) 72%,rgba(255,253,246,.08));box-shadow:inset 0 0 12px rgba(255,248,223,.40),0 9px 17px rgba(82,55,15,.09),0 0 18px rgba(240,203,118,.12);animation:gateBlessedGlow 5.4s ease-in-out infinite!important;}
  .home-gate.left{left:2px!important;border-radius:60px 22px 0 0!important;transform:rotate(-2.5deg)!important;}
  .home-gate.right{right:2px!important;border-radius:22px 60px 0 0!important;transform:rotate(2.5deg)!important;}
  .home-gate::before{width:17px!important;height:17px!important;top:-11px!important;background:radial-gradient(circle,#fff 0 18%,#fff8df 20% 42%,#f0c75e 64%,#9a6b16 100%)!important;box-shadow:0 0 12px rgba(240,203,118,.62),0 0 24px rgba(255,248,223,.30)!important;}
  .home-gate::after{inset:14px 13px 0!important;border-left:2px solid rgba(154,107,22,.44)!important;border-right:2px solid rgba(154,107,22,.44)!important;background:repeating-linear-gradient(90deg,transparent 0 9px,rgba(201,144,18,.48) 9px 11px,transparent 11px 20px),linear-gradient(180deg,rgba(255,253,246,.28),rgba(255,248,223,.10) 48%,transparent 76%)!important;box-shadow:inset 0 12px 0 -10px rgba(255,253,246,.82),inset 0 40px 0 -38px rgba(201,144,18,.48),0 -1px 0 rgba(255,248,223,.40)!important;}
  .home-game-item.dec_gates .home-game-body{left:8%!important;right:8%!important;height:78%!important;border-radius:999px 999px 8px 8px!important;background:repeating-linear-gradient(90deg,rgba(255,253,246,.96) 0 8px,transparent 8px 15px,rgba(240,203,118,.88) 15px 23px,transparent 23px 31px),linear-gradient(180deg,rgba(255,248,223,.88),rgba(201,144,18,.34))!important;border:2px solid rgba(154,107,22,.42)!important;border-bottom:none!important;box-shadow:inset 0 7px 10px rgba(255,255,255,.62),0 8px 13px rgba(82,55,15,.11),0 0 14px rgba(240,203,118,.20)!important;}
  .home-game-item.dec_gates .home-game-body::before{content:"";position:absolute;left:50%;top:-12px;width:16px;height:16px;border-radius:50%;transform:translateX(-50%);background:radial-gradient(circle,#fff,#fff8df 42%,#f0c75e 70%,#9a6b16);box-shadow:0 0 12px rgba(240,203,118,.55);}
  .home-game-item.dec_gates .home-game-detail.base{left:13%!important;right:13%!important;bottom:6px!important;height:10px!important;border-radius:999px!important;background:linear-gradient(90deg,#8a5a08,#fff3c4 45%,#c99012,#8a5a08)!important;box-shadow:0 2px 5px rgba(82,55,15,.12)!important;}

  /* Water balance pass: premium water without oversized fountain */
  @keyframes fountainGentleCrown{0%,100%{transform:translateY(0) scale(.96);opacity:.86}48%{transform:translateY(-2px) scale(1.01);opacity:.96}}
  .home-water-stream.furn_fountain{right:16%!important;bottom:7.8%!important;width:18%!important;height:6%!important;opacity:.54!important;transform:rotate(-8deg)!important;}
  .home-slot.feature-slot .home-game-item.furn_fountain{width:clamp(58px,15vw,82px)!important;height:clamp(58px,15vw,84px)!important;}
  .home-game-item.furn_fountain{filter:drop-shadow(0 6px 9px rgba(38,80,105,.12))!important;}
  .home-game-item.furn_fountain .home-game-glow{opacity:.14!important;background:radial-gradient(ellipse,rgba(219,242,255,.48),rgba(56,189,248,.10),transparent 74%)!important;}
  .home-game-item.furn_fountain .home-game-shadow{left:10%!important;right:10%!important;height:11px!important;opacity:.38!important;}
  .home-game-item.furn_fountain .home-game-body{left:10%!important;right:10%!important;bottom:7px!important;height:47%!important;border-radius:15px 15px 23px 23px!important;background:linear-gradient(180deg,#fff 0%,#e6f8ff 30%,#9dd3e8 64%,#4f86a1 100%)!important;box-shadow:inset 0 6px 8px rgba(255,255,255,.88),inset 0 -9px 12px rgba(20,76,105,.20),0 6px 9px rgba(82,55,15,.08)!important;}
  .home-game-item.furn_fountain .home-game-detail.water{left:14%!important;right:14%!important;top:-15%!important;height:52%!important;background:radial-gradient(circle at 50% 10%,#fff 0 12%,rgba(220,249,255,.96) 13% 38%,rgba(83,190,225,.58) 39% 68%,rgba(14,116,144,.14))!important;box-shadow:-14px 26px 0 -9px rgba(125,211,252,.58),14px 26px 0 -9px rgba(125,211,252,.54),0 36px 0 -10px rgba(45,139,175,.32),0 0 12px rgba(186,230,253,.18)!important;animation:fountainGentleCrown 1.55s ease-in-out infinite!important;}
  .home-game-item.furn_fountain .home-game-detail.water::before{top:-21px!important;width:5px!important;height:38px!important;background:linear-gradient(180deg,rgba(255,255,255,.98),rgba(159,228,249,.70) 58%,rgba(14,116,144,.24))!important;box-shadow:-12px 12px 0 -1px rgba(125,211,252,.48),12px 12px 0 -1px rgba(125,211,252,.44),0 7px 0 1px rgba(255,255,255,.42)!important;}
  .home-game-item.furn_fountain .home-game-detail.water::after{top:-25px!important;width:6px!important;height:6px!important;box-shadow:-16px 18px 0 -2px rgba(125,211,252,.62),16px 17px 0 -3px rgba(125,211,252,.60),-7px 40px 0 -3px rgba(255,255,255,.72),7px 43px 0 -4px rgba(125,211,252,.56)!important;opacity:.68!important;}

  .home-water-stream.furn_waterfall{right:6%!important;bottom:6.5%!important;width:37%!important;height:11%!important;opacity:.70!important;}
  .home-game-item.furn_waterfall .home-game-body::after{left:22%!important;right:18%!important;background:repeating-linear-gradient(180deg,rgba(255,255,255,1) 0 6px,rgba(218,248,255,.98) 6px 13px,rgba(77,190,225,.84) 13px 23px,rgba(255,255,255,.80) 23px 30px)!important;box-shadow:-20px 24px 0 -10px rgba(255,255,255,.66),20px 32px 0 -11px rgba(255,255,255,.56),0 54px 0 -12px rgba(255,255,255,.48),0 0 18px rgba(186,230,253,.30)!important;}
  .home-game-item.furn_waterfall .home-game-detail.water{height:43px!important;opacity:.96!important;}

  /* Water placement pass: keep water features clear of gates and visibly overflowing */
  @keyframes waterfallAbundantFlow{0%{background-position:0 -10px;transform:translateY(-7px) scaleY(1)}100%{background-position:0 34px;transform:translateY(7px) scaleY(1.03)}}
  @keyframes fountainOverflowCrown{0%,100%{transform:translateY(0) scale(.98);opacity:.9}48%{transform:translateY(-3px) scale(1.04);opacity:1}}
  @keyframes overflowMist{0%,100%{opacity:.50;transform:translateY(0) scaleX(.96)}50%{opacity:.82;transform:translateY(-2px) scaleX(1.05)}}
  .home-gate{z-index:6!important;}
  .home-slot.feature-slot{z-index:4!important;}
  .home-slot.feature-slot.furn_fountain-slot{right:24%!important;bottom:8%!important;z-index:4!important;}
  .home-slot.feature-slot.furn_waterfall-slot{right:20%!important;bottom:7.5%!important;z-index:4!important;}
  .home-water-stream{z-index:3!important;overflow:visible!important;}
  .home-water-stream.furn_fountain{right:25%!important;bottom:7.1%!important;width:20%!important;height:7%!important;opacity:.68!important;transform:rotate(-7deg)!important;}
  .home-water-stream.furn_waterfall{right:20%!important;bottom:6.2%!important;width:31%!important;height:11%!important;opacity:.76!important;transform:rotate(-10deg)!important;}
  .home-water-stream::before{background:linear-gradient(90deg,rgba(255,255,255,.9),rgba(174,236,252,.72) 35%,rgba(57,174,217,.34) 62%,rgba(255,255,255,.58))!important;box-shadow:inset 0 1px 3px rgba(255,255,255,.82),0 2px 9px rgba(14,116,144,.13),0 0 18px rgba(186,230,253,.22)!important;}
  .home-water-stream::after{left:-6%!important;right:-6%!important;top:24%!important;height:82%!important;background:radial-gradient(ellipse at 50% 40%,rgba(255,255,255,.82),rgba(125,211,252,.34) 50%,transparent 76%)!important;animation:overflowMist 2.8s ease-in-out infinite!important;}

  .home-slot.feature-slot .home-game-item.furn_fountain{width:clamp(60px,15vw,84px)!important;height:clamp(60px,15vw,86px)!important;}
  .home-game-item.furn_fountain .home-game-detail.water{left:10%!important;right:10%!important;top:-18%!important;height:58%!important;background:radial-gradient(circle at 50% 8%,#fff 0 12%,rgba(220,249,255,.98) 13% 38%,rgba(83,190,225,.64) 39% 70%,rgba(14,116,144,.16))!important;box-shadow:-16px 29px 0 -9px rgba(125,211,252,.64),16px 29px 0 -9px rgba(125,211,252,.58),0 40px 0 -10px rgba(45,139,175,.38),0 0 14px rgba(186,230,253,.22)!important;animation:fountainOverflowCrown 1.35s ease-in-out infinite!important;}
  .home-game-item.furn_fountain .home-game-detail.water::before{top:-24px!important;height:45px!important;box-shadow:-14px 14px 0 -1px rgba(125,211,252,.55),14px 14px 0 -1px rgba(125,211,252,.50),0 8px 0 1px rgba(255,255,255,.50)!important;}
  .home-game-item.furn_fountain .home-game-detail.water::after{top:-29px!important;box-shadow:-19px 21px 0 -2px rgba(125,211,252,.70),19px 20px 0 -3px rgba(125,211,252,.66),-8px 47px 0 -3px rgba(255,255,255,.80),8px 50px 0 -4px rgba(125,211,252,.62)!important;}
  .home-game-item.furn_fountain .home-game-body::after{left:15%!important;right:15%!important;bottom:-10px!important;height:15px!important;border-radius:50%!important;background:radial-gradient(ellipse at 50% 42%,rgba(255,255,255,.82),rgba(125,211,252,.42) 52%,rgba(14,116,144,.18) 78%)!important;}

  .home-slot.feature-slot .home-game-item.furn_waterfall{width:clamp(94px,23vw,128px)!important;height:clamp(104px,25vw,142px)!important;}
  .home-game-item.furn_waterfall .home-game-body::after{left:20%!important;right:16%!important;top:4%!important;bottom:-3%!important;background:repeating-linear-gradient(180deg,rgba(255,255,255,1) 0 6px,rgba(218,248,255,.98) 6px 14px,rgba(66,184,222,.86) 14px 25px,rgba(255,255,255,.82) 25px 32px)!important;background-size:100% 32px!important;box-shadow:-22px 25px 0 -10px rgba(255,255,255,.68),22px 34px 0 -11px rgba(255,255,255,.58),0 58px 0 -12px rgba(255,255,255,.52),0 0 20px rgba(186,230,253,.32)!important;animation:waterfallAbundantFlow .72s linear infinite!important;}
  .home-game-item.furn_waterfall .home-game-detail.water{left:-13%!important;right:-13%!important;bottom:-2px!important;height:48px!important;background:radial-gradient(ellipse at 50% 36%,rgba(255,255,255,.99),rgba(150,226,248,.78) 42%,rgba(14,116,144,.28) 74%,transparent 86%)!important;box-shadow:0 -6px 16px rgba(255,255,255,.50),inset 0 2px 9px rgba(255,255,255,.62),0 0 20px rgba(186,230,253,.22)!important;}
  .home-game-item.furn_waterfall .home-game-detail.water::after{content:"";position:absolute;left:7%!important;right:7%!important;top:-6px!important;height:18px!important;border-radius:50%!important;background:radial-gradient(ellipse at 50% 42%,rgba(255,255,255,.78),rgba(186,230,253,.32) 58%,transparent 76%)!important;animation:overflowMist 2s ease-in-out infinite!important;}

  /* Home composition pass: calmer zones, premium feature pieces, and stage-wide growth */
  @keyframes palaceEdgeBlessing{0%,100%{opacity:.34;transform:translateY(0) scale(.98)}50%{opacity:.58;transform:translateY(-2px) scale(1.02)}}
  @keyframes calmCreatureDrift{0%,100%{transform:translateX(0) translateY(0) scaleX(1)}45%{transform:translateX(var(--drift)) translateY(-2px) scaleX(1)}50%{transform:translateX(var(--drift)) translateY(-2px) scaleX(-1)}95%{transform:translateX(0) translateY(0) scaleX(-1)}}
  @keyframes calmButterflyGarden{0%,100%{transform:translate(0,0) rotate(-2deg)}25%{transform:translate(18px,-10px) rotate(5deg)}50%{transform:translate(38px,4px) rotate(-4deg)}75%{transform:translate(14px,12px) rotate(3deg)}}
  .home-room{isolation:isolate;}
  .home-room::after{content:"";position:absolute;left:9%;right:9%;bottom:0;height:32%;pointer-events:none;z-index:2;background:radial-gradient(ellipse at 50% 100%,rgba(134,239,172,.18),transparent 64%);}
  .home-stage-0 .home-sky{filter:saturate(.86) brightness(.98);}
  .home-stage-1 .home-sky{filter:saturate(.95) brightness(1);}
  .home-stage-2 .home-sky,.home-stage-3 .home-sky{filter:saturate(1.02) brightness(1.02);}
  .home-stage-4 .home-sky,.home-stage-5 .home-sky{filter:saturate(1.08) brightness(1.04);}
  .home-stage-6 .home-sky{filter:saturate(1.12) brightness(1.07);}
  .home-stage-0 .home-floor{background:linear-gradient(180deg,#fff7d6 0%,#d8c39a 100%)!important;}
  .home-stage-1 .home-floor{background:linear-gradient(180deg,#fff7d6 0%,#dcefb9 100%)!important;}
  .home-stage-2 .home-floor,.home-stage-3 .home-floor{background:linear-gradient(180deg,#fff7d6 0%,#e7f7c9 42%,#f7d778 100%)!important;}
  .home-stage-4 .home-floor,.home-stage-5 .home-floor,.home-stage-6 .home-floor{background:linear-gradient(180deg,#fff9df 0%,#dcfce7 38%,#f7d778 100%)!important;}
  .home-stage-4 .home-palace::before,.home-stage-5 .home-palace::before,.home-stage-6 .home-palace::before{content:"";position:absolute;left:4%;right:4%;top:4%;height:34%;border-radius:999px;background:radial-gradient(ellipse,rgba(255,253,246,.46),transparent 70%);animation:palaceEdgeBlessing 5.8s ease-in-out infinite;pointer-events:none;}
  .home-stage-5 .home-path::before,.home-stage-6 .home-path::before{opacity:.28!important;}
  .home-stage-6 .home-path-gem{opacity:.42!important;}
  .home-room .home-gem{opacity:.12!important;}
  .home-room .home-gem.g2{opacity:.18!important;}
  .home-room .home-gem.g3,.home-room .home-gem.g4{display:none!important;}
  .home-room .home-game-sparkles{opacity:.12!important;}
  .home-room .home-game-sparkles span:nth-child(n+2){display:none!important;}

  .home-slot.decor-slot{right:4%!important;bottom:30%!important;z-index:4!important;max-width:18%!important;}
  .home-slot.garden-slot{left:4%!important;right:auto!important;bottom:6%!important;z-index:4!important;max-width:28%!important;}
  .home-slot.garden-slot .animated-shop-item{min-width:clamp(80px,22vw,118px)!important;}
  .home-slot.garden-slot .home-game-item{width:clamp(76px,20vw,108px)!important;height:clamp(54px,14vw,76px)!important;}
  .home-slot.feature-slot{right:20%!important;bottom:7.5%!important;z-index:4!important;max-width:31%!important;}
  .home-slot.feature-slot::after{content:"";position:absolute;left:3%;right:3%;bottom:-5px;height:18px;border-radius:50%;background:radial-gradient(ellipse,rgba(89,76,40,.16),transparent 70%);z-index:-1;pointer-events:none;}
  .home-slot.feature-slot.furn_fountain-slot{right:24%!important;bottom:8%!important;}
  .home-slot.feature-slot.furn_waterfall-slot{right:19%!important;bottom:7.5%!important;}
  .home-slot.feature-slot.furn_lily_pond-slot{right:25%!important;bottom:6.5%!important;}

  .home-water-stream{z-index:3!important;filter:blur(.02px)!important;}
  .home-water-stream.furn_fountain{right:25%!important;bottom:7%!important;width:19%!important;height:6.5%!important;opacity:.58!important;}
  .home-water-stream.furn_waterfall{right:20%!important;bottom:6.2%!important;width:30%!important;height:10.5%!important;opacity:.70!important;}
  .home-water-stream.furn_lily_pond{right:25%!important;bottom:6.2%!important;width:21%!important;height:7%!important;opacity:.46!important;}
  .home-water-stream::before{border-radius:999px;background:linear-gradient(90deg,rgba(255,255,255,.82),rgba(170,234,251,.58) 38%,rgba(57,174,217,.22) 64%,rgba(255,255,255,.46))!important;}
  .home-water-stream::after{opacity:.58!important;}
  .home-slot.feature-slot .home-game-item.furn_fountain{width:clamp(58px,15vw,82px)!important;height:clamp(58px,15vw,84px)!important;}
  .home-game-item.furn_fountain .home-game-glow{opacity:.13!important;}
  .home-game-item.furn_fountain .home-game-detail.water{height:55%!important;top:-16%!important;opacity:.94!important;}
  .home-slot.feature-slot .home-game-item.furn_waterfall{width:clamp(92px,22vw,122px)!important;height:clamp(102px,24vw,136px)!important;}
  .home-game-item.furn_waterfall .home-game-glow{opacity:.14!important;}
  .home-game-item.furn_waterfall .home-game-detail.water{height:44px!important;}
  .home-slot.feature-slot .home-game-item.furn_lily_pond{width:clamp(76px,20vw,104px)!important;height:clamp(44px,12vw,62px)!important;}

  .home-gate{z-index:6!important;filter:drop-shadow(0 6px 10px rgba(138,90,8,.12));}
  .home-lantern{z-index:5!important;opacity:.72!important;transform:scale(.82);}
  .home-background-rainbow{opacity:.48!important;z-index:1!important;}

  .home-pet-track{z-index:5!important;opacity:.82!important;pointer-events:none;}
  .home-pet-track .animated-shop-item{animation-name:calmCreatureDrift!important;animation-timing-function:ease-in-out!important;}
  .home-pet-track.lamb-track{left:6%!important;right:62%!important;bottom:12%!important;}
  .home-pet-track.butterfly-track{left:62%!important;right:6%!important;top:36%!important;}
  .home-pet-track.butterfly-track .animated-shop-item{animation-name:calmButterflyGarden!important;animation-duration:14s!important;}
  .home-pet-track.dove-track{left:7%!important;right:63%!important;top:40%!important;}
  .home-pet-track.lamb-track .animated-shop-item{min-width:clamp(74px,22vw,116px)!important;animation-duration:20s!important;}
  .home-pet-track.butterfly-track .animated-shop-item{min-width:clamp(70px,21vw,108px)!important;}
  .home-pet-track.dove-track .animated-shop-item{min-width:clamp(70px,21vw,108px)!important;animation-duration:16s!important;}
  .home-pet-track.lamb-track .home-game-item.pet_lamb{width:clamp(40px,11vw,56px)!important;height:clamp(34px,9vw,48px)!important;}
  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{width:clamp(30px,8vw,44px)!important;height:clamp(28px,8vw,40px)!important;}
  .home-pet-track.dove-track .home-game-item.pet_dove{width:clamp(34px,9vw,48px)!important;height:clamp(30px,8vw,42px)!important;}

  .home-shop-card .home-shop-preview{transform:scale(.92);}
  .home-shop-card.expanded .home-shop-preview{transform:scale(.98);}

  /* Companion and water refinement pass
     Water tuning knobs:
     --water-speed controls ripple/flow speed.
     --water-opacity controls how visible the water layer is.
     --wave-size controls ripple expansion.
     --shimmer-intensity controls surface highlight brightness. */
  @keyframes waterSurfaceRipple{0%{transform:translate(-50%,-50%) scale(.55);opacity:0}28%{opacity:var(--water-opacity,.58)}100%{transform:translate(-50%,-50%) scale(var(--wave-size,1.28));opacity:0}}
  @keyframes waterSurfaceShimmer{0%{transform:translateX(-75%) skewX(-16deg);opacity:0}25%{opacity:var(--shimmer-intensity,.44)}62%{opacity:var(--shimmer-intensity,.44)}100%{transform:translateX(95%) skewX(-16deg);opacity:0}}
  @keyframes waterTinyBubble{0%,100%{transform:translateY(6px) scale(.65);opacity:0}30%{opacity:.56}70%{opacity:.72}100%{transform:translateY(-11px) scale(1);opacity:0}}
  @keyframes waterDownwardFlow{0%{background-position:0 -24px;transform:translateY(-3px)}100%{background-position:0 32px;transform:translateY(4px)}}
  @keyframes lambMeadowWalk{0%{transform:translateX(-10px) scaleX(1)}18%{transform:translateX(22px) translateY(-1px) scaleX(1)}38%{transform:translateX(54px) scaleX(1)}47%{transform:translateX(62px) scaleX(1)}52%{transform:translateX(62px) scaleX(-1)}72%{transform:translateX(24px) translateY(-1px) scaleX(-1)}92%{transform:translateX(-12px) scaleX(-1)}100%{transform:translateX(-10px) scaleX(1)}}
  @keyframes doveAcrossGarden{0%,100%{transform:translateX(-8px) translateY(0) rotate(-1deg)}25%{transform:translateX(22px) translateY(-8px) rotate(2deg)}50%{transform:translateX(52px) translateY(0) rotate(-1deg)}75%{transform:translateX(18px) translateY(-5px) rotate(2deg)}}
  @keyframes butterflyAcrossGarden{0%,100%{transform:translate(-12px,5px) rotate(-4deg)}20%{transform:translate(18px,-12px) rotate(7deg)}45%{transform:translate(50px,0) rotate(-5deg)}70%{transform:translate(24px,14px) rotate(4deg)}}

  .home-water-feature{
    --water-speed:3.8s;
    --water-opacity:.58;
    --wave-size:1.28;
    --shimmer-intensity:.42;
  }
  .home-water-feature .water-ripple,.home-water-feature .water-shimmer,.home-water-feature .water-bubble{position:absolute;pointer-events:none;}
  .home-water-feature .water-ripple{left:50%;top:50%;width:72%;height:72%;border-radius:50%;border:1px solid rgba(255,255,255,.72);box-shadow:0 0 8px rgba(186,230,253,.24);animation:waterSurfaceRipple var(--water-speed) ease-out infinite;}
  .home-water-feature .water-ripple.r2{width:48%;height:48%;animation-delay:calc(var(--water-speed) * -.48);opacity:.7;}
  .home-water-feature .water-shimmer{inset:8% 6%;border-radius:999px;background:linear-gradient(105deg,transparent 0 20%,rgba(255,255,255,.76) 42%,rgba(186,230,253,.34) 53%,transparent 76%);mix-blend-mode:screen;animation:waterSurfaceShimmer calc(var(--water-speed) * 1.2) ease-in-out infinite;}
  .home-water-feature .water-bubble{width:4px;height:4px;border-radius:50%;background:rgba(255,255,255,.86);box-shadow:0 0 6px rgba(186,230,253,.48);animation:waterTinyBubble calc(var(--water-speed) * .9) ease-in-out infinite;}
  .home-water-feature .water-bubble.b1{left:24%;bottom:22%;animation-delay:-.4s;}
  .home-water-feature .water-bubble.b2{left:52%;bottom:18%;animation-delay:-1.3s;}
  .home-water-feature .water-bubble.b3{right:20%;bottom:26%;animation-delay:-2s;}
  .home-water-feature.furn_fountain{--water-speed:3.2s;--water-opacity:.62;--wave-size:1.18;--shimmer-intensity:.46;}
  .home-water-feature.furn_waterfall{--water-speed:2.55s;--water-opacity:.66;--wave-size:1.34;--shimmer-intensity:.50;}
  .home-water-feature.furn_lily_pond{--water-speed:4.8s;--water-opacity:.46;--wave-size:1.22;--shimmer-intensity:.34;}
  .home-water-feature.furn_waterfall::before{animation:streamGlimmer 3.1s ease-in-out infinite,waterDownwardFlow 1.8s linear infinite!important;background-size:100% 26px!important;}
  .home-game-item.furn_waterfall .home-game-body::after{animation:waterfallAbundantFlow .72s linear infinite,waterDownwardFlow 1.45s linear infinite!important;}
  .home-game-item.furn_lily_pond .home-game-detail.water{animation:pondRipple var(--water-speed,4.8s) ease-in-out infinite!important;}

  .home-pet-track{z-index:5!important;opacity:.9!important;}
  .home-pet-track.lamb-track{left:3%!important;right:48%!important;bottom:11%!important;}
  .home-pet-track.dove-track{left:5%!important;right:48%!important;top:39%!important;}
  .home-pet-track.butterfly-track{left:48%!important;right:5%!important;top:34%!important;}
  .home-pet-track.lamb-track .animated-shop-item{min-width:clamp(132px,40vw,210px)!important;animation-name:lambMeadowWalk!important;animation-duration:24s!important;animation-timing-function:ease-in-out!important;}
  .home-pet-track.dove-track .animated-shop-item{min-width:clamp(126px,38vw,198px)!important;animation-name:doveAcrossGarden!important;animation-duration:18s!important;animation-timing-function:ease-in-out!important;}
  .home-pet-track.butterfly-track .animated-shop-item{min-width:clamp(132px,40vw,210px)!important;animation-name:butterflyAcrossGarden!important;animation-duration:16s!important;animation-timing-function:ease-in-out!important;}

  .home-pet-track.lamb-track .home-game-item.pet_lamb{width:clamp(56px,15vw,78px)!important;height:clamp(42px,11vw,58px)!important;overflow:visible!important;}
  .home-room .home-game-item.pet_lamb .home-game-glow{opacity:.08!important;}
  .home-room .home-game-item.pet_lamb .home-game-shadow{left:4%!important;right:10%!important;bottom:1px!important;height:9px!important;opacity:.32!important;filter:blur(1.2px)!important;}
  .home-room .home-game-item.pet_lamb .home-game-body{left:8%!important;right:24%!important;bottom:11px!important;height:45%!important;border-radius:62% 48% 44% 46%!important;background:radial-gradient(circle at 18% 19%,#fff 0 8%,transparent 9%),radial-gradient(circle at 40% 11%,#fff 0 10%,transparent 11%),radial-gradient(circle at 61% 24%,#fff 0 8%,transparent 9%),linear-gradient(180deg,#fffdf8 0%,#efe4d5 63%,#b9a188 100%)!important;border:1px solid rgba(116,88,55,.14)!important;box-shadow:inset 0 5px 8px rgba(255,255,255,.88),0 5px 7px rgba(82,55,15,.08)!important;}
  .home-room .home-game-item.pet_lamb .home-game-body::before{content:"";position:absolute;left:10%;right:8%;top:-7px;height:17px;border-radius:50%;background:radial-gradient(circle at 14% 66%,#fff 0 5px,transparent 6px),radial-gradient(circle at 38% 42%,#fff 0 6px,transparent 7px),radial-gradient(circle at 66% 64%,#fff 0 5px,transparent 6px)!important;}
  .home-room .home-game-item.pet_lamb .home-game-body::after{content:"";position:absolute;left:-5px;top:15px;width:9px;height:9px;border-radius:60% 40% 50% 50%;background:#eee1cf!important;box-shadow:none!important;}
  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left{right:2%!important;top:25%!important;width:21px!important;height:18px!important;border-radius:58% 48% 50% 42%!important;background:linear-gradient(180deg,#fffaf2,#d9c2a9)!important;border:1px solid rgba(116,88,55,.16)!important;box-shadow:inset 0 3px 4px rgba(255,255,255,.74)!important;animation:lambHeadBob 2.6s ease-in-out infinite!important;}
  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left::after{content:"";position:absolute;right:5px;top:6px;width:3px!important;height:3px!important;border-radius:50%;background:#3d2800!important;box-shadow:-1px -1px 0 rgba(255,255,255,.8)!important;}
  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left::before{content:"";position:absolute;right:2px!important;top:-6px!important;width:7px!important;height:11px!important;border-radius:70% 40%!important;background:#c8ad92!important;transform:rotate(20deg)!important;box-shadow:-11px 3px 0 -1px #c8ad92!important;}
  .home-room .home-game-item.pet_lamb .home-game-detail.wing-right{display:block!important;left:3%!important;right:auto!important;top:41%!important;width:8px!important;height:8px!important;border-radius:50%!important;background:#d8c6b2!important;border:none!important;transform:rotate(-18deg)!important;}
  .home-room .home-game-item.pet_lamb .home-game-icon{display:block!important;position:absolute!important;left:18%!important;right:22%!important;bottom:4px!important;width:auto!important;height:9px!important;margin:0!important;font-size:0!important;}
  .home-room .home-game-item.pet_lamb .home-game-icon::before,.home-room .home-game-item.pet_lamb .home-game-icon::after{content:"";position:absolute;bottom:0;width:3.5px!important;height:8px!important;border-radius:999px!important;background:#6f5a47!important;box-shadow:9px 0 0 #836e58!important;animation:lambLegs 1.15s ease-in-out infinite!important;}
  .home-room .home-game-item.pet_lamb .home-game-icon::before{left:4px!important;}
  .home-room .home-game-item.pet_lamb .home-game-icon::after{right:4px!important;animation-delay:.18s!important;}

  /* Waterfall background pass: make the waterfall read as scenery, not a foreground object */
  .home-slot.feature-slot.furn_waterfall-slot{
    right:11%!important;
    bottom:23%!important;
    z-index:2!important;
    max-width:30%!important;
    opacity:.82!important;
    filter:saturate(.92) brightness(1.03) drop-shadow(0 8px 12px rgba(65,88,92,.10))!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot::after{display:none!important;}
  .home-slot.feature-slot.furn_waterfall-slot .animated-shop-item{
    min-width:clamp(86px,23vw,126px)!important;
    min-height:clamp(94px,24vw,136px)!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot .home-game-item.furn_waterfall{
    width:clamp(88px,22vw,120px)!important;
    height:clamp(100px,24vw,134px)!important;
    transform:scale(.92)!important;
    filter:none!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot .home-game-shadow{
    opacity:.18!important;
    height:10px!important;
    filter:blur(2px)!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot .home-game-glow{
    opacity:.06!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot .home-game-body{
    opacity:.88!important;
    box-shadow:inset 7px 8px 10px rgba(255,255,255,.30),inset -8px -9px 12px rgba(24,44,50,.16),0 4px 8px rgba(82,55,15,.04)!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot .home-game-detail.water{
    opacity:.78!important;
    height:38px!important;
  }
  .home-water-feature.furn_waterfall{
    right:14%!important;
    bottom:20%!important;
    width:27%!important;
    height:8%!important;
    z-index:2!important;
    opacity:.48!important;
    filter:blur(.1px)!important;
    transform:rotate(-11deg)!important;
  }
  .home-water-feature.furn_waterfall .water-ripple{opacity:.55!important;}
  .home-water-feature.furn_waterfall .water-shimmer{opacity:.55!important;}


  /* Final Home composition polish: calmer feature zones, clearer stages, premium water */
  @keyframes homeSacredBuildGlow{
    0%{opacity:0;transform:translateX(-50%) translateY(18px) scale(.96);filter:blur(1.2px) brightness(1.1)}
    28%{opacity:.72;transform:translateX(-50%) translateY(8px) scale(.99);filter:blur(.45px) brightness(1.08)}
    72%{opacity:1;transform:translateX(-50%) translateY(-1px) scale(1.01);filter:blur(.05px) brightness(1.04)}
    100%{opacity:1;transform:translateX(-50%) translateY(0) scale(1);filter:none}
  }
  @keyframes homeBlessingMist{
    0%,100%{opacity:.18;transform:translateY(0) scaleX(.96)}
    50%{opacity:.36;transform:translateY(-3px) scaleX(1.04)}
  }
  @keyframes featureWaterfallSheet{
    0%{background-position:0 -28px;transform:translateY(-5px) scaleY(1.02)}
    100%{background-position:0 38px;transform:translateY(5px) scaleY(1.05)}
  }
  @keyframes fountainGentleOverflow{
    0%,100%{transform:translateY(0) scale(.98);opacity:.82}
    50%{transform:translateY(-4px) scale(1.05);opacity:.98}
  }
  @keyframes quietPathTwinkle{
    0%,100%{opacity:.24;filter:brightness(1)}
    48%{opacity:.42;filter:brightness(1.12)}
  }

  .home-room{
    isolation:isolate;
    background:linear-gradient(180deg,rgba(255,253,246,.24),rgba(255,248,223,.08));
  }
  .home-room::before{
    content:"";
    position:absolute;
    left:5%;
    right:5%;
    bottom:0;
    height:42%;
    z-index:1;
    pointer-events:none;
    background:
      radial-gradient(ellipse at 50% 42%,rgba(255,253,246,.24),transparent 64%),
      linear-gradient(180deg,transparent,rgba(119,157,88,.10));
  }
  .home-midground-glow{left:12%!important;right:12%!important;bottom:35%!important;opacity:.46!important;}
  .home-stage-4 .home-midground-glow,.home-stage-5 .home-midground-glow,.home-stage-6 .home-midground-glow{opacity:.64!important;}
  .home-distant-city{filter:saturate(.88) brightness(1.04);opacity:.16!important;}
  .home-stage-5 .home-distant-city{opacity:.42!important;}
  .home-stage-6 .home-distant-city{opacity:.50!important;}
  .home-background-rainbow{opacity:.34!important;filter:drop-shadow(0 0 12px rgba(255,248,223,.45))!important;}
  .home-background-rainbow::after{opacity:.34!important;}

  .home-cardboard,.home-little-shelter,.home-house,.home-mansion,.home-palace,.home-chapel{
    z-index:5!important;
    left:50%!important;
    filter:drop-shadow(0 16px 20px rgba(98,70,24,.16))!important;
  }
  .home-stage-0 .home-cardboard{
    top:47%!important;
    width:130px!important;
    height:76px!important;
    transform:translateX(-50%) rotate(-1deg)!important;
  }
  .home-stage-0 .home-cardboard-body{
    left:12px!important;
    right:12px!important;
    height:50px!important;
    background:linear-gradient(180deg,#e7c07c,#bf7b35)!important;
  }
  .home-stage-0 .home-cardboard-roof{
    left:0!important;
    right:0!important;
    height:32px!important;
    clip-path:polygon(8% 100%,50% 0,92% 100%)!important;
  }
  .home-stage-1 .home-little-shelter{
    top:40%!important;
    width:170px!important;
    height:112px!important;
  }
  .home-stage-1 .home-little-shelter-roof{
    height:60px!important;
    clip-path:polygon(6% 100%,50% 0,94% 100%)!important;
  }
  .home-stage-2 .home-house{
    top:35%!important;
    width:188px!important;
    height:128px!important;
  }
  .home-stage-2 .home-house-roof{
    height:58px!important;
    clip-path:polygon(0 100%,50% 0,100% 100%)!important;
  }
  .home-stage-3 .home-mansion{
    top:29%!important;
    width:224px!important;
    height:150px!important;
  }
  .home-stage-3 .home-mansion-roof{
    clip-path:polygon(0 100%,9% 55%,21% 100%,34% 50%,50% 0,66% 50%,79% 100%,91% 55%,100% 100%)!important;
  }
  .home-stage-4 .home-palace,.home-stage-5 .home-palace,.home-stage-6 .home-palace{
    top:20%!important;
    width:232px!important;
    height:164px!important;
  }
  .home-stage-5 .home-palace{
    width:248px!important;
    top:19%!important;
  }
  .home-stage-6 .home-palace{
    width:260px!important;
    top:18%!important;
    filter:drop-shadow(0 18px 26px rgba(240,203,118,.26)) drop-shadow(0 0 16px rgba(255,253,246,.38))!important;
  }
  .home-palace-star{color:#b98724!important;text-shadow:0 0 10px rgba(255,248,223,.82)!important;}
  .home-room-transforming .home-cardboard,
  .home-room-transforming .home-little-shelter,
  .home-room-transforming .home-house,
  .home-room-transforming .home-mansion,
  .home-room-transforming .home-palace,
  .home-room-transforming .home-chapel{
    animation:homeSacredBuildGlow 4.8s cubic-bezier(.2,.72,.2,1) forwards!important;
    transform-origin:50% 100%!important;
  }
  .home-transform-overlay{
    background:radial-gradient(ellipse at 50% 52%,rgba(255,253,246,.30),transparent 58%)!important;
  }
  .home-transform-overlay .build-spark{opacity:.42!important;}
  .home-transform-overlay .builder-angel{opacity:.78!important;}

  .home-gem{opacity:.08!important;animation:quietPathTwinkle 9s ease-in-out infinite!important;}
  .home-gem.g1{left:22%!important;bottom:20%!important;}
  .home-gem.g2{right:24%!important;bottom:21%!important;opacity:.12!important;}
  .home-gem.g3,.home-gem.g4{display:none!important;}
  .home-path-gem{opacity:.28!important;width:7px!important;height:7px!important;animation:quietPathTwinkle 8.5s ease-in-out infinite!important;}
  .home-path-gem.pg1{left:47%!important;bottom:28%!important;}
  .home-path-gem.pg2{left:54%!important;bottom:20%!important;}
  .home-path-gem.pg3{left:45%!important;bottom:13%!important;}
  .home-game-sparkles{opacity:.08!important;}
  .home-game-sparkles span:nth-child(n+2){display:none!important;}

  .home-gate{
    bottom:18%!important;
    width:58px!important;
    height:116px!important;
    opacity:.78!important;
    z-index:6!important;
    border-width:2.5px!important;
    box-shadow:inset 0 0 10px rgba(255,248,223,.30),0 8px 13px rgba(82,55,15,.08)!important;
  }
  .home-gate.left{left:10px!important;transform:rotate(-3deg)!important;}
  .home-gate.right{right:10px!important;transform:rotate(3deg)!important;}
  .home-lantern{z-index:6!important;opacity:.62!important;transform:scale(.72)!important;}

  .home-slot.decor-slot{
    right:5%!important;
    bottom:31%!important;
    z-index:4!important;
    max-width:16%!important;
  }
  .home-slot.garden-slot{
    left:4%!important;
    bottom:6%!important;
    z-index:4!important;
    max-width:26%!important;
  }
  .home-slot.garden-slot .home-game-item{
    width:clamp(70px,19vw,100px)!important;
    height:clamp(50px,13vw,72px)!important;
  }
  .home-slot.feature-slot{
    z-index:4!important;
    max-width:28%!important;
  }
  .home-slot.feature-slot.furn_fountain-slot{
    right:27%!important;
    bottom:7%!important;
  }
  .home-slot.feature-slot.furn_lily_pond-slot{
    right:27%!important;
    bottom:5.5%!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot{
    right:8%!important;
    bottom:22%!important;
    z-index:2!important;
    max-width:29%!important;
    opacity:.74!important;
    filter:saturate(.9) brightness(1.03)!important;
  }

  .home-water-feature{z-index:3!important;pointer-events:none!important;}
  .home-water-feature.furn_fountain{
    right:27.5%!important;
    bottom:6.4%!important;
    width:18%!important;
    height:6.2%!important;
    opacity:.56!important;
    transform:rotate(-6deg)!important;
  }
  .home-water-feature.furn_lily_pond{
    right:27%!important;
    bottom:5.4%!important;
    width:21%!important;
    height:6.6%!important;
    opacity:.42!important;
  }
  .home-water-feature.furn_waterfall{
    right:10%!important;
    bottom:17.5%!important;
    width:25%!important;
    height:11%!important;
    z-index:2!important;
    opacity:.46!important;
    transform:rotate(-10deg)!important;
  }
  .home-water-feature::before{
    background:linear-gradient(90deg,rgba(255,255,255,.82),rgba(181,238,252,.62) 34%,rgba(50,167,210,.26) 64%,rgba(255,255,255,.48))!important;
    box-shadow:inset 0 1px 3px rgba(255,255,255,.74),0 0 15px rgba(186,230,253,.18)!important;
  }
  .home-water-feature::after{animation:homeBlessingMist 3.6s ease-in-out infinite!important;opacity:.45!important;}
  .home-slot.feature-slot .home-game-item.furn_fountain{
    width:clamp(52px,13vw,74px)!important;
    height:clamp(54px,14vw,78px)!important;
  }
  .home-game-item.furn_fountain .home-game-body{
    border-radius:16px 16px 24px 24px!important;
    box-shadow:inset 0 6px 8px rgba(255,255,255,.74),inset 0 -9px 11px rgba(24,90,125,.16),0 7px 10px rgba(82,55,15,.10)!important;
  }
  .home-game-item.furn_fountain .home-game-detail.water{
    top:-18%!important;
    height:58%!important;
    animation:fountainGentleOverflow 1.75s ease-in-out infinite!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot .home-game-item.furn_waterfall{
    width:clamp(82px,20vw,112px)!important;
    height:clamp(100px,24vw,136px)!important;
    transform:scale(.9)!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot .home-game-body{
    opacity:.82!important;
    background:linear-gradient(145deg,#f8fbff 0%,#dbeafe 23%,#94d7f0 56%,#b7e7d3 100%)!important;
  }
  .home-game-item.furn_waterfall .home-game-body::after{
    left:26%!important;
    right:26%!important;
    top:6%!important;
    bottom:4%!important;
    background:repeating-linear-gradient(180deg,rgba(255,255,255,.95) 0 5px,rgba(218,248,255,.86) 5px 12px,rgba(72,181,221,.66) 12px 22px,rgba(255,255,255,.76) 22px 28px)!important;
    background-size:100% 28px!important;
    animation:featureWaterfallSheet 1.15s linear infinite!important;
    box-shadow:-12px 22px 0 -8px rgba(255,255,255,.46),12px 30px 0 -10px rgba(255,255,255,.38),0 45px 0 -13px rgba(186,230,253,.32)!important;
  }
  .home-game-item.furn_waterfall .home-game-detail.water{
    height:34px!important;
    opacity:.74!important;
  }

  .home-pet-track{
    z-index:5!important;
    opacity:.78!important;
    pointer-events:none!important;
  }
  .home-pet-track.lamb-track{
    left:4%!important;
    right:54%!important;
    bottom:10%!important;
  }
  .home-pet-track.butterfly-track{
    left:58%!important;
    right:8%!important;
    top:35%!important;
  }
  .home-pet-track.dove-track{
    left:6%!important;
    right:58%!important;
    top:40%!important;
  }
  .home-pet-track.lamb-track .animated-shop-item{min-width:clamp(118px,34vw,174px)!important;animation-duration:28s!important;}
  .home-pet-track.butterfly-track .animated-shop-item{min-width:clamp(104px,30vw,162px)!important;animation-duration:20s!important;}
  .home-pet-track.dove-track .animated-shop-item{min-width:clamp(104px,30vw,160px)!important;animation-duration:22s!important;}
  .home-pet-track.lamb-track .home-game-item.pet_lamb{
    width:clamp(48px,12vw,66px)!important;
    height:clamp(38px,10vw,52px)!important;
  }
  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{
    width:clamp(30px,8vw,44px)!important;
    height:clamp(28px,7vw,40px)!important;
  }
  .home-pet-track.dove-track .home-game-item.pet_dove{
    width:clamp(38px,10vw,54px)!important;
    height:clamp(34px,9vw,48px)!important;
  }
  .home-slot:not(.home-pet-track) .animated-shop-item:hover .home-game-item{transform:scale(1.03)!important;}

  @media (max-width:520px){
    .home-stage-4 .home-palace,.home-stage-5 .home-palace,.home-stage-6 .home-palace{
      width:clamp(204px,64vw,238px)!important;
      top:20%!important;
    }
    .home-stage-3 .home-mansion{width:clamp(198px,60vw,224px)!important;}
    .home-stage-2 .home-house{width:clamp(168px,52vw,190px)!important;}
    .home-gate{width:48px!important;height:102px!important;opacity:.62!important;}
    .home-slot.feature-slot.furn_fountain-slot{right:25%!important;bottom:7%!important;}
    .home-slot.feature-slot.furn_lily_pond-slot{right:25%!important;bottom:5.5%!important;}
    .home-slot.feature-slot.furn_waterfall-slot{right:5%!important;bottom:22%!important;max-width:30%!important;}
    .home-water-feature.furn_waterfall{right:7%!important;bottom:17%!important;width:25%!important;}
    .home-pet-track.lamb-track{left:4%!important;right:56%!important;bottom:10%!important;}
    .home-pet-track.butterfly-track{left:58%!important;right:6%!important;top:36%!important;}
  }


  /* Mobile prayer/home usability: full answered-prayer scroll and calmer home preview */
  @media (max-width: 520px){
    #answered-prayers-section .answered-altar-card{
      max-height:calc(100dvh - 118px)!important;
      display:flex!important;
      flex-direction:column!important;
      overflow:hidden!important;
    }
    #answered-prayers-section .testimony-scroll-area{
      flex:1 1 auto!important;
      max-height:none!important;
      min-height:min(360px,54dvh)!important;
      overflow-y:auto!important;
      padding:0 3px 84px 0!important;
      scroll-padding-bottom:96px!important;
    }
    #answered-prayers-section .testimony-entry{
      overflow:visible!important;
    }
    #answered-prayers-section .public-share-box{
      margin-bottom:7px!important;
    }
    #answered-prayers-section .public-share-content{
      padding-bottom:6px!important;
    }
    .home-page{
      gap:10px!important;
    }
    .home-page .focus-card{
      padding:13px!important;
    }
    .home-page .focus-card > div{
      align-items:flex-start!important;
    }
    .home-page .focus-card h2{
      font-size:17px!important;
      line-height:1.15!important;
    }
    .home-page .focus-card p{
      font-size:12.5px!important;
      line-height:1.45!important;
    }
    .home-page .sticky-preview-card{
      padding:10px!important;
      margin-left:-2px!important;
      margin-right:-2px!important;
    }
    .home-page .sticky-preview-card > div:first-child{
      margin-bottom:8px!important;
    }
    .home-page .sticky-preview-card .home-room{
      min-height:330px!important;
      border-radius:20px!important;
    }
    .home-page .home-room{
      min-height:330px!important;
    }
    .home-page .home-floating-label{
      left:7px!important;
      right:7px!important;
      top:7px!important;
    }
    .home-page .home-floating-label .placed-chip{
      max-width:46%!important;
      font-size:9.5px!important;
      padding:4px 6px!important;
      opacity:.82!important;
    }
    .home-page .home-gate{
      opacity:.68!important;
    }
    .home-page .home-pet-track{
      opacity:.84!important;
    }
    .home-page .home-shop-category-row{
      scroll-snap-type:x proximity;
      padding-bottom:8px!important;
    }
    .home-page .home-category-card{
      scroll-snap-align:start;
      min-width:92px!important;
    }
    .home-page .home-shop-card{
      min-height:118px!important;
    }
    .home-page .home-shop-card.expanded{
      min-height:164px!important;
    }
    .home-care-card > div:first-child{grid-template-columns:1fr!important;}
    .home-bless-btn{width:100%;font-size:12.5px!important;min-height:40px!important;}
    .home-care-signal-row{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:6px!important;}
    .home-care-signal{font-size:11px!important;min-height:30px!important;}
    .home-care-action-row{grid-template-columns:1fr!important;gap:6px!important;}
    .home-care-action{font-size:12px!important;min-height:38px!important;}
  }

  @media (max-width: 380px){
    #answered-prayers-section .answered-altar-card{
      max-height:calc(100dvh - 104px)!important;
    }
    #answered-prayers-section .testimony-scroll-area{
      min-height:min(330px,52dvh)!important;
      padding-bottom:96px!important;
    }
    .home-page .sticky-preview-card .home-room,
    .home-page .home-room{
      min-height:300px!important;
    }
    .home-page .home-category-card{
      min-width:88px!important;
    }
  }

  /* Growth mobile tiles: prevent stat and stone labels from overlapping */
  .growth-stat-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;}
  .growth-stat-tile{text-align:center;background:rgba(255,253,240,.55);border:1px solid var(--card-border);border-radius:14px;padding:10px 6px;min-width:0;overflow:hidden;}
  .growth-stat-value{font-size:17px;font-weight:900;color:var(--text-dark);line-height:1;overflow-wrap:anywhere;}
  .growth-stat-label{font-size:13.5px;color:var(--text-faint);font-weight:800;margin-top:4px;line-height:1.12;overflow-wrap:anywhere;}
  .testimony-stone-card{min-width:0;overflow:hidden;}
  .testimony-gem-name{overflow-wrap:anywhere;word-break:normal;hyphens:auto;max-width:100%;margin:0 auto;}

  /* Home transition anchor fix: keep every house stage centered while it builds */
  @keyframes homeAnchoredReveal{
    0%{opacity:.66;transform:translateX(-50%) translateY(10px) scale(.985);filter:blur(.35px) brightness(1.04)}
    42%{opacity:.9;transform:translateX(-50%) translateY(2px) scale(1.005);filter:blur(.08px) brightness(1.05)}
    100%{opacity:1;transform:translateX(-50%) translateY(0) scale(1);filter:none}
  }
  .home-room-transforming .home-cardboard,
  .home-room-transforming .home-little-shelter,
  .home-room-transforming .home-apartment,
  .home-room-transforming .home-house,
  .home-room-transforming .home-mansion,
  .home-room-transforming .home-palace,
  .home-room-transforming .home-chapel{
    animation:homeAnchoredReveal 4.15s cubic-bezier(.22,.7,.22,1) forwards!important;
    transform-origin:50% 100%!important;
    left:50%!important;
  }

  /* Mobile modal safety: prayer/testimony popups stay fully visible on phones */
  .mobile-modal-overlay{position:fixed;inset:0;background:rgba(60,40,0,.34);backdrop-filter:blur(8px);z-index:1400;display:flex;align-items:center;justify-content:center;padding:18px;overflow-y:auto;overscroll-behavior:contain;}
  .mobile-modal-card{max-width:460px;width:100%;padding:22px;background:linear-gradient(160deg,#fffdf2,#fff3c4);border-color:rgba(201,144,18,.44)!important;box-shadow:0 16px 60px rgba(138,90,8,.28);}
  .testimony-entry-icon{width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex:0 0 34px;background:radial-gradient(circle,#fff 0%,#edf7ff 58%,rgba(240,203,118,.28) 100%);border:1px solid rgba(125,174,212,.24);box-shadow:0 5px 12px rgba(65,95,125,.08);font-size:18px;line-height:1;}

  /* Mobile testimony editing: keep save/share controls reachable */
  .testimony-scroll-area{max-height:min(62vh,520px);overflow-y:auto;overscroll-behavior:contain;padding-right:4px;scrollbar-width:thin;scrollbar-color:rgba(201,144,18,.45) rgba(255,248,223,.35);}
  .testimony-scroll-area::-webkit-scrollbar{width:7px;}
  .testimony-scroll-area::-webkit-scrollbar-track{background:rgba(255,248,223,.35);border-radius:999px;}
  .testimony-scroll-area::-webkit-scrollbar-thumb{background:rgba(201,144,18,.45);border-radius:999px;}
  .testimony-entry.editing{border-color:rgba(201,144,18,.42);box-shadow:0 6px 22px rgba(138,90,8,.10);}
  .testimony-edit-panel{display:flex;flex-direction:column;gap:8px;margin-bottom:8px;}
  .testimony-edit-actions{display:grid;grid-template-columns:1fr 1fr;gap:7px;background:linear-gradient(180deg,rgba(255,253,246,.72),rgba(255,253,246,.96));border-radius:14px;padding-top:4px;}
  .testimony-book-scroll{max-height:min(52vh,420px);overflow-y:auto;padding-right:4px;scrollbar-width:thin;scrollbar-color:rgba(201,144,18,.45) rgba(255,248,223,.35);}

  /* User-friendly navigation pass: clearer first steps and locked tabs */
  .app-header{padding:16px 16px 12px;display:flex;align-items:center;justify-content:space-between;gap:10px;}
  .app-title-copy{flex:1;min-width:0;}
  .step-guide-card{background:rgba(255,253,246,.78);border:1px solid rgba(184,147,75,.16);border-radius:16px;padding:12px;margin-top:10px;}
  .step-guide-row{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;}
  .step-guide-pill{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;min-height:58px;border-radius:14px;border:1px solid var(--card-border);background:rgba(255,253,246,.76);color:var(--text-mid);font-size:11.5px;font-weight:900;text-align:center;line-height:1.15;overflow:hidden;}
  .step-guide-pill.done{background:linear-gradient(160deg,rgba(255,253,246,.95),rgba(232,245,233,.62));border-color:rgba(46,125,50,.22);color:var(--green);}
  .step-guide-pill.active{background:linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,243,196,.72));border-color:rgba(201,144,18,.45);color:var(--gold-deep);box-shadow:0 5px 15px rgba(180,130,20,.10);}
  .step-guide-pill.locked{opacity:.5;}
  .step-guide-icon{font-size:18px;line-height:1;}
  .step-guide-status{font-size:9.5px;color:var(--text-faint);font-weight:800;}
  .tab-icon-wrap{position:relative;display:inline-flex;align-items:center;justify-content:center;min-height:24px;}
  .tab-lock-dot{position:absolute;right:-7px;top:-4px;width:14px;height:14px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:rgba(255,253,246,.96);border:1px solid rgba(184,147,75,.28);font-size:8px;line-height:1;box-shadow:0 1px 4px rgba(80,50,0,.10);}
  .friend-code-chip{
    background:linear-gradient(135deg,rgba(246,250,255,.96),rgba(232,246,255,.86))!important;
    border:2px solid rgba(91,143,184,.72)!important;
    color:#315b78!important;
    box-shadow:0 3px 12px rgba(65,95,125,.10)!important;
  }

  /* Mobile readability pass: keeps the app calm and usable on small phones */
  @media (max-width: 520px){
    html,body{overflow-x:hidden;}
    body{font-size:14px;}
    .card{border-radius:16px!important;padding:14px!important;box-shadow:0 2px 12px rgba(138,90,8,.055)!important;}
    .app-header{padding:12px 12px 9px!important;align-items:flex-start!important;}
    .app-title-copy > div:first-child{font-size:11px!important;letter-spacing:1.8px!important;}
    .app-title-copy h1{font-size:20px!important;line-height:1.12!important;}
    .app-title-copy p{font-size:12px!important;line-height:1.3!important;max-width:230px!important;}
    .step-guide-card{padding:9px!important;border-radius:14px!important;margin-top:8px!important;}
    .step-guide-row{gap:5px!important;}
    .step-guide-pill{min-height:50px!important;border-radius:12px!important;font-size:10.5px!important;padding:6px 3px!important;}
    .step-guide-icon{font-size:16px!important;}
    .step-guide-status{font-size:8.5px!important;}
    .tab-icon-wrap{min-height:21px!important;}
    .tab-icon-wrap > span:first-child{font-size:19px!important;}
    .app-header > div:last-child{gap:4px!important;flex-wrap:wrap!important;justify-content:flex-end!important;max-width:132px!important;}
    .app-header > div:last-child button,.app-header > div:last-child > div{font-size:12px!important;padding:6px 8px!important;min-height:34px!important;}
    .gold-btn{font-size:13px!important;padding:10px 13px!important;min-height:42px!important;border-radius:12px!important;white-space:normal!important;line-height:1.25!important;}
    .bottom-tabs{padding:5px 2px max(env(safe-area-inset-bottom,6px),6px)!important;gap:1px!important;}
    .tab-btn{min-width:0!important;max-width:none!important;padding:5px 2px!important;border-radius:12px!important;gap:1px!important;}
    .tab-btn span:first-child{font-size:19px!important;}
    .tab-label{font-size:10.5px!important;line-height:1.1!important;max-width:64px!important;white-space:nowrap!important;overflow-wrap:normal!important;word-break:normal!important;overflow:hidden!important;text-overflow:ellipsis!important;}
    .tab-btn[data-tab-id="wall"] .tab-label{font-size:9.8px!important;max-width:70px!important;letter-spacing:-.1px!important;}
	    .placed-chip,.sticky-preview-label,.meaning-chip{font-size:11px!important;padding:4px 7px!important;line-height:1.2!important;white-space:normal!important;}
	    .armor-shop-grid{gap:8px!important;}
	    .armor-shop-hero{align-items:flex-start!important;gap:8px!important;}
	    .armor-shop-hero h2{font-size:17px!important;line-height:1.12!important;}
	    .armor-shop-hero p{font-size:11.8px!important;line-height:1.28!important;max-width:190px!important;}
	    .armor-righteousness-count{min-width:58px!important;max-width:64px!important;}
	    .armor-righteousness-count > div:first-child{font-size:8.5px!important;letter-spacing:.45px!important;line-height:1!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;word-break:normal!important;overflow-wrap:normal!important;}
	    .armor-righteousness-count > div:last-child{font-size:20px!important;line-height:1.05!important;white-space:nowrap!important;}
	    .armor-shop-card{padding:10px 8px!important;border-radius:16px!important;min-height:142px!important;}
	    .armor-shop-card.expanded{min-height:184px!important;}
	    .armor-shop-card .meaning-chip{font-size:10.5px!important;padding:3px 6px!important;white-space:nowrap!important;max-width:100%!important;}
	    .armor-item-name{font-size:12.5px!important;line-height:1.18!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;max-width:100%!important;}
	    .armor-action-row{display:grid!important;grid-template-columns:1fr 1fr!important;gap:5px!important;}
	    .armor-action-row button{width:100%!important;font-size:11.6px!important;min-height:38px!important;padding:8px 5px!important;white-space:nowrap!important;line-height:1.1!important;}
	    .armor-action-row.owned{grid-template-columns:1fr 1fr!important;}
	    .armor-action-row.owned button{font-size:11.4px!important;}
	    .armor-action-row.owned button:last-child{grid-column:1 / -1!important;min-height:34px!important;padding:7px 5px!important;}
	    .section-divider-label{font-size:10.5px!important;letter-spacing:.5px!important;text-align:center!important;}
    .task-row{padding:11px 12px!important;min-height:50px!important;border-radius:14px!important;}
    .task-row > div:first-child{width:30px!important;height:30px!important;font-size:15px!important;}
    .task-row div{min-width:0;}
    .daily-walk-scroll{display:flex!important;visibility:visible!important;opacity:1!important;max-height:280px!important;gap:7px!important;padding-right:2px!important;overflow-y:auto!important;-webkit-overflow-scrolling:touch!important;}
    .quest-card{padding:16px 14px!important;border-radius:16px!important;}
    .opt-btn,.sam-btn{font-size:13px!important;padding:10px 12px!important;min-height:42px!important;border-radius:12px!important;line-height:1.35!important;}
    .sg-input,.sg-textarea{font-size:15px!important;padding:11px 12px!important;border-radius:12px!important;line-height:1.5!important;}
    .sg-textarea{min-height:110px;}
    .scroll-list{max-height:280px!important;padding-right:2px!important;}
    .scroll-list.compact{max-height:250px!important;}
    .prayer-friends-card [style*="grid-template-columns: 1fr auto"]{grid-template-columns:1fr!important;}
	    .friend-checkbox-list{grid-template-columns:1fr!important;}
	    .quick-prayer-box{padding:10px!important;border-radius:14px!important;}
	    .quick-prayer-toggle{padding:3px 2px!important;gap:6px!important;}
	    .quick-prayer-toggle span:first-child{font-size:12.5px!important;}
	    .quick-prayer-count{font-size:10.8px!important;padding:3px 6px!important;}
	    .quick-prayer-summary{font-size:12px!important;}
	    .quick-prayer-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:6px!important;}
	    .quick-prayer-chip{font-size:12.5px!important;padding:8px 8px!important;}
	    .prayer-friend-row{align-items:flex-start!important;flex-direction:column!important;}
      .prayer-friend-row.compact-friend-row{align-items:center!important;flex-direction:row!important;gap:7px!important;}
      .compact-friend-identity{min-width:0!important;flex:1!important;}
      .compact-friend-identity .prayer-friend-name{font-size:13px!important;}
      .compact-friend-actions{width:auto!important;justify-content:flex-end!important;gap:5px!important;}
      .compact-friend-actions .soft-action-btn{font-size:11.4px!important;padding:7px 7px!important;min-height:31px!important;}
    .prayer-friend-row > div:last-child{width:100%;justify-content:flex-start;}
    .prayer-friend-row.compact-friend-row > div:last-child{width:auto!important;justify-content:flex-end!important;}
    .prayer-page-calm{gap:9px!important;}
    .prayer-page-calm .card p,.prayer-page-calm .card button,.prayer-page-calm .card span{line-height:1.45!important;}
    .prayer-section-tabs{gap:6px!important;padding:5px!important;}
    .prayer-tab-card{padding:9px 10px!important;min-height:44px!important;}
    .friend-card,.answered-altar-card,.public-share-box,.public-preview-card,.testimony-entry,.empty-state{border-radius:14px!important;padding:12px!important;}
    .testimony-scroll-area{max-height:58vh!important;padding-right:3px!important;}
    .testimony-book-scroll{max-height:46vh!important;padding-right:3px!important;}
    .month-scroll-list{max-height:44vh!important;padding-right:3px!important;}
    .testimony-entry.editing{max-height:76vh!important;overflow-y:auto!important;padding-bottom:10px!important;}
    .testimony-edit-actions{position:sticky!important;bottom:0!important;z-index:4!important;padding:8px 0 0!important;box-shadow:0 -8px 14px rgba(255,253,246,.88)!important;}
    .testimony-entry .public-share-box{margin-top:12px!important;padding:8px!important;}
    .public-share-toggle{padding:9px 10px!important;border-radius:12px!important;}
    .public-share-toggle-title{font-size:13px!important;}
    .public-share-toggle-copy{font-size:11.5px!important;}
    .public-share-content{margin-top:8px!important;}
    .testimony-entry > div:first-child{align-items:flex-start!important;}
    .testimony-entry > div:first-child > div:last-child{flex-wrap:wrap!important;justify-content:flex-end!important;}
    .mobile-modal-overlay{align-items:flex-start!important;padding:14px 12px max(14px,env(safe-area-inset-bottom))!important;}
    .mobile-modal-card,.stone-reward-modal,.funds-modal,.sell-modal{max-height:calc(100dvh - 28px)!important;overflow-y:auto!important;padding:18px 15px!important;border-radius:20px!important;}
    .mobile-modal-card textarea{max-height:30vh!important;}
    .testimony-entry-icon{width:30px!important;height:30px!important;flex-basis:30px!important;font-size:16px!important;}
    .testimony-label{font-size:10px!important;padding:3px 7px!important;letter-spacing:.35px!important;}
    .answered-altar-header{align-items:flex-start!important;min-width:0!important;}
    .answered-altar-icon{width:32px!important;height:32px!important;flex:0 0 32px!important;}
    .public-share-row{grid-template-columns:1fr!important;gap:8px!important;}
    .public-share-check{justify-self:start!important;}
    .home-room{min-height:270px!important;border-radius:22px!important;}
    .sticky-preview-card .home-room{min-height:260px!important;}
    .home-floating-label{left:8px!important;right:8px!important;top:8px!important;gap:5px!important;}
    .home-floating-label .placed-chip{font-size:10px!important;padding:4px 6px!important;max-width:48%!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important;}
    .home-category-card{min-width:98px!important;max-width:104px!important;padding:10px 8px!important;border-radius:15px!important;}
    .home-category-card .home-shop-preview{min-height:46px!important;margin-bottom:4px!important;}
    .home-category-card .home-shop-preview .home-game-item{transform:scale(.86);}
    .home-shop-category-row{gap:8px!important;}
    .home-shop-card,.shop-card{padding:10px 8px!important;border-radius:15px!important;min-height:126px!important;}
    .home-shop-card.expanded,.shop-card.expanded{min-height:168px!important;}
    .shop-category-card{min-width:106px!important;flex-basis:106px!important;min-height:100px!important;padding:11px 10px!important;border-radius:16px!important;}
    .shop-category-icon{width:36px!important;height:36px!important;font-size:20px!important;margin-bottom:6px!important;}
    .growth-stat-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:7px!important;}
    .growth-stat-tile{padding:9px 5px!important;border-radius:13px!important;}
    .growth-stat-value{font-size:16px!important;}
    .growth-stat-label{font-size:11.5px!important;line-height:1.05!important;}
    .testimony-stone-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:7px!important;}
    .testimony-stone-card{padding:10px 5px!important;border-radius:14px!important;min-height:132px!important;}
    .testimony-gem-wrap{width:48px!important;height:44px!important;}
    .testimony-gem{width:38px!important;height:34px!important;}
    .testimony-gem-name{font-size:11px!important;line-height:1.12!important;max-width:78px!important;}
    .stone-reward-modal{padding:20px 16px 16px!important;border-radius:20px!important;}
    .stone-reward-actions{flex-direction:column!important;}
    .closet-sheet{border-radius:22px 22px 0 0!important;max-height:92vh!important;}
    .closet-header{padding:12px 14px!important;}
	    .closet-item-btn{width:86px!important;min-width:86px!important;max-width:86px!important;min-height:78px!important;padding:9px 7px!important;border-radius:14px!important;}
	    .closet-item-btn .shop-game-icon{width:42px!important;height:46px!important;margin-bottom:1px!important;}
	    .closet-item-btn .shop-game-symbol{font-size:21px!important;margin-bottom:14px!important;}
	    .closet-item-name{font-size:11.4px!important;line-height:1.15!important;max-width:74px!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;word-break:normal!important;overflow-wrap:normal!important;}
	    .closet-item-btn .meaning-chip{max-width:72px!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;font-size:10px!important;padding:2px 5px!important;}
	    h1,h2,h3,h4,p,button,span{overflow-wrap:anywhere;}
	    .armor-item-name,.closet-item-name,.armor-shop-card .meaning-chip,.closet-item-btn .meaning-chip,.armor-action-row button{white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;word-break:normal!important;overflow-wrap:normal!important;}
	    .serif{letter-spacing:0!important;}
	  }

  @media (max-width: 380px){
    .tab-label{font-size:9.5px!important;max-width:54px!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
    .tab-btn[data-tab-id="wall"] .tab-label{font-size:8.8px!important;max-width:62px!important;}
    .tab-btn span:first-child{font-size:18px!important;}
    .card{padding:12px!important;}
    .home-room{min-height:250px!important;}
    .sticky-preview-card .home-room{min-height:240px!important;}
    .placed-chip{font-size:10px!important;}
  }

  @media (prefers-reduced-motion: reduce){
    *,*::before,*::after{animation-duration:.01ms!important;animation-iteration-count:1!important;scroll-behavior:auto!important;transition-duration:.01ms!important;}
  }

  /* Final mobile quest dot fix: keep Daily Quest progress indicators circular. */
  @media (max-width:520px){
    .quest-progress-dots .quest-dot,
    button.quest-dot{
      width:10px!important;
      min-width:10px!important;
      max-width:10px!important;
      height:10px!important;
      min-height:10px!important;
      max-height:10px!important;
      flex:0 0 10px!important;
      aspect-ratio:1 / 1!important;
      border-radius:999px!important;
      padding:0!important;
      line-height:0!important;
      display:block!important;
      box-sizing:border-box!important;
    }
  }

  /* Final calm visual polish: softer color balance, clearer mobile taps, and Home hierarchy */
  .card{
    background:linear-gradient(160deg,rgba(255,253,246,.96),rgba(246,250,255,.52))!important;
    border-color:rgba(125,174,212,.16)!important;
    box-shadow:0 2px 14px rgba(65,95,125,.055)!important;
  }
  .focus-card{
    background:linear-gradient(135deg,rgba(255,253,246,.98),rgba(236,253,245,.44))!important;
    border-color:rgba(134,190,155,.18)!important;
  }
  .soft-guide-card,.quest-card{
    background:linear-gradient(145deg,rgba(255,253,246,.94),rgba(246,250,255,.58))!important;
    border-color:rgba(125,174,212,.15)!important;
  }
  .gold-btn{
    box-shadow:0 3px 10px rgba(138,90,8,.16)!important;
  }
  .bottom-tabs{
    background:rgba(255,253,246,.98)!important;
    border-top-color:rgba(125,174,212,.20)!important;
    box-shadow:0 -3px 18px rgba(65,95,125,.08)!important;
  }
  .tab-btn.active{
    box-shadow:0 2px 9px rgba(138,90,8,.16)!important;
  }
  .sg-input,.sg-textarea{
    background:rgba(255,253,246,.92)!important;
    border-color:rgba(125,174,212,.24)!important;
  }
  .sg-input:focus,.sg-textarea:focus{
    border-color:rgba(201,144,18,.48)!important;
    box-shadow:0 0 0 3px rgba(186,230,253,.34)!important;
  }

  .home-page .sticky-preview-card{
    background:linear-gradient(160deg,rgba(255,253,246,.98),rgba(236,250,255,.58))!important;
    border-color:rgba(125,174,212,.18)!important;
  }
  .home-room{
    border-color:rgba(125,174,212,.20)!important;
    box-shadow:inset 0 -24px 34px rgba(65,95,125,.05),0 5px 18px rgba(65,95,125,.08)!important;
  }
  .home-rays{opacity:.56!important;}
  .home-midground-glow{opacity:.50!important;}
  .home-distant-city{opacity:.16!important;}
  .home-stage-5 .home-distant-city,.home-stage-6 .home-distant-city{opacity:.36!important;}
  .home-background-rainbow{opacity:.24!important;filter:drop-shadow(0 0 8px rgba(255,248,223,.32))!important;}
  .home-background-rainbow::after{display:none!important;}
  .home-gem{opacity:.055!important;filter:saturate(.8)!important;}
  .home-gem.g2{opacity:.08!important;}
  .home-path-gem{opacity:.18!important;box-shadow:0 0 4px rgba(125,211,252,.18),0 2px 4px rgba(82,55,15,.05)!important;}
  .home-path::before{opacity:.20!important;}
  .home-palace,.home-house,.home-mansion,.home-chapel{
    z-index:7!important;
    filter:drop-shadow(0 16px 22px rgba(124,90,30,.18)) drop-shadow(0 0 10px rgba(255,248,223,.30))!important;
  }
  .home-stage-4 .home-palace,.home-stage-5 .home-palace,.home-stage-6 .home-palace{
    transform:translateX(-50%) scale(1.04)!important;
  }
  .home-palace-body,.home-house-body,.home-mansion-body,.home-chapel-body{
    box-shadow:inset 0 8px 16px rgba(255,255,255,.76),0 8px 18px rgba(82,55,15,.10)!important;
  }
  .home-palace-aura{opacity:.42!important;}
  .home-slot.feature-slot,.home-slot.garden-slot,.home-slot.decor-slot{
    opacity:.78!important;
    z-index:5!important;
  }
  .home-slot.feature-slot .home-game-item,.home-slot.garden-slot .home-game-item,.home-slot.decor-slot .home-game-item{
    transform:scale(.88)!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot .home-game-item{
    transform:scale(.78)!important;
  }
  .home-water-feature{
    opacity:.64!important;
    filter:saturate(.92) brightness(1.02)!important;
  }
  .home-water-feature.furn_waterfall{opacity:.50!important;}
  .home-gate{opacity:.52!important;filter:saturate(.82)!important;}
  .home-lantern{opacity:.55!important;box-shadow:0 0 12px rgba(240,199,94,.34)!important;}
  .home-pet-track{
    opacity:.72!important;
    z-index:4!important;
  }
  .home-pet-track.lamb-track .animated-shop-item{animation-duration:36s!important;}
  .home-pet-track.butterfly-track .animated-shop-item{animation-duration:30s!important;}
  .home-pet-track.dove-track .animated-shop-item{animation-duration:32s!important;}
  .home-pet-track.lamb-track .home-game-item.pet_lamb{
    width:clamp(42px,10vw,58px)!important;
    height:clamp(34px,8.5vw,48px)!important;
  }
  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{
    width:clamp(26px,7vw,38px)!important;
    height:clamp(24px,6.5vw,34px)!important;
  }
  .home-pet-track.dove-track .home-game-item.pet_dove{
    width:clamp(34px,8.5vw,48px)!important;
    height:clamp(30px,8vw,42px)!important;
  }
  .home-slot[style*="z-index: 9"],.home-slot[style*="zIndex:9"]{
    z-index:9!important;
  }

  @media (max-width: 520px){
    body{font-size:15px!important;}
    .app-header{padding:13px 12px 10px!important;}
    .app-title-copy h1{font-size:20px!important;line-height:1.18!important;}
    .app-title-copy p{font-size:12.5px!important;line-height:1.35!important;}
    .card{padding:13px!important;border-radius:16px!important;}
    .gold-btn,button,.opt-btn,.sam-btn{
      min-height:44px!important;
      line-height:1.25!important;
    }
    .bottom-tabs{padding:6px 2px max(env(safe-area-inset-bottom,7px),7px)!important;}
    .tab-btn{padding:6px 2px!important;min-height:48px!important;}
    .tab-label{font-size:10.5px!important;line-height:1.08!important;white-space:nowrap!important;overflow-wrap:normal!important;word-break:normal!important;overflow:hidden!important;text-overflow:ellipsis!important;}
    .tab-btn[data-tab-id="wall"] .tab-label{font-size:9.8px!important;max-width:70px!important;}
    .task-row{min-height:54px!important;padding:12px!important;}
    .daily-walk-scroll{max-height:300px!important;}
    .home-page .sticky-preview-card{padding:10px!important;}
    .home-page .sticky-preview-card .home-room,.home-page .home-room{
      min-height:335px!important;
      border-radius:20px!important;
    }
    .home-stage-4 .home-palace,.home-stage-5 .home-palace,.home-stage-6 .home-palace{
      width:clamp(214px,68vw,248px)!important;
      top:20%!important;
    }
    .home-slot.feature-slot,.home-slot.garden-slot,.home-slot.decor-slot{opacity:.70!important;}
    .home-slot.feature-slot .home-game-item,.home-slot.garden-slot .home-game-item,.home-slot.decor-slot .home-game-item{transform:scale(.74)!important;}
    .home-slot.feature-slot.furn_waterfall-slot{right:5%!important;bottom:24%!important;max-width:25%!important;}
    .home-slot.feature-slot.furn_fountain-slot{right:21%!important;bottom:6%!important;}
    .home-slot.feature-slot.furn_lily_pond-slot{right:20%!important;bottom:5%!important;}
    .home-water-feature.furn_waterfall{right:6%!important;bottom:20%!important;width:22%!important;opacity:.44!important;}
    .home-water-feature.furn_fountain{right:23%!important;bottom:5%!important;width:18%!important;}
    .home-pet-track.lamb-track{left:4%!important;right:61%!important;bottom:11%!important;}
    .home-pet-track.butterfly-track{left:62%!important;right:7%!important;top:38%!important;}
    .home-pet-track.dove-track{left:5%!important;right:64%!important;top:42%!important;}
    .home-gate{width:42px!important;height:92px!important;opacity:.42!important;}
    .home-floating-label .placed-chip{opacity:.68!important;font-size:9.5px!important;}
    .home-category-card,.shop-category-card{min-height:96px!important;}
    .home-shop-card,.shop-card{min-height:124px!important;}
    .home-shop-card button,.shop-card button{min-height:42px!important;}
    h1,h2,h3,h4,p,button,span,div{word-break:normal;overflow-wrap:anywhere;}
  }


  /* Prayer Vessels readability: keep prayer/request cards gentle instead of bold-heavy */
  .prayer-page-calm .prayer-tab-card div,
  .prayer-page-calm .friend-card .placed-chip,
  .prayer-page-calm .prayer-status-chip{
    font-weight:500!important;
  }
  .prayer-page-calm .friend-card button{
    font-weight:700!important;
  }


  /* Checklist edit mode: keep the daily walk calm until the user chooses to edit. */
  .habit-edit-wrap > div:first-child{
    display:flex;
    align-items:center;
    gap:8px;
    width:100%;
  }
  .habit-edit-wrap .task-row{
    min-width:0;
  }
  .habit-row-actions{
    display:none;
    align-items:center;
    gap:6px;
    flex-shrink:0;
  }
  .checklist-edit-mode .habit-row-actions{
    display:flex;
  }
  .habit-row-actions button{
    min-height:36px;
    white-space:nowrap;
  }
  .habit-edit-tip{
    margin:10px 0 0;
    font-size:11.5px;
    color:var(--text-faint);
    line-height:1.45;
  }
  @media(max-width:430px){
    .habit-edit-wrap > div:first-child{
      gap:6px;
    }
    .habit-row-actions{
      gap:5px;
    }
    .habit-row-actions button:first-child{
      min-width:34px!important;
      width:34px!important;
      padding:0!important;
      overflow:hidden;
      font-size:0!important;
    }
    .habit-row-actions button:first-child::before{
      content:"✎";
      font-size:15px;
      color:var(--text-mid);
      line-height:1;
    }
  }


  /* Month grouping for Journal and Testimony Book */
  .month-group{
    border:1px solid rgba(125,174,212,.14);
    border-radius:16px;
    background:rgba(255,253,246,.56);
    overflow:hidden;
  }
  .month-toggle{
    width:100%;
    min-height:46px;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:10px;
    padding:10px 12px;
    border:none;
    background:linear-gradient(145deg,rgba(255,253,246,.92),rgba(246,250,255,.52));
    color:var(--text-dark);
    font-family:'Nunito',sans-serif;
    font-size:13.5px;
    font-weight:800;
    cursor:pointer;
    text-align:left;
  }
  .month-scroll-list{
    display:flex;
    flex-direction:column;
    gap:10px;
    max-height:min(54vh,460px);
    overflow-y:auto;
    padding-right:4px;
    scrollbar-width:thin;
    scrollbar-color:rgba(125,174,212,.45) rgba(246,250,255,.42);
  }
  .month-scroll-list::-webkit-scrollbar{width:7px;}
  .month-scroll-list::-webkit-scrollbar-track{background:rgba(246,250,255,.42);border-radius:999px;}
  .month-scroll-list::-webkit-scrollbar-thumb{background:rgba(125,174,212,.45);border-radius:999px;}
  .month-items{
    display:flex;
    flex-direction:column;
    gap:9px;
    padding:10px;
  }

  /* Home calm hierarchy: palace first, water second, animals/gems subtle */
  .home-room .home-palace,
  .home-room .home-house,
  .home-room .home-mansion,
  .home-room .home-chapel{
    z-index:8!important;
    filter:drop-shadow(0 15px 20px rgba(184,147,75,.20))!important;
  }
  .home-room .home-palace-aura{opacity:.34!important;}
  .home-room .home-gem{opacity:.045!important;animation:quietPathTwinkle 11s ease-in-out infinite!important;}
  .home-room .home-gem.g2{opacity:.065!important;}
  .home-room .home-gem.g3,.home-room .home-gem.g4{display:none!important;}
  .home-room .home-game-sparkles{opacity:.04!important;}
  .home-room .home-game-sparkles span:nth-child(n+2){display:none!important;}
  .home-slot.feature-slot{opacity:.74!important;z-index:5!important;}
  .home-water-feature{opacity:.52!important;z-index:4!important;}
  .home-slot.garden-slot,.home-slot.decor-slot{opacity:.58!important;z-index:4!important;}
  .home-pet-track{opacity:.72!important;z-index:5!important;filter:saturate(.88)!important;}
  .home-pet-track .home-game-item{transform:scale(.76)!important;}
  .home-slot.feature-slot .home-game-item{transform:scale(.72)!important;}
  .home-slot.feature-slot.furn_waterfall-slot{right:6%!important;bottom:24%!important;max-width:24%!important;}
  .home-slot.feature-slot.furn_fountain-slot{right:23%!important;bottom:6.5%!important;}
  .home-water-feature.furn_waterfall{right:7%!important;bottom:20%!important;width:22%!important;opacity:.46!important;}
  .home-water-feature.furn_fountain{right:24%!important;bottom:5.5%!important;width:17%!important;opacity:.50!important;}
  @media (max-width:520px){
    .home-room .home-palace,.home-room .home-house,.home-room .home-mansion,.home-room .home-chapel{transform:translateX(-50%) scale(.95)!important;}
    .home-pet-track .home-game-item{transform:scale(.68)!important;}
    .home-slot.feature-slot .home-game-item{transform:scale(.66)!important;}
  }

  /* Phone-first polish for the next real-user pass */
  @media (max-width:640px){
    .scroll-list{max-height:min(56vh,430px)!important;overflow-y:auto!important;padding-right:4px!important;}
    .friend-card,.testimony-entry,.public-share-box,.empty-state{
      border-radius:14px!important;
    }
    .prayer-page-calm .friend-card [style*="grid-template-columns: 1fr auto auto auto"]{
      grid-template-columns:1fr 1fr!important;
    }
    .prayer-page-calm .friend-card button,
    .public-share-toggle,
    .month-toggle{
      min-height:44px!important;
      font-size:13px!important;
    }
    .testimony-entry > div:first-child{
      align-items:flex-start!important;
    }
    .testimony-entry button{
      min-height:38px!important;
    }
    .home-room{
      isolation:isolate;
    }
    .home-room .home-palace,
    .home-room .home-house,
    .home-room .home-mansion,
    .home-room .home-chapel{
      z-index:9!important;
    }
    .home-water-feature{opacity:.44!important;}
    .home-pet-track{opacity:.60!important;}
    .home-gate,.home-lantern{opacity:.42!important;}
  }

  /* Answered prayers: dedicated touch scroll so testimony submission stays reachable on phones */
  #answered-prayers-section .answered-altar-card{
    display:block!important;
    max-height:none!important;
    overflow:visible!important;
  }
  #answered-prayers-section .answered-prayer-stack{
    display:flex!important;
    flex-direction:column!important;
    gap:10px!important;
    min-height:0!important;
    max-height:min(74dvh,760px)!important;
    overflow-y:auto!important;
    overscroll-behavior:auto!important;
    -webkit-overflow-scrolling:touch!important;
    padding:0 6px 96px 0!important;
    scroll-padding-bottom:120px!important;
    scrollbar-width:thin;
    scrollbar-color:rgba(125,174,212,.45) rgba(246,250,255,.42);
  }
  #answered-prayers-section .answered-prayer-stack::-webkit-scrollbar{width:7px;}
  #answered-prayers-section .answered-prayer-stack::-webkit-scrollbar-track{background:rgba(246,250,255,.42);border-radius:999px;}
  #answered-prayers-section .answered-prayer-stack::-webkit-scrollbar-thumb{background:rgba(125,174,212,.45);border-radius:999px;}
  #answered-prayers-section .public-share-content{
    max-height:none!important;
    overflow:visible!important;
    overscroll-behavior:auto!important;
    -webkit-overflow-scrolling:touch!important;
    padding:0 2px 12px 0!important;
    scroll-padding-bottom:80px!important;
    scrollbar-width:thin;
    scrollbar-color:rgba(125,174,212,.36) rgba(246,250,255,.40);
  }
  #answered-prayers-section .public-share-content::-webkit-scrollbar{width:6px;}
  #answered-prayers-section .public-share-content::-webkit-scrollbar-track{background:rgba(246,250,255,.40);border-radius:999px;}
  #answered-prayers-section .public-share-content::-webkit-scrollbar-thumb{background:rgba(125,174,212,.36);border-radius:999px;}
  #answered-prayers-section .public-share-content > div[id^="submit-public-"]{
    position:relative;
    bottom:auto;
    z-index:2;
    padding:8px 0 4px;
    background:transparent;
  }
  #answered-prayers-section .testimony-entry.public-open .public-share-content > div[id^="submit-public-"]{
    position:sticky!important;
    bottom:0!important;
    background:linear-gradient(180deg,rgba(255,253,248,0),rgba(255,253,248,.96) 32%,rgba(255,253,248,.98))!important;
  }
  @media (max-width:520px){
    #answered-prayers-section .answered-altar-card{
      max-height:none!important;
      overflow:visible!important;
    }
    #answered-prayers-section .answered-prayer-stack{
      max-height:72dvh!important;
      overflow-y:auto!important;
      padding-bottom:320px!important;
      scroll-padding-bottom:320px!important;
    }
    #answered-prayers-section .testimony-entry{
      overflow:visible!important;
    }
    #answered-prayers-section .public-share-content{
      max-height:48dvh!important;
      overflow-y:auto!important;
      overscroll-behavior:contain!important;
      padding-bottom:34px!important;
    }
    #answered-prayers-section .public-share-content > div[id^="submit-public-"]{
      position:sticky!important;
      bottom:0!important;
      background:linear-gradient(180deg,rgba(255,253,248,0),rgba(255,253,248,.96) 32%,rgba(255,253,248,.98))!important;
    }
  }

  /* Visual identity polish: softer resting areas, gold saved for sacred/reward moments */
  .card:not(.answered-altar-card):not(.stone-reward-modal):not(.funds-modal):not(.sell-modal){
    background:linear-gradient(160deg,rgba(255,253,248,.97),rgba(241,248,255,.54))!important;
    border-color:rgba(125,174,212,.14)!important;
    box-shadow:0 2px 12px rgba(65,95,125,.045)!important;
  }
  .focus-card,.soft-guide-card{
    background:linear-gradient(145deg,rgba(255,253,248,.98),rgba(236,253,245,.52))!important;
    border-color:rgba(134,190,155,.18)!important;
  }
  .quest-card,.empty-state{
    background:linear-gradient(145deg,rgba(255,253,248,.94),rgba(241,248,255,.62))!important;
    border-color:rgba(125,174,212,.15)!important;
  }
  .gold-btn:not(.reward-action-btn):not(.sacred-action-btn){
    background:linear-gradient(135deg,#FFFDF8 0%,#EAF6FF 100%)!important;
    border:1px solid rgba(125,174,212,.24)!important;
    color:#4F3510!important;
    box-shadow:0 2px 8px rgba(65,95,125,.07)!important;
  }
  .reward-action-btn,.answered-altar-card .gold-btn,.stone-reward-modal .gold-btn,.funds-modal .gold-btn,.sell-modal .gold-btn{
    background:var(--gold-grad)!important;
    border:none!important;
    box-shadow:0 4px 14px rgba(138,90,8,.22)!important;
  }
  .reward-action-btn.walk-incomplete{
    background:rgba(255,253,246,.84)!important;
    border:1.5px solid var(--card-border)!important;
    color:var(--text-faint)!important;
    box-shadow:none!important;
    text-shadow:none!important;
  }
  .task-row.done{
    border-color:rgba(211,166,75,.34)!important;
    background:linear-gradient(120deg,rgba(255,253,248,.92),rgba(236,253,245,.50))!important;
  }
  .task-row.done > div:first-child{
    background:var(--gold-grad)!important;
  }

  /* Typography polish: serif for headings/verses, readable sans for user-written content */
  .sg-textarea,.testimony-entry p,.friend-card p,.public-preview-card p,.public-share-copy,.empty-state p{
    font-family:'Nunito',sans-serif!important;
    font-style:normal!important;
    letter-spacing:0!important;
  }
  .serif{
    letter-spacing:0!important;
  }
  .section-divider-label, .placed-chip, .prayer-status-chip{
    letter-spacing:.35px!important;
  }

  /* Prayer Vessels: quietest page, fewer glows, simple readable cards */
  .prayer-page-calm{
    gap:10px!important;
  }
  .prayer-page-calm::before{
    opacity:.38!important;
    filter:blur(2px)!important;
  }
  .prayer-page-calm .card,
  .prayer-page-calm .friend-card,
  .prayer-page-calm .prayer-tab-card{
    background:rgba(255,253,248,.91)!important;
    border-color:rgba(125,174,212,.14)!important;
    box-shadow:0 1px 8px rgba(65,95,125,.035)!important;
  }
  .prayer-page-calm .prayer-tab-card.active{
    background:linear-gradient(145deg,rgba(255,253,248,.98),rgba(236,253,245,.50))!important;
    border-color:rgba(134,190,155,.26)!important;
  }
  .prayer-page-calm .friend-card::before,
  .prayer-page-calm .testimony-entry::before{
    opacity:.18!important;
  }
	  .prayer-page-calm .gold-btn:not(.reward-action-btn):not(.sacred-action-btn){
	    background:linear-gradient(135deg,rgba(255,253,248,.98),rgba(236,253,245,.68))!important;
	    border-color:rgba(134,190,155,.22)!important;
	  }
	  .prayer-page-calm .prayer-friends-card,
	  .gold-outline-card{
	    background:linear-gradient(135deg,rgba(255,252,210,.96),rgba(255,248,223,.90),rgba(255,253,246,.96))!important;
	    border:2px solid var(--gold)!important;
	    box-shadow:0 4px 18px rgba(180,130,20,.16)!important;
	  }
	  .prayer-page-calm .prayer-friends-card .soft-action-btn{
	    background:rgba(255,253,246,.86)!important;
	    border-color:rgba(201,144,18,.34)!important;
	    color:var(--gold-deep)!important;
	  }
	  .prayer-page-calm > .sacred-card.gold-outline-card{
	    display:flex;
	    flex-direction:column;
	    gap:12px;
	  }
	  .prayer-page-calm > .sacred-card.gold-outline-card > .prayer-section-tabs{
	    margin:2px 0 0;
	  }
	  .prayer-page-calm > .sacred-card.gold-outline-card > .card{
	    margin:0!important;
	  }
	  .prayer-page-calm > .sacred-card.gold-outline-card > div[id] > .card{
	    margin:0!important;
	    border-color:rgba(125,174,212,.18)!important;
	    box-shadow:0 2px 12px rgba(65,95,125,.04)!important;
	  }

	  /* Home hierarchy: palace first, water second, animals/gems/rainbow subtle */
  .home-page .sticky-preview-card{
    background:linear-gradient(160deg,rgba(255,253,248,.98),rgba(241,248,255,.62))!important;
  }
  .home-room{
    border-color:rgba(125,174,212,.18)!important;
    box-shadow:inset 0 -20px 30px rgba(65,95,125,.045),0 5px 16px rgba(65,95,125,.07)!important;
  }
  .home-palace,.home-house,.home-mansion,.home-chapel{
    z-index:10!important;
    filter:drop-shadow(0 14px 18px rgba(124,90,30,.16)) drop-shadow(0 0 8px rgba(255,248,223,.24))!important;
  }
  .home-palace-aura,.home-rays,.home-midground-glow{
    opacity:.38!important;
  }
  .home-background-rainbow{
    opacity:.16!important;
    filter:saturate(.72) blur(.1px)!important;
  }
  .home-water-feature{
    opacity:.42!important;
    filter:saturate(.88) brightness(1.02)!important;
    z-index:4!important;
  }
  .home-water-feature.furn_waterfall{
    opacity:.38!important;
  }
  .home-slot.feature-slot,.home-slot.garden-slot,.home-slot.decor-slot{
    opacity:.56!important;
    z-index:4!important;
  }
  .home-slot.feature-slot .home-game-item,.home-slot.garden-slot .home-game-item,.home-slot.decor-slot .home-game-item{
    transform:scale(.66)!important;
  }
  .home-pet-track{
    opacity:.58!important;
    z-index:5!important;
    filter:saturate(.78)!important;
  }
  .home-pet-track .home-game-item{
    transform:scale(.62)!important;
  }
  .home-gate,.home-lantern{
    opacity:.38!important;
    filter:saturate(.72)!important;
  }
  .home-gem,.home-path-gem{
    opacity:.035!important;
    animation-duration:14s!important;
  }
	  .home-gem.g2{opacity:.05!important;}
	  .home-game-sparkles{display:none!important;}

	  /* Home fixed-zone composition: house first, water second, life tucked into the garden */
	  .home-room{
	    isolation:isolate;
	  }
	  .home-sky{z-index:0!important;}
	  .home-floor,.home-path,.home-flowerbed{z-index:1!important;}
	  .home-background-rainbow{
	    z-index:1!important;
	    opacity:.13!important;
	    filter:saturate(.68) blur(.2px)!important;
	  }
	  .home-distant-city,.home-realm-country,.home-realm-planet,.home-heavenly-veil{
	    z-index:1!important;
	  }
	  .home-path::before{
	    opacity:.18!important;
	    animation-duration:13s!important;
	  }
	  .home-gem{
	    display:none!important;
	  }
	  .home-path-gem{
	    z-index:3!important;
	    opacity:.16!important;
	    width:6px!important;
	    height:6px!important;
	    box-shadow:0 0 3px rgba(125,211,252,.16),0 2px 4px rgba(82,55,15,.04)!important;
	    animation:quietPathTwinkle 12s ease-in-out infinite!important;
	  }
	  .home-path-gem.pg1{left:48%!important;bottom:29%!important;}
	  .home-path-gem.pg2{left:54%!important;bottom:21%!important;}
	  .home-path-gem.pg3{left:46%!important;bottom:14%!important;}
	  .home-palace,.home-house,.home-mansion,.home-chapel,.home-cardboard,.home-little-shelter{
	    z-index:10!important;
	    left:50%!important;
	    transform:translateX(-50%)!important;
	  }
	  .home-palace,.home-mansion{
	    filter:drop-shadow(0 16px 22px rgba(124,90,30,.18)) drop-shadow(0 0 10px rgba(255,248,223,.22))!important;
	  }
	  .home-gate{
	    z-index:7!important;
	    opacity:.48!important;
	    filter:saturate(.76) drop-shadow(0 7px 8px rgba(82,55,15,.10))!important;
	  }
	  .home-lantern{
	    z-index:7!important;
	    opacity:.44!important;
	    transform:scale(.82)!important;
	  }
	  .home-slot.decor-slot{
	    right:5%!important;
	    bottom:30%!important;
	    z-index:6!important;
	    opacity:.52!important;
	    max-width:18%!important;
	  }
	  .home-slot.garden-slot{
	    left:4%!important;
	    right:auto!important;
	    bottom:5.5%!important;
	    z-index:5!important;
	    opacity:.76!important;
	    max-width:30%!important;
	  }
	  .home-slot.garden-slot .animated-shop-item{
	    min-width:clamp(70px,19vw,104px)!important;
	  }
	  .home-slot.garden-slot .home-game-item{
	    transform:scale(.82)!important;
	    filter:drop-shadow(0 8px 9px rgba(82,55,15,.10))!important;
	  }
	  .home-slot.feature-slot{
	    opacity:.82!important;
	    filter:drop-shadow(0 7px 10px rgba(82,55,15,.10))!important;
	  }
	  .home-slot.feature-slot::after{
	    content:"";
	    position:absolute;
	    left:7%;
	    right:7%;
	    bottom:-5px;
	    height:14px;
	    border-radius:50%;
	    background:radial-gradient(ellipse,rgba(82,55,15,.16),transparent 72%);
	    z-index:-1;
	    pointer-events:none;
	  }
	  .home-slot.feature-slot.furn_fountain-slot{
	    right:19%!important;
	    left:auto!important;
	    bottom:6.5%!important;
	    top:auto!important;
	    z-index:6!important;
	    max-width:22%!important;
	  }
	  .home-slot.feature-slot.furn_lily_pond-slot{
	    right:23%!important;
	    left:auto!important;
	    bottom:5.5%!important;
	    top:auto!important;
	    z-index:5!important;
	    max-width:22%!important;
	  }
	  .home-slot.feature-slot.furn_waterfall-slot{
	    left:6%!important;
	    right:auto!important;
	    top:23%!important;
	    bottom:auto!important;
	    z-index:3!important;
	    max-width:25%!important;
	    opacity:.66!important;
	    filter:saturate(.84) brightness(1.02) drop-shadow(0 8px 10px rgba(65,88,92,.08))!important;
	  }
	  .home-slot.feature-slot.furn_waterfall-slot::before{
	    content:"";
	    position:absolute;
	    left:10%;
	    right:14%;
	    top:4%;
	    bottom:3%;
	    border-radius:42% 38% 44% 36%;
	    background:
	      radial-gradient(circle at 28% 18%,rgba(255,255,255,.38),transparent 18%),
	      linear-gradient(145deg,rgba(195,183,152,.58),rgba(120,132,116,.38) 46%,rgba(88,107,105,.30));
	    box-shadow:inset 8px 9px 12px rgba(255,255,255,.22),inset -8px -12px 14px rgba(48,64,62,.16);
	    z-index:-1;
	  }
	  .home-slot.feature-slot.furn_waterfall-slot::after{
	    display:none!important;
	  }
	  .home-slot.feature-slot.furn_fountain-slot .animated-shop-item{
	    min-width:clamp(48px,12vw,66px)!important;
	    min-height:clamp(44px,11vw,62px)!important;
	  }
	  .home-slot.feature-slot.furn_fountain-slot .home-game-item.furn_fountain{
	    width:clamp(48px,12vw,66px)!important;
	    height:clamp(46px,12vw,64px)!important;
	    transform:scale(.82)!important;
	  }
	  .home-game-item.furn_fountain .home-game-body{
	    border-radius:13px 13px 22px 22px!important;
	    box-shadow:inset 0 5px 7px rgba(255,255,255,.78),inset 0 -7px 9px rgba(24,90,125,.15),0 5px 8px rgba(82,55,15,.08)!important;
	  }
	  .home-game-item.furn_fountain .home-game-detail.water{
	    height:48%!important;
	    top:-12%!important;
	    opacity:.86!important;
	    animation:fountainGentleOverflow 2.2s ease-in-out infinite!important;
	  }
	  .home-slot.feature-slot.furn_waterfall-slot .animated-shop-item{
	    min-width:clamp(86px,22vw,118px)!important;
	    min-height:clamp(92px,24vw,128px)!important;
	  }
	  .home-slot.feature-slot.furn_waterfall-slot .home-game-item.furn_waterfall{
	    width:clamp(86px,22vw,118px)!important;
	    height:clamp(92px,24vw,128px)!important;
	    transform:scale(.86)!important;
	  }
	  .home-slot.feature-slot.furn_waterfall-slot .home-game-body{
	    opacity:.72!important;
	    background:
	      linear-gradient(180deg,rgba(255,255,255,.50),transparent 18%),
	      linear-gradient(145deg,rgba(232,244,255,.88),rgba(125,211,252,.42),rgba(77,154,176,.25))!important;
	    box-shadow:inset 6px 7px 9px rgba(255,255,255,.28),inset -7px -9px 12px rgba(31,62,68,.15),0 4px 8px rgba(82,55,15,.04)!important;
	  }
	  .home-slot.feature-slot.furn_waterfall-slot .home-game-body::after{
	    left:34%!important;
	    right:34%!important;
	    top:10%!important;
	    bottom:-2%!important;
	    background:repeating-linear-gradient(180deg,rgba(255,255,255,.88) 0 5px,rgba(185,239,252,.72) 5px 13px,rgba(48,176,214,.44) 13px 22px)!important;
	    background-size:100% 22px!important;
	    box-shadow:-14px 18px 0 -8px rgba(255,255,255,.34),14px 26px 0 -9px rgba(255,255,255,.28),0 0 16px rgba(186,230,253,.18)!important;
	    animation:waterfallAbundantFlow 1.35s linear infinite!important;
	  }
	  .home-slot.feature-slot.furn_waterfall-slot .home-game-detail.water{
	    left:4%!important;
	    right:4%!important;
	    bottom:-6px!important;
	    height:28px!important;
	    opacity:.58!important;
	    background:radial-gradient(ellipse at 50% 34%,rgba(255,255,255,.86),rgba(150,226,248,.50) 48%,rgba(14,116,144,.18) 74%,transparent 88%)!important;
	  }
	  .home-water-feature{
	    pointer-events:none!important;
	    filter:saturate(.86) brightness(1.03)!important;
	  }
	  .home-water-feature.furn_fountain{
	    right:20%!important;
	    left:auto!important;
	    bottom:5.6%!important;
	    top:auto!important;
	    width:15%!important;
	    height:5.8%!important;
	    z-index:4!important;
	    opacity:.46!important;
	    transform:rotate(-8deg)!important;
	  }
	  .home-water-feature.furn_waterfall{
	    left:9%!important;
	    right:auto!important;
	    top:43%!important;
	    bottom:auto!important;
	    width:22%!important;
	    height:7.4%!important;
	    z-index:2!important;
	    opacity:.34!important;
	    transform:rotate(8deg)!important;
	  }
	  .home-water-feature .water-ripple{
	    opacity:.45!important;
	  }
	  .home-water-feature .water-shimmer{
	    opacity:.36!important;
	  }
	  .home-pet-track{
	    z-index:6!important;
	    opacity:.74!important;
	    filter:saturate(.82)!important;
	    pointer-events:none!important;
	  }
	  .home-pet-track .home-game-shadow{
	    opacity:.44!important;
	    filter:blur(1.3px)!important;
	  }
	  .home-pet-track.lamb-track{
	    left:5%!important;
	    right:58%!important;
	    bottom:11%!important;
	    top:auto!important;
	    min-height:62px!important;
	  }
	  .home-pet-track.butterfly-track{
	    left:60%!important;
	    right:8%!important;
	    top:auto!important;
	    bottom:28%!important;
	    min-height:62px!important;
	  }
	  .home-pet-track.dove-track{
	    left:6%!important;
	    right:63%!important;
	    top:auto!important;
	    bottom:28%!important;
	    min-height:62px!important;
	  }
	  .home-pet-track.lamb-track .animated-shop-item{
	    min-width:clamp(92px,28vw,138px)!important;
	    animation-name:lambMeadowWalk!important;
	    animation-duration:32s!important;
	    animation-timing-function:ease-in-out!important;
	  }
	  .home-pet-track.butterfly-track .animated-shop-item{
	    min-width:clamp(82px,24vw,124px)!important;
	    animation-name:butterflyAcrossGarden!important;
	    animation-duration:24s!important;
	    animation-timing-function:ease-in-out!important;
	  }
	  .home-pet-track.dove-track .animated-shop-item{
	    min-width:clamp(78px,23vw,118px)!important;
	    animation-name:doveAcrossGarden!important;
	    animation-duration:28s!important;
	    animation-timing-function:ease-in-out!important;
	  }
	  .home-pet-track.lamb-track .home-game-item.pet_lamb{
	    width:clamp(42px,11vw,58px)!important;
	    height:clamp(34px,9vw,48px)!important;
	    transform:scale(.92)!important;
	  }
	  .home-pet-track.butterfly-track .home-game-item.pet_butterfly{
	    width:clamp(28px,8vw,42px)!important;
	    height:clamp(26px,8vw,38px)!important;
	    transform:scale(.88)!important;
	  }
	  .home-pet-track.dove-track .home-game-item.pet_dove{
	    width:clamp(32px,9vw,46px)!important;
	    height:clamp(28px,8vw,40px)!important;
	    transform:scale(.90)!important;
	  }
	  .home-spirit-slot{
	    z-index:9!important;
	  }
	  .home-spirit-slot::after{
	    content:"";
	    position:absolute;
	    left:22%;
	    right:22%;
	    bottom:2px;
	    height:16px;
	    border-radius:50%;
	    background:radial-gradient(ellipse,rgba(82,55,15,.16),transparent 72%);
	    z-index:-1;
	    pointer-events:none;
	  }
	  @media (max-width:520px){
	    .home-slot.feature-slot.furn_waterfall-slot{left:5%!important;top:24%!important;max-width:24%!important;}
	    .home-water-feature.furn_waterfall{left:8%!important;top:44%!important;width:21%!important;}
	    .home-slot.feature-slot.furn_fountain-slot{right:18%!important;bottom:6%!important;}
	    .home-water-feature.furn_fountain{right:19%!important;bottom:5%!important;width:15%!important;}
	    .home-pet-track.lamb-track{left:5%!important;right:62%!important;bottom:11%!important;}
	    .home-pet-track.butterfly-track{left:62%!important;right:8%!important;bottom:29%!important;}
	    .home-pet-track.dove-track{left:6%!important;right:66%!important;bottom:29%!important;}
	    .home-slot.garden-slot{left:4%!important;bottom:5%!important;max-width:28%!important;}
	  }

	  /* Major-card rhythm: small label, title, short copy, one obvious action */
  .focus-card h2,.prayer-page-calm h2,.prayer-page-calm h3,.home-page h2,.home-page h3{
    margin-bottom:4px!important;
  }
  .focus-card p,.prayer-page-calm p,.home-page p{
    line-height:1.55!important;
  }

  /* Bottom tab polish: Testimonies remains readable on phone */
  .bottom-tabs{
    background:rgba(255,253,248,.985)!important;
    border-top-color:rgba(125,174,212,.18)!important;
  }
  .tab-btn.active{
    background:linear-gradient(145deg,rgba(255,248,223,.96),rgba(240,203,118,.70))!important;
  }
  .tab-btn[data-tab-id="wall"] .tab-label{
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .verse-readable{
    font-family:'Nunito',sans-serif!important;
    font-style:normal!important;
    font-weight:700!important;
    color:#3F2A0A!important;
    letter-spacing:0!important;
  }
  .reward-action-btn{
    color:#FFFDF8!important;
    text-shadow:0 1px 2px rgba(80,50,0,.22);
  }
  .home-righteousness-count{
    flex:0 0 auto;
    min-width:74px;
  }
  .home-righteousness-count > div:first-child{
    font-size:10.5px!important;
    letter-spacing:.45px!important;
    white-space:nowrap!important;
  }
  .home-righteousness-value{
    font-size:20px!important;
    white-space:nowrap!important;
    line-height:1.05!important;
  }
  @media (max-width:520px){
    .gender-card-row{
      display:grid!important;
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:10px!important;
      flex-wrap:nowrap!important;
    }
    .gender-choice-card{
      min-width:0!important;
      width:100%!important;
      min-height:260px!important;
      padding:18px 8px 16px!important;
      border-radius:20px!important;
      gap:8px!important;
    }
    .gender-choice-card svg{
      width:92px!important;
      height:auto!important;
    }
    .gender-choice-card .serif{
      font-size:15px!important;
      line-height:1.15!important;
    }
    .gender-choice-sub{
      font-size:11px!important;
      line-height:1.25!important;
    }
    .home-righteousness-count{
      min-width:62px!important;
    }
    .home-righteousness-count > div:first-child{
      font-size:9px!important;
      letter-spacing:.2px!important;
    }
    .home-righteousness-value{
      font-size:17px!important;
    }
    .verse-readable{
      font-size:14px!important;
      line-height:1.55!important;
    }
  }

  /* Final home clarity pass: staged, grounded, readable game objects */
  @keyframes clearWaterSheet{
    0%{background-position:0 -34px;transform:translateY(-5px)}
    100%{background-position:0 38px;transform:translateY(5px)}
  }
  @keyframes gentleWaterPulse{
    0%,100%{opacity:.7;transform:scale(.96)}
    50%{opacity:1;transform:scale(1.05)}
  }
  @keyframes calmAnimalWalk{
    0%,100%{transform:translateX(-16px) scaleX(1)}
    24%{transform:translateX(26px) scaleX(1)}
    38%{transform:translateX(34px) scaleX(1)}
    50%{transform:translateX(34px) scaleX(-1)}
    76%{transform:translateX(-20px) scaleX(-1)}
    88%{transform:translateX(-24px) scaleX(-1)}
  }
  @keyframes tinyFlutter{
    0%,100%{transform:translate(-8px,4px) rotate(-5deg)}
    28%{transform:translate(12px,-8px) rotate(7deg)}
    54%{transform:translate(22px,2px) rotate(-4deg)}
    78%{transform:translate(2px,-13px) rotate(5deg)}
  }
  .home-floating-label{opacity:.72!important;}
  .home-floating-label .placed-chip{background:rgba(255,253,246,.62)!important;border-color:rgba(184,147,75,.16)!important;color:#735014!important;}
  .home-slot::after{content:"";position:absolute;left:14%;right:14%;bottom:0;height:10px;border-radius:50%;background:rgba(70,48,12,.16);filter:blur(3px);z-index:-1;pointer-events:none;}
  .home-slot.feature-slot.furn_waterfall-slot::after{opacity:.16!important;}
  .home-slot.feature-slot.furn_fountain-slot::after,.home-slot.feature-slot.furn_lily_pond-slot::after{opacity:.22!important;}
  .home-slot.garden-slot::after{opacity:.16!important;}
  .home-pet-track::after{opacity:.18!important;}

  .home-room .home-game-item{
    filter:drop-shadow(0 7px 9px rgba(82,55,15,.10));
  }
  .home-game-glow{opacity:.10!important;}
  .home-game-sparkles{display:none!important;}

  .home-slot.feature-slot.furn_waterfall-slot{
    right:7%!important;
    bottom:23%!important;
    z-index:2!important;
    opacity:.78!important;
  }
  .home-slot.feature-slot.furn_waterfall-slot .home-game-item.furn_waterfall{
    width:clamp(92px,21vw,124px)!important;
    height:clamp(110px,25vw,146px)!important;
    transform:scale(.92)!important;
  }
  .home-game-item.furn_waterfall .home-game-body{
    border-radius:46% 42% 34% 36%!important;
    background:
      radial-gradient(circle at 18% 78%,#60777d 0 12px,transparent 13px),
      radial-gradient(circle at 82% 82%,#526b72 0 10px,transparent 11px),
      radial-gradient(circle at 23% 20%,rgba(255,255,255,.85) 0 10%,transparent 11%),
      linear-gradient(145deg,#eef8fb 0%,#b8c9ce 24%,#6f8b92 58%,#3e5a62 100%)!important;
    border:1px solid rgba(61,89,97,.18)!important;
    box-shadow:inset 8px 9px 12px rgba(255,255,255,.30),inset -9px -11px 15px rgba(35,55,62,.18),0 8px 12px rgba(82,55,15,.08)!important;
  }
  .home-game-item.furn_waterfall .home-game-body::after{
    left:28%!important;
    right:25%!important;
    top:8%!important;
    bottom:3%!important;
    border-radius:999px 999px 36% 42%!important;
    background:repeating-linear-gradient(180deg,rgba(255,255,255,.98) 0 6px,rgba(222,249,255,.90) 6px 14px,rgba(77,190,225,.70) 14px 24px,rgba(255,255,255,.70) 24px 31px)!important;
    background-size:100% 31px!important;
    animation:clearWaterSheet 1.05s linear infinite!important;
    box-shadow:-13px 25px 0 -9px rgba(255,255,255,.50),13px 34px 0 -11px rgba(186,230,253,.38),0 52px 0 -13px rgba(255,255,255,.36),0 0 18px rgba(186,230,253,.20)!important;
  }
  .home-game-item.furn_waterfall .home-game-detail.water{
    left:-8%!important;
    right:-9%!important;
    bottom:-2px!important;
    height:34px!important;
    opacity:.72!important;
    background:radial-gradient(ellipse at 50% 35%,rgba(255,255,255,.94),rgba(150,226,248,.64) 44%,rgba(14,116,144,.22) 72%,transparent 84%)!important;
    animation:gentleWaterPulse 3.2s ease-in-out infinite!important;
  }
  .home-water-feature.furn_waterfall{
    right:9%!important;
    bottom:18%!important;
    width:24%!important;
    height:10%!important;
    z-index:2!important;
    opacity:.38!important;
  }

  .home-slot.feature-slot.furn_fountain-slot{
    right:29%!important;
    bottom:7%!important;
    z-index:4!important;
  }
  .home-slot.feature-slot .home-game-item.furn_fountain{
    width:clamp(58px,13vw,78px)!important;
    height:clamp(60px,14vw,84px)!important;
  }
  .home-game-item.furn_fountain .home-game-body{
    border-radius:20px 20px 28px 28px!important;
    background:linear-gradient(180deg,#fff 0%,#dff7ff 30%,#91d4eb 64%,#4f88a6 100%)!important;
    border:1px solid rgba(57,120,150,.18)!important;
    box-shadow:inset 0 7px 10px rgba(255,255,255,.82),inset 0 -10px 12px rgba(24,90,125,.17),0 7px 10px rgba(82,55,15,.10)!important;
  }
  .home-game-item.furn_fountain .home-game-detail.water{
    top:-20%!important;
    height:62%!important;
    opacity:.86!important;
    animation:fountainGentleOverflow 1.85s ease-in-out infinite!important;
  }
  .home-water-feature.furn_fountain{
    right:29%!important;
    bottom:6.2%!important;
    width:17%!important;
    height:6%!important;
    opacity:.48!important;
  }

  .home-game-item.furn_lily_pond .home-game-body{
    border-radius:48% 52% 40% 44%!important;
    background:radial-gradient(ellipse at 50% 42%,rgba(220,249,255,.98),rgba(102,205,232,.58) 48%,rgba(55,125,154,.32) 76%,rgba(73,94,63,.24) 100%)!important;
    border:1px solid rgba(75,116,72,.22)!important;
  }
  .home-game-item.furn_lily_pond .home-game-detail.water{
    opacity:.55!important;
    animation:gentleWaterPulse 3.8s ease-in-out infinite!important;
  }

  /* Premium crystal-water pass: transparent, animated fountain and background waterfall */
  @keyframes crystalBubbleRise{0%{transform:translateY(4px) scale(.88);opacity:.46}45%{opacity:1}100%{transform:translateY(-8px) scale(1.08);opacity:.58}}
  @keyframes crystalWaterShimmer{0%,100%{filter:brightness(1) saturate(1);opacity:.86}50%{filter:brightness(1.12) saturate(1.08);opacity:1}}
  @keyframes crystalDropletLift{0%{transform:translateY(7px) scale(.7);opacity:0}35%{opacity:.86}100%{transform:translateY(-26px) scale(1.05);opacity:0}}
  @keyframes crystalRippleBase{0%{transform:scale(.72);opacity:.72}70%{opacity:.22}100%{transform:scale(1.24);opacity:0}}
  @keyframes crystalWaterfallScroll{0%{background-position:0 -18px;transform:translateY(-6px)}100%{background-position:0 34px;transform:translateY(6px)}}
  @keyframes crystalMistFloat{0%,100%{transform:translateY(0) scale(.94);opacity:.32}50%{transform:translateY(-4px) scale(1.08);opacity:.68}}
  .home-room .home-slot.feature-slot.furn_fountain-slot{
    right:27%!important;
    bottom:7%!important;
    z-index:4!important;
  }
  .home-room .home-slot.feature-slot .home-game-item.furn_fountain{
    width:clamp(64px,15vw,88px)!important;
    height:clamp(66px,16vw,94px)!important;
    filter:drop-shadow(0 7px 11px rgba(38,119,150,.16)) drop-shadow(0 0 10px rgba(186,230,253,.24))!important;
  }
  .home-room .home-game-item.furn_fountain .home-game-shadow{
    left:8%!important;
    right:8%!important;
    bottom:-1px!important;
    height:14px!important;
    opacity:.32!important;
    background:radial-gradient(ellipse,rgba(30,84,105,.24),rgba(30,84,105,0) 74%)!important;
  }
  .home-room .home-game-item.furn_fountain .home-game-glow{
    display:block!important;
    opacity:.28!important;
    background:radial-gradient(ellipse,rgba(220,249,255,.74),rgba(125,211,252,.22) 42%,transparent 74%)!important;
    animation:crystalWaterShimmer 3.8s ease-in-out infinite!important;
  }
  .home-room .home-game-item.furn_fountain .home-game-body{
    left:7%!important;
    right:7%!important;
    bottom:8px!important;
    height:50%!important;
    border-radius:50% 50% 32% 32% / 36% 36% 28% 28%!important;
    background:radial-gradient(ellipse at 50% 22%,rgba(255,255,255,.98),rgba(218,248,255,.70) 32%,rgba(97,202,232,.42) 62%,rgba(20,105,135,.18) 100%)!important;
    border:1.5px solid rgba(174,236,252,.46)!important;
    box-shadow:inset 0 8px 12px rgba(255,255,255,.86),inset 0 -12px 16px rgba(14,116,144,.16),0 0 16px rgba(186,230,253,.22),0 8px 11px rgba(82,55,15,.08)!important;
    animation:crystalWaterShimmer 3.2s ease-in-out infinite!important;
  }
  .home-room .home-game-item.furn_fountain .home-game-body::before{
    left:10%!important;
    right:10%!important;
    top:8px!important;
    height:18px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse at 50% 42%,rgba(255,255,255,.92),rgba(169,234,252,.56) 48%,rgba(45,150,185,.16) 78%,transparent 86%)!important;
    box-shadow:0 22px 0 5px rgba(255,255,255,.16),0 24px 0 7px rgba(90,190,225,.12)!important;
    animation:crystalRippleBase 2.4s ease-out infinite!important;
  }
  .home-room .home-game-item.furn_fountain .home-game-body::after{
    left:18%!important;
    right:18%!important;
    bottom:-10px!important;
    height:18px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse at 50% 42%,rgba(255,255,255,.90),rgba(125,211,252,.42) 52%,rgba(14,116,144,.12) 78%,transparent 86%)!important;
    animation:crystalRippleBase 2.1s ease-out infinite .45s!important;
  }
  .home-room .home-game-item.furn_fountain .home-game-detail.water{
    left:4%!important;
    right:4%!important;
    top:-25%!important;
    height:72%!important;
    border-radius:50% 50% 48% 48%!important;
    background:radial-gradient(circle at 50% 10%,rgba(255,255,255,1) 0 10%,rgba(221,250,255,.96) 12% 34%,rgba(97,202,232,.64) 35% 66%,rgba(14,116,144,.14) 84%,transparent 92%)!important;
    box-shadow:-20px 38px 0 -12px rgba(125,211,252,.66),20px 38px 0 -12px rgba(125,211,252,.60),0 50px 0 -12px rgba(45,139,175,.34),0 0 20px rgba(186,230,253,.34)!important;
    animation:crystalBubbleRise 1.65s ease-in-out infinite!important;
    opacity:.96!important;
  }
  .home-room .home-game-item.furn_fountain .home-game-detail.water::before{
    top:-33px!important;
    height:61px!important;
    width:6px!important;
    background:linear-gradient(180deg,rgba(255,255,255,1),rgba(174,236,252,.78) 58%,rgba(14,116,144,.22))!important;
    box-shadow:-19px 18px 0 -1px rgba(125,211,252,.54),19px 18px 0 -1px rgba(125,211,252,.50),0 11px 0 1px rgba(255,255,255,.54)!important;
    animation:fountainStream 1.2s ease-in-out infinite!important;
  }
  .home-room .home-game-item.furn_fountain .home-game-detail.water::after{
    top:-39px!important;
    width:8px!important;
    height:8px!important;
    background:rgba(255,255,255,.95)!important;
    box-shadow:-26px 27px 0 -3px rgba(125,211,252,.74),25px 26px 0 -3px rgba(125,211,252,.70),-12px 60px 0 -4px rgba(255,255,255,.84),12px 64px 0 -4px rgba(125,211,252,.62),0 18px 0 -3px rgba(255,255,255,.82)!important;
    animation:crystalDropletLift 1.8s ease-in-out infinite!important;
    opacity:.9!important;
  }
  .home-room .home-water-feature.furn_fountain{
    right:27%!important;
    bottom:5.8%!important;
    width:18%!important;
    height:7%!important;
    opacity:.58!important;
    z-index:3!important;
  }

  .home-room .home-slot.feature-slot.furn_waterfall-slot{
    right:6%!important;
    bottom:22%!important;
    z-index:2!important;
    opacity:.86!important;
    filter:saturate(1.02) brightness(1.03)!important;
  }
  .home-room .home-slot.feature-slot.furn_waterfall-slot .home-game-item.furn_waterfall{
    width:clamp(98px,23vw,132px)!important;
    height:clamp(118px,27vw,156px)!important;
    transform:scale(.92)!important;
    filter:drop-shadow(0 11px 15px rgba(35,55,60,.16)) drop-shadow(0 0 12px rgba(186,230,253,.18))!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-body{
    left:2%!important;
    right:2%!important;
    bottom:14px!important;
    height:80%!important;
    border-radius:48% 44% 34% 38% / 28% 31% 30% 30%!important;
    background:radial-gradient(circle at 17% 80%,#5c7278 0 11px,transparent 12px),radial-gradient(circle at 83% 78%,#4d666e 0 11px,transparent 12px),radial-gradient(circle at 23% 20%,rgba(255,255,255,.88) 0 11%,transparent 12%),linear-gradient(145deg,#f0f8fb 0%,#aebfc4 25%,#708b92 58%,#3f5a62 100%)!important;
    border:1px solid rgba(61,89,97,.16)!important;
    box-shadow:inset 9px 10px 14px rgba(255,255,255,.34),inset -10px -12px 16px rgba(35,55,62,.20),0 9px 12px rgba(82,55,15,.08)!important;
    clip-path:polygon(10% 16%,29% 5%,53% 0,79% 13%,93% 34%,86% 88%,63% 98%,33% 94%,7% 78%,2% 39%)!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-body::before{
    left:3%!important;
    right:3%!important;
    top:5%!important;
    height:86%!important;
    border-radius:42% 48% 32% 36%!important;
    background:radial-gradient(ellipse at 48% 9%,rgba(255,255,255,.98),rgba(220,246,255,.76) 22%,transparent 35%),radial-gradient(ellipse at 36% 62%,rgba(255,255,255,.46),transparent 44%),radial-gradient(ellipse at 64% 78%,rgba(186,230,253,.34),transparent 45%)!important;
    box-shadow:-20px 39px 0 -13px rgba(255,255,255,.38),21px 43px 0 -14px rgba(255,255,255,.32)!important;
    opacity:.95!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-body::after{
    left:22%!important;
    right:18%!important;
    top:4%!important;
    bottom:-2%!important;
    border-radius:999px 999px 42% 44%!important;
    background:repeating-linear-gradient(180deg,rgba(255,255,255,1) 0 6px,rgba(218,248,255,.98) 6px 14px,rgba(75,190,225,.84) 14px 24px,rgba(255,255,255,.78) 24px 31px)!important;
    background-size:100% 31px!important;
    box-shadow:-21px 26px 0 -10px rgba(255,255,255,.66),21px 34px 0 -11px rgba(186,230,253,.56),0 58px 0 -12px rgba(255,255,255,.48),0 0 22px rgba(186,230,253,.28)!important;
    animation:crystalWaterfallScroll .78s linear infinite!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-detail.water{
    left:-12%!important;
    right:-12%!important;
    bottom:-2px!important;
    height:48px!important;
    opacity:.86!important;
    background:radial-gradient(ellipse at 50% 36%,rgba(255,255,255,.98),rgba(150,226,248,.72) 42%,rgba(14,116,144,.24) 74%,transparent 86%)!important;
    box-shadow:0 -6px 16px rgba(255,255,255,.48),inset 0 2px 9px rgba(255,255,255,.62),0 0 20px rgba(186,230,253,.20)!important;
    animation:crystalRippleBase 2.45s ease-out infinite!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-detail.water::before{
    left:9%!important;
    right:9%!important;
    top:13px!important;
    height:10px!important;
    border-top:1.5px solid rgba(255,255,255,.92)!important;
    border-bottom:1px solid rgba(125,211,252,.30)!important;
    animation:crystalRippleBase 2.8s ease-out infinite .55s!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-detail.water::after{
    left:22%!important;
    right:22%!important;
    top:-7px!important;
    height:16px!important;
    background:radial-gradient(ellipse,rgba(255,255,255,.82),rgba(186,230,253,.30) 58%,transparent 76%)!important;
    animation:crystalMistFloat 2.2s ease-in-out infinite!important;
  }
  .home-room .home-water-feature.furn_waterfall{
    right:7%!important;
    bottom:18%!important;
    width:24%!important;
    height:12%!important;
    opacity:.52!important;
    z-index:2!important;
  }
  .home-room .home-water-feature.furn_waterfall .water-ripple,
  .home-room .home-water-feature.furn_fountain .water-ripple{
    border-color:rgba(255,255,255,.82)!important;
    box-shadow:0 0 9px rgba(186,230,253,.34)!important;
  }
  .home-room .home-water-feature .water-bubble{
    animation:crystalDropletLift 2s ease-in-out infinite!important;
    background:rgba(255,255,255,.88)!important;
  }

  .home-game-item.garden_roses .home-game-body,
  .home-game-item.garden_lilies .home-game-body,
  .home-game-item.garden_vines .home-game-body{
    background:linear-gradient(180deg,#9fcf88,#5d8f45 72%,#456d34)!important;
    border:1px solid rgba(71,105,45,.18)!important;
    box-shadow:inset 0 5px 8px rgba(255,255,255,.28),0 6px 8px rgba(82,55,15,.08)!important;
  }
  .home-game-item.garden_roses .home-game-detail.flower-a,
  .home-game-item.garden_lilies .home-game-detail.flower-a,
  .home-game-item.garden_vines .home-game-detail.flower-a{
    opacity:.95!important;
    filter:drop-shadow(0 2px 3px rgba(82,55,15,.08))!important;
  }

  .home-pet-track{
    z-index:5!important;
    opacity:.80!important;
  }
  .home-pet-track.lamb-track .animated-shop-item{
    animation:calmAnimalWalk 30s ease-in-out infinite!important;
  }
  .home-pet-track.butterfly-track .animated-shop-item{
    animation:tinyFlutter 22s ease-in-out infinite!important;
  }
  .home-pet-track.lamb-track .home-game-item.pet_lamb{
    width:clamp(48px,12vw,64px)!important;
    height:clamp(38px,10vw,52px)!important;
  }
  .home-room .home-game-item.pet_lamb .home-game-body{
    left:8%!important;
    right:18%!important;
    bottom:11px!important;
    height:48%!important;
    border-radius:58% 48% 45% 48%!important;
    background:
      radial-gradient(circle at 18% 18%,#fff 0 8%,transparent 9%),
      radial-gradient(circle at 38% 8%,#fff 0 9%,transparent 10%),
      radial-gradient(circle at 58% 20%,#fff 0 8%,transparent 9%),
      linear-gradient(180deg,#fffdf8,#efe5d7 62%,#bba58d)!important;
    border:1px solid rgba(116,88,55,.14)!important;
  }
  .home-room .home-game-item.pet_lamb .home-game-body::after{
    left:-8px!important;
    top:12px!important;
    width:15px!important;
    height:13px!important;
    border-radius:55% 44% 48% 45%!important;
    background:#fbfaf7!important;
    box-shadow:0 0 0 1px rgba(116,88,55,.13),4px 3px 0 -2px #3d2800!important;
  }
  .home-room .home-game-item.pet_lamb .home-game-icon::before,
  .home-room .home-game-item.pet_lamb .home-game-icon::after{
    background:#6f5a47!important;
    box-shadow:9px 0 0 #836e58!important;
  }
  .home-room .home-game-item.pet_butterfly .home-game-body::before,
  .home-room .home-game-item.pet_butterfly .home-game-body::after{
    background:
      radial-gradient(circle at 35% 28%,rgba(255,255,255,.92) 0 4px,transparent 5px),
      linear-gradient(145deg,#f9a8d4,#9ee7f0 56%,#8b5cf6)!important;
    opacity:.9!important;
  }

	  @media (max-width:520px){
	    .home-slot.feature-slot.furn_waterfall-slot{right:4%!important;bottom:23%!important;}
	    .home-water-feature.furn_waterfall{right:6%!important;bottom:18%!important;width:23%!important;}
	    .home-slot.feature-slot.furn_fountain-slot{right:26%!important;bottom:7%!important;}
	    .home-water-feature.furn_fountain{right:27%!important;width:17%!important;}
	    .home-floating-label{display:none!important;}
	    .home-game-glow{opacity:.07!important;}
	  }

  /* Final home depth lock: the spirit always stays in front of the scene. */
  .home-room{
    isolation:isolate!important;
  }
  .home-room .home-sky,
  .home-room .home-rays,
  .home-room .home-cloud,
  .home-room .home-background-rainbow,
  .home-room .home-distant-city,
  .home-room .home-realm-country,
  .home-room .home-realm-planet,
  .home-room .home-heavenly-veil{
    z-index:0!important;
  }
  .home-room .home-floor,
  .home-room .home-path,
  .home-room .home-flowerbed,
  .home-room .home-gem,
  .home-room .home-path-gem{
    z-index:1!important;
  }
  .home-room .home-water-feature,
  .home-room .home-water-stream{
    z-index:2!important;
  }
  .home-room .home-slot.feature-slot,
  .home-room .home-slot.garden-slot,
  .home-room .home-slot.decor-slot{
    z-index:5!important;
  }
  .home-room .home-pet-track{
    z-index:7!important;
  }
  .home-room .home-cardboard,
  .home-room .home-little-shelter,
  .home-room .home-apartment,
  .home-room .home-house,
  .home-room .home-mansion,
  .home-room .home-palace,
  .home-room .home-chapel{
    z-index:12!important;
  }
  .home-room .home-gate,
  .home-room .home-lantern{
    z-index:13!important;
  }
  .home-room .home-foreground-grass{
    z-index:18!important;
  }
  .home-room .home-spirit-slot{
    z-index:40!important;
    pointer-events:none;
  }
  .home-room .home-spirit-slot .spirit-avatar,
  .home-room .home-spirit-slot > div{
    position:relative;
    z-index:41!important;
  }
  /* Home main-character pass: larger living spirit, clearer depth, and calm activity reactions. */
  .home-room .home-sunbeam-drift{
    position:absolute;
    left:12%;
    right:12%;
    top:-18%;
    height:56%;
    opacity:.22;
    background:conic-gradient(from 205deg at 50% 0%,rgba(255,255,255,.72) 0 7deg,transparent 8deg 24deg,rgba(255,243,196,.52) 25deg 34deg,transparent 35deg 360deg);
    filter:blur(1px);
    animation:homeSunbeamDrift 18s ease-in-out infinite;
    pointer-events:none;
  }
  .home-room .home-cloud.c1{animation:homeCloudDriftA 24s ease-in-out infinite;}
  .home-room .home-cloud.c2{animation:homeCloudDriftB 29s ease-in-out infinite;}
  .home-room .home-cloud.c3{animation:homeCloudDriftA 32s ease-in-out infinite reverse;}
  .home-heavenly-effect{position:absolute;inset:0;z-index:30;pointer-events:none;overflow:hidden;}
  .home-heavenly-effect span{position:absolute;display:block;opacity:0;}
  .home-heavenly-effect .effect-cloud{display:none;}
  .home-heavenly-effect .effect-ray{left:50%;top:27%;width:18px;height:142px;border-radius:999px;background:linear-gradient(180deg,rgba(255,255,255,.9),rgba(250,204,21,.32),rgba(255,248,223,.08),transparent);transform-origin:50% 100%;filter:blur(.15px);}
  .home-heavenly-effect.eff_glory_rays .effect-ray{opacity:.72;animation:effectRayPulse 4.8s ease-in-out infinite;}
  .home-heavenly-effect.eff_glory_rays .effect-ray.r1{transform:translateX(-50%) rotate(0deg);}
  .home-heavenly-effect.eff_glory_rays .effect-ray.r2{transform:translateX(-50%) rotate(26deg);animation-delay:.6s;}
  .home-heavenly-effect.eff_glory_rays .effect-ray.r3{transform:translateX(-50%) rotate(-26deg);animation-delay:1.1s;}
  .home-heavenly-effect .effect-rainbow{left:-8%;right:-8%;top:8%;height:62%;border-radius:999px 999px 0 0;border-top:13px solid rgba(248,113,113,.24);border-left:13px solid transparent;border-right:13px solid transparent;box-shadow:0 -11px 0 rgba(251,191,36,.22),0 -22px 0 rgba(74,222,128,.18),0 -33px 0 rgba(96,165,250,.18),0 -44px 0 rgba(192,132,252,.16),0 18px 42px rgba(255,255,255,.24);filter:blur(.1px);}
  .home-heavenly-effect.eff_covenant_rainbow .effect-rainbow{opacity:.82;animation:effectRainbowBreathe 7s ease-in-out infinite;}
  .home-heavenly-effect .effect-star{width:4px;height:4px;border-radius:50%;background:#fff;box-shadow:0 0 9px rgba(255,255,255,.96),0 0 18px rgba(147,197,253,.42);}
  .home-heavenly-effect.eff_shooting_stars .effect-star{opacity:.9;animation:effectShootingStar 5.8s ease-in-out infinite;}
  .home-heavenly-effect.eff_shooting_stars .effect-star::after{content:"";position:absolute;right:2px;top:1px;width:62px;height:2px;border-radius:999px;background:linear-gradient(90deg,transparent,rgba(255,255,255,.86));}
  .home-heavenly-effect.eff_shooting_stars .effect-star.s1{left:72%;top:18%;animation-delay:0s;}
  .home-heavenly-effect.eff_shooting_stars .effect-star.s2{left:62%;top:31%;animation-delay:2s;transform:scale(.72);}
  .home-heavenly-effect.eff_shooting_stars .effect-star.s3{left:79%;top:43%;animation-delay:3.8s;transform:scale(.62);}
  .home-heavenly-effect .effect-orbit{left:50%;top:48%;width:156px;height:156px;margin-left:-78px;margin-top:-78px;border-radius:50%;border:1px solid rgba(199,210,254,.46);box-shadow:0 0 22px rgba(129,140,248,.14);}
  .home-heavenly-effect .effect-orbit::before,.home-heavenly-effect .effect-orbit::after{content:"";position:absolute;width:5px;height:5px;border-radius:50%;background:#fff;box-shadow:0 0 10px rgba(255,255,255,.9);}
  .home-heavenly-effect .effect-orbit::before{left:17%;top:20%;}
  .home-heavenly-effect .effect-orbit::after{right:20%;bottom:18%;}
  .home-heavenly-effect.eff_night_aura .effect-orbit{opacity:.78;animation:effectOrbit 13s linear infinite;}
  .home-heavenly-effect.eff_night_aura .effect-orbit.o2{width:190px;height:190px;margin-left:-95px;margin-top:-95px;animation-duration:18s;animation-direction:reverse;opacity:.46;}
  .home-game-item.effect{width:clamp(64px,18vw,86px);height:clamp(58px,16vw,78px);}
  .home-game-item.effect .home-game-body{left:12%;right:12%;bottom:7px;height:67%;border-radius:48% 48% 18px 18px;background:radial-gradient(circle at 50% 28%,rgba(255,255,255,.98),var(--accent-soft) 52%,rgba(255,248,223,.72));border:1px solid rgba(184,147,75,.24);}
  .home-game-item.effect .home-game-detail.halo{left:17%;right:17%;top:16%;height:42%;border:2px solid var(--accent);border-radius:50%;opacity:.52;}
  .home-game-item.effect .home-game-detail.star{left:50%;top:44%;width:18px;height:18px;transform:translate(-50%,-50%) rotate(45deg);background:var(--accent);clip-path:polygon(50% 0,62% 36%,100% 50%,62% 64%,50% 100%,38% 64%,0 50%,38% 36%);box-shadow:0 0 16px var(--accent-soft);}
  .home-sky-bird{
    position:absolute;
    width:18px;
    height:10px;
    opacity:.32;
    pointer-events:none;
    z-index:1;
  }
  .home-sky-bird::before,.home-sky-bird::after{
    content:"";
    position:absolute;
    top:4px;
    width:10px;
    height:7px;
    border-top:2px solid rgba(82,112,125,.62);
    border-radius:50%;
  }
  .home-sky-bird::before{left:0;transform:rotate(18deg);}
  .home-sky-bird::after{right:0;transform:rotate(-18deg);}
  .home-sky-bird.bird-a{left:18%;top:23%;animation:homeBirdFloat 13s ease-in-out infinite;}
  .home-sky-bird.bird-b{right:22%;top:18%;transform:scale(.72);animation:homeBirdFloat 16s ease-in-out 2s infinite reverse;}
  .home-room .home-walk-blessing{
    position:absolute;
    left:35%;
    right:35%;
    bottom:16%;
    height:22%;
    z-index:3!important;
    pointer-events:none;
  }
  .home-room .home-walk-blessing span{
    position:absolute;
    bottom:0;
    width:7px;
    height:7px;
    border-radius:50%;
    background:radial-gradient(circle,#fff 0 20%,#f9a8d4 23% 58%,transparent 60%);
    box-shadow:0 0 8px rgba(255,248,223,.62);
    animation:homeBlessingFloat 4.8s ease-in-out infinite;
  }
  .home-room .home-walk-blessing span:nth-child(1){left:12%;animation-delay:.2s;}
  .home-room .home-walk-blessing span:nth-child(2){left:48%;animation-delay:1s;background:radial-gradient(circle,#fff 0 20%,#bae6fd 23% 58%,transparent 60%);}
  .home-room .home-walk-blessing span:nth-child(3){right:14%;animation-delay:1.8s;background:radial-gradient(circle,#fff 0 20%,#fde68a 23% 58%,transparent 60%);}
  .home-room .home-prayer-glow{
    position:absolute;
    right:18%;
    bottom:8%;
    width:18%;
    height:16%;
    border-radius:50%;
    z-index:4!important;
    opacity:.74;
    background:radial-gradient(ellipse,rgba(186,230,253,.46),rgba(255,255,255,.18) 48%,transparent 72%);
    filter:blur(.5px);
    animation:homePrayerGlow 4s ease-in-out infinite;
    pointer-events:none;
  }
  .home-room .home-prayer-glow span{
    position:absolute;
    left:50%;
    bottom:20%;
    width:6px;
    height:6px;
    border-radius:50%;
    background:rgba(255,255,255,.82);
    box-shadow:0 0 8px rgba(125,211,252,.68);
    animation:homePrayerSpark 3.8s ease-in-out infinite;
  }
  .home-room .home-prayer-glow span:nth-child(2){left:64%;animation-delay:1.4s;transform:scale(.72);}
  .home-room .home-answer-light{
    position:absolute;
    left:18%;
    bottom:31%;
    width:44px;
    height:34px;
    z-index:8!important;
    filter:drop-shadow(0 0 8px rgba(240,203,118,.48));
    animation:homeAnswerFlutter 6.2s ease-in-out infinite;
    pointer-events:none;
  }
  .home-room .answer-butterfly{
    position:absolute;
    top:8px;
    width:18px;
    height:20px;
    border-radius:70% 35% 70% 35%;
    background:linear-gradient(145deg,#fff,#fde68a 52%,#f9a8d4);
    opacity:.86;
    transform-origin:100% 50%;
    animation:homeAnswerWing .9s ease-in-out infinite;
  }
  .home-room .answer-butterfly.wing-one{left:5px;transform:rotate(-22deg);}
  .home-room .answer-butterfly.wing-two{right:5px;transform-origin:0 50%;transform:scaleX(-1) rotate(-22deg);animation-delay:.08s;}
  .home-room .home-spirit-slot{
    bottom:1.5%!important;
  }
  .home-room .home-spirit-slot > div{
    animation:homeSpiritIdle 4.6s ease-in-out infinite;
    filter:drop-shadow(0 12px 16px rgba(82,55,15,.12));
  }
  .home-room.home-care-glowing .home-spirit-slot > div,
  .home-room.home-care-blooming .home-spirit-slot > div,
  .home-room.home-care-radiant .home-spirit-slot > div{
    animation:homeSpiritHappy 3.2s ease-in-out infinite;
  }
  .home-spirit-life{
    position:absolute;
    color:#f0c75e;
    text-shadow:0 0 9px rgba(255,248,223,.88);
    pointer-events:none;
    z-index:42;
    opacity:.72;
    animation:homeSpiritSpark 4.2s ease-in-out infinite;
  }
  .home-spirit-life.sparkle-one{right:12%;top:20%;font-size:13px;}
  .home-spirit-life.sparkle-two{left:13%;top:34%;font-size:11px;animation-delay:1.6s;}
	  .home-spirit-wave{
	    position:absolute;
	    right:19%;
	    top:46%;
	    width:17px;
	    height:23px;
	    border-radius:999px;
	    border-right:4px solid rgba(245,196,151,.92);
	    border-bottom:3px solid rgba(245,196,151,.72);
	    transform-origin:50% 100%;
	    z-index:42;
	    opacity:0;
	    transform:rotate(18deg);
	    pointer-events:none;
	    filter:drop-shadow(0 2px 3px rgba(82,55,15,.10));
	  }
	  .home-spirit-wave.prayed{
	    opacity:.42;
	    animation:homeSpiritGentleWave 3.2s ease-in-out infinite;
	  }
	  .home-prayer-hands{
	    position:absolute;
	    left:50%;
	    top:54%;
	    width:42px;
	    height:30px;
	    transform:translateX(-50%);
	    z-index:44;
	    opacity:0;
	    pointer-events:none;
	  }
	  .home-prayer-hands .hand{
	    position:absolute;
	    top:6px;
	    width:16px;
	    height:21px;
	    border-radius:999px 999px 7px 7px;
	    background:linear-gradient(145deg,#ffe4c7,#f5c49a 68%,#d99b68);
	    box-shadow:inset 0 2px 4px rgba(255,255,255,.42),0 3px 7px rgba(82,55,15,.12);
	  }
	  .home-prayer-hands .hand.left{
	    left:8px;
	    transform-origin:100% 100%;
	    transform:rotate(28deg);
	  }
	  .home-prayer-hands .hand.right{
	    right:8px;
	    transform-origin:0 100%;
	    transform:rotate(-28deg);
	  }
	  .home-room.home-spirit-action-pray .home-prayer-hands .hand.left{
	    animation:prayerLeftHandClasp 1.45s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .home-prayer-hands .hand.right{
	    animation:prayerRightHandClasp 1.45s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .home-spirit-wave{
	    opacity:.9!important;
	    right:30%;
	    top:51%;
	    animation:spiritArmFoldToPrayer 1.45s ease-in-out both!important;
	  }
	  .home-room .home-pet-track{
	    z-index:24!important;
    opacity:.92;
  }
  .home-room .home-slot.feature-slot{
    z-index:10!important;
  }
  .home-room .home-cardboard,
  .home-room .home-little-shelter,
  .home-room .home-house,
  .home-room .home-mansion,
  .home-room .home-palace,
  .home-room .home-chapel{
    z-index:16!important;
  }
  @keyframes homeSunbeamDrift{0%,100%{transform:translateX(-1%) rotate(-2deg);opacity:.18}50%{transform:translateX(2%) rotate(2deg);opacity:.28}}
  @keyframes homeCloudDriftA{0%,100%{transform:translateX(0)}50%{transform:translateX(12px)}}
  @keyframes homeCloudDriftB{0%,100%{transform:translateX(0)}50%{transform:translateX(-14px)}}
  @keyframes effectRayPulse{0%,100%{opacity:.38;filter:blur(.8px)}50%{opacity:.72;filter:blur(.2px)}}
  @keyframes effectRainbowBreathe{0%,100%{transform:translateY(0) scale(1);opacity:.62}50%{transform:translateY(-3px) scale(1.02);opacity:.86}}
  @keyframes effectShootingStar{0%,64%,100%{opacity:0;transform:translate(0,0) rotate(-18deg) scale(.7)}72%{opacity:.95}86%{opacity:0;transform:translate(-92px,44px) rotate(-18deg) scale(1)}}
  @keyframes effectOrbit{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
  @keyframes homeBirdFloat{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(16px,-4px) scale(1.04)}}
  @keyframes homeBlessingFloat{0%,100%{transform:translateY(0) scale(.82);opacity:.18}50%{transform:translateY(-18px) scale(1.18);opacity:.82}}
  @keyframes homePrayerGlow{0%,100%{transform:scale(.94);opacity:.45}50%{transform:scale(1.08);opacity:.78}}
  @keyframes homePrayerSpark{0%,100%{transform:translate(-50%,0) scale(.68);opacity:.2}50%{transform:translate(-50%,-22px) scale(1);opacity:.82}}
  @keyframes homeAnswerFlutter{0%,100%{transform:translate(0,0)}35%{transform:translate(18px,-8px)}70%{transform:translate(6px,5px)}}
  @keyframes homeAnswerWing{0%,100%{scale:1 .86}50%{scale:.72 1.08}}
  @keyframes homeSpiritIdle{0%,100%{transform:translateY(0) rotate(-.4deg)}50%{transform:translateY(-4px) rotate(.4deg)}}
  @keyframes homeSpiritHappy{0%,100%{transform:translateY(0) scale(1)}35%{transform:translateY(-5px) scale(1.018)}70%{transform:translateY(-2px) scale(1.006)}}
  @keyframes homeSpiritSpark{0%,100%{transform:translateY(0) scale(.8);opacity:.22}50%{transform:translateY(-7px) scale(1.12);opacity:.84}}
  @keyframes homeSpiritWave{0%,100%{transform:rotate(10deg);opacity:.2}30%,60%{transform:rotate(-24deg);opacity:.72}45%{transform:rotate(20deg);opacity:.8}}
  @media (max-width:520px){
    .home-room .home-cardboard,
    .home-room .home-little-shelter,
    .home-room .home-apartment,
    .home-room .home-house,
    .home-room .home-mansion,
    .home-room .home-palace,
    .home-room .home-chapel{
      transform:translateX(-50%) scale(.88)!important;
      transform-origin:50% 100%!important;
    }
    .home-room .home-spirit-slot{
      bottom:1%!important;
      transform:translateX(-50%) scale(.92)!important;
    }
    .home-room .home-answer-light{left:12%;bottom:32%;transform:scale(.82);}
    .home-room .home-prayer-glow{right:14%;width:22%;}
  }

  /* Final waterfall and garden polish: upright gardens, softer premium water. */
  .home-room .home-slot.garden-slot{
    left:4.5%!important;
    bottom:5.5%!important;
    top:auto!important;
    right:auto!important;
    z-index:5!important;
    opacity:.86!important;
    transform:none!important;
    max-width:30%!important;
  }
  .home-room .home-slot.garden-slot .animated-shop-item{
    animation:none!important;
    transform:none!important;
    min-width:clamp(82px,21vw,116px)!important;
  }
  .home-room .home-slot.garden-slot .home-game-item,
  .home-room .home-slot.garden-slot .home-game-item.garden{
    transform:none!important;
    rotate:0deg!important;
    animation:none!important;
    width:clamp(78px,20vw,112px)!important;
    height:clamp(58px,15vw,82px)!important;
    filter:drop-shadow(0 8px 10px rgba(55,88,44,.12))!important;
  }
  .home-room .home-game-item.garden .home-game-shadow{
    opacity:.46!important;
    left:2%!important;
    right:2%!important;
    bottom:-1px!important;
    filter:blur(2.2px)!important;
  }
  .home-room .home-game-item.garden .home-game-body{
    left:2%!important;
    right:2%!important;
    bottom:7px!important;
    height:50%!important;
    border-radius:54% 54% 18px 18px!important;
    background:
      radial-gradient(circle at 18% 30%,rgba(255,255,255,.56) 0 4px,transparent 5px),
      radial-gradient(circle at 48% 20%,rgba(255,255,255,.45) 0 5px,transparent 6px),
      linear-gradient(180deg,#bce59a 0%,#7eb85d 50%,#4f813a 100%)!important;
    border:1px solid rgba(75,118,56,.20)!important;
    box-shadow:inset 0 7px 9px rgba(255,255,255,.32),inset 0 -9px 10px rgba(45,82,36,.16),0 7px 10px rgba(82,55,15,.08)!important;
  }
  .home-room .home-game-item.garden .home-game-detail.leaf-a{
    left:7%!important;
    right:7%!important;
    bottom:21px!important;
    height:25px!important;
    border-radius:50%!important;
    background:
      radial-gradient(circle at 14% 78%,rgba(255,255,255,.72) 0 4px,transparent 5px),
      radial-gradient(circle at 39% 52%,rgba(255,255,255,.58) 0 4px,transparent 5px),
      radial-gradient(circle at 72% 70%,rgba(255,255,255,.52) 0 3px,transparent 4px),
      linear-gradient(180deg,#acd982,#5b903d)!important;
    animation:leafRipple 5.8s ease-in-out infinite!important;
  }
  .home-room .home-game-item.garden .home-game-detail.flower-a{
    animation:flowerNod 5.6s ease-in-out infinite!important;
    transform-origin:50% 100%!important;
  }
  .home-room .home-game-item.garden_roses .home-game-detail.flower-a{
    left:12%!important;
    top:20%!important;
    background:radial-gradient(circle at 50% 50%,#fff 0 15%,#fb7185 16% 56%,transparent 58%)!important;
    box-shadow:18px -6px 0 -2px #fb7185,36px 2px 0 -3px #fda4af,54px -4px 0 -4px #fff,8px 18px 0 -5px #f9a8d4!important;
  }
  .home-room .home-game-item.garden_lilies .home-game-detail.flower-a{
    left:13%!important;
    top:19%!important;
    background:radial-gradient(ellipse at 50% 50%,#fff 0 34%,#f9a8d4 35% 54%,transparent 56%)!important;
    box-shadow:20px -7px 0 -2px #fff,43px 2px 0 -3px #fce7f3,8px 17px 0 -5px #fff,57px 15px 0 -5px #fff!important;
  }
  .home-room .home-game-item.garden_vines .home-game-body{
    left:10%!important;
    right:10%!important;
    bottom:6px!important;
    height:80%!important;
    border-radius:999px 999px 16px 16px!important;
    background:
      linear-gradient(90deg,transparent 0 21%,#477a33 22% 29%,transparent 30% 69%,#477a33 70% 77%,transparent 78%),
      radial-gradient(circle at 28% 42%,#8cc46a 0 5px,transparent 6px),
      radial-gradient(circle at 70% 55%,#8cc46a 0 5px,transparent 6px)!important;
  }
  .home-room .home-game-item.garden_vines .home-game-body::after{
    border-color:#5f8d39!important;
    border-width:3px!important;
  }

  .home-room .home-slot.feature-slot.furn_waterfall-slot{
    left:5.5%!important;
    top:22%!important;
    bottom:auto!important;
    right:auto!important;
    z-index:4!important;
    opacity:.78!important;
    max-width:27%!important;
    filter:drop-shadow(0 10px 13px rgba(38,65,70,.14))!important;
  }
  .home-room .home-slot.feature-slot.furn_waterfall-slot::before{
    left:3%!important;
    right:3%!important;
    top:1%!important;
    bottom:1%!important;
    border-radius:48% 52% 34% 38% / 30% 34% 28% 30%!important;
    background:
      radial-gradient(circle at 20% 22%,rgba(255,255,255,.38),transparent 15%),
      radial-gradient(circle at 76% 34%,rgba(255,255,255,.22),transparent 14%),
      linear-gradient(145deg,rgba(206,200,176,.66),rgba(108,126,118,.46) 44%,rgba(67,88,88,.34))!important;
    box-shadow:inset 9px 10px 14px rgba(255,255,255,.22),inset -10px -13px 16px rgba(42,61,62,.18)!important;
  }
  .home-room .home-slot.feature-slot.furn_waterfall-slot .animated-shop-item{
    animation:none!important;
    transform:none!important;
    min-width:clamp(96px,23vw,132px)!important;
    min-height:clamp(112px,27vw,152px)!important;
  }
  .home-room .home-slot.feature-slot.furn_waterfall-slot .home-game-item.furn_waterfall{
    width:clamp(96px,23vw,132px)!important;
    height:clamp(112px,27vw,152px)!important;
    transform:none!important;
    animation:waterfallSettle 7.4s ease-in-out infinite!important;
    filter:drop-shadow(0 10px 12px rgba(35,55,60,.16))!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-body{
    left:2%!important;
    right:2%!important;
    bottom:15px!important;
    height:80%!important;
    border:none!important;
    border-radius:50% 54% 34% 38% / 28% 34% 29% 31%!important;
    clip-path:polygon(9% 18%,30% 4%,52% 0,79% 14%,93% 34%,86% 88%,64% 98%,34% 94%,7% 78%,2% 39%)!important;
    background:
      radial-gradient(circle at 18% 80%,#53686d 0 10px,transparent 11px),
      radial-gradient(circle at 82% 78%,#667b7f 0 12px,transparent 13px),
      radial-gradient(circle at 24% 25%,#edf5f6 0 12%,transparent 13%),
      linear-gradient(145deg,#eef3f4 0%,#a7b1b3 26%,#6f858a 58%,#405b62 100%)!important;
    box-shadow:inset 10px 11px 15px rgba(255,255,255,.42),inset -13px -14px 17px rgba(24,44,50,.24),0 11px 14px rgba(82,55,15,.10)!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-body::before{
    left:4%!important;
    right:4%!important;
    top:4%!important;
    height:91%!important;
    border-radius:inherit!important;
    background:
      radial-gradient(ellipse at 50% 8%,rgba(255,255,255,.98),rgba(220,246,255,.78) 21%,transparent 34%),
      radial-gradient(ellipse at 34% 62%,rgba(255,255,255,.42),transparent 44%),
      radial-gradient(ellipse at 66% 76%,rgba(186,230,253,.33),transparent 45%)!important;
    opacity:.94!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-body::after{
    left:23%!important;
    right:18%!important;
    top:4%!important;
    bottom:-2%!important;
    border-radius:999px 999px 42% 44%!important;
    background:
      linear-gradient(90deg,transparent 0 18%,rgba(255,255,255,.54) 19% 24%,transparent 25% 58%,rgba(255,255,255,.48) 59% 64%,transparent 65%),
      repeating-linear-gradient(180deg,rgba(255,255,255,1) 0 6px,rgba(218,248,255,.98) 6px 14px,rgba(66,184,222,.84) 14px 25px,rgba(255,255,255,.82) 25px 32px)!important;
    background-size:100% 32px!important;
    box-shadow:-21px 25px 0 -10px rgba(255,255,255,.66),20px 34px 0 -11px rgba(255,255,255,.56),0 58px 0 -12px rgba(255,255,255,.50),0 0 20px rgba(186,230,253,.30)!important;
    animation:waterfallAbundantFlow .88s linear infinite!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-detail.water{
    left:-12%!important;
    right:-12%!important;
    bottom:-2px!important;
    height:46px!important;
    opacity:.95!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse at 50% 36%,rgba(255,255,255,.99),rgba(150,226,248,.76) 42%,rgba(14,116,144,.27) 74%,transparent 86%)!important;
    box-shadow:0 -6px 16px rgba(255,255,255,.48),inset 0 2px 9px rgba(255,255,255,.62),0 0 20px rgba(186,230,253,.20)!important;
    animation:pondRipple 2.4s ease-in-out infinite!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-detail.water::before{
    left:10%!important;
    right:10%!important;
    top:13px!important;
    height:10px!important;
    border-top:1.5px solid rgba(255,255,255,.94)!important;
    border-bottom:1px solid rgba(125,211,252,.34)!important;
  }
  .home-room .home-game-item.furn_waterfall .home-game-detail.water::after{
    content:""!important;
    position:absolute!important;
    left:7%!important;
    right:7%!important;
    top:-6px!important;
    height:18px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse at 50% 42%,rgba(255,255,255,.78),rgba(186,230,253,.32) 58%,transparent 76%)!important;
    animation:overflowMist 2.4s ease-in-out infinite!important;
  }
  .home-room .home-water-feature.furn_waterfall{
    left:9%!important;
    right:auto!important;
    top:43%!important;
    bottom:auto!important;
    width:25%!important;
    height:8.5%!important;
    opacity:.58!important;
    transform:rotate(7deg)!important;
    z-index:2!important;
  }
	  @media (max-width:520px){
	    .home-room .home-slot.garden-slot{
	      left:4%!important;
	      bottom:5%!important;
	      max-width:31%!important;
    }
    .home-room .home-slot.garden-slot .home-game-item,
    .home-room .home-slot.garden-slot .home-game-item.garden{
      width:clamp(72px,24vw,96px)!important;
      height:clamp(54px,18vw,72px)!important;
    }
    .home-room .home-slot.feature-slot.furn_waterfall-slot{
      left:5%!important;
      top:24%!important;
      max-width:27%!important;
    }
    .home-room .home-slot.feature-slot.furn_waterfall-slot .home-game-item.furn_waterfall{
      width:clamp(82px,26vw,112px)!important;
      height:clamp(96px,31vw,132px)!important;
    }
    .home-room .home-water-feature.furn_waterfall{
      left:8%!important;
      top:44%!important;
	      width:24%!important;
	    }
	  }

	  /* Final feature clarity pass: make purchased garden and water pieces read clearly against the default landscape. */
	  .home-room .home-slot.garden-slot{
	    left:6.5%!important;
	    bottom:8.5%!important;
	    padding:5px 7px 3px!important;
	    border-radius:999px!important;
	    background:
	      radial-gradient(ellipse at 50% 88%,rgba(255,253,246,.72),rgba(245,229,181,.38) 48%,transparent 74%)!important;
	    box-shadow:0 8px 14px rgba(74,108,48,.12)!important;
	    opacity:.96!important;
	  }
	  .home-room .home-game-item.garden .home-game-body{
	    bottom:8px!important;
	    height:54%!important;
	    border-radius:52% 52% 20px 20px!important;
	    background:
	      linear-gradient(180deg,rgba(255,253,246,.96) 0 14%,rgba(224,244,198,.96) 15% 29%,#9bd977 30% 58%,#4f873b 100%)!important;
	    border:1.5px solid rgba(255,253,246,.72)!important;
	    box-shadow:
	      0 0 0 2px rgba(129,172,78,.16),
	      inset 0 8px 10px rgba(255,255,255,.48),
	      inset 0 -10px 12px rgba(42,82,32,.18),
	      0 8px 12px rgba(82,55,15,.10)!important;
	  }
	  .home-room .home-game-item.garden .home-game-detail.leaf-a{
	    bottom:22px!important;
	    height:28px!important;
	    background:
	      radial-gradient(circle at 12% 78%,rgba(255,255,255,.82) 0 4px,transparent 5px),
	      radial-gradient(circle at 35% 50%,rgba(255,255,255,.65) 0 4px,transparent 5px),
	      radial-gradient(circle at 68% 72%,rgba(255,255,255,.56) 0 4px,transparent 5px),
	      linear-gradient(180deg,#c4ec9b 0%,#6aa747 68%,#3f7330 100%)!important;
	    opacity:.98!important;
	  }
	  .home-room .home-game-item.garden .home-game-detail.flower-a{
	    width:18px!important;
	    height:18px!important;
	    filter:drop-shadow(0 2px 2px rgba(94,60,20,.10))!important;
	  }
	  .home-room .home-game-item.garden_roses .home-game-detail.flower-a{
	    background:radial-gradient(circle at 50% 50%,#fff 0 14%,#fb7185 15% 52%,#be3458 54% 60%,transparent 62%)!important;
	    box-shadow:
	      20px -8px 0 -2px #fb7185,
	      39px 1px 0 -3px #fda4af,
	      58px -5px 0 -4px #fff,
	      10px 19px 0 -5px #f9a8d4,
	      47px 18px 0 -6px #fb7185!important;
	  }
	  .home-room .home-game-item.garden_lilies .home-game-detail.flower-a{
	    background:radial-gradient(circle at 50% 44%,#fff 0 32%,#facc15 33% 39%,#f9a8d4 40% 54%,transparent 57%)!important;
	    box-shadow:
	      20px -8px 0 -2px #fff,
	      43px 2px 0 -3px #fff7ed,
	      9px 18px 0 -5px #fff,
	      58px 15px 0 -5px #fce7f3!important;
	  }
	  .home-room .home-game-item.garden_vines .home-game-body{
	    background:
	      linear-gradient(90deg,transparent 0 20%,#356f2a 21% 29%,transparent 30% 70%,#356f2a 71% 79%,transparent 80%),
	      radial-gradient(circle at 27% 38%,#a7e27f 0 5px,transparent 6px),
	      radial-gradient(circle at 72% 54%,#a7e27f 0 5px,transparent 6px),
	      radial-gradient(circle at 48% 30%,#f9a8d4 0 4px,transparent 5px)!important;
	  }

	  .home-room .home-slot.feature-slot.furn_waterfall-slot{
	    left:5%!important;
	    top:21%!important;
	    opacity:.9!important;
	    filter:drop-shadow(0 12px 16px rgba(29,58,66,.18))!important;
	  }
	  .home-room .home-slot.feature-slot.furn_waterfall-slot::before{
	    background:
	      radial-gradient(circle at 20% 18%,rgba(255,255,255,.42),transparent 13%),
	      radial-gradient(circle at 76% 28%,rgba(255,255,255,.25),transparent 16%),
	      radial-gradient(circle at 33% 82%,rgba(77,92,83,.42),transparent 18%),
	      linear-gradient(145deg,rgba(222,215,190,.78),rgba(117,136,127,.56) 45%,rgba(62,86,88,.42))!important;
	    box-shadow:inset 10px 12px 16px rgba(255,255,255,.28),inset -12px -14px 18px rgba(39,57,58,.22),0 10px 16px rgba(38,58,61,.10)!important;
	  }
	  .home-room .home-game-item.furn_waterfall .home-game-body{
	    background:
	      radial-gradient(circle at 18% 76%,#4c6368 0 12px,transparent 13px),
	      radial-gradient(circle at 82% 72%,#65787b 0 14px,transparent 15px),
	      radial-gradient(circle at 22% 24%,#f2f6f3 0 11%,transparent 12%),
	      radial-gradient(circle at 70% 18%,rgba(255,255,255,.28) 0 10%,transparent 11%),
	      linear-gradient(145deg,#e9eee9 0%,#a8b5ae 25%,#6f8784 58%,#405b5e 100%)!important;
	  }
	  .home-room .home-game-item.furn_waterfall .home-game-body::after{
	    left:19%!important;
	    right:17%!important;
	    top:1%!important;
	    bottom:-4%!important;
	    background:
	      linear-gradient(90deg,transparent 0 15%,rgba(255,255,255,.64) 16% 22%,transparent 23% 45%,rgba(220,249,255,.55) 46% 56%,transparent 57% 76%,rgba(255,255,255,.50) 77% 82%,transparent 83%),
	      repeating-linear-gradient(180deg,rgba(255,255,255,1) 0 5px,rgba(220,249,255,.98) 5px 13px,rgba(73,184,222,.84) 13px 25px,rgba(255,255,255,.82) 25px 31px)!important;
	    background-size:100% 31px!important;
	    box-shadow:-23px 24px 0 -10px rgba(255,255,255,.72),22px 35px 0 -11px rgba(255,255,255,.58),0 62px 0 -11px rgba(255,255,255,.56),0 0 22px rgba(186,230,253,.34)!important;
	    animation:waterfallAbundantFlow .64s linear infinite!important;
	  }
	  .home-room .home-game-item.furn_waterfall .home-game-detail.water{
	    left:-16%!important;
	    right:-18%!important;
	    height:52px!important;
	    background:
	      radial-gradient(ellipse at 50% 34%,rgba(255,255,255,1),rgba(171,235,250,.82) 39%,rgba(27,147,181,.30) 72%,transparent 87%)!important;
	    box-shadow:0 -8px 18px rgba(255,255,255,.58),inset 0 3px 11px rgba(255,255,255,.68),0 0 22px rgba(186,230,253,.25)!important;
	  }
	  .home-room .home-water-feature.furn_waterfall{
	    left:8.5%!important;
	    top:42%!important;
	    width:27%!important;
	    height:9.5%!important;
	    opacity:.68!important;
	    background:
	      radial-gradient(ellipse at 28% 45%,rgba(255,255,255,.84),transparent 36%),
	      linear-gradient(100deg,rgba(214,248,255,.86),rgba(87,190,225,.44),rgba(255,255,255,.40))!important;
	  }

	  .home-room .home-slot.feature-slot.furn_fountain-slot{
	    right:25%!important;
	    bottom:7.2%!important;
	    z-index:5!important;
	  }
	  .home-room .home-slot.feature-slot .home-game-item.furn_fountain{
	    width:clamp(60px,15vw,84px)!important;
	    height:clamp(62px,16vw,88px)!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-body{
	    left:8%!important;
	    right:8%!important;
	    bottom:8px!important;
	    height:49%!important;
	    border-radius:17px 17px 25px 25px!important;
	    background:
	      radial-gradient(ellipse at 50% 14%,rgba(255,255,255,.96),rgba(191,232,246,.68) 40%,transparent 42%),
	      linear-gradient(180deg,#fff 0%,#eaf8fb 30%,#96c9dc 66%,#527f95 100%)!important;
	    border:1.5px solid rgba(60,103,121,.30)!important;
	    box-shadow:inset 0 7px 10px rgba(255,255,255,.9),inset 0 -10px 13px rgba(26,77,101,.23),0 7px 10px rgba(82,55,15,.10)!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-body::before{
	    top:5px!important;
	    height:15px!important;
	    background:radial-gradient(ellipse at 50% 44%,#fff,rgba(154,222,244,.94) 48%,rgba(34,112,144,.30))!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-detail.water{
	    left:12%!important;
	    right:12%!important;
	    top:-17%!important;
	    height:58%!important;
	    opacity:.98!important;
	    background:radial-gradient(circle at 50% 8%,#fff 0 12%,rgba(225,250,255,.98) 13% 38%,rgba(84,190,224,.66) 39% 69%,rgba(14,116,144,.16))!important;
	    box-shadow:-17px 30px 0 -10px rgba(125,211,252,.68),17px 30px 0 -10px rgba(125,211,252,.62),0 42px 0 -10px rgba(45,139,175,.40),0 0 14px rgba(186,230,253,.24)!important;
	  }
	  .home-room .home-water-feature.furn_fountain{
	    right:25.5%!important;
	    bottom:5.8%!important;
	    width:18%!important;
	    height:6.5%!important;
	    opacity:.62!important;
	  }

	  .home-room .home-slot.feature-slot.furn_lily_pond-slot{
	    right:12%!important;
	    bottom:5.8%!important;
	    z-index:5!important;
	  }
	  .home-room .home-slot.feature-slot .home-game-item.furn_lily_pond{
	    width:clamp(78px,20vw,108px)!important;
	    height:clamp(48px,13vw,66px)!important;
	    filter:drop-shadow(0 7px 10px rgba(28,94,105,.11))!important;
	  }
	  .home-room .home-game-item.furn_lily_pond .home-game-body{
	    left:2%!important;
	    right:2%!important;
	    bottom:8px!important;
	    height:56%!important;
	    border-radius:52%!important;
	    background:
	      radial-gradient(ellipse at 36% 35%,rgba(255,255,255,.95),transparent 23%),
	      radial-gradient(ellipse at 70% 62%,rgba(186,230,253,.55),transparent 34%),
	      radial-gradient(ellipse at 50% 50%,rgba(160,226,241,.90),rgba(51,163,190,.45) 64%,rgba(46,97,111,.26) 88%)!important;
	    border:2px solid rgba(197,214,167,.74)!important;
	    box-shadow:inset 0 7px 10px rgba(255,255,255,.70),inset 0 -8px 10px rgba(16,101,125,.18),0 7px 11px rgba(82,55,15,.10)!important;
	  }
	  .home-room .home-game-item.furn_lily_pond .home-game-body::before{
	    left:16%!important;
	    top:25%!important;
	    width:20px!important;
	    height:13px!important;
	    background:linear-gradient(145deg,#e8fbdc,#77b94d)!important;
	    box-shadow:37px 5px 0 -1px #b8e98b,21px -10px 0 -3px #86efac!important;
	  }
	  .home-room .home-game-item.furn_lily_pond .home-game-body::after{
	    top:15%!important;
	    background:radial-gradient(circle,#fff 0 16%,#facc15 17% 24%,#f9a8d4 25% 54%,transparent 56%)!important;
	  }
	  .home-room .home-game-item.furn_lily_pond .home-game-detail.water{
	    left:8%!important;
	    right:8%!important;
	    bottom:17px!important;
	    height:18px!important;
	    border-top:1.5px solid rgba(255,255,255,.76)!important;
	    border-bottom:1px solid rgba(60,180,210,.30)!important;
	  }
	  .home-room .home-water-feature.furn_lily_pond{
	    right:13%!important;
	    bottom:5.2%!important;
	    width:21%!important;
	    height:7%!important;
	    opacity:.54!important;
	  }
	  @media (max-width:520px){
	    .home-room .home-slot.garden-slot{
	      left:5%!important;
	      bottom:7%!important;
	      max-width:34%!important;
	    }
	    .home-room .home-slot.feature-slot.furn_waterfall-slot{
	      left:4%!important;
	      top:22.5%!important;
	      max-width:30%!important;
	    }
	    .home-room .home-slot.feature-slot.furn_waterfall-slot .home-game-item.furn_waterfall{
	      width:clamp(86px,29vw,114px)!important;
	      height:clamp(100px,34vw,132px)!important;
	    }
	    .home-room .home-water-feature.furn_waterfall{
	      left:7%!important;
	      top:43%!important;
	      width:28%!important;
	    }
	    .home-room .home-slot.feature-slot.furn_fountain-slot{
	      right:27%!important;
	      bottom:6.5%!important;
	    }
		    .home-room .home-slot.feature-slot.furn_lily_pond-slot{
		      right:8%!important;
		      bottom:5%!important;
		    }
		  }

	  /* Last visual lock: keep these cards matching the Open Armor Closet gold treatment. */
	  .prayer-page-calm .card:not(.gold-outline-card):not(.prayer-friends-card),
	  .prayer-page-calm .friend-card,
	  .prayer-page-calm .prayer-tab-card,
	  .prayer-page-calm .quick-prayer-box,
	  .prayer-page-calm .testimony-entry,
	  .prayer-page-calm .empty-state,
	  .prayer-page-calm .prayer-section-tabs{
	    border-color:rgba(91,143,184,.38)!important;
	    box-shadow:0 2px 10px rgba(65,95,125,.055)!important;
	  }
	  .prayer-page-calm .prayer-tab-card.active{
	    border-color:rgba(91,143,184,.52)!important;
	  }
	  .card.prayer-friends-card,
	  .prayer-page-calm .card.prayer-friends-card,
	  .card.gold-outline-card{
	    background:linear-gradient(135deg,rgba(255,252,210,.98),rgba(255,248,223,.94),rgba(255,253,246,.98))!important;
	    border:2px solid var(--gold)!important;
	    box-shadow:0 4px 18px rgba(180,130,20,.18)!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box{
	    display:grid!important;
	    grid-template-columns:repeat(3,minmax(0,1fr))!important;
	    gap:7px!important;
	    padding:8px!important;
	    margin-top:4px!important;
	    border-radius:20px!important;
	    background:linear-gradient(135deg,rgba(246,250,255,.86),rgba(255,253,246,.94),rgba(255,248,223,.62))!important;
	    border:1.8px solid rgba(91,143,184,.42)!important;
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.7),0 3px 12px rgba(65,95,125,.06)!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card{
	    display:flex!important;
	    flex-direction:column!important;
	    align-items:center!important;
	    justify-content:center!important;
	    gap:4px!important;
	    min-height:82px!important;
	    padding:10px 6px!important;
	    text-align:center!important;
	    border-radius:15px!important;
	    background:rgba(255,253,248,.78)!important;
	    border:1px solid rgba(125,174,212,.18)!important;
	    box-shadow:none!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card.active{
	    background:linear-gradient(145deg,rgba(255,253,248,.98),rgba(255,248,223,.82))!important;
	    border-color:rgba(201,144,18,.42)!important;
	    box-shadow:0 4px 12px rgba(180,130,20,.10)!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card > div:first-child{
	    font-size:19px!important;
	    margin-bottom:1px!important;
	    opacity:.9!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card > div:nth-child(2){
	    font-size:12.5px!important;
	    font-weight:800!important;
	    line-height:1.15!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card > div:nth-child(3){
	    display:inline-flex!important;
	    align-items:center!important;
	    justify-content:center!important;
	    margin-top:1px!important;
	    padding:3px 7px!important;
	    border-radius:999px!important;
	    background:rgba(246,250,255,.78)!important;
	    border:1px solid rgba(125,174,212,.18)!important;
	    color:#557895!important;
	    font-size:10.5px!important;
	    font-weight:900!important;
	    white-space:nowrap!important;
	  }
	  @media (max-width:420px){
	    .prayer-page-calm .prayer-vessel-category-box{
	      gap:5px!important;
	      padding:6px!important;
	    }
	    .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card{
	      min-height:76px!important;
	      padding:9px 4px!important;
	    }
	    .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card > div:nth-child(2){
	      font-size:11.5px!important;
	    }
	    .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card > div:nth-child(3){
	      font-size:10px!important;
	      padding:3px 5px!important;
	    }
	  }
	  .prayer-page-calm .public-share-box{
	    background:linear-gradient(135deg,rgba(255,252,210,.96),rgba(255,248,223,.88),rgba(255,253,246,.98))!important;
	    border:2px solid var(--gold)!important;
	    box-shadow:0 4px 18px rgba(180,130,20,.15)!important;
	  }

	  /* Home function polish: clearer care actions, staged features, calmer shop cards. */
	  .home-page .focus-card,
	  .home-page .soft-guide-card,
	  .home-page .home-care-card,
	  .home-page .sticky-preview-card{
	    border-color:rgba(91,143,184,.34)!important;
	    box-shadow:0 3px 14px rgba(65,95,125,.06)!important;
	  }
	  .home-page .home-care-card{
	    background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(239,248,255,.70) 58%,rgba(236,253,245,.42))!important;
	    border:1.5px solid rgba(91,143,184,.36)!important;
	  }
	  .home-growth-line{
	    margin:8px 0 0!important;
	    color:#5d7790!important;
	    font-size:12.5px!important;
	    line-height:1.35!important;
	    font-weight:800!important;
	  }
	  .home-care-signal-row{
	    display:grid!important;
	    grid-template-columns:repeat(3,minmax(0,1fr))!important;
	    gap:7px!important;
	    margin-top:12px!important;
	  }
	  .home-care-signal{
	    display:flex!important;
	    align-items:center!important;
	    justify-content:center!important;
	    gap:4px!important;
	    min-height:34px!important;
	    border-radius:999px!important;
	    background:rgba(255,255,255,.62)!important;
	    border:1px solid rgba(91,143,184,.22)!important;
	    color:#5d7790!important;
	    font-size:12px!important;
	    font-weight:800!important;
	    line-height:1!important;
	    box-shadow:0 2px 8px rgba(65,95,125,.04)!important;
	  }
	  .home-care-signal.active{
	    background:linear-gradient(135deg,rgba(255,248,223,.98),rgba(236,253,245,.86))!important;
	    border-color:rgba(201,144,18,.45)!important;
	    color:var(--gold-deep)!important;
	    box-shadow:0 4px 12px rgba(180,130,20,.10)!important;
	  }
	  .home-care-action-row{
	    display:grid!important;
	    grid-template-columns:1fr!important;
	    gap:8px!important;
	    margin-top:12px!important;
	  }
	  .home-care-action{
	    min-height:42px!important;
	    border-radius:13px!important;
	    border:1.5px solid rgba(91,143,184,.34)!important;
	    background:linear-gradient(145deg,rgba(255,255,255,.88),rgba(239,248,255,.78))!important;
	    color:#426887!important;
	    font-weight:900!important;
	    font-size:12.5px!important;
	    cursor:pointer!important;
	    box-shadow:0 3px 10px rgba(65,95,125,.055)!important;
	    display:flex!important;
	    align-items:center!important;
	    justify-content:center!important;
	    gap:5px!important;
	    padding:8px 9px!important;
	  }
	  .home-care-action.primary{
	    min-height:46px!important;
	    border:2px solid var(--gold)!important;
	    background:linear-gradient(135deg,rgba(255,252,210,.98),rgba(255,248,223,.92))!important;
	    color:var(--gold-deep)!important;
	    box-shadow:0 4px 16px rgba(180,130,20,.15)!important;
	    font-size:13.5px!important;
	  }
	  .faith-flight-panel{
	    display:flex;
	    align-items:center;
	    justify-content:space-between;
	    gap:10px;
	    margin-top:12px;
	    padding:10px 11px;
	    border-radius:15px;
	    border:1.5px solid rgba(201,144,18,.34);
	    background:linear-gradient(135deg,rgba(255,252,210,.88),rgba(255,253,246,.78));
	    box-shadow:0 3px 12px rgba(180,130,20,.08);
	  }
	  .faith-flight-title{
	    color:var(--gold-deep);
	    font-size:13px;
	    font-weight:900;
	    line-height:1.2;
	  }
	  .faith-flight-copy{
	    margin-top:2px;
	    color:var(--text-soft);
	    font-size:12.5px;
	    font-weight:800;
	    line-height:1.35;
	  }
	  .faith-flight-btn{
	    flex-shrink:0;
	    min-height:38px;
	    border-radius:999px;
	    border:2px solid var(--gold);
	    background:var(--gold-grad-s);
	    color:var(--gold-deep);
	    font-family:'Nunito',sans-serif;
	    font-size:12.5px;
	    font-weight:900;
	    padding:8px 12px;
	    cursor:pointer;
	    box-shadow:0 4px 13px rgba(180,130,20,.14);
	    white-space:nowrap;
	  }
	  .faith-flight-btn:disabled{
	    opacity:.68;
	    cursor:default;
	  }
	  .faith-flight-lock{
	    flex-shrink:0;
	    border-radius:999px;
	    padding:7px 9px;
	    color:#5d7790;
	    background:rgba(255,255,255,.62);
	    border:1px solid rgba(91,143,184,.24);
	    font-size:11.5px;
	    font-weight:900;
	    white-space:nowrap;
	  }
	  .home-care-action:active{transform:scale(.98)!important;}
	  .home-care-complete{
	    grid-column:1 / -1!important;
	    text-align:center!important;
	    border-radius:14px!important;
	    padding:11px 12px!important;
	    background:linear-gradient(135deg,rgba(236,253,245,.94),rgba(255,248,223,.76))!important;
	    border:1.5px solid rgba(46,125,50,.22)!important;
	    color:var(--green)!important;
	    font-weight:900!important;
	  }
	  .home-bless-btn{
	    border:2px solid var(--gold)!important;
	    background:linear-gradient(135deg,rgba(255,252,210,.98),rgba(255,248,223,.92))!important;
	    color:var(--gold-deep)!important;
	    border-radius:999px!important;
	    padding:10px 14px!important;
	    min-height:42px!important;
	    font-size:13px!important;
	    font-weight:900!important;
	    box-shadow:0 4px 14px rgba(180,130,20,.14)!important;
	    cursor:pointer!important;
	    white-space:nowrap!important;
	  }
	  .home-page .sticky-preview-card{
	    background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(239,248,255,.50))!important;
	    border:1.5px solid rgba(91,143,184,.34)!important;
	  }
	  .home-page .home-room{
	    box-shadow:inset 0 -28px 42px rgba(138,90,8,.06),0 8px 26px rgba(65,95,125,.10)!important;
	  }
	  .home-page .home-room::after{
	    content:"";
	    position:absolute;
	    left:6%;
	    right:6%;
	    bottom:3%;
	    height:28%;
	    border-radius:50%;
	    background:radial-gradient(ellipse at 50% 78%,rgba(255,255,255,.22),transparent 62%);
	    z-index:2;
	    pointer-events:none;
	  }
	  .home-page .home-floating-label{
	    opacity:.78!important;
	  }
	  .home-page .home-shop-category-row{
	    padding-bottom:12px!important;
	  }
	  .home-page .home-category-card{
	    min-width:106px!important;
	    border-color:rgba(91,143,184,.28)!important;
	    background:linear-gradient(160deg,rgba(255,253,246,.94),rgba(239,248,255,.66))!important;
	    box-shadow:0 3px 12px rgba(65,95,125,.055)!important;
	  }
	  .home-page .home-category-card.active{
	    border-color:var(--gold)!important;
	    background:linear-gradient(160deg,rgba(255,253,246,1),rgba(255,248,223,.86))!important;
	    box-shadow:0 6px 18px rgba(180,130,20,.15)!important;
	  }
	  .home-page .home-shop-card{
	    min-height:124px!important;
	    border-color:rgba(91,143,184,.26)!important;
	    background:linear-gradient(160deg,rgba(255,253,246,.88),rgba(239,248,255,.56))!important;
	    box-shadow:0 3px 12px rgba(65,95,125,.045)!important;
	  }
	  .home-page .home-shop-card.owned{
	    border-color:rgba(201,144,18,.48)!important;
	    background:linear-gradient(160deg,rgba(255,253,246,.96),rgba(255,248,223,.54))!important;
	  }
	  .home-page .home-shop-card .meaning-chip{
	    background:rgba(255,255,255,.70)!important;
	    border-color:rgba(91,143,184,.22)!important;
	    color:#5d7790!important;
	  }
	  .home-page .home-shop-card button{
	    white-space:nowrap!important;
	  }
	  .home-page .soft-action-btn{
	    border-color:rgba(91,143,184,.34)!important;
	    color:#426887!important;
	    background:rgba(255,255,255,.76)!important;
	  }
	  .home-page .home-slot.feature-slot.furn_fountain-slot::before,
	  .home-page .home-slot.feature-slot.furn_waterfall-slot::before,
	  .home-page .home-slot.feature-slot.furn_lily_pond-slot::before{
	    content:"Water";
	    position:absolute;
	    left:50%;
	    bottom:-15px;
	    transform:translateX(-50%);
	    border-radius:999px;
	    padding:2px 7px;
	    font-size:9px;
	    line-height:1;
	    font-weight:900;
	    color:#426887;
	    background:rgba(255,255,255,.66);
	    border:1px solid rgba(91,143,184,.20);
	    opacity:.72;
	    pointer-events:none;
	  }
	  .home-page .home-pet-track::before{
	    content:"Companion";
	    position:absolute;
	    left:50%;
	    bottom:-10px;
	    transform:translateX(-50%);
	    border-radius:999px;
	    padding:2px 7px;
	    font-size:9px;
	    line-height:1;
	    font-weight:900;
	    color:#668060;
	    background:rgba(255,255,255,.58);
	    border:1px solid rgba(102,128,96,.18);
	    opacity:.58;
	    pointer-events:none;
	  }
	  .home-page .home-pet-track::before{
	    content:none!important;
	    display:none!important;
	  }
	  .home-page .home-pet-track{
	    pointer-events:auto!important;
	    cursor:pointer!important;
	  }
	  .home-page .home-pet-track:focus-visible{
	    outline:2px solid rgba(201,144,18,.58);
	    outline-offset:6px;
	    border-radius:999px;
	  }
	  .companion-tooltip{
	    position:absolute;
	    left:50%;
	    bottom:88%;
	    transform:translateX(-50%);
	    z-index:78;
	    min-width:max-content;
	    display:flex;
	    align-items:center;
	    justify-content:center;
	    gap:5px;
	    padding:6px 9px;
	    border-radius:999px;
	    color:#486a42;
	    background:linear-gradient(135deg,rgba(255,253,246,.95),rgba(236,253,245,.88));
	    border:1.5px solid rgba(102,128,96,.26);
	    box-shadow:0 8px 20px rgba(65,95,125,.12),0 0 0 1px rgba(255,255,255,.55) inset;
	    font-size:11.5px;
	    font-weight:900;
	    pointer-events:none;
	    animation:companionTooltipFloat 2.6s ease-out both;
	  }
	  .companion-tooltip i,
	  .companion-tooltip b{
	    display:inline-flex;
	    align-items:center;
	    justify-content:center;
	    color:var(--gold);
	    font-style:normal;
	    line-height:1;
	  }
	  .companion-tooltip i{
	    color:#e8799b;
	    animation:companionHeartPop 1.25s ease-in-out infinite;
	  }
	  .companion-tooltip b{
	    animation:companionSparklePop 1.45s ease-in-out infinite .12s;
	  }
	  .home-page .home-pet-track.companion-active .animated-shop-item{
	    animation-duration:2.6s!important;
	    animation-name:companionAffectionLook!important;
	    animation-timing-function:ease-in-out!important;
	  }
	  .home-page .home-slot.feature-slot.furn_waterfall-slot{
	    left:5%!important;
	    right:auto!important;
	    top:24%!important;
	    bottom:auto!important;
	    z-index:3!important;
	  }
	  .home-page .home-water-feature.furn_waterfall{
	    left:8%!important;
	    right:auto!important;
	    top:44%!important;
	    bottom:auto!important;
	    width:23%!important;
	    opacity:.48!important;
	  }
	  .home-page .home-slot.feature-slot.furn_fountain-slot{
	    right:20%!important;
	    bottom:7%!important;
	    z-index:10!important;
	  }
	  .home-page .home-water-feature.furn_fountain{
	    right:21%!important;
	    bottom:5.5%!important;
	    width:16%!important;
	    opacity:.52!important;
	  }
	  .home-page .home-slot.feature-slot.furn_lily_pond-slot{
	    right:5%!important;
	    bottom:5.5%!important;
	    z-index:8!important;
	  }
	  .home-page .home-pet-track.lamb-track{
	    left:4%!important;
	    right:66%!important;
	    bottom:10%!important;
	    z-index:24!important;
	  }
	  .home-page .home-pet-track.butterfly-track{
	    left:70%!important;
	    right:4%!important;
	    top:40%!important;
	    z-index:22!important;
	  }
	  .home-page .home-pet-track.dove-track{
	    left:4%!important;
	    right:68%!important;
	    top:42%!important;
	    z-index:22!important;
	  }
	  .home-page .home-slot.decor-slot{
	    right:5%!important;
	    bottom:30%!important;
	    z-index:14!important;
	  }
	  .home-page .home-room .home-gem,
	  .home-page .home-room .home-path-gem{
	    opacity:.08!important;
	  }
	  @media (max-width:560px){
	    .home-care-signal-row{grid-template-columns:repeat(3,minmax(0,1fr))!important;}
	    .home-care-action-row{grid-template-columns:1fr!important;}
	    .home-care-signal{font-size:11px!important;gap:3px!important;padding:0 4px!important;}
	    .home-bless-btn{padding:9px 11px!important;font-size:12px!important;}
	    .home-page .home-righteousness-count{min-width:74px!important;}
	    .home-page .home-righteousness-value{font-size:20px!important;white-space:nowrap!important;}
	    .home-page .home-shop-card{padding:10px 8px!important;}
	    .home-page .home-slot.feature-slot.furn_waterfall-slot{left:3%!important;top:25%!important;transform:scale(.86)!important;transform-origin:left top!important;}
	    .home-page .home-water-feature.furn_waterfall{left:7%!important;top:43%!important;width:21%!important;}
	    .home-page .home-slot.feature-slot.furn_fountain-slot{right:21%!important;bottom:6.5%!important;transform:scale(.88)!important;transform-origin:center bottom!important;}
	    .home-page .home-water-feature.furn_fountain{right:22%!important;width:15%!important;}
	    .home-page .home-slot.feature-slot.furn_lily_pond-slot{right:4%!important;bottom:5%!important;transform:scale(.9)!important;transform-origin:right bottom!important;}
	    .home-page .home-pet-track::before,
	    .home-page .home-slot.feature-slot::before{display:none!important;}
	  }

	  /* Final outline pass: regular cards use a darker soft-blue outline.
	     Gold and intentional solid-blue cards keep their existing special outlines. */
	  .today-page-calm > .current-stage-card{
	    width:calc(100% + 24px)!important;
	    margin-left:-12px!important;
	    margin-right:-12px!important;
	    padding-left:18px!important;
	    padding-right:18px!important;
	    border-radius:0!important;
	    backdrop-filter:none!important;
	  }
	  .card:not(.gold-outline-card):not(.prayer-friends-card):not(.current-stage-card):not(.prayer-vessels-section):not(.solid-blue-outline-card),
	  .friend-card,
	  .prayer-tab-card,
	  .quick-prayer-box,
	  .testimony-entry,
	  .empty-state,
	  .public-preview-card,
	  .home-shop-card,
	  .home-category-card,
	  .shop-card:not(.owned):not(.expanded),
	  .shop-category-card:not(.active),
	  .testimony-stone-card:not(.locked),
	  .closet-item-btn:not(.equipped){
	    border-width:1.25px!important;
	    border-color:rgba(80,116,139,.28)!important;
	    box-shadow:0 4px 15px rgba(65,82,77,.055)!important;
	  }
	  .card.gold-outline-card,
	  .card.prayer-friends-card,
	  .prayer-page-calm .public-share-box,
	  .shop-category-card.active,
	  .shop-card.owned,
	  .shop-card.expanded,
	  .home-category-card.active,
	  .closet-item-btn.equipped{
	    border-color:var(--gold)!important;
	  }
	  .card.gold-outline-card,
	  .share-testimony-card.gold-outline-card,
	  .today-quests-card.gold-outline-card{
	    background:linear-gradient(135deg,rgba(255,250,230,.98),rgba(249,251,246,.95),rgba(255,253,247,.98))!important;
	    border:2px solid var(--gold)!important;
	    box-shadow:0 5px 19px rgba(128,100,42,.105)!important;
	  }
	  .share-testimony-card.gold-outline-card,
	  .today-quests-card.gold-outline-card{
	    border:2.5px solid #C99012!important;
	    outline:2.5px solid #C99012!important;
	    outline-offset:0!important;
	    box-shadow:0 5px 20px rgba(180,130,20,.20)!important;
	  }
	  .solid-blue-outline-card,
	  .featured-testimony-card.solid-blue-outline-card{
	    border:2px solid rgba(91,143,184,.72)!important;
	    outline:2px solid rgba(91,143,184,.72)!important;
	    outline-offset:0!important;
	    box-shadow:0 4px 18px rgba(65,95,125,.12)!important;
	  }
	  .testimony-garden-page{
	    position:relative;
	    isolation:isolate;
	  }
	  .testimony-garden-page::before{
	    content:"";
	    position:absolute;
	    inset:-18px -12px auto;
	    height:260px;
	    border-radius:0 0 999px 999px;
	    background:
	      radial-gradient(circle at 16% 20%,rgba(255,222,238,.40),transparent 34%),
	      radial-gradient(circle at 82% 12%,rgba(238,222,255,.34),transparent 34%),
	      linear-gradient(180deg,rgba(255,248,223,.52),rgba(255,255,255,0));
	    pointer-events:none;
	    z-index:-1;
	  }
	  .testimony-garden-page::after{
	    content:"✦  ✧  ·  ❀";
	    position:absolute;
	    top:12px;
	    right:12px;
	    color:rgba(201,144,18,.22);
	    letter-spacing:8px;
	    animation:testimonyGardenDrift 9s ease-in-out infinite;
	    pointer-events:none;
	  }
	  .testimony-garden-page > .card{
	    border-color:rgba(255,255,255,.62)!important;
	    outline:none!important;
	    background:linear-gradient(145deg,rgba(255,253,246,.94),rgba(255,248,223,.46),rgba(246,250,255,.38))!important;
	    box-shadow:0 18px 38px rgba(91,67,28,.075),inset 0 1px 0 rgba(255,255,255,.76)!important;
	  }
	  .testimony-garden-hero{
	    border-radius:28px!important;
	    overflow:hidden;
	  }
	  .testimony-garden-hero .unlock-note{
	    background:rgba(255,255,255,.54)!important;
	    border:none!important;
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.78);
	  }
	  .testimony-featured-garden{
	    position:relative;
	    overflow:hidden;
	    border-radius:30px!important;
	    border:none!important;
	    outline:none!important;
	    background:
	      radial-gradient(circle at 78% 12%,rgba(238,222,255,.44),transparent 36%),
	      linear-gradient(145deg,rgba(255,253,246,.98),rgba(255,248,223,.62),rgba(255,242,248,.44))!important;
	    box-shadow:0 22px 48px rgba(117,82,22,.12),inset 0 1px 0 rgba(255,255,255,.82)!important;
	  }
	  .testimony-featured-garden::before{
	    content:"";
	    position:absolute;
	    inset:-70px -70px auto auto;
	    width:190px;
	    height:190px;
	    border-radius:50%;
	    background:radial-gradient(circle,rgba(255,232,169,.44),rgba(255,232,169,0) 70%);
	    pointer-events:none;
	  }
	  .featured-testimony-image{
	    width:100%!important;
	    max-height:420px!important;
	    margin:0 auto 16px!important;
	    display:block!important;
	    border:none!important;
	    border-radius:24px!important;
	    background:rgba(255,255,255,.36)!important;
	    box-shadow:0 16px 34px rgba(138,90,8,.10)!important;
	  }
	  .testimony-share-garden{
	    border:none!important;
	    outline:none!important;
	    border-radius:24px!important;
	    box-shadow:0 14px 30px rgba(91,67,28,.07),inset 0 1px 0 rgba(255,255,255,.76)!important;
	  }
	  .testimony-share-garden .testimony-stone-toggle{
	    padding:2px 0!important;
	  }
	  .testimony-shared-garden{
	    border:none!important;
	    border-radius:28px!important;
	    padding:18px!important;
	  }
	  .testimony-category-row{
	    display:flex;
	    gap:8px;
	    overflow-x:auto;
	    padding:2px 1px 14px;
	    margin-bottom:2px;
	    -webkit-overflow-scrolling:touch;
	    scrollbar-width:none;
	  }
	  .testimony-category-row::-webkit-scrollbar{display:none;}
	  .testimony-category-row button{
	    flex:0 0 auto;
	    border:none;
	    border-radius:999px;
	    padding:8px 12px;
	    background:rgba(255,255,255,.62);
	    color:#5a4319;
	    font-family:'Nunito',sans-serif;
	    font-size:12.5px;
	    font-weight:550;
	    box-shadow:0 6px 14px rgba(91,67,28,.05),inset 0 1px 0 rgba(255,255,255,.76);
	    cursor:pointer;
	    transition:transform .18s ease,background .18s ease,box-shadow .18s ease;
	  }
	  .testimony-category-row button.active{
	    background:linear-gradient(135deg,rgba(255,248,223,.98),rgba(246,218,146,.72));
	    color:var(--gold-deep);
	    box-shadow:0 10px 22px rgba(201,144,18,.14),inset 0 1px 0 rgba(255,255,255,.82);
	  }
	  .testimony-category-row button:active{transform:scale(.98);}
	  .testimony-shared-garden .scroll-list{
	    display:grid!important;
	    grid-template-columns:repeat(auto-fit,minmax(240px,1fr))!important;
	    gap:12px!important;
	    max-height:none!important;
	    overflow:visible!important;
	    padding-right:0!important;
	  }
	  .testimony-shared-garden .scroll-list > div{
	    border:none!important;
	    background:linear-gradient(145deg,rgba(255,253,246,.96),rgba(255,248,223,.46),rgba(246,250,255,.44))!important;
	    box-shadow:0 14px 30px rgba(91,67,28,.075),inset 0 1px 0 rgba(255,255,255,.78)!important;
	    transition:transform .18s ease,box-shadow .18s ease;
	  }
	  .testimony-shared-garden .scroll-list > div:hover{
	    transform:translateY(-2px);
	    box-shadow:0 18px 36px rgba(91,67,28,.10),inset 0 1px 0 rgba(255,255,255,.84)!important;
	  }
	  @keyframes testimonyGardenDrift{
	    0%,100%{transform:translate3d(0,0,0);opacity:.52}
	    50%{transform:translate3d(-8px,8px,0);opacity:.82}
	  }
	  @media (max-width:560px){
	    .featured-testimony-image{
	      width:100%!important;
	      max-height:320px!important;
	    }
	    .testimony-featured-garden h3{font-size:19px!important;}
	    .testimony-shared-garden .scroll-list{
	      grid-template-columns:1fr!important;
	    }
	  }
	  .faith-journey-panel{
	    overflow:hidden;
	    position:relative;
	    margin-bottom:14px;
	    padding:10px;
	    border:1.5px solid rgba(91,143,184,.42);
	    border-radius:18px;
	    background:linear-gradient(135deg,rgba(255,253,246,.98),rgba(239,248,255,.76),rgba(255,250,232,.94))!important;
	  }
	  .faith-journey-panel::after{
	    content:"";
	    position:absolute;
	    width:92px;
	    height:92px;
	    right:-34px;
	    top:-42px;
	    border-radius:50%;
	    background:radial-gradient(circle,rgba(255,231,159,.42),rgba(255,231,159,0) 68%);
	    pointer-events:none;
	  }
	  .faith-journey-toggle{
	    width:100%;
	    display:flex;
	    align-items:center;
	    justify-content:space-between;
	    gap:10px;
	    padding:0;
	    border:0;
	    background:transparent;
	    cursor:pointer;
	    text-align:left;
	    font-family:inherit;
	    position:relative;
	    z-index:1;
	  }
	  .faith-journey-toggle-copy{
	    flex:1;
	    min-width:0;
	  }
	  .faith-journey-toggle-copy span{
	    display:block;
	    font-size:10.5px;
	    letter-spacing:1.4px;
	    text-transform:uppercase;
	    color:var(--text-faint);
	    font-weight:550;
	    margin-bottom:2px;
	  }
	  .faith-journey-toggle-copy b{
	    display:block;
	    color:var(--text-dark);
	    font-size:14px;
	    line-height:1.25;
	    font-weight:900;
	  }
	  .faith-journey-toggle-copy small{
	    display:block;
	    color:#6f8aa2;
	    font-size:12.5px;
	    line-height:1.35;
	    margin-top:2px;
	    font-weight:800;
	  }
	  .faith-journey-chevron{
	    flex-shrink:0;
	    color:#456d8f;
	    font-size:12px;
	    font-weight:900;
	    padding:5px 8px;
	    border-radius:999px;
	    background:rgba(255,255,255,.58);
	    border:1px solid rgba(91,143,184,.24);
	  }
	  .faith-journey-day{
	    flex-shrink:0;
	    border:1.5px solid rgba(201,144,18,.34);
	    border-radius:14px;
	    padding:7px 8px;
	    min-width:58px;
	    text-align:center;
	    background:rgba(255,252,232,.78);
	    color:var(--gold-deep);
	    box-shadow:0 3px 12px rgba(180,130,20,.09);
	  }
	  .faith-journey-day strong{
	    display:block;
	    font-size:17px;
	    line-height:1;
	  }
	  .faith-journey-day span{
	    display:block;
	    margin-top:3px;
	    font-size:9.5px;
	    color:var(--text-soft);
	    font-weight:800;
	    letter-spacing:.5px;
	    text-transform:uppercase;
	  }
	  .faith-journey-details{
	    position:relative;
	    z-index:1;
	    padding-top:11px;
	    margin-top:10px;
	    border-top:1px solid rgba(91,143,184,.18);
	    animation:fadeUp .2s ease-out;
	  }
	  .faith-journey-copy{
	    margin:0 0 12px;
	    color:#456d8f;
	    font-size:13.5px;
	    line-height:1.55;
	    position:relative;
	    z-index:1;
	  }
	  .faith-journey-growth{
	    margin:0 0 13px;
	    padding:12px 13px;
	    border-radius:16px;
	    background:rgba(255,255,255,.56);
	    border:1px solid rgba(214,185,127,.18);
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.82);
	  }
	  .faith-journey-growth-top,
	  .faith-journey-growth-meta{
	    display:flex;
	    align-items:center;
	    justify-content:space-between;
	    gap:10px;
	  }
	  .faith-journey-growth-top span{
	    color:var(--text-faint);
	    font-size:10.5px;
	    font-weight:650;
	    letter-spacing:1.1px;
	    text-transform:uppercase;
	  }
	  .faith-journey-growth-top b{
	    color:var(--gold-deep);
	    font-family:'Cormorant Garamond',Georgia,serif;
	    font-size:22px;
	    line-height:1;
	    font-weight:500;
	  }
	  .faith-journey-growth-track{
	    height:8px;
	    margin:9px 0 8px;
	    border-radius:999px;
	    background:rgba(214,185,127,.22);
	    overflow:hidden;
	  }
	  .faith-journey-growth-fill{
	    height:100%;
	    border-radius:999px;
	    background:linear-gradient(90deg,#93B98A,#E8C66F,#C99012);
	    box-shadow:0 0 12px rgba(201,154,61,.18);
	    transition:width .5s ease;
	  }
	  .faith-journey-growth-meta span{
	    min-width:0;
	    color:#7D674D;
	    font-size:11.5px;
	    line-height:1.25;
	    font-weight:600;
	  }
	  .faith-journey-growth-meta span:last-child{
	    text-align:right;
	    color:#8A5A08;
	  }
	  .faith-journey-growth-note{
	    margin:8px 0 0!important;
	    color:#7D674D!important;
	    font-size:12px!important;
	    line-height:1.35!important;
	    font-weight:500!important;
	  }
	  .faith-journey-stats{
	    display:grid;
	    grid-template-columns:repeat(3,minmax(0,1fr));
	    gap:8px;
	    position:relative;
	    z-index:1;
	  }
	  .faith-journey-stat{
	    border:1px solid rgba(91,143,184,.32);
	    border-radius:14px;
	    padding:9px 8px;
	    background:rgba(255,255,255,.55);
	    text-align:center;
	  }
	  .faith-journey-stat b{
	    display:block;
	    font-size:18px;
	    color:var(--text-dark);
	    line-height:1;
	    margin-bottom:4px;
	  }
	  .faith-journey-stat span{
	    display:block;
	    font-size:11.5px;
	    color:var(--text-soft);
	    font-weight:800;
	    line-height:1.25;
	  }
	  .faith-journey-message{
	    margin:12px 0 0;
	    padding:9px 11px;
	    border-radius:14px;
	    background:rgba(255,249,220,.62);
	    color:var(--gold-deep);
	    font-size:13px;
	    font-weight:800;
	    line-height:1.45;
	    position:relative;
	    z-index:1;
	  }
	  @media(max-width:520px){
	    .faith-journey-panel{padding:9px;margin-bottom:12px;}
	    .faith-journey-toggle{gap:8px;}
	    .faith-journey-toggle-copy span{font-size:9.5px;letter-spacing:1px;}
	    .faith-journey-toggle-copy b{font-size:13px;}
	    .faith-journey-chevron{font-size:11px;padding:4px 7px;}
	    .faith-journey-day{min-width:50px;padding:6px 7px;}
	    .faith-journey-day strong{font-size:15px;}
	    .faith-journey-day span{font-size:8.5px;}
	    .faith-journey-growth{padding:11px;margin-bottom:11px;}
	    .faith-journey-growth-top b{font-size:20px;}
	    .faith-journey-growth-meta span{font-size:10.8px;}
	    .faith-journey-stats{gap:6px;}
	    .faith-journey-stat{padding:8px 5px;}
	    .faith-journey-stat b{font-size:16px;}
	    .faith-journey-stat span{font-size:10.5px;}
	  }
	  /* Keep the spirit as a simple glowing cloak shape instead of a humanoid figure. */
	  .spirit-cute-arms,
	  .spirit-arm,
	  .spirit-hand{
	    display:none!important;
	  }

	  /* Lamb clarity pass: rounder wool, clear head, ears, tiny legs, and a softer grounded walk. */
	  .home-pet-track.lamb-track{
	    left:5%!important;
	    right:53%!important;
	    bottom:11%!important;
	  }
	  .home-pet-track.lamb-track .animated-shop-item{
	    min-width:clamp(118px,32vw,188px)!important;
	    animation-name:lambMeadowWalk!important;
	    animation-duration:30s!important;
	    animation-timing-function:ease-in-out!important;
	  }
	  .home-pet-track.lamb-track .home-game-item.pet_lamb{
	    width:clamp(58px,15vw,82px)!important;
	    height:clamp(46px,12vw,64px)!important;
	    overflow:visible!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-glow{opacity:.05!important;}
	  .home-room .home-game-item.pet_lamb .home-game-shadow{
	    left:7%!important;
	    right:8%!important;
	    bottom:0!important;
	    height:10px!important;
	    opacity:.34!important;
	    filter:blur(1.4px)!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-body{
	    left:15%!important;
	    right:20%!important;
	    bottom:13px!important;
	    height:47%!important;
	    border-radius:58% 54% 48% 50%!important;
	    background:
	      radial-gradient(circle at 13% 24%,#fff 0 10%,transparent 11%),
	      radial-gradient(circle at 31% 13%,#fff 0 12%,transparent 13%),
	      radial-gradient(circle at 52% 18%,#fff 0 10%,transparent 11%),
	      radial-gradient(circle at 72% 30%,#fff 0 9%,transparent 10%),
	      linear-gradient(180deg,#fffef9 0%,#f3eadb 62%,#bfa98e 100%)!important;
	    border:1px solid rgba(108,82,48,.18)!important;
	    box-shadow:inset 0 5px 8px rgba(255,255,255,.9),0 6px 8px rgba(82,55,15,.10)!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-body::before{
	    content:"";
	    position:absolute;
	    left:6%;
	    right:10%;
	    top:-8px;
	    height:18px;
	    border-radius:50%;
	    background:
	      radial-gradient(circle at 14% 68%,#fff 0 5px,transparent 6px),
	      radial-gradient(circle at 40% 42%,#fff 0 6px,transparent 7px),
	      radial-gradient(circle at 70% 64%,#fff 0 5px,transparent 6px)!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-body::after{
	    content:"";
	    position:absolute;
	    left:-11px!important;
	    top:12px!important;
	    width:18px!important;
	    height:15px!important;
	    border-radius:58% 46% 52% 44%!important;
	    background:linear-gradient(160deg,#fffaf2,#dec8ae)!important;
	    box-shadow:0 0 0 1px rgba(108,82,48,.16),5px 5px 0 -3px #3d2800!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left{
	    left:-3%!important;
	    right:auto!important;
	    top:31%!important;
	    width:8px!important;
	    height:12px!important;
	    border-radius:70% 40%!important;
	    background:#c9ad90!important;
	    border:1px solid rgba(108,82,48,.14)!important;
	    transform:rotate(-26deg)!important;
	    box-shadow:30px 5px 0 -3px #c9ad90!important;
	    animation:lambHeadBob 2.8s ease-in-out infinite!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left::before,
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left::after{
	    display:none!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-right{
	    display:block!important;
	    left:8%!important;
	    right:auto!important;
	    top:43%!important;
	    width:5px!important;
	    height:5px!important;
	    border-radius:50%!important;
	    background:#3d2800!important;
	    transform:none!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-icon{
	    display:block!important;
	    position:absolute!important;
	    left:22%!important;
	    right:17%!important;
	    bottom:5px!important;
	    width:auto!important;
	    height:11px!important;
	    margin:0!important;
	    font-size:0!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-icon::before,
	  .home-room .home-game-item.pet_lamb .home-game-icon::after{
	    content:"";
	    position:absolute;
	    bottom:0;
	    width:4px!important;
	    height:11px!important;
	    border-radius:999px!important;
	    background:#6f5a47!important;
	    box-shadow:12px 0 0 #836e58!important;
	    animation:lambLegs 1.25s ease-in-out infinite!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-icon::before{left:3px!important;}
	  .home-room .home-game-item.pet_lamb .home-game-icon::after{right:5px!important;animation-delay:.2s!important;}

	  /* Living sanctuary pass: time of day, ambient motion, and action reactions. */
	  .home-room.home-time-morning .home-sky{
	    filter:saturate(1.06) brightness(1.04)!important;
	  }
	  .home-room.home-time-day .home-sky{
	    filter:saturate(1.04) brightness(1.02)!important;
	  }
	  .home-room.home-time-evening .home-sky{
	    filter:saturate(1.1) sepia(.08) brightness(.98)!important;
	  }
	  .home-room.home-time-evening .home-sky::after{
	    content:"";
	    position:absolute;
	    inset:0;
	    background:linear-gradient(180deg,rgba(255,219,163,.18),rgba(255,185,130,.12) 48%,transparent);
	    pointer-events:none;
	  }
	  .home-room.home-time-night .home-sky{
	    filter:saturate(.82) brightness(.72)!important;
	  }
	  .home-room.home-time-night .home-sky::after{
	    content:"";
	    position:absolute;
	    inset:0;
	    background:
	      radial-gradient(circle at 16% 20%,rgba(255,255,255,.82) 0 1px,transparent 2px),
	      radial-gradient(circle at 34% 15%,rgba(255,255,255,.62) 0 1px,transparent 2px),
	      radial-gradient(circle at 78% 22%,rgba(255,255,255,.72) 0 1px,transparent 2px),
	      radial-gradient(circle at 88% 11%,rgba(255,248,223,.90) 0 13px,transparent 14px),
	      linear-gradient(180deg,rgba(32,50,88,.38),rgba(32,50,88,.22) 54%,transparent);
	    pointer-events:none;
	  }
	  .home-room.home-time-night .home-flowerbed,
	  .home-room.home-time-night .daily-bloom{
	    filter:drop-shadow(0 0 6px rgba(186,230,253,.42)) brightness(1.06)!important;
	  }
	  .home-ambient-particles{
	    position:absolute;
	    inset:0;
	    z-index:9;
	    pointer-events:none;
	    overflow:hidden;
	  }
	  .home-ambient-particles .particle{
	    position:absolute;
	    width:7px;
	    height:7px;
	    border-radius:50%;
	    opacity:.28;
	    background:radial-gradient(circle,#fff 0 24%,rgba(255,248,223,.72) 26% 60%,transparent 62%);
	    filter:drop-shadow(0 0 5px rgba(255,248,223,.52));
	    animation:homeAmbientFloat 11s ease-in-out infinite;
	  }
	  .home-ambient-particles .p1{left:12%;top:74%;animation-delay:-1.2s;}
	  .home-ambient-particles .p2{left:29%;top:38%;animation-delay:-4.8s;background:radial-gradient(circle,#fff 0 24%,rgba(249,168,212,.52) 26% 60%,transparent 62%);}
	  .home-ambient-particles .p3{left:54%;top:23%;animation-delay:-6.2s;}
	  .home-ambient-particles .p4{left:72%;top:66%;animation-delay:-2.8s;background:radial-gradient(circle,#fff 0 24%,rgba(186,230,253,.64) 26% 60%,transparent 62%);}
	  .home-ambient-particles .p5{left:86%;top:42%;animation-delay:-8.4s;}
	  .home-room .home-spirit-slot > div{
	    transform-origin:50% 92%!important;
	    animation:homeSpiritBreathing 4.8s ease-in-out infinite!important;
	  }
	  .home-room.home-care-glowing .home-spirit-slot > div,
	  .home-room.home-care-blooming .home-spirit-slot > div,
	  .home-room.home-care-radiant .home-spirit-slot > div{
	    animation:homeSpiritHappyBreathing 3.4s ease-in-out infinite!important;
	  }
	  .home-spirit-blink{
	    position:absolute;
	    top:38.5%;
	    width:8px;
	    height:3px;
	    border-radius:999px;
	    background:#3d2800;
	    z-index:44;
	    opacity:0;
	    transform-origin:50% 50%;
	    animation:homeSpiritBlink 7.2s ease-in-out infinite;
	    pointer-events:none;
	  }
	  .home-spirit-blink.blink-left{left:42.5%;}
	  .home-spirit-blink.blink-right{right:42.5%;animation-delay:.08s;}
	  .home-spirit-smile{
	    position:absolute;
	    left:50%;
	    top:45%;
	    width:18px;
	    height:9px;
	    transform:translateX(-50%);
	    border-bottom:2px solid rgba(61,40,0,.55);
	    border-radius:0 0 999px 999px;
	    z-index:44;
	    opacity:.55;
	    animation:homeSpiritSmile 8.5s ease-in-out infinite;
	    pointer-events:none;
	  }
	  .home-spirit-ground-shadow{
	    position:absolute;
	    left:50%;
	    bottom:2px;
	    width:88px;
	    height:18px;
	    transform:translateX(-50%);
	    border-radius:50%;
	    background:radial-gradient(ellipse,rgba(82,55,15,.18),transparent 72%);
	    filter:blur(1px);
	    z-index:39;
	    pointer-events:none;
	    animation:homeSpiritShadow 4.8s ease-in-out infinite;
	  }
	  .home-spirit-visitor{
	    position:absolute;
	    left:15%;
	    bottom:24%;
	    width:34px;
	    height:42px;
	    z-index:23;
	    opacity:.82;
	    animation:spiritVisitorFloat 6.8s ease-in-out infinite;
	    pointer-events:none;
	  }
	  .home-spirit-visitor .visitor-aura{
	    position:absolute;
	    inset:0;
	    border-radius:50%;
	    background:radial-gradient(circle,rgba(255,255,255,.68),rgba(186,230,253,.34) 48%,transparent 72%);
	    filter:blur(.4px);
	    animation:homePrayerGlow 4s ease-in-out infinite;
	  }
	  .home-spirit-visitor .visitor-head{
	    position:absolute;
	    left:50%;
	    top:7px;
	    width:15px;
	    height:15px;
	    transform:translateX(-50%);
	    border-radius:50%;
	    background:linear-gradient(180deg,#fff,#dbeafe);
	    box-shadow:0 0 8px rgba(255,255,255,.7);
	  }
	  .home-spirit-visitor .visitor-body{
	    position:absolute;
	    left:50%;
	    bottom:7px;
	    width:20px;
	    height:23px;
	    transform:translateX(-50%);
	    border-radius:999px 999px 8px 8px;
	    background:linear-gradient(180deg,rgba(255,255,255,.95),rgba(186,230,253,.62));
	  }
	  .home-reflection-butterflies{
	    position:absolute;
	    right:18%;
	    bottom:35%;
	    width:92px;
	    height:58px;
	    z-index:21;
	    pointer-events:none;
	  }
	  .reflection-butterfly{
	    position:absolute;
	    width:18px;
	    height:18px;
	    border-radius:70% 40%;
	    background:linear-gradient(145deg,#fff,#fbcfe8 42%,#7dd3fc);
	    filter:drop-shadow(0 0 5px rgba(255,255,255,.65));
	    opacity:.72;
	    animation:reflectionButterflyFloat 7s ease-in-out infinite;
	  }
	  .reflection-butterfly::after{
	    content:"";
	    position:absolute;
	    left:12px;
	    top:1px;
	    width:18px;
	    height:18px;
	    border-radius:40% 70%;
	    background:inherit;
	    transform-origin:0 50%;
	    animation:reflectionWing .9s ease-in-out infinite;
	  }
	  .reflection-butterfly.b1{left:4px;top:22px;}
	  .reflection-butterfly.b2{right:18px;top:4px;transform:scale(.78);animation-delay:-2.2s;}
	  .home-testimony-tree{
	    position:absolute;
	    right:10%;
	    bottom:18%;
	    width:50px;
	    height:70px;
	    z-index:20;
	    pointer-events:none;
	    animation:testimonyTreeGrow .9s ease-out both;
	  }
	  .home-testimony-tree .tree-trunk{
	    position:absolute;
	    left:50%;
	    bottom:0;
	    width:10px;
	    height:34px;
	    transform:translateX(-50%);
	    border-radius:999px;
	    background:linear-gradient(90deg,#8a5a08,#d6a21a,#7b4b0a);
	  }
	  .home-testimony-tree .tree-crown{
	    position:absolute;
	    left:50%;
	    top:1px;
	    width:45px;
	    height:42px;
	    transform:translateX(-50%);
	    border-radius:50%;
	    background:radial-gradient(circle at 35% 28%,#fff8df 0 8px,transparent 9px),radial-gradient(circle,#86efac,#4f8c38 72%);
	    box-shadow:0 0 12px rgba(255,248,223,.44),inset 0 5px 8px rgba(255,255,255,.28);
	    animation:testimonyTreeGlow 4.5s ease-in-out infinite;
	  }
	  .home-testimony-tree .tree-fruit{
	    position:absolute;
	    width:7px;
	    height:7px;
	    border-radius:50%;
	    background:radial-gradient(circle,#fff,#f0c75e 68%);
	    box-shadow:0 0 7px rgba(240,203,118,.58);
	    animation:fruitGlow 3.2s ease-in-out infinite;
	  }
	  .home-testimony-tree .f1{left:16px;top:22px;}
	  .home-testimony-tree .f2{right:15px;top:25px;animation-delay:1s;}
	  .home-testimony-tree .f3{left:25px;top:11px;animation-delay:1.8s;}
	  .home-room.home-care-peaceful .home-rays{opacity:.78!important;}
	  .home-room.home-care-glowing .home-rays,
	  .home-room.home-care-blooming .home-rays,
	  .home-room.home-care-radiant .home-rays{
	    opacity:.98!important;
	    filter:brightness(1.12)!important;
	  }
	  .home-room.home-care-radiant .home-palace,
	  .home-room.home-care-radiant .home-house,
	  .home-room.home-care-radiant .home-mansion,
	  .home-room.home-care-radiant .home-little-shelter,
	  .home-room.home-care-radiant .home-cardboard{
	    filter:drop-shadow(0 16px 24px rgba(240,203,118,.30)) drop-shadow(0 0 18px rgba(255,248,223,.42))!important;
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot{
	    animation:none!important;
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot > div{
	    animation:faithFlightGlow 4.2s ease-in-out both!important;
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot::before,
	  .home-room.home-faith-flight-active .home-spirit-slot::after{
	    content:"";
	    position:absolute;
	    left:50%;
	    top:55%;
	    width:8px;
	    height:8px;
	    border-radius:50%;
	    background:radial-gradient(circle,#fff 0 22%,#f0c75e 24% 58%,transparent 60%);
	    box-shadow:
	      -24px 12px 0 -2px rgba(255,248,223,.78),
	      24px 16px 0 -3px rgba(186,230,253,.72),
	      -10px 38px 0 -3px rgba(249,168,212,.70),
	      16px 42px 0 -4px rgba(255,248,223,.76);
	    z-index:43;
	    pointer-events:none;
	    animation:faithFlightPetals 4.2s ease-out both;
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot::after{
	    animation-delay:.25s;
	    transform:scale(.82);
	    opacity:.78;
	  }
	  .daily-fruit-tree{
	    position:absolute;
	    left:5.5%;
	    bottom:7%;
	    width:132px;
	    height:168px;
	    z-index:9;
	    pointer-events:none;
	    transform-origin:50% 100%;
	    animation:dailyFruitTreeSettle .8s ease-out both;
	  }
	  .daily-tree-shadow{
	    position:absolute;
	    left:5%;
	    right:5%;
	    bottom:0;
	    height:20px;
	    border-radius:50%;
	    background:radial-gradient(ellipse,rgba(54,83,40,.24),rgba(80,94,42,.12) 45%,transparent 72%);
	    filter:blur(1.5px);
	  }
	  .daily-tree-trunk{
	    position:absolute;
	    left:50%;
	    bottom:13px;
	    width:24px;
	    height:78px;
	    transform:translateX(-50%);
	    border-radius:999px 999px 8px 8px;
	    background:
	      linear-gradient(90deg,rgba(79,47,17,.92),#b47a35 36%,#e0b36b 52%,#6b4218 100%);
	    box-shadow:inset 4px 0 5px rgba(255,248,223,.34),inset -5px 0 7px rgba(57,34,11,.22),0 6px 10px rgba(82,55,15,.14);
	  }
	  .daily-tree-trunk::before,
	  .daily-tree-trunk::after{
	    content:"";
	    position:absolute;
	    bottom:33px;
	    width:48px;
	    height:13px;
	    border-radius:999px;
	    background:linear-gradient(90deg,#6f461b,#c89445 62%,#8a5a22);
	    box-shadow:inset 0 2px 3px rgba(255,248,223,.28);
	  }
	  .daily-tree-trunk::before{right:12px;transform:rotate(-34deg);}
	  .daily-tree-trunk::after{left:12px;transform:rotate(34deg);}
	  .daily-tree-branch{
	    position:absolute;
	    left:50%;
	    bottom:78px;
	    width:62px;
	    height:12px;
	    border-radius:999px;
	    background:linear-gradient(90deg,#6f461b,#c89445 58%,#7a4a19);
	    box-shadow:inset 0 2px 3px rgba(255,248,223,.28);
	    z-index:1;
	  }
	  .daily-tree-branch.branch-left{transform:translateX(-84%) rotate(-18deg);}
	  .daily-tree-branch.branch-right{transform:translateX(-16%) rotate(18deg);}
	  .daily-tree-crown{
	    position:absolute;
	    left:50%;
	    top:10px;
	    width:112px;
	    height:108px;
	    transform:translateX(-50%);
	    border-radius:48% 54% 44% 58% / 44% 46% 52% 50%;
	    background:
	      radial-gradient(circle at 22% 32%,rgba(255,255,255,.58) 0 9px,transparent 10px),
	      radial-gradient(circle at 70% 22%,rgba(255,248,223,.42) 0 10px,transparent 11px),
	      radial-gradient(circle at 42% 66%,rgba(186,230,253,.20),transparent 24%),
	      linear-gradient(160deg,#c8ed9a 0%,#7bc45f 42%,#3f8338 100%);
	    box-shadow:inset 0 12px 14px rgba(255,255,255,.30),inset 0 -15px 17px rgba(43,97,39,.18),0 9px 15px rgba(82,55,15,.12),0 0 18px rgba(134,239,172,.20);
	    animation:dailyFruitTreeSway 6.4s ease-in-out infinite;
	    z-index:2;
	  }
	  .daily-tree-crown.crown-back{
	    top:2px;
	    width:128px;
	    height:118px;
	    opacity:.72;
	    background:linear-gradient(160deg,#d8f5b2,#7ac661 58%,#427f38);
	    filter:blur(.2px);
	    z-index:0;
	    animation-delay:.4s;
	  }
	  .daily-tree-crown.crown-front{
	    top:34px;
	    width:100px;
	    height:86px;
	    opacity:.88;
	    background:
	      radial-gradient(circle at 34% 28%,rgba(255,255,255,.42) 0 7px,transparent 8px),
	      linear-gradient(160deg,#b9e985,#65ad4d 60%,#326d31);
	    z-index:3;
	    animation-delay:.8s;
	  }
	  .daily-tree-glow{
	    position:absolute;
	    left:50%;
	    top:28px;
	    width:112px;
	    height:112px;
	    transform:translateX(-50%);
	    border-radius:50%;
	    background:radial-gradient(circle,rgba(255,248,223,.28),rgba(134,239,172,.14) 44%,transparent 70%);
	    filter:blur(2px);
	    z-index:4;
	    animation:dailyTreeGlow 5.4s ease-in-out infinite;
	  }
	  .daily-fruit{
	    position:absolute;
	    width:13px;
	    height:13px;
	    border-radius:50%;
	    background:
	      radial-gradient(circle at 32% 25%,#fff 0 16%,#fef3c7 18% 38%,#f0c75e 60%,#a8670d 100%);
	    box-shadow:0 0 9px rgba(240,203,118,.54),0 3px 4px rgba(82,55,15,.12);
	    z-index:5;
	    animation:dailyFruitPop .6s ease-out both,dailyFruitGlow 4.6s ease-in-out var(--bloom-delay,0s) infinite;
	  }
	  @keyframes dailyTreeGlow{0%,100%{opacity:.32;transform:translateX(-50%) scale(.94)}50%{opacity:.64;transform:translateX(-50%) scale(1.05)}}
	  @media (max-width:520px){
	    .daily-fruit-tree{
	      left:3.5%;
	      bottom:8%;
	      width:106px;
	      height:140px;
	      transform:scale(.92);
	    }
	  }
	  .home-page .home-slot.feature-slot::before,
	  .home-page .home-slot.feature-slot.furn_fountain-slot::before,
	  .home-page .home-slot.feature-slot.furn_waterfall-slot::before,
	  .home-page .home-slot.feature-slot.furn_lily_pond-slot::before{
	    content:none!important;
	    display:none!important;
	  }
	  .spirit-visitors-layer{
	    position:absolute;
	    inset:0;
	    z-index:28;
	    pointer-events:none;
	  }
	  .spirit-visitor{
	    position:absolute;
	    width:44px;
	    height:40px;
	    transform-origin:50% 100%;
	    animation:visitorArrive .65s ease-out both,visitorIdle 9.5s ease-in-out .8s infinite;
	    filter:drop-shadow(0 6px 8px rgba(82,55,15,.10));
	    opacity:.78;
	  }
	  .spirit-visitor.visitor-0{left:15%;bottom:17%;}
	  .spirit-visitor.visitor-1{right:15%;bottom:18%;animation-delay:.18s,.88s;}
	  .spirit-visitor.visitor-2{left:68%;top:34%;animation-delay:.32s,1.2s;}
	  .visitor-shadow{
	    position:absolute;
	    left:10%;
	    right:10%;
	    bottom:0;
	    height:9px;
	    border-radius:50%;
	    background:radial-gradient(ellipse,rgba(74,62,36,.20),transparent 72%);
	    filter:blur(1px);
	  }
	  .visitor-aura{
	    position:absolute;
	    inset:2px;
	    border-radius:50%;
	    background:radial-gradient(circle,rgba(255,248,223,.28),transparent 68%);
	    animation:visitorAura 4.8s ease-in-out infinite;
	  }
	  .visitor-body{
	    position:absolute;
	    left:15px;
	    bottom:9px;
	    width:28px;
	    height:22px;
	    border-radius:55% 48% 46% 50%;
	    background:linear-gradient(145deg,#fff,#e8f6ff 56%,#bae6fd);
	    border:1px solid rgba(255,255,255,.62);
	    box-shadow:inset 0 5px 7px rgba(255,255,255,.55),inset 0 -5px 6px rgba(65,95,125,.12);
	  }
	  .visitor-head{
	    position:absolute;
	    left:30px;
	    bottom:24px;
	    width:16px;
	    height:15px;
	    border-radius:50%;
	    background:linear-gradient(145deg,#fff,#e8f6ff);
	    border:1px solid rgba(255,255,255,.62);
	  }
	  .visitor-head::after{
	    content:"";
	    position:absolute;
	    right:4px;
	    top:5px;
	    width:2.5px;
	    height:2.5px;
	    border-radius:50%;
	    background:rgba(61,40,0,.55);
	  }
	  .visitor-wing{
	    display:none;
	    position:absolute;
	    width:22px;
	    height:18px;
	    border-radius:72% 35% 72% 35%;
	    background:linear-gradient(145deg,#fff,rgba(186,230,253,.82));
	    transform-origin:100% 70%;
	    opacity:.82;
	    animation:visitorWing .86s ease-in-out infinite;
	  }
	  .visitor-wing.wing-left{left:6px;bottom:20px;transform:rotate(-24deg);}
	  .visitor-wing.wing-right{right:8px;bottom:20px;transform-origin:0 70%;transform:scaleX(-1) rotate(-24deg);animation-delay:.08s;}
	  .visitor-ear,.visitor-tail,.visitor-mark{position:absolute;display:none;}
	  .visitor-dove,
	  .visitor-golden_dove,
	  .visitor-bluebird,
	  .visitor-butterfly{
	    width:48px;
	    height:44px;
	    animation:visitorArrive .65s ease-out both,visitorFlyPath 11.5s ease-in-out .8s infinite;
	  }
	  .visitor-dove,
	  .visitor-golden_dove{
	    z-index:26;
	    animation:visitorArrive .65s ease-out both,visitorDoveAcross 18s ease-in-out .8s infinite;
	  }
	  .visitor-dove.visitor-0,
	  .visitor-golden_dove.visitor-0{left:-14%;top:35%;bottom:auto;}
	  .visitor-dove.visitor-1,
	  .visitor-golden_dove.visitor-1{left:-20%;right:auto;top:42%;bottom:auto;animation-delay:.2s,6.5s;}
	  .visitor-bluebird.visitor-0,
	  .visitor-bluebird.visitor-1{right:18%;top:38%;bottom:auto;left:auto;}
	  .visitor-butterfly.visitor-0{left:18%;bottom:34%;}
	  .visitor-butterfly.visitor-1{right:18%;bottom:35%;}
	  .visitor-dove .visitor-shadow,
	  .visitor-golden_dove .visitor-shadow,
	  .visitor-bluebird .visitor-shadow,
	  .visitor-butterfly .visitor-shadow{display:none;}
	  .visitor-dove .visitor-wing,
	  .visitor-golden_dove .visitor-wing,
	  .visitor-bluebird .visitor-wing,
	  .visitor-butterfly .visitor-wing{display:block;}
	  .visitor-golden_dove .visitor-body,
	  .visitor-golden_dove .visitor-head,
	  .visitor-golden_dove .visitor-wing{
	    background:linear-gradient(145deg,#fffdf4 0%,#ffe9a6 46%,#d79a17 100%);
	    border:1.6px solid rgba(138,90,8,.42);
	    box-shadow:inset 0 5px 8px rgba(255,255,255,.75),inset 0 -5px 7px rgba(138,90,8,.18),0 5px 9px rgba(82,55,15,.16),0 0 16px rgba(240,203,118,.55);
	  }
	  .visitor-dove .visitor-body,
	  .visitor-dove .visitor-head,
	  .visitor-dove .visitor-wing{
	    background:linear-gradient(145deg,#ffffff 0%,#eef8ff 46%,#b8dcf5 100%);
	    border:1.6px solid rgba(91,143,184,.38);
	    box-shadow:inset 0 5px 8px rgba(255,255,255,.82),inset 0 -5px 7px rgba(65,95,125,.13),0 5px 9px rgba(65,95,125,.14),0 0 14px rgba(186,230,253,.46);
	  }
	  .visitor-dove .visitor-head::after,
	  .visitor-golden_dove .visitor-head::after{
	    width:3.5px;
	    height:3.5px;
	    background:#3a2600;
	    box-shadow:0 0 0 1px rgba(255,255,255,.50);
	  }
	  .visitor-dove .visitor-body::after,
	  .visitor-golden_dove .visitor-body::after{
	    content:"";
	    position:absolute;
	    right:-5px;
	    bottom:8px;
	    width:10px;
	    height:8px;
	    clip-path:polygon(0 50%,100% 0,100% 100%);
	    background:inherit;
	    border-right:1px solid rgba(82,55,15,.18);
	    filter:brightness(.94);
	  }
	  .visitor-dove .visitor-wing,
	  .visitor-golden_dove .visitor-wing{
	    opacity:.98;
	    filter:drop-shadow(0 3px 4px rgba(82,55,15,.12));
	  }
	  .visitor-golden_dove .visitor-aura{
	    background:radial-gradient(circle,rgba(255,248,223,.55),rgba(240,203,118,.18) 52%,transparent 72%);
	  }
	  .visitor-bluebird .visitor-body,
	  .visitor-bluebird .visitor-head,
	  .visitor-bluebird .visitor-wing{
	    background:linear-gradient(145deg,#fff,#bae6fd 38%,#60a5fa);
	  }
	  .visitor-butterfly .visitor-body{
	    left:26px;
	    width:8px;
	    height:24px;
	    border-radius:999px;
	    background:linear-gradient(180deg,#7c5a1e,#4b3410);
	  }
	  .visitor-butterfly .visitor-head{left:25px;bottom:31px;width:10px;height:10px;background:#4b3410;}
	  .visitor-butterfly .visitor-wing{
	    width:24px;
	    height:25px;
	    bottom:17px;
	    background:radial-gradient(circle at 35% 28%,#fff,#f9a8d4 42%,#7dd3fc 100%);
	    opacity:.9;
	  }
	  .visitor-lamb .visitor-body{
	    left:12px;
	    width:34px;
	    height:24px;
	    border-radius:55%;
	    background:
	      radial-gradient(circle at 18% 38%,#fff 0 5px,transparent 6px),
	      radial-gradient(circle at 42% 20%,#fff 0 5px,transparent 6px),
	      linear-gradient(145deg,#fff,#f5f5f4 62%,#d6d3d1);
	  }
	  .visitor-lamb .visitor-head{left:35px;bottom:23px;background:linear-gradient(145deg,#fff,#f5f5f4);}
	  .visitor-lamb .visitor-mark{
	    display:block;
	    left:18px;
	    bottom:8px;
	    width:3px;
	    height:9px;
	    border-radius:999px;
	    background:#a8956e;
	    box-shadow:13px 0 0 #a8956e,24px 0 0 #a8956e;
	  }
	  .visitor-rabbit .visitor-body{background:linear-gradient(145deg,#fff,#f5f5f4 58%,#e7d5c2);}
	  .visitor-rabbit .visitor-head{left:34px;background:linear-gradient(145deg,#fff,#f5f5f4);}
	  .visitor-rabbit .visitor-ear{
	    display:block;
	    left:36px;
	    bottom:37px;
	    width:6px;
	    height:17px;
	    border-radius:999px;
	    background:linear-gradient(180deg,#fff,#f5f5f4);
	    transform:rotate(-12deg);
	    box-shadow:8px 1px 0 -1px #f5f5f4;
	  }
	  .visitor-rabbit .visitor-tail{
	    display:block;
	    left:9px;
	    bottom:18px;
	    width:10px;
	    height:10px;
	    border-radius:50%;
	    background:#fff;
	  }
	  .visitor-deer{
	    width:54px;
	    height:48px;
	    animation:visitorArrive .65s ease-out both,visitorDeerWatch 11s ease-in-out .8s infinite;
	  }
	  .visitor-deer.visitor-0{left:12%;bottom:24%;}
	  .visitor-deer.visitor-1{right:10%;bottom:24%;}
	  .visitor-deer .visitor-body{width:38px;height:24px;background:linear-gradient(145deg,#d9a66b,#9b6b3b);}
	  .visitor-deer .visitor-head{left:40px;background:linear-gradient(145deg,#d9a66b,#a06c3d);}
	  .visitor-deer .visitor-ear{
	    display:block;
	    left:42px;
	    bottom:36px;
	    width:6px;
	    height:14px;
	    border-radius:999px;
	    background:#9b6b3b;
	    transform:rotate(-35deg);
	    box-shadow:11px -1px 0 -1px #9b6b3b;
	  }
	  .visitor-deer .visitor-mark{
	    display:block;
	    left:21px;
	    bottom:22px;
	    width:4px;
	    height:4px;
	    border-radius:50%;
	    background:rgba(255,248,223,.82);
	    box-shadow:9px -3px 0 -1px rgba(255,248,223,.82),17px 1px 0 -1px rgba(255,248,223,.82);
	  }
	  .visitor-fox .visitor-body{width:34px;background:linear-gradient(145deg,#f7b267,#d97706);}
	  .visitor-fox .visitor-head{left:35px;background:linear-gradient(145deg,#f7b267,#d97706);border-radius:45% 55% 50% 45%;}
	  .visitor-fox .visitor-ear{
	    display:block;
	    left:36px;
	    bottom:36px;
	    width:9px;
	    height:12px;
	    clip-path:polygon(50% 0,100% 100%,0 100%);
	    background:#b45309;
	    box-shadow:10px 0 0 #b45309;
	  }
	  .visitor-fox .visitor-tail{
	    display:block;
	    left:4px;
	    bottom:15px;
	    width:22px;
	    height:12px;
	    border-radius:999px;
	    background:linear-gradient(90deg,#fff 0 22%,#d97706 23% 100%);
	    transform:rotate(-18deg);
	  }
	  @keyframes visitorArrive{from{opacity:0;transform:translateY(8px) scale(.82)}to{opacity:1;transform:translateY(0) scale(1)}}
	  @keyframes visitorIdle{0%,100%{transform:translate(0,0) scale(.94)}45%{transform:translate(5px,-1px) scale(.96)}72%{transform:translate(-3px,0) scale(.94)}}
	  @keyframes visitorAura{0%,100%{opacity:.14;transform:scale(.9)}50%{opacity:.34;transform:scale(1.06)}}
	  @keyframes visitorWing{0%,100%{scale:1 .9}50%{scale:.82 1.05}}
	  @keyframes visitorFlyPath{0%,100%{transform:translate(0,0) rotate(-1deg) scale(.94)}35%{transform:translate(14px,-7px) rotate(2deg) scale(.96)}70%{transform:translate(5px,3px) rotate(-1deg) scale(.94)}}
	  @keyframes visitorDoveAcross{
	    0%{transform:translateX(0) translateY(0) rotate(-2deg) scale(.88);opacity:0}
	    8%{opacity:.86}
	    32%{transform:translateX(38vw) translateY(-10px) rotate(2deg) scale(.94);opacity:.94}
	    58%{transform:translateX(72vw) translateY(4px) rotate(-1deg) scale(.92);opacity:.9}
	    86%{opacity:.82}
	    100%{transform:translateX(112vw) translateY(-8px) rotate(2deg) scale(.88);opacity:0}
	  }
	  @keyframes visitorDeerWatch{0%,100%{transform:translateY(0)}45%{transform:translateY(-1px)}70%{transform:translateX(5px)}}
	  @media (prefers-reduced-motion:reduce){
	    .spirit-visitor,
	    .visitor-aura,
	    .visitor-wing{
	      animation:none!important;
	    }
	  }
	  @media (max-width:520px){
	    .spirit-visitor{
	      transform:scale(.84);
	    }
	    .spirit-visitor.visitor-0{left:9%;bottom:18%;}
	    .spirit-visitor.visitor-1{right:9%;bottom:18%;}
	    .visitor-dove.visitor-0,
	    .visitor-golden_dove.visitor-0{left:-18%;top:35%;bottom:auto;}
	    .visitor-dove.visitor-1,
	    .visitor-golden_dove.visitor-1{left:-24%;right:auto;top:42%;bottom:auto;}
	  }
	  .home-spirit-blink,
	  .home-spirit-smile{
	    display:none!important;
	  }
	  .home-room .home-spirit-slot{
	    bottom:1.2%!important;
	    transform:translateX(-50%) scale(1.22)!important;
	    pointer-events:auto!important;
	    cursor:pointer!important;
	    z-index:46!important;
	  }
	  .home-room .home-spirit-slot > div{
	    animation:homeSpiritLivingIdle 5.6s ease-in-out infinite!important;
	    filter:drop-shadow(0 16px 20px rgba(82,55,15,.18)) drop-shadow(0 0 18px rgba(255,248,223,.48)) drop-shadow(0 0 15px rgba(186,230,253,.20))!important;
	  }
	  .home-room .home-spirit-slot:focus-visible{
	    outline:2px solid rgba(211,166,75,.72);
	    outline-offset:8px;
	    border-radius:999px;
	  }
	  .spirit-live-face{
	    opacity:0;
	    transform-origin:60px 54px;
	    animation:spiritBlink 6.8s ease-in-out infinite;
	    pointer-events:none;
	  }
	  .spirit-live-face.seed-face{transform-origin:60px 88px;}
	  .spirit-smile-variant{
	    opacity:0;
	    animation:spiritSmileVariation 9.5s ease-in-out infinite;
	  }
	  .home-room.home-spirit-action-wave .home-spirit-slot > div{
	    animation:homeSpiritWaveAction 1.45s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .home-spirit-slot > div{
	    animation:homeSpiritPrayAction 1.45s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .home-prayer-hands{
	    animation:prayerHandsClasp 1.45s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .home-spirit-slot > div{
	    animation:homeSpiritTwirlAction 1.45s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-sit .home-spirit-slot > div{
	    animation:homeSpiritSitAction 1.8s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-dance .home-spirit-slot > div{
	    animation:homeSpiritDanceAction 1.65s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-shine .home-spirit-slot > div{
	    transform-origin:50% 78%!important;
	    animation:homeSpiritShineAction 2.15s ease-in-out both!important;
	    filter:brightness(1.45) saturate(1.16) drop-shadow(0 0 28px rgba(255,248,223,1)) drop-shadow(0 0 24px rgba(240,203,118,.72)) drop-shadow(0 0 36px rgba(186,230,253,.36))!important;
	  }
	  .home-room.home-spirit-action-wave .home-spirit-wave{
	    opacity:1!important;
	    animation:homeSpiritBigWave 1.35s ease-in-out 2!important;
	  }
	  .home-room.home-spirit-action-pray .home-spirit-slot::after,
	  .home-room.home-spirit-action-twirl .home-spirit-slot::after,
	  .home-room.home-spirit-action-dance .home-spirit-slot::after,
	  .home-room.home-spirit-action-shine .home-spirit-slot::after{
	    content:"";
	    position:absolute;
	    left:50%;
	    top:10%;
	    width:76px;
	    height:76px;
	    border-radius:50%;
	    transform:translateX(-50%);
	    background:radial-gradient(circle,rgba(255,248,223,.52),rgba(186,230,253,.24) 48%,transparent 72%);
	    animation:spiritActionGlow 1.45s ease-out both;
	    pointer-events:none;
	    z-index:39;
	  }
	  .home-room.home-spirit-action-shine .home-spirit-slot::after{
	    top:-8%;
	    width:158px;
	    height:158px;
	    background:
	      radial-gradient(circle,rgba(255,255,255,.92),rgba(255,248,223,.70) 28%,rgba(240,203,118,.34) 52%,rgba(186,230,253,.18) 68%,transparent 80%);
	    animation:spiritShineHalo 2.15s ease-out both;
	  }
	  .home-room.home-spirit-action-shine .home-spirit-slot::before{
	    content:"";
	    position:absolute;
	    left:50%;
	    top:-18%;
	    width:184px;
	    height:184px;
	    border-radius:50%;
	    transform:translateX(-50%);
	    background:
	      conic-gradient(from 18deg,transparent 0 9%,rgba(255,248,223,.46) 10% 14%,transparent 15% 24%,rgba(186,230,253,.22) 25% 29%,transparent 30% 42%,rgba(255,255,255,.42) 43% 47%,transparent 48% 100%);
	    filter:blur(.4px);
	    opacity:0;
	    pointer-events:none;
	    z-index:38;
	    animation:spiritShineRays 2.15s ease-out both;
	  }
	  .home-room.home-spirit-action-shine .home-spirit-life.sparkle-one,
	  .home-room.home-spirit-action-shine .home-spirit-life.sparkle-two{
	    opacity:1!important;
	    animation:spiritShineSparkle 2.1s ease-out both!important;
	  }
	  .spirit-action-menu{
	    position:absolute;
	    left:50%;
	    bottom:58px;
	    width:min(236px,calc(100% - 28px));
	    transform:translateX(-50%);
	    display:grid;
	    grid-template-columns:repeat(2,minmax(0,1fr));
	    gap:7px;
	    padding:9px;
	    border-radius:18px;
	    background:linear-gradient(160deg,rgba(255,253,246,.96),rgba(240,249,255,.92));
	    border:1.5px solid rgba(211,166,75,.42);
	    box-shadow:0 10px 28px rgba(65,95,125,.16),0 0 0 1px rgba(255,255,255,.46) inset;
	    backdrop-filter:blur(12px);
	    z-index:96;
	    animation:spiritMenuInFixed .18s ease-out both;
	    pointer-events:auto;
	  }
	  .spirit-action-menu::after{
	    display:none;
	  }
	  @keyframes spiritMenuInFixed{from{opacity:0;transform:translateX(-50%) translateY(10px) scale(.96)}to{opacity:1;transform:translateX(-50%) translateY(0) scale(1)}}
	  .spirit-action-btn{
	    min-height:38px;
	    border-radius:13px;
	    border:1px solid rgba(125,174,212,.22);
	    background:rgba(255,255,255,.68);
	    color:var(--text-dark);
	    font-family:'Nunito',sans-serif;
	    font-size:12.5px;
	    font-weight:900;
	    display:flex;
	    align-items:center;
	    justify-content:center;
	    gap:5px;
	    cursor:pointer;
	    box-shadow:0 2px 8px rgba(65,95,125,.045);
	  }
	  .spirit-action-btn:hover,
	  .spirit-action-btn:focus-visible{
	    border-color:rgba(211,166,75,.56);
	    background:linear-gradient(135deg,rgba(255,252,210,.92),rgba(255,255,255,.82));
	    outline:none;
	  }
	  .spirit-action-btn.faith-action{
	    grid-column:1 / -1;
	    border:1.5px solid var(--gold);
	    background:var(--gold-grad-s);
	    color:var(--gold-deep);
	  }
	  .spirit-action-btn.faith-action.locked{
	    border-color:rgba(125,174,212,.24);
	    background:rgba(246,250,255,.72);
	    color:#5d7790;
	    cursor:default;
	    opacity:.86;
	  }
	  .spirit-action-lock-note{
	    grid-column:1 / -1;
	    color:var(--text-soft);
	    font-size:11.5px;
	    line-height:1.35;
	    text-align:center;
	    padding:0 4px 2px;
	  }
	  @keyframes spiritMenuIn{from{opacity:0;transform:translateX(-50%) translateY(8px) scale(.96)}to{opacity:1;transform:translateX(-50%) translateY(0) scale(1)}}
	  .home-window-dot,
	  .home-house-door,
	  .home-door,
	  .home-chapel-window,
	  .home-mansion-window{
	    box-shadow:0 0 10px rgba(255,248,223,.54),inset 0 1px 4px rgba(255,255,255,.72)!important;
	    animation:homeWarmWindowGlow 7.5s ease-in-out infinite;
	  }
	  .home-palace,
	  .home-house,
	  .home-mansion,
	  .home-chapel,
	  .home-little-shelter,
	  .home-cardboard{
	    animation:homeBuildingBreathe 10s ease-in-out infinite;
	  }
	  .home-room.home-time-evening .home-sky,
	  .home-room.home-time-night .home-sky{
	    filter:saturate(.96) brightness(.94);
	  }
	  .home-room.home-time-night .home-radiance,
	  .home-room.home-time-night .home-care-aura{
	    opacity:.72;
	  }
	  .home-room .particle{
	    opacity:.36!important;
	    animation-duration:8.5s!important;
	  }
	  .home-room .home-sky,
	  .home-room .home-cloud,
	  .home-room .home-rays,
	  .home-room .home-distant-city,
	  .home-room .home-background-rainbow{
	    transform:translate(
	      calc(var(--parallax-x,0) * -4px),
	      calc(var(--parallax-y,0) * -2px)
	    )!important;
	    transition:transform .22s ease-out;
	    will-change:transform;
	  }
	  .home-room .home-floor,
	  .home-room .home-path,
	  .home-room .home-care-aura,
	  .home-room .home-midground-glow{
	    transform:translate(
	      calc(var(--parallax-x,0) * 3px),
	      calc(var(--parallax-y,0) * 1.5px)
	    )!important;
	    transition:transform .22s ease-out;
	    will-change:transform;
	  }
	  .home-room .spirit-visitors-layer,
	  .home-room .home-ambient-particles{
	    transform:translate(
	      calc(var(--parallax-x,0) * 5px),
	      calc(var(--parallax-y,0) * 3px)
	    );
	    transition:transform .22s ease-out;
	    will-change:transform;
	  }
	  .home-room .home-water-feature{
	    filter:drop-shadow(0 8px 14px rgba(56,139,190,.12)) saturate(1.04)!important;
	  }
	  .home-room .water-ripple{
	    opacity:.48!important;
	    animation-duration:3.6s!important;
	  }
	  .home-room .water-shimmer{
	    opacity:.42!important;
	  }
	  .daily-tree-crown.crown-back{animation:dailyFruitTreeSway 8.6s ease-in-out infinite reverse;}
	  .daily-tree-crown.crown-front{animation:dailyFruitTreeSway 7.8s ease-in-out infinite;}
	  .home-visitors-note{
	    margin-top:10px;
	    border-radius:14px;
	    border:1px solid rgba(91,143,184,.24);
	    background:rgba(255,253,246,.64);
	    overflow:hidden;
	  }
	  .home-visitors-toggle{
	    width:100%;
	    display:flex;
	    align-items:center;
	    justify-content:space-between;
	    gap:10px;
	    border:none;
	    background:transparent;
	    padding:9px 11px;
	    color:#5d7790;
	    font-family:'Nunito',sans-serif;
	    font-size:12.5px;
	    font-weight:900;
	    cursor:pointer;
	  }
	  .home-visitors-list{
	    display:flex;
	    flex-direction:column;
	    gap:6px;
	    padding:0 10px 10px;
	  }
	  .home-visitor-note-row{
	    border-radius:12px;
	    background:rgba(246,250,255,.70);
	    border:1px solid rgba(125,174,212,.15);
	    padding:8px 9px;
	  }
	  .home-visitor-note-row span{
	    display:block;
	    color:var(--text-dark);
	    font-size:12.5px;
	    font-weight:900;
	    line-height:1.2;
	  }
	  .home-visitor-note-row small{
	    display:block;
	    margin-top:2px;
	    color:var(--text-soft);
	    font-size:12px;
	    line-height:1.35;
	  }
	  .home-page .home-shop-card,
	  .home-page .home-category-card{
	    background:rgba(255,253,246,.72)!important;
	    box-shadow:0 2px 10px rgba(65,95,125,.04)!important;
	  }
	  .home-page .home-shop-card:not(.expanded){
	    min-height:118px!important;
	  }
	  .home-page .home-shop-card p,
	  .home-page .home-shop-card .shop-card-desc{
	    display:none!important;
	  }
	  @keyframes homeAmbientFloat{0%,100%{transform:translateY(12px) translateX(0) scale(.76);opacity:.08}45%{opacity:.36}70%{transform:translateY(-28px) translateX(10px) scale(1.08);opacity:.24}}
	  @keyframes spiritBlink{0%,92%,100%{opacity:0;transform:scaleY(.16)}94%,96%{opacity:.96;transform:scaleY(1)}}
	  @keyframes spiritSmileVariation{0%,68%,100%{opacity:0}76%,88%{opacity:.62}}
	  @keyframes homeSpiritBreathing{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-3px) scale(1.015,.992)}}
	  @keyframes homeSpiritLivingIdle{0%,100%{transform:translateY(0) scale(1)}35%{transform:translateY(-4px) scale(1.018,.994)}70%{transform:translateY(-1px) scale(1.006)}}
	  @keyframes homeSpiritHappyBreathing{0%,100%{transform:translateY(0) scale(1)}30%{transform:translateY(-5px) scale(1.025,.995)}60%{transform:translateY(-2px) scale(1.01)}}
	  @keyframes homeSpiritBlink{0%,92%,100%{opacity:0;transform:scaleY(.2)}94%,96%{opacity:.78;transform:scaleY(1)}}
	  @keyframes homeSpiritSmile{0%,70%,100%{opacity:.45;transform:translateX(-50%) scaleX(.82)}82%,90%{opacity:.82;transform:translateX(-50%) scaleX(1.18)}}
	  @keyframes homeSpiritShadow{0%,100%{transform:translateX(-50%) scale(1);opacity:.18}50%{transform:translateX(-50%) scale(.88);opacity:.12}}
	  @keyframes homeSpiritWaveAction{0%,100%{transform:translateY(0) rotate(0) scale(1)}28%{transform:translateY(-6px) rotate(-4deg) scale(1.025)}56%{transform:translateY(-3px) rotate(4deg) scale(1.015)}}
	  @keyframes homeSpiritGentleWave{0%,100%{transform:rotate(16deg);opacity:.24}38%,62%{transform:rotate(-16deg);opacity:.48}}
	  @keyframes homeSpiritBigWave{
	    0%,100%{transform:rotate(18deg) translateY(0);opacity:.38}
	    18%{transform:rotate(-34deg) translateY(-3px);opacity:1}
	    36%{transform:rotate(28deg) translateY(-2px);opacity:1}
	    54%{transform:rotate(-30deg) translateY(-3px);opacity:1}
	    72%{transform:rotate(22deg) translateY(-1px);opacity:.92}
	  }
	  @keyframes homeSpiritPrayAction{0%,100%{transform:translateY(0) scale(1)}36%{transform:translateY(3px) scale(.985)}70%{transform:translateY(-3px) scale(1.018)}}
	  @keyframes prayerHandsClasp{
	    0%{opacity:0;transform:translateX(-50%) translateY(8px) scale(.74)}
	    22%{opacity:.96;transform:translateX(-50%) translateY(0) scale(1)}
	    72%{opacity:1;transform:translateX(-50%) translateY(-2px) scale(1.03)}
	    100%{opacity:0;transform:translateX(-50%) translateY(-6px) scale(.94)}
	  }
	  @keyframes prayerLeftHandClasp{
	    0%{transform:translate(-12px,6px) rotate(58deg) scale(.88);opacity:0}
	    24%{transform:translate(-3px,0) rotate(34deg) scale(1);opacity:1}
	    48%,74%{transform:translate(1px,-1px) rotate(17deg) scale(1.06);opacity:1}
	    100%{transform:translate(5px,-4px) rotate(20deg) scale(.96);opacity:0}
	  }
	  @keyframes prayerRightHandClasp{
	    0%{transform:translate(12px,6px) rotate(-58deg) scale(.88);opacity:0}
	    24%{transform:translate(3px,0) rotate(-34deg) scale(1);opacity:1}
	    48%,74%{transform:translate(-1px,-1px) rotate(-17deg) scale(1.06);opacity:1}
	    100%{transform:translate(-5px,-4px) rotate(-20deg) scale(.96);opacity:0}
	  }
	  @keyframes spiritArmFoldToPrayer{
	    0%{transform:rotate(18deg) translate(0,0);opacity:.18}
	    26%{transform:rotate(-52deg) translate(-8px,3px);opacity:.72}
	    48%,76%{transform:rotate(-76deg) translate(-14px,5px);opacity:.88}
	    100%{transform:rotate(-42deg) translate(-8px,-1px);opacity:0}
	  }
	  @keyframes homeSpiritTwirlAction{0%{transform:perspective(520px) translateY(0) rotateX(0deg) scale(1)}42%{transform:perspective(520px) translateY(-9px) rotateX(180deg) scale(1.04)}78%{transform:perspective(520px) translateY(-4px) rotateX(360deg) scale(1.02)}100%{transform:perspective(520px) translateY(0) rotateX(360deg) scale(1)}}
	  @keyframes homeSpiritSitAction{0%,100%{transform:translateY(0) scale(1)}38%,72%{transform:translateY(8px) scale(1.02,.94)}}
	  @keyframes homeSpiritDanceAction{0%,100%{transform:translateY(0) rotate(0) scale(1)}18%{transform:translateY(-5px) rotate(-5deg) scale(1.035)}36%{transform:translateY(1px) rotate(5deg) scale(1.01)}58%{transform:translateY(-6px) rotate(4deg) scale(1.035)}78%{transform:translateY(-1px) rotate(-4deg) scale(1.015)}}
	  @keyframes spiritActionGlow{0%{opacity:0;transform:translateX(-50%) scale(.78)}42%{opacity:.82;transform:translateX(-50%) scale(1.08)}100%{opacity:0;transform:translateX(-50%) scale(1.22)}}
	  @keyframes homeWarmWindowGlow{0%,100%{filter:brightness(1);opacity:.86}50%{filter:brightness(1.12);opacity:1}}
	  @keyframes homeBuildingBreathe{0%,100%{filter:drop-shadow(0 8px 12px rgba(82,55,15,.08))}50%{filter:drop-shadow(0 10px 15px rgba(82,55,15,.11)) drop-shadow(0 0 10px rgba(255,248,223,.20))}}
	  @keyframes spiritVisitorFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
	  @keyframes reflectionButterflyFloat{0%,100%{transform:translate(0,0) rotate(-8deg)}45%{transform:translate(18px,-12px) rotate(12deg)}75%{transform:translate(5px,6px) rotate(-4deg)}}
	  @keyframes reflectionWing{0%,100%{transform:scaleX(.72)}50%{transform:scaleX(1.12)}}
	  @keyframes testimonyTreeGrow{from{transform:translateY(10px) scale(.82);opacity:0}to{transform:translateY(0) scale(1);opacity:1}}
	  @keyframes testimonyTreeGlow{0%,100%{filter:brightness(1)}50%{filter:brightness(1.12)}}
	  @keyframes fruitGlow{0%,100%{transform:scale(.9);opacity:.72}50%{transform:scale(1.18);opacity:1}}
	  @keyframes dailyFruitTreeSettle{from{transform:translateY(10px) scale(.92);opacity:0}to{transform:translateY(0) scale(1);opacity:1}}
	  @keyframes dailyFruitTreeSway{0%,100%{transform:translateX(-50%) rotate(-1.2deg)}50%{transform:translateX(-50%) rotate(1.2deg)}}
	  @keyframes dailyFruitPop{from{transform:scale(.2);opacity:0}to{transform:scale(1);opacity:1}}
	  @keyframes dailyFruitGlow{0%,100%{filter:brightness(1);transform:scale(.96)}50%{filter:brightness(1.15);transform:scale(1.08)}}
	  @keyframes companionTooltipFloat{
	    0%{opacity:0;transform:translateX(-50%) translateY(7px) scale(.92)}
	    16%,78%{opacity:1;transform:translateX(-50%) translateY(0) scale(1)}
	    100%{opacity:0;transform:translateX(-50%) translateY(-8px) scale(.96)}
	  }
	  @keyframes companionHeartPop{0%,100%{transform:translateY(0) scale(.86);opacity:.72}42%{transform:translateY(-2px) scale(1.2);opacity:1}}
	  @keyframes companionSparklePop{0%,100%{transform:rotate(0deg) scale(.82);opacity:.48}50%{transform:rotate(18deg) scale(1.18);opacity:1}}
	  @keyframes companionAffectionLook{
	    0%,100%{transform:translate(0,0) scale(1)}
	    28%{transform:translate(3px,-3px) scale(1.08)}
	    56%{transform:translate(-2px,-1px) scale(1.03)}
	  }
	  @keyframes faithFlightPath{
	    0%{transform:translateX(-50%) translateY(0) rotate(0deg)}
	    24%{transform:translateX(-50%) translateY(-34px) rotate(-8deg)}
	    52%{transform:translateX(-50%) translateY(-82px) rotate(360deg)}
	    76%{transform:translateX(-50%) translateY(-36px) rotate(368deg)}
	    100%{transform:translateX(-50%) translateY(0) rotate(360deg)}
	  }
	  @keyframes faithFlightGlow{
	    0%,100%{filter:drop-shadow(0 12px 16px rgba(82,55,15,.12))}
	    38%,70%{filter:drop-shadow(0 14px 18px rgba(82,55,15,.10)) drop-shadow(0 0 20px rgba(255,248,223,.86)) drop-shadow(0 0 18px rgba(186,230,253,.44))}
	  }
	  @keyframes faithFlightPetals{
	    0%{transform:translate(-50%,0) scale(.7);opacity:0}
	    28%{opacity:.9}
	    100%{transform:translate(-50%,58px) scale(1.08);opacity:0}
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot{
	    transform:translateX(-50%) scale(1.22)!important;
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot > div{
	    animation:faithFlightAvatarPath 4.2s cubic-bezier(.22,.76,.24,1) both!important;
	    filter:drop-shadow(0 16px 20px rgba(82,55,15,.10)) drop-shadow(0 0 24px rgba(255,248,223,.86)) drop-shadow(0 0 22px rgba(186,230,253,.46))!important;
	  }
	  @keyframes faithFlightAvatarPath{
	    0%{transform:translateY(0) rotate(0deg) scale(1)}
	    24%{transform:translateY(-34px) rotate(-8deg) scale(1.04)}
	    52%{transform:translateY(-82px) rotate(360deg) scale(1.09)}
	    76%{transform:translateY(-36px) rotate(368deg) scale(1.04)}
	    100%{transform:translateY(0) rotate(360deg) scale(1)}
	  }
	  @media (max-width:560px){
	    .faith-flight-panel{align-items:flex-start;flex-direction:column;}
	    .faith-flight-btn,.faith-flight-lock{width:100%;text-align:center;}
	    .home-spirit-blink{top:36.5%;width:7px;}
	    .home-spirit-blink.blink-left{left:42%;}
	    .home-spirit-blink.blink-right{right:42%;}
	    .home-spirit-smile{top:43%;width:16px;}
	    .home-reflection-butterflies{right:10%;bottom:34%;transform:scale(.78);}
	    .home-testimony-tree{right:6%;bottom:18%;transform:scale(.82);}
	    .home-spirit-visitor{left:10%;bottom:25%;transform:scale(.82);}
	    .home-room .home-spirit-slot{bottom:1%!important;transform:translateX(-50%) scale(1.12)!important;}
	    .home-room.home-faith-flight-active .home-spirit-slot{transform:translateX(-50%) scale(1.12)!important;}
	    .spirit-action-menu{
	      bottom:54px;
	      width:min(224px,calc(100% - 20px));
	      grid-template-columns:repeat(2,minmax(0,1fr));
	      gap:6px;
	      padding:8px;
	    }
	    .spirit-action-btn{min-height:36px;font-size:12px;}
	  }
	  @media (prefers-reduced-motion: reduce){
	    .home-room.home-spirit-action-wave .home-spirit-slot > div,
	    .home-room.home-spirit-action-pray .home-spirit-slot > div,
	    .home-room.home-spirit-action-twirl .home-spirit-slot > div,
	    .home-room.home-spirit-action-sit .home-spirit-slot > div,
	    .home-room.home-spirit-action-dance .home-spirit-slot > div{
	      animation:none!important;
	      filter:drop-shadow(0 0 18px rgba(255,248,223,.78))!important;
	    }
	    .home-room .home-sky,
	    .home-room .home-cloud,
	    .home-room .home-rays,
	    .home-room .home-distant-city,
	    .home-room .home-background-rainbow,
	    .home-room .home-floor,
	    .home-room .home-path,
	    .home-room .home-care-aura,
	    .home-room .home-midground-glow,
	    .home-room .spirit-visitors-layer,
	    .home-room .home-ambient-particles,
	    .home-room .home-spirit-slot > div,
	    .home-page .home-pet-track.companion-active .animated-shop-item{
	      animation:none!important;
	      transform:none!important;
	      transition:none!important;
	    }
	    .home-room.home-faith-flight-active .home-spirit-slot{
	      animation:faithFlightReduced 900ms ease-out both!important;
	    }
	    .home-room.home-faith-flight-active .home-spirit-slot > div{
	      animation:faithFlightReducedAvatar 900ms ease-out both!important;
	      filter:drop-shadow(0 0 16px rgba(255,248,223,.78))!important;
	    }
	    .home-room.home-faith-flight-active .home-spirit-slot::before,
	    .home-room.home-faith-flight-active .home-spirit-slot::after{
	      animation:none!important;
	      opacity:.35!important;
	    }
	  }
	  @keyframes faithFlightReduced{
	    0%,100%{transform:translateX(-50%) translateY(0)}
	    50%{transform:translateX(-50%) translateY(-12px)}
	  }
	  @keyframes faithFlightReducedAvatar{
	    0%,100%{transform:translateY(0)}
	    50%{transform:translateY(-14px)}
	  }

	  /* Final polished sanctuary pass: more depth, texture, and soft realistic lighting. */
	  .home-page .home-room{
	    background:linear-gradient(180deg,rgba(255,253,246,.38),rgba(239,248,255,.10))!important;
	    box-shadow:
	      inset 0 -32px 48px rgba(92,123,68,.10),
	      inset 0 0 70px rgba(82,55,15,.055),
	      inset 0 0 0 1px rgba(255,255,255,.48),
	      0 10px 30px rgba(65,95,125,.12)!important;
	  }
	  .home-page .home-room::before{
	    content:""!important;
	    position:absolute!important;
	    inset:0!important;
	    z-index:30!important;
	    pointer-events:none!important;
	    background:
	      radial-gradient(circle at 50% 42%,transparent 0 46%,rgba(79,110,92,.06) 78%,rgba(82,55,15,.08) 100%),
	      linear-gradient(180deg,rgba(255,255,255,.16),transparent 32%,rgba(61,83,56,.08) 100%)!important;
	    mix-blend-mode:multiply;
	    opacity:.72!important;
	  }
	  .home-page .home-room::after{
	    z-index:4!important;
	    height:34%!important;
	    background:
	      radial-gradient(ellipse at 50% 70%,rgba(255,255,255,.28),transparent 60%),
	      radial-gradient(ellipse at 50% 92%,rgba(82,55,15,.08),transparent 64%)!important;
	  }
	  .home-room .home-sky{
	    filter:saturate(1.05) contrast(1.02);
	  }
	  .home-room .home-rays{
	    opacity:.68!important;
	    filter:blur(.6px) brightness(1.06)!important;
	    mix-blend-mode:screen;
	  }
	  .home-room .home-cloud{
	    background:linear-gradient(180deg,rgba(255,255,255,.82),rgba(245,252,255,.50))!important;
	    box-shadow:0 8px 24px rgba(255,255,255,.28),inset 0 1px 8px rgba(255,255,255,.55)!important;
	    opacity:.72;
	  }
	  .home-room .home-cloud.c3,
	  .home-room .home-distant-city,
	  .home-room .home-sky-bird{
	    filter:blur(.25px) saturate(.86)!important;
	    opacity:.30!important;
	  }
	  .home-room .home-floor{
	    background:
	      radial-gradient(circle at 12% 72%,rgba(255,255,255,.34) 0 2px,transparent 3px),
	      radial-gradient(circle at 82% 64%,rgba(255,255,255,.25) 0 2px,transparent 3px),
	      radial-gradient(ellipse at 50% 0%,rgba(255,248,223,.62),transparent 42%),
	      linear-gradient(180deg,#fff3c9 0%,#cfe7a8 52%,#8faa63 100%)!important;
	    border-top:1px solid rgba(255,255,255,.40)!important;
	  }
	  .home-room .home-floor::before{
	    content:"";
	    position:absolute;
	    inset:8% 0 0;
	    pointer-events:none;
	    background:
	      repeating-linear-gradient(78deg,transparent 0 18px,rgba(88,121,67,.10) 19px 20px,transparent 21px 42px),
	      radial-gradient(circle at 18% 65%,rgba(86,124,72,.18) 0 1.5px,transparent 2px),
	      radial-gradient(circle at 72% 78%,rgba(86,124,72,.14) 0 1.5px,transparent 2px);
	    opacity:.42;
	  }
	  .home-room .home-floor::after{
	    content:"";
	    position:absolute;
	    left:0;
	    right:0;
	    bottom:0;
	    height:34%;
	    pointer-events:none;
	    background:linear-gradient(180deg,transparent,rgba(54,87,44,.12));
	  }
	  .home-room .home-path{
	    background:
	      radial-gradient(circle at 42% 28%,rgba(255,255,255,.72) 0 2px,transparent 3px),
	      radial-gradient(circle at 56% 50%,rgba(160,112,36,.16) 0 2px,transparent 3px),
	      radial-gradient(circle at 38% 70%,rgba(255,255,255,.54) 0 2px,transparent 3px),
	      linear-gradient(180deg,rgba(255,255,255,.90),rgba(246,218,128,.86) 42%,rgba(205,158,55,.56) 100%)!important;
	    box-shadow:
	      inset 0 0 24px rgba(255,255,255,.62),
	      inset 12px 0 18px rgba(255,255,255,.18),
	      inset -12px 0 18px rgba(138,90,8,.08),
	      0 5px 16px rgba(82,55,15,.08)!important;
	  }
	  .home-room .home-path::before{
	    background:
	      radial-gradient(circle at 46% 22%,rgba(255,255,255,.78) 0 2px,transparent 3px),
	      radial-gradient(circle at 57% 35%,rgba(138,90,8,.20) 0 1.8px,transparent 2.8px),
	      radial-gradient(circle at 42% 52%,rgba(255,255,255,.56) 0 2px,transparent 3px),
	      radial-gradient(circle at 61% 76%,rgba(138,90,8,.15) 0 2px,transparent 3px)!important;
	    opacity:.46!important;
	  }
	  .home-room .home-path::after{
	    opacity:.22!important;
	    filter:blur(.1px);
	  }
	  .home-room .home-flowerbed{
	    background:
	      radial-gradient(circle at 18% 64%,#fff 0 3px,transparent 4px),
	      radial-gradient(circle at 26% 72%,#f9a8d4 0 3px,transparent 4px),
	      radial-gradient(circle at 46% 55%,#fff7d6 0 3px,transparent 4px),
	      radial-gradient(circle at 61% 75%,#fbcfe8 0 3px,transparent 4px),
	      radial-gradient(circle at 74% 62%,#e0f2fe 0 3px,transparent 4px),
	      linear-gradient(180deg,#bde49b,#6f9a48 72%,#557c39)!important;
	    box-shadow:inset 0 8px 12px rgba(255,255,255,.25),0 6px 12px rgba(82,55,15,.08)!important;
	    animation:homeFlowerSway 7.6s ease-in-out infinite;
	  }
	  .home-room .home-flowerbed::before,
	  .home-room .home-flowerbed::after{
	    content:"";
	    position:absolute;
	    width:38%;
	    height:32%;
	    border-radius:50%;
	    pointer-events:none;
	    background:
	      radial-gradient(circle at 30% 40%,#fff 0 3px,transparent 4px),
	      radial-gradient(circle at 55% 58%,#f9a8d4 0 3px,transparent 4px),
	      radial-gradient(circle at 72% 34%,#fff7d6 0 2.5px,transparent 3.5px);
	    opacity:.86;
	  }
	  .home-room .home-flowerbed::before{left:20%;bottom:28%;}
	  .home-room .home-flowerbed::after{right:13%;bottom:16%;transform:scale(.82);}
	  .home-room .home-cardboard,
	  .home-room .home-little-shelter,
	  .home-room .home-house,
	  .home-room .home-mansion,
	  .home-room .home-palace,
	  .home-room .home-chapel{
	    filter:drop-shadow(0 16px 22px rgba(82,55,15,.18)) drop-shadow(0 0 18px rgba(255,248,223,.18))!important;
	  }
	  .home-room .home-cardboard::before,
	  .home-room .home-little-shelter::before,
	  .home-room .home-house::before,
	  .home-room .home-mansion::before,
	  .home-room .home-palace::before{
	    box-shadow:0 0 34px rgba(255,248,223,.58),0 0 54px rgba(240,203,118,.20)!important;
	  }
	  .home-room .home-cardboard-body,
	  .home-room .home-little-shelter-body,
	  .home-room .home-house-body,
	  .home-room .home-mansion-body,
	  .home-room .home-palace-body{
	    box-shadow:
	      inset 0 10px 16px rgba(255,255,255,.50),
	      inset 0 -12px 18px rgba(138,90,8,.10),
	      0 8px 14px rgba(82,55,15,.10)!important;
	  }
	  .home-room .home-cardboard-body{
	    background:
	      repeating-linear-gradient(8deg,transparent 0 13px,rgba(138,90,8,.10) 14px 15px,transparent 16px 28px),
	      linear-gradient(180deg,#f4d28d,#c98d43)!important;
	  }
	  .home-room .home-little-shelter-roof,
	  .home-room .home-house-roof,
	  .home-room .home-mansion-roof,
	  .home-room .home-palace-roof{
	    filter:drop-shadow(0 0 16px rgba(240,203,118,.44)) drop-shadow(0 5px 8px rgba(82,55,15,.08))!important;
	  }
	  .home-room .home-door,
	  .home-room .home-house-door,
	  .home-room .home-mansion-door,
	  .home-room .home-little-shelter-door,
	  .home-room .home-cardboard-door{
	    box-shadow:0 0 18px rgba(255,248,223,.70),inset 0 4px 8px rgba(255,255,255,.38)!important;
	  }
	  .home-room .home-water-feature{
	    filter:drop-shadow(0 7px 12px rgba(40,112,145,.16)) saturate(1.06)!important;
	  }
	  .home-room .water-ripple{
	    animation-duration:3.2s!important;
	    opacity:.52!important;
	  }
	  .home-room .water-shimmer{
	    opacity:.58!important;
	    animation:waterSurfaceShimmer 3.6s ease-in-out infinite!important;
	  }
	  .home-room .home-pet-track,
	  .home-room .spirit-visitor{
	    filter:drop-shadow(0 5px 8px rgba(82,55,15,.10))!important;
	  }
	  .home-room .home-pet-track::after,
	  .home-room .home-slot::after,
	  .home-room .visitor-shadow{
	    background:radial-gradient(ellipse,rgba(82,55,15,.18),rgba(82,55,15,0) 72%)!important;
	    opacity:.22!important;
	  }
	  .home-room .home-spirit-slot{
	    bottom:1.1%!important;
	    transform:translateX(-50%) scale(1.25)!important;
	    pointer-events:auto!important;
	  }
	  .home-room .home-spirit-slot > div{
	    filter:
	      drop-shadow(0 18px 22px rgba(82,55,15,.18))
	      drop-shadow(0 0 24px rgba(255,248,223,.64))
	      drop-shadow(0 0 18px rgba(186,230,253,.28))!important;
	  }
	  .home-room .home-spirit-ground-shadow{
	    width:102px!important;
	    height:20px!important;
	    opacity:.28!important;
	    background:radial-gradient(ellipse,rgba(82,55,15,.22),rgba(82,55,15,0) 72%)!important;
	  }
	  .home-room .particle{
	    opacity:.30!important;
	    filter:drop-shadow(0 0 6px rgba(255,248,223,.56));
	  }
	  .home-room .home-gem,
	  .home-room .home-path-gem{
	    opacity:.18!important;
	    filter:saturate(.90) drop-shadow(0 0 4px rgba(255,255,255,.28))!important;
	  }
	  @keyframes homeFlowerSway{
	    0%,100%{transform:translateY(0) rotate(var(--flower-tilt,0deg))}
	    50%{transform:translateY(-2px) rotate(calc(var(--flower-tilt,0deg) + 1.5deg))}
	  }
	  .home-room .home-flowerbed.left{--flower-tilt:-7deg;}
	  .home-room .home-flowerbed.right{--flower-tilt:7deg;}
	  @keyframes waterSurfaceShimmer{
	    0%,100%{transform:translateX(-8%) scaleX(.96);opacity:.34}
	    50%{transform:translateX(8%) scaleX(1.05);opacity:.64}
	  }
	  @media (max-width:560px){
	    .home-room .home-spirit-slot{
	      transform:translateX(-50%) scale(1.14)!important;
	    }
	    .home-room .home-cloud{opacity:.62!important;}
	    .home-room .home-flowerbed{opacity:.76!important;}
	    .home-room .home-path{width:42%!important;}
	    .home-room .home-cardboard,
	    .home-room .home-little-shelter,
	    .home-room .home-house,
	    .home-room .home-mansion,
	    .home-room .home-palace{
	      filter:drop-shadow(0 12px 16px rgba(82,55,15,.15)) drop-shadow(0 0 12px rgba(255,248,223,.14))!important;
	    }
	  }
	  .spirit-avatar-svg{
	    overflow:visible;
	  }
	  .spirit-cute-layer{
	    transform-origin:60px 78px;
	    animation:spiritCuteIdle 5.8s ease-in-out infinite;
	    pointer-events:none;
	  }
	  .spirit-cute-layer.her .spirit-soft-halo{
	    fill:#fbcfe8;
	    filter:drop-shadow(0 0 20px rgba(249,168,212,.34));
	  }
	  .spirit-cute-layer.his .spirit-soft-halo{
	    fill:#bae6fd;
	    filter:drop-shadow(0 0 20px rgba(125,211,252,.32));
	  }
	  .spirit-hood-plush{
	    filter:drop-shadow(0 8px 12px rgba(82,55,15,.10)) drop-shadow(0 0 14px rgba(255,248,223,.30));
	  }
	  .spirit-hood-shadow{
	    filter:blur(.15px);
	  }
	  .spirit-face-plush{
	    filter:drop-shadow(0 3px 5px rgba(82,55,15,.06));
	  }
	  .spirit-hood-rim{
	    filter:drop-shadow(0 1px 3px rgba(255,255,255,.45));
	  }
	  .spirit-body-plush{
	    filter:drop-shadow(0 10px 14px rgba(82,55,15,.08)) drop-shadow(0 0 14px rgba(255,248,223,.18));
	  }
	  .spirit-stage-baby .spirit-cute-layer{
	    transform-origin:60px 106px;
	    transform:scale(.96) translateY(3px);
	  }
	  .spirit-stage-baby .spirit-hood-plush{
	    opacity:.90;
	  }
	  .spirit-stage-baby .spirit-body-plush{
	    opacity:.92;
	  }
	  .spirit-stage-baby .spirit-soft-halo{
	    opacity:.20;
	  }
	  .spirit-stage-baby .spirit-chest-symbol{
	    transform:scale(.86);
	    opacity:.78;
	  }
	  .spirit-stage-baby .spirit-arm{
	    opacity:.24!important;
	  }
	  .spirit-stage-baby .spirit-hand{
	    opacity:.54!important;
	  }
	  .spirit-stage-baby .spirit-cute-feet{
	    opacity:.45;
	  }
	  .spirit-cute-layer.her .spirit-hood-plush{
	    filter:drop-shadow(0 8px 10px rgba(120,45,115,.10)) drop-shadow(0 0 12px rgba(251,207,232,.25));
	  }
	  .spirit-cute-layer.his .spirit-hood-plush{
	    filter:drop-shadow(0 8px 10px rgba(30,80,95,.10)) drop-shadow(0 0 12px rgba(186,230,253,.25));
	  }
	  .spirit-cute-eyes{
	    transform-origin:60px 52px;
	    animation:spiritCuteBlink 7.2s ease-in-out infinite;
	    filter:none;
	  }
	  .spirit-closed-eyes{
	    opacity:0;
	    transform-origin:60px 52px;
	  }
	  .spirit-blush{
	    animation:spiritBlushPulse 6.4s ease-in-out infinite;
	    filter:blur(.15px);
	  }
	  .spirit-cute-smile{
	    transform-origin:60px 61px;
	    animation:spiritSmileBreathe 6.8s ease-in-out infinite;
	  }
	  .spirit-arm,
	  .spirit-hand{
	    transform-box:fill-box;
	    transition:opacity .2s ease,transform .2s ease;
	  }
	  .spirit-arm.right,
	  .spirit-hand.right{
	    transform-origin:75px 83px;
	  }
	  .spirit-arm.left,
	  .spirit-hand.left{
	    transform-origin:45px 83px;
	  }
	  .spirit-chest-symbol{
	    animation:spiritChestGlow 4.2s ease-in-out infinite;
	  }
	  .spirit-robe-highlight{
	    animation:spiritRobeShimmer 7.5s ease-in-out infinite;
	  }
	  .spirit-bottom-shadow{
	    animation:spiritRobeShimmer 7.5s ease-in-out infinite reverse;
	  }
	  .home-room.home-spirit-action-wave .spirit-cute-layer{
	    animation:spiritExpressiveTurn 1.65s cubic-bezier(.22,.75,.24,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-arm.right{
	    animation:spiritRightArmWavePath 1.65s cubic-bezier(.2,.78,.24,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-hand.right{
	    animation:spiritRightHandWave 1.65s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-cute-smile{
	    animation:spiritBigSmile 1.65s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-cute-layer{
	    animation:spiritPrayerBowLayer 2.7s cubic-bezier(.22,.72,.24,1) both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-cute-eyes{
	    opacity:0!important;
	    animation:none!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-closed-eyes{
	    animation:spiritClosedEyesHold 2.7s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-arm.left{
	    animation:spiritLeftArmPrayer 2.7s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-arm.right{
	    animation:spiritRightArmPrayer 2.7s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-hand.left{
	    animation:spiritLeftHandPrayer 2.7s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-hand.right{
	    animation:spiritRightHandPrayer 2.7s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-chest-symbol{
	    animation:spiritPrayerHeartGlow 2.7s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-cute-layer{
	    animation:spiritCuteTwirlLayer 1.65s cubic-bezier(.18,.78,.25,1) both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-arm.left{
	    animation:spiritLeftArmOpen 1.65s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-arm.right{
	    animation:spiritRightArmOpen 1.65s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-cute-layer{
	    animation:spiritCuteDanceLayer 1.95s cubic-bezier(.18,.78,.25,1) both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-arm.left{
	    animation:spiritLeftArmDance 1.95s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-arm.right{
	    animation:spiritRightArmDance 1.95s ease-in-out both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-cute-layer{
	    animation:spiritFlightOpenArms 4.5s cubic-bezier(.2,.75,.2,1) both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-arm.left{
	    animation:spiritLeftArmFlight 4.5s ease-in-out both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-arm.right{
	    animation:spiritRightArmFlight 4.5s ease-in-out both!important;
	  }
	  .gender-choice-card{
	    position:relative;
	    overflow:hidden;
	    isolation:isolate;
	  }
	  .gender-choice-card::before{
	    content:"";
	    position:absolute;
	    left:19%;
	    right:19%;
	    bottom:104px;
	    height:30px;
	    border-radius:50%;
	    background:radial-gradient(ellipse,rgba(82,55,15,.15),rgba(82,55,15,.04) 42%,transparent 72%);
	    pointer-events:none;
	    z-index:0;
	  }
	  .gender-choice-card::after{
	    content:"✦    ✧    ✦";
	    position:absolute;
	    left:16px;
	    right:16px;
	    top:28px;
	    color:rgba(201,144,18,.42);
	    font-size:17px;
	    letter-spacing:12px;
	    pointer-events:none;
	    opacity:.58;
	    z-index:0;
	  }
	  .gender-choice-card.gender-choice-female{
	    background:
	      radial-gradient(circle at 50% 32%,rgba(251,207,232,.52),transparent 44%),
	      radial-gradient(circle at 20% 78%,rgba(249,168,212,.22),transparent 32%),
	      rgba(255,253,240,.88)!important;
	  }
	  .gender-choice-card.gender-choice-male{
	    background:
	      radial-gradient(circle at 50% 32%,rgba(186,230,253,.50),transparent 44%),
	      radial-gradient(circle at 82% 78%,rgba(187,247,208,.24),transparent 32%),
	      rgba(255,253,240,.88)!important;
	  }
	  .gender-choice-card.gender-choice-female .spirit-avatar-svg{
	    filter:drop-shadow(0 14px 14px rgba(120,45,115,.13)) drop-shadow(0 0 22px rgba(251,207,232,.34));
	  }
	  .gender-choice-card.gender-choice-male .spirit-avatar-svg{
	    filter:drop-shadow(0 14px 14px rgba(30,80,95,.13)) drop-shadow(0 0 22px rgba(186,230,253,.34));
	  }
	  .gender-choice-card.gender-choice-female .spirit-cute-layer.her .spirit-hood-plush{
	    fill:#f4cdfa;
	    opacity:.76;
	  }
	  .gender-choice-card.gender-choice-male .spirit-cute-layer.his .spirit-hood-plush{
	    fill:#bff2d5;
	    opacity:.76;
	  }
	  .gender-choice-card.gender-choice-female .spirit-body-plush{
	    opacity:.76;
	  }
	  .gender-choice-card.gender-choice-male .spirit-body-plush{
	    opacity:.76;
	  }
	  .gender-choice-card.gender-choice-female .spirit-arm,
	  .gender-choice-card.gender-choice-male .spirit-arm{
	    opacity:.26!important;
	  }
	  .gender-choice-card.gender-choice-female .spirit-hand,
	  .gender-choice-card.gender-choice-male .spirit-hand{
	    opacity:.56!important;
	  }
	  .gender-choice-card.gender-choice-female > div:last-child::before,
	  .gender-choice-card.gender-choice-male > div:last-child::before{
	    content:"";
	    display:block;
	    width:96px;
	    height:30px;
	    margin:-10px auto 6px;
	    pointer-events:none;
	  }
	  .gender-choice-card.gender-choice-female > div:last-child::before{
	    background:
	      radial-gradient(circle at 18% 55%,#f9a8d4 0 4px,transparent 5px),
	      radial-gradient(circle at 34% 42%,#fff 0 3px,transparent 4px),
	      radial-gradient(circle at 67% 52%,#f9a8d4 0 4px,transparent 5px),
	      linear-gradient(90deg,transparent 0 18%,#86b86a 18% 20%,transparent 20% 100%);
	    opacity:.72;
	  }
	  .gender-choice-card.gender-choice-male > div:last-child::before{
	    background:
	      radial-gradient(ellipse at 20% 62%,#86efac 0 7px,transparent 8px),
	      radial-gradient(ellipse at 62% 48%,#bbf7d0 0 7px,transparent 8px),
	      radial-gradient(circle at 78% 67%,#8a8f5a 0 3px,transparent 4px);
	    opacity:.72;
	  }
	  .gender-choice-card:hover .spirit-avatar-svg,
	  .gender-choice-card:focus-visible .spirit-avatar-svg{
	    animation:onboardingSpiritMascotHover 1.4s ease-in-out both!important;
	  }
	  @keyframes onboardingSpiritMascotHover{
	    0%,100%{transform:translateY(0) scale(1)}
	    36%{transform:translateY(-5px) scale(1.025,.985)}
	    70%{transform:translateY(-2px) scale(.995,1.012)}
	  }
	  .gender-choice-card .spirit-cute-layer,
	  .gender-choice-card .spirit-cute-eyes,
	  .gender-choice-card .spirit-blush,
	  .gender-choice-card .spirit-cute-smile,
	  .gender-choice-card .spirit-chest-symbol,
	  .gender-choice-card .spirit-robe-highlight,
	  .gender-choice-card .spirit-arm,
	  .gender-choice-card .spirit-hand{
	    animation:none!important;
	  }
	  @keyframes spiritCuteIdle{
	    0%,100%{transform:translateY(0) rotate(0deg)}
	    40%{transform:translateY(-1.8px) rotate(.6deg)}
	    72%{transform:translateY(.8px) rotate(-.35deg)}
	  }
	  @keyframes spiritCuteBlink{
	    0%,88%,100%{transform:scaleY(1)}
	    91%,93%{transform:scaleY(.12)}
	  }
	  @keyframes spiritBlushPulse{0%,100%{opacity:.10}50%{opacity:.18}}
	  @keyframes spiritSmileBreathe{0%,100%{transform:scaleX(1)}55%{transform:scaleX(1.04)}}
	  @keyframes spiritChestGlow{0%,100%{opacity:.72;transform:scale(1);filter:drop-shadow(0 0 4px rgba(255,248,223,.42))}50%{opacity:1;transform:scale(1.08);filter:drop-shadow(0 0 11px rgba(255,248,223,.72))}}
	  @keyframes spiritRobeShimmer{0%,100%{opacity:.22}50%{opacity:.42}}
	  @keyframes spiritExpressiveTurn{
	    0%,100%{transform:translateY(0) rotate(0)}
	    22%{transform:translateY(-2px) rotate(-3deg)}
	    60%{transform:translateY(-4px) rotate(-5deg)}
	    82%{transform:translateY(-1px) rotate(2deg)}
	  }
	  @keyframes spiritRightArmWavePath{
	    0%,100%{transform:translate(0,0) rotate(0)}
	    18%{transform:translate(4px,-14px) rotate(-48deg)}
	    36%{transform:translate(5px,-17px) rotate(-68deg)}
	    56%{transform:translate(4px,-15px) rotate(-38deg)}
	    74%{transform:translate(5px,-18px) rotate(-66deg)}
	  }
	  @keyframes spiritRightHandWave{
	    0%,100%{transform:translate(0,0) rotate(0)}
	    18%{transform:translate(10px,-23px) rotate(-28deg)}
	    36%{transform:translate(12px,-27px) rotate(24deg)}
	    56%{transform:translate(10px,-24px) rotate(-24deg)}
	    74%{transform:translate(12px,-27px) rotate(22deg)}
	  }
	  @keyframes spiritBigSmile{0%,100%{transform:scaleX(1);opacity:.68}35%,72%{transform:scaleX(1.25);opacity:.9}}
	  @keyframes spiritPrayerBowLayer{
	    0%,100%{transform:translateY(0) rotate(0)}
	    24%{transform:translateY(5px) rotate(.8deg)}
	    42%,78%{transform:translateY(8px) rotate(0) scale(.985,.96)}
	  }
	  @keyframes spiritClosedEyesHold{0%,16%,100%{opacity:0}28%,82%{opacity:1}}
	  @keyframes spiritLeftArmPrayer{
	    0%,100%{transform:translate(0,0) rotate(0)}
	    32%,78%{transform:translate(13px,-8px) rotate(44deg)}
	  }
	  @keyframes spiritRightArmPrayer{
	    0%,100%{transform:translate(0,0) rotate(0)}
	    32%,78%{transform:translate(-13px,-8px) rotate(-44deg)}
	  }
	  @keyframes spiritLeftHandPrayer{
	    0%,100%{transform:translate(0,0) scale(1)}
	    32%,78%{transform:translate(22px,-10px) scale(1.05)}
	  }
	  @keyframes spiritRightHandPrayer{
	    0%,100%{transform:translate(0,0) scale(1)}
	    32%,78%{transform:translate(-22px,-10px) scale(1.05)}
	  }
	  @keyframes spiritPrayerHeartGlow{0%,100%{opacity:.62;transform:scale(1)}34%,78%{opacity:1;transform:scale(1.22)}}
	  @keyframes spiritCuteTwirlLayer{
	    0%{transform:perspective(520px) translateY(0) rotateX(0deg)}
	    18%{transform:perspective(520px) translateY(-4px) rotateX(-18deg)}
	    58%{transform:perspective(520px) translateY(-8px) rotateX(360deg)}
	    100%{transform:perspective(520px) translateY(0) rotateX(360deg)}
	  }
	  @keyframes spiritLeftArmOpen{0%,100%{transform:rotate(0)}35%,72%{transform:translate(-6px,-4px) rotate(-22deg)}}
	  @keyframes spiritRightArmOpen{0%,100%{transform:rotate(0)}35%,72%{transform:translate(6px,-4px) rotate(22deg)}}
	  @keyframes spiritCuteDanceLayer{
	    0%,100%{transform:translate(0,0) rotate(0)}
	    18%{transform:translate(-4px,-3px) rotate(-4deg)}
	    38%{transform:translate(5px,-1px) rotate(5deg)}
	    60%{transform:translate(-4px,-4px) rotate(3deg)}
	    80%{transform:translate(4px,-1px) rotate(-3deg)}
	  }
	  @keyframes spiritLeftArmDance{0%,100%{transform:rotate(0)}25%{transform:translate(-4px,-5px) rotate(-18deg)}55%{transform:translate(3px,0) rotate(13deg)}}
	  @keyframes spiritRightArmDance{0%,100%{transform:rotate(0)}25%{transform:translate(4px,0) rotate(15deg)}55%{transform:translate(-3px,-5px) rotate(-18deg)}}
	  @keyframes spiritFlightOpenArms{
	    0%,100%{transform:translateY(0) scale(1)}
	    24%{transform:translateY(-2px) scale(1.04)}
	    48%,68%{transform:translateY(-4px) scale(1.08)}
	  }
	  @keyframes spiritLeftArmFlight{0%,100%{transform:rotate(0)}24%,76%{transform:translate(-8px,-10px) rotate(-34deg)}}
	  @keyframes spiritRightArmFlight{0%,100%{transform:rotate(0)}24%,76%{transform:translate(8px,-10px) rotate(34deg)}}
	  @keyframes spiritOnboardingWave{
	    0%,100%{transform:translateY(0) rotate(0)}
	    38%{transform:translateY(-5px) rotate(-4deg)}
	    70%{transform:translateY(-3px) rotate(3deg)}
	  }
	  /* Coordinated pose animation pass: the whole spirit moves first, then the parts follow. */
	  .home-room .spirit-avatar-svg{
	    transform-box:fill-box;
	    transform-origin:50% 88%;
	  }
	  .home-room .spirit-face-plush,
	  .home-room .spirit-hood-plush,
	  .home-room .spirit-hood-shadow,
	  .home-room .spirit-hood-rim,
	  .home-room .spirit-face-highlight,
	  .home-room .spirit-cute-eyes,
	  .home-room .spirit-closed-eyes,
	  .home-room .spirit-blush,
	  .home-room .spirit-cute-smile{
	    transform-box:fill-box;
	    transform-origin:50% 78%;
	  }
	  .home-room .spirit-body-plush,
	  .home-room .spirit-robe-highlight,
	  .home-room .spirit-bottom-shadow,
	  .home-room .spirit-cute-feet,
	  .home-room .spirit-chest-symbol{
	    transform-box:fill-box;
	    transform-origin:50% 92%;
	  }
	  .home-room .spirit-arm{
	    stroke-linecap:round;
	    filter:none;
	    opacity:.38!important;
	    stroke-width:8.5px;
	  }
	  .home-room .spirit-hand{
	    opacity:.68!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-avatar-svg{
	    animation:spiritPoseWaveWhole 1.9s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-cute-layer{
	    animation:spiritPoseWaveBody 1.9s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-face-plush,
	  .home-room.home-spirit-action-wave .spirit-hood-plush,
	  .home-room.home-spirit-action-wave .spirit-hood-shadow,
	  .home-room.home-spirit-action-wave .spirit-hood-rim,
	  .home-room.home-spirit-action-wave .spirit-face-highlight,
	  .home-room.home-spirit-action-wave .spirit-cute-eyes,
	  .home-room.home-spirit-action-wave .spirit-blush,
	  .home-room.home-spirit-action-wave .spirit-cute-smile{
	    animation:spiritPoseWaveHead 1.9s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-arm.right{
	    animation:spiritPoseWaveArm 1.9s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-hand.right{
	    animation:spiritPoseWaveHand 1.9s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-arm.left,
	  .home-room.home-spirit-action-wave .spirit-hand.left{
	    animation:spiritPoseWaveCounterArm 1.9s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-cute-feet{
	    animation:spiritPoseFeetBounce 1.9s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-avatar-svg{
	    animation:spiritPosePrayWhole 3s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-cute-layer{
	    animation:spiritPosePrayBody 3s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-face-plush,
	  .home-room.home-spirit-action-pray .spirit-hood-plush,
	  .home-room.home-spirit-action-pray .spirit-hood-shadow,
	  .home-room.home-spirit-action-pray .spirit-hood-rim,
	  .home-room.home-spirit-action-pray .spirit-face-highlight,
	  .home-room.home-spirit-action-pray .spirit-blush,
	  .home-room.home-spirit-action-pray .spirit-cute-smile{
	    animation:spiritPosePrayHead 3s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-cute-eyes{
	    opacity:0!important;
	    animation:none!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-closed-eyes{
	    animation:spiritPosePrayClosedEyes 3s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-arm.left{
	    animation:spiritPosePrayLeftArm 3s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-arm.right{
	    animation:spiritPosePrayRightArm 3s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-hand.left{
	    animation:spiritPosePrayLeftHand 3s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-hand.right{
	    animation:spiritPosePrayRightHand 3s cubic-bezier(.22,.75,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-cute-feet{
	    animation:spiritPosePrayFeet 3s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-chest-symbol,
	  .home-room.home-spirit-action-pray .spirit-soft-halo{
	    animation:spiritPosePrayGlow 3s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-avatar-svg{
	    animation:spiritPoseTwirlWhole 1.85s cubic-bezier(.2,.78,.22,1) both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-cute-layer{
	    animation:spiritPoseTwirlBody 1.85s cubic-bezier(.2,.78,.22,1) both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-arm.left{
	    animation:spiritPoseTwirlLeftArm 1.85s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-arm.right{
	    animation:spiritPoseTwirlRightArm 1.85s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-hand.left{
	    animation:spiritPoseTwirlLeftHand 1.85s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-hand.right{
	    animation:spiritPoseTwirlRightHand 1.85s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-body-plush,
	  .home-room.home-spirit-action-twirl .spirit-robe-highlight{
	    animation:spiritPoseRobeTrail 1.85s cubic-bezier(.2,.78,.22,1) both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-avatar-svg{
	    animation:spiritPoseDanceWhole 2.2s cubic-bezier(.2,.72,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-cute-layer{
	    animation:spiritPoseDanceBody 2.2s cubic-bezier(.2,.72,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-face-plush,
	  .home-room.home-spirit-action-dance .spirit-hood-plush,
	  .home-room.home-spirit-action-dance .spirit-hood-shadow,
	  .home-room.home-spirit-action-dance .spirit-hood-rim,
	  .home-room.home-spirit-action-dance .spirit-face-highlight,
	  .home-room.home-spirit-action-dance .spirit-cute-eyes,
	  .home-room.home-spirit-action-dance .spirit-blush,
	  .home-room.home-spirit-action-dance .spirit-cute-smile{
	    animation:spiritPoseDanceHead 2.2s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-arm.left{
	    animation:spiritPoseDanceLeftArm 2.2s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-arm.right{
	    animation:spiritPoseDanceRightArm 2.2s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-cute-feet{
	    animation:spiritPoseDanceFeet 2.2s ease-in-out both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-avatar-svg{
	    animation:spiritPoseFaithFlightWhole 4.5s cubic-bezier(.2,.75,.2,1) both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-cute-layer{
	    animation:spiritPoseFaithFlightBody 4.5s cubic-bezier(.2,.75,.2,1) both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-arm.left{
	    animation:spiritPoseFlightLeftArm 4.5s ease-in-out both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-arm.right{
	    animation:spiritPoseFlightRightArm 4.5s ease-in-out both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-cute-feet{
	    animation:spiritPoseFlightFeet 4.5s ease-in-out both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-body-plush,
	  .home-room.home-faith-flight-active .spirit-robe-highlight{
	    animation:spiritPoseFlightRobe 4.5s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-sit .spirit-avatar-svg,
	  .home-room.home-spirit-action-kneelPray .spirit-avatar-svg{
	    animation:spiritPoseSitWhole 2.4s ease-in-out both!important;
	  }
	  @keyframes spiritPoseWaveWhole{
	    0%,100%{transform:translateY(0) scale(1)}
	    14%{transform:translateY(1px) scale(1.015,.985)}
	    30%{transform:translateY(-4px) scale(1.018) rotate(-1deg)}
	    50%{transform:translateY(-2px) scale(1.012) rotate(-2deg)}
	    72%{transform:translateY(-3px) scale(1.018) rotate(-1deg)}
	    88%{transform:translateY(1px) scale(.995,1.01)}
	  }
	  @keyframes spiritPoseWaveBody{
	    0%,100%{transform:translateY(0) rotate(0) scale(1)}
	    18%{transform:translateY(-1px) rotate(-1.5deg) scale(1.01)}
	    38%{transform:translateY(-3px) rotate(-4deg) scale(1.018,.99)}
	    58%{transform:translateY(-1px) rotate(-2deg) scale(1.012)}
	    78%{transform:translateY(-2px) rotate(-3deg) scale(1.016)}
	  }
	  @keyframes spiritPoseWaveHead{
	    0%,100%{transform:rotate(0) translateY(0)}
	    24%{transform:rotate(2deg) translateY(-.5px)}
	    42%,76%{transform:rotate(5deg) translateY(-1px)}
	  }
	  @keyframes spiritPoseWaveArm{
	    0%,100%{transform:translate(0,0) rotate(0)}
	    18%{transform:translate(3px,-8px) rotate(-30deg)}
	    32%{transform:translate(7px,-19px) rotate(-62deg)}
	    48%{transform:translate(8px,-21px) rotate(-82deg)}
	    62%{transform:translate(7px,-18px) rotate(-52deg)}
	    76%{transform:translate(8px,-21px) rotate(-78deg)}
	    90%{transform:translate(3px,-7px) rotate(-24deg)}
	  }
	  @keyframes spiritPoseWaveHand{
	    0%,100%{transform:translate(0,0) rotate(0)}
	    18%{transform:translate(7px,-13px) rotate(-18deg)}
	    32%{transform:translate(15px,-27px) rotate(-25deg)}
	    46%{transform:translate(17px,-31px) rotate(26deg)}
	    60%{transform:translate(15px,-27px) rotate(-24deg)}
	    76%{transform:translate(17px,-31px) rotate(22deg)}
	    90%{transform:translate(6px,-11px) rotate(-10deg)}
	  }
	  @keyframes spiritPoseWaveCounterArm{
	    0%,100%{transform:translate(0,0) rotate(0)}
	    32%,76%{transform:translate(1px,-2px) rotate(8deg)}
	  }
	  @keyframes spiritPoseFeetBounce{
	    0%,100%{transform:translateY(0) scaleX(1)}
	    32%,72%{transform:translateY(1px) scaleX(1.08)}
	  }
	  @keyframes spiritPosePrayWhole{
	    0%,100%{transform:translateY(0) scale(1)}
	    16%{transform:translateY(1px) scale(1.012,.99)}
	    32%{transform:translateY(5px) scale(.992,.968)}
	    42%,78%{transform:translateY(7px) scale(.986,.958)}
	    90%{transform:translateY(2px) scale(1.006,.992)}
	  }
	  @keyframes spiritPosePrayBody{
	    0%,100%{transform:translateY(0) rotate(0) scale(1)}
	    22%{transform:translateY(3px) rotate(.4deg) scale(.998,.975)}
	    38%,80%{transform:translateY(7px) rotate(0) scale(.985,.945)}
	  }
	  @keyframes spiritPosePrayHead{
	    0%,100%{transform:translateY(0) rotate(0)}
	    28%{transform:translateY(2px) rotate(3deg)}
	    42%,80%{transform:translateY(4px) rotate(5deg)}
	  }
	  @keyframes spiritPosePrayClosedEyes{0%,18%,100%{opacity:0}32%,84%{opacity:1}}
	  @keyframes spiritPosePrayLeftArm{
	    0%,100%{transform:translate(0,0) rotate(0)}
	    24%{transform:translate(7px,-4px) rotate(24deg)}
	    38%,80%{transform:translate(17px,-8px) rotate(48deg)}
	  }
	  @keyframes spiritPosePrayRightArm{
	    0%,100%{transform:translate(0,0) rotate(0)}
	    24%{transform:translate(-7px,-4px) rotate(-24deg)}
	    38%,80%{transform:translate(-17px,-8px) rotate(-48deg)}
	  }
	  @keyframes spiritPosePrayLeftHand{
	    0%,100%{transform:translate(0,0) scale(1)}
	    24%{transform:translate(13px,-5px) scale(1.02)}
	    38%,80%{transform:translate(22px,-12px) scale(1.08)}
	  }
	  @keyframes spiritPosePrayRightHand{
	    0%,100%{transform:translate(0,0) scale(1)}
	    24%{transform:translate(-13px,-5px) scale(1.02)}
	    38%,80%{transform:translate(-22px,-12px) scale(1.08)}
	  }
	  @keyframes spiritPosePrayFeet{
	    0%,100%{transform:translateY(0) scaleX(1)}
	    38%,80%{transform:translateY(-1px) scaleX(.75)}
	  }
	  @keyframes spiritPosePrayGlow{0%,100%{opacity:.58;transform:scale(1)}42%,78%{opacity:1;transform:scale(1.18)}}
	  @keyframes spiritPoseTwirlWhole{
	    0%{transform:perspective(520px) translateY(0) rotateX(0deg) scale(1)}
	    14%{transform:perspective(520px) translateY(1px) rotateX(-18deg) scale(1.02,.99)}
	    42%{transform:perspective(520px) translateY(-7px) rotateX(185deg) scale(1.055)}
	    68%{transform:perspective(520px) translateY(-5px) rotateX(360deg) scale(1.045)}
	    86%{transform:perspective(520px) translateY(1px) rotateX(368deg) scale(.995,1.015)}
	    100%{transform:perspective(520px) translateY(0) rotateX(360deg) scale(1)}
	  }
	  @keyframes spiritPoseTwirlBody{
	    0%,100%{transform:perspective(520px) rotateX(0deg) scale(1)}
	    16%{transform:perspective(520px) rotateX(-18deg) scale(1.02)}
	    68%{transform:perspective(520px) rotateX(360deg) scale(1.035)}
	  }
	  @keyframes spiritPoseTwirlLeftArm{0%,100%{transform:rotate(0)}20%,72%{transform:translate(-8px,-5px) rotate(-30deg)}}
	  @keyframes spiritPoseTwirlRightArm{0%,100%{transform:rotate(0)}20%,72%{transform:translate(8px,-5px) rotate(30deg)}}
	  @keyframes spiritPoseTwirlLeftHand{0%,100%{transform:rotate(0)}20%,72%{transform:translate(-10px,-8px) rotate(-18deg)}}
	  @keyframes spiritPoseTwirlRightHand{0%,100%{transform:rotate(0)}20%,72%{transform:translate(10px,-8px) rotate(18deg)}}
	  @keyframes spiritPoseRobeTrail{
	    0%,100%{transform:rotate(0) scale(1)}
	    24%{transform:rotate(5deg) scale(1.02,.98)}
	    52%{transform:rotate(-6deg) scale(1.05,.96)}
	    76%{transform:rotate(3deg) scale(1.02)}
	  }
	  @keyframes spiritPoseDanceWhole{
	    0%,100%{transform:translate(0,0) scale(1)}
	    12%{transform:translateY(1px) scale(1.02,.985)}
	    24%{transform:translate(-4px,-4px) rotate(-3deg) scale(1.02)}
	    42%{transform:translate(5px,-1px) rotate(3deg) scale(1.01)}
	    62%{transform:translate(-4px,-5px) rotate(3deg) scale(1.025)}
	    80%{transform:translate(4px,-1px) rotate(-2deg) scale(1.01)}
	  }
	  @keyframes spiritPoseDanceBody{
	    0%,100%{transform:rotate(0)}
	    24%{transform:rotate(-4deg)}
	    42%{transform:rotate(4deg)}
	    62%{transform:rotate(3deg)}
	    80%{transform:rotate(-3deg)}
	  }
	  @keyframes spiritPoseDanceHead{
	    0%,100%{transform:translateY(0) rotate(0)}
	    24%{transform:translateY(-1px) rotate(4deg)}
	    42%{transform:translateY(1px) rotate(-4deg)}
	    62%{transform:translateY(-1px) rotate(-3deg)}
	    80%{transform:translateY(1px) rotate(3deg)}
	  }
	  @keyframes spiritPoseDanceLeftArm{0%,100%{transform:rotate(0)}24%{transform:translate(-5px,-5px) rotate(-20deg)}48%{transform:translate(3px,0) rotate(12deg)}70%{transform:translate(-4px,-4px) rotate(-16deg)}}
	  @keyframes spiritPoseDanceRightArm{0%,100%{transform:rotate(0)}24%{transform:translate(4px,0) rotate(14deg)}48%{transform:translate(-4px,-5px) rotate(-18deg)}70%{transform:translate(5px,-3px) rotate(18deg)}}
	  @keyframes spiritPoseDanceFeet{0%,100%{transform:translateY(0)}24%,62%{transform:translateY(2px) scaleX(1.08)}42%,80%{transform:translateY(-1px) scaleX(.96)}}
	  @keyframes spiritPoseFaithFlightWhole{
	    0%{transform:translateY(0) rotate(0) scale(1);filter:drop-shadow(0 12px 16px rgba(82,55,15,.12))}
	    12%{transform:translateY(1px) rotate(0) scale(1.03,.98);filter:drop-shadow(0 0 16px rgba(255,248,223,.55))}
	    28%{transform:translateY(-32px) rotate(-5deg) scale(1.055)}
	    50%{transform:translateY(-88px) rotate(178deg) scale(1.1);filter:drop-shadow(0 0 26px rgba(255,248,223,.92)) drop-shadow(0 0 18px rgba(186,230,253,.42))}
	    68%{transform:translateY(-78px) rotate(360deg) scale(1.08)}
	    86%{transform:translateY(-8px) rotate(365deg) scale(.99,1.04)}
	    100%{transform:translateY(0) rotate(360deg) scale(1);filter:drop-shadow(0 12px 16px rgba(82,55,15,.12))}
	  }
	  @keyframes spiritPoseFaithFlightBody{0%,100%{transform:scale(1)}28%,70%{transform:scale(1.04)}}
	  @keyframes spiritPoseFlightLeftArm{0%,100%{transform:rotate(0)}18%,78%{transform:translate(-9px,-11px) rotate(-38deg)}}
	  @keyframes spiritPoseFlightRightArm{0%,100%{transform:rotate(0)}18%,78%{transform:translate(9px,-11px) rotate(38deg)}}
	  @keyframes spiritPoseFlightFeet{0%,100%{transform:translateY(0) scaleX(1)}28%,76%{transform:translateY(-5px) scaleX(.74)}88%{transform:translateY(2px) scaleX(1.12)}}
	  @keyframes spiritPoseFlightRobe{0%,100%{transform:scale(1)}28%,78%{transform:translateY(2px) scale(1.05,1.08)}88%{transform:scale(1.04,.96)}}
	  @keyframes spiritPoseSitWhole{
	    0%,100%{transform:translateY(0) scale(1)}
	    28%{transform:translateY(7px) scale(1.02,.94)}
	    50%,78%{transform:translateY(12px) scale(1.06,.86)}
	  }
	  /* One-piece mascot action system: actions read through the whole spirit silhouette. */
	  .home-room.home-spirit-action-wave .spirit-cute-layer,
	  .home-room.home-spirit-action-pray .spirit-cute-layer,
	  .home-room.home-spirit-action-twirl .spirit-cute-layer,
	  .home-room.home-spirit-action-dance .spirit-cute-layer,
	  .home-room.home-spirit-action-sit .spirit-cute-layer,
	  .home-room.home-spirit-action-kneelPray .spirit-cute-layer,
	  .home-room.home-faith-flight-active .spirit-cute-layer{
	    transform-origin:60px 105px!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-arm,
	  .home-room.home-spirit-action-wave .spirit-hand,
	  .home-room.home-spirit-action-pray .spirit-arm,
	  .home-room.home-spirit-action-pray .spirit-hand,
	  .home-room.home-spirit-action-twirl .spirit-arm,
	  .home-room.home-spirit-action-twirl .spirit-hand,
	  .home-room.home-spirit-action-dance .spirit-arm,
	  .home-room.home-spirit-action-dance .spirit-hand,
	  .home-room.home-faith-flight-active .spirit-arm,
	  .home-room.home-faith-flight-active .spirit-hand{
	    animation:none!important;
	    transform:none!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-avatar-svg{
	    animation:spiritBlobWave 1.9s cubic-bezier(.22,.76,.22,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-cute-layer{
	    animation:spiritBlobWavePose 1.9s cubic-bezier(.22,.76,.22,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-arm.right,
	  .home-room.home-spirit-action-wave .spirit-hand.right{
	    transform:translate(2px,-2px) rotate(-5deg)!important;
	  }
	  .home-room.home-spirit-action-wave .spirit-cute-smile{
	    animation:spiritBlobSmile 1.9s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-avatar-svg{
	    animation:spiritBlobPray 3s cubic-bezier(.22,.76,.22,1) both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-cute-layer{
	    animation:spiritBlobPrayPose 3s cubic-bezier(.22,.76,.22,1) both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-arm.left,
	  .home-room.home-spirit-action-pray .spirit-hand.left{
	    transform:translate(5px,-3px) scale(.92)!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-arm.right,
	  .home-room.home-spirit-action-pray .spirit-hand.right{
	    transform:translate(-5px,-3px) scale(.92)!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-cute-eyes{
	    opacity:0!important;
	    animation:none!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-closed-eyes{
	    animation:spiritBlobClosedEyes 3s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .spirit-chest-symbol,
	  .home-room.home-spirit-action-pray .spirit-soft-halo{
	    animation:spiritBlobPrayerGlow 3s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-avatar-svg{
	    animation:spiritBlobTwirl 1.85s cubic-bezier(.2,.78,.22,1) both!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-cute-layer{
	    animation:spiritBlobTwirlPose 1.85s cubic-bezier(.2,.78,.22,1) both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-avatar-svg{
	    animation:spiritBlobDance 2.2s cubic-bezier(.2,.72,.2,1) both!important;
	  }
	  .home-room.home-spirit-action-dance .spirit-cute-layer{
	    animation:spiritBlobDancePose 2.2s cubic-bezier(.2,.72,.2,1) both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-avatar-svg{
	    animation:spiritBlobFlight 4.5s cubic-bezier(.2,.75,.2,1) both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-cute-layer{
	    animation:spiritBlobFlightPose 4.5s cubic-bezier(.2,.75,.2,1) both!important;
	  }
	  .home-room.home-faith-flight-active .spirit-arm.left,
	  .home-room.home-faith-flight-active .spirit-hand.left{
	    transform:translate(-2px,-3px) rotate(-5deg)!important;
	  }
	  .home-room.home-faith-flight-active .spirit-arm.right,
	  .home-room.home-faith-flight-active .spirit-hand.right{
	    transform:translate(2px,-3px) rotate(5deg)!important;
	  }
	  @keyframes spiritBlobWave{
	    0%,100%{transform:translateY(0) rotate(0) scale(1)}
	    12%{transform:translateY(1px) scale(1.025,.985)}
	    30%{transform:translateY(-5px) rotate(-3deg) scale(1.018)}
	    48%{transform:translateY(-2px) rotate(3deg) scale(1.012)}
	    66%{transform:translateY(-4px) rotate(-2deg) scale(1.018)}
	    84%{transform:translateY(1px) rotate(1deg) scale(.998,1.012)}
	  }
	  @keyframes spiritBlobWavePose{
	    0%,100%{transform:translateY(0) scale(1)}
	    30%,66%{transform:translateY(-2px) scale(1.025,.985)}
	    48%{transform:translateY(-1px) scale(.995,1.012)}
	  }
	  @keyframes spiritBlobSmile{0%,100%{transform:scaleX(1);opacity:.46}35%,70%{transform:scaleX(1.12);opacity:.58}}
	  @keyframes spiritBlobPray{
	    0%,100%{transform:translateY(0) scale(1)}
	    18%{transform:translateY(1px) scale(1.018,.985)}
	    36%{transform:translateY(7px) scale(.98,.955)}
	    44%,78%{transform:translateY(9px) scale(.965,.94)}
	    90%{transform:translateY(2px) scale(1.006,.99)}
	  }
	  @keyframes spiritBlobPrayPose{
	    0%,100%{transform:translateY(0) scale(1)}
	    34%,80%{transform:translateY(4px) scale(.965,.935)}
	  }
	  @keyframes spiritBlobClosedEyes{0%,20%,100%{opacity:0}34%,82%{opacity:1}}
	  @keyframes spiritBlobPrayerGlow{0%,100%{opacity:.58;transform:scale(1)}42%,80%{opacity:1;transform:scale(1.2)}}
	  @keyframes spiritBlobTwirl{
	    0%{transform:perspective(520px) translateY(0) rotateX(0deg) scale(1)}
	    15%{transform:perspective(520px) translateY(1px) rotateX(-18deg) scale(1.02,.985)}
	    48%{transform:perspective(520px) translateY(-7px) rotateX(190deg) scale(1.055)}
	    70%{transform:perspective(520px) translateY(-4px) rotateX(360deg) scale(1.035)}
	    86%{transform:perspective(520px) translateY(1px) rotateX(366deg) scale(.995,1.016)}
	    100%{transform:perspective(520px) translateY(0) rotateX(360deg) scale(1)}
	  }
	  @keyframes spiritBlobTwirlPose{
	    0%,100%{transform:scale(1)}
	    28%{transform:scale(.95,1.04)}
	    52%{transform:scale(1.06,.96)}
	    74%{transform:scale(1.02)}
	  }
	  @keyframes spiritBlobDance{
	    0%,100%{transform:translate(0,0) rotate(0) scale(1)}
	    15%{transform:translateY(1px) scale(1.02,.985)}
	    28%{transform:translate(-5px,-4px) rotate(-3deg) scale(1.025)}
	    46%{transform:translate(5px,-1px) rotate(3deg) scale(1.012)}
	    64%{transform:translate(-4px,-5px) rotate(2deg) scale(1.026)}
	    82%{transform:translate(4px,-1px) rotate(-2deg) scale(1.01)}
	  }
	  @keyframes spiritBlobDancePose{
	    0%,100%{transform:scale(1)}
	    28%,64%{transform:scale(1.025,.985)}
	    46%,82%{transform:scale(.99,1.012)}
	  }
	  @keyframes spiritBlobFlight{
	    0%{transform:translateY(0) rotate(0) scale(1);filter:drop-shadow(0 12px 16px rgba(82,55,15,.12))}
	    12%{transform:translateY(1px) scale(1.035,.98);filter:drop-shadow(0 0 16px rgba(255,248,223,.55))}
	    30%{transform:translateY(-34px) rotate(-4deg) scale(1.055)}
	    52%{transform:translateY(-88px) rotate(180deg) scale(1.1);filter:drop-shadow(0 0 26px rgba(255,248,223,.92)) drop-shadow(0 0 18px rgba(186,230,253,.42))}
	    68%{transform:translateY(-76px) rotate(360deg) scale(1.08)}
	    86%{transform:translateY(-8px) rotate(365deg) scale(.99,1.04)}
	    100%{transform:translateY(0) rotate(360deg) scale(1);filter:drop-shadow(0 12px 16px rgba(82,55,15,.12))}
	  }
	  @keyframes spiritBlobFlightPose{0%,100%{transform:scale(1)}30%,74%{transform:translateY(1px) scale(1.06,1.04)}88%{transform:scale(1.04,.96)}}
	  @media (prefers-reduced-motion:reduce){
	    .spirit-cute-layer,
	    .spirit-cute-eyes,
	    .spirit-blush,
	    .spirit-cute-smile,
	    .spirit-chest-symbol,
	    .spirit-robe-highlight,
	    .spirit-arm,
	    .spirit-hand{
	      animation:none!important;
	    }
	  }
	  .home-room.home-care-glowing .home-sky,
	  .home-room.home-care-restoring .home-sky{
	    filter:saturate(1.08) brightness(1.05)!important;
	  }
	  .home-room.home-care-glowing .home-floor,
	  .home-room.home-care-restoring .home-floor{
	    filter:saturate(1.08) brightness(1.04)!important;
	  }
	  .home-room.home-care-glowing .home-rays,
	  .home-room.home-care-restoring .home-rays{
	    opacity:.98!important;
	    filter:blur(.4px) brightness(1.18)!important;
	  }
	  .home-room.home-care-glowing .home-walk-blessing{
	    left:24%!important;
	    right:24%!important;
	    bottom:11%!important;
	    height:38%!important;
	    z-index:22!important;
	  }
	  .home-room.home-care-glowing .home-walk-blessing span{
	    width:10px!important;
	    height:10px!important;
	    animation:homeWalkBrighten 3.8s ease-in-out infinite!important;
	  }
	  .home-answer-dove{
	    position:absolute;
	    left:-12%;
	    top:37%;
	    width:58px;
	    height:48px;
	    z-index:26;
	    pointer-events:none;
	    filter:drop-shadow(0 8px 11px rgba(82,55,15,.14)) drop-shadow(0 0 17px rgba(240,203,118,.58));
	    animation:answerDoveArrive 15s ease-in-out infinite;
	  }
	  .answer-dove-body{
	    position:absolute;
	    left:18px;
	    bottom:10px;
	    width:31px;
	    height:23px;
	    border-radius:56% 48% 48% 54%;
	    background:linear-gradient(145deg,#fffdf4,#ffe9a6 48%,#d79a17);
	    border:1.5px solid rgba(138,90,8,.34);
	    box-shadow:inset 0 5px 7px rgba(255,255,255,.75),inset 0 -5px 7px rgba(138,90,8,.18);
	  }
	  .answer-dove-body::after{
	    content:"";
	    position:absolute;
	    right:-7px;
	    bottom:7px;
	    width:12px;
	    height:9px;
	    clip-path:polygon(0 50%,100% 0,100% 100%);
	    background:linear-gradient(145deg,#ffe9a6,#d79a17);
	  }
	  .answer-dove-head{
	    position:absolute;
	    left:35px;
	    bottom:27px;
	    width:17px;
	    height:16px;
	    border-radius:50%;
	    background:linear-gradient(145deg,#fffdf4,#ffe9a6 60%,#d79a17);
	    border:1.5px solid rgba(138,90,8,.32);
	  }
	  .answer-dove-head::after{
	    content:"";
	    position:absolute;
	    right:4px;
	    top:5px;
	    width:3px;
	    height:3px;
	    border-radius:50%;
	    background:#3a2600;
	  }
	  .answer-dove-wing{
	    position:absolute;
	    bottom:21px;
	    width:27px;
	    height:21px;
	    border-radius:72% 35% 72% 35%;
	    background:linear-gradient(145deg,#fffdf4,#ffe9a6 48%,#d79a17);
	    border:1.4px solid rgba(138,90,8,.28);
	    opacity:.96;
	    animation:answerDoveWing 1.05s ease-in-out infinite;
	  }
	  .answer-dove-wing.left{left:5px;transform-origin:100% 70%;transform:rotate(-25deg);}
	  .answer-dove-wing.right{right:6px;transform-origin:0 70%;transform:scaleX(-1) rotate(-25deg);animation-delay:.08s;}
	  .home-testimony-particles{
	    position:absolute;
	    left:38%;
	    right:30%;
	    top:35%;
	    height:32%;
	    z-index:23;
	    pointer-events:none;
	  }
	  .home-testimony-particles span{
	    position:absolute;
	    bottom:0;
	    width:8px;
	    height:8px;
	    border-radius:50%;
	    background:radial-gradient(circle,#fff 0 25%,#f0c75e 28% 62%,transparent 64%);
	    box-shadow:0 0 12px rgba(240,203,118,.70);
	    animation:testimonyGoldRise 4.6s ease-in-out infinite;
	  }
	  .home-testimony-particles span:nth-child(1){left:12%;animation-delay:.1s;}
	  .home-testimony-particles span:nth-child(2){left:36%;animation-delay:1.2s;transform:scale(.8);}
	  .home-testimony-particles span:nth-child(3){left:62%;animation-delay:2.1s;background:radial-gradient(circle,#fff 0 25%,#f9a8d4 28% 62%,transparent 64%);}
	  .home-testimony-particles span:nth-child(4){left:82%;animation-delay:3s;transform:scale(.72);}
	  .home-room.home-spirit-action-wave .home-spirit-slot > div{
	    transform-origin:52% 72%!important;
	    animation:expressiveWaveBody 1.65s cubic-bezier(.2,.76,.24,1) both!important;
	  }
	  .home-room.home-spirit-action-wave .home-spirit-life.sparkle-one{
	    right:2%!important;
	    top:12%!important;
	    opacity:1!important;
	    animation:waveHandSparkle 1.65s ease-out both!important;
	  }
	  .home-room.home-spirit-action-pray .home-spirit-slot > div{
	    transform-origin:50% 78%!important;
	    animation:expressivePrayerBody 2.7s cubic-bezier(.22,.72,.24,1) both!important;
	  }
	  .home-room.home-spirit-action-pray .home-prayer-hands{
	    top:51%!important;
	    animation:prayerHandsClasp 2.7s ease-in-out both!important;
	    filter:drop-shadow(0 0 12px rgba(255,248,223,.82));
	  }
	  .home-room.home-spirit-action-pray .home-prayer-hands .hand.left{
	    animation:prayerLeftHandClaspLong 2.7s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .home-prayer-hands .hand.right{
	    animation:prayerRightHandClaspLong 2.7s ease-in-out both!important;
	  }
	  .home-room.home-spirit-action-pray .home-spirit-life.sparkle-one,
	  .home-room.home-spirit-action-pray .home-spirit-life.sparkle-two{
	    animation:prayerUpwardSparkle 2.7s ease-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .home-spirit-slot > div{
	    transform-origin:50% 58%!important;
	    transform-style:preserve-3d!important;
	    animation:expressiveTwirlBody 1.65s cubic-bezier(.18,.78,.25,1) both!important;
	  }
	  .home-room.home-spirit-action-dance .home-spirit-slot > div{
	    transform-origin:50% 78%!important;
	    animation:expressiveDanceBody 1.95s cubic-bezier(.18,.78,.25,1) both!important;
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot > div{
	    transform-origin:50% 78%!important;
	    animation:expressiveFaithFlight 4.5s cubic-bezier(.2,.75,.2,1) both!important;
	  }
	  @keyframes homeWalkBrighten{
	    0%,100%{transform:translateY(10px) scale(.72);opacity:.22}
	    40%{opacity:.88}
	    72%{transform:translateY(-44px) scale(1.18);opacity:.42}
	  }
	  @keyframes answerDoveArrive{
	    0%{transform:translateX(0) translateY(0) rotate(-2deg) scale(.9);opacity:0}
	    9%{opacity:.92}
	    34%{transform:translateX(40vw) translateY(-11px) rotate(3deg) scale(1);opacity:1}
	    62%{transform:translateX(76vw) translateY(2px) rotate(-1deg) scale(.96);opacity:.96}
	    88%{opacity:.86}
	    100%{transform:translateX(116vw) translateY(-8px) rotate(2deg) scale(.9);opacity:0}
	  }
	  @keyframes answerDoveWing{0%,100%{scale:1 .92}50%{scale:.78 1.08}}
	  @keyframes testimonyGoldRise{
	    0%{transform:translateY(14px) scale(.65);opacity:0}
	    34%{opacity:.95}
	    100%{transform:translateY(-58px) scale(1.1);opacity:0}
	  }
	  @keyframes homeSpiritShineAction{
	    0%,100%{transform:translateY(0) scale(1);filter:brightness(1)}
	    18%{transform:translateY(-3px) scale(1.035);filter:brightness(1.22) saturate(1.08)}
	    42%{transform:translateY(-8px) scale(1.085);filter:brightness(1.58) saturate(1.18)}
	    66%{transform:translateY(-5px) scale(1.05);filter:brightness(1.38) saturate(1.12)}
	    84%{transform:translateY(-2px) scale(1.018);filter:brightness(1.18) saturate(1.06)}
	  }
	  @keyframes spiritShineHalo{
	    0%{transform:translateX(-50%) scale(.72);opacity:0}
	    20%{opacity:1}
	    58%{transform:translateX(-50%) scale(1.22);opacity:.86}
	    100%{transform:translateX(-50%) scale(1.55);opacity:0}
	  }
	  @keyframes spiritShineRays{
	    0%{opacity:0;transform:translateX(-50%) scale(.82) rotate(-8deg)}
	    26%{opacity:.72}
	    62%{opacity:.5;transform:translateX(-50%) scale(1.12) rotate(12deg)}
	    100%{opacity:0;transform:translateX(-50%) scale(1.28) rotate(24deg)}
	  }
	  @keyframes spiritShineSparkle{
	    0%{opacity:0;transform:translateY(10px) scale(.58) rotate(0deg)}
	    25%{opacity:1}
	    70%{opacity:.76;transform:translateY(-20px) scale(1.18) rotate(18deg)}
	    100%{opacity:0;transform:translateY(-34px) scale(.82) rotate(34deg)}
	  }
	  @keyframes expressiveWaveBody{
	    0%,100%{transform:translateY(0) rotate(0) scale(1)}
	    15%{transform:translateY(-2px) rotate(-3deg) scale(1.012)}
	    34%{transform:translateY(-5px) rotate(-7deg) scale(1.025)}
	    58%{transform:translateY(-4px) rotate(5deg) scale(1.016)}
	    76%{transform:translateY(-2px) rotate(-3deg) scale(1.01)}
	  }
	  @keyframes waveHandSparkle{
	    0%{transform:translate(0,8px) scale(.5);opacity:0}
	    28%,68%{transform:translate(8px,-4px) scale(1.18);opacity:1}
	    100%{transform:translate(14px,-14px) scale(.82);opacity:0}
	  }
	  @keyframes expressivePrayerBody{
	    0%,100%{transform:translateY(0) scale(1)}
	    18%{transform:translateY(2px) rotate(.8deg) scale(.995)}
	    36%,76%{transform:translateY(9px) rotate(0) scale(.982,.95)}
	    88%{transform:translateY(3px) scale(1.006)}
	  }
	  @keyframes prayerLeftHandClaspLong{
	    0%{transform:translate(-16px,8px) rotate(62deg) scale(.82);opacity:0}
	    24%{transform:translate(-4px,0) rotate(34deg) scale(1);opacity:1}
	    42%,78%{transform:translate(1px,-1px) rotate(14deg) scale(1.06);opacity:1}
	    100%{transform:translate(5px,-6px) rotate(20deg) scale(.94);opacity:0}
	  }
	  @keyframes prayerRightHandClaspLong{
	    0%{transform:translate(16px,8px) rotate(-62deg) scale(.82);opacity:0}
	    24%{transform:translate(4px,0) rotate(-34deg) scale(1);opacity:1}
	    42%,78%{transform:translate(-1px,-1px) rotate(-14deg) scale(1.06);opacity:1}
	    100%{transform:translate(-5px,-6px) rotate(-20deg) scale(.94);opacity:0}
	  }
	  @keyframes prayerUpwardSparkle{
	    0%{transform:translateY(16px) scale(.55);opacity:0}
	    35%{opacity:.92}
	    100%{transform:translateY(-44px) scale(1.04);opacity:0}
	  }
	  @keyframes expressiveTwirlBody{
	    0%{transform:perspective(520px) translateY(0) rotateX(0deg) scale(1)}
	    18%{transform:perspective(520px) translateY(-5px) rotateX(-18deg) scale(1.03,.985)}
	    46%{transform:perspective(520px) translateY(-12px) rotateX(178deg) scale(1.055)}
	    70%{transform:perspective(520px) translateY(-8px) rotateX(342deg) scale(1.035)}
	    88%{transform:perspective(520px) translateY(-2px) rotateX(370deg) scale(1.012)}
	    100%{transform:perspective(520px) translateY(0) rotateX(360deg) scale(1)}
	  }
	  @keyframes expressiveDanceBody{
	    0%,100%{transform:translate(0,0) rotate(0) scale(1)}
	    16%{transform:translate(-6px,-4px) rotate(-5deg) scale(1.03)}
	    34%{transform:translate(6px,0) rotate(5deg) scale(1.01)}
	    54%{transform:translate(-5px,-5px) rotate(4deg) scale(1.035)}
	    74%{transform:translate(5px,-2px) rotate(-4deg) scale(1.02)}
	  }
	  @keyframes expressiveFaithFlight{
	    0%{transform:translateY(0) rotate(0deg) scale(1);filter:drop-shadow(0 16px 20px rgba(82,55,15,.10))}
	    18%{transform:translateY(-24px) rotate(-5deg) scale(1.04);filter:drop-shadow(0 0 22px rgba(255,248,223,.72))}
	    42%{transform:translateY(-86px) rotate(178deg) scale(1.1);filter:drop-shadow(0 0 28px rgba(255,248,223,.92)) drop-shadow(0 0 22px rgba(186,230,253,.44))}
	    62%{transform:translateY(-74px) rotate(360deg) scale(1.08)}
	    82%{transform:translateY(-20px) rotate(365deg) scale(1.03)}
	    100%{transform:translateY(0) rotate(360deg) scale(1);filter:drop-shadow(0 16px 20px rgba(82,55,15,.10))}
	  }

	  /* Solid Home pass: clearer function, stronger grounding, calmer scene labels. */
	  .home-page .home-care-card{
	    border:2px solid rgba(91,143,184,.46)!important;
	    background:
	      radial-gradient(circle at 90% 0%,rgba(255,248,223,.70),transparent 34%),
	      linear-gradient(145deg,rgba(255,253,246,.98),rgba(239,248,255,.76) 58%,rgba(236,253,245,.50))!important;
	    box-shadow:0 7px 22px rgba(65,95,125,.10),inset 0 0 0 1px rgba(255,255,255,.70)!important;
	  }
	  .home-page .home-room{
	    border:2px solid rgba(91,143,184,.30)!important;
	    box-shadow:
	      inset 0 -38px 58px rgba(70,104,58,.12),
	      inset 0 0 0 1px rgba(255,255,255,.62),
	      0 12px 32px rgba(65,95,125,.14)!important;
	  }
	  .home-page .home-floor{
	    box-shadow:inset 0 16px 22px rgba(255,255,255,.28),inset 0 -18px 26px rgba(59,89,44,.12)!important;
	  }
	  .home-page .home-path{
	    border-left:2px solid rgba(138,90,8,.12)!important;
	    border-right:2px solid rgba(138,90,8,.12)!important;
	    filter:drop-shadow(0 7px 10px rgba(82,55,15,.08));
	  }
	  .home-page .home-midground-glow{
	    opacity:.72!important;
	    background:radial-gradient(ellipse,rgba(255,248,223,.58),rgba(240,203,118,.14) 42%,transparent 72%)!important;
	  }
	  .home-page .home-cardboard,
	  .home-page .home-little-shelter,
	  .home-page .home-apartment,
	  .home-page .home-house,
	  .home-page .home-mansion,
	  .home-page .home-palace,
	  .home-page .home-chapel{
	    filter:
	      drop-shadow(0 17px 22px rgba(82,55,15,.20))
	      drop-shadow(0 0 18px rgba(255,248,223,.24))!important;
	  }
	  .home-page .home-cardboard::after,
	  .home-page .home-little-shelter::after,
	  .home-page .home-apartment::after,
	  .home-page .home-house::after,
	  .home-page .home-mansion::after,
	  .home-page .home-palace::after,
	  .home-page .home-chapel::after{
	    background:radial-gradient(ellipse,rgba(82,55,15,.24),rgba(82,55,15,0) 72%)!important;
	    opacity:.88!important;
	  }
	  .home-page .home-slot.feature-slot::before,
	  .home-page .home-slot.feature-slot.furn_fountain-slot::before,
	  .home-page .home-slot.feature-slot.furn_waterfall-slot::before,
	  .home-page .home-slot.feature-slot.furn_lily_pond-slot::before{
	    content:none!important;
	    display:none!important;
	  }
	  .home-page .home-water-feature{
	    opacity:.60!important;
	    filter:drop-shadow(0 8px 14px rgba(40,112,145,.18)) saturate(1.12) contrast(1.04)!important;
	  }
	  .home-page .home-slot.feature-slot .home-game-item{
	    filter:drop-shadow(0 9px 12px rgba(40,112,145,.12))!important;
	  }
	  .home-page .home-spirit-slot{
	    z-index:62!important;
	  }
	  .home-page .home-spirit-slot > div{
	    filter:
	      drop-shadow(0 18px 24px rgba(82,55,15,.20))
	      drop-shadow(0 0 30px rgba(255,248,223,.78))
	      drop-shadow(0 0 22px rgba(186,230,253,.32))!important;
	  }
	  .home-page .home-spirit-ground-shadow{
	    opacity:.34!important;
	    filter:blur(1px)!important;
	  }
	  .home-page .home-gem,
	  .home-page .home-path-gem,
	  .home-page .particle{
	    opacity:.12!important;
	  }
	  .home-page .home-room.home-care-glowing .home-rays,
	  .home-page .home-room.home-care-radiant .home-rays{
	    opacity:.82!important;
	  }
	  .home-page .home-shop-card{
	    border:1.5px solid rgba(91,143,184,.34)!important;
	    box-shadow:0 4px 14px rgba(65,95,125,.065)!important;
	  }
	  .home-page .home-shop-card.owned{
	    border:2px solid rgba(201,144,18,.52)!important;
	  }
	  @media (max-width:560px){
	    .gender-select-screen{
	      justify-content:flex-start!important;
	      min-height:100dvh!important;
	      padding:18px 14px calc(18px + env(safe-area-inset-bottom))!important;
	      overflow-y:auto!important;
	    }
	    .gender-select-screen > div{
	      max-width:100%!important;
	    }
	    .gender-select-screen h1{
	      font-size:clamp(25px,8vw,32px)!important;
	      line-height:1.12!important;
	    }
	    .gender-select-screen p{
	      max-width:100%!important;
	    }
	    .gender-signin-row{
	      margin-top:16px!important;
	      padding-bottom:4px!important;
	    }
	    .gender-choice-card{
	      min-height:238px!important;
	      padding:15px 7px 14px!important;
	    }
	    .gender-choice-card svg{
	      width:clamp(86px,27vw,104px)!important;
	      max-height:124px!important;
	    }
	    .gender-choice-card > div:last-child::before{
	      width:72px!important;
	      height:22px!important;
	      margin:-8px auto 5px!important;
	    }
	    .spirit-action-menu{
	      width:min(232px,calc(100vw - 32px))!important;
	      max-height:46vh!important;
	      overflow-y:auto!important;
	      overscroll-behavior:contain!important;
	    }
	    .home-page .home-care-card{padding:13px!important;}
	    .home-page .home-room{border-width:1.5px!important;}
	  }
	  .appearance-swatch{
	    width:34px!important;
	    height:34px!important;
	    min-width:34px!important;
	    max-width:34px!important;
	    min-height:34px!important;
	    max-height:34px!important;
	    flex:0 0 34px!important;
	    aspect-ratio:1 / 1!important;
	    border-radius:50%!important;
	    padding:0!important;
	    display:block!important;
	    line-height:0!important;
	    box-sizing:border-box!important;
	  }
	  @media (max-width:520px){
	    .appearance-swatch{
	      width:32px!important;
	      height:32px!important;
	      min-width:32px!important;
	      max-width:32px!important;
	      min-height:32px!important;
	      max-height:32px!important;
	      flex-basis:32px!important;
	    }
	  }
	  /* Final fountain realism pass: clearer stone bowl, transparent flowing water, ripples, and droplets. */
	  @keyframes fountainCrystalBubble{
	    0%,100%{transform:translateY(0) scale(.98);opacity:.86;filter:brightness(1)}
	    45%{transform:translateY(-4px) scale(1.04);opacity:1;filter:brightness(1.08)}
	  }
	  @keyframes fountainFallingStreams{
	    0%{background-position:0 -22px;transform:translateY(-2px) scaleY(.96);opacity:.74}
	    50%{opacity:1}
	    100%{background-position:0 28px;transform:translateY(2px) scaleY(1.05);opacity:.82}
	  }
	  @keyframes fountainRealRipple{
	    0%{transform:scale(.74);opacity:.72}
	    100%{transform:scale(1.28);opacity:0}
	  }
	  @keyframes fountainFloatingDroplets{
	    0%,100%{transform:translateY(2px) scale(.72);opacity:.45}
	    45%{transform:translateY(-8px) scale(1.08);opacity:.95}
	  }
	  .home-room .home-slot.feature-slot.furn_fountain-slot{
	    right:22%!important;
	    bottom:6.8%!important;
	    z-index:7!important;
	    opacity:.96!important;
	    filter:drop-shadow(0 8px 12px rgba(38,80,105,.14))!important;
	  }
	  .home-room .home-slot.feature-slot .home-game-item.furn_fountain{
	    width:clamp(66px,16vw,92px)!important;
	    height:clamp(66px,16vw,94px)!important;
	    animation:fountainSettle 6.8s ease-in-out infinite!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-shadow{
	    left:4%!important;
	    right:4%!important;
	    bottom:0!important;
	    height:14px!important;
	    opacity:.54!important;
	    background:radial-gradient(ellipse,rgba(42,74,92,.22),transparent 72%)!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-glow{
	    opacity:.18!important;
	    filter:blur(1px)!important;
	    background:radial-gradient(ellipse,rgba(219,242,255,.72),rgba(56,189,248,.14),transparent 74%)!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-body{
	    left:7%!important;
	    right:7%!important;
	    bottom:8px!important;
	    height:48%!important;
	    border-radius:22px 22px 30px 30px!important;
	    border:1.5px solid rgba(57,102,127,.28)!important;
	    background:
	      radial-gradient(ellipse at 50% 18%,rgba(255,255,255,.96),rgba(219,242,255,.92) 38%,rgba(87,154,186,.48) 58%,transparent 61%),
	      linear-gradient(180deg,#ffffff 0%,#e8f8ff 29%,#9ccfe4 62%,#477f9d 100%)!important;
	    box-shadow:
	      inset 0 8px 10px rgba(255,255,255,.92),
	      inset 0 -12px 16px rgba(19,75,105,.24),
	      0 8px 11px rgba(82,55,15,.10)!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-body::before{
	    content:""!important;
	    position:absolute!important;
	    left:4%!important;
	    right:4%!important;
	    top:5px!important;
	    height:16px!important;
	    border-radius:50%!important;
	    background:
	      radial-gradient(ellipse at 50% 42%,rgba(255,255,255,.98),rgba(174,230,250,.92) 46%,rgba(31,112,148,.26) 78%)!important;
	    box-shadow:
	      0 20px 0 4px rgba(255,255,255,.25),
	      0 23px 0 6px rgba(31,112,148,.15),
	      inset 0 1px 5px rgba(255,255,255,.8)!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-body::after{
	    content:""!important;
	    position:absolute!important;
	    left:13%!important;
	    right:13%!important;
	    bottom:-12px!important;
	    height:17px!important;
	    border-radius:50%!important;
	    background:radial-gradient(ellipse at 50% 42%,rgba(255,255,255,.86),rgba(125,211,252,.50) 52%,rgba(14,116,144,.20) 80%,transparent 88%)!important;
	    box-shadow:0 0 14px rgba(186,230,253,.24),inset 0 1px 6px rgba(255,255,255,.64)!important;
	    animation:fountainRealRipple 2.6s ease-out infinite!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-detail.water{
	    left:4%!important;
	    right:4%!important;
	    top:-24%!important;
	    height:68%!important;
	    border-radius:48% 48% 44% 44%!important;
	    background:
	      radial-gradient(circle at 50% 9%,#fff 0 11%,rgba(223,250,255,.98) 12% 35%,rgba(80,190,226,.68) 36% 68%,rgba(14,116,144,.16) 69% 100%)!important;
	    box-shadow:
	      -20px 36px 0 -11px rgba(125,211,252,.72),
	      20px 36px 0 -11px rgba(125,211,252,.66),
	      0 48px 0 -11px rgba(45,139,175,.42),
	      0 0 17px rgba(186,230,253,.30)!important;
	    animation:fountainCrystalBubble 1.35s ease-in-out infinite!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-detail.water::before{
	    content:""!important;
	    position:absolute!important;
	    left:49%!important;
	    top:-30px!important;
	    width:7px!important;
	    height:58px!important;
	    border-radius:999px!important;
	    background:
	      repeating-linear-gradient(180deg,rgba(255,255,255,1) 0 7px,rgba(182,234,251,.86) 7px 15px,rgba(48,171,212,.48) 15px 24px)!important;
	    background-size:100% 28px!important;
	    box-shadow:
	      -20px 19px 0 -1px rgba(125,211,252,.62),
	      20px 19px 0 -1px rgba(125,211,252,.56),
	      0 10px 0 1px rgba(255,255,255,.56)!important;
	    animation:fountainFallingStreams 1.05s linear infinite!important;
	  }
	  .home-room .home-game-item.furn_fountain .home-game-detail.water::after{
	    content:""!important;
	    position:absolute!important;
	    left:50%!important;
	    top:-38px!important;
	    width:8px!important;
	    height:8px!important;
	    border-radius:50%!important;
	    background:rgba(255,255,255,.94)!important;
	    box-shadow:
	      -24px 25px 0 -2px rgba(125,211,252,.78),
	      24px 23px 0 -3px rgba(125,211,252,.74),
	      -12px 59px 0 -3px rgba(255,255,255,.84),
	      12px 63px 0 -4px rgba(125,211,252,.68),
	      0 42px 0 -3px rgba(255,255,255,.78)!important;
	    animation:fountainFloatingDroplets 1.45s ease-in-out infinite!important;
	  }
	  .home-room .home-water-feature.furn_fountain{
	    right:23%!important;
	    bottom:5.3%!important;
	    width:18%!important;
	    height:7%!important;
	    opacity:.68!important;
	    transform:rotate(-7deg)!important;
	    filter:blur(.05px) drop-shadow(0 2px 5px rgba(56,139,190,.12))!important;
	  }
	  .home-room .home-water-feature.furn_fountain .water-ripple{
	    opacity:.58!important;
	    animation-duration:2.8s!important;
	  }
	  .home-room .home-water-feature.furn_fountain .water-shimmer{
	    opacity:.54!important;
	  }
	  @media (max-width:520px){
	    .home-room .home-slot.feature-slot.furn_fountain-slot{
	      right:20%!important;
	      bottom:6%!important;
	      transform:scale(.9)!important;
	      transform-origin:center bottom!important;
	    }
	    .home-room .home-water-feature.furn_fountain{
	      right:21%!important;
	      bottom:4.8%!important;
	      width:16%!important;
	    }
	  }
	  /* Calm/readability pass: clearer hierarchy, fewer heavy chips, softer ordinary cards. */
	  .focus-card,
	  .home-care-card,
	  .sticky-preview-card,
	  .prayer-page-calm .card,
	  .journal-page .card{
	    box-shadow:0 4px 16px rgba(65,95,125,.075)!important;
	  }
	  .home-page .focus-card,
	  .home-page .sticky-preview-card,
	  .home-page .home-care-card{
	    border-color:rgba(91,143,184,.34)!important;
	  }
	  .home-page .home-care-card{
	    background:
	      linear-gradient(145deg,rgba(255,253,246,.98),rgba(241,249,255,.72) 68%,rgba(236,253,245,.38))!important;
	  }
	  .home-page .faith-flight-panel{
	    margin-top:11px!important;
	    padding:10px 11px!important;
	    border:1.5px solid rgba(91,143,184,.28)!important;
	    background:rgba(255,253,246,.62)!important;
	    box-shadow:none!important;
	  }
	  .home-page .faith-flight-title{
	    color:#497093!important;
	  }
	  .home-page .faith-flight-copy{
	    color:var(--text-soft)!important;
	    font-size:12.5px!important;
	    line-height:1.35!important;
	  }
	  .home-page .faith-flight-lock,
	  .home-page .faith-flight-btn{
	    min-height:34px!important;
	    padding:7px 10px!important;
	    font-size:12.5px!important;
	    white-space:nowrap!important;
	  }
	  .home-page .home-shop-category-row{
	    gap:8px!important;
	  }
	  .home-page .home-category-card{
	    min-width:100px!important;
	    padding:10px 8px!important;
	    border-color:rgba(91,143,184,.34)!important;
	    background:rgba(255,253,246,.70)!important;
	  }
	  .home-page .home-category-card.active{
	    border-color:rgba(201,144,18,.78)!important;
	    background:linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,248,223,.54))!important;
	  }
	  .home-page .home-category-card .meaning-chip,
	  .home-page .home-shop-card .meaning-chip{
	    background:rgba(241,249,255,.72)!important;
	    border-color:rgba(91,143,184,.24)!important;
	    color:#497093!important;
	  }
	  .home-page .home-shop-card{
	    min-height:112px!important;
	    padding:11px 9px!important;
	    background:rgba(255,253,246,.62)!important;
	    border-color:rgba(91,143,184,.30)!important;
	  }
	  .home-page .home-shop-card.owned{
	    border-color:rgba(201,144,18,.48)!important;
	    background:linear-gradient(160deg,rgba(255,253,246,.92),rgba(255,248,223,.32))!important;
	  }
	  .home-page .empty-state{
	    background:rgba(241,249,255,.55)!important;
	    border-color:rgba(91,143,184,.22)!important;
	  }
	  .unlock-note{
	    background:rgba(241,249,255,.55)!important;
	    border-color:rgba(91,143,184,.20)!important;
	    color:var(--text-soft)!important;
	  }
	  .placed-chip,
	  .meaning-chip,
	  .sticky-preview-label{
	    box-shadow:none!important;
	    letter-spacing:0!important;
	  }
	  .prayer-page-calm .gold-outline-card,
	  .gold-outline-card{
	    box-shadow:0 5px 16px rgba(138,90,8,.08)!important;
	  }
	  .prayer-page-calm p,
	  .home-page p,
	  .journal-page p{
	    max-width:100%;
	  }
	  @media (max-width:520px){
	    .home-page{
	      gap:10px!important;
	    }
	    .home-page .focus-card,
	    .home-page .home-care-card,
	    .home-page .sticky-preview-card{
	      padding:13px!important;
	    }
	    .home-page .focus-card h2,
	    .home-page .home-care-card h3,
	    .home-page .sticky-preview-card h3{
	      font-size:16px!important;
	      line-height:1.2!important;
	    }
	    .home-page p,
	    .prayer-page-calm p,
	    .journal-page p{
	      font-size:12.5px!important;
	      line-height:1.45!important;
	    }
	    .home-page .home-righteousness-count{
	      min-width:54px!important;
	    }
	    .home-page .home-righteousness-value{
	      font-size:15.5px!important;
	    }
	    .home-page .home-category-card{
	      min-width:88px!important;
	      max-width:96px!important;
	      padding:9px 7px!important;
	    }
	    .home-page .home-category-card .meaning-chip{
	      display:none!important;
	    }
	    .home-page .faith-flight-panel{
	      align-items:flex-start!important;
	      gap:8px!important;
	    }
	  }
	  .spirit-cute-arms,
	  .spirit-arm,
	  .spirit-hand{
	    display:none!important;
	    opacity:0!important;
	    visibility:hidden!important;
	  }

	  /* Final lamb pass: clearer wool, face, legs, and gentle living movement. */
	  @keyframes lambSoftGraze{
	    0%,100%{transform:translateY(0) rotate(0deg)}
	    28%{transform:translateY(-1px) rotate(-1deg)}
	    56%{transform:translateY(0) rotate(.6deg)}
	    76%{transform:translateY(1px) rotate(0deg)}
	  }
	  @keyframes lambHeadGraze{
	    0%,100%{transform:translateY(0) rotate(0deg)}
	    36%{transform:translateY(2px) rotate(3deg)}
	    62%{transform:translateY(-.5px) rotate(-1deg)}
	  }
	  @keyframes lambEarFlick{
	    0%,82%,100%{transform:rotate(18deg)}
	    88%{transform:rotate(34deg)}
	    94%{transform:rotate(10deg)}
	  }
	  @keyframes lambTinySteps{
	    0%,100%{transform:translateY(0)}
	    50%{transform:translateY(-1.5px)}
	  }
	  .home-pet-track.lamb-track{
	    left:4.5%!important;
	    right:49%!important;
	    bottom:10.5%!important;
	    opacity:.9!important;
	    filter:saturate(.96) contrast(1.03)!important;
	  }
	  .home-pet-track.lamb-track .animated-shop-item{
	    min-width:clamp(142px,38vw,220px)!important;
	    animation-name:lambMeadowWalk!important;
	    animation-duration:30s!important;
	    animation-timing-function:ease-in-out!important;
	  }
	  .home-pet-track.lamb-track .home-game-item.pet_lamb{
	    width:clamp(64px,16vw,88px)!important;
	    height:clamp(48px,12vw,66px)!important;
	    overflow:visible!important;
	    animation:lambSoftGraze 3.4s ease-in-out infinite!important;
	    filter:drop-shadow(0 5px 7px rgba(82,55,15,.10))!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-shadow{
	    left:7%!important;
	    right:8%!important;
	    bottom:0!important;
	    height:10px!important;
	    opacity:.42!important;
	    filter:blur(1.6px)!important;
	    background:radial-gradient(ellipse,rgba(82,55,15,.22),transparent 72%)!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-glow{
	    opacity:.055!important;
	    filter:blur(5px)!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-body{
	    left:5%!important;
	    right:22%!important;
	    bottom:12px!important;
	    height:48%!important;
	    border-radius:64% 52% 48% 50% / 58% 62% 42% 44%!important;
	    border:1.5px solid rgba(118,91,57,.18)!important;
	    background:
	      radial-gradient(circle at 13% 30%,#ffffff 0 7px,transparent 8px),
	      radial-gradient(circle at 27% 16%,#ffffff 0 8px,transparent 9px),
	      radial-gradient(circle at 43% 13%,#fffaf2 0 8px,transparent 9px),
	      radial-gradient(circle at 60% 24%,#ffffff 0 7px,transparent 8px),
	      radial-gradient(circle at 76% 36%,#f8efe2 0 7px,transparent 8px),
	      linear-gradient(180deg,#fffdf7 0%,#f0e4d2 58%,#c6ae92 100%)!important;
	    box-shadow:
	      inset 0 6px 8px rgba(255,255,255,.86),
	      inset -4px -6px 8px rgba(112,86,54,.12),
	      0 5px 8px rgba(82,55,15,.08)!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-body::before{
	    content:""!important;
	    position:absolute!important;
	    left:7%!important;
	    right:4%!important;
	    top:-9px!important;
	    height:20px!important;
	    border-radius:999px!important;
	    background:
	      radial-gradient(circle at 12% 66%,#fff 0 5px,transparent 6px),
	      radial-gradient(circle at 31% 38%,#fff 0 6px,transparent 7px),
	      radial-gradient(circle at 52% 58%,#fff 0 5px,transparent 6px),
	      radial-gradient(circle at 74% 42%,#fff8ee 0 5px,transparent 6px)!important;
	    opacity:.98!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-body::after{
	    content:""!important;
	    position:absolute!important;
	    left:-4px!important;
	    top:18px!important;
	    width:9px!important;
	    height:9px!important;
	    border-radius:50%!important;
	    background:#f7ead8!important;
	    border:1px solid rgba(118,91,57,.15)!important;
	    box-shadow:none!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left{
	    display:block!important;
	    right:1%!important;
	    top:22%!important;
	    width:25px!important;
	    height:23px!important;
	    border-radius:56% 48% 50% 42%!important;
	    border:1.5px solid rgba(118,91,57,.20)!important;
	    background:
	      radial-gradient(circle at 55% 54%,#4a2d12 0 2.6px,transparent 3.4px),
	      radial-gradient(circle at 40% 38%,#fffdf7 0 28%,#eadac4 31% 70%,#c2a282 100%)!important;
	    box-shadow:inset 0 4px 5px rgba(255,255,255,.70),0 2px 4px rgba(82,55,15,.08)!important;
	    animation:lambHeadGraze 3.1s ease-in-out infinite!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left::after{
	    content:""!important;
	    position:absolute!important;
	    right:5px!important;
	    top:9px!important;
	    width:3px!important;
	    height:3px!important;
	    border-radius:50%!important;
	    background:#fff8ee!important;
	    box-shadow:none!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left::before{
	    content:""!important;
	    position:absolute!important;
	    right:0!important;
	    top:-7px!important;
	    width:8px!important;
	    height:14px!important;
	    border-radius:70% 42% 65% 40%!important;
	    background:linear-gradient(180deg,#d8bea0,#a98261)!important;
	    transform-origin:50% 100%!important;
	    transform:rotate(18deg)!important;
	    box-shadow:-14px 4px 0 -1px #d2b896!important;
	    animation:lambEarFlick 7s ease-in-out infinite!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-right{
	    display:block!important;
	    left:4%!important;
	    right:auto!important;
	    top:39%!important;
	    width:10px!important;
	    height:10px!important;
	    border-radius:50%!important;
	    background:radial-gradient(circle,#fffdf7 0 42%,#d5bea4 44% 100%)!important;
	    border:1px solid rgba(118,91,57,.10)!important;
	    transform:rotate(-16deg)!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-icon{
	    display:block!important;
	    position:absolute!important;
	    left:17%!important;
	    right:18%!important;
	    bottom:3px!important;
	    width:auto!important;
	    height:13px!important;
	    margin:0!important;
	    font-size:0!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-icon::before,
	  .home-room .home-game-item.pet_lamb .home-game-icon::after{
	    content:""!important;
	    position:absolute!important;
	    bottom:0!important;
	    width:4px!important;
	    height:11px!important;
	    border-radius:999px 999px 3px 3px!important;
	    background:linear-gradient(180deg,#8b735c,#4f3b2b)!important;
	    box-shadow:12px 0 0 #6e5845!important;
	    animation:lambTinySteps 1.35s ease-in-out infinite!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-icon::before{left:3px!important;}
	  .home-room .home-game-item.pet_lamb .home-game-icon::after{
	    right:5px!important;
	    animation-delay:.28s!important;
	  }
	  @media (max-width:520px){
	    .home-pet-track.lamb-track{
	      left:5%!important;
	      right:55%!important;
	      bottom:10.5%!important;
	    }
	    .home-pet-track.lamb-track .animated-shop-item{
	      min-width:clamp(112px,36vw,170px)!important;
	      animation-duration:32s!important;
	    }
	    .home-pet-track.lamb-track .home-game-item.pet_lamb{
	      width:clamp(54px,16vw,74px)!important;
	      height:clamp(42px,12vw,58px)!important;
	    }
	  }

	  /* Lamb softness correction: cute and readable, not realistic or creepy. */
	  @keyframes lambPlushIdle{
	    0%,100%{transform:translateY(0) scale(1)}
	    48%{transform:translateY(-1px) scale(1.015)}
	  }
	  @keyframes lambPlushHead{
	    0%,100%{transform:translateY(0) rotate(0)}
	    50%{transform:translateY(1px) rotate(1.2deg)}
	  }
	  @keyframes lambGentleWalk{
	    0%{transform:translateX(-8px) scaleX(1)}
	    22%{transform:translateX(18px) translateY(-.5px) scaleX(1)}
	    45%{transform:translateX(36px) scaleX(1)}
	    52%{transform:translateX(36px) scaleX(-1)}
	    74%{transform:translateX(12px) translateY(-.5px) scaleX(-1)}
	    96%{transform:translateX(-8px) scaleX(-1)}
	    100%{transform:translateX(-8px) scaleX(1)}
	  }
	  .home-pet-track.lamb-track .animated-shop-item{
	    animation-name:lambGentleWalk!important;
	    animation-duration:34s!important;
	  }
	  .home-pet-track.lamb-track .home-game-item.pet_lamb{
	    width:clamp(58px,15vw,78px)!important;
	    height:clamp(46px,12vw,62px)!important;
	    animation:lambPlushIdle 4.2s ease-in-out infinite!important;
	    filter:drop-shadow(0 5px 7px rgba(82,55,15,.08))!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-body{
	    left:6%!important;
	    right:18%!important;
	    bottom:12px!important;
	    height:50%!important;
	    border-radius:66% 58% 54% 56% / 62% 66% 46% 46%!important;
	    border:1px solid rgba(194,171,141,.22)!important;
	    background:
	      radial-gradient(circle at 15% 30%,#fff 0 7px,transparent 8px),
	      radial-gradient(circle at 31% 16%,#fff 0 8px,transparent 9px),
	      radial-gradient(circle at 50% 18%,#fff 0 8px,transparent 9px),
	      radial-gradient(circle at 68% 31%,#fffaf2 0 7px,transparent 8px),
	      linear-gradient(180deg,#fffdf8 0%,#f7efe5 64%,#dfcdb8 100%)!important;
	    box-shadow:
	      inset 0 5px 8px rgba(255,255,255,.88),
	      inset -3px -4px 6px rgba(162,126,83,.08),
	      0 4px 7px rgba(82,55,15,.06)!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-body::before{
	    top:-8px!important;
	    height:18px!important;
	    background:
	      radial-gradient(circle at 13% 65%,#fff 0 5px,transparent 6px),
	      radial-gradient(circle at 35% 42%,#fff 0 5px,transparent 6px),
	      radial-gradient(circle at 58% 60%,#fff 0 5px,transparent 6px),
	      radial-gradient(circle at 78% 45%,#fff8ee 0 4px,transparent 5px)!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-body::after{
	    left:-2px!important;
	    top:17px!important;
	    width:8px!important;
	    height:8px!important;
	    background:#f1dec8!important;
	    border-color:rgba(194,171,141,.20)!important;
	    opacity:.9!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left{
	    right:2%!important;
	    top:24%!important;
	    width:22px!important;
	    height:21px!important;
	    border-radius:58% 52% 54% 48%!important;
	    border:1px solid rgba(194,171,141,.22)!important;
	    background:
	      radial-gradient(circle at 56% 56%,#6b4a25 0 1.6px,transparent 2.3px),
	      radial-gradient(circle at 42% 38%,#fffdf8 0 30%,#f0dfca 33% 72%,#d1b99c 100%)!important;
	    box-shadow:inset 0 3px 5px rgba(255,255,255,.72),0 2px 4px rgba(82,55,15,.055)!important;
	    animation:lambPlushHead 4s ease-in-out infinite!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left::after{
	    display:none!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-left::before{
	    right:1px!important;
	    top:-5px!important;
	    width:7px!important;
	    height:11px!important;
	    background:linear-gradient(180deg,#ead5ba,#c6a17f)!important;
	    box-shadow:-11px 4px 0 -1px #e1c8aa!important;
	    animation:none!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-detail.wing-right{
	    left:5%!important;
	    top:42%!important;
	    width:8px!important;
	    height:8px!important;
	    background:radial-gradient(circle,#fffaf2 0 45%,#ddc5a9 48% 100%)!important;
	    opacity:.82!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-icon{
	    left:20%!important;
	    right:22%!important;
	    bottom:4px!important;
	    height:10px!important;
	  }
	  .home-room .home-game-item.pet_lamb .home-game-icon::before,
	  .home-room .home-game-item.pet_lamb .home-game-icon::after{
	    width:3px!important;
	    height:8px!important;
	    background:linear-gradient(180deg,#b79d7e,#8a6e52)!important;
	    box-shadow:10px 0 0 #a68b6e!important;
	    opacity:.82!important;
	    animation:lambTinySteps 1.8s ease-in-out infinite!important;
	  }
	  @media (max-width:520px){
	    .home-pet-track.lamb-track .home-game-item.pet_lamb{
	      width:clamp(50px,15vw,66px)!important;
	      height:clamp(40px,12vw,54px)!important;
	    }
	  }

	  /* Final spirit twirl pass: soft vertical turn with lift, glow, and gentle landing. */
	  @keyframes spiritSoftVerticalTwirl{
	    0%{
	      transform:perspective(640px) translateY(0) rotateY(0deg) rotateZ(0deg) scale(1);
	      filter:drop-shadow(0 12px 16px rgba(82,55,15,.12));
	    }
	    14%{
	      transform:perspective(640px) translateY(2px) rotateY(0deg) rotateZ(-2deg) scale(1.03,.97);
	    }
	    34%{
	      transform:perspective(640px) translateY(-16px) rotateY(112deg) rotateZ(2deg) scale(.92,1.05);
	      filter:drop-shadow(0 14px 18px rgba(82,55,15,.10)) drop-shadow(0 0 14px rgba(255,248,223,.54));
	    }
	    56%{
	      transform:perspective(640px) translateY(-20px) rotateY(246deg) rotateZ(-1deg) scale(.88,1.06);
	      filter:drop-shadow(0 15px 19px rgba(82,55,15,.10)) drop-shadow(0 0 20px rgba(255,248,223,.82)) drop-shadow(0 0 13px rgba(186,230,253,.38));
	    }
	    78%{
	      transform:perspective(640px) translateY(-8px) rotateY(360deg) rotateZ(1deg) scale(1.03,.99);
	    }
	    92%{
	      transform:perspective(640px) translateY(1px) rotateY(360deg) rotateZ(0deg) scale(1.018,.982);
	    }
	    100%{
	      transform:perspective(640px) translateY(0) rotateY(360deg) rotateZ(0deg) scale(1);
	      filter:drop-shadow(0 12px 16px rgba(82,55,15,.12));
	    }
	  }
	  @keyframes spiritTwirlSparkleRing{
	    0%{opacity:0;transform:translateX(-50%) translateY(8px) scale(.64) rotate(0deg)}
	    22%{opacity:.35}
	    52%{opacity:.9;transform:translateX(-50%) translateY(-10px) scale(1.1) rotate(150deg)}
	    100%{opacity:0;transform:translateX(-50%) translateY(-22px) scale(1.26) rotate(300deg)}
	  }
	  .home-room.home-spirit-action-twirl .home-spirit-slot > div{
	    animation:spiritSoftVerticalTwirl 2.15s cubic-bezier(.2,.78,.22,1) both!important;
	    transform-origin:50% 82%!important;
	    will-change:transform,filter;
	  }
	  .home-room.home-spirit-action-twirl .home-spirit-slot::before{
	    content:""!important;
	    position:absolute!important;
	    left:50%!important;
	    top:16%!important;
	    width:96px!important;
	    height:96px!important;
	    border-radius:50%!important;
	    transform:translateX(-50%)!important;
	    z-index:38!important;
	    pointer-events:none!important;
	    background:
	      radial-gradient(circle at 50% 0%,rgba(255,255,255,.92) 0 3px,transparent 4px),
	      radial-gradient(circle at 100% 48%,rgba(255,248,223,.86) 0 4px,transparent 5px),
	      radial-gradient(circle at 48% 100%,rgba(186,230,253,.76) 0 3px,transparent 4px),
	      radial-gradient(circle at 0% 52%,rgba(249,168,212,.72) 0 3px,transparent 4px);
	    filter:drop-shadow(0 0 8px rgba(255,248,223,.56));
	    animation:spiritTwirlSparkleRing 2.15s ease-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .home-spirit-slot::after{
	    width:92px!important;
	    height:92px!important;
	    top:14%!important;
	    background:radial-gradient(circle,rgba(255,248,223,.42),rgba(186,230,253,.20) 48%,transparent 72%)!important;
	    animation:spiritActionGlow 2.15s ease-out both!important;
	  }

	  /* Twirl correction: keep the spirit rounded while the sparkle ring sells the spin. */
	  @keyframes spiritSoftNoSquishTwirl{
	    0%{
	      transform:translateY(0) rotate(0deg) scale(1);
	      filter:drop-shadow(0 12px 16px rgba(82,55,15,.12));
	    }
	    12%{
	      transform:translateY(2px) rotate(-3deg) scale(1.012);
	    }
	    28%{
	      transform:translateY(-10px) rotate(8deg) scale(1.035);
	      filter:drop-shadow(0 14px 18px rgba(82,55,15,.10)) drop-shadow(0 0 12px rgba(255,248,223,.48));
	    }
	    44%{
	      transform:translateY(-17px) rotate(-10deg) scale(1.045);
	      filter:drop-shadow(0 15px 19px rgba(82,55,15,.10)) drop-shadow(0 0 18px rgba(255,248,223,.74));
	    }
	    60%{
	      transform:translateY(-13px) rotate(9deg) scale(1.036);
	      filter:drop-shadow(0 14px 18px rgba(82,55,15,.10)) drop-shadow(0 0 15px rgba(186,230,253,.34));
	    }
	    76%{
	      transform:translateY(-5px) rotate(-4deg) scale(1.018);
	    }
	    90%{
	      transform:translateY(1px) rotate(2deg) scale(1.01);
	    }
	    100%{
	      transform:translateY(0) rotate(0deg) scale(1);
	      filter:drop-shadow(0 12px 16px rgba(82,55,15,.12));
	    }
	  }
	  @keyframes spiritRobeNoSquishSwirl{
	    0%,100%{transform:skewX(0deg)}
	    28%{transform:skewX(-3deg)}
	    46%{transform:skewX(3deg)}
	    64%{transform:skewX(-2deg)}
	  }
	  .home-room.home-spirit-action-twirl .home-spirit-slot > div{
	    animation:spiritSoftNoSquishTwirl 2.05s cubic-bezier(.2,.78,.22,1) both!important;
	    transform-origin:50% 86%!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-avatar-svg,
	  .home-room.home-spirit-action-twirl .spirit-cute-layer{
	    animation:none!important;
	  }
	  .home-room.home-spirit-action-twirl .spirit-body-plush,
	  .home-room.home-spirit-action-twirl .spirit-robe-highlight{
	    animation:spiritRobeNoSquishSwirl 2.05s ease-in-out both!important;
	    transform-origin:50% 90%!important;
	  }
	  .home-room.home-spirit-action-twirl .home-spirit-slot::before{
	    animation:spiritTwirlSparkleRing 2.05s ease-out both!important;
	  }
	  .home-room.home-spirit-action-twirl .home-spirit-slot::after{
	    animation:spiritActionGlow 2.05s ease-out both!important;
	  }

	  /* PNG spirit twirl: true centered 3D spin with gentle floating. */
	  @keyframes spiritPngTwirl3d{
	    0%{
	      transform:translate3d(0,0,0) rotateY(0deg) scale(1);
	      filter:drop-shadow(0 12px 16px rgba(82,55,15,.12));
	    }
	    12%{
	      transform:translate3d(0,-2px,0) rotateY(0deg) scale(1.015);
	    }
	    35%{
	      transform:translate3d(0,-14px,0) rotateY(120deg) scale(1.02);
	      filter:drop-shadow(0 15px 20px rgba(82,55,15,.12)) drop-shadow(0 0 14px rgba(255,248,223,.56));
	    }
	    62%{
	      transform:translate3d(0,-18px,0) rotateY(260deg) scale(1.02);
	      filter:drop-shadow(0 16px 22px rgba(82,55,15,.10)) drop-shadow(0 0 22px rgba(255,248,223,.86)) drop-shadow(0 0 14px rgba(186,230,253,.34));
	    }
	    84%{
	      transform:translate3d(0,-6px,0) rotateY(360deg) scale(1.012);
	    }
	    100%{
	      transform:translate3d(0,0,0) rotateY(360deg) scale(1);
	      filter:drop-shadow(0 12px 16px rgba(82,55,15,.12));
	    }
	  }
	  .home-spirit-slot{
	    perspective:760px;
	    perspective-origin:50% 48%;
	  }
	  .home-room.home-spirit-action-twirl .home-spirit-slot,
	  .home-spirit-slot.spirit-twirl{
	    perspective:780px!important;
	    transform-style:preserve-3d!important;
	  }
	  .home-room.home-spirit-action-twirl .home-spirit-slot > div,
	  .home-spirit-slot.spirit-twirl > div{
	    animation:spiritPngTwirl3d 2.2s cubic-bezier(.2,.78,.22,1) both!important;
	    transform-origin:50% 50%!important;
	    transform-style:preserve-3d!important;
	    backface-visibility:visible!important;
	    will-change:transform,filter;
	  }
	  .home-room.home-spirit-action-twirl .spirit-avatar-svg,
	  .home-room.home-spirit-action-twirl .spirit-stage-image-wrap,
	  .home-spirit-slot.spirit-twirl .spirit-avatar-svg,
	  .home-spirit-slot.spirit-twirl .spirit-stage-image-wrap{
	    transform-style:preserve-3d!important;
	    backface-visibility:visible!important;
	    animation:none!important;
	  }
	  @media (prefers-reduced-motion:reduce){
	    .home-room.home-spirit-action-twirl .home-spirit-slot > div,
	    .home-spirit-slot.spirit-twirl > div{
	      animation:spiritActionGlow 900ms ease-out both!important;
	      transform:none!important;
	    }
	  }

	  /* Final Faith Flight pass: slower sacred rise, hover, twirl, petals, and soft landing. */
	  @keyframes faithFlightSacredPath{
	    0%{
	      transform:translateX(-50%) translateY(0) scale(1.16);
	      filter:drop-shadow(0 12px 16px rgba(82,55,15,.12));
	    }
	    12%{
	      transform:translateX(-50%) translateY(3px) scale(1.18,.96);
	    }
	    30%{
	      transform:translateX(-50%) translateY(-48px) scale(1.2);
	      filter:drop-shadow(0 16px 18px rgba(82,55,15,.10)) drop-shadow(0 0 18px rgba(255,248,223,.52));
	    }
	    52%{
	      transform:translateX(-50%) translateY(-92px) scale(1.24);
	      filter:drop-shadow(0 18px 20px rgba(82,55,15,.08)) drop-shadow(0 0 28px rgba(255,248,223,.82)) drop-shadow(0 0 24px rgba(186,230,253,.45));
	    }
	    70%{
	      transform:translateX(-50%) translateY(-70px) scale(1.22);
	    }
	    88%{
	      transform:translateX(-50%) translateY(-12px) scale(1.18);
	    }
	    96%{
	      transform:translateX(-50%) translateY(2px) scale(1.19,.97);
	    }
	    100%{
	      transform:translateX(-50%) translateY(0) scale(1.16);
	      filter:drop-shadow(0 12px 16px rgba(82,55,15,.12));
	    }
	  }
	  @keyframes faithFlightSacredAvatar{
	    0%{transform:perspective(760px) translateY(0) rotateY(0deg) rotateZ(0deg) scale(1)}
	    16%{transform:perspective(760px) translateY(-2px) rotateY(0deg) rotateZ(-3deg) scale(1.025,.98)}
	    36%{transform:perspective(760px) translateY(-10px) rotateY(90deg) rotateZ(2deg) scale(.92,1.06)}
	    54%{transform:perspective(760px) translateY(-6px) rotateY(230deg) rotateZ(-1deg) scale(.88,1.08)}
	    68%{transform:perspective(760px) translateY(-4px) rotateY(360deg) rotateZ(2deg) scale(1.04,.99)}
	    86%{transform:perspective(760px) translateY(0) rotateY(360deg) rotateZ(0deg) scale(1)}
	    100%{transform:perspective(760px) translateY(0) rotateY(360deg) rotateZ(0deg) scale(1)}
	  }
	  @keyframes faithFlightBlessingPetals{
	    0%{opacity:0;transform:translate(-50%,2px) scale(.54) rotate(0deg)}
	    18%{opacity:.38}
	    48%{opacity:.95;transform:translate(-50%,-24px) scale(1.08) rotate(126deg)}
	    100%{opacity:0;transform:translate(-50%,76px) scale(1.42) rotate(280deg)}
	  }
	  @keyframes faithFlightHaloBloom{
	    0%{opacity:0;transform:translateX(-50%) scale(.62)}
	    34%{opacity:.8;transform:translateX(-50%) scale(1.08)}
	    74%{opacity:.42;transform:translateX(-50%) scale(1.22)}
	    100%{opacity:0;transform:translateX(-50%) scale(1.34)}
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot{
	    animation:faithFlightSacredPath 5.2s cubic-bezier(.2,.74,.22,1) both!important;
	    transform-origin:50% 92%!important;
	    z-index:80!important;
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot > div{
	    animation:faithFlightSacredAvatar 5.2s cubic-bezier(.22,.76,.24,1) both!important;
	    transform-origin:50% 82%!important;
	    filter:drop-shadow(0 18px 22px rgba(82,55,15,.08)) drop-shadow(0 0 28px rgba(255,248,223,.9)) drop-shadow(0 0 24px rgba(186,230,253,.48))!important;
	    will-change:transform,filter;
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot::before{
	    content:""!important;
	    position:absolute!important;
	    left:50%!important;
	    top:10%!important;
	    width:128px!important;
	    height:128px!important;
	    border-radius:50%!important;
	    z-index:38!important;
	    pointer-events:none!important;
	    background:
	      radial-gradient(circle at 50% 0%,rgba(255,255,255,.95) 0 4px,transparent 5px),
	      radial-gradient(circle at 92% 28%,rgba(255,248,223,.9) 0 4px,transparent 5px),
	      radial-gradient(circle at 88% 72%,rgba(186,230,253,.75) 0 4px,transparent 5px),
	      radial-gradient(circle at 22% 84%,rgba(249,168,212,.72) 0 4px,transparent 5px),
	      radial-gradient(circle at 8% 36%,rgba(255,248,223,.82) 0 3px,transparent 4px);
	    filter:drop-shadow(0 0 10px rgba(255,248,223,.62));
	    animation:faithFlightBlessingPetals 5.2s ease-out both!important;
	  }
	  .home-room.home-faith-flight-active .home-spirit-slot::after{
	    content:""!important;
	    position:absolute!important;
	    left:50%!important;
	    top:5%!important;
	    width:136px!important;
	    height:136px!important;
	    border-radius:50%!important;
	    z-index:37!important;
	    pointer-events:none!important;
	    background:
	      radial-gradient(circle,rgba(255,248,223,.46),rgba(186,230,253,.22) 45%,rgba(249,168,212,.12) 58%,transparent 73%)!important;
	    animation:faithFlightHaloBloom 5.2s ease-out both!important;
	  }
	  @media (max-width:520px){
	    .home-room.home-faith-flight-active .home-spirit-slot{
	      animation:faithFlightSacredPath 5.2s cubic-bezier(.2,.74,.22,1) both!important;
	    }
	    .home-room.home-faith-flight-active .home-spirit-slot::before{
	      width:108px!important;
	      height:108px!important;
	    }
	    .home-room.home-faith-flight-active .home-spirit-slot::after{
	      width:116px!important;
	      height:116px!important;
	    }
	  }


	
	
	
	
	  /* Enhanced Home rainbow: fuller covenant arc with soft premium glow. */
	  .home-room .home-background-rainbow{
	    left:50%!important;
	    top:-8px!important;
	    width:min(142%,760px)!important;
	    height:min(54%,260px)!important;
	    transform:translateX(-50%)!important;
	    z-index:2!important;
	    opacity:.50!important;
	    filter:saturate(1.08) brightness(1.04) drop-shadow(0 0 24px rgba(255,248,223,.55))!important;
	    animation:rainbowBreathe 7.5s ease-in-out infinite!important;
	  }
	  .home-room .home-background-rainbow::before{
	    inset:0!important;
	    border-radius:999px 999px 0 0!important;
	    background:
	      conic-gradient(from 210deg at 50% 100%,
	        transparent 0 12deg,
	        rgba(248,113,113,.56) 12deg 16.5deg,
	        rgba(251,191,36,.54) 16.5deg 21deg,
	        rgba(134,239,172,.50) 21deg 25.5deg,
	        rgba(125,211,252,.52) 25.5deg 30deg,
	        rgba(196,181,253,.50) 30deg 34.5deg,
	        rgba(249,168,212,.46) 34.5deg 39deg,
	        transparent 39deg 360deg)!important;
	    mask:radial-gradient(ellipse at 50% 100%,transparent 0 48%,#000 49% 64%,transparent 65%)!important;
	    -webkit-mask:radial-gradient(ellipse at 50% 100%,transparent 0 48%,#000 49% 64%,transparent 65%)!important;
	  }
	  .home-room .home-background-rainbow::after{
	    display:block!important;
	    content:"✦"!important;
	    left:19%!important;
	    top:55%!important;
	    font-size:16px!important;
	    color:rgba(255,253,246,.92)!important;
	    opacity:.58!important;
	    text-shadow:0 0 14px rgba(255,255,255,.9),0 0 24px rgba(240,203,118,.55)!important;
	    box-shadow:150px -34px 0 -6px rgba(255,253,246,.72),300px 8px 0 -7px rgba(255,253,246,.58)!important;
	  }
	  .home-heavenly-effect.eff_covenant_rainbow{
	    z-index:33!important;
	  }
	  .home-heavenly-effect.eff_covenant_rainbow .effect-rainbow{
	    left:-18%!important;
	    right:-18%!important;
	    top:2%!important;
	    height:74%!important;
	    opacity:.78!important;
	    border-top:18px solid rgba(248,113,113,.28)!important;
	    border-left:18px solid transparent!important;
	    border-right:18px solid transparent!important;
	    box-shadow:
	      0 -15px 0 rgba(251,191,36,.25),
	      0 -30px 0 rgba(134,239,172,.22),
	      0 -45px 0 rgba(125,211,252,.22),
	      0 -60px 0 rgba(196,181,253,.19),
	      0 -75px 0 rgba(249,168,212,.16),
	      0 22px 48px rgba(255,255,255,.28)!important;
	    filter:blur(.25px) saturate(1.08)!important;
	    animation:effectRainbowBreathe 8s ease-in-out infinite!important;
	  }
	  @media(max-width:560px){
	    .home-room .home-background-rainbow{
	      top:4px!important;
	      width:160%!important;
	      height:42%!important;
	      opacity:.46!important;
	    }
	    .home-room .home-background-rainbow::after{
	      opacity:.42!important;
	      box-shadow:112px -25px 0 -7px rgba(255,253,246,.62),224px 6px 0 -8px rgba(255,253,246,.48)!important;
	    }
	    .home-heavenly-effect.eff_covenant_rainbow .effect-rainbow{
	      left:-34%!important;
	      right:-34%!important;
	      top:4%!important;
	      height:62%!important;
	      border-top-width:14px!important;
	      border-left-width:14px!important;
	      border-right-width:14px!important;
	      box-shadow:0 -12px 0 rgba(251,191,36,.23),0 -24px 0 rgba(134,239,172,.20),0 -36px 0 rgba(125,211,252,.20),0 -48px 0 rgba(196,181,253,.17),0 -60px 0 rgba(249,168,212,.14),0 18px 38px rgba(255,255,255,.24)!important;
	    }
	  }

  /* Journal background cleanup and enhanced Home Glory Rays. */
	  .premium-journal-page{background:transparent!important;}
	  .premium-journal-page::before{display:none!important;content:none!important;background:none!important;}
	  .home-heavenly-effect.eff_glory_rays{
	    z-index:34!important;
	    mix-blend-mode:screen;
	  }
	  .home-heavenly-effect.eff_glory_rays::before{
	    content:"";
	    position:absolute;
	    left:50%;
	    top:43%;
	    width:min(54%,250px);
	    height:min(54%,250px);
	    transform:translate(-50%,-50%);
	    border-radius:50%;
	    background:
	      radial-gradient(circle,rgba(255,255,255,.54) 0%,rgba(255,245,190,.32) 34%,rgba(250,204,21,.13) 58%,transparent 74%);
	    filter:blur(.3px);
	    opacity:.78;
	    animation:effectRayHalo 5.8s ease-in-out infinite;
	  }
	  .home-heavenly-effect.eff_glory_rays::after{
	    content:"";
	    position:absolute;
	    left:50%;
	    top:17%;
	    width:7px;
	    height:7px;
	    transform:translateX(-50%) rotate(45deg);
	    border-radius:2px;
	    background:rgba(255,255,255,.98);
	    box-shadow:0 0 12px rgba(255,255,255,.95),0 0 25px rgba(250,204,21,.46),-44px 58px 0 -2px rgba(255,255,255,.74),43px 66px 0 -2px rgba(255,248,223,.70);
	    opacity:.9;
	    animation:effectRaySpark 4.8s ease-in-out infinite;
	  }
	  .home-heavenly-effect.eff_glory_rays .effect-ray{
	    left:50%!important;
	    top:12%!important;
	    width:34px!important;
	    height:210px!important;
	    border-radius:999px!important;
	    background:linear-gradient(180deg,rgba(255,255,255,.84),rgba(255,246,198,.48) 28%,rgba(250,204,21,.18) 58%,transparent 100%)!important;
	    filter:blur(1.2px)!important;
	    opacity:.70!important;
	    transform-origin:50% 100%!important;
	    animation:effectRayPulse 5.6s ease-in-out infinite!important;
	  }
	  .home-heavenly-effect.eff_glory_rays .effect-ray.r1{transform:translateX(-50%) rotate(0deg)!important;}
	  .home-heavenly-effect.eff_glory_rays .effect-ray.r2{transform:translateX(-50%) rotate(19deg)!important;animation-delay:.7s!important;}
	  .home-heavenly-effect.eff_glory_rays .effect-ray.r3{transform:translateX(-50%) rotate(-19deg)!important;animation-delay:1.15s!important;}
	  @keyframes effectRayHalo{0%,100%{opacity:.62;transform:translate(-50%,-50%) scale(.96);}50%{opacity:.88;transform:translate(-50%,-50%) scale(1.04);}}
	  @keyframes effectRaySpark{0%,100%{opacity:.62;filter:blur(0);}50%{opacity:1;filter:blur(.2px);}}
	  @media(max-width:560px){
	    .home-heavenly-effect.eff_glory_rays .effect-ray{height:178px!important;width:28px!important;top:14%!important;}
	    .home-heavenly-effect.eff_glory_rays::before{width:62%;height:42%;top:43%;}
	  }

  /* Page background cleanup: let Growth, Home, and Testimony share the app background. */
	  .growth-page-calm,
	  .home-page,
	  .testimony-garden-page{
	    background:transparent!important;
	  }
	  .growth-page-calm::before,
	  .home-page::before,
	  .testimony-garden-page::before,
	  .testimony-garden-page::after{
	    display:none!important;
	    content:none!important;
	    background:none!important;
	  }

  /* Journal premium calm pass: cohesive with Today, Prayer, Growth, and Home. */
	  .premium-journal-page{position:relative!important;gap:14px!important;isolation:isolate;}
	  .premium-journal-page::before{content:"";position:absolute;inset:-24px -16px -30px;z-index:-1;pointer-events:none;background:radial-gradient(circle at 12% 0%,rgba(255,248,223,.72),transparent 32%),radial-gradient(circle at 94% 18%,rgba(246,232,255,.36),transparent 32%),radial-gradient(circle at 50% 84%,rgba(236,253,245,.26),transparent 42%);opacity:.92;}
	  .premium-journal-page .journal-write-card,.premium-journal-page .journal-history-card{border:1px solid rgba(214,181,111,.22)!important;border-radius:28px!important;background:linear-gradient(145deg,rgba(255,255,255,.66),rgba(255,251,241,.52) 58%,rgba(246,241,255,.18))!important;box-shadow:0 18px 46px rgba(82,55,15,.055),inset 0 1px 0 rgba(255,255,255,.82)!important;backdrop-filter:blur(14px) saturate(1.04);}
	  .premium-journal-page .journal-write-card{background:radial-gradient(circle at 92% 0%,rgba(255,232,170,.34),transparent 34%),radial-gradient(circle at 0% 100%,rgba(232,244,255,.34),transparent 38%),linear-gradient(145deg,rgba(255,255,255,.70),rgba(255,250,236,.56))!important;}
	  .premium-journal-page h2.serif{font-family:'Cormorant Garamond',Georgia,serif!important;font-size:20px!important;font-weight:600!important;letter-spacing:.005em!important;color:#3f2a0b!important;}
	  .premium-journal-page p{color:#5b4624!important;font-weight:500!important;}
	  .premium-journal-page .sg-textarea{border:1px solid rgba(214,181,111,.24)!important;border-radius:24px!important;background:rgba(255,253,248,.72)!important;box-shadow:inset 0 1px 0 rgba(255,255,255,.82),0 10px 28px rgba(82,55,15,.04)!important;color:#3f2a0b!important;font-size:14px!important;line-height:1.62!important;}
	  .premium-journal-page .sg-textarea::placeholder{color:rgba(91,70,36,.58)!important;}
	  .premium-journal-page .gold-btn,.premium-journal-page button[class*="gold"]{border-radius:999px!important;font-weight:650!important;box-shadow:0 10px 24px rgba(201,144,18,.12),inset 0 1px 0 rgba(255,255,255,.78)!important;}
	  .premium-journal-page .empty-state{border:1px solid rgba(91,143,184,.20)!important;border-radius:22px!important;background:rgba(241,249,255,.54)!important;box-shadow:none!important;}
	  .premium-journal-page .empty-state-title{font-weight:600!important;color:#5b4624!important;}
	  .premium-journal-page .month-scroll-list{display:flex!important;flex-direction:column!important;gap:10px!important;}
	  .premium-journal-page .month-group{border-radius:24px!important;overflow:hidden!important;}
	  .premium-journal-page .month-toggle{border:1px solid rgba(214,181,111,.22)!important;border-radius:22px!important;background:rgba(255,253,248,.68)!important;color:#3f2a0b!important;font-weight:600!important;box-shadow:0 8px 20px rgba(82,55,15,.04)!important;}
	  .premium-journal-page .placed-chip{background:rgba(241,249,255,.70)!important;border:1px solid rgba(91,143,184,.22)!important;color:#4f6e88!important;font-weight:600!important;}
	  .premium-journal-page .month-items{margin-top:8px!important;display:flex!important;flex-direction:column!important;gap:10px!important;}
	  .premium-journal-page .journal-entry-card{border:1px solid rgba(214,181,111,.18)!important;border-radius:22px!important;background:rgba(255,255,255,.52)!important;box-shadow:0 10px 24px rgba(82,55,15,.045),inset 0 1px 0 rgba(255,255,255,.72)!important;}
	  .premium-journal-page .journal-entry-date{color:#9b835e!important;font-size:12px!important;font-weight:600!important;letter-spacing:.02em!important;}
	  .premium-journal-page .journal-entry-text{margin-top:8px!important;font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;color:#4f3a1c!important;font-style:normal!important;font-weight:500!important;line-height:1.65!important;}
	  .premium-journal-page .journal-edit-btn,.premium-journal-page .journal-delete-btn{border-radius:999px!important;font-weight:600!important;box-shadow:none!important;}
	  .premium-journal-page .journal-edit-btn{background:rgba(255,248,223,.70)!important;border-color:rgba(214,181,111,.32)!important;color:#7c5817!important;}
	  .premium-journal-page .journal-view-btn{border-radius:999px!important;font-weight:650!important;box-shadow:none!important;background:rgba(241,249,255,.72)!important;border:1px solid rgba(91,143,184,.24)!important;color:#4f6e88!important;}
	  .premium-journal-page .journal-delete-btn{background:rgba(255,255,255,.54)!important;border-color:rgba(214,181,111,.20)!important;color:#7b6b55!important;font-weight:600!important;}
	  .premium-journal-page .journal-viewer-page{display:flex!important;flex-direction:column!important;gap:14px!important;animation:fadeUp .24s ease-out;}
	  .premium-journal-page .journal-back-btn{align-self:flex-start;border:1px solid rgba(214,181,111,.20);border-radius:999px;background:rgba(255,253,248,.66);color:#5b4624;font-family:'Nunito',sans-serif;font-size:13px;font-weight:650;padding:10px 14px;cursor:pointer;box-shadow:0 8px 20px rgba(82,55,15,.04);}
	  .premium-journal-page .journal-view-entry-card{padding:24px!important;border:1px solid rgba(214,181,111,.20)!important;border-radius:28px!important;background:radial-gradient(circle at 92% 0%,rgba(255,248,223,.42),transparent 34%),linear-gradient(145deg,rgba(255,255,255,.72),rgba(255,251,241,.56))!important;box-shadow:0 18px 46px rgba(82,55,15,.055),inset 0 1px 0 rgba(255,255,255,.82)!important;}
	  .premium-journal-page .journal-view-entry-card h2{margin:4px 0 18px!important;font-family:'Cormorant Garamond',Georgia,serif!important;font-size:24px!important;font-weight:600!important;line-height:1.08!important;color:#3f2a0b!important;}
	  .premium-journal-page .journal-view-full-text{white-space:pre-wrap;font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#4f3a1c;font-size:15px;font-weight:500;line-height:1.74;}
	  .premium-journal-page .journal-view-actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:22px;padding-top:16px;border-top:1px solid rgba(214,181,111,.16);}
	  .premium-journal-page .journal-view-delete-btn{width:auto!important;min-height:38px!important;padding:8px 14px!important;font-size:13px!important;line-height:1!important;}
	  @media(max-width:560px){.premium-journal-page{gap:11px!important;}.premium-journal-page::before{inset:-18px -10px -26px;}.premium-journal-page .journal-write-card,.premium-journal-page .journal-history-card{border-radius:23px!important;padding:14px!important;}.premium-journal-page h2.serif{font-size:18px!important;}.premium-journal-page .sg-textarea{min-height:170px!important;font-size:13.5px!important;border-radius:20px!important;}.premium-journal-page .journal-entry-card{padding:12px!important;}.premium-journal-page .journal-view-btn,.premium-journal-page .journal-edit-btn{padding:7px 10px!important;font-size:12.5px!important;}.premium-journal-page .journal-delete-btn{width:34px!important;min-height:34px!important;font-size:17px!important;}.premium-journal-page .journal-view-entry-card{padding:20px!important;border-radius:24px!important;}.premium-journal-page .journal-view-entry-card h2{font-size:22px!important;}.premium-journal-page .journal-view-full-text{font-size:14.5px!important;line-height:1.72!important;}.premium-journal-page .journal-view-delete-btn{width:auto!important;min-height:36px!important;font-size:12.5px!important;}}

  /* Home premium sanctuary pass: softer hierarchy, clearer preview focus, calmer shop. */
	  .home-page{
	    position:relative!important;
	    gap:14px!important;
	    isolation:isolate;
	  }
	  .home-page::before{
	    content:"";
	    position:absolute;
	    inset:-26px -16px -34px;
	    z-index:-1;
	    pointer-events:none;
	    background:
	      radial-gradient(circle at 20% 2%,rgba(255,248,223,.78),transparent 32%),
	      radial-gradient(circle at 88% 18%,rgba(236,232,255,.42),transparent 30%),
	      radial-gradient(circle at 50% 78%,rgba(236,253,245,.30),transparent 42%);
	    opacity:.9;
	  }
	  .home-page .home-hero-card,
	  .home-page .home-care-card,
	  .home-page .home-preview-card,
	  .home-page .home-shop-shell-card{
	    border:1px solid rgba(214,181,111,.22)!important;
	    border-radius:28px!important;
	    background:
	      linear-gradient(145deg,rgba(255,255,255,.60),rgba(255,251,241,.50) 58%,rgba(246,241,255,.18))!important;
	    box-shadow:
	      0 18px 46px rgba(82,55,15,.055),
	      inset 0 1px 0 rgba(255,255,255,.82)!important;
	    backdrop-filter:blur(14px) saturate(1.04);
	  }
	  .home-page .home-hero-card{
	    padding:17px!important;
	    background:
	      radial-gradient(circle at 92% 0%,rgba(255,232,170,.40),transparent 34%),
	      radial-gradient(circle at 0% 100%,rgba(232,244,255,.38),transparent 38%),
	      linear-gradient(145deg,rgba(255,255,255,.68),rgba(255,250,236,.54))!important;
	  }
	  .home-page .home-hero-card h2,
	  .home-page .home-care-card h3,
	  .home-page .home-preview-card h3,
	  .home-page .home-shop-shell-card h3{
	    color:#3f2a0b!important;
	    font-weight:650!important;
	    letter-spacing:0!important;
	  }
	  .home-page .home-hero-card h2{font-size:21px!important;}
	  .home-page .home-care-card h3{font-size:21px!important;}
	  .home-page .home-preview-card h3,
	  .home-page .home-shop-shell-card h3{font-size:18px!important;}
	  .home-page .home-hero-card p,
	  .home-page .home-care-card p,
	  .home-page .home-preview-card p,
	  .home-page .home-shop-shell-card p{
	    color:#5b4624!important;
	    font-weight:600!important;
	  }
	  .home-page .home-righteousness-count{
	    min-width:82px;
	    padding:8px 10px;
	    border:1px solid rgba(214,181,111,.20);
	    border-radius:18px;
	    background:rgba(255,253,248,.68);
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.74);
	  }
	  .home-page .home-righteousness-count > div:first-child{
	    color:#8d681e!important;
	    font-size:10.5px!important;
	    letter-spacing:.11em!important;
	    font-weight:700!important;
	  }
	  .home-page .home-righteousness-value{
	    color:#3f2a0b!important;
	    font-size:20px!important;
	    font-weight:800!important;
	  }
	  .home-page .home-care-card{
	    padding:16px!important;
	    background:
	      radial-gradient(circle at 86% 6%,rgba(255,248,223,.52),transparent 34%),
	      linear-gradient(145deg,rgba(255,255,255,.64),rgba(246,251,255,.46),rgba(245,255,248,.24))!important;
	  }
	  .home-page .home-care-card .section-label{
	    color:#8d681e!important;
	    font-weight:750!important;
	    letter-spacing:.08em!important;
	  }
	  .home-growth-line{
	    display:inline-flex!important;
	    align-items:center!important;
	    width:auto!important;
	    margin:10px 0 0!important;
	    padding:6px 10px!important;
	    border:1px solid rgba(214,181,111,.20)!important;
	    border-radius:999px!important;
	    background:rgba(255,253,248,.72)!important;
	    color:#6e4d10!important;
	    font-size:12.5px!important;
	    font-weight:650!important;
	    line-height:1.25!important;
	  }
	  .home-page .faith-flight-panel{
	    border:1px solid rgba(214,181,111,.24)!important;
	    border-radius:22px!important;
	    background:
	      radial-gradient(circle at 100% 0%,rgba(255,248,223,.52),transparent 38%),
	      rgba(255,253,248,.64)!important;
	    box-shadow:0 10px 26px rgba(82,55,15,.045)!important;
	  }
	  .home-page .faith-flight-title{
	    color:#4f3710!important;
	    font-weight:750!important;
	  }
	  .home-page .faith-flight-copy{
	    color:#6f5a35!important;
	    font-weight:600!important;
	  }
	  .home-page .faith-flight-btn{
	    background:linear-gradient(135deg,#fff5cf,#eec45c)!important;
	    border-color:rgba(201,144,18,.46)!important;
	    box-shadow:0 8px 20px rgba(201,144,18,.16),inset 0 1px 0 rgba(255,255,255,.72)!important;
	  }
	  .home-page .faith-flight-lock{
	    background:rgba(241,249,255,.68)!important;
	    border-color:rgba(91,143,184,.20)!important;
	    color:#52728d!important;
	  }
	  .home-visitors-note{
	    border-radius:20px!important;
	    background:rgba(255,253,248,.62)!important;
	    border:1px solid rgba(214,181,111,.18)!important;
	  }
	  .home-visitors-toggle{
	    color:#4f3710!important;
	    font-weight:650!important;
	  }
	  .home-page .home-preview-card{
	    padding:12px!important;
	    overflow:hidden;
	  }
	  .home-page .sticky-preview-label{
	    background:rgba(255,248,223,.76)!important;
	    border-color:rgba(214,181,111,.28)!important;
	    color:#7c5817!important;
	    font-weight:700!important;
	  }
	  .home-page .home-room{
	    min-height:390px!important;
	    border:0!important;
	    border-radius:30px!important;
	    box-shadow:
	      inset 0 1px 0 rgba(255,255,255,.72),
	      inset 0 -38px 62px rgba(82,55,15,.055),
	      0 18px 44px rgba(65,95,125,.12)!important;
	  }
	  .home-page .home-room::after{
	    content:"";
	    position:absolute;
	    inset:0;
	    z-index:70;
	    pointer-events:none;
	    border-radius:inherit;
	    box-shadow:
	      inset 0 0 0 1px rgba(255,255,255,.34),
	      inset 0 -34px 48px rgba(255,248,223,.12);
	  }
	  .home-page .home-shop-shell-card{
	    padding:15px!important;
	  }
	  .home-page .home-shop-category-row{
	    gap:10px!important;
	    padding:2px 0 8px!important;
	    scroll-snap-type:x proximity;
	  }
	  .home-page .home-category-card{
	    min-width:108px!important;
	    border:1px solid rgba(214,181,111,.20)!important;
	    border-radius:22px!important;
	    background:rgba(255,255,255,.46)!important;
	    box-shadow:0 10px 24px rgba(82,55,15,.045),inset 0 1px 0 rgba(255,255,255,.72)!important;
	    scroll-snap-align:start;
	  }
	  .home-page .home-category-card.active{
	    border-color:rgba(201,144,18,.52)!important;
	    background:
	      radial-gradient(circle at 50% 0%,rgba(255,248,223,.70),transparent 48%),
	      rgba(255,253,248,.76)!important;
	    box-shadow:0 13px 28px rgba(201,144,18,.12),inset 0 1px 0 rgba(255,255,255,.82)!important;
	  }
	  .home-page .home-category-card .meaning-chip,
	  .home-page .home-shop-card .meaning-chip{
	    border:1px solid rgba(91,143,184,.20)!important;
	    background:rgba(241,249,255,.64)!important;
	    color:#4f6e88!important;
	    font-weight:650!important;
	  }
	  .home-page .home-shop-items-grid{
	    gap:11px!important;
	  }
	  .home-page .home-shop-card{
	    border:1px solid rgba(214,181,111,.16)!important;
	    border-radius:24px!important;
	    background:rgba(255,255,255,.44)!important;
	    box-shadow:0 12px 28px rgba(82,55,15,.052),inset 0 1px 0 rgba(255,255,255,.68)!important;
	  }
	  .home-page .home-shop-card.owned{
	    border-color:rgba(201,144,18,.42)!important;
	    background:linear-gradient(160deg,rgba(255,255,255,.52),rgba(255,248,223,.34))!important;
	  }
	  .home-page .home-shop-card button,
	  .home-page .soft-action-btn{
	    border-radius:999px!important;
	    font-weight:750!important;
	    box-shadow:0 6px 16px rgba(82,55,15,.055)!important;
	  }
	  @media (max-width:560px){
	    .home-page{
	      gap:11px!important;
	    }
	    .home-page::before{
	      inset:-18px -10px -26px;
	    }
	    .home-page .home-hero-card,
	    .home-page .home-care-card,
	    .home-page .home-preview-card,
	    .home-page .home-shop-shell-card{
	      border-radius:23px!important;
	      padding:13px!important;
	    }
	    .home-page .home-hero-card h2,
	    .home-page .home-care-card h3{
	      font-size:17px!important;
	    }
	    .home-page .home-preview-card h3,
	    .home-page .home-shop-shell-card h3{
	      font-size:16px!important;
	    }
	    .home-page .home-hero-card p,
	    .home-page .home-care-card p,
	    .home-page .home-preview-card p,
	    .home-page .home-shop-shell-card p{
	      font-size:12.5px!important;
	      line-height:1.42!important;
	    }
	    .home-page .home-righteousness-count{
	      min-width:66px!important;
	      padding:7px 8px!important;
	    }
	    .home-page .home-righteousness-value{
	      font-size:16.5px!important;
	    }
	    .home-growth-line{
	      max-width:100%;
	      white-space:normal!important;
	      font-size:12px!important;
	    }
	    .home-page .home-room{
	      min-height:330px!important;
	      border-radius:24px!important;
	    }
	    .home-page .home-category-card{
	      min-width:94px!important;
	      max-width:104px!important;
	      border-radius:19px!important;
	      padding:9px 7px!important;
	    }
	    .home-page .home-shop-items-grid{
	      grid-template-columns:repeat(2,minmax(0,1fr))!important;
	      gap:9px!important;
	    }
	    .home-page .home-shop-card{
	      border-radius:20px!important;
	      padding:10px 8px!important;
	    }
	  }

	
	  /* Home typography calm pass: remove chunky weights from Home only. */
	  .home-page,
	  .home-page button,
	  .home-page input,
	  .home-page select,
	  .home-page textarea{
	    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
	  }
	  .home-page .serif{
	    font-family:'Cormorant Garamond',Georgia,serif!important;
	    font-weight:600!important;
	    letter-spacing:.005em!important;
	  }
	  .home-page .home-hero-card h2,
	  .home-page .home-care-card h3,
	  .home-page .home-preview-card h3,
	  .home-page .home-shop-shell-card h3{
	    color:#3f2a0b!important;
	    font-weight:600!important;
	    letter-spacing:.005em!important;
	    line-height:1.18!important;
	  }
	  .home-page .home-hero-card h2,
	  .home-page .home-care-card h3{font-size:19px!important;}
	  .home-page .home-preview-card h3,
	  .home-page .home-shop-shell-card h3{font-size:17px!important;}
	  .home-page .home-hero-card p,
	  .home-page .home-care-card p,
	  .home-page .home-preview-card p,
	  .home-page .home-shop-shell-card p,
	  .home-page .faith-flight-copy,
	  .home-page .empty-state{
	    color:#5b4624!important;
	    font-weight:500!important;
	  }
	  .home-page .section-label,
	  .home-page .home-care-card .section-label,
	  .home-page .home-righteousness-count > div:first-child{
	    font-weight:600!important;
	    letter-spacing:.09em!important;
	  }
	  .home-page .home-righteousness-value,
	  .home-page .faith-flight-title,
	  .home-growth-line,
	  .home-visitors-toggle,
	  .home-page .sticky-preview-label,
	  .home-page .placed-chip,
	  .home-page .meaning-chip{
	    font-weight:600!important;
	  }
	  .home-page .home-category-card,
	  .home-page .home-category-card *,
	  .home-page .home-shop-card,
	  .home-page .home-shop-card *{
	    font-weight:600!important;
	  }
	  .home-page .home-shop-card p,
	  .home-page .home-shop-card .shop-card-desc,
	  .home-page .home-category-card .meaning-chip,
	  .home-page .home-shop-card .meaning-chip{
	    font-weight:500!important;
	  }
	  .home-page .home-shop-card button,
	  .home-page .home-category-card,
	  .home-page .soft-action-btn,
	  .home-page .gold-btn{
	    font-weight:650!important;
	  }
	  @media(max-width:560px){
	    .home-page .home-hero-card h2,
	    .home-page .home-care-card h3{font-size:16.5px!important;}
	    .home-page .home-preview-card h3,
	    .home-page .home-shop-shell-card h3{font-size:15.5px!important;}
	    .home-page .home-shop-card,
	    .home-page .home-category-card{font-size:12.5px!important;}
	    .home-page .home-shop-card button,
	    .home-page .soft-action-btn{font-weight:600!important;}
	  }

  /* Home upgrade modal typography: soft, readable, not chunky. */
  .home-upgrade-card .home-upgrade-message{
    font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-weight:560!important;
    letter-spacing:.005em!important;
  }
  .home-upgrade-card button{
    font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-weight:650!important;
    letter-spacing:.01em!important;
  }
  @media (max-width:520px){
    .home-upgrade-card{
      padding:22px 20px!important;
      border-radius:26px!important;
    }
    .home-upgrade-card h2{
      font-size:23px!important;
    }
    .home-upgrade-card .home-upgrade-message{
      font-size:14.5px!important;
      line-height:1.55!important;
    }
  }

  /* Home sanctuary polish: rounded, layered homes that stay behind the spirit. */
  .home-room .home-cardboard,
  .home-room .home-little-shelter,
  .home-room .home-house,
  .home-room .home-mansion,
  .home-room .home-palace,
  .home-room .home-chapel{
    z-index:5!important;
    filter:drop-shadow(0 16px 20px rgba(98,70,24,.14)) drop-shadow(0 0 16px rgba(255,248,223,.25))!important;
  }
  .home-room .home-spirit-slot{
    z-index:40!important;
  }
  .home-cardboard-body,
  .home-little-shelter-body,
  .home-house-body,
  .home-mansion-body,
  .home-palace-body,
  .home-chapel-body{
    border-radius:28px 28px 13px 13px!important;
    background:
      radial-gradient(circle at 22% 18%,rgba(255,255,255,.72),transparent 18%),
      radial-gradient(circle at 78% 20%,rgba(255,248,223,.42),transparent 22%),
      linear-gradient(180deg,#fffdf8 0%,#fff2cf 52%,#dfb65b 100%)!important;
    box-shadow:
      inset 0 10px 18px rgba(255,255,255,.74),
      inset 0 -10px 16px rgba(138,90,8,.08),
      0 0 22px rgba(240,203,118,.22)!important;
  }
  .home-cardboard-body{
    border-radius:22px 22px 12px 12px!important;
    background:
      linear-gradient(135deg,rgba(255,248,223,.24),transparent 34%),
      linear-gradient(180deg,#f0cc8a 0%,#d2954d 58%,#b87534 100%)!important;
  }
  .home-cardboard-roof,
  .home-little-shelter-roof,
  .home-house-roof,
  .home-mansion-roof,
  .home-palace-roof,
  .home-chapel-roof,
  .home-chapel-steeple,
  .home-tower::before{
    filter:drop-shadow(0 6px 8px rgba(138,90,8,.14)) drop-shadow(0 0 13px rgba(255,248,223,.42))!important;
  }
  .home-cardboard-roof{
    clip-path:polygon(8% 100%,18% 58%,50% 8%,82% 58%,92% 100%)!important;
    border-radius:18px 18px 8px 8px!important;
  }
  .home-little-shelter-roof,
  .home-house-roof{
    clip-path:polygon(3% 100%,14% 60%,50% 0,86% 60%,97% 100%)!important;
    border-radius:24px 24px 9px 9px!important;
  }
  .home-mansion-roof{
    border-radius:22px 22px 8px 8px!important;
    clip-path:polygon(0 100%,8% 60%,19% 100%,31% 55%,42% 100%,50% 7%,58% 100%,69% 55%,81% 100%,92% 60%,100% 100%)!important;
  }
  .home-palace-roof{
    border-radius:24px 24px 9px 9px!important;
  }
  .home-cardboard-roof::after,
  .home-little-shelter-roof::after,
  .home-house-roof::after,
  .home-mansion-roof::after,
  .home-palace-roof::after,
  .home-chapel-roof::after{
    content:"";
    position:absolute;
    left:13%;
    right:13%;
    bottom:9px;
    height:6px;
    border-radius:999px;
    background:linear-gradient(90deg,transparent,rgba(255,253,246,.72),transparent);
    opacity:.76;
  }
  .home-cardboard-door,
  .home-little-shelter-door,
  .home-house-door,
  .home-mansion-door,
  .home-door,
  .home-chapel-door{
    border-radius:999px 999px 8px 8px!important;
    background:radial-gradient(circle at 50% 16%,#fff 0,#fff8df 34%,#d69d29 72%,#8a5a08 100%)!important;
    box-shadow:0 0 22px rgba(255,248,223,.70),inset 0 4px 8px rgba(255,255,255,.58)!important;
  }
  .home-window-dot,
  .home-little-shelter-window,
  .home-mansion-window,
  .home-chapel-window{
    border-radius:999px 999px 6px 6px!important;
    background:radial-gradient(circle at 50% 26%,#fff 0,#fff8df 42%,rgba(240,203,118,.55) 100%)!important;
    box-shadow:0 0 13px rgba(255,248,223,.72),inset 0 2px 5px rgba(255,255,255,.85)!important;
  }
  .home-house-body::after,
  .home-little-shelter-body::after,
  .home-mansion-body::after,
  .home-chapel-body::after{
    content:"";
    position:absolute;
    left:14%;
    right:14%;
    bottom:8px;
    height:9px;
    border-radius:999px;
    background:linear-gradient(90deg,transparent,rgba(255,248,223,.58),transparent);
    opacity:.64;
  }
  .home-mansion-column,
  .home-palace-column,
  .home-tower{
    border-radius:999px 999px 12px 12px!important;
    box-shadow:inset 5px 0 7px rgba(255,255,255,.48),inset -4px 0 8px rgba(138,90,8,.08)!important;
  }
  @media (max-width:520px){
    .home-room .home-cardboard,
    .home-room .home-little-shelter,
    .home-room .home-house,
    .home-room .home-mansion,
    .home-room .home-palace,
    .home-room .home-chapel{
      transform:translateX(-50%) scale(.94)!important;
    }
    .home-room .home-spirit-slot{
      z-index:40!important;
    }
  }

  /* Top utility chips: keep Quiet, Sound, Friends, and Refresh calm instead of chunky. */
  .top-soft-chip{
    font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-weight:560!important;
    letter-spacing:.01em!important;
    line-height:1!important;
    text-transform:none!important;
  }
  .top-soft-chip span{
    font-weight:560!important;
  }
  .top-soft-chip.friends-top-chip{
    gap:5px!important;
  }
  .top-soft-chip.friends-top-chip span:last-child:not(:first-child),
  .top-soft-chip.friends-top-chip [style*="font-weight: 900"]{
    font-weight:600!important;
  }
  .top-soft-chip.refresh-chip{
    font-weight:560!important;
    color:#6f5525!important;
  }
  @media (max-width:520px){
    .top-soft-chip{
      font-size:12px!important;
      padding:6px 9px!important;
      min-height:32px!important;
    }
    .top-soft-chip.refresh-chip{
      font-size:12px!important;
      padding:7px 10px!important;
    }
  }



  /* Premium app header refinement */
  .app-shell .app-header{
    padding:18px 20px 14px!important;
    background:linear-gradient(180deg,#FFFDFB 0%,#F8F3EB 100%)!important;
    border-bottom:1px solid rgba(80,60,30,.06)!important;
    box-shadow:0 1px 8px rgba(0,0,0,.025)!important;
    align-items:center!important;
    gap:14px!important;
  }
  .app-header .app-title-copy{
    display:flex!important;
    flex-direction:column!important;
    gap:2px!important;
  }
  .app-header .app-title-copy > div:first-child{
    color:#6f5525!important;
    font-size:12px!important;
    font-weight:680!important;
    letter-spacing:2.7px!important;
    line-height:1.1!important;
    margin:0 0 1px!important;
  }
  .app-header .app-title-copy p{
    color:#8b7b69!important;
    font-size:12px!important;
    font-weight:460!important;
    letter-spacing:.01em!important;
    line-height:1.25!important;
    margin:0!important;
  }
  .app-header .app-title-copy h1.serif{
    color:#7d5817!important;
    font-size:25px!important;
    font-weight:600!important;
    letter-spacing:.01em!important;
    line-height:1.04!important;
    margin:1px 0 0!important;
  }
  .app-header > div:last-child{
    gap:7px!important;
  }
  .app-header > div:last-child > div{
    gap:7px!important;
  }
  .app-header .top-soft-chip{
    min-height:36px!important;
    padding:7px 12px!important;
    border-radius:999px!important;
    font-size:12.5px!important;
    font-weight:560!important;
    color:#4f3b1d!important;
    box-shadow:0 3px 10px rgba(94,73,38,.045)!important;
    transition:transform .18s ease, box-shadow .18s ease, background .18s ease, border-color .18s ease!important;
  }
  .app-header .top-soft-chip:hover{
    transform:translateY(-1px);
    box-shadow:0 6px 14px rgba(94,73,38,.075)!important;
  }
  .app-header .top-soft-chip:active{
    transform:scale(.98);
  }
  .app-header .friends-top-chip{
    min-height:36px!important;
    padding:7px 12px!important;
  }
  .app-header .friends-top-chip span[aria-hidden="true"]{
    font-size:14px!important;
    line-height:1!important;
  }
  @media(max-width:520px){
    .app-shell .app-header{
      padding:14px 14px 11px!important;
      gap:10px!important;
      align-items:flex-start!important;
    }
    .app-header .app-title-copy > div:first-child{
      font-size:10.5px!important;
      letter-spacing:2.1px!important;
    }
    .app-header .app-title-copy p{
      font-size:11.5px!important;
      max-width:none!important;
    }
    .app-header .app-title-copy h1.serif{
      font-size:22px!important;
      max-width:260px!important;
    }
    .app-header > div:last-child{
      max-width:148px!important;
      gap:5px!important;
    }
    .app-header > div:last-child > div{
      gap:5px!important;
    }
    .app-header .top-soft-chip,
    .app-header .friends-top-chip{
      min-height:34px!important;
      padding:6px 10px!important;
      font-size:12px!important;
    }
  }

  /* Home path item visibility: make placed Path features distinct in the live scene. */
  .home-room.home-floor-fl_gold_path .home-path{
    background:
      radial-gradient(circle at 48% 18%,rgba(255,255,255,.82) 0 5px,transparent 6px),
      radial-gradient(circle at 42% 42%,rgba(255,248,223,.78) 0 6px,transparent 7px),
      radial-gradient(circle at 58% 66%,rgba(214,162,26,.34) 0 7px,transparent 8px),
      linear-gradient(180deg,#fff7d6 0%,#f7d778 48%,#d6a21a 100%)!important;
    box-shadow:inset 0 0 26px rgba(255,255,255,.72),0 0 18px rgba(240,203,118,.28)!important;
  }
  .home-room.home-floor-fl_gold_path .home-path::after{
    background:
      radial-gradient(circle at 50% 15%,rgba(255,255,255,.72) 0 5px,transparent 6px),
      radial-gradient(circle at 47% 35%,rgba(255,255,255,.58) 0 7px,transparent 8px),
      radial-gradient(circle at 54% 58%,rgba(255,248,223,.55) 0 8px,transparent 9px),
      radial-gradient(circle at 45% 78%,rgba(201,144,18,.22) 0 8px,transparent 9px),
      linear-gradient(90deg,rgba(255,255,255,.20),transparent 18% 82%,rgba(138,90,8,.10))!important;
    opacity:.9!important;
  }
  .home-room.home-floor-fl_marble_steps .home-path{
    background:linear-gradient(180deg,#ffffff 0%,#fffaf0 44%,#e8dcc2 100%)!important;
    box-shadow:inset 0 0 24px rgba(255,255,255,.82),0 8px 18px rgba(156,132,88,.10)!important;
  }
  .home-room.home-floor-fl_marble_steps .home-path::after{
    background:
      repeating-linear-gradient(180deg,transparent 0 24px,rgba(156,132,88,.18) 25px 27px,transparent 28px 52px),
      linear-gradient(90deg,rgba(255,255,255,.32),transparent 20% 80%,rgba(156,132,88,.08))!important;
    opacity:.88!important;
  }
  .home-room.home-floor-fl_cloud_walk .home-path{
    background:
      radial-gradient(ellipse at 50% 18%,rgba(255,255,255,.94) 0 18%,transparent 20%),
      radial-gradient(ellipse at 38% 48%,rgba(255,255,255,.84) 0 20%,transparent 22%),
      radial-gradient(ellipse at 62% 74%,rgba(255,255,255,.76) 0 22%,transparent 24%),
      linear-gradient(180deg,#effaff 0%,#dbeafe 52%,#93c5fd 100%)!important;
    border-left-color:rgba(147,197,253,.28)!important;
    border-right-color:rgba(147,197,253,.28)!important;
    box-shadow:inset 0 0 28px rgba(255,255,255,.84),0 0 20px rgba(147,197,253,.22)!important;
  }
  .home-room.home-floor-fl_cloud_walk .home-path::after{
    background:
      radial-gradient(ellipse at 45% 30%,rgba(255,255,255,.76) 0 13px,transparent 15px),
      radial-gradient(ellipse at 56% 58%,rgba(255,255,255,.70) 0 15px,transparent 17px),
      linear-gradient(90deg,rgba(255,255,255,.28),transparent 22% 78%,rgba(96,165,250,.10))!important;
    opacity:.78!important;
  }
  .home-room.home-floor-fl_pasture .home-path{
    background:
      radial-gradient(circle at 45% 26%,rgba(255,255,255,.42) 0 3px,transparent 4px),
      radial-gradient(circle at 56% 54%,rgba(255,248,223,.34) 0 3px,transparent 4px),
      linear-gradient(180deg,#f0fdf4 0%,#bbf7d0 48%,#86efac 100%)!important;
    border-left-color:rgba(74,124,64,.18)!important;
    border-right-color:rgba(74,124,64,.18)!important;
    box-shadow:inset 0 0 24px rgba(255,255,255,.62),0 0 18px rgba(134,239,172,.22)!important;
  }
  .home-room.home-floor-fl_pasture .home-path::after{
    background:
      repeating-linear-gradient(115deg,transparent 0 15px,rgba(74,124,64,.13) 16px 17px,transparent 18px 32px),
      linear-gradient(90deg,rgba(255,255,255,.22),transparent 18% 82%,rgba(74,124,64,.08))!important;
    opacity:.76!important;
  }
  .home-room.home-floor-fl_cloud_walk .home-path-gem{background:linear-gradient(135deg,#fff,#dff7ff 46%,#93c5fd)!important;}
  .home-room.home-floor-fl_pasture .home-path-gem{background:linear-gradient(135deg,#fff,#dcfce7 46%,#86efac)!important;}
  .home-room.home-floor-fl_marble_steps .home-path-gem{background:linear-gradient(135deg,#fff,#fffaf0 46%,#d6c7a1)!important;}

  /* Final Home path alignment: keep the walkway centered under the house door. */
	  .home-room .home-path{
	    left:50%!important;
	    right:auto!important;
	    top:52%!important;
	    bottom:-7%!important;
	    width:min(42%,210px)!important;
	    height:auto!important;
	    transform:translateX(-50%)!important;
	    transform-origin:50% 100%!important;
	    clip-path:polygon(46% 0,54% 0,92% 100%,8% 100%)!important;
	    z-index:2!important;
	  }
	  .home-room.home-stage-0 .home-path{
	    top:60%!important;
	    width:min(32%,158px)!important;
	    clip-path:polygon(45% 0,55% 0,90% 100%,10% 100%)!important;
	  }
	  .home-room.home-stage-1 .home-path{
	    top:57%!important;
	    width:min(35%,176px)!important;
	  }
	  .home-room.home-stage-2 .home-path{
	    top:54%!important;
	    width:min(38%,190px)!important;
	  }
	  .home-room.home-stage-5 .home-path,
	  .home-room.home-stage-6 .home-path{
	    top:50%!important;
	    width:min(46%,230px)!important;
	    clip-path:polygon(47% 0,53% 0,94% 100%,6% 100%)!important;
	  }
	  .home-room .home-path::before{
	    inset:9% 18% 0!important;
	  }
	  .home-room .home-path::after{
	    background:
	      repeating-linear-gradient(180deg,transparent 0 28px,rgba(138,90,8,.12) 29px 30px),
	      linear-gradient(90deg,rgba(255,255,255,.22),transparent 18% 82%,rgba(138,90,8,.08))!important;
	  }
	  .home-room .home-path-gem.pg1{left:49.2%!important;bottom:30%!important;}
	  .home-room .home-path-gem.pg2{left:53.2%!important;bottom:21%!important;}
	  .home-room .home-path-gem.pg3{left:46.8%!important;bottom:14%!important;}
	  @media (max-width:520px){
	    .home-room .home-path{
	      top:53%!important;
	      width:min(40%,168px)!important;
	    }
	    .home-room.home-stage-0 .home-path{top:61%!important;width:min(32%,132px)!important;}
	    .home-room.home-stage-1 .home-path{top:58%!important;width:min(35%,145px)!important;}
	    .home-room.home-stage-5 .home-path,
	    .home-room.home-stage-6 .home-path{top:51%!important;width:min(43%,180px)!important;}
	  }

	  /* Final bottom navigation polish: centered frosted pill with soft active bubble. */
	  .bottom-tabs{
	    position:fixed!important;
	    left:50%!important;
	    right:auto!important;
	    bottom:max(10px,env(safe-area-inset-bottom))!important;
	    transform:translateX(-50%)!important;
	    width:min(calc(100vw - 24px),540px)!important;
	    z-index:1000!important;
	    display:flex!important;
	    justify-content:space-between!important;
	    align-items:center!important;
	    gap:4px!important;
	    padding:8px 9px calc(8px + env(safe-area-inset-bottom))!important;
	    border:1px solid rgba(255,255,255,.72)!important;
	    border-radius:999px!important;
	    background:
	      linear-gradient(135deg,rgba(255,253,246,.78),rgba(234,246,255,.72) 48%,rgba(255,248,223,.72)),
	      rgba(255,253,246,.68)!important;
	    -webkit-backdrop-filter:blur(22px) saturate(1.18)!important;
	    backdrop-filter:blur(22px) saturate(1.18)!important;
	    box-shadow:
	      0 18px 45px rgba(65,95,125,.16),
	      0 8px 20px rgba(138,90,8,.10),
	      inset 0 1px 0 rgba(255,255,255,.84)!important;
	  }
	  .tab-btn{
	    position:relative!important;
	    isolation:isolate!important;
	    flex:1 1 0!important;
	    min-width:0!important;
	    max-width:none!important;
	    min-height:54px!important;
	    padding:7px 4px!important;
	    border:0!important;
	    border-radius:999px!important;
	    background:transparent!important;
	    box-shadow:none!important;
	    transition:transform .18s ease,color .18s ease!important;
	  }
	  .tab-btn::before{
	    content:"";
	    position:absolute;
	    inset:4px 3px;
	    z-index:-1;
	    border-radius:999px;
	    opacity:0;
	    transform:scale(.88);
	    background:linear-gradient(135deg,rgba(255,248,223,.96),rgba(255,255,255,.88) 46%,rgba(215,235,250,.78));
	    border:1px solid rgba(255,255,255,.86);
	    box-shadow:0 7px 18px rgba(138,90,8,.14), inset 0 1px 0 rgba(255,255,255,.88);
	    transition:opacity .2s ease,transform .2s ease;
	  }
	  .tab-btn.active{
	    background:transparent!important;
	    box-shadow:none!important;
	    transform:translateY(-1px)!important;
	  }
	  .tab-btn.active::before{
	    opacity:1;
	    transform:scale(1);
	  }
	  .tab-btn:active{
	    transform:translateY(0) scale(.97)!important;
	  }
	  .tab-icon-wrap{
	    position:relative!important;
	    z-index:1!important;
	    min-height:24px!important;
	  }
	  .tab-icon-wrap > span:first-child{
	    display:inline-flex!important;
	    align-items:center!important;
	    justify-content:center!important;
	    width:25px!important;
	    height:25px!important;
	    font-size:20px!important;
	    line-height:1!important;
	    filter:drop-shadow(0 1px 2px rgba(82,55,15,.08));
	  }
	  .tab-label{
	    position:relative!important;
	    z-index:1!important;
	    font-size:10.8px!important;
	    line-height:1.05!important;
	    font-weight:800!important;
	    color:var(--text-mid)!important;
	    max-width:72px!important;
	    white-space:nowrap!important;
	    overflow:hidden!important;
	    text-overflow:ellipsis!important;
	  }
	  .tab-btn.active .tab-label{
	    color:var(--text-dark)!important;
	    font-weight:900!important;
	  }
	  .tab-btn[data-tab-id="wall"] .tab-label{
	    font-size:9.8px!important;
	    max-width:76px!important;
	  }
	  .tab-lock-dot{
	    right:-8px!important;
	    top:-5px!important;
	    background:rgba(255,253,246,.96)!important;
	  }
	  @media (max-width:520px){
	    .bottom-tabs{
	      width:min(calc(100vw - 14px),430px)!important;
	      bottom:max(7px,env(safe-area-inset-bottom))!important;
	      gap:1px!important;
	      padding:7px 6px calc(7px + env(safe-area-inset-bottom))!important;
	    }
	    .tab-btn{
	      min-height:50px!important;
	      padding:6px 2px!important;
	    }
	    .tab-btn::before{
	      inset:3px 2px!important;
	    }
	    .tab-icon-wrap > span:first-child{
	      width:23px!important;
	      height:23px!important;
	      font-size:18px!important;
	    }
	    .tab-label{
	      font-size:9.8px!important;
	      max-width:58px!important;
	      letter-spacing:0!important;
	    }
	    .tab-btn[data-tab-id="wall"] .tab-label{
	      font-size:8.8px!important;
	      max-width:64px!important;
	    }
	  }

	  /* Readability pass: Today and Prayer Vessels use darker body copy. */
	  .today-page-calm,
	  .prayer-page-calm{
	    color:#493614!important;
	  }
	  .today-page-calm p,
	  .today-page-calm label,
	  .today-page-calm textarea,
	  .today-page-calm input,
	  .today-page-calm .empty-state,
	  .today-page-calm .unlock-note,
	  .today-page-calm .faith-journey-panel,
	  .today-page-calm .faith-journey-panel span,
	  .today-page-calm .faith-journey-panel b,
	  .prayer-page-calm p,
	  .prayer-page-calm label,
	  .prayer-page-calm textarea,
	  .prayer-page-calm input,
	  .prayer-page-calm .empty-state,
	  .prayer-page-calm .quick-prayer-box,
	  .prayer-page-calm .friend-card,
	  .prayer-page-calm .friend-card span,
	  .prayer-page-calm .testimony-entry{
	    color:#493614!important;
	  }
	  .today-page-calm h2,
	  .today-page-calm h3,
	  .prayer-page-calm h2,
	  .prayer-page-calm h3{
	    color:#3d2800!important;
	  }
	  .prayer-page-calm h2,
	  .prayer-page-calm h3,
	  .prayer-page-calm .prayer-friend-panel-title,
	  .prayer-page-calm .empty-state-title{
	    color:#321f00!important;
	    font-weight:800!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card > div:nth-child(2){
	    color:#321f00!important;
	    font-weight:800!important;
	  }
	  .growth-page-calm h2,
	  .growth-page-calm h3{
	    color:#321f00!important;
	    font-weight:800!important;
	  }
	  .growth-page-calm .testimony-stone-toggle h3{
	    color:#321f00!important;
	    font-weight:800!important;
	  }

	  /* Premium Growth page: softer sanctuary feel with clearer hierarchy. */
	  .growth-page-calm{
	    position:relative;
	    isolation:isolate;
	    padding:4px 0 14px;
	    color:#493614!important;
	  }
	  .growth-page-calm::before{
	    content:"";
	    position:absolute;
	    inset:-18px -14px -26px;
	    z-index:0;
	    pointer-events:none;
	    background:
	      radial-gradient(circle at 20% 6%,rgba(255,248,223,.80),transparent 36%),
	      radial-gradient(circle at 86% 18%,rgba(232,246,255,.58),transparent 34%),
	      radial-gradient(circle at 48% 76%,rgba(255,241,242,.42),transparent 42%);
	    opacity:.9;
	  }
	  .growth-page-calm > *{position:relative;z-index:1;}
	  .growth-page-calm p,
	  .growth-page-calm label,
	  .growth-page-calm span,
	  .growth-page-calm input,
	  .growth-page-calm textarea{
	    color:#493614!important;
	  }
	  .growth-page-calm .growth-account-card,
	  .growth-page-calm .growth-hero-card,
	  .growth-page-calm .growth-closet-card,
	  .growth-page-calm .growth-stones-card,
	  .growth-page-calm .growth-book-card{
	    border-radius:24px!important;
	    border:1px solid rgba(224,197,139,.42)!important;
	    background:linear-gradient(145deg,rgba(255,253,248,.88),rgba(255,249,230,.64),rgba(246,250,255,.42))!important;
	    box-shadow:0 12px 34px rgba(121,86,24,.075), inset 0 1px 0 rgba(255,255,255,.76)!important;
	    backdrop-filter:blur(8px);
	  }
	  .growth-page-calm .growth-account-card{
	    border-color:rgba(201,144,18,.50)!important;
	    background:linear-gradient(135deg,rgba(255,252,229,.92),rgba(255,253,248,.86),rgba(239,248,255,.34))!important;
	    box-shadow:0 14px 38px rgba(180,130,20,.105), inset 0 1px 0 rgba(255,255,255,.82)!important;
	  }
	  .growth-page-calm .growth-hero-card{
	    width:calc(100% + 24px)!important;
	    margin-left:-12px!important;
	    margin-right:-12px!important;
	    border-left:none!important;
	    border-right:none!important;
	    border-radius:0!important;
	    background:linear-gradient(180deg,rgba(255,253,248,.18),rgba(255,253,248,.46),rgba(255,248,223,.30))!important;
	    box-shadow:none!important;
	    backdrop-filter:none!important;
	    padding-left:18px!important;
	    padding-right:18px!important;
	  }
	  .growth-page-calm .growth-hero-card .testimony-stone-toggle{
	    padding:3px 0 2px!important;
	  }
	  .growth-page-calm .growth-hero-card .placed-chip,
	  .growth-page-calm .growth-account-card .placed-chip,
	  .growth-page-calm .growth-stones-card .placed-chip,
	  .growth-page-calm .growth-book-card .placed-chip{
	    background:linear-gradient(135deg,rgba(255,252,229,.96),rgba(255,253,248,.88))!important;
	    border:1.5px solid rgba(201,144,18,.35)!important;
	    color:#7a5200!important;
	    box-shadow:0 3px 10px rgba(180,130,20,.08)!important;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2){
	    scrollbar-width:thin;
	    scrollbar-color:rgba(201,144,18,.38) rgba(255,248,223,.35);
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2) > div{
	    border-color:rgba(224,197,139,.40)!important;
	    box-shadow:0 8px 22px rgba(121,86,24,.06)!important;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2) > div[style*='rgba(91,143,184']{
	    border-color:rgba(201,144,18,.48)!important;
	  }
	  .growth-page-calm .testimony-stone-toggle{
	    border-radius:18px;
	  }
	  .growth-page-calm .testimony-stone-chevron{
	    background:rgba(255,253,248,.88)!important;
	    border-color:rgba(224,197,139,.45)!important;
	    color:#7a5200!important;
	    box-shadow:0 4px 12px rgba(121,86,24,.06)!important;
	  }
	  .growth-page-calm .growth-closet-card{
	    background:linear-gradient(145deg,rgba(255,253,248,.92),rgba(239,248,255,.52),rgba(236,253,245,.30))!important;
	  }
	  .growth-page-calm .growth-closet-card .gold-btn,
	  .growth-page-calm .growth-account-card .gold-btn{
	    box-shadow:0 8px 20px rgba(180,130,20,.16), inset 0 1px 0 rgba(255,255,255,.68)!important;
	  }
	  .growth-page-calm .soft-action-btn,
	  .growth-page-calm .growth-closet-card button:not(.gold-btn){
	    background:linear-gradient(135deg,rgba(246,250,255,.88),rgba(255,253,248,.78))!important;
	    border:1.5px solid rgba(91,143,184,.28)!important;
	    color:#426887!important;
	    box-shadow:0 4px 12px rgba(65,95,125,.055)!important;
	  }
	  .growth-page-calm .testimony-stone-card{
	    background:linear-gradient(160deg,rgba(255,253,248,.96),rgba(255,248,223,.62),rgba(246,250,255,.30))!important;
	    border-color:rgba(224,197,139,.42)!important;
	    box-shadow:0 6px 18px rgba(121,86,24,.06)!important;
	  }
	  .growth-page-calm .testimony-stone-card.locked{
	    background:rgba(255,253,248,.42)!important;
	    border-color:rgba(80,116,139,.18)!important;
	    box-shadow:none!important;
	  }
	  .growth-page-calm .month-group,
	  .growth-page-calm .month-toggle{
	    border-color:rgba(224,197,139,.34)!important;
	  }
	  .growth-page-calm .sg-input{
	    background:rgba(255,253,248,.88)!important;
	    border-color:rgba(224,197,139,.42)!important;
	    color:#493614!important;
	  }
	  .growth-page-calm .growth-summary-card{
	    position:relative;
	    overflow:hidden;
	    display:grid;
	    grid-template-columns:auto minmax(0,1fr);
	    gap:14px;
	    align-items:center;
	    background:
	      radial-gradient(circle at 18% 28%,rgba(255,248,223,.92),transparent 34%),
	      radial-gradient(circle at 82% 18%,rgba(232,246,255,.70),transparent 36%),
	      linear-gradient(145deg,rgba(255,253,248,.96),rgba(255,248,223,.50),rgba(246,250,255,.42))!important;
	    border:1px solid rgba(224,197,139,.50)!important;
	    box-shadow:0 16px 42px rgba(121,86,24,.09), inset 0 1px 0 rgba(255,255,255,.78)!important;
	  }
	  .growth-page-calm .growth-summary-card::after{
	    content:"✦";
	    position:absolute;
	    right:18px;
	    top:12px;
	    color:rgba(201,144,18,.28);
	    font-size:24px;
	    pointer-events:none;
	  }
	  .growth-summary-orb{
	    width:112px;
	    height:112px;
	    border-radius:34px;
	    display:flex;
	    align-items:center;
	    justify-content:center;
	    background:radial-gradient(circle,rgba(255,255,255,.88),rgba(255,248,223,.54) 58%,rgba(232,246,255,.24));
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.82),0 10px 26px rgba(121,86,24,.09);
	  }
	  .growth-summary-copy{min-width:0;}
	  .growth-summary-stats{
	    grid-column:1 / -1;
	    display:grid;
	    grid-template-columns:1fr 1fr;
	    gap:8px;
	  }
	  .growth-summary-stat{
	    min-height:58px;
	    border-radius:17px;
	    padding:9px 10px;
	    display:grid;
	    grid-template-columns:auto 1fr;
	    column-gap:8px;
	    align-items:center;
	    background:rgba(255,253,248,.68);
	    border:1px solid rgba(224,197,139,.34);
	    box-shadow:0 4px 14px rgba(121,86,24,.045);
	  }
	  .growth-summary-stat span{
	    grid-row:1 / span 2;
	    width:32px;
	    height:32px;
	    border-radius:999px;
	    display:flex;
	    align-items:center;
	    justify-content:center;
	    background:linear-gradient(135deg,rgba(255,252,229,.98),rgba(239,248,255,.72));
	    border:1px solid rgba(201,144,18,.25);
	  }
	  .growth-summary-stat b{
	    color:#321f00!important;
	    font-size:18px;
	    line-height:1;
	    font-weight:900;
	  }
	  .growth-summary-stat small{
	    color:#6f5b35!important;
	    font-size:11.5px;
	    line-height:1.15;
	    font-weight:800;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2){
	    gap:10px!important;
	    padding:4px 2px 10px!important;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2) > div{
	    width:108px!important;
	    border-radius:22px!important;
	    background:linear-gradient(160deg,rgba(255,253,248,.84),rgba(255,248,223,.48))!important;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2) > div:hover{
	    transform:translateY(-2px);
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(3){
	    border-top-color:rgba(224,197,139,.28)!important;
	    background:linear-gradient(135deg,rgba(255,253,248,.48),rgba(246,250,255,.32));
	    border-radius:20px;
	    padding:13px!important;
	  }
	  .growth-page-calm .growth-account-card > div[style*="rgba(236,253,245"],
	  .growth-page-calm .growth-account-card > div[style*="rgba(246,250,255"]{
	    border-color:rgba(224,197,139,.30)!important;
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.58)!important;
	  }

	  /* Growth calmness pass: reduce visual weight and repeated dashboard feeling. */
	  .growth-page-calm h2,
	  .growth-page-calm h3,
	  .growth-page-calm .testimony-stone-toggle h3{
	    font-family:'Cormorant Garamond', Georgia, serif!important;
	    font-weight:700!important;
	    letter-spacing:0!important;
	  }
	  .growth-page-calm .growth-account-card,
	  .growth-page-calm .growth-closet-card,
	  .growth-page-calm .growth-stones-card,
	  .growth-page-calm .growth-book-card{
	    background:rgba(255,253,248,.54)!important;
	    border-color:rgba(224,197,139,.24)!important;
	    box-shadow:0 7px 22px rgba(121,86,24,.045)!important;
	  }
	  .growth-page-calm .growth-account-card{
	    outline:none!important;
	    border-color:rgba(224,197,139,.30)!important;
	  }
	  .growth-page-calm .growth-summary-card{
	    background:linear-gradient(145deg,rgba(255,253,248,.78),rgba(255,248,223,.38),rgba(246,250,255,.28))!important;
	    border-color:rgba(224,197,139,.28)!important;
	    box-shadow:0 10px 28px rgba(121,86,24,.055), inset 0 1px 0 rgba(255,255,255,.62)!important;
	  }
	  .growth-page-calm .growth-summary-card::after{opacity:.55!important;}
	  .growth-summary-orb{
	    background:radial-gradient(circle,rgba(255,255,255,.72),rgba(255,248,223,.34) 62%,rgba(232,246,255,.14))!important;
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.68),0 7px 18px rgba(121,86,24,.055)!important;
	  }
	  .growth-summary-stat{
	    min-height:50px!important;
	    background:rgba(255,253,248,.46)!important;
	    border-color:rgba(224,197,139,.20)!important;
	    box-shadow:none!important;
	  }
	  .growth-summary-stat b{font-weight:800!important;}
	  .growth-summary-stat small{font-weight:700!important;}
	  .growth-page-calm .growth-hero-card{background:transparent!important;}
	  .growth-page-calm .growth-hero-card > div:nth-child(2) > div{
	    background:rgba(255,253,248,.42)!important;
	    border-color:rgba(224,197,139,.22)!important;
	    box-shadow:none!important;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2) > div[style*="rgba(91,143,184"]{
	    background:linear-gradient(160deg,rgba(255,253,248,.74),rgba(255,248,223,.44))!important;
	    border-color:rgba(201,144,18,.34)!important;
	    box-shadow:0 6px 18px rgba(180,130,20,.07)!important;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(3){
	    background:rgba(255,253,248,.34)!important;
	    border-top-color:rgba(224,197,139,.18)!important;
	  }
	  .growth-page-calm .placed-chip{font-weight:700!important;box-shadow:none!important;}
	  .growth-page-calm .testimony-stone-chevron{box-shadow:none!important;}


	  /* Growth premium refinement: quieter layout, stronger stage focus, less dashboard noise. */
	  .growth-page-calm{
	    gap:14px!important;
	    padding:2px 0 18px!important;
	  }
	  .growth-page-calm::before{
	    inset:-30px -18px -34px!important;
	    background:
	      radial-gradient(circle at 50% 0%,rgba(255,249,230,.76),transparent 34%),
	      radial-gradient(circle at 9% 22%,rgba(238,222,255,.30),transparent 34%),
	      radial-gradient(circle at 92% 18%,rgba(229,246,255,.42),transparent 34%),
	      linear-gradient(180deg,rgba(255,253,248,.42),rgba(255,253,248,0))!important;
	    opacity:.82!important;
	  }
	  .growth-page-calm .growth-account-card,
	  .growth-page-calm .growth-closet-card,
	  .growth-page-calm .growth-stones-card,
	  .growth-page-calm .growth-book-card{
	    border-radius:26px!important;
	    border:1px solid rgba(214,181,111,.22)!important;
	    background:
	      linear-gradient(145deg,rgba(255,255,255,.58),rgba(255,251,241,.45),rgba(248,243,255,.16))!important;
	    box-shadow:
	      0 16px 40px rgba(92,61,12,.055),
	      inset 0 1px 0 rgba(255,255,255,.82)!important;
	    backdrop-filter:blur(12px) saturate(1.05)!important;
	  }
	  .growth-page-calm .growth-account-card{
	    background:
	      radial-gradient(circle at 100% 0%,rgba(255,236,173,.28),transparent 38%),
	      linear-gradient(145deg,rgba(255,255,255,.66),rgba(255,251,241,.48),rgba(236,253,245,.16))!important;
	  }
	  .growth-page-calm .growth-summary-card{
	    border:none!important;
	    border-radius:0!important;
	    width:calc(100% + 24px)!important;
	    margin-left:-12px!important;
	    margin-right:-12px!important;
	    padding:20px 22px!important;
	    background:
	      radial-gradient(circle at 16% 40%,rgba(255,248,223,.86),transparent 35%),
	      radial-gradient(circle at 82% 12%,rgba(229,246,255,.60),transparent 36%),
	      linear-gradient(180deg,rgba(255,253,248,.72),rgba(255,248,223,.34),rgba(255,253,248,.12))!important;
	    box-shadow:none!important;
	  }
	  .growth-page-calm .growth-summary-card::after{
	    right:24px!important;
	    top:18px!important;
	    color:rgba(183,126,25,.22)!important;
	  }
	  .growth-summary-orb{
	    width:128px!important;
	    height:128px!important;
	    border-radius:38px!important;
	    background:
	      radial-gradient(circle at 50% 36%,rgba(255,255,255,.90),rgba(255,248,223,.52) 58%,rgba(235,247,255,.16))!important;
	    box-shadow:
	      0 20px 42px rgba(92,61,12,.10),
	      inset 0 1px 0 rgba(255,255,255,.86)!important;
	  }
	  .growth-summary-copy .section-label,
	  .growth-page-calm .section-label{
	    color:#8b6419!important;
	    letter-spacing:.08em!important;
	    font-weight:850!important;
	  }
	  .growth-summary-copy h3{
	    font-size:clamp(24px,6vw,34px)!important;
	    line-height:1!important;
	    color:#3e2700!important;
	  }
	  .growth-summary-copy p{
	    max-width:320px!important;
	    color:#5c4825!important;
	    font-weight:650!important;
	  }
	  .growth-page-calm .growth-hero-card{
	    width:calc(100% + 24px)!important;
	    margin-left:-12px!important;
	    margin-right:-12px!important;
	    padding:17px 18px 18px!important;
	    background:linear-gradient(180deg,rgba(255,253,248,.04),rgba(255,253,248,.34),rgba(255,248,223,.18))!important;
	  }
	  .growth-page-calm .growth-hero-card .testimony-stone-toggle{
	    min-height:42px!important;
	    padding:0 2px!important;
	  }
	  .growth-page-calm .growth-hero-card .testimony-stone-toggle h3{
	    font-size:21px!important;
	    color:#3e2700!important;
	    font-weight:650!important;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2){
	    gap:12px!important;
	    padding:8px 2px 14px!important;
	    scroll-snap-type:x proximity;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2) > div{
	    width:118px!important;
	    min-height:148px!important;
	    scroll-snap-align:center;
	    border-radius:26px!important;
	    border:1px solid rgba(214,181,111,.18)!important;
	    background:linear-gradient(155deg,rgba(255,255,255,.54),rgba(255,251,241,.38))!important;
	    box-shadow:0 10px 26px rgba(92,61,12,.045)!important;
	    transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2) > div[style*="rgba(91,143,184"]{
	    border-color:rgba(199,143,30,.46)!important;
	    background:
	      radial-gradient(circle at 50% 24%,rgba(255,248,223,.72),transparent 48%),
	      linear-gradient(155deg,rgba(255,255,255,.78),rgba(255,248,223,.46))!important;
	    box-shadow:0 14px 34px rgba(180,130,20,.10)!important;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(2) > div:hover{
	    transform:translateY(-3px)!important;
	  }
	  .growth-page-calm .growth-hero-card > div:nth-child(3){
	    margin-top:4px!important;
	    padding:15px!important;
	    border-radius:24px!important;
	    border:1px solid rgba(214,181,111,.16)!important;
	    background:linear-gradient(145deg,rgba(255,255,255,.44),rgba(255,251,241,.34))!important;
	  }
	  .growth-page-calm .testimony-stone-chevron,
	  .growth-page-calm .placed-chip{
	    border-color:rgba(214,181,111,.24)!important;
	    background:rgba(255,253,248,.68)!important;
	    color:#755214!important;
	  }
	  .growth-page-calm .gold-btn{
	    border-radius:999px!important;
	    box-shadow:0 10px 24px rgba(180,130,20,.14),inset 0 1px 0 rgba(255,255,255,.72)!important;
	  }
	  .growth-page-calm .soft-action-btn,
	  .growth-page-calm button:not(.gold-btn):not(.testimony-stone-toggle){
	    border-radius:999px!important;
	  }
	  .growth-page-calm .growth-account-card > div[style*="rgba(236,253,245"],
	  .growth-page-calm .growth-account-card > div[style*="rgba(246,250,255"]{
	    background:rgba(255,253,248,.46)!important;
	    border-color:rgba(214,181,111,.18)!important;
	    border-radius:18px!important;
	  }


	  /* Growth path polish: spirit-centered hero with a quiet stage journey. */
	  .growth-stage-hero{
	    min-height:172px!important;
	    align-items:center!important;
	  }
	  .growth-stage-orb{
	    position:relative;
	  }
	  .growth-stage-orb::before{
	    content:"";
	    position:absolute;
	    inset:8px;
	    border-radius:32px;
	    background:radial-gradient(circle,rgba(255,232,173,.48),rgba(238,222,255,.22) 48%,transparent 70%);
	    filter:blur(12px);
	    animation:spiritGlowPulse 4.8s ease-in-out infinite;
	    pointer-events:none;
	  }
	  .growth-stage-orb > *{position:relative;z-index:1;}
	  .growth-stage-copy{
	    display:flex;
	    flex-direction:column;
	    gap:7px;
	  }
	  .growth-stage-progress{
	    margin-top:7px;
	    width:100%;
	    max-width:360px;
	  }
	  .growth-stage-progress-top{
	    display:flex;
	    align-items:center;
	    justify-content:space-between;
	    gap:10px;
	    margin-bottom:7px;
	    font-size:12.5px;
	    color:#5c4825!important;
	    line-height:1.35;
	  }
	  .growth-stage-progress-top span{
	    color:#5c4825!important;
	    font-weight:650!important;
	  }
	  .growth-stage-progress-top b{
	    color:#8b6419!important;
	    font-weight:800!important;
	    flex-shrink:0;
	  }
	  .growth-stage-progress-track{
	    height:16px;
	    border-radius:999px;
	    overflow:hidden;
	    background:linear-gradient(135deg,rgba(255,253,248,.84),rgba(229,246,255,.44));
	    border:1px solid rgba(214,181,111,.24);
	    box-shadow:inset 0 1px 3px rgba(92,61,12,.08);
	  }
	  .growth-stage-progress-fill{
	    height:100%;
	    border-radius:inherit;
	    background:linear-gradient(90deg,#d9a21b,#f6d77a,#c9a13a);
	    box-shadow:0 0 14px rgba(217,162,27,.28);
	    transition:width .45s ease;
	    display:flex;
	    align-items:center;
	    justify-content:center;
	    min-width:42px;
	    overflow:hidden;
	  }
	  .growth-stage-progress-label{
	    color:#fffdf7;
	    font-size:11.5px;
	    font-weight:650;
	    line-height:1;
	    text-shadow:0 1px 2px rgba(94,60,10,.24);
	    white-space:nowrap;
	    pointer-events:none;
	  }
	  .growth-path-list{
	    position:relative;
	    display:flex!important;
	    flex-direction:column!important;
	    gap:10px!important;
	    overflow:visible!important;
	    margin-top:14px!important;
	    padding:2px 0 2px 10px!important;
	  }
	  .growth-path-list::before{
	    content:"";
	    position:absolute;
	    left:42px;
	    top:30px;
	    bottom:30px;
	    width:2px;
	    border-radius:999px;
	    background:linear-gradient(180deg,rgba(201,144,18,.34),rgba(238,222,255,.34),rgba(145,180,112,.28));
	  }
	  .growth-page-calm .growth-path-step{
	    position:relative;
	    width:auto!important;
	    min-height:82px!important;
	    display:grid!important;
	    grid-template-columns:68px minmax(0,1fr) auto!important;
	    align-items:center!important;
	    gap:12px!important;
	    padding:10px 12px!important;
	    text-align:left!important;
	    border-radius:24px!important;
	    background:linear-gradient(145deg,rgba(255,255,255,.56),rgba(255,251,241,.38))!important;
	    border:1px solid rgba(214,181,111,.18)!important;
	    box-shadow:0 10px 24px rgba(92,61,12,.045)!important;
	    opacity:1!important;
	  }
	  .growth-page-calm .growth-path-step.locked{
	    opacity:.55!important;
	    filter:saturate(.72);
	  }
	  .growth-page-calm .growth-path-step.current{
	    background:radial-gradient(circle at 12% 50%,rgba(255,232,173,.50),transparent 36%),linear-gradient(145deg,rgba(255,255,255,.76),rgba(255,248,223,.48))!important;
	    border-color:rgba(201,144,18,.40)!important;
	    box-shadow:0 14px 30px rgba(180,130,20,.09)!important;
	  }
	  .growth-path-avatar{
	    position:relative;
	    z-index:1;
	    width:64px;
	    height:64px;
	    display:flex;
	    align-items:center;
	    justify-content:center;
	    border-radius:22px;
	    background:radial-gradient(circle,rgba(255,255,255,.82),rgba(255,248,223,.42));
	  }
	  .growth-path-name{
	    color:#3e2700!important;
	    font-size:16px;
	    line-height:1.15;
	    font-weight:750;
	  }
	  .growth-path-range{
	    margin-top:3px;
	    color:#6f5b35!important;
	    font-size:12.2px;
	    line-height:1.25;
	    font-weight:650;
	  }
	  .growth-path-status{
	    width:30px;
	    height:30px;
	    border-radius:999px;
	    display:flex;
	    align-items:center;
	    justify-content:center;
	    background:rgba(255,253,248,.72)!important;
	    border:1px solid rgba(214,181,111,.26)!important;
	    color:#9a6b0c!important;
	    font-weight:800!important;
	    flex-shrink:0;
	  }
	  .growth-encouragement{
	    margin:14px 2px 0!important;
	    padding:12px 14px;
	    border-radius:20px;
	    background:linear-gradient(135deg,rgba(255,248,223,.52),rgba(238,222,255,.18));
	    border:1px solid rgba(214,181,111,.18);
	    color:#5c4825!important;
	    font-size:13px!important;
	    line-height:1.45!important;
	    font-weight:650!important;
	  }
	  .growth-customize-panel{
	    background:linear-gradient(145deg,rgba(255,255,255,.40),rgba(255,251,241,.28))!important;
	  }
	  .growth-closet-copy{
	    margin:0!important;
	    color:#6f5b35!important;
	    font-size:13px!important;
	    line-height:1.35!important;
	    font-weight:650!important;
	  }

	  @media (max-width:520px){
	    .growth-page-calm{gap:10px!important;padding-bottom:10px!important;}
	    .growth-page-calm .growth-summary-card{
	      grid-template-columns:auto minmax(0,1fr)!important;
	      gap:10px!important;
	      padding:13px!important;
	      border-radius:21px!important;
	    }
	    .growth-summary-orb{width:88px!important;height:88px!important;border-radius:28px!important;}
	    .growth-summary-copy h3{font-size:19px!important;}
	    .growth-summary-copy p{font-size:12.5px!important;}
	    .growth-summary-stats{gap:7px!important;}
	    .growth-summary-stat{min-height:52px!important;padding:8px!important;}
	    .growth-summary-stat b{font-size:16px!important;}
	    .growth-summary-stat small{font-size:10.5px!important;}
	    .growth-page-calm .growth-account-card,
	    .growth-page-calm .growth-closet-card,
	    .growth-page-calm .growth-stones-card,
	    .growth-page-calm .growth-book-card{
	      border-radius:20px!important;
	      padding:13px!important;
	    }
	    .growth-page-calm .growth-hero-card{
	      width:calc(100% + 24px)!important;
	      margin-left:-12px!important;
	      margin-right:-12px!important;
	      padding-left:14px!important;
	      padding-right:14px!important;
	    }
	    .growth-page-calm .testimony-stone-toggle h3{
	      font-size:16px!important;
	    }
	    .growth-page-calm .testimony-stone-toggle p{
	      font-size:12.5px!important;
	    }
	  }


	  /* Growth stage path returned to horizontal scroll while keeping the calmer premium styling. */
	  .growth-path-list{
	    flex-direction:row!important;
	    gap:12px!important;
	    overflow-x:auto!important;
	    overflow-y:hidden!important;
	    scroll-snap-type:x proximity!important;
	    padding:8px 2px 14px!important;
	    margin-top:12px!important;
	    scrollbar-width:thin;
	    scrollbar-color:rgba(201,144,18,.34) transparent;
	    -webkit-overflow-scrolling:touch;
	  }
	  .growth-path-list::before{display:none!important;}
	  .growth-page-calm .growth-path-step{
	    width:126px!important;
	    min-width:126px!important;
	    min-height:154px!important;
	    grid-template-columns:1fr!important;
	    grid-template-rows:auto auto auto!important;
	    justify-items:center!important;
	    text-align:center!important;
	    gap:7px!important;
	    padding:11px 9px!important;
	    scroll-snap-align:center;
	  }
	  .growth-path-avatar{
	    width:74px!important;
	    height:74px!important;
	    border-radius:24px!important;
	  }
	  .growth-path-copy{min-width:0!important;}
	  .growth-path-name{
	    font-size:14px!important;
	    line-height:1.15!important;
	  }
	  .growth-path-range{
	    font-size:11px!important;
	    line-height:1.2!important;
	  }
	  .growth-path-status{
	    width:28px!important;
	    height:28px!important;
	  }

	  @media (max-width:520px){
	    .growth-page-calm .growth-stage-hero{
	      grid-template-columns:1fr!important;
	      text-align:center!important;
	      padding-top:18px!important;
	    }
	    .growth-page-calm .growth-stage-orb{
	      width:132px!important;
	      height:132px!important;
	      margin:0 auto!important;
	    }
	    .growth-stage-copy{align-items:center!important;}
	    .growth-stage-progress{max-width:100%!important;}
	    .growth-stage-progress-top{
	      flex-direction:column!important;
	      gap:4px!important;
	      text-align:center!important;
	    }
	    .growth-path-list{
	      padding-left:2px!important;
	      gap:8px!important;
	    }
	    .growth-path-list::before{left:34px!important;}
	    .growth-page-calm .growth-path-step{
	      grid-template-columns:56px minmax(0,1fr) 28px!important;
	      gap:9px!important;
	      min-height:76px!important;
	      padding:9px 10px!important;
	      border-radius:21px!important;
	    }
	    .growth-path-avatar{width:54px!important;height:54px!important;border-radius:19px!important;}
	    .growth-path-name{font-size:14.5px!important;}
	    .growth-path-range{font-size:11.5px!important;}
	    .growth-path-status{width:27px!important;height:27px!important;font-size:12px!important;}
	    .growth-encouragement{font-size:12.5px!important;padding:11px 12px!important;}
	  }


	  /* Growth typography calm pass: remove chunky weights while keeping contrast. */
	  .growth-page-calm,
	  .growth-page-calm p,
	  .growth-page-calm span,
	  .growth-page-calm small,
	  .growth-page-calm label,
	  .growth-page-calm button,
	  .growth-page-calm input,
	  .growth-page-calm textarea{
	    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
	    font-weight:500!important;
	    letter-spacing:0!important;
	  }
	  .growth-page-calm h2,
	  .growth-page-calm h3,
	  .growth-page-calm .serif,
	  .growth-page-calm .testimony-stone-toggle h3,
	  .growth-page-calm .growth-summary-copy h3{
	    font-family:'Cormorant Garamond',Georgia,serif!important;
	    font-weight:600!important;
	    letter-spacing:0!important;
	  }
	  .growth-page-calm .section-label,
	  .growth-page-calm .placed-chip,
	  .growth-page-calm .meaning-chip,
	  .growth-page-calm .growth-path-name,
	  .growth-page-calm .growth-path-range,
	  .growth-page-calm .growth-path-status,
	  .growth-page-calm .growth-stage-progress-top,
	  .growth-page-calm .growth-stage-progress-top span,
	  .growth-page-calm .growth-stage-progress-top b,
	  .growth-page-calm .growth-summary-stat b,
	  .growth-page-calm .growth-summary-stat small,
	  .growth-page-calm .testimony-gem-name,
	  .growth-page-calm .empty-state-title,
	  .growth-page-calm .stone-reward-title,
	  .growth-page-calm .stone-reward-name,
	  .growth-page-calm .growth-encouragement,
	  .growth-page-calm .growth-closet-copy{
	    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
	    font-weight:600!important;
	    letter-spacing:0!important;
	  }
	  .growth-page-calm .section-label{
	    font-weight:650!important;
	    letter-spacing:.07em!important;
	  }
	  .growth-page-calm .gold-btn,
	  .growth-page-calm .soft-action-btn,
	  .growth-page-calm button:not(.testimony-stone-toggle),
	  .growth-page-calm button:not(.testimony-stone-toggle) *{
	    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
	    font-weight:650!important;
	    letter-spacing:0!important;
	  }
	  .growth-page-calm .growth-summary-copy p,
	  .growth-page-calm .growth-account-card p,
	  .growth-page-calm .growth-closet-card p,
	  .growth-page-calm .growth-stones-card p,
	  .growth-page-calm .growth-book-card p{
	    font-weight:500!important;
	  }
	  .growth-page-calm .growth-path-name{
	    color:#3b2810!important;
	    font-size:13.8px!important;
	  }
	  .growth-page-calm .growth-path-range{
	    color:#6b5734!important;
	    font-size:11.2px!important;
	  }


	  /* Growth stage readability fix: keep stage names horizontal on all screen sizes. */
	  .growth-page-calm .growth-path-list{
	    display:flex!important;
	    flex-direction:row!important;
	    align-items:stretch!important;
	  }
	  .growth-page-calm .growth-path-step{
	    width:136px!important;
	    min-width:136px!important;
	    grid-template-columns:1fr!important;
	    grid-template-rows:auto auto auto!important;
	    justify-items:center!important;
	    text-align:center!important;
	  }
	  .growth-page-calm .growth-path-copy{
	    width:100%!important;
	    min-width:0!important;
	    display:block!important;
	  }
	  .growth-page-calm .growth-path-name,
	  .growth-page-calm .growth-path-range{
	    display:block!important;
	    width:100%!important;
	    max-width:100%!important;
	    white-space:normal!important;
	    word-break:normal!important;
	    overflow-wrap:normal!important;
	    hyphens:none!important;
	    text-align:center!important;
	  }
	  .growth-page-calm .growth-path-name{
	    font-size:14px!important;
	    line-height:1.18!important;
	  }
	  @media (max-width:520px){
	    .growth-page-calm .growth-path-step{
	      width:132px!important;
	      min-width:132px!important;
	      min-height:154px!important;
	      grid-template-columns:1fr!important;
	      grid-template-rows:auto auto auto!important;
	      gap:7px!important;
	      padding:11px 9px!important;
	    }
	    .growth-page-calm .growth-path-avatar{
	      width:72px!important;
	      height:72px!important;
	      border-radius:24px!important;
	    }
	    .growth-page-calm .growth-path-name{
	      font-size:13.6px!important;
	    }
	    .growth-page-calm .growth-path-range{
	      font-size:11px!important;
	    }
	  }

	  .prayer-page-calm > .prayer-vessels-section{
	    width:calc(100% + 24px)!important;
	    margin-left:-12px!important;
	    margin-right:-12px!important;
	    padding-left:18px!important;
	    padding-right:18px!important;
	    border:none!important;
	    outline:none!important;
	    border-radius:0!important;
	    background:#FFFDF8!important;
	    box-shadow:none!important;
	    backdrop-filter:none!important;
	  }
	  .today-page-calm .verse-readable,
	  .prayer-page-calm .verse-readable{
	    color:#453111!important;
	  }
	  .prayer-page-calm .verse-readable{
	    font-size:17px!important;
	    line-height:1.85!important;
	  }
	  .today-page-calm .gold-btn,
	  .today-page-calm .gold-btn *,
	  .prayer-page-calm .gold-btn,
	  .prayer-page-calm .gold-btn *{
	    color:#3d2800!important;
	  }


	  /* Premium Today page: calmer hierarchy and daily-entry feeling. */
	  .today-page-calm{
	    position:relative;
	    isolation:isolate;
	    padding-bottom:10px;
	  }
	  .today-page-calm::before{
	    content:"";
	    position:absolute;
	    inset:-10px -12px auto;
	    height:240px;
	    border-radius:0 0 40px 40px;
	    background:
	      radial-gradient(circle at 50% 18%,rgba(255,255,255,.74),transparent 30%),
	      radial-gradient(circle at 18% 28%,rgba(255,226,156,.28),transparent 34%),
	      radial-gradient(circle at 88% 20%,rgba(218,180,255,.18),transparent 34%),
	      linear-gradient(180deg,rgba(255,248,223,.46),rgba(255,253,246,0));
	    pointer-events:none;
	    z-index:0;
	  }
	  .today-page-calm > *{position:relative;z-index:1;}
	  .today-hero-card{
	    position:relative!important;
	    overflow:hidden!important;
	    background:
	      radial-gradient(circle at 18% 22%,rgba(255,255,255,.86),transparent 30%),
	      radial-gradient(circle at 88% 24%,rgba(255,226,156,.30),transparent 32%),
	      linear-gradient(145deg,#FFFDF8 0%,#FFF9E8 48%,#FDF7EA 100%)!important;
	  }
	  .today-hero-card::before{
	    content:"✦";
	    position:absolute;
	    right:22px;
	    top:16px;
	    color:rgba(201,144,18,.30);
	    font-size:20px;
	    text-shadow:0 0 18px rgba(255,248,223,.95);
	    animation:todayPremiumSpark 5.4s ease-in-out infinite;
	  }
	  .today-hero-card::after{
	    content:"";
	    position:absolute;
	    left:24px;
	    right:24px;
	    bottom:0;
	    height:1px;
	    background:linear-gradient(90deg,transparent,rgba(201,144,18,.22),transparent);
	  }
	  @keyframes todayPremiumSpark{0%,100%{opacity:.34;transform:translateY(0) scale(.94)}50%{opacity:.76;transform:translateY(-4px) scale(1.08)}}
	  .today-hero-card .faith-journey-panel{
	    background:rgba(255,255,255,.58)!important;
	    border:1px solid rgba(231,207,159,.30)!important;
	    box-shadow:0 8px 20px rgba(91,67,28,.045),inset 0 1px 0 rgba(255,255,255,.82)!important;
	  }
	  .today-premium-quests-card{
	    border-width:1.5px!important;
	    outline:none!important;
	    border-color:rgba(201,144,18,.42)!important;
	    background:linear-gradient(145deg,rgba(255,253,246,.95),rgba(255,248,223,.56))!important;
	    box-shadow:0 10px 26px rgba(91,67,28,.06),inset 0 1px 0 rgba(255,255,255,.78)!important;
	  }
	  .today-faith-walk-card{
	    position:relative;
	    overflow:hidden;
	    border:1px solid rgba(231,207,159,.38)!important;
	    background:
	      radial-gradient(circle at 92% 4%,rgba(255,226,156,.25),transparent 30%),
	      linear-gradient(145deg,rgba(255,253,246,.96),rgba(255,250,236,.78))!important;
	    box-shadow:0 14px 32px rgba(91,67,28,.07),inset 0 1px 0 rgba(255,255,255,.80)!important;
	  }
	  .today-faith-walk-card::before{
	    content:"";
	    position:absolute;
	    left:18px;
	    right:18px;
	    top:0;
	    height:3px;
	    border-radius:999px;
	    background:linear-gradient(90deg,transparent,rgba(201,144,18,.55),rgba(218,180,255,.25),transparent);
	  }
	  .today-next-step-card{
	    display:flex;
	    align-items:center;
	    justify-content:space-between;
	    gap:10px;
	    margin-bottom:12px;
	    padding:10px 12px;
	    border-radius:16px;
	    background:linear-gradient(135deg,rgba(255,248,223,.78),rgba(255,255,255,.64));
	    border:1px solid rgba(201,144,18,.24);
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.82);
	  }
	  .today-next-step-card span{
	    font-size:11px;
	    letter-spacing:1.25px;
	    text-transform:uppercase;
	    color:#8A5A08!important;
	    font-weight:900;
	  }
	  .today-next-step-card b{
	    color:#3D2800!important;
	    font-size:13.5px;
	    line-height:1.2;
	    text-align:right;
	  }
	  .today-faith-walk-card .daily-walk-scroll{
	    gap:9px!important;
	    max-height:360px!important;
	  }
	  .today-faith-walk-card .task-row{
	    background:rgba(255,255,255,.62)!important;
	    border:1px solid rgba(231,207,159,.30)!important;
	    box-shadow:0 7px 18px rgba(91,67,28,.045),inset 0 1px 0 rgba(255,255,255,.82)!important;
	    border-radius:18px!important;
	    transition:transform .18s ease,box-shadow .18s ease,background .18s ease!important;
	  }
	  .today-faith-walk-card .task-row:active{transform:scale(.985);}
	  .today-faith-walk-card .task-row.done{
	    background:linear-gradient(135deg,rgba(255,248,223,.78),rgba(255,255,255,.70))!important;
	    border-color:rgba(201,144,18,.34)!important;
	    box-shadow:0 9px 22px rgba(180,130,20,.09),inset 0 1px 0 rgba(255,255,255,.86)!important;
	  }
	  @media(max-width:640px){
	    .today-page-calm{gap:10px!important;}
	    .today-hero-card{padding-left:14px!important;padding-right:14px!important;}
	    .today-hero-card .spirit-avatar-wrap{transform:scale(.95);}
	    .today-next-step-card{align-items:flex-start;flex-direction:column;gap:3px;padding:10px 11px;}
	    .today-next-step-card b{text-align:left;font-size:13px;}
	    .today-faith-walk-card .daily-walk-scroll{max-height:310px!important;}
	  }


	  /* Premium Today page refinement: blend the current stage into the app background. */
	  .today-page-calm{
	    gap:14px!important;
	  }
	  .today-page-calm::before{
	    height:315px!important;
	    border-radius:0 0 34px 34px!important;
	    background:
	      radial-gradient(circle at 50% 12%,rgba(255,255,255,.82),transparent 28%),
	      radial-gradient(circle at 18% 18%,rgba(255,226,156,.22),transparent 31%),
	      radial-gradient(circle at 88% 16%,rgba(218,180,255,.13),transparent 30%),
	      linear-gradient(180deg,#FFFDF8 0%,#FFF9E8 54%,rgba(255,253,248,0) 100%)!important;
	  }
	  .today-page-calm > .today-hero-card{
	    width:calc(100% + 24px)!important;
	    margin-left:-12px!important;
	    margin-right:-12px!important;
	    padding:18px 18px 10px!important;
	    border:0!important;
	    outline:0!important;
	    border-radius:0!important;
	    background:transparent!important;
	    box-shadow:none!important;
	    overflow:visible!important;
	  }
	  .today-page-calm > .today-hero-card::before,
	  .today-page-calm > .today-hero-card::after{
	    display:none!important;
	  }
	  .today-page-calm > .today-hero-card > div:first-child{
	    position:relative;
	    padding:10px 2px 4px;
	  }
	  .today-page-calm > .today-hero-card > div:first-child::before{
	    content:"";
	    position:absolute;
	    left:4%;
	    right:4%;
	    top:8%;
	    bottom:2%;
	    border-radius:999px;
	    background:radial-gradient(ellipse at 24% 45%,rgba(255,255,255,.72),transparent 36%),radial-gradient(ellipse at 74% 48%,rgba(255,248,223,.38),transparent 42%);
	    filter:blur(8px);
	    opacity:.75;
	    pointer-events:none;
	    z-index:-1;
	  }
	  .today-page-calm > .today-hero-card h2{
	    color:#5D3B05!important;
	    letter-spacing:.01em;
	  }
	  .today-page-calm > .today-hero-card .faith-journey-panel{
	    margin-top:2px!important;
	    background:linear-gradient(145deg,rgba(255,253,246,.70),rgba(255,248,223,.34))!important;
	    border:1px solid rgba(231,207,159,.20)!important;
	    box-shadow:0 10px 24px rgba(91,67,28,.035),inset 0 1px 0 rgba(255,255,255,.72)!important;
	    backdrop-filter:blur(8px);
	  }
	  .today-premium-quests-card,
	  .today-faith-walk-card{
	    border-radius:26px!important;
	  }
	  .today-premium-quests-card{
	    border-color:rgba(231,207,159,.36)!important;
	    background:
	      radial-gradient(circle at 88% 0%,rgba(255,226,156,.23),transparent 32%),
	      linear-gradient(145deg,rgba(255,253,246,.96),rgba(255,250,236,.78))!important;
	    box-shadow:0 15px 32px rgba(91,67,28,.065),inset 0 1px 0 rgba(255,255,255,.82)!important;
	  }
	  .today-premium-quests-card .placed-chip{
	    background:rgba(255,253,246,.82)!important;
	    border-color:rgba(201,144,18,.30)!important;
	    box-shadow:0 6px 14px rgba(91,67,28,.05);
	  }
	  .today-faith-walk-card{
	    padding:18px!important;
	    background:
	      radial-gradient(circle at 50% -10%,rgba(255,255,255,.78),transparent 34%),
	      radial-gradient(circle at 96% 2%,rgba(255,226,156,.24),transparent 34%),
	      linear-gradient(145deg,rgba(255,253,246,.98),rgba(255,249,232,.82))!important;
	    box-shadow:0 18px 38px rgba(91,67,28,.08),0 1px 0 rgba(255,255,255,.72) inset!important;
	  }
	  .today-faith-walk-card::after{
	    content:"✦";
	    position:absolute;
	    right:18px;
	    top:18px;
	    color:rgba(201,144,18,.22);
	    font-size:18px;
	    pointer-events:none;
	  }
	  .today-next-step-card{
	    background:linear-gradient(135deg,rgba(255,248,223,.92),rgba(255,253,246,.78))!important;
	    border-color:rgba(201,144,18,.32)!important;
	    box-shadow:0 9px 20px rgba(91,67,28,.055),inset 0 1px 0 rgba(255,255,255,.84)!important;
	  }
	  .today-faith-walk-card .task-row{
	    min-height:62px!important;
	    padding:12px 12px!important;
	    background:linear-gradient(145deg,rgba(255,255,255,.74),rgba(255,253,246,.62))!important;
	  }
	  .today-faith-walk-card .task-row.done{
	    background:linear-gradient(135deg,rgba(255,248,223,.88),rgba(255,253,246,.78))!important;
	  }
	  .today-faith-walk-card .reward-action-btn{
	    box-shadow:0 8px 18px rgba(91,67,28,.08),inset 0 1px 0 rgba(255,255,255,.72)!important;
	    transition:transform .18s ease,box-shadow .18s ease,filter .18s ease;
	  }
	  .today-faith-walk-card .reward-action-btn:not(:disabled):hover{
	    transform:translateY(-1px);
	    box-shadow:0 12px 24px rgba(138,90,8,.15),inset 0 1px 0 rgba(255,255,255,.78)!important;
	  }
	  @media(max-width:640px){
	    .today-page-calm > .today-hero-card{padding:14px 16px 8px!important;}
	    .today-page-calm > .today-hero-card > div:first-child{gap:10px!important;}
	    .today-page-calm > .today-hero-card h2{font-size:19px!important;}
	    .today-faith-walk-card{padding:16px!important;}
	  }

	  /* Prayer Vessel sanctuary redesign: keep logic intact, make the page feel alive. */
	  @keyframes vesselFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
	  @keyframes vesselGlow{0%,100%{opacity:.62;transform:scale(.96)}50%{opacity:.96;transform:scale(1.05)}}
	  @keyframes vesselLightRise{0%{transform:translateY(18px) scale(.96);opacity:.18}50%{opacity:.72}100%{transform:translateY(-14px) scale(1.02);opacity:.28}}
	  @keyframes vesselParticleDrift{0%{transform:translate3d(0,14px,0) scale(.82);opacity:0}22%{opacity:.72}100%{transform:translate3d(var(--pv-x,18px),-42px,0) scale(1.08);opacity:0}}
	  @keyframes vesselCompanionFloat{0%,100%{transform:translateY(0) rotate(-1deg)}50%{transform:translateY(-6px) rotate(1deg)}}
	  .prayer-vessel-hero{
	    position:relative!important;
	    overflow:hidden!important;
	    display:grid!important;
	    grid-template-columns:minmax(0,1fr) minmax(176px,230px)!important;
	    grid-template-areas:"copy scene" "stats stats"!important;
	    gap:18px!important;
	    align-items:center!important;
	    margin:0 0 14px!important;
	    padding:22px!important;
	    border-radius:28px!important;
	    border:1.5px solid rgba(201,154,61,.34)!important;
	    background:
	      radial-gradient(circle at 70% 24%,rgba(243,214,255,.46),transparent 35%),
	      radial-gradient(circle at 14% 18%,rgba(255,244,203,.9),transparent 38%),
	      linear-gradient(145deg,rgba(255,254,249,.98),rgba(255,249,232,.88) 46%,rgba(246,240,255,.72))!important;
	    box-shadow:0 18px 44px rgba(117,82,22,.10),inset 0 1px 0 rgba(255,255,255,.92)!important;
	    isolation:isolate!important;
	  }
	  .prayer-vessel-hero::before{
	    content:""!important;
	    position:absolute!important;
	    inset:-38% -18% auto!important;
	    height:170px!important;
	    background:radial-gradient(ellipse,rgba(255,255,255,.94),rgba(255,231,156,.28) 45%,transparent 72%)!important;
	    opacity:.9!important;
	    pointer-events:none!important;
	    z-index:0!important;
	  }
	  .prayer-vessel-light-rays{
	    position:absolute!important;
	    inset:0!important;
	    background:
	      linear-gradient(108deg,transparent 0 35%,rgba(255,255,255,.38) 43%,transparent 50%),
	      linear-gradient(72deg,transparent 0 54%,rgba(255,243,197,.36) 61%,transparent 68%)!important;
	    opacity:.44!important;
	    pointer-events:none!important;
	    z-index:0!important;
	  }
	  .prayer-vessel-hero-copy{grid-area:copy;position:relative;z-index:2;min-width:0!important;}
	  .prayer-vessel-kicker{
	    display:inline-flex!important;
	    align-items:center!important;
	    gap:6px!important;
	    margin-bottom:8px!important;
	    padding:7px 12px!important;
	    border-radius:999px!important;
	    border:1px solid rgba(201,154,61,.34)!important;
	    background:rgba(255,253,246,.72)!important;
	    color:#7a5717!important;
	    font-size:11.5px!important;
	    font-weight:900!important;
	    letter-spacing:.8px!important;
	    text-transform:uppercase!important;
	  }
	  .prayer-vessel-kicker::before{content:"✦";color:#c99a3d;}
	  .prayer-vessel-hero-copy h2{
	    margin:0 0 6px!important;
	    color:#3d2800!important;
	    font-size:clamp(25px,5.8vw,36px)!important;
	    line-height:1.04!important;
	    font-weight:600!important;
	    letter-spacing:.01em!important;
	  }
	  .prayer-vessel-hero-copy p{
	    margin:0!important;
	    max-width:34rem!important;
	    color:#5a4319!important;
	    font-family:'Cormorant Garamond',Georgia,serif!important;
	    font-size:16px!important;
	    line-height:1.5!important;
	    font-weight:600!important;
	    letter-spacing:.005em!important;
	  }
	  .prayer-vessel-private-note{
	    margin-top:7px!important;
	    color:#775c28!important;
	    font-family:'Nunito',sans-serif!important;
	    font-size:12.5px!important;
	    font-weight:500!important;
	  }
	  .prayer-vessel-scene{
	    grid-area:scene;
	    position:relative!important;
	    z-index:2!important;
	    min-height:190px!important;
	    display:flex!important;
	    align-items:center!important;
	    justify-content:center!important;
	  }
	  .prayer-vessel-orb{
	    position:relative!important;
	    width:154px!important;
	    height:162px!important;
	    animation:vesselFloat 5.6s ease-in-out infinite!important;
	    filter:drop-shadow(0 20px 26px rgba(106,75,22,.18))!important;
	  }
	  .prayer-vessel-orb::before{
	    content:""!important;
	    position:absolute!important;
	    inset:-24px -18px 16px!important;
	    border-radius:50%!important;
	    background:radial-gradient(circle,rgba(255,248,216,.86),rgba(226,190,255,.28) 52%,transparent 72%)!important;
	    animation:vesselGlow 4.8s ease-in-out infinite!important;
	    z-index:-1!important;
	  }
	  .prayer-vessel-bowl{
	    position:absolute!important;
	    left:17px!important;
	    right:17px!important;
	    bottom:20px!important;
	    height:126px!important;
	    border-radius:48% 48% 42% 42% / 38% 38% 58% 58%!important;
	    overflow:hidden!important;
	    border:2px solid rgba(255,243,205,.82)!important;
	    background:
	      radial-gradient(circle at 34% 18%,rgba(255,255,255,.94),transparent 20%),
	      linear-gradient(145deg,rgba(255,238,169,.92),rgba(202,143,45,.86) 54%,rgba(154,103,28,.84))!important;
	    box-shadow:inset 12px 0 22px rgba(255,255,255,.32),inset -14px -16px 28px rgba(116,76,18,.22)!important;
	  }
	  .prayer-vessel-fill{
	    position:absolute!important;
	    left:7px!important;
	    right:7px!important;
	    bottom:7px!important;
	    height:var(--vessel-fill)!important;
	    border-radius:40% 40% 44% 44% / 28% 28% 48% 48%!important;
	    background:
	      radial-gradient(circle at 50% 10%,rgba(255,255,255,.95),transparent 26%),
	      linear-gradient(180deg,rgba(246,214,255,.72),rgba(255,231,145,.84) 46%,rgba(255,255,255,.62))!important;
	    box-shadow:0 -8px 18px rgba(255,255,255,.45),0 0 22px rgba(232,198,111,.56)!important;
	    animation:vesselLightRise 4.4s ease-in-out infinite alternate!important;
	  }
	  .prayer-vessel-shine{
	    position:absolute!important;
	    inset:14px 20px auto auto!important;
	    width:28px!important;
	    height:78px!important;
	    border-radius:999px!important;
	    background:linear-gradient(180deg,rgba(255,255,255,.64),transparent)!important;
	    transform:rotate(-18deg)!important;
	  }
	  .prayer-vessel-base{
	    position:absolute!important;
	    left:45px!important;
	    right:45px!important;
	    bottom:6px!important;
	    height:22px!important;
	    border-radius:50%!important;
	    background:linear-gradient(90deg,rgba(133,84,16,.24),rgba(255,245,206,.72),rgba(133,84,16,.24))!important;
	    filter:blur(.2px)!important;
	  }
	  .prayer-vessel-companion{
	    position:absolute!important;
	    right:2px!important;
	    bottom:4px!important;
	    width:78px!important;
	    height:auto!important;
	    object-fit:contain!important;
	    filter:drop-shadow(0 10px 14px rgba(86,61,22,.18))!important;
	    animation:vesselCompanionFloat 5.2s ease-in-out infinite!important;
	  }
	  .prayer-vessel-stats{
	    grid-area:stats;
	    position:relative!important;
	    z-index:2!important;
	    display:grid!important;
	    grid-template-columns:repeat(3,minmax(0,1fr))!important;
	    gap:9px!important;
	  }
	  .prayer-vessel-stats button{
	    min-height:68px!important;
	    display:flex!important;
	    flex-direction:column!important;
	    align-items:center!important;
	    justify-content:center!important;
	    gap:3px!important;
	    border-radius:18px!important;
	    border:1px solid rgba(125,174,212,.25)!important;
	    background:rgba(255,253,246,.72)!important;
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.84)!important;
	    text-align:center!important;
	    cursor:pointer!important;
	    padding:10px 8px!important;
	    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
	    transition:transform .2s ease,box-shadow .2s ease,border-color .2s ease,background .2s ease!important;
	  }
	  .prayer-vessel-stats button:hover,
	  .prayer-vessel-stats button:focus-visible{
	    transform:translateY(-2px)!important;
	    border-color:rgba(201,154,61,.48)!important;
	    background:rgba(255,253,246,.88)!important;
	    box-shadow:0 10px 22px rgba(117,82,22,.10),inset 0 1px 0 rgba(255,255,255,.9)!important;
	  }
	  .prayer-vessel-stats button.active{
	    border-color:rgba(201,154,61,.62)!important;
	    background:linear-gradient(135deg,rgba(255,248,223,.94),rgba(246,240,255,.70))!important;
	  }
	  .prayer-vessel-stats b{
	    color:#7a5717!important;
	    font-size:22px!important;
	    line-height:1!important;
	    font-weight:900!important;
	  }
	  .prayer-vessel-stats span{
	    color:#4a3512!important;
	    font-size:11.5px!important;
	    line-height:1.2!important;
	    font-weight:900!important;
	  }
	  .prayer-vessel-open-grid button{
	    min-height:82px!important;
	    border:1.5px solid rgba(201,154,61,.34)!important;
	    background:linear-gradient(180deg,rgba(255,255,255,.88),rgba(255,248,225,.74))!important;
	    box-shadow:0 8px 20px rgba(117,82,22,.075),inset 0 1px 0 rgba(255,255,255,.92)!important;
	  }
	  .prayer-vessel-open-grid em{
	    margin-top:4px!important;
	    display:inline-flex!important;
	    align-items:center!important;
	    justify-content:center!important;
	    min-width:58px!important;
	    min-height:28px!important;
	    padding:5px 12px!important;
	    border-radius:999px!important;
	    background:linear-gradient(180deg,#f3cf78,#d7a73d)!important;
	    border:1px solid rgba(184,134,31,.48)!important;
	    color:#35281d!important;
	    font-size:12px!important;
	    font-style:normal!important;
	    font-weight:900!important;
	    line-height:1!important;
	    box-shadow:0 5px 12px rgba(184,134,31,.16)!important;
	  }
	  .prayer-subpage-back{
	    width:fit-content!important;
	    max-width:100%!important;
	    min-height:42px!important;
	    display:inline-flex!important;
	    align-items:center!important;
	    justify-content:center!important;
	    border-radius:999px!important;
	    border:1px solid rgba(125,174,212,.24)!important;
	    background:rgba(255,255,255,.78)!important;
	    color:#3b2a16!important;
	    padding:10px 15px!important;
	    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
	    font-size:13.5px!important;
	    font-weight:900!important;
	    cursor:pointer!important;
	    box-shadow:0 7px 18px rgba(72,50,20,.055)!important;
	  }
	  .prayer-vessel-particle{
	    position:absolute!important;
	    z-index:1!important;
	    color:#d4a846!important;
	    text-shadow:0 0 14px rgba(255,243,203,.9)!important;
	    animation:vesselParticleDrift 7s ease-in-out infinite!important;
	    pointer-events:none!important;
	  }
	  .pv-particle-one{left:11%;top:70%;--pv-x:24px;animation-delay:.2s!important;}
	  .pv-particle-two{left:83%;top:56%;--pv-x:-32px;animation-delay:1.3s!important;color:#c8a7ff!important;}
	  .pv-particle-three{left:70%;top:78%;--pv-x:-20px;animation-delay:2.1s!important;color:#f1a8cc!important;}
	  .prayer-page-calm .prayer-vessel-category-box{
	    margin-top:12px!important;
	    padding:9px!important;
	    border-radius:24px!important;
	    border:1px solid rgba(125,174,212,.26)!important;
	    background:rgba(255,253,246,.58)!important;
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.7)!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card{
	    border-radius:18px!important;
	    background:rgba(255,255,255,.68)!important;
	    border:1.5px solid rgba(125,174,212,.25)!important;
	    box-shadow:0 6px 16px rgba(76,94,112,.05)!important;
	    transition:transform .2s ease,box-shadow .2s ease,border-color .2s ease,background .2s ease!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card:hover,
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card:focus-visible{
	    transform:translateY(-2px)!important;
	    border-color:rgba(201,154,61,.45)!important;
	    box-shadow:0 12px 24px rgba(117,82,22,.10)!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card.active{
	    background:linear-gradient(135deg,rgba(255,248,223,.95),rgba(246,240,255,.72))!important;
	    border-color:rgba(201,154,61,.62)!important;
	  }
	  .prayer-page-calm .gold-btn:not(:disabled){
	    position:relative!important;
	    overflow:hidden!important;
	    border:1px solid rgba(255,255,255,.62)!important;
	    background:linear-gradient(135deg,#fff7dc 0%,#f2d27a 36%,#c99a3d 78%,#ad7c28 100%)!important;
	    box-shadow:
	      0 12px 24px rgba(138,90,8,.20),
	      inset 0 1px 0 rgba(255,255,255,.82),
	      inset 0 -8px 18px rgba(128,78,10,.12)!important;
	    transform:translateY(0)!important;
	    transition:transform .22s ease,box-shadow .22s ease,filter .22s ease!important;
	  }
	  .prayer-page-calm .gold-btn:not(:disabled)::after{
	    content:""!important;
	    position:absolute!important;
	    inset:0!important;
	    background:linear-gradient(110deg,transparent 0 34%,rgba(255,255,255,.46) 46%,transparent 58%)!important;
	    transform:translateX(-120%)!important;
	    transition:transform .55s ease!important;
	    pointer-events:none!important;
	  }
	  .prayer-page-calm .gold-btn:not(:disabled):hover,
	  .prayer-page-calm .gold-btn:not(:disabled):focus-visible{
	    transform:translateY(-2px)!important;
	    filter:saturate(1.05) brightness(1.02)!important;
	    box-shadow:
	      0 16px 30px rgba(138,90,8,.24),
	      inset 0 1px 0 rgba(255,255,255,.86),
	      inset 0 -8px 18px rgba(128,78,10,.10)!important;
	  }
	  .prayer-page-calm .gold-btn:not(:disabled):hover::after,
	  .prayer-page-calm .gold-btn:not(:disabled):focus-visible::after{
	    transform:translateX(120%)!important;
	  }
	  #my-prayer-request > .card,
	  #prayer-for-someone > .card,
	  #my-prayers-list,
	  #others-prayers-list,
	  #answered-prayers-section{
	    border-radius:24px!important;
	    background:linear-gradient(145deg,rgba(255,254,249,.96),rgba(246,250,255,.58))!important;
	    border-color:rgba(125,174,212,.22)!important;
	    box-shadow:0 8px 24px rgba(65,95,125,.06)!important;
	  }
	  #my-prayer-request > .card h3,
	  #prayer-for-someone > .card h3,
	  #my-prayers-list h3,
	  #others-prayers-list h3,
	  #answered-prayers-section h3{
	    font-size:15.8px!important;
	    line-height:1.18!important;
	    font-weight:650!important;
	    letter-spacing:.01em!important;
	  }
	  .prayer-page-calm .friend-card h4{
	    font-size:14.4px!important;
	    line-height:1.22!important;
	    font-weight:800!important;
	  }
	  .prayer-page-calm .prayer-vessel-category-box .prayer-tab-card > div:nth-child(2){
	    font-size:12.5px!important;
	    line-height:1.18!important;
	    font-weight:700!important;
	  }
	  .prayer-page-calm .prayer-status-chip,
	  .prayer-page-calm .placed-chip{
	    font-size:11.5px!important;
	    font-weight:750!important;
	  }
	  .prayer-page-calm .card > p,
	  #my-prayer-request > .card > p,
	  #prayer-for-someone > .card > p{
	    font-size:12.8px!important;
	    line-height:1.48!important;
	  }
	  @media (max-width:680px){
	    .prayer-vessel-hero{
	      grid-template-columns:1fr!important;
	      grid-template-areas:"scene" "copy" "stats"!important;
	      padding:18px 15px!important;
	      gap:12px!important;
	      border-radius:26px!important;
	      text-align:center!important;
	    }
	    .prayer-vessel-scene{min-height:170px!important;}
	    .prayer-vessel-orb{width:136px!important;height:145px!important;}
	    .prayer-vessel-companion{right:calc(50% - 112px)!important;width:68px!important;}
	    .prayer-vessel-hero-copy h2{
	      font-size:clamp(17px,4.8vw,22px)!important;
	      line-height:1.12!important;
	      white-space:nowrap!important;
	      letter-spacing:0!important;
	    }
	    .prayer-vessel-hero-copy p{margin-left:auto!important;margin-right:auto!important;font-size:clamp(12px,3.2vw,14px)!important;line-height:1.35!important;white-space:nowrap!important;max-width:100%!important;}
	    .prayer-vessel-private-note{font-size:11.5px!important;white-space:normal!important;font-weight:500!important;}
	    .prayer-vessel-stats{grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:7px!important;}
	    .prayer-vessel-stats button{min-height:58px!important;padding:8px 4px!important;border-radius:16px!important;}
	    .prayer-vessel-stats b{font-size:19px!important;}
	    .prayer-vessel-stats span{font-size:10.5px!important;line-height:1.12!important;}
	  }
	  @media (prefers-reduced-motion:reduce){
	    .prayer-vessel-orb,
	    .prayer-vessel-fill,
	    .prayer-vessel-companion,
	    .prayer-vessel-orb::before,
	    .prayer-vessel-particle{
	      animation:none!important;
	    }
	  }

	  /* Welcome/start screen: turn the first daily step into a soft magical entry. */
	  @keyframes dailyEntryGlow{0%,100%{opacity:.58;transform:scale(.96)}50%{opacity:.9;transform:scale(1.05)}}
	  @keyframes dailyEntryFloat{0%,100%{transform:translateY(0) rotate(-1deg)}50%{transform:translateY(-8px) rotate(1deg)}}
	  @keyframes dailyEntrySpark{0%{opacity:0;transform:translateY(14px) scale(.8)}35%{opacity:.82}100%{opacity:0;transform:translateY(-24px) scale(1.08)}}
	  @keyframes dailyCtaBreathe{0%,100%{box-shadow:0 14px 28px rgba(138,90,8,.22),inset 0 1px 0 rgba(255,255,255,.78)}50%{box-shadow:0 18px 36px rgba(138,90,8,.30),0 0 24px rgba(255,226,122,.36),inset 0 1px 0 rgba(255,255,255,.84)}}
	  .daily-entry-screen{
	    position:relative!important;
	    overflow:hidden!important;
	    background:
	      radial-gradient(circle at 50% 10%,rgba(255,246,214,.9),transparent 32%),
	      radial-gradient(circle at 12% 22%,rgba(244,214,255,.32),transparent 26%),
	      radial-gradient(circle at 86% 24%,rgba(255,219,232,.30),transparent 28%),
	      #fffdf8!important;
	  }
	  .daily-entry-screen::before{
	    content:""!important;
	    position:absolute!important;
	    inset:-18% -20% auto!important;
	    height:360px!important;
	    background:radial-gradient(ellipse,rgba(255,255,255,.86),rgba(255,232,163,.34) 42%,transparent 72%)!important;
	    pointer-events:none!important;
	  }
	  .daily-entry-card{
	    position:relative!important;
	    overflow:hidden!important;
	    border-radius:30px!important;
	    padding:24px!important;
	    border:1px solid rgba(201,154,61,.28)!important;
	    background:
	      radial-gradient(circle at 50% 4%,rgba(255,250,229,.96),transparent 36%),
	      linear-gradient(160deg,rgba(255,254,249,.96),rgba(255,248,223,.68) 48%,rgba(247,241,255,.58))!important;
	    box-shadow:0 24px 60px rgba(117,82,22,.13),inset 0 1px 0 rgba(255,255,255,.9)!important;
	    isolation:isolate!important;
	  }
	  .daily-entry-card::before{
	    content:""!important;
	    position:absolute!important;
	    inset:0!important;
	    background:
	      linear-gradient(112deg,transparent 0 33%,rgba(255,255,255,.36) 43%,transparent 54%),
	      linear-gradient(70deg,transparent 0 58%,rgba(255,235,170,.28) 64%,transparent 72%)!important;
	    pointer-events:none!important;
	    z-index:0!important;
	  }
	  .daily-entry-card > *{position:relative!important;z-index:1!important;}
	  .daily-entry-hero{
	    position:relative!important;
	    padding:12px 8px 6px!important;
	  }
	  .daily-entry-spirit-wrap{
	    width:132px!important;
	    height:132px!important;
	    margin:0 auto 8px!important;
	    position:relative!important;
	    display:flex!important;
	    align-items:center!important;
	    justify-content:center!important;
	  }
	  .daily-entry-radiance{
	    position:absolute!important;
	    inset:0!important;
	    border-radius:50%!important;
	    background:radial-gradient(circle,rgba(255,255,255,.96),rgba(255,226,145,.52) 38%,rgba(231,204,255,.30) 58%,transparent 74%)!important;
	    animation:dailyEntryGlow 4.8s ease-in-out infinite!important;
	    filter:blur(1px)!important;
	  }
	  .daily-entry-spirit{
	    position:relative!important;
	    width:112px!important;
	    height:112px!important;
	    object-fit:contain!important;
	    filter:drop-shadow(0 16px 18px rgba(110,76,22,.14))!important;
	    animation:dailyEntryFloat 5.4s ease-in-out infinite!important;
	  }
	  .daily-entry-sparkle{
	    position:absolute!important;
	    z-index:2!important;
	    color:#d2a047!important;
	    text-shadow:0 0 14px rgba(255,245,203,.95)!important;
	    animation:dailyEntrySpark 6.4s ease-in-out infinite!important;
	    pointer-events:none!important;
	  }
	  .daily-entry-sparkle-one{left:18%;top:22%;animation-delay:-.8s!important;}
	  .daily-entry-sparkle-two{right:17%;top:30%;color:#c8a7ff!important;animation-delay:-2s!important;}
	  .daily-entry-sparkle-three{right:25%;top:66%;color:#f0a9c9!important;animation-delay:-3.2s!important;}
	  .daily-entry-card h1{
	    font-size:clamp(25px,6vw,32px)!important;
	    line-height:1.08!important;
	    font-weight:600!important;
	    color:#6e5322!important;
	  }
	  .daily-entry-currency-note{
	    border-radius:18px!important;
	    border:1px solid rgba(201,154,61,.26)!important;
	    background:rgba(255,253,246,.58)!important;
	    color:#4b3612!important;
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.72)!important;
	  }
	  .daily-entry-path{
	    position:relative!important;
	    gap:12px!important;
	    padding-left:6px!important;
	  }
	  .daily-entry-path::before{
	    content:""!important;
	    position:absolute!important;
	    left:29px!important;
	    top:28px!important;
	    bottom:28px!important;
	    width:2px!important;
	    border-radius:999px!important;
	    background:linear-gradient(180deg,rgba(232,198,111,.12),rgba(232,198,111,.75),rgba(203,169,255,.42))!important;
	    box-shadow:0 0 14px rgba(232,198,111,.34)!important;
	  }
	  .daily-entry-step{
	    position:relative!important;
	    border:none!important;
	    border-radius:21px!important;
	    padding:14px 15px!important;
	    background:rgba(255,255,255,.62)!important;
	    box-shadow:0 8px 22px rgba(80,92,106,.07),inset 0 1px 0 rgba(255,255,255,.82)!important;
	    transition:transform .22s ease,box-shadow .22s ease,background .22s ease!important;
	  }
	  .daily-entry-step:hover,
	  .daily-entry-step:focus-within{
	    transform:translateY(-2px)!important;
	    background:rgba(255,253,246,.82)!important;
	    box-shadow:0 14px 28px rgba(117,82,22,.10),inset 0 1px 0 rgba(255,255,255,.9)!important;
	  }
	  .daily-entry-step-icon{
	    position:relative!important;
	    z-index:1!important;
	    min-width:40px!important;
	    width:40px!important;
	    height:40px!important;
	    display:flex!important;
	    align-items:center!important;
	    justify-content:center!important;
	    border-radius:50%!important;
	    background:linear-gradient(135deg,rgba(255,248,223,.98),rgba(246,240,255,.76))!important;
	    border:1px solid rgba(201,154,61,.24)!important;
	    box-shadow:0 6px 16px rgba(117,82,22,.08)!important;
	  }
	  .daily-entry-cta{
	    min-height:52px!important;
	    border-radius:18px!important;
	    font-size:15.5px!important;
	    letter-spacing:.01em!important;
	    background:linear-gradient(135deg,#fff7dc 0%,#f5d77e 38%,#c99a3d 78%,#a87928 100%)!important;
	    box-shadow:0 14px 28px rgba(138,90,8,.22),inset 0 1px 0 rgba(255,255,255,.78)!important;
	    animation:dailyCtaBreathe 4.8s ease-in-out infinite!important;
	  }
	  .daily-entry-cta:hover,
	  .daily-entry-cta:focus-visible{
	    transform:translateY(-2px)!important;
	    filter:saturate(1.06) brightness(1.02)!important;
	  }
	  .onboarding-growth-page h2{
	    font-size:clamp(27px,7vw,38px)!important;
	    line-height:1.05!important;
	  }
	  .onboarding-growth-flow-title{
	    font-size:clamp(20px,5.2vw,25px)!important;
	    font-family:'Cormorant Garamond',Georgia,serif!important;
	    font-weight:600!important;
	  }
	  .onboarding-growth-label{
	    font-size:clamp(15px,4.3vw,18px)!important;
	  }
	  @media (max-width:520px){
	    .daily-entry-screen{padding:16px!important;align-items:flex-start!important;}
	    .daily-entry-card{padding:18px 15px!important;border-radius:26px!important;}
	    .daily-entry-spirit-wrap{width:112px!important;height:112px!important;margin-bottom:4px!important;}
	    .daily-entry-spirit{width:96px!important;height:96px!important;}
	    .daily-entry-path::before{left:27px!important;}
	    .daily-entry-step{padding:13px 12px!important;}
	    .daily-entry-step-icon{width:38px!important;height:38px!important;min-width:38px!important;}
	    .daily-entry-card h1{font-size:25px!important;}
	    .daily-entry-card p{font-size:13.5px!important;}
	    .onboarding-growth-page h2{font-size:29px!important;}
	    .onboarding-growth-flow-title{font-size:21px!important;}
	  }
	  @media (prefers-reduced-motion:reduce){
	    .daily-entry-spirit,
	    .daily-entry-radiance,
	    .daily-entry-sparkle,
	    .daily-entry-cta{
	      animation:none!important;
	    }
	  }

	  /* Balanced typography pass: solid readable body copy, softer serif headings. */
	  .app-shell,
	  .app-shell button,
	  .app-shell input,
	  .app-shell textarea,
	  .app-shell select,
	  .app-shell .card,
	  .app-shell .gold-btn,
	  .app-shell .tab-label,
	  .app-shell .task-row,
	  .app-shell .quest-nav-btn,
	  .app-shell .opt-btn,
	  .app-shell .sam-btn,
	  .app-shell .accordion-trigger,
	  .app-shell .sg-input,
	  .app-shell .sg-textarea,
	  .app-shell .home-bless-btn,
	  .app-shell .home-care-action,
	  .app-shell .next-action-btn,
	  .app-shell .prayer-tab-card,
	  .app-shell .pray-now-btn,
	  .app-shell .soft-action-btn,
	  .app-shell .prayer-filter-btn,
	  .app-shell .quick-prayer-toggle,
	  .app-shell .quick-prayer-chip,
	  .app-shell .public-share-toggle,
	  .app-shell .partner-focus-complete-btn,
	  .app-shell .friend-checkbox-row,
	  .app-shell .meaning-chip,
	  .app-shell .placed-chip,
	  .app-shell .prayer-status-chip,
	  .app-shell .testimony-label,
	  .app-shell .public-preview-label,
	  .app-shell .empty-state-title,
	  .app-shell .prayer-friend-name,
	  .app-shell .growth-stat-value,
	  .app-shell .growth-stat-label,
	  .app-shell .step-guide-pill,
	  .app-shell .section-divider-label,
	  .app-shell .stage-reveal-kicker,
	  .app-shell .onboarding-growth-flow,
	  .app-shell .onboarding-growth-flow *{
	    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
	    color:#3d2800;
	  }
	  .app-shell h1,
	  .app-shell h2,
	  .app-shell h3,
	  .app-shell h4,
	  .app-shell .serif,
	  .app-shell .onboarding-growth-flow-title,
	  .app-shell .onboarding-growth-label{
	    font-family:'Cormorant Garamond',Georgia,serif!important;
	    font-weight:600!important;
	    letter-spacing:.01em!important;
	  }
	  .app-shell p,
	  .app-shell label,
	  .app-shell li,
	  .app-shell input,
	  .app-shell textarea,
	  .app-shell .empty-state,
	  .app-shell .unlock-note{
	    color:#3d2800!important;
	    font-weight:600;
	  }
	  .app-shell .gold-btn,
	  .app-shell .tab-label,
	  .app-shell .quest-nav-btn,
	  .app-shell .opt-btn,
	  .app-shell .sam-btn,
	  .app-shell .home-bless-btn,
	  .app-shell .home-care-action,
	  .app-shell .next-action-btn,
	  .app-shell .prayer-tab-card,
	  .app-shell .pray-now-btn,
	  .app-shell .soft-action-btn,
	  .app-shell .prayer-filter-btn,
	  .app-shell .quick-prayer-toggle span:first-child,
	  .app-shell .quick-prayer-chip,
	  .app-shell .public-share-toggle-title,
	  .app-shell .partner-focus-complete-btn,
	  .app-shell .friend-checkbox-row,
	  .app-shell .meaning-chip,
	  .app-shell .placed-chip,
	  .app-shell .prayer-status-chip,
	  .app-shell .testimony-label,
	  .app-shell .public-preview-label,
	  .app-shell .empty-state-title,
	  .app-shell .prayer-friend-name,
	  .app-shell .growth-stat-value,
	  .app-shell .growth-stat-label,
	  .app-shell .step-guide-pill,
	  .app-shell .section-divider-label,
	  .app-shell .stage-reveal-kicker{
	    font-weight:700!important;
	    letter-spacing:.01em!important;
	  }

	  /* Final animal polish: clearer lamb, butterfly, and dove silhouettes. */
	  .visitor-lamb{
	    width:58px!important;
	    height:46px!important;
	    filter:drop-shadow(0 7px 8px rgba(82,55,15,.12))!important;
	  }
	  .visitor-lamb .visitor-body{
	    left:9px!important;
	    bottom:10px!important;
	    width:38px!important;
	    height:25px!important;
	    border-radius:54% 50% 46% 48%!important;
	    background:
	      radial-gradient(circle at 15% 34%,#fff 0 6px,transparent 7px),
	      radial-gradient(circle at 34% 21%,#fff 0 7px,transparent 8px),
	      radial-gradient(circle at 53% 30%,#fff 0 7px,transparent 8px),
	      radial-gradient(circle at 73% 40%,#fff 0 6px,transparent 7px),
	      linear-gradient(145deg,#fffdf8 0%,#f4efe7 62%,#cfc0ad 100%)!important;
	    border:1.2px solid rgba(145,111,68,.18)!important;
	    box-shadow:inset 0 5px 8px rgba(255,255,255,.86),inset 0 -5px 7px rgba(111,82,45,.10),0 6px 10px rgba(82,55,15,.11)!important;
	  }
	  .visitor-lamb .visitor-body::before{
	    content:"";
	    position:absolute;
	    left:6px;
	    right:4px;
	    top:-9px;
	    height:17px;
	    border-radius:999px;
	    background:
	      radial-gradient(circle at 12% 72%,#fff 0 5px,transparent 6px),
	      radial-gradient(circle at 32% 43%,#fff 0 6px,transparent 7px),
	      radial-gradient(circle at 55% 60%,#fff 0 5px,transparent 6px),
	      radial-gradient(circle at 78% 48%,#fff 0 5px,transparent 6px);
	  }
	  .visitor-lamb .visitor-head{
	    left:38px!important;
	    bottom:23px!important;
	    width:17px!important;
	    height:16px!important;
	    border-radius:56% 48% 50% 52%!important;
	    background:linear-gradient(145deg,#fffdf8,#eadfce 70%,#c9b8a2)!important;
	    border:1px solid rgba(145,111,68,.16)!important;
	    box-shadow:inset 0 3px 4px rgba(255,255,255,.75)!important;
	  }
	  .visitor-lamb .visitor-head::before{
	    content:"";
	    position:absolute;
	    left:1px;
	    top:-4px;
	    width:7px;
	    height:10px;
	    border-radius:70% 42%;
	    background:#d8c6b2;
	    transform:rotate(-24deg);
	    box-shadow:11px 1px 0 -1px #d8c6b2;
	  }
	  .visitor-lamb .visitor-head::after{
	    right:4px!important;
	    top:6px!important;
	    width:2.7px!important;
	    height:2.7px!important;
	    background:#3d2800!important;
	    box-shadow:-1px -1px 0 rgba(255,255,255,.75),0 5px 0 -1px rgba(122,83,45,.32)!important;
	  }
	  .visitor-lamb .visitor-mark{
	    display:block!important;
	    left:16px!important;
	    bottom:4px!important;
	    width:4px!important;
	    height:12px!important;
	    border-radius:999px!important;
	    background:#9d8d78!important;
	    box-shadow:11px 0 0 #b3a28b,23px 0 0 #9d8d78,34px -1px 0 #b3a28b!important;
	  }
	  .visitor-lamb .visitor-tail{
	    display:block!important;
	    left:5px!important;
	    bottom:23px!important;
	    width:9px!important;
	    height:9px!important;
	    border-radius:50%!important;
	    background:#fffdf8!important;
	    box-shadow:0 0 0 1px rgba(145,111,68,.10)!important;
	  }
	  .visitor-butterfly{
	    width:54px!important;
	    height:48px!important;
	    opacity:.86!important;
	    filter:drop-shadow(0 6px 8px rgba(82,55,15,.10))!important;
	  }
	  .visitor-butterfly .visitor-body{
	    left:25px!important;
	    bottom:12px!important;
	    width:5px!important;
	    height:24px!important;
	    border-radius:999px!important;
	    background:linear-gradient(180deg,#6f4b12,#3f2b0b)!important;
	    box-shadow:0 0 0 1px rgba(255,248,223,.20)!important;
	  }
	  .visitor-butterfly .visitor-head{
	    left:22.5px!important;
	    bottom:34px!important;
	    width:10px!important;
	    height:9px!important;
	    background:#4b3410!important;
	    box-shadow:0 0 0 1px rgba(255,248,223,.20)!important;
	  }
	  .visitor-butterfly .visitor-head::before,
	  .visitor-butterfly .visitor-head::after{
	    content:"";
	    position:absolute;
	    top:-7px;
	    width:1px;
	    height:8px;
	    border-radius:999px;
	    background:rgba(75,52,16,.72);
	  }
	  .visitor-butterfly .visitor-head::before{left:2px;transform:rotate(-24deg);}
	  .visitor-butterfly .visitor-head::after{right:2px;transform:rotate(24deg);}
	  .visitor-butterfly .visitor-wing{
	    display:block!important;
	    width:25px!important;
	    height:28px!important;
	    bottom:17px!important;
	    border-radius:72% 42% 76% 38%!important;
	    background:
	      radial-gradient(circle at 35% 26%,rgba(255,255,255,.95) 0 4px,transparent 5px),
	      radial-gradient(circle at 60% 70%,rgba(255,248,223,.72) 0 4px,transparent 5px),
	      linear-gradient(145deg,#fff 0%,#f9a8d4 44%,#8ed8ff 100%)!important;
	    border:1px solid rgba(125,143,184,.20)!important;
	    box-shadow:inset 0 5px 7px rgba(255,255,255,.58),0 4px 7px rgba(82,55,15,.10)!important;
	    opacity:.94!important;
	    animation:visitorWing .92s ease-in-out infinite!important;
	  }
	  .visitor-butterfly .visitor-wing.wing-left{
	    left:2px!important;
	    transform:rotate(-18deg)!important;
	    transform-origin:95% 72%!important;
	  }
	  .visitor-butterfly .visitor-wing.wing-right{
	    right:2px!important;
	    transform:scaleX(-1) rotate(-18deg)!important;
	    transform-origin:5% 72%!important;
	  }
	  .visitor-dove,
	  .visitor-golden_dove{
	    width:58px!important;
	    height:48px!important;
	    opacity:.90!important;
	    filter:drop-shadow(0 7px 9px rgba(65,95,125,.13)) drop-shadow(0 0 10px rgba(255,248,223,.32))!important;
	  }
	  .visitor-dove .visitor-body,
	  .visitor-golden_dove .visitor-body{
	    left:15px!important;
	    bottom:11px!important;
	    width:32px!important;
	    height:23px!important;
	    border-radius:58% 50% 48% 56%!important;
	  }
	  .visitor-dove .visitor-head,
	  .visitor-golden_dove .visitor-head{
	    left:38px!important;
	    bottom:27px!important;
	    width:16px!important;
	    height:15px!important;
	  }
	  .visitor-dove .visitor-body::before,
	  .visitor-golden_dove .visitor-body::before{
	    content:"";
	    position:absolute;
	    left:-10px;
	    bottom:3px;
	    width:15px;
	    height:15px;
	    clip-path:polygon(100% 8%,0 50%,100% 92%,72% 50%);
	    background:inherit;
	    filter:brightness(.97);
	  }
	  .visitor-dove .visitor-head::before,
	  .visitor-golden_dove .visitor-head::before{
	    content:"";
	    position:absolute;
	    right:-7px;
	    top:7px;
	    width:9px;
	    height:6px;
	    clip-path:polygon(0 0,100% 50%,0 100%);
	    background:linear-gradient(90deg,#fff3c4,#d99a12);
	    filter:drop-shadow(0 1px 1px rgba(82,55,15,.12));
	  }
	  .visitor-dove .visitor-wing,
	  .visitor-golden_dove .visitor-wing{
	    width:28px!important;
	    height:22px!important;
	    border-radius:80% 28% 74% 34%!important;
	    opacity:.96!important;
	  }
	  .visitor-dove .visitor-wing.wing-left,
	  .visitor-golden_dove .visitor-wing.wing-left{
	    left:2px!important;
	    bottom:21px!important;
	    transform:rotate(-28deg)!important;
	  }
	  .visitor-dove .visitor-wing.wing-right,
	  .visitor-golden_dove .visitor-wing.wing-right{
	    right:7px!important;
	    bottom:21px!important;
	    transform:scaleX(-1) rotate(-28deg)!important;
	  }
	  .home-game-item.pet_lamb .home-game-body{
	    background:
	      radial-gradient(circle at 18% 28%,#fff 0 10%,transparent 11%),
	      radial-gradient(circle at 36% 18%,#fff 0 11%,transparent 12%),
	      radial-gradient(circle at 56% 26%,#fff 0 10%,transparent 11%),
	      radial-gradient(circle at 76% 34%,#fff 0 9%,transparent 10%),
	      linear-gradient(180deg,#fffdf8,#f4efe7 58%,#cfc0ad)!important;
	    border:1.2px solid rgba(145,111,68,.18)!important;
	  }
	  .home-game-item.pet_lamb .home-game-detail.wing-left::after{
	    width:3px!important;
	    height:3px!important;
	    background:#3d2800!important;
	  }
	  .home-game-item.pet_dove .home-game-body{
	    background:linear-gradient(145deg,#fff,#f3fbff 58%,#c9e5f8)!important;
	    border:1.2px solid rgba(91,143,184,.22)!important;
	  }
	  .home-game-item.pet_dove .home-game-detail.wing-left,
	  .home-game-item.pet_dove .home-game-detail.wing-right{
	    background:linear-gradient(145deg,#fff,rgba(226,241,255,.96) 68%,#bddff5)!important;
	    border:1px solid rgba(91,143,184,.18)!important;
	  }

	  /* Onboarding spirit life: calm floating, glow, petals/leaves, and tap delight. */
	  .gender-choice-card{
	    transform-origin:50% 100%!important;
	    overflow:visible!important;
	    background:transparent!important;
	    border-color:transparent!important;
	    outline:none!important;
	    box-shadow:none!important;
	  }
	  .onboarding-growth-flow{
	    width:min(100%,360px);
	    margin:0 auto 30px;
	    padding:16px 16px 18px;
	    border:1.35px solid rgba(201,154,61,.42);
	    border-radius:26px;
	    background:linear-gradient(145deg,rgba(255,254,249,.86),rgba(255,249,232,.66));
	    box-shadow:0 12px 30px rgba(128,93,32,.08);
	  }
	  .onboarding-growth-flow-title{
	    font-family:'Cormorant Garamond',Georgia,serif;
	    font-size:18px;
	    color:#6E5322;
	    font-weight:600;
	    margin-bottom:12px;
	    letter-spacing:.01em;
	  }
	  .onboarding-growth-flow-steps{
	    display:flex;
	    flex-direction:column;
	    align-items:center;
	    justify-content:center;
	    gap:7px;
	  }
	  .onboarding-growth-step{
	    width:100%;
	    flex:0 0 auto;
	    min-width:0;
	    padding:11px 14px;
	    border-radius:20px;
	    background:rgba(255,255,255,.62);
	    border:1px solid rgba(91,126,148,.18);
	    color:#3E3A2E;
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.65);
	    display:grid;
	    grid-template-columns:36px 1fr;
	    align-items:center;
	    column-gap:10px;
	    text-align:left;
	  }
	  .onboarding-growth-icon{
	    display:grid;
	    place-items:center;
	    font-size:22px;
	    line-height:1;
	    margin:0;
	  }
	  .onboarding-growth-label{
	    display:block;
	    font-family:'Cormorant Garamond',Georgia,serif;
	    font-size:17px;
	    line-height:1.1;
	    font-weight:700;
	    white-space:nowrap;
	    letter-spacing:.01em;
	  }
	  .onboarding-growth-note{
	    display:block;
	    margin-top:3px;
	    font-size:11.5px;
	    line-height:1.35;
	    color:#6B6254;
	    font-weight:700;
	  }
	  .onboarding-growth-arrow{
	    color:#B8892E;
	    font-size:18px;
	    line-height:1;
	    font-weight:900;
	    flex:0 0 auto;
	  }
	  .onboarding-spirit-growth-preview{
	    display:flex;
	    align-items:center;
	    justify-content:center;
	    gap:12px;
	    margin:8px auto 18px;
	  }
	  .onboarding-stage-preview{
	    flex:0 1 190px;
	    min-width:0;
	    display:flex;
	    flex-direction:column;
	    align-items:center;
	    justify-content:flex-end;
	    padding:10px 8px 8px;
	    border-radius:26px;
	    background:linear-gradient(145deg,rgba(255,254,249,.64),rgba(255,249,232,.34));
	    border:1px solid rgba(201,154,61,.22);
	    box-shadow:0 12px 26px rgba(128,93,32,.06);
	  }
	  .onboarding-stage-preview .spirit-avatar-svg{
	    filter:drop-shadow(0 16px 18px rgba(93,62,24,.14));
	  }
	  .onboarding-stage-preview span{
	    margin-top:2px;
	    color:#4A3A22;
	    font-size:12.5px;
	    font-weight:900;
	  }
	  .onboarding-growth-preview-arrow{
	    color:#B8892E;
	    font-size:24px;
	    font-weight:900;
	    filter:drop-shadow(0 2px 6px rgba(184,137,46,.18));
	  }
	  .gender-choice-card:hover,
	  .gender-choice-card:focus-visible{
	    background:transparent!important;
	    box-shadow:none!important;
	    border-color:transparent!important;
	    outline:none!important;
	  }
	  .onboarding-spirit-stage{
	    position:relative;
	    width:178px;
	    height:214px;
	    margin:0 auto 2px;
	    display:grid;
	    place-items:center;
	    isolation:isolate;
	    transform-origin:50% 80%;
	    animation:onboardingSpiritCalmFloat 5.6s ease-in-out infinite;
	  }
	  .onboarding-spirit-stage::before{
	    content:"";
	    position:absolute;
	    left:18%;
	    right:18%;
	    bottom:8px;
	    height:20px;
	    border-radius:999px;
	    background:rgba(115,85,34,.12);
	    filter:blur(8px);
	    transform-origin:center;
	    animation:onboardingGroundBreath 5.6s ease-in-out infinite;
	    z-index:0;
	  }
	  .onboarding-spirit-male::after{
	    content:"";
	    position:absolute;
	    left:4%;
	    right:4%;
	    top:8%;
	    bottom:8%;
	    border-radius:42% 58% 50% 50%;
	    background:
	      radial-gradient(circle at 52% 36%,rgba(219,242,255,.72),rgba(186,230,253,.30) 38%,transparent 68%),
	      radial-gradient(circle at 62% 72%,rgba(187,247,208,.30),transparent 44%);
	    filter:blur(7px);
	    opacity:.82;
	    z-index:1;
	    pointer-events:none;
	  }
	  .onboarding-spirit-art{
	    position:absolute;
	    inset:0;
	    z-index:3;
	    background-image:url("/onboarding.png?v=3");
	    background-repeat:no-repeat;
	    background-size:430px auto;
	    filter:drop-shadow(0 16px 18px rgba(97,62,23,.15));
	    transform-origin:50% 76%;
	    animation:onboardingBreath 5.6s ease-in-out infinite;
	    -webkit-mask-image:radial-gradient(ellipse at center,#000 58%,rgba(0,0,0,.72) 75%,transparent 100%);
	    mask-image:radial-gradient(ellipse at center,#000 58%,rgba(0,0,0,.72) 75%,transparent 100%);
	  }
	  .onboarding-spirit-female .onboarding-spirit-art{
	    background-position:-38px -18px;
	  }
	  .onboarding-spirit-male .onboarding-spirit-art{
	    background-position:-216px -18px;
	    animation-delay:.45s;
	  }
	  .onboarding-name-preview{
	    width:190px;
	    height:226px;
	  }
	  .onboarding-aura-ring{
	    position:absolute;
	    z-index:1;
	    width:150px;
	    height:150px;
	    border-radius:50%;
	    border:2px solid rgba(255,255,255,.55);
	    opacity:.5;
	    filter:blur(.2px);
	    animation:onboardingAuraRing 6.2s ease-in-out infinite;
	  }
	  .onboarding-spirit-female .onboarding-aura-ring{
	    border-color:rgba(251,207,232,.7);
	    box-shadow:0 0 32px rgba(249,168,212,.25);
	  }
	  .onboarding-spirit-male .onboarding-aura-ring{
	    border-color:rgba(186,230,253,.72);
	    box-shadow:0 0 32px rgba(125,211,252,.24);
	  }
	  .onboarding-aura-ring.ring-two{
	    width:188px;
	    height:188px;
	    opacity:.32;
	    animation-delay:1.1s;
	  }
	  .onboarding-smile-warmth{
	    position:absolute;
	    z-index:4;
	    left:50%;
	    top:90px;
	    width:42px;
	    height:22px;
	    border-radius:50%;
	    background:radial-gradient(circle,rgba(255,239,196,.45),transparent 70%);
	    transform:translateX(-50%);
	    opacity:.2;
	    animation:onboardingSmileWarmth 10.5s ease-in-out infinite;
	  }
	  .onboarding-drift{
	    position:absolute;
	    z-index:6;
	    width:10px;
	    height:10px;
	    opacity:.7;
	    filter:drop-shadow(0 0 8px rgba(255,255,255,.8));
	    animation:onboardingDriftUp 7.2s ease-in-out infinite;
	  }
	  .onboarding-spirit-female .onboarding-drift{
	    border-radius:70% 30% 65% 35%;
	    background:linear-gradient(145deg,#fbcfe8,#f472b6);
	  }
	  .onboarding-spirit-male .onboarding-drift{
	    border-radius:70% 0 70% 0;
	    background:linear-gradient(145deg,#bbf7d0,#65a30d);
	  }
	  .onboarding-drift.one{left:22px;top:92px;animation-delay:.4s}
	  .onboarding-drift.two{right:24px;top:66px;animation-delay:2.1s;transform:scale(.8)}
	  .onboarding-drift.three{right:38px;bottom:42px;animation-delay:4s;transform:scale(.65)}
	  .gender-choice-card .spirit-avatar-svg{
	    transform-origin:50% 84%!important;
	    animation:onboardingSpiritCalmFloat 5.4s ease-in-out infinite!important;
	    will-change:transform,filter;
	  }
	  .gender-choice-card.gender-choice-female .spirit-avatar-svg{
	    filter:drop-shadow(0 15px 18px rgba(120,45,115,.14)) drop-shadow(0 0 24px rgba(251,207,232,.42))!important;
	  }
	  .gender-choice-card.gender-choice-male .spirit-avatar-svg{
	    animation-delay:.6s!important;
	    filter:drop-shadow(0 15px 18px rgba(30,80,95,.14)) drop-shadow(0 0 24px rgba(186,230,253,.42))!important;
	  }
	  .gender-choice-card::before{
	    animation:onboardingAuraPulse 5.8s ease-in-out infinite!important;
	  }
	  .gender-choice-card::after{
	    animation:onboardingSparkleDrift 6.8s ease-in-out infinite!important;
	  }
	  .gender-choice-card.gender-choice-female > div:last-child::before{
	    animation:onboardingPetalsDrift 7.2s ease-in-out infinite!important;
	    filter:drop-shadow(0 0 7px rgba(249,168,212,.38));
	  }
	  .gender-choice-card.gender-choice-male > div:last-child::before{
	    animation:onboardingLeavesDrift 7.8s ease-in-out infinite!important;
	    filter:drop-shadow(0 0 7px rgba(134,239,172,.34));
	  }
	  .gender-choice-card .spirit-live-face{
	    animation:spiritBlink 7.1s ease-in-out infinite!important;
	  }
	  .gender-choice-card.gender-choice-male .spirit-live-face{
	    animation-delay:2.1s!important;
	  }
	  .gender-choice-card .spirit-smile-variant{
	    animation:spiritSmileVariation 10.8s ease-in-out infinite!important;
	  }
	  .gender-choice-card:hover,
	  .gender-choice-card:focus-visible{
	    transform:translateY(-4px)!important;
	  }
	  .gender-choice-card:hover .spirit-avatar-svg,
	  .gender-choice-card:focus-visible .spirit-avatar-svg{
	    animation:onboardingSpiritHoverGlow 1.4s ease-in-out both!important;
	  }
	  .gender-choice-card:active .spirit-avatar-svg{
	    animation:onboardingSpiritTapJoy .82s cubic-bezier(.22,.75,.24,1) both!important;
	  }
	  .gender-choice-card:hover .onboarding-spirit-stage,
	  .gender-choice-card:focus-visible .onboarding-spirit-stage{
	    animation:onboardingSpiritHoverGlow 1.4s ease-in-out both;
	  }
	  .gender-choice-card:active .onboarding-spirit-stage{
	    animation:onboardingSpiritTapJoy .82s cubic-bezier(.22,.75,.24,1) both;
	  }
	  .gender-choice-card:active .onboarding-aura-ring{
	    animation:onboardingAuraBright .82s ease-out both;
	  }
	  @keyframes onboardingSpiritCalmFloat{
	    0%,100%{transform:translateY(0) rotate(0deg) scale(1)}
	    28%{transform:translateY(-7px) rotate(.7deg) scale(1.008,.992)}
	    58%{transform:translateY(-3px) rotate(-.45deg) scale(.998,1.012)}
	    82%{transform:translateY(2px) rotate(.25deg) scale(1.004,.996)}
	  }
	  @keyframes onboardingBreath{
	    0%,100%{transform:scale(1)}
	    42%{transform:scale(1.012,.99)}
	    70%{transform:scale(.998,1.01)}
	  }
	  @keyframes onboardingGroundBreath{
	    0%,100%{transform:scaleX(1);opacity:.64}
	    48%{transform:scaleX(.86);opacity:.38}
	  }
	  @keyframes onboardingAuraRing{
	    0%,100%{transform:scale(.94);opacity:.34}
	    50%{transform:scale(1.06);opacity:.62}
	  }
	  @keyframes onboardingAuraBright{
	    0%{transform:scale(1);opacity:.48}
	    48%{transform:scale(1.15);opacity:.86}
	    100%{transform:scale(1.02);opacity:.48}
	  }
	  @keyframes onboardingSmileWarmth{
	    0%,66%,100%{opacity:.16;transform:translateX(-50%) scale(.9)}
	    72%,80%{opacity:.55;transform:translateX(-50%) scale(1.08)}
	  }
	  @keyframes onboardingDriftUp{
	    0%,100%{opacity:0;transform:translateY(8px) rotate(0deg) scale(.6)}
	    18%{opacity:.78}
	    58%{opacity:.58;transform:translateY(-18px) rotate(24deg) scale(1)}
	    82%{opacity:0;transform:translateY(-28px) rotate(42deg) scale(.8)}
	  }
	  @keyframes onboardingAuraPulse{
	    0%,100%{opacity:.52;transform:scaleX(1)}
	    50%{opacity:.78;transform:scaleX(1.08)}
	  }
	  @keyframes onboardingSparkleDrift{
	    0%,100%{opacity:.42;transform:translateY(0) scale(1)}
	    36%{opacity:.78;transform:translateY(-5px) scale(1.04)}
	    70%{opacity:.55;transform:translateY(2px) scale(.98)}
	  }
	  @keyframes onboardingPetalsDrift{
	    0%,100%{opacity:.58;transform:translateY(0) rotate(0deg)}
	    36%{opacity:.84;transform:translateY(-8px) rotate(3deg)}
	    72%{opacity:.66;transform:translateY(-2px) rotate(-2deg)}
	  }
	  @keyframes onboardingLeavesDrift{
	    0%,100%{opacity:.56;transform:translateY(0) rotate(0deg)}
	    40%{opacity:.82;transform:translateY(-9px) rotate(-4deg)}
	    76%{opacity:.64;transform:translateY(-2px) rotate(3deg)}
	  }
	  @keyframes onboardingSpiritHoverGlow{
	    0%,100%{transform:translateY(0) scale(1)}
	    42%{transform:translateY(-6px) scale(1.018,.988)}
	    78%{transform:translateY(-2px) scale(.998,1.01)}
	  }
	  @keyframes onboardingSpiritTapJoy{
	    0%{transform:translateY(0) scale(1)}
	    22%{transform:translateY(3px) scale(1.035,.955);filter:brightness(1.08)}
	    52%{transform:translateY(-11px) scale(.982,1.04);filter:brightness(1.16)}
	    78%{transform:translateY(-3px) scale(1.01,.995)}
	    100%{transform:translateY(0) scale(1)}
	  }
	  @media (prefers-reduced-motion:reduce){
	    .gender-choice-card .spirit-avatar-svg,
	    .onboarding-spirit-stage,
	    .onboarding-spirit-art,
	    .onboarding-spirit-stage::before,
	    .onboarding-aura-ring,
	    .onboarding-smile-warmth,
	    .onboarding-drift,
	    .gender-choice-card::before,
	    .gender-choice-card::after,
	    .gender-choice-card.gender-choice-female > div:last-child::before,
	    .gender-choice-card.gender-choice-male > div:last-child::before,
	    .gender-choice-card .spirit-live-face,
	    .gender-choice-card .spirit-smile-variant{
	      animation:none!important;
	    }
	  }

	  @media (max-width:520px){
	    .onboarding-growth-flow{
	      margin-bottom:24px!important;
	      padding:14px 13px 16px!important;
	      border-radius:22px!important;
	    }
	    .onboarding-growth-flow-steps{
	      gap:6px!important;
	    }
	    .onboarding-growth-step{
	      padding:10px 11px!important;
	      border-radius:16px!important;
	      grid-template-columns:32px 1fr!important;
	      column-gap:9px!important;
	    }
	    .onboarding-growth-icon{
	      font-size:19px!important;
	    }
	    .onboarding-growth-label{
	      font-size:15.5px!important;
	      white-space:normal!important;
	    }
	    .onboarding-growth-note{
	      font-size:10.8px!important;
	    }
	    .onboarding-growth-arrow{
	      font-size:15px!important;
	    }
	    .onboarding-spirit-growth-preview{
	      gap:6px!important;
	      margin-bottom:16px!important;
	    }
	    .onboarding-stage-preview{
	      flex-basis:142px!important;
	      padding:7px 4px 8px!important;
	      border-radius:22px!important;
	    }
	    .onboarding-stage-preview .spirit-avatar-svg{
	      width:112px!important;
	      height:128px!important;
	    }
	    .onboarding-stage-preview span{
	      font-size:11.5px!important;
	    }
	    .onboarding-growth-preview-arrow{
	      font-size:18px!important;
	    }
	    .gender-card-row{
	      gap:14px!important;
	      align-items:stretch!important;
	    }
	    .gender-choice-card{
	      overflow:hidden!important;
	      padding:14px 6px 15px!important;
	      min-height:246px!important;
	    }
	    .gender-choice-card .onboarding-spirit-stage{
	      width:132px!important;
	      height:164px!important;
	      margin-bottom:0!important;
	    }
	    .gender-choice-card .onboarding-spirit-art{
	      background-size:330px auto!important;
	    }
	    .gender-choice-card .onboarding-spirit-female .onboarding-spirit-art,
	    .onboarding-spirit-female .onboarding-spirit-art{
	      background-position:-28px -14px;
	    }
	    .gender-choice-card .onboarding-spirit-male .onboarding-spirit-art,
	    .onboarding-spirit-male .onboarding-spirit-art{
	      background-position:-166px -14px;
	    }
	    .gender-choice-card .onboarding-aura-ring{
	      width:112px!important;
	      height:112px!important;
	    }
	    .gender-choice-card .onboarding-aura-ring.ring-two{
	      width:136px!important;
	      height:136px!important;
	    }
	    .onboarding-name-preview{
	      width:168px!important;
	      height:202px!important;
	    }
	    .onboarding-name-preview .onboarding-spirit-art{
	      background-size:410px auto!important;
	    }
	    .onboarding-name-preview.onboarding-spirit-female .onboarding-spirit-art{
	      background-position:-35px -16px!important;
	    }
	    .onboarding-name-preview.onboarding-spirit-male .onboarding-spirit-art{
	      background-position:-207px -16px!important;
	    }
	  }

	  .splash-intro{
	    position:fixed;
	    inset:0;
	    z-index:3000;
	    display:flex;
	    align-items:center;
	    justify-content:center;
	    padding:24px;
	    background:
	      radial-gradient(circle at 50% 38%,rgba(255,247,218,.94),rgba(255,253,246,.9) 46%,rgba(246,250,255,.86) 74%,rgba(255,241,247,.72)),
	      radial-gradient(circle at 18% 18%,rgba(219,234,254,.52),transparent 36%),
	      radial-gradient(circle at 84% 78%,rgba(252,231,243,.48),transparent 38%),
	      linear-gradient(180deg,#fffdf7,#f8fbff 58%,#fff7fb);
	    animation:splashIntroFade 2.55s ease-in-out both;
	    cursor:pointer;
	  }
	  .welcome-back-overlay{
	    animation:welcomeBackOverlayIn .34s ease-out both;
	  }
	  .welcome-back-card{
	    animation:welcomeBackCardIn .42s cubic-bezier(.22,.75,.24,1) both;
	  }
	  .splash-intro-card{
	    width:min(82vw,420px);
	    max-height:86vh;
	    display:flex;
	    flex-direction:column;
	    align-items:center;
	    text-align:center;
	    animation:splashIntroRise 2.1s cubic-bezier(.22,.75,.24,1) both;
	  }
	  .splash-intro-logo{
	    width:auto;
	    max-width:min(78vw,360px);
	    max-height:82vh;
	    height:auto;
	    display:block;
	    mix-blend-mode:multiply;
	    filter:drop-shadow(0 18px 32px rgba(138,90,8,.18)) drop-shadow(0 4px 10px rgba(82,55,15,.08)) saturate(1.03);
	    -webkit-mask-image:
	      radial-gradient(ellipse at center,#000 0 72%,rgba(0,0,0,.96) 81%,rgba(0,0,0,.62) 93%,transparent 100%);
	    mask-image:
	      radial-gradient(ellipse at center,#000 0 72%,rgba(0,0,0,.96) 81%,rgba(0,0,0,.62) 93%,transparent 100%);
	  }
	  .splash-intro-star{
	    position:absolute;
	    top:17%;
	    left:50%;
	    transform:translateX(-50%);
	    color:#e3ad38;
	    font-size:26px;
	    filter:drop-shadow(0 0 14px rgba(227,173,56,.42));
	    animation:splashStarShine 1.8s ease-in-out infinite;
	  }
	  @keyframes splashIntroFade{
	    0%,82%{opacity:1}
	    100%{opacity:0;visibility:hidden}
	  }
	  @keyframes splashIntroRise{
	    0%{opacity:1;transform:translateY(4px) scale(.992)}
	    20%,80%{opacity:1;transform:translateY(0) scale(1)}
	    100%{opacity:0;transform:translateY(-4px) scale(1.004)}
	  }
	  @keyframes splashStarShine{
	    0%,100%{opacity:.65;transform:translateX(-50%) scale(.92) rotate(0deg)}
	    50%{opacity:1;transform:translateX(-50%) scale(1.08) rotate(8deg)}
	  }
	  @keyframes welcomeBackOverlayIn{
	    from{opacity:0;backdrop-filter:blur(0)}
	    to{opacity:1;backdrop-filter:blur(8px)}
	  }
	  @keyframes welcomeBackCardIn{
	    0%{opacity:0;transform:translateY(12px) scale(.97)}
	    100%{opacity:1;transform:translateY(0) scale(1)}
	  }
	  @media (max-width:520px){
	    .splash-intro{
	      padding:16px;
	      background:
	        radial-gradient(circle at 50% 42%,rgba(255,247,218,.93),rgba(255,253,246,.91) 52%,rgba(246,250,255,.86) 78%,rgba(255,241,247,.74)),
	        radial-gradient(circle at 14% 20%,rgba(219,234,254,.5),transparent 38%),
	        radial-gradient(circle at 88% 82%,rgba(252,231,243,.46),transparent 40%),
	        linear-gradient(180deg,#fffdf7,#f8fbff 58%,#fff7fb);
	    }
	    .splash-intro-card{
	      width:min(90vw,340px);
	      max-height:84vh;
	      animation:splashIntroMobileRise 2.35s cubic-bezier(.22,.75,.24,1) both;
	    }
	    .splash-intro-logo{
	      width:auto;
	      max-width:min(88vw,330px);
	      max-height:80vh;
	      filter:drop-shadow(0 14px 24px rgba(138,90,8,.16)) drop-shadow(0 3px 8px rgba(82,55,15,.08)) saturate(1.02);
	    }
	  }
	  @keyframes splashIntroMobileRise{
	    0%{opacity:1;transform:translateY(2px) scale(.996)}
	    14%,84%{opacity:1;transform:translateY(0) scale(1)}
	    100%{opacity:0;transform:translateY(-2px) scale(1.002)}
	  }
	  @media (prefers-reduced-motion:reduce){
	    .splash-intro,
	    .splash-intro-card,
	    .splash-intro-star,
	    .welcome-back-overlay,
	    .welcome-back-card{
	      animation:none!important;
	    }
	  }

	  /* Premium onboarding growth page: soft hero, glass cards, and clear growth path. */
	  @keyframes onboardingGlowDrift{
	    0%,100%{transform:translate3d(0,0,0) scale(1);opacity:.72}
	    50%{transform:translate3d(0,-10px,0) scale(1.04);opacity:.92}
	  }
	  @keyframes onboardingSpiritFloat{
	    0%,100%{transform:translateY(0) scale(1)}
	    50%{transform:translateY(-8px) scale(1.012)}
	  }
	  @keyframes onboardingPathPulse{
	    0%,100%{opacity:.58;transform:scale(.98)}
	    50%{opacity:1;transform:scale(1.02)}
	  }
	  @keyframes onboardingSparkleDrift{
	    0%,100%{transform:translateY(0) rotate(0deg);opacity:.38}
	    50%{transform:translateY(-12px) rotate(8deg);opacity:.9}
	  }
	  .onboarding-growth-page{
	    position:relative!important;
	    overflow:hidden!important;
	    background:
	      radial-gradient(circle at 50% 10%,rgba(255,248,223,.92),transparent 30%),
	      radial-gradient(circle at 12% 18%,rgba(249,168,212,.22),transparent 28%),
	      radial-gradient(circle at 88% 22%,rgba(216,180,254,.18),transparent 30%),
	      radial-gradient(circle at 50% 95%,rgba(255,255,255,.82),transparent 42%),
	      linear-gradient(180deg,#fff9e8 0%,#fffdf8 46%,#fdf7ee 100%)!important;
	  }
	  .onboarding-growth-page::before{
	    content:"";
	    position:absolute;
	    inset:-18% -12% auto;
	    height:58%;
	    pointer-events:none;
	    background:
	      linear-gradient(108deg,transparent 0 38%,rgba(255,255,255,.42) 40%,transparent 44%),
	      linear-gradient(76deg,transparent 0 46%,rgba(255,248,223,.52) 48%,transparent 52%),
	      radial-gradient(circle at 50% 22%,rgba(255,255,255,.78),transparent 34%);
	    opacity:.82;
	    filter:blur(.2px);
	  }
	  .onboarding-growth-page::after{
	    content:"";
	    position:absolute;
	    inset:auto 8% 5%;
	    height:34%;
	    border-radius:50%;
	    pointer-events:none;
	    background:radial-gradient(ellipse,rgba(232,213,172,.28),transparent 66%);
	    filter:blur(18px);
	  }
	  .onboarding-growth-shell{
	    position:relative;
	    isolation:isolate;
	    padding:clamp(18px,4vw,30px);
	    border-radius:34px;
	    border:1px solid rgba(255,255,255,.72);
	    background:
	      linear-gradient(145deg,rgba(255,253,246,.74),rgba(255,248,223,.42) 46%,rgba(255,245,250,.30)),
	      rgba(255,253,246,.56);
	    -webkit-backdrop-filter:blur(20px) saturate(1.08);
	    backdrop-filter:blur(20px) saturate(1.08);
	    box-shadow:
	      0 24px 70px rgba(138,90,8,.13),
	      0 10px 28px rgba(91,76,54,.08),
	      inset 0 1px 0 rgba(255,255,255,.86);
	  }
	  .onboarding-growth-rays{
	    position:absolute;
	    inset:8px;
	    z-index:-1;
	    border-radius:30px;
	    pointer-events:none;
	    background:
	      radial-gradient(circle at 50% 8%,rgba(255,248,223,.82),transparent 29%),
	      radial-gradient(circle at 18% 42%,rgba(249,168,212,.16),transparent 34%),
	      radial-gradient(circle at 84% 54%,rgba(216,180,254,.15),transparent 34%);
	    animation:onboardingGlowDrift 7s ease-in-out infinite;
	  }
	  .onboarding-growth-sparkle{
	    position:absolute;
	    z-index:1;
	    color:#c9952f;
	    font-size:18px;
	    pointer-events:none;
	    text-shadow:0 0 14px rgba(255,248,223,.84);
	    animation:onboardingSparkleDrift 4.8s ease-in-out infinite;
	  }
	  .onboarding-growth-sparkle.s1{left:10%;top:13%;animation-delay:-.8s}
	  .onboarding-growth-sparkle.s2{right:12%;top:18%;font-size:15px;animation-delay:-2s}
	  .onboarding-growth-sparkle.s3{right:18%;bottom:26%;font-size:13px;animation-delay:-3.1s}
	  .onboarding-growth-hero{
	    position:relative;
	    margin:0 auto 16px;
	    padding:4px 4px 0;
	  }
	  .onboarding-growth-kicker{
	    display:inline-flex;
	    align-items:center;
	    justify-content:center;
	    min-height:28px;
	    padding:5px 13px;
	    margin-bottom:10px;
	    border-radius:999px;
	    border:1px solid rgba(201,154,61,.34);
	    background:rgba(255,253,246,.56);
	    color:#7b5b1d!important;
	    font-size:11px;
	    font-weight:800;
	    letter-spacing:.16em;
	    text-transform:uppercase;
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.8);
	  }
	  .onboarding-growth-page h2{
	    font-size:clamp(28px,6vw,40px)!important;
	    line-height:.98!important;
	    color:#7a5513!important;
	    letter-spacing:.01em!important;
	    text-shadow:0 8px 28px rgba(201,154,61,.20)!important;
	  }
	  .onboarding-growth-page .onboarding-growth-hero p{
	    font-size:clamp(15px,3.5vw,18px)!important;
	    line-height:1.58!important;
	    color:#5c4420!important;
	    text-wrap:balance;
	  }
	  .onboarding-spirit-growth-preview{
	    position:relative;
	    width:min(100%,520px)!important;
	    gap:16px!important;
	    margin:10px auto 20px!important;
	    padding:12px!important;
	    border-radius:30px!important;
	    border:1px solid rgba(255,255,255,.72)!important;
	    background:linear-gradient(135deg,rgba(255,255,255,.54),rgba(255,248,223,.34),rgba(255,245,250,.32))!important;
	    box-shadow:inset 0 1px 0 rgba(255,255,255,.82),0 14px 30px rgba(138,90,8,.08)!important;
	  }
	  .onboarding-spirit-growth-preview::before{
	    content:none!important;
	    position:absolute;
	    left:24%;
	    right:24%;
	    top:50%;
	    height:2px;
	    border-radius:999px;
	    background:linear-gradient(90deg,transparent,rgba(201,154,61,.48),rgba(249,168,212,.38),rgba(201,154,61,.48),transparent);
	    transform:translateY(-50%);
	    animation:onboardingPathPulse 3.8s ease-in-out infinite;
	  }
	  .onboarding-stage-preview{
	    position:relative!important;
	    z-index:2!important;
	    flex:1 1 0!important;
	    min-height:176px!important;
	    padding:14px 10px 12px!important;
	    border-radius:26px!important;
	    border:1px solid rgba(255,255,255,.78)!important;
	    background:
	      radial-gradient(circle at 50% 32%,rgba(255,248,223,.58),transparent 52%),
	      linear-gradient(155deg,rgba(255,255,255,.62),rgba(255,253,246,.38))!important;
	    box-shadow:
	      0 12px 28px rgba(138,90,8,.08),
	      inset 0 1px 0 rgba(255,255,255,.84)!important;
	    overflow:visible!important;
	  }
	  .onboarding-stage-preview > div{
	    animation:onboardingSpiritFloat 5.2s ease-in-out infinite;
	    transform-origin:50% 82%;
	  }
	  .onboarding-stage-preview:last-child > div{
	    animation-delay:-1.1s;
	  }
	  .onboarding-stage-preview .spirit-stage-image-wrap,
	  .onboarding-stage-preview .spirit-avatar-svg{
	    filter:drop-shadow(0 18px 24px rgba(138,90,8,.14)) drop-shadow(0 0 18px rgba(255,248,223,.44))!important;
	  }
	  .onboarding-stage-preview span{
	    display:inline-flex!important;
	    align-items:center!important;
	    justify-content:center!important;
	    min-height:27px!important;
	    padding:5px 12px!important;
	    margin-top:6px!important;
	    border-radius:999px!important;
	    background:rgba(255,253,246,.74)!important;
	    border:1px solid rgba(201,154,61,.22)!important;
	    color:#5c3e0c!important;
	    font-size:12px!important;
	    font-weight:800!important;
	    box-shadow:0 5px 12px rgba(138,90,8,.06)!important;
	  }
	  .onboarding-growth-preview-arrow{
	    position:relative!important;
	    z-index:3!important;
	    display:flex!important;
	    flex-direction:column!important;
	    align-items:center!important;
	    justify-content:center!important;
	    flex:0 0 auto!important;
	    min-width:70px!important;
	    color:#9b6a14!important;
	    font-size:12px!important;
	    font-weight:800!important;
	    text-transform:uppercase!important;
	    letter-spacing:.08em!important;
	    filter:none!important;
	  }
	  .onboarding-growth-preview-arrow b{
	    display:grid;
	    place-items:center;
	    width:38px;
	    height:38px;
	    margin-top:6px;
	    border-radius:50%;
	    color:#fff8df;
	    font-size:21px;
	    line-height:1;
	    background:linear-gradient(135deg,#d9a83c,#9d6f14);
	    box-shadow:0 10px 20px rgba(138,90,8,.18),0 0 0 6px rgba(255,248,223,.62);
	  }
	  .onboarding-growth-flow{
	    position:relative!important;
	    width:min(100%,440px)!important;
	    margin:0 auto 22px!important;
	    padding:18px!important;
	    border-radius:28px!important;
	    border:1px solid rgba(255,255,255,.72)!important;
	    background:linear-gradient(145deg,rgba(255,255,255,.58),rgba(255,253,246,.38),rgba(255,245,250,.22))!important;
	    box-shadow:0 14px 34px rgba(138,90,8,.08),inset 0 1px 0 rgba(255,255,255,.82)!important;
	  }
	  .onboarding-growth-flow-title{
	    margin:0 0 14px!important;
	    font-size:clamp(19px,4.2vw,24px)!important;
	    color:#65450e!important;
	    font-family:'Cormorant Garamond',Georgia,serif!important;
	    font-weight:700!important;
	  }
	  .onboarding-growth-flow-steps{
	    position:relative!important;
	    gap:9px!important;
	  }
	  .onboarding-growth-flow-steps::before{
	    content:"";
	    position:absolute;
	    left:25px;
	    top:42px;
	    bottom:42px;
	    width:2px;
	    border-radius:999px;
	    background:linear-gradient(180deg,rgba(201,154,61,.15),rgba(201,154,61,.58),rgba(249,168,212,.36),rgba(201,154,61,.16));
	  }
	  .onboarding-growth-step{
	    position:relative!important;
	    z-index:1!important;
	    padding:12px 14px!important;
	    grid-template-columns:42px 1fr!important;
	    border-radius:21px!important;
	    border:1px solid rgba(255,255,255,.72)!important;
	    background:linear-gradient(135deg,rgba(255,255,255,.72),rgba(255,253,246,.48))!important;
	    box-shadow:0 7px 18px rgba(138,90,8,.06),inset 0 1px 0 rgba(255,255,255,.9)!important;
	  }
	  .onboarding-growth-icon{
	    width:34px!important;
	    height:34px!important;
	    border-radius:50%!important;
	    background:linear-gradient(145deg,#fffdf8,#fff3c4)!important;
	    border:1px solid rgba(201,154,61,.30)!important;
	    box-shadow:0 7px 14px rgba(138,90,8,.10),inset 0 1px 0 rgba(255,255,255,.9)!important;
	    font-size:19px!important;
	  }
	  .onboarding-growth-label{
	    font-size:clamp(18px,4.2vw,22px)!important;
	    line-height:1.02!important;
	    color:#4a320a!important;
	    white-space:normal!important;
	  }
	  .onboarding-growth-note{
	    margin-top:5px!important;
	    font-size:12.5px!important;
	    line-height:1.45!important;
	    color:#6c5630!important;
	    font-weight:700!important;
	  }
	  .onboarding-growth-arrow{
	    position:relative!important;
	    z-index:2!important;
	    width:26px!important;
	    height:26px!important;
	    display:grid!important;
	    place-items:center!important;
	    margin:-2px 0!important;
	    border-radius:50%!important;
	    color:#a67416!important;
	    background:rgba(255,248,223,.82)!important;
	    border:1px solid rgba(201,154,61,.28)!important;
	    box-shadow:0 4px 10px rgba(138,90,8,.08)!important;
	    font-size:15px!important;
	  }
	  .onboarding-growth-cta{
	    position:relative!important;
	    min-width:min(100%,270px)!important;
	    border-radius:999px!important;
	    background:linear-gradient(135deg,#ffe8a3 0%,#e6b648 42%,#b77b13 100%)!important;
	    color:#3b2600!important;
	    box-shadow:
	      0 16px 34px rgba(184,137,46,.26),
	      0 0 0 7px rgba(255,248,223,.62),
	      inset 0 1px 0 rgba(255,255,255,.78)!important;
	    transform:translateY(0);
	    transition:transform .18s ease,box-shadow .18s ease,filter .18s ease!important;
	  }
	  .onboarding-growth-cta:hover,
	  .onboarding-growth-cta:focus-visible{
	    transform:translateY(-2px)!important;
	    filter:saturate(1.04) brightness(1.02)!important;
	    box-shadow:
	      0 20px 40px rgba(184,137,46,.32),
	      0 0 0 8px rgba(255,248,223,.70),
	      0 0 26px rgba(255,232,163,.38),
	      inset 0 1px 0 rgba(255,255,255,.8)!important;
	  }
	  @media (max-width:640px){
	    .onboarding-growth-page{
	      justify-content:flex-start!important;
	      padding:14px 12px 20px!important;
	    }
	    .onboarding-growth-shell{
	      max-width:100%!important;
	      padding:17px 13px 20px!important;
	      border-radius:28px!important;
	    }
	    .onboarding-growth-kicker{
	      font-size:10px!important;
	      margin-bottom:9px!important;
	    }
	    .onboarding-growth-page .onboarding-growth-hero{
	      margin-bottom:12px!important;
	    }
	    .onboarding-growth-page .onboarding-growth-hero p{
	      margin-bottom:14px!important;
	      font-size:14.5px!important;
	    }
	    .onboarding-spirit-growth-preview{
	      gap:8px!important;
	      padding:9px!important;
	      border-radius:24px!important;
	      margin-bottom:15px!important;
	    }
	    .onboarding-spirit-growth-preview::before{
	      left:20%!important;
	      right:20%!important;
	    }
	    .onboarding-stage-preview{
	      min-height:150px!important;
	      padding:9px 4px 8px!important;
	      border-radius:22px!important;
	    }
	    .onboarding-stage-preview .spirit-stage-image-wrap,
	    .onboarding-stage-preview .spirit-avatar-svg{
	      width:112px!important;
	      height:126px!important;
	    }
	    .onboarding-stage-preview span{
	      font-size:10.5px!important;
	      padding:4px 8px!important;
	    }
	    .onboarding-growth-preview-arrow{
	      min-width:42px!important;
	      font-size:9px!important;
	      letter-spacing:.04em!important;
	    }
	    .onboarding-growth-preview-arrow b{
	      width:30px!important;
	      height:30px!important;
	      font-size:17px!important;
	      box-shadow:0 8px 16px rgba(138,90,8,.16),0 0 0 4px rgba(255,248,223,.58)!important;
	    }
	    .onboarding-growth-flow{
	      padding:13px!important;
	      border-radius:23px!important;
	      margin-bottom:18px!important;
	    }
	    .onboarding-growth-flow-title{
	      font-size:19px!important;
	      margin-bottom:11px!important;
	    }
	    .onboarding-growth-step{
	      padding:10px!important;
	      grid-template-columns:36px 1fr!important;
	      border-radius:18px!important;
	    }
	    .onboarding-growth-flow-steps::before{
	      left:21px!important;
	      top:38px!important;
	      bottom:38px!important;
	    }
	    .onboarding-growth-icon{
	      width:30px!important;
	      height:30px!important;
	      font-size:17px!important;
	    }
	    .onboarding-growth-label{
	      font-size:18px!important;
	    }
	    .onboarding-growth-note{
	      font-size:11.5px!important;
	    }
	    .onboarding-growth-cta{
	      width:100%!important;
	      min-width:0!important;
	      padding:14px 18px!important;
	    }
	  }
	  @media (max-width:390px){
	    .onboarding-growth-page{
	      padding-left:8px!important;
	      padding-right:8px!important;
	    }
	    .onboarding-stage-preview .spirit-stage-image-wrap,
	    .onboarding-stage-preview .spirit-avatar-svg{
	      width:98px!important;
	      height:112px!important;
	    }
	    .onboarding-growth-preview-arrow span{
	      display:none!important;
	    }
	  }
	  @media (prefers-reduced-motion:reduce){
	    .onboarding-growth-rays,
	    .onboarding-growth-sparkle,
	    .onboarding-stage-preview > div,
	    .onboarding-spirit-growth-preview::before{
	      animation:none!important;
	    }
	  }


	  /* Premium iOS onboarding refinement: elegant glass, calm depth, and softer motion. */
	  @keyframes onboardingPremiumLight{
	    0%,100%{transform:translate3d(0,0,0) scale(1);opacity:.56}
	    50%{transform:translate3d(0,-8px,0) scale(1.035);opacity:.78}
	  }
	  @keyframes onboardingButtonShimmer{
	    0%{transform:translateX(-120%) skewX(-18deg);opacity:0}
	    22%{opacity:.55}
	    48%{opacity:0}
	    100%{transform:translateX(150%) skewX(-18deg);opacity:0}
	  }
	  @keyframes onboardingCardRise{
	    from{opacity:0;transform:translateY(12px)}
	    to{opacity:1;transform:translateY(0)}
	  }
	  .onboarding-growth-page{
	    background:
	      radial-gradient(circle at 50% 0%,rgba(255,246,210,.78),transparent 28%),
	      radial-gradient(circle at 15% 18%,rgba(230,214,255,.18),transparent 30%),
	      radial-gradient(circle at 88% 20%,rgba(255,214,232,.14),transparent 34%),
	      radial-gradient(circle at 50% 100%,rgba(255,255,255,.84),transparent 42%),
	      linear-gradient(180deg,#fcfaf5 0%,#fffdf8 48%,#fbf3e8 100%)!important;
	  }
	  .onboarding-growth-page::before{
	    inset:-24% -18% auto!important;
	    height:62%!important;
	    background:
	      radial-gradient(circle at 50% 28%,rgba(255,255,255,.80),transparent 28%),
	      linear-gradient(104deg,transparent 0 39%,rgba(255,255,255,.28) 41%,transparent 46%),
	      linear-gradient(78deg,transparent 0 50%,rgba(243,209,137,.25) 52%,transparent 57%)!important;
	    opacity:.74!important;
	    animation:onboardingPremiumLight 12s ease-in-out infinite;
	  }
	  .onboarding-growth-page::after{
	    inset:auto 14% 3%!important;
	    height:28%!important;
	    background:
	      radial-gradient(ellipse at 50% 70%,rgba(197,141,37,.14),transparent 66%),
	      radial-gradient(ellipse at 16% 42%,rgba(161,135,95,.08),transparent 50%)!important;
	    filter:blur(22px)!important;
	  }
	  .onboarding-growth-shell{
	    max-width:620px!important;
	    padding:clamp(24px,5vw,42px)!important;
	    border-radius:38px!important;
	    border:1px solid rgba(201,154,61,.18)!important;
	    background:
	      linear-gradient(145deg,rgba(255,255,255,.66),rgba(255,251,241,.52) 44%,rgba(248,239,255,.26)),
	      rgba(252,250,245,.62)!important;
	    box-shadow:
	      0 34px 90px rgba(92,61,12,.12),
	      0 12px 34px rgba(83,62,32,.08),
	      inset 0 1px 0 rgba(255,255,255,.88),
	      inset 0 -1px 0 rgba(201,154,61,.08)!important;
	    animation:onboardingCardRise .55s ease both;
	  }
	  .onboarding-growth-rays{
	    inset:10px!important;
	    border-radius:34px!important;
	    background:
	      radial-gradient(circle at 50% 8%,rgba(255,247,222,.62),transparent 34%),
	      radial-gradient(circle at 12% 42%,rgba(230,214,255,.16),transparent 36%),
	      radial-gradient(circle at 86% 50%,rgba(255,218,232,.14),transparent 35%)!important;
	    opacity:.86!important;
	    animation:onboardingPremiumLight 10s ease-in-out infinite!important;
	  }
	  .onboarding-growth-sparkle{
	    color:rgba(176,124,24,.68)!important;
	    opacity:.58;
	    font-size:15px!important;
	    text-shadow:0 0 18px rgba(255,244,205,.82)!important;
	  }
	  .onboarding-growth-kicker{
	    min-height:30px!important;
	    padding:6px 16px!important;
	    margin-bottom:16px!important;
	    border:1px solid rgba(167,113,24,.24)!important;
	    background:linear-gradient(180deg,rgba(255,255,255,.72),rgba(255,250,235,.42))!important;
	    color:#806018!important;
	    font-size:11px!important;
	    font-weight:800!important;
	    letter-spacing:.18em!important;
	    box-shadow:0 8px 18px rgba(95,68,21,.06),inset 0 1px 0 rgba(255,255,255,.88)!important;
	  }
	  .onboarding-growth-page h2{
	    margin-bottom:14px!important;
	    font-family:'Cormorant Garamond',Georgia,serif!important;
	    font-size:clamp(40px,8.4vw,68px)!important;
	    line-height:.96!important;
	    font-weight:600!important;
	    letter-spacing:-.025em!important;
	    color:#6f4a0f!important;
	    text-shadow:0 18px 44px rgba(177,124,30,.18)!important;
	  }
	  .onboarding-growth-page .onboarding-growth-hero p{
	    max-width:510px!important;
	    margin-bottom:24px!important;
	    font-family:'Cormorant Garamond',Georgia,serif!important;
	    font-size:clamp(16px,3.4vw,19px)!important;
	    line-height:1.82!important;
	    color:rgba(78,55,22,.72)!important;
	    font-style:normal!important;
	    font-weight:500!important;
	  }
	  .onboarding-spirit-growth-preview{
	    width:min(100%,560px)!important;
	    gap:18px!important;
	    margin:4px auto 26px!important;
	    padding:16px!important;
	    border-radius:34px!important;
	    border:1px solid rgba(201,154,61,.16)!important;
	    background:
	      linear-gradient(145deg,rgba(255,255,255,.66),rgba(255,251,242,.45),rgba(248,239,255,.22))!important;
	    box-shadow:
	      0 24px 54px rgba(92,61,12,.10),
	      inset 0 1px 0 rgba(255,255,255,.9),
	      inset 0 -1px 0 rgba(201,154,61,.08)!important;
	  }
	  .onboarding-stage-preview{
	    min-height:205px!important;
	    padding:18px 12px 14px!important;
	    border-radius:30px!important;
	    border:1px solid rgba(201,154,61,.14)!important;
	    background:
	      radial-gradient(circle at 50% 36%,rgba(255,242,205,.64),transparent 48%),
	      linear-gradient(160deg,rgba(255,255,255,.78),rgba(255,251,244,.45))!important;
	    box-shadow:
	      0 18px 38px rgba(92,61,12,.09),
	      0 1px 0 rgba(255,255,255,.88) inset!important;
	  }
	  .onboarding-stage-preview .spirit-stage-image-wrap,
	  .onboarding-stage-preview .spirit-avatar-svg{
	    width:142px!important;
	    height:154px!important;
	    filter:drop-shadow(0 20px 28px rgba(92,61,12,.16)) drop-shadow(0 0 22px rgba(255,238,181,.46))!important;
	  }
	  .onboarding-stage-preview span{
	    margin-top:8px!important;
	    min-height:30px!important;
	    padding:6px 14px!important;
	    background:linear-gradient(180deg,rgba(255,255,255,.82),rgba(255,248,225,.56))!important;
	    border:1px solid rgba(176,124,24,.22)!important;
	    color:#5a3a09!important;
	    font-size:12.5px!important;
	    font-weight:800!important;
	  }
	  .onboarding-growth-preview-arrow{
	    min-width:84px!important;
	    color:#8a6118!important;
	    letter-spacing:.12em!important;
	  }
	  .onboarding-growth-preview-arrow b{
	    width:44px!important;
	    height:44px!important;
	    background:
	      radial-gradient(circle at 35% 25%,rgba(255,255,255,.72),transparent 30%),
	      linear-gradient(145deg,#d7a43a,#a26d12)!important;
	    box-shadow:0 16px 30px rgba(92,61,12,.20),0 0 0 8px rgba(255,247,222,.68),inset 0 1px 0 rgba(255,255,255,.68)!important;
	  }
	  .onboarding-growth-flow{
	    width:min(100%,500px)!important;
	    margin:0 auto 24px!important;
	    padding:22px!important;
	    border-radius:32px!important;
	    border:1px solid rgba(201,154,61,.16)!important;
	    background:linear-gradient(145deg,rgba(255,255,255,.68),rgba(255,251,243,.48),rgba(248,239,255,.20))!important;
	    box-shadow:0 24px 52px rgba(92,61,12,.09),inset 0 1px 0 rgba(255,255,255,.9)!important;
	  }
	  .onboarding-growth-flow-title{
	    margin-bottom:18px!important;
	    font-family:'Cormorant Garamond',Georgia,serif!important;
	    font-size:clamp(24px,4.8vw,34px)!important;
	    line-height:1.02!important;
	    font-weight:600!important;
	    letter-spacing:-.015em!important;
	    color:#65450e!important;
	  }
	  .onboarding-growth-flow-steps{
	    gap:12px!important;
	  }
	  .onboarding-growth-flow-steps::before{
	    left:27px!important;
	    top:46px!important;
	    bottom:46px!important;
	    width:1.5px!important;
	    background:linear-gradient(180deg,rgba(197,141,37,.12),rgba(197,141,37,.56),rgba(216,180,254,.28),rgba(197,141,37,.14))!important;
	    box-shadow:0 0 14px rgba(197,141,37,.16)!important;
	  }
	  .onboarding-growth-step{
	    padding:15px 16px!important;
	    grid-template-columns:46px 1fr!important;
	    border-radius:24px!important;
	    border:1px solid rgba(201,154,61,.10)!important;
	    background:linear-gradient(135deg,rgba(255,255,255,.78),rgba(255,252,246,.55))!important;
	    box-shadow:0 12px 26px rgba(92,61,12,.07),inset 0 1px 0 rgba(255,255,255,.92)!important;
	  }
	  .onboarding-growth-icon{
	    width:38px!important;
	    height:38px!important;
	    background:radial-gradient(circle at 35% 25%,rgba(255,255,255,.86),transparent 34%),linear-gradient(145deg,#fffdf8,#fff2bf)!important;
	    border:1px solid rgba(197,141,37,.28)!important;
	    box-shadow:0 10px 18px rgba(92,61,12,.11),0 0 16px rgba(255,238,181,.34),inset 0 1px 0 rgba(255,255,255,.92)!important;
	  }
	  .onboarding-growth-label{
	    font-family:'Cormorant Garamond',Georgia,serif!important;
	    font-size:clamp(20px,4.2vw,25px)!important;
	    font-weight:700!important;
	    color:#4e3308!important;
	  }
	  .onboarding-growth-note{
	    max-width:360px!important;
	    font-size:13.25px!important;
	    line-height:1.55!important;
	    color:rgba(83,64,32,.78)!important;
	    font-weight:700!important;
	  }
	  .onboarding-growth-arrow{
	    width:28px!important;
	    height:28px!important;
	    margin:-4px 0 -2px!important;
	    color:#9b6a14!important;
	    background:linear-gradient(180deg,rgba(255,255,255,.78),rgba(255,241,194,.72))!important;
	    box-shadow:0 7px 16px rgba(92,61,12,.08),0 0 16px rgba(255,236,174,.28)!important;
	  }
	  .onboarding-growth-cta{
	    overflow:hidden!important;
	    min-height:54px!important;
	    min-width:min(100%,315px)!important;
	    border:1px solid rgba(161,105,18,.30)!important;
	    background:
	      linear-gradient(135deg,rgba(255,255,255,.86),rgba(248,242,255,.64) 48%,rgba(255,240,187,.72))!important;
	    color:#4e3308!important;
	    font-family:'Nunito',sans-serif!important;
	    font-size:15px!important;
	    font-weight:900!important;
	    letter-spacing:.01em!important;
	    box-shadow:0 18px 38px rgba(92,61,12,.14),0 0 0 8px rgba(255,248,223,.52),inset 0 1px 0 rgba(255,255,255,.9)!important;
	  }
	  .onboarding-growth-cta::after{
	    content:"";
	    position:absolute;
	    top:-30%;
	    bottom:-30%;
	    left:0;
	    width:42%;
	    background:linear-gradient(90deg,transparent,rgba(255,255,255,.58),transparent);
	    animation:onboardingButtonShimmer 5.8s ease-in-out infinite;
	  }
	  @media (max-width:640px){
	    .onboarding-growth-shell{padding:22px 14px 24px!important;border-radius:30px!important;}
	    .onboarding-growth-page h2{font-size:clamp(34px,10vw,48px)!important;}
	    .onboarding-growth-page .onboarding-growth-hero p{font-size:15.5px!important;line-height:1.68!important;margin-bottom:18px!important;}
	    .onboarding-spirit-growth-preview{gap:9px!important;padding:11px!important;border-radius:26px!important;}
	    .onboarding-stage-preview{min-height:164px!important;padding:11px 5px 10px!important;border-radius:23px!important;}
	    .onboarding-stage-preview .spirit-stage-image-wrap,
	    .onboarding-stage-preview .spirit-avatar-svg{width:116px!important;height:130px!important;}
	    .onboarding-growth-preview-arrow{min-width:42px!important;}
	    .onboarding-growth-preview-arrow b{width:32px!important;height:32px!important;font-size:17px!important;}
	    .onboarding-growth-flow{padding:15px!important;border-radius:25px!important;}
	    .onboarding-growth-flow-title{font-size:24px!important;}
	    .onboarding-growth-step{padding:12px!important;grid-template-columns:40px 1fr!important;}
	    .onboarding-growth-icon{width:32px!important;height:32px!important;}
	    .onboarding-growth-label{font-size:20px!important;}
	    .onboarding-growth-note{font-size:12.5px!important;}
	  }
	  @media (prefers-reduced-motion:reduce){
	    .onboarding-growth-page::before,
	    .onboarding-growth-cta::after{
	      animation:none!important;
	    }
	  }


	  /* Final onboarding scale pass: calmer sizing and mobile two-line scripture. */
	  .onboarding-growth-shell{
	    max-width:570px!important;
	    padding:clamp(20px,4.2vw,34px)!important;
	  }
	  .onboarding-growth-kicker{
	    min-height:28px!important;
	    padding:5px 15px!important;
	    margin-bottom:12px!important;
	    font-size:10.5px!important;
	  }
	  .onboarding-growth-page h2{
	    font-size:clamp(34px,7vw,54px)!important;
	    line-height:1!important;
	    margin-bottom:10px!important;
	    letter-spacing:-.018em!important;
	  }
	  .onboarding-growth-page .onboarding-growth-hero p{
	    max-width:560px!important;
	    font-size:clamp(14px,2.2vw,17px)!important;
	    line-height:1.58!important;
	    margin-bottom:20px!important;
	  }
	  .onboarding-spirit-growth-preview{
	    width:min(100%,520px)!important;
	    gap:14px!important;
	    padding:13px!important;
	    margin-bottom:22px!important;
	    border-radius:30px!important;
	  }
	  .onboarding-stage-preview{
	    min-height:178px!important;
	    padding:14px 10px 12px!important;
	    border-radius:26px!important;
	  }
	  .onboarding-stage-preview .spirit-stage-image-wrap,
	  .onboarding-stage-preview .spirit-avatar-svg{
	    width:124px!important;
	    height:136px!important;
	  }
	  .onboarding-stage-preview span{
	    min-height:27px!important;
	    padding:5px 12px!important;
	    font-size:11.75px!important;
	  }
	  .onboarding-growth-preview-arrow{
	    min-width:66px!important;
	    font-size:10.5px!important;
	  }
	  .onboarding-growth-preview-arrow b{
	    width:38px!important;
	    height:38px!important;
	    font-size:18px!important;
	  }
	  .onboarding-growth-flow{
	    width:min(100%,470px)!important;
	    padding:18px!important;
	    margin-bottom:22px!important;
	    border-radius:28px!important;
	  }
	  .onboarding-growth-flow-title{
	    font-size:clamp(23px,4vw,29px)!important;
	    margin-bottom:15px!important;
	  }
	  .onboarding-growth-flow-steps{
	    gap:10px!important;
	  }
	  .onboarding-growth-step{
	    padding:12px 14px!important;
	    grid-template-columns:41px 1fr!important;
	    border-radius:21px!important;
	  }
	  .onboarding-growth-icon{
	    width:34px!important;
	    height:34px!important;
	    font-size:17px!important;
	  }
	  .onboarding-growth-label{
	    font-size:clamp(18px,3.4vw,22px)!important;
	    line-height:1.05!important;
	  }
	  .onboarding-growth-note{
	    font-size:12.5px!important;
	    line-height:1.45!important;
	    font-weight:650!important;
	  }
	  .onboarding-growth-arrow{
	    width:24px!important;
	    height:24px!important;
	    font-size:15px!important;
	    margin:-3px 0!important;
	  }
	  .onboarding-growth-flow-steps::before{
	    left:24px!important;
	    top:42px!important;
	    bottom:42px!important;
	  }
	  .onboarding-growth-cta{
	    min-height:48px!important;
	    min-width:min(100%,285px)!important;
	    font-size:14px!important;
	  }
	  @media (max-width:640px){
	    .onboarding-growth-shell{
	      width:100%!important;
	      padding:20px 13px 22px!important;
	      border-radius:28px!important;
	    }
	    .onboarding-growth-kicker{
	      margin-bottom:11px!important;
	      font-size:10px!important;
	      letter-spacing:.16em!important;
	    }
	    .onboarding-growth-page h2{
	      font-size:clamp(33px,9.2vw,42px)!important;
	      line-height:1.02!important;
	      margin-bottom:10px!important;
	    }
	    .onboarding-growth-page .onboarding-growth-hero p{
	      width:min(100%,370px)!important;
	      max-width:370px!important;
	      font-size:clamp(12px,3.25vw,13.4px)!important;
	      line-height:1.55!important;
	      margin-bottom:17px!important;
	      text-wrap:balance;
	    }
	    .onboarding-spirit-growth-preview{
	      gap:8px!important;
	      padding:9px!important;
	      margin-bottom:18px!important;
	      border-radius:24px!important;
	    }
	    .onboarding-stage-preview{
	      min-height:148px!important;
	      padding:9px 4px 9px!important;
	      border-radius:21px!important;
	    }
	    .onboarding-stage-preview .spirit-stage-image-wrap,
	    .onboarding-stage-preview .spirit-avatar-svg{
	      width:96px!important;
	      height:112px!important;
	    }
	    .onboarding-stage-preview span{
	      min-height:25px!important;
	      padding:4px 10px!important;
	      font-size:10.8px!important;
	    }
	    .onboarding-growth-preview-arrow{
	      min-width:38px!important;
	      letter-spacing:.08em!important;
	      font-size:9px!important;
	    }
	    .onboarding-growth-preview-arrow span{
	      display:none!important;
	    }
	    .onboarding-growth-preview-arrow b{
	      width:30px!important;
	      height:30px!important;
	      font-size:16px!important;
	      box-shadow:0 10px 22px rgba(92,61,12,.18),0 0 0 6px rgba(255,247,222,.62),inset 0 1px 0 rgba(255,255,255,.68)!important;
	    }
	    .onboarding-growth-flow{
	      padding:13px!important;
	      margin-bottom:18px!important;
	      border-radius:23px!important;
	    }
	    .onboarding-growth-flow-title{
	      font-size:21px!important;
	      margin-bottom:12px!important;
	    }
	    .onboarding-growth-step{
	      padding:10px 11px!important;
	      grid-template-columns:36px 1fr!important;
	      border-radius:18px!important;
	    }
	    .onboarding-growth-icon{
	      width:29px!important;
	      height:29px!important;
	      font-size:15px!important;
	    }
	    .onboarding-growth-label{
	      font-size:17px!important;
	    }
	    .onboarding-growth-note{
	      font-size:11.6px!important;
	      line-height:1.38!important;
	    }
	    .onboarding-growth-arrow{
	      width:21px!important;
	      height:21px!important;
	      font-size:13px!important;
	    }
	    .onboarding-growth-flow-steps::before{
	      left:21px!important;
	      top:38px!important;
	      bottom:38px!important;
	    }
	    .onboarding-growth-cta{
	      min-height:46px!important;
	      min-width:min(100%,260px)!important;
	      font-size:13.5px!important;
	    }
	  }

	  /* Calm borderless card system: main anchors stay framed, repeated cards become soft sections. */
	  .card{
	    border-color:transparent!important;
	    box-shadow:0 8px 22px rgba(65,82,77,.035),inset 0 1px 0 rgba(255,255,255,.68)!important;
	  }
	  .focus-card,
	  .gold-outline-card,
	  .prayer-vessel-hero,
	  .prayer-friends-card,
	  .spirit-friends-hero-card,
	  .featured-testimony-card,
	  .testimony-featured-garden,
	  .home-upgrade-card,
	  .onboarding-growth-shell,
	  .onboarding-growth-flow{
	    border-color:rgba(201,154,61,.32)!important;
	    box-shadow:0 16px 38px rgba(91,67,28,.075),inset 0 1px 0 rgba(255,255,255,.78)!important;
	  }
	  .sacred-card:not(.prayer-friends-card):not(.spirit-friends-hero-card):not(.gold-outline-card),
	  .soft-guide-card,
	  .answered-altar-card,
	  .current-stage-card,
	  .prayer-vessels-section,
	  .journal-history-card,
	  .testimony-shared-garden,
	  .home-care-card,
	  .growth-stage-card{
	    border-color:transparent!important;
	    box-shadow:none!important;
	    background:transparent!important;
	  }
	  .prayer-friend-row,
	  .compact-friend-row,
	  .journal-entry-card,
	  .testimony-entry,
	  .month-entry-card,
	  .quest-card,
	  .home-shop-card,
	  .shop-card,
	  .testimony-stone-card{
	    border-color:transparent!important;
	    box-shadow:none!important;
	    background:linear-gradient(145deg,rgba(255,254,249,.56),rgba(255,253,246,.34))!important;
	  }
	  .prayer-friend-row + .prayer-friend-row,
	  .compact-friend-row + .compact-friend-row,
	  .testimony-entry + .testimony-entry,
	  .journal-entry-card + .journal-entry-card{
	    position:relative;
	  }
	  .prayer-friend-row + .prayer-friend-row::before,
	  .compact-friend-row + .compact-friend-row::before,
	  .testimony-entry + .testimony-entry::before,
	  .journal-entry-card + .journal-entry-card::before{
	    content:"";
	    position:absolute;
	    left:16px;
	    right:16px;
	    top:-6px;
	    height:1px;
	    background:linear-gradient(90deg,transparent,rgba(184,147,75,.18),transparent);
	  }
	  .home-shop-card.expanded,
	  .shop-card.expanded,
	  .testimony-entry.editing{
	    border-color:rgba(201,154,61,.20)!important;
	    box-shadow:0 10px 22px rgba(91,67,28,.055),inset 0 1px 0 rgba(255,255,255,.72)!important;
	  }
	  .prayer-section-tabs,
	  .testimony-category-row,
	  .home-shop-category-row,
	  .month-scroll-list,
	  .scroll-list{
	    border-bottom:1px solid rgba(184,147,75,.12);
	    padding-bottom:12px!important;
	    margin-bottom:12px!important;
	  }
	  .app-content{
	    padding:4px 8px 0!important;
	  }
	  .app-content > .today-page-calm,
	  .app-content > .prayer-page-calm,
	  .app-content > .home-page,
	  .app-content > .growth-page-calm,
	  .app-content > .premium-journal-page,
	  .app-content > .testimony-garden-page,
	  .app-content > .spirit-friends-dashboard{
	    width:100%;
	  }
	  .app-content .current-stage-card,
	  .app-content .prayer-vessels-section,
	  .app-content .journal-history-card,
	  .app-content .testimony-shared-garden,
	  .app-content .home-care-card,
	  .app-content .growth-stage-card,
	  .app-content .home-shop-shell-card{
	    margin-left:0!important;
	    margin-right:0!important;
	    width:100%!important;
	  }
	  @media (max-width:640px){
	    .app-content{
	      padding-left:0!important;
	      padding-right:0!important;
	    }
	    .app-content > .today-page-calm,
	    .app-content > .prayer-page-calm,
	    .app-content > .home-page,
	    .app-content > .growth-page-calm,
	    .app-content > .premium-journal-page,
	    .app-content > .testimony-garden-page,
	    .app-content > .spirit-friends-dashboard{
	      width:100vw!important;
	      margin-left:calc(50% - 50vw)!important;
	      margin-right:calc(50% - 50vw)!important;
	      padding-left:12px!important;
	      padding-right:12px!important;
	    }
	    .app-content .current-stage-card,
	    .app-content .prayer-vessels-section,
	    .app-content .journal-history-card,
	    .app-content .testimony-shared-garden,
	    .app-content .home-care-card,
	    .app-content .growth-stage-card{
	      border-radius:0!important;
	      padding-left:16px!important;
	      padding-right:16px!important;
	    }
	    .card{
	      border-color:transparent!important;
	      box-shadow:0 5px 14px rgba(65,82,77,.025),inset 0 1px 0 rgba(255,255,255,.58)!important;
	    }
	    .focus-card,
	    .gold-outline-card,
	    .prayer-vessel-hero,
	    .prayer-friends-card,
	    .spirit-friends-hero-card,
	    .featured-testimony-card,
	    .testimony-featured-garden{
	      border-color:rgba(201,154,61,.30)!important;
	    }
	  }

	  /* Growth full-screen calm layout: truly borderless, no divider lines. */
	  .growth-page-calm{
	    background:transparent!important;
	  }
	  .growth-page-calm > .card,
	  .growth-page-calm .growth-account-card,
	  .growth-page-calm .growth-hero-card,
	  .growth-page-calm .growth-closet-card,
	  .growth-page-calm .growth-stones-card,
	  .growth-page-calm .growth-book-card,
	  .growth-page-calm .growth-summary-card{
	    width:100%!important;
	    margin-left:0!important;
	    margin-right:0!important;
	    border-color:transparent!important;
	    outline:none!important;
	    box-shadow:none!important;
	    background:transparent!important;
	    backdrop-filter:none!important;
	  }
	  .growth-page-calm > .card + .card{
	    position:relative;
	    margin-top:4px!important;
	    padding-top:18px!important;
	  }
	  .growth-page-calm > .card + .card::before{
	    display:none!important;
	    content:none!important;
	  }
	  .growth-page-calm .growth-summary-card{
	    padding-left:16px!important;
	    padding-right:16px!important;
	  }
	  .growth-page-calm .growth-path-step,
	  .growth-page-calm .growth-summary-stat,
	  .growth-page-calm .testimony-stone-card,
	  .growth-page-calm .month-toggle{
	    border-color:transparent!important;
	    box-shadow:none!important;
	    background:linear-gradient(145deg,rgba(255,254,249,.54),rgba(255,253,246,.30))!important;
	  }
	  @media (max-width:640px){
	    .growth-page-calm{
	      padding-left:0!important;
	      padding-right:0!important;
	    }
	    .growth-page-calm > .card,
	    .growth-page-calm .growth-summary-card,
	    .growth-page-calm .growth-hero-card{
	      width:100vw!important;
	      margin-left:calc(50% - 50vw)!important;
	      margin-right:calc(50% - 50vw)!important;
	      border-radius:0!important;
	      padding-left:16px!important;
	      padding-right:16px!important;
	    }
	    .growth-page-calm > .card + .card::before{
	      display:none!important;
	      content:none!important;
	    }
	  }

	  /* Whole-app calm page pass: fewer borders, wider sections, softer hierarchy. */
	  .today-page-calm > .card,
	  .prayer-page-calm > .card,
	  .home-page > .card,
	  .premium-journal-page > .card,
	  .testimony-garden-page > .card,
	  .spirit-friends-dashboard > .sacred-card,
	  .spirit-friends-dashboard .prayer-friend-panel,
	  .home-page .home-hero-card,
	  .home-page .home-care-card,
	  .home-page .home-preview-card,
	  .home-page .home-shop-shell-card,
	  .premium-journal-page .journal-write-card,
	  .premium-journal-page .journal-history-card,
	  .testimony-garden-page .testimony-shared-garden,
	  .testimony-garden-page .testimony-featured-garden{
	    border-color:transparent!important;
	    outline:none!important;
	    box-shadow:none!important;
	  }
	  .today-page-calm > .card:not(.prayer-vessel-hero),
	  .prayer-page-calm > .card:not(.prayer-vessel-hero):not(.prayer-friends-card),
	  .home-page > .card:not(.sticky-preview-card),
	  .premium-journal-page > .card,
	  .testimony-garden-page > .card:not(.featured-testimony-card),
	  .spirit-friends-dashboard > .sacred-card:not(.spirit-friends-hero-card){
	    background:transparent!important;
	    backdrop-filter:none!important;
	  }
	  .prayer-page-calm .prayer-tab-card,
	  .prayer-page-calm .friend-card,
	  .prayer-page-calm .quick-prayer-box,
	  .prayer-page-calm .empty-state,
	  .spirit-friends-dashboard .prayer-friend-row,
	  .spirit-friends-dashboard .compact-friend-row,
	  .premium-journal-page .journal-entry-card,
	  .testimony-garden-page .testimony-entry,
	  .home-page .home-shop-card,
	  .home-page .home-category-card,
	  .quest-card{
	    border-color:transparent!important;
	    box-shadow:none!important;
	    background:linear-gradient(145deg,rgba(255,254,249,.48),rgba(255,253,246,.26))!important;
	  }
	  .prayer-section-tabs,
	  .testimony-category-row,
	  .home-shop-category-row,
	  .month-scroll-list,
	  .scroll-list{
	    border-bottom-color:transparent!important;
	  }
	  .today-page-calm > .card + .card,
	  .prayer-page-calm > .card + .card,
	  .home-page > .card + .card,
	  .premium-journal-page > .card + .card,
	  .testimony-garden-page > .card + .card,
	  .spirit-friends-dashboard > .sacred-card + .sacred-card{
	    margin-top:2px!important;
	  }
	  .prayer-friend-row + .prayer-friend-row::before,
	  .compact-friend-row + .compact-friend-row::before,
	  .testimony-entry + .testimony-entry::before,
	  .journal-entry-card + .journal-entry-card::before{
	    display:none!important;
	    content:none!important;
	  }
	  @media (max-width:640px){
	    .today-page-calm > .card,
	    .prayer-page-calm > .card,
	    .home-page > .card,
	    .premium-journal-page > .card,
	    .testimony-garden-page > .card,
	    .spirit-friends-dashboard > .sacred-card{
	      border-radius:0!important;
	      margin-left:calc(50% - 50vw)!important;
	      margin-right:calc(50% - 50vw)!important;
	      width:100vw!important;
	      padding-left:16px!important;
	      padding-right:16px!important;
	    }
	    .prayer-page-calm .prayer-vessel-hero,
	    .home-page .sticky-preview-card,
	    .spirit-friends-dashboard .spirit-friends-hero-card,
	    .testimony-garden-page .featured-testimony-card{
	      border-radius:24px!important;
	      margin-left:0!important;
	      margin-right:0!important;
	      width:100%!important;
	    }
	  }

	  /* Final spirit polish: solid, cute cloak companions with simple soul-like faces. */
	  .spirit-avatar-svg{
	    filter:drop-shadow(0 10px 14px rgba(82,55,15,.12));
	  }
	  .spirit-cute-layer{
	    opacity:1!important;
	    transform-origin:60px 96px!important;
	    filter:drop-shadow(0 10px 12px rgba(82,55,15,.12)) drop-shadow(0 0 10px rgba(255,248,223,.28));
	  }
	  .spirit-cute-layer .spirit-soft-halo{
	    opacity:.22!important;
	    filter:blur(.2px);
	  }
	  .spirit-cute-layer.her .spirit-soft-halo{
	    fill:#ffd7ef!important;
	  }
	  .spirit-cute-layer.his .spirit-soft-halo{
	    fill:#c7f4ff!important;
	  }
	  .spirit-cute-layer .spirit-hood-plush{
	    opacity:1!important;
	    stroke:rgba(255,255,255,.58);
	    stroke-width:1.4px;
	    paint-order:stroke fill;
	    filter:drop-shadow(0 5px 8px rgba(82,55,15,.11));
	  }
	  .spirit-cute-layer.her .spirit-hood-plush{
	    fill:#efb8f4!important;
	  }
	  .spirit-cute-layer.his .spirit-hood-plush{
	    fill:#9ee7cf!important;
	  }
	  .spirit-cute-layer .spirit-hood-shadow{
	    opacity:.62!important;
	    filter:none!important;
	  }
	  .spirit-cute-layer.her .spirit-hood-shadow{
	    fill:#dfa4ec!important;
	  }
	  .spirit-cute-layer.his .spirit-hood-shadow{
	    fill:#80d8bd!important;
	  }
	  .spirit-cute-layer .spirit-face-plush{
	    opacity:1!important;
	    filter:drop-shadow(0 2px 3px rgba(82,55,15,.08));
	  }
	  .spirit-cute-layer .spirit-hood-rim{
	    opacity:.74!important;
	    stroke-width:5.8px!important;
	    filter:drop-shadow(0 1px 2px rgba(255,255,255,.55));
	  }
	  .spirit-cute-layer.her .spirit-hood-rim{
	    stroke:#f5a7dc!important;
	  }
	  .spirit-cute-layer.his .spirit-hood-rim{
	    stroke:#65d6b7!important;
	  }
	  .spirit-cute-layer .spirit-body-plush{
	    opacity:1!important;
	    stroke:rgba(255,255,255,.42);
	    stroke-width:1.2px;
	    paint-order:stroke fill;
	    filter:drop-shadow(0 8px 10px rgba(82,55,15,.10));
	  }
	  .spirit-cute-layer.her .spirit-body-plush{
	    fill:#d7b6ff!important;
	  }
	  .spirit-cute-layer.his .spirit-body-plush{
	    fill:#8fd3ff!important;
	  }
	  .spirit-cute-layer .spirit-face-highlight{
	    opacity:.24!important;
	  }
	  .spirit-cute-layer .spirit-eye{
	    r:1.7px;
	    fill:#3a2606!important;
	    opacity:.78!important;
	  }
	  .spirit-cute-layer .spirit-blush{
	    opacity:.14!important;
	    filter:none!important;
	  }
	  .spirit-cute-layer .spirit-cute-smile{
	    opacity:.64!important;
	    stroke-width:1.15px!important;
	  }
	  .spirit-cute-layer .spirit-chest-symbol{
	    opacity:.92!important;
	    filter:drop-shadow(0 0 7px rgba(255,248,223,.72));
	  }
	  .spirit-cute-layer .spirit-robe-highlight{
	    opacity:.46!important;
	    stroke-width:2.4px!important;
	  }
	  .spirit-cute-layer .spirit-bottom-shadow{
	    opacity:.22!important;
	  }
	  .spirit-stage-baby .spirit-cute-layer{
	    transform:translateY(2px) scale(.94)!important;
	  }
	  .spirit-stage-baby .spirit-cute-layer .spirit-hood-plush{
	    filter:drop-shadow(0 6px 8px rgba(82,55,15,.09))!important;
	  }
	  .gender-choice-card .spirit-avatar-svg{
	    transform:scale(1.08);
	  }
	  .gender-choice-card.gender-choice-female .spirit-cute-layer.her .spirit-hood-plush{
	    fill:#f0bbf5!important;
	    opacity:1!important;
	  }
	  .gender-choice-card.gender-choice-male .spirit-cute-layer.his .spirit-hood-plush{
	    fill:#9be8cf!important;
	    opacity:1!important;
	  }
	  .gender-choice-card.gender-choice-female .spirit-body-plush,
	  .gender-choice-card.gender-choice-male .spirit-body-plush{
	    opacity:1!important;
	  }
	  .home-room .spirit-cute-layer{
	    filter:drop-shadow(0 12px 14px rgba(82,55,15,.13)) drop-shadow(0 0 16px rgba(255,248,223,.46));
	  }
	  @media (max-width:520px){
	    .gender-choice-card .spirit-avatar-svg{
	      transform:scale(1.03);
	    }
	    .home-room .spirit-cute-layer .spirit-eye{
	      r:1.85px;
	    }
	  }

	  /* Final calm surface pass: let pages breathe without boxed outlines. */
	  .card,
	  .sacred-card,
	  .focus-card,
	  .soft-guide-card,
	  .gold-outline-card{
	    border-color:transparent!important;
	    outline:none!important;
	  }
	  .today-page-calm > .card,
	  .prayer-page-calm > .card,
	  .home-page > .card,
	  .growth-page-calm > .card,
	  .premium-journal-page > .card,
	  .testimony-garden-page > .card,
	  .spirit-friends-dashboard > .sacred-card{
	    border:none!important;
	    outline:none!important;
	    box-shadow:none!important;
	    background:transparent!important;
	    backdrop-filter:none!important;
	  }
	  .today-page-calm .today-hero-card,
	  .prayer-page-calm .prayer-vessel-hero,
	  .home-page .sticky-preview-card,
	  .spirit-friends-dashboard .spirit-friends-hero-card,
	  .testimony-garden-page .featured-testimony-card,
	  .testimony-garden-page .testimony-featured-garden{
	    border:none!important;
	    outline:none!important;
	    background:linear-gradient(145deg,rgba(255,253,248,.72),rgba(255,248,223,.38),rgba(246,250,255,.20))!important;
	    box-shadow:0 12px 32px rgba(91,67,28,.045),inset 0 1px 0 rgba(255,255,255,.70)!important;
	  }
	  .prayer-page-calm .prayer-tab-card,
	  .prayer-page-calm .friend-card,
	  .prayer-page-calm .quick-prayer-box,
	  .spirit-friends-dashboard .prayer-friend-panel,
	  .spirit-friends-dashboard .prayer-friend-row,
	  .spirit-friends-dashboard .compact-friend-row,
	  .premium-journal-page .journal-entry-card,
	  .premium-journal-page .month-toggle,
	  .testimony-garden-page .testimony-entry,
	  .home-page .home-shop-card,
	  .home-page .home-category-card,
	  .growth-page-calm .growth-path-step,
	  .growth-page-calm .growth-summary-stat,
	  .growth-page-calm .testimony-stone-card{
	    border:none!important;
	    outline:none!important;
	    box-shadow:none!important;
	    background:rgba(255,253,248,.38)!important;
	  }
	  .today-page-calm > .card + .card,
	  .prayer-page-calm > .card + .card,
	  .home-page > .card + .card,
	  .growth-page-calm > .card + .card,
	  .premium-journal-page > .card + .card,
	  .testimony-garden-page > .card + .card,
	  .spirit-friends-dashboard > .sacred-card + .sacred-card{
	    margin-top:0!important;
	  }
	  .today-page-calm > .card + .card::before,
	  .prayer-page-calm > .card + .card::before,
	  .home-page > .card + .card::before,
	  .growth-page-calm > .card + .card::before,
	  .premium-journal-page > .card + .card::before,
	  .testimony-garden-page > .card + .card::before,
	  .spirit-friends-dashboard > .sacred-card + .sacred-card::before{
	    display:none!important;
	    content:none!important;
	  }
	  .prayer-section-tabs,
	  .testimony-category-row,
	  .home-shop-category-row,
	  .month-scroll-list,
	  .scroll-list{
	    border:none!important;
	  }
	  @media (max-width:640px){
	    .today-page-calm > .card,
	    .prayer-page-calm > .card,
	    .home-page > .card,
	    .growth-page-calm > .card,
	    .premium-journal-page > .card,
	    .testimony-garden-page > .card,
	    .spirit-friends-dashboard > .sacred-card{
	      width:100vw!important;
	      margin-left:calc(50% - 50vw)!important;
	      margin-right:calc(50% - 50vw)!important;
	      border-radius:0!important;
	      padding-left:16px!important;
	      padding-right:16px!important;
	    }
	    .today-page-calm .today-hero-card,
	    .prayer-page-calm .prayer-vessel-hero,
	    .home-page .sticky-preview-card,
	    .spirit-friends-dashboard .spirit-friends-hero-card,
	    .testimony-garden-page .featured-testimony-card{
	      width:calc(100vw - 24px)!important;
	      margin-left:calc(50% - 50vw + 12px)!important;
	      margin-right:calc(50% - 50vw + 12px)!important;
	      border-radius:24px!important;
	    }
	  }

	  /* Final text color pass: one solid readable color across the app. */
	  :root{
	    --sg-readable-text:#3f2a0b;
	  }
	  body,
	  #root,
	  .app-content,
	  .card,
	  .sacred-card,
	  .today-page-calm,
	  .prayer-page-calm,
	  .home-page,
	  .growth-page-calm,
	  .premium-journal-page,
	  .testimony-garden-page,
	  .spirit-friends-dashboard,
	  h1,h2,h3,h4,h5,h6,
	  p,span,small,b,strong,label,div,
	  button,input,textarea,select,
	  .serif,
	  .section-label,
	  .placed-chip,
	  .meaning-chip,
	  .prayer-status-chip,
	  .growth-path-name,
	  .growth-path-range,
	  .growth-path-status,
	  .journal-entry-text,
	  .testimony-entry,
	  .empty-state,
	  .empty-state-title{
	    color:var(--sg-readable-text)!important;
	  }
	  input::placeholder,
	  textarea::placeholder{
	    color:rgba(63,42,11,.58)!important;
	  }
	  button:disabled,
	  .muted,
	  .locked,
	  .unlock-note{
	    color:var(--sg-readable-text)!important;
	    opacity:.72;
	  }


	  /* Gold diamond pass: keep Home gem accents warm and cohesive. */
	  .home-gem,
	  .home-path-gem,
	  .home-room .home-gem,
	  .home-room .home-path-gem{
	    background:linear-gradient(135deg,#fff9d9 0%,#ffe8a3 28%,#f1c24c 62%,#b77a08 100%)!important;
	    border-color:rgba(183,122,8,.30)!important;
	    box-shadow:inset 0 6px 10px rgba(255,255,255,.58),inset 0 -7px 12px rgba(112,72,9,.13),0 0 12px rgba(226,174,57,.20),0 5px 10px rgba(82,55,15,.08)!important;
	    filter:saturate(1.04) brightness(1.02)!important;
	  }
	  .home-gem::before,
	  .home-path-gem::after{
	    background:linear-gradient(135deg,rgba(255,255,255,.92),rgba(255,242,188,.56))!important;
	    border-color:rgba(255,255,255,.46)!important;
	    box-shadow:0 0 12px rgba(255,223,126,.32)!important;
	  }
	  .home-gem.g3,
	  .home-gem.g4,
	  .home-path-gem.pg1,
	  .home-path-gem.pg2,
	  .home-path-gem.pg3{
	    background:linear-gradient(135deg,#fffdf0 0%,#ffe8a3 36%,#e7b638 72%,#a96f05 100%)!important;
	  }

	  /* Onboarding calm typography: remove chunky/heavy text without changing the flow. */
	  .onboarding-growth-page,
	  .onboarding-growth-page *,
	  .gender-choice-card,
	  .gender-choice-card *,
	  .spirit-name-card,
	  .spirit-name-card *{
	    font-family:Nunito,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
	    font-weight:500!important;
	    letter-spacing:0!important;
	  }
	  .onboarding-growth-title,
	  .onboarding-growth-page h1,
	  .onboarding-growth-page h2,
	  .onboarding-growth-page .serif,
	  .gender-choice-card h2,
	  .gender-choice-card h3,
	  .spirit-name-card h2,
	  .spirit-name-card h3{
	    font-family:"Cormorant Garamond",Georgia,serif!important;
	    font-weight:600!important;
	    letter-spacing:0!important;
	  }
	  .onboarding-growth-flow-title,
	  .onboarding-growth-label,
	  .onboarding-growth-card-title,
	  .onboarding-growth-cta,
	  .gender-choice-title,
	  .gender-choice-card .section-label,
	  .spirit-name-card .section-label{
	    font-weight:600!important;
	  }
	  .onboarding-growth-note,
	  .onboarding-growth-page p,
	  .gender-choice-card p,
	  .spirit-name-card p{
	    font-weight:500!important;
	  }

	  /* Extra calm page pass: softer dividers instead of boxed-in cards. */
	  .today-page-calm .card,
	  .prayer-page-calm .card,
	  .home-page .card,
	  .growth-page-calm .card,
	  .premium-journal-page .card,
	  .testimony-garden-page .card,
	  .spirit-friends-dashboard .sacred-card{
	    border-color:transparent!important;
	  }
	  .today-page-calm .card:not(:first-child),
	  .prayer-page-calm .card:not(:first-child),
	  .home-page .card:not(:first-child),
	  .growth-page-calm .card:not(:first-child),
	  .premium-journal-page .card:not(:first-child),
	  .testimony-garden-page .card:not(:first-child){
	    box-shadow:inset 0 1px 0 rgba(144,109,42,.08)!important;
	  }


	  /* Home neglect message layering: keep the dim world behind, message in front of the spirit. */
	  .home-neglect-overlay{
	    z-index:18!important;
	  }
	  .home-neglect-message{
	    position:absolute!important;
	    top:18px!important;
	    bottom:auto!important;
	    left:50%!important;
	    transform:translateX(-50%)!important;
	    z-index:90!important;
	    width:min(84%,360px)!important;
	    background:rgba(34,27,18,.76)!important;
	    border:1px solid rgba(255,232,163,.30)!important;
	    box-shadow:0 14px 30px rgba(20,12,4,.24),inset 0 1px 0 rgba(255,255,255,.14)!important;
	  }
	  .home-neglect-message p{
	    color:#fff0b8!important;
	    font-weight:600!important;
	  }

	  /* Soft ivory page backdrop with brighter calm cards. */
	  :root{
	    --sg-app-bg:#fffaf0;
	    --sg-card-bright:rgba(255,255,255,.96);
	    --sg-card-brighter:rgba(255,255,255,.985);
	  }
	  body,
	  #root,
	  .app-shell,
	  .app-content,
	  .today-page-calm,
	  .prayer-page-calm,
	  .home-page,
	  .growth-page-calm,
	  .premium-journal-page,
	  .testimony-garden-page,
	  .spirit-friends-dashboard{
	    background:var(--sg-app-bg)!important;
	  }
	  .card,
	  .sacred-card,
	  .focus-card,
	  .soft-guide-card,
	  .prayer-friend-panel,
	  .friend-card,
	  .home-shop-card,
	  .home-category-card,
	  .testimony-entry,
	  .journal-entry-card,
	  .growth-path-step,
	  .growth-summary-stat,
	  .testimony-stone-card,
	  .prayer-tab-card,
	  .quick-prayer-box{
	    background:var(--sg-card-bright)!important;
	  }
	  .today-page-calm .today-hero-card,
	  .prayer-page-calm .prayer-vessel-hero,
	  .home-page .sticky-preview-card,
	  .spirit-friends-dashboard .spirit-friends-hero-card,
	  .testimony-garden-page .featured-testimony-card,
	  .testimony-garden-page .testimony-featured-garden{
	    background:linear-gradient(145deg,var(--sg-card-brighter),rgba(255,250,238,.88))!important;
	    box-shadow:0 14px 36px rgba(91,67,28,.075),inset 0 1px 0 rgba(255,255,255,.82)!important;
	  }
	  .remove-friend-btn{
	    background:rgba(255,255,255,.72)!important;
	    border:1px solid rgba(144,109,42,.16)!important;
	    color:rgba(63,42,11,.78)!important;
	  }
	  @media (max-width:520px){
	    .home-neglect-message{
	      top:14px!important;
	      width:min(88%,320px)!important;
	      padding:9px 12px!important;
	    }
	    .home-neglect-message p{
	      font-size:12px!important;
	      line-height:1.38!important;
	    }
	    .compact-friend-actions{
	      flex-wrap:wrap!important;
	      justify-content:flex-end!important;
	    }
	  }


	

	  /* Final Home polish: keep dim progress notice high and make premium effects brighter. */
	  .home-neglect-message{
	    top:18px!important;
	    transform:translateX(-50%)!important;
	    z-index:95!important;
	  }
	  .home-room .home-background-rainbow{
	    top:-18px!important;
	    width:min(176%,900px)!important;
	    height:min(62%,310px)!important;
	    opacity:.66!important;
	    filter:saturate(1.18) brightness(1.12) drop-shadow(0 0 30px rgba(255,248,223,.72)) drop-shadow(0 10px 38px rgba(196,181,253,.20))!important;
	  }
	  .home-room .home-background-rainbow::before{
	    background:
	      conic-gradient(from 210deg at 50% 100%,
	        transparent 0 10.5deg,
	        rgba(248,113,113,.68) 10.5deg 15deg,
	        rgba(251,191,36,.68) 15deg 19.5deg,
	        rgba(134,239,172,.60) 19.5deg 24deg,
	        rgba(125,211,252,.62) 24deg 28.5deg,
	        rgba(196,181,253,.58) 28.5deg 33deg,
	        rgba(249,168,212,.54) 33deg 37.5deg,
	        transparent 37.5deg 360deg)!important;
	    mask:radial-gradient(ellipse at 50% 100%,transparent 0 45%,#000 46% 66%,transparent 67%)!important;
	    -webkit-mask:radial-gradient(ellipse at 50% 100%,transparent 0 45%,#000 46% 66%,transparent 67%)!important;
	  }
	  .home-heavenly-effect.eff_covenant_rainbow .effect-rainbow{
	    left:-44%!important;
	    right:-44%!important;
	    top:-2%!important;
	    height:82%!important;
	    opacity:.92!important;
	    border-top-width:19px!important;
	    filter:blur(.12px) saturate(1.18) brightness(1.08)!important;
	    box-shadow:
	      0 -16px 0 rgba(251,191,36,.34),
	      0 -32px 0 rgba(134,239,172,.29),
	      0 -48px 0 rgba(125,211,252,.29),
	      0 -64px 0 rgba(196,181,253,.25),
	      0 -80px 0 rgba(249,168,212,.22),
	      0 24px 56px rgba(255,255,255,.34)!important;
	  }
	  .home-heavenly-effect.eff_glory_rays{
	    filter:drop-shadow(0 0 22px rgba(255,247,210,.54));
	  }
	  .home-heavenly-effect.eff_glory_rays::before{
	    width:min(68%,310px)!important;
	    height:min(62%,310px)!important;
	    background:
	      radial-gradient(circle,rgba(255,255,255,.72) 0%,rgba(255,247,207,.42) 30%,rgba(250,204,21,.19) 56%,transparent 76%)!important;
	    opacity:.95!important;
	  }
	  .home-heavenly-effect.eff_glory_rays .effect-ray{
	    width:42px!important;
	    height:238px!important;
	    background:linear-gradient(180deg,rgba(255,255,255,.92),rgba(255,246,198,.62) 25%,rgba(250,204,21,.25) 58%,transparent 100%)!important;
	    opacity:.86!important;
	    filter:blur(1px)!important;
	  }
	  @media(max-width:560px){
	    .home-neglect-message{top:12px!important;}
	    .home-room .home-background-rainbow{top:-2px!important;width:190%!important;height:48%!important;opacity:.58!important;}
	    .home-heavenly-effect.eff_covenant_rainbow .effect-rainbow{left:-58%!important;right:-58%!important;top:0!important;height:70%!important;border-top-width:15px!important;}
	    .home-heavenly-effect.eff_glory_rays .effect-ray{height:198px!important;width:32px!important;top:12%!important;}
	    .home-heavenly-effect.eff_glory_rays::before{width:74%!important;height:48%!important;}
	  }



	  /* Calm text pass: hide empty helper lines and shorten Home shop currency visually. */
	  .home-page .home-hero-card p:empty,
	  .home-page .home-shop-shell-card p:empty,
	  .testimony-share-garden p:empty,
	  #my-prayers-list h3 + p:empty{
	    display:none!important;
	  }
	  .home-shop-price{
	    font-size:12px!important;
	    letter-spacing:.01em!important;
	    color:#9a6a14!important;
	  }
	  .home-page .home-righteousness-count > div:first-child{
	    font-size:10px!important;
	    letter-spacing:.08em!important;
	    font-weight:600!important;
	  }
	  .premium-journal-page .journal-write-card p,
	  .testimony-garden-page .empty-state p:not(.empty-state-title){
	    max-width:34rem;
	  }



	  /* Hide trimmed helper copy areas so Home, Journal, and Testimony stay airy. */
	  .home-page .home-hero-card h2 + p,
	  .home-page .home-shop-shell-card h3 + p,
	  .testimony-share-garden h3 + p{
	    display:none!important;
	    margin:0!important;
	  }
	  .home-page .home-hero-card,
	  .home-page .home-shop-shell-card,
	  .testimony-share-garden,
	  .premium-journal-page .journal-write-card{
	    padding-block:14px!important;
	  }



	  /* Final rainbow and spirit-name polish. */
	  .home-room .home-background-rainbow{
	    top:6px!important;
	    width:min(118%,610px)!important;
	    height:min(56%,260px)!important;
	    opacity:.66!important;
	    filter:saturate(.92) brightness(1.06) drop-shadow(0 0 16px rgba(255,248,223,.48))!important;
	  }
	  .home-room .home-background-rainbow::before{
	    background:
	      conic-gradient(from 207deg at 50% 100%,
	        transparent 0 15deg,
	        rgba(252,165,165,.56) 15deg 20deg,
	        rgba(253,224,138,.58) 20deg 25deg,
	        rgba(187,247,208,.50) 25deg 30deg,
	        rgba(186,230,253,.54) 30deg 35deg,
	        rgba(221,214,254,.52) 35deg 40deg,
	        transparent 40deg 360deg)!important;
	    mask:radial-gradient(ellipse at 50% 100%,transparent 0 45%,#000 46% 68%,transparent 69%)!important;
	    -webkit-mask:radial-gradient(ellipse at 50% 100%,transparent 0 45%,#000 46% 68%,transparent 69%)!important;
	  }
	  .home-heavenly-effect.eff_covenant_rainbow .effect-rainbow{
	    left:-10%!important;
	    right:-10%!important;
	    top:4%!important;
	    height:70%!important;
	    opacity:.78!important;
	    border-top:13px solid rgba(252,165,165,.34)!important;
	    border-left:13px solid transparent!important;
	    border-right:13px solid transparent!important;
	    box-shadow:
	      0 -12px 0 rgba(253,224,138,.34),
	      0 -24px 0 rgba(187,247,208,.28),
	      0 -36px 0 rgba(186,230,253,.30),
	      0 -48px 0 rgba(221,214,254,.26),
	      0 16px 34px rgba(255,255,255,.24)!important;
	    filter:saturate(.94) brightness(1.05) blur(.06px)!important;
	  }
	  .today-page-calm h1.serif{
	    color:#9a6a14!important;
	    text-shadow:none!important;
	  }
	  .app-header .app-title-copy h1.serif,
	  .today-page-calm .app-title-copy h1.serif,
	  .growth-page-calm .app-title-copy h1.serif{
	    color:#9a6a14!important;
	  }
	  .home-page .home-righteousness-count > div:first-child{
	    font-size:9px!important;
	    line-height:1!important;
	    letter-spacing:.08em!important;
	    font-weight:600!important;
	    color:#8f651d!important;
	  }
	  @media(max-width:560px){
	    .home-room .home-background-rainbow{top:8px!important;width:142%!important;height:38%!important;opacity:.68!important;}
	    .home-heavenly-effect.eff_covenant_rainbow .effect-rainbow{left:-26%!important;right:-26%!important;top:9%!important;height:54%!important;border-top-width:12px!important;border-left-width:12px!important;border-right-width:12px!important;}
	  }

	  /* Armor Closet: keep Halo and Wings side by side. */
	  .closet-pieces-grid{
	    display:grid!important;
	    grid-template-columns:repeat(2,minmax(0,1fr))!important;
	    gap:12px!important;
	  }
	  .closet-pieces-grid .closet-item-btn{
	    width:100%!important;
	    min-width:0!important;
	    max-width:none!important;
	  }

  /* Gold decorative symbols: keep diamonds/stars gold after the solid text pass. */
	  .onboarding-growth-sparkle,
	  .daily-entry-sparkle,
	  .prayer-vessel-particle,
	  .home-palace-star,
	  .home-spirit-life,
	  .build-spark,
	  .builder-angel::after,
	  .home-transform-mini::after,
	  .home-background-rainbow::after,
	  .home-heavenly-veil::after,
	  .prayer-vessel-kicker::before,
	  .home-floating-particle,
	  .testimony-featured-garden .section-label::before{
	    color:#c99012!important;
	    text-shadow:0 0 10px rgba(240,203,118,.62)!important;
	  }
	  .onboarding-growth-cta::after,
	  .gold-btn::after{
	    color:#c99012!important;
	  }
	  .stat-pill span:first-child,
	  .placed-chip span:first-child,
	  .meaning-chip span:first-child{
	    color:#c99012!important;
	  }
	  .gem-icon,
	  .diamond-icon,
	  [class*="diamond"],
	  [class*="gem"]{
	    color:#c99012!important;
	  }

	  /* Calm border pass: softer surfaces, fewer boxed outlines. */
	  .app-shell .card,
	  .today-page-calm .card,
	  .prayer-page-calm .card,
	  .growth-page-calm .card,
	  .home-page .card,
	  .premium-journal-page .card,
	  .testimony-garden-page .card,
	  .spirit-friends-dashboard .card,
	  .onboarding-growth-panel,
	  .onboarding-step-card,
	  .onboarding-stage-card{
	    border-color:rgba(117,96,61,.10)!important;
	    box-shadow:
	      0 1px 0 rgba(255,255,255,.78) inset,
	      0 10px 28px rgba(94,73,38,.055)!important;
	  }
	  .app-shell .card:not(.gold-outline-card):not(.solid-gold-line):not(.solid-blue-outline-card),
	  .home-shop-shell-card,
	  .journal-write-card,
	  .testimony-share-garden,
	  .spirit-friends-dashboard .card{
	    background:rgba(255,255,255,.72)!important;
	  }
	  .section-divider-line,
	  .timeline-line,
	  .soft-divider{
	    opacity:.38!important;
	  }
	  .onboarding-growth-panel,
	  .onboarding-stage-card,
	  .onboarding-step-card{
	    background:linear-gradient(145deg,rgba(255,255,255,.72),rgba(255,251,240,.58))!important;
	  }
	  .onboarding-growth-shell,
	  .onboarding-spirit-growth-preview,
	  .onboarding-growth-flow{
	    border-color:rgba(199,159,85,.14)!important;
	    box-shadow:0 14px 36px rgba(94,73,38,.055),inset 0 1px 0 rgba(255,255,255,.82)!important;
	  }
	  .onboarding-stage-preview,
	  .onboarding-growth-step{
	    border:0!important;
	    outline:0!important;
	    background:linear-gradient(145deg,rgba(255,255,255,.78),rgba(255,252,244,.58))!important;
	    box-shadow:0 8px 18px rgba(94,73,38,.04),inset 0 1px 0 rgba(255,255,255,.86)!important;
	  }
	  .onboarding-growth-preview-arrow::before,
	  .onboarding-spirit-growth-preview::before{
	    opacity:.22!important;
	  }
	  .onboarding-stage-preview span,
	  .onboarding-growth-label{
	    color:#4f3b1d!important;
	    font-weight:650!important;
	  }
	  .friends-top-chip{
	    position:relative!important;
	    overflow:visible!important;
	  }
	  .friend-heart-dot{
	    position:absolute;
	    right:-7px;
	    top:-9px;
	    width:18px;
	    height:18px;
	    border-radius:999px;
	    display:inline-flex;
	    align-items:center;
	    justify-content:center;
	    background:rgba(255,255,255,.98)!important;
	    color:#ff2d55!important;
	    font-size:12px;
	    line-height:1;
	    font-weight:800;
	    z-index:6;
	    box-shadow:0 2px 8px rgba(55,42,20,.12),0 0 0 1px rgba(255,45,85,.10)!important;
	    text-shadow:0 1px 5px rgba(255,45,85,.24);
	    animation:friendHeartPulse 2.6s ease-in-out infinite;
	  }
	  @keyframes friendHeartPulse{
	    0%,100%{transform:scale(1);}
	    50%{transform:scale(1.12);}
	  }
	  .friend-heart-inline{
	    position:relative!important;
	    right:auto!important;
	    top:auto!important;
	    margin-left:2px;
	    flex-shrink:0;
	  }
	  .tab-icon-wrap{position:relative;}
	  .tab-heart-dot{
	    position:absolute;
	    right:-7px;
	    top:-6px;
	    width:16px;
	    height:16px;
	    border-radius:999px;
	    display:inline-flex;
	    align-items:center;
	    justify-content:center;
	    background:rgba(255,255,255,.98)!important;
	    color:#ff2d55!important;
	    font-family:Arial,sans-serif!important;
	    font-size:10px;
	    font-weight:800;
	    line-height:1;
	    z-index:5;
	    box-shadow:0 2px 8px rgba(55,42,20,.12),0 0 0 1px rgba(255,45,85,.10)!important;
	    text-shadow:0 1px 5px rgba(255,45,85,.24)!important;
	    animation:friendHeartPulse 2.6s ease-in-out infinite;
	  }
	  @media(max-width:560px){
	    .friend-heart-dot{right:-6px;top:-8px;width:17px;height:17px;font-size:11px;}
	    .onboarding-growth-panel{padding:18px!important;}
	    .onboarding-step-card{padding:13px 14px!important;}
	  }


	  /* Final title scale + Home Preview edge cleanup. */
	  .onboarding-growth-flow-title{
	    font-size:clamp(22px,4.4vw,30px)!important;
	    line-height:1.12!important;
	  }
	  .onboarding-growth-label{
	    font-size:clamp(18px,3.6vw,24px)!important;
	    line-height:1.16!important;
	    letter-spacing:0!important;
	  }
	  .home-page .sticky-preview-card .home-room,
	  .home-page .home-room{
	    border:0!important;
	    outline:0!important;
	    box-shadow:none!important;
	    overflow:hidden!important;
	    background-clip:padding-box!important;
	  }
	  .home-page .home-room::before,
	  .home-page .home-room::after,
	  .home-page .home-background-rainbow,
	  .home-page .home-background-rainbow::before,
	  .home-page .home-background-rainbow::after,
	  .home-page .home-heavenly-effect,
	  .home-page .effect-rainbow,
	  .home-page .home-rays,
	  .home-page .home-heavenly-veil{
	    border:0!important;
	    outline:0!important;
	    box-shadow:none!important;
	    background-color:transparent!important;
	  }
	  @media(max-width:560px){
	    .onboarding-growth-flow-title{font-size:21px!important;}
	    .onboarding-growth-label{font-size:17px!important;}
	  }


	  /* Final onboarding diamond color pass. */
	  .onboarding-growth-page .onboarding-growth-sparkle,
	  .onboarding-growth-page .onboarding-stage-preview::before,
	  .onboarding-growth-page .onboarding-stage-preview::after,
	  .onboarding-growth-page .onboarding-spirit-growth-preview::before,
	  .onboarding-growth-page .onboarding-spirit-growth-preview::after,
	  .onboarding-growth-page .onboarding-growth-step::before,
	  .onboarding-growth-page .onboarding-growth-step::after,
	  .onboarding-growth-page [aria-hidden="true"]{
	    color:#c99012!important;
	    text-shadow:0 0 12px rgba(240,203,118,.62)!important;
	  }
	  .onboarding-growth-page .onboarding-growth-sparkle.s2{
	    color:#d9a321!important;
	  }


  /* Final Spirit Home progress polish: organic sanctuary homes, no sharp blocks. */
  .home-room .home-cardboard,
  .home-room .home-little-shelter,
  .home-room .home-house,
  .home-room .home-mansion,
  .home-room .home-palace,
  .home-room .home-chapel{
    z-index:5!important;
    overflow:visible!important;
    filter:
      drop-shadow(0 18px 22px rgba(93,67,28,.12))
      drop-shadow(0 0 18px rgba(255,248,223,.30))!important;
  }
  .home-room .home-cardboard::after,
  .home-room .home-little-shelter::after,
  .home-room .home-house::after,
  .home-room .home-mansion::after,
  .home-room .home-palace::after,
  .home-room .home-chapel::after{
    content:""!important;
    position:absolute!important;
    left:2%!important;
    right:2%!important;
    bottom:-13px!important;
    height:26px!important;
    border-radius:50%!important;
    z-index:-1!important;
    background:
      radial-gradient(circle at 23% 46%,rgba(187,247,208,.50) 0 8px,transparent 9px),
      radial-gradient(circle at 68% 36%,rgba(253,224,138,.45) 0 7px,transparent 8px),
      radial-gradient(ellipse at 50% 50%,rgba(115,142,87,.22),rgba(82,55,15,.08) 58%,transparent 72%)!important;
    filter:blur(.2px)!important;
  }
  .home-room .home-cardboard-body,
  .home-room .home-little-shelter-body,
  .home-room .home-house-body,
  .home-room .home-mansion-body,
  .home-room .home-palace-body,
  .home-room .home-chapel-body{
    border:0!important;
    border-radius:38px 38px 18px 18px / 44px 44px 18px 18px!important;
    background:
      radial-gradient(circle at 24% 18%,rgba(255,255,255,.78),transparent 21%),
      radial-gradient(circle at 78% 24%,rgba(255,248,223,.56),transparent 25%),
      linear-gradient(160deg,rgba(255,255,255,.72),transparent 36%),
      linear-gradient(180deg,#fffdf8 0%,#fff0c7 50%,#d8aa42 100%)!important;
    box-shadow:
      inset 0 12px 18px rgba(255,255,255,.68),
      inset 0 -12px 18px rgba(126,83,15,.09),
      0 10px 18px rgba(82,55,15,.08),
      0 0 24px rgba(240,203,118,.18)!important;
  }
  .home-room .home-cardboard-body{
    border-radius:32px 32px 16px 16px / 36px 36px 16px 16px!important;
    background:
      radial-gradient(circle at 28% 20%,rgba(255,248,223,.52),transparent 26%),
      linear-gradient(150deg,rgba(255,255,255,.30),transparent 38%),
      linear-gradient(180deg,#eec786 0%,#d39852 58%,#b97838 100%)!important;
  }
  .home-room .home-cardboard-roof,
  .home-room .home-little-shelter-roof,
  .home-room .home-house-roof,
  .home-room .home-mansion-roof,
  .home-room .home-palace-roof,
  .home-room .home-chapel-roof{
    clip-path:none!important;
    border:0!important;
    border-radius:999px 999px 28px 28px / 86px 86px 24px 24px!important;
    background:
      radial-gradient(circle at 42% 18%,rgba(255,255,255,.76),transparent 24%),
      linear-gradient(180deg,#fff8df 0%,#f0cb76 54%,#c99012 100%)!important;
    box-shadow:
      inset 0 8px 12px rgba(255,255,255,.58),
      inset 0 -8px 14px rgba(126,83,15,.10),
      0 8px 12px rgba(82,55,15,.08),
      0 0 18px rgba(255,248,223,.34)!important;
  }
  .home-room .home-cardboard-roof{
    left:10px!important;
    right:10px!important;
    top:3px!important;
    height:38px!important;
    transform:translateY(3px) scaleX(.94)!important;
  }
  .home-room .home-little-shelter-roof,
  .home-room .home-house-roof{
    left:7px!important;
    right:7px!important;
    transform:translateY(4px) scaleX(.96)!important;
  }
  .home-room .home-mansion-roof,
  .home-room .home-palace-roof{
    transform:translateY(2px) scaleX(.98)!important;
  }
  .home-room .home-tower,
  .home-room .home-palace-column,
  .home-room .home-mansion-column{
    border:0!important;
    border-radius:999px 999px 18px 18px / 58px 58px 16px 16px!important;
    background:
      radial-gradient(circle at 35% 14%,rgba(255,255,255,.62),transparent 24%),
      linear-gradient(180deg,#fffdf8,#f3d889 62%,#d7a43a)!important;
    box-shadow:inset 5px 0 9px rgba(255,255,255,.50),inset -4px 0 8px rgba(126,83,15,.08),0 5px 10px rgba(82,55,15,.07)!important;
  }
  .home-room .home-cardboard-door,
  .home-room .home-little-shelter-door,
  .home-room .home-house-door,
  .home-room .home-mansion-door,
  .home-room .home-door,
  .home-room .home-chapel-door{
    border:0!important;
    border-radius:999px 999px 10px 10px!important;
    background:
      radial-gradient(circle at 50% 18%,#fff 0,#fff8df 32%,#e3b64a 70%,#8a5a08 100%)!important;
    box-shadow:0 0 20px rgba(255,248,223,.74),inset 0 4px 8px rgba(255,255,255,.62)!important;
  }
  .home-room .home-window-dot,
  .home-room .home-little-shelter-window,
  .home-room .home-mansion-window,
  .home-room .home-chapel-window{
    border:0!important;
    border-radius:999px!important;
    background:radial-gradient(circle at 48% 35%,#fff 0,#fff8df 40%,rgba(240,203,118,.60) 100%)!important;
    box-shadow:0 0 12px rgba(255,248,223,.68),inset 0 2px 5px rgba(255,255,255,.82)!important;
  }
  .home-room .home-flowerbed{
    opacity:.72!important;
    filter:saturate(.92) brightness(1.03)!important;
  }
  .home-room .home-midground-glow{
    border-radius:50%!important;
    background:radial-gradient(ellipse at 50% 70%,rgba(255,248,223,.45),rgba(187,247,208,.16) 48%,transparent 72%)!important;
  }
  .home-room .home-palace-star{
    color:#c99012!important;
    text-shadow:0 0 12px rgba(240,203,118,.62)!important;
  }

  /* Final simple shelter image stage: cozy sanctuary asset behind the spirit. */
  .home-room .home-simple-shelter-image{
    top:31%!important;
    left:50%!important;
    width:clamp(190px,56vw,250px)!important;
    height:auto!important;
    transform:translateX(-50%)!important;
    z-index:4!important;
    filter:
      drop-shadow(0 18px 20px rgba(70,48,22,.18))
      drop-shadow(0 0 22px rgba(255,236,170,.38))!important;
  }
  .home-room .home-simple-shelter-image img{
    display:block;
    width:100%;
    height:auto;
    border:0;
    border-radius:34px;
    object-fit:contain;
    mix-blend-mode:multiply;
    opacity:.96;
    -webkit-mask-image:radial-gradient(ellipse at 50% 46%,#000 0 48%,rgba(0,0,0,.92) 58%,rgba(0,0,0,.35) 73%,transparent 88%);
    mask-image:radial-gradient(ellipse at 50% 46%,#000 0 48%,rgba(0,0,0,.92) 58%,rgba(0,0,0,.35) 73%,transparent 88%);
  }
  .home-room .home-simple-shelter-image::before{
    content:"";
    position:absolute;
    left:19%;
    right:19%;
    top:32%;
    height:42%;
    border-radius:50%;
    background:radial-gradient(ellipse,rgba(255,221,117,.32),transparent 68%);
    filter:blur(10px);
    pointer-events:none;
    z-index:1;
  }
  .home-room .home-simple-shelter-image::after{
    left:10%!important;
    right:10%!important;
    bottom:2px!important;
    height:24px!important;
    background:radial-gradient(ellipse,rgba(87,67,35,.18),transparent 72%)!important;
  }
  .home-room .home-stage-0 .home-simple-shelter-image,
  .home-room.home-stage-0 .home-simple-shelter-image{
    z-index:4!important;
  }
  .home-room .home-cozy-shelter-image{
    top:16%!important;
    left:50%!important;
    width:clamp(196px,58vw,264px)!important;
    height:auto!important;
    transform:translateX(-50%)!important;
    z-index:4!important;
    pointer-events:none!important;
    background:transparent!important;
    filter:drop-shadow(0 16px 18px rgba(70,48,22,.14)) drop-shadow(0 0 14px rgba(255,226,150,.26))!important;
  }
  .home-room .home-cozy-shelter-image img{
    display:block!important;
    width:100%!important;
    height:auto!important;
    object-fit:contain!important;
    border:0!important;
    border-radius:0!important;
    opacity:.96!important;
    transform:translateY(-7%) scale(.98)!important;
    -webkit-mask-image:radial-gradient(ellipse at 50% 54%,#000 0 62%,rgba(0,0,0,.86) 76%,transparent 94%)!important;
    mask-image:radial-gradient(ellipse at 50% 54%,#000 0 62%,rgba(0,0,0,.86) 76%,transparent 94%)!important;
  }

  .home-room .home-gate{display:none!important;}
  .home-room .home-lantern{display:none!important;}
  .home-room .home-heavenly-cottage-image{
    top:11%!important;
    left:50%!important;
    width:clamp(196px,58vw,268px)!important;
    height:auto!important;
    transform:translateX(-50%)!important;
    z-index:4!important;
    pointer-events:none!important;
    background:transparent!important;
    filter:drop-shadow(0 18px 24px rgba(70,48,22,.15)) drop-shadow(0 0 18px rgba(255,226,150,.24))!important;
  }
  .home-room .home-heavenly-cottage-image img{
    display:block!important;
    width:100%!important;
    height:auto!important;
    object-fit:contain!important;
    border:0!important;
    border-radius:0!important;
    opacity:.94!important;
    transform:translateY(-8%) scale(.98)!important;
    -webkit-mask-image:radial-gradient(ellipse at 50% 58%,#000 0 48%,rgba(0,0,0,.90) 64%,rgba(0,0,0,.26) 80%,transparent 93%)!important;
    mask-image:radial-gradient(ellipse at 50% 58%,#000 0 48%,rgba(0,0,0,.90) 64%,rgba(0,0,0,.26) 80%,transparent 93%)!important;
  }

  .home-room .home-garden-home-image{
    top:7%!important;
    left:50%!important;
    width:clamp(210px,62vw,292px)!important;
    height:auto!important;
    transform:translateX(-50%)!important;
    z-index:4!important;
    pointer-events:none!important;
    background:transparent!important;
    filter:drop-shadow(0 18px 24px rgba(70,48,22,.14)) drop-shadow(0 0 18px rgba(255,226,150,.22))!important;
  }
  .home-room .home-garden-home-image img{
    display:block!important;
    width:100%!important;
    height:auto!important;
    object-fit:contain!important;
    border:0!important;
    border-radius:0!important;
    opacity:.94!important;
    transform:translateY(-6%) scale(.98)!important;
    -webkit-mask-image:radial-gradient(ellipse at 50% 55%,#000 0 54%,rgba(0,0,0,.88) 70%,rgba(0,0,0,.26) 84%,transparent 95%)!important;
    mask-image:radial-gradient(ellipse at 50% 55%,#000 0 54%,rgba(0,0,0,.88) 70%,rgba(0,0,0,.26) 84%,transparent 95%)!important;
  }

  .home-room .home-radiant-heavenly-home-image{
    inset:0!important;
    top:0!important;
    left:0!important;
    width:100%!important;
    height:100%!important;
    transform:none!important;
    z-index:3!important;
    pointer-events:none!important;
    overflow:hidden!important;
    background:transparent!important;
    filter:none!important;
  }
  .home-room .home-radiant-heavenly-home-image img{
    display:block!important;
    width:100%!important;
    height:100%!important;
    object-fit:cover!important;
    object-position:center top!important;
    border:0!important;
    border-radius:0!important;
    opacity:.92!important;
    transform:none!important;
    mix-blend-mode:normal!important;
    -webkit-mask-image:none!important;
    mask-image:none!important;
  }

  @media(max-width:520px){
    .home-room .home-simple-shelter-image{
      top:34%!important;
      width:clamp(174px,61vw,224px)!important;
    }
  }

  /* Final premium Home Shop polish: calmer collectible cards and clearer states. */
  .home-page .home-shop-shell-card{
    background:rgba(255,255,255,.82)!important;
    border:1px solid rgba(230,215,184,.58)!important;
    box-shadow:0 12px 34px rgba(53,40,29,.045)!important;
  }
  .home-page .home-shop-shell-card h3{
    color:#35281d!important;
    font-weight:600!important;
    letter-spacing:.01em!important;
  }
  .home-shop-category-row{
    gap:8px!important;
    padding:2px 1px 8px!important;
    scrollbar-width:none;
  }
  .home-shop-category-row::-webkit-scrollbar{display:none;}
  .home-category-card{
    min-width:108px!important;
    padding:9px 9px 10px!important;
    border-radius:18px!important;
    border:1px solid rgba(230,215,184,.62)!important;
    background:rgba(255,255,255,.68)!important;
    box-shadow:0 6px 16px rgba(53,40,29,.04)!important;
    transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease, background .18s ease!important;
  }
  .home-category-card.active{
    border-color:rgba(215,167,61,.72)!important;
    background:linear-gradient(180deg,rgba(255,252,244,.98),rgba(255,246,221,.86))!important;
    box-shadow:0 9px 22px rgba(184,134,31,.12)!important;
    transform:translateY(-1px);
  }
  .home-category-card:hover{
    transform:translateY(-1px);
    box-shadow:0 9px 22px rgba(53,40,29,.07)!important;
  }
  .home-category-card .meaning-chip,
  .home-shop-card .meaning-chip{
    border:0!important;
    background:transparent!important;
    color:#8b7b69!important;
    box-shadow:none!important;
    padding:0!important;
    border-radius:0!important;
    text-transform:uppercase!important;
    letter-spacing:.08em!important;
    font-size:9.5px!important;
    font-weight:600!important;
    line-height:1.15!important;
  }
  .home-category-card > div[style*="font-size: 13"]{
    color:#35281d!important;
    font-weight:650!important;
    font-size:12.5px!important;
    line-height:1.1!important;
  }
  .home-category-card .home-shop-preview{
    min-height:44px!important;
    margin-bottom:4px!important;
  }
  .home-category-card .home-game-item{
    filter:drop-shadow(0 5px 9px rgba(53,40,29,.10))!important;
  }
  .home-category-card .home-game-glow,
  .home-shop-card .home-game-glow{
    opacity:.16!important;
    filter:blur(2px)!important;
  }
  .home-shop-section-header{
    display:flex;
    align-items:flex-end;
    justify-content:space-between;
    gap:12px;
    margin:5px 2px 10px;
    padding:0 2px 9px;
    border-bottom:1px solid rgba(230,215,184,.50);
  }
  .home-shop-section-header h3{
    margin:2px 0 0;
    color:#35281d;
    font-family:Georgia, "Times New Roman", serif;
    font-size:18px;
    font-weight:500;
    letter-spacing:.01em;
  }
  .home-shop-eyebrow{
    display:block;
    color:#b8861f;
    font-size:10px;
    font-weight:650;
    letter-spacing:.12em;
    text-transform:uppercase;
  }
  .home-shop-collected{
    flex-shrink:0;
    color:#8b7b69;
    font-size:11.5px;
    font-weight:500;
    line-height:1.2;
    padding-bottom:2px;
  }
  .home-page .home-shop-items-grid{
    gap:10px!important;
  }
  .home-page .home-shop-card{
    position:relative!important;
    min-height:0!important;
    padding:11px 10px 10px!important;
    border-radius:18px!important;
    border:1px solid rgba(230,215,184,.62)!important;
    background:#fff!important;
    box-shadow:0 8px 22px rgba(53,40,29,.045)!important;
    transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease, background .18s ease!important;
  }
  .home-page .home-shop-card:hover{
    transform:translateY(-2px);
    box-shadow:0 12px 26px rgba(53,40,29,.075)!important;
  }
  .home-page .home-shop-card.owned{
    border-color:rgba(215,167,61,.58)!important;
    background:linear-gradient(180deg,#fff,rgba(255,248,229,.78))!important;
  }
  .home-page .home-shop-card.equipped{
    border-color:rgba(184,134,31,.82)!important;
    background:linear-gradient(180deg,#fffdf8,rgba(255,240,199,.86))!important;
    box-shadow:0 10px 24px rgba(184,134,31,.12)!important;
  }
  .home-page .home-shop-card.locked{
    opacity:.72;
  }
  .home-page .home-shop-card.locked .home-shop-preview{
    filter:saturate(.74);
  }
  .home-page .home-shop-card.equipped::before{
    content:"✓";
    position:absolute;
    top:9px;
    right:9px;
    width:20px;
    height:20px;
    border-radius:999px;
    display:grid;
    place-items:center;
    color:#fffdf7;
    font-size:12px;
    font-weight:700;
    background:linear-gradient(180deg,#d7a73d,#b8861f);
    box-shadow:0 5px 12px rgba(184,134,31,.22);
    z-index:2;
  }
  .home-page .home-shop-card.locked::before{
    content:"🔒";
    position:absolute;
    top:9px;
    right:9px;
    font-size:13px;
    filter:saturate(.7);
    z-index:2;
  }
  .home-page .home-shop-card .home-shop-preview{
    min-height:64px!important;
    margin:0 auto 6px!important;
    transform:scale(.98)!important;
  }
  .home-page .home-shop-card .home-game-item{
    filter:drop-shadow(0 8px 12px rgba(53,40,29,.12))!important;
    animation:homeShopItemCalmFloat 7s ease-in-out infinite;
  }
  .home-page .home-shop-card.equipped .home-game-item{
    filter:drop-shadow(0 9px 13px rgba(53,40,29,.13)) drop-shadow(0 0 9px rgba(215,167,61,.18))!important;
  }
  .home-page .home-shop-card > div[style*="font-size: 13.5"]{
    color:#35281d!important;
    font-size:13px!important;
    font-weight:650!important;
    margin-bottom:5px!important;
  }
  .home-shop-price{
    color:#8b7b69!important;
    font-size:11.5px!important;
    font-weight:550!important;
    margin:0 0 8px!important;
  }
  .home-page .home-shop-card button{
    min-height:31px!important;
    border-radius:11px!important;
    font-size:12px!important;
    font-weight:650!important;
    box-shadow:none!important;
  }
  .home-page .home-shop-card button.soft-action-btn{
    background:#fff!important;
    border:1px solid rgba(230,215,184,.72)!important;
    color:#6b5a46!important;
  }
  .home-page .home-shop-card button:not(.soft-action-btn){
    background:linear-gradient(180deg,#f3cf78,#d7a73d)!important;
    border:1px solid rgba(184,134,31,.58)!important;
    color:#35281d!important;
  }
  .home-page .home-shop-card.locked button:not(.soft-action-btn){
    background:rgba(255,250,240,.72)!important;
    border-color:rgba(230,215,184,.72)!important;
    color:#8b7b69!important;
  }
  .home-page .home-shop-card.equipped button:not(.soft-action-btn){
    background:linear-gradient(180deg,#d7a73d,#b8861f)!important;
    color:#fffdf7!important;
  }
  .home-page .home-shop-card button[style*="margin-top"]{
    min-height:29px!important;
    color:#8b7b69!important;
    background:transparent!important;
    border:0!important;
    text-decoration:underline;
    text-underline-offset:3px;
  }
  @keyframes homeShopItemCalmFloat{
    0%,100%{transform:translateY(0);}
    50%{transform:translateY(-1.5px);}
  }
  @media (prefers-reduced-motion: reduce){
    .home-page .home-shop-card,
    .home-category-card,
    .home-page .home-shop-card .home-game-item{
      animation:none!important;
      transition:none!important;
      transform:none!important;
    }
  }
  @media(max-width:720px){
    .home-page .home-shop-items-grid{
      grid-template-columns:1fr!important;
    }
    .home-shop-section-header{
      align-items:flex-start;
      flex-direction:column;
      gap:4px;
    }
    .home-shop-section-header h3{font-size:17px;}
    .home-page .home-shop-card{
      padding:10px!important;
    }
  }

  /* Final scale pass: calmer onboarding text, larger centered homes, compact 2-up Home Shop. */
  .onboarding-growth-flow-title{
    font-size:clamp(17px,4.6vw,20px)!important;
    font-weight:500!important;
    letter-spacing:.01em!important;
  }
  .onboarding-growth-label{
    font-size:clamp(14px,4vw,16px)!important;
    font-weight:600!important;
    letter-spacing:0!important;
  }
  .onboarding-growth-note{
    font-size:clamp(11px,3.1vw,12.5px)!important;
    line-height:1.45!important;
    font-weight:400!important;
  }
  .onboarding-growth-step{
    padding:10px 12px!important;
    min-height:0!important;
    gap:9px!important;
    grid-template-columns:34px 1fr!important;
  }
  .onboarding-growth-icon{
    width:30px!important;
    height:30px!important;
    font-size:15px!important;
  }
  .onboarding-growth-arrow{
    width:28px!important;
    height:28px!important;
    font-size:14px!important;
    margin:5px auto!important;
  }
  .home-room .home-simple-shelter-image{
    top:23%!important;
    width:clamp(228px,67vw,300px)!important;
  }
  .home-room .home-little-shelter{
    top:29%!important;
    width:clamp(214px,64vw,258px)!important;
    height:132px!important;
  }
  .home-room .home-little-shelter-roof{
    height:72px!important;
  }
  .home-room .home-little-shelter-body{
    height:86px!important;
  }
  .home-room .home-little-shelter-door{
    height:54px!important;
  }
  .home-room .home-little-shelter-window{
    top:64px!important;
  }
  .home-page .home-shop-items-grid{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:8px!important;
  }
  .home-page .home-shop-card{
    padding:8px 7px!important;
    border-radius:15px!important;
  }
  .home-page .home-shop-card .home-shop-preview{
    min-height:46px!important;
    margin-bottom:3px!important;
    transform:scale(.78)!important;
  }
  .home-page .home-shop-card > div[style*="font-size: 13.5"]{
    font-size:11.5px!important;
    line-height:1.15!important;
    margin-bottom:4px!important;
  }
  .home-shop-price{
    font-size:10.5px!important;
    margin-bottom:5px!important;
  }
  .home-page .home-shop-card .meaning-chip{
    font-size:8.4px!important;
    letter-spacing:.06em!important;
  }
  .home-page .home-shop-card button{
    min-height:27px!important;
    border-radius:9px!important;
    font-size:10.5px!important;
    padding:6px 0!important;
  }
  .home-page .home-shop-card button[style*="margin-top"]{
    min-height:24px!important;
    font-size:10.2px!important;
    margin-top:4px!important;
  }
  .home-shop-section-header{
    margin-top:2px!important;
    margin-bottom:8px!important;
  }
  .home-shop-section-header h3{
    font-size:16px!important;
  }
  .home-shop-eyebrow{
    font-size:9px!important;
  }
  .home-shop-collected{
    font-size:10.5px!important;
  }
  .home-room .home-simple-shelter-image img{
    transform:translateY(-18%) scale(1.04)!important;
    transform-origin:50% 48%!important;
  }
  .home-room .home-simple-shelter-image::before{
    top:18%!important;
  }
  .home-room .home-simple-shelter-image::after{
    bottom:20%!important;
  }
  @media(max-width:520px){
    .home-room .home-simple-shelter-image{
      top:24%!important;
      width:clamp(210px,73vw,270px)!important;
    }
    .home-room .home-little-shelter{
      top:30%!important;
      width:clamp(200px,70vw,246px)!important;
    }
    .home-room .home-cozy-shelter-image{
      top:16%!important;
      width:clamp(184px,62vw,244px)!important;
    }
    .home-room .home-heavenly-cottage-image{
      top:10%!important;
      width:clamp(188px,63vw,248px)!important;
    }
    .home-room .home-garden-home-image{
      top:8%!important;
      width:clamp(198px,66vw,260px)!important;
    }
    /* Mobile home image vertical alignment */
    .home-room .home-cozy-shelter-image img{
      transform:translateY(-8%) scale(.98)!important;
    }
    .home-room .home-heavenly-cottage-image img{
      transform:translateY(-8%) scale(.98)!important;
    }
    .home-room .home-garden-home-image img{
      transform:translateY(-7%) scale(.98)!important;
    }
    .home-room .home-radiant-heavenly-home-image{
      inset:0!important;
      width:100%!important;
      height:100%!important;
    }
    .home-room .home-radiant-heavenly-home-image img{
      height:100%!important;
      object-fit:cover!important;
      object-position:center top!important;
      transform:none!important;
    }
    .home-page .home-shop-items-grid{
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:8px!important;
    }
    .home-page .home-shop-card{
      padding:7px 6px!important;
    }
    .home-page .home-shop-card .home-shop-preview{
      min-height:42px!important;
      transform:scale(.72)!important;
    }
  }


  /* Final Harmony Pass: cleaner contrast, brighter cards, calmer backgrounds. */
  :root{
    --sg-bg-warm:#FBF7EE;
    --sg-bg-soft:#FFFBF4;
    --sg-surface:#FFFFFF;
    --sg-surface-ivory:#FFFDF8;
    --sg-surface-warm:#FFF9EC;
    --sg-text-main:#3B2A16;
    --sg-text-soft:#725B39;
    --sg-text-muted:#9A8464;
    --sg-border-soft:rgba(141,111,61,.13);
    --sg-border-warm:rgba(199,159,85,.18);
    --sg-shadow-soft:0 10px 28px rgba(72,50,20,.055);
    --sg-shadow-card:0 14px 34px rgba(72,50,20,.075);
    --sg-highlight:rgba(255,255,255,.82);
  }
  html,body,#root,
  .app-shell,
  .app-content{
    background:
      radial-gradient(circle at 50% -8%,rgba(255,255,255,.94) 0,rgba(255,250,235,.60) 30%,transparent 58%),
      linear-gradient(180deg,var(--sg-bg-soft) 0%,var(--sg-bg-warm) 100%)!important;
    color:var(--sg-text-main)!important;
  }
  .app-shell .app-header{
    background:linear-gradient(180deg,rgba(255,253,249,.94),rgba(250,245,235,.88))!important;
    border-bottom:1px solid rgba(80,60,30,.055)!important;
    box-shadow:0 1px 10px rgba(72,50,20,.025)!important;
  }
  .app-shell .app-header,
  .app-shell .app-header *,
  .top-soft-chip,
  .quiet-mode-chip,
  .sound-toggle-chip,
  .friends-top-chip,
  .refresh-chip{
    font-family:Nunito,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;
    font-weight:500!important;
    color:var(--sg-text-main)!important;
  }
  .app-title-copy h1,
  .app-title-copy .serif{
    font-family:"Cormorant Garamond",Georgia,serif!important;
    font-weight:600!important;
    color:#9A6A14!important;
  }
  .app-content h1,
  .app-content h2,
  .app-content h3,
  .app-content h4,
  .serif{
    color:var(--sg-text-main)!important;
  }
  .app-content p,
  .app-content small,
  .app-content label,
  .app-content span{
    color:inherit;
  }

  /* Main page sections should feel open, not boxed. */
  .today-page-calm,
  .prayer-page-calm,
  .home-page,
  .growth-page-calm,
  .premium-journal-page,
  .testimony-garden-page,
  .spirit-friends-dashboard{
    background:transparent!important;
  }
  .today-page-calm > .card,
  .prayer-page-calm > .card,
  .home-page > .card,
  .growth-page-calm > .card,
  .premium-journal-page > .card,
  .testimony-garden-page > .card,
  .spirit-friends-dashboard > .card,
  .spirit-friends-dashboard > .sacred-card{
    background:transparent!important;
    border:0!important;
    box-shadow:none!important;
    backdrop-filter:none!important;
  }

  /* Important hero cards keep premium depth and contrast. */
  .today-page-calm .today-hero-card,
  .today-page-calm .today-faith-walk-card,
  .prayer-page-calm .prayer-vessel-hero,
  .home-page .sticky-preview-card,
  .growth-page-calm .growth-summary-card,
  .growth-page-calm .growth-stage-hero,
  .spirit-friends-dashboard .spirit-friends-hero-card,
  .testimony-garden-page .featured-testimony-card,
  .testimony-garden-page .testimony-featured-garden,
  .premium-journal-page .journal-write-card{
    background:linear-gradient(145deg,rgba(255,255,255,.96),rgba(255,252,246,.90))!important;
    border:1px solid var(--sg-border-warm)!important;
    box-shadow:var(--sg-shadow-card),inset 0 1px 0 var(--sg-highlight)!important;
    backdrop-filter:blur(10px)!important;
  }

  /* Supporting cards become brighter and quieter. */
  .card,
  .sacred-card,
  .focus-card,
  .soft-guide-card,
  .answered-altar-card,
  .quest-card,
  .prayer-tab-card,
  .quick-prayer-box,
  .friend-card,
  .prayer-friend-panel,
  .prayer-friend-row,
  .compact-friend-row,
  .home-shop-shell-card,
  .home-shop-card,
  .home-category-card,
  .growth-hero-card,
  .growth-closet-card,
  .growth-stones-card,
  .growth-book-card,
  .growth-path-step,
  .journal-history-card,
  .journal-entry-card,
  .testimony-entry,
  .testimony-share-garden,
  .testimony-stone-card,
  .month-toggle,
  .faith-journey-panel,
  .stat-pill,
  .task-row,
  .opt-btn,
  .sam-btn{
    background:linear-gradient(145deg,rgba(255,255,255,.88),rgba(255,253,248,.76))!important;
    border:1px solid var(--sg-border-soft)!important;
    box-shadow:var(--sg-shadow-soft),inset 0 1px 0 rgba(255,255,255,.72)!important;
    color:var(--sg-text-main)!important;
  }
  .card p,
  .sacred-card p,
  .focus-card p,
  .soft-guide-card p,
  .prayer-tab-card p,
  .home-shop-card p,
  .growth-page-calm p,
  .premium-journal-page p,
  .testimony-garden-page p,
  .spirit-friends-dashboard p{
    color:var(--sg-text-soft)!important;
  }
  .section-label,
  .meaning-chip,
  .placed-chip,
  .prayer-status-chip,
  .home-shop-eyebrow,
  .home-shop-collected,
  .growth-path-range,
  .growth-path-status,
  .journal-entry-date,
  .testimony-meta,
  .empty-state small{
    color:var(--sg-text-muted)!important;
    font-weight:500!important;
  }

  /* Calm dividers between sections. */
  .today-page-calm > .card + .card,
  .prayer-page-calm > .card + .card,
  .home-page > .card + .card,
  .growth-page-calm > .card + .card,
  .premium-journal-page > .card + .card,
  .testimony-garden-page > .card + .card,
  .spirit-friends-dashboard > .card + .card,
  .spirit-friends-dashboard > .sacred-card + .sacred-card{
    border-top:1px solid rgba(141,111,61,.09)!important;
    padding-top:20px!important;
    margin-top:20px!important;
  }
  .section-divider-line,
  .soft-divider,
  .timeline-line{
    background:rgba(141,111,61,.10)!important;
    opacity:1!important;
  }

  /* Inputs and text areas need strong contrast against brighter cards. */
  .sg-input,
  .sg-textarea,
  input,
  textarea,
  select{
    background:rgba(255,255,255,.92)!important;
    border:1px solid rgba(141,111,61,.18)!important;
    color:var(--sg-text-main)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.7)!important;
  }
  .sg-input:focus,
  .sg-textarea:focus,
  input:focus,
  textarea:focus,
  select:focus{
    border-color:rgba(201,144,18,.48)!important;
    box-shadow:0 0 0 3px rgba(240,203,118,.22),inset 0 1px 0 rgba(255,255,255,.72)!important;
  }

  /* Buttons/chips stay consistent and less bulky. */
  .top-soft-chip,
  .quiet-mode-chip,
  .sound-toggle-chip,
  .friends-top-chip,
  .refresh-chip,
  .meaning-chip,
  .placed-chip,
  .prayer-status-chip,
  .stat-pill,
  .faith-journey-day,
  .testimony-category-chip{
    background:rgba(255,255,255,.78)!important;
    border:1px solid rgba(201,144,18,.20)!important;
    box-shadow:0 5px 14px rgba(72,50,20,.045)!important;
  }
  .gold-btn,
  button.primary,
  .quest-nav-btn.primary{
    color:var(--sg-text-main)!important;
    box-shadow:0 9px 18px rgba(184,134,31,.16),inset 0 1px 0 rgba(255,255,255,.5)!important;
  }

  /* Mobile: keep contrast but avoid heavy card stacking. */
  @media(max-width:640px){
    .app-content{
      padding-left:14px!important;
      padding-right:14px!important;
    }
    .today-page-calm > .card,
    .prayer-page-calm > .card,
    .home-page > .card,
    .growth-page-calm > .card,
    .premium-journal-page > .card,
    .testimony-garden-page > .card,
    .spirit-friends-dashboard > .card,
    .spirit-friends-dashboard > .sacred-card{
      width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      padding-left:0!important;
      padding-right:0!important;
    }
    .today-page-calm .today-hero-card,
    .today-page-calm .today-faith-walk-card,
    .prayer-page-calm .prayer-vessel-hero,
    .home-page .sticky-preview-card,
    .growth-page-calm .growth-summary-card,
    .growth-page-calm .growth-stage-hero,
    .spirit-friends-dashboard .spirit-friends-hero-card,
    .testimony-garden-page .featured-testimony-card,
    .premium-journal-page .journal-write-card{
      width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      border-radius:24px!important;
    }
    .card,
    .sacred-card,
    .focus-card,
    .soft-guide-card{
      border-radius:22px!important;
    }
  }


  /* Final Garden Home Fit: make the Garden Home image fill the sanctuary stage. */
  .home-room .home-garden-home-image{
    inset:0!important;
    top:0!important;
    left:0!important;
    width:100%!important;
    height:100%!important;
    transform:none!important;
    z-index:3!important;
    pointer-events:none!important;
    overflow:hidden!important;
    background:transparent!important;
    filter:none!important;
  }
  .home-room .home-garden-home-image img{
    display:block!important;
    width:100%!important;
    height:100%!important;
    object-fit:cover!important;
    object-position:center center!important;
    border:0!important;
    border-radius:0!important;
    opacity:.9!important;
    transform:none!important;
    mix-blend-mode:normal!important;
    -webkit-mask-image:none!important;
    mask-image:none!important;
  }
  @media(max-width:520px){
    .home-room .home-garden-home-image{
      inset:0!important;
      width:100%!important;
      height:100%!important;
    }
    .home-room .home-garden-home-image img{
      height:100%!important;
      object-fit:cover!important;
      object-position:center center!important;
      transform:none!important;
    }
  }


  /* Home progression now uses app-native CSS shapes instead of image artwork. */
  .home-room.home-stage-2,
  .home-room.home-stage-3{
    background:linear-gradient(180deg,rgba(255,253,248,.30),rgba(255,248,223,.10))!important;
  }
  .home-room.home-stage-2 .home-sky,
  .home-room.home-stage-2 .home-rays,
  .home-room.home-stage-2 .home-cloud,
  .home-room.home-stage-2 .home-sunbeam-drift,
  .home-room.home-stage-2 .home-floor,
  .home-room.home-stage-2 .home-midground-glow,
  .home-room.home-stage-2 .home-path,
  .home-room.home-stage-2 .home-flowerbed,
  .home-room.home-stage-2 .home-gem,
  .home-room.home-stage-2 .home-path-gem,
  .home-room.home-stage-2 .home-distant-city,
  .home-room.home-stage-3 .home-sky,
  .home-room.home-stage-3 .home-rays,
  .home-room.home-stage-3 .home-cloud,
  .home-room.home-stage-3 .home-sunbeam-drift,
  .home-room.home-stage-3 .home-floor,
  .home-room.home-stage-3 .home-midground-glow,
  .home-room.home-stage-3 .home-path,
  .home-room.home-stage-3 .home-flowerbed,
  .home-room.home-stage-3 .home-gem,
  .home-room.home-stage-3 .home-path-gem,
  .home-room.home-stage-3 .home-distant-city{
    opacity:1!important;
    visibility:visible!important;
  }
  .home-room.home-stage-2 .home-word-scroll,
  .home-room.home-stage-3 .home-word-scroll,
  .home-room.home-stage-4 .home-word-scroll,
  .home-room.home-stage-2 .home-prayer-light-trail,
  .home-room.home-stage-3 .home-prayer-light-trail,
  .home-room.home-stage-4 .home-prayer-light-trail{
    display:block!important;
  }


  /* Final Card Breathing Room Restore: undo cramped card borders without flattening the app. */
  .card,
  .sacred-card,
  .focus-card,
  .soft-guide-card,
  .today-hero-card,
  .today-faith-walk-card,
  .prayer-vessel-hero,
  .prayer-tab-card,
  .sticky-preview-card,
  .home-shop-shell-card,
  .home-shop-card,
  .home-category-card,
  .growth-summary-card,
  .growth-stage-hero,
  .journal-write-card,
  .journal-entry-card,
  .featured-testimony-card,
  .testimony-shared-card,
  .spirit-friends-hero-card{
    box-sizing:border-box!important;
    max-width:100%!important;
    overflow:visible!important;
    border:1px solid rgba(204,174,116,.24)!important;
    background:rgba(255,255,252,.78)!important;
    box-shadow:0 8px 24px rgba(62,44,18,.035)!important;
  }

  .today-page-calm > .card + .card,
  .prayer-page-calm > .card + .card,
  .home-page > .card + .card,
  .growth-page-calm > .card + .card,
  .premium-journal-page > .card + .card,
  .testimony-garden-page > .card + .card,
  .spirit-friends-dashboard > .card + .card,
  .spirit-friends-dashboard > .sacred-card + .sacred-card{
    border-top:0!important;
    padding-top:0!important;
    margin-top:10px!important;
  }

  .today-page-calm > .card,
  .prayer-page-calm > .card,
  .home-page > .card,
  .growth-page-calm > .card,
  .testimony-garden-page > .card,
  .premium-journal-page > .card,
  .spirit-friends-dashboard > .card,
  .spirit-friends-dashboard > .sacred-card{
    width:100%!important;
    margin-left:0!important;
    margin-right:0!important;
    padding-left:18px!important;
    padding-right:18px!important;
  }

  .prayer-page-calm .prayer-vessel-hero,
  .prayer-page-calm .prayer-tab-card,
  .home-page .sticky-preview-card,
  .home-page .home-shop-shell-card,
  .home-page .home-shop-card,
  .home-page .home-category-card,
  .growth-page-calm .growth-summary-card,
  .growth-page-calm .growth-stage-hero,
  .testimony-garden-page .featured-testimony-card,
  .testimony-garden-page .testimony-shared-card{
    padding-left:18px!important;
    padding-right:18px!important;
  }

  .prayer-page-calm h1,
  .prayer-page-calm h2,
  .prayer-page-calm h3,
  .growth-page-calm h1,
  .growth-page-calm h2,
  .growth-page-calm h3,
  .testimony-garden-page h1,
  .testimony-garden-page h2,
  .testimony-garden-page h3,
  .home-page h1,
  .home-page h2,
  .home-page h3,
  .prayer-page-calm p,
  .growth-page-calm p,
  .testimony-garden-page p,
  .home-page p{
    max-width:100%!important;
    overflow-wrap:normal!important;
    word-break:normal!important;
    letter-spacing:0!important;
  }

  .section-divider-line,
  .soft-divider{
    height:1px!important;
    border:0!important;
    background:linear-gradient(90deg,transparent,rgba(122,94,49,.12),transparent)!important;
  }

  @media(max-width:640px){
    .app-content{
      padding-left:14px!important;
      padding-right:14px!important;
    }
    .today-page-calm > .card,
    .prayer-page-calm > .card,
    .home-page > .card,
    .growth-page-calm > .card,
    .testimony-garden-page > .card,
    .premium-journal-page > .card,
    .spirit-friends-dashboard > .card,
    .spirit-friends-dashboard > .sacred-card{
      padding-left:16px!important;
      padding-right:16px!important;
      border-radius:22px!important;
    }
    .prayer-page-calm .prayer-vessel-hero,
    .prayer-page-calm .prayer-tab-card,
    .home-page .sticky-preview-card,
    .home-page .home-shop-shell-card,
    .home-page .home-shop-card,
    .home-page .home-category-card,
    .growth-page-calm .growth-summary-card,
    .growth-page-calm .growth-stage-hero,
    .testimony-garden-page .featured-testimony-card,
    .testimony-garden-page .testimony-shared-card{
      padding-left:16px!important;
      padding-right:16px!important;
    }
  }


  /* Hard Restore Growth and Testimony Cards: undo old full-bleed compression. */
  .app-content > .growth-page-calm,
  .app-content > .testimony-garden-page{
    width:100%!important;
    max-width:100%!important;
    margin-left:0!important;
    margin-right:0!important;
    padding-left:0!important;
    padding-right:0!important;
    overflow:visible!important;
  }

  .growth-page-calm > .card,
  .growth-page-calm .growth-summary-card,
  .growth-page-calm .growth-stage-hero,
  .growth-page-calm .growth-account-card,
  .growth-page-calm .growth-hero-card,
  .growth-page-calm .growth-closet-card,
  .growth-page-calm .growth-stones-card,
  .growth-page-calm .growth-book-card,
  .testimony-garden-page > .card,
  .testimony-garden-page .featured-testimony-card,
  .testimony-garden-page .testimony-featured-garden,
  .testimony-garden-page .testimony-shared-garden,
  .testimony-garden-page .testimony-shared-card{
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    margin-left:0!important;
    margin-right:0!important;
    padding:18px!important;
    border-radius:24px!important;
    border:1px solid rgba(204,174,116,.26)!important;
    background:linear-gradient(145deg,rgba(255,255,252,.88),rgba(255,250,240,.66))!important;
    box-shadow:0 8px 24px rgba(62,44,18,.04), inset 0 1px 0 rgba(255,255,255,.72)!important;
    overflow:visible!important;
    box-sizing:border-box!important;
  }

  .growth-page-calm .growth-path-step,
  .growth-page-calm .growth-summary-stat,
  .growth-page-calm .testimony-stone-card,
  .growth-page-calm .month-toggle,
  .testimony-garden-page .testimony-entry,
  .testimony-garden-page .empty-state{
    min-width:0!important;
    max-width:100%!important;
    padding:14px!important;
    border-radius:18px!important;
    border:1px solid rgba(204,174,116,.18)!important;
    background:rgba(255,255,252,.72)!important;
    box-shadow:0 5px 16px rgba(62,44,18,.028)!important;
    box-sizing:border-box!important;
  }

  .growth-page-calm > .card + .card,
  .testimony-garden-page > .card + .card{
    margin-top:18px!important;
    padding-top:18px!important;
    border-top:0!important;
  }

  .growth-page-calm h1,
  .growth-page-calm h2,
  .growth-page-calm h3,
  .growth-page-calm p,
  .growth-page-calm span,
  .growth-page-calm small,
  .testimony-garden-page h1,
  .testimony-garden-page h2,
  .testimony-garden-page h3,
  .testimony-garden-page p,
  .testimony-garden-page span,
  .testimony-garden-page small{
    min-width:0!important;
    max-width:100%!important;
    white-space:normal!important;
    overflow:visible!important;
    text-overflow:clip!important;
    word-break:normal!important;
    overflow-wrap:break-word!important;
    letter-spacing:0!important;
  }

  .growth-page-calm .section-label,
  .testimony-garden-page .section-label{
    white-space:normal!important;
  }

  @media(max-width:640px){
    .growth-page-calm > .card,
    .growth-page-calm .growth-summary-card,
    .growth-page-calm .growth-stage-hero,
    .growth-page-calm .growth-account-card,
    .growth-page-calm .growth-hero-card,
    .growth-page-calm .growth-closet-card,
    .growth-page-calm .growth-stones-card,
    .growth-page-calm .growth-book-card,
    .testimony-garden-page > .card,
    .testimony-garden-page .featured-testimony-card,
    .testimony-garden-page .testimony-featured-garden,
    .testimony-garden-page .testimony-shared-garden,
    .testimony-garden-page .testimony-shared-card{
      width:100%!important;
      max-width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      padding:16px!important;
      border-radius:22px!important;
    }

    .growth-page-calm .growth-path-step,
    .growth-page-calm .growth-summary-stat,
    .growth-page-calm .testimony-stone-card,
    .testimony-garden-page .testimony-entry,
    .testimony-garden-page .empty-state{
      padding:13px!important;
      border-radius:16px!important;
    }
  }


  /* Final Growth Path, Friends Borders, and Tight Spacing Fix */
  .growth-page-calm,
  .testimony-garden-page,
  .spirit-friends-dashboard{
    width:100%!important;
    max-width:100%!important;
    margin-left:0!important;
    margin-right:0!important;
    padding-left:0!important;
    padding-right:0!important;
    overflow:visible!important;
  }

  .growth-page-calm > .card,
  .growth-page-calm .growth-summary-card,
  .growth-page-calm .growth-stage-hero,
  .growth-page-calm .growth-account-card,
  .growth-page-calm .growth-hero-card,
  .growth-page-calm .growth-closet-card,
  .growth-page-calm .growth-stones-card,
  .growth-page-calm .growth-book-card,
  .testimony-garden-page > .card,
  .testimony-garden-page .featured-testimony-card,
  .testimony-garden-page .testimony-featured-garden,
  .testimony-garden-page .testimony-shared-garden,
  .testimony-garden-page .testimony-shared-card,
  .spirit-friends-dashboard > .card,
  .spirit-friends-dashboard > .sacred-card,
  .spirit-friends-dashboard .spirit-friends-hero-card,
  .spirit-friends-dashboard .spirit-friends-tools-card,
  .spirit-friends-dashboard .accountability-primary-card,
  .spirit-friends-dashboard .blessing-community-card,
  .spirit-friends-dashboard .community-feed-card,
  .spirit-friends-dashboard .friends-garden-card{
    box-sizing:border-box!important;
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    margin-left:0!important;
    margin-right:0!important;
    padding:18px!important;
    border-radius:24px!important;
    border:1px solid rgba(204,174,116,.24)!important;
    background:linear-gradient(145deg,rgba(255,255,252,.90),rgba(255,250,240,.64))!important;
    box-shadow:0 8px 24px rgba(62,44,18,.04), inset 0 1px 0 rgba(255,255,255,.72)!important;
    overflow:visible!important;
    backdrop-filter:none!important;
  }

  .growth-page-calm .growth-path-list{
    display:flex!important;
    flex-direction:row!important;
    flex-wrap:nowrap!important;
    gap:12px!important;
    width:100%!important;
    max-width:100%!important;
    overflow-x:auto!important;
    overflow-y:hidden!important;
    margin-top:14px!important;
    padding:4px 4px 12px!important;
    scroll-snap-type:x proximity;
    -webkit-overflow-scrolling:touch;
  }
  .growth-page-calm .growth-path-list::before{
    left:20px!important;
    right:20px!important;
    top:auto!important;
    bottom:38px!important;
    width:auto!important;
    height:2px!important;
    background:linear-gradient(90deg,rgba(201,144,18,.28),rgba(238,222,255,.30),rgba(145,180,112,.22))!important;
  }
  .growth-page-calm .growth-path-step{
    flex:0 0 138px!important;
    width:138px!important;
    min-width:138px!important;
    max-width:138px!important;
    min-height:220px!important;
    display:flex!important;
    flex-direction:column!important;
    align-items:center!important;
    justify-content:flex-start!important;
    gap:9px!important;
    padding:13px 10px!important;
    text-align:center!important;
    border-radius:28px!important;
    scroll-snap-align:start;
    overflow:hidden!important;
  }
  .growth-page-calm .growth-path-avatar{
    width:76px!important;
    height:76px!important;
    flex:0 0 76px!important;
  }
  .growth-page-calm .growth-path-copy{
    width:100%!important;
    min-width:0!important;
  }
  .growth-page-calm .growth-path-name{
    font-size:17px!important;
    line-height:1.12!important;
    white-space:normal!important;
    overflow-wrap:normal!important;
    word-break:normal!important;
  }
  .growth-page-calm .growth-path-range{
    font-size:12px!important;
    line-height:1.22!important;
    white-space:normal!important;
    overflow-wrap:normal!important;
    word-break:normal!important;
  }
  .growth-page-calm .growth-path-status{
    margin-top:auto!important;
    flex:0 0 30px!important;
  }

  .spirit-friends-dashboard .prayer-friend-panel,
  .spirit-friends-dashboard .prayer-friend-row,
  .spirit-friends-dashboard .compact-friend-row,
  .spirit-friends-dashboard .friend-list-row,
  .spirit-friends-dashboard .activity-row,
  .spirit-friends-dashboard .note-row{
    box-sizing:border-box!important;
    max-width:100%!important;
    min-width:0!important;
    border:1px solid rgba(204,174,116,.18)!important;
    background:rgba(255,255,252,.72)!important;
    box-shadow:0 5px 14px rgba(62,44,18,.025)!important;
  }

  .growth-page-calm > .card + .card,
  .growth-page-calm > .sacred-card + .sacred-card,
  .testimony-garden-page > .card + .card,
  .testimony-garden-page > .sacred-card + .sacred-card,
  .spirit-friends-dashboard > .card + .card,
  .spirit-friends-dashboard > .sacred-card + .sacred-card{
    margin-top:10px!important;
    padding-top:10px!important;
    border-top:0!important;
  }

  @media(max-width:640px){
    .growth-page-calm,
    .testimony-garden-page,
    .spirit-friends-dashboard{
      width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      padding-left:0!important;
      padding-right:0!important;
    }
    .growth-page-calm > .card,
    .growth-page-calm .growth-summary-card,
    .growth-page-calm .growth-stage-hero,
    .growth-page-calm .growth-account-card,
    .growth-page-calm .growth-hero-card,
    .growth-page-calm .growth-closet-card,
    .growth-page-calm .growth-stones-card,
    .growth-page-calm .growth-book-card,
    .testimony-garden-page > .card,
    .testimony-garden-page .featured-testimony-card,
    .testimony-garden-page .testimony-featured-garden,
    .testimony-garden-page .testimony-shared-garden,
    .testimony-garden-page .testimony-shared-card,
    .spirit-friends-dashboard > .card,
    .spirit-friends-dashboard > .sacred-card{
      width:100%!important;
      max-width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      padding:15px!important;
      border-radius:22px!important;
    }
    .growth-page-calm .growth-path-step{
      flex-basis:132px!important;
      width:132px!important;
      min-width:132px!important;
      max-width:132px!important;
      min-height:210px!important;
    }
    .growth-page-calm > .card + .card,
    .growth-page-calm > .sacred-card + .sacred-card,
    .testimony-garden-page > .card + .card,
    .testimony-garden-page > .sacred-card + .sacred-card,
    .spirit-friends-dashboard > .card + .card,
    .spirit-friends-dashboard > .sacred-card + .sacred-card{
      margin-top:8px!important;
      padding-top:8px!important;
    }
  }


  /* Final Home and Journal Card Border Restore */
  .home-page,
  .premium-journal-page{
    width:100%!important;
    max-width:100%!important;
    margin-left:0!important;
    margin-right:0!important;
    padding-left:0!important;
    padding-right:0!important;
    overflow:visible!important;
  }

  .home-page > .card,
  .home-page .home-hero-card,
  .home-page .home-care-card,
  .home-page .home-preview-card,
  .home-page .sticky-preview-card,
  .home-page .home-shop-shell-card,
  .premium-journal-page > .card,
  .premium-journal-page .journal-write-card,
  .premium-journal-page .journal-history-card{
    box-sizing:border-box!important;
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    margin-left:0!important;
    margin-right:0!important;
    padding:18px!important;
    border-radius:24px!important;
    border:1px solid rgba(204,174,116,.24)!important;
    background:linear-gradient(145deg,rgba(255,255,252,.90),rgba(255,250,240,.64))!important;
    box-shadow:0 8px 24px rgba(62,44,18,.04), inset 0 1px 0 rgba(255,255,255,.72)!important;
    overflow:visible!important;
    backdrop-filter:none!important;
  }

  .home-page .home-shop-card,
  .home-page .home-category-card,
  .premium-journal-page .journal-entry-card,
  .premium-journal-page .month-toggle,
  .premium-journal-page .empty-state{
    box-sizing:border-box!important;
    max-width:100%!important;
    min-width:0!important;
    border:1px solid rgba(204,174,116,.18)!important;
    border-radius:18px!important;
    background:rgba(255,255,252,.72)!important;
    box-shadow:0 5px 14px rgba(62,44,18,.025)!important;
  }

  .home-page .home-shop-card.equipped,
  .home-page .home-category-card.active{
    border-color:rgba(201,144,18,.42)!important;
    background:linear-gradient(145deg,rgba(255,249,228,.92),rgba(255,255,252,.78))!important;
    box-shadow:0 8px 20px rgba(180,130,20,.08), inset 0 1px 0 rgba(255,255,255,.78)!important;
  }

  .home-page .home-room{
    border:0!important;
    outline:0!important;
    box-shadow:none!important;
  }

  .home-page > .card + .card,
  .premium-journal-page > .card + .card{
    margin-top:10px!important;
    padding-top:10px!important;
    border-top:0!important;
  }

  .home-page h1,
  .home-page h2,
  .home-page h3,
  .home-page p,
  .premium-journal-page h1,
  .premium-journal-page h2,
  .premium-journal-page h3,
  .premium-journal-page p,
  .premium-journal-page span,
  .premium-journal-page small{
    min-width:0!important;
    max-width:100%!important;
    white-space:normal!important;
    overflow:visible!important;
    text-overflow:clip!important;
    word-break:normal!important;
    overflow-wrap:break-word!important;
  }

  @media(max-width:640px){
    .home-page,
    .premium-journal-page{
      width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      padding-left:0!important;
      padding-right:0!important;
    }
    .home-page > .card,
    .home-page .home-hero-card,
    .home-page .home-care-card,
    .home-page .home-preview-card,
    .home-page .sticky-preview-card,
    .home-page .home-shop-shell-card,
    .premium-journal-page > .card,
    .premium-journal-page .journal-write-card,
    .premium-journal-page .journal-history-card{
      width:100%!important;
      max-width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      padding:15px!important;
      border-radius:22px!important;
    }
    .home-page .home-shop-card,
    .home-page .home-category-card,
    .premium-journal-page .journal-entry-card{
      border-radius:16px!important;
    }
    .home-page > .card + .card,
    .premium-journal-page > .card + .card{
      margin-top:8px!important;
      padding-top:8px!important;
    }
  }


  /* Final Mobile Width, Banner, and Tight Card Spacing Polish */
  html,
  body,
  #root{
    max-width:100%!important;
    overflow-x:hidden!important;
  }

  .app-shell,
  .app-shell > div,
  .app-content,
  .today-page-calm,
  .home-page,
  .prayer-page-calm,
  .testimony-garden-page,
  .growth-page-calm,
  .premium-journal-page,
  .spirit-friends-dashboard{
    box-sizing:border-box!important;
    max-width:100%!important;
    overflow-x:hidden!important;
  }

  .app-shell .app-header{
    margin:0!important;
    padding:18px 22px 15px!important;
    border-bottom:1px solid rgba(80,60,30,.055)!important;
    background:
      radial-gradient(circle at 12% 0%,rgba(255,255,255,.82),transparent 42%),
      linear-gradient(180deg,#fffdfb 0%,#f8f3eb 100%)!important;
    box-shadow:0 1px 10px rgba(63,45,24,.035)!important;
  }

  .app-header .app-title-copy{
    gap:3px!important;
    min-width:0!important;
  }

  .app-header .app-title-copy > div:first-child{
    color:#755824!important;
    font-size:11.5px!important;
    font-weight:620!important;
    letter-spacing:3px!important;
  }

  .app-header .app-title-copy p{
    color:#8c7b67!important;
    font-size:12.5px!important;
    font-weight:430!important;
  }

  .app-header .app-title-copy h1.serif{
    color:#9b6f17!important;
    font-size:26px!important;
    font-weight:580!important;
    letter-spacing:.01em!important;
  }

  .app-header .top-soft-chip,
  .app-header .friends-top-chip{
    min-height:38px!important;
    border-radius:999px!important;
    padding:7px 13px!important;
    border-color:rgba(204,173,107,.48)!important;
    background:rgba(255,253,247,.9)!important;
    color:#4a3922!important;
    font-size:13px!important;
    font-weight:520!important;
    line-height:1!important;
    box-shadow:0 5px 15px rgba(82,59,27,.055)!important;
  }

  .app-header .top-soft-chip[style*="linear-gradient"],
  .app-header .friends-top-chip[style*="linear-gradient"]{
    background:linear-gradient(135deg,#fff8da,#efd06a)!important;
    border-color:#d5a944!important;
  }

  .growth-page-calm,
  .testimony-garden-page{
    gap:8px!important;
  }

  .growth-page-calm > .card,
  .growth-page-calm > .sacred-card,
  .testimony-garden-page > .card,
  .testimony-garden-page > .sacred-card{
    margin-top:0!important;
    margin-bottom:8px!important;
  }

  .growth-page-calm > * + *,
  .testimony-garden-page > * + *{
    margin-top:8px!important;
  }

  @media(max-width:640px){
    .app-shell{
      width:100%!important;
      min-width:0!important;
    }

    .app-shell > div{
      width:100%!important;
      max-width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      padding-left:0!important;
      padding-right:0!important;
    }

    .app-content{
      width:100%!important;
      max-width:100%!important;
      padding-left:12px!important;
      padding-right:12px!important;
      margin-left:0!important;
      margin-right:0!important;
      overflow-x:hidden!important;
    }

    .app-content > .today-page-calm,
    .app-content > .home-page,
    .app-content > .prayer-page-calm,
    .app-content > .testimony-garden-page,
    .app-content > .growth-page-calm,
    .app-content > .premium-journal-page,
    .app-content > .spirit-friends-dashboard{
      width:100%!important;
      max-width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      padding-left:0!important;
      padding-right:0!important;
    }

    .app-content *,
    .home-page *,
    .growth-page-calm *,
    .testimony-garden-page *,
    .premium-journal-page *,
    .spirit-friends-dashboard *{
      box-sizing:border-box!important;
    }

    .app-shell .app-header{
      width:100%!important;
      max-width:100%!important;
      padding:14px 14px 12px!important;
      gap:10px!important;
      align-items:flex-start!important;
    }

    .app-header .app-title-copy > div:first-child{
      font-size:10px!important;
      letter-spacing:2.5px!important;
    }

    .app-header .app-title-copy p{
      font-size:12px!important;
    }

    .app-header .app-title-copy h1.serif{
      font-size:24px!important;
      max-width:230px!important;
      color:#9b6f17!important;
    }

    .app-header > div:last-child{
      max-width:150px!important;
      gap:6px!important;
    }

    .app-header > div:last-child > div{
      gap:6px!important;
    }

    .app-header .top-soft-chip,
    .app-header .friends-top-chip{
      min-height:35px!important;
      padding:6px 10px!important;
      font-size:12.5px!important;
      font-weight:500!important;
    }

    .home-page > .card,
    .home-page .home-care-card,
    .home-page .home-preview-card,
    .home-page .sticky-preview-card,
    .home-page .home-shop-shell-card,
    .home-page .home-shop-card,
    .premium-journal-page > .card,
    .premium-journal-page .journal-entry-card,
    .premium-journal-page .journal-form-card,
    .premium-journal-page .journal-history-card{
      width:100%!important;
      max-width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
    }

    .growth-page-calm,
    .testimony-garden-page{
      gap:6px!important;
    }

    .growth-page-calm > .card,
    .growth-page-calm > .sacred-card,
    .testimony-garden-page > .card,
    .testimony-garden-page > .sacred-card{
      margin-bottom:6px!important;
    }

    .growth-page-calm > * + *,
    .testimony-garden-page > * + *{
      margin-top:6px!important;
    }
  }


  /* Final Tight Growth and Testimony Card Gaps */
  .growth-page-calm,
  .testimony-garden-page{
    display:flex!important;
    flex-direction:column!important;
    gap:6px!important;
    row-gap:6px!important;
  }

  .growth-page-calm > *,
  .testimony-garden-page > *{
    margin-top:0!important;
    margin-bottom:0!important;
  }

  .growth-page-calm > .card,
  .growth-page-calm > .sacred-card,
  .growth-page-calm .growth-summary-card,
  .growth-page-calm .growth-stage-hero,
  .growth-page-calm .growth-account-card,
  .growth-page-calm .growth-hero-card,
  .growth-page-calm .growth-closet-card,
  .growth-page-calm .growth-stones-card,
  .growth-page-calm .growth-book-card,
  .testimony-garden-page > .card,
  .testimony-garden-page > .sacred-card,
  .testimony-garden-page .featured-testimony-card,
  .testimony-garden-page .testimony-featured-garden,
  .testimony-garden-page .testimony-shared-garden,
  .testimony-garden-page .testimony-shared-card{
    margin-top:0!important;
    margin-bottom:6px!important;
  }

  .growth-page-calm > .card + .card,
  .growth-page-calm > .sacred-card + .sacred-card,
  .testimony-garden-page > .card + .card,
  .testimony-garden-page > .sacred-card + .sacred-card{
    margin-top:0!important;
    padding-top:0!important;
  }

  @media(max-width:640px){
    .growth-page-calm,
    .testimony-garden-page{
      gap:5px!important;
      row-gap:5px!important;
    }

    .growth-page-calm > .card,
    .growth-page-calm > .sacred-card,
    .growth-page-calm .growth-summary-card,
    .growth-page-calm .growth-stage-hero,
    .growth-page-calm .growth-account-card,
    .growth-page-calm .growth-hero-card,
    .growth-page-calm .growth-closet-card,
    .growth-page-calm .growth-stones-card,
    .growth-page-calm .growth-book-card,
    .testimony-garden-page > .card,
    .testimony-garden-page > .sacred-card,
    .testimony-garden-page .featured-testimony-card,
    .testimony-garden-page .testimony-featured-garden,
    .testimony-garden-page .testimony-shared-garden,
    .testimony-garden-page .testimony-shared-card{
      margin-bottom:5px!important;
    }
  }


  /* Final Today and Testimony Border Repair */
  .today-page-calm,
  .testimony-garden-page{
    width:100%!important;
    max-width:100%!important;
    gap:10px!important;
    row-gap:10px!important;
  }

  .today-page-calm > .card,
  .today-page-calm .today-hero-card,
  .today-page-calm .today-faith-walk-card,
  .today-page-calm .current-stage-card,
  .today-page-calm .faith-journey-panel,
  .today-page-calm .verse-readable,
  .testimony-garden-page > .card,
  .testimony-garden-page .featured-testimony-card,
  .testimony-garden-page .testimony-featured-garden,
  .testimony-garden-page .testimony-shared-garden,
  .testimony-garden-page .testimony-shared-card{
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    box-sizing:border-box!important;
    border:1px solid rgba(187,205,218,.72)!important;
    border-radius:28px!important;
    background:linear-gradient(145deg,rgba(255,255,255,.96),rgba(255,252,242,.9))!important;
    box-shadow:0 12px 30px rgba(78,58,30,.055)!important;
    overflow:hidden!important;
  }

  .today-page-calm > .card,
  .today-page-calm .today-hero-card,
  .today-page-calm .today-faith-walk-card,
  .testimony-garden-page > .card,
  .testimony-garden-page .featured-testimony-card,
  .testimony-garden-page .testimony-featured-garden,
  .testimony-garden-page .testimony-shared-garden,
  .testimony-garden-page .testimony-shared-card{
    padding:18px!important;
    margin:0 0 8px!important;
  }

  .today-page-calm > .card + .card,
  .testimony-garden-page > .card + .card{
    margin-top:0!important;
    padding-top:18px!important;
  }

  .today-page-calm > .card + .card::before,
  .testimony-garden-page > .card + .card::before{
    display:none!important;
    content:none!important;
  }

  .today-page-calm h1,
  .today-page-calm h2,
  .today-page-calm h3,
  .testimony-garden-page h1,
  .testimony-garden-page h2,
  .testimony-garden-page h3{
    max-width:100%!important;
    overflow-wrap:normal!important;
    word-break:normal!important;
  }

  .today-page-calm p,
  .today-page-calm span,
  .testimony-garden-page p,
  .testimony-garden-page span{
    min-width:0!important;
    word-break:normal!important;
    overflow-wrap:break-word!important;
  }

  @media(max-width:640px){
    .today-page-calm,
    .testimony-garden-page{
      gap:8px!important;
      row-gap:8px!important;
    }

    .today-page-calm > .card,
    .today-page-calm .today-hero-card,
    .today-page-calm .today-faith-walk-card,
    .testimony-garden-page > .card,
    .testimony-garden-page .featured-testimony-card,
    .testimony-garden-page .testimony-featured-garden,
    .testimony-garden-page .testimony-shared-garden,
    .testimony-garden-page .testimony-shared-card{
      border-radius:24px!important;
      padding:15px!important;
      margin:0 0 7px!important;
    }

    .today-page-calm > .card + .card,
    .testimony-garden-page > .card + .card{
      padding-top:15px!important;
    }
  }


  /* Final Growth Stage Title Unclip */
  .growth-page-calm .growth-path-list{
    gap:10px!important;
    padding:6px 8px 14px!important;
    overflow-x:auto!important;
    overflow-y:visible!important;
  }

  .growth-page-calm .growth-path-step{
    flex:0 0 164px!important;
    width:164px!important;
    min-width:164px!important;
    max-width:164px!important;
    min-height:232px!important;
    padding:13px 12px!important;
    overflow:visible!important;
    border-radius:28px!important;
  }

  .growth-page-calm .growth-path-copy{
    width:100%!important;
    min-width:0!important;
    overflow:visible!important;
  }

  .growth-page-calm .growth-path-name{
    display:block!important;
    width:100%!important;
    max-width:100%!important;
    font-size:16px!important;
    line-height:1.18!important;
    white-space:normal!important;
    overflow:visible!important;
    text-overflow:clip!important;
    overflow-wrap:normal!important;
    word-break:normal!important;
    hyphens:none!important;
  }

  .growth-page-calm .growth-path-range,
  .growth-page-calm .growth-path-status{
    display:block!important;
    max-width:100%!important;
    overflow:visible!important;
    text-overflow:clip!important;
    white-space:normal!important;
    word-break:normal!important;
    hyphens:none!important;
  }

  @media(max-width:640px){
    .growth-page-calm .growth-path-list{
      gap:9px!important;
      padding:6px 10px 14px!important;
    }

    .growth-page-calm .growth-path-step{
      flex:0 0 158px!important;
      width:158px!important;
      min-width:158px!important;
      max-width:158px!important;
      min-height:228px!important;
      padding:12px 11px!important;
    }

    .growth-page-calm .growth-path-name{
      font-size:15.5px!important;
      line-height:1.18!important;
    }
  }


  /* Final Today and Growth Border Stabilizer */
  .today-page-calm,
  .growth-page-calm{
    width:100%!important;
    max-width:100%!important;
    display:flex!important;
    flex-direction:column!important;
    gap:10px!important;
    row-gap:10px!important;
    overflow:visible!important;
  }

  .today-page-calm > *,
  .growth-page-calm > *{
    box-sizing:border-box!important;
    max-width:100%!important;
    min-width:0!important;
  }

  .today-page-calm > .card,
  .today-page-calm .today-hero-card,
  .today-page-calm .today-faith-walk-card,
  .today-page-calm .current-stage-card,
  .today-page-calm .faith-journey-panel,
  .today-page-calm .verse-readable,
  .growth-page-calm > .card,
  .growth-page-calm .growth-summary-card,
  .growth-page-calm .growth-stage-hero,
  .growth-page-calm .growth-account-card,
  .growth-page-calm .growth-hero-card,
  .growth-page-calm .growth-closet-card,
  .growth-page-calm .growth-stones-card,
  .growth-page-calm .growth-book-card{
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    box-sizing:border-box!important;
    margin:0 0 8px!important;
    padding:18px!important;
    border:0!important;
    border-radius:26px!important;
    background:linear-gradient(145deg,rgba(255,255,255,.96),rgba(255,252,244,.91))!important;
    box-shadow:
      inset 0 0 0 1.4px rgba(187,205,218,.82),
      0 10px 26px rgba(74,54,29,.055)!important;
    overflow:visible!important;
    clip-path:none!important;
  }

  .today-page-calm > .card + .card,
  .growth-page-calm > .card + .card{
    margin-top:0!important;
    padding-top:18px!important;
  }

  .today-page-calm > .card::before,
  .today-page-calm > .card::after,
  .growth-page-calm > .card::before,
  .growth-page-calm > .card::after{
    pointer-events:none!important;
  }

  .today-page-calm > .card + .card::before,
  .growth-page-calm > .card + .card::before{
    display:none!important;
    content:none!important;
  }

  .growth-page-calm .growth-path-list{
    width:100%!important;
    max-width:100%!important;
    box-sizing:border-box!important;
    display:flex!important;
    flex-direction:row!important;
    flex-wrap:nowrap!important;
    gap:10px!important;
    padding:8px 8px 16px!important;
    margin:10px 0 0!important;
    overflow-x:auto!important;
    overflow-y:visible!important;
    scroll-padding-left:8px!important;
  }

  .growth-page-calm .growth-path-step{
    flex:0 0 166px!important;
    width:166px!important;
    min-width:166px!important;
    max-width:166px!important;
    min-height:230px!important;
    padding:13px 12px!important;
    border:0!important;
    border-radius:28px!important;
    background:rgba(255,255,252,.74)!important;
    box-shadow:
      inset 0 0 0 1.2px rgba(218,205,176,.56),
      0 6px 18px rgba(74,54,29,.045)!important;
    overflow:visible!important;
    box-sizing:border-box!important;
  }

  .growth-page-calm .growth-path-copy,
  .growth-page-calm .growth-path-name,
  .growth-page-calm .growth-path-range,
  .growth-page-calm .growth-path-status{
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    overflow:visible!important;
    text-overflow:clip!important;
    white-space:normal!important;
    overflow-wrap:normal!important;
    word-break:normal!important;
    hyphens:none!important;
    box-sizing:border-box!important;
  }

  .growth-page-calm .growth-path-name{
    font-size:15.5px!important;
    line-height:1.18!important;
  }

  .growth-page-calm .growth-path-range{
    font-size:11.5px!important;
    line-height:1.25!important;
  }

  @media(max-width:640px){
    .today-page-calm,
    .growth-page-calm{
      gap:8px!important;
      row-gap:8px!important;
    }

    .today-page-calm > .card,
    .today-page-calm .today-hero-card,
    .today-page-calm .today-faith-walk-card,
    .today-page-calm .current-stage-card,
    .today-page-calm .faith-journey-panel,
    .today-page-calm .verse-readable,
    .growth-page-calm > .card,
    .growth-page-calm .growth-summary-card,
    .growth-page-calm .growth-stage-hero,
    .growth-page-calm .growth-account-card,
    .growth-page-calm .growth-hero-card,
    .growth-page-calm .growth-closet-card,
    .growth-page-calm .growth-stones-card,
    .growth-page-calm .growth-book-card{
      border-radius:23px!important;
      padding:15px!important;
      margin-bottom:7px!important;
    }

    .growth-page-calm .growth-path-list{
      gap:9px!important;
      padding:8px 8px 15px!important;
    }

    .growth-page-calm .growth-path-step{
      flex-basis:158px!important;
      width:158px!important;
      min-width:158px!important;
      max-width:158px!important;
      min-height:224px!important;
      padding:12px 11px!important;
    }

    .growth-page-calm .growth-path-name{
      font-size:15px!important;
      line-height:1.18!important;
    }
  }


  /* Final friend notification + remove confirmation polish. */
  .friend-heart-dot,
  .tab-heart-dot{
    color:#ff2d55!important;
    -webkit-text-fill-color:#ff2d55!important;
    background:rgba(255,255,255,.98)!important;
    border:1px solid rgba(255,45,85,.18)!important;
    box-shadow:0 2px 8px rgba(55,42,20,.12),0 0 0 1px rgba(255,45,85,.08)!important;
  }
  .friend-heart-dot::before,
  .tab-heart-dot::before{
    color:#ff2d55!important;
    -webkit-text-fill-color:#ff2d55!important;
  }
  .friend-remove-overlay{
    position:fixed!important;
    inset:0!important;
    z-index:10050!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    padding:24px!important;
    background:rgba(54,43,30,.24)!important;
    backdrop-filter:blur(8px)!important;
    -webkit-backdrop-filter:blur(8px)!important;
  }
  .friend-remove-card{
    position:relative!important;
    width:min(92vw,390px)!important;
    border-radius:30px!important;
    padding:30px 24px 22px!important;
    text-align:center!important;
    background:linear-gradient(155deg,rgba(255,253,246,.98),rgba(255,248,226,.92),rgba(246,250,255,.80))!important;
    border:1.5px solid rgba(214,176,94,.44)!important;
    box-shadow:0 24px 70px rgba(54,43,30,.22),inset 0 1px 0 rgba(255,255,255,.88)!important;
    color:var(--text-dark)!important;
    animation:stageRevealIn .28s ease-out both!important;
  }
  .friend-remove-card h3{
    margin:2px 0 8px!important;
    font-size:clamp(24px,7vw,32px)!important;
    line-height:1.05!important;
    color:#3c2a13!important;
    font-weight:650!important;
  }
  .friend-remove-card p{
    margin:0 auto 18px!important;
    max-width:280px!important;
    color:#6f5b3f!important;
    font-size:14px!important;
    line-height:1.45!important;
    text-align:center!important;
  }
  .friend-remove-close{
    position:absolute!important;
    right:14px!important;
    top:14px!important;
    width:38px!important;
    height:38px!important;
    border-radius:999px!important;
    border:1px solid rgba(184,147,75,.24)!important;
    background:rgba(255,253,246,.86)!important;
    color:#4f3b1d!important;
    font-size:24px!important;
    line-height:1!important;
    cursor:pointer!important;
    box-shadow:0 7px 18px rgba(91,67,28,.08)!important;
  }
  .friend-remove-mark{
    width:54px!important;
    height:54px!important;
    margin:0 auto 12px!important;
    border-radius:999px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    background:linear-gradient(145deg,rgba(255,248,223,.98),rgba(246,218,146,.72))!important;
    border:1px solid rgba(201,144,18,.34)!important;
    color:#ff2d55!important;
    -webkit-text-fill-color:#ff2d55!important;
    font-size:25px!important;
    box-shadow:0 10px 24px rgba(201,144,18,.16),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .friend-remove-actions{
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    gap:10px!important;
    align-items:center!important;
  }
  .friend-remove-actions.single{
    grid-template-columns:1fr!important;
  }
  .friend-remove-actions .soft-action-btn{
    width:100%!important;
    min-height:44px!important;
    justify-content:center!important;
  }
  .partner-request-card.success .partner-request-mark{
    color:#ff2d55!important;
    -webkit-text-fill-color:#ff2d55!important;
  }
  .partner-request-card.info .partner-request-mark{
    color:#b8861f!important;
    -webkit-text-fill-color:#b8861f!important;
  }
  .partner-request-card.error .partner-request-mark{
    color:#8b2f2f!important;
    -webkit-text-fill-color:#8b2f2f!important;
    background:linear-gradient(145deg,rgba(255,245,239,.98),rgba(250,221,207,.78))!important;
    border-color:rgba(139,47,47,.22)!important;
  }
  @media(max-width:560px){
    .friend-remove-overlay{align-items:center!important;padding:18px!important;}
    .friend-remove-card{padding:28px 18px 20px!important;border-radius:26px!important;}
  }


  /* Final typography and mobile rhythm pass */
  .app-shell{
    color:#35281D!important;
    -webkit-font-smoothing:antialiased;
    text-rendering:optimizeLegibility;
  }

  .app-shell p,
  .app-shell span,
  .app-shell li,
  .app-shell label,
  .app-shell button,
  .app-shell input,
  .app-shell textarea,
  .app-shell .tab-label{
    color:inherit;
  }

  .app-shell .card,
  .app-shell .sacred-card,
  .app-shell .focus-card,
  .app-shell .soft-guide-card,
  .app-shell .current-stage-card,
  .app-shell .prayer-vessels-section,
  .app-shell .today-hero-card,
  .app-shell .today-faith-walk-card,
  .app-shell .growth-summary-card,
  .app-shell .growth-stage-hero,
  .app-shell .growth-account-card,
  .app-shell .growth-hero-card,
  .app-shell .growth-closet-card,
  .app-shell .growth-stones-card,
  .app-shell .growth-book-card,
  .app-shell .home-care-card,
  .app-shell .home-preview-card,
  .app-shell .home-shop-shell-card,
  .app-shell .journal-write-card,
  .app-shell .journal-history-card,
  .app-shell .testimony-garden-hero,
  .app-shell .featured-testimony-card,
  .app-shell .share-testimony-card,
  .app-shell .testimony-shared-garden,
  .app-shell .prayer-friends-card,
  .app-shell .spirit-friends-tools-card,
  .app-shell .community-feed-card,
  .app-shell .accountability-primary-card,
  .app-shell .blessing-community-card{
    color:#35281D!important;
  }

  .app-shell h1,
  .app-shell .page-title,
  .app-shell .today-title,
  .app-shell .home-section-title,
  .app-shell .growth-page-calm h1,
  .app-shell .testimony-garden-page h1,
  .app-shell .prayer-page-calm h1,
  .app-shell .premium-journal-page h1{
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-size:clamp(30px,6.4vw,42px)!important;
    line-height:1.05!important;
    font-weight:650!important;
    color:#3A2812!important;
    letter-spacing:0!important;
  }

  .app-shell h2,
  .app-shell .card h2,
  .app-shell .section-title,
  .app-shell .today-page-calm h2,
  .app-shell .growth-page-calm h2,
  .app-shell .home-page h2,
  .app-shell .prayer-page-calm h2,
  .app-shell .testimony-garden-page h2,
  .app-shell .premium-journal-page h2,
  .app-shell .spirit-friends-dashboard h2{
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-size:clamp(24px,5.2vw,32px)!important;
    line-height:1.12!important;
    font-weight:650!important;
    color:#3A2812!important;
    letter-spacing:0!important;
  }

  .app-shell h3,
  .app-shell .card h3,
  .app-shell .home-care-card h3,
  .app-shell .friend-card h3,
  .app-shell .testimony-entry h3,
  .app-shell .journal-entry-card h3,
  .app-shell .growth-path-name,
  .app-shell .prayer-vessel-title,
  .app-shell .answered-altar-header h3{
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
    font-size:clamp(18px,4.25vw,22px)!important;
    line-height:1.22!important;
    font-weight:750!important;
    color:#35281D!important;
    letter-spacing:0!important;
  }

  .app-shell p,
  .app-shell .card p,
  .app-shell .card div,
  .app-shell .today-page-calm p,
  .app-shell .growth-page-calm p,
  .app-shell .home-page p,
  .app-shell .prayer-page-calm p,
  .app-shell .testimony-garden-page p,
  .app-shell .premium-journal-page p,
  .app-shell .spirit-friends-dashboard p{
    font-size:15.5px;
    line-height:1.5;
    color:#4B3A25;
  }

  .app-shell .muted,
  .app-shell .helper-text,
  .app-shell .section-subtitle,
  .app-shell .card small,
  .app-shell .growth-path-range,
  .app-shell .growth-path-status,
  .app-shell .testimony-label,
  .app-shell .shop-card small,
  .app-shell .home-shop-card small{
    font-size:13px!important;
    line-height:1.35!important;
    color:#6B5940!important;
    font-weight:600!important;
  }

  .app-shell .gold-btn,
  .app-shell .soft-action-btn,
  .app-shell .quest-nav-btn,
  .app-shell .home-bless-btn,
  .app-shell .home-care-action,
  .app-shell .sam-btn,
  .app-shell .tab-btn,
  .app-shell .shop-card button,
  .app-shell .home-shop-card button{
    font-size:14px!important;
    line-height:1.18!important;
    font-weight:700!important;
  }

  .app-shell .chip,
  .app-shell .placed-chip,
  .app-shell .section-divider-label,
  .app-shell .stage-reveal-kicker,
  .app-shell .testimony-category-row button,
  .app-shell .shop-card .collection-label,
  .app-shell .home-shop-card .collection-label{
    font-size:12px!important;
    line-height:1.2!important;
    font-weight:650!important;
    letter-spacing:.04em!important;
  }

  .today-page-calm,
  .growth-page-calm,
  .home-page,
  .prayer-page-calm,
  .testimony-garden-page,
  .premium-journal-page,
  .spirit-friends-dashboard{
    gap:14px!important;
    row-gap:14px!important;
  }

  .today-page-calm > .card,
  .growth-page-calm > .card,
  .home-page > .card,
  .prayer-page-calm > .card,
  .testimony-garden-page > .card,
  .premium-journal-page > .card,
  .spirit-friends-dashboard > .card{
    margin-bottom:12px!important;
  }

  @media(max-width:640px){
    .app-shell h1,
    .app-shell .page-title,
    .app-shell .today-title,
    .app-shell .home-section-title,
    .app-shell .growth-page-calm h1,
    .app-shell .testimony-garden-page h1,
    .app-shell .prayer-page-calm h1,
    .app-shell .premium-journal-page h1{
      font-size:clamp(27px,8vw,34px)!important;
      line-height:1.06!important;
    }

    .app-shell h2,
    .app-shell .card h2,
    .app-shell .section-title,
    .app-shell .today-page-calm h2,
    .app-shell .growth-page-calm h2,
    .app-shell .home-page h2,
    .app-shell .prayer-page-calm h2,
    .app-shell .testimony-garden-page h2,
    .app-shell .premium-journal-page h2,
    .app-shell .spirit-friends-dashboard h2{
      font-size:clamp(22px,6.8vw,29px)!important;
      line-height:1.1!important;
    }

    .app-shell h3,
    .app-shell .card h3,
    .app-shell .home-care-card h3,
    .app-shell .friend-card h3,
    .app-shell .testimony-entry h3,
    .app-shell .journal-entry-card h3,
    .app-shell .growth-path-name,
    .app-shell .prayer-vessel-title,
    .app-shell .answered-altar-header h3{
      font-size:17px!important;
      line-height:1.22!important;
    }

    .app-shell p,
    .app-shell .card p,
    .app-shell .today-page-calm p,
    .app-shell .growth-page-calm p,
    .app-shell .home-page p,
    .app-shell .prayer-page-calm p,
    .app-shell .testimony-garden-page p,
    .app-shell .premium-journal-page p,
    .app-shell .spirit-friends-dashboard p{
      font-size:14.5px!important;
      line-height:1.45!important;
    }

    .today-page-calm,
    .growth-page-calm,
    .home-page,
    .prayer-page-calm,
    .testimony-garden-page,
    .premium-journal-page,
    .spirit-friends-dashboard{
      gap:9px!important;
      row-gap:9px!important;
    }

    .today-page-calm > .card,
    .growth-page-calm > .card,
    .home-page > .card,
    .prayer-page-calm > .card,
    .testimony-garden-page > .card,
    .premium-journal-page > .card,
    .spirit-friends-dashboard > .card{
      margin-bottom:8px!important;
      padding:15px!important;
    }

    .today-page-calm .card,
    .growth-page-calm .card,
    .home-page .card,
    .prayer-page-calm .card,
    .testimony-garden-page .card,
    .premium-journal-page .card,
    .spirit-friends-dashboard .card{
      border-radius:22px!important;
    }

    .app-shell .gold-btn,
    .app-shell .soft-action-btn,
    .app-shell .quest-nav-btn,
    .app-shell .home-bless-btn,
    .app-shell .home-care-action,
    .app-shell .sam-btn,
    .app-shell .shop-card button,
    .app-shell .home-shop-card button{
      font-size:13.5px!important;
    }
  }

  /* Final requested layout pass */
  .prayer-page-calm .prayer-vessel-hero{
    background:linear-gradient(135deg,rgba(255,253,246,.98),rgba(255,243,196,.68))!important;
    border:1px solid rgba(196,143,32,.22)!important;
    box-shadow:0 8px 24px rgba(138,90,8,.08)!important;
  }
  .prayer-page-calm .prayer-vessel-hero-copy{
    text-align:center!important;
    align-items:center!important;
    justify-content:center!important;
  }
  .prayer-page-calm .prayer-vessel-hero-copy h2{
    display:block!important;
    width:min(100%,720px)!important;
    max-width:100%!important;
    box-sizing:border-box!important;
    margin:0 auto 10px!important;
    padding:0 18px!important;
    font-size:clamp(18px,4vw,24px)!important;
    line-height:1.16!important;
    text-align:center!important;
    white-space:normal!important;
    overflow-wrap:break-word!important;
    word-break:normal!important;
    text-wrap:balance;
  }
  .prayer-page-calm .prayer-vessel-hero-copy p{
    text-align:center!important;
    white-space:normal!important;
  }
  .today-faith-walk-title-row{
    display:flex!important;
    align-items:baseline!important;
    gap:8px!important;
    white-space:nowrap!important;
    flex-wrap:nowrap!important;
  }
  .today-faith-walk-title-row h2{
    white-space:nowrap!important;
    font-size:clamp(18px,4.8vw,24px)!important;
  }
  .today-faith-walk-count{
    white-space:nowrap!important;
    flex-shrink:0!important;
  }


  /* Top three visual polish pass: unified headers, calmer spacing, stronger sacred centerpieces */
  .app-shell .today-hero-card,
  .app-shell .growth-stage-hero,
  .app-shell .home-care-card,
  .app-shell .journal-write-card:first-child,
  .app-shell .prayer-vessels-section,
  .app-shell .testimony-garden-hero,
  .app-shell .spirit-friends-dashboard > .card:first-child{
    position:relative!important;
    overflow:hidden!important;
    background:linear-gradient(150deg,rgba(255,253,246,.98),rgba(255,247,220,.76) 56%,rgba(246,250,255,.50))!important;
    border:1px solid rgba(196,143,32,.22)!important;
    box-shadow:0 8px 26px rgba(138,90,8,.075)!important;
  }
  .app-shell .today-hero-card::before,
  .app-shell .growth-stage-hero::before,
  .app-shell .home-care-card::before,
  .app-shell .journal-write-card:first-child::before,
  .app-shell .prayer-vessels-section::before,
  .app-shell .testimony-garden-hero::before,
  .app-shell .spirit-friends-dashboard > .card:first-child::before{
    content:"";
    position:absolute;
    inset:-40% -18% auto;
    height:150px;
    background:radial-gradient(ellipse,rgba(255,255,255,.72),rgba(255,243,196,.34) 46%,rgba(255,255,255,0) 72%);
    pointer-events:none;
    opacity:.84;
  }
  .app-shell .today-hero-card > *,
  .app-shell .growth-stage-hero > *,
  .app-shell .home-care-card > *,
  .app-shell .journal-write-card:first-child > *,
  .app-shell .prayer-vessels-section > *,
  .app-shell .testimony-garden-hero > *,
  .app-shell .spirit-friends-dashboard > .card:first-child > *{
    position:relative;
    z-index:1;
  }

  .app-shell .today-page-calm,
  .app-shell .growth-page-calm,
  .app-shell .home-page,
  .app-shell .prayer-page-calm,
  .app-shell .testimony-garden-page,
  .app-shell .premium-journal-page,
  .app-shell .spirit-friends-dashboard{
    gap:18px!important;
    row-gap:18px!important;
  }
  .app-shell .today-page-calm > .card,
  .app-shell .growth-page-calm > .card,
  .app-shell .home-page > .card,
  .app-shell .prayer-page-calm > .card,
  .app-shell .testimony-garden-page > .card,
  .app-shell .premium-journal-page > .card,
  .app-shell .spirit-friends-dashboard > .card{
    margin-bottom:0!important;
    border-color:rgba(184,147,75,.16)!important;
    box-shadow:0 3px 14px rgba(80,55,20,.045)!important;
  }
  .app-shell .card,
  .app-shell .sacred-card,
  .app-shell .friend-card,
  .app-shell .testimony-entry,
  .app-shell .home-shop-card,
  .app-shell .shop-card{
    box-shadow:0 3px 14px rgba(80,55,20,.045)!important;
  }
  .app-shell .card p,
  .app-shell .sacred-card p,
  .app-shell .friend-card p,
  .app-shell .testimony-entry p{
    max-width:68ch;
  }

  .app-shell .prayer-vessel-hero,
  .app-shell .testimony-featured-garden,
  .app-shell .home-preview-card,
  .app-shell .home-room{
    border-color:rgba(201,144,18,.24)!important;
    box-shadow:0 12px 34px rgba(138,90,8,.10)!important;
  }
  .app-shell .testimony-featured-garden,
  .app-shell .home-preview-card{
    background:linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,248,223,.76) 48%,rgba(246,250,255,.52))!important;
  }
  .app-shell .testimony-featured-garden .featured-testimony-image{
    max-height:260px!important;
    object-fit:contain!important;
    background:radial-gradient(circle at 50% 42%,rgba(255,243,196,.58),rgba(255,253,246,.24) 58%,rgba(246,250,255,.30))!important;
  }
  .app-shell .home-preview-card .home-room,
  .app-shell .sticky-preview-card .home-room{
    min-height:380px!important;
  }
  .app-shell .prayer-vessel-kicker,
  .app-shell .section-label,
  .app-shell .section-divider-label{
    color:#8A5A08!important;
  }
  .app-shell .testimony-share-garden h3 + p:empty{
    display:none!important;
  }

  @media(max-width:640px){
    .app-shell .today-page-calm,
    .app-shell .growth-page-calm,
    .app-shell .home-page,
    .app-shell .prayer-page-calm,
    .app-shell .testimony-garden-page,
    .app-shell .premium-journal-page,
    .app-shell .spirit-friends-dashboard{
      gap:12px!important;
      row-gap:12px!important;
    }
    .app-shell .today-page-calm > .card,
    .app-shell .growth-page-calm > .card,
    .app-shell .home-page > .card,
    .app-shell .prayer-page-calm > .card,
    .app-shell .testimony-garden-page > .card,
    .app-shell .premium-journal-page > .card,
    .app-shell .spirit-friends-dashboard > .card{
      padding:16px!important;
      border-radius:22px!important;
    }
    .app-shell .testimony-featured-garden .featured-testimony-image{
      max-height:210px!important;
    }
    .app-shell .home-preview-card .home-room,
    .app-shell .sticky-preview-card .home-room{
      min-height:330px!important;
    }
  }


  /* Refined bottom navigation polish: softer sacred garden tab bar */
  .app-shell .bottom-tabs{
    position:fixed!important;
    left:50%!important;
    right:auto!important;
    bottom:max(10px,env(safe-area-inset-bottom))!important;
    transform:translateX(-50%)!important;
    width:min(calc(100vw - 22px),548px)!important;
    min-height:66px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:3px!important;
    padding:8px 10px calc(8px + env(safe-area-inset-bottom))!important;
    border-radius:999px!important;
    border:1px solid rgba(201,144,18,.18)!important;
    background:
      linear-gradient(135deg,rgba(255,253,246,.92),rgba(255,248,223,.82) 54%,rgba(246,250,255,.76)),
      rgba(255,253,246,.82)!important;
    -webkit-backdrop-filter:blur(24px) saturate(1.14)!important;
    backdrop-filter:blur(24px) saturate(1.14)!important;
    box-shadow:
      0 16px 42px rgba(80,55,20,.12),
      0 5px 18px rgba(125,174,212,.10),
      inset 0 1px 0 rgba(255,255,255,.88)!important;
  }
  .app-shell .tab-btn{
    position:relative!important;
    isolation:isolate!important;
    flex:1 1 0!important;
    min-width:0!important;
    max-width:none!important;
    min-height:52px!important;
    padding:6px 3px 5px!important;
    border:0!important;
    border-radius:999px!important;
    background:transparent!important;
    box-shadow:none!important;
    color:#735014!important;
    opacity:.84!important;
    transform:none!important;
    transition:opacity .18s ease,transform .18s ease,color .18s ease!important;
  }
  .app-shell .tab-btn::before{
    content:""!important;
    position:absolute!important;
    inset:3px 2px!important;
    z-index:-1!important;
    border-radius:999px!important;
    opacity:0!important;
    transform:scale(.90)!important;
    background:linear-gradient(145deg,rgba(255,255,255,.96),rgba(255,243,196,.92) 48%,rgba(240,199,94,.46))!important;
    border:1px solid rgba(201,144,18,.22)!important;
    box-shadow:0 8px 20px rgba(138,90,8,.16),0 0 0 4px rgba(255,243,196,.24),inset 0 1px 0 rgba(255,255,255,.92)!important;
    transition:opacity .2s ease,transform .2s ease!important;
  }
  .app-shell .tab-btn.active{
    opacity:1!important;
    transform:translateY(-2px)!important;
  }
  .app-shell .tab-btn.active::before{
    opacity:1!important;
    transform:scale(1)!important;
  }
  .app-shell .tab-btn:active{
    transform:translateY(0) scale(.97)!important;
  }
  .app-shell .tab-btn[style*="opacity: 0.62"],
  .app-shell .tab-btn[aria-label$="locked"]{
    opacity:.42!important;
    filter:saturate(.72)!important;
  }
  .app-shell .tab-icon-wrap{
    position:relative!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    min-height:25px!important;
    margin-bottom:1px!important;
  }
  .app-shell .tab-icon-wrap > span:first-child{
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    width:25px!important;
    height:25px!important;
    font-size:20px!important;
    line-height:1!important;
    filter:drop-shadow(0 1px 2px rgba(82,55,15,.08))!important;
    transform:translateZ(0)!important;
  }
  .app-shell .tab-btn.active .tab-icon-wrap > span:first-child{
    filter:drop-shadow(0 2px 5px rgba(138,90,8,.18))!important;
  }
  .app-shell .tab-label{
    display:block!important;
    width:100%!important;
    max-width:74px!important;
    font-size:10.6px!important;
    line-height:1.08!important;
    font-weight:850!important;
    letter-spacing:0!important;
    color:#735014!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    text-align:center!important;
  }
  .app-shell .tab-btn.active .tab-label{
    color:#3A2600!important;
    font-weight:950!important;
  }
  .app-shell .tab-btn[data-tab-id="wall"] .tab-label{
    font-size:9.7px!important;
    max-width:78px!important;
  }
  .app-shell .tab-heart-dot{
    position:absolute!important;
    right:-8px!important;
    top:-6px!important;
    width:16px!important;
    height:16px!important;
    min-width:16px!important;
    border-radius:999px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    color:#b83258!important;
    -webkit-text-fill-color:#b83258!important;
    background:linear-gradient(145deg,#fff,#fff1f5)!important;
    border:1px solid rgba(184,50,88,.16)!important;
    box-shadow:0 3px 8px rgba(138,90,8,.12),0 0 0 3px rgba(255,241,245,.42)!important;
    font-size:9px!important;
    line-height:1!important;
  }
  .app-shell .tab-lock-dot{
    position:absolute!important;
    right:-9px!important;
    top:-6px!important;
    width:15px!important;
    height:15px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    border-radius:999px!important;
    background:rgba(255,253,246,.96)!important;
    border:1px solid rgba(184,147,75,.20)!important;
    box-shadow:0 2px 6px rgba(80,55,20,.08)!important;
    font-size:8px!important;
    line-height:1!important;
  }
  @media(max-width:520px){
    .app-shell .bottom-tabs{
      width:min(calc(100vw - 12px),430px)!important;
      min-height:61px!important;
      bottom:max(7px,env(safe-area-inset-bottom))!important;
      gap:1px!important;
      padding:7px 6px calc(7px + env(safe-area-inset-bottom))!important;
    }
    .app-shell .tab-btn{
      min-height:48px!important;
      padding:5px 1px 4px!important;
    }
    .app-shell .tab-btn::before{
      inset:3px 1px!important;
    }
    .app-shell .tab-icon-wrap{
      min-height:23px!important;
    }
    .app-shell .tab-icon-wrap > span:first-child{
      width:22px!important;
      height:22px!important;
      font-size:18px!important;
    }
    .app-shell .tab-label{
      max-width:57px!important;
      font-size:9.4px!important;
      line-height:1.05!important;
    }
    .app-shell .tab-btn[data-tab-id="wall"] .tab-label{
      max-width:62px!important;
      font-size:8.6px!important;
    }
    .app-shell .tab-heart-dot{
      right:-7px!important;
      top:-6px!important;
      width:14px!important;
      height:14px!important;
      min-width:14px!important;
      font-size:8px!important;
    }
  }


  /* Beautiful empty states polish */
  .app-shell .empty-state{
    position:relative!important;
    overflow:hidden!important;
    padding:20px 16px!important;
    border-radius:22px!important;
    text-align:center!important;
    background:linear-gradient(155deg,rgba(255,253,246,.96),rgba(255,248,223,.68) 54%,rgba(246,250,255,.54))!important;
    border:1px dashed rgba(201,144,18,.26)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.74),0 4px 16px rgba(80,55,20,.045)!important;
  }
  .app-shell .empty-state::before{
    content:"";
    position:absolute;
    left:18%;
    right:18%;
    top:-42px;
    height:88px;
    border-radius:999px;
    background:radial-gradient(ellipse,rgba(255,255,255,.82),rgba(255,243,196,.36) 50%,rgba(255,255,255,0) 74%);
    pointer-events:none;
  }
  .app-shell .empty-state > *{
    position:relative;
    z-index:1;
  }
  .app-shell .empty-state-icon{
    width:48px;
    height:48px;
    margin:0 auto 10px;
    border-radius:50%;
    display:flex;
    align-items:center;
    justify-content:center;
    font-size:25px;
    background:radial-gradient(circle at 32% 24%,#fff 0,#fff8df 48%,rgba(240,199,94,.34) 100%);
    border:1px solid rgba(201,144,18,.22);
    box-shadow:0 7px 18px rgba(138,90,8,.09),inset 0 1px 0 rgba(255,255,255,.86);
  }
  .app-shell .empty-state-title{
    margin:0 0 6px!important;
    font-size:15px!important;
    line-height:1.22!important;
    font-weight:900!important;
    color:#5B3A08!important;
  }
  .app-shell .empty-state-copy{
    margin:0 auto 14px!important;
    max-width:34ch!important;
    font-size:13.5px!important;
    line-height:1.55!important;
    color:#735014!important;
  }
  .app-shell .empty-state .gold-btn,
  .app-shell .empty-state .soft-action-btn{
    max-width:260px!important;
    margin-left:auto!important;
    margin-right:auto!important;
  }
  @media(max-width:520px){
    .app-shell .empty-state{
      padding:18px 13px!important;
      border-radius:20px!important;
    }
    .app-shell .empty-state-icon{
      width:44px!important;
      height:44px!important;
      font-size:23px!important;
    }
    .app-shell .empty-state-copy{
      font-size:13px!important;
      max-width:30ch!important;
    }
  }


  /* Answered prayer celebration modal */
  .answered-celebration-overlay{
    position:fixed;
    inset:0;
    z-index:10060;
    display:flex;
    align-items:center;
    justify-content:center;
    padding:20px;
    background:rgba(54,43,30,.28);
    -webkit-backdrop-filter:blur(10px);
    backdrop-filter:blur(10px);
  }
  .answered-celebration-card{
    position:relative;
    overflow:hidden;
    width:min(92vw,420px);
    padding:30px 22px 22px;
    border-radius:30px;
    text-align:center;
    background:linear-gradient(155deg,#fffdf6 0%,#fff3c4 52%,#f0f9ff 100%);
    border:1.5px solid rgba(201,144,18,.34);
    box-shadow:0 24px 70px rgba(80,55,20,.24),inset 0 1px 0 rgba(255,255,255,.9);
    animation:modalIn .28s ease-out;
  }
  .answered-celebration-card::before{
    content:"";
    position:absolute;
    left:12%;
    right:12%;
    top:-52px;
    height:130px;
    border-radius:999px;
    background:radial-gradient(ellipse,rgba(255,255,255,.9),rgba(255,243,196,.62) 48%,rgba(255,255,255,0) 74%);
    pointer-events:none;
  }
  .answered-celebration-close{
    position:absolute;
    top:12px;
    right:12px;
    z-index:2;
    width:34px;
    height:34px;
    border-radius:50%;
    border:1px solid rgba(201,144,18,.20);
    background:rgba(255,253,246,.78);
    color:#8A5A08;
    font-size:20px;
    font-weight:800;
    cursor:pointer;
  }
  .answered-celebration-icon{
    position:relative;
    z-index:1;
    width:66px;
    height:66px;
    margin:0 auto 12px;
    border-radius:50%;
    display:flex;
    align-items:center;
    justify-content:center;
    font-size:34px;
    background:radial-gradient(circle at 32% 24%,#fff 0,#fff8df 45%,rgba(240,199,94,.52) 100%);
    border:1px solid rgba(201,144,18,.24);
    box-shadow:0 10px 24px rgba(138,90,8,.12),0 0 0 8px rgba(255,243,196,.34);
  }
  .answered-celebration-kicker{
    position:relative;
    z-index:1;
    margin:0 0 6px;
    font-size:11px!important;
    letter-spacing:.16em;
    text-transform:uppercase;
    color:#8A5A08!important;
    font-weight:900;
  }
  .answered-celebration-card h2{
    position:relative;
    z-index:1;
    margin:0 0 8px;
    color:#3A2600!important;
    font-size:30px!important;
    line-height:1.05!important;
  }
  .answered-celebration-copy{
    position:relative;
    z-index:1;
    margin:0 auto 14px!important;
    max-width:30ch;
    color:#735014!important;
    font-size:15px!important;
    line-height:1.5!important;
    font-weight:700;
  }
  .answered-celebration-note{
    position:relative;
    z-index:1;
    margin:0 0 14px;
    padding:12px 14px;
    border-radius:18px;
    background:rgba(255,253,246,.62);
    border:1px solid rgba(201,144,18,.18);
  }
  .answered-celebration-note span{
    display:block;
    color:#5B3A08!important;
    font-weight:900;
    font-size:14px;
  }
  .answered-celebration-note p{
    margin:5px 0 0!important;
    color:#735014!important;
    font-size:13px!important;
    line-height:1.45!important;
    max-height:4.4em;
    overflow:hidden;
  }
  .answered-celebration-helper{
    position:relative;
    z-index:1;
    margin:-3px 0 12px!important;
    color:#497093!important;
    font-size:13px!important;
    font-weight:800;
  }
  .answered-celebration-actions{
    position:relative;
    z-index:1;
    display:grid;
    grid-template-columns:1fr;
    gap:8px;
  }
  .answered-celebration-actions .soft-action-btn{
    width:100%;
    justify-content:center;
    min-height:44px;
  }
  .answered-celebration-spark{
    position:absolute;
    z-index:1;
    color:#C99012;
    opacity:.72;
    animation:twinkle 2.4s ease-in-out infinite;
  }
  .answered-celebration-spark.one{left:16%;top:28%;font-size:18px;}
  .answered-celebration-spark.two{right:16%;top:22%;font-size:16px;animation-delay:.6s;}
  .answered-celebration-spark.three{right:21%;bottom:28%;font-size:14px;animation-delay:1.1s;}
  @media(max-width:520px){
    .answered-celebration-card{padding:28px 18px 20px;border-radius:26px;}
    .answered-celebration-card h2{font-size:27px!important;}
  }


  /* Home progress strip: links spirit growth to home growth */
  .app-shell .home-growth-strip{
    position:relative!important;
    overflow:hidden!important;
    background:linear-gradient(155deg,rgba(255,253,246,.97),rgba(255,248,223,.74) 54%,rgba(236,253,245,.56))!important;
    border:1px solid rgba(201,144,18,.20)!important;
    border-radius:24px!important;
    box-shadow:0 5px 18px rgba(80,55,20,.055)!important;
  }
  .app-shell .home-growth-strip::before{
    content:"";
    position:absolute;
    left:10%;
    right:10%;
    top:-54px;
    height:120px;
    border-radius:999px;
    background:radial-gradient(ellipse,rgba(255,255,255,.86),rgba(255,243,196,.42) 50%,rgba(255,255,255,0) 74%);
    pointer-events:none;
  }
  .app-shell .home-growth-strip > *{
    position:relative;
    z-index:1;
  }
  .app-shell .home-growth-strip-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:12px;
    margin-bottom:12px;
  }
  .app-shell .home-growth-strip-head h3{
    margin:0 0 4px!important;
    font-size:18px!important;
    line-height:1.16!important;
    color:#3A2600!important;
  }
  .app-shell .home-growth-strip-head p{
    margin:0!important;
    font-size:13.5px!important;
    line-height:1.45!important;
    color:#735014!important;
    font-weight:750!important;
  }
  .app-shell .home-growth-stage-pill{
    flex-shrink:0;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    min-width:48px;
    min-height:34px;
    border-radius:999px;
    background:rgba(255,253,246,.82);
    border:1px solid rgba(201,144,18,.22);
    color:#8A5A08!important;
    font-size:12px!important;
    font-weight:950!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.82);
  }
  .app-shell .home-growth-track{
    position:relative;
    height:8px;
    margin:0 2px 12px;
    border-radius:999px;
    overflow:hidden;
    background:rgba(184,147,75,.15);
    box-shadow:inset 0 1px 3px rgba(80,55,20,.08);
  }
  .app-shell .home-growth-track-fill{
    position:absolute;
    inset:0 auto 0 0;
    border-radius:999px;
    background:linear-gradient(90deg,#f0c75e,#fff3c4 48%,#86efac);
    box-shadow:0 0 14px rgba(240,199,94,.38);
  }
  .app-shell .home-growth-milestones{
    display:grid;
    grid-template-columns:repeat(5,minmax(0,1fr));
    gap:6px;
    margin-bottom:11px;
  }
  .app-shell .home-growth-dot{
    min-width:0;
    border-radius:16px;
    padding:8px 4px 7px;
    text-align:center;
    background:rgba(255,253,246,.52);
    border:1px solid rgba(184,147,75,.13);
    opacity:.58;
  }
  .app-shell .home-growth-dot.active{
    opacity:1;
    background:rgba(255,253,246,.78);
    border-color:rgba(201,144,18,.22);
  }
  .app-shell .home-growth-dot.current{
    background:linear-gradient(145deg,rgba(255,255,255,.96),rgba(255,243,196,.72));
    box-shadow:0 7px 18px rgba(138,90,8,.10);
  }
  .app-shell .home-growth-dot span{
    display:flex;
    align-items:center;
    justify-content:center;
    width:28px;
    height:28px;
    margin:0 auto 5px;
    border-radius:50%;
    background:radial-gradient(circle at 32% 24%,#fff,#fff8df 50%,rgba(240,199,94,.42));
    border:1px solid rgba(201,144,18,.18);
    font-size:15px;
  }
  .app-shell .home-growth-dot small{
    display:block;
    max-width:100%;
    overflow:hidden;
    text-overflow:ellipsis;
    white-space:nowrap;
    color:#735014!important;
    font-size:10px!important;
    line-height:1.05!important;
    font-weight:850!important;
  }
  .app-shell .home-growth-next-line{
    margin:0!important;
    padding:9px 11px;
    border-radius:15px;
    background:rgba(255,253,246,.58);
    border:1px solid rgba(201,144,18,.14);
    color:#5B3A08!important;
    font-size:13px!important;
    line-height:1.45!important;
    font-weight:750!important;
    text-align:center;
  }
  @media(max-width:520px){
    .app-shell .home-growth-strip{padding:13px!important;border-radius:22px!important;}
    .app-shell .home-growth-strip-head{gap:8px;margin-bottom:10px;}
    .app-shell .home-growth-strip-head h3{font-size:16px!important;}
    .app-shell .home-growth-strip-head p{font-size:12.5px!important;}
    .app-shell .home-growth-stage-pill{min-width:42px;min-height:30px;font-size:11px!important;}
    .app-shell .home-growth-milestones{gap:4px;}
    .app-shell .home-growth-dot{padding:7px 2px 6px;border-radius:13px;}
    .app-shell .home-growth-dot span{width:24px;height:24px;font-size:13px;margin-bottom:4px;}
    .app-shell .home-growth-dot small{font-size:8.8px!important;}
    .app-shell .home-growth-next-line{font-size:12.5px!important;}
  }


  /* Desktop spacing pass: keeps the mobile rhythm, with more room to breathe */
  @media(min-width:900px){
    .app-shell{
      padding-bottom:116px!important;
      background:
        radial-gradient(circle at 50% -10%,rgba(255,248,223,.58),transparent 34%),
        #FFFDF8!important;
    }
    .app-shell .app-main-frame{
      width:min(100%,820px)!important;
      max-width:820px!important;
      padding-left:22px!important;
      padding-right:22px!important;
      box-sizing:border-box!important;
    }
    .app-shell .app-header{
      max-width:760px!important;
      margin:0 auto!important;
      padding:30px 4px 20px!important;
    }
    .app-shell .app-content{
      max-width:760px!important;
      margin:0 auto!important;
      padding:8px 0 0!important;
    }
    .app-shell .today-page-calm,
    .app-shell .growth-page-calm,
    .app-shell .home-page,
    .app-shell .prayer-page-calm,
    .app-shell .testimony-garden-page,
    .app-shell .premium-journal-page,
    .app-shell .spirit-friends-dashboard{
      width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      padding-left:0!important;
      padding-right:0!important;
      gap:16px!important;
      row-gap:22px!important;
    }
    .app-shell .today-page-calm > .card,
    .app-shell .growth-page-calm > .card,
    .app-shell .home-page > .card,
    .app-shell .prayer-page-calm > .card,
    .app-shell .testimony-garden-page > .card,
    .app-shell .premium-journal-page > .card,
    .app-shell .spirit-friends-dashboard > .card{
      width:100%!important;
      margin-left:0!important;
      margin-right:0!important;
      border-radius:26px!important;
    }
    .app-shell .today-faith-walk-card,
    .app-shell .current-stage-card,
    .app-shell .home-care-card,
    .app-shell .home-growth-strip,
    .app-shell .journal-write-card,
    .app-shell .journal-history-card,
    .app-shell .testimony-shared-garden,
    .app-shell .share-testimony-card,
    .app-shell .prayer-vessels-section{
      padding:22px!important;
    }
    .app-shell .prayer-vessel-hero,
    .app-shell .testimony-featured-garden,
    .app-shell .home-preview-card,
    .app-shell .today-hero-card{
      border-radius:30px!important;
    }
    .app-shell .home-preview-card .home-room,
    .app-shell .sticky-preview-card .home-room{
      min-height:440px!important;
    }
    .app-shell .home-shop-items-grid{
      gap:14px!important;
    }
    .app-shell .home-shop-card,
    .app-shell .shop-card,
    .app-shell .testimony-entry,
    .app-shell .journal-entry-card{
      border-radius:20px!important;
    }
    .app-shell .bottom-tabs{
      width:min(560px,calc(100vw - 54px))!important;
      bottom:18px!important;
    }
  }
  @media(min-width:1200px){
    .app-shell .app-main-frame{
      width:min(100%,880px)!important;
      max-width:880px!important;
    }
    .app-shell .app-content,
    .app-shell .app-header{
      max-width:800px!important;
    }
  }


  /* Friend list name-chip readability pass */
  .app-shell .friends-garden-panel .scroll-list.compact{
    grid-template-columns:repeat(auto-fit,minmax(250px,1fr))!important;
    gap:11px!important;
  }
  .app-shell .friends-garden-panel .compact-friend-row{
    display:flex!important;
    align-items:flex-start!important;
    flex-wrap:wrap!important;
    gap:10px!important;
    min-height:auto!important;
    padding:12px!important;
  }
  .app-shell .friends-garden-panel .compact-friend-identity{
    flex:1 1 100%!important;
    width:100%!important;
    min-width:0!important;
    align-items:flex-start!important;
  }
  .app-shell .friends-garden-panel .compact-friend-identity .friend-spirit-avatar{
    flex:0 0 38px!important;
    margin-top:1px!important;
  }
  .app-shell .friends-garden-panel .compact-friend-identity .prayer-friend-name,
  .app-shell .friend-checkbox-row span{
    white-space:normal!important;
    overflow:visible!important;
    text-overflow:clip!important;
    overflow-wrap:anywhere!important;
    word-break:normal!important;
    line-height:1.25!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions{
    width:100%!important;
    justify-content:flex-start!important;
    flex-wrap:wrap!important;
    padding-left:45px!important;
    gap:7px!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions .soft-action-btn{
    flex:0 1 auto!important;
    max-width:100%!important;
  }
  .app-shell .friend-checkbox-list{
    grid-template-columns:repeat(auto-fit,minmax(190px,1fr))!important;
  }
  .app-shell .friend-checkbox-row{
    align-items:flex-start!important;
    min-height:42px!important;
    line-height:1.25!important;
  }
  @media(max-width:560px){
    .app-shell .friends-garden-panel .scroll-list.compact,
    .app-shell .friend-checkbox-list{
      grid-template-columns:1fr!important;
    }
    .app-shell .friends-garden-panel .compact-friend-actions{
      padding-left:45px!important;
    }
  }


  /* Simple Home page: preview, blessing, decorate */
  .app-shell .simple-home-page{
    gap:14px!important;
  }
  .app-shell .simple-home-preview{
    order:1!important;
  }
  .app-shell .simple-home-blessing{
    order:2!important;
  }
  .app-shell .simple-home-decorate{
    order:3!important;
  }
  .app-shell .simple-home-decorate-toggle{
    width:100%;
    border:0;
    background:transparent;
    padding:0;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:12px;
    text-align:left;
    cursor:pointer;
    font-family:'Nunito',sans-serif;
    color:var(--text-dark);
  }
  .app-shell .simple-home-decorate-toggle h3{
    margin:0 0 3px!important;
    color:var(--text-dark)!important;
    font-size:18px!important;
    line-height:1.18!important;
  }
  .app-shell .simple-home-decorate-toggle p{
    margin:0!important;
    color:var(--text-soft)!important;
    font-size:13px!important;
    line-height:1.35!important;
    font-weight:700!important;
  }
  .app-shell .simple-home-decorate-toggle > span{
    width:34px;
    height:34px;
    border-radius:50%;
    display:flex;
    align-items:center;
    justify-content:center;
    flex:0 0 34px;
    background:rgba(255,248,223,.82);
    border:1px solid rgba(184,147,75,.22);
    color:var(--gold-deep);
    font-weight:900;
  }
  .app-shell .simple-home-decorate-body{
    margin-top:14px;
    display:flex;
    flex-direction:column;
    gap:14px;
  }


  /* Home neglect warning below the page banner */
  .app-shell .home-neglect-top-warning{
    order:0!important;
    margin:0!important;
    padding:10px 13px!important;
    border-radius:18px!important;
    background:linear-gradient(135deg,rgba(255,248,223,.88),rgba(255,238,224,.72))!important;
    border:1px solid rgba(150,50,50,.20)!important;
    box-shadow:0 5px 16px rgba(91,67,28,.045)!important;
  }
  .app-shell .home-neglect-top-warning p{
    margin:0!important;
    color:#8b3030!important;
    font-size:13px!important;
    line-height:1.45!important;
    font-weight:800!important;
    text-align:center!important;
  }


  /* Prayer Circle light feedback and calmer cards */
  .app-shell .friend-light-message{
    position:fixed;
    left:50%;
    bottom:86px;
    transform:translateX(-50%);
    z-index:9997;
    width:min(86vw,360px);
    padding:11px 15px;
    border-radius:999px;
    text-align:center;
    background:linear-gradient(135deg,rgba(255,253,246,.96),rgba(255,248,223,.88));
    border:1px solid rgba(201,144,18,.22);
    color:var(--gold-deep);
    font-size:13px;
    line-height:1.35;
    font-weight:850;
    box-shadow:0 10px 26px rgba(91,67,28,.10);
    animation:toastPop .24s ease-out;
    pointer-events:none;
  }
  .app-shell .spirit-friends-dashboard > .sacred-card,
  .app-shell .spirit-friends-dashboard .prayer-friend-panel,
  .app-shell .spirit-friends-dashboard .friend-card{
    box-shadow:0 7px 18px rgba(91,67,28,.035),inset 0 1px 0 rgba(255,255,255,.72)!important;
    border-color:rgba(184,147,75,.16)!important;
  }
  .app-shell .spirit-friends-dashboard .prayer-friends-card{
    box-shadow:0 10px 24px rgba(91,67,28,.055)!important;
    border-color:rgba(201,144,18,.26)!important;
  }
  .app-shell .spirit-friends-dashboard .prayer-friend-row{
    box-shadow:0 4px 12px rgba(91,67,28,.025),inset 0 1px 0 rgba(255,255,255,.72)!important;
    border-color:rgba(184,147,75,.14)!important;
    background:rgba(255,255,255,.56)!important;
  }
  .app-shell .spirit-friends-dashboard .compact-friend-row{
    padding:13px!important;
  }
  .app-shell .spirit-friends-dashboard .soft-action-btn{
    box-shadow:none!important;
  }
  .app-shell .spirit-friends-dashboard .soft-action-btn.friend-alert-btn{
    box-shadow:0 5px 14px rgba(201,144,18,.10)!important;
  }
  .app-shell .spirit-friends-dashboard .placed-chip,
  .app-shell .spirit-friends-dashboard .friend-activity-pill{
    box-shadow:none!important;
    border-color:rgba(184,147,75,.16)!important;
  }


  /* Gold restraint pass: gold stays sacred, supporting UI gets softer color */
  .app-shell .card:not(.gold-outline-card):not(.prayer-friends-card):not(.current-stage-card):not(.today-hero-card),
  .app-shell .sacred-card:not(.prayer-friends-card),
  .app-shell .home-shop-shell-card,
  .app-shell .journal-history-card,
  .app-shell .testimony-shared-garden,
  .app-shell .share-testimony-card{
    border-color:rgba(184,147,75,.14)!important;
    box-shadow:0 6px 18px rgba(91,67,28,.035),inset 0 1px 0 rgba(255,255,255,.72)!important;
  }
  .app-shell .placed-chip:not(.active):not(.home-growth-stage-pill),
  .app-shell .meaning-chip,
  .app-shell .prayer-status-chip,
  .app-shell .sticky-preview-label{
    background:rgba(255,253,246,.72)!important;
    border-color:rgba(184,147,75,.14)!important;
    color:#735014!important;
    box-shadow:none!important;
  }
  .app-shell .prayer-status-chip{
    background:rgba(246,250,255,.78)!important;
    border-color:rgba(125,174,212,.18)!important;
    color:#557895!important;
  }
  .app-shell .friend-card.prayed,
  .app-shell .pray-now-btn.prayed,
  .app-shell .soft-action-btn.friend-alert-btn{
    background:linear-gradient(135deg,rgba(246,250,255,.96),rgba(232,245,239,.80))!important;
    border-color:rgba(125,174,212,.24)!important;
    color:#315b78!important;
    box-shadow:0 5px 14px rgba(65,95,125,.06)!important;
  }
  .app-shell .soft-action-btn:not(.friend-alert-btn),
  .app-shell .home-category-card:not(.active),
  .app-shell .shop-category-card:not(.active),
  .app-shell .home-shop-card:not(.equipped):not(.owned),
  .app-shell .shop-card:not(.owned){
    background:linear-gradient(145deg,rgba(255,253,246,.82),rgba(246,250,255,.34))!important;
    border-color:rgba(184,147,75,.13)!important;
    box-shadow:0 4px 12px rgba(91,67,28,.025)!important;
  }
  .app-shell .home-category-card.active,
  .app-shell .shop-category-card.active,
  .app-shell .home-shop-card.equipped,
  .app-shell .shop-card.owned,
  .app-shell .tab-btn.active{
    border-color:rgba(201,144,18,.48)!important;
  }
  .app-shell .unlock-note,
  .app-shell .empty-state{
    background:linear-gradient(155deg,rgba(255,253,246,.90),rgba(236,253,245,.34),rgba(246,250,255,.38))!important;
    border-color:rgba(184,147,75,.13)!important;
  }
  .app-shell .home-neglect-top-warning{
    background:linear-gradient(135deg,rgba(255,253,246,.88),rgba(255,241,242,.62))!important;
    border-color:rgba(190,80,80,.18)!important;
  }
  .app-shell .gold-btn,
  .app-shell .pray-now-btn:not(.prayed),
  .app-shell .tab-btn.active{
    box-shadow:0 6px 18px rgba(201,144,18,.18)!important;
  }


  /* Final Home neglect warning glow: stronger alert outline. */
  .app-shell .home-neglect-top-warning{
    position:relative!important;
    overflow:hidden!important;
    padding:13px 16px!important;
    border-radius:21px!important;
    background:
      radial-gradient(circle at 14% 28%,rgba(255,255,255,.86),transparent 28%),
      linear-gradient(135deg,rgba(255,253,246,.98),rgba(255,239,230,.86),rgba(255,248,223,.96))!important;
    border:2.5px solid rgba(172,62,62,.62)!important;
    outline:2px solid rgba(255,225,156,.74)!important;
    outline-offset:3px!important;
    box-shadow:
      0 0 0 5px rgba(255,243,196,.30),
      0 0 22px rgba(201,94,61,.22),
      0 12px 28px rgba(91,49,28,.10),
      inset 0 1px 0 rgba(255,255,255,.92)!important;
  }
  .app-shell .home-neglect-top-warning::before{
    content:""!important;
    position:absolute!important;
    inset:2px!important;
    border-radius:18px!important;
    border:1px solid rgba(255,255,255,.72)!important;
    pointer-events:none!important;
  }
  .app-shell .home-neglect-top-warning p{
    position:relative!important;
    z-index:1!important;
    color:#7E2525!important;
    font-size:13.5px!important;
    line-height:1.45!important;
    font-weight:700!important;
    text-align:center!important;
  }
  .app-shell .home-room .home-neglect-message{
    border:2px solid rgba(255,225,156,.78)!important;
    outline:1.5px solid rgba(172,62,62,.42)!important;
    outline-offset:2px!important;
    box-shadow:
      0 0 0 4px rgba(255,243,196,.18),
      0 0 24px rgba(255,225,156,.36),
      0 10px 26px rgba(0,0,0,.22)!important;
  }
  .app-shell .home-room .home-neglect-message p{
    color:rgba(255,242,190,.98)!important;
    font-weight:700!important;
  }
  @media(max-width:520px){
    .app-shell .home-neglect-top-warning{
      padding:12px 14px!important;
      border-width:2px!important;
      outline-width:1.5px!important;
      outline-offset:2px!important;
      box-shadow:0 0 0 4px rgba(255,243,196,.24),0 0 18px rgba(201,94,61,.20),0 10px 22px rgba(91,49,28,.08),inset 0 1px 0 rgba(255,255,255,.92)!important;
    }
    .app-shell .home-neglect-top-warning p{font-size:13px!important;}
  }

  /* Button/chip calm pass: one primary action per card */
  .app-shell .prayer-card-action-row{
    grid-template-columns:minmax(0,1fr) auto auto auto!important;
    gap:6px!important;
  }
  .app-shell .prayer-card-action-row .pray-now-btn{
    min-width:0!important;
  }
  .app-shell .prayer-secondary-action{
    min-height:34px!important;
    padding:7px 8px!important;
    border:0!important;
    background:rgba(255,253,246,.48)!important;
    color:#9A6B16!important;
    font-size:11.5px!important;
    font-weight:750!important;
    box-shadow:none!important;
  }
  .app-shell .prayer-secondary-action.danger{
    color:rgba(139,48,48,.62)!important;
  }
  .app-shell .partner-inline-btn{
    display:none!important;
  }
  .app-shell .remove-friend-btn{
    width:31px!important;
    min-width:31px!important;
    min-height:31px!important;
    padding:0!important;
    border-radius:50%!important;
    font-size:17px!important;
    line-height:1!important;
    color:rgba(115,80,20,.42)!important;
    background:rgba(255,253,246,.36)!important;
    border-color:rgba(184,147,75,.10)!important;
    box-shadow:none!important;
  }
  .app-shell .compact-friend-actions{
    gap:6px!important;
  }
  .app-shell .placed-chip:not(button),
  .app-shell .meaning-chip,
  .app-shell .prayer-status-chip,
  .app-shell .friend-activity-pill{
    pointer-events:none;
  }
  .app-shell button.placed-chip{
    min-height:34px!important;
    font-weight:800!important;
  }
  @media(max-width:520px){
    .app-shell .prayer-card-action-row{
      grid-template-columns:1fr auto auto!important;
    }
    .app-shell .prayer-secondary-action{
      padding:7px 7px!important;
      font-size:11px!important;
    }
  }


  /* Home decorate category width pass */
  .app-shell .home-page.simple-home-page .home-shop-category-row{
    gap:10px!important;
    padding:4px 2px 14px!important;
    scroll-padding-inline:2px!important;
  }
  .app-shell .home-page.simple-home-page .home-category-card{
    min-width:138px!important;
    flex:0 0 138px!important;
    max-width:none!important;
    min-height:116px!important;
    padding:12px 12px!important;
    border-radius:18px!important;
  }
  .app-shell .home-page.simple-home-page .home-category-card > div[style*="font-size: 12"]{
    font-size:13px!important;
    line-height:1.2!important;
    max-width:100%!important;
    white-space:normal!important;
    overflow-wrap:normal!important;
    word-break:normal!important;
  }
  .app-shell .home-page.simple-home-page .home-category-card > div[style*="font-size: 10"]{
    line-height:1.25!important;
    white-space:normal!important;
  }
  @media(min-width:760px){
    .app-shell .home-page.simple-home-page .home-shop-category-row{
      display:grid!important;
      grid-template-columns:repeat(4,minmax(140px,1fr))!important;
      overflow:visible!important;
    }
    .app-shell .home-page.simple-home-page .home-category-card{
      width:auto!important;
      min-width:0!important;
      flex:auto!important;
    }
  }
  @media(max-width:420px){
    .app-shell .home-page.simple-home-page .home-category-card{
      min-width:132px!important;
      flex-basis:132px!important;
      padding:11px 10px!important;
    }
  }


  /* Final frame and border tightening pass */
  .app-shell .card,
  .app-shell .sacred-card,
  .app-shell .focus-card,
  .app-shell .soft-guide-card,
  .app-shell .current-stage-card,
  .app-shell .prayer-vessels-section,
  .app-shell .today-hero-card,
  .app-shell .today-faith-walk-card,
  .app-shell .growth-summary-card,
  .app-shell .growth-stage-hero,
  .app-shell .growth-account-card,
  .app-shell .growth-hero-card,
  .app-shell .growth-closet-card,
  .app-shell .growth-stones-card,
  .app-shell .growth-book-card,
  .app-shell .home-care-card,
  .app-shell .home-preview-card,
  .app-shell .home-shop-shell-card,
  .app-shell .journal-write-card,
  .app-shell .journal-history-card,
  .app-shell .testimony-garden-hero,
  .app-shell .featured-testimony-card,
  .app-shell .share-testimony-card,
  .app-shell .testimony-shared-garden,
  .app-shell .prayer-friends-card,
  .app-shell .spirit-friends-tools-card,
  .app-shell .community-feed-card,
  .app-shell .accountability-primary-card,
  .app-shell .blessing-community-card{
    border-width:1px!important;
    border-color:rgba(184,147,75,.13)!important;
    box-shadow:0 5px 16px rgba(91,67,28,.032),inset 0 1px 0 rgba(255,255,255,.68)!important;
  }
  .app-shell .gold-outline-card,
  .app-shell .prayer-friends-card,
  .app-shell .public-share-box{
    border-width:1.25px!important;
    border-color:rgba(201,144,18,.34)!important;
  }
  @media(min-width:900px){
    .app-shell .app-main-frame{
      width:min(100%,760px)!important;
      max-width:760px!important;
      padding-left:18px!important;
      padding-right:18px!important;
    }
    .app-shell .app-content,
    .app-shell .app-header{
      max-width:700px!important;
    }
    .app-shell .today-page-calm,
    .app-shell .growth-page-calm,
    .app-shell .home-page,
    .app-shell .prayer-page-calm,
    .app-shell .testimony-garden-page,
    .app-shell .premium-journal-page,
    .app-shell .spirit-friends-dashboard{
      gap:18px!important;
      row-gap:18px!important;
    }
    .app-shell .today-page-calm > .card,
    .app-shell .growth-page-calm > .card,
    .app-shell .home-page > .card,
    .app-shell .prayer-page-calm > .card,
    .app-shell .testimony-garden-page > .card,
    .app-shell .premium-journal-page > .card,
    .app-shell .spirit-friends-dashboard > .card{
      border-radius:22px!important;
    }
    .app-shell .prayer-vessel-hero,
    .app-shell .testimony-featured-garden,
    .app-shell .home-preview-card,
    .app-shell .today-hero-card{
      border-radius:24px!important;
    }
    .app-shell .home-preview-card .home-room,
    .app-shell .sticky-preview-card .home-room{
      min-height:400px!important;
    }
  }
  @media(min-width:1200px){
    .app-shell .app-main-frame{
      width:min(100%,780px)!important;
      max-width:780px!important;
    }
    .app-shell .app-content,
    .app-shell .app-header{
      max-width:720px!important;
    }
  }
  @media(max-width:520px){
    .app-shell .card,
    .app-shell .sacred-card,
    .app-shell .focus-card,
    .app-shell .soft-guide-card{
      border-color:rgba(184,147,75,.12)!important;
      box-shadow:0 3px 12px rgba(91,67,28,.03)!important;
    }
  }


  /* Final lightweight text pass: remove chunky typography */
  .app-shell h1,
  .app-shell .page-title,
  .app-shell .today-title,
  .app-shell .home-section-title,
  .app-shell .growth-page-calm h1,
  .app-shell .testimony-garden-page h1,
  .app-shell .prayer-page-calm h1,
  .app-shell .premium-journal-page h1{
    font-weight:560!important;
    letter-spacing:0!important;
  }
  .app-shell h2,
  .app-shell .card h2,
  .app-shell .section-title,
  .app-shell .today-page-calm h2,
  .app-shell .growth-page-calm h2,
  .app-shell .home-page h2,
  .app-shell .prayer-page-calm h2,
  .app-shell .testimony-garden-page h2,
  .app-shell .premium-journal-page h2,
  .app-shell .spirit-friends-dashboard h2{
    font-weight:560!important;
    letter-spacing:0!important;
  }
  .app-shell h3,
  .app-shell .card h3,
  .app-shell .home-care-card h3,
  .app-shell .friend-card h3,
  .app-shell .testimony-entry h3,
  .app-shell .journal-entry-card h3,
  .app-shell .growth-path-name,
  .app-shell .prayer-vessel-title,
  .app-shell .answered-altar-header h3{
    font-weight:650!important;
    letter-spacing:0!important;
  }
  .app-shell .gold-btn,
  .app-shell .soft-action-btn,
  .app-shell .pray-now-btn,
  .app-shell .quest-nav-btn,
  .app-shell .home-bless-btn,
  .app-shell .home-care-action,
  .app-shell .sam-btn,
  .app-shell .shop-card button,
  .app-shell .home-shop-card button,
  .app-shell .prayer-filter-btn,
  .app-shell .next-action-btn{
    font-weight:650!important;
    letter-spacing:0!important;
  }
  .app-shell .placed-chip,
  .app-shell .meaning-chip,
  .app-shell .prayer-status-chip,
  .app-shell .sticky-preview-label,
  .app-shell .friend-activity-pill,
  .app-shell .testimony-label,
  .app-shell .section-divider-label,
  .app-shell .stage-reveal-kicker,
  .app-shell .stone-reward-title,
  .app-shell .prayer-friend-panel-title,
  .app-shell .partner-focus-label,
  .app-shell .public-preview-label{
    font-weight:600!important;
    letter-spacing:.025em!important;
  }
  .app-shell .prayer-friend-name,
  .app-shell .friend-notification-title,
  .app-shell .friend-notification-time,
  .app-shell .accountability-update-title,
  .app-shell .public-share-toggle-title,
  .app-shell .public-share-title,
  .app-shell .testimony-gem-name,
  .app-shell .growth-stat-value,
  .app-shell .empty-state-title{
    font-weight:680!important;
    letter-spacing:0!important;
  }
  .app-shell .tab-btn.active .tab-label,
  .app-shell .tab-label{
    font-weight:620!important;
    letter-spacing:0!important;
  }
  .app-shell b,
  .app-shell strong{
    font-weight:680!important;
  }
  .app-shell [style*="font-weight: 900"],
  .app-shell [style*="fontWeight: 900"],
  .app-shell [style*="font-weight:900"],
  .app-shell [style*="fontWeight:900"]{
    font-weight:680!important;
  }
  .app-shell [style*="font-weight: 800"],
  .app-shell [style*="fontWeight: 800"],
  .app-shell [style*="font-weight:800"],
  .app-shell [style*="fontWeight:800"]{
    font-weight:650!important;
  }
  .app-shell p,
  .app-shell .card p,
  .app-shell .home-page p,
  .app-shell .prayer-page-calm p,
  .app-shell .testimony-garden-page p,
  .app-shell .premium-journal-page p,
  .app-shell .growth-page-calm p,
  .app-shell .spirit-friends-dashboard p{
    font-weight:400!important;
  }


  /* Home decorate readability pass: uncramp item cards */
  .app-shell .home-page.simple-home-page .simple-home-decorate-body{
    gap:16px!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-section-header{
    align-items:flex-start!important;
    margin:8px 2px 14px!important;
    padding:0 2px 12px!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-section-header h3{
    font-size:24px!important;
    line-height:1.05!important;
    margin-top:5px!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-eyebrow{
    font-size:11px!important;
    letter-spacing:.13em!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-collected{
    font-size:13px!important;
    line-height:1.35!important;
    white-space:normal!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-items-grid{
    gap:12px!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-card{
    padding:14px 13px 13px!important;
    border-radius:20px!important;
    min-height:230px!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-card .home-shop-preview{
    min-height:70px!important;
    margin-bottom:8px!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-card .meaning-chip{
    display:inline-flex!important;
    font-size:9px!important;
    letter-spacing:.07em!important;
    padding:2px 5px!important;
    background:rgba(246,250,255,.72)!important;
    border:1px solid rgba(125,174,212,.18)!important;
    border-radius:4px!important;
    color:#557895!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-card > div[style*="font-size: 13.5"]{
    font-size:14px!important;
    line-height:1.25!important;
    margin:4px auto 7px!important;
    max-width:13ch!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-price{
    font-size:12.5px!important;
    line-height:1.2!important;
    margin:0 0 10px!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-card > div[style*="display: flex"]{
    gap:8px!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-card button{
    min-height:38px!important;
    font-size:13px!important;
    line-height:1.1!important;
    padding:8px 10px!important;
    white-space:nowrap!important;
  }
  .app-shell .home-page.simple-home-page .home-shop-card button[style*="margin-top"]{
    min-height:36px!important;
    margin-top:9px!important;
    text-decoration:none!important;
    border-radius:12px!important;
    background:rgba(255,248,223,.72)!important;
  }
  @media(max-width:720px){
    .app-shell .home-page.simple-home-page .home-shop-items-grid{
      grid-template-columns:1fr!important;
      gap:12px!important;
    }
    .app-shell .home-page.simple-home-page .home-shop-card{
      min-height:auto!important;
      padding:15px 16px 14px!important;
      display:grid!important;
      grid-template-columns:88px minmax(0,1fr)!important;
      column-gap:14px!important;
      row-gap:7px!important;
      align-items:center!important;
    }
    .app-shell .home-page.simple-home-page .home-shop-card .home-shop-preview{
      grid-row:1 / span 4!important;
      min-height:86px!important;
      margin:0!important;
      align-self:center!important;
    }
    .app-shell .home-page.simple-home-page .home-shop-card .home-shop-preview .home-game-item{
      transform:scale(.92)!important;
    }
    .app-shell .home-page.simple-home-page .home-shop-card > div[style*="text-align: center"]{
      text-align:left!important;
      margin-bottom:0!important;
    }
    .app-shell .home-page.simple-home-page .home-shop-card > div[style*="font-size: 13.5"]{
      text-align:left!important;
      max-width:none!important;
      font-size:15px!important;
      margin:0!important;
    }
    .app-shell .home-page.simple-home-page .home-shop-price{
      text-align:left!important;
      margin:0!important;
    }
    .app-shell .home-page.simple-home-page .home-shop-card > div[style*="display: flex"]{
      grid-column:1 / -1!important;
      margin-top:8px!important;
      display:grid!important;
      grid-template-columns:1fr 1fr!important;
      gap:9px!important;
    }
    .app-shell .home-page.simple-home-page .home-shop-card button[style*="margin-top"]{
      grid-column:1 / -1!important;
    }
  }
  @media(max-width:390px){
    .app-shell .home-page.simple-home-page .home-shop-card{
      grid-template-columns:78px minmax(0,1fr)!important;
      column-gap:12px!important;
      padding:14px 13px!important;
    }
    .app-shell .home-page.simple-home-page .home-shop-card button{
      font-size:12.5px!important;
      padding:8px 7px!important;
    }
  }


  /* Page banner blend pass: make headers feel native to each page */
  .app-shell .app-header{
    background:transparent!important;
    padding-bottom:14px!important;
  }
  .app-shell .today-hero-card,
  .app-shell .growth-stage-hero,
  .app-shell .growth-hero-card,
  .app-shell .home-care-card,
  .app-shell .journal-write-card:first-child,
  .app-shell .prayer-vessels-section,
  .app-shell .prayer-vessel-hero,
  .app-shell .testimony-garden-hero,
  .app-shell .spirit-friends-dashboard > .card:first-child,
  .app-shell .spirit-friends-hero-card{
    background:linear-gradient(145deg,rgba(255,253,246,.94),rgba(246,250,255,.38) 58%,rgba(236,253,245,.24))!important;
    border:1px solid rgba(184,147,75,.13)!important;
    box-shadow:0 4px 16px rgba(91,67,28,.035),inset 0 1px 0 rgba(255,255,255,.68)!important;
    border-radius:22px!important;
    overflow:hidden!important;
  }
  .app-shell .today-hero-card::before,
  .app-shell .growth-stage-hero::before,
  .app-shell .growth-hero-card::before,
  .app-shell .home-care-card::before,
  .app-shell .journal-write-card:first-child::before,
  .app-shell .prayer-vessels-section::before,
  .app-shell .prayer-vessel-hero::before,
  .app-shell .testimony-garden-hero::before,
  .app-shell .spirit-friends-dashboard > .card:first-child::before,
  .app-shell .spirit-friends-hero-card::before,
  .app-shell .today-hero-card::after,
  .app-shell .spirit-friends-hero-card::after{
    opacity:.22!important;
    filter:none!important;
  }
  .app-shell .today-hero-card,
  .app-shell .growth-stage-hero,
  .app-shell .growth-hero-card,
  .app-shell .home-care-card,
  .app-shell .journal-write-card:first-child,
  .app-shell .testimony-garden-hero,
  .app-shell .spirit-friends-dashboard > .card:first-child,
  .app-shell .spirit-friends-hero-card{
    padding:16px!important;
  }
  .app-shell .prayer-vessels-section,
  .app-shell .prayer-vessel-hero{
    padding:16px!important;
  }
  .app-shell .today-hero-card h1,
  .app-shell .today-hero-card h2,
  .app-shell .growth-stage-hero h1,
  .app-shell .growth-stage-hero h2,
  .app-shell .growth-hero-card h1,
  .app-shell .growth-hero-card h2,
  .app-shell .home-care-card h1,
  .app-shell .home-care-card h2,
  .app-shell .journal-write-card:first-child h1,
  .app-shell .journal-write-card:first-child h2,
  .app-shell .prayer-vessels-section h1,
  .app-shell .prayer-vessels-section h2,
  .app-shell .prayer-vessel-hero h1,
  .app-shell .prayer-vessel-hero h2,
  .app-shell .testimony-garden-hero h1,
  .app-shell .testimony-garden-hero h2,
  .app-shell .spirit-friends-dashboard > .card:first-child h1,
  .app-shell .spirit-friends-dashboard > .card:first-child h2,
  .app-shell .spirit-friends-hero-card h1,
  .app-shell .spirit-friends-hero-card h2{
    color:#3A2812!important;
    font-weight:560!important;
    letter-spacing:0!important;
  }
  .app-shell .today-hero-card p,
  .app-shell .growth-stage-hero p,
  .app-shell .growth-hero-card p,
  .app-shell .home-care-card p,
  .app-shell .journal-write-card:first-child p,
  .app-shell .prayer-vessels-section p,
  .app-shell .prayer-vessel-hero p,
  .app-shell .testimony-garden-hero p,
  .app-shell .spirit-friends-dashboard > .card:first-child p,
  .app-shell .spirit-friends-hero-card p{
    color:#5F4A2E!important;
    line-height:1.45!important;
  }
  .app-shell .prayer-vessel-hero-copy p{
    white-space:normal!important;
    max-width:32ch!important;
  }
  .app-shell .prayer-vessel-kicker,
  .app-shell .stage-reveal-kicker,
  .app-shell .section-label,
  .app-shell .section-divider-label{
    color:#8B7B69!important;
  }
  .app-shell .today-page-calm,
  .app-shell .growth-page-calm,
  .app-shell .home-page,
  .app-shell .prayer-page-calm,
  .app-shell .testimony-garden-page,
  .app-shell .premium-journal-page,
  .app-shell .spirit-friends-dashboard{
    gap:14px!important;
    row-gap:14px!important;
  }
  @media(max-width:520px){
    .app-shell .today-hero-card,
    .app-shell .growth-stage-hero,
    .app-shell .growth-hero-card,
    .app-shell .home-care-card,
    .app-shell .journal-write-card:first-child,
    .app-shell .prayer-vessels-section,
    .app-shell .prayer-vessel-hero,
    .app-shell .testimony-garden-hero,
    .app-shell .spirit-friends-dashboard > .card:first-child,
    .app-shell .spirit-friends-hero-card{
      padding:14px!important;
      border-radius:20px!important;
    }
    .app-shell .prayer-vessel-hero{
      gap:12px!important;
    }
    .app-shell .prayer-vessel-hero-copy p{
      max-width:100%!important;
    }
  }



  /* Spirit Garden Home signature identity pass. */
  .home-page .home-stage-signature-copy{
    margin:7px 0 0!important;
    max-width:360px!important;
    font-size:12.5px!important;
    line-height:1.5!important;
    color:#7b633d!important;
  }
  .home-room .home-signature-vessel{
    position:absolute;
    left:50%;
    bottom:18%;
    width:48px;
    height:48px;
    transform:translateX(-50%);
    z-index:8;
    pointer-events:none;
    opacity:.86;
    filter:drop-shadow(0 8px 12px rgba(82,55,15,.10)) drop-shadow(0 0 14px rgba(255,248,223,.44));
  }
  .home-room .home-signature-vessel .vessel-bowl{
    position:absolute;
    left:8px;
    right:8px;
    bottom:7px;
    height:25px;
    border-radius:9px 9px 18px 18px;
    background:
      radial-gradient(circle at 32% 20%,rgba(255,255,255,.94) 0 4px,transparent 5px),
      linear-gradient(180deg,#fffaf0 0%,#f7dd91 52%,#c99012 100%);
    box-shadow:inset 0 5px 8px rgba(255,255,255,.62),inset 0 -6px 8px rgba(110,73,10,.10);
  }
  .home-room .home-signature-vessel .vessel-light{
    position:absolute;
    left:50%;
    top:0;
    width:30px;
    height:30px;
    border-radius:999px;
    transform:translateX(-50%);
    background:radial-gradient(circle,#fff 0 18%,#fff8df 38%,rgba(240,203,118,.42) 68%,transparent 76%);
    animation:homeVesselGlow 4.8s ease-in-out infinite;
  }
  .home-room .home-signature-vessel .vessel-cross{
    position:absolute;
    left:50%;
    top:10px;
    width:10px;
    height:14px;
    transform:translateX(-50%);
  }
  .home-room .home-signature-vessel .vessel-cross::before,
  .home-room .home-signature-vessel .vessel-cross::after{
    content:"";
    position:absolute;
    background:rgba(138,90,8,.44);
    border-radius:999px;
    box-shadow:0 0 8px rgba(255,248,223,.72);
  }
  .home-room .home-signature-vessel .vessel-cross::before{left:4px;top:0;width:2px;height:14px;}
  .home-room .home-signature-vessel .vessel-cross::after{left:0;top:5px;width:10px;height:2px;}
  .home-room.home-stage-0 .home-signature-vessel{bottom:21%;transform:translateX(-50%) scale(.78);opacity:.72;}
  .home-room.home-stage-1 .home-signature-vessel{bottom:20%;transform:translateX(-50%) scale(.88);}
  .home-room.home-stage-2 .home-signature-vessel{bottom:19%;transform:translateX(-50%) scale(.96);}
  .home-room.home-stage-3 .home-signature-vessel{bottom:18%;transform:translateX(-50%) scale(1);}
  .home-room.home-stage-4 .home-signature-vessel,
  .home-room.home-stage-5 .home-signature-vessel,
  .home-room.home-stage-6 .home-signature-vessel{bottom:17%;transform:translateX(-50%) scale(1.08);}
  .home-room .home-prayer-light-trail{
    position:absolute;
    inset:0;
    z-index:7;
    pointer-events:none;
    opacity:.72;
  }
  .home-room .trail-light{
    position:absolute;
    width:9px;
    height:9px;
    border-radius:999px;
    background:radial-gradient(circle,#fff 0 24%,#fff8df 46%,rgba(240,203,118,.55) 74%,transparent 100%);
    box-shadow:0 0 10px rgba(240,203,118,.34),0 0 18px rgba(255,248,223,.42);
    animation:homePrayerLightFloat 6.5s ease-in-out infinite;
  }
  .home-room .trail-light.l1{left:28%;bottom:28%;animation-delay:.1s;}
  .home-room .trail-light.l2{left:48%;bottom:38%;animation-delay:1.8s;}
  .home-room .trail-light.l3{right:28%;bottom:30%;animation-delay:3.1s;}
  .home-room .home-word-scroll{
    position:absolute;
    right:9%;
    bottom:24%;
    z-index:6;
    min-width:72px;
    padding:5px 9px;
    border-radius:999px;
    background:rgba(255,253,246,.70);
    border:1px solid rgba(230,215,184,.58);
    color:#7b633d;
    font-size:9px;
    font-weight:900;
    letter-spacing:.7px;
    text-transform:uppercase;
    pointer-events:none;
    opacity:.68;
    box-shadow:0 5px 12px rgba(82,55,15,.06);
  }
  .home-room.home-stage-0 .home-word-scroll,
  .home-room.home-stage-1 .home-word-scroll{opacity:.38;transform:scale(.9);right:7%;}
  .home-room.home-stage-4 .home-word-scroll,
  .home-room.home-stage-5 .home-word-scroll,
  .home-room.home-stage-6 .home-word-scroll{opacity:.78;}
  .home-room .home-path{
    box-shadow:inset 0 0 22px rgba(255,255,255,.55),0 0 18px rgba(255,248,223,.20)!important;
  }
  .home-room .home-path::before{
    background:
      radial-gradient(circle at 50% 30%,rgba(255,255,255,.72) 0 2px,transparent 3px),
      radial-gradient(circle at 44% 63%,rgba(186,230,253,.42) 0 2px,transparent 3px),
      radial-gradient(circle at 58% 78%,rgba(187,247,208,.46) 0 2px,transparent 3px)!important;
    opacity:.32!important;
  }
  @keyframes homeVesselGlow{
    0%,100%{opacity:.66;transform:translateX(-50%) scale(.92)}
    50%{opacity:1;transform:translateX(-50%) scale(1.08)}
  }
  @keyframes homePrayerLightFloat{
    0%,100%{opacity:.22;transform:translate3d(0,0,0) scale(.82)}
    45%{opacity:.88;transform:translate3d(12px,-18px,0) scale(1.08)}
    72%{opacity:.46;transform:translate3d(-7px,-29px,0) scale(.94)}
  }
  @media(max-width:560px){
    .home-page .home-stage-signature-copy{font-size:12px!important;max-width:300px!important;}
    .home-room .home-signature-vessel{width:42px;height:42px;bottom:18%;}
    .home-room .home-word-scroll{right:5%;bottom:25%;font-size:8px;padding:4px 7px;}
  }


  /* Header sound/friends light polish. */
  .app-header .top-soft-chip:not(.friends-top-chip) span[aria-hidden="true"]{
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    min-width:18px!important;
    font-size:15px!important;
    line-height:1!important;
  }
  .app-header .friends-top-chip{
    background:transparent!important;
    border:none!important;
    box-shadow:none!important;
    padding:5px 2px 6px!important;
    border-radius:0!important;
    color:#735014!important;
    text-decoration:underline!important;
    text-decoration-color:rgba(138,90,8,.30)!important;
    text-decoration-thickness:2px!important;
    text-underline-offset:5px!important;
  }
  .app-header .friends-top-chip:hover,
  .app-header .friends-top-chip:focus-visible{
    color:#8A5A08!important;
    text-decoration-color:#C99012!important;
    outline:none!important;
  }
  .app-header .friends-top-chip span[aria-hidden="true"]{
    display:none!important;
  }


  /* Final Prayer Circle friend row alignment: no text crossing borders. */
  .app-shell .friends-garden-panel .scroll-list.compact{
    display:flex!important;
    flex-direction:column!important;
    gap:9px!important;
  }
  .app-shell .friends-garden-panel .compact-friend-row,
  .app-shell .spirit-friends-dashboard .compact-friend-row{
    position:relative!important;
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto!important;
    align-items:center!important;
    gap:10px!important;
    padding:10px 12px!important;
    min-height:62px!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
  }
  .app-shell .friends-garden-panel .compact-friend-row::after,
  .app-shell .spirit-friends-dashboard .compact-friend-row::after{
    content:"";
    position:absolute;
    left:58px;
    right:12px;
    bottom:0;
    height:1px;
    background:rgba(230,215,184,.62);
    pointer-events:none;
  }
  .app-shell .friends-garden-panel .compact-friend-row:last-child::after,
  .app-shell .spirit-friends-dashboard .compact-friend-row:last-child::after{
    display:none;
  }
  .app-shell .friends-garden-panel .compact-friend-identity,
  .app-shell .spirit-friends-dashboard .compact-friend-identity{
    display:grid!important;
    grid-template-columns:46px minmax(0,1fr)!important;
    align-items:center!important;
    gap:10px!important;
    min-width:0!important;
    width:auto!important;
  }
  .app-shell .friends-garden-panel .compact-friend-identity .friend-spirit-avatar,
  .app-shell .spirit-friends-dashboard .compact-friend-identity .friend-spirit-avatar{
    width:44px!important;
    height:44px!important;
    margin:0!important;
    flex:none!important;
  }
  .app-shell .friends-garden-panel .compact-friend-identity .prayer-friend-name,
  .app-shell .spirit-friends-dashboard .compact-friend-identity .prayer-friend-name{
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    line-height:1.2!important;
    padding-right:4px!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions,
  .app-shell .spirit-friends-dashboard .compact-friend-actions{
    display:flex!important;
    align-items:center!important;
    justify-content:flex-end!important;
    flex-wrap:nowrap!important;
    gap:6px!important;
    width:auto!important;
    padding-left:0!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions .soft-action-btn,
  .app-shell .spirit-friends-dashboard .compact-friend-actions .soft-action-btn{
    position:relative!important;
    z-index:1!important;
    min-height:34px!important;
    border-radius:14px!important;
    background:rgba(255,253,246,.96)!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions .partner-inline-btn,
  .app-shell .spirit-friends-dashboard .compact-friend-actions .partner-inline-btn{
    display:none!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions .remove-friend-btn,
  .app-shell .spirit-friends-dashboard .compact-friend-actions .remove-friend-btn{
    width:34px!important;
    min-width:34px!important;
    height:34px!important;
    min-height:34px!important;
    padding:0!important;
    border-radius:999px!important;
    font-size:20px!important;
    line-height:1!important;
  }
  @media(max-width:430px){
    .app-shell .friends-garden-panel .compact-friend-row,
    .app-shell .spirit-friends-dashboard .compact-friend-row{
      grid-template-columns:minmax(0,1fr) auto!important;
      gap:8px!important;
      padding:9px 8px!important;
    }
    .app-shell .friends-garden-panel .compact-friend-identity,
    .app-shell .spirit-friends-dashboard .compact-friend-identity{
      grid-template-columns:42px minmax(0,1fr)!important;
      gap:9px!important;
    }
    .app-shell .friends-garden-panel .compact-friend-identity .friend-spirit-avatar,
    .app-shell .spirit-friends-dashboard .compact-friend-identity .friend-spirit-avatar{
      width:40px!important;
      height:40px!important;
    }
    .app-shell .friends-garden-panel .compact-friend-actions .soft-action-btn:not(.remove-friend-btn),
    .app-shell .spirit-friends-dashboard .compact-friend-actions .soft-action-btn:not(.remove-friend-btn){
      min-height:32px!important;
      padding:7px 9px!important;
      font-size:12px!important;
      white-space:nowrap!important;
    }
    .app-shell .friends-garden-panel .compact-friend-actions .remove-friend-btn,
    .app-shell .spirit-friends-dashboard .compact-friend-actions .remove-friend-btn{
      width:32px!important;
      min-width:32px!important;
      height:32px!important;
      min-height:32px!important;
    }
    .app-shell .friends-garden-panel .compact-friend-row::after,
    .app-shell .spirit-friends-dashboard .compact-friend-row::after{
      left:52px;
      right:8px;
    }
  }


  /* Home remove button and Heaven's Word badge placement fix. */
  .home-page .home-shop-card button{
    color:inherit;
  }
  .home-page .home-shop-card button:last-child{
    color:#111111!important;
  }
  .home-page .home-remove-btn,
  .home-remove-btn{
    color:#111111!important;
    font-weight:900!important;
  }
  .home-room .home-word-scroll{
    top:14px!important;
    left:14px!important;
    right:auto!important;
    bottom:auto!important;
    z-index:4!important;
    opacity:.42!important;
    transform:scale(.88)!important;
    transform-origin:left top!important;
  }
  .home-room.home-stage-0 .home-word-scroll,
  .home-room.home-stage-1 .home-word-scroll{
    opacity:.28!important;
    left:12px!important;
    top:12px!important;
  }
  .home-room.home-stage-4 .home-word-scroll,
  .home-room.home-stage-5 .home-word-scroll,
  .home-room.home-stage-6 .home-word-scroll{
    opacity:.48!important;
  }
  @media(max-width:560px){
    .home-room .home-word-scroll{
      top:10px!important;
      left:10px!important;
      right:auto!important;
      bottom:auto!important;
      font-size:7.5px!important;
      padding:4px 6px!important;
      opacity:.34!important;
    }
  }

  /* Inbox, friend row, and closet safe-area polish */
  .app-shell .community-feed-card .soft-action-btn.friend-notification-bell{
    min-width:0!important;
  }
  .app-shell .friends-garden-panel .compact-friend-row,
  .app-shell .spirit-friends-dashboard .compact-friend-row{
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto!important;
    align-items:center!important;
    gap:6px!important;
    padding:9px 10px!important;
    min-height:0!important;
  }
  .app-shell .friends-garden-panel .compact-friend-identity,
  .app-shell .spirit-friends-dashboard .compact-friend-identity{
    width:auto!important;
    min-width:0!important;
    flex:initial!important;
    gap:8px!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions,
  .app-shell .spirit-friends-dashboard .compact-friend-actions{
    width:auto!important;
    padding-left:0!important;
    justify-content:flex-end!important;
    flex-wrap:nowrap!important;
    gap:4px!important;
  }
  .app-shell .compact-friend-actions .soft-action-btn:not(.remove-friend-btn){
    min-height:31px!important;
    padding:7px 8px!important;
    font-size:12px!important;
  }
  .app-shell .compact-friend-actions .remove-friend-btn{
    width:28px!important;
    min-width:28px!important;
    min-height:28px!important;
    font-size:16px!important;
  }
  .closet-overlay{
    z-index:1200!important;
  }
  .closet-sheet{
    max-height:82vh!important;
    padding-bottom:calc(178px + env(safe-area-inset-bottom,0px))!important;
    scroll-padding-bottom:190px!important;
  }
  .closet-header{
    align-items:flex-start!important;
    gap:12px!important;
  }
  .closet-header h2{
    line-height:1.15!important;
  }
  .closet-header p{
    max-width:250px!important;
    line-height:1.3!important;
  }
  .closet-pieces-grid{
    padding-bottom:96px!important;
    gap:16px 12px!important;
  }
  .closet-pieces-grid > div{
    gap:10px!important;
  }
  .closet-pieces-grid > div > button:last-child{
    position:relative!important;
    z-index:2!important;
    min-height:40px!important;
    padding:9px 16px!important;
    margin-bottom:22px!important;
    border-radius:12px!important;
    background:rgba(255,253,246,.94)!important;
    color:#3A2600!important;
    font-weight:900!important;
    box-shadow:0 4px 12px rgba(82,55,15,.08)!important;
  }
  .closet-item-btn{
    min-height:142px!important;
  }
  .closet-item-btn.equipped{
    padding-bottom:14px!important;
  }
  .closet-item-name{
    white-space:normal!important;
    display:-webkit-box!important;
    -webkit-line-clamp:2!important;
    -webkit-box-orient:vertical!important;
    min-height:2.35em!important;
    line-height:1.18!important;
  }
  @media(max-width:520px){
    .closet-sheet{
      max-height:80vh!important;
      padding-bottom:calc(206px + env(safe-area-inset-bottom,0px))!important;
    }
    .closet-header p{
      max-width:220px!important;
      font-size:12.5px!important;
    }
    .closet-pieces-grid{
      gap:18px 10px!important;
      padding-bottom:122px!important;
    }
  }

	

  /* Instagram-inspired Prayer Circle friends list. */
  .app-shell .friends-garden-panel{
    padding:18px 14px!important;
    border-radius:28px!important;
    background:#fffefb!important;
    border:1px solid rgba(231,207,159,.24)!important;
    box-shadow:0 14px 30px rgba(91,67,28,.045)!important;
  }
  .app-shell .friends-garden-panel .prayer-friend-panel-title{
    margin:0 0 12px!important;
    padding:0 2px 8px!important;
    border-bottom:1px solid rgba(46,38,25,.10)!important;
    color:#171717!important;
    font-size:15px!important;
    font-weight:900!important;
    letter-spacing:.02em!important;
  }
  .app-shell .friends-garden-panel .scroll-list.compact{
    gap:0!important;
    max-height:min(62vh,560px)!important;
    padding:0!important;
  }
  .app-shell .friends-garden-panel .compact-friend-row{
    grid-template-columns:minmax(0,1fr) auto!important;
    gap:12px!important;
    min-height:78px!important;
    padding:10px 0!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
  }
  .app-shell .friends-garden-panel .compact-friend-row::after{
    left:68px!important;
    right:0!important;
    background:rgba(20,20,20,.08)!important;
  }
  .app-shell .friends-garden-panel .instagram-friend-identity{
    grid-template-columns:58px minmax(0,1fr)!important;
    gap:14px!important;
    align-items:center!important;
  }
  .app-shell .friends-garden-panel .instagram-friend-identity .friend-spirit-avatar{
    width:58px!important;
    height:58px!important;
    border-radius:50%!important;
    background:linear-gradient(145deg,rgba(242,255,237,.95),rgba(255,239,247,.78))!important;
    box-shadow:0 8px 18px rgba(91,67,28,.08)!important;
  }
  .app-shell .friends-garden-panel .instagram-friend-identity .friend-spirit-avatar img{
    width:84%!important;
    height:84%!important;
    object-fit:contain!important;
  }
  .app-shell .friends-garden-panel .instagram-friend-copy{
    display:flex!important;
    flex-direction:column!important;
    gap:2px!important;
    min-width:0!important;
  }
  .app-shell .friends-garden-panel .instagram-friend-copy .prayer-friend-name{
    color:#151515!important;
    font-size:16px!important;
    font-weight:900!important;
    line-height:1.15!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .app-shell .friends-garden-panel .instagram-friend-subtitle{
    color:#7b818b!important;
    font-size:13.5px!important;
    font-weight:650!important;
    line-height:1.2!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions{
    gap:12px!important;
    align-items:center!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions .soft-action-btn:not(.remove-friend-btn):not(.partner-inline-btn){
    min-width:112px!important;
    min-height:42px!important;
    padding:10px 18px!important;
    border:0!important;
    border-radius:13px!important;
    background:#eef0f4!important;
    color:#111111!important;
    font-size:15px!important;
    font-weight:900!important;
    box-shadow:none!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions .soft-action-btn.friend-alert-btn:not(.remove-friend-btn):not(.partner-inline-btn){
    background:linear-gradient(135deg,#fff0bd,#d8a236)!important;
    color:#2d230f!important;
  }
  .app-shell .friends-garden-panel .compact-friend-actions .remove-friend-btn{
    width:30px!important;
    min-width:30px!important;
    height:30px!important;
    min-height:30px!important;
    border:0!important;
    background:transparent!important;
    color:#111111!important;
    font-size:29px!important;
    font-weight:300!important;
    line-height:1!important;
    box-shadow:none!important;
  }
  @media(max-width:430px){
    .app-shell .friends-garden-panel{
      padding:16px 12px!important;
    }
    .app-shell .friends-garden-panel .compact-friend-row{
      min-height:74px!important;
      gap:8px!important;
    }
    .app-shell .friends-garden-panel .instagram-friend-identity{
      grid-template-columns:52px minmax(0,1fr)!important;
      gap:12px!important;
    }
    .app-shell .friends-garden-panel .instagram-friend-identity .friend-spirit-avatar{
      width:52px!important;
      height:52px!important;
    }
    .app-shell .friends-garden-panel .compact-friend-row::after{
      left:64px!important;
    }
    .app-shell .friends-garden-panel .instagram-friend-copy .prayer-friend-name{
      font-size:15.5px!important;
    }
    .app-shell .friends-garden-panel .instagram-friend-subtitle{
      font-size:12.5px!important;
    }
    .app-shell .friends-garden-panel .compact-friend-actions{
      gap:7px!important;
    }
    .app-shell .friends-garden-panel .compact-friend-actions .soft-action-btn:not(.remove-friend-btn):not(.partner-inline-btn){
      min-width:86px!important;
      min-height:38px!important;
      padding:8px 12px!important;
      font-size:13.5px!important;
    }
    .app-shell .friends-garden-panel .compact-friend-actions .remove-friend-btn{
      width:26px!important;
      min-width:26px!important;
      height:26px!important;
      min-height:26px!important;
      font-size:25px!important;
    }
  }


  /* Softer friends names and prayer prompt. */
  .app-shell .friends-garden-panel .friend-prayer-prompt{
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:12px!important;
    margin:2px 0 12px!important;
    padding:12px 14px!important;
    border-radius:18px!important;
    background:linear-gradient(135deg,rgba(255,248,223,.78),rgba(246,250,255,.58))!important;
    border:1px solid rgba(231,207,159,.32)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.84)!important;
  }
  .app-shell .friends-garden-panel .friend-prayer-prompt-kicker{
    display:block!important;
    margin-bottom:3px!important;
    color:#9a731d!important;
    font-size:10px!important;
    font-weight:850!important;
    letter-spacing:.12em!important;
    text-transform:uppercase!important;
  }
  .app-shell .friends-garden-panel .friend-prayer-prompt p{
    margin:0!important;
    color:#4b3822!important;
    font-size:13.5px!important;
    font-weight:650!important;
    line-height:1.45!important;
  }
  .app-shell .friends-garden-panel .friend-prayer-prompt > span{
    flex-shrink:0!important;
    width:38px!important;
    height:38px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    border-radius:50%!important;
    background:rgba(255,255,255,.74)!important;
    box-shadow:0 7px 16px rgba(91,67,28,.08)!important;
    font-size:20px!important;
  }
  .app-shell .friends-garden-panel .instagram-friend-copy .prayer-friend-name{
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
    color:#2f2618!important;
    font-size:15.75px!important;
    font-weight:720!important;
    letter-spacing:0!important;
  }
  @media(max-width:430px){
    .app-shell .friends-garden-panel .friend-prayer-prompt{
      padding:11px 12px!important;
      margin-bottom:10px!important;
    }
    .app-shell .friends-garden-panel .friend-prayer-prompt p{
      font-size:12.75px!important;
    }
    .app-shell .friends-garden-panel .instagram-friend-copy .prayer-friend-name{
      font-size:15px!important;
      font-weight:720!important;
    }
  }





  /* Accountability partner collapsible chooser. */
  .partner-ask-toggle{
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    gap:8px!important;
    border-radius:16px!important;
    min-height:42px!important;
  }
  .partner-ask-toggle .quick-prayer-chevron{
    font-size:17px!important;
    line-height:1!important;
  }
  .partner-ask-panel{
    background:rgba(255,253,246,.62)!important;
    border-color:rgba(231,207,159,.24)!important;
    box-shadow:none!important;
  }
  .accountability-primary-card .friend-checkbox-list{
    gap:7px!important;
  }
  .accountability-primary-card .friend-checkbox-row{
    min-height:42px!important;
    border-radius:14px!important;
    background:rgba(255,255,255,.68)!important;
  }

  /* Premium Prayer Circle simplification. */
  .spirit-friends-dashboard .spirit-friends-hero-card{
    border-color:rgba(231,207,159,.24)!important;
    box-shadow:0 14px 32px rgba(91,67,28,.055)!important;
  }
  .friends-hero-summary{
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:10px!important;
    margin-top:12px!important;
    padding:10px 12px!important;
    border-radius:18px!important;
    background:rgba(255,255,255,.64)!important;
    border:1px solid rgba(184,147,75,.16)!important;
    color:#3a2a18!important;
    font-size:14px!important;
    font-weight:720!important;
    line-height:1.25!important;
  }
  .friends-hero-summary.has-new-blessing{
    background:linear-gradient(135deg,rgba(255,251,232,.96),rgba(255,241,184,.72))!important;
    border-color:rgba(201,144,18,.28)!important;
    box-shadow:0 8px 20px rgba(201,144,18,.10)!important;
  }
  .new-blessing-shortcut{
    flex-shrink:0!important;
    display:inline-flex!important;
    align-items:center!important;
    gap:7px!important;
    min-height:34px!important;
    border-radius:999px!important;
    border:1px solid rgba(138,90,8,.28)!important;
    background:linear-gradient(135deg,#fff3c4,#d9a238)!important;
    color:#2f220d!important;
    padding:7px 11px!important;
    font-family:'Nunito',sans-serif!important;
    font-size:12px!important;
    font-weight:900!important;
    cursor:pointer!important;
    box-shadow:0 6px 14px rgba(138,90,8,.14)!important;
    white-space:nowrap!important;
  }
  .new-blessing-shortcut span{
    min-width:19px!important;
    height:19px!important;
    border-radius:50%!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    background:rgba(255,255,255,.75)!important;
    font-size:11px!important;
  }
  .spirit-friends-dashboard .prayer-circle-tool-grid{
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    max-width:520px!important;
  }
  .spirit-friends-dashboard .prayer-circle-tab{
    min-height:48px!important;
    border-radius:18px!important;
    border:1px solid rgba(231,207,159,.36)!important;
    background:rgba(255,255,255,.68)!important;
    color:#5c4a2f!important;
    box-shadow:none!important;
    justify-content:center!important;
    font-size:14.5px!important;
  }
  .spirit-friends-dashboard .prayer-circle-tab.active,
  .spirit-friends-dashboard .prayer-circle-tab.friend-alert-btn{
    border-color:rgba(201,144,18,.42)!important;
    background:linear-gradient(135deg,rgba(255,251,232,.95),rgba(255,241,184,.62))!important;
    color:#2f220d!important;
    box-shadow:0 8px 18px rgba(201,144,18,.10)!important;
  }
  .spirit-friends-dashboard .spirit-friends-tools-card{
    border-color:rgba(231,207,159,.20)!important;
    box-shadow:0 12px 28px rgba(91,67,28,.045)!important;
  }
  .spirit-friends-dashboard .friends-garden-panel{
    padding:12px 4px 4px!important;
    border:0!important;
    box-shadow:none!important;
    background:transparent!important;
  }
  .spirit-friends-dashboard .friends-garden-panel .prayer-friend-panel-title{
    border-bottom:0!important;
    padding:0 2px 4px!important;
    margin-bottom:8px!important;
    color:#3a2a18!important;
    font-size:14px!important;
    letter-spacing:.08em!important;
    text-transform:uppercase!important;
  }
  .spirit-friends-dashboard .friends-garden-panel .scroll-list.compact{
    max-height:min(58vh,520px)!important;
    gap:0!important;
    padding:0!important;
  }
  .spirit-friends-dashboard .friends-garden-panel .compact-friend-row{
    min-height:72px!important;
    padding:9px 0!important;
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
  }
  .spirit-friends-dashboard .friends-garden-panel .compact-friend-row::after{
    left:66px!important;
    right:4px!important;
    background:rgba(46,38,25,.075)!important;
  }
  .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions .soft-action-btn:not(.remove-friend-btn){
    min-width:92px!important;
    min-height:38px!important;
    border-radius:14px!important;
    background:#f0f1f3!important;
    color:#2c2418!important;
    font-size:13.5px!important;
    padding:8px 12px!important;
    box-shadow:none!important;
  }
  .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions .soft-action-btn.friend-alert-btn:not(.remove-friend-btn){
    background:linear-gradient(135deg,#fff2c0,#d8a236)!important;
  }
  .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions .remove-friend-btn{
    color:rgba(58,42,24,.42)!important;
    font-size:24px!important;
  }
  .spirit-friends-dashboard .community-feed-card.blessings-new{
    animation:softBlessingPulse 2.8s ease-in-out infinite!important;
  }
  @keyframes softBlessingPulse{
    0%,100%{box-shadow:0 12px 28px rgba(201,144,18,.12),inset 0 1px 0 rgba(255,255,255,.82)}
    50%{box-shadow:0 14px 34px rgba(201,144,18,.20),0 0 0 1px rgba(201,144,18,.18),inset 0 1px 0 rgba(255,255,255,.88)}
  }
  @media(max-width:430px){
    .friends-hero-summary{align-items:flex-start!important;flex-direction:column!important;}
    .new-blessing-shortcut{width:100%!important;justify-content:center!important;}
    .spirit-friends-dashboard .prayer-circle-tab{min-height:46px!important;font-size:14px!important;}
    .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions .soft-action-btn:not(.remove-friend-btn){min-width:82px!important;font-size:12.75px!important;padding:8px 10px!important;}
  }

  /* Premium Home pass: stage identity, blessing, preview, and restrained color. */
  .home-page .simple-home-preview{
    background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(246,250,255,.62))!important;
    border:1px solid rgba(125,174,212,.22)!important;
    box-shadow:0 16px 34px rgba(67,82,100,.075),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .home-page .home-stage-signature-copy{
    margin-top:7px!important;
    color:#6f604d!important;
    font-size:13px!important;
    line-height:1.48!important;
  }
  .home-room.home-stage-0{--stage-tint:rgba(186,230,253,.16);}
  .home-room.home-stage-1{--stage-tint:rgba(220,252,231,.18);}
  .home-room.home-stage-2{--stage-tint:rgba(187,247,208,.24);}
  .home-room.home-stage-3{--stage-tint:rgba(219,234,254,.22);}
  .home-room.home-stage-4{--stage-tint:rgba(252,231,243,.18);}
  .home-room.home-stage-5,
  .home-room.home-stage-6{--stage-tint:rgba(255,248,223,.22);}
  .home-room::before{
    background:
      radial-gradient(circle at 24% 18%,var(--stage-tint,rgba(255,248,223,.18)),transparent 34%),
      radial-gradient(circle at 78% 22%,rgba(255,255,255,.32),transparent 28%)!important;
  }
  .home-room.home-time-evening .home-sky{filter:saturate(.98) brightness(.98) sepia(.05)!important;}
  .home-room.home-time-night .home-sky{filter:saturate(.88) brightness(.82)!important;}
  .home-room.home-time-night .home-window-dot,
  .home-room.home-time-night .home-door,
  .home-room.home-time-night .home-little-shelter-door,
  .home-room.home-time-night .home-house-door,
  .home-room.home-time-night .home-mansion-door{
    box-shadow:0 0 18px rgba(255,248,223,.78),inset 0 0 10px rgba(255,255,255,.72)!important;
  }
  .home-care-card.simple-home-blessing{
    border:1px solid rgba(125,174,212,.20)!important;
    background:linear-gradient(145deg,rgba(255,253,246,.96),rgba(236,253,245,.58))!important;
    box-shadow:0 12px 28px rgba(63,91,79,.065),inset 0 1px 0 rgba(255,255,255,.82)!important;
  }
  .home-care-card.simple-home-blessing.blessed-today{
    background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(255,248,223,.78))!important;
    border-color:rgba(201,144,18,.25)!important;
  }
  .home-blessing-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
  .home-blessing-head > span{
    width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;
    background:rgba(255,255,255,.72);box-shadow:0 8px 18px rgba(91,67,28,.07);font-size:19px;flex-shrink:0;
  }
  .home-blessing-overlay{
    background:radial-gradient(circle at 50% 22%,rgba(255,255,255,.86),rgba(236,253,245,.44) 24%,rgba(255,248,223,.22) 52%,transparent 70%)!important;
    animation:homeRestore 3.05s ease-out forwards!important;
  }
  .home-blessing-overlay::after{
    content:"Peace";position:absolute;left:50%;top:18%;transform:translateX(-50%);
    padding:6px 13px;border-radius:999px;background:rgba(255,253,246,.74);border:1px solid rgba(231,207,159,.32);
    color:#6f5321;font-size:12px;font-weight:850;letter-spacing:.08em;text-transform:uppercase;
    box-shadow:0 10px 22px rgba(91,67,28,.08);animation:homeTransformSpark 2.8s ease-out forwards;
  }
  .home-transform-overlay{
    background:radial-gradient(circle at 50% 38%,rgba(255,255,255,.68),rgba(219,234,254,.28) 27%,rgba(255,248,223,.20) 54%,transparent 74%)!important;
  }
  .home-transform-overlay .build-scaffold{opacity:.48!important;}
  .home-transform-overlay::after{
    border-radius:26px!important;
    border-color:rgba(125,174,212,.22)!important;
    background:linear-gradient(180deg,rgba(255,255,255,.48),rgba(219,234,254,.16),rgba(240,203,118,.10))!important;
  }
  .home-shop-shell-card.simple-home-decorate{
    border-color:rgba(231,207,159,.22)!important;
    background:rgba(255,253,246,.82)!important;
    box-shadow:0 10px 24px rgba(91,67,28,.045)!important;
  }
  .home-shop-card{
    border-color:rgba(125,174,212,.18)!important;
    background:linear-gradient(145deg,rgba(255,253,246,.95),rgba(246,250,255,.42))!important;
  }
  .home-shop-card.equipped{
    border-color:rgba(201,144,18,.38)!important;
    box-shadow:0 10px 22px rgba(201,144,18,.08)!important;
  }
  .home-shop-card .meaning-chip{
    background:rgba(236,253,245,.62)!important;
    border-color:rgba(134,239,172,.24)!important;
    color:#4b5f45!important;
  }
  .home-shop-price{color:#7a5b19!important;}
  @media(max-width:430px){
    .home-page .home-stage-signature-copy{font-size:12.25px!important;}
    .home-blessing-head > span{width:34px;height:34px;font-size:17px;}
  }

  /* Final home realism pass: make every stage read as a real home, not abstract shapes. */
  .home-page .home-room{
    min-height:390px!important;
  }
  .home-page .home-room .home-cardboard,
  .home-page .home-room .home-little-shelter,
  .home-page .home-room .home-house,
  .home-page .home-room .home-mansion,
  .home-page .home-room .home-palace,
  .home-page .home-room .home-chapel{
    overflow:visible!important;
    z-index:18!important;
    filter:
      drop-shadow(0 18px 18px rgba(79,53,17,.18))
      drop-shadow(0 0 18px rgba(255,244,206,.42))!important;
  }
  .home-page .home-room .home-cardboard{top:36%!important;width:176px!important;height:118px!important;}
  .home-page .home-room .home-little-shelter{top:31%!important;width:206px!important;height:146px!important;}
  .home-page .home-room .home-house{top:24%!important;width:246px!important;height:174px!important;}
  .home-page .home-room .home-mansion{top:19%!important;width:278px!important;height:198px!important;}
  .home-page .home-room .home-palace{top:13%!important;width:300px!important;height:222px!important;}

  .home-page .home-room .home-cardboard-roof,
  .home-page .home-room .home-little-shelter-roof,
  .home-page .home-room .home-house-roof,
  .home-page .home-room .home-mansion-roof,
  .home-page .home-room .home-palace-roof,
  .home-page .home-room .home-chapel-roof{
    clip-path:polygon(50% 0,100% 72%,92% 100%,8% 100%,0 72%)!important;
    border-radius:12px 12px 8px 8px!important;
    background:
      linear-gradient(90deg,rgba(255,255,255,.28),transparent 18%,transparent 82%,rgba(124,81,11,.10)),
      linear-gradient(180deg,#fff4c8 0%,#e0ae42 56%,#9f6a12 100%)!important;
    border:2px solid rgba(139,92,18,.22)!important;
    box-shadow:0 8px 10px rgba(79,53,17,.10), inset 0 7px 10px rgba(255,255,255,.50)!important;
  }
  .home-page .home-room .home-cardboard-roof{left:0!important;right:0!important;top:0!important;height:54px!important;}
  .home-page .home-room .home-little-shelter-roof{left:0!important;right:0!important;top:0!important;height:70px!important;}
  .home-page .home-room .home-house-roof{left:0!important;right:0!important;top:0!important;height:84px!important;}
  .home-page .home-room .home-mansion-roof{left:4px!important;right:4px!important;top:2px!important;height:78px!important;clip-path:polygon(8% 100%,20% 34%,34% 100%,50% 10%,66% 100%,80% 34%,92% 100%)!important;}
  .home-page .home-room .home-palace-roof{left:36px!important;right:36px!important;top:2px!important;height:74px!important;clip-path:polygon(50% 0,96% 100%,4% 100%)!important;}

  .home-page .home-room .home-cardboard-roof::after,
  .home-page .home-room .home-little-shelter-roof::after,
  .home-page .home-room .home-house-roof::after{
    content:""!important;
    position:absolute!important;
    left:9%!important;
    right:9%!important;
    bottom:-4px!important;
    height:10px!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,rgba(255,255,255,.72),#f0c75e,rgba(138,90,8,.28))!important;
    box-shadow:0 4px 8px rgba(79,53,17,.12)!important;
  }
  .home-page .home-room .home-house-roof::before,
  .home-page .home-room .home-little-shelter-roof::before{
    content:""!important;
    position:absolute!important;
    right:22%!important;
    top:12px!important;
    width:18px!important;
    height:34px!important;
    border-radius:5px 5px 2px 2px!important;
    background:linear-gradient(180deg,#fff1c9,#b7791f)!important;
    border:1px solid rgba(138,90,8,.24)!important;
    box-shadow:inset 3px 0 5px rgba(255,255,255,.44)!important;
  }

  .home-page .home-room .home-cardboard-body,
  .home-page .home-room .home-little-shelter-body,
  .home-page .home-room .home-house-body,
  .home-page .home-room .home-mansion-body,
  .home-page .home-room .home-palace-body,
  .home-page .home-room .home-chapel-body{
    border:2px solid rgba(139,92,18,.20)!important;
    background:
      linear-gradient(90deg,rgba(255,255,255,.32),transparent 22%,transparent 78%,rgba(116,78,18,.10)),
      linear-gradient(180deg,#fffdf6 0%,#fff0c3 52%,#dfb04e 100%)!important;
    box-shadow:
      inset 0 10px 16px rgba(255,255,255,.62),
      inset 0 -10px 18px rgba(126,83,15,.10),
      0 12px 16px rgba(79,53,17,.09)!important;
  }
  .home-page .home-room .home-cardboard-body{left:22px!important;right:22px!important;bottom:0!important;height:78px!important;border-radius:16px 16px 8px 8px!important;}
  .home-page .home-room .home-little-shelter-body{left:25px!important;right:25px!important;bottom:0!important;height:98px!important;border-radius:22px 22px 10px 10px!important;}
  .home-page .home-room .home-house-body{left:34px!important;right:34px!important;bottom:0!important;height:122px!important;border-radius:24px 24px 10px 10px!important;}
  .home-page .home-room .home-mansion-body{left:24px!important;right:24px!important;bottom:0!important;height:132px!important;border-radius:22px 22px 10px 10px!important;}
  .home-page .home-room .home-palace-body{left:58px!important;right:58px!important;bottom:0!important;height:142px!important;border-radius:32px 32px 12px 12px!important;}

  .home-page .home-room .home-cardboard-body::after,
  .home-page .home-room .home-little-shelter-body::after,
  .home-page .home-room .home-house-body::after{
    content:""!important;
    position:absolute!important;
    top:26px!important;
    right:22px!important;
    width:28px!important;
    height:28px!important;
    border-radius:7px!important;
    background:
      linear-gradient(90deg,transparent 47%,rgba(138,90,8,.22) 48% 52%,transparent 53%),
      linear-gradient(0deg,transparent 47%,rgba(138,90,8,.22) 48% 52%,transparent 53%),
      radial-gradient(circle at 50% 38%,#fff 0,#fff7d6 44%,#f2cb68 100%)!important;
    border:1px solid rgba(138,90,8,.22)!important;
    box-shadow:0 0 12px rgba(255,248,223,.70)!important;
  }
  .home-page .home-room .home-little-shelter-window,
  .home-page .home-room .home-window-dot,
  .home-page .home-room .home-mansion-window,
  .home-page .home-room .home-chapel-window{
    width:30px!important;
    height:30px!important;
    border-radius:7px!important;
    border:1px solid rgba(138,90,8,.22)!important;
    background:
      linear-gradient(90deg,transparent 47%,rgba(138,90,8,.22) 48% 52%,transparent 53%),
      linear-gradient(0deg,transparent 47%,rgba(138,90,8,.22) 48% 52%,transparent 53%),
      radial-gradient(circle at 48% 34%,#fff 0,#fff8df 42%,#f1ca67 100%)!important;
    box-shadow:0 0 12px rgba(255,248,223,.70)!important;
  }
  .home-page .home-room .home-little-shelter-window{top:75px!important;}
  .home-page .home-room .home-little-shelter-window.left{left:42px!important;}
  .home-page .home-room .home-little-shelter-window.right{right:42px!important;}
  .home-page .home-room .home-window-dot{top:82px!important;}
  .home-page .home-room .home-window-dot.left{left:55px!important;}
  .home-page .home-room .home-window-dot.right{right:55px!important;}
  .home-page .home-room .home-mansion-window{top:82px!important;}
  .home-page .home-room .home-mansion-window.left{left:62px!important;}
  .home-page .home-room .home-mansion-window.right{right:62px!important;}

  .home-page .home-room .home-cardboard-door,
  .home-page .home-room .home-little-shelter-door,
  .home-page .home-room .home-house-door,
  .home-page .home-room .home-mansion-door,
  .home-page .home-room .home-door,
  .home-page .home-room .home-chapel-door{
    border:1px solid rgba(91,58,8,.22)!important;
    border-radius:18px 18px 5px 5px!important;
    background:
      radial-gradient(circle at 50% 18%,#fff 0,#fff6d3 28%,#d19a25 72%,#7a4e08 100%)!important;
    box-shadow:0 0 18px rgba(255,248,223,.78),inset 0 6px 8px rgba(255,255,255,.55)!important;
  }
  .home-page .home-room .home-cardboard-door{left:50%!important;bottom:0!important;width:36px!important;height:52px!important;transform:translateX(-50%)!important;}
  .home-page .home-room .home-little-shelter-door{left:50%!important;bottom:0!important;width:42px!important;height:62px!important;transform:translateX(-50%)!important;}
  .home-page .home-room .home-house-door{left:50%!important;bottom:0!important;width:46px!important;height:68px!important;transform:translateX(-50%)!important;}
  .home-page .home-room .home-mansion-door,
  .home-page .home-room .home-door{left:50%!important;bottom:0!important;width:52px!important;height:78px!important;transform:translateX(-50%)!important;}

  .home-page .home-room .home-mansion-column{
    top:76px!important;
    bottom:0!important;
    width:20px!important;
    border-radius:999px 999px 8px 8px!important;
    background:linear-gradient(90deg,rgba(255,255,255,.75),#f8e4a3,#c99012)!important;
    border:1px solid rgba(138,90,8,.18)!important;
  }
  .home-page .home-room .home-mansion-column.c1{left:42%!important;}
  .home-page .home-room .home-mansion-column.c2{right:42%!important;}
  .home-page .home-room .home-tower{
    bottom:0!important;
    width:58px!important;
    height:164px!important;
    border:2px solid rgba(138,90,8,.18)!important;
    border-radius:28px 28px 9px 9px!important;
    background:
      linear-gradient(90deg,rgba(255,255,255,.38),transparent 42%,rgba(126,83,15,.08)),
      linear-gradient(180deg,#fffdf6,#fff0c3 56%,#d8a13a)!important;
  }
  .home-page .home-room .home-tower.left{left:8px!important;}
  .home-page .home-room .home-tower.right{right:8px!important;}
  .home-page .home-room .home-tower::before{
    content:""!important;
    position:absolute!important;
    left:6px!important;
    right:6px!important;
    top:-30px!important;
    height:42px!important;
    border-radius:28px 28px 8px 8px!important;
    background:linear-gradient(180deg,#fff7d6,#d6a21a)!important;
    border:1px solid rgba(138,90,8,.20)!important;
  }
  .home-page .home-room .home-palace-star{
    top:-30px!important;
    color:#c99012!important;
    text-shadow:0 0 14px rgba(240,203,118,.76)!important;
  }
  .home-page .home-room .home-cardboard::after,
  .home-page .home-room .home-little-shelter::after,
  .home-page .home-room .home-house::after,
  .home-page .home-room .home-mansion::after,
  .home-page .home-room .home-palace::after{
    content:""!important;
    position:absolute!important;
    left:6%!important;
    right:6%!important;
    bottom:-13px!important;
    height:20px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse at 50% 50%,rgba(71,50,18,.23),rgba(71,50,18,0) 72%)!important;
    z-index:-1!important;
  }
  .home-page .home-room .home-spirit-slot{
    bottom:-1%!important;
    transform:translateX(-50%) scale(.92)!important;
  }
  @media(max-width:560px){
    .home-page .home-room{min-height:360px!important;}
    .home-page .home-room .home-cardboard{top:38%!important;width:158px!important;}
    .home-page .home-room .home-little-shelter{top:33%!important;width:188px!important;}
    .home-page .home-room .home-house{top:26%!important;width:218px!important;}
    .home-page .home-room .home-mansion{top:21%!important;width:246px!important;}
    .home-page .home-room .home-palace{top:16%!important;width:264px!important;}
    .home-page .home-room .home-spirit-slot{bottom:-2%!important;transform:translateX(-50%) scale(.88)!important;}
  }



  /* Final custom icon polish: solid gold-brown symbols with no pale icon background. */
  .app-glow-icon{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    overflow:visible;
    border-radius:0;
    vertical-align:middle;
    color:#6f4b12;
    background:transparent!important;
    box-shadow:none!important;
  }
  .app-glow-icon svg{
    width:100%;
    height:100%;
    display:block;
    overflow:visible;
    filter:drop-shadow(0 1px 0 rgba(255,248,223,.34));
  }
  .app-glow-icon .icon-fill{
    fill:#c99012;
  }
  .app-glow-icon .icon-deep{
    fill:#5f3b08;
  }
  .app-glow-icon .icon-stroke{
    stroke:#5f3b08;
    stroke-width:2.35;
    stroke-linecap:round;
    stroke-linejoin:round;
    fill:none;
  }
  .tab-btn.active .app-glow-icon .icon-fill,
  .top-soft-chip .app-glow-icon .icon-fill{
    fill:#b7791f;
  }
  .tab-btn.active .app-glow-icon .icon-deep,
  .top-soft-chip .app-glow-icon .icon-deep{
    fill:#4f3006;
  }
  .tab-btn.active .app-glow-icon .icon-stroke,
  .top-soft-chip .app-glow-icon .icon-stroke{
    stroke:#4f3006;
  }
  .empty-state-icon .journal-glow-icon{
    width:42px!important;
    height:42px!important;
    margin:0 auto;
  }


  /* Premium Home depth pass: richer homes, clearer ground, less flat geometry. */
  .home-page .home-room{
    background:
      radial-gradient(circle at 50% 18%,rgba(255,255,255,.74),transparent 30%),
      linear-gradient(180deg,#fffdf8 0%,#fff8df 54%,#f7df9a 100%)!important;
  }
  .home-page .home-room .home-sky::after{
    content:"";
    position:absolute;
    left:0;right:0;bottom:-1px;height:38%;
    background:linear-gradient(180deg,transparent,rgba(255,253,246,.72));
    pointer-events:none;
  }
  .home-page .home-room .home-floor{
    background:
      radial-gradient(ellipse at 50% 18%,rgba(255,255,255,.72),transparent 48%),
      radial-gradient(circle at 18% 86%,rgba(134,239,172,.32),transparent 28%),
      radial-gradient(circle at 82% 88%,rgba(249,168,212,.22),transparent 28%),
      linear-gradient(180deg,#fff8df 0%,#e7f7c9 42%,#d9bd65 100%)!important;
  }
  .home-page .home-room .home-midground-glow{
    left:9%!important;right:9%!important;bottom:30%!important;height:92px!important;
    background:radial-gradient(ellipse at 50% 70%,rgba(255,253,246,.72),rgba(255,243,196,.28) 42%,transparent 74%)!important;
  }
  .home-page .home-room .home-path{
    top:49%!important;
    bottom:auto!important;
    height:56%!important;
    width:min(42%,168px)!important;
    background:
      linear-gradient(90deg,rgba(138,90,8,.10) 0 1px,transparent 1px calc(100% - 1px),rgba(138,90,8,.10) calc(100% - 1px)),
      linear-gradient(180deg,rgba(255,255,255,.82),rgba(246,215,126,.76) 52%,rgba(198,144,18,.44))!important;
    clip-path:polygon(42% 0,58% 0,100% 100%,0 100%)!important;
    border:0!important;
    box-shadow:inset 0 0 24px rgba(255,255,255,.58),0 10px 18px rgba(82,55,15,.08)!important;
  }
  .home-page .home-room .home-path::after{
    background:
      repeating-linear-gradient(180deg,transparent 0 21px,rgba(116,78,18,.16) 22px 23px),
      repeating-linear-gradient(90deg,transparent 0 28px,rgba(116,78,18,.08) 29px 30px)!important;
    opacity:.5!important;
  }
  .home-page .home-room .home-path::before{
    inset:9% 16% 0!important;
    background:
      radial-gradient(circle at 50% 15%,rgba(255,255,255,.85) 0 2px,transparent 3px),
      radial-gradient(circle at 43% 42%,rgba(255,253,246,.66) 0 2px,transparent 3px),
      radial-gradient(circle at 58% 70%,rgba(255,243,196,.62) 0 2px,transparent 3px)!important;
    opacity:.55!important;
  }
  .home-page .home-room .home-flowerbed{
    bottom:2%!important;
    height:28%!important;
    width:33%!important;
    opacity:.82!important;
    background:
      radial-gradient(circle at 25% 68%,#fff 0 3px,transparent 4px),
      radial-gradient(circle at 36% 58%,#f9a8d4 0 3px,transparent 4px),
      radial-gradient(circle at 56% 70%,#fff7d6 0 3px,transparent 4px),
      radial-gradient(circle at 70% 55%,#bae6fd 0 3px,transparent 4px),
      linear-gradient(180deg,#a8d582,#5f8b39)!important;
    box-shadow:inset 0 8px 14px rgba(255,255,255,.22),0 10px 16px rgba(82,55,15,.08)!important;
  }
  .home-page .home-room .home-flowerbed.left{left:-7%!important;}
  .home-page .home-room .home-flowerbed.right{right:-7%!important;}

  .home-page .home-room .home-cardboard,
  .home-page .home-room .home-little-shelter,
  .home-page .home-room .home-house,
  .home-page .home-room .home-mansion,
  .home-page .home-room .home-palace{
    filter:drop-shadow(0 18px 20px rgba(74,48,12,.20)) drop-shadow(0 0 18px rgba(255,248,223,.46))!important;
  }
  .home-page .home-room .home-cardboard-roof,
  .home-page .home-room .home-little-shelter-roof,
  .home-page .home-room .home-house-roof,
  .home-page .home-room .home-mansion-roof,
  .home-page .home-room .home-palace-roof{
    background:
      repeating-linear-gradient(135deg,rgba(95,59,8,.12) 0 4px,transparent 4px 12px),
      linear-gradient(180deg,#fff0b8 0%,#d59d2e 55%,#86580f 100%)!important;
    border-color:rgba(95,59,8,.24)!important;
  }
  .home-page .home-room .home-cardboard-body,
  .home-page .home-room .home-little-shelter-body,
  .home-page .home-room .home-house-body,
  .home-page .home-room .home-mansion-body,
  .home-page .home-room .home-palace-body{
    background:
      linear-gradient(90deg,rgba(95,59,8,.10),transparent 14%,transparent 86%,rgba(95,59,8,.12)),
      linear-gradient(180deg,#fffdf6 0%,#fff0c8 44%,#ddb155 100%)!important;
    border-color:rgba(95,59,8,.20)!important;
    box-shadow:
      inset 0 10px 15px rgba(255,255,255,.64),
      inset 0 -12px 16px rgba(95,59,8,.12),
      0 12px 18px rgba(74,48,12,.10)!important;
  }
  .home-page .home-room .home-cardboard-body::before,
  .home-page .home-room .home-little-shelter-body::before,
  .home-page .home-room .home-house-body::before,
  .home-page .home-room .home-mansion-body::before,
  .home-page .home-room .home-palace-body::before{
    content:""!important;
    position:absolute!important;
    left:10%!important;right:10%!important;top:12px!important;height:1px!important;
    background:rgba(95,59,8,.12)!important;
    box-shadow:0 19px 0 rgba(95,59,8,.08),0 38px 0 rgba(255,255,255,.22)!important;
    border-radius:999px!important;
    pointer-events:none!important;
  }
  .home-page .home-room .home-cardboard-door,
  .home-page .home-room .home-little-shelter-door,
  .home-page .home-room .home-house-door,
  .home-page .home-room .home-mansion-door,
  .home-page .home-room .home-door{
    background:linear-gradient(180deg,#fff4c6 0%,#c99012 58%,#5f3b08 100%)!important;
    box-shadow:0 0 18px rgba(255,248,223,.72),inset 0 5px 8px rgba(255,255,255,.48),inset 0 -8px 8px rgba(95,59,8,.18)!important;
  }
  .home-page .home-room .home-cardboard-door::after,
  .home-page .home-room .home-little-shelter-door::after,
  .home-page .home-room .home-house-door::after,
  .home-page .home-room .home-mansion-door::after,
  .home-page .home-room .home-door::after{
    content:""!important;
    position:absolute!important;
    left:50%!important;bottom:-9px!important;
    width:58px!important;height:12px!important;
    transform:translateX(-50%)!important;
    border-radius:50% 50% 8px 8px!important;
    background:linear-gradient(180deg,#fff4c6,#c99012)!important;
    box-shadow:0 6px 8px rgba(74,48,12,.12)!important;
    z-index:-1!important;
  }
  .home-page .home-room .home-cardboard-door::before,
  .home-page .home-room .home-little-shelter-door::before,
  .home-page .home-room .home-house-door::before,
  .home-page .home-room .home-mansion-door::before,
  .home-page .home-room .home-door::before{
    content:""!important;
    position:absolute!important;
    right:8px!important;top:48%!important;
    width:4px!important;height:4px!important;border-radius:50%!important;
    background:#fff8df!important;
    box-shadow:0 0 6px rgba(255,248,223,.88)!important;
  }
  .home-page .home-room .home-window-dot,
  .home-page .home-room .home-little-shelter-window,
  .home-page .home-room .home-mansion-window{
    background:
      linear-gradient(90deg,transparent 46%,rgba(95,59,8,.36) 47% 53%,transparent 54%),
      linear-gradient(0deg,transparent 46%,rgba(95,59,8,.36) 47% 53%,transparent 54%),
      radial-gradient(circle at 50% 34%,#fffdf6 0%,#fff0b8 46%,#dca22e 100%)!important;
    box-shadow:0 0 13px rgba(255,248,223,.72),inset 0 2px 4px rgba(255,255,255,.78)!important;
  }
  .home-page .home-room.home-time-night .home-window-dot,
  .home-page .home-room.home-time-night .home-little-shelter-window,
  .home-page .home-room.home-time-night .home-mansion-window{
    box-shadow:0 0 18px rgba(255,230,150,.82),inset 0 2px 4px rgba(255,255,255,.78)!important;
  }

  .home-page .home-room.home-stage-0 .home-cardboard-body{background:linear-gradient(180deg,#f2d193,#bd813b)!important;}
  .home-page .home-room.home-stage-1 .home-little-shelter-body{background:linear-gradient(180deg,#fff8df,#e3c468 56%,#b98925)!important;}
  .home-page .home-room.home-stage-2 .home-house-body{background:linear-gradient(180deg,#fffdf6,#f3dda4 52%,#d6a243)!important;}
  .home-page .home-room.home-stage-3 .home-mansion-body{background:linear-gradient(180deg,#fffdf8,#e9f0d2 48%,#d5ad48)!important;}
  .home-page .home-room.home-stage-4 .home-palace-body,
  .home-page .home-room.home-stage-5 .home-palace-body,
  .home-page .home-room.home-stage-6 .home-palace-body{background:linear-gradient(180deg,#fffdf8,#fff1c9 45%,#d9a12e 100%)!important;}
  .home-page .home-room.home-stage-5 .home-palace,
  .home-page .home-room.home-stage-6 .home-palace{filter:drop-shadow(0 20px 24px rgba(126,83,15,.24)) drop-shadow(0 0 24px rgba(255,248,223,.56))!important;}
  .home-page .home-room.home-stage-6 .home-palace-star{font-size:22px!important;top:-38px!important;}
  .home-page .home-room.home-stage-6 .home-palace::before{
    content:""!important;
    position:absolute!important;
    inset:-22px -16px auto!important;
    height:78px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse,rgba(255,248,223,.48),transparent 68%)!important;
    z-index:-1!important;
  }

  @media(max-width:560px){
    .home-page .home-room .home-path{top:51%!important;width:min(40%,150px)!important;}
    .home-page .home-room .home-flowerbed{height:25%!important;width:35%!important;}
    .home-page .home-room .home-cardboard-door::after,
    .home-page .home-room .home-little-shelter-door::after,
    .home-page .home-room .home-house-door::after,
    .home-page .home-room .home-mansion-door::after,
    .home-page .home-room .home-door::after{width:48px!important;}
  }


  /* Final Prayer Vessel magic pass: personal, filled, and quietly alive. */
  @keyframes prayerVesselHeroArrive{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes prayerVesselStarWake{0%,100%{opacity:.16;transform:translateY(0) scale(.86)}50%{opacity:.74;transform:translateY(-7px) scale(1.08)}}
  @keyframes prayerVesselCloudDrift{0%,100%{transform:translateX(0);opacity:.42}50%{transform:translateX(10px);opacity:.68}}
  @keyframes prayerVesselAnsweredPulse{0%,100%{opacity:.35;transform:scale(.94)}50%{opacity:.9;transform:scale(1.08)}}
  @keyframes prayerVesselPedestalGlow{0%,100%{opacity:.55;filter:blur(.1px)}50%{opacity:.86;filter:blur(.5px)}}
  .prayer-vessel-hero{animation:prayerVesselHeroArrive .7s ease-out both!important;min-height:340px!important;background:radial-gradient(circle at 52% 12%,rgba(255,255,255,.96),transparent 26%),radial-gradient(circle at 72% 38%,rgba(255,231,161,.48),transparent 28%),radial-gradient(circle at 16% 28%,rgba(224,242,254,.42),transparent 34%),linear-gradient(145deg,rgba(255,254,249,.98),rgba(255,250,235,.9) 52%,rgba(246,250,255,.72))!important;}
  .prayer-vessel-hero::after{content:""!important;position:absolute!important;left:8%!important;right:8%!important;bottom:18px!important;height:70px!important;border-radius:50%!important;background:radial-gradient(ellipse,rgba(255,255,255,.72),rgba(232,213,166,.22) 46%,transparent 72%)!important;pointer-events:none!important;z-index:0!important;}
  .prayer-vessel-hero-copy h2{white-space:normal!important;max-width:15ch!important;}
  .prayer-vessel-hero-copy p{white-space:normal!important;}
  .prayer-vessel-answer-highlight{margin-top:12px!important;display:flex!important;flex-direction:column!important;gap:4px!important;max-width:31rem!important;padding:10px 12px!important;border-radius:16px!important;border:1px solid rgba(201,154,61,.28)!important;background:linear-gradient(135deg,rgba(255,248,223,.86),rgba(255,255,255,.62))!important;box-shadow:inset 0 1px 0 rgba(255,255,255,.8)!important;text-align:left!important;}
  .prayer-vessel-answer-highlight strong{color:#7a5717!important;font-size:13.5px!important;line-height:1.25!important;font-weight:900!important;}
  .prayer-vessel-answer-highlight span{color:#5a4319!important;font-size:12.5px!important;line-height:1.4!important;font-weight:700!important;}
  .prayer-vessel-scene{min-height:240px!important;align-self:stretch!important;}
  .prayer-vessel-scene::before{content:""!important;position:absolute!important;left:8%!important;right:8%!important;bottom:18px!important;height:44px!important;border-radius:50%!important;background:radial-gradient(ellipse at 50% 18%,rgba(255,255,255,.9),rgba(238,223,190,.72) 44%,rgba(174,137,69,.18) 74%,transparent 75%)!important;box-shadow:0 12px 26px rgba(91,67,28,.12),inset 0 1px 0 rgba(255,255,255,.92)!important;animation:prayerVesselPedestalGlow 5.5s ease-in-out infinite!important;z-index:1!important;}
  .prayer-vessel-cloud{position:absolute!important;display:block!important;border-radius:999px!important;background:rgba(255,255,255,.68)!important;box-shadow:22px 2px 0 rgba(255,255,255,.5),46px 8px 0 rgba(255,255,255,.36)!important;filter:blur(.2px)!important;z-index:0!important;pointer-events:none!important;animation:prayerVesselCloudDrift 8s ease-in-out infinite!important;}
  .pv-cloud-one{width:72px;height:18px;left:2%;top:28%;}
  .pv-cloud-two{width:82px;height:20px;right:8%;top:12%;animation-delay:-3s!important;}
  .prayer-vessel-star{position:absolute!important;z-index:3!important;color:#d5a23a!important;text-shadow:0 0 16px rgba(255,243,203,.94)!important;animation:prayerVesselStarWake 5.8s ease-in-out infinite!important;pointer-events:none!important;}
  .pv-star-one{left:18%;top:16%;animation-delay:-1.1s!important;}
  .pv-star-two{right:20%;top:31%;color:#a9c7f5!important;animation-delay:-2.2s!important;}
  .pv-star-three{left:62%;bottom:24%;color:#e7b65c!important;animation-delay:-3.3s!important;}
  .prayer-vessel-orb{z-index:4!important;}
  .prayer-vessel-pedestal{position:absolute!important;left:50%!important;bottom:18px!important;width:138px!important;height:34px!important;transform:translateX(-50%)!important;border-radius:50%!important;background:linear-gradient(90deg,rgba(139,95,28,.16),rgba(255,255,255,.78),rgba(139,95,28,.16))!important;box-shadow:0 12px 22px rgba(91,67,28,.10)!important;z-index:2!important;}
  .vessel-empty .prayer-vessel-fill{opacity:.34!important;height:var(--vessel-fill)!important;}
  .vessel-growing .prayer-vessel-fill{opacity:.78!important;}
  .vessel-full .prayer-vessel-fill,.vessel-radiant .prayer-vessel-fill{opacity:.92!important;box-shadow:0 -10px 22px rgba(255,255,255,.58),0 0 28px rgba(232,198,111,.72)!important;}
  .vessel-radiant .prayer-vessel-orb::before{background:radial-gradient(circle,rgba(255,248,216,.95),rgba(255,216,144,.38) 40%,rgba(226,190,255,.34) 62%,transparent 76%)!important;}
  .prayer-vessel-answer-spark{position:absolute!important;left:50%!important;top:36%!important;width:52px!important;height:52px!important;margin:-26px 0 0 -26px!important;border-radius:50%!important;background:radial-gradient(circle,rgba(255,255,255,.96),rgba(255,221,114,.68) 34%,transparent 68%)!important;opacity:0!important;pointer-events:none!important;}
  .has-answered-prayers .prayer-vessel-answer-spark{animation:prayerVesselAnsweredPulse 4.6s ease-in-out infinite!important;}
  .prayer-vessel-companion.watching{transform-origin:center bottom!important;right:-4px!important;bottom:32px!important;width:74px!important;animation:vesselCompanionFloat 5.2s ease-in-out infinite!important;}
  .pv-particle-four{left:48%;top:86%;--pv-x:10px;animation-delay:3.1s!important;color:#9ac6ec!important;}
  @media(max-width:680px){.prayer-vessel-hero{min-height:0!important;padding:18px 16px 20px!important;}.prayer-vessel-scene{min-height:220px!important;}.prayer-vessel-hero-copy h2{max-width:none!important;font-size:clamp(24px,6.2vw,30px)!important;line-height:1.08!important;}.prayer-vessel-answer-highlight{text-align:center!important;margin-left:auto!important;margin-right:auto!important;}.prayer-vessel-companion.watching{right:calc(50% - 116px)!important;bottom:32px!important;width:66px!important;}.prayer-vessel-pedestal{width:124px!important;bottom:20px!important;}}
  @media(prefers-reduced-motion:reduce){.prayer-vessel-hero,.prayer-vessel-star,.prayer-vessel-cloud,.prayer-vessel-scene::before,.prayer-vessel-answer-spark{animation:none!important;}}


  /* Final Home cloud polish: replace bar-like clouds with soft layered puffs. */
  .home-page .home-room .home-cloud{
    background:transparent!important;
    border-radius:0!important;
    filter:drop-shadow(0 10px 16px rgba(91,67,28,.055)) blur(.05px)!important;
    box-shadow:none!important;
    overflow:visible!important;
    opacity:.78!important;
  }
  .home-page .home-room .home-cloud::before,
  .home-page .home-room .home-cloud::after{
    content:""!important;
    position:absolute!important;
    pointer-events:none!important;
  }
  .home-page .home-room .home-cloud::before{
    left:0!important;
    right:0!important;
    bottom:0!important;
    height:100%!important;
    border-radius:999px!important;
    background:
      radial-gradient(circle at 17% 68%,rgba(255,255,255,.92) 0 16px,transparent 17px),
      radial-gradient(circle at 34% 48%,rgba(255,255,255,.96) 0 24px,transparent 25px),
      radial-gradient(circle at 55% 42%,rgba(255,255,255,.88) 0 29px,transparent 30px),
      radial-gradient(circle at 76% 62%,rgba(255,255,255,.88) 0 20px,transparent 21px),
      linear-gradient(180deg,rgba(255,255,255,.78),rgba(255,255,255,.48));
    box-shadow:inset 0 1px 0 rgba(255,255,255,.92),0 12px 28px rgba(255,255,255,.22)!important;
  }
  .home-page .home-room .home-cloud::after{
    left:18%!important;
    right:20%!important;
    bottom:18%!important;
    height:28%!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,transparent,rgba(255,248,223,.38),transparent)!important;
    opacity:.72!important;
  }
  .home-page .home-room .home-cloud.c1{width:178px!important;height:58px!important;left:-36px!important;top:66px!important;}
  .home-page .home-room .home-cloud.c2{width:224px!important;height:68px!important;right:-58px!important;top:86px!important;opacity:.72!important;}
  .home-page .home-room .home-cloud.c3{width:158px!important;height:52px!important;left:35%!important;top:38px!important;opacity:.54!important;transform:scale(.92)!important;}
  .home-page .home-room .home-cloud.c2::before{
    background:
      radial-gradient(circle at 14% 70%,rgba(255,255,255,.86) 0 18px,transparent 19px),
      radial-gradient(circle at 31% 48%,rgba(255,255,255,.94) 0 28px,transparent 29px),
      radial-gradient(circle at 52% 38%,rgba(255,255,255,.9) 0 34px,transparent 35px),
      radial-gradient(circle at 73% 54%,rgba(255,255,255,.86) 0 25px,transparent 26px),
      radial-gradient(circle at 90% 72%,rgba(255,255,255,.74) 0 16px,transparent 17px),
      linear-gradient(180deg,rgba(255,255,255,.72),rgba(255,255,255,.42));
  }
  .home-page .home-room .home-cloud.c3::before{
    background:
      radial-gradient(circle at 18% 70%,rgba(255,255,255,.74) 0 13px,transparent 14px),
      radial-gradient(circle at 40% 48%,rgba(255,255,255,.82) 0 21px,transparent 22px),
      radial-gradient(circle at 64% 54%,rgba(255,255,255,.76) 0 24px,transparent 25px),
      radial-gradient(circle at 84% 72%,rgba(255,255,255,.62) 0 13px,transparent 14px),
      linear-gradient(180deg,rgba(255,255,255,.58),rgba(255,255,255,.30));
  }
  .home-room.home-time-evening .home-cloud::before{filter:sepia(.1) saturate(.95) brightness(1.02)!important;}
  .home-room.home-time-night .home-cloud::before{opacity:.62!important;filter:saturate(.72) brightness(.86)!important;}
  @media(max-width:560px){
    .home-page .home-room .home-cloud.c1{width:150px!important;height:50px!important;left:-40px!important;top:62px!important;}
    .home-page .home-room .home-cloud.c2{width:186px!important;height:58px!important;right:-62px!important;top:86px!important;}
    .home-page .home-room .home-cloud.c3{width:132px!important;height:44px!important;left:34%!important;top:42px!important;}
  }


  /* Final Home scene cleanup: remove streaks/skyline and make every stage feel calmer. */
  .home-page .home-room{
    border-radius:24px!important;
    background:linear-gradient(180deg,#eef8ff 0%,#fff8df 50%,#eef8d7 100%)!important;
    isolation:isolate!important;
  }
  .home-page .home-room .home-rays,
  .home-page .home-room .home-sunbeam-drift,
  .home-page .home-room .home-distant-city,
  .home-page .home-room.home-stage-0 .home-distant-city,
  .home-page .home-room.home-stage-1 .home-distant-city,
  .home-page .home-room.home-stage-2 .home-distant-city,
  .home-page .home-room.home-stage-3 .home-distant-city,
  .home-page .home-room.home-stage-4 .home-distant-city,
  .home-page .home-room.home-stage-5 .home-distant-city,
  .home-page .home-room.home-stage-6 .home-distant-city{
    display:none!important;
    opacity:0!important;
    visibility:hidden!important;
  }
  .home-page .home-room .home-heavenly-effect.eff_glory_rays,
  .home-page .home-room .home-heavenly-effect.eff_glory_rays::before,
  .home-page .home-room .home-heavenly-effect.eff_glory_rays::after,
  .home-page .home-room .home-heavenly-effect.eff_glory_rays .effect-ray{
    display:none!important;
    opacity:0!important;
  }
  .home-page .home-room .home-sky{
    background:
      radial-gradient(circle at 50% 20%,rgba(255,255,255,.78),transparent 26%),
      radial-gradient(circle at 18% 28%,rgba(219,234,254,.52),transparent 30%),
      radial-gradient(circle at 82% 32%,rgba(255,248,223,.58),transparent 28%),
      linear-gradient(180deg,#dff3ff 0%,#fff8df 72%,#fffdf6 100%)!important;
    filter:none!important;
  }
  .home-page .home-room .home-sky::after{
    content:""!important;
    position:absolute!important;
    left:7%!important;
    right:7%!important;
    bottom:-16px!important;
    height:86px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse at 50% 50%,rgba(255,255,255,.60),rgba(255,248,223,.24) 44%,transparent 72%)!important;
    pointer-events:none!important;
    z-index:1!important;
  }
  .home-page .home-room .home-midground-glow{
    left:10%!important;
    right:10%!important;
    bottom:31%!important;
    height:108px!important;
    opacity:.58!important;
    background:
      radial-gradient(ellipse at 50% 54%,rgba(255,255,255,.72),rgba(255,248,223,.38) 36%,rgba(187,247,208,.15) 58%,transparent 75%)!important;
    filter:blur(.3px)!important;
  }
  .home-page .home-room .home-floor{
    background:
      linear-gradient(180deg,rgba(255,253,246,.82) 0%,rgba(230,247,207,.84) 38%,rgba(190,220,144,.70) 100%)!important;
    border-top:0!important;
  }
  .home-page .home-room .home-path{
    top:50%!important;
    bottom:auto!important;
    height:52%!important;
    width:min(42%,174px)!important;
    border:0!important;
    background:
      linear-gradient(180deg,rgba(255,255,255,.68),rgba(244,214,122,.58) 52%,rgba(198,146,40,.44))!important;
    box-shadow:inset 0 0 22px rgba(255,255,255,.56),0 10px 18px rgba(82,55,15,.06)!important;
    opacity:.82!important;
  }
  .home-page .home-room .home-path::after{
    background:linear-gradient(180deg,rgba(138,90,8,.10),transparent 1px) 0 0/100% 28px!important;
    opacity:.20!important;
  }
  .home-page .home-room .home-cardboard,
  .home-page .home-room .home-little-shelter,
  .home-page .home-room .home-house,
  .home-page .home-room .home-mansion,
  .home-page .home-room .home-palace{
    z-index:8!important;
    filter:drop-shadow(0 19px 23px rgba(74,48,12,.18)) drop-shadow(0 0 24px rgba(255,248,223,.52))!important;
  }
  .home-page .home-room .home-cardboard::before,
  .home-page .home-room .home-little-shelter::before,
  .home-page .home-room .home-house::before,
  .home-page .home-room .home-mansion::before,
  .home-page .home-room .home-palace::before{
    content:""!important;
    position:absolute!important;
    left:50%!important;
    top:45%!important;
    width:132%!important;
    height:88%!important;
    transform:translate(-50%,-50%)!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse,rgba(255,255,255,.42),rgba(255,248,223,.20) 46%,transparent 72%)!important;
    z-index:-2!important;
    pointer-events:none!important;
  }
  .home-page .home-room .home-cardboard-roof,
  .home-page .home-room .home-little-shelter-roof,
  .home-page .home-room .home-house-roof,
  .home-page .home-room .home-mansion-roof,
  .home-page .home-room .home-palace-roof{
    background:
      linear-gradient(90deg,rgba(255,255,255,.20),transparent 22%,transparent 78%,rgba(95,59,8,.10)),
      repeating-linear-gradient(135deg,rgba(95,59,8,.10) 0 4px,transparent 4px 14px),
      linear-gradient(180deg,#fff4c6 0%,#dfad3f 54%,#9a6412 100%)!important;
    box-shadow:inset 0 9px 13px rgba(255,255,255,.62),inset 0 -10px 14px rgba(95,59,8,.13),0 8px 14px rgba(82,55,15,.10)!important;
  }
  .home-page .home-room .home-cardboard-body,
  .home-page .home-room .home-little-shelter-body,
  .home-page .home-room .home-house-body,
  .home-page .home-room .home-mansion-body,
  .home-page .home-room .home-palace-body{
    background:
      radial-gradient(circle at 24% 16%,rgba(255,255,255,.78),transparent 22%),
      linear-gradient(90deg,rgba(95,59,8,.08),transparent 16%,transparent 84%,rgba(95,59,8,.10)),
      linear-gradient(180deg,#fffdf8 0%,#fff0cb 50%,#d8a446 100%)!important;
    box-shadow:inset 0 12px 18px rgba(255,255,255,.68),inset 0 -13px 18px rgba(95,59,8,.12),0 12px 18px rgba(74,48,12,.10)!important;
  }
  .home-page .home-room.home-stage-2 .home-house,
  .home-page .home-room.home-stage-3 .home-mansion,
  .home-page .home-room.home-stage-4 .home-palace,
  .home-page .home-room.home-stage-5 .home-palace,
  .home-page .home-room.home-stage-6 .home-palace{
    top:29%!important;
  }
  .home-page .home-room.home-stage-4 .home-palace,
  .home-page .home-room.home-stage-5 .home-palace,
  .home-page .home-room.home-stage-6 .home-palace{
    top:20%!important;
  }
  .home-page .home-room .home-flowerbed{
    bottom:1%!important;
    opacity:.74!important;
    box-shadow:inset 0 10px 16px rgba(255,255,255,.22),0 12px 20px rgba(82,55,15,.08)!important;
  }
  .home-page .home-room .home-signature-vessel,
  .home-page .home-room .home-prayer-light-trail,
  .home-page .home-room .home-word-scroll{
    opacity:.32!important;
    filter:saturate(.85) brightness(1.04)!important;
  }
  .home-page .home-room .home-word-scroll{
    top:23%!important;
    z-index:2!important;
  }
  .home-page .home-room .home-background-rainbow,
  .home-page .home-room .home-heavenly-effect.eff_covenant_rainbow .effect-rainbow{
    opacity:.38!important;
    filter:saturate(.80) brightness(1.06) blur(.2px)!important;
  }
  .home-page .home-room .home-spirit-slot{
    z-index:45!important;
  }
  @media(max-width:560px){
    .home-page .home-room .home-midground-glow{bottom:30%!important;height:96px!important;}
    .home-page .home-room .home-path{top:51%!important;width:min(40%,152px)!important;}
    .home-page .home-room.home-stage-2 .home-house,
    .home-page .home-room.home-stage-3 .home-mansion{top:31%!important;}
    .home-page .home-room.home-stage-4 .home-palace,
    .home-page .home-room.home-stage-5 .home-palace,
    .home-page .home-room.home-stage-6 .home-palace{top:22%!important;}
  }







  /* Home realism hard pass: cleaner background, real-home silhouettes, no overlapping streaks. */
  .home-page .home-room{
    overflow:hidden!important;
    isolation:isolate!important;
    border-radius:24px!important;
    background:linear-gradient(180deg,#eaf7ff 0%,#fffaf0 48%,#e7f2cf 100%)!important;
  }
  .home-page .home-room .home-rays,
  .home-page .home-room .home-sunbeam-drift,
  .home-page .home-room .home-distant-city,
  .home-page .home-room .home-heavenly-veil,
  .home-page .home-room .home-realm-planet,
  .home-page .home-room .home-realm-country,
  .home-page .home-room .home-word-scroll,
  .home-page .home-room .home-prayer-light-trail,
  .home-page .home-room .home-signature-vessel,
  .home-page .home-room .home-gem,
  .home-page .home-room .home-path-gem,
  .home-page .home-room .home-walk-blessing,
  .home-page .home-room .home-prayer-glow,
  .home-page .home-room .home-answer-light,
  .home-page .home-room .home-answer-dove,
  .home-page .home-room .home-testimony-particles,
  .home-page .home-room .home-background-rainbow,
  .home-page .home-room .home-heavenly-effect.eff_glory_rays,
  .home-page .home-room .home-heavenly-effect.eff_covenant_rainbow,
  .home-page .home-room .home-heavenly-effect.eff_covenant_rainbow .effect-rainbow{
    display:none!important;
    opacity:0!important;
    visibility:hidden!important;
  }
  .home-page .home-room .home-sky{
    inset:0 0 38%!important;
    background:
      radial-gradient(circle at 50% 15%,rgba(255,255,255,.92) 0 14%,transparent 28%),
      radial-gradient(circle at 18% 32%,rgba(219,234,254,.62),transparent 32%),
      radial-gradient(circle at 84% 36%,rgba(255,248,223,.62),transparent 32%),
      linear-gradient(180deg,#dff2ff 0%,#f7fbff 48%,#fff8df 100%)!important;
    filter:none!important;
  }
  .home-page .home-room .home-sky::after{
    content:""!important;
    position:absolute!important;
    left:8%!important;
    right:8%!important;
    bottom:-22px!important;
    height:94px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse at 50% 50%,rgba(255,255,255,.72),rgba(255,248,223,.22) 46%,transparent 74%)!important;
    pointer-events:none!important;
    z-index:1!important;
  }
  .home-page .home-room .home-sky-bird{
    opacity:.18!important;
    z-index:2!important;
  }
  .home-page .home-room .home-cloud{
    background:transparent!important;
    box-shadow:none!important;
    filter:none!important;
    opacity:.76!important;
    overflow:visible!important;
    z-index:2!important;
  }
  .home-page .home-room .home-cloud::before,
  .home-page .home-room .home-cloud::after{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    border-radius:999px!important;
    background:
      radial-gradient(circle at 18% 70%,rgba(255,255,255,.70) 0 13px,transparent 14px),
      radial-gradient(circle at 37% 48%,rgba(255,255,255,.92) 0 23px,transparent 24px),
      radial-gradient(circle at 62% 52%,rgba(255,255,255,.88) 0 27px,transparent 28px),
      radial-gradient(circle at 84% 70%,rgba(255,255,255,.70) 0 15px,transparent 16px),
      linear-gradient(180deg,rgba(255,255,255,.58),rgba(255,255,255,.26));
    box-shadow:0 10px 22px rgba(125,151,171,.08)!important;
  }
  .home-page .home-room .home-cloud::after{
    transform:translateY(11px) scaleX(.86)!important;
    opacity:.42!important;
    filter:blur(7px)!important;
  }
  .home-page .home-room .home-cloud.c1{width:158px!important;height:56px!important;left:-42px!important;top:72px!important;}
  .home-page .home-room .home-cloud.c2{width:194px!important;height:62px!important;right:-58px!important;top:92px!important;}
  .home-page .home-room .home-cloud.c3{width:142px!important;height:50px!important;left:35%!important;top:44px!important;opacity:.54!important;}
  .home-page .home-room .home-floor{
    height:45%!important;
    background:
      radial-gradient(circle at 17% 82%,rgba(255,255,255,.32) 0 6px,transparent 7px),
      radial-gradient(circle at 83% 82%,rgba(255,255,255,.32) 0 6px,transparent 7px),
      linear-gradient(180deg,rgba(255,253,246,.88) 0%,rgba(226,246,204,.92) 40%,rgba(166,203,116,.70) 100%)!important;
    border-top:1px solid rgba(123,154,97,.18)!important;
  }
  .home-page .home-room .home-midground-glow{
    left:16%!important;
    right:16%!important;
    bottom:32%!important;
    height:82px!important;
    opacity:.42!important;
    background:radial-gradient(ellipse at 50% 55%,rgba(255,255,255,.66),rgba(255,248,223,.22) 45%,transparent 75%)!important;
    filter:blur(.2px)!important;
    z-index:3!important;
  }
  .home-page .home-room .home-path{
    top:53%!important;
    bottom:auto!important;
    height:48%!important;
    width:min(37%,150px)!important;
    opacity:.72!important;
    z-index:4!important;
    background:linear-gradient(180deg,rgba(255,255,255,.78),rgba(242,216,130,.50) 52%,rgba(184,136,45,.32))!important;
    border:0!important;
    box-shadow:inset 0 0 18px rgba(255,255,255,.48),0 10px 16px rgba(82,55,15,.05)!important;
  }
  .home-page .home-room .home-path::after{
    background:repeating-linear-gradient(180deg,transparent 0 31px,rgba(100,80,42,.10) 32px 33px)!important;
    opacity:.22!important;
  }
  .home-page .home-room .home-cardboard,
  .home-page .home-room .home-little-shelter,
  .home-page .home-room .home-house,
  .home-page .home-room .home-mansion,
  .home-page .home-room .home-palace{
    z-index:24!important;
    filter:drop-shadow(0 21px 22px rgba(74,48,12,.20)) drop-shadow(0 2px 0 rgba(255,255,255,.35))!important;
  }
  .home-page .home-room .home-cardboard::before,
  .home-page .home-room .home-little-shelter::before,
  .home-page .home-room .home-house::before,
  .home-page .home-room .home-mansion::before,
  .home-page .home-room .home-palace::before{
    display:none!important;
    content:none!important;
  }
  .home-page .home-room .home-cardboard::after,
  .home-page .home-room .home-little-shelter::after,
  .home-page .home-room .home-house::after,
  .home-page .home-room .home-mansion::after,
  .home-page .home-room .home-palace::after{
    left:10%!important;
    right:10%!important;
    bottom:-11px!important;
    height:18px!important;
    background:radial-gradient(ellipse,rgba(74,48,12,.20),rgba(74,48,12,0) 72%)!important;
    z-index:-1!important;
  }
  .home-page .home-room .home-cardboard-roof,
  .home-page .home-room .home-little-shelter-roof,
  .home-page .home-room .home-house-roof,
  .home-page .home-room .home-mansion-roof,
  .home-page .home-room .home-palace-roof{
    border:1px solid rgba(126,83,15,.22)!important;
    background:
      linear-gradient(90deg,rgba(255,255,255,.24),transparent 22%,transparent 78%,rgba(77,48,9,.12)),
      repeating-linear-gradient(135deg,rgba(95,59,8,.12) 0 3px,transparent 3px 13px),
      linear-gradient(180deg,#fff2bd 0%,#d9a035 58%,#8f5c12 100%)!important;
    box-shadow:inset 0 10px 14px rgba(255,255,255,.58),inset 0 -10px 12px rgba(77,48,9,.14),0 7px 12px rgba(74,48,12,.12)!important;
  }
  .home-page .home-room .home-cardboard-body,
  .home-page .home-room .home-little-shelter-body,
  .home-page .home-room .home-house-body,
  .home-page .home-room .home-mansion-body,
  .home-page .home-room .home-palace-body{
    border:1.5px solid rgba(126,83,15,.26)!important;
    background:
      radial-gradient(circle at 24% 14%,rgba(255,255,255,.72),transparent 23%),
      linear-gradient(90deg,rgba(77,48,9,.07),transparent 16%,transparent 84%,rgba(77,48,9,.09)),
      linear-gradient(180deg,#fffdf8 0%,#fff0cb 52%,#d6a143 100%)!important;
    box-shadow:inset 0 12px 18px rgba(255,255,255,.70),inset 0 -14px 18px rgba(77,48,9,.13),0 12px 18px rgba(74,48,12,.10)!important;
  }
  .home-page .home-room .home-cardboard-body::before,
  .home-page .home-room .home-little-shelter-body::before,
  .home-page .home-room .home-house-body::before,
  .home-page .home-room .home-mansion-body::before,
  .home-page .home-room .home-palace-body::before{
    content:""!important;
    position:absolute!important;
    left:13%!important;
    right:13%!important;
    top:15px!important;
    height:1px!important;
    border-radius:999px!important;
    background:rgba(126,83,15,.16)!important;
    box-shadow:0 18px 0 rgba(126,83,15,.10),0 36px 0 rgba(255,255,255,.22)!important;
  }
  .home-page .home-room .home-cardboard{top:38%!important;width:158px!important;height:106px!important;}
  .home-page .home-room .home-cardboard-roof{height:48px!important;clip-path:polygon(50% 0,100% 100%,0 100%)!important;}
  .home-page .home-room .home-cardboard-body{left:20px!important;right:20px!important;height:70px!important;border-radius:14px 14px 7px 7px!important;background:linear-gradient(180deg,#f1d193,#bd813b 100%)!important;}
  .home-page .home-room .home-cardboard-door{left:50%!important;transform:translateX(-50%)!important;width:34px!important;height:48px!important;border-radius:16px 16px 4px 4px!important;background:linear-gradient(180deg,#fff0cb,#b9803a)!important;border:1.5px solid rgba(93,60,15,.24)!important;}
  .home-page .home-room .home-little-shelter{top:33%!important;width:188px!important;height:132px!important;}
  .home-page .home-room .home-little-shelter-roof{height:66px!important;clip-path:polygon(50% 0,100% 100%,0 100%)!important;}
  .home-page .home-room .home-little-shelter-roof::before,
  .home-page .home-room .home-house-roof::before{
    content:""!important;
    position:absolute!important;
    right:24%!important;
    top:18%!important;
    width:16px!important;
    height:28px!important;
    border-radius:4px 4px 0 0!important;
    background:linear-gradient(180deg,#fff4c6,#b77b1f)!important;
    border:1px solid rgba(93,60,15,.20)!important;
  }
  .home-page .home-room .home-little-shelter-body{left:24px!important;right:24px!important;height:90px!important;border-radius:22px 22px 10px 10px!important;}
  .home-page .home-room .home-little-shelter-door{left:50%!important;transform:translateX(-50%)!important;width:40px!important;height:58px!important;}
  .home-page .home-room .home-little-shelter-window{top:73px!important;width:20px!important;height:25px!important;background:linear-gradient(180deg,#fffdf8,#ffe39a)!important;}
  .home-page .home-room .home-little-shelter-window.left{left:42px!important;}
  .home-page .home-room .home-little-shelter-window.right{right:42px!important;}
  .home-page .home-room .home-house{top:28%!important;width:224px!important;height:158px!important;}
  .home-page .home-room .home-house-roof{left:0!important;right:0!important;top:0!important;height:78px!important;clip-path:polygon(50% 0,100% 100%,0 100%)!important;}
  .home-page .home-room .home-house-body{left:32px!important;right:32px!important;height:110px!important;border-radius:24px 24px 10px 10px!important;}
  .home-page .home-room .home-house-door{left:50%!important;transform:translateX(-50%)!important;width:46px!important;height:66px!important;}
  .home-page .home-room .home-window-dot{
    top:88px!important;
    width:30px!important;
    height:35px!important;
    border-radius:12px!important;
    background:linear-gradient(180deg,#fffdf8,#ffe39a)!important;
    border:1.5px solid rgba(126,83,15,.22)!important;
    box-shadow:inset 0 0 8px rgba(255,255,255,.76)!important;
  }
  .home-page .home-room .home-window-dot::before{
    content:""!important;
    position:absolute!important;
    left:50%!important;
    top:4px!important;
    bottom:4px!important;
    width:1px!important;
    background:rgba(126,83,15,.16)!important;
  }
  .home-page .home-room .home-window-dot::after{
    content:""!important;
    position:absolute!important;
    left:5px!important;
    right:5px!important;
    top:50%!important;
    height:1px!important;
    background:rgba(126,83,15,.16)!important;
  }
  .home-page .home-room .home-window-dot.left{left:54px!important;}
  .home-page .home-room .home-window-dot.right{right:54px!important;}
  .home-page .home-room .home-mansion{top:22%!important;width:254px!important;height:184px!important;}
  .home-page .home-room .home-mansion-roof{left:4px!important;right:4px!important;top:0!important;height:74px!important;clip-path:polygon(6% 100%,20% 36%,34% 100%,50% 12%,66% 100%,80% 36%,94% 100%)!important;}
  .home-page .home-room .home-mansion-body{left:24px!important;right:24px!important;height:126px!important;border-radius:22px 22px 10px 10px!important;}
  .home-page .home-room .home-mansion-column{bottom:10px!important;width:12px!important;height:76px!important;background:linear-gradient(90deg,#c99a38,#fff8df 50%,#aa741f)!important;z-index:27!important;}
  .home-page .home-room .home-mansion-column.c1{left:84px!important;}
  .home-page .home-room .home-mansion-column.c2{right:84px!important;}
  .home-page .home-room .home-mansion-door{left:50%!important;transform:translateX(-50%)!important;width:44px!important;height:70px!important;}
  .home-page .home-room .home-mansion-window{top:78px!important;width:28px!important;height:35px!important;background:linear-gradient(180deg,#fffdf8,#ffe39a)!important;}
  .home-page .home-room .home-mansion-window.left{left:54px!important;}
  .home-page .home-room .home-mansion-window.right{right:54px!important;}
  .home-page .home-room .home-palace{top:17%!important;width:268px!important;height:204px!important;}
  .home-page .home-room .home-palace-roof{left:38px!important;right:38px!important;top:16px!important;height:70px!important;clip-path:polygon(50% 0,96% 100%,4% 100%)!important;}
  .home-page .home-room .home-palace-body{left:54px!important;right:54px!important;height:132px!important;border-radius:28px 28px 12px 12px!important;}
  .home-page .home-room .home-tower{
    bottom:0!important;
    width:48px!important;
    height:150px!important;
    border-radius:20px 20px 10px 10px!important;
    border:1.5px solid rgba(126,83,15,.24)!important;
    background:linear-gradient(180deg,#fffdf8,#fff0cb 54%,#d6a143 100%)!important;
    box-shadow:inset 0 10px 14px rgba(255,255,255,.58),inset 0 -12px 14px rgba(77,48,9,.11)!important;
  }
  .home-page .home-room .home-tower.left{left:2px!important;}
  .home-page .home-room .home-tower.right{right:2px!important;}
  .home-page .home-room .home-tower::before{
    left:7px!important;
    top:-24px!important;
    width:32px!important;
    height:36px!important;
    border-radius:18px 18px 6px 6px!important;
    background:linear-gradient(180deg,#fff2bd,#d9a035 72%,#8f5c12)!important;
  }
  .home-page .home-room .home-door{left:50%!important;transform:translateX(-50%)!important;width:44px!important;height:72px!important;}
  .home-page .home-room .home-palace-star{top:-14px!important;font-size:18px!important;color:#8a5a08!important;}
  .home-page .home-room .home-flowerbed{
    bottom:1%!important;
    width:28%!important;
    height:28%!important;
    opacity:.80!important;
    background:
      radial-gradient(circle at 24% 70%,#fff 0 4px,transparent 5px),
      radial-gradient(circle at 41% 61%,#f9a8d4 0 4px,transparent 5px),
      radial-gradient(circle at 58% 73%,#fff8df 0 4px,transparent 5px),
      radial-gradient(circle at 72% 61%,#bae6fd 0 4px,transparent 5px),
      linear-gradient(180deg,#a8d36f,#638d38)!important;
    box-shadow:inset 0 13px 18px rgba(255,255,255,.24),0 12px 18px rgba(54,80,28,.10)!important;
  }
  .home-page .home-room .home-gate,
  .home-page .home-room .home-lantern{
    opacity:.40!important;
    z-index:7!important;
  }
  .home-page .home-room .home-slot:not(.home-spirit-slot){
    z-index:30!important;
  }
  .home-page .home-room .home-spirit-slot{
    z-index:46!important;
  }
  @media(max-width:560px){
    .home-page .home-room .home-cloud.c1{width:142px!important;height:52px!important;left:-48px!important;top:66px!important;}
    .home-page .home-room .home-cloud.c2{width:174px!important;height:58px!important;right:-66px!important;top:88px!important;}
    .home-page .home-room .home-cloud.c3{width:124px!important;height:46px!important;left:34%!important;top:42px!important;}
    .home-page .home-room .home-cardboard{top:40%!important;width:148px!important;}
    .home-page .home-room .home-little-shelter{top:35%!important;width:174px!important;}
    .home-page .home-room .home-house{top:30%!important;width:204px!important;}
    .home-page .home-room .home-mansion{top:25%!important;width:230px!important;}
    .home-page .home-room .home-palace{top:21%!important;width:244px!important;}
    .home-page .home-room .home-palace-body{left:48px!important;right:48px!important;}
    .home-page .home-room .home-tower{width:44px!important;}
    .home-page .home-room .home-path{top:54%!important;width:min(35%,136px)!important;}
  }


  /* Friends page premium calm pass. */
  .app-shell .spirit-friends-dashboard{
    gap:14px!important;
  }
  .app-shell .spirit-friends-dashboard::before,
  .app-shell .spirit-friends-dashboard::after{
    opacity:.34!important;
  }
  .app-shell .spirit-friends-dashboard > .sacred-card{
    border:1px solid rgba(231,207,159,.20)!important;
    border-radius:30px!important;
    background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(250,252,247,.76))!important;
    box-shadow:0 14px 30px rgba(67,82,100,.055),inset 0 1px 0 rgba(255,255,255,.84)!important;
  }
  .app-shell .spirit-friends-hero-card{
    padding:20px!important;
    border-color:rgba(125,174,212,.20)!important;
    background:
      radial-gradient(circle at 86% 20%,rgba(255,244,202,.55),transparent 28%),
      radial-gradient(circle at 10% 10%,rgba(236,253,245,.48),transparent 34%),
      linear-gradient(145deg,rgba(255,253,246,.98),rgba(246,250,255,.58))!important;
  }
  .app-shell .spirit-friends-hero-card .section-divider-label,
  .app-shell .spirit-friends-hero-card p,
  .app-shell .spirit-friends-dashboard .prayer-friend-copy{
    color:#7a674e!important;
  }
  .app-shell .friends-hero-summary{
    border:0!important;
    background:rgba(255,255,255,.56)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.78)!important;
    padding:11px 12px!important;
  }
  .app-shell .friends-hero-summary > span{
    min-width:0!important;
    overflow-wrap:anywhere!important;
  }
  .app-shell .prayer-circle-tool-grid{
    max-width:none!important;
    gap:9px!important;
  }
  .app-shell .prayer-circle-tab{
    min-height:50px!important;
    border-radius:18px!important;
    border:1px solid rgba(231,207,159,.26)!important;
    background:rgba(255,255,255,.62)!important;
    color:#3a2a18!important;
    font-weight:820!important;
  }
  .app-shell .prayer-circle-tab.active{
    background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(236,253,245,.66))!important;
    border-color:rgba(125,174,212,.26)!important;
    box-shadow:0 8px 18px rgba(67,82,100,.055)!important;
  }
  .app-shell .spirit-friends-tools-card{
    padding:14px!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel{
    padding:6px 0 0!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .prayer-friend-panel-title{
    margin:0 0 10px!important;
    padding:0!important;
    font-size:12px!important;
    letter-spacing:.16em!important;
    color:#8a714f!important;
  }
  .app-shell .friend-prayer-prompt{
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto!important;
    align-items:center!important;
    gap:12px!important;
    margin:0 0 12px!important;
    padding:13px 14px!important;
    border:1px solid rgba(125,174,212,.18)!important;
    border-radius:22px!important;
    background:linear-gradient(145deg,rgba(246,250,255,.76),rgba(236,253,245,.52))!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.78)!important;
  }
  .app-shell .friend-prayer-prompt-kicker{
    display:block!important;
    margin-bottom:3px!important;
    font-size:11px!important;
    letter-spacing:.13em!important;
    text-transform:uppercase!important;
    color:#8a714f!important;
    font-weight:900!important;
  }
  .app-shell .friend-prayer-prompt p{
    margin:0!important;
    color:#3a2a18!important;
    font-size:13.5px!important;
    line-height:1.42!important;
    font-weight:680!important;
  }
  .app-shell .friend-prayer-prompt > span:last-child{
    width:38px!important;
    height:38px!important;
    border-radius:50%!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    background:rgba(255,255,255,.68)!important;
    box-shadow:0 6px 14px rgba(67,82,100,.06)!important;
    font-size:19px!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .scroll-list.compact{
    max-height:min(56vh,560px)!important;
    padding:0!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-row{
    min-height:76px!important;
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto!important;
    align-items:center!important;
    gap:10px!important;
    padding:10px 2px!important;
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-row::after{
    left:62px!important;
    right:2px!important;
    background:rgba(46,38,25,.065)!important;
  }
  .app-shell .instagram-friend-identity{
    display:grid!important;
    grid-template-columns:48px minmax(0,1fr)!important;
    gap:12px!important;
    min-width:0!important;
    align-items:center!important;
  }
  .app-shell .friend-spirit-avatar{
    width:48px!important;
    height:48px!important;
    border-radius:50%!important;
    background:linear-gradient(145deg,rgba(236,253,245,.88),rgba(255,248,223,.62))!important;
    box-shadow:0 8px 18px rgba(67,82,100,.08)!important;
  }
  .app-shell .friend-spirit-avatar img{
    width:100%!important;
    height:100%!important;
    object-fit:cover!important;
  }
  .app-shell .instagram-friend-copy .prayer-friend-name,
  .app-shell .spirit-friends-dashboard .prayer-friend-name{
    color:#332719!important;
    font-family:'Nunito',sans-serif!important;
    font-size:15.5px!important;
    font-weight:760!important;
    letter-spacing:0!important;
    line-height:1.24!important;
    white-space:normal!important;
    overflow:visible!important;
    text-overflow:clip!important;
  }
  .app-shell .instagram-friend-subtitle{
    margin-top:3px!important;
    color:#8a714f!important;
    font-size:12px!important;
    font-weight:760!important;
    line-height:1.25!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions{
    display:flex!important;
    align-items:center!important;
    gap:8px!important;
    flex-shrink:0!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions .soft-action-btn:not(.remove-friend-btn){
    min-width:94px!important;
    min-height:38px!important;
    border:0!important;
    border-radius:14px!important;
    background:#eef1f4!important;
    color:#2f281d!important;
    font-size:13px!important;
    font-weight:820!important;
    box-shadow:none!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions .soft-action-btn.friend-alert-btn:not(.remove-friend-btn){
    background:linear-gradient(135deg,#fff4c8,#d6a036)!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .remove-friend-btn{
    width:36px!important;
    min-width:36px!important;
    height:36px!important;
    border:0!important;
    background:transparent!important;
    color:rgba(58,42,24,.42)!important;
    font-size:25px!important;
    box-shadow:none!important;
  }
  .app-shell .community-feed-card{
    border-color:rgba(231,207,159,.18)!important;
  }
  .app-shell .community-feed-card.blessings-new{
    background:linear-gradient(145deg,rgba(255,248,223,.98),rgba(255,241,184,.62))!important;
    border-color:rgba(201,144,18,.26)!important;
  }
  .app-shell .new-blessing-shortcut,
  .app-shell .community-feed-card .friend-alert-btn{
    background:linear-gradient(135deg,#fff4c8,#d8a236)!important;
    color:#2f220d!important;
  }
  .app-shell .accountability-primary-card{
    border-color:rgba(188,222,161,.24)!important;
  }
  .app-shell .accountability-primary-card .partner-ask-toggle{
    border-radius:16px!important;
    background:rgba(255,255,255,.64)!important;
  }
  @media(max-width:430px){
    .app-shell .friends-hero-summary{
      flex-direction:column!important;
      align-items:stretch!important;
    }
    .app-shell .new-blessing-shortcut{width:100%!important;justify-content:center!important;}
    .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-row{
      grid-template-columns:minmax(0,1fr) auto!important;
      gap:7px!important;
    }
    .app-shell .instagram-friend-identity{
      grid-template-columns:44px minmax(0,1fr)!important;
      gap:10px!important;
    }
    .app-shell .friend-spirit-avatar{width:44px!important;height:44px!important;}
    .app-shell .instagram-friend-copy .prayer-friend-name,
    .app-shell .spirit-friends-dashboard .prayer-friend-name{font-size:14.5px!important;}
    .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions{gap:5px!important;}
    .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions .soft-action-btn:not(.remove-friend-btn){min-width:78px!important;font-size:12.2px!important;padding:7px 9px!important;}
    .app-shell .spirit-friends-dashboard .friends-garden-panel .remove-friend-btn{width:30px!important;min-width:30px!important;}
  }



  /* Friends page main action accent: one solid gold line. */
  .app-shell .spirit-friends-dashboard .friends-garden-panel{
    position:relative!important;
    padding-top:18px!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel::before{
    content:""!important;
    position:absolute!important;
    left:2px!important;
    right:2px!important;
    top:0!important;
    height:3px!important;
    border-radius:999px!important;
    background:#C99012!important;
    box-shadow:0 5px 14px rgba(201,144,18,.16)!important;
  }



  /* Friends list height: show three friends, then scroll. */
  .app-shell .spirit-friends-dashboard .friends-garden-panel .scroll-list.compact{
    max-height:252px!important;
    overflow-y:auto!important;
    overscroll-behavior:contain!important;
  }
  @media(max-width:430px){
    .app-shell .spirit-friends-dashboard .friends-garden-panel .scroll-list.compact{
      max-height:238px!important;
    }
  }


  /* Friends page second calm pass: collapse secondary tools. */
  .app-shell .blessing-card-head{
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto!important;
    gap:12px!important;
    align-items:center!important;
  }
  .app-shell .blessing-tools-body{
    margin-top:12px!important;
    padding-top:12px!important;
    border-top:1px solid rgba(231,207,159,.20)!important;
  }
  .app-shell .blessing-community-card{
    background:linear-gradient(145deg,rgba(255,253,246,.96),rgba(255,248,253,.32))!important;
  }
  .app-shell .blessing-community-card .blessing-options-grid{
    grid-template-columns:repeat(auto-fit,minmax(118px,1fr))!important;
    gap:8px!important;
  }
  .app-shell .blessing-community-card .blessing-option-btn{
    min-height:50px!important;
    border-radius:16px!important;
  }
  .app-shell .accountability-primary-card{order:4!important;}
  .app-shell .blessing-community-card{order:5!important;}
  @media(max-width:430px){
    .app-shell .blessing-card-head{grid-template-columns:1fr auto!important;gap:9px!important;}
    .app-shell .blessing-community-card .blessing-options-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
  }







  /* Final Friends page premium refinement: calmer hierarchy, spacious rows, lighter controls. */
  .app-shell .spirit-friends-dashboard{
    gap:22px!important;
    padding-bottom:24px!important;
  }
  .app-shell .spirit-friends-dashboard::before,
  .app-shell .spirit-friends-dashboard::after{
    opacity:.18!important;
  }
  .app-shell .spirit-friends-dashboard > .sacred-card{
    border:1px solid rgba(214,185,127,.18)!important;
    border-radius:30px!important;
    background:linear-gradient(145deg,rgba(255,253,248,.98),rgba(255,250,239,.84))!important;
    box-shadow:0 14px 32px rgba(84,62,30,.05),inset 0 1px 0 rgba(255,255,255,.86)!important;
    padding:24px!important;
  }
  .app-shell .spirit-friends-dashboard .section-label,
  .app-shell .spirit-friends-dashboard .prayer-friend-panel-title{
    color:#9B835C!important;
    font-size:10.5px!important;
    font-weight:650!important;
    letter-spacing:.16em!important;
    line-height:1.2!important;
  }
  .app-shell .spirit-friends-dashboard h2,
  .app-shell .spirit-friends-dashboard h3{
    color:#342313!important;
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-weight:500!important;
    letter-spacing:0!important;
    line-height:1.02!important;
  }
  .app-shell .spirit-friends-dashboard p,
  .app-shell .spirit-friends-dashboard .prayer-friend-copy{
    color:#6F5C43!important;
    font-size:13.5px!important;
    line-height:1.5!important;
    font-weight:400!important;
  }

  .app-shell .spirit-friends-hero-card{
    padding:26px 24px!important;
    border:0!important;
    background:
      radial-gradient(circle at 87% 18%,rgba(255,239,184,.48),transparent 29%),
      radial-gradient(circle at 10% 12%,rgba(236,253,245,.50),transparent 32%),
      linear-gradient(145deg,#FFFDF8,#FFF9EA 58%,#F8FBFF)!important;
    box-shadow:0 18px 40px rgba(84,62,30,.065),inset 0 1px 0 rgba(255,255,255,.88)!important;
  }
  .app-shell .spirit-friends-hero-card::before,
  .app-shell .spirit-friends-hero-card::after{
    opacity:.42!important;
  }
  .app-shell .spirit-friends-hero-card h2{
    margin:5px 0 8px!important;
    font-size:clamp(32px,7.8vw,46px)!important;
  }
  .app-shell .spirit-friends-hero-card .prayer-circle-subtitle{
    max-width:32ch!important;
    margin:0 0 18px!important;
    font-size:14.5px!important;
  }
  .app-shell .friends-hero-summary{
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:14px!important;
    margin-top:0!important;
    padding:14px 16px!important;
    border:0!important;
    border-radius:22px!important;
    background:rgba(255,255,255,.58)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.82)!important;
  }
  .app-shell .friends-hero-summary > span{
    color:#4A3212!important;
    font-size:14.5px!important;
    line-height:1.35!important;
    font-weight:650!important;
  }
  .app-shell .spirit-friends-hero-card [style*="borderTop"]{
    margin-top:18px!important;
    padding-top:18px!important;
    border-top:1px solid rgba(214,185,127,.18)!important;
  }
  .app-shell .prayer-circle-tool-grid{
    max-width:none!important;
    gap:12px!important;
  }
  .app-shell .prayer-circle-tab{
    min-height:48px!important;
    border-radius:17px!important;
    border:1px solid rgba(214,185,127,.22)!important;
    background:rgba(255,255,255,.58)!important;
    color:#4A3212!important;
    font-size:14px!important;
    font-weight:650!important;
    box-shadow:none!important;
  }
  .app-shell .prayer-circle-tab.active{
    background:linear-gradient(145deg,rgba(255,253,248,.98),rgba(255,248,223,.72))!important;
    border-color:rgba(201,154,61,.30)!important;
    box-shadow:0 8px 18px rgba(84,62,30,.045)!important;
  }

  .app-shell .spirit-friends-tools-card{
    order:2!important;
    padding:24px!important;
    background:linear-gradient(145deg,#FFFDF8,#FFF9EF)!important;
  }
  .app-shell .spirit-friends-dashboard .prayer-friend-panel{
    padding:0!important;
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel{
    padding-top:20px!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel::before{
    left:0!important;
    right:0!important;
    top:0!important;
    height:2px!important;
    background:linear-gradient(90deg,#C99012,rgba(201,144,18,.12))!important;
    box-shadow:none!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .prayer-friend-panel-title{
    margin:0 0 14px!important;
  }
  .app-shell .friend-prayer-prompt{
    margin:0 0 16px!important;
    padding:15px 16px!important;
    border:0!important;
    border-radius:22px!important;
    background:linear-gradient(145deg,rgba(246,250,255,.78),rgba(236,253,245,.50))!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.82)!important;
  }
  .app-shell .friend-prayer-prompt-kicker{
    color:#8A714F!important;
    font-size:10.5px!important;
    font-weight:650!important;
    letter-spacing:.15em!important;
  }
  .app-shell .friend-prayer-prompt p{
    color:#4A3212!important;
    font-size:13.5px!important;
    line-height:1.45!important;
    font-weight:500!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .scroll-list.compact{
    max-height:258px!important;
    gap:0!important;
    padding:0!important;
    overflow-y:auto!important;
    overscroll-behavior:contain!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-row{
    min-height:86px!important;
    padding:14px 0!important;
    grid-template-columns:minmax(0,1fr) auto!important;
    gap:14px!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-row::after{
    left:66px!important;
    right:0!important;
    bottom:0!important;
    height:1px!important;
    background:rgba(214,185,127,.18)!important;
  }
  .app-shell .instagram-friend-identity{
    grid-template-columns:54px minmax(0,1fr)!important;
    gap:14px!important;
  }
  .app-shell .friend-spirit-avatar{
    width:54px!important;
    height:54px!important;
    border:1px solid rgba(255,255,255,.82)!important;
    box-shadow:0 9px 20px rgba(84,62,30,.075)!important;
  }
  .app-shell .instagram-friend-copy .prayer-friend-name,
  .app-shell .spirit-friends-dashboard .prayer-friend-name{
    color:#332719!important;
    font-size:15.5px!important;
    font-weight:620!important;
    line-height:1.26!important;
    letter-spacing:0!important;
  }
  .app-shell .instagram-friend-subtitle{
    color:#8A714F!important;
    font-size:12px!important;
    font-weight:600!important;
    line-height:1.28!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions{
    gap:8px!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions .soft-action-btn:not(.remove-friend-btn){
    min-width:96px!important;
    min-height:40px!important;
    padding:8px 12px!important;
    border:0!important;
    border-radius:14px!important;
    background:#F0F2F4!important;
    color:#342313!important;
    font-size:13px!important;
    font-weight:700!important;
    box-shadow:none!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions .soft-action-btn.friend-alert-btn:not(.remove-friend-btn){
    background:linear-gradient(135deg,#FFF4C8,#D9A23A)!important;
    color:#342313!important;
  }
  .app-shell .spirit-friends-dashboard .friends-garden-panel .remove-friend-btn{
    width:36px!important;
    min-width:36px!important;
    height:36px!important;
    border:0!important;
    background:transparent!important;
    color:rgba(58,42,24,.38)!important;
    font-size:24px!important;
    box-shadow:none!important;
  }

  .app-shell .community-feed-card.blessings-inbox-card{
    order:3!important;
    padding:24px!important;
    border:0!important;
    background:linear-gradient(145deg,#FFFDF8,#FFF9EF)!important;
    box-shadow:0 14px 32px rgba(84,62,30,.05),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .app-shell .community-feed-card.blessings-inbox-card.blessings-new{
    background:linear-gradient(145deg,rgba(255,248,223,.98),rgba(255,241,184,.66))!important;
    box-shadow:0 16px 34px rgba(201,154,61,.12),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .app-shell .community-feed-card.blessings-inbox-card h3{
    font-size:clamp(24px,5.8vw,32px)!important;
    margin:0 0 5px!important;
  }
  .app-shell .community-feed-card.blessings-inbox-card > div:first-child{
    align-items:center!important;
    gap:18px!important;
  }
  .app-shell .community-feed-card .community-feed-glow-line{
    margin:14px 0!important;
    opacity:.62!important;
  }
  .app-shell .community-feed-card .soft-action-btn{
    min-height:42px!important;
    padding:8px 14px!important;
    border-radius:15px!important;
    font-weight:650!important;
  }
  .app-shell .new-blessing-shortcut,
  .app-shell .community-feed-card .friend-alert-btn{
    background:linear-gradient(135deg,#FFF4C8,#D8A236)!important;
    color:#342313!important;
    border:0!important;
  }
  .app-shell .community-feed-card .prayer-friend-row{
    padding:14px 0!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
    border-bottom:1px solid rgba(214,185,127,.16)!important;
  }
  .app-shell .community-feed-card.blessings-inbox-card .scroll-list.compact{
    gap:10px!important;
  }
  .app-shell .community-feed-card.blessings-inbox-card .prayer-friend-row.unread-friend-activity{
    position:relative!important;
    padding:14px 13px 14px 16px!important;
    border:1px solid rgba(201,144,18,.26)!important;
    border-radius:20px!important;
    background:
      radial-gradient(circle at 0% 12%,rgba(255,243,196,.66),transparent 42%),
      linear-gradient(145deg,rgba(244,206,113,.34),rgba(255,242,199,.78) 54%,rgba(255,253,246,.92))!important;
    box-shadow:
      0 12px 26px rgba(201,144,18,.13),
      inset 0 1px 0 rgba(255,255,255,.82),
      inset 0 0 0 1px rgba(255,255,255,.36)!important;
  }
  .app-shell .community-feed-card.blessings-inbox-card .prayer-friend-row.unread-friend-activity::before{
    left:8px!important;
    top:14px!important;
    bottom:14px!important;
    width:3px!important;
    opacity:.55!important;
    background:linear-gradient(180deg,rgba(201,144,18,.70),rgba(255,220,126,.28))!important;
  }

  .app-shell .accountability-primary-card{
    order:4!important;
    padding:24px!important;
    border:0!important;
    background:linear-gradient(145deg,#FFFDF8,#F8FBF4)!important;
    box-shadow:0 14px 32px rgba(84,62,30,.05),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .app-shell .accountability-primary-card h3{
    font-size:clamp(24px,5.8vw,32px)!important;
  }
  .app-shell .accountability-update-card{
    margin:16px 0!important;
    padding:16px!important;
    border-radius:22px!important;
    background:linear-gradient(145deg,rgba(236,253,245,.74),rgba(255,248,223,.50))!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.78)!important;
  }
  .app-shell .accountability-primary-card .accountability-partner-scroll{
    gap:14px!important;
    padding:8px 2px 14px!important;
  }
  .app-shell .accountability-primary-card .accountability-partner-slide{
    min-width:min(90%,340px)!important;
    padding:18px!important;
    border:0!important;
    border-radius:24px!important;
    background:linear-gradient(150deg,rgba(255,253,248,.98),rgba(236,253,245,.62))!important;
    box-shadow:0 12px 26px rgba(84,62,30,.06),inset 0 1px 0 rgba(255,255,255,.84)!important;
  }
  .app-shell .accountability-primary-card .accountability-partner-slide > * + *{
    margin-top:14px!important;
  }
  .app-shell .partner-focus-label{
    display:flex!important;
    flex-direction:column!important;
    gap:8px!important;
    color:#7D674D!important;
    font-size:12px!important;
    font-weight:650!important;
  }
  .app-shell .partner-focus-select,
  .app-shell .partner-custom-focus-input{
    min-height:46px!important;
    border-radius:15px!important;
  }
  .app-shell .partner-focus-complete-btn{
    min-height:46px!important;
    border-radius:15px!important;
    font-weight:700!important;
  }
  .app-shell .accountability-primary-card .partner-ask-toggle{
    min-height:46px!important;
    border:0!important;
    border-radius:16px!important;
    background:rgba(255,255,255,.62)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.78)!important;
  }
  .app-shell .partner-ask-panel,
  .app-shell .accountability-primary-card .prayer-friend-panel{
    margin-top:12px!important;
    padding:14px!important;
    border:1px solid rgba(214,185,127,.16)!important;
    border-radius:20px!important;
    background:rgba(255,255,255,.42)!important;
  }
  .app-shell .friend-checkbox-list{
    gap:8px!important;
  }
  .app-shell .friend-checkbox-row{
    min-height:44px!important;
    padding:10px 12px!important;
    border-radius:14px!important;
  }
  @media(max-width:430px){
    .app-shell .spirit-friends-dashboard{gap:20px!important;}
    .app-shell .spirit-friends-dashboard > .sacred-card,
    .app-shell .spirit-friends-hero-card,
    .app-shell .spirit-friends-tools-card,
    .app-shell .community-feed-card.blessings-inbox-card,
    .app-shell .accountability-primary-card{padding:20px!important;border-radius:26px!important;}
    .app-shell .friends-hero-summary{align-items:stretch!important;padding:13px!important;}
    .app-shell .prayer-circle-tool-grid{gap:10px!important;}
    .app-shell .prayer-circle-tab{min-height:46px!important;font-size:13.5px!important;}
    .app-shell .spirit-friends-dashboard .friends-garden-panel .scroll-list.compact{max-height:246px!important;}
    .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-row{min-height:82px!important;gap:9px!important;padding:13px 0!important;}
    .app-shell .instagram-friend-identity{grid-template-columns:50px minmax(0,1fr)!important;gap:11px!important;}
    .app-shell .friend-spirit-avatar{width:50px!important;height:50px!important;}
    .app-shell .instagram-friend-copy .prayer-friend-name,
    .app-shell .spirit-friends-dashboard .prayer-friend-name{font-size:14.5px!important;}
    .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions{gap:5px!important;}
    .app-shell .spirit-friends-dashboard .friends-garden-panel .compact-friend-actions .soft-action-btn:not(.remove-friend-btn){min-width:76px!important;min-height:38px!important;padding:7px 9px!important;font-size:12px!important;}
    .app-shell .spirit-friends-dashboard .friends-garden-panel .remove-friend-btn{width:30px!important;min-width:30px!important;height:34px!important;font-size:22px!important;}
    .app-shell .community-feed-card.blessings-inbox-card > div:first-child{gap:12px!important;}
    .app-shell .accountability-primary-card .accountability-partner-slide{min-width:92%!important;padding:16px!important;}
  }

  /* Testimony page structure: featured, share, Instagram, filters, wall, book. */
  .app-shell .testimony-garden-page .featured-testimony-card,
  .app-shell .testimony-garden-page .testimony-featured-garden{
    order:1!important;
    position:relative!important;
  }
  .app-shell .testimony-action-card{
    order:2!important;
    border-color:rgba(231,207,159,.20)!important;
    background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(246,250,255,.52))!important;
  }
  .app-shell .testimony-action-card::before{
    content:""!important;
    display:block!important;
    height:3px!important;
    width:100%!important;
    border-radius:999px!important;
    margin:0 0 13px!important;
    background:#C99012!important;
    box-shadow:0 5px 14px rgba(201,144,18,.14)!important;
  }
  .app-shell .testimony-shared-garden{
    order:3!important;
  }
  .app-shell .testimony-book-preview-card{
    order:4!important;
    border-color:rgba(231,207,159,.20)!important;
    background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(255,248,253,.36))!important;
  }
  .app-shell .testimony-book-mini-list{
    display:flex!important;
    flex-direction:column!important;
    gap:8px!important;
  }
  .app-shell .testimony-book-mini-row{
    display:flex!important;
    gap:10px!important;
    align-items:flex-start!important;
    padding:11px 0!important;
    border-top:1px solid rgba(231,207,159,.22)!important;
  }
  .app-shell .testimony-book-mini-row:first-child{border-top:0!important;padding-top:0!important;}
  .app-shell .testimony-book-mini-icon{
    width:34px!important;
    height:34px!important;
    border-radius:50%!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    flex-shrink:0!important;
    font-size:17px!important;
  }
  .app-shell .testimony-book-mini-row h4{
    margin:0!important;
    color:#332719!important;
    font-size:13.5px!important;
    font-weight:900!important;
    line-height:1.25!important;
  }
  .app-shell .testimony-book-mini-row p{
    margin:3px 0 0!important;
    color:#7a674e!important;
    font-size:12.75px!important;
    line-height:1.42!important;
  }
  .app-shell .testimony-book-mini-row small{
    display:block!important;
    margin-top:3px!important;
    color:#9a8564!important;
    font-size:11.5px!important;
  }
  .app-shell .testimony-book-mini-actions{
    display:flex!important;
    gap:5px!important;
    flex-shrink:0!important;
  }
  .app-shell .testimony-book-mini-actions button{
    min-height:32px!important;
    border-radius:10px!important;
    border:1px solid rgba(231,207,159,.32)!important;
    background:rgba(255,253,246,.82)!important;
    color:#8a5a08!important;
    font-size:12px!important;
    font-weight:900!important;
    cursor:pointer!important;
  }
  @media(max-width:430px){
    .app-shell .testimony-book-mini-row{align-items:stretch!important;}
    .app-shell .testimony-book-mini-actions{flex-direction:column!important;}
    .app-shell .testimony-book-mini-actions button{padding:6px 8px!important;}
  }




  /* Testimony title lives inside the featured card. */
  .app-shell .testimony-wall-card-title{
    margin-bottom:14px!important;
    padding-bottom:13px!important;
    border-bottom:1px solid rgba(231,207,159,.28)!important;
  }
  .app-shell .testimony-wall-card-title::before{
    content:""!important;
    display:block!important;
    height:3px!important;
    width:100%!important;
    border-radius:999px!important;
    margin-bottom:13px!important;
    background:#C99012!important;
    box-shadow:0 5px 14px rgba(201,144,18,.14)!important;
  }


  .app-shell .blessing-sent-confirmation{
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    gap:8px!important;
    margin-top:12px!important;
    padding:10px 14px!important;
    border-radius:16px!important;
    background:linear-gradient(135deg,rgba(255,238,170,.92),rgba(255,248,223,.82))!important;
    border:1px solid rgba(201,144,18,.34)!important;
    color:#6F4A12!important;
    font-size:13px!important;
    font-weight:750!important;
    box-shadow:0 8px 18px rgba(151,103,21,.10)!important;
    animation:popIn .22s ease-out both!important;
  }
  .app-shell .blessing-sent-confirmation span:first-child{
    width:20px!important;
    height:20px!important;
    border-radius:999px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    background:#C99012!important;
    color:#fffdf5!important;
    font-size:13px!important;
    font-weight:900!important;
    flex:0 0 auto!important;
  }

  /* Blessings Inbox: send and receive live together. */
  .app-shell .blessings-inbox-card{
    order:4!important;
    position:relative!important;
    border:2px solid #C99012!important;
    box-shadow:0 12px 28px rgba(201,144,18,.12),inset 0 1px 0 rgba(255,255,255,.82)!important;
  }
  .app-shell .blessings-inbox-card::before{
    display:none!important;
    content:none!important;
  }
  .app-shell .blessing-inbox-send-card{
    margin-top:12px!important;
    padding:12px!important;
    border:1px solid rgba(231,207,159,.24)!important;
    border-radius:20px!important;
    background:linear-gradient(145deg,rgba(255,250,230,.84),rgba(255,253,246,.88))!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.78)!important;
  }
  .app-shell .blessings-inbox-card .blessing-options-grid{
    grid-template-columns:repeat(auto-fit,minmax(112px,1fr))!important;
    gap:8px!important;
  }
  .app-shell .blessings-inbox-card .blessing-option-btn{
    min-height:50px!important;
    border-radius:16px!important;
  }
  .app-shell .accountability-primary-card{order:5!important;}


  /* Prayer landing: match the calm premium rhythm of Today page. */
  .app-shell .prayer-vessels-section{
    padding:0 2px!important;
  }
  .app-shell .prayer-vessel-hero{
    margin-bottom:12px!important;
    border-color:rgba(125,174,212,.22)!important;
    background:
      radial-gradient(circle at 52% 12%,rgba(255,255,255,.96),transparent 25%),
      radial-gradient(circle at 78% 36%,rgba(255,248,223,.58),transparent 30%),
      radial-gradient(circle at 16% 24%,rgba(219,234,254,.46),transparent 34%),
      linear-gradient(145deg,rgba(255,254,249,.98),rgba(255,250,235,.86) 52%,rgba(246,250,255,.74))!important;
    box-shadow:0 15px 34px rgba(67,82,100,.07),inset 0 1px 0 rgba(255,255,255,.88)!important;
  }
  .prayer-vessel-primary-action{
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    width:fit-content!important;
    min-height:42px!important;
    margin-top:13px!important;
    padding:10px 18px!important;
    border-radius:999px!important;
    border:1px solid rgba(184,134,31,.40)!important;
    background:linear-gradient(135deg,#fff5cf,#e6bd57 52%,#c8952b)!important;
    color:#2f220d!important;
    font-family:'Nunito',sans-serif!important;
    font-size:13.5px!important;
    font-weight:900!important;
    cursor:pointer!important;
    box-shadow:0 10px 22px rgba(138,90,8,.16),inset 0 1px 0 rgba(255,255,255,.74)!important;
  }
  .app-shell .prayer-vessel-open-grid{
    gap:10px!important;
    margin-top:2px!important;
  }
  .app-shell .prayer-vessel-open-grid button{
    position:relative!important;
    align-items:flex-start!important;
    min-height:104px!important;
    padding:15px 14px!important;
    border-radius:24px!important;
    border:1px solid rgba(231,207,159,.28)!important;
    background:linear-gradient(145deg,rgba(255,253,246,.96),rgba(246,250,255,.54))!important;
    box-shadow:0 10px 24px rgba(67,82,100,.055),inset 0 1px 0 rgba(255,255,255,.86)!important;
    text-align:left!important;
  }
  .app-shell .prayer-vessel-open-grid button.active,
  .app-shell .prayer-vessel-open-grid button:focus-visible{
    border-color:rgba(201,144,18,.38)!important;
    background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(255,248,223,.78))!important;
  }
  .app-shell .prayer-vessel-open-grid b{
    color:#3a2a18!important;
    font-size:24px!important;
    margin-bottom:4px!important;
  }
  .app-shell .prayer-vessel-open-grid span{
    color:#3a2a18!important;
    font-size:13.5px!important;
    font-weight:900!important;
  }
  .app-shell .prayer-vessel-open-grid em{
    position:absolute!important;
    right:12px!important;
    bottom:12px!important;
    min-width:58px!important;
    min-height:30px!important;
    background:rgba(255,255,255,.72)!important;
    border:1px solid rgba(125,174,212,.25)!important;
    color:#5d4b32!important;
    box-shadow:none!important;
  }
  .prayer-landing-pulse{
    display:grid!important;
    grid-template-columns:auto minmax(0,1fr) auto!important;
    gap:10px!important;
    align-items:center!important;
    width:100%!important;
    margin-top:10px!important;
    padding:10px 11px!important;
    border-radius:20px!important;
    border:1px solid rgba(231,207,159,.18)!important;
    background:linear-gradient(145deg,rgba(255,253,246,.88),rgba(236,253,245,.36))!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.80)!important;
    cursor:pointer!important;
    text-align:left!important;
    font-family:'Nunito',sans-serif!important;
  }
  .prayer-landing-day-chip{
    width:46px!important;
    min-height:46px!important;
    border-radius:16px!important;
    display:flex!important;
    flex-direction:column!important;
    align-items:center!important;
    justify-content:center!important;
    background:rgba(255,255,255,.66)!important;
    border:1px solid rgba(231,207,159,.24)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.84)!important;
  }
  .prayer-landing-day-chip strong{font-size:18px!important;line-height:1!important;color:#3a2a18!important;font-weight:900!important;}
  .prayer-landing-day-chip span{margin-top:2px!important;font-size:8.8px!important;text-transform:uppercase!important;letter-spacing:.10em!important;color:#8a714f!important;font-weight:900!important;}
  .prayer-landing-pulse-copy{display:block!important;min-width:0!important;}
  .prayer-landing-pulse-copy > span{
    display:block!important;
    margin-bottom:1px!important;
    color:#8a714f!important;
    font-size:10.5px!important;
    font-weight:900!important;
    letter-spacing:.12em!important;
    text-transform:uppercase!important;
  }
  .prayer-landing-pulse-copy p{
    margin:0!important;
    color:#3a2a18!important;
    font-size:14px!important;
    line-height:1.24!important;
    font-weight:850!important;
    font-family:'Nunito',sans-serif!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .prayer-landing-open{
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    min-height:30px!important;
    padding:6px 11px!important;
    border-radius:999px!important;
    background:rgba(255,255,255,.72)!important;
    border:1px solid rgba(125,174,212,.20)!important;
    color:#5d4b32!important;
    font-size:12px!important;
    font-weight:900!important;
    white-space:nowrap!important;
  }
  .app-shell .recent-prayers-preview-card{
    border-radius:28px!important;
    border-color:rgba(125,174,212,.20)!important;
    background:linear-gradient(145deg,rgba(255,253,246,.96),rgba(246,250,255,.58))!important;
    box-shadow:0 12px 28px rgba(67,82,100,.06),inset 0 1px 0 rgba(255,255,255,.82)!important;
  }
  @media(max-width:680px){
    .prayer-vessel-primary-action{margin-left:auto!important;margin-right:auto!important;}
    .app-shell .prayer-vessel-open-grid{grid-template-columns:1fr!important;}
    .app-shell .prayer-vessel-open-grid button{
      min-height:74px!important;
      padding:12px 84px 12px 14px!important;
      justify-content:center!important;
    }
    .app-shell .prayer-vessel-open-grid b{font-size:21px!important;margin-bottom:2px!important;}
    .app-shell .prayer-vessel-open-grid em{top:50%!important;bottom:auto!important;transform:translateY(-50%)!important;}
    .prayer-landing-pulse{border-radius:18px!important;padding:9px!important;gap:8px!important;}
    .prayer-landing-day-chip{width:42px!important;min-height:42px!important;border-radius:14px!important;}
    .prayer-landing-pulse-copy p{font-size:13px!important;}
    .prayer-landing-open{min-height:28px!important;padding:5px 9px!important;font-size:11.5px!important;}
  }

  /* Prayer message and completed-action polish. */
  .angel-message{
    padding:8px 14px!important;
    border-color:rgba(184,147,75,.20)!important;
    font-family:Georgia,serif!important;
    font-size:15px!important;
    font-weight:500!important;
    letter-spacing:.01em!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-primary{
    white-space:nowrap!important;
    min-width:0!important;
    padding-left:12px!important;
    padding-right:12px!important;
    font-size:13.5px!important;
    letter-spacing:0!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-primary .pray-check-icon{
    flex:0 0 20px!important;
  }
  @media(max-width:380px){
    .app-shell .prayer-page-calm .planted-prayer-primary{font-size:12.5px!important;}
  }

  /* Prayer Vessel copy cleanup: status line + compact answered badge. */
  .prayer-vessel-hero-copy > p:not(.prayer-vessel-private-note){
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    width:fit-content!important;
    max-width:min(100%,30rem)!important;
    margin-top:3px!important;
    padding:7px 12px!important;
    border-radius:999px!important;
    background:rgba(255,255,255,.56)!important;
    border:1px solid rgba(224,197,139,.24)!important;
    color:#5a4319!important;
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
    font-size:13.5px!important;
    line-height:1.25!important;
    font-weight:800!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.72)!important;
  }
  .prayer-vessel-answer-highlight.compact{
    width:fit-content!important;
    max-width:min(100%,32rem)!important;
    padding:8px 12px!important;
    gap:2px!important;
    border-radius:18px!important;
    background:linear-gradient(135deg,rgba(255,248,223,.92),rgba(255,255,255,.70))!important;
  }
  .prayer-vessel-answer-highlight.compact strong{
    font-size:12.8px!important;
    letter-spacing:.01em!important;
  }
  .prayer-vessel-answer-highlight.compact span{
    font-size:12px!important;
    color:#775c28!important;
  }
  @media(max-width:680px){
    .prayer-vessel-hero-copy > p:not(.prayer-vessel-private-note){
      margin-left:auto!important;
      margin-right:auto!important;
      font-size:12.6px!important;
      padding:7px 10px!important;
    }
    .prayer-vessel-answer-highlight.compact{width:100%!important;}
  }

  /* Prayer and Friends typography: match Today page, remove chunky weights. */
  .app-shell .prayer-page-calm,
  .app-shell .spirit-friends-dashboard{
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
  }
  .app-shell .prayer-vessel-hero-copy h1,
  .app-shell .prayer-vessel-hero-copy h2,
  .app-shell .spirit-friends-hero-card h1,
  .app-shell .spirit-friends-hero-card h2,
  .app-shell .spirit-friends-dashboard h1,
  .app-shell .spirit-friends-dashboard h2{
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-weight:500!important;
    letter-spacing:0!important;
    color:#3A2812!important;
  }
  .app-shell .prayer-vessel-kicker,
  .app-shell .prayer-friend-panel-title,
  .app-shell .friend-prayer-prompt-kicker,
  .app-shell .prayer-vessel-open-grid em,
  .app-shell .prayer-status-chip,
  .app-shell .friend-alert-badge,
  .app-shell .friends-hero-summary > span{
    font-weight:650!important;
    letter-spacing:.08em!important;
    color:#8a714f!important;
  }
  .app-shell .prayer-vessel-hero-copy p,
  .app-shell .prayer-vessel-private-note,
  .app-shell .prayer-circle-subtitle,
  .app-shell .spirit-friends-dashboard p,
  .app-shell .prayer-friend-copy,
  .app-shell .friend-prayer-prompt p{
    font-weight:400!important;
    color:#6f5c43!important;
    line-height:1.52!important;
  }
  .app-shell .prayer-vessel-primary-action,
  .app-shell .pray-now-btn,
  .app-shell .prayer-secondary-action,
  .app-shell .soft-action-btn,
  .app-shell .spirit-friends-dashboard .soft-action-btn,
  .app-shell .prayer-circle-tab,
  .app-shell .partner-focus-complete-btn,
  .app-shell .friend-checkbox-row,
  .app-shell .prayer-filter-btn,
  .app-shell .blessing-option-btn{
    font-weight:650!important;
    letter-spacing:0!important;
  }
  .app-shell .prayer-vessel-open-grid b,
  .app-shell .prayer-landing-day-chip strong,
  .app-shell .prayer-vessel-answer-highlight.compact strong{
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-weight:500!important;
    letter-spacing:0!important;
    color:#3A2812!important;
  }
  .app-shell .prayer-vessel-open-grid span,
  .app-shell .prayer-landing-day-chip span,
  .app-shell .prayer-vessel-answer-highlight.compact span,
  .app-shell .instagram-friend-copy .prayer-friend-name,
  .app-shell .spirit-friends-dashboard .prayer-friend-name,
  .app-shell .friend-notification-title,
  .app-shell .accountability-update-title{
    font-weight:560!important;
    letter-spacing:0!important;
  }
  .app-shell .instagram-friend-subtitle,
  .app-shell .friend-notification-time,
  .app-shell .friend-activity-pill,
  .app-shell .meaning-chip,
  .app-shell .placed-chip{
    font-weight:500!important;
  }
  .app-shell .empty-state-title,
  .app-shell .prayer-friend-panel h3,
  .app-shell .spirit-friends-dashboard h3{
    font-weight:600!important;
  }
  @media(max-width:430px){
    .app-shell .prayer-vessel-hero-copy h2,
    .app-shell .spirit-friends-hero-card h2{
      font-size:clamp(24px,6vw,30px)!important;
      line-height:1.08!important;
    }
  }


  /* Final Home refinement: Today-style typography and clearer home art. */
  .app-shell .home-page,
  .app-shell .home-page button,
  .app-shell .home-page input,
  .app-shell .home-page select,
  .app-shell .home-page textarea{
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
  }
  .app-shell .home-page .serif,
  .app-shell .home-page h1,
  .app-shell .home-page h2,
  .app-shell .home-page h3{
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-weight:500!important;
    letter-spacing:0!important;
    color:#3A2812!important;
    line-height:1.12!important;
  }
  .app-shell .home-page h2{font-size:clamp(24px,5.4vw,32px)!important;}
  .app-shell .home-page h3{font-size:clamp(18px,4.4vw,24px)!important;}
  .app-shell .home-page p,
  .app-shell .home-page .home-stage-message,
  .app-shell .home-page .home-care-copy,
  .app-shell .home-page .faith-flight-copy,
  .app-shell .home-page .empty-state-copy{
    font-weight:400!important;
    color:#6f5c43!important;
    line-height:1.52!important;
    letter-spacing:0!important;
  }
  .app-shell .home-page .section-label,
  .app-shell .home-page .home-righteousness-count > div:first-child,
  .app-shell .home-page .home-category-card .meaning-chip,
  .app-shell .home-page .home-shop-card .meaning-chip,
  .app-shell .home-page .sticky-preview-label{
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
    font-weight:620!important;
    letter-spacing:.1em!important;
    color:#8a714f!important;
  }
  .app-shell .home-page .home-category-card,
  .app-shell .home-page .home-category-card *,
  .app-shell .home-page .home-shop-card,
  .app-shell .home-page .home-shop-card *,
  .app-shell .home-page .home-remove-btn,
  .app-shell .home-page .soft-action-btn,
  .app-shell .home-page .gold-btn,
  .app-shell .home-page .placed-chip,
  .app-shell .home-page .meaning-chip,
  .app-shell .home-page .home-visitors-toggle,
  .app-shell .home-page .faith-flight-title{
    font-weight:600!important;
    letter-spacing:0!important;
  }
  .app-shell .home-page .home-shop-card p,
  .app-shell .home-page .home-shop-card .shop-card-desc,
  .app-shell .home-page .home-category-card p,
  .app-shell .home-page .home-category-card small{
    font-weight:400!important;
  }
  .app-shell .home-page .home-righteousness-value,
  .app-shell .home-page .home-growth-line{
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-weight:500!important;
  }

  .app-shell .home-page .home-room{
    border-radius:24px!important;
    border:1px solid rgba(231,207,159,.34)!important;
    background:#f8fbff!important;
    box-shadow:0 16px 34px rgba(91,67,28,.07),inset 0 -26px 46px rgba(138,90,8,.055)!important;
  }
  .app-shell .home-page .home-room .home-rays,
  .app-shell .home-page .home-room .home-sunbeam-drift,
  .app-shell .home-page .home-room .home-distant-city,
  .app-shell .home-page .home-room .home-heavenly-veil,
  .app-shell .home-page .home-room .home-realm-planet,
  .app-shell .home-page .home-room .home-realm-country,
  .app-shell .home-page .home-room .home-word-scroll,
  .app-shell .home-page .home-room .home-prayer-light-trail,
  .app-shell .home-page .home-room .home-signature-vessel,
  .app-shell .home-page .home-room .home-background-rainbow,
  .app-shell .home-page .home-room .home-heavenly-effect.eff_glory_rays,
  .app-shell .home-page .home-room .home-heavenly-effect.eff_covenant_rainbow,
  .app-shell .home-page .home-room .home-heavenly-effect.eff_covenant_rainbow .effect-rainbow{
    display:none!important;
    opacity:0!important;
  }
  .app-shell .home-page .home-room .home-sky{
    background:
      radial-gradient(circle at 50% 5%,rgba(255,253,246,.98) 0 13%,rgba(255,248,223,.64) 14% 27%,transparent 38%),
      linear-gradient(180deg,#dff3ff 0%,#f8fbff 48%,#fff8df 100%)!important;
  }
  .app-shell .home-page .home-room.home-time-evening .home-sky{
    background:
      radial-gradient(circle at 74% 14%,rgba(255,235,176,.70) 0 13%,transparent 32%),
      linear-gradient(180deg,#f3e6d8 0%,#edf5ff 48%,#fff4d2 100%)!important;
  }
  .app-shell .home-page .home-room.home-time-night .home-sky{
    background:
      radial-gradient(circle at 72% 12%,rgba(255,248,223,.54) 0 10%,transparent 25%),
      linear-gradient(180deg,#20304d 0%,#4f6587 48%,#d8e6f2 100%)!important;
  }
  .app-shell .home-page .home-room .home-cloud{
    opacity:.78!important;
    background:transparent!important;
    box-shadow:none!important;
    filter:drop-shadow(0 8px 12px rgba(98,126,145,.08))!important;
  }
  .app-shell .home-page .home-room .home-cloud::before,
  .app-shell .home-page .home-room .home-cloud::after{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    border-radius:999px!important;
    background:
      radial-gradient(circle at 18% 58%,rgba(255,255,255,.96) 0 24%,transparent 25%),
      radial-gradient(circle at 36% 38%,rgba(255,255,255,.98) 0 29%,transparent 30%),
      radial-gradient(circle at 58% 46%,rgba(255,255,255,.94) 0 32%,transparent 33%),
      radial-gradient(circle at 78% 60%,rgba(255,255,255,.9) 0 24%,transparent 25%),
      linear-gradient(180deg,rgba(255,255,255,.82),rgba(255,255,255,.48))!important;
  }
  .app-shell .home-page .home-room .home-cloud::after{
    transform:translateY(8px) scaleX(.88)!important;
    opacity:.36!important;
    filter:blur(8px)!important;
  }
  .app-shell .home-page .home-room .home-cloud.c1{width:150px!important;height:56px!important;left:-34px!important;top:70px!important;}
  .app-shell .home-page .home-room .home-cloud.c2{width:186px!important;height:64px!important;right:-52px!important;top:92px!important;opacity:.70!important;}
  .app-shell .home-page .home-room .home-cloud.c3{width:132px!important;height:48px!important;left:36%!important;top:42px!important;opacity:.52!important;}

  .app-shell .home-page .home-room .home-floor{
    background:linear-gradient(180deg,#f8f4d8 0%,#d9edc8 44%,#b8d99c 100%)!important;
  }
  .app-shell .home-page .home-room .home-floor::before{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    background:linear-gradient(90deg,rgba(255,255,255,.22) 0 1px,transparent 1px 44px),linear-gradient(180deg,rgba(255,255,255,.18) 0 1px,transparent 1px 38px)!important;
    opacity:.32!important;
  }
  .app-shell .home-page .home-room .home-path{
    top:53%!important;
    width:min(34%,142px)!important;
    background:linear-gradient(180deg,rgba(255,253,246,.76),rgba(232,190,83,.48) 78%,rgba(174,132,46,.54))!important;
    box-shadow:inset 0 0 18px rgba(255,255,255,.38)!important;
  }
  .app-shell .home-page .home-room .home-path::after{
    opacity:.18!important;
  }

  .app-shell .home-page .home-room .home-cardboard,
  .app-shell .home-page .home-room .home-little-shelter,
  .app-shell .home-page .home-room .home-house,
  .app-shell .home-page .home-room .home-mansion,
  .app-shell .home-page .home-room .home-palace{
    z-index:18!important;
    filter:drop-shadow(0 18px 18px rgba(92,62,18,.16)) drop-shadow(0 0 14px rgba(255,248,223,.28))!important;
  }
  .app-shell .home-page .home-room .home-cardboard::before,
  .app-shell .home-page .home-room .home-little-shelter::before,
  .app-shell .home-page .home-room .home-house::before,
  .app-shell .home-page .home-room .home-mansion::before,
  .app-shell .home-page .home-room .home-palace::before{
    display:none!important;
    content:none!important;
  }
  .app-shell .home-page .home-room .home-cardboard::after,
  .app-shell .home-page .home-room .home-little-shelter::after,
  .app-shell .home-page .home-room .home-house::after,
  .app-shell .home-page .home-room .home-mansion::after,
  .app-shell .home-page .home-room .home-palace::after{
    content:""!important;
    position:absolute!important;
    left:12%!important;
    right:12%!important;
    bottom:-12px!important;
    height:18px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse,rgba(70,48,18,.18),rgba(70,48,18,0) 72%)!important;
    z-index:-1!important;
  }
  .app-shell .home-page .home-room .home-cardboard-roof,
  .app-shell .home-page .home-room .home-little-shelter-roof,
  .app-shell .home-page .home-room .home-house-roof,
  .app-shell .home-page .home-room .home-mansion-roof,
  .app-shell .home-page .home-room .home-palace-roof{
    background:
      repeating-linear-gradient(115deg,rgba(255,255,255,.16) 0 9px,rgba(138,90,8,.08) 9px 13px),
      linear-gradient(180deg,#fff0b9 0%,#dca93f 58%,#9d6a16 100%)!important;
    filter:drop-shadow(0 7px 8px rgba(88,55,13,.16))!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{
    background:
      radial-gradient(circle at 22% 16%,rgba(255,255,255,.72),transparent 19%),
      linear-gradient(180deg,#fffdf8 0%,#fff1cf 54%,#d4a24a 100%)!important;
    border:1.5px solid rgba(116,84,28,.22)!important;
    box-shadow:inset 0 10px 16px rgba(255,255,255,.72),inset 0 -12px 16px rgba(90,56,12,.08),0 0 18px rgba(255,248,223,.24)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body{
    background:linear-gradient(180deg,#f3d29a 0%,#c38a45 100%)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body::before{
    content:""!important;
    display:block!important;
    position:absolute!important;
    left:20px!important;
    top:16px!important;
    width:22px!important;
    height:19px!important;
    border-radius:7px!important;
    background:linear-gradient(180deg,#fff8df,#f3d29a)!important;
    box-shadow:54px 0 0 rgba(255,248,223,.74)!important;
    border:1px solid rgba(116,84,28,.18)!important;
  }
  .app-shell .home-page .home-room .home-little-shelter-window,
  .app-shell .home-page .home-room .home-window-dot,
  .app-shell .home-page .home-room .home-mansion-window{
    border-radius:9px!important;
    background:
      linear-gradient(90deg,transparent 47%,rgba(138,90,8,.18) 48% 52%,transparent 53%),
      linear-gradient(180deg,transparent 47%,rgba(138,90,8,.16) 48% 52%,transparent 53%),
      radial-gradient(circle at 50% 24%,#fff 0,#fff7d6 36%,#f2c66d 100%)!important;
    border:1px solid rgba(116,84,28,.20)!important;
    box-shadow:0 0 12px rgba(255,248,223,.66),inset 0 2px 5px rgba(255,255,255,.78)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-door,
  .app-shell .home-page .home-room .home-little-shelter-door,
  .app-shell .home-page .home-room .home-house-door,
  .app-shell .home-page .home-room .home-mansion-door,
  .app-shell .home-page .home-room .home-door{
    border-radius:999px 999px 8px 8px!important;
    background:radial-gradient(circle at 50% 16%,#fff 0,#fff6d6 31%,#d29929 72%,#7a5011 100%)!important;
    border:1.5px solid rgba(90,56,12,.26)!important;
    box-shadow:0 0 18px rgba(255,248,223,.68),inset 0 5px 7px rgba(255,255,255,.55)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-door::before,
  .app-shell .home-page .home-room .home-little-shelter-door::before,
  .app-shell .home-page .home-room .home-house-door::before,
  .app-shell .home-page .home-room .home-mansion-door::before,
  .app-shell .home-page .home-room .home-door::before{
    content:""!important;
    position:absolute!important;
    left:50%!important;
    top:9px!important;
    bottom:6px!important;
    width:1px!important;
    background:rgba(90,56,12,.18)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-door::after,
  .app-shell .home-page .home-room .home-little-shelter-door::after,
  .app-shell .home-page .home-room .home-house-door::after,
  .app-shell .home-page .home-room .home-mansion-door::after,
  .app-shell .home-page .home-room .home-door::after{
    content:""!important;
    position:absolute!important;
    right:8px!important;
    top:48%!important;
    width:4px!important;
    height:4px!important;
    border-radius:50%!important;
    background:#fff8df!important;
    box-shadow:0 0 7px rgba(255,248,223,.9)!important;
  }
  .app-shell .home-page .home-room .home-palace-balcony,
  .app-shell .home-page .home-room .home-palace-balcony::after,
  .app-shell .home-page .home-room .home-palace-steps,
  .app-shell .home-page .home-room .home-palace-aura{
    opacity:.72!important;
  }
  .app-shell .home-page .home-room .home-flowerbed{
    opacity:.82!important;
    filter:saturate(.86) brightness(1.02)!important;
  }
  .app-shell .home-page .home-room .home-spirit-slot{
    z-index:46!important;
  }
  @media(max-width:560px){
    .app-shell .home-page h2{font-size:clamp(22px,6vw,28px)!important;}
    .app-shell .home-page h3{font-size:clamp(17px,5vw,22px)!important;}
    .app-shell .home-page .home-room .home-house{top:31%!important;width:198px!important;}
    .app-shell .home-page .home-room .home-mansion{top:26%!important;width:224px!important;}
    .app-shell .home-page .home-room .home-palace{top:22%!important;width:236px!important;}
    .app-shell .home-page .home-room .home-path{top:55%!important;width:min(34%,128px)!important;}
  }


  /* Prayer page: keep main prayer sections side by side on mobile. */
  .app-shell .prayer-vessel-open-grid{
    display:grid!important;
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
    gap:8px!important;
  }
  .app-shell .prayer-vessel-open-grid button{
    min-height:92px!important;
    padding:11px 8px 34px!important;
    align-items:center!important;
    justify-content:flex-start!important;
    text-align:center!important;
    border-radius:20px!important;
  }
  .app-shell .prayer-vessel-open-grid b{
    font-size:20px!important;
    margin:0 0 4px!important;
    line-height:1!important;
  }
  .app-shell .prayer-vessel-open-grid span{
    display:block!important;
    width:100%!important;
    font-size:12.25px!important;
    line-height:1.14!important;
    white-space:normal!important;
    overflow-wrap:normal!important;
  }
  .app-shell .prayer-vessel-open-grid em{
    left:50%!important;
    right:auto!important;
    bottom:8px!important;
    top:auto!important;
    transform:translateX(-50%)!important;
    min-width:42px!important;
    min-height:24px!important;
    padding:4px 7px!important;
    font-size:11px!important;
    line-height:1!important;
    border-radius:999px!important;
  }
  @media(max-width:430px){
    .app-shell .prayer-vessel-open-grid{gap:7px!important;}
    .app-shell .prayer-vessel-open-grid button{
      min-height:86px!important;
      padding:10px 5px 31px!important;
      border-radius:18px!important;
    }
    .app-shell .prayer-vessel-open-grid b{font-size:18px!important;}
    .app-shell .prayer-vessel-open-grid span{font-size:11px!important;line-height:1.1!important;}
    .app-shell .prayer-vessel-open-grid em{font-size:10px!important;min-width:36px!important;min-height:22px!important;bottom:7px!important;}
  }
  @media(max-width:360px){
    .app-shell .prayer-vessel-open-grid button{padding-left:4px!important;padding-right:4px!important;}
    .app-shell .prayer-vessel-open-grid span{font-size:10.2px!important;}
  }


  /* Final Growth typography: match Today page and remove chunky text. */
  .app-shell .growth-page-calm,
  .app-shell .growth-page-calm button,
  .app-shell .growth-page-calm input,
  .app-shell .growth-page-calm textarea,
  .app-shell .growth-page-calm select{
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
  }
  .app-shell .growth-page-calm .serif,
  .app-shell .growth-page-calm h1,
  .app-shell .growth-page-calm h2,
  .app-shell .growth-page-calm h3,
  .app-shell .growth-page-calm .growth-summary-copy h3,
  .app-shell .growth-page-calm .testimony-stone-toggle h3{
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-weight:500!important;
    letter-spacing:0!important;
    color:#3A2812!important;
    line-height:1.12!important;
  }
  .app-shell .growth-page-calm h2{font-size:clamp(24px,5.4vw,32px)!important;}
  .app-shell .growth-page-calm h3,
  .app-shell .growth-page-calm .growth-summary-copy h3,
  .app-shell .growth-page-calm .testimony-stone-toggle h3{
    font-size:clamp(18px,4.4vw,24px)!important;
  }
  .app-shell .growth-page-calm p,
  .app-shell .growth-page-calm .growth-summary-copy p,
  .app-shell .growth-page-calm .growth-account-card p,
  .app-shell .growth-page-calm .growth-closet-card p,
  .app-shell .growth-page-calm .growth-stones-card p,
  .app-shell .growth-page-calm .growth-book-card p,
  .app-shell .growth-page-calm .growth-closet-copy,
  .app-shell .growth-page-calm .growth-encouragement{
    font-weight:400!important;
    color:#6f5c43!important;
    line-height:1.52!important;
    letter-spacing:0!important;
  }
  .app-shell .growth-page-calm .section-label,
  .app-shell .growth-page-calm .growth-stage-progress-top span,
  .app-shell .growth-page-calm .growth-stat-label,
  .app-shell .growth-page-calm .stone-reward-title,
  .app-shell .growth-page-calm .testimony-label{
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
    font-weight:620!important;
    letter-spacing:.1em!important;
    color:#8a714f!important;
    text-transform:uppercase!important;
  }
  .app-shell .growth-page-calm .placed-chip,
  .app-shell .growth-page-calm .meaning-chip,
  .app-shell .growth-page-calm .testimony-stone-chevron,
  .app-shell .growth-page-calm .growth-path-status,
  .app-shell .growth-page-calm .growth-stage-progress-label,
  .app-shell .growth-page-calm .growth-stage-progress-top b,
  .app-shell .growth-page-calm .growth-summary-stat b,
  .app-shell .growth-page-calm .growth-summary-stat small,
  .app-shell .growth-page-calm .growth-stat-value,
  .app-shell .growth-page-calm .growth-stat-label,
  .app-shell .growth-page-calm .testimony-gem-name,
  .app-shell .growth-page-calm .stone-reward-name,
  .app-shell .growth-page-calm .empty-state-title{
    font-weight:600!important;
    letter-spacing:0!important;
  }
  .app-shell .growth-page-calm .growth-path-name{
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
    font-weight:600!important;
    letter-spacing:0!important;
    color:#3A2812!important;
  }
  .app-shell .growth-page-calm .growth-path-range,
  .app-shell .growth-page-calm .growth-stage-progress-top,
  .app-shell .growth-page-calm small,
  .app-shell .growth-page-calm label,
  .app-shell .growth-page-calm span{
    font-weight:500!important;
    letter-spacing:0!important;
  }
  .app-shell .growth-page-calm .gold-btn,
  .app-shell .growth-page-calm .soft-action-btn,
  .app-shell .growth-page-calm button:not(.testimony-stone-toggle),
  .app-shell .growth-page-calm button:not(.testimony-stone-toggle) *{
    font-weight:650!important;
    letter-spacing:0!important;
  }
  .app-shell .growth-page-calm b,
  .app-shell .growth-page-calm strong{
    font-weight:650!important;
  }
  .app-shell .growth-page-calm [style*="font-weight: 900"],
  .app-shell .growth-page-calm [style*="fontWeight: 900"],
  .app-shell .growth-page-calm [style*="font-weight:900"],
  .app-shell .growth-page-calm [style*="fontWeight:900"]{
    font-weight:600!important;
  }
  .app-shell .growth-page-calm [style*="font-weight: 800"],
  .app-shell .growth-page-calm [style*="fontWeight: 800"],
  .app-shell .growth-page-calm [style*="font-weight:800"],
  .app-shell .growth-page-calm [style*="fontWeight:800"]{
    font-weight:580!important;
  }
  .app-shell .growth-page-calm .growth-summary-card,
  .app-shell .growth-page-calm .growth-stage-hero,
  .app-shell .growth-page-calm .growth-hero-card,
  .app-shell .growth-page-calm .growth-account-card,
  .app-shell .growth-page-calm .growth-closet-card,
  .app-shell .growth-page-calm .growth-stones-card,
  .app-shell .growth-page-calm .growth-book-card{
    color:#3A2812!important;
  }
  @media(max-width:520px){
    .app-shell .growth-page-calm h3,
    .app-shell .growth-page-calm .growth-summary-copy h3,
    .app-shell .growth-page-calm .testimony-stone-toggle h3{
      font-size:clamp(17px,5vw,22px)!important;
    }
    .app-shell .growth-page-calm .growth-path-name{font-size:14.5px!important;}
    .app-shell .growth-page-calm .growth-path-range{font-size:11.5px!important;}
  }


  /* Prayer records: soft blue outline for saved prayer cards. */
  .app-shell .prayer-page-calm #my-prayers-list .friend-card,
  .app-shell .prayer-page-calm #others-prayers-list .friend-card,
  .app-shell .prayer-page-calm #answered-prayers-section .testimony-entry{
    border:1.5px solid rgba(125,174,212,.46)!important;
    outline:1px solid rgba(219,234,254,.78)!important;
    outline-offset:2px!important;
    background:
      linear-gradient(145deg,rgba(255,253,246,.96),rgba(246,250,255,.68))!important;
    box-shadow:
      0 10px 22px rgba(67,82,100,.06),
      inset 0 1px 0 rgba(255,255,255,.84)!important;
  }
  .app-shell .prayer-page-calm #my-prayers-list .friend-card.prayed,
  .app-shell .prayer-page-calm #others-prayers-list .friend-card.prayed,
  .app-shell .prayer-page-calm #answered-prayers-section .testimony-entry.public-open{
    border-color:rgba(91,143,184,.58)!important;
    outline-color:rgba(191,219,254,.88)!important;
    background:
      linear-gradient(145deg,rgba(255,253,246,.98),rgba(240,249,255,.78))!important;
  }
  .app-shell .prayer-page-calm #my-prayers-list .scroll-list,
  .app-shell .prayer-page-calm #others-prayers-list .scroll-list,
  .app-shell .prayer-page-calm #answered-prayers-section .answered-prayer-stack{
    padding:4px 5px 8px!important;
  }
  .app-shell .prayer-page-calm #my-prayers-list .friend-card + .friend-card,
  .app-shell .prayer-page-calm #others-prayers-list .friend-card + .friend-card{
    margin-top:10px!important;
  }
  @media(max-width:520px){
    .app-shell .prayer-page-calm #my-prayers-list .friend-card,
    .app-shell .prayer-page-calm #others-prayers-list .friend-card,
    .app-shell .prayer-page-calm #answered-prayers-section .testimony-entry{
      border-width:1.25px!important;
      outline-offset:1px!important;
      box-shadow:0 7px 16px rgba(67,82,100,.05),inset 0 1px 0 rgba(255,255,255,.82)!important;
    }
  }

  /* Unified prayer composer: calmer open form treatment. */
  .app-shell .prayer-page-calm #unified-prayer-request{
    scroll-margin-top:18px!important;
  }
  .app-shell .prayer-page-calm .unified-prayer-composer-card{
    margin:2px 0 24px!important;
    padding:6px 2px 2px!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
  }
  .app-shell .prayer-page-calm .unified-prayer-heading{
    margin:0 0 24px!important;
    padding:0 2px!important;
  }
  .app-shell .prayer-page-calm .unified-prayer-heading h3{
    margin:0 0 6px!important;
    color:#3A2812!important;
    font-size:clamp(24px,6vw,31px)!important;
    font-weight:500!important;
    line-height:1.1!important;
    letter-spacing:0!important;
  }
  .app-shell .prayer-page-calm .unified-prayer-heading p{
    margin:0!important;
    max-width:33rem!important;
    color:#7d6949!important;
    font-size:14.5px!important;
    line-height:1.55!important;
    font-weight:500!important;
  }
  .app-shell .prayer-page-calm .prayer-kind-label{
    margin:0 0 10px!important;
    color:#4A3212!important;
    font-size:14px!important;
    font-weight:650!important;
    letter-spacing:0!important;
  }
  .app-shell .prayer-page-calm .prayer-kind-choice-row{
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:8px!important;
    margin:0 0 28px!important;
  }
  .app-shell .prayer-page-calm .prayer-kind-choice-row button{
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    min-height:48px!important;
    border-radius:999px!important;
    border:1.25px solid rgba(231,207,159,.42)!important;
    background:rgba(255,253,246,.72)!important;
    color:#4A3212!important;
    padding:10px 13px!important;
    cursor:pointer!important;
    text-align:center!important;
    box-shadow:none!important;
    transition:background .18s,border-color .18s,box-shadow .18s,transform .18s!important;
  }
  .app-shell .prayer-page-calm .prayer-kind-choice-row button.active{
    border-color:rgba(125,174,212,.72)!important;
    background:linear-gradient(145deg,rgba(240,249,255,.96),rgba(255,253,246,.88))!important;
    box-shadow:0 6px 14px rgba(67,82,100,.06)!important;
  }
  .app-shell .prayer-page-calm .prayer-kind-choice-row button:focus-visible,
  .app-shell .prayer-page-calm .quick-prayer-toggle:focus-visible,
  .app-shell .prayer-page-calm .quick-prayer-chip:focus-within,
  .app-shell .prayer-page-calm .prayer-commitment-options button:focus-visible{
    outline:2px solid rgba(91,143,184,.72)!important;
    outline-offset:3px!important;
  }
  .app-shell .prayer-page-calm .prayer-kind-choice-row span{
    font-size:14px!important;
    font-weight:650!important;
    line-height:1.15!important;
  }
  .app-shell .prayer-page-calm .unified-prayer-fields{
    display:flex!important;
    flex-direction:column!important;
    gap:22px!important;
  }
  .app-shell .prayer-page-calm .unified-prayer-fields .sg-input,
  .app-shell .prayer-page-calm .unified-prayer-fields .sg-textarea{
    border:1.25px solid rgba(231,207,159,.48)!important;
    background:rgba(255,253,246,.78)!important;
    border-radius:16px!important;
    padding:14px 15px!important;
    box-shadow:none!important;
    color:#3A2812!important;
  }
  .app-shell .prayer-page-calm .unified-prayer-fields .sg-input:focus,
  .app-shell .prayer-page-calm .unified-prayer-fields .sg-textarea:focus{
    border-color:rgba(125,174,212,.78)!important;
    box-shadow:0 0 0 3px rgba(219,234,254,.62)!important;
  }
  .app-shell .prayer-page-calm .unified-prayer-fields .sg-textarea::placeholder,
  .app-shell .prayer-page-calm .unified-prayer-fields .sg-input::placeholder{
    color:rgba(90,78,58,.62)!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-box{
    display:flex!important;
    flex-direction:column!important;
    gap:12px!important;
    padding:0!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-toggle{
    width:100%!important;
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:12px!important;
    padding:0!important;
    border:0!important;
    background:transparent!important;
    color:#3A2812!important;
    font-family:'Nunito',sans-serif!important;
    cursor:pointer!important;
    text-align:left!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-title-wrap{
    display:flex!important;
    align-items:baseline!important;
    gap:8px!important;
    min-width:0!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-title,
  .app-shell .prayer-page-calm .quick-prayer-toggle span:first-child{
    font-size:14px!important;
    font-weight:650!important;
    color:#4A3212!important;
    line-height:1.2!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-optional{
    font-size:11.5px!important;
    font-weight:500!important;
    color:#8a714f!important;
    line-height:1.2!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-meta{
    flex-shrink:0!important;
    font-size:12px!important;
    font-weight:600!important;
    color:#6f8798!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-summary{
    margin:0!important;
    color:#7d6949!important;
    font-size:12.5px!important;
    line-height:1.45!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-grid{
    display:flex!important;
    flex-wrap:wrap!important;
    gap:8px!important;
    margin:0!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-chip{
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    min-height:36px!important;
    border:1px solid rgba(231,207,159,.38)!important;
    border-radius:999px!important;
    background:rgba(255,253,246,.78)!important;
    padding:8px 12px!important;
    color:#4A3212!important;
    font-size:12.5px!important;
    font-weight:600!important;
    line-height:1.1!important;
    cursor:pointer!important;
    box-shadow:none!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-chip.selected{
    border-color:rgba(125,174,212,.62)!important;
    background:linear-gradient(135deg,rgba(240,249,255,.96),rgba(255,248,223,.58))!important;
    box-shadow:0 4px 10px rgba(67,82,100,.05)!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-chip input{
    position:absolute!important;
    opacity:0!important;
    pointer-events:none!important;
  }
  .app-shell .prayer-page-calm .quick-prayer-chip span{
    overflow:visible!important;
    white-space:nowrap!important;
    text-overflow:clip!important;
  }
  .app-shell .prayer-page-calm .prayer-commitment-heading{
    margin:4px 0 -10px!important;
  }
  .app-shell .prayer-page-calm .prayer-commitment-heading div{
    color:#4A3212!important;
    font-size:14px!important;
    font-weight:650!important;
    line-height:1.25!important;
  }
  .app-shell .prayer-page-calm .prayer-commitment-heading p{
    margin:5px 0 0!important;
    color:#7d6949!important;
    font-size:12.5px!important;
    line-height:1.45!important;
    font-weight:500!important;
  }
  .app-shell .prayer-page-calm .prayer-commitment-picker{
    display:flex!important;
    flex-direction:column!important;
    gap:10px!important;
  }
  .app-shell .prayer-page-calm .prayer-commitment-options{
    display:grid!important;
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
    gap:8px!important;
  }
  .app-shell .prayer-page-calm .prayer-commitment-options button{
    min-height:42px!important;
    border-radius:999px!important;
    border:1.25px solid rgba(231,207,159,.42)!important;
    background:rgba(255,253,246,.72)!important;
    color:#5f4d32!important;
    font-size:13px!important;
    font-weight:650!important;
    cursor:pointer!important;
    box-shadow:none!important;
  }
  .app-shell .prayer-page-calm .prayer-commitment-options button.selected{
    border-color:rgba(201,154,61,.82)!important;
    background:linear-gradient(135deg,rgba(255,247,220,.98),rgba(232,198,111,.78))!important;
    color:#3A2812!important;
    box-shadow:0 6px 14px rgba(174,127,43,.10)!important;
  }
  .app-shell .prayer-page-calm .prayer-commitment-note{
    margin:0!important;
    color:#7d6949!important;
    font-size:12.5px!important;
    line-height:1.45!important;
    font-weight:500!important;
  }
  .app-shell .prayer-page-calm .unified-prayer-fields .gold-btn{
    width:100%!important;
    min-height:50px!important;
    margin-top:2px!important;
    border-radius:16px!important;
    color:#3A2812!important;
    font-size:14px!important;
    font-weight:800!important;
    box-shadow:0 10px 22px rgba(174,127,43,.16)!important;
  }
  .app-shell .prayer-page-calm .unified-prayer-fields .gold-btn:disabled{
    opacity:.56!important;
    box-shadow:none!important;
  }
  @media(max-width:380px){
    .app-shell .prayer-page-calm .unified-prayer-heading h3{font-size:23px!important;}
    .app-shell .prayer-page-calm .prayer-kind-choice-row button{min-height:46px!important;padding:9px 10px!important;}
    .app-shell .prayer-page-calm .quick-prayer-chip{padding:8px 10px!important;font-size:12px!important;}
  }


  /* Final home calm polish: remove prayer lights and define homes. */
  .app-shell .home-page .home-room{
    background:#f8fbff!important;
    border:1.25px solid rgba(125,174,212,.20)!important;
    box-shadow:0 12px 28px rgba(67,82,100,.06),inset 0 -24px 34px rgba(111,132,74,.07)!important;
  }
  .app-shell .home-page .home-room .home-rays,
  .app-shell .home-page .home-room .home-sunbeam-drift,
  .app-shell .home-page .home-room .home-prayer-light-trail,
  .app-shell .home-page .home-room .home-prayer-glow,
  .app-shell .home-page .home-room .home-walk-blessing,
  .app-shell .home-page .home-room .home-path-gem,
  .app-shell .home-page .home-room .home-gem{
    display:none!important;
  }
  .app-shell .home-page .home-room .home-care-aura{
    opacity:.22!important;
    background:radial-gradient(circle at 50% 42%,rgba(255,253,246,.34),transparent 60%)!important;
  }
  .app-shell .home-page .home-room .home-sky{
    background:linear-gradient(180deg,#dff3ff 0%,#f7fbff 42%,#fff6d7 100%)!important;
  }
  .app-shell .home-page .home-room .home-sky::after{
    content:""!important;
    position:absolute!important;
    left:0!important;
    right:0!important;
    bottom:-1px!important;
    height:70px!important;
    background:linear-gradient(180deg,rgba(255,255,255,0),rgba(255,253,246,.72))!important;
    pointer-events:none!important;
  }
  .app-shell .home-page .home-room .home-cloud{
    background:transparent!important;
    opacity:.82!important;
    filter:drop-shadow(0 8px 10px rgba(98,126,145,.08))!important;
  }
  .app-shell .home-page .home-room .home-cloud::before{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    border-radius:999px!important;
    background:
      radial-gradient(circle at 17% 64%,rgba(255,255,255,.98) 0 22%,transparent 23%),
      radial-gradient(circle at 34% 42%,rgba(255,255,255,1) 0 30%,transparent 31%),
      radial-gradient(circle at 55% 48%,rgba(255,255,255,.98) 0 34%,transparent 35%),
      radial-gradient(circle at 77% 62%,rgba(255,255,255,.94) 0 24%,transparent 25%),
      linear-gradient(180deg,rgba(255,255,255,.86),rgba(255,255,255,.58))!important;
  }
  .app-shell .home-page .home-room .home-cloud::after{
    content:""!important;
    position:absolute!important;
    left:8%!important;
    right:8%!important;
    bottom:-8px!important;
    height:22px!important;
    border-radius:999px!important;
    background:radial-gradient(ellipse,rgba(98,126,145,.10),transparent 70%)!important;
    filter:blur(5px)!important;
    opacity:.5!important;
  }
  .app-shell .home-page .home-room .home-cloud.c1{width:150px!important;height:58px!important;left:-32px!important;top:74px!important;}
  .app-shell .home-page .home-room .home-cloud.c2{width:188px!important;height:66px!important;right:-48px!important;top:98px!important;opacity:.72!important;}
  .app-shell .home-page .home-room .home-cloud.c3{width:132px!important;height:50px!important;left:35%!important;top:46px!important;opacity:.54!important;}
  .app-shell .home-page .home-room .home-distant-city{
    opacity:.10!important;
    filter:blur(.2px)!important;
  }
  .app-shell .home-page .home-room .home-background-rainbow{
    opacity:.20!important;
    filter:none!important;
  }
  .app-shell .home-page .home-room .home-background-rainbow::after{display:none!important;}
  .app-shell .home-page .home-room .home-floor{
    height:43%!important;
    background:linear-gradient(180deg,#f8f5d8 0%,#dcefca 48%,#a7ca82 100%)!important;
    border-top:1px solid rgba(93,125,79,.16)!important;
  }
  .app-shell .home-page .home-room .home-floor::before{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    background:linear-gradient(90deg,rgba(255,255,255,.18) 0 1px,transparent 1px 48px),linear-gradient(180deg,rgba(93,125,79,.06) 0 1px,transparent 1px 42px)!important;
    opacity:.36!important;
  }
  .app-shell .home-page .home-room .home-midground-glow{
    left:16%!important;
    right:16%!important;
    bottom:37%!important;
    height:52px!important;
    opacity:.34!important;
    background:radial-gradient(ellipse,rgba(255,253,246,.56),rgba(255,253,246,0) 72%)!important;
  }
  .app-shell .home-page .home-room .home-path{
    top:55%!important;
    bottom:auto!important;
    height:46%!important;
    width:min(31%,126px)!important;
    background:linear-gradient(180deg,rgba(255,253,246,.74),rgba(231,190,89,.42) 70%,rgba(147,110,43,.42))!important;
    border-left:1px solid rgba(116,84,28,.10)!important;
    border-right:1px solid rgba(116,84,28,.10)!important;
    box-shadow:inset 0 0 14px rgba(255,255,255,.36)!important;
  }
  .app-shell .home-page .home-room .home-path::before{display:none!important;}
  .app-shell .home-page .home-room .home-path::after{
    background:repeating-linear-gradient(180deg,transparent 0 27px,rgba(116,84,28,.10) 28px 29px)!important;
    opacity:.22!important;
  }
  .app-shell .home-page .home-room .home-signature-vessel,
  .app-shell .home-page .home-room .home-word-scroll{
    opacity:.34!important;
    z-index:3!important;
  }
  .app-shell .home-page .home-room .home-cardboard,
  .app-shell .home-page .home-room .home-little-shelter,
  .app-shell .home-page .home-room .home-house,
  .app-shell .home-page .home-room .home-mansion,
  .app-shell .home-page .home-room .home-palace,
  .app-shell .home-page .home-room .home-chapel{
    z-index:22!important;
    filter:drop-shadow(0 18px 18px rgba(82,55,15,.18)) drop-shadow(0 0 10px rgba(255,253,246,.28))!important;
  }
  .app-shell .home-page .home-room .home-cardboard-roof,
  .app-shell .home-page .home-room .home-little-shelter-roof,
  .app-shell .home-page .home-room .home-house-roof,
  .app-shell .home-page .home-room .home-mansion-roof,
  .app-shell .home-page .home-room .home-palace-roof{
    background:repeating-linear-gradient(112deg,rgba(255,255,255,.16) 0 8px,rgba(91,55,12,.08) 8px 12px),linear-gradient(180deg,#ffeab0 0%,#d59d35 58%,#875914 100%)!important;
    filter:drop-shadow(0 7px 7px rgba(82,55,15,.18))!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{
    background:radial-gradient(circle at 24% 16%,rgba(255,255,255,.72),transparent 18%),linear-gradient(180deg,#fffdf8 0%,#fff0c8 55%,#cf9942 100%)!important;
    border:1.5px solid rgba(101,71,22,.28)!important;
    box-shadow:inset 0 9px 15px rgba(255,255,255,.72),inset 0 -10px 14px rgba(82,55,15,.09),0 0 14px rgba(255,253,246,.22)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body{
    background:linear-gradient(180deg,#e7be7e 0%,#b97838 100%)!important;
  }
  .app-shell .home-page .home-room .home-window-dot,
  .app-shell .home-page .home-room .home-little-shelter-window,
  .app-shell .home-page .home-room .home-mansion-window{
    background:linear-gradient(90deg,transparent 47%,rgba(90,56,12,.22) 48% 52%,transparent 53%),linear-gradient(180deg,transparent 47%,rgba(90,56,12,.18) 48% 52%,transparent 53%),radial-gradient(circle at 50% 25%,#fff 0,#fff6d6 36%,#edbf5e 100%)!important;
    border:1px solid rgba(90,56,12,.24)!important;
    box-shadow:0 0 10px rgba(255,248,223,.58),inset 0 2px 5px rgba(255,255,255,.78)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-door,
  .app-shell .home-page .home-room .home-little-shelter-door,
  .app-shell .home-page .home-room .home-house-door,
  .app-shell .home-page .home-room .home-mansion-door,
  .app-shell .home-page .home-room .home-door{
    background:radial-gradient(circle at 50% 17%,#fff 0,#fff2c2 31%,#c98b24 72%,#6e4510 100%)!important;
    border:1.5px solid rgba(82,55,15,.32)!important;
    box-shadow:0 0 14px rgba(255,248,223,.56),inset 0 5px 7px rgba(255,255,255,.52)!important;
  }
  .app-shell .home-page .home-room .home-cardboard::after,
  .app-shell .home-page .home-room .home-little-shelter::after,
  .app-shell .home-page .home-room .home-house::after,
  .app-shell .home-page .home-room .home-mansion::after,
  .app-shell .home-page .home-room .home-palace::after{
    content:""!important;
    position:absolute!important;
    left:10%!important;
    right:10%!important;
    bottom:-13px!important;
    height:18px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse,rgba(62,42,16,.18),rgba(62,42,16,0) 72%)!important;
    z-index:-1!important;
  }
  .app-shell .home-page .home-room .home-flowerbed{
    opacity:.76!important;
    filter:saturate(.9) brightness(1.02)!important;
  }
  .app-shell .home-page .home-room .home-spirit-slot{
    z-index:48!important;
  }
  @media(max-width:560px){
    .app-shell .home-page .home-room .home-house{top:31%!important;width:200px!important;}
    .app-shell .home-page .home-room .home-mansion{top:26%!important;width:224px!important;}
    .app-shell .home-page .home-room .home-palace{top:22%!important;width:236px!important;}
    .app-shell .home-page .home-room .home-path{top:56%!important;width:min(31%,118px)!important;}
  }


  /* Recorded prayers: planted prayer cards. */
  .app-shell .prayer-page-calm .recorded-prayers-header{
    display:flex!important;
    justify-content:space-between!important;
    align-items:flex-start!important;
    gap:12px!important;
    margin-bottom:14px!important;
  }
  .app-shell .prayer-page-calm .recorded-prayers-header h3{
    margin:0!important;
    color:#3A2812!important;
    font-size:clamp(22px,5.6vw,28px)!important;
    font-weight:500!important;
    line-height:1.08!important;
    letter-spacing:0!important;
  }
  .app-shell .prayer-page-calm .recorded-prayers-header p{
    margin:5px 0 0!important;
    color:#7d6949!important;
    font-size:13.5px!important;
    line-height:1.45!important;
    font-weight:500!important;
  }
  .app-shell .prayer-page-calm .prayer-list-filter-row{
    display:flex!important;
    gap:7px!important;
    overflow-x:auto!important;
    padding:0 1px 12px!important;
    margin-bottom:4px!important;
    -webkit-overflow-scrolling:touch!important;
  }
  .app-shell .prayer-page-calm .prayer-list-filter-row button{
    flex:0 0 auto!important;
    min-height:34px!important;
    border-radius:999px!important;
    border:1px solid rgba(231,207,159,.38)!important;
    background:rgba(255,253,246,.72)!important;
    color:#6f5c3d!important;
    padding:7px 12px!important;
    font-size:12px!important;
    font-weight:650!important;
    cursor:pointer!important;
  }
  .app-shell .prayer-page-calm .prayer-list-filter-row button.active{
    border-color:rgba(125,174,212,.62)!important;
    background:linear-gradient(145deg,rgba(240,249,255,.96),rgba(255,253,246,.86))!important;
    color:#3A2812!important;
  }
  .app-shell .prayer-page-calm #my-prayers-list .planted-prayer-card,
  .app-shell .prayer-page-calm #others-prayers-list .planted-prayer-card{
    position:relative!important;
    overflow:visible!important;
    border:1px solid rgba(231,207,159,.34)!important;
    outline:0!important;
    outline-offset:0!important;
    border-radius:22px!important;
    padding:16px!important;
    background:linear-gradient(145deg,rgba(255,253,246,.96),rgba(255,251,235,.64))!important;
    box-shadow:0 8px 18px rgba(67,82,100,.045),inset 0 1px 0 rgba(255,255,255,.82)!important;
  }
  .app-shell .prayer-page-calm #my-prayers-list .planted-prayer-card.prayed,
  .app-shell .prayer-page-calm #others-prayers-list .planted-prayer-card.prayed{
    border-color:rgba(125,174,212,.40)!important;
    background:linear-gradient(145deg,rgba(255,253,246,.98),rgba(240,249,255,.58))!important;
    animation:prayerCardCompleted .55s ease-out both!important;
  }
  @keyframes prayerCardCompleted{
    0%{transform:scale(.992);box-shadow:0 0 0 rgba(125,174,212,0);}
    55%{transform:scale(1.006);box-shadow:0 12px 26px rgba(125,174,212,.10);}
    100%{transform:scale(1);}
  }
  .app-shell .prayer-page-calm .planted-prayer-topline{
    display:flex!important;
    justify-content:space-between!important;
    align-items:center!important;
    gap:10px!important;
    margin-bottom:13px!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-category{
    display:inline-flex!important;
    align-items:center!important;
    gap:6px!important;
    color:#7d6949!important;
    font-size:11.5px!important;
    font-weight:650!important;
    letter-spacing:.03em!important;
    text-transform:uppercase!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-category span{
    color:#C99A3D!important;
    font-size:13px!important;
  }
  .app-shell .prayer-page-calm .prayer-status-chip{
    border:0!important;
    border-radius:999px!important;
    padding:5px 9px!important;
    background:rgba(240,249,255,.72)!important;
    color:#5f7f98!important;
    font-size:11px!important;
    font-weight:650!important;
    line-height:1!important;
    box-shadow:none!important;
  }
  .app-shell .prayer-page-calm .prayer-status-chip.status-answered{
    background:rgba(236,253,245,.72)!important;
    color:#557A5B!important;
  }
  .app-shell .prayer-page-calm .prayer-status-chip.status-growing,
  .app-shell .prayer-page-calm .prayer-status-chip.status-praying{
    background:rgba(255,248,223,.62)!important;
    color:#8a714f!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-body{
    display:grid!important;
    grid-template-columns:46px minmax(0,1fr)!important;
    gap:12px!important;
    align-items:start!important;
    margin-bottom:16px!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-copy h4{
    margin:0 0 7px!important;
    color:#3A2812!important;
    font-size:17px!important;
    font-weight:650!important;
    line-height:1.18!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-copy p{
    margin:0!important;
    color:#4f432f!important;
    font-size:14.5px!important;
    line-height:1.62!important;
    font-weight:500!important;
    white-space:pre-wrap!important;
  }
  .app-shell .prayer-page-calm .prayer-seedling{
    position:relative!important;
    width:42px!important;
    height:58px!important;
    border-radius:999px!important;
    background:linear-gradient(180deg,rgba(236,253,245,.56),rgba(255,248,223,.34))!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.82)!important;
  }
  .app-shell .prayer-page-calm .seedling-soil{
    position:absolute!important;
    left:9px!important;
    right:9px!important;
    bottom:10px!important;
    height:7px!important;
    border-radius:50%!important;
    background:rgba(138,106,56,.30)!important;
  }
  .app-shell .prayer-page-calm .seedling-stem{
    position:absolute!important;
    left:50%!important;
    bottom:16px!important;
    width:3px!important;
    height:18px!important;
    border-radius:999px!important;
    transform:translateX(-50%)!important;
    background:linear-gradient(180deg,#86b870,#557A5B)!important;
  }
  .app-shell .prayer-page-calm .seedling-leaf{
    position:absolute!important;
    bottom:27px!important;
    width:13px!important;
    height:8px!important;
    border-radius:999px 999px 999px 0!important;
    background:#86b870!important;
    opacity:.0!important;
  }
  .app-shell .prayer-page-calm .seedling-leaf.left{left:10px!important;transform:rotate(-30deg)!important;}
  .app-shell .prayer-page-calm .seedling-leaf.right{right:10px!important;transform:scaleX(-1) rotate(-30deg)!important;}
  .app-shell .prayer-page-calm .seedling-bloom{
    position:absolute!important;
    left:50%!important;
    top:11px!important;
    width:13px!important;
    height:13px!important;
    border-radius:50%!important;
    transform:translateX(-50%) scale(.5)!important;
    background:radial-gradient(circle,#fffdf6 0 23%,#f6cf72 24% 42%,#f9a8d4 43% 100%)!important;
    opacity:0!important;
  }
  .app-shell .prayer-page-calm .seedling-sprout .seedling-leaf.left{opacity:.72!important;}
  .app-shell .prayer-page-calm .seedling-flowering .seedling-leaf,
  .app-shell .prayer-page-calm .seedling-bloom .seedling-leaf{opacity:.86!important;}
  .app-shell .prayer-page-calm .seedling-flowering .seedling-bloom{opacity:.55!important;transform:translateX(-50%) scale(.78)!important;}
  .app-shell .prayer-page-calm .seedling-bloom .seedling-bloom{opacity:1!important;transform:translateX(-50%) scale(1)!important;}
  .app-shell .prayer-page-calm .prayer-journey-section{
    margin:0 0 16px!important;
    padding:12px!important;
    border-radius:17px!important;
    background:rgba(255,253,246,.56)!important;
  }
  .app-shell .prayer-page-calm .prayer-journey-heading,
  .app-shell .prayer-page-calm .prayer-journey-meta{
    display:flex!important;
    justify-content:space-between!important;
    gap:10px!important;
    align-items:center!important;
  }
  .app-shell .prayer-page-calm .prayer-journey-heading span:first-child{
    color:#4A3212!important;
    font-size:12.5px!important;
    font-weight:700!important;
  }
  .app-shell .prayer-page-calm .prayer-journey-heading span:last-child,
  .app-shell .prayer-page-calm .prayer-journey-meta span{
    color:#7d6949!important;
    font-size:11.5px!important;
    font-weight:600!important;
  }
  .app-shell .prayer-page-calm .prayer-journey-bar{
    height:7px!important;
    border-radius:999px!important;
    overflow:hidden!important;
    margin:9px 0 8px!important;
    background:rgba(231,207,159,.34)!important;
  }
  .app-shell .prayer-page-calm .prayer-journey-bar span{
    display:block!important;
    height:100%!important;
    border-radius:inherit!important;
    background:linear-gradient(90deg,#86b870,#E8C66F)!important;
    transition:width .35s ease!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-actions{
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto!important;
    gap:8px!important;
    align-items:center!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-primary{
    min-height:46px!important;
    border-radius:15px!important;
    font-weight:800!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-primary.prayed{
    background:linear-gradient(135deg,rgba(236,253,245,.94),rgba(240,249,255,.78))!important;
    color:#557A5B!important;
    border-color:rgba(134,190,155,.34)!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-more{
    position:relative!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-more summary{
    list-style:none!important;
    width:44px!important;
    height:44px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    border-radius:14px!important;
    border:1px solid rgba(231,207,159,.42)!important;
    background:rgba(255,253,246,.72)!important;
    color:#6f5c3d!important;
    cursor:pointer!important;
    font-weight:800!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-more summary::-webkit-details-marker{display:none!important;}
  .app-shell .prayer-page-calm .planted-prayer-menu{
    position:absolute!important;
    right:0!important;
    bottom:50px!important;
    z-index:20!important;
    min-width:150px!important;
    border:1px solid rgba(231,207,159,.44)!important;
    border-radius:15px!important;
    background:rgba(255,253,246,.98)!important;
    box-shadow:0 14px 30px rgba(67,82,100,.12)!important;
    padding:6px!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-menu button{
    width:100%!important;
    min-height:38px!important;
    border:0!important;
    border-radius:10px!important;
    background:transparent!important;
    color:#4A3212!important;
    text-align:left!important;
    padding:8px 10px!important;
    font-size:12.5px!important;
    font-weight:650!important;
    cursor:pointer!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-menu button:hover,
  .app-shell .prayer-page-calm .planted-prayer-menu button:focus-visible{
    background:rgba(255,248,223,.62)!important;
    outline:none!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-menu button.danger{
    color:#A04444!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-edit{
    display:flex!important;
    flex-direction:column!important;
    gap:10px!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-edit-actions{
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    gap:8px!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-edit-actions > button:not(.gold-btn){
    min-height:42px!important;
    border:1px solid rgba(231,207,159,.42)!important;
    border-radius:13px!important;
    background:rgba(255,253,246,.74)!important;
    color:#7d6949!important;
    font-weight:750!important;
    cursor:pointer!important;
  }
  /* Hard fix: keep recorded prayer text from collapsing into stacked words. */
  .app-shell .prayer-page-calm #my-prayers-list .planted-prayer-card,
  .app-shell .prayer-page-calm #others-prayers-list .planted-prayer-card{
    display:block!important;
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-body{
    display:block!important;
    position:relative!important;
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    padding-right:0!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-copy{
    display:block!important;
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-copy h4,
  .app-shell .prayer-page-calm .planted-prayer-copy p{
    display:block!important;
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    white-space:normal!important;
    overflow-wrap:break-word!important;
    word-break:normal!important;
    hyphens:none!important;
  }
  .app-shell .prayer-page-calm .prayer-seedling{
    position:absolute!important;
    right:0!important;
    top:-4px!important;
    transform:scale(.78)!important;
    transform-origin:top right!important;
    opacity:.24!important;
    pointer-events:none!important;
  }
  .app-shell .prayer-page-calm .planted-prayer-copy h4{
    padding-right:42px!important;
  }

  @media(max-width:430px){
    .app-shell .prayer-page-calm #my-prayers-list,
    .app-shell .prayer-page-calm #others-prayers-list{padding:14px!important;}
    .app-shell .prayer-page-calm #my-prayers-list .planted-prayer-card,
    .app-shell .prayer-page-calm #others-prayers-list .planted-prayer-card{padding:14px!important;border-radius:20px!important;}
    .app-shell .prayer-page-calm .planted-prayer-body{grid-template-columns:40px minmax(0,1fr)!important;gap:10px!important;}
    .app-shell .prayer-page-calm .prayer-seedling{width:38px!important;height:54px!important;}
    .app-shell .prayer-page-calm .planted-prayer-copy p{font-size:14px!important;}
  }


  /* Final Home definition pass: make each stage read as a real, dimensional home. */
  .app-shell .home-page .home-room .home-cardboard,
  .app-shell .home-page .home-room .home-little-shelter,
  .app-shell .home-page .home-room .home-house,
  .app-shell .home-page .home-room .home-mansion,
  .app-shell .home-page .home-room .home-palace{
    filter:drop-shadow(0 20px 18px rgba(72,48,16,.22)) drop-shadow(0 2px 0 rgba(255,255,255,.32))!important;
  }
  .app-shell .home-page .home-room .home-cardboard::before,
  .app-shell .home-page .home-room .home-little-shelter::before,
  .app-shell .home-page .home-room .home-house::before,
  .app-shell .home-page .home-room .home-mansion::before,
  .app-shell .home-page .home-room .home-palace::before{
    content:""!important;
    display:block!important;
    position:absolute!important;
    left:8%!important;
    right:8%!important;
    bottom:-10px!important;
    height:24px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse,rgba(68,45,16,.20),rgba(68,45,16,0) 72%)!important;
    z-index:-2!important;
    pointer-events:none!important;
  }
  .app-shell .home-page .home-room .home-cardboard::after,
  .app-shell .home-page .home-room .home-little-shelter::after,
  .app-shell .home-page .home-room .home-house::after,
  .app-shell .home-page .home-room .home-mansion::after,
  .app-shell .home-page .home-room .home-palace::after{
    content:""!important;
    display:block!important;
    position:absolute!important;
    left:15%!important;
    right:15%!important;
    bottom:-4px!important;
    height:9px!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,transparent,rgba(255,253,246,.58),transparent)!important;
    z-index:1!important;
    pointer-events:none!important;
  }

  .app-shell .home-page .home-room .home-cardboard-roof,
  .app-shell .home-page .home-room .home-little-shelter-roof,
  .app-shell .home-page .home-room .home-house-roof,
  .app-shell .home-page .home-room .home-mansion-roof,
  .app-shell .home-page .home-room .home-palace-roof{
    background:
      linear-gradient(180deg,rgba(255,255,255,.38),transparent 26%),
      repeating-linear-gradient(115deg,rgba(255,255,255,.18) 0 8px,rgba(94,58,12,.16) 8px 11px,rgba(181,120,24,.10) 11px 18px),
      linear-gradient(180deg,#ffe7a2 0%,#d9a13a 55%,#7f4f12 100%)!important;
    border-bottom:2px solid rgba(81,52,14,.28)!important;
    box-shadow:inset 0 3px 0 rgba(255,255,255,.36),inset 0 -8px 10px rgba(93,56,11,.18),0 9px 11px rgba(72,48,16,.18)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-roof::after,
  .app-shell .home-page .home-room .home-little-shelter-roof::after,
  .app-shell .home-page .home-room .home-house-roof::after,
  .app-shell .home-page .home-room .home-mansion-roof::after,
  .app-shell .home-page .home-room .home-palace-roof::after{
    content:""!important;
    position:absolute!important;
    left:8%!important;
    right:8%!important;
    bottom:8px!important;
    height:4px!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,rgba(255,253,246,.12),rgba(255,253,246,.72),rgba(255,253,246,.12))!important;
    opacity:.82!important;
  }

  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{
    background:
      linear-gradient(90deg,rgba(255,255,255,.18) 0 1px,transparent 1px 28px),
      linear-gradient(180deg,rgba(255,255,255,.56),rgba(255,255,255,0) 24%),
      linear-gradient(180deg,#fffdf7 0%,#f8e8bd 45%,#dfb45f 74%,#bd8428 100%)!important;
    border:2px solid rgba(82,55,15,.26)!important;
    box-shadow:
      inset 0 11px 15px rgba(255,255,255,.72),
      inset 8px 0 12px rgba(255,255,255,.30),
      inset -8px 0 14px rgba(93,56,11,.10),
      inset 0 -14px 16px rgba(82,55,15,.13),
      0 0 0 1px rgba(255,255,255,.30)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body{
    background:
      repeating-linear-gradient(90deg,rgba(93,56,11,.10) 0 1px,transparent 1px 22px),
      linear-gradient(180deg,#eec88b 0%,#c98d48 58%,#9c632d 100%)!important;
  }

  .app-shell .home-page .home-room .home-house-body::before,
  .app-shell .home-page .home-room .home-little-shelter-body::before,
  .app-shell .home-page .home-room .home-mansion-body::before,
  .app-shell .home-page .home-room .home-palace-body::before{
    content:""!important;
    display:block!important;
    position:absolute!important;
    left:12%!important;
    right:12%!important;
    top:9px!important;
    height:8px!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,transparent,rgba(255,255,255,.68),transparent)!important;
    box-shadow:0 18px 0 -4px rgba(255,255,255,.24)!important;
    z-index:3!important;
  }
  .app-shell .home-page .home-room .home-house-body::after,
  .app-shell .home-page .home-room .home-little-shelter-body::after,
  .app-shell .home-page .home-room .home-mansion-body::after,
  .app-shell .home-page .home-room .home-palace-body::after{
    content:""!important;
    display:block!important;
    position:absolute!important;
    left:10px!important;
    right:10px!important;
    bottom:7px!important;
    height:7px!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,rgba(82,55,15,.12),rgba(255,253,246,.46),rgba(82,55,15,.12))!important;
    z-index:2!important;
  }

  .app-shell .home-page .home-room .home-window-dot,
  .app-shell .home-page .home-room .home-little-shelter-window,
  .app-shell .home-page .home-room .home-mansion-window{
    width:24px!important;
    height:32px!important;
    border-radius:11px 11px 5px 5px!important;
    background:
      linear-gradient(90deg,transparent 46%,rgba(76,52,18,.30) 47% 53%,transparent 54%),
      linear-gradient(180deg,transparent 48%,rgba(76,52,18,.22) 49% 53%,transparent 54%),
      radial-gradient(circle at 48% 20%,#fff 0,#fff8df 34%,#f0c75e 70%,#c99012 100%)!important;
    border:2px solid rgba(90,56,12,.30)!important;
    box-shadow:0 0 12px rgba(255,248,223,.72),inset 0 3px 5px rgba(255,255,255,.82),inset 0 -5px 8px rgba(138,90,8,.16)!important;
    z-index:7!important;
  }
  .app-shell .home-page .home-room .home-window-dot.left{left:50px!important;}
  .app-shell .home-page .home-room .home-window-dot.right{right:50px!important;}
  .app-shell .home-page .home-room.home-stage-2 .home-window-dot{top:80px!important;}

  .app-shell .home-page .home-room .home-cardboard-door,
  .app-shell .home-page .home-room .home-little-shelter-door,
  .app-shell .home-page .home-room .home-house-door,
  .app-shell .home-page .home-room .home-mansion-door,
  .app-shell .home-page .home-room .home-door{
    background:
      radial-gradient(circle at 50% 16%,rgba(255,255,255,.95) 0 8%,transparent 9%),
      linear-gradient(90deg,rgba(255,255,255,.24),transparent 36%,rgba(82,55,15,.12)),
      linear-gradient(180deg,#fff1ba 0%,#d19028 55%,#74470d 100%)!important;
    border:2px solid rgba(76,52,18,.34)!important;
    box-shadow:0 0 16px rgba(255,248,223,.66),inset 0 6px 8px rgba(255,255,255,.46),inset 0 -9px 10px rgba(82,55,15,.22)!important;
    z-index:8!important;
  }
  .app-shell .home-page .home-room .home-house-door::before,
  .app-shell .home-page .home-room .home-little-shelter-door::before,
  .app-shell .home-page .home-room .home-mansion-door::before,
  .app-shell .home-page .home-room .home-door::before{
    content:""!important;
    display:block!important;
    position:absolute!important;
    left:50%!important;
    top:10px!important;
    bottom:8px!important;
    width:1.5px!important;
    background:rgba(76,52,18,.22)!important;
    transform:translateX(-50%)!important;
  }
  .app-shell .home-page .home-room .home-house-door::after,
  .app-shell .home-page .home-room .home-little-shelter-door::after,
  .app-shell .home-page .home-room .home-mansion-door::after,
  .app-shell .home-page .home-room .home-door::after{
    content:""!important;
    display:block!important;
    position:absolute!important;
    right:8px!important;
    top:48%!important;
    width:5px!important;
    height:5px!important;
    border-radius:50%!important;
    background:#fff8df!important;
    box-shadow:0 0 8px rgba(255,248,223,.92),0 0 0 1px rgba(82,55,15,.16)!important;
  }

  .app-shell .home-page .home-room .home-palace-column{
    background:linear-gradient(90deg,#966219 0%,#fff4c9 32%,#d6a13a 58%,#794d10 100%)!important;
    border:1px solid rgba(76,52,18,.30)!important;
    box-shadow:inset 3px 0 5px rgba(255,255,255,.50),0 3px 8px rgba(82,55,15,.16)!important;
    z-index:9!important;
  }
  .app-shell .home-page .home-room .home-palace-balcony{
    height:15px!important;
    background:linear-gradient(180deg,#fff8df,#d99a12)!important;
    border:1px solid rgba(76,52,18,.24)!important;
    box-shadow:inset 0 4px 5px rgba(255,255,255,.56),0 4px 8px rgba(82,55,15,.14)!important;
    z-index:10!important;
    opacity:.96!important;
  }
  .app-shell .home-page .home-room .home-palace-steps{
    opacity:.96!important;
    background:repeating-linear-gradient(180deg,#fff8df 0 5px,#d99a12 5px 7px,#9a6b16 7px 8px)!important;
    border-color:rgba(76,52,18,.20)!important;
  }
  .app-shell .home-page .home-room .home-palace-star{
    color:#8a5a08!important;
    text-shadow:0 0 10px rgba(255,248,223,.95),0 2px 4px rgba(82,55,15,.18)!important;
  }
  .app-shell .home-page .home-room.home-stage-4 .home-palace,
  .app-shell .home-page .home-room.home-stage-5 .home-palace,
  .app-shell .home-page .home-room.home-stage-6 .home-palace{
    top:20%!important;
  }
  .app-shell .home-page .home-room.home-stage-6 .home-palace{
    top:18%!important;
  }
  @media(max-width:560px){
    .app-shell .home-page .home-room .home-window-dot{width:21px!important;height:29px!important;}
    .app-shell .home-page .home-room .home-window-dot.left{left:48px!important;}
    .app-shell .home-page .home-room .home-window-dot.right{right:48px!important;}
    .app-shell .home-page .home-room.home-stage-4 .home-palace,
    .app-shell .home-page .home-room.home-stage-5 .home-palace,
    .app-shell .home-page .home-room.home-stage-6 .home-palace{top:22%!important;}
  }


  /* Home cleanup: remove decorative shapes that visually overlap the house. */
  .app-shell .home-page .home-room .home-word-scroll,
  .app-shell .home-page .home-room .home-signature-vessel,
  .app-shell .home-page .home-room .home-gem,
  .app-shell .home-page .home-room .home-path-gem{
    display:none!important;
  }
  .app-shell .home-page .home-room .home-rays,
  .app-shell .home-page .home-room .home-sunbeam-drift{
    opacity:.08!important;
    filter:blur(1px)!important;
    mix-blend-mode:normal!important;
    z-index:0!important;
  }
  .app-shell .home-page .home-room .home-background-rainbow{
    opacity:.13!important;
    z-index:1!important;
  }
  .app-shell .home-page .home-room .home-distant-city{
    opacity:.16!important;
    z-index:1!important;
  }
  .app-shell .home-page .home-room .home-midground-glow{
    opacity:.18!important;
    z-index:2!important;
  }
  .app-shell .home-page .home-room .home-walk-blessing,
  .app-shell .home-page .home-room .home-reflection-butterflies,
  .app-shell .home-page .home-room .home-answer-light,
  .app-shell .home-page .home-room .home-answer-dove,
  .app-shell .home-page .home-room .home-testimony-particles{
    z-index:7!important;
    opacity:.54!important;
  }
  .app-shell .home-page .home-room .home-cardboard,
  .app-shell .home-page .home-room .home-little-shelter,
  .app-shell .home-page .home-room .home-house,
  .app-shell .home-page .home-room .home-mansion,
  .app-shell .home-page .home-room .home-palace{
    z-index:30!important;
  }
  .app-shell .home-page .home-room .home-spirit-slot{
    z-index:52!important;
  }




  /* Premium Heavenly Home page refinement: calmer spacing, framed art, and subtle atmosphere. */
  @keyframes homePageSoftRise{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
  @keyframes homeAmbientDrift{0%,100%{opacity:.05;transform:translateY(0) scale(.92);}50%{opacity:.11;transform:translateY(-10px) scale(1.05);}}
  .app-shell .home-page.simple-home-page{
    position:relative!important;
    isolation:isolate!important;
    gap:18px!important;
    padding-top:8px!important;
    background:
      radial-gradient(circle at 78% 4%,rgba(220,232,209,.22),transparent 22%),
      radial-gradient(circle at 18% 20%,rgba(255,236,178,.20),transparent 24%),
      linear-gradient(180deg,#FCF8EF 0%,#FFFDF7 42%,#FCF8EF 100%)!important;
    animation:homePageSoftRise .42s ease-out both!important;
  }
  .app-shell .home-page.simple-home-page::before{
    content:""!important;
    position:absolute!important;
    right:8px!important;
    top:10px!important;
    width:92px!important;
    height:128px!important;
    border-radius:64% 36% 58% 42%!important;
    background:
      radial-gradient(ellipse at 34% 30%,rgba(134,160,111,.18) 0 18%,transparent 19%),
      radial-gradient(ellipse at 58% 44%,rgba(134,160,111,.13) 0 16%,transparent 17%),
      linear-gradient(120deg,transparent 46%,rgba(134,160,111,.12) 47% 50%,transparent 51%)!important;
    opacity:.62!important;
    pointer-events:none!important;
    z-index:-1!important;
  }
  .app-shell .home-page.simple-home-page::after{
    content:"✦    ✧      ✦"!important;
    position:absolute!important;
    left:8%!important;
    right:8%!important;
    top:72px!important;
    color:#C99A3D!important;
    opacity:.08!important;
    font-size:13px!important;
    letter-spacing:34px!important;
    pointer-events:none!important;
    animation:homeAmbientDrift 8s ease-in-out infinite!important;
    z-index:-1!important;
  }
  .app-shell .home-page.simple-home-page .sticky-preview-card.simple-home-preview{
    position:relative!important;
    overflow:hidden!important;
    padding:24px!important;
    border:0!important;
    border-radius:28px!important;
    background:
      radial-gradient(circle at 50% 40%,rgba(255,248,223,.34),transparent 48%),
      linear-gradient(145deg,#FFFDF8 0%,#FFF9EA 100%)!important;
    box-shadow:0 18px 42px rgba(88,65,30,.09),inset 0 1px 0 rgba(255,255,255,.82)!important;
  }
  .app-shell .home-page.simple-home-page .sticky-preview-card.simple-home-preview::before{
    content:""!important;
    position:absolute!important;
    inset:12px!important;
    border-radius:24px!important;
    border:1px solid rgba(201,154,61,.10)!important;
    pointer-events:none!important;
  }
  .app-shell .home-page.simple-home-page .section-label{
    margin-bottom:8px!important;
    font-size:10.5px!important;
    letter-spacing:.19em!important;
    color:#B79A64!important;
    font-weight:700!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-preview h3{
    font-size:clamp(28px,7vw,39px)!important;
    line-height:1!important;
    margin-bottom:10px!important;
    color:#342313!important;
  }
  .app-shell .home-page.simple-home-page .home-stage-title-copy,
  .app-shell .home-page.simple-home-page .home-stage-signature-copy{
    max-width:32ch!important;
    color:#6F5C43!important;
    font-size:14.75px!important;
    line-height:1.55!important;
  }
  .app-shell .home-page.simple-home-page .home-stage-signature-copy{
    margin:5px 0 0!important;
  }
  .app-shell .home-page.simple-home-page .sticky-preview-label{
    display:inline-flex!important;
    align-items:center!important;
    min-height:32px!important;
    padding:6px 11px!important;
    border-radius:999px!important;
    border:1px solid rgba(201,154,61,.22)!important;
    background:rgba(255,253,246,.78)!important;
    color:#7E5A1E!important;
    font-size:11.5px!important;
    font-weight:700!important;
    letter-spacing:.01em!important;
    box-shadow:none!important;
    white-space:nowrap!important;
  }
  .app-shell .home-page.simple-home-page .home-faith-growth-indicator{
    margin-top:14px!important;
    padding:11px 12px!important;
    border-radius:18px!important;
    background:rgba(255,253,246,.58)!important;
    border:1px solid rgba(201,154,61,.11)!important;
    max-width:360px!important;
  }
  .app-shell .home-page.simple-home-page .home-faith-growth-indicator > span,
  .app-shell .home-page.simple-home-page .home-faith-growth-indicator small{
    display:block!important;
    color:#7D674D!important;
    font-size:11.75px!important;
    line-height:1.2!important;
    font-weight:650!important;
  }
  .app-shell .home-page.simple-home-page .home-faith-growth-indicator small{
    margin-top:6px!important;
    color:#9A8564!important;
    font-weight:500!important;
  }
  .app-shell .home-page.simple-home-page .home-faith-growth-track{
    display:grid!important;
    grid-template-columns:repeat(5,minmax(0,1fr))!important;
    gap:5px!important;
    margin-top:8px!important;
  }
  .app-shell .home-page.simple-home-page .home-faith-growth-track i{
    height:5px!important;
    border-radius:999px!important;
    background:rgba(214,185,127,.24)!important;
  }
  .app-shell .home-page.simple-home-page .home-faith-growth-track i.active{
    background:linear-gradient(90deg,#86B870,#E8C66F)!important;
    box-shadow:0 0 10px rgba(232,198,111,.20)!important;
  }
  .app-shell .home-page.simple-home-page .home-art-frame{
    position:relative!important;
    margin-top:22px!important;
    padding:12px!important;
    border-radius:28px!important;
    background:
      radial-gradient(circle at 50% 44%,rgba(255,236,178,.32),transparent 52%),
      linear-gradient(145deg,rgba(255,253,246,.96),rgba(255,248,231,.74))!important;
    border:1px solid rgba(201,154,61,.20)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.86),inset 0 -18px 34px rgba(116,84,28,.04),0 12px 28px rgba(84,62,30,.055)!important;
  }
  .app-shell .home-page.simple-home-page .home-art-frame::before{
    content:""!important;
    position:absolute!important;
    inset:22px 12% auto!important;
    height:44%!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse,rgba(255,223,136,.18),transparent 68%)!important;
    pointer-events:none!important;
    z-index:0!important;
  }
  .app-shell .home-page.simple-home-page .home-art-frame .home-room{
    min-height:420px!important;
    border-radius:24px!important;
    overflow:hidden!important;
    position:relative!important;
    z-index:1!important;
  }
  .app-shell .home-page.simple-home-page .home-art-sparkle{
    position:absolute!important;
    z-index:3!important;
    color:#C99A3D!important;
    opacity:.18!important;
    font-size:16px!important;
    pointer-events:none!important;
  }
  .app-shell .home-page.simple-home-page .home-art-sparkle.s1{left:18px!important;top:22px!important;}
  .app-shell .home-page.simple-home-page .home-art-sparkle.s2{right:24px!important;bottom:34px!important;font-size:13px!important;}
  .app-shell .home-page.simple-home-page .home-art-bird{
    position:absolute!important;
    z-index:3!important;
    width:24px!important;
    height:12px!important;
    opacity:.16!important;
    pointer-events:none!important;
  }
  .app-shell .home-page.simple-home-page .home-art-bird::before,
  .app-shell .home-page.simple-home-page .home-art-bird::after{
    content:""!important;
    position:absolute!important;
    width:12px!important;
    height:8px!important;
    border-top:2px solid #6F8FA7!important;
    border-radius:50%!important;
  }
  .app-shell .home-page.simple-home-page .home-art-bird::before{left:1px!important;transform:rotate(16deg)!important;}
  .app-shell .home-page.simple-home-page .home-art-bird::after{right:1px!important;transform:rotate(-16deg)!important;}
  .app-shell .home-page.simple-home-page .home-art-bird.b1{left:13%!important;top:24%!important;}
  .app-shell .home-page.simple-home-page .home-art-bird.b2{right:18%!important;top:18%!important;transform:scale(.82)!important;}
  .app-shell .home-page.simple-home-page .simple-home-blessing{
    position:relative!important;
    overflow:hidden!important;
    padding:24px!important;
    border:0!important;
    border-radius:28px!important;
    background:linear-gradient(145deg,#FFFDF8,#FFF8E7)!important;
    box-shadow:0 16px 34px rgba(88,65,30,.075),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing::before{
    content:"✨"!important;
    margin-right:6px!important;
    color:#C99A3D!important;
  }
  .app-shell .home-page.simple-home-page .home-blessing-dove{
    position:absolute!important;
    right:20px!important;
    top:20px!important;
    width:68px!important;
    height:48px!important;
    opacity:.075!important;
    pointer-events:none!important;
  }
  .app-shell .home-page.simple-home-page .home-blessing-dove::before,
  .app-shell .home-page.simple-home-page .home-blessing-dove::after{
    content:""!important;
    position:absolute!important;
    border-radius:50%!important;
    border-top:3px solid #8A5A08!important;
  }
  .app-shell .home-page.simple-home-page .home-blessing-dove::before{left:7px;top:18px;width:34px;height:22px;transform:rotate(-20deg);}
  .app-shell .home-page.simple-home-page .home-blessing-dove::after{right:7px;top:18px;width:34px;height:22px;transform:rotate(20deg);}
  .app-shell .home-page.simple-home-page .simple-home-blessing h3{
    font-size:clamp(24px,6vw,32px)!important;
    line-height:1.05!important;
    max-width:16ch!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing p{
    max-width:36ch!important;
    font-size:14.5px!important;
    line-height:1.6!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing .gold-btn{
    min-height:56px!important;
    border-radius:18px!important;
    border:1px solid rgba(201,154,61,.34)!important;
    background:linear-gradient(135deg,rgba(255,253,246,.96),rgba(255,239,184,.92))!important;
    color:#5F3F09!important;
    box-shadow:0 10px 22px rgba(201,154,61,.14)!important;
    transition:transform .18s ease,box-shadow .18s ease,filter .18s ease!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing .gold-btn:not(:disabled):hover,
  .app-shell .home-page.simple-home-page .simple-home-blessing .gold-btn:not(:disabled):focus-visible{
    transform:translateY(-2px)!important;
    box-shadow:0 14px 28px rgba(201,154,61,.18)!important;
    outline:3px solid rgba(240,199,94,.20)!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing .gold-btn:not(:disabled):active{
    transform:translateY(0) scale(.99)!important;
  }
  @media(max-width:560px){
    .app-shell .home-page.simple-home-page .sticky-preview-card.simple-home-preview,
    .app-shell .home-page.simple-home-page .simple-home-blessing{padding:20px!important;border-radius:26px!important;}
    .app-shell .home-page.simple-home-page .home-art-frame{padding:9px!important;border-radius:24px!important;}
    .app-shell .home-page.simple-home-page .home-art-frame .home-room{min-height:390px!important;border-radius:21px!important;}
    .app-shell .home-page.simple-home-page .home-faith-growth-indicator{max-width:none!important;}
  }
  @media(prefers-reduced-motion:reduce){
    .app-shell .home-page.simple-home-page,
    .app-shell .home-page.simple-home-page::after{animation:none!important;}
  }

  /* Testimony Share preview: a quiet glimpse of the public wall inside the share card. */
  .app-shell .testimony-share-preview{
    margin-top:14px!important;
    padding:12px!important;
    border-radius:18px!important;
    background:linear-gradient(145deg,rgba(255,253,246,.84),rgba(246,250,255,.48))!important;
    border:1px solid rgba(125,174,212,.14)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.72)!important;
  }
  .app-shell .testimony-share-preview-head{
    display:flex!important;
    justify-content:space-between!important;
    align-items:flex-start!important;
    gap:10px!important;
    margin-bottom:9px!important;
  }
  .app-shell .testimony-share-preview-head p{
    margin:2px 0 0!important;
    color:#6f5c43!important;
    font-size:13px!important;
    line-height:1.35!important;
  }
  .app-shell .testimony-share-preview-head button{
    border:1px solid rgba(224,197,139,.35)!important;
    background:rgba(255,253,246,.74)!important;
    color:#8a5a08!important;
    border-radius:999px!important;
    padding:7px 10px!important;
    font-size:12px!important;
    font-weight:650!important;
    cursor:pointer!important;
    white-space:nowrap!important;
  }
  .app-shell .testimony-share-preview-list{
    display:flex!important;
    flex-direction:column!important;
    gap:7px!important;
  }
  .app-shell .testimony-share-preview-item{
    padding:10px 11px!important;
    border-radius:14px!important;
    background:rgba(255,255,255,.58)!important;
    border:1px solid rgba(224,197,139,.18)!important;
  }
  .app-shell .testimony-share-preview-item strong{
    display:block!important;
    color:#3A2812!important;
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-size:17px!important;
    font-weight:500!important;
    line-height:1.1!important;
    margin-bottom:4px!important;
  }
  .app-shell .testimony-share-preview-item span{
    display:block!important;
    color:#6f5c43!important;
    font-size:13px!important;
    line-height:1.45!important;
    font-weight:400!important;
  }


  /* Shared Testimonies page: separate reading space. */
  .app-shell .testimony-shared-page-hero{
    border-color:rgba(201,144,18,.20)!important;
    background:linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,248,223,.58),rgba(246,250,255,.48))!important;
    box-shadow:0 10px 26px rgba(138,90,8,.06)!important;
  }
  .app-shell .testimony-shared-page .testimony-shared-garden{
    margin-top:0!important;
  }
  @media(max-width:430px){
    .app-shell .testimony-shared-page-hero > div{
      flex-direction:column!important;
    }
    .app-shell .testimony-shared-page-hero .soft-action-btn{
      width:100%!important;
      justify-content:center!important;
    }
  }



  /* Testimony Wall: Today-page visual rhythm. */
  .app-shell .testimony-wall-hero-today{
    border:1.5px solid rgba(201,144,18,.34)!important;
    background:linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,249,228,.72) 54%,rgba(247,251,255,.58))!important;
    box-shadow:0 14px 34px rgba(138,90,8,.08)!important;
  }
  .app-shell .testimony-wall-today-style{
    border:1px solid rgba(231,207,159,.22)!important;
    background:rgba(255,253,246,.94)!important;
    box-shadow:0 10px 28px rgba(138,90,8,.055)!important;
    padding:18px!important;
  }
  .app-shell .testimony-wall-today-style::before{
    content:""!important;
    display:block!important;
    width:100%!important;
    height:2px!important;
    border-radius:999px!important;
    margin:0 0 16px!important;
    background:linear-gradient(90deg,rgba(201,144,18,.72),rgba(201,144,18,.08))!important;
  }
  .app-shell .testimony-wall-heading{
    display:flex!important;
    justify-content:space-between!important;
    align-items:flex-start!important;
    gap:14px!important;
    margin-bottom:14px!important;
  }
  .app-shell .testimony-wall-heading h3{
    margin:0 0 4px!important;
    color:#3A2812!important;
    font-size:25px!important;
    line-height:1.05!important;
    font-weight:500!important;
    letter-spacing:0!important;
  }
  .app-shell .testimony-wall-heading p{
    margin:0!important;
    color:#7a674e!important;
    font-size:13.5px!important;
    line-height:1.45!important;
    font-weight:400!important;
  }
  .app-shell .testimony-wall-heading > span{
    min-width:42px!important;
    height:42px!important;
    border-radius:15px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    color:#8A5A08!important;
    font-size:18px!important;
    font-weight:700!important;
    background:rgba(255,248,223,.82)!important;
    border:1px solid rgba(201,144,18,.18)!important;
    flex-shrink:0!important;
  }
  .app-shell .testimony-wall-today-style .testimony-category-row{
    gap:18px!important;
    padding:4px 2px 16px!important;
    margin-bottom:2px!important;
    border-bottom:1px solid rgba(231,207,159,.20)!important;
  }
  .app-shell .testimony-wall-today-style .testimony-category-row button{
    min-height:auto!important;
    padding:0 0 6px!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
    color:#9a8564!important;
    font-size:13px!important;
    font-weight:700!important;
    letter-spacing:.2px!important;
  }
  .app-shell .testimony-wall-today-style .testimony-category-row button.active{
    color:#3A2812!important;
    background:transparent!important;
    border-bottom:2px solid #C99012!important;
    transform:none!important;
  }
  .app-shell .testimony-wall-today-style .scroll-list{
    display:flex!important;
    flex-direction:column!important;
    gap:12px!important;
    margin-top:14px!important;
  }
  .app-shell .testimony-wall-story-card{
    background:linear-gradient(160deg,rgba(255,255,255,.76),rgba(255,252,241,.84))!important;
    border:1px solid rgba(231,207,159,.18)!important;
    border-radius:20px!important;
    padding:16px 15px!important;
    box-shadow:none!important;
  }
  .app-shell .testimony-wall-story-card:hover{
    transform:none!important;
    box-shadow:0 8px 22px rgba(138,90,8,.055)!important;
    border-color:rgba(201,144,18,.24)!important;
  }
  .app-shell .testimony-wall-story-content{
    display:flex!important;
    gap:12px!important;
    align-items:flex-start!important;
  }
  .app-shell .testimony-wall-story-mark{
    width:30px!important;
    height:30px!important;
    border-radius:50%!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    flex-shrink:0!important;
    color:#C99012!important;
    background:rgba(255,248,223,.78)!important;
    border:1px solid rgba(201,144,18,.16)!important;
    font-size:15px!important;
    margin-top:2px!important;
  }
  .app-shell .testimony-wall-story-head h4{
    margin:0!important;
    color:#3A2812!important;
    font-size:20px!important;
    line-height:1.08!important;
    font-weight:500!important;
    letter-spacing:0!important;
  }
  .app-shell .testimony-wall-story-text{
    margin:10px 0 10px!important;
    color:#5f4d34!important;
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
    font-size:14.5px!important;
    line-height:1.65!important;
    font-weight:400!important;
    font-style:normal!important;
  }
  .app-shell .testimony-wall-story-meta{
    margin:0!important;
    color:#9a8564!important;
    font-size:12.5px!important;
    line-height:1.35!important;
    font-weight:500!important;
  }
  .app-shell .testimony-wall-read-more{
    background:transparent!important;
    border:0!important;
    border-bottom:1px solid rgba(201,144,18,.45)!important;
    border-radius:0!important;
    padding:0 0 2px!important;
    color:#8A5A08!important;
    font-size:12.75px!important;
    font-weight:800!important;
    cursor:pointer!important;
    margin-bottom:10px!important;
  }
  @media(max-width:430px){
    .app-shell .testimony-wall-today-style{padding:16px!important;}
    .app-shell .testimony-wall-heading h3{font-size:22px!important;}
    .app-shell .testimony-wall-heading > span{min-width:38px!important;height:38px!important;font-size:16px!important;}
    .app-shell .testimony-wall-today-style .testimony-category-row{gap:15px!important;}
    .app-shell .testimony-wall-story-card{padding:14px!important;border-radius:18px!important;}
    .app-shell .testimony-wall-story-head h4{font-size:18px!important;}
  }


  /* Testimonies page calm spacing pass. */
  .app-shell .testimony-garden-page{
    gap:16px!important;
  }
  .app-shell .testimony-garden-page .testimony-featured-garden{
    padding:20px!important;
    border-color:rgba(201,144,18,.22)!important;
    box-shadow:0 12px 30px rgba(138,90,8,.07)!important;
  }
  .app-shell .testimony-wall-card-title{
    margin-bottom:14px!important;
  }
  .app-shell .testimony-wall-card-title h2{
    font-weight:500!important;
    line-height:1.05!important;
  }
  .app-shell .testimony-wall-card-title p{
    max-width:28ch!important;
  }
  .app-shell .featured-testimony-image{
    margin:2px 0 16px!important;
    box-shadow:0 8px 20px rgba(138,90,8,.055)!important;
  }
  .app-shell .testimony-action-card{
    padding:18px!important;
  }
  .app-shell .testimony-action-card h3{
    font-weight:500!important;
    line-height:1.12!important;
  }
  .app-shell .testimony-action-card p{
    max-width:30ch!important;
  }
  .app-shell .testimony-shared-page{
    gap:16px!important;
  }
  .app-shell .testimony-shared-page-hero{
    padding:18px!important;
  }
  .app-shell .testimony-shared-page-hero > button{
    margin-bottom:16px!important;
  }
  .app-shell .testimony-shared-page-hero h2{
    line-height:1.06!important;
  }
  .app-shell .testimony-shared-page-hero p{
    max-width:31ch!important;
  }
  .app-shell .testimony-wall-today-style{
    margin-top:2px!important;
  }
  .app-shell .testimony-wall-heading{
    margin-bottom:16px!important;
  }
  .app-shell .testimony-wall-story-card + .testimony-wall-story-card{
    margin-top:2px!important;
  }
  .app-shell .testimony-wall-story-text{
    margin-top:12px!important;
    margin-bottom:12px!important;
  }
  .app-shell .testimony-book-preview-card h3{
    font-weight:500!important;
  }
  @media(max-width:430px){
    .app-shell .testimony-garden-page{gap:14px!important;}
    .app-shell .testimony-shared-page-hero{padding:16px!important;}
    .app-shell .testimony-shared-page-hero h2{font-size:24px!important;}
    .app-shell .testimony-shared-page-hero p{font-size:13px!important;}
    .app-shell .testimony-action-card p,
    .app-shell .testimony-wall-card-title p{max-width:100%!important;}
  }




  /* Premium Shared Testimonies gallery refinement. */
  @keyframes testimonyWallRise{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
  @keyframes testimonyWallSparkle{0%,100%{opacity:.035;transform:translateY(0) scale(.94);}50%{opacity:.075;transform:translateY(-8px) scale(1.04);}}
  .app-shell .testimony-shared-page{
    position:relative!important;
    isolation:isolate!important;
    gap:20px!important;
    padding-bottom:116px!important;
    background:
      radial-gradient(circle at 50% 0%,rgba(255,232,164,.20),transparent 30%),
      radial-gradient(circle at 10% 18%,rgba(236,246,255,.22),transparent 24%),
      linear-gradient(180deg,#FCF8EF 0%,#FFFDF8 44%,#FCF8EF 100%)!important;
    animation:testimonyWallRise .38s ease-out both!important;
  }
  .app-shell .testimony-shared-page::before{
    content:"✦  ✧      ✦"!important;
    position:absolute!important;
    left:8%!important;
    right:8%!important;
    top:64px!important;
    color:#C99A3D!important;
    font-size:13px!important;
    letter-spacing:28px!important;
    opacity:.055!important;
    pointer-events:none!important;
    animation:testimonyWallSparkle 8s ease-in-out infinite!important;
    z-index:-1!important;
  }
  .app-shell .testimony-shared-page::after{
    content:""!important;
    position:absolute!important;
    right:8px!important;
    top:26px!important;
    width:104px!important;
    height:142px!important;
    opacity:.55!important;
    pointer-events:none!important;
    z-index:-1!important;
    background:
      radial-gradient(ellipse at 30% 28%,rgba(134,160,111,.16) 0 17%,transparent 18%),
      radial-gradient(ellipse at 54% 44%,rgba(134,160,111,.13) 0 15%,transparent 16%),
      radial-gradient(ellipse at 38% 64%,rgba(134,160,111,.11) 0 14%,transparent 15%),
      linear-gradient(120deg,transparent 48%,rgba(134,160,111,.12) 49% 51%,transparent 52%)!important;
  }
  .app-shell .testimony-shared-page-hero.testimony-wall-hero-today{
    position:relative!important;
    overflow:hidden!important;
    padding:24px!important;
    border:0!important;
    border-radius:30px!important;
    background:
      radial-gradient(circle at 88% 22%,rgba(220,232,209,.28),transparent 28%),
      linear-gradient(145deg,#FFFDF8,#FFF8EA 62%,#FBFCF8)!important;
    box-shadow:0 18px 42px rgba(84,62,30,.085),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .app-shell .testimony-shared-page-hero.testimony-wall-hero-today::before{
    content:""!important;
    position:absolute!important;
    inset:12px!important;
    border-radius:25px!important;
    border:1px solid rgba(201,154,61,.11)!important;
    pointer-events:none!important;
  }
  .app-shell .testimony-wall-back-link{
    border:0!important;
    background:transparent!important;
    color:#7D674D!important;
    padding:0!important;
    margin:0 0 22px!important;
    font-size:13.25px!important;
    line-height:1.2!important;
    font-weight:650!important;
    cursor:pointer!important;
    min-height:28px!important;
    transition:transform .18s ease,color .18s ease!important;
  }
  .app-shell .testimony-wall-back-link:hover,
  .app-shell .testimony-wall-back-link:focus-visible{
    color:#8A5A08!important;
    transform:translateX(-2px)!important;
    outline:none!important;
  }
  .app-shell .testimony-wall-hero-layout{
    position:relative!important;
    display:grid!important;
    grid-template-columns:minmax(0,1fr) 104px!important;
    gap:18px!important;
    align-items:center!important;
    margin-bottom:22px!important;
  }
  .app-shell .testimony-wall-hero-copy h2{
    margin:5px 0 8px!important;
    color:#342313!important;
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-size:clamp(34px,8vw,48px)!important;
    line-height:.98!important;
    font-weight:500!important;
    letter-spacing:0!important;
  }
  .app-shell .testimony-wall-hero-copy p{
    margin:0!important;
    color:#6F5C43!important;
    font-size:15px!important;
    line-height:1.55!important;
    font-weight:400!important;
  }
  .app-shell .testimony-wall-gold-divider{
    display:block!important;
    width:88px!important;
    height:2px!important;
    border-radius:999px!important;
    margin-top:18px!important;
    background:linear-gradient(90deg,#C99A3D,rgba(201,154,61,.06))!important;
  }
  .app-shell .testimony-wall-botanical{
    position:relative!important;
    width:92px!important;
    height:118px!important;
    opacity:.42!important;
  }
  .app-shell .testimony-wall-botanical::before{
    content:""!important;
    position:absolute!important;
    left:44px!important;
    top:8px!important;
    bottom:8px!important;
    width:2px!important;
    border-radius:999px!important;
    background:rgba(134,160,111,.35)!important;
    transform:rotate(16deg)!important;
  }
  .app-shell .testimony-wall-botanical span{
    position:absolute!important;
    width:34px!important;
    height:18px!important;
    border-radius:999px 999px 999px 0!important;
    background:rgba(134,160,111,.24)!important;
  }
  .app-shell .testimony-wall-botanical span:nth-child(1){left:13px!important;top:28px!important;transform:rotate(-32deg)!important;}
  .app-shell .testimony-wall-botanical span:nth-child(2){right:7px!important;top:54px!important;transform:scaleX(-1) rotate(-28deg)!important;}
  .app-shell .testimony-wall-botanical span:nth-child(3){left:24px!important;bottom:20px!important;transform:rotate(-22deg)!important;}
  .app-shell .testimony-wall-share-card{
    position:relative!important;
    width:100%!important;
    min-height:74px!important;
    border-radius:24px!important;
    border:1.5px solid rgba(201,154,61,.28)!important;
    background:rgba(255,253,246,.88)!important;
    box-shadow:0 10px 26px rgba(84,62,30,.06)!important;
    padding:15px 16px!important;
    display:grid!important;
    grid-template-columns:auto minmax(0,1fr) auto!important;
    gap:13px!important;
    align-items:center!important;
    text-align:left!important;
    cursor:pointer!important;
    transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease!important;
  }
  .app-shell .testimony-wall-share-card:hover,
  .app-shell .testimony-wall-share-card:focus-visible{
    transform:translateY(-2px)!important;
    box-shadow:0 14px 30px rgba(84,62,30,.085)!important;
    border-color:rgba(201,154,61,.42)!important;
    outline:3px solid rgba(240,199,94,.15)!important;
  }
  .app-shell .testimony-wall-share-icon{
    width:44px!important;
    height:44px!important;
    border-radius:50%!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    color:#8A5A08!important;
    background:linear-gradient(135deg,#FFF3C4,#E8C66F)!important;
    box-shadow:0 7px 16px rgba(201,154,61,.16)!important;
    font-size:20px!important;
  }
  .app-shell .testimony-wall-share-copy strong{
    display:block!important;
    color:#342313!important;
    font-size:16px!important;
    line-height:1.2!important;
    font-weight:720!important;
  }
  .app-shell .testimony-wall-share-copy small{
    display:block!important;
    margin-top:3px!important;
    color:#7D674D!important;
    font-size:12.75px!important;
    line-height:1.35!important;
    font-weight:400!important;
  }
  .app-shell .testimony-wall-share-chevron{color:#A8874D!important;font-size:27px!important;line-height:1!important;}

  .app-shell #shared-testimonies-wall.testimony-wall-today-style{
    position:relative!important;
    overflow:hidden!important;
    padding:22px!important;
    border:0!important;
    border-radius:30px!important;
    background:#FFFDF8!important;
    box-shadow:0 18px 42px rgba(84,62,30,.075),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .app-shell #shared-testimonies-wall.testimony-wall-today-style::before{
    content:""!important;
    display:block!important;
    width:100%!important;
    height:2px!important;
    border-radius:999px!important;
    margin:0 0 20px!important;
    background:linear-gradient(90deg,rgba(201,154,61,.82),rgba(201,154,61,.08),transparent)!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-heading{
    margin-bottom:18px!important;
    align-items:flex-start!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-heading h3{
    margin:4px 0 7px!important;
    color:#342313!important;
    font-size:clamp(26px,6.2vw,36px)!important;
    line-height:1.02!important;
    font-weight:500!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-heading p{
    color:#6F5C43!important;
    font-size:14.25px!important;
    line-height:1.52!important;
    max-width:30ch!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-stat-badge{
    min-width:74px!important;
    height:auto!important;
    border-radius:22px!important;
    padding:10px 9px!important;
    display:flex!important;
    flex-direction:column!important;
    align-items:center!important;
    justify-content:center!important;
    gap:1px!important;
    background:linear-gradient(145deg,rgba(255,248,223,.88),rgba(255,253,246,.92))!important;
    border:1px solid rgba(201,154,61,.18)!important;
    box-shadow:none!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-stat-badge b{font-size:13px!important;color:#C99A3D!important;line-height:1!important;}
  .app-shell #shared-testimonies-wall .testimony-wall-stat-badge strong{font-size:22px!important;color:#342313!important;line-height:1!important;font-weight:650!important;}
  .app-shell #shared-testimonies-wall .testimony-wall-stat-badge small{font-size:9.5px!important;color:#8A714F!important;line-height:1.05!important;text-align:center!important;font-weight:650!important;}
  .app-shell #shared-testimonies-wall .testimony-category-row{
    display:flex!important;
    gap:9px!important;
    overflow-x:auto!important;
    padding:2px 2px 16px!important;
    margin:0 0 8px!important;
    border-bottom:0!important;
    scrollbar-width:none!important;
  }
  .app-shell #shared-testimonies-wall .testimony-category-row::-webkit-scrollbar{display:none!important;}
  .app-shell #shared-testimonies-wall .testimony-category-row button{
    flex:0 0 auto!important;
    min-height:42px!important;
    padding:10px 15px!important;
    border-radius:999px!important;
    border:1px solid rgba(201,154,61,.18)!important;
    background:rgba(255,253,246,.78)!important;
    color:#7D674D!important;
    box-shadow:none!important;
    font-size:12.75px!important;
    font-weight:700!important;
    transition:transform .18s ease,background .18s ease,border-color .18s ease,box-shadow .18s ease!important;
  }
  .app-shell #shared-testimonies-wall .testimony-category-row button.active{
    border-color:rgba(201,154,61,.45)!important;
    background:linear-gradient(135deg,#FFF3C4,#E8C66F)!important;
    color:#342313!important;
    box-shadow:0 8px 18px rgba(201,154,61,.14)!important;
    transform:translateY(-1px)!important;
  }
  .app-shell #shared-testimonies-wall .scroll-list{
    gap:14px!important;
    margin-top:10px!important;
    max-height:58vh!important;
    padding-right:4px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-card{
    position:relative!important;
    overflow:hidden!important;
    padding:22px!important;
    border-radius:26px!important;
    background:linear-gradient(145deg,#FFFDF8,#FFF9ED)!important;
    border:1px solid rgba(201,154,61,.14)!important;
    box-shadow:0 12px 30px rgba(84,62,30,.06)!important;
    transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-card::after{
    content:"✦"!important;
    position:absolute!important;
    right:18px!important;
    top:16px!important;
    color:#C99A3D!important;
    opacity:.10!important;
    pointer-events:none!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-card:hover,
  .app-shell #shared-testimonies-wall .testimony-wall-story-card:focus-within{
    transform:translateY(-2px)!important;
    box-shadow:0 16px 34px rgba(84,62,30,.08)!important;
    border-color:rgba(201,154,61,.24)!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-content{gap:14px!important;}
  .app-shell #shared-testimonies-wall .testimony-wall-story-mark{
    width:38px!important;
    height:38px!important;
    border-radius:50%!important;
    background:rgba(255,246,220,.84)!important;
    border:1px solid rgba(201,154,61,.16)!important;
    color:#C99A3D!important;
    font-size:17px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-head .section-label{
    color:#B79A64!important;
    font-size:10px!important;
    letter-spacing:.16em!important;
    margin-bottom:7px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-head h4{
    color:#342313!important;
    font-size:clamp(23px,5.2vw,30px)!important;
    line-height:1.04!important;
    font-weight:500!important;
    padding-right:22px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-text{
    color:#5F4D34!important;
    font-size:16px!important;
    line-height:1.7!important;
    margin:14px 0 12px!important;
    display:-webkit-box!important;
    -webkit-line-clamp:6!important;
    -webkit-box-orient:vertical!important;
    overflow:hidden!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-read-more{
    color:#8A5A08!important;
    border-bottom:1px solid rgba(201,154,61,.42)!important;
    font-size:12.75px!important;
    font-weight:700!important;
    margin-bottom:12px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-meta{
    color:#9A8564!important;
    font-size:12.25px!important;
    line-height:1.35!important;
  }
  .app-shell #shared-testimonies-wall .empty-state{
    border:1px dashed rgba(201,154,61,.28)!important;
    border-radius:26px!important;
    background:linear-gradient(145deg,rgba(255,253,246,.78),rgba(255,248,223,.42))!important;
    padding:30px 18px!important;
    text-align:center!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-empty-dove{
    position:relative!important;
    width:74px!important;
    height:52px!important;
    margin:0 auto 16px!important;
    opacity:.62!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-empty-dove::before,
  .app-shell #shared-testimonies-wall .testimony-wall-empty-dove::after{
    content:""!important;
    position:absolute!important;
    top:20px!important;
    width:36px!important;
    height:22px!important;
    border-top:3px solid rgba(134,160,111,.50)!important;
    border-radius:50%!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-empty-dove::before{left:4px!important;transform:rotate(-20deg)!important;}
  .app-shell #shared-testimonies-wall .testimony-wall-empty-dove::after{right:4px!important;transform:rotate(20deg)!important;}
  .app-shell #shared-testimonies-wall .testimony-wall-empty-dove span{
    position:absolute!important;
    left:50%!important;
    top:22px!important;
    width:20px!important;
    height:14px!important;
    border-radius:50%!important;
    transform:translateX(-50%)!important;
    background:rgba(255,253,246,.92)!important;
    border:1px solid rgba(134,160,111,.22)!important;
  }
  .app-shell #shared-testimonies-wall .empty-state-title{
    color:#342313!important;
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-size:25px!important;
    line-height:1.05!important;
    font-weight:500!important;
  }
  .app-shell #shared-testimonies-wall .empty-state-copy{
    color:#6F5C43!important;
    font-size:14px!important;
    line-height:1.55!important;
    max-width:31ch!important;
    margin-left:auto!important;
    margin-right:auto!important;
  }
  .app-shell #shared-testimonies-wall .empty-state .soft-action-btn{
    min-height:48px!important;
    border-radius:16px!important;
    border:1px solid rgba(201,154,61,.28)!important;
    background:linear-gradient(135deg,#FFF3C4,#E8C66F)!important;
    color:#342313!important;
    box-shadow:0 10px 22px rgba(201,154,61,.14)!important;
  }
  @media(max-width:430px){
    .app-shell .testimony-shared-page-hero.testimony-wall-hero-today,
    .app-shell #shared-testimonies-wall.testimony-wall-today-style{padding:19px!important;border-radius:27px!important;}
    .app-shell .testimony-wall-hero-layout{grid-template-columns:minmax(0,1fr) 58px!important;gap:10px!important;}
    .app-shell .testimony-wall-botanical{width:58px!important;height:92px!important;opacity:.34!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-heading{gap:10px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-stat-badge{min-width:64px!important;padding:8px 7px!important;border-radius:19px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-story-card{padding:18px!important;border-radius:23px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-story-content{gap:11px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-story-mark{width:32px!important;height:32px!important;font-size:15px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-story-text{font-size:15px!important;line-height:1.66!important;}
  }
  @media(prefers-reduced-motion:reduce){
    .app-shell .testimony-shared-page,
    .app-shell .testimony-shared-page::before{animation:none!important;}
    .app-shell .testimony-wall-share-card,
    .app-shell #shared-testimonies-wall .testimony-wall-story-card,
    .app-shell #shared-testimonies-wall .testimony-category-row button{transition:none!important;}
  }

  /* Share Testimony: premium journal-style writing experience. */
  @keyframes testimonyJournalRise{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
  @keyframes testimonySparkDrift{0%{opacity:0;transform:translateY(12px) scale(.88);}35%{opacity:.28;}100%{opacity:0;transform:translateY(-28px) scale(1.08);}}
  .app-shell .testimony-journal-share-page{gap:18px!important;max-width:680px!important;margin:0 auto!important;animation:testimonyJournalRise .38s ease-out both!important;position:relative!important;overflow:hidden!important;}
  .app-shell .testimony-journal-share-page::before,.app-shell .testimony-journal-share-page::after{content:"✦"!important;position:absolute!important;pointer-events:none!important;color:#C99012!important;opacity:0!important;animation:testimonySparkDrift 5.8s ease-in-out infinite!important;z-index:0!important;}
  .app-shell .testimony-journal-share-page::before{left:8%;top:18%;animation-delay:.4s!important;}
  .app-shell .testimony-journal-share-page::after{right:10%;top:42%;animation-delay:2.4s!important;}
  .app-shell .testimony-journal-back{align-self:flex-start!important;border:0!important;background:transparent!important;padding:4px 2px!important;color:#8f7b5d!important;font-size:13.5px!important;font-weight:650!important;cursor:pointer!important;position:relative!important;z-index:1!important;}
  .app-shell .testimony-journal-back:hover{color:#5f4d34!important;}
  .app-shell .testimony-journal-header{text-align:left!important;padding:8px 2px 2px!important;position:relative!important;z-index:1!important;}
  .app-shell .testimony-journal-header h2{margin:4px 0 10px!important;color:#3A2812!important;font-size:32px!important;line-height:1.02!important;font-weight:500!important;letter-spacing:0!important;}
  .app-shell .testimony-journal-header p{margin:0!important;max-width:38ch!important;color:#7a674e!important;font-size:14.25px!important;line-height:1.6!important;font-weight:400!important;}
  .app-shell .testimony-journal-card{padding:32px!important;border-radius:28px!important;border:1px solid rgba(214,185,127,.24)!important;background:radial-gradient(circle at 18% 0%,rgba(255,248,223,.72),transparent 34%),linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,249,235,.94))!important;box-shadow:0 18px 46px rgba(90,62,22,.08)!important;position:relative!important;z-index:1!important;overflow:hidden!important;}
  .app-shell .testimony-journal-card::after{content:""!important;position:absolute!important;inset:0!important;pointer-events:none!important;opacity:.24!important;background-image:linear-gradient(rgba(138,90,8,.035) 1px,transparent 1px)!important;background-size:100% 22px!important;mix-blend-mode:multiply!important;}
  .app-shell .testimony-journal-card > *{position:relative!important;z-index:1!important;}
  .app-shell .testimony-field-group{display:flex!important;flex-direction:column!important;gap:10px!important;margin-bottom:28px!important;}
  .app-shell .testimony-field-group label{color:#5f4d34!important;font-size:11.5px!important;font-weight:850!important;letter-spacing:1.8px!important;text-transform:uppercase!important;}
  .app-shell .testimony-journal-input,.app-shell .testimony-journal-textarea{width:100%!important;border:1px solid rgba(214,185,127,.24)!important;background:rgba(255,249,235,.74)!important;color:#3A2812!important;outline:0!important;font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;transition:border-color .22s ease, box-shadow .22s ease, background .22s ease!important;}
  .app-shell .testimony-journal-input{min-height:56px!important;border-radius:18px!important;padding:0 17px!important;font-size:15.5px!important;}
  .app-shell .testimony-journal-textarea{min-height:250px!important;resize:none!important;overflow:hidden!important;border-radius:22px!important;padding:18px!important;font-size:16px!important;line-height:1.8!important;}
  .app-shell .testimony-journal-input:hover,.app-shell .testimony-journal-textarea:hover{border-color:rgba(201,144,18,.38)!important;}
  .app-shell .testimony-journal-input:focus,.app-shell .testimony-journal-textarea:focus{border-color:rgba(201,144,18,.72)!important;background:rgba(255,253,246,.96)!important;box-shadow:0 0 0 4px rgba(255,226,122,.22)!important;}
  .app-shell .testimony-anonymous-row{display:flex!important;justify-content:space-between!important;gap:16px!important;align-items:center!important;margin-bottom:28px!important;padding:16px 0!important;border-top:1px solid rgba(214,185,127,.18)!important;border-bottom:1px solid rgba(214,185,127,.18)!important;}
  .app-shell .testimony-anonymous-row h3{margin:0 0 4px!important;color:#3A2812!important;font-size:16px!important;font-weight:700!important;}
  .app-shell .testimony-anonymous-row p{margin:0!important;color:#8f7b5d!important;font-size:13.25px!important;line-height:1.45!important;}
  .app-shell .testimony-toggle{width:54px!important;height:32px!important;border:0!important;border-radius:999px!important;background:#ded6c8!important;padding:3px!important;cursor:pointer!important;transition:background .22s ease!important;flex-shrink:0!important;}
  .app-shell .testimony-toggle span{display:block!important;width:26px!important;height:26px!important;border-radius:50%!important;background:#fffdf6!important;box-shadow:0 3px 10px rgba(69,48,20,.18)!important;transition:transform .22s ease!important;}
  .app-shell .testimony-toggle.on{background:linear-gradient(135deg,#F3D27A,#C99012)!important;}
  .app-shell .testimony-toggle.on span{transform:translateX(22px)!important;}
  .app-shell .testimony-encouragement{display:flex!important;align-items:center!important;gap:10px!important;margin:0 0 18px!important;color:#7a674e!important;}
  .app-shell .testimony-encouragement span{opacity:.72!important;font-size:18px!important;}
  .app-shell .testimony-encouragement p{margin:0!important;font-size:13.5px!important;line-height:1.5!important;}
  .app-shell .testimony-live-preview{margin:-8px 0 22px!important;padding:16px!important;border-radius:20px!important;border:1px solid rgba(214,185,127,.18)!important;background:rgba(255,253,246,.68)!important;}
  .app-shell .testimony-live-preview h4{margin:0 0 8px!important;color:#3A2812!important;font-size:20px!important;font-weight:500!important;line-height:1.1!important;}
  .app-shell .testimony-live-preview p{margin:0 0 8px!important;color:#6f5c43!important;font-size:13.75px!important;line-height:1.55!important;}
  .app-shell .testimony-live-preview small{color:#9a8564!important;font-size:12.25px!important;}
  .app-shell .testimony-share-footnote{margin:0 0 28px!important;color:#9a8564!important;font-size:12.5px!important;line-height:1.55!important;}
  .app-shell .testimony-journal-actions{display:grid!important;grid-template-columns:1fr 1.35fr!important;gap:20px!important;align-items:center!important;padding-top:22px!important;border-top:1px solid rgba(214,185,127,.18)!important;}
  .app-shell .testimony-draft-btn{min-height:48px!important;border-radius:16px!important;border:1px solid rgba(214,185,127,.34)!important;background:transparent!important;color:#8A5A08!important;font-weight:850!important;cursor:pointer!important;}
  .app-shell .testimony-draft-btn:disabled{opacity:.42!important;cursor:default!important;}
  .app-shell .testimony-submit-btn{min-height:50px!important;border-radius:16px!important;box-shadow:0 10px 22px rgba(201,144,18,.20)!important;transition:transform .2s ease, box-shadow .2s ease!important;}
  .app-shell .testimony-submit-btn:not(:disabled):hover{transform:translateY(-2px)!important;box-shadow:0 14px 28px rgba(201,144,18,.24)!important;}
  .app-shell .testimony-field-enter-1{animation:testimonyJournalRise .36s ease-out both!important;animation-delay:.04s!important;}
  .app-shell .testimony-field-enter-2{animation:testimonyJournalRise .36s ease-out both!important;animation-delay:.09s!important;}
  .app-shell .testimony-field-enter-3{animation:testimonyJournalRise .36s ease-out both!important;animation-delay:.14s!important;}
  .app-shell .testimony-field-enter-4{animation:testimonyJournalRise .36s ease-out both!important;animation-delay:.19s!important;}
  .app-shell .testimony-submit-success{text-align:center!important;padding:24px 4px!important;animation:testimonyJournalRise .34s ease-out both!important;}
  .app-shell .testimony-submit-check{width:70px!important;height:70px!important;border-radius:50%!important;margin:0 auto 18px!important;display:flex!important;align-items:center!important;justify-content:center!important;color:#3A2812!important;font-size:31px!important;font-weight:900!important;background:linear-gradient(135deg,#FFF3C4,#D7A22A)!important;box-shadow:0 12px 28px rgba(201,144,18,.22)!important;}
  .app-shell .testimony-submit-success h3{margin:0 0 8px!important;color:#3A2812!important;font-size:30px!important;font-weight:500!important;}
  .app-shell .testimony-submit-success p{margin:0 auto 10px!important;max-width:34ch!important;color:#7a674e!important;font-size:14.5px!important;line-height:1.6!important;}
  .app-shell .testimony-submit-blessing{color:#8A5A08!important;font-weight:700!important;}
  .app-shell .testimony-submit-actions{display:grid!important;grid-template-columns:1fr 1fr!important;gap:12px!important;margin-top:24px!important;}
  .app-shell .testimony-submit-actions > button:not(.gold-btn){border:1px solid rgba(214,185,127,.34)!important;background:transparent!important;border-radius:16px!important;color:#8A5A08!important;font-weight:850!important;min-height:48px!important;cursor:pointer!important;}
  @media(max-width:430px){.app-shell .testimony-journal-share-page{gap:15px!important;}.app-shell .testimony-journal-header h2{font-size:29px!important;}.app-shell .testimony-journal-card{padding:24px 18px!important;border-radius:26px!important;}.app-shell .testimony-journal-actions,.app-shell .testimony-submit-actions{grid-template-columns:1fr!important;gap:10px!important;}.app-shell .testimony-field-group{margin-bottom:24px!important;}.app-shell .testimony-anonymous-row{align-items:flex-start!important;}}


  /* Main Testimonies page premium editorial polish. */
  .app-shell .testimony-garden-page{
    background:#FCF8EF!important;
    padding-bottom:116px!important;
  }
  .app-shell .testimony-garden-page::before{
    content:"✦"!important;
    position:absolute!important;
    right:10%!important;
    top:28px!important;
    color:#C99A3D!important;
    opacity:.12!important;
    pointer-events:none!important;
  }
  .app-shell .testimony-garden-page::after{
    content:"⌇"!important;
    position:absolute!important;
    right:5%!important;
    top:86px!important;
    color:#7D674D!important;
    opacity:.08!important;
    pointer-events:none!important;
  }
  .app-shell .testimony-garden-page .testimony-featured-garden{
    order:1!important;
    background:#FFFDF8!important;
    border:1px solid rgba(201,154,61,.26)!important;
    border-radius:26px!important;
    box-shadow:0 10px 30px rgba(84,62,30,.06)!important;
  }
  .app-shell .testimony-main-share-section{
    order:2!important;
    display:flex!important;
    flex-direction:column!important;
    gap:14px!important;
    animation:testimonyJournalRise .34s ease-out both!important;
  }
  .app-shell .testimony-editorial-heading{
    padding:6px 4px 8px!important;
  }
  .app-shell .testimony-editorial-heading .section-label::before{
    content:"🌿"!important;
    margin-right:6px!important;
    opacity:.75!important;
  }
  .app-shell .testimony-editorial-heading h2{
    margin:4px 0 6px!important;
    color:#3F2C1E!important;
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-size:31px!important;
    line-height:1.04!important;
    font-weight:500!important;
    letter-spacing:0!important;
  }
  .app-shell .testimony-editorial-heading p{
    margin:0!important;
    color:#7D674D!important;
    font-size:14px!important;
    line-height:1.5!important;
  }
  .app-shell .testimony-premium-action-card{
    width:100%!important;
    min-height:70px!important;
    border-radius:24px!important;
    padding:15px 16px!important;
    display:grid!important;
    grid-template-columns:auto minmax(0,1fr) auto!important;
    align-items:center!important;
    gap:13px!important;
    text-align:left!important;
    text-decoration:none!important;
    cursor:pointer!important;
    background:#FFFDF8!important;
    box-shadow:0 8px 22px rgba(84,62,30,.055)!important;
    transition:transform .18s ease,border-color .18s ease,box-shadow .18s ease!important;
  }
  .app-shell .testimony-premium-action-card.primary{
    border:1.5px solid rgba(201,154,61,.32)!important;
  }
  .app-shell .testimony-premium-action-card.secondary{
    border:1px solid rgba(110,87,58,.12)!important;
    box-shadow:0 6px 18px rgba(84,62,30,.045)!important;
  }
  .app-shell .testimony-premium-action-card:hover,
  .app-shell .testimony-premium-action-card:focus-visible{
    transform:translateY(-2px)!important;
    border-color:rgba(201,154,61,.46)!important;
    box-shadow:0 12px 28px rgba(84,62,30,.075)!important;
    outline:none!important;
  }
  .app-shell .testimony-action-icon{
    width:42px!important;
    height:42px!important;
    border-radius:50%!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    color:#8A5A08!important;
    background:rgba(255,246,220,.88)!important;
    border:1px solid rgba(201,154,61,.18)!important;
    font-size:19px!important;
    flex-shrink:0!important;
  }
  .app-shell .testimony-premium-action-card.secondary .testimony-action-icon{
    color:#7D674D!important;
    background:rgba(255,253,248,.82)!important;
    border-color:rgba(110,87,58,.12)!important;
  }
  .app-shell .testimony-action-copy strong{
    display:block!important;
    color:#3F2C1E!important;
    font-size:16px!important;
    line-height:1.2!important;
    font-weight:750!important;
  }
  .app-shell .testimony-action-copy small{
    display:block!important;
    color:#7D674D!important;
    font-size:12.75px!important;
    line-height:1.35!important;
    margin-top:3px!important;
    font-weight:400!important;
  }
  .app-shell .testimony-action-chevron{
    color:#A8874D!important;
    font-size:26px!important;
    line-height:1!important;
  }
  .app-shell .testimony-recent-card{
    order:3!important;
    border:1px solid rgba(110,87,58,.12)!important;
    border-radius:24px!important;
    background:#FFFDF8!important;
    box-shadow:0 10px 30px rgba(84,62,30,.06)!important;
    padding:20px!important;
  }
  .app-shell .testimony-recent-head{
    display:flex!important;
    justify-content:space-between!important;
    align-items:flex-start!important;
    gap:14px!important;
    margin-bottom:14px!important;
  }
  .app-shell .testimony-recent-head h3{
    margin:2px 0 0!important;
    color:#3F2C1E!important;
    font-size:17px!important;
    font-weight:650!important;
    line-height:1.2!important;
  }
  .app-shell .testimony-recent-head button{
    min-height:34px!important;
    border-radius:999px!important;
    border:1px solid rgba(201,154,61,.26)!important;
    background:rgba(255,253,248,.84)!important;
    color:#8A5A08!important;
    padding:7px 11px!important;
    font-size:12.5px!important;
    font-weight:750!important;
    cursor:pointer!important;
    white-space:nowrap!important;
  }
  .app-shell .testimony-recent-list{
    display:flex!important;
    flex-direction:column!important;
    gap:10px!important;
  }
  .app-shell .testimony-recent-preview-card{
    display:flex!important;
    gap:12px!important;
    align-items:flex-start!important;
    border-radius:20px!important;
    border:1px solid rgba(201,154,61,.16)!important;
    background:linear-gradient(160deg,rgba(255,250,238,.86),rgba(255,253,248,.96))!important;
    box-shadow:0 6px 18px rgba(84,62,30,.045)!important;
    padding:14px!important;
  }
  .app-shell .testimony-recent-icon{
    width:34px!important;
    height:34px!important;
    border-radius:50%!important;
    background:rgba(255,246,220,.82)!important;
    border:1px solid rgba(201,154,61,.16)!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    color:#C99A3D!important;
    flex-shrink:0!important;
  }
  .app-shell .testimony-recent-preview-card strong{
    display:block!important;
    color:#3F2C1E!important;
    font-size:18px!important;
    line-height:1.08!important;
    font-weight:500!important;
    margin-bottom:5px!important;
  }
  .app-shell .testimony-recent-preview-card p{
    margin:0!important;
    color:#7D674D!important;
    font-size:13px!important;
    line-height:1.5!important;
    display:-webkit-box!important;
    -webkit-line-clamp:3!important;
    -webkit-box-orient:vertical!important;
    overflow:hidden!important;
  }
  .app-shell .testimony-recent-empty{
    border:1px dashed rgba(201,154,61,.24)!important;
    border-radius:20px!important;
    background:rgba(255,250,238,.62)!important;
    padding:22px 14px!important;
    text-align:center!important;
    color:#7D674D!important;
  }
  .app-shell .testimony-recent-empty span{display:block!important;font-size:22px!important;margin-bottom:6px!important;}
  .app-shell .testimony-recent-empty p{margin:0!important;font-size:13.5px!important;}
  .app-shell .testimony-review-note{
    display:flex!important;
    align-items:center!important;
    gap:7px!important;
    border-top:1px solid rgba(110,87,58,.10)!important;
    margin-top:14px!important;
    padding-top:12px!important;
    color:#9A8564!important;
    font-size:11.75px!important;
    line-height:1.35!important;
  }
  .app-shell .testimony-review-note button{
    margin-left:auto!important;
    border:0!important;
    background:transparent!important;
    color:#8A5A08!important;
    cursor:pointer!important;
    font-size:11.75px!important;
    font-weight:700!important;
    padding:0!important;
  }
  .app-shell .testimony-book-preview-card{
    order:4!important;
    border:1px solid rgba(110,87,58,.12)!important;
    border-radius:24px!important;
    background:#FFFDF8!important;
    box-shadow:0 10px 30px rgba(84,62,30,.06)!important;
    padding:20px!important;
  }
  .app-shell .testimony-book-count{
    display:inline-flex!important;
    align-items:center!important;
    gap:5px!important;
    border-radius:999px!important;
    border:1px solid rgba(201,154,61,.22)!important;
    background:rgba(255,250,238,.72)!important;
    color:#8A5A08!important;
    padding:7px 10px!important;
    font-size:12px!important;
    font-weight:750!important;
    white-space:nowrap!important;
  }
  .app-shell .testimony-book-empty{
    position:relative!important;
    overflow:hidden!important;
    border:1px dashed rgba(201,154,61,.28)!important;
    border-radius:22px!important;
    background:rgba(255,250,238,.62)!important;
    padding:26px 16px!important;
    text-align:center!important;
  }
  .app-shell .testimony-book-empty::before,
  .app-shell .testimony-book-empty::after{
    content:"✦"!important;
    position:absolute!important;
    color:#C99A3D!important;
    opacity:.14!important;
    pointer-events:none!important;
  }
  .app-shell .testimony-book-empty::before{left:18%;top:16px!important;}
  .app-shell .testimony-book-empty::after{right:18%;bottom:18px!important;}
  .app-shell .testimony-book-empty-icon{
    display:block!important;
    font-size:24px!important;
    margin-bottom:8px!important;
  }
  .app-shell .testimony-book-empty button{
    margin-top:14px!important;
    min-height:40px!important;
    border-radius:999px!important;
    border:1px solid rgba(201,154,61,.28)!important;
    background:rgba(255,253,248,.82)!important;
    color:#5F4D34!important;
    padding:8px 13px!important;
    font-size:12.75px!important;
    font-weight:750!important;
    cursor:pointer!important;
  }
  .app-shell .testimony-book-mini-row{
    border-top:1px solid rgba(110,87,58,.10)!important;
    padding:13px 0!important;
  }
  .app-shell .testimony-book-mini-row h4{
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-size:18px!important;
    font-weight:500!important;
    color:#3F2C1E!important;
  }
  @media(max-width:430px){
    .app-shell .testimony-editorial-heading h2{font-size:28px!important;}
    .app-shell .testimony-recent-card,
    .app-shell .testimony-book-preview-card{padding:18px!important;}
    .app-shell .testimony-recent-preview-card{gap:10px!important;}
    .app-shell .testimony-recent-icon{width:30px!important;height:30px!important;}
  }



  /* Border breathing-room fix: soften nested frames that felt cramped. */
  .app-shell .testimony-shared-page,
  .app-shell .testimony-garden-page{
    padding-left:14px!important;
    padding-right:14px!important;
  }
  .app-shell .testimony-shared-page-hero.testimony-wall-hero-today,
  .app-shell #shared-testimonies-wall.testimony-wall-today-style,
  .app-shell .testimony-recent-card,
  .app-shell .testimony-book-preview-card,
  .app-shell .testimony-garden-page .testimony-featured-garden{
    border:1px solid rgba(201,154,61,.10)!important;
    outline:none!important;
    box-shadow:0 14px 34px rgba(84,62,30,.055)!important;
  }
  .app-shell .testimony-shared-page-hero.testimony-wall-hero-today::before,
  .app-shell #shared-testimonies-wall.testimony-wall-today-style::before,
  .app-shell .testimony-garden-page .testimony-featured-garden::before{
    opacity:.38!important;
    border-color:rgba(201,154,61,.06)!important;
  }
  .app-shell .testimony-shared-page-hero.testimony-wall-hero-today{
    padding:26px 24px!important;
  }
  .app-shell #shared-testimonies-wall.testimony-wall-today-style{
    padding:24px 22px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-card,
  .app-shell .testimony-recent-preview-card,
  .app-shell .testimony-premium-action-card,
  .app-shell .testimony-wall-share-card{
    border-color:rgba(201,154,61,.10)!important;
    box-shadow:0 8px 22px rgba(84,62,30,.045)!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-card{
    padding:24px 22px!important;
    margin-inline:1px!important;
  }
  .app-shell #shared-testimonies-wall .scroll-list{
    padding:2px 2px 4px!important;
    gap:16px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-mark,
  .app-shell .testimony-action-icon,
  .app-shell .testimony-wall-share-icon,
  .app-shell .testimony-recent-icon{
    border-color:rgba(201,154,61,.10)!important;
  }
  .app-shell #shared-testimonies-wall .testimony-category-row{
    padding-left:0!important;
    padding-right:0!important;
  }
  .app-shell #shared-testimonies-wall .testimony-category-row button{
    border-color:rgba(201,154,61,.12)!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-stat-badge,
  .app-shell .testimony-book-count,
  .app-shell .testimony-recent-head button{
    border-color:rgba(201,154,61,.12)!important;
    background:rgba(255,253,246,.72)!important;
  }
  .app-shell .home-page.simple-home-page .sticky-preview-card.simple-home-preview,
  .app-shell .home-page.simple-home-page .simple-home-blessing,
  .app-shell .home-page.simple-home-page .home-shop-shell-card{
    border:1px solid rgba(201,154,61,.09)!important;
    outline:none!important;
    box-shadow:0 14px 34px rgba(84,62,30,.052)!important;
  }
  .app-shell .home-page.simple-home-page .sticky-preview-card.simple-home-preview::before,
  .app-shell .home-page.simple-home-page .home-art-frame{
    border-color:rgba(201,154,61,.10)!important;
  }
  .app-shell .home-page.simple-home-page .home-art-frame{
    padding:14px!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.78),0 8px 22px rgba(84,62,30,.04)!important;
  }
  @media(max-width:430px){
    .app-shell .testimony-shared-page,
    .app-shell .testimony-garden-page{
      padding-left:12px!important;
      padding-right:12px!important;
    }
    .app-shell .testimony-shared-page-hero.testimony-wall-hero-today,
    .app-shell #shared-testimonies-wall.testimony-wall-today-style{
      padding:20px 17px!important;
      border-radius:25px!important;
    }
    .app-shell #shared-testimonies-wall .testimony-wall-story-card{
      padding:20px 17px!important;
      border-radius:22px!important;
    }
    .app-shell #shared-testimonies-wall .testimony-wall-story-content{
      gap:10px!important;
    }
    .app-shell .home-page.simple-home-page .sticky-preview-card.simple-home-preview,
    .app-shell .home-page.simple-home-page .simple-home-blessing{
      padding:19px 17px!important;
    }
    .app-shell .home-page.simple-home-page .home-art-frame{
      padding:10px!important;
    }
  }



  /* Premium compact Featured Testimony card: story-first, no dominant image. */
  .app-shell .testimony-garden-page .premium-featured-testimony{
    position:relative!important;
    overflow:hidden!important;
    padding:24px!important;
    border:1px solid rgba(201,154,61,.16)!important;
    border-radius:26px!important;
    background:#FFFDF8!important;
    box-shadow:0 12px 30px rgba(84,62,30,.06)!important;
  }
  .app-shell .testimony-garden-page .premium-featured-testimony::before{
    content:""!important;
    position:absolute!important;
    right:-24px!important;
    top:-30px!important;
    width:116px!important;
    height:132px!important;
    opacity:.055!important;
    pointer-events:none!important;
    background:
      radial-gradient(ellipse at 30% 26%,#8AA06F 0 16%,transparent 17%),
      radial-gradient(ellipse at 56% 42%,#8AA06F 0 14%,transparent 15%),
      radial-gradient(ellipse at 36% 62%,#8AA06F 0 13%,transparent 14%),
      linear-gradient(120deg,transparent 48%,#8AA06F 49% 51%,transparent 52%)!important;
    transform:rotate(10deg)!important;
  }
  .app-shell .testimony-garden-page .premium-featured-testimony::after{
    content:"✦"!important;
    position:absolute!important;
    right:22px!important;
    bottom:22px!important;
    color:#C99A3D!important;
    opacity:.07!important;
    pointer-events:none!important;
    font-size:30px!important;
  }
  .app-shell .premium-featured-header{
    position:relative!important;
    z-index:1!important;
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:12px!important;
    margin-bottom:20px!important;
  }
  .app-shell .premium-featured-header .section-label{
    margin:0!important;
    color:#B79A64!important;
    font-size:10.5px!important;
    letter-spacing:.17em!important;
    font-weight:700!important;
  }
  .app-shell .premium-featured-read-time{
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    min-height:30px!important;
    padding:6px 10px!important;
    border-radius:999px!important;
    border:1px solid rgba(201,154,61,.16)!important;
    background:rgba(255,248,223,.54)!important;
    color:#7E5A1E!important;
    font-size:12px!important;
    line-height:1!important;
    font-weight:650!important;
    white-space:nowrap!important;
    box-shadow:none!important;
  }
  .app-shell .premium-featured-story{
    position:relative!important;
    z-index:1!important;
    display:grid!important;
    grid-template-columns:auto minmax(0,1fr)!important;
    gap:15px!important;
    align-items:flex-start!important;
  }
  .app-shell .premium-featured-icon{
    width:42px!important;
    height:42px!important;
    border-radius:50%!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    flex-shrink:0!important;
    color:#C99A3D!important;
    background:rgba(255,246,220,.78)!important;
    border:1px solid rgba(201,154,61,.12)!important;
    font-size:18px!important;
    margin-top:2px!important;
  }
  .app-shell .premium-featured-copy h3{
    margin:0 0 7px!important;
    color:#342313!important;
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-size:clamp(28px,6.6vw,38px)!important;
    line-height:1.02!important;
    font-weight:500!important;
    letter-spacing:0!important;
  }
  .app-shell .premium-featured-author{
    margin:0 0 16px!important;
    color:#8F7B5D!important;
    font-size:13px!important;
    line-height:1.35!important;
    font-weight:500!important;
  }
  .app-shell .premium-featured-excerpt{
    max-height:none!important;
    overflow:visible!important;
    padding:0!important;
  }
  .app-shell .premium-featured-excerpt p{
    margin:0 0 12px!important;
    color:#5F4D34!important;
    font-family:'Nunito',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif!important;
    font-size:15px!important;
    line-height:1.68!important;
    font-weight:400!important;
    font-style:normal!important;
  }
  .app-shell .premium-featured-excerpt:not(.open) p:first-child{
    display:-webkit-box!important;
    -webkit-line-clamp:4!important;
    -webkit-box-orient:vertical!important;
    overflow:hidden!important;
  }
  .app-shell .premium-featured-excerpt.open p:first-child{
    display:block!important;
    overflow:visible!important;
  }
  .app-shell .premium-featured-read-action{
    min-height:44px!important;
    margin-top:4px!important;
    padding:8px 0!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    color:#8A5A08!important;
    display:inline-flex!important;
    align-items:center!important;
    gap:6px!important;
    font-size:13.5px!important;
    line-height:1!important;
    font-weight:720!important;
    cursor:pointer!important;
    text-decoration:none!important;
    box-shadow:none!important;
  }
  .app-shell .premium-featured-read-action span{
    display:inline-block!important;
    transition:transform .18s ease!important;
  }
  .app-shell .premium-featured-read-action:hover span,
  .app-shell .premium-featured-read-action:focus-visible span{
    transform:translateX(2px)!important;
  }
  .app-shell .premium-featured-read-action:hover,
  .app-shell .premium-featured-read-action:focus-visible{
    color:#5F3F09!important;
    outline:2px solid rgba(240,199,94,.22)!important;
    outline-offset:4px!important;
  }
  .app-shell .featured-testimony-image.premium-featured-image{
    width:100%!important;
    aspect-ratio:16/10!important;
    height:auto!important;
    min-height:210px!important;
    object-fit:cover!important;
    object-position:center!important;
    display:block!important;
    background:linear-gradient(145deg,rgba(255,253,246,.96),rgba(255,248,223,.62))!important;
  }

  .app-shell .testimony-garden-page .premium-featured-testimony .premium-featured-image{
    display:block!important;
    position:relative!important;
    z-index:1!important;
    width:100%!important;
    height:clamp(190px,42vw,260px)!important;
    object-fit:cover!important;
    object-position:center!important;
    margin:0 0 22px!important;
    border-radius:22px!important;
    border:1px solid rgba(201,154,61,.12)!important;
    background:#FFFDF8!important;
    box-shadow:0 12px 26px rgba(84,62,30,.075)!important;
  }
  .app-shell .testimony-garden-page .premium-featured-testimony .testimony-wall-card-title{
    display:none!important;
  }
  @media(max-width:430px){
    .app-shell .testimony-garden-page .premium-featured-testimony{padding:21px 18px!important;border-radius:24px!important;}
    .app-shell .premium-featured-header{align-items:flex-start!important;flex-wrap:wrap!important;margin-bottom:18px!important;}
    .app-shell .premium-featured-story{grid-template-columns:1fr!important;gap:12px!important;}
    .app-shell .premium-featured-icon{width:38px!important;height:38px!important;}
    .app-shell .testimony-garden-page .premium-featured-testimony .premium-featured-image{height:clamp(170px,50vw,220px)!important;border-radius:19px!important;margin-bottom:18px!important;}
    .app-shell .premium-featured-copy h3{font-size:clamp(26px,8vw,34px)!important;}
    .app-shell .premium-featured-excerpt p{font-size:14.5px!important;line-height:1.66!important;}
  }



  /* Heavenly Home layout refactor: separate intro, progress, artwork, and blessing. */
  .app-shell .home-page.simple-home-page{gap:24px!important;}
  .app-shell .home-page.simple-home-page .home-intro-section{display:flex!important;justify-content:space-between!important;align-items:flex-start!important;gap:18px!important;padding:12px 6px 0!important;border:0!important;background:transparent!important;box-shadow:none!important;}
  .app-shell .home-page.simple-home-page .home-intro-copy{min-width:0!important;flex:1!important;}
  .app-shell .home-page.simple-home-page .home-intro-copy .section-label{margin-bottom:10px!important;}
  .app-shell .home-page.simple-home-page .home-intro-copy h2{margin:0 0 13px!important;color:#342313!important;font-family:'Cormorant Garamond',Georgia,serif!important;font-size:clamp(36px,9vw,54px)!important;line-height:.98!important;font-weight:500!important;letter-spacing:0!important;}
  .app-shell .home-page.simple-home-page .home-intro-copy .home-stage-title-copy,
  .app-shell .home-page.simple-home-page .home-intro-copy .home-stage-signature-copy{max-width:34ch!important;margin:0!important;color:#6F5C43!important;font-size:15px!important;line-height:1.58!important;}
  .app-shell .home-page.simple-home-page .home-intro-copy .home-stage-signature-copy{margin-top:4px!important;}
  .app-shell .home-page.simple-home-page .home-intro-actions{display:flex!important;flex-direction:column!important;align-items:flex-end!important;gap:8px!important;flex-shrink:0!important;padding-top:24px!important;}
  .app-shell .home-page.simple-home-page .home-intro-actions .soft-action-btn{min-height:34px!important;padding:6px 11px!important;border-radius:999px!important;font-size:12px!important;}
  .app-shell .home-page.simple-home-page .home-progress-card{margin-top:0!important;padding:16px 18px!important;border:1px solid rgba(201,154,61,.09)!important;border-radius:22px!important;background:rgba(255,253,246,.80)!important;box-shadow:0 10px 24px rgba(84,62,30,.045)!important;}
  .app-shell .home-page.simple-home-page .home-progress-card .home-faith-growth-indicator{margin:0!important;padding:0!important;border:0!important;border-radius:0!important;background:transparent!important;max-width:none!important;}
  .app-shell .home-page.simple-home-page .home-progress-card .home-faith-growth-indicator > span,
  .app-shell .home-page.simple-home-page .home-progress-card .home-faith-growth-indicator small{font-size:12px!important;}
  .app-shell .home-page.simple-home-page .home-progress-card .home-faith-growth-track{margin-top:9px!important;}
  .app-shell .home-page.simple-home-page .home-art-card{margin-top:0!important;padding:18px!important;border:1px solid rgba(201,154,61,.09)!important;border-radius:30px!important;background:linear-gradient(145deg,#FFFDF8,#FFF9EA)!important;box-shadow:0 16px 38px rgba(84,62,30,.065)!important;}
  .app-shell .home-page.simple-home-page .home-art-card::before{display:none!important;}
  .app-shell .home-page.simple-home-page .home-art-card .home-art-frame{margin-top:0!important;padding:10px!important;border-radius:26px!important;border:0!important;background:radial-gradient(circle at 50% 38%,rgba(255,236,178,.28),transparent 54%),linear-gradient(145deg,rgba(255,253,246,.95),rgba(255,248,231,.68))!important;box-shadow:inset 0 1px 0 rgba(255,255,255,.82),0 8px 22px rgba(84,62,30,.04)!important;}
  .app-shell .home-page.simple-home-page .home-art-card .home-room{min-height:430px!important;height:clamp(380px,72vw,470px)!important;}
  .app-shell .home-page.simple-home-page .home-bird-prayer-note{
    margin:10px 2px 0!important;
    color:#9A845F!important;
    font-size:11.5px!important;
    line-height:1.45!important;
    text-align:center!important;
    font-weight:600!important;
    letter-spacing:.01em!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing{margin-top:4px!important;}
  @media(max-width:560px){
    .app-shell .home-page.simple-home-page{gap:22px!important;}
    .app-shell .home-page.simple-home-page .home-intro-section{flex-direction:column!important;gap:12px!important;padding:10px 4px 0!important;}
    .app-shell .home-page.simple-home-page .home-intro-actions{align-items:flex-start!important;padding-top:0!important;}
    .app-shell .home-page.simple-home-page .home-intro-copy h2{font-size:clamp(34px,10.5vw,46px)!important;}
    .app-shell .home-page.simple-home-page .home-progress-card{padding:15px 16px!important;border-radius:21px!important;}
    .app-shell .home-page.simple-home-page .home-art-card{padding:12px!important;border-radius:27px!important;}
    .app-shell .home-page.simple-home-page .home-art-card .home-art-frame{padding:7px!important;border-radius:23px!important;}
    .app-shell .home-page.simple-home-page .home-art-card .home-room{min-height:390px!important;height:clamp(370px,92vw,430px)!important;}
  }


  /* Global premium rhythm pass: lighter controls, calmer cards, and more generous spacing. */
  .app-shell .today-page-calm,
  .app-shell .growth-page-calm,
  .app-shell .home-page,
  .app-shell .prayer-page-calm,
  .app-shell .testimony-garden-page,
  .app-shell .testimony-shared-page,
  .app-shell .premium-journal-page,
  .app-shell .spirit-friends-dashboard{
    gap:clamp(20px,4vw,24px)!important;
  }
  .app-shell .card,
  .app-shell .sacred-card,
  .app-shell .focus-card,
  .app-shell .soft-guide-card,
  .app-shell .today-hero-card,
  .app-shell .today-faith-walk-card,
  .app-shell .growth-summary-card,
  .app-shell .growth-stage-hero,
  .app-shell .growth-account-card,
  .app-shell .growth-hero-card,
  .app-shell .growth-closet-card,
  .app-shell .growth-stones-card,
  .app-shell .growth-book-card,
  .app-shell .home-care-card,
  .app-shell .home-preview-card,
  .app-shell .home-shop-shell-card,
  .app-shell .journal-write-card,
  .app-shell .journal-history-card,
  .app-shell .testimony-garden-hero,
  .app-shell .featured-testimony-card,
  .app-shell .share-testimony-card,
  .app-shell .testimony-shared-garden,
  .app-shell .prayer-friends-card,
  .app-shell .spirit-friends-tools-card,
  .app-shell .community-feed-card,
  .app-shell .accountability-primary-card,
  .app-shell .blessing-community-card{
    padding:clamp(22px,5vw,28px)!important;
    border-color:rgba(201,154,61,.10)!important;
    box-shadow:0 12px 30px rgba(84,62,30,.045)!important;
  }
  .app-shell .card > * + *,
  .app-shell .sacred-card > * + *,
  .app-shell .focus-card > * + *,
  .app-shell .testimony-recent-card > * + *,
  .app-shell .testimony-book-preview-card > * + *,
  .app-shell .home-progress-card > * + *,
  .app-shell .simple-home-blessing > * + *{
    margin-top:14px;
  }
  .app-shell .gold-btn,
  .app-shell .soft-action-btn,
  .app-shell .quest-nav-btn,
  .app-shell .opt-btn,
  .app-shell .sam-btn,
  .app-shell .home-bless-btn,
  .app-shell .home-care-action,
  .app-shell .next-action-btn,
  .app-shell .pray-now-btn,
  .app-shell .partner-focus-complete-btn,
  .app-shell .shop-card button,
  .app-shell .home-shop-card button,
  .app-shell .testimony-submit-actions > button,
  .app-shell .testimony-journal-actions > button{
    min-height:48px!important;
    padding:10px 15px!important;
    border-width:1px!important;
    border-radius:14px!important;
    font-weight:700!important;
    box-shadow:0 6px 16px rgba(84,62,30,.055)!important;
  }
  .app-shell .gold-btn{
    min-height:50px!important;
    padding-inline:18px!important;
  }
  .app-shell .soft-action-btn.muted,
  .app-shell button.muted,
  .app-shell .testimony-wall-read-more,
  .app-shell .premium-featured-read-action,
  .app-shell .testimony-recent-head button,
  .app-shell .testimony-review-note button{
    box-shadow:none!important;
  }
  .app-shell .testimony-wall-read-more,
  .app-shell .premium-featured-read-action,
  .app-shell .testimony-recent-head button,
  .app-shell .testimony-review-note button,
  .app-shell .testimony-book-empty button{
    min-height:42px!important;
    padding:7px 2px!important;
    background:transparent!important;
    border:0!important;
    border-bottom:1px solid rgba(201,154,61,.28)!important;
    border-radius:0!important;
    color:#8A5A08!important;
  }
  .app-shell .testimony-category-row,
  .app-shell #shared-testimonies-wall .testimony-category-row,
  .app-shell .home-shop-category-row,
  .app-shell .prayer-list-filter-row,
  .app-shell .prayer-kind-choice-row,
  .app-shell .home-move-chip-row{
    gap:12px!important;
    padding:4px 2px 14px!important;
    overflow-x:auto!important;
    scrollbar-width:none!important;
  }
  .app-shell .testimony-category-row::-webkit-scrollbar,
  .app-shell .home-shop-category-row::-webkit-scrollbar,
  .app-shell .prayer-list-filter-row::-webkit-scrollbar,
  .app-shell .prayer-kind-choice-row::-webkit-scrollbar,
  .app-shell .home-move-chip-row::-webkit-scrollbar{display:none!important;}
  .app-shell #shared-testimonies-wall .testimony-category-row button,
  .app-shell .testimony-category-row button,
  .app-shell .prayer-list-filter-row button,
  .app-shell .prayer-kind-choice-row button,
  .app-shell .home-move-chip,
  .app-shell .quick-prayer-chip,
  .app-shell .prayer-filter-btn{
    min-height:38px!important;
    padding:8px 13px!important;
    border-width:1px!important;
    border-radius:999px!important;
    box-shadow:none!important;
    flex:0 0 auto!important;
  }
  .app-shell #shared-testimonies-wall .testimony-category-row button.active,
  .app-shell .testimony-category-row button.active,
  .app-shell .prayer-list-filter-row button.active,
  .app-shell .home-move-chip.active{
    transform:none!important;
    box-shadow:0 5px 14px rgba(201,154,61,.12)!important;
  }
  .app-shell .home-category-card,
  .app-shell .shop-category-card{
    min-height:96px!important;
    padding:12px!important;
    border-width:1px!important;
    box-shadow:0 8px 18px rgba(84,62,30,.04)!important;
  }
  .app-shell .home-category-card.active,
  .app-shell .shop-category-card.active{
    transform:none!important;
    box-shadow:0 8px 20px rgba(201,154,61,.12)!important;
  }
  .app-shell .placed-chip,
  .app-shell .meaning-chip,
  .app-shell .sticky-preview-label,
  .app-shell .prayer-status-chip,
  .app-shell .testimony-wall-stat-badge,
  .app-shell .testimony-book-count{
    border-width:1px!important;
    padding:6px 10px!important;
    box-shadow:none!important;
    font-weight:650!important;
  }
  .app-shell .testimony-recent-preview-card,
  .app-shell #shared-testimonies-wall .testimony-wall-story-card,
  .app-shell .testimony-premium-action-card,
  .app-shell .testimony-wall-share-card,
  .app-shell .planted-prayer-card,
  .app-shell .home-shop-card,
  .app-shell .shop-card,
  .app-shell .friend-card,
  .app-shell .journal-entry-card{
    border-color:rgba(201,154,61,.09)!important;
    box-shadow:0 8px 22px rgba(84,62,30,.04)!important;
  }
  .app-shell .section-divider{margin:8px 0 16px!important;}
  .app-shell .section-divider-line{opacity:.55!important;}
  .app-shell .section-divider-label{letter-spacing:.16em!important;font-weight:650!important;}
  @media(max-width:430px){
    .app-shell .card,
    .app-shell .sacred-card,
    .app-shell .focus-card,
    .app-shell .soft-guide-card,
    .app-shell .testimony-recent-card,
    .app-shell .testimony-book-preview-card,
    .app-shell .home-progress-card,
    .app-shell .simple-home-blessing{
      padding:20px!important;
    }
    .app-shell .gold-btn,
    .app-shell .soft-action-btn,
    .app-shell .quest-nav-btn,
    .app-shell .opt-btn,
    .app-shell .sam-btn,
    .app-shell .home-bless-btn,
    .app-shell .home-care-action,
    .app-shell .shop-card button,
    .app-shell .home-shop-card button{
      min-height:46px!important;
      padding:9px 13px!important;
    }
    .app-shell .home-category-card,
    .app-shell .shop-category-card{min-height:90px!important;padding:11px!important;}
  }





  /* Testimony page heading: make the wall title part of the featured story card. */
  .app-shell .testimony-wall-feature-heading{
    position:relative!important;
    z-index:1!important;
    margin:0 0 12px!important;
    padding-bottom:12px!important;
    border-bottom:1px solid rgba(214,185,127,.18)!important;
  }
  .app-shell .testimony-wall-feature-heading h2{
    margin:0!important;
    color:#342313!important;
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-size:clamp(34px,7.4vw,48px)!important;
    line-height:.98!important;
    font-weight:500!important;
    letter-spacing:0!important;
  }
  .app-shell .testimony-wall-feature-heading + .premium-featured-header{
    margin-top:14px!important;
  }
  @media(max-width:430px){
    .app-shell .testimony-wall-feature-heading{margin-bottom:10px!important;padding-bottom:10px!important;}
    .app-shell .testimony-wall-feature-heading h2{font-size:clamp(32px,9vw,42px)!important;}
  }

  /* Featured Testimony cleanup: remove the decorative diamond circle. */
  .app-shell .premium-featured-story{
    grid-template-columns:minmax(0,1fr)!important;
  }
  .app-shell .premium-featured-icon{
    display:none!important;
  }

  /* Final app-wide layout rhythm: wider content, connected sections, safe edges. */
  .app-shell .app-main-frame{
    width:min(100%,900px)!important;
    max-width:min(100%,900px)!important;
    padding-inline:clamp(8px,2vw,14px)!important;
  }
  .app-shell .app-content{
    padding-inline:clamp(4px,1.4vw,8px)!important;
  }
  .app-shell .app-header{
    padding-inline:clamp(10px,2.4vw,18px)!important;
  }
  .app-shell .today-page-calm,
  .app-shell .growth-page-calm,
  .app-shell .home-page,
  .app-shell .prayer-page-calm,
  .app-shell .testimony-garden-page,
  .app-shell .testimony-shared-page,
  .app-shell .premium-journal-page,
  .app-shell .spirit-friends-dashboard,
  .app-shell .testimony-journal-share-page{
    gap:clamp(20px,3.8vw,24px)!important;
  }
  .app-shell .today-page-calm > .card,
  .app-shell .growth-page-calm > .card,
  .app-shell .home-page > .card,
  .app-shell .prayer-page-calm > .card,
  .app-shell .testimony-garden-page > .card,
  .app-shell .testimony-shared-page > .card,
  .app-shell .premium-journal-page > .card,
  .app-shell .spirit-friends-dashboard > .card,
  .app-shell .testimony-journal-card{
    margin-block:0!important;
  }
  .app-shell .home-page.simple-home-page{gap:clamp(20px,3.8vw,24px)!important;}
  .app-shell .home-page.simple-home-page .home-intro-section{padding-inline:2px!important;}
  .app-shell .home-page.simple-home-page .home-progress-card,
  .app-shell .home-page.simple-home-page .home-art-card,
  .app-shell .home-page.simple-home-page .simple-home-blessing{
    margin-top:0!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing{margin-top:0!important;}
  .app-shell .testimony-main-share-section,
  .app-shell .testimony-recent-card,
  .app-shell .testimony-book-preview-card,
  .app-shell #shared-testimonies-wall,
  .app-shell .prayer-vessels-section,
  .app-shell .answered-altar-card,
  .app-shell .accountability-primary-card,
  .app-shell .spirit-friends-tools-card,
  .app-shell .community-feed-card,
  .app-shell .blessing-community-card{
    margin-top:0!important;
  }
  .app-shell .card,
  .app-shell .sacred-card,
  .app-shell .focus-card,
  .app-shell .soft-guide-card,
  .app-shell .testimony-recent-card,
  .app-shell .testimony-book-preview-card,
  .app-shell .testimony-premium-action-card,
  .app-shell .testimony-wall-share-card,
  .app-shell .planted-prayer-card,
  .app-shell .friend-card{
    padding:clamp(20px,4.4vw,26px)!important;
  }
  .app-shell .section-divider{margin:6px 0 14px!important;}
  @media(max-width:560px){
    .app-shell .app-main-frame{padding-inline:6px!important;}
    .app-shell .app-content{padding-inline:4px!important;}
    .app-shell .app-header{padding-inline:12px!important;}
    .app-shell .today-page-calm,
    .app-shell .growth-page-calm,
    .app-shell .home-page,
    .app-shell .prayer-page-calm,
    .app-shell .testimony-garden-page,
    .app-shell .testimony-shared-page,
    .app-shell .premium-journal-page,
    .app-shell .spirit-friends-dashboard,
    .app-shell .testimony-journal-share-page{gap:20px!important;}
  }





  /* Final Growth page visual refinement: softer hierarchy, spacious progress, premium cards. */
  .app-shell .growth-page-calm{
    gap:22px!important;
    padding-bottom:26px!important;
  }
  .app-shell .growth-page-calm > .card,
  .app-shell .growth-page-calm .growth-summary-card,
  .app-shell .growth-page-calm .growth-stage-hero,
  .app-shell .growth-page-calm .growth-account-card,
  .app-shell .growth-page-calm .growth-hero-card,
  .app-shell .growth-page-calm .growth-closet-card,
  .app-shell .growth-page-calm .growth-stones-card,
  .app-shell .growth-page-calm .growth-book-card{
    border:1px solid rgba(214,185,127,.14)!important;
    border-radius:30px!important;
    background:linear-gradient(145deg,rgba(255,253,248,.98),rgba(255,250,239,.80))!important;
    box-shadow:0 14px 32px rgba(84,62,30,.05),inset 0 1px 0 rgba(255,255,255,.86)!important;
    padding:24px!important;
    outline:none!important;
  }
  .app-shell .growth-page-calm .growth-stage-hero{
    order:1!important;
    display:grid!important;
    grid-template-columns:auto minmax(0,1fr)!important;
    gap:22px!important;
    align-items:center!important;
    border-color:rgba(125,174,212,.16)!important;
    background:
      radial-gradient(circle at 18% 22%,rgba(219,234,254,.60),transparent 32%),
      radial-gradient(circle at 88% 82%,rgba(255,243,196,.66),transparent 34%),
      linear-gradient(145deg,rgba(255,253,248,.98),rgba(247,252,255,.82))!important;
  }
  .app-shell .growth-page-calm .growth-stage-orb{
    width:154px!important;
    min-width:154px!important;
    height:154px!important;
    border-radius:44px!important;
    display:flex!important;
    align-items:center!important;
    justify-content:center!important;
    background:radial-gradient(circle at 50% 30%,rgba(255,255,255,.88),rgba(255,246,216,.48) 62%,rgba(219,234,254,.36))!important;
    box-shadow:0 16px 34px rgba(84,62,30,.07),inset 0 1px 0 rgba(255,255,255,.92)!important;
  }
  .app-shell .growth-page-calm .growth-stage-copy h3{
    margin:3px 0 5px!important;
    font-size:clamp(31px,7.2vw,46px)!important;
    line-height:1!important;
  }
  .app-shell .growth-page-calm .growth-stage-copy p{
    max-width:34ch!important;
    font-size:14.5px!important;
    line-height:1.56!important;
    color:#6F5C43!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress{
    margin-top:18px!important;
    padding:14px!important;
    border:0!important;
    border-radius:20px!important;
    background:rgba(255,255,255,.58)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress-top{
    display:flex!important;
    justify-content:space-between!important;
    align-items:flex-start!important;
    gap:12px!important;
    margin-bottom:10px!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress-top span{
    max-width:28ch!important;
    color:#7D674D!important;
    font-size:12px!important;
    line-height:1.35!important;
    letter-spacing:0!important;
    text-transform:none!important;
    font-weight:500!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress-top b{
    flex-shrink:0!important;
    font-family:'Cormorant Garamond',Georgia,serif!important;
    color:#8A5A08!important;
    font-size:27px!important;
    line-height:1!important;
    font-weight:500!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress-track{
    height:10px!important;
    border-radius:999px!important;
    background:rgba(214,185,127,.22)!important;
    overflow:hidden!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress-fill{
    height:100%!important;
    min-width:28px!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,#93B98A,#E8C66F,#C99012)!important;
    box-shadow:0 0 14px rgba(201,154,61,.20)!important;
    position:relative!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress-label{
    position:absolute!important;
    right:7px!important;
    top:50%!important;
    transform:translateY(-50%)!important;
    color:#FFFDF8!important;
    font-size:9.5px!important;
    line-height:1!important;
    font-weight:650!important;
  }
  .app-shell .growth-page-calm .growth-hero-card{order:2!important;}
  .app-shell .growth-page-calm .growth-closet-card{order:3!important;background:linear-gradient(145deg,#FFFDF8,#F8FBFF)!important;}
  .app-shell .growth-page-calm .growth-account-card{order:0!important;background:linear-gradient(145deg,#FFFDF8,#F8FBFF)!important;}
  .app-shell .growth-page-calm .growth-stones-card{order:5!important;background:linear-gradient(145deg,#FFFDF8,#F8FCFF)!important;}
  .app-shell .growth-page-calm .growth-book-card{order:6!important;background:linear-gradient(145deg,#FFFDF8,#FFF8FB)!important;}
  .app-shell .growth-page-calm .testimony-stone-toggle{
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
    padding:0!important;
    min-height:auto!important;
  }
  .app-shell .growth-page-calm .growth-hero-card .testimony-stone-toggle:first-child{
    margin-bottom:16px!important;
  }
  .app-shell .growth-page-calm .testimony-stone-toggle h3{
    font-size:clamp(24px,5.8vw,32px)!important;
    line-height:1.05!important;
  }
  .app-shell .growth-page-calm .placed-chip{
    background:rgba(255,253,248,.74)!important;
    border:1px solid rgba(214,185,127,.22)!important;
    border-radius:999px!important;
    color:#7A5717!important;
    font-weight:650!important;
    box-shadow:none!important;
  }
  .app-shell .growth-page-calm .growth-path-list{
    gap:12px!important;
    padding:5px 2px 12px!important;
    overflow-x:auto!important;
    scrollbar-width:none!important;
  }
  .app-shell .growth-page-calm .growth-path-list::-webkit-scrollbar{display:none!important;}
  .app-shell .growth-page-calm .growth-path-list::before{display:none!important;}
  .app-shell .growth-page-calm .growth-path-step{
    min-width:168px!important;
    padding:14px!important;
    border:1px solid rgba(214,185,127,.16)!important;
    border-radius:22px!important;
    background:rgba(255,255,255,.56)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.84)!important;
  }
  .app-shell .growth-page-calm .growth-path-step.current{
    border-color:rgba(201,154,61,.28)!important;
    background:linear-gradient(145deg,rgba(255,248,223,.86),rgba(236,253,245,.56))!important;
    box-shadow:0 10px 22px rgba(84,62,30,.06),inset 0 1px 0 rgba(255,255,255,.88)!important;
  }
  .app-shell .growth-page-calm .growth-path-avatar{
    width:62px!important;
    height:62px!important;
    min-width:62px!important;
    border-radius:22px!important;
    background:radial-gradient(circle at 50% 30%,rgba(255,255,255,.86),rgba(255,248,223,.56))!important;
    box-shadow:0 8px 18px rgba(84,62,30,.06)!important;
  }
  .app-shell .growth-page-calm .growth-path-copy{
    min-width:0!important;
  }
  .app-shell .growth-page-calm .growth-path-name{
    font-size:14.5px!important;
    line-height:1.25!important;
    font-weight:600!important;
  }
  .app-shell .growth-page-calm .growth-path-range{
    margin-top:3px!important;
    color:#8A714F!important;
    font-size:12px!important;
    line-height:1.25!important;
  }
  .app-shell .growth-page-calm .growth-path-status,
  .app-shell .growth-page-calm .testimony-stone-chevron{
    width:32px!important;
    height:32px!important;
    min-width:32px!important;
    border:1px solid rgba(214,185,127,.20)!important;
    border-radius:50%!important;
    background:rgba(255,253,248,.76)!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    box-shadow:none!important;
  }
  .app-shell .growth-page-calm .growth-customize-panel{
    margin-top:18px!important;
    padding-top:18px!important;
    border-top:1px solid rgba(214,185,127,.18)!important;
  }
  .app-shell .growth-page-calm .growth-customize-panel .testimony-stone-toggle{
    padding:12px 0!important;
  }
  .app-shell .growth-page-calm .growth-closet-copy{
    margin:4px 0 0!important;
    font-size:14px!important;
  }
  .app-shell .growth-page-calm .growth-closet-card .gold-btn,
  .app-shell .growth-page-calm .growth-closet-card button:not(.gold-btn),
  .app-shell .growth-page-calm .growth-account-card .gold-btn,
  .app-shell .growth-page-calm .growth-account-card button:not(.gold-btn){
    min-height:46px!important;
    border-radius:15px!important;
    padding:9px 12px!important;
  }
  .app-shell .growth-page-calm .testimony-stone-grid{
    gap:12px!important;
    margin-top:18px!important;
  }
  .app-shell .growth-page-calm .testimony-stone-card,
  .app-shell .growth-page-calm .month-group,
  .app-shell .growth-page-calm .month-toggle{
    border:1px solid rgba(214,185,127,.16)!important;
    border-radius:20px!important;
    background:rgba(255,255,255,.54)!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.84)!important;
  }
  .app-shell .growth-page-calm .testimony-stone-card{padding:14px!important;}
  .app-shell .growth-page-calm .sg-input{
    min-height:46px!important;
    border-radius:15px!important;
    border-color:rgba(214,185,127,.28)!important;
    background:rgba(255,253,248,.84)!important;
  }
  @media(max-width:560px){
    .app-shell .growth-page-calm{gap:20px!important;padding-bottom:22px!important;}
    .app-shell .growth-page-calm > .card,
    .app-shell .growth-page-calm .growth-summary-card,
    .app-shell .growth-page-calm .growth-stage-hero,
    .app-shell .growth-page-calm .growth-account-card,
    .app-shell .growth-page-calm .growth-hero-card,
    .app-shell .growth-page-calm .growth-closet-card,
    .app-shell .growth-page-calm .growth-stones-card,
    .app-shell .growth-page-calm .growth-book-card{
      padding:20px!important;
      border-radius:27px!important;
    }
    .app-shell .growth-page-calm .growth-stage-hero{
      grid-template-columns:1fr!important;
      text-align:left!important;
      gap:18px!important;
    }
    .app-shell .growth-page-calm .growth-stage-orb{
      width:136px!important;
      min-width:136px!important;
      height:136px!important;
      border-radius:38px!important;
      margin:0 auto!important;
    }
    .app-shell .growth-page-calm .growth-stage-copy h3{
      font-size:clamp(31px,9vw,40px)!important;
    }
    .app-shell .growth-page-calm .growth-stage-progress-top span{max-width:22ch!important;}
    .app-shell .growth-page-calm .growth-path-step{
      min-width:152px!important;
      padding:13px!important;
    }
    .app-shell .growth-page-calm .growth-path-avatar{
      width:58px!important;
      height:58px!important;
      min-width:58px!important;
      border-radius:20px!important;
    }
  }

  /* Final Heavenly Home artwork polish: softer cloud puffs and clearer house definition. */
  .app-shell .home-page .home-room{
    overflow:hidden!important;
    background:linear-gradient(180deg,#EAF7FF 0%,#FFF9E7 52%,#E8F4D8 100%)!important;
  }
  .app-shell .home-page .home-room .home-rays,
  .app-shell .home-page .home-room .home-sunbeam-drift,
  .app-shell .home-page .home-room .home-distant-city,
  .app-shell .home-page .home-room .home-word-scroll,
  .app-shell .home-page .home-room .home-signature-vessel,
  .app-shell .home-page .home-room .home-gem,
  .app-shell .home-page .home-room .home-path-gem,
  .app-shell .home-page .home-room .home-heavenly-effect.eff_glory_rays,
  .app-shell .home-page .home-room .home-heavenly-effect.eff_glory_rays::before,
  .app-shell .home-page .home-room .home-heavenly-effect.eff_glory_rays::after{
    display:none!important;
    opacity:0!important;
    visibility:hidden!important;
  }
  .app-shell .home-page .home-room .home-sky{
    inset:0 0 40%!important;
    background:
      radial-gradient(circle at 50% 18%,rgba(255,255,255,.82),transparent 24%),
      radial-gradient(circle at 20% 30%,rgba(219,234,254,.56),transparent 32%),
      radial-gradient(circle at 82% 34%,rgba(255,245,207,.62),transparent 31%),
      linear-gradient(180deg,#DDF2FF 0%,#F8FCFF 52%,#FFF8DF 100%)!important;
  }
  .app-shell .home-page .home-room .home-sky::after{
    content:""!important;
    position:absolute!important;
    left:8%!important;
    right:8%!important;
    bottom:-26px!important;
    height:92px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse at 50% 50%,rgba(255,255,255,.74),rgba(255,248,223,.26) 48%,transparent 74%)!important;
    pointer-events:none!important;
    z-index:1!important;
  }
  .app-shell .home-page .home-room .home-cloud{
    background:transparent!important;
    border-radius:0!important;
    box-shadow:none!important;
    filter:drop-shadow(0 9px 14px rgba(92,124,148,.08))!important;
    overflow:visible!important;
    opacity:.78!important;
    z-index:3!important;
  }
  .app-shell .home-page .home-room .home-cloud::before,
  .app-shell .home-page .home-room .home-cloud::after{
    content:""!important;
    position:absolute!important;
    pointer-events:none!important;
  }
  .app-shell .home-page .home-room .home-cloud::before{
    inset:0!important;
    border-radius:999px!important;
    background:
      radial-gradient(circle at 15% 68%,rgba(255,255,255,.90) 0 18px,transparent 19px),
      radial-gradient(circle at 32% 48%,rgba(255,255,255,.98) 0 28px,transparent 29px),
      radial-gradient(circle at 53% 40%,rgba(255,255,255,.94) 0 32px,transparent 33px),
      radial-gradient(circle at 72% 54%,rgba(255,255,255,.90) 0 26px,transparent 27px),
      radial-gradient(circle at 90% 70%,rgba(255,255,255,.76) 0 16px,transparent 17px),
      linear-gradient(180deg,rgba(255,255,255,.76),rgba(239,249,255,.42))!important;
    box-shadow:inset 0 4px 6px rgba(255,255,255,.70),0 10px 20px rgba(120,145,164,.08)!important;
  }
  .app-shell .home-page .home-room .home-cloud::after{
    left:14%!important;
    right:16%!important;
    bottom:5px!important;
    height:18px!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,transparent,rgba(232,243,250,.44),transparent)!important;
    opacity:.62!important;
    filter:blur(2px)!important;
  }
  .app-shell .home-page .home-room .home-cloud.c1{width:184px!important;height:66px!important;left:-46px!important;top:60px!important;}
  .app-shell .home-page .home-room .home-cloud.c2{width:230px!important;height:76px!important;right:-66px!important;top:80px!important;opacity:.72!important;}
  .app-shell .home-page .home-room .home-cloud.c3{width:162px!important;height:58px!important;left:34%!important;top:30px!important;opacity:.52!important;}
  .app-shell .home-page .home-room.home-time-evening .home-cloud::before{filter:sepia(.08) saturate(.94) brightness(1.03)!important;}
  .app-shell .home-page .home-room.home-time-night .home-cloud::before{opacity:.62!important;filter:saturate(.72) brightness(.82)!important;}

  .app-shell .home-page .home-room .home-floor{
    height:45%!important;
    background:
      radial-gradient(circle at 16% 78%,rgba(255,255,255,.34) 0 5px,transparent 6px),
      radial-gradient(circle at 84% 82%,rgba(255,255,255,.30) 0 5px,transparent 6px),
      linear-gradient(180deg,rgba(255,253,246,.86) 0%,rgba(223,244,204,.94) 42%,rgba(166,204,120,.72) 100%)!important;
    border-top:1px solid rgba(117,148,89,.16)!important;
  }
  .app-shell .home-page .home-room .home-path{
    top:53%!important;
    height:48%!important;
    width:min(38%,154px)!important;
    opacity:.76!important;
    z-index:5!important;
    border:0!important;
    background:linear-gradient(180deg,rgba(255,255,255,.78),rgba(244,216,132,.52) 52%,rgba(185,137,45,.34))!important;
    box-shadow:inset 0 0 18px rgba(255,255,255,.50),0 10px 16px rgba(82,55,15,.05)!important;
  }
  .app-shell .home-page .home-room .home-path::after{opacity:.18!important;}
  .app-shell .home-page .home-room .home-midground-glow{
    left:16%!important;
    right:16%!important;
    bottom:32%!important;
    height:82px!important;
    opacity:.36!important;
    background:radial-gradient(ellipse at 50% 55%,rgba(255,255,255,.66),rgba(255,248,223,.20) 45%,transparent 76%)!important;
    filter:blur(.2px)!important;
    z-index:4!important;
  }

  .app-shell .home-page .home-room .home-cardboard,
  .app-shell .home-page .home-room .home-little-shelter,
  .app-shell .home-page .home-room .home-house,
  .app-shell .home-page .home-room .home-mansion,
  .app-shell .home-page .home-room .home-palace{
    z-index:28!important;
    filter:drop-shadow(0 22px 22px rgba(74,48,12,.20)) drop-shadow(0 2px 0 rgba(255,255,255,.36))!important;
  }
  .app-shell .home-page .home-room .home-cardboard::before,
  .app-shell .home-page .home-room .home-little-shelter::before,
  .app-shell .home-page .home-room .home-house::before,
  .app-shell .home-page .home-room .home-mansion::before,
  .app-shell .home-page .home-room .home-palace::before{display:none!important;content:none!important;}
  .app-shell .home-page .home-room .home-cardboard::after,
  .app-shell .home-page .home-room .home-little-shelter::after,
  .app-shell .home-page .home-room .home-house::after,
  .app-shell .home-page .home-room .home-mansion::after,
  .app-shell .home-page .home-room .home-palace::after{
    content:""!important;
    position:absolute!important;
    left:10%!important;
    right:10%!important;
    bottom:-12px!important;
    height:18px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse,rgba(74,48,12,.18),rgba(74,48,12,0) 72%)!important;
    z-index:-1!important;
    pointer-events:none!important;
  }
  .app-shell .home-page .home-room .home-cardboard-roof,
  .app-shell .home-page .home-room .home-little-shelter-roof,
  .app-shell .home-page .home-room .home-house-roof,
  .app-shell .home-page .home-room .home-mansion-roof,
  .app-shell .home-page .home-room .home-palace-roof{
    border:1px solid rgba(126,83,15,.24)!important;
    background:
      linear-gradient(90deg,rgba(255,255,255,.26),transparent 22%,transparent 78%,rgba(77,48,9,.12)),
      repeating-linear-gradient(135deg,rgba(95,59,8,.13) 0 3px,transparent 3px 14px),
      linear-gradient(180deg,#FFF1BA 0%,#D9A035 58%,#8F5C12 100%)!important;
    box-shadow:inset 0 10px 14px rgba(255,255,255,.58),inset 0 -10px 12px rgba(77,48,9,.14),0 8px 13px rgba(74,48,12,.13)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{
    border:1.5px solid rgba(126,83,15,.26)!important;
    background:
      radial-gradient(circle at 24% 14%,rgba(255,255,255,.72),transparent 23%),
      linear-gradient(90deg,rgba(77,48,9,.07),transparent 16%,transparent 84%,rgba(77,48,9,.09)),
      linear-gradient(180deg,#FFFDF8 0%,#FFF0CB 52%,#D6A143 100%)!important;
    box-shadow:inset 0 12px 18px rgba(255,255,255,.72),inset 0 -14px 18px rgba(77,48,9,.13),0 12px 18px rgba(74,48,12,.10)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body{background:linear-gradient(180deg,#F1D193 0%,#BD813B 100%)!important;}
  .app-shell .home-page .home-room .home-cardboard-body::before,
  .app-shell .home-page .home-room .home-little-shelter-body::before,
  .app-shell .home-page .home-room .home-house-body::before,
  .app-shell .home-page .home-room .home-mansion-body::before,
  .app-shell .home-page .home-room .home-palace-body::before{
    content:""!important;
    position:absolute!important;
    left:13%!important;
    right:13%!important;
    top:15px!important;
    height:1px!important;
    border-radius:999px!important;
    background:rgba(126,83,15,.16)!important;
    box-shadow:0 18px 0 rgba(126,83,15,.10),0 36px 0 rgba(255,255,255,.22)!important;
  }
  .app-shell .home-page .home-room .home-window-dot,
  .app-shell .home-page .home-room .home-little-shelter-window,
  .app-shell .home-page .home-room .home-mansion-window{
    background:
      linear-gradient(90deg,transparent 46%,rgba(126,83,15,.20) 47% 53%,transparent 54%),
      linear-gradient(180deg,transparent 48%,rgba(126,83,15,.18) 49% 53%,transparent 54%),
      radial-gradient(circle at 48% 20%,#FFF 0,#FFF8DF 34%,#F2C85E 70%,#C99012 100%)!important;
    border:1.5px solid rgba(126,83,15,.22)!important;
    box-shadow:0 0 14px rgba(255,248,223,.70),inset 0 3px 5px rgba(255,255,255,.82),inset 0 -5px 8px rgba(138,90,8,.14)!important;
    z-index:34!important;
  }
  .app-shell .home-page .home-room .home-cardboard-door,
  .app-shell .home-page .home-room .home-little-shelter-door,
  .app-shell .home-page .home-room .home-house-door,
  .app-shell .home-page .home-room .home-mansion-door,
  .app-shell .home-page .home-room .home-door{
    background:
      radial-gradient(circle at 50% 16%,rgba(255,255,255,.94) 0 8%,transparent 9%),
      linear-gradient(90deg,rgba(255,255,255,.24),transparent 36%,rgba(82,55,15,.12)),
      linear-gradient(180deg,#FFF1BA 0%,#D19028 55%,#74470D 100%)!important;
    border:1.5px solid rgba(76,52,18,.34)!important;
    box-shadow:0 0 16px rgba(255,248,223,.62),inset 0 6px 8px rgba(255,255,255,.46),inset 0 -9px 10px rgba(82,55,15,.22)!important;
    z-index:36!important;
  }
  .app-shell .home-page .home-room .home-gate,
  .app-shell .home-page .home-room .home-lantern{opacity:.34!important;z-index:8!important;}
  .app-shell .home-page .home-room .home-slot:not(.home-spirit-slot){z-index:38!important;}
  .app-shell .home-page .home-room .home-spirit-slot{z-index:58!important;}
  @media(max-width:560px){
    .app-shell .home-page .home-room .home-cloud.c1{width:150px!important;height:54px!important;left:-48px!important;top:62px!important;}
    .app-shell .home-page .home-room .home-cloud.c2{width:188px!important;height:62px!important;right:-70px!important;top:84px!important;}
    .app-shell .home-page .home-room .home-cloud.c3{width:132px!important;height:48px!important;left:34%!important;top:38px!important;}
  }


  /* Heavenly Home realism pass: architectural details without changing growth logic. */
  .app-shell .home-page .home-room .home-cardboard,
  .app-shell .home-page .home-room .home-little-shelter,
  .app-shell .home-page .home-room .home-house,
  .app-shell .home-page .home-room .home-mansion,
  .app-shell .home-page .home-room .home-palace{
    transform:translateX(-50%) translate3d(calc(var(--parallax-x,0) * -2px),calc(var(--parallax-y,0) * -1px),0)!important;
    filter:drop-shadow(0 24px 22px rgba(64,43,14,.22)) drop-shadow(0 5px 3px rgba(255,255,255,.20))!important;
  }
  .app-shell .home-page .home-room .home-cardboard::before,
  .app-shell .home-page .home-room .home-little-shelter::before,
  .app-shell .home-page .home-room .home-house::before,
  .app-shell .home-page .home-room .home-mansion::before{
    display:block!important;
    content:""!important;
    position:absolute!important;
    right:23%!important;
    top:17%!important;
    width:15px!important;
    height:30px!important;
    border-radius:4px 4px 2px 2px!important;
    background:linear-gradient(90deg,#8c5a18,#f3c968 42%,#b8781d 100%)!important;
    border:1px solid rgba(78,49,14,.22)!important;
    box-shadow:inset 3px 0 5px rgba(255,255,255,.38),0 3px 6px rgba(67,42,14,.12)!important;
    z-index:1!important;
  }
  .app-shell .home-page .home-room .home-cardboard::before{display:none!important;}
  .app-shell .home-page .home-room .home-little-shelter::before{right:25%!important;top:12%!important;width:13px!important;height:25px!important;}
  .app-shell .home-page .home-room .home-mansion::before{right:20%!important;top:10%!important;width:16px!important;height:34px!important;}

  .app-shell .home-page .home-room .home-cardboard-roof,
  .app-shell .home-page .home-room .home-little-shelter-roof,
  .app-shell .home-page .home-room .home-house-roof,
  .app-shell .home-page .home-room .home-mansion-roof,
  .app-shell .home-page .home-room .home-palace-roof{
    overflow:visible!important;
    filter:drop-shadow(0 9px 9px rgba(74,48,12,.14))!important;
    background:
      linear-gradient(110deg,rgba(255,255,255,.30),transparent 22%,transparent 68%,rgba(64,37,8,.16)),
      repeating-linear-gradient(115deg,rgba(95,59,8,.16) 0 2px,transparent 2px 13px),
      linear-gradient(180deg,#FFF4C9 0%,#E3AE42 45%,#A56A18 78%,#6F4210 100%)!important;
    box-shadow:
      inset 0 12px 16px rgba(255,255,255,.56),
      inset 0 -13px 14px rgba(74,43,8,.20),
      0 2px 0 rgba(255,255,255,.42)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-roof::before,
  .app-shell .home-page .home-room .home-little-shelter-roof::before,
  .app-shell .home-page .home-room .home-house-roof::before,
  .app-shell .home-page .home-room .home-mansion-roof::before,
  .app-shell .home-page .home-room .home-palace-roof::before{
    content:""!important;
    position:absolute!important;
    left:8%!important;
    right:8%!important;
    bottom:2px!important;
    height:7px!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,rgba(116,72,14,.20),rgba(255,238,168,.78) 44%,rgba(116,72,14,.26))!important;
    box-shadow:0 4px 7px rgba(74,48,12,.12)!important;
    pointer-events:none!important;
  }
  .app-shell .home-page .home-room .home-cardboard-roof::after,
  .app-shell .home-page .home-room .home-little-shelter-roof::after,
  .app-shell .home-page .home-room .home-house-roof::after,
  .app-shell .home-page .home-room .home-mansion-roof::after,
  .app-shell .home-page .home-room .home-palace-roof::after{
    content:""!important;
    position:absolute!important;
    left:18%!important;
    right:18%!important;
    top:18%!important;
    height:18%!important;
    border-radius:50%!important;
    background:linear-gradient(90deg,transparent,rgba(255,255,255,.34),transparent)!important;
    filter:blur(.4px)!important;
    opacity:.72!important;
    pointer-events:none!important;
  }

  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{
    background:
      radial-gradient(circle at 24% 16%,rgba(255,255,255,.70),transparent 22%),
      linear-gradient(90deg,rgba(72,47,16,.08),transparent 12%,transparent 86%,rgba(72,47,16,.11)),
      repeating-linear-gradient(180deg,rgba(122,82,22,.075) 0 1px,transparent 1px 15px),
      linear-gradient(180deg,#FFFDF9 0%,#FFF1CE 45%,#E1B04E 100%)!important;
    border:1.5px solid rgba(93,59,18,.28)!important;
    box-shadow:
      inset 0 13px 20px rgba(255,255,255,.72),
      inset 0 -16px 18px rgba(83,51,13,.15),
      inset 8px 0 12px rgba(255,255,255,.18),
      inset -9px 0 14px rgba(77,48,9,.10),
      0 13px 18px rgba(64,43,14,.12)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body{
    background:
      repeating-linear-gradient(0deg,rgba(95,59,24,.12) 0 1px,transparent 1px 16px),
      linear-gradient(180deg,#F3D59C 0%,#C98B46 100%)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body::after,
  .app-shell .home-page .home-room .home-little-shelter-body::after,
  .app-shell .home-page .home-room .home-house-body::after,
  .app-shell .home-page .home-room .home-mansion-body::after,
  .app-shell .home-page .home-room .home-palace-body::after{
    content:""!important;
    position:absolute!important;
    left:10%!important;
    right:10%!important;
    bottom:8px!important;
    height:9px!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,transparent,rgba(95,59,8,.18),transparent)!important;
    box-shadow:0 -22px 0 -4px rgba(255,255,255,.16),0 -44px 0 -4px rgba(116,72,14,.08)!important;
    pointer-events:none!important;
  }

  .app-shell .home-page .home-room .home-window-dot,
  .app-shell .home-page .home-room .home-little-shelter-window,
  .app-shell .home-page .home-room .home-mansion-window{
    border-radius:10px 10px 5px 5px!important;
    background:
      linear-gradient(90deg,transparent 46%,rgba(86,56,18,.30) 47% 53%,transparent 54%),
      linear-gradient(180deg,transparent 48%,rgba(86,56,18,.24) 49% 53%,transparent 54%),
      radial-gradient(circle at 42% 18%,#FFFFFF 0,#FFF9E5 28%,#F3D47A 68%,#B97818 100%)!important;
    border:1.5px solid rgba(85,54,18,.30)!important;
    box-shadow:0 0 12px rgba(255,248,223,.55),inset 0 4px 5px rgba(255,255,255,.78),inset 0 -6px 8px rgba(95,59,8,.18),0 5px 8px rgba(74,48,12,.10)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-door,
  .app-shell .home-page .home-room .home-little-shelter-door,
  .app-shell .home-page .home-room .home-house-door,
  .app-shell .home-page .home-room .home-mansion-door,
  .app-shell .home-page .home-room .home-door{
    border-radius:18px 18px 6px 6px!important;
    background:
      radial-gradient(circle at 50% 17%,rgba(255,255,255,.95) 0 7%,transparent 8%),
      linear-gradient(90deg,rgba(255,255,255,.24),transparent 36%,rgba(61,39,14,.14)),
      repeating-linear-gradient(90deg,rgba(92,57,10,.10) 0 1px,transparent 1px 9px),
      linear-gradient(180deg,#FFE9A8 0%,#C7861E 58%,#6D410E 100%)!important;
    border:1.5px solid rgba(58,38,15,.36)!important;
    box-shadow:0 0 14px rgba(255,248,223,.54),inset 0 7px 8px rgba(255,255,255,.42),inset 0 -9px 10px rgba(58,38,15,.25),0 4px 8px rgba(74,48,12,.12)!important;
  }
  .app-shell .home-page .home-room .home-house-door::before,
  .app-shell .home-page .home-room .home-little-shelter-door::before,
  .app-shell .home-page .home-room .home-mansion-door::before,
  .app-shell .home-page .home-room .home-door::before{
    content:""!important;
    position:absolute!important;
    left:50%!important;
    top:9px!important;
    bottom:7px!important;
    width:1px!important;
    background:rgba(74,43,8,.20)!important;
    box-shadow:inset 0 0 0 1px rgba(255,255,255,.18)!important;
  }
  .app-shell .home-page .home-room .home-house-door::after,
  .app-shell .home-page .home-room .home-little-shelter-door::after,
  .app-shell .home-page .home-room .home-mansion-door::after,
  .app-shell .home-page .home-room .home-door::after{
    content:""!important;
    position:absolute!important;
    right:8px!important;
    top:29px!important;
    width:4px!important;
    height:4px!important;
    border-radius:50%!important;
    background:#fff9df!important;
    box-shadow:0 0 7px rgba(255,248,223,.9),0 1px 0 rgba(58,38,15,.2)!important;
  }

  .app-shell .home-page .home-room .home-mansion-column,
  .app-shell .home-page .home-room .home-palace-column{
    background:linear-gradient(90deg,#A56A18,#FFF1C7 40%,#E0AC42 70%,#7B4A10)!important;
    border-color:rgba(78,49,14,.24)!important;
    box-shadow:inset 4px 0 6px rgba(255,255,255,.46),inset -3px 0 5px rgba(77,48,9,.15),0 5px 8px rgba(74,48,12,.10)!important;
  }
  .app-shell .home-page .home-room .home-palace-balcony,
  .app-shell .home-page .home-room .home-palace-steps{
    background:linear-gradient(180deg,#FFF9DF,#D8A43E 72%,#9A6416)!important;
    border-color:rgba(78,49,14,.22)!important;
    box-shadow:inset 0 5px 7px rgba(255,255,255,.54),0 5px 8px rgba(74,48,12,.10)!important;
  }
  .app-shell .home-page .home-room.home-stage-0 .home-cardboard{top:42%!important;width:146px!important;height:88px!important;}
  .app-shell .home-page .home-room.home-stage-1 .home-little-shelter{top:36%!important;width:184px!important;height:120px!important;}
  .app-shell .home-page .home-room.home-stage-2 .home-house{top:30%!important;width:204px!important;height:142px!important;}
  .app-shell .home-page .home-room.home-stage-3 .home-mansion{top:25%!important;width:236px!important;height:164px!important;}
  .app-shell .home-page .home-room.home-stage-4 .home-palace{top:20%!important;width:244px!important;height:174px!important;}
  .app-shell .home-page .home-room.home-stage-5 .home-palace{top:18%!important;width:262px!important;height:184px!important;}
  .app-shell .home-page .home-room.home-stage-6 .home-palace{top:16%!important;width:284px!important;height:196px!important;}
  @media(max-width:560px){
    .app-shell .home-page .home-room.home-stage-4 .home-palace{top:21%!important;width:230px!important;}
    .app-shell .home-page .home-room.home-stage-5 .home-palace{top:20%!important;width:244px!important;}
    .app-shell .home-page .home-room.home-stage-6 .home-palace{top:19%!important;width:258px!important;}
  }


  /* Heavenly Home storybook detail pass: foundations, trim, and real architectural cues. */
  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{
    overflow:visible!important;
  }
  .app-shell .home-page .home-room .home-little-shelter-body::before,
  .app-shell .home-page .home-room .home-house-body::before,
  .app-shell .home-page .home-room .home-mansion-body::before,
  .app-shell .home-page .home-room .home-palace-body::before{
    content:""!important;
    position:absolute!important;
    left:9%!important;
    right:9%!important;
    top:12px!important;
    height:1px!important;
    border-radius:999px!important;
    background:linear-gradient(90deg,transparent,rgba(107,68,17,.22),transparent)!important;
    box-shadow:
      0 19px 0 rgba(107,68,17,.10),
      0 38px 0 rgba(255,255,255,.20),
      0 57px 0 rgba(107,68,17,.08)!important;
    pointer-events:none!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body::after,
  .app-shell .home-page .home-room .home-little-shelter-body::after,
  .app-shell .home-page .home-room .home-house-body::after,
  .app-shell .home-page .home-room .home-mansion-body::after,
  .app-shell .home-page .home-room .home-palace-body::after{
    content:""!important;
    position:absolute!important;
    left:-4px!important;
    right:-4px!important;
    bottom:-8px!important;
    height:15px!important;
    border-radius:6px 6px 10px 10px!important;
    background:
      repeating-linear-gradient(90deg,rgba(92,65,28,.16) 0 1px,transparent 1px 16px),
      linear-gradient(180deg,#FFF6D8,#D7A74B 76%,#9A6416)!important;
    border:1px solid rgba(84,57,20,.22)!important;
    box-shadow:inset 0 4px 5px rgba(255,255,255,.46),0 8px 10px rgba(64,43,14,.14)!important;
    pointer-events:none!important;
    z-index:5!important;
  }

  .app-shell .home-page .home-room .home-window-dot::after,
  .app-shell .home-page .home-room .home-little-shelter-window::after,
  .app-shell .home-page .home-room .home-mansion-window::after{
    content:""!important;
    position:absolute!important;
    left:-4px!important;
    right:-4px!important;
    bottom:-6px!important;
    height:5px!important;
    border-radius:999px!important;
    background:linear-gradient(180deg,#FFF6D5,#C99012)!important;
    border:1px solid rgba(89,58,19,.18)!important;
    box-shadow:0 3px 5px rgba(64,43,14,.10)!important;
  }
  .app-shell .home-page .home-room .home-window-dot::before,
  .app-shell .home-page .home-room .home-little-shelter-window::before,
  .app-shell .home-page .home-room .home-mansion-window::before{
    content:""!important;
    position:absolute!important;
    inset:4px!important;
    border-radius:7px 7px 3px 3px!important;
    border:1px solid rgba(255,255,255,.34)!important;
    box-shadow:inset 0 0 0 1px rgba(84,57,20,.10)!important;
    pointer-events:none!important;
  }

  .app-shell .home-page .home-room .home-little-shelter::before,
  .app-shell .home-page .home-room .home-house::before,
  .app-shell .home-page .home-room .home-mansion::before{
    box-shadow:inset 3px 0 5px rgba(255,255,255,.38),0 3px 6px rgba(67,42,14,.12),0 -4px 0 rgba(111,69,15,.22)!important;
  }
  .app-shell .home-page .home-room .home-little-shelter::after,
  .app-shell .home-page .home-room .home-house::after,
  .app-shell .home-page .home-room .home-mansion::after,
  .app-shell .home-page .home-room .home-palace::after{
    left:6%!important;
    right:6%!important;
    bottom:-17px!important;
    height:24px!important;
    background:radial-gradient(ellipse,rgba(55,39,18,.20),rgba(55,39,18,.08) 42%,rgba(55,39,18,0) 74%)!important;
    filter:blur(.4px)!important;
  }

  .app-shell .home-page .home-room .home-little-shelter-roof,
  .app-shell .home-page .home-room .home-house-roof{
    clip-path:polygon(50% 0,102% 91%,94% 100%,6% 100%,-2% 91%)!important;
  }
  .app-shell .home-page .home-room .home-little-shelter-roof{
    left:0!important;
    right:0!important;
  }
  .app-shell .home-page .home-room .home-house-roof{
    left:2px!important;
    right:2px!important;
  }
  .app-shell .home-page .home-room .home-mansion-roof{
    clip-path:polygon(4% 100%,12% 64%,20% 100%,30% 52%,40% 100%,50% 6%,60% 100%,70% 52%,80% 100%,88% 64%,96% 100%)!important;
  }
  .app-shell .home-page .home-room .home-palace-roof{
    clip-path:polygon(0 100%,7% 50%,16% 100%,27% 40%,38% 100%,50% 0,62% 100%,73% 40%,84% 100%,93% 50%,100% 100%)!important;
  }

  .app-shell .home-page .home-room .home-cardboard-door{width:28px!important;height:38px!important;left:50%!important;transform:translateX(-50%)!important;}
  .app-shell .home-page .home-room .home-little-shelter-door{width:38px!important;height:56px!important;}
  .app-shell .home-page .home-room .home-house-door{width:40px!important;height:60px!important;left:50%!important;transform:translateX(-50%)!important;}
  .app-shell .home-page .home-room .home-mansion-door{width:46px!important;height:68px!important;}
  .app-shell .home-page .home-room .home-door{width:46px!important;height:68px!important;}

  .app-shell .home-page .home-room.home-stage-1 .home-little-shelter-window{top:76px!important;width:21px!important;height:27px!important;}
  .app-shell .home-page .home-room.home-stage-1 .home-little-shelter-window.left{left:39px!important;}
  .app-shell .home-page .home-room.home-stage-1 .home-little-shelter-window.right{right:39px!important;}
  .app-shell .home-page .home-room.home-stage-2 .home-window-dot{top:82px!important;width:24px!important;height:32px!important;}
  .app-shell .home-page .home-room.home-stage-2 .home-window-dot.left{left:48px!important;}
  .app-shell .home-page .home-room.home-stage-2 .home-window-dot.right{right:48px!important;}
  .app-shell .home-page .home-room.home-stage-3 .home-mansion-window{top:78px!important;width:28px!important;height:36px!important;}
  .app-shell .home-page .home-room.home-stage-3 .home-mansion-window.left{left:54px!important;}
  .app-shell .home-page .home-room.home-stage-3 .home-mansion-window.right{right:54px!important;}

  .app-shell .home-page .home-room .home-tower{
    background:
      linear-gradient(90deg,rgba(74,43,8,.10),transparent 25%,transparent 74%,rgba(74,43,8,.13)),
      repeating-linear-gradient(180deg,rgba(121,79,18,.08) 0 1px,transparent 1px 16px),
      linear-gradient(180deg,#FFFDF8,#FFF0C8 52%,#D9A13C 100%)!important;
    border:1.5px solid rgba(91,57,16,.28)!important;
    box-shadow:inset 0 12px 18px rgba(255,255,255,.62),inset 0 -14px 16px rgba(74,43,8,.12),0 12px 16px rgba(64,43,14,.12)!important;
    overflow:visible!important;
  }
  .app-shell .home-page .home-room .home-tower::before{
    width:38px!important;
    height:42px!important;
    top:-39px!important;
    background:
      linear-gradient(110deg,rgba(255,255,255,.28),transparent 42%),
      linear-gradient(180deg,#FFF4C9,#DFA83C 68%,#8B5513)!important;
    border-color:rgba(91,57,16,.28)!important;
    filter:drop-shadow(0 5px 7px rgba(64,43,14,.12))!important;
  }
  .app-shell .home-page .home-room .home-tower::after{
    content:""!important;
    position:absolute!important;
    left:50%!important;
    top:34px!important;
    width:17px!important;
    height:56px!important;
    transform:translateX(-50%)!important;
    border-radius:999px 999px 6px 6px!important;
    background:
      linear-gradient(180deg,rgba(255,255,255,.82),rgba(255,244,203,.72) 36%,rgba(201,144,18,.34)),
      repeating-linear-gradient(180deg,transparent 0 12px,rgba(92,57,10,.18) 12px 13px)!important;
    border:1px solid rgba(91,57,16,.20)!important;
    box-shadow:0 0 12px rgba(255,248,223,.46),inset 0 4px 5px rgba(255,255,255,.68)!important;
  }

  .app-shell .home-page .home-room.home-stage-4 .home-palace-body::after,
  .app-shell .home-page .home-room.home-stage-5 .home-palace-body::after,
  .app-shell .home-page .home-room.home-stage-6 .home-palace-body::after{
    left:16%!important;
    right:16%!important;
    bottom:20px!important;
    height:44px!important;
    border-radius:14px!important;
    background:
      radial-gradient(circle at 18% 32%,rgba(255,255,255,.88) 0 5px,transparent 6px),
      radial-gradient(circle at 50% 24%,rgba(255,255,255,.94) 0 7px,transparent 8px),
      radial-gradient(circle at 82% 32%,rgba(255,255,255,.88) 0 5px,transparent 6px),
      linear-gradient(180deg,rgba(255,248,223,.56),rgba(201,144,18,.18))!important;
    border:1px solid rgba(91,57,16,.16)!important;
    box-shadow:inset 0 4px 6px rgba(255,255,255,.48),0 5px 8px rgba(64,43,14,.08)!important;
  }
  .app-shell .home-page .home-room.home-stage-4 .home-palace-steps,
  .app-shell .home-page .home-room.home-stage-5 .home-palace-steps,
  .app-shell .home-page .home-room.home-stage-6 .home-palace-steps{
    height:24px!important;
    bottom:-18px!important;
    background:
      repeating-linear-gradient(180deg,#FFF7D6 0 5px,#D8A43E 5px 7px),
      linear-gradient(180deg,#FFF7D6,#C99012)!important;
  }
  .app-shell .home-page .home-room.home-stage-5 .home-palace-star,
  .app-shell .home-page .home-room.home-stage-6 .home-palace-star{
    top:-20px!important;
    text-shadow:0 0 12px rgba(255,248,223,.9),0 2px 4px rgba(64,43,14,.12)!important;
  }

  .app-shell .home-page .home-room.home-stage-0 .home-cardboard-body::before{
    content:""!important;
    position:absolute!important;
    inset:9px 12px auto!important;
    height:1px!important;
    background:rgba(91,57,16,.18)!important;
    box-shadow:0 17px 0 rgba(91,57,16,.13),0 34px 0 rgba(255,255,255,.18)!important;
  }
  .app-shell .home-page .home-room.home-stage-0 .home-cardboard-body::after{
    bottom:-6px!important;
    height:12px!important;
  }
  @media(max-width:560px){
    .app-shell .home-page .home-room.home-stage-1 .home-little-shelter-window.left{left:36px!important;}
    .app-shell .home-page .home-room.home-stage-1 .home-little-shelter-window.right{right:36px!important;}
    .app-shell .home-page .home-room.home-stage-2 .home-window-dot.left{left:43px!important;}
    .app-shell .home-page .home-room.home-stage-2 .home-window-dot.right{right:43px!important;}
    .app-shell .home-page .home-room.home-stage-3 .home-mansion-window.left{left:48px!important;}
    .app-shell .home-page .home-room.home-stage-3 .home-mansion-window.right{right:48px!important;}
    .app-shell .home-page .home-room .home-tower::after{height:46px!important;top:34px!important;}
  }


  /* Premium Heavenly Home refinement: depth, presence, and quiet living atmosphere. */
  @keyframes homeCloudDriftSlow{
    0%,100%{transform:translate3d(0,0,0);}
    50%{transform:translate3d(10px,-2px,0);}
  }
  @keyframes homeBirdDriftSlow{
    0%{transform:translate3d(-18px,0,0) scale(.92);opacity:.10;}
    18%,72%{opacity:.32;}
    100%{transform:translate3d(42px,-4px,0) scale(.92);opacity:.08;}
  }
  @keyframes homeRayBreatheSlow{
    0%,100%{opacity:.28;transform:translate3d(-1px,0,0) rotate(.1deg);}
    50%{opacity:.42;transform:translate3d(3px,1px,0) rotate(-.15deg);}
  }
  @keyframes homeParticleLiftSlow{
    0%{transform:translate3d(0,12px,0) scale(.82);opacity:0;}
    24%,72%{opacity:.42;}
    100%{transform:translate3d(8px,-34px,0) scale(1.05);opacity:0;}
  }
  @keyframes homeSparkleDriftQuiet{
    0%,100%{opacity:.10;transform:translateY(0) scale(.86);}
    50%{opacity:.36;transform:translateY(-7px) scale(1.08);}
  }
  .app-shell .home-page.simple-home-page{
    gap:22px!important;
  }
  .app-shell .home-page.simple-home-page .home-art-card{
    padding:18px!important;
    border:1.75px solid rgba(231,207,159,.46)!important;
    border-radius:31px!important;
    background:
      radial-gradient(circle at 50% 0%,rgba(255,255,255,.88),transparent 42%),
      linear-gradient(145deg,rgba(255,253,248,.98),rgba(255,249,236,.88))!important;
    box-shadow:
      0 20px 44px rgba(88,64,30,.09),
      0 1px 0 rgba(255,255,255,.82) inset!important;
  }
  .app-shell .home-page.simple-home-page .home-art-frame{
    position:relative!important;
    padding:10px!important;
    border-radius:26px!important;
    background:
      radial-gradient(circle at 22% 14%,rgba(255,255,255,.56),transparent 30%),
      linear-gradient(145deg,rgba(255,253,246,.62),rgba(255,248,223,.30))!important;
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,.90),
      inset 0 0 0 1px rgba(255,255,255,.46)!important;
  }
  .app-shell .home-page.simple-home-page .home-art-frame::after{
    content:""!important;
    position:absolute!important;
    inset:10px!important;
    border-radius:23px!important;
    pointer-events:none!important;
    opacity:.18!important;
    background-image:
      radial-gradient(circle at 18% 20%,rgba(138,90,8,.12) 0 .55px,transparent .8px),
      radial-gradient(circle at 72% 76%,rgba(91,126,148,.10) 0 .55px,transparent .85px)!important;
    background-size:24px 24px,28px 28px!important;
    z-index:5!important;
  }
  .app-shell .home-page .home-art-card .home-room{
    min-height:410px!important;
    height:clamp(398px,96vw,452px)!important;
    border-radius:24px!important;
    border:1px solid rgba(231,207,159,.36)!important;
    background:
      radial-gradient(circle at 50% 12%,rgba(255,255,255,.72),transparent 28%),
      linear-gradient(180deg,#E8F5FB 0%,#FFF8E3 55%,#E6F0D6 100%)!important;
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,.82),
      inset 0 -42px 52px rgba(111,92,38,.08),
      0 14px 28px rgba(92,67,30,.08)!important;
  }
  .app-shell .home-page .home-room .home-sky{
    filter:saturate(.88) brightness(1.02)!important;
  }
  .app-shell .home-page .home-room .home-sky::before{
    content:""!important;
    position:absolute!important;
    inset:-10% -12% 18% -12%!important;
    pointer-events:none!important;
    background:
      radial-gradient(circle at 50% 5%,rgba(255,255,255,.68),transparent 19%),
      linear-gradient(112deg,transparent 0 37%,rgba(255,255,255,.34) 43%,transparent 50%),
      linear-gradient(78deg,transparent 0 48%,rgba(255,230,156,.18) 54%,transparent 64%)!important;
    filter:blur(.2px)!important;
    animation:homeRayBreatheSlow 18s ease-in-out infinite!important;
    z-index:2!important;
  }
  .app-shell .home-page .home-room .home-cloud{
    animation:homeCloudDriftSlow 34s ease-in-out infinite!important;
    filter:drop-shadow(0 10px 15px rgba(92,124,148,.07)) saturate(.92)!important;
    opacity:.66!important;
  }
  .app-shell .home-page .home-room .home-cloud.c2{animation-duration:42s!important;animation-delay:-12s!important;opacity:.60!important;}
  .app-shell .home-page .home-room .home-cloud.c3{animation-duration:38s!important;animation-delay:-18s!important;opacity:.48!important;}
  .app-shell .home-page .home-room .home-sky-bird{
    animation:homeBirdDriftSlow 24s ease-in-out infinite!important;
    opacity:.28!important;
  }
  .app-shell .home-page .home-room .home-sky-bird.b2{
    animation-duration:31s!important;
    animation-delay:-13s!important;
    opacity:.20!important;
  }
  .app-shell .home-page .home-room .home-floor{
    filter:saturate(.82) brightness(1.02)!important;
    background:
      radial-gradient(ellipse at 50% 42%,rgba(106,81,35,.13),transparent 32%),
      radial-gradient(circle at 19% 75%,rgba(255,255,255,.28) 0 4px,transparent 5px),
      radial-gradient(circle at 82% 80%,rgba(255,255,255,.24) 0 4px,transparent 5px),
      linear-gradient(180deg,rgba(255,253,246,.90) 0%,rgba(225,242,205,.92) 44%,rgba(152,191,105,.60) 100%)!important;
  }
  .app-shell .home-page .home-room .home-midground-glow{
    bottom:28%!important;
    height:112px!important;
    opacity:.42!important;
    background:
      radial-gradient(ellipse at 50% 58%,rgba(255,255,255,.56),rgba(255,237,171,.20) 46%,transparent 73%)!important;
    filter:blur(1px)!important;
  }
  .app-shell .home-page .home-room .home-path{
    width:min(34%,144px)!important;
    opacity:.64!important;
    filter:saturate(.86)!important;
  }
  .app-shell .home-page .home-room .home-flowerbed{
    width:25%!important;
    height:25%!important;
    opacity:.72!important;
    filter:saturate(.78) brightness(1.02)!important;
    bottom:2.5%!important;
  }
  .app-shell .home-page .home-room .home-flowerbed.left{left:-7%!important;}
  .app-shell .home-page .home-room .home-flowerbed.right{right:-7%!important;}
  .app-shell .home-page .home-room .home-cardboard,
  .app-shell .home-page .home-room .home-little-shelter,
  .app-shell .home-page .home-room .home-house,
  .app-shell .home-page .home-room .home-mansion,
  .app-shell .home-page .home-room .home-palace{
    transform:translateX(-50%) translate3d(calc(var(--parallax-x,0) * -2px),calc(var(--parallax-y,0) * -1px),0) scale(1.15)!important;
    transform-origin:50% 100%!important;
    filter:
      drop-shadow(0 30px 24px rgba(55,39,18,.23))
      drop-shadow(0 8px 8px rgba(107,68,17,.10))
      drop-shadow(0 0 13px rgba(255,240,190,.28))!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{
    filter:saturate(1.04) brightness(1.03)!important;
  }
  .app-shell .home-page .home-room .home-window-dot,
  .app-shell .home-page .home-room .home-little-shelter-window,
  .app-shell .home-page .home-room .home-mansion-window{
    box-shadow:
      0 0 18px rgba(255,230,156,.74),
      0 0 7px rgba(255,255,255,.52),
      inset 0 4px 5px rgba(255,255,255,.80),
      inset 0 -6px 8px rgba(95,59,8,.16)!important;
  }
  .app-shell .home-page .home-room .home-spirit-slot{
    filter:brightness(1.045) contrast(1.035) drop-shadow(0 0 15px rgba(255,248,223,.42))!important;
  }
  .app-shell .home-page .home-room .home-spirit-ground-shadow{
    opacity:.40!important;
    height:17px!important;
    filter:blur(3px)!important;
    background:radial-gradient(ellipse,rgba(54,40,19,.24),rgba(54,40,19,0) 72%)!important;
  }
  .app-shell .home-page .home-room .home-spirit-life{
    animation:homeSparkleDriftQuiet 4.8s ease-in-out infinite!important;
    color:#E9B949!important;
    text-shadow:0 0 9px rgba(255,248,223,.86)!important;
  }
  .app-shell .home-page .home-room .home-spirit-life.sparkle-two{animation-delay:-2.2s!important;}
  .app-shell .home-page .home-room .home-ambient-particles .particle{
    display:block!important;
    width:4px!important;
    height:4px!important;
    border-radius:50%!important;
    background:radial-gradient(circle,#FFFDF8 0 28%,#E9B949 34% 62%,transparent 66%)!important;
    box-shadow:0 0 10px rgba(255,243,196,.66)!important;
    opacity:.34!important;
    animation:homeParticleLiftSlow 8.5s ease-in-out infinite!important;
  }
  .app-shell .home-page .home-room .home-ambient-particles .p1{left:18%!important;top:61%!important;animation-delay:-1s!important;}
  .app-shell .home-page .home-room .home-ambient-particles .p2{left:71%!important;top:55%!important;animation-delay:-4.4s!important;}
  .app-shell .home-page .home-room .home-ambient-particles .p3{left:39%!important;top:38%!important;animation-delay:-6s!important;}
  .app-shell .home-page .home-room .home-ambient-particles .p4{left:82%!important;top:68%!important;animation-delay:-2.7s!important;}
  .app-shell .home-page .home-room .home-ambient-particles .p5{left:12%!important;top:43%!important;animation-delay:-7.1s!important;}
  .app-shell .home-page .home-room .home-foreground-grass{
    opacity:.48!important;
    filter:saturate(.82)!important;
  }
  .app-shell .home-page .home-room .home-foreground-grass::before,
  .app-shell .home-page .home-room .home-foreground-grass::after{
    content:""!important;
    position:absolute!important;
    bottom:18px!important;
    width:92px!important;
    height:34px!important;
    border-radius:50%!important;
    opacity:.46!important;
    background:
      radial-gradient(circle at 18% 70%,#FFFDF8 0 3px,transparent 4px),
      radial-gradient(circle at 36% 58%,#F7B7CB 0 3px,transparent 4px),
      radial-gradient(circle at 58% 72%,#EAF7FF 0 3px,transparent 4px),
      linear-gradient(180deg,rgba(151,190,99,.42),rgba(95,139,64,.28))!important;
  }
  .app-shell .home-page .home-room .home-foreground-grass::before{left:8%!important;}
  .app-shell .home-page .home-room .home-foreground-grass::after{right:8%!important;}
  .app-shell .home-page.simple-home-page .simple-home-blessing{
    padding:24px!important;
    border-radius:28px!important;
    background:
      radial-gradient(circle at 90% 10%,rgba(255,243,196,.34),transparent 28%),
      linear-gradient(145deg,rgba(255,253,248,.98),rgba(255,250,238,.90))!important;
    border:1px solid rgba(231,207,159,.32)!important;
    box-shadow:0 18px 34px rgba(88,64,30,.075),inset 0 1px 0 rgba(255,255,255,.82)!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing::before{
    opacity:.09!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing .home-blessing-head{
    margin-bottom:14px!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing h3{
    margin-top:8px!important;
    margin-bottom:10px!important;
    font-size:clamp(20px,5vw,26px)!important;
    line-height:1.18!important;
    font-weight:500!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing p{
    font-size:13px!important;
    line-height:1.62!important;
    max-width:34ch!important;
  }
  @media(max-width:560px){
    .app-shell .home-page.simple-home-page .home-art-card{padding:14px!important;border-radius:28px!important;}
    .app-shell .home-page.simple-home-page .home-art-frame{padding:8px!important;}
    .app-shell .home-page .home-art-card .home-room{min-height:390px!important;height:clamp(384px,103vw,430px)!important;}
    .app-shell .home-page .home-room .home-cardboard,
    .app-shell .home-page .home-room .home-little-shelter,
    .app-shell .home-page .home-room .home-house,
    .app-shell .home-page .home-room .home-mansion,
    .app-shell .home-page .home-room .home-palace{
      transform:translateX(-50%) translate3d(calc(var(--parallax-x,0) * -1.5px),calc(var(--parallax-y,0) * -1px),0) scale(1.12)!important;
    }
    .app-shell .home-page .home-room .home-flowerbed{width:23%!important;height:23%!important;}
    .app-shell .home-page.simple-home-page .simple-home-blessing{padding:22px!important;}
  }
  @media(prefers-reduced-motion:reduce){
    .app-shell .home-page .home-room .home-cloud,
    .app-shell .home-page .home-room .home-sky::before,
    .app-shell .home-page .home-room .home-sky-bird,
    .app-shell .home-page .home-room .home-ambient-particles .particle,
    .app-shell .home-page .home-room .home-spirit-life{
      animation:none!important;
    }
  }


  /* Home house layer fix: roof in front, no stray chimney/gold stick. */
  .app-shell .home-page .home-room .home-cardboard::before,
  .app-shell .home-page .home-room .home-little-shelter::before,
  .app-shell .home-page .home-room .home-house::before,
  .app-shell .home-page .home-room .home-mansion::before,
  .app-shell .home-page .home-room .home-palace::before{
    display:none!important;
    content:none!important;
    width:0!important;
    height:0!important;
    opacity:0!important;
    visibility:hidden!important;
    background:none!important;
    box-shadow:none!important;
  }
  .app-shell .home-page .home-room .home-cardboard-roof,
  .app-shell .home-page .home-room .home-little-shelter-roof,
  .app-shell .home-page .home-room .home-house-roof,
  .app-shell .home-page .home-room .home-mansion-roof,
  .app-shell .home-page .home-room .home-palace-roof{
    z-index:46!important;
    pointer-events:none!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{
    z-index:32!important;
  }
  .app-shell .home-page .home-room .home-cardboard-door,
  .app-shell .home-page .home-room .home-little-shelter-door,
  .app-shell .home-page .home-room .home-house-door,
  .app-shell .home-page .home-room .home-mansion-door,
  .app-shell .home-page .home-room .home-door,
  .app-shell .home-page .home-room .home-window-dot,
  .app-shell .home-page .home-room .home-little-shelter-window,
  .app-shell .home-page .home-room .home-mansion-window,
  .app-shell .home-page .home-room .home-palace-balcony,
  .app-shell .home-page .home-room .home-palace-column,
  .app-shell .home-page .home-room .home-palace-steps{
    z-index:50!important;
  }
  .app-shell .home-page .home-room .home-tower{
    z-index:35!important;
  }
  .app-shell .home-page .home-room .home-tower::before,
  .app-shell .home-page .home-room .home-tower::after{
    z-index:36!important;
  }


  /* Home roof lift: keep roof clearly above the main structure. */
  .app-shell .home-page .home-room .home-cardboard-roof,
  .app-shell .home-page .home-room .home-little-shelter-roof,
  .app-shell .home-page .home-room .home-house-roof,
  .app-shell .home-page .home-room .home-mansion-roof,
  .app-shell .home-page .home-room .home-palace-roof{
    transform:translateY(-8px)!important;
    transform-origin:50% 100%!important;
  }
  .app-shell .home-page .home-room.home-stage-0 .home-cardboard-roof{
    transform:translateY(-6px)!important;
  }
  .app-shell .home-page .home-room.home-stage-4 .home-palace-roof,
  .app-shell .home-page .home-room.home-stage-5 .home-palace-roof,
  .app-shell .home-page .home-room.home-stage-6 .home-palace-roof{
    transform:translateY(-10px)!important;
  }
  @media(max-width:560px){
    .app-shell .home-page .home-room .home-cardboard-roof,
    .app-shell .home-page .home-room .home-little-shelter-roof,
    .app-shell .home-page .home-room .home-house-roof,
    .app-shell .home-page .home-room .home-mansion-roof,
    .app-shell .home-page .home-room .home-palace-roof{
      transform:translateY(-7px)!important;
    }
    .app-shell .home-page .home-room.home-stage-4 .home-palace-roof,
    .app-shell .home-page .home-room.home-stage-5 .home-palace-roof,
    .app-shell .home-page .home-room.home-stage-6 .home-palace-roof{
      transform:translateY(-9px)!important;
    }
  }


  /* Final home roof separation + dim warning polish. */
  .app-shell .home-page .home-room .home-cardboard-roof,
  .app-shell .home-page .home-room .home-little-shelter-roof,
  .app-shell .home-page .home-room .home-house-roof,
  .app-shell .home-page .home-room .home-mansion-roof,
  .app-shell .home-page .home-room .home-palace-roof{
    transform:translateY(-16px)!important;
    z-index:70!important;
    filter:drop-shadow(0 9px 9px rgba(95,64,14,.16)) drop-shadow(0 0 13px rgba(255,238,177,.42))!important;
  }
  .app-shell .home-page .home-room.home-stage-1 .home-little-shelter-roof,
  .app-shell .home-page .home-room.home-stage-2 .home-house-roof{transform:translateY(-18px)!important;}
  .app-shell .home-page .home-room.home-stage-4 .home-palace-roof,
  .app-shell .home-page .home-room.home-stage-5 .home-palace-roof,
  .app-shell .home-page .home-room.home-stage-6 .home-palace-roof{transform:translateY(-20px)!important;}
  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{transform:translateY(6px)!important;z-index:42!important;}
  .app-shell .home-page .home-neglect-top-warning{
    border:2px solid rgba(184,107,28,.58)!important;
    background:linear-gradient(135deg,rgba(255,245,221,.96),rgba(255,226,176,.78))!important;
    box-shadow:0 0 0 4px rgba(255,216,139,.20),0 14px 34px rgba(151,83,21,.18),inset 0 1px 0 rgba(255,255,255,.84)!important;
    border-radius:24px!important;
    padding:14px 16px!important;
  }
  .app-shell .home-page .home-neglect-top-warning p{color:#6f3517!important;font-weight:650!important;}
  .app-shell .home-page .home-room .home-neglect-overlay{
    box-shadow:inset 0 0 0 3px rgba(255,210,122,.42),inset 0 0 34px rgba(255,225,157,.20)!important;
  }
  .app-shell .home-page .home-room .home-neglect-message{
    border:2px solid rgba(255,216,139,.72)!important;
    background:linear-gradient(145deg,rgba(49,31,15,.76),rgba(91,54,20,.72))!important;
    box-shadow:0 0 0 4px rgba(255,216,139,.12),0 14px 34px rgba(20,10,0,.24)!important;
  }
  .app-shell .home-page .home-room .home-neglect-message p{color:#ffe7a8!important;font-weight:650!important;}

  /* Home house cleanup: remove stray rectangle trims/windows across every growth stage. */
  .app-shell .home-page .home-room .home-cardboard-body::before,
  .app-shell .home-page .home-room .home-little-shelter-body::before,
  .app-shell .home-page .home-room .home-house-body::before,
  .app-shell .home-page .home-room .home-mansion-body::before,
  .app-shell .home-page .home-room .home-palace-body::before,
  .app-shell .home-page .home-room .home-cardboard-roof::before,
  .app-shell .home-page .home-room .home-little-shelter-roof::before,
  .app-shell .home-page .home-room .home-house-roof::before,
  .app-shell .home-page .home-room .home-mansion-roof::before,
  .app-shell .home-page .home-room .home-palace-roof::before{
    content:none!important;
    display:none!important;
    width:0!important;
    height:0!important;
    opacity:0!important;
    box-shadow:none!important;
    background:none!important;
  }
  .app-shell .home-page .home-room .home-window-dot,
  .app-shell .home-page .home-room .home-little-shelter-window,
  .app-shell .home-page .home-room .home-mansion-window{
    display:none!important;
  }

  /* Instagram card icon: transparent glyph from uploaded screenshot. */
  .app-shell .testimony-premium-action-card.secondary .instagram-action-icon{
    background:linear-gradient(135deg,#D80B6A 0%,#C01670 54%,#A23AAE 100%)!important;
    border-color:rgba(255,255,255,.36)!important;
    box-shadow:0 8px 18px rgba(176,39,115,.16)!important;
  }
  .app-shell .instagram-action-icon img{
    display:block!important;
    width:25px!important;
    height:25px!important;
    object-fit:contain!important;
  }

  /* Shared Testimonies width refinement: let stories breathe across the card. */
  .app-shell .testimony-shared-page{
    width:100%!important;
  }
  .app-shell #shared-testimonies-wall.testimony-wall-today-style{
    width:100%!important;
    padding:clamp(22px,4.6vw,30px)!important;
  }
  .app-shell #shared-testimonies-wall .scroll-list{
    padding-right:2px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-card{
    width:100%!important;
    padding:clamp(22px,4.8vw,30px)!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-content,
  .app-shell .testimony-wall-story-content{
    display:block!important;
    width:100%!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-mark,
  .app-shell .testimony-wall-story-mark{
    position:absolute!important;
    right:18px!important;
    top:18px!important;
    width:28px!important;
    height:28px!important;
    opacity:.16!important;
    pointer-events:none!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-head h4{
    padding-right:36px!important;
    max-width:100%!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-text{
    max-width:62ch!important;
    margin-right:auto!important;
  }
  @media(max-width:430px){
    .app-shell #shared-testimonies-wall.testimony-wall-today-style{padding:20px 17px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-story-card{padding:21px 18px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-story-mark{right:14px!important;top:14px!important;width:24px!important;height:24px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-story-head h4{padding-right:28px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-story-text{max-width:none!important;}
  }


  /* Shared Testimonies journal readability pass: wider, calmer story pages. */
  .app-shell .testimony-shared-page{
    width:100%!important;
    padding-inline:0!important;
  }
  .app-shell .testimony-shared-page-hero.testimony-wall-hero-today,
  .app-shell #shared-testimonies-wall.testimony-wall-today-style{
    width:100%!important;
    box-sizing:border-box!important;
  }
  .app-shell .testimony-shared-page-hero.testimony-wall-hero-today{
    padding:clamp(20px,4vw,26px)!important;
    border-radius:28px!important;
  }
  .app-shell #shared-testimonies-wall.testimony-wall-today-style{
    padding:clamp(20px,4vw,26px)!important;
    border-radius:28px!important;
    background:linear-gradient(145deg,#FFFDF8,#FFF9EE)!important;
    box-shadow:0 14px 34px rgba(84,62,30,.06),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .app-shell #shared-testimonies-wall.testimony-wall-today-style::before{
    height:1px!important;
    margin-bottom:18px!important;
    opacity:.72!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-heading{
    margin-bottom:16px!important;
    gap:14px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-heading h3{
    max-width:100%!important;
    line-height:1.08!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-heading p{
    max-width:42ch!important;
  }
  .app-shell #shared-testimonies-wall .testimony-category-row{
    gap:12px!important;
    padding:2px 2px 14px!important;
    margin:0 0 8px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-category-row button{
    min-height:36px!important;
    padding:8px 13px!important;
    border-width:1px!important;
    background:rgba(255,253,246,.58)!important;
    color:#7D674D!important;
    font-size:12.5px!important;
    font-weight:650!important;
    box-shadow:none!important;
    transform:none!important;
  }
  .app-shell #shared-testimonies-wall .testimony-category-row button.active{
    background:rgba(255,248,223,.78)!important;
    border-color:rgba(201,154,61,.34)!important;
    color:#5F3F09!important;
    box-shadow:none!important;
    transform:none!important;
  }
  .app-shell #shared-testimonies-wall .scroll-list{
    gap:16px!important;
    margin-top:8px!important;
    padding-right:2px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-card{
    width:100%!important;
    box-sizing:border-box!important;
    padding:28px!important;
    border-radius:26px!important;
    background:linear-gradient(145deg,rgba(255,253,248,.98),rgba(255,250,239,.96))!important;
    border:1px solid rgba(214,185,127,.22)!important;
    box-shadow:0 10px 26px rgba(84,62,30,.045)!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-card::after{
    opacity:.045!important;
    right:20px!important;
    top:18px!important;
    font-size:28px!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-card:hover,
  .app-shell #shared-testimonies-wall .testimony-wall-story-card:focus-within{
    transform:none!important;
    box-shadow:0 12px 28px rgba(84,62,30,.06)!important;
    border-color:rgba(214,185,127,.30)!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-content{
    display:block!important;
    width:100%!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-mark{
    position:absolute!important;
    right:22px!important;
    top:22px!important;
    width:24px!important;
    height:24px!important;
    opacity:.13!important;
    border:0!important;
    background:transparent!important;
    pointer-events:none!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-head .section-label{
    display:block!important;
    margin:0 0 12px!important;
    color:#A78E63!important;
    font-size:10px!important;
    line-height:1.1!important;
    letter-spacing:.17em!important;
    font-weight:650!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-head h4{
    max-width:100%!important;
    margin:0!important;
    padding-right:34px!important;
    color:#342313!important;
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-size:clamp(24px,5.5vw,33px)!important;
    line-height:1.14!important;
    font-weight:500!important;
    letter-spacing:0!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-text{
    max-width:68ch!important;
    margin:18px 0 22px!important;
    color:#5F4D34!important;
    font-size:clamp(15.5px,3.9vw,16.5px)!important;
    line-height:1.72!important;
    font-weight:400!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-read-more{
    display:inline-flex!important;
    margin:0 0 18px!important;
    min-height:34px!important;
    padding:4px 0!important;
    border-bottom:1px solid rgba(201,154,61,.34)!important;
    color:#8A5A08!important;
    font-size:13px!important;
    font-weight:650!important;
  }
  .app-shell #shared-testimonies-wall .testimony-wall-story-meta{
    margin:0!important;
    padding-top:16px!important;
    border-top:1px solid rgba(214,185,127,.22)!important;
    color:#8F7B5D!important;
    font-size:12.75px!important;
    line-height:1.45!important;
    word-break:normal!important;
    overflow-wrap:break-word!important;
  }
  @media(max-width:430px){
    .app-shell .testimony-shared-page-hero.testimony-wall-hero-today,
    .app-shell #shared-testimonies-wall.testimony-wall-today-style{
      padding:20px 18px!important;
      border-radius:26px!important;
    }
    .app-shell #shared-testimonies-wall .testimony-wall-story-card{
      padding:24px 21px!important;
      border-radius:24px!important;
    }
    .app-shell #shared-testimonies-wall .testimony-wall-story-head h4{
      padding-right:26px!important;
      line-height:1.16!important;
    }
    .app-shell #shared-testimonies-wall .testimony-wall-story-text{
      max-width:none!important;
      margin:17px 0 20px!important;
      line-height:1.7!important;
    }
    .app-shell #shared-testimonies-wall .testimony-wall-story-mark{
      right:16px!important;
      top:18px!important;
      width:22px!important;
      height:22px!important;
    }
  }
  @media(max-width:360px){
    .app-shell .testimony-shared-page-hero.testimony-wall-hero-today,
    .app-shell #shared-testimonies-wall.testimony-wall-today-style{padding:18px 16px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-story-card{padding:22px 18px!important;}
    .app-shell #shared-testimonies-wall .testimony-wall-story-text{font-size:15px!important;}
  }

  /* Final Prayer Vessel sanctuary refinement: softer, calmer, more personal. */
  @keyframes vesselSanctuaryBreath{
    0%,100%{filter:drop-shadow(0 18px 32px rgba(198,145,34,.20)) drop-shadow(0 0 0 rgba(255,230,150,0));transform:translateY(0) scale(1);}
    50%{filter:drop-shadow(0 22px 38px rgba(198,145,34,.27)) drop-shadow(0 0 26px rgba(255,226,122,.34));transform:translateY(-1px) scale(1.012);}
  }
  @keyframes vesselLightDrift{
    0%,100%{opacity:.58;transform:translateY(0) scale(.92);}
    50%{opacity:.98;transform:translateY(-7px) scale(1.08);}
  }
  @keyframes vesselAmbientParticle{
    0%{opacity:0;transform:translateY(10px) scale(.8);}
    35%{opacity:.42;}
    100%{opacity:0;transform:translateY(-34px) scale(1.12);}
  }
  @keyframes vesselCompanionReady{
    0%,100%{transform:translateY(0) rotate(-1deg);}
    50%{transform:translateY(-3px) rotate(1deg);}
  }
  .app-shell .prayer-vessels-section{
    padding:10px 12px 12px!important;
  }
  .app-shell .prayer-vessel-hero{
    padding:28px 22px 24px!important;
    gap:24px!important;
    border:1px solid rgba(125,174,212,.12)!important;
    outline:1px solid rgba(255,255,255,.70)!important;
    background:
      radial-gradient(circle at 50% 14%,rgba(255,255,255,.98),transparent 28%),
      radial-gradient(circle at 50% 48%,rgba(255,231,161,.34),transparent 34%),
      radial-gradient(circle at 18% 32%,rgba(224,242,254,.26),transparent 32%),
      linear-gradient(145deg,rgba(255,254,249,.98),rgba(255,251,238,.88) 58%,rgba(247,252,255,.54))!important;
    box-shadow:0 16px 44px rgba(78,64,38,.07),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .app-shell .prayer-vessel-hero::after{
    opacity:.48!important;
    bottom:20px!important;
  }
  .app-shell .prayer-vessel-light-rays{opacity:.16!important;}
  .app-shell .prayer-vessel-particle{
    animation:vesselAmbientParticle 7.6s ease-in-out infinite!important;
    color:rgba(201,144,18,.55)!important;
  }
  .app-shell .prayer-vessel-orb{
    animation:vesselSanctuaryBreath 7.8s ease-in-out infinite!important;
  }
  .app-shell .prayer-vessel-orb::before{
    opacity:.52!important;
    filter:blur(22px)!important;
    transform:scale(calc(1 + (var(--vessel-brightness,0) * .025)))!important;
  }
  .app-shell .prayer-vessel-bowl{
    box-shadow:inset 0 16px 24px rgba(255,255,255,.48), inset 0 -18px 28px rgba(141,93,13,.16), 0 18px 32px rgba(110,83,32,.12)!important;
  }
  .app-shell .prayer-vessel-fill{
    opacity:calc(.28 + (var(--stored-prayer-count,0) * .055))!important;
  }
  .prayer-vessel-stored-lights{
    position:absolute;
    inset:20% 17% 20%;
    z-index:4;
    pointer-events:none;
  }
  .stored-prayer-light{
    position:absolute;
    width:7px;
    height:7px;
    border-radius:999px;
    background:radial-gradient(circle,#fff 0 20%,#fff5c8 34%,#e9b949 72%,rgba(233,185,73,0) 100%);
    box-shadow:0 0 12px rgba(255,230,150,.82),0 0 22px rgba(233,185,73,.38);
    animation:vesselLightDrift 5.8s ease-in-out infinite;
  }
  .stored-prayer-light-1{left:48%;top:66%;animation-delay:.1s;}
  .stored-prayer-light-2{left:34%;top:56%;animation-delay:.8s;}
  .stored-prayer-light-3{left:62%;top:54%;animation-delay:1.4s;}
  .stored-prayer-light-4{left:43%;top:43%;animation-delay:2s;}
  .stored-prayer-light-5{left:57%;top:39%;animation-delay:2.6s;}
  .stored-prayer-light-6{left:29%;top:45%;animation-delay:3.2s;}
  .stored-prayer-light-7{left:69%;top:47%;animation-delay:3.8s;}
  .stored-prayer-light-8{left:50%;top:31%;animation-delay:4.4s;}
  .vessel-empty .stored-prayer-light{display:none!important;}
  .app-shell .prayer-vessel-companion.companion-ready{
    animation:vesselCompanionReady 5.8s ease-in-out infinite!important;
    filter:drop-shadow(0 8px 14px rgba(101,70,22,.12));
  }
  .app-shell .prayer-vessel-hero-copy{
    gap:10px!important;
  }
  .app-shell .prayer-vessel-hero-copy > p:not(.prayer-vessel-private-note){
    padding:0!important;
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
    max-width:28rem!important;
    color:#6f5c43!important;
    font-size:14.5px!important;
    font-weight:450!important;
    line-height:1.5!important;
  }
  .app-shell .prayer-vessel-primary-action{
    min-height:50px!important;
    padding:13px 22px!important;
    border-radius:17px!important;
    font-size:14px!important;
    letter-spacing:.01em!important;
    box-shadow:0 10px 24px rgba(161,111,19,.18),inset 0 1px 0 rgba(255,255,255,.45)!important;
  }
  .app-shell .prayer-vessel-open-grid{
    gap:10px!important;
    margin-top:6px!important;
  }
  .app-shell .prayer-vessel-open-grid button{
    border:1px solid rgba(224,197,139,.20)!important;
    background:rgba(255,253,246,.62)!important;
    box-shadow:0 8px 22px rgba(78,64,38,.04),inset 0 1px 0 rgba(255,255,255,.76)!important;
    padding:14px 12px!important;
  }
  .app-shell .prayer-vessel-open-grid button.active{
    border-color:rgba(201,144,18,.30)!important;
    background:linear-gradient(145deg,rgba(255,249,228,.86),rgba(255,255,255,.68))!important;
  }
  .app-shell .prayer-vessel-open-grid b{
    font-size:24px!important;
    line-height:1!important;
  }
  .app-shell .prayer-vessel-open-grid span{
    font-size:12.5px!important;
    color:#6f5c43!important;
  }
  .app-shell .prayer-vessel-open-grid em{
    border:0!important;
    background:transparent!important;
    color:#9a7b42!important;
    padding:0!important;
  }
  .app-shell .prayer-landing-pulse{
    margin-top:4px!important;
    border:1px solid rgba(125,174,212,.14)!important;
    background:linear-gradient(135deg,rgba(255,253,246,.92),rgba(246,250,255,.50))!important;
    box-shadow:0 10px 24px rgba(67,82,100,.05)!important;
  }
  .app-shell .prayer-landing-pulse-copy > span{
    color:#8a714f!important;
    font-weight:650!important;
  }
  .app-shell .prayer-landing-pulse-copy p{
    color:#3A2812!important;
    font-family:'Cormorant Garamond',Georgia,serif!important;
    font-weight:500!important;
    font-size:clamp(18px,4.2vw,22px)!important;
    line-height:1.12!important;
  }
  .app-shell .prayer-landing-open{
    background:rgba(255,255,255,.70)!important;
    border-color:rgba(224,197,139,.24)!important;
    color:#6f4b11!important;
  }
  @media(max-width:680px){
    .app-shell .prayer-vessels-section{padding:8px 6px 10px!important;}
    .app-shell .prayer-vessel-hero{padding:22px 16px 20px!important;gap:20px!important;}
    .app-shell .prayer-vessel-open-grid{grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:7px!important;}
    .app-shell .prayer-vessel-open-grid button{min-height:72px!important;padding:10px 5px!important;}
    .app-shell .prayer-vessel-open-grid b{font-size:20px!important;}
    .app-shell .prayer-vessel-open-grid span{font-size:10.7px!important;line-height:1.16!important;}
    .app-shell .prayer-vessel-open-grid em{font-size:10px!important;position:static!important;transform:none!important;margin-top:2px!important;}
  }
  @media(prefers-reduced-motion:reduce){
    .app-shell .prayer-vessel-orb,
    .app-shell .prayer-vessel-particle,
    .stored-prayer-light,
    .app-shell .prayer-vessel-companion.companion-ready{animation:none!important;}
  }




  /* Final testimony card polish: prevent clipped icons and soften the card rhythm. */
  .app-shell .testimony-garden-page .testimony-premium-action-card{
    overflow:visible!important;
    padding:18px 18px!important;
    gap:14px!important;
    align-items:center!important;
  }
  .app-shell .testimony-garden-page .testimony-action-icon,
  .app-shell .testimony-garden-page .testimony-wall-share-icon,
  .app-shell .testimony-garden-page .testimony-recent-icon{
    width:48px!important;
    height:48px!important;
    min-width:48px!important;
    flex:0 0 48px!important;
    overflow:visible!important;
    border-radius:18px!important;
    font-size:21px!important;
    line-height:1!important;
  }
  .app-shell .testimony-garden-page .testimony-share-action-icon{
    padding:0!important;
    overflow:hidden!important;
    background:linear-gradient(135deg,rgba(255,248,223,.98),rgba(236,253,245,.72))!important;
    border:1px solid rgba(231,207,159,.42)!important;
  }
  .app-shell .testimony-garden-page .testimony-share-action-icon img{
    display:block!important;
    width:100%!important;
    height:100%!important;
    object-fit:cover!important;
    object-position:center!important;
  }
  .app-shell .testimony-garden-page .instagram-action-icon{
    padding:0!important;
    overflow:hidden!important;
    background:#d80b6a!important;
    border:0!important;
  }
  .app-shell .testimony-garden-page .instagram-action-icon img{
    display:block!important;
    width:100%!important;
    height:100%!important;
    object-fit:contain!important;
    object-position:center!important;
  }
  .app-shell .testimony-garden-page .premium-featured-testimony{
    padding:26px 24px!important;
    overflow:visible!important;
  }

  /* Final Blessings Inbox unread treatment: whole row glows, not just the side line. */
  .app-shell .community-feed-card.blessings-inbox-card .scroll-list.compact > .prayer-friend-row.unread-friend-activity{
    position:relative!important;
    margin:2px 0 4px!important;
    padding:18px 18px 18px 20px!important;
    border:1px solid rgba(176,121,23,.32)!important;
    border-bottom:1px solid rgba(176,121,23,.32)!important;
    border-radius:22px!important;
    background:
      radial-gradient(circle at 8% 12%,rgba(255,244,202,.78),transparent 36%),
      linear-gradient(145deg,rgba(226,174,61,.36),rgba(247,218,144,.46) 42%,rgba(255,247,222,.92) 100%)!important;
    box-shadow:
      0 14px 30px rgba(176,121,23,.15),
      inset 0 1px 0 rgba(255,255,255,.86),
      inset 0 0 0 1px rgba(255,255,255,.44)!important;
  }
  .app-shell .community-feed-card.blessings-inbox-card .scroll-list.compact > .prayer-friend-row.unread-friend-activity::before{
    content:""!important;
    position:absolute!important;
    left:12px!important;
    top:18px!important;
    bottom:18px!important;
    width:2px!important;
    border-radius:999px!important;
    opacity:.22!important;
    background:rgba(138,90,8,.55)!important;
  }
  .app-shell .community-feed-card.blessings-inbox-card .scroll-list.compact > .prayer-friend-row.unread-friend-activity > div{
    position:relative!important;
    z-index:1!important;
  }

  /* Growth current stage compact polish */
  .app-shell .growth-page-calm .growth-stage-hero{
    gap:16px!important;
    padding:20px!important;
  }
  .app-shell .growth-page-calm .growth-stage-copy h3{
    margin:2px 0 4px!important;
    line-height:.98!important;
  }
  .app-shell .growth-page-calm .growth-stage-copy p{
    line-height:1.34!important;
    margin-bottom:0!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress{
    margin-top:10px!important;
    padding:11px 12px!important;
    border-radius:18px!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress-top{
    display:grid!important;
    grid-template-columns:1fr!important;
    gap:5px!important;
    margin-bottom:8px!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress-top span{
    max-width:none!important;
    white-space:nowrap!important;
    font-size:11.5px!important;
    line-height:1.2!important;
  }
  .app-shell .growth-page-calm .growth-stage-progress-top b{
    font-size:31px!important;
  }


  /* Absolute final home cleanup: no small rectangle windows, trim bars, or balcony plates. */
  .app-shell .home-page .home-room .home-window-dot,
  .app-shell .home-page .home-room .home-little-shelter-window,
  .app-shell .home-page .home-room .home-mansion-window,
  .app-shell .home-page .home-room .home-palace-balcony,
  .app-shell .home-page .home-room .home-palace-balcony::before,
  .app-shell .home-page .home-room .home-palace-balcony::after{
    display:none!important;
    content:none!important;
    width:0!important;
    height:0!important;
    opacity:0!important;
    visibility:hidden!important;
    background:none!important;
    border:0!important;
    box-shadow:none!important;
  }
  .app-shell .home-page .home-room .home-cardboard-body::before,
  .app-shell .home-page .home-room .home-little-shelter-body::before,
  .app-shell .home-page .home-room .home-house-body::before,
  .app-shell .home-page .home-room .home-mansion-body::before,
  .app-shell .home-page .home-room .home-palace-body::before,
  .app-shell .home-page .home-room.home-stage-0 .home-cardboard-body::before,
  .app-shell .home-page .home-room .home-cardboard-roof::before,
  .app-shell .home-page .home-room .home-little-shelter-roof::before,
  .app-shell .home-page .home-room .home-house-roof::before,
  .app-shell .home-page .home-room .home-mansion-roof::before,
  .app-shell .home-page .home-room .home-palace-roof::before{
    display:none!important;
    content:none!important;
    width:0!important;
    height:0!important;
    opacity:0!important;
    visibility:hidden!important;
    background:none!important;
    border:0!important;
    box-shadow:none!important;
  }

  /* Hard stop for leftover house rectangles: removes trim bars, faux windows, chimneys, and scroll/vessel plaques from every home stage. */
  .app-shell .home-page .home-room .home-cardboard::before,
  .app-shell .home-page .home-room .home-little-shelter::before,
  .app-shell .home-page .home-room .home-house::before,
  .app-shell .home-page .home-room .home-mansion::before,
  .app-shell .home-page .home-room .home-palace::before,
  .app-shell .home-page .home-room .home-cardboard-roof::before,
  .app-shell .home-page .home-room .home-little-shelter-roof::before,
  .app-shell .home-page .home-room .home-house-roof::before,
  .app-shell .home-page .home-room .home-mansion-roof::before,
  .app-shell .home-page .home-room .home-palace-roof::before,
  .app-shell .home-page .home-room .home-cardboard-roof::after,
  .app-shell .home-page .home-room .home-little-shelter-roof::after,
  .app-shell .home-page .home-room .home-house-roof::after,
  .app-shell .home-page .home-room .home-mansion-roof::after,
  .app-shell .home-page .home-room .home-palace-roof::after,
  .app-shell .home-page .home-room .home-cardboard-body::before,
  .app-shell .home-page .home-room .home-little-shelter-body::before,
  .app-shell .home-page .home-room .home-house-body::before,
  .app-shell .home-page .home-room .home-mansion-body::before,
  .app-shell .home-page .home-room .home-palace-body::before,
  .app-shell .home-page .home-room .home-cardboard-body::after,
  .app-shell .home-page .home-room .home-little-shelter-body::after,
  .app-shell .home-page .home-room .home-house-body::after,
  .app-shell .home-page .home-room .home-mansion-body::after,
  .app-shell .home-page .home-room .home-palace-body::after,
  .app-shell .home-page .home-room .home-window-dot,
  .app-shell .home-page .home-room .home-window-dot::before,
  .app-shell .home-page .home-room .home-window-dot::after,
  .app-shell .home-page .home-room .home-little-shelter-window,
  .app-shell .home-page .home-room .home-little-shelter-window::before,
  .app-shell .home-page .home-room .home-little-shelter-window::after,
  .app-shell .home-page .home-room .home-mansion-window,
  .app-shell .home-page .home-room .home-mansion-window::before,
  .app-shell .home-page .home-room .home-mansion-window::after,
  .app-shell .home-page .home-room .home-palace-balcony,
  .app-shell .home-page .home-room .home-palace-balcony::before,
  .app-shell .home-page .home-room .home-palace-balcony::after,
  .app-shell .home-page .home-room .home-word-scroll,
  .app-shell .home-page .home-room .home-signature-vessel{
    content:none!important;
    display:none!important;
    width:0!important;
    height:0!important;
    min-width:0!important;
    min-height:0!important;
    max-width:0!important;
    max-height:0!important;
    opacity:0!important;
    visibility:hidden!important;
    overflow:hidden!important;
    background:none!important;
    border:0!important;
    box-shadow:none!important;
    filter:none!important;
    transform:none!important;
    pointer-events:none!important;
  }



  /* Premium home visual refinement: richer houses, softer clouds, calmer depth. */
  @keyframes homeCloudBreathFinal{
    0%,100%{transform:translate3d(0,0,0);}
    50%{transform:translate3d(12px,-4px,0);}
  }
  @keyframes homeTinyAtmosphereFinal{
    0%,100%{transform:translateY(0) scale(.9);opacity:.10;}
    50%{transform:translateY(-10px) scale(1.04);opacity:.24;}
  }
  @keyframes homeBirdDriftFinal{
    0%{transform:translateX(-12px) translateY(0) scale(.92);opacity:.10;}
    50%{opacity:.24;}
    100%{transform:translateX(18px) translateY(-5px) scale(.98);opacity:.10;}
  }

  .app-shell .home-page.simple-home-page .home-art-card{
    padding:18px!important;
    border-radius:30px!important;
    border:1.5px solid rgba(222,197,143,.46)!important;
    background:
      linear-gradient(145deg,rgba(255,253,248,.98),rgba(255,248,235,.90))!important;
    box-shadow:0 18px 44px rgba(86,62,27,.08),inset 0 1px 0 rgba(255,255,255,.90)!important;
  }
  .app-shell .home-page.simple-home-page .home-art-card .home-art-frame{
    padding:15px!important;
    border-radius:28px!important;
    border:1px solid rgba(214,185,127,.24)!important;
    background:
      radial-gradient(circle at 50% 28%,rgba(255,255,255,.78),transparent 32%),
      radial-gradient(circle at 50% 70%,rgba(240,199,94,.16),transparent 48%),
      linear-gradient(145deg,rgba(255,253,246,.97),rgba(255,249,232,.72))!important;
    box-shadow:inset 0 1px 0 rgba(255,255,255,.88),inset 0 -22px 36px rgba(94,71,33,.045),0 10px 28px rgba(84,62,30,.05)!important;
  }
  .app-shell .home-page.simple-home-page .home-art-frame .home-room{
    min-height:430px!important;
    border-radius:24px!important;
    border:0!important;
    box-shadow:inset 0 0 0 1px rgba(255,255,255,.72),inset 0 -36px 56px rgba(99,80,43,.075)!important;
    overflow:hidden!important;
  }

  .app-shell .home-page .home-room .home-sky{
    background:
      radial-gradient(circle at 48% 10%,rgba(255,255,255,.86) 0 9%,transparent 28%),
      linear-gradient(180deg,#EAF7FF 0%,#FFF7DD 58%,#F7F0D1 100%)!important;
    filter:saturate(.90) brightness(1.03)!important;
  }
  .app-shell .home-page .home-room .home-sky::after{
    content:""!important;
    position:absolute!important;
    inset:0!important;
    z-index:1!important;
    pointer-events:none!important;
    background:
      linear-gradient(180deg,rgba(255,255,255,.18),transparent 48%),
      radial-gradient(ellipse at 50% 94%,rgba(255,255,255,.35),transparent 55%)!important;
  }
  .app-shell .home-page .home-room .home-cloud{
    background:
      radial-gradient(circle at 18% 58%,rgba(255,255,255,.92) 0 22%,transparent 23%),
      radial-gradient(circle at 38% 42%,rgba(255,255,255,.97) 0 30%,transparent 31%),
      radial-gradient(circle at 60% 52%,rgba(255,255,255,.91) 0 27%,transparent 28%),
      radial-gradient(circle at 79% 60%,rgba(255,255,255,.86) 0 20%,transparent 21%),
      linear-gradient(180deg,rgba(255,255,255,.80),rgba(255,255,255,.46))!important;
    border-radius:999px!important;
    filter:blur(.12px)!important;
    box-shadow:0 12px 28px rgba(118,154,170,.08),inset 0 1px 0 rgba(255,255,255,.88)!important;
    opacity:.70!important;
    animation:homeCloudBreathFinal 36s ease-in-out infinite!important;
  }
  .app-shell .home-page .home-room .home-cloud::before,
  .app-shell .home-page .home-room .home-cloud::after{
    content:""!important;
    position:absolute!important;
    border-radius:50%!important;
    background:rgba(255,255,255,.78)!important;
    filter:blur(.1px)!important;
  }
  .app-shell .home-page .home-room .home-cloud::before{left:16%;top:-30%;width:35%;height:78%;}
  .app-shell .home-page .home-room .home-cloud::after{right:18%;top:-18%;width:28%;height:65%;}
  .app-shell .home-page .home-room .home-cloud.c1{width:168px!important;height:46px!important;left:-42px!important;top:52px!important;}
  .app-shell .home-page .home-room .home-cloud.c2{width:196px!important;height:52px!important;right:-50px!important;top:62px!important;animation-delay:-12s!important;}
  .app-shell .home-page .home-room .home-cloud.c3{width:132px!important;height:38px!important;left:35%!important;top:30px!important;opacity:.50!important;animation-delay:-20s!important;}
  .app-shell .home-page .home-room .home-rays,
  .app-shell .home-page .home-room .home-sunbeam-drift{
    opacity:.055!important;
    filter:blur(1.8px)!important;
    mix-blend-mode:normal!important;
  }
  .app-shell .home-page .home-room .home-distant-city,
  .app-shell .home-page .home-room .home-background-rainbow,
  .app-shell .home-page .home-room .home-midground-glow{
    opacity:.08!important;
    filter:blur(.25px)!important;
  }

  .app-shell .home-page .home-room .home-floor{
    background:
      radial-gradient(ellipse at 50% 26%,rgba(255,255,255,.34),transparent 44%),
      linear-gradient(180deg,#F7F1D6 0%,#DFEDBC 45%,#D4E1A3 100%)!important;
    filter:saturate(.84)!important;
  }
  .app-shell .home-page .home-room .home-floor::after{
    content:""!important;
    position:absolute!important;
    left:12%!important;
    right:12%!important;
    bottom:11%!important;
    height:24%!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse,rgba(87,68,28,.18),rgba(87,68,28,0) 72%)!important;
    opacity:.36!important;
    z-index:2!important;
    pointer-events:none!important;
  }
  .app-shell .home-page .home-room .home-path{
    opacity:.82!important;
    background:linear-gradient(180deg,rgba(255,249,219,.80),rgba(217,154,18,.38))!important;
    box-shadow:inset 0 0 22px rgba(255,255,255,.46)!important;
  }
  .app-shell .home-page .home-room .home-flowerbed{
    width:22%!important;
    height:23%!important;
    bottom:2%!important;
    opacity:.74!important;
    filter:saturate(.78) brightness(.98)!important;
  }
  .app-shell .home-page .home-room .home-flowerbed.left{left:-8%!important;}
  .app-shell .home-page .home-room .home-flowerbed.right{right:-8%!important;}

  .app-shell .home-page .home-room.home-stage-0 .home-cardboard{top:41%!important;width:160px!important;height:94px!important;}
  .app-shell .home-page .home-room.home-stage-1 .home-little-shelter{top:35%!important;width:204px!important;height:132px!important;}
  .app-shell .home-page .home-room.home-stage-2 .home-house{top:28%!important;width:232px!important;height:166px!important;}
  .app-shell .home-page .home-room.home-stage-3 .home-mansion{top:23%!important;width:260px!important;height:178px!important;}
  .app-shell .home-page .home-room.home-stage-4 .home-palace{top:18%!important;width:268px!important;height:190px!important;}
  .app-shell .home-page .home-room.home-stage-5 .home-palace{top:16%!important;width:286px!important;height:202px!important;}
  .app-shell .home-page .home-room.home-stage-6 .home-palace{top:14%!important;width:304px!important;height:214px!important;}

  .app-shell .home-page .home-room .home-cardboard,
  .app-shell .home-page .home-room .home-little-shelter,
  .app-shell .home-page .home-room .home-house,
  .app-shell .home-page .home-room .home-mansion,
  .app-shell .home-page .home-room .home-palace{
    filter:drop-shadow(0 18px 22px rgba(86,62,27,.22)) drop-shadow(0 0 18px rgba(255,237,171,.16))!important;
  }
  .app-shell .home-page .home-room .home-cardboard::after,
  .app-shell .home-page .home-room .home-little-shelter::after,
  .app-shell .home-page .home-room .home-house::after,
  .app-shell .home-page .home-room .home-mansion::after,
  .app-shell .home-page .home-room .home-palace::after{
    content:""!important;
    display:block!important;
    position:absolute!important;
    left:4%!important;
    right:4%!important;
    bottom:-15px!important;
    height:24px!important;
    border-radius:50%!important;
    background:radial-gradient(ellipse,rgba(72,54,25,.24),rgba(72,54,25,0) 72%)!important;
    opacity:.72!important;
    z-index:-1!important;
    pointer-events:none!important;
    visibility:visible!important;
    min-width:0!important;
    min-height:0!important;
    max-width:none!important;
    max-height:none!important;
    overflow:visible!important;
    filter:none!important;
    transform:none!important;
  }

  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{
    transform:translateY(0)!important;
    background:
      linear-gradient(90deg,rgba(255,255,255,.20),transparent 38%,rgba(100,67,18,.08)),
      linear-gradient(180deg,#FFF8E2 0%,#F4D18A 56%,#C98E32 100%)!important;
    border:2px solid rgba(95,63,15,.28)!important;
    box-shadow:inset 0 10px 14px rgba(255,255,255,.62),inset 0 -12px 16px rgba(78,50,13,.16),0 0 0 1px rgba(255,255,255,.30)!important;
  }
  .app-shell .home-page .home-room .home-cardboard-roof,
  .app-shell .home-page .home-room .home-little-shelter-roof,
  .app-shell .home-page .home-room .home-house-roof,
  .app-shell .home-page .home-room .home-mansion-roof,
  .app-shell .home-page .home-room .home-palace-roof{
    background:
      repeating-linear-gradient(110deg,rgba(118,73,12,.18) 0 2px,transparent 2px 18px),
      linear-gradient(180deg,#FFE7A4 0%,#D99A12 64%,#9A5F0A 100%)!important;
    filter:drop-shadow(0 11px 11px rgba(88,58,12,.24))!important;
    z-index:72!important;
  }
  .app-shell .home-page .home-room.home-stage-1 .home-little-shelter-roof,
  .app-shell .home-page .home-room.home-stage-2 .home-house-roof{transform:translateY(-10px)!important;}
  .app-shell .home-page .home-room.home-stage-3 .home-mansion-roof{transform:translateY(-8px)!important;}
  .app-shell .home-page .home-room.home-stage-4 .home-palace-roof,
  .app-shell .home-page .home-room.home-stage-5 .home-palace-roof,
  .app-shell .home-page .home-room.home-stage-6 .home-palace-roof{transform:translateY(-10px)!important;}

  .app-shell .home-page .home-room .home-cardboard-door,
  .app-shell .home-page .home-room .home-little-shelter-door,
  .app-shell .home-page .home-room .home-house-door,
  .app-shell .home-page .home-room .home-mansion-door,
  .app-shell .home-page .home-room .home-door{
    background:
      radial-gradient(circle at 62% 43%,#FFF8DF 0 6%,transparent 7%),
      linear-gradient(90deg,rgba(255,255,255,.20),transparent 45%,rgba(65,41,12,.16)),
      linear-gradient(180deg,#E3A13B 0%,#9B631A 100%)!important;
    border:2px solid rgba(71,47,14,.36)!important;
    box-shadow:0 0 14px rgba(255,230,150,.62),inset 0 5px 7px rgba(255,255,255,.36),inset 0 -8px 10px rgba(55,35,11,.24)!important;
  }
  .app-shell .home-page .home-room .home-spirit-ground-shadow{opacity:.36!important;filter:blur(4px)!important;}
  .app-shell .home-page .home-room .home-spirit-slot{filter:brightness(1.04) contrast(1.035) drop-shadow(0 12px 18px rgba(75,55,22,.14))!important;}
  .app-shell .home-page .home-room .home-spirit-life{display:block!important;opacity:.28!important;animation:homeTinyAtmosphereFinal 5.8s ease-in-out infinite!important;}
  .app-shell .home-page .home-room .home-ambient-particles .particle{
    background:radial-gradient(circle,#fff 0 18%,#F0C75E 38%,rgba(240,199,94,0) 72%)!important;
    opacity:.16!important;
    animation:homeTinyAtmosphereFinal 8.5s ease-in-out infinite!important;
  }
  .app-shell .home-page .home-room .home-sky-bird{opacity:.22!important;animation:homeBirdDriftFinal 12s ease-in-out infinite alternate!important;}
  .app-shell .home-page .home-room .home-sky-bird.bird-b{animation-delay:-5s!important;}

  .app-shell .home-page.simple-home-page .simple-home-blessing{
    padding:26px 24px!important;
    border-radius:28px!important;
    background:linear-gradient(145deg,#FFFDF8,#FFF9EC)!important;
    box-shadow:0 14px 34px rgba(84,62,30,.07),inset 0 1px 0 rgba(255,255,255,.86)!important;
  }
  .app-shell .home-page.simple-home-page .simple-home-blessing .section-label{margin-bottom:12px!important;}
  .app-shell .home-page.simple-home-page .simple-home-blessing h3{margin-bottom:12px!important;line-height:1.12!important;font-weight:500!important;}
  .app-shell .home-page.simple-home-page .simple-home-blessing p{font-size:14px!important;line-height:1.58!important;color:#6F5C43!important;}

  @media(max-width:560px){
    .app-shell .home-page.simple-home-page .home-art-card{padding:14px!important;}
    .app-shell .home-page.simple-home-page .home-art-card .home-art-frame{padding:10px!important;border-radius:24px!important;}
    .app-shell .home-page.simple-home-page .home-art-frame .home-room{min-height:400px!important;height:clamp(400px,106vw,440px)!important;border-radius:21px!important;}
    .app-shell .home-page .home-room.home-stage-0 .home-cardboard{width:148px!important;}
    .app-shell .home-page .home-room.home-stage-1 .home-little-shelter{width:188px!important;}
    .app-shell .home-page .home-room.home-stage-2 .home-house{top:30%!important;width:210px!important;}
    .app-shell .home-page .home-room.home-stage-3 .home-mansion{top:25%!important;width:236px!important;}
    .app-shell .home-page .home-room.home-stage-4 .home-palace{top:20%!important;width:242px!important;}
    .app-shell .home-page .home-room.home-stage-5 .home-palace{top:19%!important;width:256px!important;}
    .app-shell .home-page .home-room.home-stage-6 .home-palace{top:18%!important;width:272px!important;}
    .app-shell .home-page .home-room .home-flowerbed{width:20%!important;height:21%!important;}
  }


  /* Final roof/body join fix: keep homes defined without the visible roof gap. */
  .app-shell .home-page .home-room .home-cardboard-body,
  .app-shell .home-page .home-room .home-little-shelter-body,
  .app-shell .home-page .home-room .home-house-body,
  .app-shell .home-page .home-room .home-mansion-body,
  .app-shell .home-page .home-room .home-palace-body{
    transform:translateY(0)!important;
  }
  .app-shell .home-page .home-room.home-stage-1 .home-little-shelter-roof,
  .app-shell .home-page .home-room.home-stage-2 .home-house-roof{
    transform:translateY(-8px)!important;
  }
  .app-shell .home-page .home-room.home-stage-3 .home-mansion-roof,
  .app-shell .home-page .home-room.home-stage-4 .home-palace-roof,
  .app-shell .home-page .home-room.home-stage-5 .home-palace-roof,
  .app-shell .home-page .home-room.home-stage-6 .home-palace-roof{
    transform:translateY(-9px)!important;
  }


  /* Blessings Inbox unread count: red floating number on the Open button. */
  .app-shell .community-feed-card.blessings-inbox-card .soft-action-btn.friend-alert-btn{
    position:relative!important;
    overflow:visible!important;
    padding-right:18px!important;
  }
  .app-shell .community-feed-card.blessings-inbox-card .friend-alert-badge{
    position:absolute!important;
    top:-10px!important;
    right:-9px!important;
    min-width:22px!important;
    height:22px!important;
    padding:0 6px!important;
    margin:0!important;
    border-radius:999px!important;
    background:linear-gradient(180deg,#FF5A5A,#C92424)!important;
    border:2px solid #FFFDF8!important;
    color:#fff!important;
    font-size:11.5px!important;
    font-weight:900!important;
    line-height:1!important;
    box-shadow:0 6px 14px rgba(185,36,36,.24),0 0 0 1px rgba(185,36,36,.16)!important;
    z-index:3!important;
  }

  /* Final onboarding welcome symbol color. */
  .onboarding-welcome-diamond{
    color:#C99A3D!important;
    -webkit-text-fill-color:#C99A3D!important;
    text-shadow:0 0 18px rgba(232,198,111,.58)!important;
    filter:drop-shadow(0 4px 14px rgba(201,154,61,.38))!important;
  }

`;

// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
function HomeIcon({ size = 22 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      className="app-glow-icon home-glow-icon"
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 32 32" focusable="false">
        <path className="icon-fill" d="M6.5 15.6 16 7.7l9.5 7.9v10.1a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2V15.6Z" />
        <path className="icon-deep" d="M13.8 27.7v-7.6a2.2 2.2 0 0 1 4.4 0v7.6h-4.4Z" opacity=".9" />
        <path className="icon-stroke" d="M4.6 15.7 16 6.2l11.4 9.5" />
        <path className="icon-stroke" d="M8.1 15v10.4c0 .8.6 1.4 1.4 1.4h13c.8 0 1.4-.6 1.4-1.4V15" />
        <path className="icon-stroke" d="M12 16.7h3.1v3.1H12zM16.9 16.7H20v3.1h-3.1z" />
      </svg>
    </span>
  );
}

function JournalIcon({ size = 22 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      className="app-glow-icon journal-glow-icon"
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 32 32" focusable="false">
        <path className="icon-fill" d="M9.1 4.8h13.7a2.6 2.6 0 0 1 2.6 2.6v18.9a2 2 0 0 1-2 2H9.1a2.5 2.5 0 0 1-2.5-2.5V7.3a2.5 2.5 0 0 1 2.5-2.5Z" />
        <path className="icon-deep" d="M9.5 5.2h3.1v22.7H9.5a2.7 2.7 0 0 1-2.7-2.7V7.9a2.7 2.7 0 0 1 2.7-2.7Z" opacity=".72" />
        <path className="icon-stroke" d="M10.2 4.8h12.3a2.8 2.8 0 0 1 2.8 2.8v17.1a2.8 2.8 0 0 1-2.8 2.8H10.2a3.5 3.5 0 0 1-3.5-3.5V8.3a3.5 3.5 0 0 1 3.5-3.5Z" />
        <path className="icon-stroke" d="M12.3 5.2v22.3M16.4 11.2h5.5M16.4 15.5h5.5" />
        <path className="icon-deep" d="M12.5 23.4 15 25l2.5-1.6v4.9L15 26.7l-2.5 1.6v-4.9Z" />
      </svg>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
function GoldBtn({
  children,
  onClick,
  disabled = false,
  style = {},
  className = "",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`gold-btn ${className}`}
      style={style}
    >
      {children}
    </button>
  );
}

function Card({
  children,
  style = {},
  className = "",
  id,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  id?: string;
}) {
  return (
    <div id={id} className={`card ${className}`} style={style}>
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: "0 0 8px",
        fontSize: 13,
        letterSpacing: 2,
        textTransform: "uppercase",
        color: C.textFaint,
        fontWeight: 700,
      }}
    >
      {children}
    </p>
  );
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="section-divider">
      <div className="section-divider-line" />
      <span className="section-divider-label">{label}</span>
      <div className="section-divider-line" />
    </div>
  );
}

function StatPill({ icon, value }: { icon: string; value: string | number }) {
  return (
    <div className="stat-pill">
      <div
        style={{
          fontSize: 13,
          lineHeight: 1,
          fontWeight: 800,
          color: C.textDark,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 13.5, color: C.textFaint }}>{icon}</div>
    </div>
  );
}

function Toast({ msg, onDone }: { msg: string; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2400);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      style={{
        position: "fixed",
        bottom: 90,
        left: "50%",
        transform: "translateX(-50%)",
        background: "linear-gradient(135deg,#FFF8DF,#F0C75E)",
        border: `1.5px solid ${C.gold}`,
        borderRadius: 14,
        padding: "12px 20px",
        fontSize: 13,
        fontWeight: 700,
        color: C.textDark,
        zIndex: 9998,
        width: "min(86vw, 380px)",
        maxWidth: "86vw",
        textAlign: "center",
        whiteSpace: "normal",
        lineHeight: 1.35,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 8px 28px rgba(138,90,8,.28)",
        animation: "toastPop .3s ease-out",
      }}
    >
      {msg}
    </div>
  );
}


function PrayerLightTrail({ light }: { light: PrayerLight }) {
  return (
    <div
      className="prayer-light"
      style={
        {
          left: light.fromX,
          top: light.fromY,
          "--dx": `${light.toX - light.fromX}px`,
          "--dy": `${light.toY - light.fromY}px`,
        } as React.CSSProperties
      }
    />
  );
}

function AngelPrayerMessage({ msg }: { msg: AngelMessage }) {
  return <div className="angel-message">👼 {msg.text}</div>;
}

function Sparkle({ x, y }: { x: number; y: number }) {
  const [v, setV] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setV(false), 900);
    return () => clearTimeout(t);
  }, []);
  if (!v) return null;
  return (
    <div
      style={{
        position: "fixed",
        left: x - 12,
        top: y - 12,
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      {["✦", "✧", "★", "✦", "✧", "⋆"].map((c, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            fontSize: 13,
            left: 12,
            top: 12,
            color: C.goldLight,
            animation: `sp${i} .85s ease-out forwards`,
          }}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

function ProgressBar({
  value,
  style = {},
}: {
  value: number;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        height: 5,
        borderRadius: 999,
        background: "rgba(180,130,20,.15)",
        overflow: "hidden",
        ...style,
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${Math.round(Math.min(value, 1) * 100)}%`,
          background: GOLD_GRAD,
          borderRadius: 999,
          transition: "width .5s",
        }}
      />
    </div>
  );
}

function FaithJourneyPanel({
  stats,
  open,
  onToggle,
  showDayChip = true,
  stageName,
  nextStageName,
  progressPercent,
  totalXP,
}: {
  stats: FaithJourneyStats;
  open: boolean;
  onToggle: () => void;
  showDayChip?: boolean;
  stageName?: string;
  nextStageName?: string;
  progressPercent?: number;
  totalXP?: number;
}) {
  return (
    <div className="faith-journey-panel">
      <button
        type="button"
        className="faith-journey-toggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        {showDayChip && (
          <div className="faith-journey-day" aria-label={`Journey day ${stats.journeyDay}`}>
            <strong>{stats.journeyDay}</strong>
            <span>Day</span>
          </div>
        )}
        <div className="faith-journey-toggle-copy">
          <span>Today in My Faith Journey</span>
          <small>See how your spirit has grown.</small>
        </div>
        <span className="faith-journey-chevron">{open ? "Hide" : "Open"}</span>
      </button>

      {open && (
        <div className="faith-journey-details">
          <p className="faith-journey-copy">
            A quiet record of prayers, answers, and testimonies.
          </p>

          {typeof progressPercent === "number" && (
            <div className="faith-journey-growth" aria-label="Spirit growth progression">
              <div className="faith-journey-growth-top">
                <span>Spirit Growth</span>
                <b>{Math.round(progressPercent)}%</b>
              </div>
              <div className="faith-journey-growth-track" aria-hidden="true">
                <div
                  className="faith-journey-growth-fill"
                  style={{ width: Math.max(4, Math.min(100, progressPercent)) + "%" }}
                />
              </div>
              <div className="faith-journey-growth-meta">
                <span>{stageName || "Current stage"}</span>
                <span>{nextStageName ? "Next: " + nextStageName : "Keep shining"}</span>
              </div>
              {typeof totalXP === "number" && (
                <p className="faith-journey-growth-note">{totalXP} righteousness gathered.</p>
              )}
            </div>
          )}

          <div className="faith-journey-stats" aria-label="Faith journey totals">
            <div className="faith-journey-stat">
              <b>{stats.prayerCount}</b>
              <span>prayers</span>
            </div>
            <div className="faith-journey-stat">
              <b>{stats.answeredPrayerCount}</b>
              <span>answered</span>
            </div>
            <div className="faith-journey-stat">
              <b>{stats.testimonyCount}</b>
              <span>testimonies</span>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}

function ShopGameIcon({ item }: { item: ClothingItem }) {
  return (
    <div
      className={`shop-game-icon slot-${item.slot}`}
      style={{ "--item-color": item.color } as React.CSSProperties}
      aria-label={item.name}
    >
      <span className="shop-game-shadow" />
      <span className="shop-game-glow" />
      <span className="shop-game-body" />
      <span className="shop-game-symbol">{item.icon}</span>
      <span className="shop-game-sparkles" aria-hidden="true">
        <span>✦</span>
        <span>✧</span>
        <span>⋆</span>
      </span>
    </div>
  );
}

function clothingMeaning(item: ClothingItem): string {
  if (item.id.includes("belt")) return "Truth";
  if (item.id.includes("breastplate") || item.id.includes("robe_righteousness"))
    return "Righteousness";
  if (item.id.includes("shield")) return "Faith";
  if (item.id.includes("shoes") || item.id.includes("sandals")) return "Peace";
  if (item.id.includes("helmet")) return "Salvation";
  if (item.id.includes("sword")) return "Word";
  if (item.id.includes("holy_drip")) return "Joy";
  if (item.id.includes("halo")) return "Grace";
  if (item.id.includes("wings")) return "Mercy";
  if (item.slot === "set") return "Full Set";
  return SLOT_LABELS[item.slot] || item.slot;
}

function StoneRewardModal({
  stone,
  onClose,
  onView,
}: {
  stone: TestimonyStone;
  onClose: () => void;
  onView: () => void;
}) {
  const [a, b, c] = testimonyGemColors(stone.kind);
  return (
    <div
      className="stone-reward-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="stone-reward-modal">
        <div className="testimony-gem-wrap">
          <div className="testimony-gem-shadow" />
          <div
            className={`testimony-gem${
              stone.kind === "white" ? " testimony-gem-white" : ""
            }`}
            style={
              {
                "--gem-a": a,
                "--gem-b": b,
                "--gem-c": c,
              } as React.CSSProperties
            }
          >
            <span className="testimony-gem-core" />
          </div>
        </div>
        <div className="stone-reward-title">
          {stone.ultimate
            ? "Ultimate Stone Received"
            : "Heavenly Stone Received"}
        </div>
        <h2 className="stone-reward-name">{stone.name}</h2>
        <p className="stone-reward-copy">
          {stone.desc}
          <br />
          <strong style={{ color: C.goldDeep }}>Source:</strong> {stone.source}
        </p>
        <div className="stone-reward-actions">
          <button
            onClick={onClose}
            style={{
              flex: 1,
              background: "rgba(255,253,246,.86)",
              border: `1px solid ${C.cardBorder}`,
              borderRadius: 14,
              padding: "12px",
              color: C.textMid,
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            Close
          </button>
          <GoldBtn onClick={onView} style={{ flex: 1 }}>
            View Stones
          </GoldBtn>
        </div>
      </div>
    </div>
  );
}

// ─── LOW FUNDS MODAL ─────────────────────────────────────────────────────────
function LowFundsModal({
  needed,
  current,
  onClose,
}: {
  needed: number;
  current: number;
  onClose: () => void;
}) {
  return (
    <div
      className="funds-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="funds-modal">
        <div style={{ fontSize: 52, marginBottom: 10 }}>✨</div>
        <h2
          className="serif"
          style={{ margin: "0 0 8px", fontSize: 22, color: C.goldDeep }}
        >
          Build more righteousness!
        </h2>
        <p
          style={{
            margin: "0 0 16px",
            fontSize: 14,
            color: C.textSoft,
            lineHeight: 1.65,
          }}
        >
          Let's build more righteousness so we can make your heavenly home and
          spirit shine.
        </p>
        <div
          style={{
            background: "rgba(255,248,223,.55)",
            border: `1px solid ${C.gold}40`,
            borderRadius: 16,
            padding: "12px 16px",
            marginBottom: 18,
          }}
        >
	        <div
	          style={{
	            display: "flex",
              justifyContent: "space-between",
              fontSize: 13,
              color: C.textMid,
              marginBottom: 4,
            }}
          >
            <span>You have</span>
            <span style={{ fontWeight: 800, color: C.goldDeep }}>
              ✨ {current}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 13,
              color: C.textMid,
            }}
          >
            <span>You need</span>
            <span style={{ fontWeight: 800, color: C.red }}>✨ {needed}</span>
          </div>
          <div
            style={{
              borderTop: `1px solid ${C.cardBorder}`,
              marginTop: 8,
              paddingTop: 8,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 13,
              color: C.textFaint,
            }}
          >
            <span>Still need</span>
            <span style={{ fontWeight: 800, color: C.textMid }}>
              ✨ {needed - current} more
            </span>
          </div>
        </div>
        <p
          style={{
            margin: "0 0 14px",
            fontSize: 13,
            color: C.textFaint,
            lineHeight: 1.6,
          }}
        >
          Complete your daily habits, quests, and good samaritan acts to earn
          more righteousness.
        </p>
        <GoldBtn onClick={onClose} style={{ width: "100%" }}>
          Keep Growing 🌱
        </GoldBtn>
      </div>
    </div>
  );
}

// ─── SELL CONFIRM MODAL ───────────────────────────────────────────────────────
function SellModal({
  itemName,
  itemIcon,
  sellPrice,
  onConfirm,
  onCancel,
}: {
  itemName: string;
  itemIcon: string;
  sellPrice: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="sell-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="sell-modal">
        <div style={{ fontSize: 44, marginBottom: 8 }}>{itemIcon}</div>
        <h2
          className="serif"
          style={{ margin: "0 0 6px", fontSize: 20, color: C.textDark }}
        >
          Sell {itemName}?
        </h2>
        <p
          style={{
            margin: "0 0 16px",
            fontSize: 13,
            color: C.textSoft,
            lineHeight: 1.6,
          }}
        >
          You'll receive{" "}
          <strong style={{ color: C.goldDeep }}>✨ {sellPrice}</strong>{" "}
          back (50% of purchase price).
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              background: "rgba(255,253,240,.8)",
              border: `1px solid ${C.cardBorder}`,
              borderRadius: 14,
              padding: "12px",
              color: C.textMid,
              fontWeight: 800,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            Cancel
          </button>
          <GoldBtn onClick={onConfirm} style={{ flex: 1 }}>
            Sell for ✨ {sellPrice}
          </GoldBtn>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SPIRIT AVATAR
// ─────────────────────────────────────────────────────────────────────────────
function SpiritAvatar({
  stageKey,
  xp,
  xpMax,
  stageAura,
  stageGlow,
  animated,
  equipped = [],
  gender = "female",
  skinTone = "#FDDBB4",
  hairColor = DEFAULT_HAIR_COLOR,
  size = 120,
  showBar = true,
}: {
  stageKey: string;
  xp: number;
  xpMax: number;
  stageAura: string;
  stageGlow: string;
  animated: boolean;
  equipped?: ClothingItem[];
  gender?: Gender;
  skinTone?: string;
  hairColor?: string;
  size?: number;
  showBar?: boolean;
}) {
  const pct = xpMax > 0 ? Math.min(xp / xpMax, 1) : 0;
  const svgId = useId().replace(/:/g, "");
  const auraId = `aura_${stageKey}_${svgId}`;
  const glowId = `soft_glow_${svgId}`;
  const skinGradId = `skin_grad_${svgId}`;
  const bodyGradId = `body_grad_${svgId}`;
  const hairGradId = `hair_grad_${svgId}`;
  const swordBladeGradId = `sword_blade_${svgId}`;
  const swordHiltGradId = `sword_hilt_${svgId}`;
  const swordGemGradId = `sword_gem_${svgId}`;
  const isFemale = gender === "female";
  const spiritStageAccent: Record<string, { primary: string; secondary: string }> = {
    seed: { primary: "#86efac", secondary: "#fef3c7" },
    baby: isFemale
      ? { primary: "#f5a7dc", secondary: "#f0bbf5" }
      : { primary: "#65d6b7", secondary: "#9be8cf" },
    teen: isFemale
      ? { primary: "#d7b6ff", secondary: "#ffd7ef" }
      : { primary: "#8fd3ff", secondary: "#c7f4ff" },
    adult: isFemale
      ? { primary: "#c9a5ff", secondary: "#f5a7dc" }
      : { primary: "#7dd3fc", secondary: "#80d8bd" },
    radiant: isFemale
      ? { primary: "#f5a7dc", secondary: "#fde68a" }
      : { primary: "#86efac", secondary: "#d9f99d" },
  };
  const spiritAccent = spiritStageAccent[stageKey] || {
    primary: stageGlow,
    secondary: stageAura,
  };
  const isLeafStage = !isFemale && (stageKey === "teen" || stageKey === "adult");
  const avatarGlow = isFemale ? stageGlow : isLeafStage ? "#22c55e" : spiritAccent.primary;
  const isBabyStage = stageKey === "baby";
  const femaleStageImageSrc: Record<string, string> = {
    baby: "/characters/female/baby-spirit.png",
    teen: "/characters/female/growing-spirit.png?v=transparent-20260622",
    adult: "/characters/female/mature-spirit.png",
    radiant: "/characters/female/radiant-spirit.png",
  };
  const maleStageImageSrc: Record<string, string> = {
    baby: "/characters/male/baby-spirit.png?v=green-20260617",
    teen: "/characters/male/growing-spirit.png?v=transparent-20260622",
    adult: "/characters/male/mature-spirit.png?v=transparent-20260622",
    radiant: "/characters/male/radiant-spirit.png",
  };
  const stageImageSrc = isFemale
    ? femaleStageImageSrc[stageKey]
    : maleStageImageSrc[stageKey];
  const growingSpiritScale = stageKey === "teen" ? 0.9 : 1;
  const renderedAvatarSize = stageImageSrc ? size * 1.18 * growingSpiritScale : size;
  const getSlot = (slot: Slot) => equipped.find((c) => c.slot === slot);
  const setItem = getSlot("set"),
    robeItem = getSlot("robe"),
    crownItem = getSlot("crown"),
    hatItem = getSlot("hat"),
    wingsItem = getSlot("wings"),
    haloItem = getSlot("halo"),
    handItem = getSlot("hand"),
    shieldItem = getSlot("shield"),
    feetItem = getSlot("feet"),
    beltItem = getSlot("belt"),
    sashItem = getSlot("sash");
  const isArmorSet = setItem?.id === "set_armor_god";
  const bodyColor =
    setItem?.bodyColor ||
    robeItem?.bodyColor ||
    (isFemale ? "#d7b6ff" : "#8fd3ff");
  const shoeColor = setItem?.feetColor || feetItem?.feetColor || "#a8956e";
  const wingColor = setItem?.wingColor || wingsItem?.wingColor;
  const haloColor = setItem?.haloColor || haloItem?.haloColor;
  const haloY = stageImageSrc ? (isBabyStage ? 27 : stageKey === "teen" ? 18 : 25) : 34;
  const haloRx = stageImageSrc ? 17.5 : 18;
  const haloRy = stageImageSrc ? 4.7 : 5.5;
  const crownColor = setItem?.crownColor || crownItem?.crownColor;
  const armorBeltColor = setItem?.beltColor || beltItem?.beltColor;
  const helmetColor = setItem?.crownColor || hatItem?.hatColor;
  const shieldColor =
    shieldItem?.handColor || (isArmorSet ? "#5d9cec" : undefined);
  const swordColor =
    handItem?.handColor || (isArmorSet ? "#8b7cf6" : undefined);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <svg
        className={`spirit-avatar-svg spirit-${gender} spirit-stage-${stageKey}`}
        width={renderedAvatarSize}
        height={renderedAvatarSize * 1.15}
        viewBox="0 0 120 138"
        style={{ overflow: "visible" }}
      >
        <defs>
          <radialGradient id={auraId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={avatarGlow} stopOpacity="0.4" />
            <stop offset="100%" stopColor={avatarGlow} stopOpacity="0" />
          </radialGradient>
          <radialGradient id={skinGradId} cx="38%" cy="28%" r="78%">
            <stop offset="0%" stopColor="#fff7ed" stopOpacity="0.9" />
            <stop offset="42%" stopColor={skinTone} stopOpacity="1" />
            <stop offset="100%" stopColor="#b8754a" stopOpacity="0.32" />
          </radialGradient>
          <linearGradient id={bodyGradId} x1="28%" y1="8%" x2="78%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.64" />
            <stop offset="24%" stopColor={spiritAccent.secondary} stopOpacity="0.58" />
            <stop offset="58%" stopColor={bodyColor} stopOpacity="0.98" />
            <stop offset="100%" stopColor={spiritAccent.primary} stopOpacity="0.74" />
          </linearGradient>
          <linearGradient id={hairGradId} x1="20%" y1="10%" x2="82%" y2="100%">
            <stop offset="0%" stopColor="#fff7ed" stopOpacity="0.28" />
            <stop offset="32%" stopColor={hairColor} stopOpacity="0.96" />
            <stop offset="100%" stopColor="#1f1309" stopOpacity="0.52" />
          </linearGradient>
          <linearGradient id={swordBladeGradId} x1="32%" y1="4%" x2="76%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.96" />
            <stop offset="24%" stopColor="#dbeafe" stopOpacity="0.9" />
            <stop offset="52%" stopColor="#a5b4fc" stopOpacity="0.82" />
            <stop offset="78%" stopColor="#8b5cf6" stopOpacity="0.72" />
            <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.78" />
          </linearGradient>
          <linearGradient id={swordHiltGradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fff8df" stopOpacity="1" />
            <stop offset="42%" stopColor="#f0cb76" stopOpacity="0.98" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.76" />
          </linearGradient>
          <radialGradient id={swordGemGradId} cx="36%" cy="28%" r="70%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="38%" stopColor="#bae6fd" stopOpacity="0.95" />
            <stop offset="72%" stopColor="#a78bfa" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.82" />
          </radialGradient>
          <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="2.5"
              result="blur"
            />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <ellipse
          cx="60"
          cy="70"
          rx="54"
          ry="58"
          fill={`url(#${auraId})`}
          style={{
            animation: animated ? "av_pulse 3s ease-in-out infinite" : "none",
          }}
        />
        {wingColor &&
          stageKey !== "seed" &&
          (() => {
            const isFireSet =
              setItem?.id === "set_seraph" || wingsItem?.id === "wings_fire";
            if (isFireSet)
              return (
                <g opacity="0.88" filter={`url(#${glowId})`}>
                  <path
                    d="M44 92 Q10 55 15 22 Q30 50 44 78Z"
                    fill="#ef4444"
                    opacity="0.75"
                  />
                  <path
                    d="M44 92 Q18 65 22 35 Q34 58 44 78Z"
                    fill="#fb923c"
                    opacity="0.65"
                  />
                  <path
                    d="M76 92 Q110 55 105 22 Q90 50 76 78Z"
                    fill="#ef4444"
                    opacity="0.75"
                  />
                  <path
                    d="M76 92 Q102 65 98 35 Q86 58 76 78Z"
                    fill="#fb923c"
                    opacity="0.65"
                  />
                </g>
              );
            return (
              <g opacity="0.82">
                <path
                  d={`M44 90 Q16 60 20 32 Q34 56 44 78Z`}
                  fill={wingColor}
                  opacity="0.75"
                />
                <path
                  d={`M76 90 Q104 60 100 32 Q86 56 76 78Z`}
                  fill={wingColor}
                  opacity="0.75"
                />
                {(wingsItem?.id === "wings_angel" ||
                  setItem?.id === "set_angelic") && (
                  <>
                    <path
                      d={`M44 78 Q10 55 16 28 Q30 52 44 70Z`}
                      fill={wingColor}
                      opacity="0.45"
                    />
                    <path
                      d={`M76 78 Q110 55 104 28 Q90 52 76 70Z`}
                      fill={wingColor}
                      opacity="0.45"
                    />
                  </>
                )}
                {wingsItem?.id === "wings_rose" && (
                  <>
                    <path
                      d={`M42 88 Q14 58 18 30 Q32 54 42 76Z`}
                      fill="#fda4af"
                      opacity="0.35"
                    />
                    <path
                      d={`M78 88 Q106 58 102 30 Q88 54 78 76Z`}
                      fill="#fda4af"
                      opacity="0.35"
                    />
                  </>
                )}
              </g>
            );
          })()}
        {stageImageSrc ? (
          <g>
            {!isFemale && (
              <ellipse
                cx="60"
                cy="69"
                rx={stageKey === "baby" ? "47" : isLeafStage ? "58" : "52"}
                ry={stageKey === "baby" ? "51" : isLeafStage ? "64" : "58"}
                fill="url(#auraId)"
                opacity={isLeafStage ? "0.62" : "0.46"}
              />
            )}
            <g className={`spirit-stage-image-wrap spirit-stage-image-${stageKey} ${isFemale ? "female" : "male"}`}>
              <image
                href={stageImageSrc}
                x={stageKey === "baby" ? "-8" : "-5"}
                y={stageKey === "baby" ? "-2" : "-9"}
                width={stageKey === "baby" ? "136" : "130"}
                height={stageKey === "baby" ? "142" : "154"}
                preserveAspectRatio="xMidYMid meet"
              />
            </g>
            {swordColor && stageKey !== "seed" && (
              <g className="spirit-front-sword" opacity="0.96" filter={`url(#${glowId})`}>
                <path
                  d="M101 48 Q107 55 105 62 L88 106 Q86 110 83 107 L80 104 Q78 101 81 98 L97 56 Q99 51 101 48Z"
                  fill={`url(#${swordBladeGradId})`}
                  opacity="0.82"
                />
                <path
                  d="M101 52 L102 61 L86 103 L83 101Z"
                  fill="#ffffff"
                  opacity="0.32"
                />
                <path
                  d="M98.5 57 L85 100"
                  stroke="#ffffff"
                  strokeWidth="1.35"
                  strokeLinecap="round"
                  opacity="0.84"
                />
                <path
                  d="M104 61 L91 103"
                  stroke="#6366f1"
                  strokeWidth="1"
                  strokeLinecap="round"
                  opacity="0.36"
                />
                <path
                  d="M102 46 L108 61 L96 59Z"
                  fill="#f8fafc"
                  opacity="0.94"
                />
                <path
                  d="M81 101 Q75 100 71 104 Q78 108 84 107Z"
                  fill={`url(#${swordHiltGradId})`}
                  opacity="0.94"
                />
                <path
                  d="M85 106 Q91 107 94 113 Q86 114 82 109Z"
                  fill={`url(#${swordHiltGradId})`}
                  opacity="0.9"
                />
                <circle
                  cx="83"
                  cy="106"
                  r="3.2"
                  fill={`url(#${swordGemGradId})`}
                  opacity="0.98"
                />
                <circle cx="81.8" cy="104.8" r=".9" fill="#fff" opacity="0.9" />
                <path
                  d="M78 107 L88 118"
                  stroke={`url(#${swordHiltGradId})`}
                  strokeWidth="3.6"
                  strokeLinecap="round"
                  opacity="0.9"
                />
                <path
                  d="M76 112 L82 118"
                  stroke="#fff8df"
                  strokeWidth="2.3"
                  strokeLinecap="round"
                  opacity="0.88"
                />
                <path
                  d="M94 56 l1.6 3 l3 1.6 l-3 1.6 l-1.6 3 l-1.6 -3 l-3 -1.6 l3 -1.6Z"
                  fill="#ffffff"
                  opacity="0.72"
                />
                <path
                  d="M91 82 l1.1 2.2 l2.2 1.1 l-2.2 1.1 l-1.1 2.2 l-1.1 -2.2 l-2.2 -1.1 l2.2 -1.1Z"
                  fill="#dbeafe"
                  opacity="0.62"
                />
                <circle cx="106" cy="70" r="1.2" fill="#fff8df" opacity="0.72" />
                <circle cx="96" cy="102" r="1" fill="#c4b5fd" opacity="0.62" />
              </g>
            )}
          </g>
        ) : stageKey === "seed" ? (
          <g>
            <ellipse
              cx="60"
              cy="100"
              rx="16"
              ry="12"
              fill={stageAura}
              opacity="0.62"
            />
            <path
              d="M60 74 Q53 84 60 91 Q67 84 60 74Z"
              fill={stageGlow}
              opacity="0.92"
            />
            <ellipse
              cx="55"
              cy="80"
              rx="4"
              ry="6"
              fill={stageAura}
              opacity="0.86"
              transform="rotate(-28 55 80)"
            />
            <ellipse
              cx="65"
              cy="80"
              rx="4"
              ry="6"
              fill={stageAura}
              opacity="0.86"
              transform="rotate(28 65 80)"
            />
            <circle cx="60" cy="99" r="4" fill={stageGlow} opacity="0.9" />
            <circle cx="56" cy="87" r="1.2" fill="#3d2800" opacity="0.45" />
            <circle cx="64" cy="87" r="1.2" fill="#3d2800" opacity="0.45" />
            <path
              d="M57 91 Q60 94 63 91"
              stroke="#3d2800"
              strokeWidth="1"
              fill="none"
              strokeLinecap="round"
              opacity="0.35"
            />
            <g className="spirit-live-face seed-face">
              <ellipse cx="56" cy="87" rx="2" ry="1.5" fill={stageAura} />
              <ellipse cx="64" cy="87" rx="2" ry="1.5" fill={stageAura} />
              <path
                className="spirit-smile-variant"
                d="M56 91 Q60 96 64 91"
                stroke="#3d2800"
                strokeWidth="1"
                fill="none"
                strokeLinecap="round"
              />
            </g>
          </g>
        ) : stageKey === "baby" ? (
          <g>
            <ellipse cx="60" cy="126" rx="27" ry="7" fill={stageGlow} opacity="0.16" filter={`url(#${glowId})`} />
            <ellipse cx="60" cy="74" rx="45" ry="51" fill={stageAura} opacity="0.18" />
            <circle cx="60" cy="72" r="39" fill="none" stroke="#fff8df" strokeWidth="1.1" opacity="0.38" />
            <circle cx="60" cy="72" r="31" fill="none" stroke={stageGlow} strokeWidth="1" opacity="0.2" />
            <circle cx="60" cy="73" r="36" fill={stageGlow} opacity="0.1" filter={`url(#${glowId})`} />
            <g className="baby-spirit-magic" opacity="0.78" filter={`url(#${glowId})`}>
              <path d="M24 70 l2 4 l4 2 l-4 2 l-2 4 l-2 -4 l-4 -2 l4 -2Z" fill="#fff8df" opacity="0.72" />
              <path d="M94 66 l1.8 3.4 l3.4 1.8 l-3.4 1.8 l-1.8 3.4 l-1.8 -3.4 l-3.4 -1.8 l3.4 -1.8Z" fill="#fff8df" opacity="0.66" />
              <path d="M80 42 l1.4 2.7 l2.7 1.4 l-2.7 1.4 l-1.4 2.7 l-1.4 -2.7 l-2.7 -1.4 l2.7 -1.4Z" fill="#fff" opacity="0.74" />
              <path d="M29 101 Q27 98 24.5 100 Q22 103 29 108 Q36 103 33.5 100 Q31 98 29 101Z" fill="#f0abfc" opacity="0.42" />
              <path d="M91 96 Q89 93 86.5 95 Q84 98 91 103 Q98 98 95.5 95 Q93 93 91 96Z" fill="#f0abfc" opacity="0.38" />
              <g opacity="0.62">
                <ellipse cx="31" cy="116" rx="3.2" ry="2.1" fill="#f9a8d4" transform="rotate(28 31 116)" />
                <ellipse cx="27.5" cy="113.5" rx="3.2" ry="2.1" fill="#fbcfe8" transform="rotate(-28 27.5 113.5)" />
                <circle cx="29.4" cy="114.7" r="1.4" fill="#fef3c7" />
              </g>
              <g opacity="0.58">
                <ellipse cx="89" cy="117" rx="3" ry="2" fill="#f9a8d4" transform="rotate(-24 89 117)" />
                <ellipse cx="92.5" cy="114.8" rx="3" ry="2" fill="#fbcfe8" transform="rotate(24 92.5 114.8)" />
                <circle cx="90.8" cy="115.8" r="1.25" fill="#fef3c7" />
              </g>
              <circle cx="27" cy="88" r="1.5" fill="#fff" opacity="0.75" />
              <circle cx="92" cy="87" r="1.4" fill="#fff" opacity="0.7" />
              <circle cx="82" cy="119" r="1.3" fill="#fef3c7" opacity="0.78" />
              <circle cx="36" cy="122" r="1.3" fill="#fef3c7" opacity="0.72" />
            </g>
            <path
              d="M36 86 Q41 73 52 70 Q60 76 68 70 Q79 73 84 86 Q90 109 74 126 Q60 136 46 126 Q30 109 36 86Z"
              fill={`url(#${bodyGradId})`}
              opacity="0.96"
            />
            <path
              d="M41 86 Q45 77 53 75 Q60 79 67 75 Q75 77 79 86 Q73 85 68 87 Q60 90 52 87 Q47 85 41 86Z"
              fill="#fff"
              opacity="0.2"
            />
            <path
              d="M43 83 Q47 100 42 118"
              stroke="#fff8df"
              strokeWidth="2.4"
              strokeLinecap="round"
              opacity="0.22"
              fill="none"
            />
            <path
              d="M77 83 Q73 100 78 118"
              stroke="#fff8df"
              strokeWidth="2.4"
              strokeLinecap="round"
              opacity="0.22"
              fill="none"
            />
            <ellipse cx="39.5" cy="98" rx="5.2" ry="10.4" fill={`url(#${bodyGradId})`} opacity="0.84" transform="rotate(8 39.5 98)" />
            <ellipse cx="80.5" cy="98" rx="5.2" ry="10.4" fill={`url(#${bodyGradId})`} opacity="0.84" transform="rotate(-8 80.5 98)" />
            <ellipse cx="38.8" cy="106" rx="3.3" ry="3.9" fill={`url(#${skinGradId})`} opacity="0.92" />
            <ellipse cx="81.2" cy="106" rx="3.3" ry="3.9" fill={`url(#${skinGradId})`} opacity="0.92" />
            <path
              d="M44 91 Q49 82 56 84 Q60 90 64 84 Q71 82 76 91 Q75 109 60 116 Q45 109 44 91Z"
              fill="#fffaf0"
              opacity="0.86"
            />
            <path
              d="M48 94 Q60 111 72 94"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              opacity="0.38"
              fill="none"
            />
            <ellipse
              cx="60"
              cy="107"
              rx="10.5"
              ry="8.5"
              fill="#fff7ed"
              opacity="0.22"
              filter={`url(#${glowId})`}
            />
            <ellipse cx="49" cy="127" rx="7.2" ry="4.4" fill={shoeColor} opacity="0.62" />
            <ellipse cx="71" cy="127" rx="7.2" ry="4.4" fill={shoeColor} opacity="0.62" />
            <path
              d="M60 96.8 L63.2 104.2 L70.6 107.4 L63.2 110.6 L60 118 L56.8 110.6 L49.4 107.4 L56.8 104.2Z"
              fill={isFemale ? "#f472b6" : spiritAccent.primary}
              opacity="0.94"
              filter={`url(#${glowId})`}
            />
            <path
              d="M47 123 Q60 131 73 123"
              stroke="rgba(82,55,15,.16)"
              strokeWidth="3"
              strokeLinecap="round"
              opacity="0.28"
            />
            <g className="baby-spirit-sprout" filter={`url(#${glowId})`}>
              <path
                d="M60 24 Q60 31 60 37"
                stroke="#6b8f2a"
                strokeWidth="2.5"
                strokeLinecap="round"
                opacity="0.88"
              />
              <ellipse
                cx="54.2"
                cy="25.5"
                rx="8"
                ry="4.8"
                fill="#a7d85d"
                opacity="0.94"
                transform="rotate(25 54.2 25.5)"
              />
              <ellipse
                cx="65.8"
                cy="25.5"
                rx="8"
                ry="4.8"
                fill="#91c848"
                opacity="0.94"
                transform="rotate(-25 65.8 25.5)"
              />
              <path d="M51 24 Q55 21 60 24" stroke="#f4ffd5" strokeWidth="1.1" strokeLinecap="round" opacity="0.45" />
              <path d="M61 24 Q66 21 70 24" stroke="#f4ffd5" strokeWidth="1.1" strokeLinecap="round" opacity="0.45" />
            </g>
            <circle cx="60" cy="58.5" r="23.2" fill={`url(#${skinGradId})`} opacity="0.99" />
            <ellipse cx="51" cy="62.5" rx="8.2" ry="7.4" fill="#fda4af" opacity={isFemale ? "0.32" : "0.21"} />
            <ellipse cx="69" cy="62.5" rx="8.2" ry="7.4" fill="#fda4af" opacity={isFemale ? "0.32" : "0.21"} />
            <ellipse cx="51" cy="64" rx="1.1" ry=".6" fill="#fff" opacity="0.58" />
            <ellipse cx="69" cy="64" rx="1.1" ry=".6" fill="#fff" opacity="0.58" />
            <path
              d="M46 49 Q50 36 59 34 Q69 34 75 46 Q68 40 60 41 Q52 41 46 49Z"
              fill={`url(#${hairGradId})`}
              opacity="0.83"
            />
            <path
              d="M50 41 Q56 35 66 37 Q63 45 56 47 Q51 48 48 52 Q47 47 50 41Z"
              fill={`url(#${hairGradId})`}
              opacity="0.95"
            />
            <path
              d="M50 40 Q56 35 66 37"
              stroke="#fff7ed"
              strokeWidth="1.5"
              strokeLinecap="round"
              opacity="0.27"
            />
            <g className="baby-spirit-face">
              <path d="M47.8 49.4 Q52.2 46.1 56.8 48.5" stroke="#5a301d" strokeWidth="1.35" fill="none" strokeLinecap="round" opacity="0.64" />
              <path d="M63.2 48.5 Q67.8 46.1 72.2 49.4" stroke="#5a301d" strokeWidth="1.35" fill="none" strokeLinecap="round" opacity="0.64" />
              <ellipse cx="52.7" cy="58" rx="4.9" ry="6.55" fill="#3d2800" opacity="0.92" />
              <ellipse cx="67.3" cy="58" rx="4.9" ry="6.55" fill="#3d2800" opacity="0.92" />
              <path d="M48.3 57.2 Q46.8 56.8 45.9 55.5" stroke="#3d2800" strokeWidth="1.05" fill="none" strokeLinecap="round" opacity="0.7" />
              <path d="M71.7 57.2 Q73.2 56.8 74.1 55.5" stroke="#3d2800" strokeWidth="1.05" fill="none" strokeLinecap="round" opacity="0.7" />
              <ellipse cx="52.7" cy="59.8" rx="2.65" ry="3.55" fill="#7a4a28" opacity="0.5" />
              <ellipse cx="67.3" cy="59.8" rx="2.65" ry="3.55" fill="#7a4a28" opacity="0.5" />
              <circle cx="51.2" cy="55.2" r="1.65" fill="#fff" opacity="0.96" />
              <circle cx="65.8" cy="55.2" r="1.65" fill="#fff" opacity="0.96" />
              <circle cx="54.9" cy="61.9" r=".9" fill="#fff8df" opacity="0.64" />
              <circle cx="69.5" cy="61.9" r=".9" fill="#fff8df" opacity="0.64" />
              <circle cx="50.7" cy="60.3" r=".62" fill="#fff" opacity="0.48" />
              <circle cx="65.3" cy="60.3" r=".62" fill="#fff" opacity="0.48" />
              <path
                d="M55 67.2 Q60 70.9 65 67.2"
                stroke="#5a301d"
                strokeWidth="1.38"
                fill="none"
                strokeLinecap="round"
                opacity="0.66"
              />
            </g>
          </g>
        ) : stageKey === "teen" && isFemale ? (
          <g>
            <ellipse cx="60" cy="128" rx="27" ry="6.5" fill="#a78bfa" opacity="0.13" filter={`url(#${glowId})`} />
            <ellipse cx="60" cy="70" rx="45" ry="59" fill="#fde7f4" opacity="0.16" />
            <circle cx="60" cy="66" r="41" fill="#fbcfe8" opacity="0.13" filter={`url(#${glowId})`} />
            <circle cx="60" cy="66" r="39" fill="none" stroke="#fbcfe8" strokeWidth="1.2" opacity="0.32" />
            <circle cx="60" cy="66" r="29" fill="none" stroke="#f9a8d4" strokeWidth=".9" opacity="0.2" />
            <g filter={`url(#${glowId})`} opacity="0.58">
              <ellipse cx="27" cy="90" rx="3.5" ry="2.1" fill="#fbcfe8" transform="rotate(-24 27 90)" />
              <ellipse cx="91" cy="84" rx="3.2" ry="1.9" fill="#f9a8d4" transform="rotate(28 91 84)" />
              <path d="M24 108 l1.5 3 l3 1.5 l-3 1.5 l-1.5 3 l-1.5 -3 l-3 -1.5 l3 -1.5Z" fill="#fff8df" opacity="0.62" />
              <path d="M94 53 l1.4 2.8 l2.8 1.4 l-2.8 1.4 l-1.4 2.8 l-1.4 -2.8 l-2.8 -1.4 l2.8 -1.4Z" fill="#fff8df" opacity="0.58" />
              <g opacity="0.72">
                <ellipse cx="28" cy="116" rx="3.2" ry="2.1" fill="#f9a8d4" transform="rotate(26 28 116)" />
                <ellipse cx="24.6" cy="113.7" rx="3.2" ry="2.1" fill="#fbcfe8" transform="rotate(-26 24.6 113.7)" />
                <circle cx="26.3" cy="114.8" r="1.3" fill="#fef3c7" />
              </g>
              <g opacity="0.64">
                <ellipse cx="94" cy="108" rx="3" ry="2" fill="#f9a8d4" transform="rotate(-24 94 108)" />
                <ellipse cx="97.3" cy="105.8" rx="3" ry="2" fill="#fbcfe8" transform="rotate(24 97.3 105.8)" />
                <circle cx="95.7" cy="106.8" r="1.2" fill="#fef3c7" />
              </g>
            </g>
            <path
              d="M37 62 Q34 38 48 27 Q58 17 72 25 Q86 35 83 62 Q83 83 78 101 Q74 114 78 121 Q67 113 69 93 Q70 81 70 74 Q62 83 55 78 Q51 88 47 102 Q43 114 46 121 Q35 110 39 93 Q37 78 37 62Z"
              fill={`url(#${hairGradId})`}
              opacity="0.82"
            />
            <path
              d="M42 62 Q36 78 39 96 Q42 112 38 121 Q50 110 49 94 Q48 84 50 75"
              fill={`url(#${hairGradId})`}
              opacity="0.62"
            />
            <path
              d="M78 62 Q84 78 81 96 Q78 112 82 121 Q70 110 71 94 Q72 84 70 75"
              fill={`url(#${hairGradId})`}
              opacity="0.62"
            />
            <path
              d="M47 39 Q55 27 70 32"
              stroke="#fff7ed"
              strokeWidth="2"
              strokeLinecap="round"
              opacity="0.3"
            />
            <path
              d="M46 48 Q53 36 65 34 Q73 34 78 41 Q69 40 61 45 Q54 49 46 54Z"
              fill="#fff7ed"
              opacity="0.22"
            />
            <path
              d="M54.5 66 Q60 70 65.5 66 L65 78 Q60 82 55 78Z"
              fill={`url(#${skinGradId})`}
              opacity="0.88"
            />
            <circle cx="60" cy="51" r="21" fill={`url(#${skinGradId})`} opacity="0.99" />
            <path
              d="M40 51 Q41.8 33 53 27 Q62 21 73 28 Q82 36 80.5 52 Q72 42 62 42 Q52 42 40 51Z"
              fill={`url(#${hairGradId})`}
              opacity="0.98"
            />
            <path
              d="M44.5 45 Q54 33 66.5 32 Q74 33 79 40 Q69 39 61 44 Q55 50 44.5 55Z"
              fill={`url(#${hairGradId})`}
              opacity="0.98"
            />
            <path
              d="M43 65 Q36 75 42 84 Q47 91 40 101"
              stroke={hairColor}
              strokeWidth="3.2"
              strokeLinecap="round"
              opacity="0.34"
              fill="none"
            />
            <path
              d="M77 65 Q84 75 78 84 Q73 91 80 101"
              stroke={hairColor}
              strokeWidth="3.2"
              strokeLinecap="round"
              opacity="0.34"
              fill="none"
            />
            <path
              d="M47 72 Q40 82 46 91 Q52 99 44 110"
              stroke="#fff7ed"
              strokeWidth="1.35"
              strokeLinecap="round"
              opacity="0.22"
              fill="none"
            />
            <path
              d="M73 72 Q80 82 74 91 Q68 99 76 110"
              stroke="#fff7ed"
              strokeWidth="1.35"
              strokeLinecap="round"
              opacity="0.22"
              fill="none"
            />
            <path
              d="M49 39 Q56 28 70 33"
              stroke="#fff7ed"
              strokeWidth="1.9"
              strokeLinecap="round"
              opacity="0.32"
            />
            <path
              d="M52 42 Q58 37 65 38"
              stroke="#fff7ed"
              strokeWidth="1.2"
              strokeLinecap="round"
              opacity="0.24"
            />
            <path
              d="M44 79 Q49 70 60 70 Q71 70 76 79 Q70 85 60 86 Q50 85 44 79Z"
              fill={`url(#${bodyGradId})`}
              opacity="0.98"
            />
            <path
              d="M42 82 Q49 75 60 77 Q71 75 78 82 Q84 102 77 125 Q67 136 60 134 Q53 136 43 125 Q36 102 42 82Z"
              fill={`url(#${bodyGradId})`}
              opacity="0.97"
            />
            <path
              d="M41 99 Q49 119 60 127 Q71 119 79 99 Q77 118 71 128 Q60 136 49 128 Q43 118 41 99Z"
              fill="#e9c8ff"
              opacity="0.5"
            />
            <path
              d="M43 84 Q51 101 60 106 Q69 101 77 84"
              stroke="#fff8df"
              strokeWidth="2.6"
              strokeLinecap="round"
              opacity="0.46"
              fill="none"
            />
            <path
              d="M45 98 Q53 112 60 116 Q67 112 75 98"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              opacity="0.26"
              fill="none"
            />
            <path
              d="M48 84 Q54 78 60 80 Q66 78 72 84 L69 99 Q60 108 51 99Z"
              fill="#fffaf0"
              opacity="0.84"
            />
            <path
              d="M48 86 Q60 80 72 86 L69 97 Q60 105 51 97Z"
              fill="#7e4a98"
              opacity="0.58"
              filter={`url(#${glowId})`}
            />
            <path
              d="M52 87 Q60 83 68 87 L66 94 Q60 100 54 94Z"
              fill="#fff"
              opacity="0.18"
            />
            <path
              d="M45 80 Q36 86 34 99 Q37 105 42 104 Q43 92 49 84Z"
              fill={`url(#${bodyGradId})`}
              opacity="0.92"
            />
            <path
              d="M75 80 Q84 86 86 99 Q83 105 78 104 Q77 92 71 84Z"
              fill={`url(#${bodyGradId})`}
              opacity="0.92"
            />
            <path
              d="M38 99 Q40 109 45 113"
              stroke="#fff"
              strokeWidth="2.2"
              strokeLinecap="round"
              opacity="0.24"
              fill="none"
            />
            <path
              d="M82 99 Q80 109 75 113"
              stroke="#fff"
              strokeWidth="2.2"
              strokeLinecap="round"
              opacity="0.24"
              fill="none"
            />
            <ellipse cx="37.5" cy="104" rx="3.7" ry="4.5" fill={`url(#${skinGradId})`} opacity="0.95" />
            <ellipse cx="82.5" cy="104" rx="3.7" ry="4.5" fill={`url(#${skinGradId})`} opacity="0.95" />
            <ellipse cx="50" cy="128" rx="7.5" ry="4.6" fill={shoeColor} opacity="0.72" />
            <ellipse cx="70" cy="128" rx="7.5" ry="4.6" fill={shoeColor} opacity="0.72" />
            <path
              d="M43 63 Q39 81 43 94 Q47 107 42 116"
              stroke={hairColor}
              strokeWidth="4.2"
              strokeLinecap="round"
              opacity="0.32"
              fill="none"
            />
            <path
              d="M77 63 Q81 81 77 94 Q73 107 78 116"
              stroke={hairColor}
              strokeWidth="4.2"
              strokeLinecap="round"
              opacity="0.32"
              fill="none"
            />
            <g filter={`url(#${glowId})`} opacity="0.9">
              <ellipse cx="77.5" cy="40" rx="4.3" ry="3" fill="#f9a8d4" transform="rotate(24 77.5 40)" />
              <ellipse cx="72.3" cy="36.2" rx="4.3" ry="3" fill="#fbcfe8" transform="rotate(-24 72.3 36.2)" />
              <ellipse cx="75.5" cy="34.2" rx="3.7" ry="2.5" fill="#fda4af" transform="rotate(86 75.5 34.2)" />
              <ellipse cx="72.1" cy="41.6" rx="3.5" ry="2.4" fill="#f9a8d4" transform="rotate(-72 72.1 41.6)" />
              <circle cx="75.1" cy="38.3" r="2" fill="#fff3c4" />
            </g>
            <ellipse cx="52" cy="56.2" rx="4.45" ry="5.15" fill="#3d2800" opacity="0.92" />
            <ellipse cx="68" cy="56.2" rx="4.45" ry="5.15" fill="#3d2800" opacity="0.92" />
            <ellipse cx="52" cy="57.5" rx="2.55" ry="3" fill="#7a4a28" opacity="0.46" />
            <ellipse cx="68" cy="57.5" rx="2.55" ry="3" fill="#7a4a28" opacity="0.46" />
            <circle cx="50.7" cy="53.7" r="1.42" fill="#fff" opacity="0.96" />
            <circle cx="66.7" cy="53.7" r="1.42" fill="#fff" opacity="0.96" />
            <circle cx="53.8" cy="60.2" r=".68" fill="#fff8df" opacity="0.62" />
            <circle cx="69.8" cy="60.2" r=".68" fill="#fff8df" opacity="0.62" />
            <path d="M47.2 48.4 Q51.5 45.6 55.7 48" stroke="#6b3a22" strokeWidth="1.35" fill="none" strokeLinecap="round" opacity="0.58" />
            <path d="M64.3 48 Q68.5 45.6 72.8 48.4" stroke="#6b3a22" strokeWidth="1.35" fill="none" strokeLinecap="round" opacity="0.58" />
            <path d="M47.2 55.2 Q45.8 54.6 45 53.3" stroke="#3d2800" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.58" />
            <path d="M72.8 55.2 Q74.2 54.6 75 53.3" stroke="#3d2800" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.58" />
            <ellipse cx="50" cy="63" rx="5" ry="3.1" fill="#f9a8d4" opacity="0.22" />
            <ellipse cx="70" cy="63" rx="5" ry="3.1" fill="#f9a8d4" opacity="0.22" />
            <path
              d="M55 65 Q60 68.7 65 65"
              stroke="#5a301d"
              strokeWidth="1.45"
              fill="none"
              strokeLinecap="round"
              opacity="0.64"
            />
            <g className="spirit-live-face">
              <ellipse cx="52" cy="56.2" rx="5" ry="3.35" fill={`url(#${skinGradId})`} />
              <ellipse cx="68" cy="56.2" rx="5" ry="3.35" fill={`url(#${skinGradId})`} />
              <path
                className="spirit-smile-variant"
                d="M55 65 Q60 69.5 65 65"
                stroke="#5a301d"
                strokeWidth="1.35"
                fill="none"
                strokeLinecap="round"
              />
            </g>
          </g>
        ) : stageKey === "radiant" && isFemale ? (
          <g>
            <ellipse cx="60" cy="129" rx="34" ry="8" fill="#f9a8d4" opacity="0.18" filter={`url(#${glowId})`} />
            <ellipse cx="60" cy="68" rx="52" ry="66" fill="#fff7ed" opacity="0.18" />
            <circle cx="60" cy="62" r="48" fill="#fbcfe8" opacity="0.17" filter={`url(#${glowId})`} />
            <circle cx="60" cy="62" r="46" fill="none" stroke="#fde68a" strokeWidth="1.55" opacity="0.42" />
            <circle cx="60" cy="62" r="36" fill="none" stroke="#f9a8d4" strokeWidth="1.05" opacity="0.34" />
            <circle cx="60" cy="62" r="25" fill="none" stroke="#fff8df" strokeWidth=".75" opacity="0.2" />
            <g filter={`url(#${glowId})`} opacity="0.7">
              <path d="M60 8 l2.4 5 l5 2.4 l-5 2.4 l-2.4 5 l-2.4 -5 l-5 -2.4 l5 -2.4Z" fill="#fff8df" opacity="0.82" />
              <path d="M21 91 l1.8 3.6 l3.6 1.8 l-3.6 1.8 l-1.8 3.6 l-1.8 -3.6 l-3.6 -1.8 l3.6 -1.8Z" fill="#fff8df" opacity="0.65" />
              <path d="M97 79 l1.6 3.2 l3.2 1.6 l-3.2 1.6 l-1.6 3.2 l-1.6 -3.2 l-3.2 -1.6 l3.2 -1.6Z" fill="#fff8df" opacity="0.62" />
              <ellipse cx="19" cy="108" rx="3.6" ry="2.2" fill="#f9a8d4" transform="rotate(-25 19 108)" />
              <ellipse cx="101" cy="104" rx="3.5" ry="2.1" fill="#c084fc" transform="rotate(25 101 104)" />
              <ellipse cx="30" cy="46" rx="3.3" ry="2" fill="#f9a8d4" transform="rotate(25 30 46)" />
              <ellipse cx="92" cy="44" rx="3.3" ry="2" fill="#c084fc" transform="rotate(-25 92 44)" />
            </g>
            <path
              d="M26 63 Q27 31 46 19 Q60 6 79 21 Q95 34 94 64 Q94 92 84 132 Q70 114 72 79 Q63 92 52 80 Q49 114 36 134 Q25 98 26 63Z"
              fill={`url(#${hairGradId})`}
              opacity="0.84"
            />
            <path
              d="M37 56 Q24 82 27 130 Q42 119 45 96 Q48 78 49 69"
              fill={`url(#${hairGradId})`}
              opacity="0.7"
            />
            <path
              d="M83 56 Q96 82 93 130 Q78 119 75 96 Q72 78 71 69"
              fill={`url(#${hairGradId})`}
              opacity="0.7"
            />
            <path
              d="M42 59 Q29 78 36 96 Q44 115 32 132"
              stroke={hairColor}
              strokeWidth="4.6"
              strokeLinecap="round"
              opacity="0.32"
              fill="none"
            />
            <path
              d="M78 59 Q91 78 84 96 Q76 115 88 132"
              stroke={hairColor}
              strokeWidth="4.6"
              strokeLinecap="round"
              opacity="0.32"
              fill="none"
            />
            <path
              d="M47 63 Q35 82 42 100 Q47 114 39 128"
              stroke="#fff7ed"
              strokeWidth="1.55"
              strokeLinecap="round"
              opacity="0.28"
              fill="none"
            />
            <path
              d="M73 63 Q85 82 78 100 Q73 114 81 128"
              stroke="#fff7ed"
              strokeWidth="1.55"
              strokeLinecap="round"
              opacity="0.28"
              fill="none"
            />
            <path d="M34 72 Q30 91 36 110 Q39 121 35 130" stroke="#fff7ed" strokeWidth="1.15" strokeLinecap="round" opacity="0.2" fill="none" />
            <path d="M86 72 Q90 91 84 110 Q81 121 85 130" stroke="#fff7ed" strokeWidth="1.15" strokeLinecap="round" opacity="0.2" fill="none" />
            <path
              d="M55 65 Q60 69 65 65 L65 76 Q60 81 55 76Z"
              fill={`url(#${skinGradId})`}
              opacity="0.9"
            />
            <path
              d="M38 80 Q48 65 60 65 Q72 65 82 80 Q76 91 60 92 Q44 91 38 80Z"
              fill="#fff7ed"
              opacity="0.82"
            />
            <path
              d="M41 79 Q49 68 60 69 Q71 68 79 79 Q76 94 68 103 Q60 108 52 103 Q44 94 41 79Z"
              fill={`url(#${bodyGradId})`}
              opacity="0.98"
            />
            <path
              d="M32 96 Q40 88 49 90 Q50 115 37 135 Q25 132 17 125 Q23 110 32 96Z"
              fill="#fff7ed"
              opacity="0.42"
            />
            <path
              d="M88 96 Q80 88 71 90 Q70 115 83 135 Q95 132 103 125 Q97 110 88 96Z"
              fill="#fff7ed"
              opacity="0.42"
            />
            <path
              d="M36 93 Q47 83 60 86 Q73 83 84 93 Q96 116 90 134 Q77 143 60 141 Q43 143 30 134 Q24 116 36 93Z"
              fill="#fff1fb"
              opacity="0.86"
            />
            <path
              d="M33 101 Q45 119 60 132 Q75 119 87 101 Q86 122 77 136 Q68 142 60 140 Q52 142 43 136 Q34 122 33 101Z"
              fill="#f0abfc"
              opacity="0.44"
            />
            <path
              d="M39 96 Q49 90 60 91 Q71 90 81 96 Q76 111 69 124 Q64 134 60 137 Q56 134 51 124 Q44 111 39 96Z"
              fill="#f9a8d4"
              opacity="0.2"
            />
            <path d="M33 122 Q44 132 51 130 Q57 136 60 132 Q63 136 69 130 Q76 132 87 122" stroke="#fff8df" strokeWidth="3" strokeLinecap="round" opacity="0.62" fill="none" />
            <path d="M41 84 Q51 98 60 102 Q69 98 79 84" stroke="#fef3c7" strokeWidth="2.4" strokeLinecap="round" opacity="0.58" fill="none" />
            <path d="M49 84 Q55 94 60 98 Q65 94 71 84" stroke="#fbbf24" strokeWidth=".85" strokeLinecap="round" opacity="0.52" fill="none" />
            <path d="M47 96 Q53 116 60 130 Q67 116 73 96" stroke="#fbbf24" strokeWidth=".9" strokeLinecap="round" opacity="0.46" fill="none" />
            <path d="M55 104 Q58 116 60 128 Q62 116 65 104" stroke="#fff8df" strokeWidth="1.05" strokeLinecap="round" opacity="0.42" fill="none" />
            <path
              d="M42 84 Q34 98 27 115 Q22 129 27 134 Q40 129 46 113 Q50 97 53 87Z"
              fill="#fff7ed"
              opacity="0.72"
            />
            <path
              d="M78 84 Q86 98 93 115 Q98 129 93 134 Q80 129 74 113 Q70 97 67 87Z"
              fill="#fff7ed"
              opacity="0.72"
            />
            <path d="M31 113 Q35 126 45 133" stroke="#fef3c7" strokeWidth="2.4" strokeLinecap="round" opacity="0.35" fill="none" />
            <path d="M89 113 Q85 126 75 133" stroke="#fef3c7" strokeWidth="2.4" strokeLinecap="round" opacity="0.35" fill="none" />
            <ellipse cx="26.5" cy="116" rx="4.1" ry="5" fill={`url(#${skinGradId})`} opacity="0.96" />
            <ellipse cx="93.5" cy="116" rx="4.1" ry="5" fill={`url(#${skinGradId})`} opacity="0.96" />
            <ellipse cx="49" cy="131" rx="8.8" ry="5.1" fill="#f0abfc" opacity="0.7" />
            <ellipse cx="71" cy="131" rx="8.8" ry="5.1" fill="#f0abfc" opacity="0.7" />
            <path
              d="M47 89 Q54 82 60 84 Q66 82 73 89 L70 120 Q60 132 50 120Z"
              fill="#f9a8d4"
              opacity="0.58"
              filter={`url(#${glowId})`}
            />
            <path d="M52 91 Q60 86 68 91 L66 108 Q60 121 54 108Z" fill="#fff" opacity="0.24" />
            <rect x="42" y="96" width="36" height="5.5" rx="2.75" fill="#fde68a" opacity="0.78" />
            <path d="M44 99 Q60 103 76 99" stroke="#fff8df" strokeWidth="1.2" strokeLinecap="round" opacity="0.55" fill="none" />
            <g filter={`url(#${glowId})`} opacity="0.95">
              <ellipse cx="63" cy="99" rx="5.2" ry="3.6" fill="#f9a8d4" transform="rotate(24 63 99)" />
              <ellipse cx="57" cy="96" rx="5.2" ry="3.6" fill="#fbcfe8" transform="rotate(-24 57 96)" />
              <ellipse cx="62" cy="93.1" rx="4.3" ry="2.9" fill="#fda4af" transform="rotate(86 62 93.1)" />
              <ellipse cx="57.5" cy="101.5" rx="4.2" ry="2.8" fill="#f9a8d4" transform="rotate(-72 57.5 101.5)" />
              <circle cx="60" cy="98" r="2.25" fill="#fff3c4" />
              <circle cx="60" cy="98" r=".9" fill="#fff8df" opacity=".9" />
            </g>
            <path
              d="M56 74 Q60 69 64 74 Q60 82 56 74Z"
              fill="#fff8df"
              opacity="0.9"
              filter={`url(#${glowId})`}
            />
            <path d="M58 75 Q60 72 62 75 Q60 79 58 75Z" fill="#f9a8d4" opacity="0.82" />
            <path d="M52 74 Q60 66 68 74" stroke="#fbbf24" strokeWidth=".9" strokeLinecap="round" opacity="0.5" fill="none" />
            <path d="M50 78 Q60 82 70 78" stroke="#fff8df" strokeWidth="1" strokeLinecap="round" opacity="0.38" fill="none" />
            <circle cx="60" cy="48" r="22.7" fill={`url(#${skinGradId})`} opacity="0.99" />
            <ellipse cx="60" cy="49" rx="18.6" ry="20.2" fill="#fff7ed" opacity="0.08" />
            <path
              d="M35 50 Q37 27 53 20 Q64 12 79 23 Q89 33 85 53 Q75 38 63 39 Q49 40 35 50Z"
              fill={`url(#${hairGradId})`}
              opacity="0.98"
            />
            <path
              d="M40 43 Q54 27 69 28 Q79 30 84 40 Q72 38 62 44 Q54 52 40 57Z"
              fill={`url(#${hairGradId})`}
              opacity="0.98"
            />
            <path
              d="M44 47 Q53 37 61.5 38 Q70 38 79 44 Q68 44 60 48 Q51 53 44 58Z"
              fill="#fff7ed"
              opacity="0.18"
            />
            <path d="M46 37 Q58 22 75 29" stroke="#fff7ed" strokeWidth="2.25" strokeLinecap="round" opacity="0.38" />
            <path d="M50 41 Q59 33 69 35" stroke="#fff7ed" strokeWidth="1.4" strokeLinecap="round" opacity="0.3" />
            <path d="M42 55 Q49 44 58.5 42.5" stroke="#fff7ed" strokeWidth="1.08" strokeLinecap="round" opacity="0.22" />
            <path d="M78 55 Q71 44 61.5 42.5" stroke="#fff7ed" strokeWidth="1.08" strokeLinecap="round" opacity="0.22" />
            <path d="M55 34 Q63 29 72 33" stroke="#fff8df" strokeWidth=".85" strokeLinecap="round" opacity="0.28" />
            <g filter={`url(#${glowId})`} opacity="0.95">
              <ellipse cx="78" cy="37" rx="4.8" ry="3.3" fill="#f9a8d4" transform="rotate(24 78 37)" />
              <ellipse cx="72.3" cy="33" rx="4.8" ry="3.3" fill="#fbcfe8" transform="rotate(-24 72.3 33)" />
              <ellipse cx="76" cy="31" rx="4" ry="2.7" fill="#fda4af" transform="rotate(86 76 31)" />
              <ellipse cx="71.8" cy="39" rx="3.8" ry="2.6" fill="#f9a8d4" transform="rotate(-72 71.8 39)" />
              <circle cx="75.3" cy="35.2" r="2.15" fill="#fff3c4" />
            </g>
            <ellipse cx="51.2" cy="54.6" rx="5.3" ry="6.15" fill="#3d2800" opacity="0.95" />
            <ellipse cx="68.8" cy="54.6" rx="5.3" ry="6.15" fill="#3d2800" opacity="0.95" />
            <ellipse cx="51.7" cy="56.1" rx="3.1" ry="3.6" fill="#7a4a28" opacity="0.48" />
            <ellipse cx="69.3" cy="56.1" rx="3.1" ry="3.6" fill="#7a4a28" opacity="0.48" />
            <circle cx="49.6" cy="51.4" r="1.75" fill="#fff" opacity="0.97" />
            <circle cx="67.2" cy="51.4" r="1.75" fill="#fff" opacity="0.97" />
            <circle cx="53.8" cy="58.9" r=".85" fill="#fff8df" opacity="0.7" />
            <circle cx="71.4" cy="58.9" r=".85" fill="#fff8df" opacity="0.7" />
            <path d="M46.4 46.5 Q51.4 43.4 56.5 46.4" stroke="#6b3a22" strokeWidth="1.35" fill="none" strokeLinecap="round" opacity="0.62" />
            <path d="M63.5 46.4 Q68.6 43.4 73.6 46.5" stroke="#6b3a22" strokeWidth="1.35" fill="none" strokeLinecap="round" opacity="0.62" />
            <path d="M47.5 53.6 Q46 53 45.1 51.8" stroke="#3d2800" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.58" />
            <path d="M72.5 53.6 Q74 53 74.9 51.8" stroke="#3d2800" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.58" />
            <ellipse cx="49.2" cy="61.7" rx="5.6" ry="3.5" fill="#f9a8d4" opacity="0.26" />
            <ellipse cx="70.8" cy="61.7" rx="5.6" ry="3.5" fill="#f9a8d4" opacity="0.26" />
            <path d="M54.7 64 Q60 68.9 65.3 64" stroke="#b43b62" strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.78" />
            <g className="spirit-live-face">
              <ellipse cx="51.2" cy="54.6" rx="5.7" ry="3.85" fill={`url(#${skinGradId})`} />
              <ellipse cx="68.8" cy="54.6" rx="5.7" ry="3.85" fill={`url(#${skinGradId})`} />
              <path
                className="spirit-smile-variant"
                d="M54.7 64 Q60 69.5 65.3 64"
                stroke="#b43b62"
                strokeWidth="1.35"
                fill="none"
                strokeLinecap="round"
              />
            </g>
          </g>
        ) : stageKey === "adult" && isFemale ? (
          <g>
            <ellipse cx="60" cy="128" rx="31" ry="7.5" fill="#c084fc" opacity="0.15" filter={`url(#${glowId})`} />
            <ellipse cx="60" cy="70" rx="48" ry="61" fill="#f5d0fe" opacity="0.14" />
            <circle cx="60" cy="64" r="43" fill="#fbcfe8" opacity="0.13" filter={`url(#${glowId})`} />
            <circle cx="60" cy="63" r="42" fill="none" stroke="#f0abfc" strokeWidth="1.25" opacity="0.31" />
            <circle cx="60" cy="63" r="31" fill="none" stroke="#fff8df" strokeWidth=".95" opacity="0.24" />
            <g filter={`url(#${glowId})`} opacity="0.62">
              <path d="M23 81 l1.7 3.4 l3.4 1.7 l-3.4 1.7 l-1.7 3.4 l-1.7 -3.4 l-3.4 -1.7 l3.4 -1.7Z" fill="#fff8df" opacity="0.62" />
              <path d="M94 72 l1.6 3.2 l3.2 1.6 l-3.2 1.6 l-1.6 3.2 l-1.6 -3.2 l-3.2 -1.6 l3.2 -1.6Z" fill="#fff8df" opacity="0.58" />
              <ellipse cx="24" cy="105" rx="3.6" ry="2.1" fill="#f9a8d4" transform="rotate(-23 24 105)" />
              <ellipse cx="96" cy="96" rx="3.3" ry="2" fill="#f9a8d4" transform="rotate(28 96 96)" />
              <g opacity="0.72">
                <ellipse cx="28" cy="118" rx="3.2" ry="2.1" fill="#f9a8d4" transform="rotate(25 28 118)" />
                <ellipse cx="24.5" cy="115.5" rx="3.2" ry="2.1" fill="#fbcfe8" transform="rotate(-25 24.5 115.5)" />
                <circle cx="26.3" cy="116.7" r="1.3" fill="#fef3c7" />
              </g>
              <g opacity="0.68">
                <ellipse cx="91" cy="120" rx="3.2" ry="2.1" fill="#f9a8d4" transform="rotate(-25 91 120)" />
                <ellipse cx="94.5" cy="117.5" rx="3.2" ry="2.1" fill="#fbcfe8" transform="rotate(25 94.5 117.5)" />
                <circle cx="92.8" cy="118.7" r="1.3" fill="#fef3c7" />
              </g>
            </g>
            <path
              d="M34 61 Q34 36 48 25 Q60 14 74 25 Q87 36 86 61 Q85 87 78 118 Q68 105 70 78 Q62 88 54 79 Q51 102 42 120 Q34 92 34 61Z"
              fill={`url(#${hairGradId})`}
              opacity="0.86"
            />
            <path
              d="M40 60 Q31 86 35 118 Q47 105 47 78"
              fill={`url(#${hairGradId})`}
              opacity="0.62"
            />
            <path
              d="M80 60 Q89 86 85 118 Q73 105 73 78"
              fill={`url(#${hairGradId})`}
              opacity="0.62"
            />
            <path
              d="M44 64 Q35 80 41 93 Q46 104 38 116"
              stroke={hairColor}
              strokeWidth="4"
              strokeLinecap="round"
              opacity="0.3"
              fill="none"
            />
            <path
              d="M76 64 Q85 80 79 93 Q74 104 82 116"
              stroke={hairColor}
              strokeWidth="4"
              strokeLinecap="round"
              opacity="0.3"
              fill="none"
            />
            <path
              d="M55 66 Q60 69 65 66 L65 76 Q60 80 55 76Z"
              fill={`url(#${skinGradId})`}
              opacity="0.9"
            />
            <path
              d="M41 80 Q48 67 60 67 Q72 67 79 80 Q73 87 60 88 Q47 87 41 80Z"
              fill="#ffe7fb"
              opacity="0.72"
            />
            <path
              d="M44 79 Q50 70 60 70 Q70 70 76 79 Q73 93 68 101 Q60 105 52 101 Q47 93 44 79Z"
              fill={`url(#${bodyGradId})`}
              opacity="0.99"
            />
            <path
              d="M40 94 Q48 86 60 88 Q72 86 80 94 Q88 115 84 130 Q73 139 60 137 Q47 139 36 130 Q32 115 40 94Z"
              fill={`url(#${bodyGradId})`}
              opacity="0.98"
            />
            <path
              d="M36 102 Q46 119 60 128 Q74 119 84 102 Q82 120 74 132 Q67 138 60 136 Q53 138 46 132 Q38 120 36 102Z"
              fill="#e9c8ff"
              opacity="0.48"
            />
            <path
              d="M34 122 Q45 132 52 129 Q57 135 60 132 Q63 135 68 129 Q75 132 86 122"
              stroke="#fff8df"
              strokeWidth="2.7"
              strokeLinecap="round"
              opacity="0.56"
              fill="none"
            />
            <path
              d="M41 82 Q51 96 60 99 Q69 96 79 82"
              stroke="#fff8df"
              strokeWidth="2.5"
              strokeLinecap="round"
              opacity="0.5"
              fill="none"
            />
            <path
              d="M43 82 Q36 93 32 108 Q29 121 33 127 Q43 123 47 110 Q50 96 52 86Z"
              fill="#f7d6ff"
              opacity="0.86"
            />
            <path
              d="M77 82 Q84 93 88 108 Q91 121 87 127 Q77 123 73 110 Q70 96 68 86Z"
              fill="#f7d6ff"
              opacity="0.86"
            />
            <path
              d="M34 110 Q37 121 44 126"
              stroke="#fff"
              strokeWidth="2.2"
              strokeLinecap="round"
              opacity="0.25"
              fill="none"
            />
            <path
              d="M86 110 Q83 121 76 126"
              stroke="#fff"
              strokeWidth="2.2"
              strokeLinecap="round"
              opacity="0.25"
              fill="none"
            />
            <ellipse cx="31" cy="112" rx="3.8" ry="4.8" fill={`url(#${skinGradId})`} opacity="0.96" />
            <ellipse cx="89" cy="112" rx="3.8" ry="4.8" fill={`url(#${skinGradId})`} opacity="0.96" />
            <ellipse cx="49" cy="130" rx="8.5" ry="5" fill={shoeColor} opacity="0.68" />
            <ellipse cx="71" cy="130" rx="8.5" ry="5" fill={shoeColor} opacity="0.68" />
            <path
              d="M48 88 Q54 82 60 84 Q66 82 72 88 L69 117 Q60 128 51 117Z"
              fill="#8b5ca8"
              opacity="0.66"
              filter={`url(#${glowId})`}
            />
            <path
              d="M52 91 Q60 87 68 91 L66 106 Q60 117 54 106Z"
              fill="#fff"
              opacity="0.14"
            />
            <path
              d="M55 75 Q60 70 65 75 Q60 83 55 75Z"
              fill="#fff8df"
              opacity="0.78"
              filter={`url(#${glowId})`}
            />
            <path
              d="M57 76 Q60 73 63 76 Q60 80 57 76Z"
              fill="#f9a8d4"
              opacity="0.76"
            />
            <rect x="43" y="96" width="34" height="5.4" rx="2.7" fill="#fbcfe8" opacity="0.82" />
            <g filter={`url(#${glowId})`} opacity="0.9">
              <ellipse cx="62" cy="99" rx="4.2" ry="3" fill="#f9a8d4" transform="rotate(24 62 99)" />
              <ellipse cx="57.5" cy="96.2" rx="4.2" ry="3" fill="#fbcfe8" transform="rotate(-24 57.5 96.2)" />
              <ellipse cx="62" cy="93.7" rx="3.7" ry="2.5" fill="#fda4af" transform="rotate(86 62 93.7)" />
              <ellipse cx="57.8" cy="101" rx="3.6" ry="2.4" fill="#f9a8d4" transform="rotate(-72 57.8 101)" />
              <circle cx="60" cy="97.8" r="1.9" fill="#fff3c4" />
            </g>
            <circle cx="60" cy="49" r="22" fill={`url(#${skinGradId})`} opacity="0.99" />
            <path
              d="M38 51 Q40 30 53 23 Q63 16 76 25 Q85 34 82 53 Q73 41 63 41 Q52 42 38 51Z"
              fill={`url(#${hairGradId})`}
              opacity="0.98"
            />
            <path
              d="M43.5 44 Q54 30 67.5 30 Q76 32 81 40 Q70 39 62 44 Q55 51 43.5 56Z"
              fill={`url(#${hairGradId})`}
              opacity="0.98"
            />
            <path
              d="M48 38 Q57 25 72 31"
              stroke="#fff7ed"
              strokeWidth="2"
              strokeLinecap="round"
              opacity="0.32"
            />
            <path
              d="M52 42 Q58 36 66 37"
              stroke="#fff7ed"
              strokeWidth="1.2"
              strokeLinecap="round"
              opacity="0.24"
            />
            <g filter={`url(#${glowId})`} opacity="0.9">
              <ellipse cx="78" cy="38" rx="4.6" ry="3.2" fill="#f9a8d4" transform="rotate(24 78 38)" />
              <ellipse cx="72.5" cy="34" rx="4.6" ry="3.2" fill="#fbcfe8" transform="rotate(-24 72.5 34)" />
              <ellipse cx="76" cy="32" rx="3.8" ry="2.6" fill="#fda4af" transform="rotate(86 76 32)" />
              <ellipse cx="72" cy="40" rx="3.8" ry="2.5" fill="#f9a8d4" transform="rotate(-72 72 40)" />
              <circle cx="75.4" cy="36.1" r="2.1" fill="#fff3c4" />
              <circle cx="80.8" cy="34.8" r="1.2" fill="#86efac" opacity="0.56" />
              <circle cx="71.1" cy="40.8" r="1.1" fill="#86efac" opacity="0.5" />
            </g>
            <ellipse cx="51.5" cy="55.3" rx="5.1" ry="5.95" fill="#3d2800" opacity="0.93" />
            <ellipse cx="68.5" cy="55.3" rx="5.1" ry="5.95" fill="#3d2800" opacity="0.93" />
            <ellipse cx="51.5" cy="56.8" rx="3" ry="3.4" fill="#7a4a28" opacity="0.46" />
            <ellipse cx="68.5" cy="56.8" rx="3" ry="3.4" fill="#7a4a28" opacity="0.46" />
            <circle cx="49.9" cy="52.3" r="1.62" fill="#fff" opacity="0.96" />
            <circle cx="66.9" cy="52.3" r="1.62" fill="#fff" opacity="0.96" />
            <circle cx="54.1" cy="59.6" r=".75" fill="#fff8df" opacity="0.62" />
            <circle cx="70.1" cy="59.6" r=".75" fill="#fff8df" opacity="0.62" />
            <path d="M47.2 47.3 Q51.6 44.4 55.9 47" stroke="#6b3a22" strokeWidth="1.35" fill="none" strokeLinecap="round" opacity="0.58" />
            <path d="M64.1 47 Q68.4 44.4 72.8 47.3" stroke="#6b3a22" strokeWidth="1.35" fill="none" strokeLinecap="round" opacity="0.58" />
            <path d="M47.8 54.2 Q46.2 53.6 45.2 52.2" stroke="#3d2800" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.58" />
            <path d="M72.2 54.2 Q73.8 53.6 74.8 52.2" stroke="#3d2800" strokeWidth="1" fill="none" strokeLinecap="round" opacity="0.58" />
            <ellipse cx="50" cy="62" rx="5.2" ry="3.3" fill="#f9a8d4" opacity="0.23" />
            <ellipse cx="70" cy="62" rx="5.2" ry="3.3" fill="#f9a8d4" opacity="0.23" />
            <path
              d="M55 64 Q60 68.2 65 64"
              stroke="#5a301d"
              strokeWidth="1.45"
              fill="none"
              strokeLinecap="round"
              opacity="0.64"
            />
            <g className="spirit-live-face">
              <ellipse cx="51.5" cy="55.3" rx="5.65" ry="3.75" fill={`url(#${skinGradId})`} />
              <ellipse cx="68.5" cy="55.3" rx="5.65" ry="3.75" fill={`url(#${skinGradId})`} />
              <path
                className="spirit-smile-variant"
                d="M55 64 Q60 69 65 64"
                stroke="#5a301d"
                strokeWidth="1.35"
                fill="none"
                strokeLinecap="round"
              />
            </g>
          </g>
        ) : (
         <g>
            {isFemale ? (
              <g>
                <path
                  d="M44 82 Q38 101 34 124 Q48 130 60 126 Q72 130 86 124 Q82 101 76 82 Q66 92 60 92 Q54 92 44 82Z"
                  fill={`url(#${bodyGradId})`}
                  opacity="0.95"
                />
                <path
                  d="M42 91 Q48 105 60 109 Q72 105 78 91"
                  stroke="#fff8df"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  opacity="0.34"
                />
              </g>
            ) : (
              <g>
                <rect
                  x="47"
                  y="84"
                  width="26"
                  height="28"
                  rx="5"
                  fill={`url(#${bodyGradId})`}
                  opacity="0.95"
                />
                <rect
                  x="47"
                  y="108"
                  width="11"
                  height="16"
                  rx="5"
                  fill={`url(#${bodyGradId})`}
                  opacity="0.9"
                />
                <rect
                  x="62"
                  y="108"
                  width="11"
                  height="16"
                  rx="5"
                  fill={`url(#${bodyGradId})`}
                  opacity="0.9"
                />
              </g>
            )}
            <rect
              x={isFemale ? 49 : 47}
              y="72"
              width={isFemale ? 22 : 26}
              height={isFemale ? 16 : 20}
              rx="7"
              fill={`url(#${bodyGradId})`}
              opacity="0.97"
            />
            {(armorBeltColor ||
              robeItem?.id === "breastplate_righteousness" ||
              isArmorSet) && (
              <g>
                <path
                  d="M49 74 Q60 68 71 74 L68 88 Q60 94 52 88Z"
                  fill="rgba(255,255,255,.38)"
                  opacity="0.72"
                />
                <path
                  d="M52 76 Q60 72 68 76 L66 86 Q60 90 54 86Z"
                  fill="#fff8df"
                  opacity="0.42"
                />
              </g>
            )}
            {armorBeltColor && (
              <rect
                x="46"
                y="83"
                width="28"
                height="5"
                rx="2.5"
                fill={armorBeltColor}
                opacity="0.92"
              />
            )}
            {sashItem && (
              <path
                d={`M49 72 L71 88`}
                stroke={sashItem.sashColor}
                strokeWidth="5"
                strokeLinecap="round"
                opacity="0.8"
              />
            )}
            {(shieldColor || swordColor) && (
              <g filter={`url(#${glowId})`}>
                {shieldColor && (
                  <g>
                    <path
                      d="M22 88 Q30 82 38 88 L36 103 Q30 112 24 103Z"
                      fill={shieldColor}
                      opacity="0.9"
                    />
                    <path
                      d="M27 91 Q30 89 33 91 L32 100 Q30 104 28 100Z"
                      fill="#fff8df"
                      opacity="0.55"
                    />
                  </g>
                )}
                {swordColor && (
                  <g>
                    <line
                      x1="86"
                      y1="88"
                      x2="99"
                      y2="111"
                      stroke={swordColor}
                      strokeWidth="3.5"
                      strokeLinecap="round"
                    />
                    <line
                      x1="82"
                      y1="97"
                      x2="94"
                      y2="97"
                      stroke={swordColor}
                      strokeWidth="3"
                      strokeLinecap="round"
                      opacity="0.7"
                    />
                    <path
                      d="M99 111 L103 118"
                      stroke="#fff8df"
                      strokeWidth="2"
                      strokeLinecap="round"
                      opacity="0.8"
                    />
                  </g>
                )}
              </g>
            )}
            {isFemale ? (
              <g>
                <ellipse
                  cx="44"
                  cy="122"
                  rx="8"
                  ry="5"
                  fill={shoeColor}
                  opacity="0.9"
                />
                <ellipse
                  cx="76"
                  cy="122"
                  rx="8"
                  ry="5"
                  fill={shoeColor}
                  opacity="0.9"
                />
              </g>
            ) : (
              <g>
                <ellipse
                  cx="52"
                  cy="124"
                  rx="7"
                  ry="4"
                  fill={shoeColor}
                  opacity="0.9"
                />
                <ellipse
                  cx="68"
                  cy="124"
                  rx="7"
                  ry="4"
                  fill={shoeColor}
                  opacity="0.9"
                />
              </g>
            )}
            {isFemale ? (
              <g>
                <path
                  d="M40 62 Q38 47 47 39 Q53 32 60 32 Q67 32 74 39 Q82 47 80 62 Q79 78 74 91 Q69 80 72 63 Q66 70 60 70 Q54 70 48 63 Q51 80 46 91 Q41 78 40 62Z"
                  fill={`url(#${hairGradId})`}
                  opacity="0.85"
                />
                <path
                  d="M44 58 Q41 69 44 86 Q50 77 48 64"
                  fill={`url(#${hairGradId})`}
                  opacity="0.58"
                />
                <path
                  d="M76 58 Q79 69 76 86 Q70 77 72 64"
                  fill={`url(#${hairGradId})`}
                  opacity="0.58"
                />
                <path
                  d="M47 52 Q51 47 55 51 Q59 55 64 49 Q68 45 73 50"
                  stroke="#fff7ed"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                  opacity="0.26"
                  fill="none"
                />
                <path
                  d="M50 40 Q57 34 64 36"
                  stroke="#fff7ed"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  opacity="0.28"
                />
                <g filter={`url(#${glowId})`} opacity="0.82">
                  <ellipse cx="78" cy="47" rx="3.8" ry="2.8" fill="#f9a8d4" transform="rotate(24 78 47)" />
                  <ellipse cx="73" cy="43" rx="3.8" ry="2.8" fill="#fbcfe8" transform="rotate(-24 73 43)" />
                  <circle cx="75.8" cy="45.2" r="2" fill="#fff3c4" />
                </g>
              </g>
            ) : (
              <path
                d="M44 58 Q42 46 50 39 Q55 33 60 33 Q65 33 70 39 Q78 46 76 58"
                fill={`url(#${hairGradId})`}
                opacity="0.85"
              />
            )}
            <circle cx="60" cy="56" r="15.4" fill={`url(#${skinGradId})`} opacity="0.97" />
            <ellipse
              cx="55"
              cy="52"
              rx="2.5"
              ry={isFemale ? 3 : 2.5}
              fill="#3d2800"
              opacity="0.75"
            />
            <ellipse
              cx="65"
              cy="52"
              rx="2.5"
              ry={isFemale ? 3 : 2.5}
              fill="#3d2800"
              opacity="0.75"
            />
            <circle cx="54.5" cy="51" r="0.8" fill="white" opacity="0.7" />
            <circle cx="64.5" cy="51" r="0.8" fill="white" opacity="0.7" />
            {isFemale && (
              <>
                <line
                  x1="53"
                  y1="49"
                  x2="52"
                  y2="47"
                  stroke="#3d2800"
                  strokeWidth="1"
                  opacity="0.5"
                />
                <line
                  x1="55.5"
                  y1="49"
                  x2="55"
                  y2="46.5"
                  stroke="#3d2800"
                  strokeWidth="1"
                  opacity="0.5"
                />
                <line
                  x1="64"
                  y1="49"
                  x2="64"
                  y2="46.5"
                  stroke="#3d2800"
                  strokeWidth="1"
                  opacity="0.5"
                />
                <line
                  x1="66.5"
                  y1="49"
                  x2="67.5"
                  y2="47"
                  stroke="#3d2800"
                  strokeWidth="1"
                  opacity="0.5"
                />
              </>
            )}
            <path
              d={`M55 59 Q60 ${isFemale ? 63 : 62} 65 59`}
              stroke="#3d2800"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              opacity="0.6"
            />
            <g className="spirit-live-face">
              <ellipse cx="55" cy="52" rx="3.2" ry="2.1" fill={`url(#${skinGradId})`} />
              <ellipse cx="65" cy="52" rx="3.2" ry="2.1" fill={`url(#${skinGradId})`} />
              <path
                className="spirit-smile-variant"
                d="M54.5 59 Q60 65 65.5 59"
                stroke="#3d2800"
                strokeWidth="1.35"
                fill="none"
                strokeLinecap="round"
              />
            </g>
            <circle cx="52" cy="58" r={isFemale ? "3.2" : "2.8"} fill={isFemale ? "#f9a8d4" : spiritAccent.primary} opacity={isFemale ? "0.26" : "0.18"} />
            <circle cx="68" cy="58" r={isFemale ? "3.2" : "2.8"} fill={isFemale ? "#f9a8d4" : spiritAccent.primary} opacity={isFemale ? "0.26" : "0.18"} />
            <path
              d="M56 76 Q60 72 64 76 Q60 82 56 76Z"
              fill={isFemale ? "#f9a8d4" : spiritAccent.primary}
              opacity={isFemale ? "0.64" : "0.55"}
              filter={`url(#${glowId})`}
            />
            <path
              d="M42 78 Q48 72 54 76"
              stroke={spiritAccent.secondary}
              strokeWidth="1.3"
              strokeLinecap="round"
              opacity="0.38"
            />
            <path
              d="M78 78 Q72 72 66 76"
              stroke={spiritAccent.secondary}
              strokeWidth="1.3"
              strokeLinecap="round"
              opacity="0.38"
            />
          </g>
        )}
        {false && stageKey !== "seed" && (
          <g className={`spirit-cute-layer ${isFemale ? "her" : "his"}`}>
            <ellipse
              className="spirit-soft-halo"
              cx="60"
              cy="72"
              rx="46"
              ry="55"
              fill={isFemale ? "#fbcfe8" : "#bae6fd"}
              opacity="0.18"
            />
            <path
              className="spirit-hood-plush"
              d={
                isBabyStage
                  ? "M29 62 Q31 31 60 21 Q89 31 91 62 Q89 88 75 108 Q60 121 45 108 Q31 88 29 62Z"
                  : "M25 62 Q28 27 60 16 Q92 27 95 62 Q93 92 78 116 Q60 134 42 116 Q27 92 25 62Z"
              }
              fill={isFemale ? "#f7d6ff" : "#c8f7df"}
              opacity={isBabyStage ? "0.90" : "0.92"}
            />
            <path
              className="spirit-hood-shadow"
              d="M36 61 Q39 36 60 27 Q81 36 84 61 Q77 48 60 46 Q43 48 36 61Z"
              fill={isFemale ? "#f0bdf5" : "#99e9c7"}
              opacity="0.48"
            />
            <ellipse
              className="spirit-face-plush"
              cx="60"
              cy="53"
              rx={isBabyStage ? "20" : "22"}
              ry={isBabyStage ? "19" : "21"}
              fill={`url(#${skinGradId})`}
              opacity="0.94"
            />
            <path
              className="spirit-hood-rim"
              d="M35 58 Q40 34 60 29 Q80 34 85 58 Q74 46 60 46 Q46 46 35 58Z"
              fill="none"
              stroke={isFemale ? "#f9a8d4" : "#86efac"}
              strokeWidth="5.2"
              strokeLinecap="round"
              opacity="0.56"
            />
            <path
              className="spirit-body-plush"
              d={
                isBabyStage
                  ? "M36 77 Q40 63 60 62 Q80 63 84 77 Q90 102 74 122 Q60 133 46 122 Q30 102 36 77Z"
                  : "M31 76 Q36 57 60 56 Q84 57 89 76 Q96 108 78 130 Q60 144 42 130 Q24 108 31 76Z"
              }
              fill={`url(#${bodyGradId})`}
              opacity={isBabyStage ? "0.92" : "0.94"}
            />
            <ellipse className="spirit-face-highlight" cx="52" cy="45" rx="7" ry="4.5" fill="#fff" opacity="0.18" transform="rotate(-18 52 45)" />
            <g className="spirit-cute-eyes">
              <circle className="spirit-eye left" cx="55" cy="53" r="1.55" fill="#3d2800" opacity="0.72" />
              <circle className="spirit-eye right" cx="65" cy="53" r="1.55" fill="#3d2800" opacity="0.72" />
            </g>
            <g className="spirit-closed-eyes">
              <path d="M53.3 53 Q55 54 56.7 53" stroke="#3d2800" strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.42" />
              <path d="M63.3 53 Q65 54 66.7 53" stroke="#3d2800" strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.42" />
            </g>
            <circle className="spirit-blush left" cx="51.5" cy="60" r="2.4" fill={isFemale ? "#f9a8d4" : "#93c5fd"} opacity="0.10" />
            <circle className="spirit-blush right" cx="68.5" cy="60" r="2.4" fill={isFemale ? "#f9a8d4" : "#93c5fd"} opacity="0.10" />
            <path className="spirit-cute-smile" d="M57 60.8 Q60 62.8 63 60.8" stroke="#3d2800" strokeWidth="1.05" fill="none" strokeLinecap="round" opacity="0.50" />
            <g className="spirit-cute-feet">
              <ellipse cx={isFemale ? 50 : 53} cy="127" rx="4.8" ry="2.8" fill={shoeColor} opacity="0.18" />
              <ellipse cx={isFemale ? 70 : 67} cy="127" rx="4.8" ry="2.8" fill={shoeColor} opacity="0.18" />
            </g>
            <g className="spirit-chest-symbol" filter={`url(#${glowId})`}>
              <path
                d={isFemale ? "M60 79 Q55 74 51 79 Q51 86 60 91 Q69 86 69 79 Q65 74 60 79Z" : "M60 74 L62.8 80 L69.5 80.5 L64.2 84.8 L66 91 L60 87.6 L54 91 L55.8 84.8 L50.5 80.5 L57.2 80Z"}
                fill={isFemale ? "#fb8fd1" : "#7dd3fc"}
                opacity="0.88"
              />
            </g>
            <path className="spirit-robe-highlight" d="M42 88 Q60 103 78 88" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" opacity="0.38" />
            <path className="spirit-bottom-shadow" d="M43 119 Q60 131 77 119" stroke="rgba(82,55,15,.16)" strokeWidth="3.5" strokeLinecap="round" opacity="0.28" />
          </g>
        )}
        {!stageImageSrc && stageKey === "radiant" && (
          <g filter={`url(#${glowId})`} opacity="0.9">
            <circle
              cx="60"
              cy="54"
              r="29"
              fill="none"
              stroke={stageGlow}
              strokeWidth="1.2"
              opacity="0.28"
            />
            <circle
              cx="60"
              cy="54"
              r="39"
              fill="none"
              stroke={stageGlow}
              strokeWidth="0.9"
              opacity="0.16"
            />
            {[18, 72, 126, 180, 234, 288, 342].map((a, i) => (
              <g key={i}>
                <circle
                  cx={60 + Math.cos((a * Math.PI) / 180) * 36}
                  cy={54 + Math.sin((a * Math.PI) / 180) * 30}
                  r={i % 2 === 0 ? 2.2 : 1.6}
                  fill={stageGlow}
                  opacity="0.62"
                />
                <path
                  d={`M${60 + Math.cos((a * Math.PI) / 180) * 41} ${
                    54 + Math.sin((a * Math.PI) / 180) * 34
                  } l2 4 l4 2 l-4 2 l-2 4 l-2 -4 l-4 -2 l4 -2Z`}
                  fill="#fff8df"
                  opacity="0.58"
                />
              </g>
            ))}
            <path
              d="M44 76 Q60 91 76 76 Q72 103 60 122 Q48 103 44 76Z"
              fill="#fff8df"
              opacity="0.24"
            />
          </g>
        )}
        {haloColor && stageKey !== "seed" && (
          <ellipse
            cx="60"
            cy={haloY}
            rx={haloRx}
            ry={haloRy}
            fill="none"
            stroke={haloColor}
            strokeWidth={stageImageSrc ? "3.4" : "4"}
            opacity={stageImageSrc ? "0.86" : "0.92"}
            filter={`url(#${glowId})`}
          />
        )}
        {hatItem &&
          stageKey !== "seed" &&
          (() => {
            const hc = hatItem.hatColor || "#f9a8d4";
            if (hatItem.id === "helmet_salvation")
              return (
                <g filter={`url(#${glowId})`}>
                  <path
                    d="M44 47 Q47 34 60 31 Q73 34 76 47 Q68 42 60 42 Q52 42 44 47Z"
                    fill={hc}
                    opacity="0.96"
                  />
                  <path
                    d="M50 40 Q60 34 70 40"
                    stroke="#fff8df"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    opacity="0.62"
                  />
                  <rect
                    x="55"
                    y="35"
                    width="10"
                    height="12"
                    rx="5"
                    fill="#fff8df"
                    opacity="0.38"
                  />
                </g>
              );
            if (hatItem.id === "hat_flower")
              return (
                <g filter={`url(#${glowId})`}>
                  {[0, 60, 120, 180, 240, 300].map((a, i) => (
                    <ellipse
                      key={i}
                      cx={60 + Math.cos((a * Math.PI) / 180) * 8}
                      cy={36 + Math.sin((a * Math.PI) / 180) * 5}
                      rx="5"
                      ry="4"
                      fill={hc}
                      opacity="0.75"
                      transform={`rotate(${a},${
                        60 + Math.cos((a * Math.PI) / 180) * 8
                      },${36 + Math.sin((a * Math.PI) / 180) * 5})`}
                    />
                  ))}
                  <circle cx="60" cy="36" r="4" fill="#fef3c7" opacity="0.9" />
                </g>
              );
            if (hatItem.id === "hat_star")
              return (
                <g filter={`url(#${glowId})`}>
                  {[0, 72, 144, 216, 288].map((a, i) => (
                    <line
                      key={i}
                      x1="60"
                      y1="36"
                      x2={60 + Math.cos((a * Math.PI) / 180) * 11}
                      y2={36 + Math.sin((a * Math.PI) / 180) * 11}
                      stroke={hc}
                      strokeWidth="3"
                      strokeLinecap="round"
                      opacity="0.8"
                    />
                  ))}
                  <circle cx="60" cy="36" r="4" fill={hc} opacity="0.85" />
                </g>
              );
            if (hatItem.id === "hat_veil")
              return (
                <g>
                  <path d="M42 42 Q60 30 78 42" fill={hc} opacity="0.3" />
                  <path
                    d="M42 42 Q40 55 44 70"
                    stroke={hc}
                    strokeWidth="2"
                    fill="none"
                    opacity="0.25"
                  />
                  <path
                    d="M78 42 Q80 55 76 70"
                    stroke={hc}
                    strokeWidth="2"
                    fill="none"
                    opacity="0.25"
                  />
                </g>
              );
            if (hatItem.id === "hat_ribbon")
              return (
                <g filter={`url(#${glowId})`}>
                  <path
                    d="M48 38 Q56 30 60 36 Q64 30 72 38 Q64 42 60 38 Q56 42 48 38Z"
                    fill={hc}
                    opacity="0.85"
                  />
                  <circle cx="60" cy="38" r="2.5" fill="#fff" opacity="0.8" />
                </g>
              );
            return null;
          })()}
        {isArmorSet && !hatItem && stageKey !== "seed" && (
          <g filter={`url(#${glowId})`}>
            <path
              d="M44 47 Q47 34 60 31 Q73 34 76 47 Q68 42 60 42 Q52 42 44 47Z"
              fill={helmetColor || "#d7b46a"}
              opacity="0.96"
            />
            <path
              d="M50 40 Q60 34 70 40"
              stroke="#fff8df"
              strokeWidth="2.5"
              strokeLinecap="round"
              opacity="0.62"
            />
            <rect
              x="55"
              y="35"
              width="10"
              height="12"
              rx="5"
              fill="#fff8df"
              opacity="0.38"
            />
          </g>
        )}
        {crownColor && stageKey !== "seed" && (
          <g filter={`url(#${glowId})`}>
            {(crownItem?.id === "crown_gold" ||
              setItem?.id === "set_royal") && (
              <>
                <path
                  d="M45 43 L51 30 L60 39 L69 30 L75 43Z"
                  fill={crownColor}
                  opacity="0.95"
                />
                <circle cx="51" cy="30" r="2.5" fill={crownColor} />
                <circle cx="60" cy="27" r="3.2" fill="white" opacity="0.85" />
                <circle cx="69" cy="30" r="2.5" fill={crownColor} />
              </>
            )}
            {crownItem?.id === "crown_laurel" && (
              <>
                <path
                  d="M42 45 Q50 36 60 34 Q70 36 78 45"
                  fill="none"
                  stroke={crownColor}
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />
                <circle cx="42" cy="45" r="3" fill={crownColor} opacity="0.8" />
                <circle cx="78" cy="45" r="3" fill={crownColor} opacity="0.8" />
                <circle
                  cx="60"
                  cy="33"
                  r="3.5"
                  fill={crownColor}
                  opacity="0.7"
                />
              </>
            )}
            {crownItem?.id === "crown_thorns" && (
              <path
                d="M43 46 Q48 38 54 42 Q57 36 60 40 Q63 36 66 42 Q72 38 77 46"
                fill="none"
                stroke={crownColor}
                strokeWidth="2.5"
                strokeLinecap="round"
                opacity="0.85"
              />
            )}
          </g>
        )}
        {!stageImageSrc && stageKey === "teen" && (
          <g opacity="0.82">
            <circle
              cx="60"
              cy="58"
              r="25"
              fill="none"
              stroke={stageGlow}
              strokeWidth="0.7"
              opacity="0.13"
            />
            {[32, 88, 148, 212, 276, 330].map((a, i) => (
              <path
                key={i}
                d={`M${60 + Math.cos((a * Math.PI) / 180) * 29} ${
                  62 + Math.sin((a * Math.PI) / 180) * 25
                } l1.5 3 l3 1.5 l-3 1.5 l-1.5 3 l-1.5 -3 l-3 -1.5 l3 -1.5Z`}
                fill={stageGlow}
                opacity={i % 2 === 0 ? 0.42 : 0.28}
              />
            ))}
            <path
              d="M45 114 Q60 123 75 114"
              stroke={stageGlow}
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              opacity="0.26"
            />
          </g>
        )}
        {!stageImageSrc && stageKey === "adult" && (
          <g filter={`url(#${glowId})`}>
            <circle
              cx="60"
              cy="54"
              r="27"
              fill="none"
              stroke={stageGlow}
              strokeWidth="1.1"
              opacity="0.24"
            />
            <circle
              cx="60"
              cy="54"
              r="41"
              fill="none"
              stroke={stageGlow}
              strokeWidth="0.7"
              opacity="0.14"
            />
            <path
              d="M38 124 Q48 111 60 121 Q72 111 82 124"
              stroke={stageGlow}
              strokeWidth="2.2"
              fill="none"
              strokeLinecap="round"
              opacity="0.36"
            />
            <path
              d="M43 82 Q60 95 77 82"
              stroke="#fff8df"
              strokeWidth="1.7"
              fill="none"
              strokeLinecap="round"
              opacity="0.42"
            />
            {[45, 135, 225, 315].map((a, i) => (
              <circle
                key={i}
                cx={60 + Math.cos((a * Math.PI) / 180) * 34}
                cy={58 + Math.sin((a * Math.PI) / 180) * 30}
                r="2"
                fill="#fff8df"
                opacity="0.48"
              />
            ))}
          </g>
        )}
      </svg>
      {showBar && xpMax > 0 && (
        <div style={{ width: "82%", marginTop: 4 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 13.5,
              color: C.textFaint,
              marginBottom: 3,
            }}
          >
            <span>spirit xp</span>
            <span>
              {xp}/{xpMax === Infinity ? "∞" : xpMax}
            </span>
          </div>
          <ProgressBar value={pct} />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// QUEST ENGINE
// ─────────────────────────────────────────────────────────────────────────────
function QuestEngine({
  quests,
  doneQuiz,
  doneTrivia,
  samaritan,
  preachLog,
  onAnswerQuiz,
  onAnswerTrivia,
  onLogSamaritan,
  onSavePreach,
}: {
  quests: QuestItem[];
  doneQuiz: DoneMap;
  doneTrivia: DoneMap;
  samaritan: SamaritanMap;
  preachLog: PreachEntry[];
  onAnswerQuiz: (qi: number, sel: string) => void;
  onAnswerTrivia: (qi: number, sel: string) => void;
  onLogSamaritan: (act: (typeof SAMARITAN_ACTS)[0], e: ButtonEvent) => void;
  onSavePreach: (text: string, prompt: string) => void;
}) {
  const today = todayKey();
  const [currentIdx, setCurrentIdx] = useState<number>(() => {
    let start = 0;
    for (let i = 0; i < quests.length; i++) {
      if (isQuestDone(quests[i], doneQuiz, doneTrivia, samaritan, preachLog)) {
        start = i + 1;
      } else break;
    }
    return Math.min(start, quests.length - 1);
  });
  const [quizSel, setQuizSel] = useState<string | null>(null);
  const [triviaSel, setTriviaSel] = useState<string | null>(null);
  const [preachText, setPreachText] = useState("");
  const [preachIdx] = useState(() =>
    Math.floor(Math.random() * PREACH_PROMPTS.length)
  );

  const quest = quests[currentIdx];

  function isQuestDone(
    q: QuestItem,
    dq: DoneMap,
    dt: DoneMap,
    sam: SamaritanMap,
    pl: PreachEntry[]
  ): boolean {
    if (q.kind === "verse") return !!dq[q.idx];
    if (q.kind === "trivia") return !!dt[q.idx];
    if (q.kind === "samaritan") return !!sam[SAMARITAN_ACTS[q.idx].id];
    if (q.kind === "preach") return pl.some((e) => e.date === today);
    return false;
  }

  function goToQuest(nextIdx: number) {
    setCurrentIdx(Math.max(0, Math.min(quests.length - 1, nextIdx)));
    setQuizSel(null);
    setTriviaSel(null);
    setPreachText("");
  }

  function advance() {
    goToQuest(currentIdx + 1);
  }

  const doneCount = quests.filter((q) =>
    isQuestDone(q, doneQuiz, doneTrivia, samaritan, preachLog)
  ).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="quest-progress-dots">
        {quests.map((q, i) => {
          const done = isQuestDone(
            q,
            doneQuiz,
            doneTrivia,
            samaritan,
            preachLog
          );
          return (
            <button
              type="button"
              key={i}
              aria-label={`Go to quest ${i + 1}`}
              onClick={() => goToQuest(i)}
              className={`quest-dot${
                done ? " done-dot" : i === currentIdx ? " active-dot" : ""
              }`}
            />
          );
        })}
      </div>
      <div style={{ textAlign: "center", marginBottom: -4 }}>
        <span
          style={{
            fontSize: 13.5,
            color: C.textFaint,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
          }}
        >
          Quest {currentIdx + 1} of {quests.length} · {doneCount} complete
        </span>
      </div>

      <div className="quest-card" key={currentIdx}>
        {quest.kind === "verse" &&
          (() => {
            const q = VERSE_QUIZ[quest.idx];
            const alreadyDone = !!doneQuiz[quest.idx];
            return (
              <>
                <div
                  style={{
                    fontSize: 13.5,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: C.textFaint,
                    fontWeight: 700,
                    marginBottom: 10,
                  }}
                >
                  📖 Verse Quiz · +{q.reward} Righteousness
                </div>
                <p
                  className="serif"
                  style={{
                    fontSize: 16,
                    lineHeight: 1.75,
                    color: "#4F3510",
                    fontStyle: "normal",
                    margin: "0 0 18px",
                  }}
                >
                  "{q.verse}"
                </p>
                <p
                  style={{
                    fontSize: 13.5,
                    color: C.textFaint,
                    margin: "0 0 14px",
                  }}
                >
                  — {q.ref}
                </p>
                {alreadyDone ? (
                  <div
                    style={{
                      textAlign: "center",
                      color: C.green,
                      fontWeight: 800,
                      fontSize: 15,
                    }}
                  >
                    ✓ Answered · Correct answer: {q.answer}
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {q.options.map((opt) => {
                        const sel = quizSel === opt;
                        const show = !!quizSel;
                        const correct = opt === q.answer;
                        return (
                          <button
                            key={opt}
                            disabled={!!quizSel}
                            onClick={() => {
                              if (quizSel) return;
                              setQuizSel(opt);
                              if (opt === q.answer) {
                                onAnswerQuiz(quest.idx, opt);
                                setTimeout(advance, 900);
                              }
                            }}
                            className={`opt-btn${
                              show && correct
                                ? " correct"
                                : show && sel && !correct
                                ? " wrong"
                                : ""
                            }`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    {quizSel && quizSel !== q.answer && (
                      <div
                        style={{
                          marginTop: 12,
                          background: "rgba(246,250,255,.78)",
                          border: `1px solid ${C.cardBorder}`,
                          borderRadius: 12,
                          padding: "10px 12px",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 8px",
                            fontSize: 13.5,
                            color: C.textMid,
                            lineHeight: 1.45,
                          }}
                        >
                          Correct answer:{" "}
                          <strong style={{ color: C.green }}>{q.answer}</strong>
                        </p>
                        <GoldBtn
                          onClick={() => setQuizSel(null)}
                          style={{
                            width: "100%",
                            padding: "9px 12px",
                            minHeight: 40,
                          }}
                        >
                          Try Again
                        </GoldBtn>
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}

        {quest.kind === "trivia" &&
          (() => {
            const q = BIBLE_TRIVIA[quest.idx];
            const alreadyDone = !!doneTrivia[quest.idx];
            return (
              <>
                <div
                  style={{
                    fontSize: 13.5,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: C.textFaint,
                    fontWeight: 700,
                    marginBottom: 10,
                  }}
                >
                  🏆 Bible Trivia · +{q.reward} Righteousness
                </div>
                <p
                  style={{
                    fontSize: 16,
                    color: C.textDark,
                    margin: "0 0 18px",
                    lineHeight: 1.55,
                    fontWeight: 600,
                  }}
                >
                  {q.q}
                </p>
                {alreadyDone ? (
                  <div
                    style={{
                      textAlign: "center",
                      color: C.green,
                      fontWeight: 800,
                      fontSize: 15,
                    }}
                  >
                    ✓ Answered · Correct answer: {q.answer}
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {q.options.map((opt) => {
                        const sel = triviaSel === opt;
                        const show = !!triviaSel;
                        const correct = opt === q.answer;
                        return (
                          <button
                            key={opt}
                            disabled={!!triviaSel}
                            onClick={() => {
                              if (triviaSel) return;
                              setTriviaSel(opt);
                              if (opt === q.answer) {
                                onAnswerTrivia(quest.idx, opt);
                                setTimeout(advance, 900);
                              }
                            }}
                            className={`opt-btn${
                              show && correct
                                ? " correct"
                                : show && sel && !correct
                                ? " wrong"
                                : ""
                            }`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    {triviaSel && triviaSel !== q.answer && (
                      <div
                        style={{
                          marginTop: 12,
                          background: "rgba(246,250,255,.78)",
                          border: `1px solid ${C.cardBorder}`,
                          borderRadius: 12,
                          padding: "10px 12px",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 8px",
                            fontSize: 13.5,
                            color: C.textMid,
                            lineHeight: 1.45,
                          }}
                        >
                          Correct answer:{" "}
                          <strong style={{ color: C.green }}>{q.answer}</strong>
                        </p>
                        <GoldBtn
                          onClick={() => setTriviaSel(null)}
                          style={{
                            width: "100%",
                            padding: "9px 12px",
                            minHeight: 40,
                          }}
                        >
                          Try Again
                        </GoldBtn>
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}

        {quest.kind === "samaritan" &&
          (() => {
            const act = SAMARITAN_ACTS[quest.idx];
            const done = !!samaritan[act.id];
            return (
              <>
                <div
                  style={{
                    fontSize: 13.5,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: C.textFaint,
                    fontWeight: 700,
                    marginBottom: 12,
                  }}
                >
                  🤝 Good Samaritan · +{act.reward} Righteousness
                </div>
                <p
                  style={{
                    margin: "0 0 18px",
                    fontSize: 13,
                    color: C.textSoft,
                    lineHeight: 1.65,
                  }}
                >
                  Log an act of love and kindness today.
                </p>
                <button
                  onClick={(e: ButtonEvent) => {
                    if (done) return;
                    onLogSamaritan(act, e);
                    setTimeout(advance, 600);
                  }}
                  disabled={done}
                  className="sam-btn"
                  style={
                    done
                      ? {}
                      : {
                          background: "rgba(255,250,200,.8)",
                          borderColor: C.gold,
                        }
                  }
                >
                  <span style={{ fontSize: 28 }}>{act.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 15,
                        color: done ? C.goldDeep : C.textDark,
                        fontWeight: done ? 700 : 600,
                      }}
                    >
                      {act.label}
                    </div>
                    <div
                      style={{
                        fontSize: 13.5,
                        color: done ? C.gold : C.textFaint,
                        marginTop: 2,
                      }}
                    >
                      {done
                        ? "✓ logged today"
                        : `Tap to log · +${act.reward} righteousness`}
                    </div>
                  </div>
                </button>
                {done && (
                  <GoldBtn
                    onClick={advance}
                    style={{ marginTop: 14, width: "100%" }}
                  >
                    Next Quest →
                  </GoldBtn>
                )}
              </>
            );
          })()}

        {quest.kind === "preach" &&
          (() => {
            const todayEntry = preachLog.find((e) => e.date === today);
            return (
              <>
                <div
                  style={{
                    fontSize: 13.5,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: C.textFaint,
                    fontWeight: 700,
                    marginBottom: 10,
                  }}
                >
                  📢 Preach the Word · +40 Righteousness
                </div>
                {todayEntry ? (
                  <div
                    style={{
                      textAlign: "center",
                      color: C.green,
                      fontWeight: 800,
                      fontSize: 15,
                    }}
                  >
                    ✓ Word shared today!
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        background: `${C.goldShine}55`,
                        border: `1.5px solid ${C.gold}60`,
                        borderRadius: 12,
                        padding: "12px 14px",
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13.5,
                          letterSpacing: 2,
                          textTransform: "uppercase",
                          color: C.textFaint,
                          fontWeight: 700,
                          marginBottom: 6,
                        }}
                      >
                        today's prompt
                      </div>
                      <p
                        className="serif"
                        style={{
                          margin: 0,
                          fontSize: 14,
                          color: C.textMid,
                          lineHeight: 1.65,
                          fontStyle: "normal",
                        }}
                      >
                        "{PREACH_PROMPTS[preachIdx]}"
                      </p>
                    </div>
                    <textarea
                      value={preachText}
                      onChange={(e) => setPreachText(e.target.value)}
                      placeholder="Dear friend, I want you to know..."
                      className="sg-textarea"
                      style={{ minHeight: 130 }}
                    />
                    <GoldBtn
                      onClick={() => {
                        if (!preachText.trim()) return;
                        onSavePreach(
                          preachText.trim(),
                          PREACH_PROMPTS[preachIdx]
                        );
                        setTimeout(advance, 700);
                      }}
                      style={{ marginTop: 12, width: "100%" }}
                    >
                      Preach the Word 📢
                    </GoldBtn>
                  </>
                )}
              </>
            );
          })()}
      </div>

      <div className="quest-nav-row">
        <button
          onClick={() => goToQuest(currentIdx - 1)}
          disabled={currentIdx === 0}
          className="quest-nav-btn"
        >
          ← Back
        </button>
        <button
          onClick={() => goToQuest(currentIdx + 1)}
          disabled={currentIdx >= quests.length - 1}
          className="quest-nav-btn primary"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HEAVENLY CLOSET
// ─────────────────────────────────────────────────────────────────────────────
function HeavenlyCloset({
  ownedIds,
  equippedIds,
  onChange,
  onClose,
  onSell,
  onGoShop,
  gender,
  skinTone,
  hairColor,
  stageKey,
  stageAura,
  stageGlow,
  xp,
  xpMax,
}: {
  ownedIds: string[];
  equippedIds: string[];
  onChange: (ids: string[]) => void;
  onClose: () => void;
  onSell: (item: ClothingItem) => void;
  onGoShop: () => void;
  gender: Gender;
  skinTone: string;
  hairColor: string;
  stageKey: string;
  stageAura: string;
  stageGlow: string;
  xp: number;
  xpMax: number;
}) {
  const owned = CLOTHES.filter((c) => ownedIds.includes(c.id));
  const equippedObjs: ClothingItem[] = equippedIds
    .map((id) => CLOTHES.find((c) => c.id === id))
    .filter((c): c is ClothingItem => Boolean(c));
  const [sellTarget, setSellTarget] = useState<ClothingItem | null>(null);

  function toggle(item: ClothingItem) {
    if (equippedIds.includes(item.id)) {
      onChange(equippedIds.filter((id) => id !== item.id));
      return;
    }
    if (item.slot === "set") {
      onChange([
        ...equippedIds.filter(
          (id) => CLOTHES.find((x) => x.id === id)?.slot !== "set"
        ),
        item.id,
      ]);
      return;
    }
    const replace = equippedIds.filter(
      (id) => CLOTHES.find((x) => x.id === id)?.slot === item.slot
    );
    onChange([...equippedIds.filter((id) => !replace.includes(id)), item.id]);
  }

  const closetItems = [...owned].sort((a, b) => {
    const aSlot = SLOT_ORDER.indexOf(a.slot);
    const bSlot = SLOT_ORDER.indexOf(b.slot);
    return (aSlot === -1 ? 999 : aSlot) - (bSlot === -1 ? 999 : bSlot);
  });

  return (
    <div
      className="closet-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="closet-sheet">
        {sellTarget && (
          <SellModal
            itemName={sellTarget.name}
            itemIcon={sellTarget.icon}
            sellPrice={Math.floor(sellTarget.cost * 0.5)}
            onConfirm={() => {
              onSell(sellTarget);
              setSellTarget(null);
            }}
            onCancel={() => setSellTarget(null)}
          />
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "12px 0 0",
          }}
        >
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 999,
              background: `${C.gold}50`,
            }}
          />
        </div>
        <div className="closet-header">
          <div>
            <h2
              className="serif"
              style={{ margin: 0, fontSize: 19, color: C.textDark }}
            >
              🛡️ Armor Closet
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: C.textMid }}>
              Equip your spirit · sell pieces you no longer need
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: `${C.gold}25`,
              border: `1px solid ${C.gold}`,
              borderRadius: 12,
              width: 40,
              height: 40,
              cursor: "pointer",
              fontSize: 20,
              color: C.textDark,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: "14px 18px",
            background: `${C.goldShine}30`,
            borderBottom: `1px solid ${C.gold}30`,
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <div
            style={{
              animation: "float 3.5s ease-in-out infinite",
              flexShrink: 0,
            }}
          >
            <SpiritAvatar
              stageKey={stageKey}
              xp={xp}
              xpMax={xpMax}
              stageAura={stageAura}
              stageGlow={stageGlow}
              animated={false}
              equipped={equippedObjs}
              gender={gender}
              skinTone={skinTone}
              hairColor={hairColor}
              size={94}
              showBar={false}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: "0 0 6px",
                fontSize: 13.5,
                color: C.textFaint,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Wearing now
            </p>
            {equippedObjs.length === 0 ? (
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: C.textFaint,
                  fontStyle: "normal",
                }}
              >
                Nothing equipped yet
              </p>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {equippedObjs.map((item) => (
                  <span
                    key={item.id}
                    style={{
                      background: "rgba(255,253,240,.8)",
                      border: `1px solid ${C.gold}50`,
                      borderRadius: 8,
                      padding: "3px 8px",
                      fontSize: 13,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      color: C.textDark,
                      fontWeight: 600,
                    }}
                  >
                    {item.icon}{" "}
                    <span style={{ fontSize: 13.5 }}>{item.name}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: "16px 18px" }}>
          {owned.length === 0 ? (
            <div className="empty-state" style={{ margin: "18px 0" }}>
              <div style={{ fontSize: 38, marginBottom: 8 }}>🛡️</div>
              <p className="empty-state-title">Your closet is waiting.</p>
              <p
                style={{ margin: "0 0 14px", fontSize: 13.5, lineHeight: 1.6 }}
              >
                Add one armor piece first. Each piece has a meaning so the
                closet stays simple to understand.
              </p>
              <GoldBtn onClick={onGoShop} style={{ width: "100%" }}>
                Go to Armor Shop
              </GoldBtn>
            </div>
          ) : (
            <div>
              <SectionLabel>Closet Pieces</SectionLabel>
              <div className="closet-pieces-grid">
                {closetItems.map((item) => {
                  const on = equippedIds.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <button
                        onClick={() => toggle(item)}
                        className={`closet-item-btn${on ? " equipped" : ""}`}
                        style={{
                          background: on
                            ? `linear-gradient(135deg,${item.color},${item.color}dd)`
                            : C.cardBg,
                        }}
                      >
                        <ShopGameIcon item={item} />
                        <div
                          className="closet-item-name"
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: C.textDark,
                            lineHeight: 1.3,
                          }}
                        >
                          {item.name}
                        </div>
                        <span className="meaning-chip">
                          {clothingMeaning(item)}
                        </span>
                        {on && (
                          <div
                            style={{
                              fontSize: 13.5,
                              color: C.gold,
                              fontWeight: 800,
                            }}
                          >
                            ✓ ON
                          </div>
                        )}
                      </button>
                      <button
                        onClick={() => setSellTarget(item)}
                        style={{
                          background: "rgba(255,240,220,.6)",
                          border: `1px solid ${C.cardBorder}`,
                          borderRadius: 8,
                          padding: "4px 10px",
                          color: C.textFaint,
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight: 700,
                        }}
                      >
                        Sell
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type ShopItemMotion = "walk" | "flutter" | "hop" | "glow" | "sway" | "idle";

function itemMotionSeed(id: string): number {
  return id
    .split("")
    .reduce((sum, ch, idx) => sum + ch.charCodeAt(0) * (idx + 3), 17);
}

function getShopItemMotion(item: HomeItem): ShopItemMotion {
  if (item.id.includes("butterfly")) return "flutter";
  if (item.id.includes("dove")) return "flutter";
  if (item.id.includes("lamb")) return "walk";
  if (item.slot === "pet") return "hop";
  if (
    item.id.includes("lamp") ||
    item.id.includes("lantern") ||
    item.id.includes("fountain")
  )
    return "glow";
  if (
    item.slot === "garden" ||
    item.id.includes("vines") ||
    item.id.includes("rainbow")
  )
    return "sway";
  if (item.slot === "decor" || item.slot === "furniture") return "idle";
  return "idle";
}

function AnimatedShopItem({
  item,
  children,
  wide = false,
}: {
  item: HomeItem;
  children: React.ReactNode;
  wide?: boolean;
}) {
  const seed = itemMotionSeed(item.id);
  const motion = getShopItemMotion(item);
  const isButterfly = item.id.includes("butterfly");
  const isLamb = item.id.includes("lamb");
  const isDove = item.id.includes("dove");
  const duration = isLamb
    ? wide
      ? 13 + (seed % 4)
      : 11 + (seed % 4)
    : isButterfly
    ? wide
      ? 8.2 + (seed % 4) * 0.7
      : 6.4 + (seed % 4) * 0.55
    : isDove
    ? wide
      ? 9.5 + (seed % 4) * 0.6
      : 6.2 + (seed % 4) * 0.5
    : motion === "walk"
    ? 8.5 + (seed % 5)
    : motion === "flutter"
    ? 5.2 + (seed % 4) * 0.7
    : motion === "hop"
    ? 4.6 + (seed % 3) * 0.6
    : 4.8 + (seed % 4) * 0.5;
  const drift = isLamb
    ? wide
      ? 94 + (seed % 26)
      : 40 + (seed % 18)
    : isButterfly
    ? wide
      ? 118 + (seed % 34)
      : 58 + (seed % 24)
    : isDove
    ? wide
      ? 88 + (seed % 28)
      : 32 + (seed % 16)
    : motion === "walk"
    ? 20 + (seed % 12)
    : motion === "flutter"
    ? 12 + (seed % 12)
    : motion === "hop"
    ? 7 + (seed % 5)
    : 5 + (seed % 4);

  return (
    <div
      className={`animated-shop-item motion-${motion}`}
      style={
        {
          "--motion-delay": `${-(seed % 37) / 10}s`,
          "--motion-duration": `${duration}s`,
          "--drift": `${drift}px`,
        } as React.CSSProperties
      }
    >
      {children}
    </div>
  );
}

function HomeShopPreviewObject({ item }: { item: HomeItem }) {
  const accent = item.accent || item.color || C.goldLight;
  const details =
    item.slot === "garden" ? (
      <>
        <span className="home-game-detail leaf-a" />
        <span className="home-game-detail flower-a" />
      </>
    ) : item.slot === "furniture" ? (
      <>
        <span className="home-game-detail water" />
        <span className="home-game-detail base" />
      </>
    ) : item.slot === "decor" ? (
      <span className="home-game-detail base" />
    ) : item.slot === "pet" ? (
      <>
        <span className="home-game-detail wing-left" />
        <span className="home-game-detail wing-right" />
      </>
    ) : item.slot === "wallpaper" ? (
      <span className="home-game-detail cloud-a" />
  ) : item.slot === "floor" ? (
    <span className="home-game-detail base" />
  ) : item.slot === "effect" ? (
    <>
      <span className="home-game-detail halo" />
      <span className="home-game-detail star" />
    </>
  ) : item.slot === "window" ? (
    <span className="home-game-detail cross" />
  ) : null;

  return (
    <div
      className={`home-shop-preview preview-${item.slot}`}
      aria-hidden="true"
    >
      <AnimatedShopItem item={item}>
        <div
          className={`home-game-item ${item.slot} ${item.id}`}
          style={
            {
              "--accent": accent,
              "--accent-soft": `${accent}55`,
              "--wallpaper-bg":
                item.bg || "linear-gradient(180deg,#e8f6ff,#fff8df)",
            } as React.CSSProperties
          }
        >
          <span className="home-game-shadow" />
          <span className="home-game-glow" />
          <span className="home-game-body" />
          {details}
          <span className="home-game-icon" />
          <span className="home-game-sparkles" aria-hidden="true">
            <span>✦</span>
            <span>✧</span>
            <span>⋆</span>
          </span>
        </div>
      </AnimatedShopItem>
    </div>
  );
}

function WaterFeature({ itemId }: { itemId: string }) {
  return (
    <div
      className={`home-water-feature home-water-stream ${itemId}`}
      aria-hidden="true"
    >
      <span className="water-ripple r1" />
      <span className="water-ripple r2" />
      <span className="water-shimmer" />
      <span className="water-bubble b1" />
      <span className="water-bubble b2" />
      <span className="water-bubble b3" />
    </div>
  );
}

function PalaceDetails() {
  return (
    <>
      <div className="home-palace-aura" />
      <div className="home-palace-column c1" />
      <div className="home-palace-column c2" />
      <div className="home-palace-steps" />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HEAVENLY HOME PREVIEW
// ─────────────────────────────────────────────────────────────────────────────
function HeavenlyHomePreview({
  placedHomeItems,
  homeBlooms = [],
  spiritVisitors = [],
  spirit,
  neglectDarkness = 0,
  homeRestored = false,
  homeBlessed = false,
  homeTransforming = false,
  homeGrowthStage = HOME_GROWTH_STAGES[0],
  homeCareMood = "peaceful",
  homeCareState,
  faithFlightActive = false,
  faithFlightUnlocked = false,
  faithFlightProgress = 0,
  onFaithFlight,
}: {
  placedHomeItems: PlacedHomeMap;
  homeBlooms?: HomeBloom[];
  spiritVisitors?: SpiritVisitor[];
  spirit?: HomeSpiritPreview;
  neglectDarkness?: number;
  homeRestored?: boolean;
  homeBlessed?: boolean;
  homeTransforming?: boolean;
  homeGrowthStage?: HomeGrowthStage;
  homeCareMood?: HomeCareMood;
  homeCareState?: HomeCareState;
  faithFlightActive?: boolean;
  faithFlightUnlocked?: boolean;
  faithFlightProgress?: number;
  onFaithFlight?: () => void;
}) {
  const [spiritAction, setSpiritAction] = useState<
    "wave" | "pray" | "sit" | "dance" | "shine" | null
  >(null);
  const [showSpiritActions, setShowSpiritActions] = useState<boolean>(false);
  const [companionTooltip, setCompanionTooltip] = useState<string | null>(null);
  const [homeParallax, setHomeParallax] = useState({ x: 0, y: 0 });
  const getItem = (slot: HomeSlot) =>
    HOME_ITEMS.find((item) => item.id === placedHomeItems[slot]);
  const wallpaper = getItem("wallpaper"),
    floor = getItem("floor"),
    home = getItem("window"),
    feature = getItem("furniture"),
    decor = getItem("decor"),
    pet = getItem("pet"),
    garden = getItem("garden"),
    effect = getItem("effect");
  const showBackgroundRainbow =
    decor?.id === "dec_rainbow" || effect?.id === "eff_covenant_rainbow";
  const showGates = false;
  const showLanterns = false;
  const palaceAccent = home?.accent || C.goldLight;
  const homeStyleClass = home ? `home-style-${home.id}` : "";
  const visibleBlooms = homeBlooms.slice(0, 12);
  const visibleVisitors = activeSpiritVisitors(spiritVisitors);
  const hour = new Date().getHours();
  const timeOfDay =
    hour >= 5 && hour < 11
      ? "morning"
      : hour >= 11 && hour < 17
      ? "day"
      : hour >= 17 && hour < 20
      ? "evening"
      : "night";
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const handleHomePointerMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduceMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setHomeParallax({
      x: ((e.clientX - rect.left) / rect.width - 0.5) * 2,
      y: ((e.clientY - rect.top) / rect.height - 0.5) * 2,
    });
  };
  useEffect(() => {
    if (reduceMotion || typeof window === "undefined") return;
    const handleOrientation = (event: DeviceOrientationEvent) => {
      const gamma = Math.max(-20, Math.min(20, event.gamma || 0));
      const beta = Math.max(-20, Math.min(20, (event.beta || 45) - 45));
      setHomeParallax({ x: gamma / 20, y: beta / 20 });
    };
    window.addEventListener("deviceorientation", handleOrientation);
    return () => window.removeEventListener("deviceorientation", handleOrientation);
  }, [reduceMotion]);
  const playSpiritAction = (
    next: "wave" | "pray" | "sit" | "dance" | "shine"
  ) => {
    setShowSpiritActions(false);
    setSpiritAction(next);
    const actionDuration =
      next === "pray"
        ? 3000
        : next === "shine"
        ? 2300
        : next === "dance"
        ? 2200
        : next === "sit"
        ? 2200
        : 1900;
    window.setTimeout(() => setSpiritAction(null), actionDuration);
  };
  const showCompanionAffection = (name: string) => {
    setCompanionTooltip(name);
    window.setTimeout(() => setCompanionTooltip(null), 2600);
  };

  const placedStyle = (base: React.CSSProperties): React.CSSProperties => ({
    ...base,
    pointerEvents: "none",
  });
  const renderHomeObject = (item: HomeItem) => {
    const accent = item.accent || C.goldLight;
    const details =
      item.slot === "garden" ? (
        <>
          <span className="home-game-detail leaf-a" />
          <span className="home-game-detail flower-a" />
        </>
      ) : item.slot === "furniture" ? (
        <>
          <span className="home-game-detail water" />
          <span className="home-game-detail base" />
        </>
      ) : item.slot === "decor" ? (
        <span className="home-game-detail base" />
      ) : item.slot === "pet" ? (
        <>
          <span className="home-game-detail wing-left" />
          <span className="home-game-detail wing-right" />
        </>
      ) : item.slot === "effect" ? (
        <>
          <span className="home-game-detail halo" />
          <span className="home-game-detail star" />
        </>
      ) : null;

    return (
      <AnimatedShopItem
        item={item}
        wide={
          item.id === "pet_lamb" ||
          item.id === "pet_butterfly" ||
          item.id === "pet_dove"
        }
      >
        <div
          className={`home-game-item ${item.slot} ${item.id}`}
          style={
            {
              "--accent": accent,
              "--accent-soft": `${accent}55`,
            } as React.CSSProperties
          }
          aria-label={item.name}
        >
          <span className="home-game-shadow" />
          <span className="home-game-glow" />
          <span className="home-game-body" />
          {details}
          <span className="home-game-icon" />
          <span className="home-game-sparkles" aria-hidden="true">
            <span>✦</span>
            <span>✧</span>
            <span>⋆</span>
          </span>
        </div>
      </AnimatedShopItem>
    );
  };
  const renderSpiritVisitor = (visitor: SpiritVisitor, index: number) => (
    <div
      key={visitor.id}
      className={`spirit-visitor visitor-${visitor.kind} visitor-${index}`}
      aria-label={`${SPIRIT_VISITOR_COPY[visitor.kind].name} visitor`}
      title={visitor.reason}
    >
      <span className="visitor-shadow" />
      <span className="visitor-aura" />
      <span className="visitor-wing wing-left" />
      <span className="visitor-wing wing-right" />
      <span className="visitor-body" />
      <span className="visitor-head" />
      <span className="visitor-ear ear-left" />
      <span className="visitor-ear ear-right" />
      <span className="visitor-tail" />
      <span className="visitor-mark" />
    </div>
  );

  // Darkness overlay based on neglect
  const darkOpacity = neglectDarkness;
  const darkHue = neglectDarkness > 0.5 ? "rgba(20,10,40," : "rgba(40,30,10,";

  return (
    <div
      className={`home-room home-stage-${homeGrowthStage.level}${
        homeRestored ? " home-room-complete" : ""
      }${homeBlessed ? " home-room-blessed" : ""}${
        homeTransforming ? " home-room-transforming" : ""
      }${faithFlightActive ? " home-faith-flight-active" : ""
      }${spiritAction ? ` home-spirit-action-${spiritAction}` : ""
      }${floor ? ` home-floor-${floor.id}` : ""
      } home-care-${homeCareMood} home-time-${timeOfDay}`}
      onMouseMove={handleHomePointerMove}
      onMouseLeave={() => setHomeParallax({ x: 0, y: 0 })}
      style={
        {
          "--parallax-x": homeParallax.x.toFixed(3),
          "--parallax-y": homeParallax.y.toFixed(3),
        } as React.CSSProperties
      }
    >
      <div className={`home-care-aura ${homeCareMood}`} aria-hidden="true" />
      <div className="home-ambient-particles" aria-hidden="true">
        <span className="particle p1" />
        <span className="particle p2" />
        <span className="particle p3" />
        <span className="particle p4" />
        <span className="particle p5" />
      </div>
      {/* Sky — locked */}
      <div
        className="home-sky"
        style={{
          background:
            wallpaper?.bg ||
            "linear-gradient(180deg,#bfe8ff 0%,#fff7d6 54%,#ffe5a3 100%)",
        }}
      >
        <div className="home-rays" />
        <div className="home-sunbeam-drift" aria-hidden="true" />
        <div className="home-cloud c1" />
        <div className="home-cloud c2" />
        <div className="home-cloud c3" />
        <span className="home-sky-bird bird-a" aria-hidden="true" />
        <span className="home-sky-bird bird-b" aria-hidden="true" />
        <div className="home-distant-city" />
        {showBackgroundRainbow && (
          <div className="home-background-rainbow" aria-hidden="true" />
        )}
        {homeGrowthStage.level >= 6 && <div className="home-realm-planet" />}
        {homeGrowthStage.level >= 6 && <div className="home-heavenly-veil" />}
      </div>

      {/* Floor — locked */}
      <div
        className="home-floor"
        style={{
          background: `linear-gradient(180deg,#fff7d6 0%, ${
            floor?.color || "#f7d778"
          } 100%)`,
        }}
      />
      <div className="home-midground-glow" />
      <div className="home-path" />
      <div className={`home-signature-vessel vessel-${HOME_STAGE_SIGNATURES[homeGrowthStage.level]?.vessel || "seed"}`} aria-hidden="true">
        <span className="vessel-bowl" />
        <span className="vessel-light" />
        <span className="vessel-cross" />
      </div>
      <div className="home-word-scroll" aria-hidden="true">
        <span>Heaven's Word</span>
      </div>
      <div className="home-flowerbed left" />
      <div className="home-flowerbed right" />
      <span className="home-gem g1" aria-hidden="true" />
      <span className="home-gem g2" aria-hidden="true" />
      <span className="home-gem g3" aria-hidden="true" />
      <span className="home-gem g4" aria-hidden="true" />
      <span className="home-path-gem pg1" aria-hidden="true" />
      <span className="home-path-gem pg2" aria-hidden="true" />
      <span className="home-path-gem pg3" aria-hidden="true" />
      {homeCareState?.completedWalk && (
        <div className="home-walk-blessing" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      )}
      {homeCareState?.prayedToday && (
        <div className="home-spirit-visitor" aria-hidden="true">
          <span className="visitor-aura" />
          <span className="visitor-head" />
          <span className="visitor-body" />
        </div>
      )}
      {homeCareState?.journaledToday && (
        <div className="home-reflection-butterflies" aria-hidden="true">
          <span className="reflection-butterfly b1" />
          <span className="reflection-butterfly b2" />
        </div>
      )}
      {homeCareState?.answeredToday && (
        <div className="home-answer-light" aria-hidden="true">
          <span className="answer-butterfly wing-one" />
          <span className="answer-butterfly wing-two" />
        </div>
      )}
      {homeCareState?.answeredToday && (
        <div className="home-answer-dove" aria-hidden="true">
          <span className="answer-dove-wing left" />
          <span className="answer-dove-wing right" />
          <span className="answer-dove-body" />
          <span className="answer-dove-head" />
        </div>
      )}
      {homeCareState?.testimonySharedToday && (
        <div className="home-testimony-particles" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>
      )}
      {homeGrowthStage.level >= 5 && <div className="home-realm-country" />}

      {/* Label chips — locked */}
      <div className="home-floating-label">
        <span className="placed-chip">{homeGrowthStage.icon} {homeGrowthStage.name}</span>
        <span className="placed-chip">
          {floor ? `${floor.icon} ${floor.name}` : "🟨 Golden Path"}
        </span>
      </div>

      {/* Home building — handmade in CSS so the space feels personal and app-native. */}
      {homeGrowthStage.level === 0 ? (
        <div className={`home-cardboard ${homeStyleClass}`}>
          <div className="home-cardboard-roof" />
          <div className="home-cardboard-body" />
          <div className="home-cardboard-door" />
        </div>
      ) : homeGrowthStage.level === 1 ? (
        <div className={`home-little-shelter ${homeStyleClass}`}>
          <div className="home-little-shelter-roof" />
          <div className="home-little-shelter-body" />
          <div className="home-little-shelter-door" />
        </div>
      ) : homeGrowthStage.level === 2 ? (
        <div className={`home-house ${homeStyleClass}`}>
          <div className="home-house-roof" />
          <div className="home-house-body" />
          <div className="home-house-door" />
        </div>
      ) : homeGrowthStage.level === 3 ? (
        <div className={`home-mansion ${homeStyleClass}`}>
          <div className="home-mansion-roof" />
          <div className="home-mansion-body" />
          <div className="home-mansion-column c1" />
          <div className="home-mansion-column c2" />
          <div className="home-mansion-door" />
        </div>
      ) : (
        <div className={`home-palace ${homeStyleClass}`}>
          <div className="home-palace-star">✦</div>
          <div className="home-tower left" />
          <div className="home-tower right" />
          <div className="home-palace-roof" />
          <div className="home-palace-body" />
          <div className="home-door" />
          <PalaceDetails />
        </div>
      )}

      {/* Gates & Lanterns — locked structural elements (z-index:3, pointer-events:none) */}
      {showGates && (
        <>
          <div className="home-gate left" />
          <div className="home-gate right" />
        </>
      )}
      {showLanterns && (
        <>
          <div className="home-lantern left" />
          <div className="home-lantern right" />
        </>
      )}

      {/* Placed items */}
      {garden && (
        <div
          className="home-slot garden-slot"
          style={placedStyle({
            bottom: garden.id === "garden_vines" ? "22%" : "7%",
            left: garden.id === "garden_vines" ? "4%" : "5%",
            zIndex: 4,
          })}
        >
          {renderHomeObject(garden)}
        </div>
      )}
      {feature &&
        ["furn_fountain", "furn_waterfall", "furn_lily_pond"].includes(
          feature.id
        ) && <WaterFeature itemId={feature.id} />}
      {feature && (
        <div
          className={`home-slot feature-slot ${feature.id}-slot`}
          style={placedStyle(
            feature.id === "furn_fountain"
              ? { bottom: "9%", right: "11%", zIndex: 6 }
              : feature.id === "furn_waterfall"
              ? { bottom: "14%", right: "8%", zIndex: 6 }
              : feature.id === "furn_lily_pond"
              ? { bottom: "7%", right: "12%", zIndex: 6 }
              : { bottom: "9%", right: "11%", zIndex: 6 }
          )}
        >
          {renderHomeObject(feature)}
        </div>
      )}
      {pet && (
        <div
          className={`home-slot${companionTooltip ? " companion-active" : ""}${
            pet.id === "pet_lamb"
              ? " home-pet-track lamb-track"
              : pet.id === "pet_butterfly"
              ? " home-pet-track butterfly-track"
              : pet.id === "pet_dove"
              ? " home-pet-track dove-track"
              : ""
          }`}
          style={{
            ...(pet.id === "pet_lamb"
              ? { bottom: "15%", left: "5%", right: "58%", zIndex: 5 }
              : pet.id === "pet_butterfly"
              ? { top: "33%", left: "60%", right: "4%", zIndex: 5 }
              : pet.id === "pet_dove"
              ? { top: "38%", left: "5%", right: "60%", zIndex: 5 }
              : { bottom: "17%", left: "5%", right: "62%", zIndex: 5 }),
            pointerEvents: "auto",
          }}
          onClick={(e) => {
            e.stopPropagation();
            showCompanionAffection(pet.name);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              showCompanionAffection(pet.name);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Interact with ${pet.name}`}
        >
          {renderHomeObject(pet)}
          {companionTooltip && (
            <div className="companion-tooltip">
              <span>{companionTooltip}</span>
              <span aria-hidden="true">♡</span>
              <b aria-hidden="true">✦</b>
            </div>
          )}
        </div>
      )}

      {visibleVisitors.length > 0 && (
        <div className="spirit-visitors-layer" aria-hidden="true">
          {visibleVisitors.map((visitor, index) =>
            renderSpiritVisitor(visitor, index)
          )}
        </div>
      )}

      {effect && (
        <div
          className={`home-heavenly-effect ${effect.id}`}
          aria-hidden="true"
        >
          <span className="effect-cloud c1" />
          <span className="effect-cloud c2" />
          <span className="effect-cloud c3" />
          <span className="effect-ray r1" />
          <span className="effect-ray r2" />
          <span className="effect-ray r3" />
          <span className="effect-rainbow" />
          <span className="effect-star s1" />
          <span className="effect-star s2" />
          <span className="effect-star s3" />
          <span className="effect-orbit o1" />
          <span className="effect-orbit o2" />
        </div>
      )}

      {/* Spirit */}
	      {spirit && (
	        <div
	          className="home-slot home-spirit-slot"
            role="button"
            tabIndex={0}
            aria-label="Open spirit actions"
            aria-expanded={showSpiritActions}
            onClick={() => setShowSpiritActions((open) => !open)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowSpiritActions((open) => !open);
              }
            }}
	          style={{
	            bottom: "2%",
	            left: "50%",
	            transform: "translateX(-50%)",
	            zIndex: 40,
	          }}
        >
          <SpiritAvatar
            stageKey={spirit.stageKey}
            xp={spirit.xp}
            xpMax={spirit.xpMax}
            stageAura={spirit.stageAura}
            stageGlow={spirit.stageGlow}
            animated={spirit.animated}
            equipped={spirit.equipped}
            gender={spirit.gender}
            skinTone={spirit.skinTone}
            hairColor={spirit.hairColor}
            size={126}
            showBar={false}
          />
          <span className="home-spirit-life sparkle-one" aria-hidden="true">✦</span>
          <span className="home-spirit-life sparkle-two" aria-hidden="true">✧</span>
          <span className="home-prayer-hands" aria-hidden="true">
            <span className="hand left" />
            <span className="hand right" />
          </span>
          <span
            className={`home-spirit-wave${
              homeCareState?.prayedToday ? " prayed" : ""
            }`}
            aria-hidden="true"
          />
          <span className="home-spirit-ground-shadow" aria-hidden="true" />
        </div>
      )}

      {showSpiritActions && (
        <div
          className="spirit-action-menu"
          role="menu"
          aria-label="Spirit actions"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {([
            ["shine", "✨", "Shine"],
            ["dance", "💖", "Dance"],
          ] as const).map(([action, icon, label]) => (
            <button
              key={action}
              type="button"
              role="menuitem"
              className="spirit-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                playSpiritAction(action);
              }}
              aria-label={`${label} with spirit`}
            >
              <span aria-hidden="true">{icon}</span>
              <span>{label}</span>
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className={`spirit-action-btn faith-action${
              faithFlightUnlocked ? "" : " locked"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (!faithFlightUnlocked) return;
              setShowSpiritActions(false);
              onFaithFlight?.();
            }}
            aria-label={
              faithFlightUnlocked
                ? "Faith Flight"
                : "Faith Flight locked until seven day streak"
            }
            disabled={!faithFlightUnlocked}
          >
            <span aria-hidden="true">{faithFlightUnlocked ? "🕊️" : "🔒"}</span>
            <span>
              {faithFlightUnlocked
                ? "Faith Flight"
                : `Faith Flight ${faithFlightProgress}/7`}
            </span>
          </button>
          {!faithFlightUnlocked && (
            <div className="spirit-action-lock-note">
              Complete your 7-day streak to unlock Faith Flight.
            </div>
          )}
        </div>
      )}

      <div className="home-foreground-grass" />

      {!wallpaper &&
        !floor &&
        !home &&
        !decor &&
        !garden &&
        !feature &&
        !pet &&
        !effect && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              textAlign: "center",
              padding: "0 24px 24px",
              zIndex: 11,
            }}
          >
            <div
              style={{
                background: "rgba(255,253,246,.78)",
                border: `1px solid ${C.cardBorder}`,
                borderRadius: 18,
                padding: "12px 16px",
                backdropFilter: "blur(8px)",
              }}
            >
              <p
                className="serif"
                style={{ margin: 0, fontSize: 18, color: C.textDark }}
              >
                Your home is waiting.
              </p>
              <p style={{ margin: "5px 0 0", fontSize: 13, color: C.textSoft }}>
                Begin with one faithful step.
              </p>
            </div>
          </div>
        )}

      {/* Neglect darkness overlay — fades in as days are missed */}
      {homeRestored && <div className="home-restore-overlay" />}
      {homeTransforming && (
        <div className="home-transform-overlay" aria-hidden="true">
          <div className="build-scaffold" />
          <span className="build-beam b1" />
          <span className="build-beam b2" />
          <span className="build-beam b3" />
          <span className="builder-angel a1">👼</span>
          <span className="builder-angel a2">👼</span>
          <span className="builder-angel a3">👼</span>
          <span className="build-spark s1">✦</span>
          <span className="build-spark s2">✧</span>
          <span className="build-spark s3">⋆</span>
        </div>
      )}
      {homeBlessed && <div className="home-blessing-overlay" />}
      {neglectDarkness > 0 && (
        <div
          className="home-neglect-overlay"
          style={{
            background: `${darkHue}${(neglectDarkness * 0.7).toFixed(2)})`,
            opacity: 1,
          }}
        >
          {neglectDarkness > 0.3 && (
            <div
              className="home-neglect-message"
              style={{
                position: "absolute",
                bottom: 20,
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(0,0,0,.52)",
                borderRadius: 14,
                padding: "10px 16px",
                textAlign: "center",
                backdropFilter: "blur(8px)",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: "rgba(255,230,150,.9)",
                  fontWeight: 700,
                  lineHeight: 1.5,
                }}
              >
                {neglectDarkness > 0.7
                  ? "Your home is dim. Return with one faithful step."
                  : "Your home misses you. Tend it gently today."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HEAVENLY HOME PAGE
// ─────────────────────────────────────────────────────────────────────────────
function HeavenlyHomePage({
  righteousness,
  ownedHomeItems,
  placedHomeItems,
  homeBlooms,
  spiritVisitors,
  expandedHomeItem,
  setExpandedHomeItem,
  onBuy,
  onPlace,
  onRemovePlaced,
  spirit,
  neglectDarkness,
  homeRestored = false,
  homeBlessed = false,
  homeTransforming = false,
  quietMode = false,
  homeCareState,
  faithFlightProgress,
  faithFlightUnlocked,
  faithFlightActive,
  adminBloomPreview = false,
  onBlessHome,
  onFaithFlight,
  onGoToday,
  onGoPrayer,
  onGoJournal,
}: {
  righteousness: number;
  ownedHomeItems: string[];
  placedHomeItems: PlacedHomeMap;
  homeBlooms: HomeBloom[];
  spiritVisitors: SpiritVisitor[];
  expandedHomeItem: string | null;
  setExpandedHomeItem: (id: string | null) => void;
  onBuy: (item: HomeItem) => void;
  onPlace: (item: HomeItem) => void;
  onRemovePlaced: (item: HomeItem) => void;
  spirit: HomeSpiritPreview;
  neglectDarkness: number;
  homeRestored?: boolean;
  homeBlessed?: boolean;
  homeTransforming?: boolean;
  quietMode?: boolean;
  homeCareState: HomeCareState;
  faithFlightProgress: number;
  faithFlightUnlocked: boolean;
  faithFlightActive: boolean;
  adminBloomPreview?: boolean;
  onBlessHome: () => void;
  onFaithFlight: () => void;
  onGoToday: () => void;
  onGoPrayer: () => void;
  onGoJournal: () => void;
}) {
  const [selectedSlot, setSelectedSlot] = useState<HomeSlot>("wallpaper");
  const [previewHomeItemId, setPreviewHomeItemId] = useState<string | null>(null);
  const [showHomeVisitors, setShowHomeVisitors] = useState<boolean>(false);
  const [showDecorateHome, setShowDecorateHome] = useState<boolean>(false);
  const homeShopSlots = HOME_SLOT_ORDER.filter((slot) =>
    HOME_SHOP_ITEMS.some((item) => item.slot === slot)
  );
  const activeSelectedSlot = homeShopSlots.includes(selectedSlot)
    ? selectedSlot
    : homeShopSlots[0] || "wallpaper";
  const selectedItems = HOME_SHOP_ITEMS.filter(
    (item) => item.slot === activeSelectedSlot
  );
  const previewHomeItem =
    HOME_SHOP_ITEMS.find((item) => item.id === previewHomeItemId) || null;
  const previewPlacedHomeItems = previewHomeItem
    ? { ...placedHomeItems, [previewHomeItem.slot]: previewHomeItem.id }
    : placedHomeItems;
  const homeGrowthStage = getHomeGrowthStageForSpiritStage(spirit.stageKey);
  const previewHomeGrowthStage = homeGrowthStage;
  const nextHomeStage =
    HOME_GROWTH_STAGES.find((stage) => stage.level > previewHomeGrowthStage.level) || null;
  const homeJourneyIndex = Math.max(
    0,
    SPIRIT_STAGES.findIndex((stage) => stage.key === spirit.stageKey)
  );
  const currentSpiritStage = SPIRIT_STAGES[homeJourneyIndex] || SPIRIT_STAGES[0];
  const nextSpiritHomeStage =
    homeJourneyIndex >= 0 && homeJourneyIndex < SPIRIT_STAGES.length - 1
      ? SPIRIT_STAGES[homeJourneyIndex + 1]
      : null;
  const homeJourneyPercent =
    SPIRIT_STAGES.length > 1
      ? (homeJourneyIndex / (SPIRIT_STAGES.length - 1)) * 100
      : 100;
  const visibleVisitors = activeSpiritVisitors(spiritVisitors);
  const homeSignature =
    HOME_STAGE_SIGNATURES[previewHomeGrowthStage.level] ||
    HOME_STAGE_SIGNATURES[0];

  return (
    <div
      className="home-page simple-home-page"
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      {neglectDarkness > 0 && (
        <div className="home-neglect-top-warning">
          <p>
            Your home is dim today. One faithful step can brighten it.
          </p>
        </div>
      )}
      <section className="home-intro-section">
        <div className="home-intro-copy">
          <SectionLabel>Your Heavenly Home</SectionLabel>
          <h2 className="serif">
            {previewHomeGrowthStage.icon} {previewHomeGrowthStage.name}
          </h2>
          <p className="home-stage-title-copy">
            Your home is growing in faith.
          </p>
          <p className="home-stage-signature-copy">
            Every faithful habit helps your home grow.
          </p>
        </div>
        <div className="home-intro-actions">
          <span className="sticky-preview-label">
            {previewHomeItem ? "Previewing piece" : "⌂ Living Home"}
          </span>
          {previewHomeItem && (
            <button
              type="button"
              onClick={() => setPreviewHomeItemId(null)}
              className="soft-action-btn muted"
            >
              Clear
            </button>
          )}
        </div>
      </section>

      <Card className="home-progress-card" style={{ padding: 16 }}>
        <div
          className="home-faith-growth-indicator"
          aria-label={`Home growth stage: ${currentSpiritStage.name}`}
        >
          <span>Growing in Faith</span>
          <div className="home-faith-growth-track" aria-hidden="true">
            {SPIRIT_STAGES.map((stageItem, index) => (
              <i
                key={stageItem.key}
                className={index <= homeJourneyIndex ? "active" : ""}
              />
            ))}
          </div>
          <small>
            {nextSpiritHomeStage
              ? `Next: ${nextSpiritHomeStage.name}`
              : "Fully blooming"}
          </small>
        </div>
      </Card>

      <Card
        className="sticky-preview-card home-preview-card simple-home-preview home-art-card"
        style={{
          padding: 14,
          background: "rgba(255,253,246,.94)",
          borderColor: C.gold + "40",
        }}
      >
        <div className="home-art-frame">
          <span className="home-art-sparkle s1" aria-hidden="true">✦</span>
          <span className="home-art-sparkle s2" aria-hidden="true">✧</span>
          <span className="home-art-bird b1" aria-hidden="true" />
          <span className="home-art-bird b2" aria-hidden="true" />
          <HeavenlyHomePreview
            placedHomeItems={previewPlacedHomeItems}
            homeBlooms={homeBlooms}
            spiritVisitors={spiritVisitors}
            homeGrowthStage={previewHomeGrowthStage}
            spirit={spirit}
            neglectDarkness={neglectDarkness}
            homeRestored={homeRestored}
            homeBlessed={homeBlessed}
            homeTransforming={homeTransforming}
            homeCareMood={homeCareState.mood}
            homeCareState={homeCareState}
            faithFlightActive={faithFlightActive}
            faithFlightUnlocked={faithFlightUnlocked}
            faithFlightProgress={faithFlightProgress}
            onFaithFlight={onFaithFlight}
          />
        </div>
        <p className="home-bird-prayer-note">
          Birds appear when someone prays for you.
        </p>
      </Card>

      <Card className={`home-care-card simple-home-blessing${homeCareState.blessedToday ? " blessed-today" : ""}`} style={{ padding: 16 }}>
        <span className="home-blessing-dove" aria-hidden="true" />
        <div className="home-blessing-head">
          <div>
            <SectionLabel>Today's Blessing</SectionLabel>
            <h3
              className="serif"
              style={{ margin: "0 0 5px", fontSize: 18, color: C.textDark }}
            >
              {homeCareState.blessedToday ? "Blessing rests here today." : homeCareState.title}
            </h3>
          </div>
        </div>
        <p
          style={{
            margin: "0 0 12px",
            fontSize: 13.5,
            color: C.textSoft,
            lineHeight: 1.5,
          }}
        >
          {homeCareState.blessedToday
            ? "Peace is resting here. Return tomorrow for a new blessing."
            : homeCareState.message}
        </p>
        <GoldBtn
          onClick={onBlessHome}
          disabled={homeCareState.blessedToday}
          style={{ width: "100%" }}
        >
          {homeCareState.blessedToday ? "Blessed Today ✓" : "Bless Home ✨"}
        </GoldBtn>
      </Card>

      <Card className="home-shop-shell-card simple-home-decorate" style={{ padding: 14 }}>
        <button
          type="button"
          className="simple-home-decorate-toggle"
          onClick={() => setShowDecorateHome((open) => !open)}
          aria-expanded={showDecorateHome}
        >
          <div>
            <SectionLabel>Decorate</SectionLabel>
            <p>
              {ownedHomeItems.length} saved pieces · ✨ {righteousness}
            </p>
          </div>
          <span aria-hidden="true">{showDecorateHome ? "⌃" : "⌄"}</span>
        </button>

        {showDecorateHome && (
          <div className="simple-home-decorate-body">
            <div className="home-shop-category-row">
              {homeShopSlots.map((slot) => {
                const sample = HOME_SHOP_ITEMS.find((item) => item.slot === slot);
                return (
                  <button
                    key={slot}
                    className={`home-category-card${activeSelectedSlot === slot ? " active" : ""}`}
                    onClick={() => {
                      setSelectedSlot(slot);
                      setExpandedHomeItem(null);
                      setPreviewHomeItemId(null);
                    }}
                  >
                    {sample && <HomeShopPreviewObject item={sample} />}
                    <div
                      style={{ fontSize: 13, fontWeight: 900, color: C.textDark }}
                    >
                      {HOME_SLOT_LABELS[slot]}
                    </div>
                    <div
                      className="meaning-chip"
                      style={{ marginTop: 5, fontSize: 11 }}
                    >
                      {HOME_ROLE_LABELS[slot]}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="home-shop-section-header">
              <div>
                <span className="home-shop-eyebrow">
                  {HOME_SLOT_LABELS[activeSelectedSlot]} Collection
                </span>
                <h3>{HOME_SLOT_LABELS[activeSelectedSlot]}</h3>
              </div>
              <span className="home-shop-collected">
                Collected {selectedItems.filter((item) => ownedHomeItems.includes(item.id)).length} of {selectedItems.length}
              </span>
            </div>

            <div
              className="armor-shop-grid home-shop-items-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              {selectedItems.map((item) => {
                const owned = ownedHomeItems.includes(item.id);
                const placed = placedHomeItems[item.slot] === item.id;
                const canAfford = HOME_SHOP_TEST_MODE || righteousness >= item.cost;
                return (
                  <div
                    key={item.id}
                    className={`home-shop-card${owned ? " owned" : ""}${placed ? " equipped" : ""}${!canAfford && !owned && !HOME_SHOP_TEST_MODE ? " locked" : ""}`}
                    style={{
                      background: owned
                        ? `linear-gradient(160deg,rgba(255,253,246,.98),${item.color || item.accent || C.goldPale}38)`
                        : "rgba(255,253,240,.58)",
                    }}
                  >
                    <HomeShopPreviewObject item={item} />
                    <div style={{ textAlign: "center", marginBottom: 6 }}>
                      <span className="meaning-chip">
                        {HOME_ROLE_LABELS[item.slot]}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 900,
                        color: C.textDark,
                        marginBottom: 8,
                        lineHeight: 1.25,
                        textAlign: "center",
                      }}
                    >
                      {item.name}
                    </div>
                    <div
                      className="home-shop-price"
                      style={{
                        fontSize: 12.5,
                        color: C.textDark,
                        textAlign: "center",
                        lineHeight: 1.25,
                        margin: "-3px 0 8px",
                        fontWeight: 700,
                      }}
                    >
                      ✨ {item.cost}
                    </div>
                    {!HOME_SHOP_TEST_MODE && !canAfford && !owned && (
                      <div
                        style={{
                          fontSize: 13,
                          color: C.red,
                          fontWeight: 700,
                          textAlign: "center",
                          marginBottom: 4,
                        }}
                      >
                        Need ✨{item.cost - righteousness} more
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 6, marginTop: "auto" }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewHomeItemId((current) =>
                            current === item.id ? null : item.id
                          );
                          window.setTimeout(() => {
                            document
                              .querySelector(".home-page .sticky-preview-card")
                              ?.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                          }, 60);
                        }}
                        className={`soft-action-btn${previewHomeItemId === item.id ? "" : " muted"}`}
                        style={{
                          flex: 0.82,
                          minHeight: 36,
                          padding: "8px 0",
                          fontSize: 13,
                        }}
                      >
                        {previewHomeItemId === item.id ? "Previewing" : "Preview"}
                      </button>
                      {!owned ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onBuy(item);
                          }}
                          style={{
                            flex: 1,
                            background: canAfford ? GOLD_GRAD : `${C.goldPale}40`,
                            border: `1px solid ${canAfford ? C.gold : C.goldPale}`,
                            borderRadius: 12,
                            padding: "9px 0",
                            color: canAfford ? C.textDark : C.textFaint,
                            fontWeight: 900,
                            cursor: canAfford ? "pointer" : "default",
                            fontSize: 13,
                            minHeight: 36,
                          }}
                        >
                          {HOME_SHOP_TEST_MODE ? "Add Home Item" : "Buy"}
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onPlace(item);
                          }}
                          style={{
                            flex: 1,
                            background: placed ? GOLD_GRAD : `${C.goldPale}50`,
                            border: `1px solid ${C.gold}`,
                            borderRadius: 12,
                            padding: "9px 0",
                            color: C.textDark,
                            fontWeight: 900,
                            cursor: "pointer",
                            fontSize: 13,
                            minHeight: 36,
                          }}
                        >
                          {placed ? "✓ Placed" : "Place"}
                        </button>
                      )}
                    </div>
                    {owned && placed && (
                      <button
                        type="button"
                        className="home-remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemovePlaced(item);
                        }}
                        style={{
                          marginTop: 7,
                          width: "100%",
                          minHeight: 34,
                          borderRadius: 12,
                          border: `1px solid ${C.cardBorder}`,
                          background: "rgba(255,255,255,.66)",
                          color: "#111111",
                          fontWeight: 900,
                          fontSize: 12,
                          cursor: "pointer",
                        }}
                      >
                        Remove from Home
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>


    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ONBOARDING
// ─────────────────────────────────────────────────────────────────────────────
function PrayersPage({
  prayers,
  today,
  dailyPrayerFocus,
  spiritName,
  friendsOnly = false,
  onAddPrayer,
  onPrayForPerson,
  onMarkAnswered,
  onDeletePrayer,
  onTogglePublicShare,
  onUpdatePrayer,
  onSubmitPublicTestimony,
  currentUser,
  friendProfile,
  spiritFriends,
  incomingFriendRequests,
  outgoingFriendRequests,
  sharedPrayers,
  receivedPrayerSignals,
  accountabilityPartnerUid,
  accountabilityPartnerUids,
  friendStatus,
  onSignIn,
  onSendFriendRequest,
  onRespondFriendRequest,
  onCancelFriendRequest,
  onSendAccountabilityRequest,
  onRespondAccountabilityRequest,
  onRemoveAccountabilityPartner,
  onDeleteSpiritFriend,
  onCompleteAccountabilityFocus,
  onSendPrayedForFriend,
  onSendBlessingToFriend,
  onSharePrayerWithFriends,
}: {
  prayers: PrayerFriend[];
  today: string;
  dailyPrayerFocus: (typeof PRAYER_FOCUS)[number];
  spiritName?: string;
  friendsOnly?: boolean;
  onAddPrayer: (
    kind: "mine" | "other",
    name: string,
    request: string,
    challengeDays: PrayerChallengeDays
  ) => void;
  onPrayForPerson: (prayer: PrayerFriend, e: ButtonEvent) => void;
  onMarkAnswered: (id: string, note?: string, sharePublic?: boolean) => void;
  onDeletePrayer: (id: string) => void;
  onTogglePublicShare: (id: string, sharePublic: boolean) => void;
  onUpdatePrayer: (id: string, updates: Partial<PrayerFriend>) => void;
  onSubmitPublicTestimony: (prayer: PrayerFriend) => void;
  currentUser: User | null;
  friendProfile: SpiritFriendProfile | null;
  spiritFriends: SpiritFriend[];
  incomingFriendRequests: SpiritFriendRequest[];
  outgoingFriendRequests: SpiritFriendRequest[];
  sharedPrayers: SharedPrayer[];
  receivedPrayerSignals: PrayerSignal[];
  accountabilityPartnerUid?: string;
  accountabilityPartnerUids?: string[];
  friendStatus: string;
  onSignIn: () => void;
  onSendFriendRequest: (identifier: string) => void;
  onRespondFriendRequest: (
    request: SpiritFriendRequest,
    accept: boolean
  ) => void;
  onCancelFriendRequest: (request: SpiritFriendRequest) => void;
  onSendAccountabilityRequest?: (friend: SpiritFriend) => void;
  onRespondAccountabilityRequest?: (
    request: SpiritFriendRequest,
    accept: boolean
  ) => void;
  onRemoveAccountabilityPartner?: (friend: SpiritFriend) => void;
  onDeleteSpiritFriend?: (friend: SpiritFriend) => void;
  onCompleteAccountabilityFocus?: (
    friend: SpiritFriend,
    focus: string
  ) => void;
  onSendPrayedForFriend: (friend: SpiritFriend) => void;
  onSendBlessingToFriend: (
    friend: SpiritFriend,
    blessing: string,
    message?: string
  ) => void;
  onSharePrayerWithFriends: (prayer: PrayerFriend, friendUids: string[]) => void;
}) {
	  const [myRequest, setMyRequest] = useState("");
	  const [myQuickTopics, setMyQuickTopics] = useState<string[]>([]);
	  const [showMyQuickTopics, setShowMyQuickTopics] = useState(false);
	  const [name, setName] = useState("");
	  const [request, setRequest] = useState("");
	  const [otherQuickTopics, setOtherQuickTopics] = useState<string[]>([]);
	  const [showOtherQuickTopics, setShowOtherQuickTopics] = useState(false);
  const [showHeavensWordForOthers, setShowHeavensWordForOthers] =
    useState(false);
  const [myChallengeDays, setMyChallengeDays] =
    useState<PrayerDurationChoice>(7);
  const [answerTarget, setAnswerTarget] = useState<PrayerFriend | null>(null);
  const [answerNote, setAnswerNote] = useState("");
  const [answerSharePublic, setAnswerSharePublic] = useState(false);
  const [showAnsweredPrayerDetails, setShowAnsweredPrayerDetails] =
    useState(false);
  const [editingPrayerId, setEditingPrayerId] = useState<string | null>(null);
  const [editPrayerName, setEditPrayerName] = useState("");
  const [editPrayerRequest, setEditPrayerRequest] = useState("");
  const [editTestimonyNote, setEditTestimonyNote] = useState("");
  const [prayerFilter, setPrayerFilter] = useState<
    "mine" | "others" | "answered" | null
  >(null);
  const [prayerAddKind, setPrayerAddKind] = useState<"mine" | "other">("mine");
  const [openPublicShareId, setOpenPublicShareId] = useState<string | null>(
    null
  );
  const [showSpiritFriends, setShowSpiritFriends] = useState(false);
  const [friendTool, setFriendTool] = useState<
    "add" | "friends" | "notes" | "share" | null
  >(null);
  const [showFriendList, setShowFriendList] = useState(false);
  const [friendPendingRemoval, setFriendPendingRemoval] =
    useState<SpiritFriend | null>(null);
  const [friendIdentifier, setFriendIdentifier] = useState("");
  const [inviteCopied, setInviteCopied] = useState(false);
  const prayedForFriendKey = `sg_prayed_for_friends_${
    currentUser?.uid || "guest"
  }_${today}`;
  const [prayedForFriendIds, setPrayedForFriendIds] = useState<string[]>(() =>
    LS.get<string[]>(prayedForFriendKey, [])
  );
  const [friendPrayerLights, setFriendPrayerLights] = useState<PrayerLight[]>([]);
  const [friendLightMessage, setFriendLightMessage] = useState<string | null>(null);
  const partnerFocusKey = `sg_partner_focus_${currentUser?.uid || "guest"}`;
  const [partnerFocusByUid, setPartnerFocusByUid] = useState<
    Record<string, string>
  >(() => LS.get<Record<string, string>>(partnerFocusKey, {}));
  const customPartnerFocusKey = `${partnerFocusKey}_custom`;
  const [customPartnerFocusByUid, setCustomPartnerFocusByUid] = useState<
    Record<string, string>
  >(() => LS.get<Record<string, string>>(customPartnerFocusKey, {}));
  const savedPartnerFocusKey = `${partnerFocusKey}_saved_options`;
  const [savedPartnerFocusOptions, setSavedPartnerFocusOptions] = useState<
    string[]
  >(() => LS.get<string[]>(savedPartnerFocusKey, []));
  const partnerFocusCompletionKey = `sg_partner_focus_done_${
    currentUser?.uid || "guest"
  }_${today}`;
  const [completedPartnerFocusUids, setCompletedPartnerFocusUids] = useState<
    string[]
  >(() => LS.get<string[]>(partnerFocusCompletionKey, []));
  const [showCustomBlessing, setShowCustomBlessing] = useState(false);
  const [showBlessingPanel, setShowBlessingPanel] = useState(false);
  const [showPendingAccountability, setShowPendingAccountability] = useState(false);
  const [showAccountabilityManage, setShowAccountabilityManage] = useState(false);
  const [showPartnerAskList, setShowPartnerAskList] = useState(false);
  const [selectedBlessingFriendUid, setSelectedBlessingFriendUid] = useState("");
  const [customBlessingNote, setCustomBlessingNote] = useState("");
  const [blessingSentMessage, setBlessingSentMessage] = useState<string | null>(null);
  const sentBlessingKey = `sg_sent_blessings_${
    currentUser?.uid || "guest"
  }_${today}`;
  const [sentBlessingKeys, setSentBlessingKeys] = useState<string[]>(() =>
    LS.get<string[]>(sentBlessingKey, [])
  );
  const [selectedPrayerToShare, setSelectedPrayerToShare] = useState("");
  const [selectedFriendUids, setSelectedFriendUids] = useState<string[]>([]);
  const archivedSharedPrayerKey = `sg_archived_shared_prayers_${
    currentUser?.uid || "guest"
  }`;
  const [archivedSharedPrayerIds, setArchivedSharedPrayerIds] = useState<
    string[]
  >(() => LS.get<string[]>(archivedSharedPrayerKey, []));
  const challengeOptions: PrayerChallengeDays[] = [7, 21, 40];
  const baseAccountabilityFocusOptions = [
    "Pray daily",
    "Read one chapter",
    "Share 3 gratitudes",
    "Keep a 7-day streak",
    "Send encouragement",
  ];
  const accountabilityFocusOptions = useMemo(() => {
    const seen = new Set<string>();
    return [
      ...baseAccountabilityFocusOptions,
      ...savedPartnerFocusOptions,
    ]
      .map((focus) => focus.trim())
      .filter((focus) => {
        const key = focus.toLowerCase();
        if (!focus || key === "custom" || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .concat("Custom");
  }, [savedPartnerFocusOptions]);
  const safePrayers = Array.isArray(prayers) ? prayers : [];
  const safeSpiritFriends = Array.isArray(spiritFriends) ? spiritFriends : [];
  const safeIncomingRequests = Array.isArray(incomingFriendRequests)
    ? incomingFriendRequests
    : [];
  const safeOutgoingRequests = Array.isArray(outgoingFriendRequests)
    ? outgoingFriendRequests
    : [];
  const safeSharedPrayers = Array.isArray(sharedPrayers) ? sharedPrayers : [];
  const safeReceivedPrayerSignals = Array.isArray(receivedPrayerSignals)
    ? receivedPrayerSignals
    : [];
  const activePrayers = safePrayers.filter((p) => !p.answered);
  const friendDisplayLabel = (friend: SpiritFriend) => {
    const rawName = String(friend.displayName || "").trim();
    const looksLikeEmail = rawName.includes("@");
    const looksLikeEmailLocal =
      !!friend.email && rawName.toLowerCase() === friend.email.split("@")[0]?.toLowerCase();
    const readableEmailName = friendlyNameFromEmail(friend.email);
    const cleaned =
      rawName &&
      !looksLikeEmail &&
      !looksLikeEmailLocal &&
      !isPlaceholderFriendName(rawName)
        ? rawName.replace(/[._-]+/g, " ").trim()
        : readableEmailName || "Spirit Friend";
    if (!cleaned) return "Spirit Friend";
    return cleaned
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };
  const friendSpiritImageSrc = (
    friend?: Pick<SpiritFriend, "gender" | "stageKey"> | null,
    fallbackStage: "baby" | "growing" | "mature" | "radiant" = "baby"
  ) => {
    const genderFolder = normalizeSpiritGender(friend?.gender) || "female";
    const rawStage = String(friend?.stageKey || fallbackStage || "baby").toLowerCase();
    const fileStage =
      rawStage === "teen" || rawStage === "growing" || rawStage === "growth"
        ? "growing"
        : rawStage === "adult" || rawStage === "mature"
        ? "mature"
        : rawStage === "radiant"
        ? "radiant"
        : "baby";
    return "/characters/" + genderFolder + "/" + fileStage + "-spirit.png?v=friend-avatar-20260626";
  };
  const myPrayers = activePrayers.filter((p) => (p.kind || "other") === "mine");
  const otherPrayers = activePrayers.filter(
    (p) => (p.kind || "other") === "other"
  );
  const answeredPrayers = safePrayers.filter((p) => p.answered);
  const compactPrayerText = (text?: string | null, max = 86) => {
    const safeText = (text || "").trim();
    return safeText.length > max ? safeText.slice(0, max).trim() + "..." : safeText;
  };
  const prayerAnsweredDate = (prayer: PrayerFriend) => {
    if (!prayer.answeredDate) return null;
    const date = new Date(`${prayer.answeredDate}T12:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const prayerVesselTotal =
    myPrayers.length + otherPrayers.length + answeredPrayers.length;
  const prayerVesselFill = Math.min(
    100,
    Math.max(prayerVesselTotal > 0 ? 18 : 5, prayerVesselTotal * 11)
  );
  const answeredMonth = new Date();
  const answeredThisMonth = answeredPrayers.filter((prayer) => {
    const date = prayerAnsweredDate(prayer);
    return (
      date &&
      date.getFullYear() === answeredMonth.getFullYear() &&
      date.getMonth() === answeredMonth.getMonth()
    );
  });
  const lastAnsweredPrayer = [...answeredPrayers].sort((a, b) => {
    const bTime = prayerAnsweredDate(b)?.getTime() || 0;
    const aTime = prayerAnsweredDate(a)?.getTime() || 0;
    return bTime - aTime;
  })[0] || null;
  const lastAnsweredDate = lastAnsweredPrayer
    ? prayerAnsweredDate(lastAnsweredPrayer)?.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      }) || "Recently"
    : "";
  const lastAnsweredText = lastAnsweredPrayer
    ? compactPrayerText(lastAnsweredPrayer.answeredNote || lastAnsweredPrayer.request, 82)
    : "";
  const vesselStageClass =
    prayerVesselTotal === 0
      ? "vessel-empty"
      : answeredPrayers.length > 0 || prayerVesselFill >= 82
      ? "vessel-radiant"
      : prayerVesselFill >= 50
      ? "vessel-full"
      : "vessel-growing";
  const vesselOwnerName = (spiritName || "My").trim();
  const prayerVesselTitle =
    vesselOwnerName && vesselOwnerName !== "My"
      ? `${vesselOwnerName}'s Prayer Vessel`
      : "My Prayer Vessel";
  const vesselPurposeText =
    prayerVesselTotal === 0
      ? "Your vessel is ready to hold what is on your heart."
      : prayerVesselTotal === 1
      ? "One prayer is resting safely before God."
      : answeredPrayers.length > 0
      ? "Answered prayers are shining with gratitude."
      : prayerVesselTotal >= 5
      ? `${prayerVesselTotal} prayers are growing in faith.`
      : `${prayerVesselTotal} prayers are resting safely before God.`;
  const publicPreviewCount = answeredPrayers.filter(
    (p) => p.sharePublic
  ).length;
  const latestActivePrayer = activePrayers[0] || null;
  const prayersAddedToday = safePrayers.filter(
    (prayer) => prayer.challengeStarted === today
  ).length;
  const prayerLandingTarget = latestActivePrayer
    ? (latestActivePrayer.kind || "other") === "mine"
      ? latestActivePrayer.title || "Your prayer"
      : latestActivePrayer.name
      ? `Pray for ${latestActivePrayer.name}`
      : latestActivePrayer.title || "Pray for someone"
    : "Add a prayer";
  useEffect(() => {
    setArchivedSharedPrayerIds(
      LS.get<string[]>(archivedSharedPrayerKey, [])
    );
  }, [archivedSharedPrayerKey]);
  const visibleSharedPrayers = useMemo(
    () =>
      safeSharedPrayers.filter(
        (shared) => !archivedSharedPrayerIds.includes(shared.id)
      ),
    [archivedSharedPrayerIds, safeSharedPrayers]
  );
  const prayerNoteCount =
    safeReceivedPrayerSignals.length + visibleSharedPrayers.length;
  const prayerNoteIds = useMemo(
    () => [
      ...safeReceivedPrayerSignals.map((signal) => `signal:${signal.id}`),
      ...visibleSharedPrayers.map((shared) => `shared:${shared.id}`),
    ],
    [safeReceivedPrayerSignals, visibleSharedPrayers]
  );
  const prayerNoteIdKey = prayerNoteIds.join("|");
  const seenPrayerNoteKey = `sg_seen_prayer_notes_${
    currentUser?.uid || "guest"
  }`;
  const [seenPrayerNoteIds, setSeenPrayerNoteIds] = useState<string[]>(() =>
    LS.get<string[]>(seenPrayerNoteKey, [])
  );
  useEffect(() => {
    setSeenPrayerNoteIds(LS.get<string[]>(seenPrayerNoteKey, []));
  }, [seenPrayerNoteKey]);
  const unreadPrayerNoteCount = prayerNoteIds.filter(
    (id) => !seenPrayerNoteIds.includes(id)
  ).length;
  const incomingAccountabilityRequests = safeIncomingRequests.filter(
    (request) => request.requestType === "accountability"
  );
  const outgoingAccountabilityRequests = safeOutgoingRequests.filter(
    (request) => request.requestType === "accountability"
  );
  const incomingFriendRequestsOnly = safeIncomingRequests.filter(
    (request) => request.requestType !== "accountability"
  );
  const outgoingFriendRequestsOnly = safeOutgoingRequests.filter(
    (request) => request.requestType !== "accountability"
  );
  const hasIncomingFriendRequests = incomingFriendRequestsOnly.length > 0;
  const hasUnreadPrayerNotes = unreadPrayerNoteCount > 0;
  const friendNotificationCount =
    incomingFriendRequestsOnly.length +
    incomingAccountabilityRequests.length +
    unreadPrayerNoteCount;
  const visibleFriendStatus =
    friendStatus &&
    !["Friends loaded", "Friends updated", "Loading friends..."].includes(
      friendStatus
    )
      ? friendStatus
      : "";
  useEffect(() => {
    setPartnerFocusByUid(
      LS.get<Record<string, string>>(partnerFocusKey, {})
    );
  }, [partnerFocusKey]);
  useEffect(() => {
    setCustomPartnerFocusByUid(
      LS.get<Record<string, string>>(customPartnerFocusKey, {})
    );
  }, [customPartnerFocusKey]);
  useEffect(() => {
    setSavedPartnerFocusOptions(LS.get<string[]>(savedPartnerFocusKey, []));
  }, [savedPartnerFocusKey]);
  useEffect(() => {
    setCompletedPartnerFocusUids(
      LS.get<string[]>(partnerFocusCompletionKey, [])
    );
  }, [partnerFocusCompletionKey]);
  useEffect(() => {
    setPrayedForFriendIds(LS.get<string[]>(prayedForFriendKey, []));
  }, [prayedForFriendKey]);
  useEffect(() => {
    setSentBlessingKeys(LS.get<string[]>(sentBlessingKey, []));
  }, [sentBlessingKey]);
  useEffect(() => {
    if (!blessingSentMessage) return;
    const timer = window.setTimeout(() => setBlessingSentMessage(null), 2600);
    return () => window.clearTimeout(timer);
  }, [blessingSentMessage]);
  useEffect(() => {
    if (!friendsOnly) return;
    setShowSpiritFriends(true);
    setFriendTool((current) => {
      if (current) return current;
      if (hasUnreadPrayerNotes) return "notes";
      if (hasIncomingFriendRequests) return "add";
      return safeSpiritFriends.length > 0 ? "friends" : null;
    });
    setShowFriendList(false);
  }, [
    friendsOnly,
    hasIncomingFriendRequests,
    hasUnreadPrayerNotes,
    safeSpiritFriends.length,
  ]);
  useEffect(() => {
    if (!showSpiritFriends || friendTool !== "notes" || prayerNoteIds.length === 0)
      return;
    setSeenPrayerNoteIds(prayerNoteIds);
    LS.set(seenPrayerNoteKey, prayerNoteIds);
  }, [friendTool, prayerNoteIdKey, prayerNoteIds, seenPrayerNoteKey, showSpiritFriends]);
  const signedInWithGoogle = !!currentUser?.email && !currentUser.isAnonymous;
  const activeAccountabilityPartnerUids =
    accountabilityPartnerUids && accountabilityPartnerUids.length > 0
      ? accountabilityPartnerUids
      : accountabilityPartnerUid
      ? [accountabilityPartnerUid]
      : [];
  const accountabilityPartners = safeSpiritFriends.filter((friend) =>
    activeAccountabilityPartnerUids.includes(friend.uid)
  );
  const accountabilityPartner = accountabilityPartners[0] || null;
  const availableAccountabilityFriends = safeSpiritFriends.filter(
    (friend) =>
      !activeAccountabilityPartnerUids.includes(friend.uid) &&
      !outgoingAccountabilityRequests.some((request) => request.toUid === friend.uid)
  );
  const latestAccountabilityUpdate = safeReceivedPrayerSignals.find(
    (signal) =>
      signal.type === "accountability" &&
      activeAccountabilityPartnerUids.includes(signal.fromUid) &&
      !seenPrayerNoteIds.includes(`signal:${signal.id}`)
  );
  const openPartnerChooser = () => {
    if (!signedInWithGoogle) {
      showToast("Sign in with Google to choose an accountability partner.");
      onSignIn();
      return;
    }
    setShowSpiritFriends(true);
    setFriendTool(safeSpiritFriends.length > 0 ? "friends" : "add");
    setShowFriendList(true);
    setShowPendingAccountability(true);
    setShowPartnerAskList(true);
    window.setTimeout(() => {
      document
        .querySelector(
          safeSpiritFriends.length > 0
            ? ".accountability-primary-card"
            : ".spirit-friends-tools-card"
        )
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };
  const blessingOptions = [
    "🙏 Praying for you",
    "❤️ Stay strong",
    "✨ God is with you",
    "🌿 Keep growing",
    "🕊️ Peace be with you",
  ];
  const blessingShortLabels: Record<string, string> = {
    "🙏 Praying for you": "Praying",
    "❤️ Stay strong": "Stay strong",
    "✨ God is with you": "God is with you",
    "🌿 Keep growing": "Keep growing",
    "🕊️ Peace be with you": "Peace be with you",
  };
  const selectedBlessingFriend =
    safeSpiritFriends.find((friend) => friend.uid === selectedBlessingFriendUid) ||
    safeSpiritFriends[0] ||
    null;
  const formatFriendTime = (value: any) => {
    const date =
      typeof value === "number"
        ? new Date(value)
        : typeof value === "string"
        ? new Date(value)
        : value?.toDate
        ? value.toDate()
        : value instanceof Date
        ? value
        : null;
    if (!date || Number.isNaN(date.getTime())) return "Today";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const encouragementRows = [
    ...safeReceivedPrayerSignals.map((signal) => ({
      id: `signal-${signal.id}`,
      icon:
        signal.type === "blessing"
          ? "🕊️"
          : signal.type === "accountability"
          ? "🌿"
          : "🙏",
      text:
        signal.type === "blessing"
          ? `${signal.fromName} sent you a blessing`
          : signal.type === "accountability"
          ? `${signal.fromName} completed a shared focus`
          : `${signal.fromName} prayed for you`,
      subtext:
        signal.type === "blessing"
          ? signal.message || signal.blessingLabel || ""
          : signal.type === "accountability"
          ? signal.message || "Growing together"
          : "",
      time: formatFriendTime(signal.createdAt),
      signal,
      shared: null as SharedPrayer | null,
    })),
    ...visibleSharedPrayers.map((shared) => ({
      id: `shared-${shared.id}`,
      icon: "🙏",
      text: `${shared.ownerName} shared a prayer`,
      subtext: shortText(shared.request, 90),
      time: formatFriendTime(shared.createdAt),
      signal: null,
      shared,
    })),
  ];
  const friendNotificationRows = [
    ...incomingFriendRequestsOnly.map((request) => ({
      id: `friend-request-${request.id}`,
      icon: "✨",
      title: "New friend request",
      text: `${request.fromName || "A Spirit Friend"} wants to pray with you.`,
      time: "New",
      unread: true,
    })),
    ...incomingAccountabilityRequests.map((request) => ({
      id: `accountability-request-${request.id}`,
      icon: "🌿",
      title: "Accountability request",
      text: `${request.fromName || "A Spirit Friend"} wants to walk with you.`,
      time: "New",
      unread: true,
    })),
    ...safeReceivedPrayerSignals.map((signal) => {
      const isUnread = !seenPrayerNoteIds.includes(`signal:${signal.id}`);
      return {
        id: `signal-notice-${signal.id}`,
        icon:
          signal.type === "blessing"
            ? "💛"
            : signal.type === "accountability"
            ? "🌿"
            : "🙏",
        title:
          signal.type === "blessing"
            ? "Blessing received"
            : signal.type === "accountability"
            ? "Partner update"
            : "Friend prayed for you",
        text:
          signal.type === "blessing"
            ? signal.message || signal.blessingLabel || `${signal.fromName} sent a blessing.`
            : signal.type === "accountability"
            ? signal.message || `${signal.fromName} completed a shared focus.`
            : `${signal.fromName} prayed for you.`,
        time: formatFriendTime(signal.createdAt),
        unread: isUnread,
      };
    }),
    ...visibleSharedPrayers.map((shared) => {
      const isUnread = !seenPrayerNoteIds.includes(`shared:${shared.id}`);
      return {
        id: `shared-notice-${shared.id}`,
        icon: "🙏",
        title: "New prayer shared",
        text: `${shared.ownerName} shared a prayer with you.`,
        time: formatFriendTime(shared.createdAt),
        unread: isUnread,
      };
    }),
  ];
  const shownEncouragementRows =
    friendTool === "notes" ? encouragementRows : encouragementRows.slice(0, 4);
  const prayerToShare =
    activePrayers.find((p) => p.id === selectedPrayerToShare) ||
    activePrayers[0] ||
    null;
  const showMineList = prayerFilter === "mine";
  const showOthersList = prayerFilter === "others";
  const showAnsweredList = prayerFilter === "answered";
	  const shortText = (text?: string | null, max = 120) => compactPrayerText(text, max);

	  const toggleQuickTopic = (
	    topic: string,
	    setter: React.Dispatch<React.SetStateAction<string[]>>
	  ) => {
	    setter((prev) =>
	      prev.includes(topic)
	        ? prev.filter((item) => item !== topic)
	        : [...prev, topic]
	    );
	  };

	  const buildQuickPrayerRequest = (topics: string[], note: string) => {
	    const trimmedNote = note.trim();
	    const parts: string[] = [];
	    if (topics.length) parts.push(`Please pray for: ${topics.join(", ")}.`);
	    if (trimmedNote) parts.push(trimmedNote);
	    return parts.join("\n\n").trim();
	  };

	  const myPrayerText = buildQuickPrayerRequest(myQuickTopics, myRequest);
	  const otherPrayerText = buildQuickPrayerRequest(otherQuickTopics, request);

	  const renderQuickPrayerTopics = (
	    selected: string[],
	    setter: React.Dispatch<React.SetStateAction<string[]>>,
	    open: boolean,
	    setOpen: React.Dispatch<React.SetStateAction<boolean>>
	  ) => (
	    <section className={`quick-prayer-box${open ? " open" : ""}`}>
	      <button
	        type="button"
	        className="quick-prayer-toggle"
	        onClick={() => setOpen((v) => !v)}
	        aria-expanded={open}
	      >
	        <span className="quick-prayer-title-wrap">
	          <span className="quick-prayer-title">Prayer Topics</span>
	          <span className="quick-prayer-optional">Optional</span>
	        </span>
	        <span className="quick-prayer-meta">
	          {selected.length ? `${selected.length} selected` : open ? "Close" : "Choose"}
	        </span>
	      </button>
	      {!open && selected.length > 0 && (
	        <div className="quick-prayer-summary">{selected.join(", ")}</div>
	      )}
	      {open && (
	        <div className="quick-prayer-grid">
	          {QUICK_PRAYER_TOPICS.map((topic) => {
	            const active = selected.includes(topic);
	            return (
	              <label
	                key={topic}
	                className={`quick-prayer-chip${active ? " selected" : ""}`}
	              >
	                <input
	                  type="checkbox"
	                  checked={active}
	                  onChange={() => toggleQuickTopic(topic, setter)}
	                />
	                <span>{topic}</span>
	              </label>
	            );
	          })}
	        </div>
	      )}
	    </section>
	  );

  function showRecentPrayerLanding() {
    setPrayerFilter(null);
    window.setTimeout(() => {
      document
        .getElementById("recent-prayers-preview")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 180);
  }

	  function addMyPrayer() {
	    if (!myPrayerText) return;
	    onAddPrayer("mine", "Me", myPrayerText, myChallengeDays || undefined);
	    setMyRequest("");
	    setMyQuickTopics([]);
	    setShowMyQuickTopics(false);
    showRecentPrayerLanding();
  }

	  function addPrayerForSomeone() {
	    if (!name.trim() || !otherPrayerText) return;
	    onAddPrayer("other", name.trim(), otherPrayerText, 7);
	    setName("");
	    setRequest("");
	    setOtherQuickTopics([]);
	    setShowOtherQuickTopics(false);
    showRecentPrayerLanding();
  }

  function toggleSelectedFriend(uid: string) {
    setSelectedFriendUids((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  }

  function submitFriendRequest() {
    const value = friendIdentifier.trim();
    if (!value) return;
    onSendFriendRequest(value);
    setFriendIdentifier("");
  }

  function markAllFriendNotificationsRead() {
    setSeenPrayerNoteIds(prayerNoteIds);
    LS.set(seenPrayerNoteKey, prayerNoteIds);
  }

  async function copyInviteCode() {
    const code = friendProfile?.inviteCode;
    if (!code) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const input = document.createElement("input");
        input.value = code;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setFriendIdentifier(code);
      setInviteCopied(true);
      window.setTimeout(() => setInviteCopied(false), 1600);
    } catch (error) {
      console.error(error);
    }
  }

  function shareSelectedPrayer() {
    if (!prayerToShare || selectedFriendUids.length === 0) return;
    onSharePrayerWithFriends(prayerToShare, selectedFriendUids);
    setSelectedFriendUids([]);
  }

  function sendBlessing(label: string) {
    if (!selectedBlessingFriend) return;
    spiritHaptic("light");
    const recipientName = friendDisplayLabel(selectedBlessingFriend);
    const sentKey = `${selectedBlessingFriend.uid}|${label}`;
    const nextSentBlessingKeys = sentBlessingKeys.includes(sentKey)
      ? sentBlessingKeys
      : [...sentBlessingKeys, sentKey];
    setSentBlessingKeys(nextSentBlessingKeys);
    LS.set(sentBlessingKey, nextSentBlessingKeys);
    onSendBlessingToFriend(
      selectedBlessingFriend,
      label,
      customBlessingNote.trim() || undefined
    );
    setBlessingSentMessage(`Blessing sent to ${recipientName} ✓`);
    setCustomBlessingNote("");
    setShowCustomBlessing(false);
  }

  function markFriendPrayed(friend: SpiritFriend, e?: ButtonEvent) {
    if (prayedForFriendIds.includes(friend.uid)) return;
    spiritHaptic("light");
    if (e?.currentTarget && typeof window !== "undefined") {
      const target = e.currentTarget.getBoundingClientRect();
      const fromX = window.innerWidth / 2 - 11;
      const fromY = Math.max(88, window.innerHeight - 155);
      const toX = target.left + target.width / 2 - 11;
      const toY = target.top + target.height / 2 - 11;
      const lightId = Date.now() + Math.random();
      setFriendPrayerLights((lights) => [
        ...lights,
        { id: lightId, fromX, fromY, toX, toY },
      ]);
      window.setTimeout(
        () =>
          setFriendPrayerLights((lights) =>
            lights.filter((light) => light.id !== lightId)
          ),
        1150
      );
    }
    const next = [...prayedForFriendIds, friend.uid];
    setPrayedForFriendIds(next);
    LS.set(prayedForFriendKey, next);
    onSendPrayedForFriend(friend);
    const friendName = friendDisplayLabel(friend);
    setFriendLightMessage(`A small light was sent to ${friendName}.`);
    window.setTimeout(() => setFriendLightMessage(null), 2600);
  }

  function updatePartnerFocus(friendUid: string, focus: string) {
    const next = { ...partnerFocusByUid, [friendUid]: focus };
    setPartnerFocusByUid(next);
    LS.set(partnerFocusKey, next);
    spiritHaptic("light");
  }

  function updateCustomPartnerFocus(friendUid: string, focus: string) {
    const next = { ...customPartnerFocusByUid, [friendUid]: focus };
    setCustomPartnerFocusByUid(next);
    LS.set(customPartnerFocusKey, next);
  }

  function savePartnerFocusOption(focus: string) {
    const cleanFocus = focus.trim().replace(/\s+/g, " ").slice(0, 80);
    if (!cleanFocus || cleanFocus.toLowerCase() === "custom") return "";
    const alreadySaved = [
      ...baseAccountabilityFocusOptions,
      ...savedPartnerFocusOptions,
    ].some((option) => option.trim().toLowerCase() === cleanFocus.toLowerCase());
    if (alreadySaved) return cleanFocus;
    const next = [cleanFocus, ...savedPartnerFocusOptions].slice(0, 8);
    setSavedPartnerFocusOptions(next);
    LS.set(savedPartnerFocusKey, next);
    return cleanFocus;
  }

  function resolvedPartnerFocus(friendUid: string) {
    const selectedFocus = partnerFocusByUid[friendUid] || "Pray daily";
    return selectedFocus === "Custom"
      ? customPartnerFocusByUid[friendUid]?.trim() || ""
      : selectedFocus;
  }

  function completePartnerFocus(partner: SpiritFriend) {
    if (completedPartnerFocusUids.includes(partner.uid)) return;
    const focus = resolvedPartnerFocus(partner.uid);
    if (!focus) return;
    if (partnerFocusByUid[partner.uid] === "Custom") {
      const savedFocus = savePartnerFocusOption(focus);
      if (savedFocus) {
        const nextFocusByUid = { ...partnerFocusByUid, [partner.uid]: savedFocus };
        setPartnerFocusByUid(nextFocusByUid);
        LS.set(partnerFocusKey, nextFocusByUid);
      }
    }
    spiritHaptic("medium");
    const next = [...completedPartnerFocusUids, partner.uid];
    setCompletedPartnerFocusUids(next);
    LS.set(partnerFocusCompletionKey, next);
    onCompleteAccountabilityFocus?.(partner, focus);
  }

  function undoPartnerFocus(partner: SpiritFriend) {
    if (!completedPartnerFocusUids.includes(partner.uid)) return;
    const next = completedPartnerFocusUids.filter((uid) => uid !== partner.uid);
    setCompletedPartnerFocusUids(next);
    LS.set(partnerFocusCompletionKey, next);
    spiritHaptic("light");
    showToast(`Focus reopened for ${friendDisplayLabel(partner)}.`);
  }

  function encouragementText(signal: PrayerSignal) {
    if (signal.type === "blessing") {
      return `${signal.fromName} sent you a blessing`;
    }
    if (signal.type === "accountability") {
      return `${signal.fromName} completed a shared focus`;
    }
    return `${signal.fromName} prayed for you today`;
  }

  function archiveSharedPrayer(sharedId: string) {
    setArchivedSharedPrayerIds((prev) => {
      const next = Array.from(new Set([...prev, sharedId]));
      LS.set(archivedSharedPrayerKey, next);
      return next;
    });
  }

  function markSharedPrayerPrayed(shared: SharedPrayer) {
    spiritHaptic("light");
    onSendPrayedForFriend({
      id: shared.id,
      uid: shared.ownerUid,
      displayName: shared.ownerName,
    });
    archiveSharedPrayer(shared.id);
  }

  function goToPrayerSection(
    sectionId: string,
    focusId?: string,
    revealAnswered = false
  ) {
    let targetSectionId = sectionId;
    if (revealAnswered) {
      setPrayerFilter("answered");
      setShowAnsweredPrayerDetails(true);
    } else if (sectionId === "my-prayer-request") {
      setPrayerAddKind("mine");
      setPrayerFilter("mine");
      targetSectionId = "unified-prayer-request";
    } else if (sectionId === "prayer-for-someone") {
      setPrayerAddKind("other");
      setPrayerFilter("others");
      targetSectionId = "unified-prayer-request";
    }
    window.setTimeout(
      () => {
        document
          .getElementById(targetSectionId)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (focusId)
          window.setTimeout(
            () => document.getElementById(focusId)?.focus(),
            360
          );
      },
      revealAnswered ? 80 : 0
    );
  }

  function scrollAnsweredPrayerPanel(prayerId: string, delay = 140) {
    window.setTimeout(() => {
      const target = document.getElementById(`public-share-${prayerId}`);
      const panel = document.querySelector(
        "#answered-prayers-section .answered-prayer-stack"
      ) as HTMLElement | null;
      if (!target || !panel) return;
      const targetTop = target.offsetTop;
      const idealTop = Math.max(0, targetTop - 18);
      panel.scrollTo({ top: idealTop, behavior: "smooth" });
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => {
        const submit = document.getElementById(`submit-public-${prayerId}`);
        if (!submit) return;
        const submitBottom = submit.offsetTop + submit.offsetHeight;
        const visibleBottom = panel.scrollTop + panel.clientHeight - 28;
        if (submitBottom > visibleBottom) {
          panel.scrollTo({
            top: Math.max(0, submitBottom - panel.clientHeight + 80),
            behavior: "smooth",
          });
        }
        submit.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 260);
    }, delay);
  }

  function startEditPrayer(prayer: PrayerFriend) {
    setEditingPrayerId(prayer.id);
    setEditPrayerName(prayer.name || "");
    setEditPrayerRequest(prayer.request);
    setEditTestimonyNote(prayer.answeredNote || "");
  }

  function cancelEditPrayer() {
    setEditingPrayerId(null);
    setEditPrayerName("");
    setEditPrayerRequest("");
    setEditTestimonyNote("");
  }

  function savePrayerEdit(prayer: PrayerFriend) {
    const requestText = editPrayerRequest.trim();
    if (!requestText) return;
    onUpdatePrayer(prayer.id, {
      name: (prayer.kind || "other") === "mine" ? "Me" : editPrayerName.trim(),
      request: requestText,
      answeredNote: prayer.answered
        ? editTestimonyNote.trim() || undefined
        : prayer.answeredNote,
    });
    cancelEditPrayer();
  }

  const renderChallengePicker = (
    value: PrayerDurationChoice,
    onChange: (days: PrayerDurationChoice) => void
  ) => (
    <div className="prayer-commitment-picker">
      <div className="prayer-commitment-options">
        <button
          key="ongoing"
          type="button"
          className={value == null ? "selected" : ""}
          onClick={() => onChange(null)}
        >
          Ongoing
        </button>
        {challengeOptions.map((days) => (
          <button
            key={days}
            type="button"
            className={value === days ? "selected" : ""}
            onClick={() => onChange(days)}
          >
            {days} days
          </button>
        ))}
      </div>
      <p className="prayer-commitment-note">
        {value == null
          ? "✦ Save this prayer without a set duration."
          : "✦ Small daily prayers become lasting testimonies."}
      </p>
    </div>
  );

  const renderPrayerCard = (prayer: PrayerFriend) => {
    const prayedDates = Array.isArray(prayer.prayedDates)
      ? prayer.prayedDates
      : [];
    const prayedToday = prayedDates.includes(today);
    const isMine = (prayer.kind || "other") === "mine";
    const streakDays = prayerStreak(prayedDates, today);
    const hasDuration = typeof prayer.challengeDays === "number";
    const challengeDays = prayer.challengeDays || 0;
    const uniquePrayerDays = new Set(prayedDates).size;
    const challengeCount = hasDuration
      ? Math.min(uniquePrayerDays, challengeDays)
      : uniquePrayerDays;
    const challengeDone = hasDuration && challengeCount >= challengeDays;
    const progressValue = hasDuration && challengeDays > 0
      ? Math.min(challengeCount / challengeDays, 1)
      : Math.min(challengeCount / 7, 1);
    const seedStage = challengeDone
      ? "bloom"
      : progressValue >= 0.66
      ? "flowering"
      : progressValue >= 0.33
      ? "sprout"
      : "seed";
    const status = prayer.answered
      ? "Answered"
      : challengeDone
      ? "Completed"
      : prayedToday
      ? "Praying"
      : prayedDates.length > 0
      ? "Growing"
      : "Praying";
    const primaryPrayerLabel = prayer.answered
      ? "Answered ✓"
      : challengeDone
      ? "Mark Completed"
      : prayedToday
      ? "Completed Today ✓"
      : "Pray Today";
    const primaryPrayerDisabled = prayer.answered || (!challengeDone && prayedToday);
    const editing = editingPrayerId === prayer.id;
    const title = isMine ? "Personal Prayer" : prayer.name || "Someone";
    const categoryLabel = isMine ? "Personal Prayer" : "Prayer for Others";
    return (
      <div
        key={prayer.id}
        className={`friend-card planted-prayer-card${prayedToday ? " prayed" : ""}${
          prayer.answered ? " answered" : ""
        }`}
      >
        {editing ? (
          <div className="planted-prayer-edit">
            {!isMine && (
              <input
                value={editPrayerName}
                onChange={(e) => setEditPrayerName(e.target.value)}
                placeholder="Person's name"
                className="sg-input"
              />
            )}
            <textarea
              value={editPrayerRequest}
              onChange={(e) => setEditPrayerRequest(e.target.value)}
              className="sg-textarea"
              style={{
                minHeight: 96,
                fontFamily: "'Nunito',sans-serif",
                lineHeight: 1.5,
              }}
            />
            <div className="planted-prayer-edit-actions">
              <GoldBtn
                onClick={() => savePrayerEdit(prayer)}
                disabled={!editPrayerRequest.trim()}
                style={{ minHeight: 42 }}
              >
                Save
              </GoldBtn>
              <button type="button" onClick={cancelEditPrayer}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="planted-prayer-topline">
              <span className="planted-prayer-category">
                <span aria-hidden="true">{isMine ? "♡" : "✦"}</span>
                {categoryLabel}
              </span>
              <span className={`prayer-status-chip status-${status.toLowerCase()}`}>
                {status}
              </span>
            </div>

            <div className="planted-prayer-body">
              <div className={`prayer-seedling seedling-${seedStage}`} aria-hidden="true">
                <span className="seedling-stem" />
                <span className="seedling-leaf left" />
                <span className="seedling-leaf right" />
                <span className="seedling-bloom" />
                <span className="seedling-soil" />
              </div>
              <div className="planted-prayer-copy">
                <h4>{title}</h4>
                <p>{shortText(prayer.request, 180)}</p>
              </div>
            </div>

            <div className="prayer-journey-section" aria-label="Prayer Journey">
              <div className="prayer-journey-heading">
                <span>Prayer Journey</span>
                <span>{hasDuration ? `${challengeCount}/${challengeDays} days` : "Ongoing"}</span>
              </div>
              <div className="prayer-journey-bar" aria-hidden="true">
                <span style={{ width: `${Math.round(progressValue * 100)}%` }} />
              </div>
              <div className="prayer-journey-meta">
                <span>{challengeCount} prayer day{challengeCount === 1 ? "" : "s"}</span>
                <span>{streakDays || 0} day streak</span>
              </div>
            </div>

            <div className="planted-prayer-actions">
              <button
                disabled={primaryPrayerDisabled}
                onClick={(e) => {
                  if (challengeDone) {
                    setAnswerTarget(prayer);
                    setAnswerNote(prayer.answeredNote || "");
                    setAnswerSharePublic(false);
                    return;
                  }
                  onPrayForPerson(prayer, e as ButtonEvent);
                }}
                className={`pray-now-btn planted-prayer-primary${prayedToday || challengeDone || prayer.answered ? " prayed" : ""}`}
              >
                <span className="pray-check-icon" aria-hidden="true">
                  {prayer.answered || prayedToday || challengeDone ? "✓" : "🙏"}
                </span>
                {primaryPrayerLabel}
              </button>
              <details className="planted-prayer-more">
                <summary aria-label="Prayer actions">•••</summary>
                <div className="planted-prayer-menu">
                  <button type="button" onClick={() => startEditPrayer(prayer)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAnswerTarget(prayer);
                      setAnswerNote("");
                      setAnswerSharePublic(false);
                    }}
                  >
                    Mark Answered
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => onDeletePrayer(prayer.id)}
                  >
                    Delete
                  </button>
                </div>
              </details>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      id="prayer-page-top"
      className="prayer-page-calm"
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      {friendPrayerLights.map((light) => (
        <PrayerLightTrail key={light.id} light={light} />
      ))}
      {friendLightMessage && (
        <div className="friend-light-message">{friendLightMessage}</div>
      )}
      {!friendsOnly && (
      <Card
        className="prayer-vessels-section"
        style={{
          padding: 16,
          background: "transparent",
          border: "none",
          outline: "none",
          boxShadow: "none",
        }}
      >
        <div
          className={`prayer-vessel-hero ${vesselStageClass}${
            answeredPrayers.length > 0 ? " has-answered-prayers" : ""
          }`}
          style={{
            ["--vessel-fill" as any]: `${prayerVesselFill}%`,
            ["--stored-prayer-count" as any]: Math.min(prayerVesselTotal, 8),
            ["--vessel-brightness" as any]: Math.min(prayerVesselTotal, 8),
          }}
        >
          <div className="prayer-vessel-light-rays" aria-hidden="true" />
          <span className="prayer-vessel-particle pv-particle-one">✦</span>
          <span className="prayer-vessel-particle pv-particle-two">✧</span>
          <span className="prayer-vessel-particle pv-particle-three">✦</span>
          <span className="prayer-vessel-particle pv-particle-four">✧</span>
          <div className="prayer-vessel-hero-copy">
            <span className="prayer-vessel-kicker">Prayer Vessel</span>
            <h2 className="serif">{prayerVesselTitle}</h2>
            <p>{vesselPurposeText}</p>
            {answeredPrayers.length > 0 ? (
              <div className="prayer-vessel-answer-highlight compact">
                <strong>
                  ✨ God answered {answeredThisMonth.length || answeredPrayers.length} prayer
                  {(answeredThisMonth.length || answeredPrayers.length) === 1 ? "" : "s"} this month
                </strong>
                {lastAnsweredPrayer && (
                  <span>
                    {lastAnsweredDate}{lastAnsweredText ? ` · “${lastAnsweredText}”` : ""}
                  </span>
                )}
              </div>
            ) : safePrayers.length === 0 ? (
              <p className="prayer-vessel-private-note">
                Private unless you choose to share later.
              </p>
            ) : null}
            <button
              type="button"
              className="prayer-vessel-primary-action"
              onClick={() => goToPrayerSection("my-prayer-request", "my-prayer-text")}
            >
              {safePrayers.length > 0 ? "Add Prayer" : "Start a Prayer"}
            </button>
          </div>
          <div className="prayer-vessel-scene" aria-hidden="true">
            <span className="prayer-vessel-cloud pv-cloud-one" />
            <span className="prayer-vessel-cloud pv-cloud-two" />
            <span className="prayer-vessel-star pv-star-one">✦</span>
            <span className="prayer-vessel-star pv-star-two">✧</span>
            <span className="prayer-vessel-star pv-star-three">✦</span>
            <div className="prayer-vessel-orb">
              <div className="prayer-vessel-bowl">
                <div className="prayer-vessel-fill" />
                <div className="prayer-vessel-stored-lights">
                  {Array.from({ length: Math.min(prayerVesselTotal, 8) }).map((_, i) => (
                    <span key={i} className={`stored-prayer-light stored-prayer-light-${i + 1}`} />
                  ))}
                </div>
                <div className="prayer-vessel-answer-spark" />
                <div className="prayer-vessel-shine" />
              </div>
              <div className="prayer-vessel-base" />
            </div>
            <div className="prayer-vessel-pedestal" />
            <img
              className="prayer-vessel-companion watching companion-ready"
              src="/characters/female/baby-spirit.png"
              alt=""
              loading="lazy"
            />
          </div>
        </div>

          <div className="prayer-vessel-stats prayer-vessel-open-grid" aria-label="Prayer pages">
            <button
              type="button"
              className={prayerFilter === "mine" ? "active" : ""}
              onClick={() => goToPrayerSection("my-prayer-request", "my-prayer-text")}
            >
              <b>{myPrayers.length}</b>
              <span>Personal Prayers</span>
              <em>Open</em>
            </button>
            <button
              type="button"
              className={prayerFilter === "others" ? "active" : ""}
              onClick={() => goToPrayerSection("prayer-for-someone", "prayer-person-name")}
            >
              <b>{otherPrayers.length}</b>
              <span>Prayer for Others</span>
              <em>Open</em>
            </button>
            <button
              type="button"
              className={prayerFilter === "answered" ? "active" : ""}
              onClick={() => goToPrayerSection("answered-prayers-section", undefined, true)}
            >
              <b>{answeredPrayers.length}</b>
              <span>Answered Prayers</span>
              <em>Open</em>
            </button>
          </div>

          {safePrayers.length > 0 && (
            <button
              type="button"
              className="prayer-landing-pulse"
              onClick={() => {
                if (activePrayers.length > 0) {
                  document
                    .getElementById("recent-prayers-preview")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                } else {
                  goToPrayerSection("my-prayer-request", "my-prayer-text");
                }
              }}
            >
              <span className="prayer-landing-day-chip">
                <strong>{prayersAddedToday}</strong>
                <span>today</span>
              </span>
              <span className="prayer-landing-pulse-copy">
                <span>Today's Focus</span>
                <p>{prayerLandingTarget}</p>
              </span>
              <span className="prayer-landing-open">Open</span>
            </button>
          )}

      </Card>
      )}

      {!friendsOnly && !prayerFilter && activePrayers.length > 0 && (
        <Card
          id="recent-prayers-preview"
          className="recent-prayers-preview-card sacred-card"
          style={{ padding: 16 }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <SectionLabel>RECENT PRAYERS</SectionLabel>
              <h3
                className="serif"
                style={{ margin: 0, fontSize: 22, color: C.textDark }}
              >
                Newly Recorded
              </h3>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 13.5,
                  color: C.textSoft,
                  lineHeight: 1.5,
                }}
              >
                New prayers appear here first so you can find them right away.
              </p>
            </div>
            <span className="placed-chip" style={{ flexShrink: 0 }}>
              {activePrayers.length} active
            </span>
          </div>
          <div
            className="scroll-list"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              maxHeight: "min(56vh, 520px)",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              paddingRight: 4,
            }}
          >
            {activePrayers.slice(0, 3).map(renderPrayerCard)}
          </div>
        </Card>
      )}

      {friendsOnly ? (
        <div
          className="spirit-friends-dashboard"
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <Card
            className="sacred-card prayer-friends-card spirit-friends-hero-card"
            style={{
              padding: 18,
              background:
                "linear-gradient(145deg,rgba(255,253,246,.98),rgba(255,250,236,.82))",
              border: "1px solid rgba(231,207,159,.46)",
              outline: "none",
              outlineOffset: 0,
              boxShadow: "0 12px 28px rgba(91,67,28,.07)",
            }}
          >
            <SectionLabel>SPIRIT FRIENDS</SectionLabel>
            <h2
              className="serif"
              style={{ margin: "0 0 3px", fontSize: 24, color: C.textDark }}
            >
              Prayer Circle
            </h2>
            <p className="prayer-circle-subtitle">A gentle place to pray for friends today.</p>
            <div className={`friends-hero-summary${hasUnreadPrayerNotes ? " has-new-blessing" : ""}`}>
              <span>
                {safeSpiritFriends.length > 0
                  ? `${safeSpiritFriends.length} friend${
                      safeSpiritFriends.length === 1 ? "" : "s"
                    } praying with you`
                  : "Begin with one trusted friend"}
              </span>
              {hasUnreadPrayerNotes && (
                <button
                  type="button"
                  className="new-blessing-shortcut"
                  onClick={() => {
                    setShowSpiritFriends(true);
                    setFriendTool("notes");
                    setShowFriendList(false);
                    window.setTimeout(() => {
                      document
                        .querySelector(".community-feed-card")
                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 80);
                  }}
                >
                  ✨ New Blessing
                  <span>{unreadPrayerNoteCount}</span>
                </button>
              )}
            </div>
            {signedInWithGoogle && (
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: "1px solid rgba(184,147,75,.22)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    className="prayer-circle-tool-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                      width: "100%",
                      maxWidth: 420,
                    }}
                  >
                    <button
                      type="button"
                      className={`soft-action-btn prayer-circle-tab${
                        friendTool === "add" ? " active" : ""
                      }${
                        incomingFriendRequestsOnly.length > 0
                          ? " friend-alert-btn"
                          : " muted"
                      }`}
                      onClick={() =>
                        setFriendTool(friendTool === "add" ? null : "add")
                      }
                    >
                      Add Friend
                      {incomingFriendRequestsOnly.length > 0 && (
                        <span className="friend-heart-dot friend-heart-inline" aria-label="New friend request">
                          ♥
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`soft-action-btn prayer-circle-tab${friendTool === "friends" ? " active" : " muted"}`}
                      onClick={() => {
                        setFriendTool(
                          friendTool === "friends" ? null : "friends"
                        );
                        setShowFriendList(true);
                      }}
                    >
                      Friends
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {!signedInWithGoogle ? (
            <Card className="sacred-card" style={{ padding: 16 }}>
              <h3
                className="serif"
                style={{ margin: "0 0 6px", fontSize: 18, color: C.textDark }}
              >
                Save your friends space
              </h3>
              <p style={{ margin: "0 0 12px", fontSize: 13.5, lineHeight: 1.55 }}>
                Sign in to add trusted friends and keep prayer notes private.
              </p>
              <GoldBtn onClick={onSignIn} style={{ width: "100%" }}>
                Sign in with Google
              </GoldBtn>
            </Card>
          ) : (
            <>
              {visibleFriendStatus && (
                <div className="unlock-note" style={{ textAlign: "center" }}>
                  {visibleFriendStatus}
                </div>
              )}

              {(friendTool === "add" || friendTool === "friends") && (
              <Card className="sacred-card spirit-friends-tools-card" style={{ padding: 16 }}>
                {friendTool === "add" && (
                  <div className="prayer-friend-panel add-friend-panel" style={{ marginTop: 0 }}>
                    <div className="prayer-friend-panel-title">Add Friend</div>
                    <button
                      type="button"
                      className="placed-chip friend-code-chip"
                      onClick={copyInviteCode}
                      disabled={!friendProfile?.inviteCode}
                      title="Copy invite code"
                      style={{
                        cursor: friendProfile?.inviteCode ? "pointer" : "default",
                        fontFamily: "'Nunito',sans-serif",
                        marginBottom: 2,
                      }}
                    >
                      Invite code: {friendProfile?.inviteCode || "loading..."} ·{" "}
                      {inviteCopied ? "Copied" : "Copy"}
                    </button>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0,1fr) auto",
                        gap: 8,
                      }}
                    >
                      <input
                        value={friendIdentifier}
                        onChange={(e) => setFriendIdentifier(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && submitFriendRequest()
                        }
                        placeholder="Email or invite code"
                        className="sg-input"
                      />
                      <GoldBtn
                        onClick={submitFriendRequest}
                        disabled={!friendIdentifier.trim()}
                        style={{ minWidth: 76 }}
                      >
                        Add
                      </GoldBtn>
                    </div>
                    {(incomingFriendRequestsOnly.length > 0 ||
                      outgoingFriendRequestsOnly.length > 0) && (
                      <div
                        className="scroll-list compact"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                          maxHeight: 230,
                          overflowY: "auto",
                          WebkitOverflowScrolling: "touch",
                          marginTop: 10,
                        }}
                      >
                        {incomingFriendRequestsOnly.map((request) => (
                          <div key={request.id} className="prayer-friend-row">
                            <div style={{ minWidth: 0 }}>
                              <div className="prayer-friend-name">
                                {request.fromName || friendlyNameFromEmail(request.fromEmail) || "Spirit Friend"}
                              </div>
                              <div className="prayer-friend-copy">
                                wants to pray with you.
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                              <button
                                onClick={() => onRespondFriendRequest(request, true)}
                                className="soft-action-btn"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => onRespondFriendRequest(request, false)}
                                className="soft-action-btn muted"
                              >
                                Decline
                              </button>
                            </div>
                          </div>
                        ))}
                        {outgoingFriendRequestsOnly.map((request) => (
                          <div key={request.id} className="prayer-friend-row">
                            <div style={{ minWidth: 0 }}>
                              <div className="prayer-friend-name">
                                {request.toName || friendlyNameFromEmail(request.toEmail) || "Spirit Friend"}
                              </div>
                              <div className="prayer-friend-copy">Request sent.</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => onCancelFriendRequest(request)}
                              className="soft-action-btn muted"
                              style={{ flexShrink: 0 }}
                            >
                              Undo
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {friendTool === "friends" && (
                  <div className="prayer-friend-panel friends-garden-panel" style={{ marginTop: 0 }}>
                    <div className="prayer-friend-panel-title">Friends</div>
                    {safeSpiritFriends.length > 0 && (
                      <div className="friend-prayer-prompt">
                        <div>
                          <span className="friend-prayer-prompt-kicker">Who can I pray for today?</span>
                          <p>Lord, bless my friends with peace, wisdom, and protection today.</p>
                        </div>
                        <span aria-hidden="true">🙏</span>
                      </div>
                    )}
                    {safeSpiritFriends.length === 0 ? (
                      <div className="empty-state" style={{ padding: 14 }}>
                        <div className="empty-state-icon">🤝</div>
                        <p className="empty-state-title">No prayer partners yet.</p>
                        <p className="empty-state-copy">
                          Invite someone you trust and build righteousness together through prayer.
                        </p>
                        <button
                          type="button"
                          className="soft-action-btn"
                          onClick={() => setFriendTool("add")}
                          style={{ width: "100%", justifyContent: "center" }}
                        >
                          Invite a Friend
                        </button>
                      </div>
                    ) : (
                      <div
                        className="scroll-list compact"
                        style={{
                          maxHeight: 300,
                          overflowY: "auto",
                          WebkitOverflowScrolling: "touch",
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {safeSpiritFriends.map((friend) => {
                          const prayedForFriendToday = prayedForFriendIds.includes(
                            friend.uid
                          );
                          const unreadFriendSignal = safeReceivedPrayerSignals.find(
                            (signal) =>
                              signal.fromUid === friend.uid &&
                              !seenPrayerNoteIds.includes(`signal:${signal.id}`)
                          );
                          const friendActivityLabel =
                            unreadFriendSignal?.type === "blessing"
                              ? "💛 New Blessing"
                              : unreadFriendSignal?.type === "accountability"
                              ? "🌿 Partner Update"
                              : unreadFriendSignal
                              ? "🙏 Prayed For You"
                              : "";
                          return (
                            <div
                              key={friend.uid}
                              className={`prayer-friend-row compact-friend-row${
                                unreadFriendSignal ? " has-friend-activity" : ""
                              }`}
                            >
                              <div className="compact-friend-identity instagram-friend-identity">
                                <span className="friend-spirit-avatar"><img src={friendSpiritImageSrc(friend)} alt="" /></span>
                                <div className="instagram-friend-copy" style={{ minWidth: 0 }}>
                                  <div className="prayer-friend-name">
                                    {friendDisplayLabel(friend)}
                                  </div>
                                  {friendActivityLabel && (
                                    <div className="instagram-friend-subtitle">
                                      {friendActivityLabel}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="compact-friend-actions">
                                <button
                                  onClick={(e) => markFriendPrayed(friend, e as ButtonEvent)}
                                  className={`soft-action-btn${
                                    prayedForFriendToday ? " friend-alert-btn" : ""
                                  }`}
                                  disabled={prayedForFriendToday}
                                  style={{
                                    cursor: prayedForFriendToday
                                      ? "default"
                                      : "pointer",
                                  }}
                                >
                                  {prayedForFriendToday ? "✓ Prayed" : "I prayed"}
                                </button>
                                <button
                                  type="button"
                                  className="soft-action-btn muted remove-friend-btn"
                                  aria-label={`Remove ${friendDisplayLabel(friend)}`}
                                  title="Remove friend"
                                  onClick={() => setFriendPendingRemoval(friend)}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </Card>
              )}

              <Card
                className={`sacred-card community-feed-card blessings-inbox-card${hasUnreadPrayerNotes && friendTool !== "notes" ? " blessings-new" : ""}`}
                style={{
                  padding: 16,
                  background:
                    hasUnreadPrayerNotes && friendTool !== "notes"
                      ? "linear-gradient(145deg,rgba(255,248,223,.98),rgba(246,218,146,.68))"
                      : undefined,
                  borderColor:
                    hasUnreadPrayerNotes && friendTool !== "notes"
                      ? "rgba(201,144,18,.34)"
                      : undefined,
                  boxShadow:
                    hasUnreadPrayerNotes && friendTool !== "notes"
                      ? "0 12px 28px rgba(201,144,18,.14), inset 0 1px 0 rgba(255,255,255,.82)"
                      : undefined,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <div>
                    <h3
                      className="serif"
                      style={{ margin: 0, fontSize: 18, color: C.textDark }}
                    >
                      Blessings Inbox
                    </h3>
                    <div className="prayer-friend-copy">
                      {unreadPrayerNoteCount > 0
                        ? `${unreadPrayerNoteCount} new encouragement${
                            unreadPrayerNoteCount === 1 ? "" : "s"
                          }`
                        : `${encouragementRows.length} encouragement${
                            encouragementRows.length === 1 ? "" : "s"
                          }`}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 7, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className={`soft-action-btn${
                        hasUnreadPrayerNotes ? " friend-alert-btn" : " muted"
                      }`}
                      onClick={() =>
                        setFriendTool(friendTool === "notes" ? null : "notes")
                      }
                    >
                      {friendTool === "notes" ? "Close" : "Open"}
                      {hasUnreadPrayerNotes && (
                        <span className="friend-alert-badge">
                          {unreadPrayerNoteCount}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <div className="community-feed-glow-line" />
                  {friendTool === "notes" && (
                    <div className="blessing-inbox-send-card">
                      <div className="blessing-card-head">
                        <div style={{ minWidth: 0 }}>
                          <div className="prayer-friend-panel-title">Send a Blessing</div>
                          <div className="prayer-friend-copy">
                            Choose a friend and send a short encouragement.
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`soft-action-btn${showBlessingPanel ? "" : " muted"}`}
                          onClick={() => setShowBlessingPanel((open) => !open)}
                          style={{ flexShrink: 0 }}
                        >
                          {showBlessingPanel ? "Close" : "Send"}
                        </button>
                      </div>
                      {showBlessingPanel && (
                        <div className="blessing-tools-body">
                          {safeSpiritFriends.length > 0 ? (
                            <>
                              <select
                                value={selectedBlessingFriend?.uid || ""}
                                onChange={(e) => setSelectedBlessingFriendUid(e.target.value)}
                                className="sg-input"
                                style={{ marginBottom: 10 }}
                              >
                                {safeSpiritFriends.map((friend) => (
                                  <option key={friend.uid} value={friend.uid}>
                                    {friendDisplayLabel(friend)}
                                  </option>
                                ))}
                              </select>
                              <div className="blessing-options-grid">
                                {blessingOptions.map((blessing) => {
                                  const [icon, ...rest] = blessing.split(" ");
                                  const blessingSent =
                                    !!selectedBlessingFriend &&
                                    sentBlessingKeys.includes(
                                      `${selectedBlessingFriend.uid}|${blessing}`
                                    );
                                  return (
                                    <button
                                      key={blessing}
                                      type="button"
                                      className={`soft-action-btn blessing-option-btn${
                                        blessingSent ? " friend-alert-btn" : " muted"
                                      }`}
                                      onClick={() => sendBlessing(blessing)}
                                      title={blessing}
                                    >
                                      <span style={{ fontSize: 18 }}>
                                        {blessingSent ? "✓" : icon}
                                      </span>
                                      <span>
                                        {blessingSent ? "Sent" : blessingShortLabels[blessing] || rest.join(" ")}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                              <button
                                type="button"
                                className="placed-chip"
                                onClick={() => setShowCustomBlessing((open) => !open)}
                                style={{
                                  marginTop: 10,
                                  cursor: "pointer",
                                  fontFamily: "'Nunito',sans-serif",
                                }}
                              >
                                {showCustomBlessing ? "Close note" : "+ Custom note"}
                              </button>
                              {showCustomBlessing && (
                                <div style={{ marginTop: 10 }}>
                                  <textarea
                                    value={customBlessingNote}
                                    onChange={(e) => setCustomBlessingNote(e.target.value)}
                                    className="sg-textarea"
                                    placeholder="Write a short blessing..."
                                    style={{
                                      minHeight: 74,
                                      fontFamily: "'Nunito',sans-serif",
                                      lineHeight: 1.5,
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="soft-action-btn"
                                    disabled={!selectedBlessingFriend || !customBlessingNote.trim()}
                                    onClick={() => sendBlessing("🕊️ A gentle blessing")}
                                    style={{ width: "100%", marginTop: 8 }}
                                  >
                                    Send Custom Blessing
                                  </button>
                                </div>
                              )}
                              {blessingSentMessage && (
                                <div
                                  className="blessing-sent-confirmation"
                                  role="status"
                                  aria-live="polite"
                                >
                                  <span aria-hidden="true">✓</span>
                                  <span>{blessingSentMessage}</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <button
                              type="button"
                              className="soft-action-btn"
                              onClick={() => setFriendTool("add")}
                              style={{ width: "100%" }}
                            >
                              Add a Friend
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {friendTool === "notes" && (
                  <div style={{ marginTop: 12 }}>
                    {shownEncouragementRows.length === 0 ? (
                      <div className="empty-state" style={{ padding: 14 }}>
                        <div className="empty-state-icon">💌</div>
                        <p className="empty-state-title">No blessings yet.</p>
                        <p className="empty-state-copy">
                          When friends pray for you or share encouragement, it will glow here.
                        </p>
                      </div>
                    ) : (
                      <div
                        className="scroll-list compact"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                          maxHeight: 260,
                          overflowY: "auto",
                          WebkitOverflowScrolling: "touch",
                        }}
                      >
                        {shownEncouragementRows.map((row) => (
                          <div
                            key={row.id}
                            className={`prayer-friend-row${
                              (row.signal &&
                                !seenPrayerNoteIds.includes(
                                  `signal:${row.signal.id}`
                                )) ||
                              (row.shared &&
                                !seenPrayerNoteIds.includes(
                                  `shared:${row.shared.id}`
                                ))
                                ? " unread-friend-activity"
                                : ""
                            }`}
                          >
                            <div style={{ fontSize: 20, flexShrink: 0 }}>
                              {row.icon}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{
                                  fontSize: 13.5,
                                  fontWeight: 500,
                                  color: C.textDark,
                                  lineHeight: 1.35,
                                }}
                              >
                                {row.text}
                              </div>
                              {row.subtext && (
                                <div className="prayer-friend-copy">
                                  {row.subtext}
                                </div>
                              )}
                            </div>
                            <span
                              className="prayer-friend-copy"
                              style={{ flexShrink: 0 }}
                            >
                              {row.time}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </Card>

              <Card className="sacred-card accountability-primary-card" style={{ padding: 16 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <h3
                      className="serif"
                      style={{ margin: 0, fontSize: 18, color: C.textDark }}
                    >
                      Accountability Partner
                    </h3>
                  </div>
                  {incomingAccountabilityRequests.length > 0 && (
                    <button
                      type="button"
                      className="soft-action-btn friend-alert-btn"
                      onClick={() => setShowPendingAccountability(true)}
                      style={{ flexShrink: 0 }}
                    >
                      Partner
                      <span className="friend-alert-badge">
                        {incomingAccountabilityRequests.length}
                      </span>
                    </button>
                  )}
                </div>

                {incomingAccountabilityRequests.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                    {incomingAccountabilityRequests.map((request) => (
                      <div key={request.id} className="prayer-friend-row">
                        <div style={{ minWidth: 0 }}>
                          <div className="prayer-friend-name">{request.fromName || friendlyNameFromEmail(request.fromEmail) || "Spirit Friend"}</div>
                          <div className="prayer-friend-copy">
                            wants to be your accountability partner.
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button
                            type="button"
                            className="soft-action-btn"
                            onClick={() => onRespondAccountabilityRequest?.(request, true)}
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            className="soft-action-btn muted"
                            onClick={() => onRespondAccountabilityRequest?.(request, false)}
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {latestAccountabilityUpdate && (
                  <div className="accountability-update-card">
                    <div>
                      <div className="accountability-update-title">
                        🌿 {latestAccountabilityUpdate.fromName} completed a shared focus
                      </div>
                      <div className="prayer-friend-copy">
                        {latestAccountabilityUpdate.message ||
                          "Send a little encouragement for their walk today."}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="soft-action-btn"
                      onClick={() => {
                        setSelectedBlessingFriendUid(
                          latestAccountabilityUpdate.fromUid
                        );
                        setFriendTool("notes");
                        setShowBlessingPanel(true);
                        setShowCustomBlessing(true);
                      }}
                    >
                      Send Encouragement
                    </button>
                  </div>
                )}

                {accountabilityPartners.length > 0 ? (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        marginBottom: 8,
                      }}
                    >
                      <div className="prayer-friend-copy">
                        {accountabilityPartners.length} partner
                        {accountabilityPartners.length === 1 ? "" : "s"} walking with you
                      </div>
                      <button
                        type="button"
                        className="soft-action-btn muted"
                        onClick={() => {
                          setShowAccountabilityManage((open) => !open);
                          setShowPartnerAskList(false);
                        }}
                      >
                        {showAccountabilityManage ? "Done" : "Manage"}
                      </button>
                    </div>
                    <div className="accountability-partner-scroll">
                      {accountabilityPartners.map((partner) => (
                        <div key={partner.uid} className="accountability-partner-slide">
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span className="friend-spirit-avatar partner"><img src={friendSpiritImageSrc(partner)} alt="" /></span>
                            <div style={{ minWidth: 0 }}>
                              <div className="prayer-friend-name">
                                {friendDisplayLabel(partner)}
                              </div>
                              <div className="prayer-friend-copy">Shared focus</div>
                            </div>
                          </div>
                          <label className="partner-focus-label">
                            Shared focus
                            <select
                              className="sg-input partner-focus-select"
                              value={
                                accountabilityFocusOptions.includes(
                                  partnerFocusByUid[partner.uid]
                                )
                                  ? partnerFocusByUid[partner.uid]
                                  : "Pray daily"
                              }
                              onChange={(event) =>
                                updatePartnerFocus(partner.uid, event.target.value)
                              }
                            >
                              {accountabilityFocusOptions.map((focus) => (
                                <option key={focus} value={focus}>
                                  {focus}
                                </option>
                              ))}
                            </select>
                            {partnerFocusByUid[partner.uid] === "Custom" && (
                              <input
                                type="text"
                                className="sg-input partner-custom-focus-input"
                                value={customPartnerFocusByUid[partner.uid] || ""}
                                onChange={(event) =>
                                  updateCustomPartnerFocus(
                                    partner.uid,
                                    event.target.value
                                  )
                                }
                                onBlur={(event) => savePartnerFocusOption(event.target.value)}
                                placeholder="Write your shared focus..."
                                maxLength={80}
                              />
                            )}
                          </label>
                          <button
                            type="button"
                            className={`partner-focus-complete-btn${
                              completedPartnerFocusUids.includes(partner.uid)
                                ? " completed"
                                : ""
                            }`}
                            disabled={
                              completedPartnerFocusUids.includes(partner.uid) ||
                              !resolvedPartnerFocus(partner.uid)
                            }
                            onClick={() => completePartnerFocus(partner)}
                          >
                            {completedPartnerFocusUids.includes(partner.uid)
                              ? "✓ Completed Today"
                              : "Complete Focus"}
                          </button>
                          {completedPartnerFocusUids.includes(partner.uid) && (
                            <button
                              type="button"
                              className="partner-focus-undo-btn"
                              onClick={() => undoPartnerFocus(partner)}
                            >
                              Undo
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {accountabilityPartners.length > 1 && (
                      <div className="prayer-friend-copy" style={{ textAlign: "center", margin: "5px 0 9px" }}>
                        Swipe to see each partner
                      </div>
                    )}
                    <div className="partner-ask-collapse" style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        className="soft-action-btn muted partner-ask-toggle"
                        onClick={() => setShowPartnerAskList((open) => !open)}
                        style={{ width: "100%" }}
                      >
                        {showPartnerAskList ? "Hide friend options" : "Ask a friend to partner"}
                        <span className={`quick-prayer-chevron${showPartnerAskList ? " open" : ""}`}>⌄</span>
                      </button>
                      {showPartnerAskList && (
                        <div className="prayer-friend-panel partner-ask-panel" style={{ marginTop: 8 }}>
                          <div className="prayer-friend-panel-title">Ask another friend</div>
                          {availableAccountabilityFriends.length > 0 ? (
                            <div className="friend-checkbox-list">
                              {availableAccountabilityFriends.map((friend) => (
                                <button
                                  key={friend.uid}
                                  type="button"
                                  className="friend-checkbox-row"
                                  disabled={!onSendAccountabilityRequest}
                                  onClick={() => onSendAccountabilityRequest?.(friend)}
                                >
                                  <span>Ask {friendDisplayLabel(friend)}</span>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="prayer-friend-copy" style={{ lineHeight: 1.5 }}>
                              Everyone available is already a partner or has a pending request.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {showAccountabilityManage && (
                      <div
                        className="prayer-friend-panel"
                        style={{ marginTop: 10 }}
                      >
                        <div className="prayer-friend-panel-title">Partners</div>
                        {accountabilityPartners.map((friend) => (
                          <div
                            key={friend.uid}
                            className="prayer-friend-row compact-friend-row"
                          >
                            <div className="compact-friend-identity">
                              <span className="friend-spirit-avatar small"><img src={friendSpiritImageSrc(friend)} alt="" /></span>
                              <div className="prayer-friend-name">
                                {friendDisplayLabel(friend)}
                              </div>
                            </div>
                            <div className="compact-friend-actions">
                              <button
                                type="button"
                                className="soft-action-btn muted remove-friend-btn"
                                aria-label={`Remove ${friendDisplayLabel(friend)} as accountability partner`}
                                title="Remove partner"
                                onClick={() => onRemoveAccountabilityPartner?.(friend)}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="soft-action-btn"
                          onClick={() => {
                            setShowAccountabilityManage(false);
                            setShowPartnerAskList(true);
                          }}
                          style={{ width: "100%" }}
                        >
                          Ask Another Friend
                        </button>
                      </div>
                    )}
                  </div>
                ) : outgoingAccountabilityRequests.length > 0 ? (
                  <div>
                    <div className="friend-card" style={{ marginBottom: 8 }}>
                      <div className="prayer-friend-name">
                        {outgoingAccountabilityRequests[0].toName || friendlyNameFromEmail(outgoingAccountabilityRequests[0].toEmail) || "Spirit Friend"}
                      </div>
                      <div className="prayer-friend-copy">Pending acceptance</div>
                      <button
                        type="button"
                        className="soft-action-btn muted"
                        onClick={() => onCancelFriendRequest(outgoingAccountabilityRequests[0])}
                        style={{ width: "100%", marginTop: 10 }}
                      >
                        Cancel Request
                      </button>
                    </div>
                    {outgoingAccountabilityRequests.length > 1 && (
                      <button
                        type="button"
                        className="placed-chip"
                        onClick={() => setShowPendingAccountability((open) => !open)}
                        style={{ cursor: "pointer", fontFamily: "'Nunito',sans-serif" }}
                      >
                        {showPendingAccountability ? "Hide pending" : "View pending requests"}
                      </button>
                    )}
                    {showPendingAccountability && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 8 }}>
                        {outgoingAccountabilityRequests.slice(1).map((request) => (
                          <div key={request.id} className="prayer-friend-row">
                            <div style={{ minWidth: 0 }}>
                              <div className="prayer-friend-name">
                                {request.toName || friendlyNameFromEmail(request.toEmail) || "Spirit Friend"}
                              </div>
                              <div className="prayer-friend-copy">Pending acceptance</div>
                            </div>
                            <button
                              type="button"
                              className="soft-action-btn muted"
                              onClick={() => onCancelFriendRequest(request)}
                            >
                              Undo
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {availableAccountabilityFriends.length > 0 && (
                      <div className="partner-ask-collapse" style={{ marginTop: 10 }}>
                        <button
                          type="button"
                          className="soft-action-btn muted partner-ask-toggle"
                          onClick={() => setShowPartnerAskList((open) => !open)}
                          style={{ width: "100%" }}
                        >
                          {showPartnerAskList ? "Hide friend options" : "Ask another friend"}
                          <span className={`quick-prayer-chevron${showPartnerAskList ? " open" : ""}`}>⌄</span>
                        </button>
                        {showPartnerAskList && (
                          <div className="prayer-friend-panel partner-ask-panel" style={{ marginTop: 8 }}>
                            <div className="prayer-friend-panel-title">Partner with a friend</div>
                            <div className="friend-checkbox-list">
                              {availableAccountabilityFriends.map((friend) => (
                                <button
                                  key={friend.uid}
                                  type="button"
                                  className="friend-checkbox-row"
                                  disabled={!onSendAccountabilityRequest}
                                  onClick={() => onSendAccountabilityRequest?.(friend)}
                                >
                                  <span>Ask {friendDisplayLabel(friend)}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="empty-state" style={{ padding: 14 }}>
                    <p className="empty-state-title">Choose one trusted partner</p>
                    <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.5 }}>
                      Invite one friend to encourage your daily walk with God.
                    </p>
                    {availableAccountabilityFriends.length > 0 && showPartnerAskList && (
                      <div className="friend-checkbox-list" style={{ marginBottom: 10 }}>
                        {availableAccountabilityFriends.map((friend) => (
                          <button
                            key={friend.uid}
                            type="button"
                            className="friend-checkbox-row"
                            disabled={!onSendAccountabilityRequest}
                            onClick={() => onSendAccountabilityRequest?.(friend)}
                          >
                            <span>Ask {friendDisplayLabel(friend)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className="soft-action-btn"
                      onClick={() => {
                        if (safeSpiritFriends.length > 0) {
                          setShowPartnerAskList((open) => !open);
                        } else {
                          openPartnerChooser();
                        }
                      }}
                      style={{ width: "100%" }}
                    >
                      {safeSpiritFriends.length > 0
                        ? showPartnerAskList
                          ? "Hide friends"
                          : "Choose from friends"
                        : "Add a Friend"}
                    </button>
                  </div>
                )}
              </Card>



            </>
          )}
        </div>
      ) : showSpiritFriends ? (
      <Card
        className="sacred-card prayer-friends-card"
	        style={{
	          padding: 16,
	          background:
	            "linear-gradient(135deg,rgba(255,252,210,.96),rgba(255,248,223,.9),rgba(255,253,246,.96))",
	          border: `2px solid ${C.gold}`,
	          outline: `2px solid ${C.gold}`,
	          outlineOffset: -3,
	          boxShadow: "0 4px 18px rgba(180,130,20,.16)",
	        }}
	      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 10,
            marginBottom: showSpiritFriends ? 12 : 0,
          }}
        >
          <div>
            <SectionLabel>Spirit Friends</SectionLabel>
            <h3
              className="serif"
              style={{ margin: "0 0 4px", fontSize: 17, color: C.textDark }}
            >
              Pray Together
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: C.textSoft,
                lineHeight: 1.55,
                maxWidth: 520,
              }}
            >
              Add trusted friends, share selected prayers, and let them know
              when you prayed.
            </p>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              className="placed-chip"
              onClick={() => {
                setShowSpiritFriends(true);
                setFriendTool("add");
                setShowFriendList(false);
              }}
              style={{
                border: `1px solid ${
                  friendTool === "add" ? C.gold : "rgba(184,147,75,.20)"
                }`,
                cursor: "pointer",
                fontFamily: "'Nunito',sans-serif",
              }}
            >
              Add a friend
            </button>
            <button
              type="button"
              className="placed-chip"
              onClick={() => {
                setShowSpiritFriends(true);
                setFriendTool(hasIncomingFriendRequests ? "add" : "friends");
                setShowFriendList(!hasIncomingFriendRequests);
              }}
              style={{
                background: hasIncomingFriendRequests
                  ? GOLD_GRAD
                  : "rgba(255,253,246,.82)",
                border: `1.5px solid ${
                  hasIncomingFriendRequests || friendTool === "friends"
                    ? C.gold
                    : "rgba(184,147,75,.20)"
                }`,
                boxShadow: hasIncomingFriendRequests
                  ? "0 6px 16px rgba(180,130,20,.18)"
                  : undefined,
                color: hasIncomingFriendRequests ? C.textDark : undefined,
                cursor: "pointer",
                fontFamily: "'Nunito',sans-serif",
              }}
            >
              Friends
              {hasIncomingFriendRequests
                ? ` ${incomingFriendRequestsOnly.length}`
                : ""}
            </button>
            <button
              type="button"
              className="placed-chip"
              onClick={() => {
                setShowSpiritFriends(true);
                setFriendTool("notes");
                setShowFriendList(false);
              }}
              style={{
                background: hasUnreadPrayerNotes
                  ? GOLD_GRAD
                  : "rgba(255,253,246,.82)",
                border: `1.5px solid ${
                  hasUnreadPrayerNotes || friendTool === "notes"
                    ? C.gold
                    : "rgba(184,147,75,.20)"
                }`,
                boxShadow: hasUnreadPrayerNotes
                  ? "0 6px 16px rgba(180,130,20,.18)"
                  : undefined,
                cursor: "pointer",
                fontFamily: "'Nunito',sans-serif",
                color: hasUnreadPrayerNotes ? C.textDark : undefined,
              }}
            >
              {hasUnreadPrayerNotes ? `Blessings ${unreadPrayerNoteCount}` : "Notes"}
            </button>
            <button
              type="button"
              onClick={() =>
                setShowSpiritFriends((open) => {
                  if (open) {
                    setFriendTool(null);
                    setShowFriendList(false);
                  } else {
                    setFriendTool(null);
                    setShowFriendList(false);
                  }
                  return !open;
                })
              }
              className="soft-action-btn"
              aria-expanded={showSpiritFriends}
            >
              {showSpiritFriends ? "Hide" : "Open"}
            </button>
          </div>
        </div>

        {showSpiritFriends &&
          (!signedInWithGoogle ? (
            <div className="empty-state" style={{ padding: 14 }}>
              <p className="empty-state-title" style={{ fontWeight: 700 }}>
                Sign in to add prayer friends.
              </p>
              <p
                style={{ margin: "0 0 12px", fontSize: 13.5, lineHeight: 1.55 }}
              >
                Friend requests and shared prayers need a saved account so they
                can stay private between selected people.
              </p>
              <GoldBtn onClick={onSignIn} style={{ width: "100%" }}>
                Sign in with Google
              </GoldBtn>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {safeSpiritFriends.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setFriendTool("share");
                    setShowFriendList(false);
                  }}
                  className={`soft-action-btn${
                    friendTool === "share" ? "" : " muted"
                  }`}
                  style={{ width: "100%", minHeight: 44 }}
                >
                  Share Prayer
                  {activePrayers.length > 0 ? ` · ${activePrayers.length}` : ""}
                </button>
              )}

              {visibleFriendStatus && (
                <span className="placed-chip" style={{ color: C.textSoft }}>
                  {visibleFriendStatus}
                </span>
              )}

              {friendTool === "add" && (
                <div className="prayer-friend-panel add-friend-panel">
                  <div className="prayer-friend-panel-title">Add Friend</div>
                  <p
                    style={{
                      margin: "0 0 10px",
                      fontSize: 13,
                      color: C.textSoft,
                      lineHeight: 1.5,
                    }}
                  >
                    Share your invite code, or enter a friend's email or code to
                    send a gentle prayer request.
                  </p>
                  <button
                    type="button"
                    className="placed-chip friend-code-chip"
                    onClick={copyInviteCode}
                    disabled={!friendProfile?.inviteCode}
                    title="Copy invite code"
                    style={{
                      alignSelf: "flex-start",
                      cursor: friendProfile?.inviteCode ? "pointer" : "default",
                      fontFamily: "'Nunito',sans-serif",
                    }}
                  >
                    Invite code: {friendProfile?.inviteCode || "loading..."} ·{" "}
                    {inviteCopied ? "Copied" : "Copy"}
                  </button>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: 8,
                    }}
                  >
                    <input
                      value={friendIdentifier}
                      onChange={(e) => setFriendIdentifier(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && submitFriendRequest()
                      }
                      placeholder="Email or invite code"
                      className="sg-input"
                    />
                    <GoldBtn
                      onClick={submitFriendRequest}
                      disabled={!friendIdentifier.trim()}
                      style={{ minWidth: 96 }}
                    >
                      Add
                    </GoldBtn>
                  </div>

                  {incomingFriendRequestsOnly.length > 0 && (
                    <>
                      <div className="prayer-friend-panel-title">
                        Friend Requests
                      </div>
                      {incomingFriendRequestsOnly.map((request) => (
                        <div key={request.id} className="prayer-friend-row">
                          <div style={{ minWidth: 0 }}>
                            <div className="prayer-friend-name">
                              {request.fromName || friendlyNameFromEmail(request.fromEmail) || "Spirit Friend"}
                            </div>
                            <div className="prayer-friend-copy">
                              wants to pray with you.
                            </div>
                          </div>
                          <div
                            style={{ display: "flex", gap: 6, flexShrink: 0 }}
                          >
                            <button
                              onClick={() =>
                                onRespondFriendRequest(request, true)
                              }
                              className="soft-action-btn"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() =>
                                onRespondFriendRequest(request, false)
                              }
                              className="soft-action-btn muted"
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {outgoingFriendRequestsOnly.length > 0 && (
                    <>
                      <div className="prayer-friend-panel-title">
                        Pending Requests
                      </div>
                      {outgoingFriendRequestsOnly.map((request) => (
                        <div key={request.id} className="prayer-friend-row">
                          <div style={{ minWidth: 0 }}>
                            <div className="prayer-friend-name">
                              {request.toName || friendlyNameFromEmail(request.toEmail) || "Spirit Friend"}
                            </div>
                            <div className="prayer-friend-copy">
                              Request sent. They’ll see it when they open Spirit
                              Garden.
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => onCancelFriendRequest(request)}
                            className="soft-action-btn muted"
                            style={{ flexShrink: 0 }}
                          >
                            Undo
                          </button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {friendTool === "friends" && (
                <div className="prayer-friend-panel">
                  <div className="prayer-friend-panel-title">
                    Accountability Partner
                  </div>
                  {incomingAccountabilityRequests.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        marginBottom: 10,
                      }}
                    >
                      {incomingAccountabilityRequests.map((request) => (
                        <div key={request.id} className="prayer-friend-row">
                          <div style={{ minWidth: 0 }}>
                            <div className="prayer-friend-name">
                              {request.fromName || friendlyNameFromEmail(request.fromEmail) || "Spirit Friend"}
                            </div>
                            <div className="prayer-friend-copy">
                              wants to be your accountability partner.
                            </div>
                          </div>
                          <div
                            style={{ display: "flex", gap: 6, flexShrink: 0 }}
                          >
                            <button
                              type="button"
                              className="soft-action-btn"
                              onClick={() =>
                                onRespondAccountabilityRequest?.(request, true)
                              }
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              className="soft-action-btn muted"
                              onClick={() =>
                                onRespondAccountabilityRequest?.(request, false)
                              }
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {accountabilityPartner ? (
                    <div className="friend-card prayed">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          marginBottom: 10,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div className="prayer-friend-name">
                            {friendDisplayLabel(accountabilityPartner)}
                          </div>
                          <div className="prayer-friend-copy">
                            Spirit stage · Growing with you
                          </div>
                        </div>
                      </div>
                      <div className="unlock-note">
                        Open Spirit Friends to view and complete your shared focus.
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state" style={{ padding: 14 }}>
                      <p className="empty-state-title">
                        Choose one trusted partner
                      </p>
                      <p
                        style={{
                          margin: "0 0 12px",
                          fontSize: 13,
                          lineHeight: 1.55,
                        }}
                      >
                        {safeSpiritFriends.length > 0
                          ? "Send a request first. They can accept when they open Spirit Garden."
                          : "Add a friend first, then Spirit Garden can become a gentle place to encourage each other."}
                      </p>
                      {outgoingAccountabilityRequests.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                          {outgoingAccountabilityRequests.map((request) => (
                            <div key={request.id} className="prayer-friend-row">
                              <div style={{ minWidth: 0 }}>
                                <div className="prayer-friend-name">
                                  {request.toName || friendlyNameFromEmail(request.toEmail) || "Spirit Friend"}
                                </div>
                                <div className="prayer-friend-copy">
                                  Partner request sent. They can accept when
                                  they open Spirit Garden.
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => onCancelFriendRequest(request)}
                                className="soft-action-btn muted"
                                style={{ flexShrink: 0 }}
                              >
                                Undo
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {safeSpiritFriends.length > 0 ? (
                        <div className="partner-ask-collapse">
                          <button
                            type="button"
                            className="soft-action-btn muted partner-ask-toggle"
                            onClick={() => setShowPartnerAskList((open) => !open)}
                            style={{ width: "100%" }}
                          >
                            {showPartnerAskList ? "Hide friend options" : "Choose from friends"}
                            <span className={`quick-prayer-chevron${showPartnerAskList ? " open" : ""}`}>⌄</span>
                          </button>
                          {showPartnerAskList && (
                            <div className="friend-checkbox-list" style={{ marginTop: 8 }}>
                              {safeSpiritFriends.map((friend) => {
                                const alreadyPending =
                                  outgoingAccountabilityRequests.some(
                                    (request) => request.toUid === friend.uid
                                  );
                                return (
                                  <button
                                    key={friend.uid}
                                    type="button"
                                    className="friend-checkbox-row"
                                    disabled={
                                      alreadyPending ||
                                      !!accountabilityPartner ||
                                      !onSendAccountabilityRequest
                                    }
                                    onClick={() =>
                                      onSendAccountabilityRequest?.(friend)
                                    }
                                  >
                                    <span>
                                      {alreadyPending
                                        ? `Request sent to ${friendDisplayLabel(friend)}`
                                        : `Ask ${friendDisplayLabel(friend)}`}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="soft-action-btn"
                          onClick={() => setFriendTool("add")}
                        >
                          Add a friend
                        </button>
                      )}
                    </div>
                  )}

                  {safeSpiritFriends.length > 0 && (
                    <>
                      <div className="prayer-friend-panel-title">
                        Send a Blessing
                      </div>
                      <select
                        value={selectedBlessingFriend?.uid || ""}
                        onChange={(e) => setSelectedBlessingFriendUid(e.target.value)}
                        className="sg-input"
                      >
                        {safeSpiritFriends.map((friend) => (
                          <option key={friend.uid} value={friend.uid}>
                            {friendDisplayLabel(friend)}
                          </option>
                        ))}
                      </select>
                      <div className="blessing-options-grid">
                        {blessingOptions.map((blessing) => {
                          const [icon, ...rest] = blessing.split(" ");
                          const blessingSent =
                            !!selectedBlessingFriend &&
                            sentBlessingKeys.includes(
                              `${selectedBlessingFriend.uid}|${blessing}`
                            );
                          return (
                            <button
                              key={blessing}
                              type="button"
                              className={`soft-action-btn blessing-option-btn${
                                blessingSent ? " friend-alert-btn" : " muted"
                              }`}
                              onClick={() => sendBlessing(blessing)}
                              title={blessing}
                            >
                              <span style={{ fontSize: 18 }}>
                                {blessingSent ? "✓" : icon}
                              </span>
                              <span>
                                {blessingSent ? "Sent" : blessingShortLabels[blessing] || rest.join(" ")}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <textarea
                        value={customBlessingNote}
                        onChange={(e) => setCustomBlessingNote(e.target.value)}
                        className="sg-textarea"
                        placeholder="Optional note..."
                        style={{
                          minHeight: 74,
                          fontFamily: "'Nunito',sans-serif",
                          lineHeight: 1.5,
                        }}
                      />
                      <button
                        type="button"
                        className="soft-action-btn"
                        disabled={!selectedBlessingFriend || !customBlessingNote.trim()}
                        onClick={() => sendBlessing("🕊️ A gentle blessing")}
                      >
                        Send Custom Blessing
                      </button>
                      {blessingSentMessage && (
                        <div
                          className="blessing-sent-confirmation"
                          role="status"
                          aria-live="polite"
                        >
                          <span aria-hidden="true">✓</span>
                          <span>{blessingSentMessage}</span>
                        </div>
                      )}
                    </>
                  )}

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                    }}
                  >
                    <div className="prayer-friend-panel-title">Friends</div>
                    {safeSpiritFriends.length > 0 && (
                      <button
                        type="button"
                        className="placed-chip"
                        onClick={() => setShowFriendList((open) => !open)}
                        style={{
                          border: "1px solid rgba(184,147,75,.20)",
                          cursor: "pointer",
                          fontFamily: "'Nunito',sans-serif",
                        }}
                      >
                        {showFriendList ? "Hide list" : "Show list"}
                      </button>
                    )}
                  </div>
                  {safeSpiritFriends.length > 0 ? (
                    showFriendList ? (
                      <div
                        className="scroll-list compact"
                        style={{
                          maxHeight: 220,
                          overflowY: "auto",
                          WebkitOverflowScrolling: "touch",
                        }}
                      >
                        {safeSpiritFriends.map((friend) => {
                          const prayedForFriendToday =
                            prayedForFriendIds.includes(friend.uid);
                          return (
                            <div
                              key={friend.uid}
                              className="prayer-friend-row compact-friend-row"
                            >
                              <div className="compact-friend-identity">
                                <span className="friend-spirit-avatar"><img src={friendSpiritImageSrc(friend)} alt="" /></span>
                                <div className="prayer-friend-name">
                                  {friendDisplayLabel(friend)}
                                </div>
                              </div>
                              <div className="compact-friend-actions">
                                <button
                                  onClick={(e) => markFriendPrayed(friend, e as ButtonEvent)}
                                  className={`soft-action-btn${
                                    prayedForFriendToday ? " friend-alert-btn" : ""
                                  }`}
                                  disabled={prayedForFriendToday}
                                  style={{
                                    cursor: prayedForFriendToday
                                      ? "default"
                                      : "pointer",
                                  }}
                                >
                                  {prayedForFriendToday ? "✓ Prayed" : "I prayed"}
                                </button>
                                <button
                                  type="button"
                                  className="soft-action-btn muted remove-friend-btn"
                                  aria-label={`Remove ${friendDisplayLabel(friend)}`}
                                  title="Remove friend"
                                  onClick={() => setFriendPendingRemoval(friend)}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="unlock-note">
                        {safeSpiritFriends.length} friend
                        {safeSpiritFriends.length === 1 ? "" : "s"} saved.
                      </div>
                    )
                  ) : null}
                </div>
              )}

              {friendTool === "notes" && (
                <div className="prayer-friend-panel">
                  <div className="prayer-friend-panel-title">
                    Recent Encouragements
                  </div>
		                  {safeReceivedPrayerSignals.length === 0 &&
	                  visibleSharedPrayers.length === 0 ? (
                    <div className="empty-state" style={{ padding: 14 }}>
                      <p className="empty-state-title">No notes yet.</p>
                      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55 }}>
                        Prayer, blessings, and shared requests from trusted friends will appear here.
                      </p>
                    </div>
                  ) : (
                    <div
                      className="scroll-list compact"
                      style={{
                        maxHeight: 280,
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                      }}
                    >
		                      {safeReceivedPrayerSignals.map((signal) => (
	                        <div key={signal.id} className="prayer-friend-row">
	                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13.5,
                                fontWeight: 500,
                                color: C.textDark,
                                lineHeight: 1.35,
                              }}
                            >
                              {signal.type === "blessing"
                                ? signal.blessingLabel || "🕊️ Blessing"
                                : encouragementText(signal)}
                            </div>
                            <div className="prayer-friend-copy">
                              {signal.type === "blessing" && signal.message
                                ? signal.message
                                : signal.createdAt
                                ? new Date(signal.createdAt).toLocaleDateString(
                                    undefined,
                                    { month: "short", day: "numeric" }
                                  )
	                                : "Today"}
	                            </div>
	                          </div>
	                          <button
	                            onClick={() =>
	                              onSendPrayedForFriend(
		                                safeSpiritFriends.find(
	                                  (friend) => friend.uid === signal.fromUid
	                                ) || {
	                                  id: signal.id,
	                                  uid: signal.fromUid,
	                                  displayName: signal.fromName,
	                                }
	                              )
	                            }
	                            className="soft-action-btn"
	                          >
	                            Pray for them too
	                          </button>
	                        </div>
	                      ))}
	                      {visibleSharedPrayers.map((shared) => (
	                        <div key={shared.id} className="friend-card">
	                          <div className="prayer-friend-name">
	                            Prayer from {shared.ownerName}
                          </div>
                          <p
                            style={{
                              margin: "5px 0 0",
                              fontSize: 13.5,
                              color: C.textSoft,
                              lineHeight: 1.55,
                            }}
	                          >
	                            {shortText(shared.request, 160)}
	                          </p>
	                          <div
	                            style={{
	                              display: "flex",
	                              gap: 7,
	                              flexWrap: "wrap",
	                              marginTop: 10,
	                            }}
	                          >
	                            <button
	                              onClick={() => markSharedPrayerPrayed(shared)}
	                              className="soft-action-btn"
	                            >
	                              Mark prayed
	                            </button>
	                            <button
	                              onClick={() => archiveSharedPrayer(shared.id)}
	                              className="soft-action-btn muted"
	                            >
	                              Archive
	                            </button>
	                          </div>
	                        </div>
	                      ))}
                    </div>
                  )}
                </div>
              )}

              {friendTool === "share" && (
                <div className="prayer-friend-panel">
                  <div className="prayer-friend-panel-title">Share Prayer</div>
                  {safeSpiritFriends.length === 0 ? (
                    <div className="unlock-note">
                      Add a friend first, then you can share selected prayers.
                    </div>
                  ) : activePrayers.length === 0 ? (
                    <div className="unlock-note">
                      Add a prayer request first, then choose who can see it.
                    </div>
                  ) : (
                    <>
                      <select
                        value={prayerToShare?.id || ""}
                        onChange={(e) =>
                          setSelectedPrayerToShare(e.target.value)
                        }
                        className="sg-input"
                        style={{ marginBottom: 8 }}
                      >
                        {activePrayers.map((prayer) => (
                          <option key={prayer.id} value={prayer.id}>
                            {(prayer.kind || "other") === "mine"
                              ? "My prayer"
                              : `For ${prayer.name || "Someone"}`}
                            : {shortText(prayer.request, 42)}
                          </option>
                        ))}
                      </select>
                      <div className="friend-checkbox-list">
                        {safeSpiritFriends.map((friend) => (
                          <label
                            key={friend.uid}
                            className="friend-checkbox-row"
                          >
                            <input
                              type="checkbox"
                              checked={selectedFriendUids.includes(friend.uid)}
                              onChange={() => toggleSelectedFriend(friend.uid)}
                            />
                            <span>{friendDisplayLabel(friend)}</span>
                          </label>
                        ))}
                      </div>
                      <GoldBtn
                        onClick={shareSelectedPrayer}
                        disabled={
                          !prayerToShare || selectedFriendUids.length === 0
                        }
                        style={{ width: "100%", marginTop: 8 }}
                      >
                        Share with Selected Friends
                      </GoldBtn>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
      </Card>
      ) : null}

      {friendPendingRemoval && (
        <div
          className="friend-remove-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Remove Spirit Friend"
          onClick={() => setFriendPendingRemoval(null)}
        >
          <div
            className="friend-remove-card"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="friend-remove-close"
              aria-label="Close"
              onClick={() => setFriendPendingRemoval(null)}
            >
              ×
            </button>
            <div className="friend-remove-mark">♡</div>
            <h3 className="serif">Remove friend?</h3>
            <p>
              Remove {friendDisplayLabel(friendPendingRemoval)} from Spirit Friends?
            </p>
            <div className="friend-remove-actions">
              <button
                type="button"
                className="soft-action-btn muted"
                onClick={() => setFriendPendingRemoval(null)}
              >
                Keep
              </button>
              <button
                type="button"
                className="soft-action-btn friend-alert-btn"
                onClick={() => {
                  const friend = friendPendingRemoval;
                  if (!friend) return;
                  setFriendPendingRemoval(null);
                  onDeleteSpiritFriend?.(friend);
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {!friendsOnly && prayerFilter && (
      <>
      <button
        type="button"
        className="prayer-subpage-back"
        onClick={() => setPrayerFilter(null)}
      >
        ← Back to Prayer Vessels
      </button>
      {(showMineList || showOthersList) && (
      <>
      <div id="unified-prayer-request">
        <div className="unified-prayer-composer-card">
          <div className="unified-prayer-heading">
            <h3 className="serif">Add a Prayer</h3>
            <p>Write what is on your heart, then choose a gentle rhythm to return to it.</p>
          </div>

          <div className="prayer-kind-label">Who are you praying for?</div>
          <div className="prayer-kind-choice-row" role="tablist" aria-label="Who are you praying for?">
            <button
              type="button"
              className={prayerAddKind === "mine" ? "active" : ""}
              onClick={() => {
                setPrayerAddKind("mine");
                setPrayerFilter("mine");
              }}
            >
              <span>Myself</span>
            </button>
            <button
              type="button"
              className={prayerAddKind === "other" ? "active" : ""}
              onClick={() => {
                setPrayerAddKind("other");
                setPrayerFilter("others");
              }}
            >
              <span>Someone Else</span>
            </button>
          </div>

          {prayerAddKind === "mine" ? (
            <div className="unified-prayer-fields">
              {renderQuickPrayerTopics(
                myQuickTopics,
                setMyQuickTopics,
                showMyQuickTopics,
                setShowMyQuickTopics
              )}
              <textarea
                id="my-prayer-text"
                value={myRequest}
                onChange={(e) => setMyRequest(e.target.value)}
                placeholder="Write what is on your heart..."
                className="sg-textarea"
                style={{
                  minHeight: 96,
                  fontFamily: "'Nunito',sans-serif",
                  lineHeight: 1.5,
                }}
              />
              <div className="prayer-commitment-heading">
                <div>Prayer Commitment</div>
                <p>Choose how long you would like to faithfully return to this prayer.</p>
              </div>
              {renderChallengePicker(myChallengeDays, setMyChallengeDays)}
              <GoldBtn
                onClick={addMyPrayer}
                disabled={!myPrayerText}
                style={{ width: "100%" }}
              >
                Add My Prayer
              </GoldBtn>
            </div>
          ) : (
            <div className="unified-prayer-fields">
              <div
                style={{
                  margin: "0 0 4px",
                  background: "rgba(255,248,223,.48)",
                  border: `2px solid ${C.gold}`,
                  borderRadius: 14,
                  padding: 10,
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowHeavensWordForOthers((open) => !open)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    color: C.textDark,
                    fontWeight: 500,
                    fontSize: 17,
                    textAlign: "left",
                  }}
                  aria-expanded={showHeavensWordForOthers}
                >
                  <span>Heaven's Word</span>
                  <span
                    className="placed-chip"
                    style={{
                      fontSize: 12.5,
                      padding: "6px 11px",
                      border: `2px solid ${C.gold}`,
                      background: "rgba(255,253,246,.88)",
                      color: C.goldDeep,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {showHeavensWordForOthers ? "Hide" : "Open"}
                  </span>
                </button>
                {showHeavensWordForOthers && (
                  <div style={{ marginTop: 10 }}>
                    <p
                      className="serif"
                      style={{
                        margin: "0 0 8px",
                        fontSize: "clamp(22px, 5.6vw, 26px)",
                        lineHeight: 1.55,
                        color: "#3F2A0C",
                        fontStyle: "normal",
                        fontWeight: 500,
                      }}
                    >
                      "Praying for someone is truly great. Even if someone has no
                      personal righteousness, if you pray for them, they are saved
                      from death and rescued from difficulty."
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "clamp(15px, 3.8vw, 16px)",
                        lineHeight: 1.6,
                        color: C.textMid,
                      }}
                    >
                      The power, force, and result of prayer are that great.
                    </p>
                  </div>
                )}
              </div>
              <input
                id="prayer-person-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Person's name"
                className="sg-input"
              />
              {renderQuickPrayerTopics(
                otherQuickTopics,
                setOtherQuickTopics,
                showOtherQuickTopics,
                setShowOtherQuickTopics
              )}
              <textarea
                value={request}
                onChange={(e) => setRequest(e.target.value)}
                placeholder="Write what is on your heart..."
                className="sg-textarea"
                style={{
                  minHeight: 90,
                  fontFamily: "'Nunito',sans-serif",
                  lineHeight: 1.5,
                }}
              />
              <GoldBtn
                onClick={addPrayerForSomeone}
                disabled={!name.trim() || !otherPrayerText}
                style={{ width: "100%" }}
              >
                Add Prayer
              </GoldBtn>
            </div>
          )}
        </div>
      </div>

      {showMineList && (
        <Card id="my-prayers-list" style={{ padding: 16 }}>
          <div className="recorded-prayers-header">
            <div>
              <h3 className="serif">Growing Prayers</h3>
              <p>Continue nurturing the prayers you've planted.</p>
            </div>
            <span className="placed-chip" style={{ fontWeight: 500 }}>
              {myPrayers.length} planted
            </span>
          </div>
          <div className="prayer-list-filter-row" aria-label="Prayer filters">
            <button type="button" className="active">All</button>
            <button type="button">Growing</button>
            <button type="button">Answered</button>
            <button type="button">Archived</button>
          </div>
          {myPrayers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🙏</div>
              <p className="empty-state-title" style={{ fontWeight: 500 }}>
                No prayer requests yet.
              </p>
              <p className="empty-state-copy">
                Bring your first need to God, then return to it anytime.
              </p>
              <GoldBtn
                onClick={() => goToPrayerSection("my-prayer-request", "my-prayer-text")}
                style={{ width: "100%" }}
              >
                Add a Prayer 💛
              </GoldBtn>
            </div>
          ) : (
            <div className="scroll-list">{myPrayers.map(renderPrayerCard)}</div>
          )}
        </Card>
      )}

      {showOthersList && (
        <Card id="others-prayers-list" style={{ padding: 16 }}>
          <div className="recorded-prayers-header">
            <div>
              <h3 className="serif">Growing Prayers</h3>
              <p>Keep returning to the people you're lifting in prayer.</p>
            </div>
            <span className="placed-chip" style={{ fontWeight: 500 }}>
              {otherPrayers.length} planted
            </span>
          </div>
          <div className="prayer-list-filter-row" aria-label="Prayer filters">
            <button type="button" className="active">All</button>
            <button type="button">Growing</button>
            <button type="button">Answered</button>
            <button type="button">Archived</button>
          </div>
          {otherPrayers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🤲</div>
              <p className="empty-state-title" style={{ fontWeight: 500 }}>
                No prayers for others yet.
              </p>
              <p className="empty-state-copy">
                Add someone and lift them gently in prayer today.
              </p>
              <GoldBtn
                onClick={() => goToPrayerSection("prayer-for-someone", "prayer-person-name")}
                style={{ width: "100%" }}
              >
                Add Someone
              </GoldBtn>
            </div>
          ) : (
            <div className="scroll-list">
              {otherPrayers.map(renderPrayerCard)}
            </div>
          )}
        </Card>
      )}
      </>
      )}

      {showAnsweredList && (
        <div id="answered-prayers-section">
          <Card className="answered-altar-card" style={{ padding: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <div className="answered-altar-header">
                <div className="answered-altar-icon" aria-hidden="true">
                  🌤️
                </div>
                <div>
                  <h3
                    className="serif"
                    style={{ margin: 0, fontSize: 17, color: C.textDark }}
                  >
                    Answered Prayers
                  </h3>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexShrink: 0,
                }}
              >
                {publicPreviewCount > 0 && (
                  <span className="placed-chip">
                    {publicPreviewCount} public
                  </span>
                )}
                <span className="placed-chip">
                  {answeredPrayers.length} answered
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setShowAnsweredPrayerDetails((open) => !open)
                  }
                  className="soft-action-btn"
                  style={{ minHeight: 34, padding: "7px 10px" }}
                >
                  {showAnsweredPrayerDetails ? "Hide" : "Open"}
                </button>
              </div>
            </div>
            {showAnsweredPrayerDetails && (answeredPrayers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🌤️</div>
                <p className="empty-state-title">
                  No testimonies saved yet.
                </p>
                <p className="empty-state-copy">
                  Keep watching with faith. When God answers, save it here and give thanks.
                </p>
                <GoldBtn
                  onClick={() =>
                    goToPrayerSection("my-prayer-request", "my-prayer-text")
                  }
                  style={{ width: "100%" }}
                >
                  View Active Prayers
                </GoldBtn>
              </div>
            ) : (
              <div
                className="answered-prayer-stack"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  maxHeight: "min(78dvh, 720px)",
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "auto",
                  paddingRight: 4,
                  paddingBottom: 96,
                  scrollPaddingBottom: 120,
                }}
              >
                {answeredPrayers.map((prayer) => {
                  const isMine = (prayer.kind || "other") === "mine";
                  const editing = editingPrayerId === prayer.id;
                  const publicShareOpen = openPublicShareId === prayer.id;
                  return (
                    <div
                      key={prayer.id}
                      className={`testimony-entry${editing ? " editing" : ""}${
                        publicShareOpen ? " public-open" : ""
                      }`}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            minWidth: 0,
                          }}
                        >
                          <span
                            className="testimony-entry-icon"
                            aria-hidden="true"
                          >
                            🌤️
                          </span>
                          <div>
                            <span className="testimony-label">
                              Testimony Entry
                            </span>
                            <h4
                              style={{
                                margin: "5px 0 0",
                                fontSize: 15,
                                color: C.textDark,
                              }}
                            >
                              {isMine ? "My Prayer" : prayer.name}
                            </h4>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button
                            onClick={() => startEditPrayer(prayer)}
                            style={{
                              background: "rgba(255,253,246,.78)",
                              border: `1px solid ${C.cardBorder}`,
                              borderRadius: 12,
                              padding: "7px 10px",
                              color: C.goldDeep,
                              fontWeight: 900,
                              cursor: "pointer",
                              minHeight: 34,
                              fontSize: 13,
                            }}
                          >
                            Edit
                          </button>
                          <button
                            aria-label={`Delete answered ${
                              isMine
                                ? "prayer"
                                : `prayer for ${prayer.name || "someone"}`
                            }`}
                            onClick={() => onDeletePrayer(prayer.id)}
                            style={{
                              background: "rgba(255,253,246,.78)",
                              border: `1px solid ${C.cardBorder}`,
                              borderRadius: 12,
                              padding: "7px 10px",
                              color: C.textFaint,
                              fontWeight: 900,
                              cursor: "pointer",
                              minHeight: 34,
                              fontSize: 13,
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      {editing ? (
                        <div className="testimony-edit-panel">
                          <textarea
                            value={editTestimonyNote}
                            onChange={(e) =>
                              setEditTestimonyNote(e.target.value)
                            }
                            placeholder="Edit testimony note..."
                            className="sg-textarea"
                            style={{
                              minHeight: 92,
                              fontFamily: "'Nunito',sans-serif",
                              lineHeight: 1.5,
                            }}
                          />
                          <div className="testimony-edit-actions">
                            <GoldBtn
                              onClick={() => savePrayerEdit(prayer)}
                              style={{ minHeight: 40 }}
                            >
                              Save Testimony
                            </GoldBtn>
                            <button
                              onClick={cancelEditPrayer}
                              style={{
                                background: "rgba(255,253,246,.78)",
                                border: `1px solid ${C.cardBorder}`,
                                borderRadius: 12,
                                padding: "9px 10px",
                                color: C.textFaint,
                                fontWeight: 900,
                                cursor: "pointer",
                                minHeight: 40,
                                fontSize: 13,
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p
                          style={{
                            margin: "0 0 8px",
                            fontSize: 13.5,
                            color: C.textSoft,
                            lineHeight: 1.55,
                          }}
                        >
                          {shortText(
                            prayer.answeredNote || prayer.request,
                            150
                          )}
                        </p>
                      )}
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
                      >
                        <span className="prayer-status-chip">Answered</span>
                        <span
                          className="placed-chip"
                          style={{ color: C.green }}
                        >
                          Give thanks to God
                        </span>
                        <span className="placed-chip">
                          {prayer.answeredDate || today}
                        </span>
                      </div>
                      <div
                        id={`public-share-${prayer.id}`}
                        className="public-share-box"
                      >
                        <button
                          type="button"
                          className="public-share-toggle"
                          aria-expanded={publicShareOpen}
                          onClick={() => {
                            if (publicShareOpen) {
                              setOpenPublicShareId(null);
                              return;
                            }
                            setOpenPublicShareId(prayer.id);
                            if (!publicShareOpen) {
                              scrollAnsweredPrayerPanel(prayer.id);
                            }
                          }}
                        >
                          <div>
                            <div className="public-share-toggle-title">
                              {prayer.sharePublic
                                ? "Public sharing is on"
                                : "Public sharing"}
                            </div>
                            <div className="public-share-toggle-copy">
                              {prayer.sharePublic
                                ? "Anonymous preview saved for later."
                                : "Optional. Private unless you choose to share."}
                            </div>
                          </div>
                          <span
                            className={`public-share-chevron${
                              publicShareOpen ? " open" : ""
                            }`}
                          >
                            ⌄
                          </span>
                        </button>
                        {publicShareOpen && (
                          <div className="public-share-content">
                            <label className="public-share-row">
                              <div>
                                <div className="public-share-title">
                                  Share this testimony publicly later?
                                </div>
                                <div className="public-share-copy">
                                  Opt in only. The public preview stays
                                  anonymous and does not include private request
                                  details.
                                </div>
                              </div>
                              <input
                                type="checkbox"
                                checked={!!prayer.sharePublic}
                                onChange={(e) =>
                                  onTogglePublicShare(
                                    prayer.id,
                                    e.target.checked
                                  )
                                }
                                className="public-share-check"
                              />
                            </label>
                            {prayer.sharePublic && prayer.publicSubmittedAt && (
                              <div className="unlock-note" style={{ marginTop: 10 }}>
                                Pending approval · submitted on {prayer.publicSubmittedAt}. It will appear on the Wall after you approve it.
                              </div>
                            )}
                            {prayer.sharePublic && !prayer.publicSubmittedAt && (
                              <div id={`submit-public-${prayer.id}`}>
                                <GoldBtn
                                  onClick={() => onSubmitPublicTestimony(prayer)}
                                  style={{ width: "100%", marginTop: 10 }}
                                >
                                  Submit to Testimony Wall
                                </GoldBtn>
                              </div>
                            )}
                            {prayer.sharePublic && (
                              <div className="public-preview-card">
                                <span className="public-preview-label">
                                  Public Preview
                                </span>
                                <h4
                                  className="serif"
                                  style={{
                                    margin: "8px 0 4px",
                                    fontSize: 16,
                                    color: C.goldDeep,
                                  }}
                                >
                                  Anonymous testimony
                                </h4>
                                <p
                                  style={{
                                    margin: "0 0 8px",
                                    fontSize: 13.5,
                                    color: C.textSoft,
                                    lineHeight: 1.55,
                                  }}
                                >
                                  {prayer.answeredNote ||
                                    "God answered a prayer in His timing."}
                                </p>
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: 12.5,
                                    color: C.textFaint,
                                    lineHeight: 1.45,
                                  }}
                                >
                                  Shared without names or private request
                                  details. You can turn this off anytime.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </Card>
        </div>
      )}
      </>
      )}

      {answerTarget && (
        <div className="mobile-modal-overlay">
          <Card className="mobile-modal-card">
            <div style={{ textAlign: "center", fontSize: 44, marginBottom: 8 }}>
              🌤️
            </div>
            <h2
              className="serif"
              style={{
                margin: "0 0 6px",
                color: C.goldDeep,
                fontSize: 24,
                textAlign: "center",
              }}
            >
              Prayer answered?
            </h2>
            <p
              style={{
                margin: "0 0 12px",
                color: C.textSoft,
                fontSize: 13,
                lineHeight: 1.6,
                textAlign: "center",
              }}
            >
              How did God answer this prayer? Don't forget to give thanks to
              God.
            </p>
            <textarea
              value={answerNote}
              onChange={(e) => setAnswerNote(e.target.value)}
              placeholder="How did God answer this prayer?"
              className="sg-textarea"
              style={{
                minHeight: 110,
                fontFamily: "'Nunito',sans-serif",
                lineHeight: 1.5,
                marginBottom: 12,
              }}
            />
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                background: "rgba(246,250,255,.68)",
                border: "1px solid rgba(125,174,212,.18)",
                borderRadius: 14,
                padding: "10px 12px",
                marginBottom: 12,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={answerSharePublic}
                onChange={(e) => setAnswerSharePublic(e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 2, accentColor: C.gold }}
              />
              <span style={{ fontSize: 13, color: C.textSoft, lineHeight: 1.5 }}>
                Turn this into a testimony draft after saving. You can review it before submitting to the Wall.
              </span>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => {
                  setAnswerTarget(null);
                  setAnswerNote("");
                  setAnswerSharePublic(false);
                }}
                style={{
                  flex: 1,
                  background: "rgba(255,253,246,.78)",
                  border: `1px solid ${C.cardBorder}`,
                  borderRadius: 14,
                  padding: "12px 14px",
                  color: C.textMid,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <GoldBtn
                onClick={() => {
                  onMarkAnswered(answerTarget.id, answerNote.trim(), answerSharePublic);
                  setPrayerFilter("answered");
                  setShowAnsweredPrayerDetails(true);
                  if (answerSharePublic) {
                    setOpenPublicShareId(answerTarget.id);
                  }
                  setAnswerTarget(null);
                  setAnswerNote("");
                  setAnswerSharePublic(false);
                }}
                style={{ flex: 1 }}
              >
                Give Thanks ✨
              </GoldBtn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function AnsweredPrayerCelebrationModal({
  celebration,
  onClose,
  onViewAnswered,
}: {
  celebration: { id: string; title: string; note?: string; sharePublic: boolean };
  onClose: () => void;
  onViewAnswered: () => void;
}) {
  return (
    <div className="answered-celebration-overlay" role="dialog" aria-modal="true" aria-label="Prayer Answered">
      <div className="answered-celebration-card">
        <span className="answered-celebration-spark one">✦</span>
        <span className="answered-celebration-spark two">✧</span>
        <span className="answered-celebration-spark three">✦</span>
        <button className="answered-celebration-close" type="button" onClick={onClose} aria-label="Close">
          ×
        </button>
        <div className="answered-celebration-icon">🌤️</div>
        <p className="answered-celebration-kicker">Answered Prayer</p>
        <h2 className="serif">Prayer Answered</h2>
        <p className="answered-celebration-copy">
          Don’t forget to give thanks to God.
        </p>
        <div className="answered-celebration-note">
          <span>{celebration.title}</span>
          {celebration.note && <p>{celebration.note}</p>}
        </div>
        {celebration.sharePublic && (
          <p className="answered-celebration-helper">
            Your testimony draft is ready in Answered Prayers.
          </p>
        )}
        <div className="answered-celebration-actions">
          <GoldBtn onClick={onClose} style={{ width: "100%" }}>
            Give Thanks ✨
          </GoldBtn>
          <button type="button" className="soft-action-btn" onClick={onViewAnswered}>
            {celebration.sharePublic ? "Save as Testimony" : "View Answered Prayers"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OnboardingScreen({
  spiritName,
  onStart,
}: {
  spiritName: string;
  onStart: () => void;
}) {
  const steps = [
    [
      "💛",
      "Check in with your heart",
      "Begin by naming how your heart feels today.",
    ],
    [
      "🌅",
      "Complete today's walk",
      "Small faithful steps earn righteousness and help your spirit shine.",
    ],
    [
      "🙏",
      "Lift a prayer",
      "Bring your needs to God, pray for others, and save answered prayers as testimony.",
    ],
  ];
  return (
    <div
      className="gender-select-screen daily-entry-screen"
      style={{
        minHeight: "100vh",
        background: PAGE_GRAD,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 22,
      }}
    >
      <Card
        className="daily-entry-card"
        style={{
          maxWidth: 560,
          width: "100%",
          padding: 22,
          background:
            "linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,248,223,.62))",
          borderColor: `${C.gold}55`,
        }}
      >
        <div className="daily-entry-hero" style={{ textAlign: "center", marginBottom: 18 }}>
          <span className="daily-entry-sparkle daily-entry-sparkle-one">✦</span>
          <span className="daily-entry-sparkle daily-entry-sparkle-two">✧</span>
          <span className="daily-entry-sparkle daily-entry-sparkle-three">❀</span>
          <div className="daily-entry-spirit-wrap" aria-hidden="true">
            <div className="daily-entry-radiance" />
            <img
              className="daily-entry-spirit"
              src="/characters/female/baby-spirit.png"
              alt=""
              loading="eager"
            />
          </div>
          <h1
            className="serif"
            style={{ margin: "0 0 6px", fontSize: 30, color: C.goldDeep }}
          >
            Welcome, {spiritName || "little spirit"}
          </h1>
          <p
            style={{
              margin: 0,
              color: C.textMid,
              fontSize: 14,
              lineHeight: 1.6,
              fontWeight: 500,
            }}
          >
            Your spirit begins to shine through small daily acts of faith.
          </p>
        </div>
        <div
          className="unlock-note daily-entry-currency-note"
          style={{ marginBottom: 14, fontWeight: 500 }}
        >
          Righteousness is your heavenly currency. The more righteous you are,
          the more your spirit and home can shine.
        </div>
        <div
          className="daily-entry-path"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 10,
            marginBottom: 18,
          }}
        >
          {steps.map(([icon, title, desc], index) => (
            <div
              key={title}
              className="daily-entry-step"
              style={{
                ["--step-index" as any]: index,
                display: "flex",
                gap: 12,
                alignItems: "center",
                background: "rgba(255,253,246,.74)",
                border: `1px solid ${C.cardBorder}`,
                borderRadius: 16,
                padding: "12px 14px",
              }}
            >
              <div className="daily-entry-step-icon" style={{ fontSize: 24, width: 34, textAlign: "center" }}>
                {icon}
              </div>
              <div>
                <div
                  style={{ fontSize: 14, fontWeight: 800, color: C.goldDeep }}
                >
                  {title}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: C.textMid,
                    lineHeight: 1.45,
                    fontWeight: 400,
                  }}
                >
                  {desc}
                </div>
              </div>
            </div>
          ))}
        </div>
        <GoldBtn className="daily-entry-cta" onClick={onStart} style={{ width: "100%", fontSize: 15 }}>
          Begin Today's Walk ✨
        </GoldBtn>
      </Card>
    </div>
  );
}

function StageUpModal({
  stage,
  gender,
  skinTone,
  hairColor,
  equipped,
  onClose,
  onGoGrowth,
}: {
  stage: (typeof SPIRIT_STAGES)[number];
  gender: Gender;
  skinTone: string;
  hairColor: string;
  equipped: ClothingItem[];
  onClose: () => void;
  onGoGrowth: () => void;
}) {
  const personalLine =
    stage.key === "baby"
      ? "Your faith has become tender, alive, and ready to grow."
      : stage.key === "teen"
      ? "Your faith is stretching upward with fresh strength."
      : stage.key === "adult"
      ? "Your faith is becoming steady, rooted, and bright."
      : stage.key === "radiant"
      ? "Your light is becoming visible, warm, and full of witness."
      : "A new seed of faith has begun to glow.";
  return (
    <div
      className="stage-reveal-page"
      role="dialog"
      aria-modal="true"
      aria-labelledby="stage-reveal-title"
      style={{
        position: "fixed",
        inset: 0,
        background:
          "radial-gradient(circle at 50% 34%,rgba(255,239,166,.78),transparent 31%),linear-gradient(160deg,#f6faff 0%,#fffdf7 58%,#fff4cf 100%)",
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "max(24px,env(safe-area-inset-top)) 20px max(24px,env(safe-area-inset-bottom))",
        overflowY: "auto",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close spirit growth celebration"
        style={{
          position: "fixed",
          top: "max(16px,env(safe-area-inset-top))",
          right: 16,
          width: 42,
          height: 42,
          borderRadius: "50%",
          border: "1px solid rgba(124,90,30,.18)",
          background: "rgba(255,253,247,.76)",
          color: C.textMid,
          fontSize: 20,
          cursor: "pointer",
          zIndex: 3,
        }}
      >
        ×
      </button>

      <div className="stage-reveal-content" style={{ width: "min(100%,560px)", textAlign: "center" }}>
        <div className="stage-reveal-kicker">A new season begins</div>
        <div className="stage-reveal-avatar-wrap" aria-hidden="true">
          <div className="stage-reveal-radiance" />
          {[...Array(10)].map((_, index) => (
            <span
              key={index}
              className="stage-reveal-spark"
              style={{
                left: `${10 + ((index * 19) % 82)}%`,
                top: `${8 + ((index * 23) % 76)}%`,
                animationDelay: `${index * 0.16}s`,
              }}
            >
              {index % 3 === 0 ? "✦" : "·"}
            </span>
          ))}
          <div className="stage-reveal-avatar">
            <SpiritAvatar
              stageKey={stage.key}
              xp={0}
              xpMax={1}
              stageAura={stage.aura}
              stageGlow={stage.glow}
              animated
              equipped={equipped}
              gender={gender}
              skinTone={skinTone}
              hairColor={hairColor}
              size={280}
              showBar={false}
            />
          </div>
        </div>
        <h2
          id="stage-reveal-title"
          className="serif"
          style={{ margin: "2px 0 6px", color: C.textDark, fontSize: "clamp(30px,8vw,42px)", lineHeight: 1.05 }}
        >
          {stage.name}
        </h2>
        <p
          className="serif stage-reveal-grown"
          style={{
            margin: "0 0 8px",
            color: C.textDark,
            fontSize: 18,
            lineHeight: 1.32,
            fontWeight: 700,
          }}
        >
          Your spirit has grown.
        </p>
        <p
          className="stage-reveal-line"
          style={{
            margin: "0 auto 6px",
            color: C.textSoft,
            fontSize: 15.5,
            lineHeight: 1.52,
            maxWidth: 520,
          }}
        >
          {personalLine}
        </p>
        <p
          className="stage-reveal-desc"
          style={{
            margin: "0 auto 20px",
            color: C.textFaint,
            fontSize: 14,
            lineHeight: 1.5,
            maxWidth: 520,
          }}
        >
          {stage.desc}
        </p>
        <GoldBtn onClick={onGoGrowth} style={{ width: "min(100%,340px)", minHeight: 48 }}>
          Continue Growing
        </GoldBtn>
      </div>
    </div>
  );
}

function WelcomeBackModal({
  name,
  onBegin,
  onClose,
}: {
  name: string;
  onBegin: () => void;
  onClose: () => void;
}) {
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(60,40,0,.26)",
        backdropFilter: "blur(8px)",
        zIndex: 1320,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}
    >
      <Card
        style={{
          maxWidth: 390,
          width: "100%",
          textAlign: "center",
          padding: "24px 20px 20px",
          background:
            "linear-gradient(160deg,rgba(255,253,246,.98),rgba(240,249,255,.95))",
          borderColor: "rgba(125,211,252,.34)",
          boxShadow: "0 18px 58px rgba(80,80,40,.18)",
        }}
      >
        <div
          style={{
            width: 58,
            height: 58,
            borderRadius: "50%",
            margin: "0 auto 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "radial-gradient(circle at 35% 25%,#fff,#fff3c4 52%,#e9b949)",
            border: `1.5px solid ${C.goldPale}`,
            boxShadow: "0 0 28px rgba(233,185,73,.34)",
            color: C.goldDeep,
            fontSize: 24,
          }}
        >
          ✦
        </div>
        <h2
          className="serif"
          style={{ margin: "0 0 6px", fontSize: 24, color: C.textDark }}
        >
          Welcome back, {name}
          {name.includes("♡") ? "" : " ♡"}
        </h2>
        <p
          style={{
            margin: "0 0 18px",
            color: C.textMid,
            fontSize: 15,
            lineHeight: 1.55,
          }}
        >
          Let&apos;s make your spirit shine today.
        </p>
        <GoldBtn onClick={onBegin} style={{ width: "100%" }}>
          Begin Today&apos;s Walk
        </GoldBtn>
      </Card>
    </div>
  );
}

function SplashIntro({ onSkip }: { onSkip: () => void }) {
  return (
    <div
      className="splash-intro"
      role="status"
      aria-label="Welcome to Spirit Garden. Let your spirit shine."
      onClick={onSkip}
    >
      <span className="splash-intro-star" aria-hidden="true">
        ✦
      </span>
      <div className="splash-intro-card">
        <img
          className="splash-intro-logo"
          src="/spirit-garden-splash-v5.png"
          alt="Spirit Garden"
        />
      </div>
    </div>
  );
}

function HomeGrowthModal({
  stage,
  onClose,
  onGoHome,
}: {
  stage: HomeGrowthStage;
  onClose: () => void;
  onGoHome: () => void;
}) {
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(60,40,0,.28)",
        backdropFilter: "blur(8px)",
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}
    >
      <Card
        className="home-upgrade-card"
        style={{
          maxWidth: 440,
          width: "100%",
          padding: 24,
          textAlign: "center",
          background: "linear-gradient(160deg,#fffdf7,#fff8df)",
          borderColor: `${C.gold}55`,
          boxShadow: "0 16px 50px rgba(138,90,8,.18)",
        }}
      >
        <div className="home-transform-mini" aria-hidden="true">
          <span
            style={{
              fontSize: 42,
              filter: "drop-shadow(0 4px 10px rgba(138,90,8,.18))",
            }}
          >
            {stage.icon}
          </span>
        </div>
        <h2
          className="serif"
          style={{ margin: "0 0 8px", color: C.goldDeep, fontSize: 25, fontWeight: 500, letterSpacing: 0 }}
        >
          Your home is growing
        </h2>
        <p
          className="home-upgrade-message"
          style={{
            margin: "0 0 10px",
            color: C.textDark,
            fontSize: 15,
            lineHeight: 1.6,
            fontWeight: 560,
          }}
        >
          Your home is becoming a {stage.name}.
        </p>
        <p
          style={{
            margin: "0 0 16px",
            color: C.textSoft,
            fontSize: 13.5,
            lineHeight: 1.65,
          }}
        >
          {stage.desc}
        </p>
        <GoldBtn onClick={onGoHome} style={{ width: "100%", fontWeight: 650 }}>
          See Home 🏡
        </GoldBtn>
      </Card>
    </div>
  );
}

function DayCompleteModal({
  tasks,
  streak,
  prayedToday,
  journalSavedToday,
  onClose,
  onGoHome,
}: {
  tasks: Task[];
  streak: number;
  prayedToday: boolean;
  journalSavedToday: boolean;
  onClose: () => void;
  onGoHome: () => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(60,40,0,.34)",
        backdropFilter: "blur(8px)",
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}
    >
      <Card
        style={{
          maxWidth: 460,
          width: "100%",
          padding: 22,
          textAlign: "center",
          background: "linear-gradient(160deg,#fffdf2,#fff3c4)",
          borderColor: `${C.gold}70`,
          boxShadow: "0 16px 60px rgba(138,90,8,.28)",
        }}
      >
        <div style={{ fontSize: 52, marginBottom: 8 }}>✨</div>
        <h2
          className="serif"
          style={{ margin: "0 0 6px", color: C.goldDeep, fontSize: 28 }}
        >
          Faith Walk Complete
        </h2>
        <p
          style={{
            margin: "0 0 14px",
            color: C.textSoft,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          Your daily walk is building consistency.
        </p>
        <div
          style={{
            background: "rgba(255,253,246,.72)",
            border: `1px solid ${C.cardBorder}`,
            borderRadius: 16,
            padding: 12,
            marginBottom: 14,
            textAlign: "left",
          }}
        >
          <div
            style={{
              fontSize: 13.5,
              textTransform: "uppercase",
              letterSpacing: 1.4,
              color: C.textFaint,
              fontWeight: 900,
              marginBottom: 8,
            }}
          >
            Faith Walk Streak
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {tasks.map((t) => (
              <div key={t.id} style={{ fontSize: 13, color: C.textMid }}>
                ✓ {t.icon} {t.label}
              </div>
            ))}
            {prayedToday && (
              <div style={{ fontSize: 13, color: C.textMid }}>
                ✓ Prayer lifted
              </div>
            )}
            {journalSavedToday && (
              <div style={{ fontSize: 13, color: C.textMid }}>
                ✓ Reflection saved
              </div>
            )}
            <div style={{ fontSize: 13, color: C.textMid }}>
              ✓ +50 righteousness
            </div>
            <div style={{ fontSize: 13, color: C.textMid }}>
              🔥 {streak} day streak
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              background: "rgba(255,253,246,.78)",
              border: `1px solid ${C.cardBorder}`,
              borderRadius: 14,
              padding: "12px 14px",
              color: C.textMid,
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Close
          </button>
          <GoldBtn onClick={onGoHome} style={{ flex: 1 }}>
            Visit Home
          </GoldBtn>
        </div>
      </Card>
    </div>
  );
}

function FeatureLockedCard({
  tab,
  onGoToday,
}: {
  tab: TabId;
  onGoToday: () => void;
}) {
  const lock = FEATURE_UNLOCKS[tab];
  return (
    <Card
      style={{
        padding: 24,
        textAlign: "center",
        background:
          "linear-gradient(160deg,rgba(255,253,246,.98),rgba(255,248,223,.55))",
        borderColor: `${C.gold}35`,
      }}
    >
      <div style={{ fontSize: 42, marginBottom: 10 }}>🌱</div>
      <h2
        className="serif"
        style={{ margin: "0 0 8px", fontSize: 24, color: C.goldDeep }}
      >
        {lock?.title || "This unlocks soon"}
      </h2>
      <p
        style={{
          margin: "0 0 12px",
          fontSize: 13.5,
          color: C.textSoft,
          lineHeight: 1.65,
        }}
      >
	        {lock?.desc ||
	          "Keep caring for your spirit to unlock this."}
      </p>
      {lock && (
        <div className="unlock-note" style={{ marginBottom: 14 }}>
	          {lock.xp > 0 ? `${lock.xp} spirit XP needed.` : "Keep walking with your spirit."}
        </div>
      )}
      <GoldBtn onClick={onGoToday} style={{ width: "100%" }}>
        Return to Today 🌅
      </GoldBtn>
    </Card>
  );
}

function GenderSelect({
  onSelect,
  onSignIn,
  hairColor,
  signInBusy = false,
}: {
  onSelect: (g: Gender, name: string) => void;
  onSignIn?: () => void | Promise<void>;
  hairColor: string;
  signInBusy?: boolean;
}) {
  const [chosenGender, setChosenGender] = useState<Gender | null>(null);
  const [nameVal, setNameVal] = useState("");
  const [growthIntroName, setGrowthIntroName] = useState<string | null>(null);
  const [hov, setHov] = useState<Gender | null>(null);
  const babyStage = SPIRIT_STAGES.find((s) => s.key === "baby") || SPIRIT_STAGES[1];
  const radiantStage =
    SPIRIT_STAGES.find((s) => s.key === "radiant") ||
    SPIRIT_STAGES[SPIRIT_STAGES.length - 1];
  async function continueWithGoogleFromName() {
    const cleanName = formatSpiritName(nameVal.trim());
    if (!cleanName || signInBusy) return;
    if (!onSignIn) {
      setGrowthIntroName(cleanName);
      return;
    }
    await onSignIn();
    if (auth.currentUser?.email) {
      setGrowthIntroName(cleanName);
    }
  }

  const growthFlow = (
    <div className="onboarding-growth-flow" aria-label="How growth works">
      <div className="onboarding-growth-flow-title">How Growth Works</div>
      <div className="onboarding-growth-flow-steps">
        <div className="onboarding-growth-step">
          <span className="onboarding-growth-icon" aria-hidden="true">🌱</span>
          <span>
            <span className="onboarding-growth-label">Daily Habits</span>
            <span className="onboarding-growth-note">Pray, read, reflect, and care for your spirit.</span>
          </span>
        </div>
        <span className="onboarding-growth-arrow" aria-hidden="true">↓</span>
        <div className="onboarding-growth-step">
          <span className="onboarding-growth-icon" aria-hidden="true">✨</span>
          <span>
            <span className="onboarding-growth-label">Earn Righteousness</span>
            <span className="onboarding-growth-note">Each faithful step adds light to your garden.</span>
          </span>
        </div>
        <span className="onboarding-growth-arrow" aria-hidden="true">↓</span>
        <div className="onboarding-growth-step">
          <span className="onboarding-growth-icon" aria-hidden="true">🌿</span>
          <span>
            <span className="onboarding-growth-label">Spirit Grows</span>
            <span className="onboarding-growth-note">Your spirit unlocks new stages as you continue walking with God.</span>
          </span>
        </div>
      </div>
    </div>
  );

  if (chosenGender && growthIntroName !== null) {
    return (
      <div
        className="onboarding-growth-page"
        style={{
          minHeight: "100vh",
          background: PAGE_GRAD,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div className="onboarding-growth-shell" style={{ textAlign: "center", maxWidth: 560, width: "100%" }}>
          <div className="onboarding-growth-rays" aria-hidden="true" />
          <div className="onboarding-growth-sparkle s1" aria-hidden="true">✦</div>
          <div className="onboarding-growth-sparkle s2" aria-hidden="true">✧</div>
          <div className="onboarding-growth-sparkle s3" aria-hidden="true">✦</div>
          <div className="onboarding-growth-hero">
            <div className="onboarding-growth-kicker">Spirit Garden</div>
          <h2
            className="serif"
            style={{
              margin: "0 0 8px",
              fontSize: "clamp(24px,5vw,34px)",
              color: C.goldDeep,
              textShadow: "0 2px 10px rgba(201,154,61,.16)",
            }}
          >
            Your spirit can grow
          </h2>
          <p
            className="serif"
            style={{
              margin: "0 auto 18px",
              maxWidth: 460,
              fontSize: 15,
              color: C.textMid,
              lineHeight: 1.55,
              fontStyle: "normal",
            }}
          >
            "Though outwardly we are wasting away, yet inwardly we are being renewed day by day."{" "}
            <span style={{ whiteSpace: "nowrap" }}>— 2 Corinthians 4:16</span>
          </p>
          </div>
          <div className="onboarding-spirit-growth-preview" aria-hidden="true">
            <div className="onboarding-stage-preview">
              <SpiritAvatar
                stageKey={babyStage.key}
                xp={babyStage.xpMin}
                xpMax={babyStage.xpMax}
                stageAura={babyStage.aura}
                stageGlow={babyStage.glow}
                animated={false}
                gender={chosenGender}
                hairColor={hairColor}
                size={118}
                showBar={false}
              />
              <span>Baby Spirit</span>
            </div>
            <div className="onboarding-growth-preview-arrow">
              <span>grows into</span>
              <b>→</b>
            </div>
            <div className="onboarding-stage-preview">
              <SpiritAvatar
                stageKey={radiantStage.key}
                xp={radiantStage.xpMin}
                xpMax={radiantStage.xpMax}
                stageAura={radiantStage.aura}
                stageGlow={radiantStage.glow}
                animated={false}
                gender={chosenGender}
                hairColor={hairColor}
                size={118}
                showBar={false}
              />
              <span>Radiant Spirit</span>
            </div>
          </div>
          {growthFlow}
          <GoldBtn
            className="onboarding-growth-cta"
            onClick={() => onSelect(chosenGender, growthIntroName)}
            style={{ fontSize: 15, padding: "14px 34px" }}
          >
            Enter Spirit Garden ✦
          </GoldBtn>
          <button
            onClick={() => setGrowthIntroName(null)}
            style={{
              display: "block",
              margin: "14px auto 0",
              background: "none",
              border: "none",
              color: C.textFaint,
              cursor: "pointer",
              fontSize: 13,
              textDecoration: "underline",
              padding: 8,
            }}
          >
            ← Back to name
          </button>
        </div>
      </div>
    );
  }

  if (chosenGender) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: PAGE_GRAD,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div style={{ textAlign: "center", maxWidth: 440, width: "100%" }}>
          <div
            className={`onboarding-spirit-stage onboarding-name-preview onboarding-spirit-${chosenGender}`}
            aria-hidden="true"
            style={{ marginBottom: 10 }}
          >
            <span className="onboarding-aura-ring ring-one" />
            <span className="onboarding-aura-ring ring-two" />
            <span className="onboarding-spirit-art" />
            <span className="onboarding-drift one" />
            <span className="onboarding-drift two" />
            <span className="onboarding-drift three" />
          </div>
          <h2
            className="serif"
            style={{
              margin: "0 0 6px",
              fontSize: "clamp(22px,5vw,30px)",
              color: C.textDark,
            }}
          >
            Give your spirit a name
          </h2>
          <p
            className="serif"
            style={{
              margin: "0 0 24px",
              fontSize: 13,
              color: C.textSoft,
              lineHeight: 1.7,
              fontStyle: "normal",
            }}
          >
            "I have called you by name; you are mine." — Isaiah 43:1
          </p>
          <input
            value={nameVal}
            onChange={(e) => setNameVal(formatSpiritName(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                continueWithGoogleFromName();
              }
            }}
	            placeholder="Spirit name"
            maxLength={24}
            autoFocus
            className="sg-input"
	            style={{ marginBottom: 16, fontSize: 18, textAlign: "center" }}
	          />
          {onSignIn && (
            <div
              style={{
                margin: "0 0 12px",
                padding: "14px",
                borderRadius: 20,
                background: "linear-gradient(145deg,rgba(255,253,246,.86),rgba(255,248,223,.52))",
                border: `1px solid ${C.cardBorder}`,
                boxShadow: "0 10px 24px rgba(138,90,8,.06)",
              }}
            >
              <button
                type="button"
                onClick={continueWithGoogleFromName}
                disabled={signInBusy || !nameVal.trim()}
                style={{
                  width: "100%",
                  minHeight: 50,
                  borderRadius: 16,
                  border: `1.5px solid ${signInBusy || !nameVal.trim() ? C.goldPale : C.gold}`,
                  background: signInBusy || !nameVal.trim()
                    ? "rgba(255,248,223,.58)"
                    : "linear-gradient(135deg,#fffdf6,#fff4c7)",
                  color: signInBusy || !nameVal.trim() ? C.textFaint : C.textDark,
                  fontFamily: "'Nunito',sans-serif",
                  fontSize: 15,
                  fontWeight: 850,
                  cursor: signInBusy ? "wait" : !nameVal.trim() ? "default" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 9,
                  boxShadow: signInBusy || !nameVal.trim() ? "none" : "0 6px 16px rgba(201,154,61,.14)",
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 18 }}>G</span>
                {signInBusy ? "Opening Google..." : "Continue with Google"}
              </button>
              <p style={{ margin: "9px 6px 0", color: C.textFaint, fontSize: 12.5, lineHeight: 1.45 }}>
                Save your spirit, home, prayers, and journal across devices.
              </p>
            </div>
          )}

          <button
            onClick={() => setChosenGender(null)}
            style={{
              marginTop: 16,
              background: "none",
              border: "none",
              color: C.textFaint,
              cursor: "pointer",
              fontSize: 13,
              textDecoration: "underline",
              padding: 8,
            }}
          >
            ← Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: PAGE_GRAD,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 520, width: "100%" }}>
        <div
          className="onboarding-welcome-diamond"
          style={{
            fontSize: 52,
            marginBottom: 10,
            color: C.gold,
            filter: `drop-shadow(0 4px 16px ${C.goldLight})`,
          }}
        >
          ✦
        </div>
        <h1
          className="serif"
          style={{
            fontSize: "clamp(28px,6vw,38px)",
            color: C.textDark,
            margin: "0 0 8px",
          }}
        >
          Welcome to Spirit Garden
        </h1>
        <p
          className="serif"
          style={{
            fontSize: 15,
            color: C.textMid,
            margin: "0 0 10px",
            lineHeight: 1.75,
            fontStyle: "normal",
          }}
        >
          "Before I formed you in the womb I knew you."{" "}
          <span style={{ whiteSpace: "nowrap" }}>— Jeremiah 1:5</span>
        </p>
        <p style={{ fontSize: 14, color: C.textSoft, margin: "0 0 40px" }}>
          How shall we know your spirit?
        </p>
        <div
          style={{
            display: "flex",
            gap: 16,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
          className="gender-card-row"
        >
          {(
            [
              {
                g: "female",
                label: "Her Spirit",
                sub: "Gentle, blooming, radiant",
                icon: "🌸",
              },
              {
                g: "male",
                label: "His Spirit",
                sub: "Steady, rooted, bright",
                icon: "🌿",
              },
            ] as const
          ).map(({ g, label, sub, icon }) => (
            <button
              key={g}
              onClick={() => setChosenGender(g)}
              onMouseEnter={() => setHov(g)}
              onMouseLeave={() => setHov(null)}
              className={`gender-choice-card gender-choice-${g}`}
              style={{
                background:
                  hov === g
                    ? "rgba(255,253,240,.46)"
                    : "transparent",
                border: "2px solid transparent",
                outline: "none",
                borderRadius: 28,
                padding: "34px 34px 28px",
                cursor: "pointer",
                transition: "all .28s",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                minWidth: 210,
                minHeight: 330,
                boxShadow:
                  hov === g
                    ? "0 18px 42px rgba(180,130,20,.12)"
                    : "none",
                transform: hov === g ? "translateY(-5px)" : "none",
              }}
            >
              <div
                className={`onboarding-spirit-stage onboarding-spirit-${g}`}
                aria-hidden="true"
              >
                <span className="onboarding-aura-ring ring-one" />
                <span className="onboarding-aura-ring ring-two" />
                <span className="onboarding-spirit-art" />
                <span className="onboarding-smile-warmth" />
                <span className="onboarding-drift one" />
                <span className="onboarding-drift two" />
                <span className="onboarding-drift three" />
              </div>
              <div>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
                <div
                  className="serif"
                  style={{ fontSize: 17, fontWeight: 800, color: C.textDark }}
                >
                  {label}
                </div>
                <div className="gender-choice-sub" style={{ fontSize: 13, color: C.textSoft }}>{sub}</div>
              </div>
            </button>
	          ))}
	        </div>
	        {onSignIn && (
	          <div
              className="gender-signin-row"
	            style={{
	              marginTop: 22,
	              display: "flex",
	              alignItems: "center",
	              justifyContent: "center",
	              gap: 6,
	              flexWrap: "wrap",
	              fontSize: 13,
	              color: C.textFaint,
	            }}
	          >
	            <span>Already have an account?</span>
	            <button
	              type="button"
	              onClick={onSignIn}
                disabled={signInBusy}
	              style={{
	                background: "transparent",
	                border: "none",
	                color: signInBusy ? C.textFaint : C.goldDeep,
	                cursor: signInBusy ? "wait" : "pointer",
	                fontFamily: "'Nunito',sans-serif",
	                fontSize: 13,
	                fontWeight: 800,
	                padding: "6px 4px",
	                textDecoration: "underline",
	                textUnderlineOffset: 3,
	              }}
	            >
	              {signInBusy ? "Opening Google..." : "Sign in here"}
	            </button>
	          </div>
	        )}
	      </div>
	    </div>
	  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TESTIMONY WALL
// ─────────────────────────────────────────────────────────────────────────────
const FEATURED_TESTIMONY = {
  title: "A Life of Thanksgiving",
  author: "E ♡",
  intro:
    "One of the greatest lessons I learned from our senior pastor is what true thanksgiving really means. Throughout his life, he faced injustice, false accusations, misunderstandings, and many hardships. Yet whenever he faced those situations, the first thing he would do was pray, give thanks to God, and confess that he was happy.",
  body: [
    "Even when it felt like the whole world was against him, he remained joyful because he knew Jesus was with him every step of the way, loving him, guiding him, and taking care of everything. He is someone who loves God absolutely, and watching him live this way helped me understand what it means to give thanks in all circumstances.",
    "I came to understand this lesson even more deeply two years ago when I unexpectedly lost my teaching job because of teacher cuts caused by declining student enrollment.",
    "When my principal broke the news to me, I remember thanking God and believing that He was still taking care of my life.",
    "Surprised by my positive attitude, he asked me, “Erica, are you a person of faith?”",
    "I told him that I believed God had a purpose in everything. I testified that God had given me that job in the first place, and if it was His will for me to stay, He would make a way. If not, then His will was somewhere else.",
    "Looking back, I realized how much my faith in the Trinity had grown. Even in uncertainty, I was able to give thanks instead of becoming fearful.",
    "What amazed me was that during that season, for three weeks in a row, the message we continued to receive at church was: “God will help you in the present the same way He helped you in the past. So do not worry, but do the work that you must do and give thanks.”",
    "I felt that God was looking out for me. I held onto those words with absolute faith, remembered everything God had done for me, and gave a lot of thanksgiving.",
    "That same month, I visited our retreat center. There was a hill there that had once caught fire, and nearly every tree had died. During that time, our pastor received a strong inspiration to pray for one specific tree. He desperately prayed for that tree to live again, and miraculously, it revived.",
    "As I prayed under that tree, I said, “God, thank You so much for reviving this tree. Just as You brought this tree back to life, can You also resurrect my dead job? If You restore my job, I will testify to what You have done for me.”",
    "The very next day, I received a call from my school district's HR department asking when I could come in to sign my contract for the next school year. I was completely shocked.",
    "I called my principal to ask what had happened, but he told me he didn't know either. He just said, “Go sign the contract and don't ask any questions.”",
    "I remember falling to my knees and giving so much thanks to God for answering my prayer.",
    "At that moment, I realized something clearly: because I responded with thanksgiving and faith instead of fear, I believe God blessed me and answered my prayer.",
    "For the last 13 years, thanksgiving has transformed the way I live. It has removed so much anxiety from my life because I learned to trust that God is taking care of me.",
    "The Bible teaches that we receive according to what we have done. Because of that, I believe that as long as I remain faithful, fulfill my responsibilities, and continue doing the work entrusted to me, God will bless me according to His will.",
    "Two teachings from our pastor have remained deeply in my heart: “Thanksgiving prayer is the prayer that brings you close to God.” And: “True victory is to be victorious in love.”",
    "I am so thankful to God for showing me the path to eternal life. I can honestly say that I am truly happy, able to give thanks, live with joy, experience God's love every day, and achieve victory by being victorious in love.",
    "Over the years, I have learned that one of the easiest things to forget is how much God has helped us.",
    "That is one of the reasons I created Spirit Garden. Just as that tree became a testimony of God's power to revive what seemed dead, I hope Spirit Garden can become a place where people record their prayers, answered prayers, and miracles so they never forget what God has done for them. So often, we forget the countless ways God has helped us and shown His love throughout our lives. My prayer is that Spirit Garden helps people look back with thanksgiving, find encouragement, strengthen their faith, and remember the beautiful testimonies God has written in their lives.",
    "The miracle pine tree still stands today as a reminder of God's power and love. In the same way, I believe God is still working miracles and fulfilling His promises every day. I hope Spirit Garden can become a place where those miracles are remembered, shared, and passed on to encourage others. I offer this app as an expression of thanksgiving to the Trinity.",
    "May this testimony encourage you to trust God, give thanks in all circumstances, and remember that because of His great love for us, God can turn even our greatest hardships into blessings and testimonies that reveal His love and power.",
    "Let us continue to make our spirits shine as we walk in faith, give thanks in all circumstances, and share the testimonies of what God has done in our lives.",
  ],
};

const ADMIN_EMAIL = "ericamaelouise@gmail.com";
const INSTAGRAM_URL = "https://www.instagram.com/spiritgarden.app";

const googleAuthRedirectHelp =
  "Google sign-in needs these redirect URIs in Google Cloud: " +
  GOOGLE_AUTH_REDIRECT_URIS.join(", ");

function TestimonyWallPage({
  currentUser,
  onAdminSignIn,
  onApprovedCountChange,
  onSaveDraft,
  testimonyBookItems = [],
  onEditTestimonyEntry,
  onDeleteTestimonyEntry,
}: {
  currentUser: User | null;
  onAdminSignIn: () => void;
  onApprovedCountChange?: (count: number) => void;
  onSaveDraft?: (draft: { title: string; testimony: string; displayName: string }) => void;
  testimonyBookItems?: Array<{
    id: string;
    prayerId: string;
    icon: string;
    title: string;
    detail: string;
    date?: string;
    accent: string;
  }>;
  onEditTestimonyEntry?: (id: string) => void;
  onDeleteTestimonyEntry?: (id: string) => void;
}) {
  const [testimonies, setTestimonies] = useState<PublicTestimony[]>([]);
  const [pendingTestimonies, setPendingTestimonies] = useState<PublicTestimony[]>([]);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [featuredOpen, setFeaturedOpen] = useState(false);
  const [shareFormOpen, setShareFormOpen] = useState(false);
  const [sharedTestimoniesOpen, setSharedTestimoniesOpen] = useState(false);
  const [shareTitle, setShareTitle] = useState("");
  const [shareText, setShareText] = useState("");
  const [shareDisplayName, setShareDisplayName] = useState("Anonymous");
  const [sharePostAnonymous, setSharePostAnonymous] = useState(true);
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [shareSubmitted, setShareSubmitted] = useState(false);
  const [shareSuccessKind, setShareSuccessKind] = useState<"submitted" | "draft" | null>(null);
  const [testimonyCategory, setTestimonyCategory] = useState("All");
  const [loading, setLoading] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminMode, setAdminMode] = useState(() =>
    typeof window !== "undefined" &&
    (window.location.hash === "#admin-wall" || window.location.pathname === "/admin")
  );
  const isAdmin =
    currentUser?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  const testimonyCategories = [
    "All",
    "Answered Prayer",
    "Thanksgiving",
    "Spiritual Growth",
    "God's Guidance",
    "Peace",
  ];
  const getTestimonyCategory = (item: PublicTestimony) => {
    const text = `${item.prayerTitle} ${item.testimony}`.toLowerCase();
    if (/peace|anxiety|calm|rest|comfort/.test(text)) return "Peace";
    if (/guide|guidance|direction|path|purpose|will/.test(text))
      return "God's Guidance";
    if (/grow|growth|faith|spirit|trust|closer/.test(text))
      return "Spiritual Growth";
    if (/thank|thanksgiving|grateful|gratitude|praise/.test(text))
      return "Thanksgiving";
    return "Answered Prayer";
  };
  const filteredTestimonies =
    testimonyCategory === "All"
      ? testimonies
      : testimonies.filter(
          (item) => getTestimonyCategory(item) === testimonyCategory
        );
  const shareAuthorName = sharePostAnonymous
    ? "Anonymous"
    : shareDisplayName.trim() || currentUser?.displayName || "Spirit Garden Friend";
  const sharePreviewTitle = shareTitle.trim() || "Answered Prayer";
  const sharePreviewText =
    shareText.trim() ||
    "Share how God answered your prayer, guided you, strengthened you, provided for you, or transformed your heart.";

  const featuredReadingMinutes = Math.max(
    1,
    Math.ceil(
      (FEATURED_TESTIMONY.intro.split(/\s+/).length +
        FEATURED_TESTIMONY.body.join(" ").split(/\s+/).length) /
        220
    )
  );
  const recentSharedTestimonies = testimonies.slice(0, 2);

  async function loadTestimonies() {
    setLoading(true);
    setError(null);
    try {
      const testimonyQuery = query(
        collection(db, "publicTestimonies"),
        where("approved", "==", true)
      );
      const snapshot = await getDocs(testimonyQuery);
      const rows = snapshot.docs
        .map((doc) => {
          const data = doc.data() as Record<string, any>;
          const createdAtValue = data.createdAt;
          const createdAt =
            createdAtValue && typeof createdAtValue.toDate === "function"
              ? createdAtValue.toDate().toISOString()
              : typeof createdAtValue === "string"
              ? createdAtValue
              : undefined;
          return {
            id: doc.id,
            testimony: String(data.testimony || ""),
            prayerTitle: String(data.prayerTitle || "Answered Prayer"),
            displayName: String(data.displayName || "Anonymous"),
            createdAt,
            submittedAt: String(data.submittedAt || ""),
            userId: String(data.userId || ""),
            approved: Boolean(data.approved),
            rejected: Boolean(data.rejected),
          };
        })
        .filter((item) => item.testimony.trim().length > 0)
        .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
      setTestimonies(rows);
      onApprovedCountChange?.(rows.length);
      setExpandedIds((current) => {
        const next: Record<string, boolean> = {};
        rows.forEach((item) => {
          if (current[item.id]) next[item.id] = true;
        });
        return next;
      });
    } catch (err) {
      console.error(err);
      setError("The Testimony Wall could not load yet. Check Firestore rules or try again.");
    } finally {
      setLoading(false);
    }
  }

  async function loadPendingTestimonies() {
    setAdminLoading(true);
    try {
      const pendingQuery = query(
        collection(db, "publicTestimonies"),
        where("approved", "==", false)
      );
      const snapshot = await getDocs(pendingQuery);
      const rows = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data() as Record<string, any>;
          const createdAtValue = data.createdAt;
          const createdAt =
            createdAtValue && typeof createdAtValue.toDate === "function"
              ? createdAtValue.toDate().toISOString()
              : typeof createdAtValue === "string"
              ? createdAtValue
              : undefined;
          return {
            id: docSnap.id,
            testimony: String(data.testimony || ""),
            prayerTitle: String(data.prayerTitle || "Answered Prayer"),
            displayName: String(data.displayName || "Anonymous"),
            createdAt,
            submittedAt: String(data.submittedAt || ""),
            userId: String(data.userId || ""),
            approved: Boolean(data.approved),
            rejected: Boolean(data.rejected),
          };
        })
        .filter((item) => item.testimony.trim().length > 0 && !item.rejected)
        .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
      setPendingTestimonies(rows);
    } catch (err) {
      console.error(err);
      setError("Pending testimonies could not load yet.");
    } finally {
      setAdminLoading(false);
    }
  }

  async function moderateTestimony(id: string, approved: boolean) {
    try {
      await updateDoc(doc(db, "publicTestimonies", id), {
        approved,
        rejected: !approved,
        reviewedAt: serverTimestamp(),
      });
      setPendingTestimonies((items) => items.filter((item) => item.id !== id));
      if (approved) loadTestimonies();
    } catch (err) {
      console.error(err);
      setError("Could not update this testimony yet.");
    }
  }

  function saveDirectTestimonyDraft() {
    const testimony = shareText.trim();
    if (!testimony) {
      setError("Write a short testimony before saving a draft.");
      return;
    }
    setError(null);
    onSaveDraft?.({
      title: shareTitle.trim() || "Testimony Draft",
      testimony,
      displayName: shareAuthorName,
    });
    setShareSubmitted(true);
    setShareSuccessKind("draft");
  }

  async function submitDirectTestimony() {
    const testimony = shareText.trim();
    if (!testimony) {
      setError("Write a short testimony before submitting.");
      return;
    }

    setShareSubmitting(true);
    setError(null);
    try {
      const user = await ensureAnonymousUser();
      if (!user) {
        setError("Google sign-in is still finishing. Please try again in a moment.");
        return;
      }
      await addDoc(collection(db, "publicTestimonies"), {
        testimony,
        prayerTitle: shareTitle.trim() || "Testimony",
        createdAt: serverTimestamp(),
        submittedAt: todayKey(),
        userId: user.uid,
        displayName: shareAuthorName,
        approved: false,
        rejected: false,
        source: "direct",
      });
      setShareText("");
      setShareTitle("");
      setShareDisplayName("Anonymous");
      setSharePostAnonymous(true);
      setShareSubmitted(true);
      setShareSuccessKind("submitted");
    } catch (err) {
      console.error(err);
      setError("Could not submit this testimony yet. Please try again.");
    } finally {
      setShareSubmitting(false);
    }
  }

  useEffect(() => {
    loadTestimonies();
  }, []);

  useEffect(() => {
    if (!adminMode || !isAdmin) return;
    loadPendingTestimonies();
  }, [adminMode, isAdmin]);

  const openShareTestimonyPage = () => {
    setShareSubmitted(false);
    setShareSuccessKind(null);
    setShareFormOpen(true);
    setSharedTestimoniesOpen(false);
    if (typeof window !== "undefined") {
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
    }
  };

  const closeShareTestimonyPage = () => {
    setShareFormOpen(false);
    if (typeof window !== "undefined") {
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
    }
  };

  const openSharedTestimoniesPage = () => {
    setShareFormOpen(false);
    setSharedTestimoniesOpen(true);
    if (typeof window !== "undefined") {
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
    }
  };

  const closeSharedTestimoniesPage = () => {
    setSharedTestimoniesOpen(false);
    if (typeof window !== "undefined") {
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
    }
  };

  const renderSharedTestimoniesWall = () => (
    <Card id="shared-testimonies-wall" className="testimony-shared-garden testimony-wall-today-style">
      <div className="testimony-wall-heading">
        <div>
          <SectionLabel>Testimony Wall</SectionLabel>
          <h3 className="serif">Stories of God's Faithfulness</h3>
          <p>Answered prayers and hope, gathered gently.</p>
        </div>
        {filteredTestimonies.length > 0 && (
          <span className="testimony-wall-stat-badge">
            <b aria-hidden="true">✦</b>
            <strong>{filteredTestimonies.length}</strong>
            <small>Stories Shared</small>
          </span>
        )}
      </div>
      <div className="testimony-category-row" aria-label="Testimony categories">
        {testimonyCategories.map((category) => (
          <button
            key={category}
            type="button"
            className={testimonyCategory === category ? "active" : ""}
            onClick={() => setTestimonyCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="empty-state">
          <div style={{ fontSize: 34, marginBottom: 8 }}>🌤️</div>
          <p className="empty-state-title">Loading testimonies...</p>
        </div>
      ) : filteredTestimonies.length === 0 ? (
        <div className="empty-state">
          <div className="testimony-wall-empty-dove" aria-hidden="true"><span /></div>
          <p className="empty-state-title">
            {testimonies.length === 0
              ? "No public testimonies yet."
              : "No testimonies in this garden yet."}
          </p>
          <p className="empty-state-copy">
            Be the first to share what God has done and encourage someone else's faith.
          </p>
          <button
            type="button"
            className="soft-action-btn"
            onClick={openShareTestimonyPage}
            style={{ width: "100%", justifyContent: "center" }}
          >
            Share a Testimony
          </button>
        </div>
      ) : (
        <div className="scroll-list" style={{ maxHeight: "58vh", paddingRight: 6 }}>
          {filteredTestimonies.map((item) => {
            const isLong = item.testimony.length > 132;
            const expanded = !!expandedIds[item.id];
            const visibleTestimony =
              isLong && !expanded ? item.testimony.slice(0, 132).trimEnd() + "..." : item.testimony;
            return (
            <div key={item.id} className="testimony-wall-story-card">
              <div className="testimony-wall-story-content">
                <div className="testimony-wall-story-mark" aria-hidden="true">✦</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="testimony-wall-story-head">
                    <div style={{ minWidth: 0 }}>
                      <SectionLabel>{getTestimonyCategory(item)}</SectionLabel>
                      <h4 className="serif">
                        {item.prayerTitle}
                      </h4>
                    </div>
                  </div>
                  <p className="testimony-wall-story-text">
                    "{visibleTestimony}"
                  </p>
                  {isLong && (
                    <button
                      onClick={() =>
                        setExpandedIds((current) => ({
                          ...current,
                          [item.id]: !current[item.id],
                        }))
                      }
                      className="testimony-wall-read-more"
                    >
                      {expanded ? "Show less" : "Read more"}
                    </button>
                  )}
                  <p className="testimony-wall-story-meta">
                    — {item.displayName}
                    {item.createdAt ? ` · ${new Date(item.createdAt).toLocaleDateString(undefined, { month: "long", year: "numeric" })}` : ""}
                  </p>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </Card>
  );

  if (sharedTestimoniesOpen) {
    return (
      <div
        className="testimony-garden-page testimony-shared-page"
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        {error && (
          <Card style={{ padding: 14, borderColor: "rgba(183,28,28,.18)", background: "rgba(255,245,245,.74)" }}>
            <p style={{ margin: 0, fontSize: 13.5, color: C.red, lineHeight: 1.55 }}>{error}</p>
          </Card>
        )}
        <Card className="testimony-shared-page-hero testimony-wall-hero-today" style={{ padding: 16 }}>
          <button
            type="button"
            onClick={closeSharedTestimoniesPage}
            className="testimony-wall-back-link"
          >
            ← Back to Testimonies
          </button>
          <div className="testimony-wall-hero-layout">
            <div className="testimony-wall-hero-copy">
              <SectionLabel>Community</SectionLabel>
              <h2 className="serif">Shared Testimonies</h2>
              <p>
                Answered prayers and hope,<br />
                gathered in one quiet place.
              </p>
              <span className="testimony-wall-gold-divider" aria-hidden="true" />
            </div>
            <div className="testimony-wall-botanical" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          </div>
          <button
            type="button"
            onClick={openShareTestimonyPage}
            className="testimony-wall-share-card"
          >
            <span className="testimony-wall-share-icon" aria-hidden="true">✎</span>
            <span className="testimony-wall-share-copy">
              <strong>Share Testimony</strong>
              <small>Encourage someone with your story.</small>
            </span>
            <span className="testimony-wall-share-chevron" aria-hidden="true">›</span>
          </button>
        </Card>
        {renderSharedTestimoniesWall()}
      </div>
    );
  }

  if (shareFormOpen) {
    return (
      <div className="testimony-garden-page testimony-share-page testimony-journal-share-page">
        <button
          type="button"
          onClick={closeShareTestimonyPage}
          className="testimony-journal-back"
        >
          ← Back to Testimonies
        </button>

        <div className="testimony-journal-header">
          <SectionLabel>Share</SectionLabel>
          <h2 className="serif">Share Your Testimony</h2>
          <p>
            Tell how God has worked in your life. Every testimony is reviewed before it appears publicly.
          </p>
        </div>

        {error && (
          <Card style={{ padding: 14, borderColor: "rgba(183,28,28,.18)", background: "rgba(255,245,245,.74)" }}>
            <p style={{ margin: 0, fontSize: 13.5, color: C.red, lineHeight: 1.55 }}>{error}</p>
          </Card>
        )}

        <Card className="testimony-journal-card">
          {shareSuccessKind ? (
            <div className="testimony-submit-success">
              <div className="testimony-submit-check">✓</div>
              <h3 className="serif">{shareSuccessKind === "draft" ? "Draft Saved" : "Thank You"}</h3>
              <p>
                {shareSuccessKind === "draft"
                  ? "Your testimony draft has been saved in your Testimony Book."
                  : "Your testimony has been submitted for review."}
              </p>
              <p className="testimony-submit-blessing">
                Your story may become someone else's answered prayer.
              </p>
              <div className="testimony-submit-actions">
                <GoldBtn onClick={closeShareTestimonyPage} style={{ width: "100%" }}>
                  Back to Testimonies
                </GoldBtn>
                <button
                  type="button"
                  onClick={() => {
                    setShareSubmitted(false);
                    setShareSuccessKind(null);
                    setShareText("");
                    setShareTitle("");
                  }}
                >
                  Write Another
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="testimony-field-group testimony-field-enter-1">
                <label htmlFor="share-testimony-title">Title</label>
                <input
                  id="share-testimony-title"
                  value={shareTitle}
                  onChange={(e) => setShareTitle(e.target.value)}
                  maxLength={70}
                  placeholder="Answered Prayer"
                  className="testimony-journal-input"
                />
              </div>

              <div className="testimony-anonymous-row testimony-field-enter-2">
                <div>
                  <h3>Post anonymously</h3>
                  <p>Share your testimony without displaying your name.</p>
                </div>
                <button
                  type="button"
                  aria-pressed={sharePostAnonymous}
                  className={sharePostAnonymous ? "testimony-toggle on" : "testimony-toggle"}
                  onClick={() => setSharePostAnonymous((value) => !value)}
                >
                  <span />
                </button>
              </div>

              {!sharePostAnonymous && (
                <div className="testimony-field-group testimony-field-enter-2">
                  <label htmlFor="share-testimony-name">Display Name</label>
                  <input
                    id="share-testimony-name"
                    value={shareDisplayName === "Anonymous" ? "" : shareDisplayName}
                    onChange={(e) => setShareDisplayName(e.target.value)}
                    maxLength={32}
                    placeholder={currentUser?.displayName || "Your name"}
                    className="testimony-journal-input"
                  />
                </div>
              )}

              <div className="testimony-encouragement testimony-field-enter-3">
                <span>🌿</span>
                <p>Every testimony can strengthen someone else's faith.</p>
              </div>

              <div className="testimony-field-group testimony-field-enter-3">
                <label htmlFor="share-testimony-story">Your Story</label>
                <textarea
                  id="share-testimony-story"
                  value={shareText}
                  onChange={(e) => setShareText(e.target.value)}
                  onInput={(e) => {
                    const target = e.currentTarget;
                    target.style.height = "auto";
                    target.style.height = Math.max(target.scrollHeight, 250) + "px";
                  }}
                  placeholder="Share how God answered your prayer, guided you, strengthened you, provided for you, or transformed your heart..."
                  className="testimony-journal-textarea"
                />
              </div>

              <div className="testimony-live-preview testimony-field-enter-4">
                <SectionLabel>Preview</SectionLabel>
                <h4 className="serif">{sharePreviewTitle}</h4>
                <p>"{sharePreviewText.length > 170 ? sharePreviewText.slice(0, 170).trimEnd() + "..." : sharePreviewText}"</p>
                <small>— {shareAuthorName}</small>
              </div>

              <p className="testimony-share-footnote testimony-field-enter-4">
                Only share what you're comfortable making public. Personal information is never required.
              </p>

              <div className="testimony-journal-actions testimony-field-enter-4">
                <button
                  type="button"
                  onClick={saveDirectTestimonyDraft}
                  disabled={!shareText.trim()}
                  className="testimony-draft-btn"
                >
                  Save Draft
                </button>
                <GoldBtn
                  onClick={submitDirectTestimony}
                  disabled={shareSubmitting || !shareText.trim()}
                  className="testimony-submit-btn"
                  style={{ width: "100%" }}
                >
                  {shareSubmitting ? "Submitting..." : "Submit for Review"}
                </GoldBtn>
              </div>
            </>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div
      className="testimony-garden-page"
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      {error && (
        <Card style={{ padding: 14, borderColor: "rgba(183,28,28,.18)", background: "rgba(255,245,245,.74)" }}>
          <p style={{ margin: 0, fontSize: 13.5, color: C.red, lineHeight: 1.55 }}>{error}</p>
        </Card>
      )}

      {adminMode && !isAdmin && (
        <Card
          style={{
            padding: 16,
            background: "linear-gradient(160deg,rgba(255,253,246,.98),rgba(246,250,255,.7))",
            borderColor: "rgba(125,174,212,.28)",
          }}
        >
          <SectionLabel>Admin Review</SectionLabel>
          <h3 className="serif" style={{ margin: "0 0 6px", fontSize: 18, color: C.textDark }}>
            Sign in as Erica
          </h3>
          <p style={{ margin: "0 0 12px", fontSize: 13.5, color: C.textSoft, lineHeight: 1.6 }}>
            Testimony approval is private. Sign in with {ADMIN_EMAIL} to approve or reject public testimonies.
          </p>
          <GoldBtn onClick={onAdminSignIn} style={{ width: "100%" }}>
            Sign in with Google
          </GoldBtn>
        </Card>
      )}

      {adminMode && isAdmin && (
        <Card
          style={{
            padding: 16,
            background: "linear-gradient(160deg,rgba(255,253,246,.98),rgba(246,250,255,.7))",
            borderColor: "rgba(125,174,212,.28)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 12 }}>
            <div>
              <SectionLabel>Hidden Admin</SectionLabel>
              <h3 className="serif" style={{ margin: 0, fontSize: 18, color: C.textDark }}>
                Pending Testimonies
              </h3>
            </div>
            <button
              className="top-soft-chip refresh-chip"
              onClick={loadPendingTestimonies}
              disabled={adminLoading}
              style={{
                background: "rgba(255,253,246,.82)",
                border: "1px solid " + C.cardBorder,
                borderRadius: 999,
                padding: "8px 11px",
                color: C.goldDeep,
                fontWeight: 900,
                cursor: adminLoading ? "default" : "pointer",
                fontSize: 12.5,
              }}
            >
              {adminLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
          {pendingTestimonies.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-title">No testimonies waiting right now.</p>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6 }}>
                Submitted testimonies will appear here before they go public.
              </p>
            </div>
          ) : (
            <div className="scroll-list" style={{ maxHeight: "52vh", paddingRight: 6 }}>
              {pendingTestimonies.map((item) => (
                <div key={item.id} className="testimony-entry">
                  <SectionLabel>Pending Approval</SectionLabel>
                  <h4 style={{ margin: "0 0 8px", fontSize: 15.5, color: C.textDark }}>
                    {item.prayerTitle}
                  </h4>
                  <p style={{ margin: "0 0 10px", fontSize: 13.5, color: C.textMid, lineHeight: 1.6 }}>
                    {item.testimony}
                  </p>
                  <p style={{ margin: "0 0 10px", fontSize: 12.5, color: C.textFaint }}>
                    {item.submittedAt || "Submitted recently"} · {item.displayName || "Anonymous"}
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <GoldBtn onClick={() => moderateTestimony(item.id, true)}>
                      Approve
                    </GoldBtn>
                    <button
                      onClick={() => moderateTestimony(item.id, false)}
                      style={{
                        background: "rgba(255,253,246,.82)",
                        border: "1px solid " + C.cardBorder,
                        borderRadius: 14,
                        padding: "11px 10px",
                        color: C.textFaint,
                        fontWeight: 900,
                        cursor: "pointer",
                        minHeight: 44,
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card className="featured-testimony-card testimony-featured-garden premium-featured-testimony">
        <div className="testimony-wall-feature-heading">
          <h2 className="serif">Testimony Wall</h2>
        </div>
        <div className="premium-featured-header">
          <SectionLabel>Featured Testimony ✨</SectionLabel>
          <span className="premium-featured-read-time">
            {featuredReadingMinutes} min read
          </span>
        </div>

        <img
          className="featured-testimony-image premium-featured-image"
          src="/prayergarden.png?v=featured-testimony-20260810"
          alt="A peaceful Spirit Garden testimony scene"
          loading="eager"
          onError={(event) => {
            const image = event.currentTarget;
            if (!image.src.includes("spirit-garden-splash")) {
              image.src = "/spirit-garden-splash-v5.png";
            }
          }}
        />

        <div className="premium-featured-story">
          <div className="premium-featured-copy">
            <h3 className="serif">{FEATURED_TESTIMONY.title}</h3>
            <p className="premium-featured-author">— {FEATURED_TESTIMONY.author}</p>

            <div className={`premium-featured-excerpt${featuredOpen ? " open" : ""}`}>
              <p>{FEATURED_TESTIMONY.intro}</p>
              {featuredOpen &&
                FEATURED_TESTIMONY.body.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
            </div>

            <button
              type="button"
              onClick={() => setFeaturedOpen((open) => !open)}
              className="premium-featured-read-action"
            >
              {featuredOpen ? "Show Less" : "Read Story"}
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </Card>

      <section className="testimony-main-share-section">
        <div className="testimony-editorial-heading">
          <SectionLabel>Share</SectionLabel>
          <h2 className="serif">Stories of faithfulness</h2>
          <p>Share what God has done.</p>
        </div>

        <button
          type="button"
          onClick={openShareTestimonyPage}
          className="testimony-premium-action-card primary"
        >
          <span className="testimony-action-icon testimony-share-action-icon" aria-hidden="true"><img src="/icons/spirit-garden-icon-1024.png" alt="" /></span>
          <span className="testimony-action-copy">
            <strong>Share Testimony</strong>
            <small>Write your story and encourage others.</small>
          </span>
          <span className="testimony-action-chevron" aria-hidden="true">›</span>
        </button>

        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="testimony-premium-action-card secondary"
        >
          <span className="testimony-action-icon instagram-action-icon" aria-hidden="true"><img src="/icons/instagram-transparent-icon.png" alt="" /></span>
          <span className="testimony-action-copy">
            <strong>Follow on Instagram</strong>
            <small>Daily inspiration and testimonies.</small>
          </span>
          <span className="testimony-action-chevron" aria-hidden="true">›</span>
        </a>
      </section>

      <Card className="testimony-recent-card" style={{ padding: 16 }}>
        <div className="testimony-recent-head">
          <div>
            <SectionLabel>Recently Shared</SectionLabel>
            <h3>New stories</h3>
          </div>
          <button type="button" onClick={openSharedTestimoniesPage}>
            View all <span aria-hidden="true">›</span>
          </button>
        </div>

        {recentSharedTestimonies.length > 0 ? (
          <div className="testimony-recent-list">
            {recentSharedTestimonies.map((item) => (
              <div key={item.id} className="testimony-recent-preview-card">
                <span className="testimony-recent-icon" aria-hidden="true">✦</span>
                <div>
                  <strong className="serif">{item.prayerTitle}</strong>
                  <p>{item.testimony.length > 96 ? item.testimony.slice(0, 96).trimEnd() + "..." : item.testimony}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="testimony-recent-empty">
            <span aria-hidden="true">🌱</span>
            <p>No public testimonies yet.</p>
          </div>
        )}

        <div className="testimony-review-note">
          <span aria-hidden="true">♢</span>
          <span>Reviewed before publishing</span>
          {isAdmin && (
            <button
              type="button"
              onClick={() => {
                setAdminMode((mode) => !mode);
                if (typeof window !== "undefined") {
                  window.location.hash = adminMode ? "" : "admin-wall";
                }
              }}
            >
              {adminMode ? "Close admin review" : "Admin review"}
            </button>
          )}
        </div>
      </Card>

      <Card className="testimony-book-preview-card" style={{ padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{ minWidth: 0 }}>
            <SectionLabel>Testimony Book</SectionLabel>
            <h3 className="serif" style={{ margin: 0, fontSize: 18, color: C.textDark }}>
              Your Saved Testimonies
            </h3>
            <p style={{ margin: "3px 0 0", fontSize: 13, color: C.textSoft, lineHeight: 1.45 }}>
              Drafts and answered prayers you saved.
            </p>
          </div>
          <span className="testimony-book-count">
            <span aria-hidden="true">▣</span> {testimonyBookItems.length} saved
          </span>
        </div>
        {testimonyBookItems.length === 0 ? (
          <div className="testimony-book-empty">
            <span className="testimony-book-empty-icon" aria-hidden="true">🌱</span>
            <p className="empty-state-title">No saved testimonies yet.</p>
            <p className="empty-state-copy">
              Save a draft or answered prayer to begin.
            </p>
            <button type="button" onClick={openShareTestimonyPage}>
              ＋ Save a Draft
            </button>
          </div>
        ) : (
          <div className="testimony-book-mini-list">
            {testimonyBookItems.slice(0, 3).map((item) => (
              <div key={item.id} className="testimony-book-mini-row">
                <span className="testimony-book-mini-icon" style={{ background: "linear-gradient(135deg,rgba(255,255,255,.92)," + item.accent + "55)" }}>
                  {item.icon}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h4>{item.title}</h4>
                  <p>{item.detail.length > 110 ? item.detail.slice(0, 110).trimEnd() + "..." : item.detail}</p>
                  {item.date && <small>{item.date}</small>}
                </div>
                <div className="testimony-book-mini-actions">
                  {onEditTestimonyEntry && (
                    <button type="button" onClick={() => onEditTestimonyEntry(item.prayerId)}>Edit</button>
                  )}
                  {onDeleteTestimonyEntry && (
                    <button type="button" onClick={() => onDeleteTestimonyEntry(item.prayerId)}>Delete</button>
                  )}
                </div>
              </div>
            ))}
            {testimonyBookItems.length > 3 && (
              <p className="prayer-friend-copy" style={{ margin: "8px 0 0", textAlign: "center" }}>
                Showing 3 newest.
              </p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────────────────
type TabId =
  | "today"
  | "quests"
  | "prayers"
  | "friends"
  | "wall"
  | "journal"
  | "home"
  | "shop"
  | "growth";
const TABS: [TabId, string, string][] = [
  ["today", "🌅", "Today"],
  ["prayers", "🙏", "Prayer"],
  ["wall", "📜", "Testimonies"],
  ["growth", "🌱", "Growth"],
];
const FEATURE_UNLOCKS: Partial<
  Record<TabId, { xp: number; title: string; desc: string }>
> = {
  prayers: {
    xp: 0,
    title: "Prayer opens after Today",
    desc: "Finish Today's Walk once, then lift one prayer.",
  },
  journal: {
    xp: 40,
    title: "Journal unlocks soon",
    desc: "Save short reflections as your rhythm grows.",
  },
  quests: {
    xp: 60,
    title: "Quests unlock soon",
    desc: "Extra challenges open after a little growth.",
  },
  home: {
    xp: 0,
    title: "Home opens after Prayer",
    desc: "Begin Prayer Vessels first, then visit Home.",
  },
  shop: {
    xp: 0,
    title: "Closet opens after Home",
    desc: "Visit Home first, then style your spirit.",
  },
  growth: {
    xp: 80,
    title: "Growth opens soon",
    desc: "Your progress page opens as your spirit grows.",
  },
};

export default function App() {
  const [today, setToday] = useState(todayKey);

  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const hasAuthError =
      path.includes("/__/auth/handler") ||
      params.has("error") ||
      params.has("error_description");
    if (!hasAuthError) return;
    clearGoogleRedirectPending();
    clearGoogleAuthStorage();
    window.history.replaceState({}, document.title, "/");
    setCloudStatus(googleAuthRedirectHelp);
    showToast("Google sign-in needs one Firebase setting updated.");
  }, []);

  useEffect(() => {
    const refreshCalendarDay = () => {
      const nextDay = todayKey();
      setToday((currentDay) =>
        currentDay === nextDay ? currentDay : nextDay
      );
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshCalendarDay();
    };
    const timer = window.setInterval(refreshCalendarDay, 30 * 1000);
    window.addEventListener("focus", refreshCalendarDay);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshCalendarDay);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const checkForAppUpdate = async () => {
      try {
        const res = await fetch(`/asset-manifest.json?ts=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const manifest = await res.json();
        const buildId =
          manifest?.files?.["main.js"] ||
          manifest?.files?.["main.css"] ||
          JSON.stringify(manifest?.files || manifest);
        if (!buildId || cancelled) return;

        const savedBuildId = localStorage.getItem("sg_app_build_id");
        if (!savedBuildId) {
          localStorage.setItem("sg_app_build_id", buildId);
          return;
        }

        const reloadKey = `sg_reloaded_build_${buildId}`;
        if (savedBuildId !== buildId && sessionStorage.getItem(reloadKey) !== "1") {
          localStorage.setItem("sg_app_build_id", buildId);
          sessionStorage.setItem(reloadKey, "1");
          window.location.reload();
        }
      } catch {
        // Offline or cached launches should keep working quietly.
      }
    };

    checkForAppUpdate();
    const timer = window.setInterval(checkForAppUpdate, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const [gender, setGender] = useState<Gender | null>(() =>
    LS.get<Gender | null>("sg_gender", null)
  );
  const [spiritName, setSpiritName] = useState<string>(() =>
    LS.get<string>("sg_spirit_name", "")
  );
  const [editingSpiritName, setEditingSpiritName] = useState(false);
  const [spiritNameDraft, setSpiritNameDraft] = useState<string>(() =>
    LS.get<string>("sg_spirit_name", "")
  );
  const skinTone = "#FDDBB4";
  const hairColor = DEFAULT_HAIR_COLOR;
  const [tasks, setTasks] = useState<Task[]>(() =>
    LS.get<Task[]>("sg_tasks_v4", PRESET_TASKS)
      .filter((t) => t.id !== "thanksgiving" && t.id !== "kindness")
      .map((t) => ({
        id: t.id,
        label: t.label,
        icon: t.icon,
        optional: t.optional,
      }))
  );
  const [checked, setChecked] = useState<CheckedMap>(() =>
    LS.get<CheckedMap>(`sg_chk_${today}`, {})
  );
  const [totalXP, setTotalXP] = useState<number>(() =>
    LS.get<number>("sg_xp_v3", 0)
  );
  const [righteousness, setRighteousness] = useState<number>(() =>
    LS.get<number>("sg_r", 0)
  );
  const [streak, setStreak] = useState<number>(() =>
    LS.get<number>("sg_streak_v3", 0)
  );
  const [graceDays, setGraceDays] = useState<number>(() =>
    LS.get<number>("sg_grace", 1)
  );
  const [lastActive, setLastActive] = useState<string>(() =>
    LS.get<string>("sg_lastActive", today)
  );
  const [journal, setJournal] = useState<string>(() =>
    LS.get<string>(`sg_j_${today}`, "")
  );
  const [entries, setEntries] = useState<JournalEntry[]>(() =>
    LS.get<JournalEntry[]>("sg_jentries_v3", [])
  );
  const [editingEntry, setEditingEntry] = useState<string | null>(null);
  const [editText, setEditText] = useState<string>("");
  const [viewingJournalEntry, setViewingJournalEntry] = useState<JournalEntry | null>(null);
  const [openJournalMonths, setOpenJournalMonths] = useState<
    Record<string, boolean>
  >({});
  const [newTaskLabel, setNewTaskLabel] = useState<string>("");
  const [newTaskIcon, setNewTaskIcon] = useState<string>("✨");
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingTaskLabel, setEditingTaskLabel] = useState<string>("");
  const [editingTaskIcon, setEditingTaskIcon] = useState<string>("✨");
  const [checklistEditMode, setChecklistEditMode] = useState<boolean>(false);
  const [activeTaskMenuId, setActiveTaskMenuId] = useState<string | null>(null);
  const taskPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const taskTouchStartX = useRef<number>(0);
  const [showAddTask, setShowAddTask] = useState<boolean>(false);
  const [showFaithJourney, setShowFaithJourney] = useState<boolean>(false);
  const [showTodayQuests, setShowTodayQuests] = useState<boolean>(false);
  const [dayDone, setDayDone] = useState<boolean>(() =>
    LS.get<boolean>(`sg_done_${today}`, false)
  );
  const activeChecklistDayRef = useRef(today);
  const [firstDayComplete, setFirstDayComplete] = useState<boolean>(
    () =>
      LS.get<boolean>("sg_first_day_complete_v1", false) ||
      LS.get<boolean>(`sg_done_${today}`, false)
  );
  const [sparks, setSparks] = useState<Spark[]>([]);
  const [tab, setTab] = useState<TabId>(() =>
    typeof window !== "undefined" && window.location.pathname === "/admin"
      ? "wall"
      : "today"
  );
  const [journalSaved, setJournalSaved] = useState<boolean>(false);
  const [toast, setToast] = useState<string | null>(null);
  const [partnerRequestNotice, setPartnerRequestNotice] = useState<{
    title: string;
    message: string;
    tone?: "success" | "info" | "error";
  } | null>(null);
  const [ownedItems, setOwnedItems] = useState<string[]>(() =>
    LS.get<string[]>("sg_owned_v3", []).filter((id) =>
      CLOTHES.some((item) => item.id === id)
    )
  );
  const [equippedItems, setEquippedItems] = useState<string[]>(() =>
    LS.get<string[]>("sg_equipped_v3", []).filter((id) =>
      CLOTHES.some((item) => item.id === id)
    )
  );
  const [doneQuiz, setDoneQuiz] = useState<DoneMap>(() =>
    LS.get<DoneMap>(`sg_quiz_${today}`, {})
  );
  const [doneTrivia, setDoneTrivia] = useState<DoneMap>(() =>
    LS.get<DoneMap>(`sg_trivia_${today}`, {})
  );
  const [samaritan, setSamaritan] = useState<SamaritanMap>(() =>
    LS.get<SamaritanMap>(`sg_sam_${today}`, {})
  );
  const [preachLog, setPreachLog] = useState<PreachEntry[]>(() =>
    LS.get<PreachEntry[]>("sg_preach_v3", [])
  );
  const [prayerFriends, setPrayerFriends] = useState<PrayerFriend[]>(() =>
    LS.get<PrayerFriend[]>("sg_prayer_friends_v1", [])
  );
  const [answeredCelebration, setAnsweredCelebration] = useState<{
    id: string;
    title: string;
    note?: string;
    sharePublic: boolean;
  } | null>(null);
  const [prayerLights, setPrayerLights] = useState<PrayerLight[]>([]);
  const [angelMessages, setAngelMessages] = useState<AngelMessage[]>([]);
  const [onboarded, setOnboarded] = useState<boolean>(() =>
    LS.get<boolean>("sg_onboarded_v1", false)
  );
  const [showDaySummary, setShowDaySummary] = useState<boolean>(false);
  const [showCloset, setShowCloset] = useState<boolean>(false);
  const [expandedShopItem, setExpandedShopItem] = useState<string | null>(null);
  const [previewClothingItemId, setPreviewClothingItemId] = useState<
    string | null
  >(null);
  const [activeShopSlot, setActiveShopSlot] = useState<Slot>("set");
  const [ownedHomeItems, setOwnedHomeItems] = useState<string[]>(() =>
    LS.get<string[]>("sg_home_owned_v1", []).filter((id) =>
      HOME_ITEMS.some((item) => item.id === id)
    )
  );
  const [placedHomeItems, setPlacedHomeItems] = useState<PlacedHomeMap>(() => {
    const saved = LS.get<PlacedHomeMap>("sg_home_placed_v1", {});
    return Object.fromEntries(
      Object.entries(saved).filter(
        ([, id]) => id && HOME_ITEMS.some((item) => item.id === id)
      )
    ) as PlacedHomeMap;
  });
  const [homeBlooms, setHomeBlooms] = useState<HomeBloom[]>(() =>
    LS.get<HomeBloom[]>("sg_home_blooms_v1", [])
  );
  const [spiritVisitors, setSpiritVisitors] = useState<SpiritVisitor[]>(() =>
    activeSpiritVisitors(LS.get<SpiritVisitor[]>("sg_spirit_visitors_v1", []))
  );
  const [expandedHomeItem, setExpandedHomeItem] = useState<string | null>(null);
  const [activityLog, setActivityLog] = useState<Record<string, boolean>>(
    () => ({
      ...LS.get<Record<string, boolean>>("sg_activity_v1", {}),
      [today]: true,
    })
  );
  const [lowFundsModal, setLowFundsModal] = useState<{ needed: number } | null>(
    null
  );
  const [lastStageKey, setLastStageKey] = useState<string>(() =>
    LS.get<string>("sg_last_stage_key_v1", "")
  );
  const [stageUpModal, setStageUpModal] = useState<
    (typeof SPIRIT_STAGES)[number] | null
  >(null);
  const [lastHomeStageLevel, setLastHomeStageLevel] = useState<number>(() =>
    LS.get<number>("sg_last_home_stage_level_v1", -1)
  );
  const [homeGrowthModal, setHomeGrowthModal] =
    useState<HomeGrowthStage | null>(null);
  const [quietMode, setQuietMode] = useState<boolean>(true);
  const [soundOn, setSoundOn] = useState<boolean>(() =>
    LS.get<boolean>("sg_sound_on_v1", true)
  );
  const audioCtxRef = useRef<AudioContext | null>(null);
  const [moodLog, setMoodLog] = useState<Record<string, MoodEntry>>(() =>
    LS.get<Record<string, MoodEntry>>("sg_mood_v1", {})
  );
  const [homeRestored, setHomeRestored] = useState<boolean>(false);
  const [homeBlessed, setHomeBlessed] = useState<boolean>(false);
  const [homeBlessedDate, setHomeBlessedDate] = useState<string>(() =>
    LS.get<string>("sg_home_blessed_date_v1", "")
  );
  const [homeTransforming, setHomeTransforming] = useState<boolean>(false);
  const [faithFlightUnlocked, setFaithFlightUnlocked] = useState<boolean>(() =>
    LS.get<boolean>("sg_faith_flight_unlocked_v1", false)
  );
  const [faithFlightActive, setFaithFlightActive] = useState<boolean>(false);
  const [homeVisited, setHomeVisited] = useState<boolean>(() =>
    LS.get<boolean>("sg_home_visited_v1", false)
  );
  const [closetVisited, setClosetVisited] = useState<boolean>(() =>
    LS.get<boolean>("sg_closet_visited_v1", false)
  );
  const [testimonyStones, setTestimonyStones] = useState<TestimonyStone[]>(() =>
    LS.get<TestimonyStone[]>("sg_testimony_stones_v1", [])
  );
  const [approvedTestimonyCount, setApprovedTestimonyCount] = useState<number>(0);
  const [seenTestimonyCount, setSeenTestimonyCount] = useState<number>(() =>
    LS.get<number>("sg_seen_testimony_count_guest", -1)
  );
  const [questStoneDates, setQuestStoneDates] = useState<
    Record<string, boolean>
  >(() => LS.get<Record<string, boolean>>("sg_quest_stone_dates_v1", {}));
  const [showTestimonyStones, setShowTestimonyStones] =
    useState<boolean>(false);
  const [showTestimonyBook, setShowTestimonyBook] = useState<boolean>(false);
  const [openTestimonyMonths, setOpenTestimonyMonths] = useState<
    Record<string, boolean>
  >({});
  const [showGrowthMap, setShowGrowthMap] = useState<boolean>(false);
  const [showCustomizeGrowth, setShowCustomizeGrowth] =
    useState<boolean>(false);
  const [stonePopup, setStonePopup] = useState<TestimonyStone | null>(null);
  const [stoneQueue, setStoneQueue] = useState<TestimonyStone[]>([]);
  const [cloudUser, setCloudUser] = useState<User | null>(null);
  const [cloudStatus, setCloudStatus] = useState<string>("");
  const [pushStatus, setPushStatus] = useState<string>(() =>
    LS.get<string>("sg_push_status_v1", "")
  );
  const [pushBusy, setPushBusy] = useState<boolean>(false);
  const [lastCloudSavedAt, setLastCloudSavedAt] = useState<string>(() =>
    LS.get<string>("sg_last_cloud_saved_at_v1", "")
  );
  const [showWelcomeBack, setShowWelcomeBack] = useState<boolean>(false);
  const [showSplashIntro, setShowSplashIntro] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("sg_splash_intro_seen_v1") !== "1";
  });
  const [googleSignInBusy, setGoogleSignInBusy] = useState<boolean>(false);
  const [showCloudPrompt, setShowCloudPrompt] = useState<boolean>(() =>
    LS.get<boolean>("sg_cloud_prompt_dismissed_v1", false) ? false : true
  );
  const [friendProfile, setFriendProfile] =
    useState<SpiritFriendProfile | null>(null);
  const [spiritFriends, setSpiritFriends] = useState<SpiritFriend[]>([]);
  const [incomingFriendRequests, setIncomingFriendRequests] = useState<
    SpiritFriendRequest[]
  >([]);
  const [outgoingFriendRequests, setOutgoingFriendRequests] = useState<
    SpiritFriendRequest[]
  >([]);
  const [accountabilityPartnerUid, setAccountabilityPartnerUid] =
    useState<string>("");
  const [accountabilityPartnerUids, setAccountabilityPartnerUids] = useState<
    string[]
  >([]);
  const [sharedPrayers, setSharedPrayers] = useState<SharedPrayer[]>([]);
  const [receivedPrayerSignals, setReceivedPrayerSignals] = useState<
    PrayerSignal[]
  >([]);
  const [friendStatus, setFriendStatus] = useState<string>("");
  const spiritFriendUidKey = useMemo(
    () => spiritFriends.map((friend) => friend.uid).sort().join("|"),
    [spiritFriends]
  );
  const googleSignInInProgressRef = useRef(false);
  const welcomeBackShownRef = useRef(false);
  const pendingWelcomeBackRef = useRef(false);

	  const showToast = (msg: string) => setToast(msg);
  const showWelcomeBackOnce = () => {
    if (welcomeBackShownRef.current) return;
    welcomeBackShownRef.current = true;
    LS.set("sg_show_welcome_after_signin_v1", false);
    if (showSplashIntro) {
      pendingWelcomeBackRef.current = true;
      return;
    }
    setShowWelcomeBack(true);
  };
  const dismissSplashIntro = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("sg_splash_intro_seen_v1", "1");
    }
    setShowSplashIntro(false);
  }, []);
	  const cloudDisplayName =
    cloudUser?.displayName?.split(" ")[0] ||
    cloudUser?.email?.split("@")[0] ||
    spiritName ||
    "friend";

  function gentleHaptic(pattern: number | number[] = 24) {
    spiritHapticPattern(pattern);
  }

  useEffect(() => {
    if (!showSplashIntro) return;
    const timer = window.setTimeout(dismissSplashIntro, 2550);
    return () => window.clearTimeout(timer);
  }, [dismissSplashIntro, showSplashIntro]);

  useEffect(() => {
    if (showSplashIntro || !pendingWelcomeBackRef.current) return;
    pendingWelcomeBackRef.current = false;
    const timer = window.setTimeout(() => setShowWelcomeBack(true), 120);
    return () => window.clearTimeout(timer);
  }, [showSplashIntro]);
  const lastSavedText = lastCloudSavedAt
    ? new Date(lastCloudSavedAt).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";
  const isAdminUser =
    cloudUser?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  const previewHomeBlooms = useMemo(() => {
    if (!isAdminUser) return homeBlooms;
    const sampleBlooms: HomeBloom[] = Array.from({ length: 12 }, (_, index) => {
      const d = new Date();
      d.setDate(d.getDate() - index);
      return { date: `admin-preview-${dateKey(d)}`, kind: "walk" };
    });
    const savedDates = new Set(homeBlooms.map((bloom) => bloom.date));
    return [
      ...homeBlooms,
      ...sampleBlooms.filter((bloom) => !savedDates.has(bloom.date)),
    ];
  }, [homeBlooms, isAdminUser]);
  const previewSpiritVisitors = useMemo(() => {
    if (!isAdminUser) return spiritVisitors;
    const now = Date.now();
    const sampleVisitors: SpiritVisitor[] = [
      {
        id: "admin-preview-prayer-dove",
        kind: "dove",
        reason: "A Dove visited because someone prayed for you.",
        createdAt: now - 1000,
        expiresAt: now + 30 * 60 * 1000,
      },
      {
        id: "admin-preview-golden-dove",
        kind: "golden_dove",
        reason: "A Golden Dove came with the light of an answered prayer.",
        createdAt: now - 2000,
        expiresAt: now + 30 * 60 * 1000,
      },
      {
        id: "admin-preview-bluebird",
        kind: "bluebird",
        reason: "A Bluebird came to celebrate faithfulness.",
        createdAt: now - 3000,
        expiresAt: now + 30 * 60 * 1000,
      },
    ];
    const savedIds = new Set(spiritVisitors.map((visitor) => visitor.id));
    return activeSpiritVisitors([
      ...spiritVisitors,
      ...sampleVisitors.filter((visitor) => !savedIds.has(visitor.id)),
    ]);
  }, [isAdminUser, spiritVisitors]);

	  async function saveSpiritNameEdit() {
	    const nextName = formatSpiritName(spiritNameDraft.trim());
	    if (!nextName) return;
	    setSpiritName(nextName);
    setSpiritNameDraft(nextName);
	    setEditingSpiritName(false);
	    if (cloudUser?.email) {
	      try {
	        await setDoc(
	          doc(db, "friendProfiles", cloudUser.uid),
	          stripUndefinedForFirestore({
	            uid: cloudUser.uid,
	            displayName: nextName,
	            email: cloudUser.email,
	            emailLower: cloudUser.email.toLowerCase(),
	            inviteCode: friendInviteCode(cloudUser.uid),
            gender: profileGenderOrFallback(gender),
            stageKey: stage.key,
	            updatedAt: serverTimestamp(),
	          }),
	          { merge: true }
	        );
	        setFriendProfile((current) =>
	          current
            ? {
                ...current,
                displayName: nextName,
                gender: profileGenderOrFallback(gender || current.gender),
                stageKey: stage.key,
              }
            : current
	        );
	      } catch (error) {
	        console.error("Could not sync spirit friend name", error);
	      }
	    }
	    showToast("Spirit name updated.");
	  }

		  function clearSignedInSession(status = "Signed out", resetToStart = false) {
		    setCloudUser(null);
		    setCloudStatus(status);
	    setFriendProfile(null);
	    setSpiritFriends([]);
	    setIncomingFriendRequests([]);
	    setOutgoingFriendRequests([]);
	    setAccountabilityPartnerUid("");
	    setAccountabilityPartnerUids([]);
	    setSharedPrayers([]);
	    setReceivedPrayerSignals([]);
	    setFriendStatus("");
	    setLastCloudSavedAt("");
	    LS.set("sg_last_cloud_saved_at_v1", "");
	    setShowWelcomeBack(false);
	    welcomeBackShownRef.current = false;
		    setShowCloudPrompt(true);
		    LS.set("sg_cloud_prompt_dismissed_v1", false);
	    setQuietMode(true);
		    if (resetToStart) {
		      setGender(null);
		      setOnboarded(false);
		      setTab("today");
		      LS.set("sg_gender", null);
		      LS.set("sg_onboarded_v1", false);
		    }
		  }

  function mergePrayerFriendsForRestore(
    localPrayers: PrayerFriend[],
    cloudPrayers: PrayerFriend[]
  ) {
    const byId = new Map<string, PrayerFriend>();
    [...localPrayers, ...cloudPrayers].forEach((prayer) => {
      if (!prayer?.id) return;
      byId.set(prayer.id, { ...byId.get(prayer.id), ...prayer });
    });
    return Array.from(byId.values()).sort((a, b) => {
      const aTime = Number(String(a.id).match(/(\d{10,})/)?.[1] || 0);
      const bTime = Number(String(b.id).match(/(\d{10,})/)?.[1] || 0);
      return bTime - aTime;
    });
  }

  const buildCloudProgress = () => stripUndefinedForFirestore({
    dailyDate: today,
    gender: normalizeSpiritGender(gender),
    spiritName: formatSpiritName(spiritName),
    tasks,
    checked,
    totalXP,
    righteousness,
    streak,
    graceDays,
    lastActive,
    journal,
    entries,
    dayDone,
    firstDayComplete,
    ownedItems,
    equippedItems,
    doneQuiz,
    doneTrivia,
    samaritan,
    preachLog,
    prayerFriends,
    ownedHomeItems,
    placedHomeItems,
    homeBlooms,
    spiritVisitors,
    onboarded,
    activityLog,
    moodLog,
    homeVisited,
    closetVisited,
    faithFlightUnlocked,
    testimonyStones,
    questStoneDates,
  });

  function applyCloudProgress(data: Record<string, any>) {
    const cloudDailyDate = data.dailyDate || data.lastActive;
    const cloudDailyStateIsCurrent = cloudDailyDate === today;
    if (data.gender) setGender(data.gender);
    if (typeof data.spiritName === "string") {
      setSpiritName(data.spiritName);
      setSpiritNameDraft(data.spiritName);
    }
    if (Array.isArray(data.tasks)) setTasks(data.tasks);
    if (cloudDailyStateIsCurrent && data.checked) setChecked(data.checked);
    if (typeof data.totalXP === "number") setTotalXP(data.totalXP);
    if (typeof data.righteousness === "number") setRighteousness(data.righteousness);
    if (typeof data.streak === "number") setStreak(data.streak);
    if (typeof data.graceDays === "number") setGraceDays(data.graceDays);
    if (typeof data.lastActive === "string") setLastActive(data.lastActive);
    if (cloudDailyStateIsCurrent && typeof data.journal === "string")
      setJournal(data.journal);
    if (Array.isArray(data.entries)) setEntries(data.entries);
    if (cloudDailyStateIsCurrent && typeof data.dayDone === "boolean")
      setDayDone(data.dayDone);
    if (typeof data.firstDayComplete === "boolean") setFirstDayComplete(data.firstDayComplete);
    if (Array.isArray(data.ownedItems)) setOwnedItems(data.ownedItems);
    if (Array.isArray(data.equippedItems)) setEquippedItems(data.equippedItems);
    if (data.doneQuiz) setDoneQuiz(data.doneQuiz);
    if (data.doneTrivia) setDoneTrivia(data.doneTrivia);
    if (data.samaritan) setSamaritan(data.samaritan);
    if (Array.isArray(data.preachLog)) setPreachLog(data.preachLog);
    if (Array.isArray(data.prayerFriends)) {
      setPrayerFriends((current) => {
        const restored = mergePrayerFriendsForRestore(current, data.prayerFriends);
        LS.set("sg_prayer_friends_v1", restored);
        return restored;
      });
    }
    if (Array.isArray(data.ownedHomeItems)) setOwnedHomeItems(data.ownedHomeItems);
    if (data.placedHomeItems) setPlacedHomeItems(data.placedHomeItems);
    if (Array.isArray(data.homeBlooms)) setHomeBlooms(data.homeBlooms);
    if (Array.isArray(data.spiritVisitors))
      setSpiritVisitors(activeSpiritVisitors(data.spiritVisitors));
    if (typeof data.onboarded === "boolean") setOnboarded(data.onboarded);
    if (data.activityLog) setActivityLog(data.activityLog);
    if (data.moodLog) setMoodLog(data.moodLog);
    if (typeof data.homeVisited === "boolean") setHomeVisited(data.homeVisited);
    if (typeof data.closetVisited === "boolean") setClosetVisited(data.closetVisited);
    if (typeof data.faithFlightUnlocked === "boolean")
      setFaithFlightUnlocked(data.faithFlightUnlocked);
    if (Array.isArray(data.testimonyStones)) setTestimonyStones(data.testimonyStones);
    if (data.questStoneDates) setQuestStoneDates(data.questStoneDates);
  }

  async function saveProgressToCloud(userOverride?: User | null) {
    const user = userOverride || cloudUser;
    if (!user || user.isAnonymous) {
      setCloudStatus("Sign in with Google to save your garden.");
      setShowCloudPrompt(true);
      return;
    }
    setCloudStatus("Saving...");
    await setDoc(
      doc(db, "users", user.uid),
      stripUndefinedForFirestore({
        progress: buildCloudProgress(),
        updatedAt: serverTimestamp(),
        email: user.email || null,
        emailLower: user.email?.toLowerCase() || null,
        displayName: spiritName || user.displayName || null,
      }),
      { merge: true }
    );
    const savedAt = new Date().toISOString();
    setLastCloudSavedAt(savedAt);
    LS.set("sg_last_cloud_saved_at_v1", savedAt);
    setCloudStatus("Saved");
    showToast("Spirit Garden saved to cloud.");
  }

  async function enablePushNotifications() {
    const user = cloudUser;
    if (!user?.email) {
      showToast("Sign in with Google first to enable notifications.");
      await handleGoogleSignIn();
      return;
    }

    setPushBusy(true);
    setPushStatus("Opening notification permission...");
    try {
      const token = await requestPushToken();
      if (!token) throw new Error("missing-token");
      await setDoc(
        doc(db, "users", user.uid),
        stripUndefinedForFirestore({
          notificationsEnabled: true,
          notificationToken: token,
          email: user.email || null,
          emailLower: user.email?.toLowerCase() || null,
          notificationUpdatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
      const nextStatus = "Notifications enabled on this device.";
      setPushStatus(nextStatus);
      LS.set("sg_push_status_v1", nextStatus);
      showToast("Notifications enabled.");
    } catch (error: any) {
      console.error("Notification setup failed", error);
      const errorMessage = String(error?.message || "");
      const message =
        errorMessage === "missing-vapid-key"
          ? "Add your Firebase Web Push certificate key first."
          : errorMessage === "notifications-need-https"
          ? "Notifications need the live HTTPS site, not a local preview."
          : errorMessage === "notification-permission-denied"
          ? "Notifications were not allowed on this device."
          : errorMessage === "messaging-not-supported"
          ? "This browser does not support Firebase push notifications. Try Chrome/Edge, or the installed Home Screen app on a supported device."
          : errorMessage === "notifications-not-supported"
          ? "Notifications are not supported on this device."
          : errorMessage === "service-worker-not-supported"
          ? "This browser cannot run the notification service worker."
          : errorMessage.startsWith("push-token-failed")
          ? `Notification token failed: ${errorMessage.replace("push-token-failed:", "")}`
          : error?.code === "permission-denied"
          ? "Could not save the notification token. Deploy the latest Firestore rules."
          : "Could not enable notifications yet.";
      setPushStatus(message);
      LS.set("sg_push_status_v1", message);
      showToast(message);
    } finally {
      setPushBusy(false);
    }
  }

  async function queuePushNotification({
    toUid,
    toEmail,
    kind,
    data = {},
  }: {
    toUid?: string;
    toEmail?: string;
    kind: PushNotificationKind;
    data?: Record<string, string>;
  }) {
    const user = cloudUser;
    if (!user?.email || (!toUid && !toEmail) || toUid === user.uid) return;
    const copy = PUSH_NOTIFICATION_COPY[kind];
    const fromName = (data.fromName || spiritName || user.displayName || "A Spirit Friend").trim();
    const title =
      kind === "friend_request"
        ? "New friend request"
        : kind === "prayer_note"
        ? `${fromName} prayed for you`
        : kind === "blessing"
        ? `${fromName} sent you a blessing`
        : kind === "partner_request"
        ? `${fromName} invited you to partner`
        : kind === "partner_focus"
        ? `${fromName} completed a shared focus`
        : copy.title;
    const body =
      kind === "friend_request"
        ? `${fromName} wants to pray with you in Spirit Garden.`
        : kind === "prayer_note"
        ? "A small prayer light was sent your way."
        : kind === "blessing"
        ? data.blessingLabel || "Open your Blessings Inbox to read it."
        : kind === "partner_request"
        ? "They want to walk with you as accountability partners."
        : kind === "partner_focus"
        ? data.focus || "Your shared focus was completed today."
        : copy.body;
    try {
      const recipientKey = (toUid || toEmail?.toLowerCase() || "unknown").replace(/[^a-zA-Z0-9_-]/g, "_");
      const actionKey = String(
        data.requestId ||
          data.prayerId ||
          data.sourcePrayerId ||
          data.focusId ||
          data.blessingLabel ||
          (kind === "prayer_note" || kind === "blessing" || kind === "partner_focus" ? today : "general")
      ).replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 80);
      const requestBaseId = ["push", kind, user.uid, recipientKey, actionKey].join("_").slice(0, 390);
      let requestRef = doc(db, "notificationRequests", requestBaseId);
      const existing = await getDoc(requestRef);
      if (existing.exists()) {
        const existingStatus = String(existing.data()?.status || "").trim().toLowerCase();
        if (["pending", "processing", "sent"].includes(existingStatus)) return;
        requestRef = doc(db, "notificationRequests", requestBaseId + "_retry_" + Date.now());
      }
      await setDoc(requestRef, {
        toUid: toUid || null,
        toEmail: toEmail?.toLowerCase() || null,
        fromUid: user.uid,
        type: kind,
        title,
        body,
        data,
        status: "pending",
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error(error);
    }
  }

  async function loadProgressFromCloud(userOverride?: User | null) {
    const user = userOverride || cloudUser;
    if (!user) return;
    setCloudStatus("Loading...");
    const snapshot = await getDoc(doc(db, "users", user.uid));
    const progress = snapshot.exists()
      ? (snapshot.data() as Record<string, any>).progress
      : null;
    if (progress) {
      applyCloudProgress(progress);
      setCloudStatus("Loaded");
      showToast("Cloud progress loaded.");
    } else {
      setCloudStatus("No cloud save yet");
    }
  }

  async function handleGoogleSignIn() {
    if (googleSignInInProgressRef.current) return;
    googleSignInInProgressRef.current = true;
    setGoogleSignInBusy(true);
    welcomeBackShownRef.current = false;
    try {
      setCloudStatus("Opening Google...");
      LS.set("sg_show_welcome_after_signin_v1", true);
      const user = await signInWithGoogle();
      setQuietMode(true);
      setTab("today");
      setCloudUser(user);
      const snapshot = await getDoc(doc(db, "users", user.uid));
      const progress = snapshot.exists()
        ? ((snapshot.data() as Record<string, any>).progress as Record<string, any> | undefined)
        : undefined;
      const hasSavedGarden = Boolean(progress?.gender && progress?.onboarded);
      const hasLocalGarden = Boolean(normalizeSpiritGender(gender) && onboarded);
      if (hasSavedGarden) {
        if (typeof progress?.spiritName === "string") {
          LS.set("sg_spirit_name", progress.spiritName);
        }
        applyCloudProgress(progress);
        setCloudStatus("Loaded");
        showToast("Cloud progress loaded.");
      } else if (hasLocalGarden) {
        setCloudStatus("Saving your garden...");
        await saveProgressToCloud(user);
      } else {
        setGender(null);
        setSpiritName("");
        setSpiritNameDraft("");
        setOnboarded(false);
        setTab("today");
        setQuietMode(true);
        LS.set("sg_gender", null);
        LS.set("sg_spirit_name", "");
        LS.set("sg_onboarded_v1", false);
        setCloudStatus("Choose your spirit to begin.");
      }
      const savedSpiritName =
        hasSavedGarden && typeof progress?.spiritName === "string"
          ? progress.spiritName
          : undefined;
      await ensureFriendProfile(user, savedSpiritName || user.displayName || undefined);
      await loadFriendData(user, savedSpiritName || user.displayName || undefined);
      setShowCloudPrompt(false);
      LS.set("sg_cloud_prompt_dismissed_v1", true);
      if (
        hasSavedGarden &&
        LS.get<boolean>("sg_show_welcome_after_signin_v1", false)
      ) {
        showWelcomeBackOnce();
      } else {
        LS.set("sg_show_welcome_after_signin_v1", false);
      }
      showToast("Signed in with Google.");
    } catch (error: any) {
      console.error(error);
      if (error?.code !== "auth/redirect-started") {
        clearGoogleRedirectPending();
        clearGoogleAuthStorage();
      }
      const message =
        error?.code === "auth/unauthorized-domain"
          ? "Add this domain in Firebase Auth settings."
          : error?.code === "auth/operation-not-allowed"
          ? "Enable Google sign-in in Firebase Authentication."
          : error?.code === "auth/network-request-failed"
          ? "Google sign-in needs a stronger connection. Please try again."
          : error?.code === "auth/web-storage-unsupported"
          ? "This browser is blocking sign-in storage. Allow cookies/site data, then try again."
          : error?.code === "auth/redirect-uri-mismatch"
          ? googleAuthRedirectHelp
          : error?.code === "auth/pwa-popup-blocked"
          ? "Google sign-in could not open inside the installed app. Open Spirit Garden in Safari once, sign in, then reopen the Home Screen app."
          : error?.code === "auth/popup-blocked"
          ? "Google sign-in was blocked. Allow pop-ups for Spirit Garden, then try again."
          : error?.code === "auth/popup-closed-by-user" || error?.code === "auth/cancelled-popup-request"
          ? "Google sign-in was closed before finishing."
          : error?.code === "auth/redirect-started"
          ? "Finish signing in with Google, then return to Spirit Garden."
          : error?.code === "auth/missing-initial-state"
          ? "Google sign-in got stuck from an old redirect. Please try again."
          : String(error?.message || "").includes("redirect_uri_mismatch")
          ? googleAuthRedirectHelp
          : error?.code
          ? `Google sign-in could not finish (${error.code}). Try again.`
          : "Google sign-in could not finish. Try again.";
      setCloudStatus(message);
      if (error?.code !== "auth/redirect-started") {
        LS.set("sg_show_welcome_after_signin_v1", false);
      }
      showToast(message);
    } finally {
      googleSignInInProgressRef.current = false;
      setGoogleSignInBusy(false);
    }
  }

	  async function handleSignOut() {
	    await signOutUser();
	    clearSignedInSession("Signed out", true);
	    showToast("Signed out of Spirit Garden.");
	  }


  function friendDisplayName(user: User) {
    const savedName = LS.get<string>("sg_spirit_name", "").trim();
    const localName = spiritName?.trim();
    const googleName = user.displayName?.trim();
    const emailName = user.email?.split("@")[0]?.replace(/[._-]+/g, " ").trim();
    const bestName = savedName || localName || googleName || emailName || "Spirit Friend";
    return bestName.includes("@") ? "Spirit Friend" : bestName;
  }

  function appFriendDisplayLabel(friend: Partial<SpiritFriend> | null | undefined) {
    const rawName = String(friend?.displayName || "").trim();
    const looksLikeEmail = rawName.includes("@");
    const looksLikeEmailLocal =
      !!friend?.email &&
      rawName.toLowerCase() === friend.email.split("@")[0]?.toLowerCase();
    const readableEmailName = friendlyNameFromEmail(friend?.email);
    const cleaned =
      rawName &&
      !looksLikeEmail &&
      !looksLikeEmailLocal &&
      !isPlaceholderFriendName(rawName)
        ? rawName.replace(/[._-]+/g, " ").trim()
        : readableEmailName || "Spirit Friend";
    return cleaned
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  function friendInviteCode(uid: string) {
    return `SG-${uid.slice(0, 6).toUpperCase()}`;
  }

  function friendFromAcceptedRequest(
    request: SpiritFriendRequest,
    userUid: string
  ): SpiritFriend | null {
    if (request.status !== "accepted" || request.requestType === "accountability")
      return null;
    const isSender = request.fromUid === userUid;
    const friendUid = isSender ? request.toUid : request.fromUid;
    if (!friendUid || friendUid === userUid) return null;
    return {
      id: request.id,
      uid: friendUid,
      displayName: (isSender ? request.toName : request.fromName) || "Spirit Friend",
      email: isSender ? request.toEmail : request.fromEmail,
      gender: isSender ? request.toGender : request.fromGender,
      stageKey: isSender ? request.toStageKey : request.fromStageKey,
    };
  }

  function mergeSpiritFriends(...friendLists: SpiritFriend[][]) {
    const byUid = new Map<string, SpiritFriend>();
    friendLists.flat().forEach((friend) => {
      if (!friend.uid) return;
      byUid.set(friend.uid, { ...byUid.get(friend.uid), ...friend });
    });
    return Array.from(byUid.values()).sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  }

  function spiritStageKeyFromCloudProgress(progress: Record<string, any> | undefined) {
    if (!progress) return undefined;
    if (typeof progress.stageKey === "string") return progress.stageKey;
    const xp = typeof progress.totalXP === "number" ? progress.totalXP : undefined;
    if (xp === undefined) return undefined;
    return (
      [...SPIRIT_STAGES].reverse().find((stageOption) => xp >= stageOption.xpMin)
        ?.key || undefined
    );
  }

  function spiritGenderFromCloudProgress(progress: Record<string, any> | undefined) {
    return normalizeSpiritGender(progress?.gender);
  }

  function spiritNameFromCloudProgress(progress: Record<string, any> | undefined) {
    const chosenName = String(progress?.spiritName || progress?.name || "").trim();
    return chosenName && !chosenName.includes("@") ? chosenName : "";
  }

  async function hydrateSpiritFriendNames(friends: SpiritFriend[]) {
    return Promise.all(
      friends.map(async (friend) => {
        try {
          const [profileSnapshot, userSnapshot] = await Promise.all([
            getDoc(doc(db, "friendProfiles", friend.uid)),
            getDoc(doc(db, "users", friend.uid)),
          ]);
          const profileData = profileSnapshot.exists()
            ? (profileSnapshot.data() as Partial<SpiritFriendProfile>)
            : {};
          const progress = userSnapshot.exists()
            ? ((userSnapshot.data() as Record<string, any>).progress as
                | Record<string, any>
                | undefined)
            : undefined;
          const rawProfileName = String(profileData.displayName || "").trim();
          const profileName =
            rawProfileName &&
            !rawProfileName.includes("@") &&
            !isPlaceholderFriendName(rawProfileName)
              ? rawProfileName
              : "";
          const progressName = spiritNameFromCloudProgress(progress);
          const email = profileData.email || friend.email;
          const friendName =
            String(friend.displayName || "").trim() &&
            !isPlaceholderFriendName(friend.displayName) &&
            !String(friend.displayName || "").includes("@")
              ? friend.displayName
              : "";
          return {
            ...friend,
            displayName: profileName || progressName || friendName || friendlyNameFromEmail(email) || "Spirit Friend",
            email,
            gender:
              normalizeSpiritGender(profileData.gender) ||
              spiritGenderFromCloudProgress(progress) ||
              normalizeSpiritGender(friend.gender),
            stageKey:
              profileData.stageKey || spiritStageKeyFromCloudProgress(progress) || friend.stageKey,
          };
        } catch {
          return friend;
        }
      })
    );
  }

  async function hydrateFriendRequestNames(
    requests: SpiritFriendRequest[],
    userUid: string
  ) {
    return Promise.all(
      requests.map(async (request) => {
        const otherUid =
          request.fromUid === userUid ? request.toUid : request.fromUid;
        if (!otherUid) return request;
        try {
          const [profileSnapshot, userSnapshot] = await Promise.all([
            getDoc(doc(db, "friendProfiles", otherUid)),
            getDoc(doc(db, "users", otherUid)),
          ]);
          const data = profileSnapshot.exists()
            ? (profileSnapshot.data() as Partial<SpiritFriendProfile>)
            : {};
          const progress = userSnapshot.exists()
            ? ((userSnapshot.data() as Record<string, any>).progress as
                | Record<string, any>
                | undefined)
            : undefined;
          const rawChosenName = String(data.displayName || "").trim();
          const progressName = spiritNameFromCloudProgress(progress);
          const fallbackEmail = request.fromUid === otherUid ? request.fromEmail : request.toEmail;
          const chosenName =
            (rawChosenName &&
            !rawChosenName.includes("@") &&
            !isPlaceholderFriendName(rawChosenName)
              ? rawChosenName
              : "") ||
            progressName ||
            friendlyNameFromEmail(fallbackEmail);
          const chosenGender =
            normalizeSpiritGender(data.gender) || spiritGenderFromCloudProgress(progress);
          const chosenStageKey = data.stageKey || spiritStageKeyFromCloudProgress(progress);
          const fromUpdates = {
            ...(chosenName ? { fromName: chosenName } : {}),
            ...(chosenGender ? { fromGender: chosenGender } : {}),
            ...(chosenStageKey ? { fromStageKey: chosenStageKey } : {}),
          };
          const toUpdates = {
            ...(chosenName ? { toName: chosenName } : {}),
            ...(chosenGender ? { toGender: chosenGender } : {}),
            ...(chosenStageKey ? { toStageKey: chosenStageKey } : {}),
          };
          return request.fromUid === otherUid
            ? { ...request, ...fromUpdates }
            : { ...request, ...toUpdates };
        } catch {
          return request;
        }
      })
    );
  }

  function accountabilityPartnerIdsFromRequests(
    requests: SpiritFriendRequest[],
    userUid: string
  ) {
    return Array.from(
      new Set(
        requests
          .filter(
            (request) =>
              request.status === "accepted" &&
              request.requestType === "accountability"
          )
          .map((request) =>
            request.fromUid === userUid ? request.toUid : request.fromUid
          )
          .filter((uid) => uid && uid !== userUid)
      )
    );
  }

  async function ensureFriendProfile(user: User, preferredName?: string) {
    if (!user.email) return null;
    const displayName = (preferredName?.trim() || friendDisplayName(user)).trim() || "Spirit Friend";
    const profile: SpiritFriendProfile = {
      uid: user.uid,
      displayName: displayName.includes("@") ? "Spirit Friend" : displayName,
      email: user.email,
      inviteCode: friendInviteCode(user.uid),
      gender: profileGenderOrFallback(gender),
      stageKey: stage.key || "seed",
    };
    await setDoc(
      doc(db, "friendProfiles", user.uid),
      stripUndefinedForFirestore({
        ...profile,
        emailLower: user.email.toLowerCase(),
        updatedAt: serverTimestamp(),
      }),
      { merge: true }
    );
    setFriendProfile(profile);
    return profile;
  }

  async function loadFriendData(
    userOverride?: User | null,
    preferredName?: string
  ) {
    const user = userOverride || cloudUser;
    if (!user?.email) return;
    setFriendStatus("Loading friends...");
    const profile = await ensureFriendProfile(user, preferredName);
    if (!profile) return;
    const toIsoDate = (value: any) =>
      value && typeof value.toDate === "function"
        ? value.toDate().toISOString()
        : typeof value === "string"
        ? value
        : undefined;

    const incomingSnapshot = await getDocs(
      query(
        collection(db, "friendRequests"),
        where("toUid", "==", user.uid),
        where("status", "==", "pending")
      )
    );
    const outgoingSnapshot = await getDocs(
      query(
        collection(db, "friendRequests"),
        where("fromUid", "==", user.uid),
        where("status", "==", "pending")
      )
    );
    const acceptedAccountabilityFromSnapshot = await getDocs(
      query(
        collection(db, "friendRequests"),
        where("fromUid", "==", user.uid),
        where("status", "==", "accepted")
      )
    );
    const acceptedAccountabilityToSnapshot = await getDocs(
      query(
        collection(db, "friendRequests"),
        where("toUid", "==", user.uid),
        where("status", "==", "accepted")
      )
    );
    const friendshipSnapshot = await getDocs(
      query(
        collection(db, "friendships"),
        where("participantUids", "array-contains", user.uid)
      )
    );
    const sharedSnapshot = await getDocs(
      query(
        collection(db, "sharedPrayers"),
        where("friendUids", "array-contains", user.uid)
      )
    );
    const signalSnapshot = await getDocs(
      query(collection(db, "friendSignals"), where("toUid", "==", user.uid))
    );

    setIncomingFriendRequests(
      await hydrateFriendRequestNames(incomingSnapshot.docs.map((snap) => ({
        id: snap.id,
        ...(snap.data() as Omit<SpiritFriendRequest, "id">),
      })), user.uid)
    );
    setOutgoingFriendRequests(
      await hydrateFriendRequestNames(outgoingSnapshot.docs.map((snap) => ({
        id: snap.id,
        ...(snap.data() as Omit<SpiritFriendRequest, "id">),
      })), user.uid)
    );
    const acceptedAccountabilityRequests = [
      ...acceptedAccountabilityFromSnapshot.docs,
      ...acceptedAccountabilityToSnapshot.docs,
    ]
      .map((snap) => ({
        id: snap.id,
        ...(snap.data() as Omit<SpiritFriendRequest, "id">),
      }))
      .filter((request) => request.status === "accepted");
    const acceptedPartnerIds = accountabilityPartnerIdsFromRequests(
      acceptedAccountabilityRequests,
      user.uid
    );
    setAccountabilityPartnerUids(acceptedPartnerIds);
    setAccountabilityPartnerUid(acceptedPartnerIds[0] || "");
    const friendshipFriends = friendshipSnapshot.docs
      .map((snap) => {
        const data = snap.data() as Record<string, any>;
        const otherUid = (data.participantUids || []).find(
          (uid: string) => uid !== user.uid
        );
        if (!otherUid) return null;
        const friend: SpiritFriend = {
          id: snap.id,
          uid: otherUid,
          displayName:
            data.participantNames?.[otherUid] ||
            data.participantEmails?.[otherUid]?.split("@")[0] ||
            "Spirit Friend",
          email: data.participantEmails?.[otherUid],
        };
        return friend;
      })
      .filter((friend): friend is SpiritFriend => Boolean(friend));
    const acceptedRequestFriends = acceptedAccountabilityRequests
      .map((request) => friendFromAcceptedRequest(request, user.uid))
      .filter((friend): friend is SpiritFriend => Boolean(friend));
    setSpiritFriends(
      await hydrateSpiritFriendNames(
        mergeSpiritFriends(friendshipFriends, acceptedRequestFriends)
      )
    );
    setSharedPrayers(
      sharedSnapshot.docs.map((snap) => ({
        id: snap.id,
        ...(snap.data() as Omit<SharedPrayer, "id">),
        createdAt: toIsoDate(snap.data().createdAt),
      }))
    );
    setReceivedPrayerSignals(
      signalSnapshot.docs
        .map((snap) => ({
          id: snap.id,
          ...(snap.data() as Omit<PrayerSignal, "id">),
          createdAt: toIsoDate(snap.data().createdAt),
        }))
        .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))
    );
    setFriendStatus("Friends loaded");
  }

  async function sendFriendRequest(identifier: string) {
    try {
      const user = auth.currentUser;
      if (!user?.email || user.isAnonymous) {
        showToast("Sign in with Google to add prayer friends.");
        await handleGoogleSignIn();
        return;
      }
      setFriendStatus("Finding friend...");
      const cleaned = identifier.trim();
      const emailLookup = cleaned.toLowerCase();
      const inviteLookup = cleaned.toUpperCase();
      let targetSnapshot = await getDocs(
        query(
          collection(db, "friendProfiles"),
          where("emailLower", "==", emailLookup)
        )
      );
      if (targetSnapshot.empty) {
        targetSnapshot = await getDocs(
          query(
            collection(db, "friendProfiles"),
            where("inviteCode", "==", inviteLookup)
          )
        );
      }
      const targetDoc = targetSnapshot.docs[0];
      if (!targetDoc) {
        setFriendStatus("No friend found yet");
        showToast("No friend found with that email or invite code.");
        return;
      }
      const target = targetDoc.data() as SpiritFriendProfile;
      if (target.uid === user.uid) {
        setFriendStatus("That is your own invite code");
        showToast("That is your own account.");
        return;
      }
      const profile = await ensureFriendProfile(user);
      if (!profile) return;
      const requestId = `${user.uid}_${target.uid}`;
      const pendingRequest: SpiritFriendRequest = {
        id: requestId,
        fromUid: user.uid,
        toUid: target.uid,
        fromName: profile.displayName,
        toName: target.displayName || target.email?.split("@")[0] || "Spirit Friend",
        fromEmail: profile.email,
        toEmail: target.email || "",
        fromGender: profile.gender,
        toGender: target.gender,
        fromStageKey: profile.stageKey,
        toStageKey: target.stageKey,
        status: "pending",
        requestType: "friend",
      };
      await setDoc(
        doc(db, "friendRequests", requestId),
        stripUndefinedForFirestore({
          ...pendingRequest,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
      setOutgoingFriendRequests((prev) =>
        prev.some((request) => request.id === requestId)
          ? prev.map((request) =>
              request.id === requestId ? { ...request, ...pendingRequest } : request
            )
          : [pendingRequest, ...prev]
      );
      await queuePushNotification({
        toUid: target.uid,
        kind: "friend_request",
        data: { requestId, fromName: profile.displayName || "Spirit friend" },
      });
      spiritHaptic("light");
      setFriendStatus(
        "Request sent. They’ll see it when they open Spirit Garden."
      );
      showToast("Request sent. They’ll see it when they open Spirit Garden.");
      loadFriendData(user).catch((error) => {
        console.error("Friend request sent, but friends could not refresh", error);
        setFriendStatus("Request sent. Refresh to update friends.");
      });
    } catch (error) {
      console.error(error);
      setFriendStatus("Could not send request");
      showToast("Could not send friend request yet.");
    }
  }

  async function respondFriendRequest(
    request: SpiritFriendRequest,
    accept: boolean
  ) {
    try {
      const user = cloudUser;
      if (!user?.email) return;
      await updateDoc(doc(db, "friendRequests", request.id), {
        status: accept ? "accepted" : "declined",
        respondedAt: serverTimestamp(),
      });
      if (accept) {
        const myProfile = await ensureFriendProfile(user);
        if (!myProfile) return;
        const participantUids = [request.fromUid, request.toUid].sort();
        const friendshipId = participantUids.join("_");
        await setDoc(
          doc(db, "friendships", friendshipId),
          stripUndefinedForFirestore({
            participantUids,
            participantNames: {
              [request.fromUid]: request.fromName,
              [request.toUid]: myProfile.displayName || request.toName,
            },
            participantEmails: {
              [request.fromUid]: request.fromEmail || "",
              [request.toUid]: myProfile.email,
            },
            createdAt: serverTimestamp(),
          }),
          { merge: true }
        );
      }
      spiritHaptic(accept ? "medium" : "light");
      setFriendStatus(accept ? "Friend added" : "Request declined");
      showToast(accept ? "Spirit friend added." : "Friend request declined.");
      await loadFriendData(user);
    } catch (error) {
      console.error(error);
      setFriendStatus("Could not update request");
      showToast("Could not update friend request yet.");
    }
  }

  async function cancelFriendRequest(request: SpiritFriendRequest) {
    try {
      const user = cloudUser;
      if (!user?.email) return;
      await updateDoc(doc(db, "friendRequests", request.id), {
        status: "cancelled",
        cancelledAt: serverTimestamp(),
      });
      setOutgoingFriendRequests((prev) =>
        prev.filter((pending) => pending.id !== request.id)
      );
      const isAccountabilityRequest =
        request.requestType === "accountability";
      spiritHaptic("warning");
      setFriendStatus(
        isAccountabilityRequest
          ? "Accountability request cancelled"
          : "Friend request cancelled"
      );
      showToast(
        isAccountabilityRequest
          ? "Accountability request cancelled."
          : "Friend request cancelled."
      );
    } catch (error) {
      console.error(error);
      setFriendStatus("Could not cancel request");
      showToast("Could not cancel request yet.");
    }
  }

  async function sendAccountabilityRequest(friend: SpiritFriend) {
    try {
      const user = auth.currentUser;
      if (!user?.email || user.isAnonymous) {
        showToast("Sign in with Google to choose an accountability partner.");
        await handleGoogleSignIn();
        return;
      }
      if (!friend?.uid || friend.uid === user.uid) {
        showToast("Choose a friend first.");
        return;
      }

      setFriendStatus("Sending partner request...");
      const profile = await ensureFriendProfile(user);
      if (!profile) {
        throw new Error("Your friend profile is still loading. Please try again in a moment.");
      }

      const friendName = appFriendDisplayLabel(friend);
      const pairKey = [user.uid, friend.uid].sort().join("_");
      const requestId = `accountability_${pairKey}`;
      const requestRef = doc(db, "friendRequests", requestId);

      let existingRequest: (Partial<SpiritFriendRequest> & { id: string }) | null = null;
      try {
        const existingSnapshot = await getDoc(requestRef);
        if (existingSnapshot.exists()) {
          existingRequest = {
            id: existingSnapshot.id,
            ...(existingSnapshot.data() as Partial<SpiritFriendRequest>),
          };
        }
      } catch (lookupError) {
        console.error("Could not check stable partner request", lookupError);
      }

      if (
        existingRequest?.requestType === "accountability" &&
        existingRequest.status === "accepted"
      ) {
        const message = `${friendName} is already one of your accountability partners.`;
        setFriendStatus("Already accountability partners");
        setPartnerRequestNotice({
          title: "Already partners",
          message,
          tone: "info",
        });
        showToast(message);
        return;
      }

      if (
        existingRequest?.requestType === "accountability" &&
        existingRequest.status === "pending"
      ) {
        const incoming = existingRequest.fromUid === friend.uid;
        const message = incoming
          ? `${friendName} already sent you a partner request. Open your requests to accept it.`
          : `Your partner request to ${friendName} is still waiting for acceptance.`;
        setFriendStatus(incoming ? "Partner request waiting" : "Partner request already sent");
        setPartnerRequestNotice({
          title: incoming ? "Request waiting" : "Request already sent",
          message,
          tone: "info",
        });
        showToast(incoming ? "You already have a request from them." : "Partner request already sent.");
        return;
      }

      const pendingRequest: SpiritFriendRequest = {
        id: requestId,
        fromUid: user.uid,
        toUid: friend.uid,
        fromName: profile.displayName || friendDisplayName(user),
        toName: friend.displayName || friendName,
        fromEmail: profile.email || user.email,
        toEmail: friend.email || "",
        fromGender: profile.gender || profileGenderOrFallback(gender),
        toGender: normalizeSpiritGender(friend.gender) || "female",
        fromStageKey: profile.stageKey || stage.key || "seed",
        toStageKey: friend.stageKey || "baby",
        status: "pending",
        requestType: "accountability",
      };

      const partnerRequestPayload = stripUndefinedForFirestore({
        fromUid: pendingRequest.fromUid,
        toUid: pendingRequest.toUid,
        fromName: pendingRequest.fromName,
        toName: pendingRequest.toName,
        fromEmail: pendingRequest.fromEmail,
        toEmail: pendingRequest.toEmail,
        fromGender: pendingRequest.fromGender,
        toGender: pendingRequest.toGender,
        fromStageKey: pendingRequest.fromStageKey,
        toStageKey: pendingRequest.toStageKey,
        status: "pending",
        requestType: "accountability",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await setDoc(requestRef, partnerRequestPayload, { merge: true });
      await queuePushNotification({
        toUid: friend.uid,
        kind: "partner_request",
        data: {
          requestId,
          fromName: profile.displayName || friendDisplayName(user),
        },
      });

      setOutgoingFriendRequests((prev) => {
        const withoutSame = prev.filter((request) => request.id !== requestId);
        return [pendingRequest, ...withoutSame];
      });
      spiritHaptic("light");
      setFriendStatus("Accountability partner request sent.");
      setPartnerRequestNotice({
        title: "Partner request sent",
        message: `${friendName} will see your request when they open Spirit Garden.`,
        tone: "success",
      });
      showToast(`Partner request sent to ${friendName}.`);
      loadFriendData(user).catch((error) => {
        console.error("Partner request sent, but friends could not refresh", error);
        setFriendStatus("Partner request sent. Refresh to update friends.");
      });
    } catch (error: any) {
      console.error("sendAccountabilityRequest failed", error);
      const rawMessage = String(error?.message || "").trim();
      const message =
        error?.code === "permission-denied"
          ? "Partner request was blocked by Firestore rules. Deploy the latest rules, then try again."
          : error?.code
          ? `Could not send partner request yet (${error.code}). ${rawMessage}`.trim()
          : rawMessage || "Could not send partner request yet.";
      setFriendStatus(message);
      setPartnerRequestNotice({
        title: "Could not send request",
        message,
        tone: "error",
      });
      showToast(message);
    }
  }

  async function respondAccountabilityRequest(
    request: SpiritFriendRequest,
    accept: boolean
  ) {
    try {
      const user = cloudUser;
      if (!user?.email) return;
      await updateDoc(doc(db, "friendRequests", request.id), {
        status: accept ? "accepted" : "declined",
        respondedAt: serverTimestamp(),
      });
      if (accept) {
        const partnerUid =
          request.fromUid === user.uid ? request.toUid : request.fromUid;
        setAccountabilityPartnerUid(partnerUid);
        setAccountabilityPartnerUids((prev) =>
          prev.includes(partnerUid) ? prev : [...prev, partnerUid]
        );
      }
      spiritHaptic(accept ? "medium" : "light");
      setFriendStatus(
        accept
          ? "Accountability partner accepted"
          : "Accountability request declined"
      );
      showToast(
        accept
          ? "Accountability partner accepted."
          : "Accountability request declined."
      );
      await loadFriendData(user);
    } catch (error) {
      console.error(error);
      setFriendStatus("Could not update partner request");
      showToast("Could not update accountability request yet.");
    }
  }

  async function removeAccountabilityPartner(friend: SpiritFriend) {
    try {
      const user = cloudUser;
      if (!user?.email) return;
      const requestIds = [
        `accountability_${user.uid}_${friend.uid}`,
        `accountability_${friend.uid}_${user.uid}`,
      ];
      await Promise.allSettled(
        requestIds.map((requestId) =>
          updateDoc(doc(db, "friendRequests", requestId), {
            status: "removed",
            removedAt: serverTimestamp(),
          })
        )
      );
      setAccountabilityPartnerUids((prev) =>
        prev.filter((uid) => uid !== friend.uid)
      );
      setAccountabilityPartnerUid((prev) =>
        prev === friend.uid
          ? accountabilityPartnerUids.find((uid) => uid !== friend.uid) || ""
          : prev
      );
      setFriendStatus("Accountability partner removed.");
      showToast(`${friend.displayName} removed as accountability partner.`);
      await loadFriendData(user);
    } catch (error) {
      console.error(error);
      setFriendStatus("Could not remove partner");
      showToast("Could not remove accountability partner yet.");
    }
  }

  async function deleteSpiritFriend(friend: SpiritFriend) {
    try {
      const user = cloudUser;
      if (!user?.email || !friend?.uid) return;
      const participantUids = [user.uid, friend.uid].sort();
      const friendshipId = participantUids.join("_");
      const friendshipDocIds = Array.from(
        new Set([friend.id, friendshipId].filter(Boolean))
      );
      const [sentRequests, receivedRequests] = await Promise.all([
        getDocs(
          query(
            collection(db, "friendRequests"),
            where("fromUid", "==", user.uid),
            where("toUid", "==", friend.uid)
          )
        ),
        getDocs(
          query(
            collection(db, "friendRequests"),
            where("fromUid", "==", friend.uid),
            where("toUid", "==", user.uid)
          )
        ),
      ]);
      const relatedRequestRefs = [
        ...sentRequests.docs.map((snap) => snap.ref),
        ...receivedRequests.docs.map((snap) => snap.ref),
      ];
      await Promise.allSettled([
        ...friendshipDocIds.map((docId) =>
          deleteDoc(doc(db, "friendships", docId))
        ),
        ...relatedRequestRefs.map((requestRef) =>
          updateDoc(requestRef, {
            status: "removed",
            removedAt: serverTimestamp(),
          })
        ),
      ]);
      setSpiritFriends((prev) => prev.filter((item) => item.uid !== friend.uid));
      setIncomingFriendRequests((prev) =>
        prev.filter(
          (request) =>
            request.fromUid !== friend.uid && request.toUid !== friend.uid
        )
      );
      setOutgoingFriendRequests((prev) =>
        prev.filter(
          (request) =>
            request.fromUid !== friend.uid && request.toUid !== friend.uid
        )
      );
      setAccountabilityPartnerUids((prev) =>
        prev.filter((uid) => uid !== friend.uid)
      );
      setAccountabilityPartnerUid((prev) =>
        prev === friend.uid
          ? accountabilityPartnerUids.find((uid) => uid !== friend.uid) || ""
          : prev
      );
      spiritHaptic("warning");
      setFriendStatus("Friend removed.");
      showToast(`${appFriendDisplayLabel(friend)} removed from Spirit Friends.`);
      await loadFriendData(user);
    } catch (error) {
      console.error(error);
      setFriendStatus("Could not remove friend");
      showToast("Could not remove friend yet.");
    }
  }

  async function sendPrayedForFriend(friend: SpiritFriend) {
    try {
      const user = cloudUser;
      if (!user?.email) return;
      const profile = await ensureFriendProfile(user);
      const prayerSignalRef = await addDoc(collection(db, "friendSignals"), {
        fromUid: user.uid,
        toUid: friend.uid,
        fromName: profile?.displayName || friendDisplayName(user),
        toName: friend.displayName,
        type: "prayed",
        createdAt: serverTimestamp(),
      });
      await queuePushNotification({
        toUid: friend.uid,
        kind: "prayer_note",
        data: {
          requestId: prayerSignalRef.id,
          fromName: profile?.displayName || friendDisplayName(user),
        },
      });
      spiritHaptic("light");
      showToast(`You let ${friend.displayName} know you prayed.`);
    } catch (error) {
      console.error(error);
      showToast("Could not send prayer note yet.");
    }
  }

  async function sendBlessingToFriend(
    friend: SpiritFriend,
    blessing: string,
    message?: string
  ) {
    try {
      const user = cloudUser;
      if (!user?.email) return;
      const profile = await ensureFriendProfile(user);
      const blessingSignalRef = await addDoc(collection(db, "friendSignals"), {
        fromUid: user.uid,
        toUid: friend.uid,
        fromName: profile?.displayName || friendDisplayName(user),
        toName: friend.displayName,
        type: "blessing",
        blessingLabel: blessing,
        message: message || "",
        createdAt: serverTimestamp(),
      });
      await queuePushNotification({
        toUid: friend.uid,
        kind: "blessing",
        data: {
          requestId: blessingSignalRef.id,
          fromName: profile?.displayName || friendDisplayName(user),
          blessingLabel: message || blessing || "Open your Blessings Inbox to read it.",
        },
      });
      spiritHaptic("light");
      showToast(`Blessing sent to ${friend.displayName}.`);
    } catch (error) {
      console.error(error);
      showToast("Could not send blessing yet.");
    }
  }

  async function completeAccountabilityFocus(
    friend: SpiritFriend,
    focus: string
  ) {
    try {
      const user = cloudUser;
      if (!user?.email) return;
      const profile = await ensureFriendProfile(user);
      await addDoc(collection(db, "friendSignals"), {
        fromUid: user.uid,
        toUid: friend.uid,
        fromName: profile?.displayName || friendDisplayName(user),
        toName: friend.displayName,
        type: "accountability",
        message: `Completed our shared focus: ${focus}`,
        createdAt: serverTimestamp(),
      });
      await queuePushNotification({
        toUid: friend.uid,
        kind: "partner_focus",
        data: {
          focus,
          focusId: `${friend.uid}_${today}`,
          fromName: profile?.displayName || friendDisplayName(user),
        },
      });
      spiritHaptic("medium");
      showToast(`Shared focus completed with ${friend.displayName}.`);
    } catch (error) {
      console.error(error);
      showToast("Could not notify your partner yet.");
    }
  }

  async function updateSharedPrayerCopies(prayer: PrayerFriend) {
    try {
      const user = cloudUser;
      if (!user?.email) return;
      const ownerSharedSnapshot = await getDocs(
        query(collection(db, "sharedPrayers"), where("ownerUid", "==", user.uid))
      );
      const matchingDocs = ownerSharedSnapshot.docs.filter(
        (snap) => snap.data().prayerId === prayer.id
      );
      await Promise.all(
        matchingDocs.map((snap) =>
          updateDoc(doc(db, "sharedPrayers", snap.id), {
            request: prayer.request,
            prayerKind: prayer.kind,
            prayerName: prayer.name || "",
            updatedAt: serverTimestamp(),
          })
        )
      );
    } catch (error) {
      console.error(error);
      showToast("Prayer updated, but shared notes could not sync yet.");
    }
  }

  async function sharePrayerWithFriends(
    prayer: PrayerFriend,
    friendUids: string[]
  ) {
    try {
      const user = cloudUser;
      if (!user?.email || friendUids.length === 0) return;
      const profile = await ensureFriendProfile(user);
      const ownerSharedSnapshot = await getDocs(
        query(collection(db, "sharedPrayers"), where("ownerUid", "==", user.uid))
      );
      const matchingDocs = ownerSharedSnapshot.docs.filter(
        (snap) => snap.data().prayerId === prayer.id
      );
      if (matchingDocs.length > 0) {
        await Promise.all(
          matchingDocs.map((snap) => {
            const existing = snap.data() as SharedPrayer;
            const mergedFriendUids = Array.from(
              new Set([...(existing.friendUids || []), ...friendUids])
            );
            return updateDoc(doc(db, "sharedPrayers", snap.id), {
              ownerName: profile?.displayName || friendDisplayName(user),
              request: prayer.request,
              prayerKind: prayer.kind,
              prayerName: prayer.name || "",
              friendUids: mergedFriendUids,
              updatedAt: serverTimestamp(),
            });
          })
        );
        await Promise.all(
          friendUids.map((uid) =>
            queuePushNotification({
              toUid: uid,
              kind: "shared_prayer",
              data: { prayerId: prayer.id },
            })
          )
        );
        spiritHaptic("light");
        showToast("Shared prayer updated for selected friends.");
        await loadFriendData(user);
        return;
      }
      await addDoc(collection(db, "sharedPrayers"), {
        prayerId: prayer.id,
        ownerUid: user.uid,
        ownerName: profile?.displayName || friendDisplayName(user),
        request: prayer.request,
        prayerKind: prayer.kind,
        prayerName: prayer.name || "",
        friendUids,
        createdAt: serverTimestamp(),
      });
      await Promise.all(
        friendUids.map((uid) =>
          queuePushNotification({
            toUid: uid,
            kind: "shared_prayer",
            data: { prayerId: prayer.id },
          })
        )
      );
      spiritHaptic("light");
      showToast("Prayer shared privately with selected friends.");
      await loadFriendData(user);
    } catch (error) {
      console.error(error);
      showToast("Could not share prayer yet.");
    }
  }
  const playSound = (
    kind: "earn" | "stone" | "level" | "answered",
    force = false
  ) => {
    if ((!soundOn && !force) || typeof window === "undefined") return;
    void (async () => {
      try {
        const AudioCtor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!AudioCtor) return;
        let ctx = audioCtxRef.current;
        if (!ctx || ctx.state === "closed") {
          ctx = new AudioCtor();
          audioCtxRef.current = ctx;
        }
        if (ctx.state === "suspended") await ctx.resume().catch(() => {});
        const startAt = ctx.currentTime + 0.01;
        const master = ctx.createGain();
        master.gain.setValueAtTime(0.0001, startAt);
        master.gain.exponentialRampToValueAtTime(
          kind === "stone" ? 0.13 : 0.095,
          startAt + 0.025
        );
        master.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.7);
        master.connect(ctx.destination);

        const notes: Record<"earn" | "stone" | "level" | "answered", number[]> = {
          earn: [660, 880],
          stone: [523.25, 659.25, 987.77],
          level: [523.25, 659.25, 783.99, 1046.5],
          answered: [587.33, 739.99, 987.77],
        };
        notes[kind].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const noteStart = startAt + i * 0.08;
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, noteStart);
          gain.gain.setValueAtTime(0.0001, noteStart);
          gain.gain.exponentialRampToValueAtTime(0.48, noteStart + 0.022);
          gain.gain.exponentialRampToValueAtTime(0.0001, noteStart + 0.34);
          osc.connect(gain);
          gain.connect(master);
          osc.start(noteStart);
          osc.stop(noteStart + 0.38);
        });
        window.setTimeout(() => master.disconnect(), 900);
      } catch {}
    })();
  };
  const spawnSpark = (e: ButtonEvent) => {
    const r = e.currentTarget.getBoundingClientRect();
    setSparks((s) => [
      ...s,
      {
        id: Date.now() + Math.random(),
        x: r.left + r.width / 2,
        y: r.top + r.height / 2,
      },
    ]);
  };
  const gainR = (amt: number, label?: string) => {
    setRighteousness((r) => r + amt);
    playSound("earn");
    showToast(`+${amt} ${label || "righteousness earned"} ✨`);
  };
  const awardStone = (stone: TestimonyStone) => {
    const alreadyOwned = testimonyStones.some(
      (existing) =>
        existing.id === stone.id ||
        (existing.kind === stone.kind && stone.kind !== "white")
    );
    if (alreadyOwned) return;
    setTestimonyStones((prev) => {
      if (
        prev.some(
          (existing) =>
            existing.id === stone.id ||
            (existing.kind === stone.kind && stone.kind !== "white")
        )
      )
        return prev;
      return [stone, ...prev];
    });
    playSound("stone");
    setStoneQueue((prev) => [...prev, stone]);
    showToast(`${stone.name} added to your heavenly stones ${stone.icon}`);
  };
  const addSpiritVisitor = (
    kind: SpiritVisitorKind,
    reason: string,
    options: { force?: boolean; minutes?: number } = {}
  ) => {
    const now = Date.now();
    const active = activeSpiritVisitors(spiritVisitors, now);
    const alreadyHere = active.some((visitor) => visitor.kind === kind);
    const chance =
      kind === "golden_dove" ? 1 : kind === "deer" || kind === "bluebird" ? 0.62 : 0.38;
    if (!options.force && (alreadyHere || active.length >= 3 || Math.random() > chance))
      return;
    const minutes =
      options.minutes ?? (kind === "golden_dove" || kind === "deer" ? 30 : 18);
    const visitor: SpiritVisitor = {
      id: `sv_${kind}_${now}_${Math.round(Math.random() * 1000)}`,
      kind,
      reason,
      createdAt: now,
      expiresAt: now + minutes * 60 * 1000,
    };
    setSpiritVisitors((prev) =>
      activeSpiritVisitors([visitor, ...prev], now).slice(0, 3)
    );
    const copy = SPIRIT_VISITOR_COPY[kind];
    showToast(`${copy.article} ${copy.name} visited. ${reason}`);
  };

  const stagePreviewXP = totalXP;
  const stage = useMemo(
    () =>
      [...SPIRIT_STAGES].reverse().find((s) => stagePreviewXP >= s.xpMin) ||
      SPIRIT_STAGES[0],
    [stagePreviewXP]
  );
  const xpInStage = stagePreviewXP - stage.xpMin;
  const stageRange = stage.xpMax === Infinity ? 500 : stage.xpMax - stage.xpMin;
  const stageIndex = SPIRIT_STAGES.findIndex((s) => s.key === stage.key);
  const nextStage =
    stageIndex >= 0 && stageIndex < SPIRIT_STAGES.length - 1
      ? SPIRIT_STAGES[stageIndex + 1]
      : null;
  const xpToNext = nextStage ? Math.max(0, nextStage.xpMin - stagePreviewXP) : 0;
  const stageProgressPercent = Math.min(100, Math.max(0, (xpInStage / stageRange) * 100));
  const babySpiritXp =
    SPIRIT_STAGES.find((s) => s.key === "baby")?.xpMin ?? 200;
  const babySpiritUnlocked = totalXP >= babySpiritXp;
  const dailyPrayerFocus = useMemo(() => {
    const seed = today
      .split("-")
      .map(Number)
      .reduce((a, b) => a + b, 0);
    return PRAYER_FOCUS[seed % PRAYER_FOCUS.length];
  }, [today]);
  const todayMood = moodLog[today];
  const appHomeGrowthStage = useMemo(
    () => getHomeGrowthStageForSpiritStage(stage.key),
    [stage.key]
  );
  const testimonyBook = useMemo(() => {
    return prayerFriends
      .filter((prayer) => prayer.answered && prayer.answeredDate)
      .sort((a, b) =>
        (b.answeredDate || "").localeCompare(a.answeredDate || "")
      )
      .slice(0, 30)
      .map((prayer) => ({
        id: `answered_${prayer.id}`,
        prayerId: prayer.id,
        icon: "🌤️",
        title: `Answered prayer${prayer.name ? `: ${prayer.name}` : ""}`,
        detail: prayer.answeredNote || prayer.request,
        date: prayer.answeredDate,
        accent: "#7dd3fc",
      }));
  }, [prayerFriends]);
  const faithJourneyStats = useMemo<FaithJourneyStats>(() => {
    const startDate = earliestJourneyDate({
      activityLog,
      entries,
      prayerFriends,
      lastActive,
      today,
    });
    const previousStage = stageIndex > 0 ? SPIRIT_STAGES[stageIndex - 1] : stage;
    return {
      journeyDay: daysSinceDateKey(startDate, today),
      prayerCount: prayerFriends.length,
      answeredPrayerCount: prayerFriends.filter((prayer) => prayer.answered).length,
      testimonyCount: prayerFriends.filter((prayer) => prayer.publicSubmittedAt).length,
      previousStageName: previousStage.name,
      currentStageName: stage.name,
    };
  }, [activityLog, entries, lastActive, prayerFriends, stage, stageIndex, today]);
  const journalMonthGroups = useMemo(() => groupByMonth(entries), [entries]);
  const journalEntryPreview = (entry: JournalEntry): string => {
    const text = entry.text.trim();
    return text.length > 200 ? text.slice(0, 200).trimEnd() + "…" : text;
  };
  const testimonyMonthGroups = useMemo(
    () => groupByMonth(testimonyBook),
    [testimonyBook]
  );

  const activeTasks = tasks;
  const requiredTasks = activeTasks.filter((task) => !task.optional);
  const visibleTodayTasks = requiredTasks;
  const doneCount = requiredTasks.filter((t) => checked[t.id]).length;
  const progress = requiredTasks.length > 0 ? doneCount / requiredTasks.length : 0;
  const neglectDarkness = useMemo(
    () => computeNeglectDarkness(activityLog),
    [activityLog]
  );
  const prayersSentToday = prayerFriends.some((prayer) =>
    Array.isArray(prayer.prayedDates) && prayer.prayedDates.includes(today)
  );
  const journalSavedToday = entries.some((entry) => entry.date === today);
  const prayerAddedToday = prayerFriends.some(
    (prayer) => prayer.challengeStarted === today
  );
  const bloomAddedToday = homeBlooms.some((bloom) => bloom.date === today);
  const faithFlightStreak = useMemo(
    () => computeWalkBloomStreak(homeBlooms),
    [homeBlooms]
  );
  const faithFlightProgress = Math.min(faithFlightStreak, 7);
  const effectiveFaithFlightUnlocked = faithFlightUnlocked || isAdminUser;
  const answeredPrayerToday = prayerFriends.some(
    (prayer) => prayer.answered && prayer.answeredDate === today
  );
  const testimonySharedToday = prayerFriends.some(
    (prayer) => prayer.publicSubmittedAt === today
  );
  const prayedInHomeToday = prayersSentToday || prayerAddedToday;
  const homeCareState: HomeCareState = testimonySharedToday
    ? {
        mood: "radiant",
        title: "A testimony blessing is glowing.",
        message: "Sharing a testimony brought soft golden light near your home.",
        completedWalk: dayDone,
        prayedToday: prayedInHomeToday,
        journaledToday: journalSavedToday,
        answeredToday: answeredPrayerToday,
        testimonySharedToday: true,
      }
    : answeredPrayerToday
    ? {
        mood: "radiant",
        title: "A testimony is shining.",
        message: "An answered prayer brought peace home.",
        completedWalk: dayDone,
        prayedToday: prayedInHomeToday,
        journaledToday: journalSavedToday,
        answeredToday: true,
        testimonySharedToday: false,
      }
    : journalSavedToday
    ? {
        mood: "blooming",
        title: "Your home is growing.",
        message: "Your reflection brought new life home.",
        completedWalk: dayDone,
        prayedToday: prayedInHomeToday,
        journaledToday: true,
        answeredToday: false,
        testimonySharedToday: false,
      }
    : prayedInHomeToday
    ? {
        mood: "peaceful",
        title: "Prayer brought peace home.",
        message: "Your home feels still and cared for.",
        completedWalk: dayDone,
        prayedToday: true,
        journaledToday: false,
        answeredToday: false,
        testimonySharedToday: false,
      }
    : dayDone
    ? {
        mood: neglectDarkness > 0 ? "restoring" : "glowing",
        title: bloomAddedToday
          ? "Your home grew brighter today."
          : neglectDarkness > 0
          ? "Your home is healing."
          : "Your home is brightening.",
        message: bloomAddedToday
          ? "Today’s Walk helped your home grow."
          : "Today’s Walk helped your home shine.",
        completedWalk: true,
        prayedToday: false,
        journaledToday: false,
        answeredToday: false,
        testimonySharedToday: false,
      }
    : {
        mood: neglectDarkness > 0 ? "restoring" : "peaceful",
        title: "Your home is waiting gently.",
        message: "Tend it with one faithful step today.",
        completedWalk: false,
        prayedToday: false,
        journaledToday: false,
        answeredToday: false,
        testimonySharedToday: false,
      };
  homeCareState.blessedToday = homeBlessedDate === today;
  if (homeCareState.blessedToday && !homeCareState.answeredToday && !homeCareState.testimonySharedToday) {
    homeCareState.mood = "glowing";
  }
  useEffect(() => {
    if (faithFlightUnlocked || faithFlightProgress < 7) return;
    setFaithFlightUnlocked(true);
    showToast("Faith Flight unlocked. Your spirit can fly now.");
  }, [faithFlightProgress, faithFlightUnlocked]);
  const prayerVesselsStarted = prayerFriends.length > 0 || prayersSentToday;
  const homeStarted =
    homeVisited ||
    ownedHomeItems.length > 0 ||
    Object.values(placedHomeItems).some(Boolean);
  const firstFlowOpen = firstDayComplete || dayDone;
  const isTabUnlocked = (id: TabId): boolean => {
    if (id === "today" || id === "quests") return true;
    if (id === "prayers") return true;
    if (id === "friends") return true;
    if (id === "wall") return true;
    if (id === "home" || id === "journal") return true;
    if (id === "shop")
      return (
        babySpiritUnlocked ||
        closetVisited ||
        ownedItems.length > 0 ||
        equippedItems.length > 0
      );
    if (id === "growth") return true;
    const lock = FEATURE_UNLOCKS[id];
    if (!lock) return dayDone;
    if (!dayDone) return false;
    if (totalXP >= lock.xp) return true;
    return false;
  };
  const visibleTabs = TABS.filter(([id]) => {
    if (quietMode)
      return (
        id === "today" ||
        id === "prayers" ||
        id === "wall" ||
        id === "growth"
      );
    if (id === "today" || id === "growth") return true;
    if (id === "wall") return isTabUnlocked("wall");
    if (id === "prayers") return true;
    if (id === "home") return isTabUnlocked("home");
    if (id === "shop") return isTabUnlocked("shop");
    if (id === "journal") return isTabUnlocked("journal");
    return false;
  });
  useEffect(() => {
    if (
      quietMode &&
      tab !== "today" &&
      tab !== "prayers" &&
      tab !== "friends" &&
      tab !== "wall" &&
      tab !== "journal" &&
      tab !== "home" &&
      tab !== "growth"
    ) {
      setTab("today");
    }
  }, [quietMode, tab]);
  const showHeaderControls = true;
  const showFriendHeaderButton = true;
  const pendingFriendRequestCount = incomingFriendRequests.filter(
    (request) => request.status === "pending"
  ).length;
  const friendHeaderNoteIds = [
    ...receivedPrayerSignals.map((signal) => `signal:${signal.id}`),
    ...sharedPrayers.map((shared) => `shared:${shared.id}`),
  ];
  const friendHeaderSeenKey = `sg_seen_prayer_notes_${
    cloudUser?.uid || "guest"
  }`;
  const friendHeaderSeenIds = LS.get<string[]>(friendHeaderSeenKey, []);
  const unreadFriendHeaderCount = friendHeaderNoteIds.filter(
    (id) => !friendHeaderSeenIds.includes(id)
  ).length;
  const hasFriendHeaderNotice =
    pendingFriendRequestCount > 0 || unreadFriendHeaderCount > 0;
  const hasPrayerNotice = unreadFriendHeaderCount > 0;
  const homeNoticeKey = `sg_seen_home_visitors_${cloudUser?.uid || "guest"}`;
  const activeHomeVisitorIds = activeSpiritVisitors(spiritVisitors).map(
    (visitor) => visitor.id
  );
  const seenHomeVisitorIds = LS.get<string[]>(homeNoticeKey, []);
  const hasHomeNotice = !isAdminUser && activeHomeVisitorIds.some(
    (id) => !seenHomeVisitorIds.includes(id)
  );
  const testimonyNoticeKey = `sg_seen_testimony_count_${cloudUser?.uid || "guest"}`;
  const testimonyNoticeTotal = Math.max(1, approvedTestimonyCount);
  const hasTestimonyNotice = !isAdminUser && seenTestimonyCount < testimonyNoticeTotal;
  const openArmorCloset = () => {
    if (!babySpiritUnlocked && !isAdminUser) {
      showToast(`Armor Closet unlocks at Baby Spirit (${babySpiritXp} XP).`);
      return;
    }
    if (quietMode) setQuietMode(false);
    setShowCloset(true);
  };
  const openArmorShop = () => {
    if (!babySpiritUnlocked && !isAdminUser) {
      showToast(`Armor Shop unlocks at Baby Spirit (${babySpiritXp} XP).`);
      return;
    }
    if (quietMode) setQuietMode(false);
    setTab("shop");
  };
  const nextTask = requiredTasks.find((t) => !checked[t.id]);
  const nextStep = nextTask
    ? {
        title: `Next: ${nextTask.label}`,
        desc: "One faithful step for today's walk.",
        action: "Continue Today",
        tab: "today" as TabId,
        targetId: "daily-walk",
      }
    : !dayDone
    ? {
        title: "Complete today",
        desc: "Finish your walk and let your spirit shine.",
        action: "Complete Day",
        tab: "today" as TabId,
        targetId: "daily-walk",
      }
    : dayDone
    ? {
        title: "Your spirit shines brighter today.",
        desc: "Your walk will refresh tomorrow.",
        action: isTabUnlocked("home") && prayersSentToday ? "See Home" : "Go to Prayer",
        tab: isTabUnlocked("home") && prayersSentToday ? ("home" as TabId) : ("prayers" as TabId),
        targetId: "",
      }
    : isTabUnlocked("prayers") && !prayersSentToday
    ? {
        title: "Lift one prayer",
        desc: "Pray for yourself or someone else.",
        action: "Open Prayers",
        tab: "prayers" as TabId,
        targetId: "",
      }
    : isTabUnlocked("journal") && !journalSavedToday
    ? {
        title: "Save a reflection",
        desc: "Write how today went with God.",
        action: "Open Journal",
        tab: "journal" as TabId,
        targetId: "",
      }
    : isTabUnlocked("home")
    ? {
        title: "Visit Home",
        desc: "See how today’s light shaped it.",
        action: "Visit Home",
        tab: "home" as TabId,
        targetId: "",
      }
    : {
        title: "Rest for today",
        desc: "Fresh mercy comes tomorrow.",
        action: "Today",
        tab: "today" as TabId,
        targetId: "",
      };
  const friendlyFlowSteps = [
    {
      id: "today" as TabId,
      icon: "🌅",
      label: "Today",
      status: dayDone ? "done" : "start",
      done: dayDone || doneCount > 0 || !!todayMood,
      unlocked: true,
    },
    {
      id: "prayers" as TabId,
      icon: "🙏",
      label: "Prayer",
      status: prayersSentToday ? "lifted" : "next",
      done: prayersSentToday || prayerFriends.length > 0,
      unlocked: isTabUnlocked("prayers"),
    },
    {
      id: "home" as TabId,
      icon: "🏡",
      label: "Home",
      status: homeStarted ? "open" : "soon",
      done: homeStarted,
      unlocked: isTabUnlocked("home"),
    },
    {
      id: "growth" as TabId,
      icon: "🌱",
      label: "Growth",
      status: totalXP > 0 ? "growing" : "preview",
      done: totalXP > 0,
      unlocked: isTabUnlocked("growth"),
    },
  ];

  const goToNextStep = () => {
    if (nextStep.title === "Complete today") {
      completeDay();
      return;
    }
    setTab(nextStep.tab);
    if (nextStep.tab === "prayers" && !nextStep.targetId) {
      window.setTimeout(() => {
        document
          .getElementById("prayer-page-top")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return;
    }
    if (nextStep.targetId) {
      window.setTimeout(() => {
        document
          .getElementById(nextStep.targetId)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
    }
  };
  const goToQuickAction = (nextTab: TabId, targetId = "") => {
    if (!isTabUnlocked(nextTab)) return;
    if (nextTab === "quests") {
      setTab("today");
      setShowTodayQuests(true);
      window.setTimeout(() => {
        document
          .getElementById("today-quests")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }
    setTab(nextTab);
    if (targetId)
      window.setTimeout(() => {
        document
          .getElementById(targetId)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
  };

  const equippedObjs: ClothingItem[] = useMemo(
    () =>
      equippedItems
        .map((id) => CLOTHES.find((c) => c.id === id))
        .filter((c): c is ClothingItem => Boolean(c)),
    [equippedItems]
  );
  const closetOwnedIds = useMemo(
    () => (isAdminUser ? CLOTHES.map((item) => item.id) : ownedItems),
    [isAdminUser, ownedItems]
  );
  const previewClothingItem =
    CLOTHES.find((item) => item.id === previewClothingItemId) || null;
  const previewEquippedObjs: ClothingItem[] = useMemo(() => {
    if (!previewClothingItem) return equippedObjs;
    const filtered = equippedObjs.filter((item) => {
      if (previewClothingItem.slot === "set") return item.slot !== "set";
      return item.slot !== previewClothingItem.slot && item.slot !== "set";
    });
    return [previewClothingItem, ...filtered];
  }, [equippedObjs, previewClothingItem]);
  const dailyQuests = useMemo(() => buildDailyQuests(today), [today]);
  const isDailyQuestDone = (q: QuestItem): boolean => {
    if (q.kind === "verse") return !!doneQuiz[q.idx];
    if (q.kind === "trivia") return !!doneTrivia[q.idx];
    if (q.kind === "samaritan") return !!samaritan[SAMARITAN_ACTS[q.idx].id];
    if (q.kind === "preach") return preachLog.some((e) => e.date === today);
    return false;
  };
  const allDailyQuestsDone = dailyQuests.every(isDailyQuestDone);

  useEffect(() => {
    if (!allDailyQuestsDone || questStoneDates[today]) return;
    spiritHaptic("success");
    const completedDates = { ...questStoneDates, [today]: true };

    if (TESTIMONY_STONE_FAST_MODE) {
      [...QUEST_STONE_KINDS, "answered" as TestimonyStoneKind].forEach(
        (kind) => {
          awardStone(
            stoneForKind(kind, today, "quest reward", `${today}_${kind}`)
          );
        }
      );
      setQuestStoneDates(completedDates);
      return;
    }

    let questStreak = 0;
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      if (completedDates[dateKey(d)]) questStreak++;
      else break;
    }
    const seed = today
      .split("-")
      .map(Number)
      .reduce((a, b) => a * 31 + b, 11);
    const guaranteed = questStreak >= 3;
    const earnedByChance = seed % 100 < 35;
    if (guaranteed || earnedByChance) {
      const kind = QUEST_STONE_KINDS[seed % QUEST_STONE_KINDS.length];
      awardStone(
        stoneForKind(
          kind,
          today,
          guaranteed
            ? `${questStreak}-day quest streak`
            : "all daily quests complete",
          today
        )
      );
    } else {
      showToast("All quests complete. Keep going for a Heavenly Stone ✨");
    }
    setQuestStoneDates(completedDates);
  }, [allDailyQuestsDone, questStoneDates, today, dailyQuests]);

  useEffect(() => {
    const hasWhite = testimonyStones.some((stone) => stone.kind === "white");
    const hasEveryCoreStone =
      QUEST_STONE_KINDS.every((kind) =>
        testimonyStones.some((stone) => stone.kind === kind)
      ) && testimonyStones.some((stone) => stone.kind === "answered");
    if (!hasWhite && hasEveryCoreStone) {
      awardStone(
        stoneForKind(
          "white",
          today,
          "completed the heavenly stone collection",
          "ultimate"
        )
      );
    }
  }, [testimonyStones, today]);

  useEffect(() => {
    if (!lastStageKey) {
      setLastStageKey(stage.key);
      LS.set("sg_last_stage_key_v1", stage.key);
      return;
    }

    const previousStage = SPIRIT_STAGES.find((s) => s.key === lastStageKey);
    const grewIntoNewStage =
      stage.key !== lastStageKey &&
      (!previousStage || stage.xpMin > previousStage.xpMin);

    if (grewIntoNewStage) {
      spiritHaptic("success");
      playSound("level");
      setStageUpModal(stage);
    }

    if (stage.key !== lastStageKey) {
      setLastStageKey(stage.key);
      LS.set("sg_last_stage_key_v1", stage.key);
    }
  }, [stage, lastStageKey]);

  useEffect(() => {
    if (lastHomeStageLevel < 0) {
      setLastHomeStageLevel(appHomeGrowthStage.level);
      LS.set("sg_last_home_stage_level_v1", appHomeGrowthStage.level);
      return;
    }
    if (appHomeGrowthStage.level > lastHomeStageLevel) {
      spiritHaptic("medium");
      setHomeGrowthModal(appHomeGrowthStage);
      setHomeTransforming(true);
      setTimeout(() => setHomeTransforming(false), 4400);
    }
    if (appHomeGrowthStage.level !== lastHomeStageLevel) {
      setLastHomeStageLevel(appHomeGrowthStage.level);
      LS.set("sg_last_home_stage_level_v1", appHomeGrowthStage.level);
    }
  }, [appHomeGrowthStage, lastHomeStageLevel]);

  // Daily walk state follows the local calendar day, not a rolling 24-hour timer.
  useEffect(() => {
    if (activeChecklistDayRef.current === today) return;
    activeChecklistDayRef.current = today;
    setChecked(LS.get<CheckedMap>(`sg_chk_${today}`, {}));
    setDayDone(LS.get<boolean>(`sg_done_${today}`, false));
    setJournal(LS.get<string>(`sg_j_${today}`, ""));
    setJournalSaved(false);
    setShowDaySummary(false);
  }, [today]);

  // Mark today as active whenever the app is open
  useEffect(() => {
    setActivityLog((prev) => {
      if (prev[today]) return prev;
      const next = { ...prev, [today]: true };
      LS.set("sg_activity_v1", next);
      return next;
    });
  }, [today]);

  useEffect(() => {
    if (lastActive !== today) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yKey = dateKey(yesterday);
      if (lastActive !== yKey) {
        if (graceDays > 0) {
          setGraceDays((g) => g - 1);
          showToast("Grace day used — streak preserved 🕊️");
        } else {
          setStreak(0);
          showToast("Streak reset. Begin again with grace. 🌱");
        }
      }
      setLastActive(today);
    }
  }, [today]);

  useEffect(() => LS.set("sg_gender", gender), [gender]);
  useEffect(() => LS.set("sg_spirit_name", spiritName), [spiritName]);
  useEffect(() => LS.set(`sg_chk_${today}`, checked), [checked, today]);
  useEffect(() => LS.set("sg_xp_v3", totalXP), [totalXP]);
  useEffect(() => LS.set("sg_r", righteousness), [righteousness]);
  useEffect(() => LS.set("sg_streak_v3", streak), [streak]);
  useEffect(() => LS.set("sg_grace", graceDays), [graceDays]);
  useEffect(() => LS.set("sg_lastActive", lastActive), [lastActive]);
  useEffect(() => LS.set(`sg_j_${today}`, journal), [journal, today]);
  useEffect(() => LS.set("sg_jentries_v3", entries), [entries]);
  useEffect(() => LS.set("sg_tasks_v4", tasks), [tasks]);
  useEffect(() => LS.set(`sg_done_${today}`, dayDone), [dayDone, today]);
  useEffect(
    () => LS.set("sg_first_day_complete_v1", firstDayComplete),
    [firstDayComplete]
  );
  useEffect(() => LS.set("sg_owned_v3", ownedItems), [ownedItems]);
  useEffect(() => LS.set("sg_equipped_v3", equippedItems), [equippedItems]);
  useEffect(() => LS.set("sg_home_owned_v1", ownedHomeItems), [ownedHomeItems]);
  useEffect(
    () => LS.set("sg_home_placed_v1", placedHomeItems),
    [placedHomeItems]
  );
  useEffect(() => LS.set("sg_home_blooms_v1", homeBlooms), [homeBlooms]);
  useEffect(
    () => LS.set("sg_spirit_visitors_v1", spiritVisitors),
    [spiritVisitors]
  );
  useEffect(() => {
    const cleanVisitors = () =>
      setSpiritVisitors((prev) => {
        const next = activeSpiritVisitors(prev);
        return next.length === prev.length ? prev : next;
      });
    cleanVisitors();
    const timer = window.setInterval(cleanVisitors, 60000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => LS.set(`sg_quiz_${today}`, doneQuiz), [doneQuiz, today]);
  useEffect(
    () => LS.set(`sg_trivia_${today}`, doneTrivia),
    [doneTrivia, today]
  );
  useEffect(() => LS.set(`sg_sam_${today}`, samaritan), [samaritan]);
  useEffect(() => LS.set("sg_preach_v3", preachLog), [preachLog]);
  useEffect(
    () => LS.set("sg_prayer_friends_v1", prayerFriends),
    [prayerFriends]
  );
  useEffect(() => LS.set("sg_onboarded_v1", onboarded), [onboarded]);
  useEffect(() => LS.set("sg_activity_v1", activityLog), [activityLog]);
  useEffect(() => LS.set("sg_quiet_mode_v2", quietMode), [quietMode]);
  useEffect(() => LS.set("sg_sound_on_v1", soundOn), [soundOn]);
  useEffect(
    () => LS.set("sg_last_cloud_saved_at_v1", lastCloudSavedAt),
    [lastCloudSavedAt]
  );
  useEffect(() => LS.set("sg_mood_v1", moodLog), [moodLog]);
  useEffect(() => LS.set("sg_home_visited_v1", homeVisited), [homeVisited]);
  useEffect(
    () => LS.set("sg_faith_flight_unlocked_v1", faithFlightUnlocked),
    [faithFlightUnlocked]
  );
  useEffect(
    () => LS.set("sg_closet_visited_v1", closetVisited),
    [closetVisited]
  );
  useEffect(
    () => LS.set("sg_testimony_stones_v1", testimonyStones),
    [testimonyStones]
  );
  useEffect(
    () => LS.set("sg_quest_stone_dates_v1", questStoneDates),
    [questStoneDates]
  );
  const lastAuthUidRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isGoogleRedirectPending()) return;
    let cancelled = false;
    setCloudStatus("Finishing Google sign-in...");
    completeGoogleRedirectSignIn()
      .then(async (user) => {
        if (cancelled || !user?.email) return;
        setCloudUser(user);
        setCloudStatus(`Signed in as ${user.email}`);
        try {
          const userSnapshot = await getDoc(doc(db, "users", user.uid));
          const savedProgress = userSnapshot.exists()
            ? (userSnapshot.data() as Record<string, any>).progress
            : null;
          const savedSpiritName =
            typeof savedProgress?.spiritName === "string"
              ? savedProgress.spiritName
              : undefined;
          if (savedProgress?.gender && savedProgress?.onboarded) {
            if (savedSpiritName) LS.set("sg_spirit_name", savedSpiritName);
            applyCloudProgress(savedProgress);
          }
          await loadFriendData(user, savedSpiritName);
        } catch (error) {
          console.error(error);
          setFriendStatus("Could not load friends yet");
        }
        setShowCloudPrompt(false);
        LS.set("sg_cloud_prompt_dismissed_v1", true);
        showToast("Signed in with Google.");
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Google redirect sign-in failed", error);
        clearGoogleRedirectPending();
        clearGoogleAuthStorage();
        const message =
          String(error?.message || "").includes("redirect_uri_mismatch") ||
          error?.code === "auth/redirect-uri-mismatch" ||
          error?.code === "auth/unauthorized-domain"
            ? googleAuthRedirectHelp
            : "Google sign-in could not finish. Please try again.";
        setCloudStatus(message);
        showToast(message);
      });
    return () => {
      cancelled = true;
    };
  }, []);
	  useEffect(() => {
	    const unsubscribe = onAuthStateChanged(auth, async (user) => {
	      if (user?.email) {
	        lastAuthUidRef.current = user.uid;
	        setCloudUser(user);
	        setCloudStatus(`Signed in as ${user.email}`);
	        try {
	          const userSnapshot = await getDoc(doc(db, "users", user.uid));
	          const savedProgress = userSnapshot.exists()
	            ? (userSnapshot.data() as Record<string, any>).progress
	            : null;
          const savedSpiritName =
            typeof savedProgress?.spiritName === "string"
              ? savedProgress.spiritName
              : undefined;
          const hasSavedGarden = Boolean(savedProgress?.gender && savedProgress?.onboarded);
          if (hasSavedGarden) {
            if (savedSpiritName) LS.set("sg_spirit_name", savedSpiritName);
            applyCloudProgress(savedProgress);
            setCloudStatus("Loaded");
          }
          await loadFriendData(user, savedSpiritName || user.displayName || undefined);
	        } catch (error) {
	          console.error(error);
	          setFriendStatus("Could not load friends yet");
	        }
	      } else {
        if (isGoogleRedirectPending()) {
          setCloudStatus("Finishing Google sign-in...");
          return;
        }
        clearGoogleRedirectPending();
	        lastAuthUidRef.current = null;
	        welcomeBackShownRef.current = false;
	        clearSignedInSession("");
	      }
	    });
    return unsubscribe;
  }, []);
  useEffect(() => {
    if (!cloudUser?.email) return;
    const user = cloudUser;
    const toIsoDate = (value: any) =>
      value && typeof value.toDate === "function"
        ? value.toDate().toISOString()
        : typeof value === "string"
        ? value
        : undefined;
    const handleFriendError = (error: unknown) => {
      console.error(error);
      setFriendStatus("Could not update friends live yet");
    };
    const unsubscribers: Array<() => void> = [];
    const safeSubscribe = (
      friendQuery: ReturnType<typeof query>,
      onNext: Parameters<typeof onSnapshot>[1]
    ) => {
      try {
        const unsubscribe = onSnapshot(
          friendQuery,
          (snapshot) => {
            try {
              onNext(snapshot);
            } catch (error) {
              handleFriendError(error);
            }
          },
          handleFriendError
        );
        unsubscribers.push(unsubscribe);
      } catch (error) {
        handleFriendError(error);
      }
    };
    safeSubscribe(
      query(
        collection(db, "friendRequests"),
        where("toUid", "==", user.uid),
        where("status", "==", "pending")
      ),
      (snapshot) => {
          hydrateFriendRequestNames(snapshot.docs.map((snap) => ({
              id: snap.id,
              ...(snap.data() as Omit<SpiritFriendRequest, "id">),
            })), user.uid).then(setIncomingFriendRequests);
          setFriendStatus("Friends updated");
      }
    );
    safeSubscribe(
      query(
        collection(db, "friendRequests"),
        where("fromUid", "==", user.uid),
        where("status", "==", "pending")
      ),
      (snapshot) => {
          hydrateFriendRequestNames(snapshot.docs.map((snap) => ({
              id: snap.id,
              ...(snap.data() as Omit<SpiritFriendRequest, "id">),
            })), user.uid).then(setOutgoingFriendRequests);
      }
    );
    safeSubscribe(
      query(
        collection(db, "friendRequests"),
        where("fromUid", "==", user.uid),
        where("status", "==", "accepted")
      ),
      (snapshot) => {
        const acceptedRequests = snapshot.docs
          .map((snap) => ({
            id: snap.id,
            ...(snap.data() as Omit<SpiritFriendRequest, "id">),
          }));
        const acceptedPartnerIds = accountabilityPartnerIdsFromRequests(
          acceptedRequests,
          user.uid
        );
        if (acceptedPartnerIds.length) {
          setAccountabilityPartnerUids((prev) =>
            Array.from(new Set([...prev, ...acceptedPartnerIds]))
          );
          setAccountabilityPartnerUid((prev) => prev || acceptedPartnerIds[0]);
        }
        const acceptedFriends = acceptedRequests
          .map((request) => friendFromAcceptedRequest(request, user.uid))
          .filter((friend): friend is SpiritFriend => Boolean(friend));
        if (acceptedFriends.length) {
          setSpiritFriends((prev) => mergeSpiritFriends(prev, acceptedFriends));
        }
      }
    );
    safeSubscribe(
      query(
        collection(db, "friendRequests"),
        where("toUid", "==", user.uid),
        where("status", "==", "accepted")
      ),
      (snapshot) => {
        const acceptedRequests = snapshot.docs.map((snap) => ({
          id: snap.id,
          ...(snap.data() as Omit<SpiritFriendRequest, "id">),
        }));
        const acceptedPartnerIds = accountabilityPartnerIdsFromRequests(
          acceptedRequests,
          user.uid
        );
        if (acceptedPartnerIds.length) {
          setAccountabilityPartnerUids((prev) =>
            Array.from(new Set([...prev, ...acceptedPartnerIds]))
          );
          setAccountabilityPartnerUid((prev) => prev || acceptedPartnerIds[0]);
        }
        const acceptedFriends = acceptedRequests
          .map((request) => friendFromAcceptedRequest(request, user.uid))
          .filter((friend): friend is SpiritFriend => Boolean(friend));
        if (acceptedFriends.length) {
          setSpiritFriends((prev) => mergeSpiritFriends(prev, acceptedFriends));
        }
      }
    );
    safeSubscribe(
      query(
        collection(db, "friendships"),
        where("participantUids", "array-contains", user.uid)
      ),
      (snapshot) => {
          const friendshipFriends = snapshot.docs
            .map((snap) => {
              const data = snap.data() as Record<string, any>;
              const otherUid = (data.participantUids || []).find(
                (uid: string) => uid !== user.uid
              );
              if (!otherUid) return null;
              const friend: SpiritFriend = {
                id: snap.id,
                uid: otherUid,
                displayName:
                  data.participantNames?.[otherUid] ||
                  data.participantEmails?.[otherUid]?.split("@")[0] ||
                  "Spirit Friend",
                email: data.participantEmails?.[otherUid],
              };
              return friend;
            })
            .filter((friend): friend is SpiritFriend => Boolean(friend));
          hydrateSpiritFriendNames(friendshipFriends).then((namedFriends) => {
            setSpiritFriends((prev) =>
              mergeSpiritFriends(prev, namedFriends)
            );
          });
      }
    );
    safeSubscribe(
      query(
        collection(db, "sharedPrayers"),
        where("friendUids", "array-contains", user.uid)
      ),
      (snapshot) => {
          setSharedPrayers(
            snapshot.docs.map((snap) => ({
              id: snap.id,
              ...(snap.data() as Omit<SharedPrayer, "id">),
              createdAt: toIsoDate(snap.data().createdAt),
            }))
          );
      }
    );
    safeSubscribe(
      query(collection(db, "friendSignals"), where("toUid", "==", user.uid)),
      (snapshot) => {
          setReceivedPrayerSignals(
            snapshot.docs
              .map((snap) => ({
                id: snap.id,
                ...(snap.data() as Omit<PrayerSignal, "id">),
                createdAt: toIsoDate(snap.data().createdAt),
              }))
              .sort((a, b) =>
                (b.createdAt || "").localeCompare(a.createdAt || "")
              )
          );
      }
    );
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [cloudUser?.uid, cloudUser?.email]);
  useEffect(() => {
    if (!cloudUser?.email || cloudUser.isAnonymous || !spiritName.trim()) return;
    const chosenName = spiritName.trim();
    setDoc(
      doc(db, "friendProfiles", cloudUser.uid),
      stripUndefinedForFirestore({
        uid: cloudUser.uid,
        displayName: chosenName,
        email: cloudUser.email,
        emailLower: cloudUser.email.toLowerCase(),
        inviteCode: friendInviteCode(cloudUser.uid),
        gender: profileGenderOrFallback(gender),
        stageKey: stage.key,
        updatedAt: serverTimestamp(),
      }),
      { merge: true }
    )
      .then(() => {
        setFriendProfile((current) =>
          current
            ? {
                ...current,
                displayName: chosenName,
                gender: profileGenderOrFallback(gender || current.gender),
                stageKey: stage.key,
              }
            : current
        );
      })
      .catch((error) => console.error("Could not sync spirit name", error));
  }, [cloudUser?.uid, cloudUser?.email, cloudUser?.isAnonymous, gender, spiritName, stage.key]);
  useEffect(() => {
    if (!cloudUser?.uid || !spiritFriendUidKey) return;
    const friendUids = Array.from(
      new Set(spiritFriendUidKey.split("|").filter(Boolean))
    );
    const unsubscribers = friendUids.flatMap((friendUid) => [
      onSnapshot(
        doc(db, "friendProfiles", friendUid),
        (snapshot) => {
          if (!snapshot.exists()) return;
          const data = snapshot.data() as Partial<SpiritFriendProfile>;
          const rawChosenName = String(data.displayName || "").trim();
          const chosenName = rawChosenName && !rawChosenName.includes("@") ? rawChosenName : "";
          setSpiritFriends((current) =>
            current.map((friend) =>
              friend.uid === friendUid
                ? {
                    ...friend,
                    displayName: chosenName || friend.displayName,
                    email: data.email || friend.email,
                    gender: normalizeSpiritGender(data.gender) || friend.gender,
                    stageKey: data.stageKey || friend.stageKey,
                  }
                : friend
            )
          );
        },
        (error) => console.error("Could not listen for spirit profile", error)
      ),
      onSnapshot(
        doc(db, "users", friendUid),
        (snapshot) => {
          if (!snapshot.exists()) return;
          const progress = (snapshot.data() as Record<string, any>).progress as
            | Record<string, any>
            | undefined;
          const progressName = spiritNameFromCloudProgress(progress);
          const progressGender = spiritGenderFromCloudProgress(progress);
          const progressStageKey = spiritStageKeyFromCloudProgress(progress);
          if (!progressName && !progressGender && !progressStageKey) return;
          setSpiritFriends((current) =>
            current.map((friend) =>
              friend.uid === friendUid
                ? {
                    ...friend,
                    displayName: progressName || friend.displayName,
                    gender: progressGender || friend.gender,
                    stageKey: progressStageKey || friend.stageKey,
                  }
                : friend
            )
          );
        },
        (error) => console.error("Could not listen for spirit progress", error)
      ),
    ]);
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [cloudUser?.uid, spiritFriendUidKey]);
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    listenForForegroundMessages((payload) => {
      const data = payload.data || {};
      showToast(data.body || payload.notification?.body || data.title || "New notification");
    }).then((cleanup) => {
      unsubscribe = cleanup;
    });
    return () => {
      unsubscribe?.();
    };
  }, []);
  useEffect(() => {
    if (!cloudUser || cloudUser.isAnonymous) return;
    if (!onboarded || !normalizeSpiritGender(gender) || !formatSpiritName(spiritName)) return;
    const t = window.setTimeout(() => {
      saveProgressToCloud(cloudUser).catch((error) => {
        console.error(error);
        setCloudStatus("Could not autosave yet");
      });
    }, 2500);
    return () => window.clearTimeout(t);
  }, [
    cloudUser,
    gender,
    spiritName,
    tasks,
    checked,
    totalXP,
    righteousness,
    streak,
    graceDays,
    journal,
    entries,
    dayDone,
    ownedItems,
    equippedItems,
    prayerFriends,
    ownedHomeItems,
    placedHomeItems,
    homeBlooms,
    spiritVisitors,
    moodLog,
    faithFlightUnlocked,
    testimonyStones,
  ]);
  useEffect(() => {
    if (stonePopup || stoneQueue.length === 0) return;
    const [next, ...rest] = stoneQueue;
    setStonePopup(next);
    setStoneQueue(rest);
  }, [stonePopup, stoneQueue]);
  useEffect(() => {
    if (!isTabUnlocked(tab)) setTab("today");
    if (!isTabUnlocked("shop") && showCloset) setShowCloset(false);
  }, [tab, showCloset]);
  useEffect(() => {
    if (tab === "home" && !homeVisited) setHomeVisited(true);
  }, [tab, homeVisited]);

  useEffect(() => {
    if (tab !== "home") return;
    LS.set(homeNoticeKey, activeHomeVisitorIds);
  }, [activeHomeVisitorIds.join("|"), homeNoticeKey, tab]);
  useEffect(() => {
    if ((tab === "shop" || showCloset) && !closetVisited)
      setClosetVisited(true);
  }, [tab, showCloset, closetVisited]);

  useEffect(() => {
    setSeenTestimonyCount(LS.get<number>(testimonyNoticeKey, -1));
  }, [testimonyNoticeKey]);

  useEffect(() => {
    const approvedQuery = query(
      collection(db, "publicTestimonies"),
      where("approved", "==", true)
    );
    const unsubscribe = onSnapshot(
      approvedQuery,
      (snapshot) => setApprovedTestimonyCount(snapshot.docs.length),
      (error) => console.error("Could not watch testimony activity", error)
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (tab !== "wall") return;
    const nextSeenCount = Math.max(1, approvedTestimonyCount);
    setSeenTestimonyCount(nextSeenCount);
    LS.set(testimonyNoticeKey, nextSeenCount);
  }, [approvedTestimonyCount, tab, testimonyNoticeKey]);

  const iconOpts = [
    "✨",
    "🔥",
    "💫",
    "🌿",
    "🕊️",
    "⚡",
    "🌸",
    "🍃",
    "🔶",
    "🌊",
    "🎯",
    "🌺",
  ];

  function toggleTask(id: string, e: ButtonEvent) {
    const task = tasks.find((t) => t.id === id);
    if (!task) return;
    const was = !!checked[id];
    if (!was) {
      const willFinishRequired = requiredTasks.every((requiredTask) =>
        requiredTask.id === id ? true : !!checked[requiredTask.id]
      );
      spiritHaptic(willFinishRequired ? "success" : "light");
      playSound(willFinishRequired ? "stone" : "earn");
      spawnSpark(e);
    }
    setChecked((p) => ({ ...p, [id]: !was }));
    setTotalXP((x) => (was ? Math.max(0, x - 10) : x + 10));
    if (was) {
      setRighteousness((r) => Math.max(0, r - 10));
      showToast("-10 righteousness removed");
    } else {
      gainR(10, "righteousness earned");
    }
    setActivityLog((prev) => ({ ...prev, [today]: true }));
  }

  function addTask() {
    if (!newTaskLabel.trim()) return;
    setTasks((p) => [
      ...p,
      {
        id: "c_" + Date.now(),
        label: newTaskLabel.trim(),
        icon: newTaskIcon,
        optional: false,
      },
    ]);
    setNewTaskLabel("");
    setNewTaskIcon("✨");
    setShowAddTask(false);
  }

  function removeTask(id: string) {
    spiritHaptic("warning");
    if (checked[id]) setTotalXP((x) => Math.max(0, x - 10));
    setTasks((p) => p.filter((t) => t.id !== id));
    setChecked((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });
    if (editingTaskId === id) cancelEditTask();
  }

  function startEditTask(task: Task) {
    setEditingTaskId(task.id);
    setEditingTaskLabel(task.label);
    setEditingTaskIcon(task.icon);
    setShowAddTask(false);
  }

  function saveTaskEdit() {
    const label = editingTaskLabel.trim();
    if (!editingTaskId || !label) return;
    setTasks((prev) =>
      prev.map((task) =>
        task.id === editingTaskId
          ? { ...task, label, icon: editingTaskIcon }
          : task
      )
    );
    cancelEditTask();
  }

  function cancelEditTask() {
    setEditingTaskId(null);
    setEditingTaskLabel("");
    setEditingTaskIcon("✨");
  }

  function toggleJournalMonth(key: string) {
    setOpenJournalMonths((prev) => ({ ...prev, [key]: prev[key] === false }));
  }

  function toggleTestimonyMonth(key: string) {
    setOpenTestimonyMonths((prev) => ({ ...prev, [key]: prev[key] === false }));
  }

  function clearTaskPressTimer() {
    if (taskPressTimer.current) {
      clearTimeout(taskPressTimer.current);
      taskPressTimer.current = null;
    }
  }

  function openTaskMenu(id: string) {
    if (dayDone) return;
    setActiveTaskMenuId(id);
  }

  function handleTaskTouchStart(id: string, e: React.TouchEvent<HTMLElement>) {
    taskTouchStartX.current = e.touches[0]?.clientX || 0;
    clearTaskPressTimer();
    taskPressTimer.current = setTimeout(() => openTaskMenu(id), 520);
  }

  function handleTaskTouchMove(id: string, e: React.TouchEvent<HTMLElement>) {
    const x = e.touches[0]?.clientX || taskTouchStartX.current;
    if (taskTouchStartX.current - x > 42) {
      clearTaskPressTimer();
      openTaskMenu(id);
    }
    if (Math.abs(taskTouchStartX.current - x) > 12) clearTaskPressTimer();
  }

  function handleTaskTouchEnd() {
    clearTaskPressTimer();
  }

  function recordMood(option: (typeof MOOD_OPTIONS)[number]) {
    setMoodLog((prev) => ({
      ...prev,
      [today]: {
        mood: option.mood,
        label: option.label,
        verse: option.verse,
        ref: option.ref,
      },
    }));
    showToast(`${option.label} check-in saved`);
  }

  function completeDay() {
    if (doneCount < requiredTasks.length || dayDone) return;
    spiritHaptic("success");
    setDayDone(true);
    setFirstDayComplete(true);
    setHomeBlooms((prev) => {
      if (prev.some((bloom) => bloom.date === today)) return prev;
      return [{ date: today, kind: "walk" }, ...prev].slice(0, 60);
    });
    addSpiritVisitor(Math.random() > 0.5 ? "lamb" : "rabbit", "Today’s Walk brought gentle life to your home.");
    setStreak((s) => {
      const next = s + 1;
      if ([7, 30, 100].includes(next)) {
        window.setTimeout(() => spiritHaptic("medium"), 140);
      }
      return next;
    });
    setTotalXP((x) => x + 30);
    gainR(50, "righteousness · home restored");
    window.setTimeout(() => {
      showToast("Your heavenly home grew brighter today.");
    }, 650);
    setShowDaySummary(true);
    showToast("Your spirit shines brighter today.");
    setHomeRestored(true);
    setTimeout(() => setHomeRestored(false), 2600);
    setActivityLog((prev) => ({ ...prev, [today]: true }));
  }

  function letSpiritFly() {
    if (!effectiveFaithFlightUnlocked || faithFlightActive) return;
    spiritHaptic("success");
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    setFaithFlightActive(true);
    showToast(
      reduceMotion
        ? "Faith Flight blessing released."
        : "Faith Flight. Your spirit is soaring gently."
    );
    setTimeout(() => setFaithFlightActive(false), reduceMotion ? 900 : 5200);
  }

  function saveJournal() {
    const t = journal.trim();
    if (!t) return;
    spiritHaptic("light");
    setEntries((p) =>
      [{ date: today, text: t }, ...p.filter((e) => e.date !== today)].slice(
        0,
        30
      )
    );
    setJournalSaved(true);
    gainR(15, "reflection written");
    addSpiritVisitor("butterfly", "A Butterfly appeared after your reflection.");
    setTimeout(() => setJournalSaved(false), 2200);
  }

  function tryBuyClothes(item: ClothingItem) {
    if (isAdminUser && !ownedItems.includes(item.id)) {
      showToast("Admin preview: item is available in your Armor Closet ✓");
      setShowCloset(true);
      return;
    }
    if (!ARMOR_CLOSET_TEST_MODE && righteousness < item.cost) {
      setLowFundsModal({ needed: item.cost });
      return;
    }
    if (ownedItems.includes(item.id)) {
      showToast("Already in your Armor Closet ✓");
      return;
    }
    if (!ARMOR_CLOSET_TEST_MODE) setRighteousness((r) => r - item.cost);
    spiritHaptic("light");
    setOwnedItems((p) => [...p, item.id]);
    showToast(`${item.icon} ${item.name} added to your Armor Closet!`);
  }

  function sellClothes(item: ClothingItem) {
    if (isAdminUser && !ownedItems.includes(item.id)) {
      showToast("Admin preview item is available, but not owned to sell.");
      return;
    }
    const refund = Math.floor(item.cost * 0.5);
    setOwnedItems((p) => p.filter((id) => id !== item.id));
    setEquippedItems((p) => p.filter((id) => id !== item.id));
    setRighteousness((r) => r + refund);
    showToast(`Sold ${item.name} for ✨ ${refund}`);
  }

  function tryBuyHomeItem(item: HomeItem) {
    if (!HOME_SHOP_TEST_MODE && righteousness < item.cost) {
      setLowFundsModal({ needed: item.cost });
      return;
    }
    if (ownedHomeItems.includes(item.id)) {
      showToast("Already in your Heavenly Home ✓");
      return;
    }
    if (!HOME_SHOP_TEST_MODE) setRighteousness((r) => r - item.cost);
    spiritHaptic("medium");
    setOwnedHomeItems((p) => [...p, item.id]);
    setPlacedHomeItems((p) => ({ ...p, [item.slot]: item.id }));
    showToast(`${item.name} added to your Heavenly Home!`);
  }

  function placeHomeItem(item: HomeItem) {
    if (!ownedHomeItems.includes(item.id)) return;
    spiritHaptic("light");
    setPlacedHomeItems((p) => ({ ...p, [item.slot]: item.id }));
    showToast(`${item.name} placed`);
  }

  function removeHomeItem(item: HomeItem) {
    if (placedHomeItems[item.slot] !== item.id) return;
    spiritHaptic("light");
    setPlacedHomeItems((prev) => {
      const next = { ...prev };
      delete next[item.slot];
      return next;
    });
    showToast(`${item.name} removed from Home`);
  }

  function sellHomeItem(item: HomeItem) {
    const refund = Math.floor(item.cost * 0.5);
    setOwnedHomeItems((p) => p.filter((id) => id !== item.id));
    setPlacedHomeItems((prev) => {
      const next = { ...prev };
      if (next[item.slot] === item.id) delete next[item.slot];
      return next;
    });
    setRighteousness((r) => r + refund);
    showToast(`Sold ${item.name} for ✨ ${refund}`);
  }

  function answerQuiz(qi: number, sel: string) {
    const q = VERSE_QUIZ[qi];
    if (sel === q.answer && !doneQuiz[qi]) {
      spiritHaptic("light");
      setDoneQuiz((p) => ({ ...p, [qi]: true }));
      setTotalXP((x) => x + 5);
      gainR(q.reward, "verse quiz!");
    }
  }

  function answerTrivia(qi: number, sel: string) {
    const q = BIBLE_TRIVIA[qi];
    if (sel === q.answer && !doneTrivia[qi]) {
      spiritHaptic("light");
      setDoneTrivia((p) => ({ ...p, [qi]: true }));
      setTotalXP((x) => x + 5);
      gainR(q.reward, "trivia correct!");
    }
  }

  function logSamaritan(act: (typeof SAMARITAN_ACTS)[0], e: ButtonEvent) {
    if (samaritan[act.id]) return;
    spiritHaptic("light");
    spawnSpark(e);
    setSamaritan((p) => ({ ...p, [act.id]: true }));
    setTotalXP((x) => x + 15);
    gainR(act.reward, act.label);
  }

  function savePreach(text: string, prompt: string) {
    spiritHaptic("light");
    setPreachLog((p) => [{ date: today, text, prompt }, ...p].slice(0, 20));
    gainR(40, "Word preached!");
    setTotalXP((x) => x + 20);
  }

  function savePrayerFriendsImmediately(nextPrayers: PrayerFriend[]) {
    LS.set("sg_prayer_friends_v1", nextPrayers);
    const user = auth.currentUser || cloudUser;
    if (!user || user.isAnonymous) return;
    setDoc(
      doc(db, "users", user.uid),
      stripUndefinedForFirestore({
        progress: { prayerFriends: nextPrayers },
        updatedAt: serverTimestamp(),
      }),
      { merge: true }
    )
      .then(() => {
        const savedAt = new Date().toISOString();
        setLastCloudSavedAt(savedAt);
        LS.set("sg_last_cloud_saved_at_v1", savedAt);
        setCloudStatus("Prayers saved");
      })
      .catch((error) => {
        console.error("Could not save prayers immediately", error);
        setCloudStatus("Could not save prayers yet");
      });
  }

  function updatePrayerFriendsAndSave(
    updater: (current: PrayerFriend[]) => PrayerFriend[]
  ) {
    setPrayerFriends((current) => {
      const next = updater(current);
      savePrayerFriendsImmediately(next);
      return next;
    });
  }

  function addPrayerFriend(
    kind: "mine" | "other",
    name: string,
    request: string,
    challengeDays?: PrayerChallengeDays
  ) {
    spiritHaptic("light");
    updatePrayerFriendsAndSave((p) => [
      {
        id: "pf_" + Date.now(),
        kind,
        name,
        request,
        challengeDays,
        challengeStarted: today,
        righteousnessGiven: 0,
        prayedDates: [],
      },
      ...p,
    ]);
    showToast(
      kind === "mine"
        ? challengeDays
          ? `${challengeDays}-day personal prayer added 💛`
          : "Ongoing personal prayer added 💛"
        : `Prayer for ${name} added 🕊️`
    );
  }

  function prayForFriend(friend: PrayerFriend, e: ButtonEvent) {
    const prayedDates = Array.isArray(friend.prayedDates)
      ? friend.prayedDates
      : [];
    if (prayedDates.includes(today) || friend.answered) return;
    spiritHaptic("light");
    const isMine = (friend.kind || "other") === "mine";

    if (!isMine) {
      const target = e.currentTarget.getBoundingClientRect();
      const fromX = window.innerWidth / 2 - 11;
      const fromY = Math.max(80, window.innerHeight - 150);
      const toX = target.left + target.width / 2 - 11;
      const toY = target.top + target.height / 2 - 11;
      const lightId = Date.now() + Math.random();
      setPrayerLights((lights) => [
        ...lights,
        { id: lightId, fromX, fromY, toX, toY },
      ]);
      setTimeout(
        () =>
          setPrayerLights((lights) =>
            lights.filter((light) => light.id !== lightId)
          ),
        1150
      );
    }
    const angelId = Date.now() + Math.random();
    setAngelMessages((messages) => [
      ...messages,
      {
        id: angelId,
        text: isMine
          ? "An angel carries your prayer to God."
          : `An angel carries your prayer for ${
              friend.name || "someone"
            } to God.`,
      },
    ]);
    setTimeout(
      () =>
        setAngelMessages((messages) =>
          messages.filter((message) => message.id !== angelId)
        ),
      2200
    );

    updatePrayerFriendsAndSave((prev) =>
      prev.map((item) =>
        item.id === friend.id
          ? {
              ...item,
              prayedDates: Array.from(
                new Set([
                  ...(Array.isArray(item.prayedDates) ? item.prayedDates : []),
                  today,
                ])
              ),
            }
          : item
      )
    );
    const challengeDays = friend.challengeDays;
    const nextPrayerCount = new Set([...prayedDates, today]).size;
    const completedPrayerCondition = isMine && typeof challengeDays === "number" && nextPrayerCount >= challengeDays;
    if (completedPrayerCondition) {
      awardStone(
        stoneForKind(
          "prayer",
          today,
          "Prayer streak complete",
          friend.id
        )
      );
      addSpiritVisitor("bluebird", "A Bluebird came to celebrate your faithfulness.", {
        force: true,
        minutes: 30,
      });
    } else {
      showToast(
        isMine
          ? "Prayer lifted · carried to God"
          : "Prayer lifted for them · carried to God"
      );
    }
    if (!isMine) {
      addSpiritVisitor("dove", "A Dove visited because you prayed for someone today.");
    }
    setActivityLog((prev) => ({ ...prev, [today]: true }));
  }

  function updatePrayer(id: string, updates: Partial<PrayerFriend>) {
    const currentPrayer = prayerFriends.find((prayer) => prayer.id === id);
    const nextPrayer = currentPrayer ? { ...currentPrayer, ...updates } : null;
    updatePrayerFriendsAndSave((prev) =>
      prev.map((prayer) =>
        prayer.id === id ? { ...prayer, ...updates } : prayer
      )
    );
    if (
      nextPrayer &&
      (updates.request !== undefined ||
        updates.name !== undefined ||
        updates.kind !== undefined)
    ) {
      updateSharedPrayerCopies(nextPrayer);
    }
    showToast(
      updates.answeredNote !== undefined
        ? "Testimony updated"
        : "Prayer updated"
    );
  }

  function editTestimonyEntry(id: string) {
    const prayer = prayerFriends.find((item) => item.id === id);
    if (!prayer) return;
    const next = window.prompt(
      "Edit testimony note",
      prayer.answeredNote || prayer.request
    );
    if (next == null) return;
    updatePrayer(id, { answeredNote: next.trim() || undefined });
  }

  function deletePrayer(id: string) {
    if (!window.confirm("Delete this prayer?")) return;
    spiritHaptic("warning");
    updatePrayerFriendsAndSave((prev) => prev.filter((prayer) => prayer.id !== id));
    showToast("Prayer removed");
  }

  function deleteTestimonyEntry(id: string) {
    if (!window.confirm("Remove this testimony entry from the book?")) return;
    spiritHaptic("warning");
    updatePrayerFriendsAndSave((prev) =>
      prev.map((prayer) =>
        prayer.id === id
          ? {
              ...prayer,
              answered: false,
              answeredDate: undefined,
              answeredNote: undefined,
            }
          : prayer
      )
    );
    showToast("Testimony entry removed");
  }

  function togglePrayerPublicShare(id: string, sharePublic: boolean) {
    spiritHaptic("light");
    updatePrayerFriendsAndSave((prev) =>
      prev.map((prayer) =>
        prayer.id === id
          ? {
              ...prayer,
              sharePublic,
              publicDisplayName: sharePublic
                ? "Anonymous testimony"
                : undefined,
            }
          : prayer
      )
    );
    showToast(
      sharePublic ? "Saved as public testimony preview" : "Kept private"
    );
  }
  function saveTestimonyDraftToBook(draft: { title: string; testimony: string; displayName: string }) {
    const title = draft.title.trim() || "Testimony Draft";
    const testimony = draft.testimony.trim();
    if (!testimony) return;
    const id = `draft_${Date.now()}_${Math.round(Math.random() * 1000)}`;
    updatePrayerFriendsAndSave((prev) => [
      {
        id,
        kind: "mine",
        name: title,
        request: title,
        righteousnessGiven: 0,
        prayedDates: [today],
        answered: true,
        answeredDate: today,
        answeredNote: testimony,
        sharePublic: true,
        publicDisplayName: draft.displayName || "Anonymous",
      },
      ...prev,
    ]);
    spiritHaptic("light");
    showToast("Draft saved in your Testimony Book.");
  }

  async function submitPublicTestimony(prayer: PrayerFriend) {
    if (prayer.publicSubmittedAt) {
      showToast("This testimony is already waiting for review.");
      return;
    }

    try {
      const user = await ensureAnonymousUser();
      if (!user) {
        showToast("Google sign-in is still finishing. Please try again in a moment.");
        return;
      }

      await addDoc(collection(db, "publicTestimonies"), {
        testimony: prayer.answeredNote || prayer.request,
        prayerTitle: prayer.name || "Answered Prayer",
        sourcePrayerId: prayer.id,
        createdAt: serverTimestamp(),
        submittedAt: today,
        userId: user.uid,
        displayName: "Anonymous",
        approved: false,
      });
      await queuePushNotification({
        toEmail: ADMIN_EMAIL,
        kind: "testimony_alert",
        data: { sourcePrayerId: prayer.id },
      });

      updatePrayerFriendsAndSave((prev) =>
        prev.map((item) =>
          item.id === prayer.id ? { ...item, publicSubmittedAt: today } : item
        )
      );
      spiritHaptic("light");
      showToast("Your testimony was submitted for review.");
      addSpiritVisitor("deer", "A Deer appeared because you shared a testimony.", {
        minutes: 30,
      });
    } catch (error) {
      console.error(error);
      showToast("Could not submit testimony yet. Please try again.");
    }
  }

  function markPrayerAnswered(id: string, note?: string, sharePublic = false) {
    spiritHaptic("light");
    const answeredPrayer = prayerFriends.find((prayer) => prayer.id === id);
    setAnsweredCelebration({
      id,
      title: answeredPrayer?.name || "Answered Prayer",
      note: note || answeredPrayer?.answeredNote || answeredPrayer?.request,
      sharePublic,
    });
    updatePrayerFriendsAndSave((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              answered: true,
              answeredDate: today,
              answeredNote: note || undefined,
              sharePublic: sharePublic ? true : item.sharePublic,
              publicDisplayName: sharePublic
                ? "Anonymous testimony"
                : item.publicDisplayName,
            }
          : item
      )
    );
    awardStone(stoneForKind("answered", today, "answered prayer saved", id));
    playSound("answered");
    setHomeBlessed(true);
    addSpiritVisitor(
      "golden_dove",
      "A Golden Dove came with the light of an answered prayer.",
      { force: true, minutes: 30 }
    );
    setTimeout(() => setHomeBlessed(false), 2600);
    showToast(
      sharePublic
        ? "Answered prayer saved. Testimony draft is ready."
        : "Answered prayer saved. Give thanks to God."
    );
  }

  function blessHomeToday() {
    if (homeBlessedDate === today) {
      showToast("Your home is already blessed for today.");
      return;
    }
    spiritHaptic("light");
    setHomeBlessedDate(today);
    LS.set("sg_home_blessed_date_v1", today);
    setHomeBlessed(true);
    playSound("earn");
    awardStone(stoneForKind("gratitude", today, "home blessing", "home"));
    window.setTimeout(() => setHomeBlessed(false), 3200);
    showToast("Your home is filled with peace today.");
  }

  if (!gender)
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        {showSplashIntro ? (
          <SplashIntro onSkip={dismissSplashIntro} />
        ) : (
          <>
            <GenderSelect
              onSelect={(g, name) => {
                LS.set("sg_gender", g);
                setGender(g);
                setQuietMode(true);
                setTab("today");
                if (name) {
                  setSpiritName(name);
                  setSpiritNameDraft(name);
	                LS.set("sg_spirit_name", name);
                }
              }}
              onSignIn={handleGoogleSignIn}
              hairColor={hairColor}
              signInBusy={googleSignInBusy}
            />
            {showWelcomeBack && (
              <WelcomeBackModal
                name={spiritName || cloudDisplayName || "friend"}
                onClose={() => setShowWelcomeBack(false)}
                onBegin={() => {
                  setShowWelcomeBack(false);
                  setQuietMode(true);
                  setTab("today");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}
          </>
        )}
      </>
    );
  if (!onboarded)
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        {showSplashIntro ? (
          <SplashIntro onSkip={dismissSplashIntro} />
        ) : (
          <>
            <OnboardingScreen
              spiritName={spiritName}
              onStart={() => {
                setQuietMode(true);
                setTab("today");
                setOnboarded(true);
              }}
            />
            {showWelcomeBack && (
              <WelcomeBackModal
                name={spiritName || cloudDisplayName || "friend"}
                onClose={() => setShowWelcomeBack(false)}
                onBegin={() => {
                  setShowWelcomeBack(false);
                  setQuietMode(true);
                  setOnboarded(true);
                  setTab("today");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}
          </>
        )}
      </>
    );

  return (
    <div
      className="app-shell"
      style={{
        minHeight: "100vh",
        background: C.pageBg,
        padding: "0 0 80px",
        color: C.textDark,
        position: "relative",
        overflow: "visible",
      }}
    >
      <style>{GLOBAL_CSS}</style>

      {partnerRequestNotice && (
        <div
          className="friend-remove-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={partnerRequestNotice.title}
          onClick={() => setPartnerRequestNotice(null)}
        >
          <div
            className={`friend-remove-card partner-request-card ${partnerRequestNotice.tone || "info"}`}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="friend-remove-close"
              aria-label="Close"
              onClick={() => setPartnerRequestNotice(null)}
            >
              ×
            </button>
            <div className="friend-remove-mark partner-request-mark">
              {partnerRequestNotice.tone === "error" ? "!" : "♡"}
            </div>
            <h3 className="serif">{partnerRequestNotice.title}</h3>
            <p>{partnerRequestNotice.message}</p>
            <div className="friend-remove-actions single">
              <button
                type="button"
                className="soft-action-btn friend-alert-btn"
                onClick={() => setPartnerRequestNotice(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          overflow: "hidden",
        }}
      >
        {[...Array(18)].map((_, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              fontSize: i % 3 === 0 ? 12 : 7,
              left: `${(i * 19 + i * i * 2) % 100}%`,
              top: `${(i * 13 + i * 7) % 100}%`,
              opacity: 0.035 + (i % 4) * 0.018,
              animation: `twinkle ${3.5 + (i % 5) * 0.8}s ease-in-out ${
                i * 0.18
              }s infinite`,
              color: i % 3 === 0 ? C.goldDeep : C.goldLight,
            }}
          >
            {["✦", "✧", "⋆", "·"][i % 4]}
          </div>
        ))}
      </div>

      {sparks.map((s) => (
        <Sparkle key={s.id} x={s.x} y={s.y} />
      ))}
      {prayerLights.map((light) => (
        <PrayerLightTrail key={light.id} light={light} />
      ))}
      {angelMessages.map((msg) => (
        <AngelPrayerMessage key={msg.id} msg={msg} />
      ))}
      {toast && <Toast msg={toast} onDone={() => setToast(null)} />}
      {showSplashIntro && <SplashIntro onSkip={dismissSplashIntro} />}
      {showWelcomeBack && (
        <WelcomeBackModal
          name={spiritName || cloudDisplayName || "friend"}
          onClose={() => setShowWelcomeBack(false)}
          onBegin={() => {
            setShowWelcomeBack(false);
            setQuietMode(true);
            setTab("today");
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
      {stonePopup && (
        <StoneRewardModal
          stone={stonePopup}
          onClose={() => setStonePopup(null)}
          onView={() => {
            setStonePopup(null);
            setShowTestimonyStones(true);
            setTab("growth");
          }}
        />
      )}
      {lowFundsModal && (
        <LowFundsModal
          needed={lowFundsModal.needed}
          current={righteousness}
          onClose={() => setLowFundsModal(null)}
        />
      )}
      {stageUpModal && (
        <StageUpModal
          stage={stageUpModal}
          gender={gender}
          skinTone={skinTone}
          hairColor={hairColor}
          equipped={equippedObjs}
          onClose={() => setStageUpModal(null)}
          onGoGrowth={() => {
            setStageUpModal(null);
            setTab("growth");
          }}
        />
      )}
      {homeGrowthModal && (
        <HomeGrowthModal
          stage={homeGrowthModal}
          onClose={() => setHomeGrowthModal(null)}
          onGoHome={() => {
            setHomeGrowthModal(null);
            setTab("home");
          }}
        />
      )}
      {showCloset && (
        <HeavenlyCloset
          ownedIds={closetOwnedIds}
          equippedIds={equippedItems}
          onChange={setEquippedItems}
          onClose={() => setShowCloset(false)}
          onSell={sellClothes}
          onGoShop={() => {
            setShowCloset(false);
            openArmorShop();
          }}
          gender={gender}
          skinTone={skinTone}
          hairColor={hairColor}
          stageKey={stage.key}
          stageAura={stage.aura}
          stageGlow={stage.glow}
          xp={xpInStage}
          xpMax={stageRange}
        />
      )}
      {answeredCelebration && (
        <AnsweredPrayerCelebrationModal
          celebration={answeredCelebration}
          onClose={() => setAnsweredCelebration(null)}
          onViewAnswered={() => {
            const targetId = answeredCelebration.id;
            setAnsweredCelebration(null);
            setTab("prayers");
            window.setTimeout(() => {
              document
                .getElementById("answered-prayers-section")
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
              if (answeredCelebration.sharePublic) {
                document
                  .getElementById("submit-public-" + targetId)
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }, 180);
          }}
        />
      )}
      {showDaySummary && (
        <DayCompleteModal
          tasks={activeTasks.filter((t) => checked[t.id])}
          streak={streak}
          prayedToday={prayersSentToday}
          journalSavedToday={journalSavedToday}
          onClose={() => setShowDaySummary(false)}
          onGoHome={() => {
            setShowDaySummary(false);
            setTab("home");
          }}
        />
      )}
      {showCloudPrompt && !cloudUser?.email && onboarded && (
        <div
          style={{
            position: "fixed",
            left: 12,
            right: 12,
            bottom: 78,
            zIndex: 900,
            maxWidth: 520,
            margin: "0 auto",
          }}
        >
          <Card
            style={{
              padding: 14,
              background:
                "linear-gradient(160deg,rgba(255,253,246,.98),rgba(236,253,245,.78))",
              borderColor: "rgba(134,190,155,.24)",
              boxShadow: "0 10px 30px rgba(65,95,125,.14)",
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 className="serif" style={{ margin: "0 0 3px", fontSize: 16, color: C.textDark }}>
                  Save your garden?
                </h3>
                <p style={{ margin: 0, fontSize: 12.5, color: C.textSoft, lineHeight: 1.45 }}>
                  Sign in to keep prayers, testimonies, home, and progress across devices.
                </p>
              </div>
              <GoldBtn onClick={handleGoogleSignIn} style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                Save
              </GoldBtn>
              <button
                aria-label="Dismiss save prompt"
                onClick={() => {
                  setShowCloudPrompt(false);
                  LS.set("sg_cloud_prompt_dismissed_v1", true);
                }}
                style={{
                  border: `1px solid ${C.cardBorder}`,
                  background: "rgba(255,253,246,.82)",
                  color: C.textFaint,
                  borderRadius: 12,
                  width: 36,
                  height: 36,
                  cursor: "pointer",
                  fontWeight: 900,
                }}
              >
                ×
              </button>
            </div>
          </Card>
        </div>
      )}

      <div
        className="app-main-frame"
        style={{
          maxWidth: 900,
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <header className="app-header">
          <div className="app-title-copy">
            <div
              style={{
                fontSize: 13.5,
                letterSpacing: 2.5,
                textTransform: "uppercase",
                color: C.textFaint,
                fontWeight: 700,
                marginBottom: 2,
              }}
            >
              Spirit Garden
            </div>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: 12.5,
                lineHeight: 1.28,
                color: C.textMid,
                fontWeight: 500,
                letterSpacing: 0.1,
              }}
            >
              Let your spirit shine
            </p>
            <h1
              className="serif"
              style={{
                margin: "2px 0 0",
                fontSize: 22,
                fontWeight: 700,
                color: C.textDark,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {spiritName || "Daily Walk"}
            </h1>

          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              alignItems: "flex-end",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              {showHeaderControls && (
                <>
                <button
                  className="top-soft-chip"
                  aria-label="Open Home"
                  onClick={() => {
                    setQuietMode(false);
                    setTab("home");
                  }}
                  style={{
                    flexShrink: 0,
                    background: tab === "home" ? GOLD_GRAD_S : "rgba(255,253,246,.88)",
                    border: `1px solid ${tab === "home" ? C.gold : "rgba(190,165,115,.42)"}`,
                    borderRadius: 999,
                    padding: "6px 8px",
                    color: C.textMid,
                    fontWeight: 900,
                    cursor: "pointer",
                    fontSize: 12,
                    minHeight: 34,
                    minWidth: 38,
                  }}
                >
                  <HomeIcon size={23} />
                </button>
                <button
                  className="top-soft-chip"
                  aria-label="Open Journal"
                  onClick={() => {
                    setQuietMode(false);
                    setTab("journal");
                  }}
                  style={{
                    flexShrink: 0,
                    background: tab === "journal" ? GOLD_GRAD_S : "rgba(255,253,246,.88)",
                    border: `1px solid ${tab === "journal" ? C.gold : "rgba(190,165,115,.42)"}`,
                    borderRadius: 999,
                    padding: "6px 8px",
                    color: C.textMid,
                    fontWeight: 900,
                    cursor: "pointer",
                    fontSize: 12,
                    minHeight: 34,
                    minWidth: 38,
                  }}
                >
                  <JournalIcon size={23} />
                </button>
                <button
                  className="top-soft-chip"
                  aria-label={soundOn ? "Turn sound off" : "Turn sound on"}
                  onClick={() => {
                    const next = !soundOn;
                    setSoundOn(next);
                    if (next) playSound("earn", true);
                  }}
                  style={{
                    flexShrink: 0,
                    background: soundOn ? GOLD_GRAD_S : "rgba(255,253,246,.88)",
                    border: `1px solid ${soundOn ? C.gold : "rgba(190,165,115,.42)"}`,
                    borderRadius: 999,
                    padding: "6px 8px",
                    color: C.textMid,
                    fontWeight: 900,
                    cursor: "pointer",
                    fontSize: 12,
                    minHeight: 34,
                  }}
                >
                  <span aria-hidden="true">{soundOn ? "🔊" : "🔇"}</span>
                </button>
                </>
              )}
            </div>
            {showFriendHeaderButton && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 6,
                  width: "100%",
                }}
              >
                <button
                  className="top-soft-chip friends-top-chip"
                  type="button"
                  aria-label="Open Spirit Friends"
                  onClick={() => setTab("friends")}
                  style={{
                    position: "relative",
                    flexShrink: 0,
                    background: tab === "friends" || hasFriendHeaderNotice
                      ? GOLD_GRAD_S
                      : "rgba(255,253,246,.88)",
                    border: `1px solid ${
                      tab === "friends" || hasFriendHeaderNotice ? C.gold : "rgba(190,165,115,.42)"
                    }`,
                    borderRadius: 999,
                    padding: "7px 10px",
                    color: C.textDark,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 900,
                    minHeight: 34,
                    boxShadow: tab === "friends" || hasFriendHeaderNotice
                      ? "0 6px 16px rgba(180,130,20,.14)"
                      : "0 3px 10px rgba(94,73,38,.05)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                  }}
                >
                  <span>Friends</span>
                  {hasFriendHeaderNotice && (
                    <span className="friend-heart-dot" aria-label="New friend activity">
                      ♥
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        </header>
        <div className="app-content" style={{ padding: "4px 8px 0" }}>
          {tab !== "today" && !isTabUnlocked(tab) ? (
            <FeatureLockedCard tab={tab} onGoToday={() => setTab("today")} />
          ) : (
            <>
              {/* ════ TODAY ════ */}
              {tab === "today" && (
                <div
                  className="today-page-calm"
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  <Card
                    className="current-stage-card today-hero-card"
                    style={{
                      padding: 14,
                      background: C.pageBg,
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                    }}
                  >
                    <div
                      style={{ display: "flex", gap: 14, alignItems: "center" }}
                    >
                      <div
                        style={{
                          flexShrink: 0,
                          animation: "float 3.5s ease-in-out infinite",
                        }}
                      >
                        <SpiritAvatar
                          stageKey={stage.key}
                          xp={xpInStage}
                          xpMax={stageRange}
                          stageAura={stage.aura}
                          stageGlow={stage.glow}
                          animated
                          equipped={equippedObjs}
                          gender={gender}
                          skinTone={skinTone}
                          hairColor={hairColor}
                          size={126}
                          showBar={false}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 10,
                            marginBottom: 4,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 12,
                                letterSpacing: 1.4,
                                textTransform: "uppercase",
                                color: C.textFaint,
                                fontWeight: 800,
                                marginBottom: 2,
                              }}
                            >
                              Current stage
                            </div>
                            <h2
                              className="serif"
                              style={{
                                margin: 0,
                                fontSize: 21,
                                color: C.textDark,
                                lineHeight: 1.15,
                              }}
                            >
                              {stage.name}
                            </h2>
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: 13.5,
                            color: C.textSoft,
                            lineHeight: 1.4,
                            marginBottom: 8,
                          }}
                        >
                          {stage.desc}
                        </div>
                        <button
                          type="button"
                          onClick={() => setTab("growth")}
                          style={{
                            marginTop: 2,
                            background: "rgba(255,253,248,.78)",
                            border: "1px solid rgba(184,147,75,.24)",
                            borderRadius: 999,
                            padding: "8px 13px",
                            color: C.textDark,
                            cursor: "pointer",
                            fontSize: 12.5,
                            fontWeight: 800,
                            fontFamily: "'Nunito',sans-serif",
                            boxShadow: "0 6px 14px rgba(72,50,20,.045)",
                          }}
                        >
                          Growth
                        </button>
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <FaithJourneyPanel
                        stats={faithJourneyStats}
                        open={showFaithJourney}
                        onToggle={() => setShowFaithJourney((open) => !open)}
                        stageName={stage.name}
                        nextStageName={nextStage?.name}
                        progressPercent={stage.xpMax === Infinity ? 100 : stageProgressPercent}
                        totalXP={totalXP}
                      />
                    </div>
                  </Card>

                  <div id="today-quests">
                    <Card className="today-quests-card gold-outline-card today-premium-quests-card" style={{ padding: 16 }}>
                      <button
                        type="button"
                        onClick={() => setShowTodayQuests((open) => !open)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          textAlign: "left",
                        }}
                      >
                        <div>
                          <h2
                            className="serif"
                            style={{
                              margin: "0 0 3px",
                              fontSize: 17,
                              color: C.textDark,
                            }}
                          >
                            🏆 Daily Quests
                          </h2>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 13,
                              color: C.textSoft,
                              lineHeight: 1.45,
                            }}
                          >
                            7 quests chosen for today.
                          </p>
                        </div>
                        <span className="placed-chip">
                          {showTodayQuests ? "Hide" : "Open"}
                        </span>
                      </button>
                      {showTodayQuests && (
                        <div style={{ marginTop: 14 }}>
                          <QuestEngine
                            quests={dailyQuests}
                            doneQuiz={doneQuiz}
                            doneTrivia={doneTrivia}
                            samaritan={samaritan}
                            preachLog={preachLog}
                            onAnswerQuiz={answerQuiz}
                            onAnswerTrivia={answerTrivia}
                            onLogSamaritan={logSamaritan}
                            onSavePreach={savePreach}
                          />
                        </div>
                      )}
                    </Card>
                  </div>

                  <div id="daily-walk">
                    <Card className={`today-faith-walk-card${checklistEditMode ? " checklist-edit-mode" : ""}`} style={{ padding: 16 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 10,
                          gap: 8,
                        }}
                      >
                        <div>
                          <div className="today-faith-walk-title-row">
                          <h2
                            className="serif"
                            style={{
                              margin: 0,
                              fontSize: 17,
                              color: C.textDark,
                            }}
                          >
                            Today’s Faith Walk
                          </h2>
                          <p
                            className="today-faith-walk-count"
                            style={{
                              margin: 0,
                              fontSize: 13,
                              color: C.textSoft,
                              fontWeight: 800,
                            }}
	                          >
	                            {doneCount}/{requiredTasks.length}
	                          </p>
                          </div>
	                        </div>
                        <button
                          onClick={() => {
                            setChecklistEditMode((editing) => {
                              const next = !editing;
                              if (!next) {
                                cancelEditTask();
                                setActiveTaskMenuId(null);
                              }
                              return next;
                            });
                            setShowAddTask(false);
                          }}
                          style={{
                            background: checklistEditMode ? GOLD_GRAD : "rgba(255,253,246,.76)",
                            border: `1px solid ${checklistEditMode ? C.gold : C.cardBorder}`,
                            borderRadius: 999,
                            padding: "8px 11px",
                            color: checklistEditMode ? C.textDark : C.textMid,
                            cursor: "pointer",
                            fontSize: 12.5,
                            fontWeight: 800,
                            whiteSpace: "nowrap",
                            minHeight: 38,
                          }}
                        >
                          {checklistEditMode ? "Done" : "Edit"}
                        </button>
                      </div>

                      <div
                        style={{
                          height: 7,
                          background: `${C.goldPale}40`,
                          borderRadius: 999,
                          overflow: "hidden",
                          marginBottom: 14,
                          boxShadow: "inset 0 1px 3px rgba(180,130,20,.15)",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.round(progress * 100)}%`,
                            background: GOLD_GRAD,
                            borderRadius: 999,
                            transition: "width .4s",
                          }}
                        />
                      </div>

                      {dayDone && (
                        <p
                          style={{
                            margin: "-4px 0 12px",
                            fontSize: 12.5,
                            color: "#6f8aa2",
                            lineHeight: 1.45,
                            fontWeight: 800,
                            textAlign: "center",
                          }}
                        >
                          Your walk will refresh tomorrow.
                        </p>
                      )}

                      {/* Task list */}
                      <div className="daily-walk-scroll">
                        {visibleTodayTasks.map((task) => {
                          const on = !!checked[task.id];
                          return (
                            <div
                              key={task.id}
                              className={`habit-edit-wrap${
                                activeTaskMenuId === task.id ? " menu-open" : ""
                              }`}
                              onTouchStart={(e) =>
                                checklistEditMode && handleTaskTouchStart(task.id, e)
                              }
                              onTouchMove={(e) =>
                                checklistEditMode && handleTaskTouchMove(task.id, e)
                              }
                              onTouchEnd={handleTaskTouchEnd}
                              onTouchCancel={handleTaskTouchEnd}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 7,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <button
                                  onClick={(e) => {
                                    if (checklistEditMode && activeTaskMenuId === task.id) {
                                      setActiveTaskMenuId(null);
                                      return;
                                    }
                                    toggleTask(task.id, e as ButtonEvent);
                                  }}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    setChecklistEditMode(true);
                                    openTaskMenu(task.id);
                                  }}
                                  className={`task-row${on ? " done" : ""}`}
                                  title={
                                    on
                                      ? "Tap to uncheck"
                                      : checklistEditMode
                                      ? "Tap to complete, or use the edit controls"
                                      : "Tap to complete"
                                  }
                                >
                                  <div
                                    style={{
                                      width: 34,
                                      height: 34,
                                      borderRadius: "50%",
                                      background: on
                                        ? GOLD_GRAD
                                        : "rgba(255,240,180,.5)",
                                      border: `2.5px solid ${
                                        on ? C.gold : C.goldPale
                                      }`,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      boxShadow: on
                                        ? `0 2px 10px rgba(180,130,20,.4)`
                                        : "none",
                                      transition: "all .25s",
                                      fontSize: 17,
                                    }}
                                  >
                                    {on ? (
                                      <span
                                        style={{
                                          color: C.textDark,
                                          fontWeight: 900,
                                          fontSize: 16,
                                        }}
                                      >
                                        ✓
                                      </span>
                                    ) : (
                                      <span>{task.icon}</span>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      flex: 1,
                                      minWidth: 0,
                                      marginLeft: 12,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: 600,
                                        color: on ? C.textDark : C.textMid,
                                        fontSize: 15,
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {task.label}
                                    </div>
                                    {on && !quietMode && (
                                      <div
                                        style={{
                                          fontSize: 13.5,
                                          color: C.gold,
                                          fontWeight: 500,
                                          marginTop: 1,
                                        }}
                                      >
                                        Righteousness earned
                                      </div>
                                    )}
                                  </div>
                                </button>
                                <div className="habit-row-actions">
                                  <button
                                    onClick={() => startEditTask(task)}
                                    aria-label={`Edit ${task.label}`}
                                    style={{
                                      background: "rgba(255,253,246,.54)",
                                      border: `1px solid ${C.cardBorder}`,
                                      borderRadius: 10,
                                      padding: "0 8px",
                                      minWidth: 36,
                                      height: 36,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      fontSize: 12,
                                      color: C.textFaint,
                                      flexShrink: 0,
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => removeTask(task.id)}
                                    aria-label={`Remove ${task.label}`}
                                    style={{
                                      background: "rgba(255,253,246,.48)",
                                      border: `1px solid ${C.cardBorder}`,
                                      borderRadius: 10,
                                      width: 34,
                                      height: 36,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      fontSize: 16,
                                      color: C.textFaint,
                                      flexShrink: 0,
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                              {editingTaskId === task.id && (
                                <div
                                  style={{
                                    background: "rgba(255,253,246,.72)",
                                    border: `1px solid ${C.cardBorder}`,
                                    borderRadius: 14,
                                    padding: 10,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 6,
                                      marginBottom: 8,
                                      overflowX: "auto",
                                      paddingBottom: 2,
                                    }}
                                  >
                                    {iconOpts.map((ic) => (
                                      <button
                                        key={ic}
                                        onClick={() => setEditingTaskIcon(ic)}
                                        style={{
                                          background:
                                            editingTaskIcon === ic
                                              ? GOLD_GRAD
                                              : "rgba(255,253,240,.8)",
                                          border: `1px solid ${C.goldPale}`,
                                          borderRadius: 10,
                                          padding: 7,
                                          fontSize: 17,
                                          cursor: "pointer",
                                          flexShrink: 0,
                                          minWidth: 38,
                                          minHeight: 38,
                                        }}
                                      >
                                        {ic}
                                      </button>
                                    ))}
                                  </div>
                                  <input
                                    value={editingTaskLabel}
                                    onChange={(e) =>
                                      setEditingTaskLabel(e.target.value)
                                    }
                                    onKeyDown={(e) =>
                                      e.key === "Enter" && saveTaskEdit()
                                    }
                                    placeholder="Habit name"
                                    className="sg-input"
                                    autoFocus
                                  />
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: 8,
                                      marginTop: 8,
                                    }}
                                  >
                                    <GoldBtn
                                      onClick={saveTaskEdit}
                                      disabled={!editingTaskLabel.trim()}
                                      style={{ minHeight: 40 }}
                                    >
                                      Save
                                    </GoldBtn>
                                    <button
                                      onClick={cancelEditTask}
                                      style={{
                                        background: "rgba(255,253,240,.7)",
                                        border: `1px solid ${C.cardBorder}`,
                                        borderRadius: 12,
                                        padding: "10px 12px",
                                        color: C.textMid,
                                        cursor: "pointer",
                                        fontSize: 13,
                                        fontWeight: 500,
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div style={{ marginTop: 12 }}>
                        {showAddTask ? (
                          <div
                            style={{
                              padding: 14,
                              background: `${C.goldShine}40`,
                              borderRadius: 14,
                              border: `1px solid ${C.gold}40`,
                            }}
                          >
                            <p
                              style={{
                                margin: "0 0 10px",
                                fontSize: 13,
                                color: C.textSoft,
                              }}
                            >
                              Add a custom faith habit
                            </p>
                            <div
                              style={{
                                display: "flex",
                                gap: 6,
                                marginBottom: 10,
                                overflowX: "auto",
                                paddingBottom: 4,
                              }}
                            >
                              {iconOpts.map((ic) => (
                                <button
                                  key={ic}
                                  onClick={() => setNewTaskIcon(ic)}
                                  style={{
                                    background:
                                      newTaskIcon === ic
                                        ? GOLD_GRAD
                                        : "rgba(255,253,240,.8)",
                                    border: `1px solid ${C.goldPale}`,
                                    borderRadius: 10,
                                    padding: 8,
                                    fontSize: 18,
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    minWidth: 40,
                                    minHeight: 40,
                                  }}
                                >
                                  {ic}
                                </button>
                              ))}
                            </div>
                            <input
                              value={newTaskLabel}
                              onChange={(e) => setNewTaskLabel(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && addTask()}
                              placeholder="e.g. Listen to worship"
                              className="sg-input"
                              autoFocus
                            />
                            <div
                              style={{ display: "flex", gap: 8, marginTop: 8 }}
                            >
                              <GoldBtn onClick={addTask} style={{ flex: 1 }}>
                                Add Habit
                              </GoldBtn>
                              <button
                                onClick={() => setShowAddTask(false)}
                                style={{
                                  background: "rgba(255,253,240,.7)",
                                  border: `1px solid ${C.cardBorder}`,
                                  borderRadius: 12,
                                  padding: "12px 16px",
                                  color: C.textMid,
                                  cursor: "pointer",
                                  fontSize: 14,
                                  fontWeight: 600,
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowAddTask(true)}
                            style={{
                              width: "100%",
                              background:
                                "linear-gradient(135deg,rgba(246,250,255,.92),rgba(236,247,255,.82))",
                              border: "1.5px solid rgba(91,143,184,.38)",
                              borderRadius: 14,
                              padding: 14,
                              color: "#456d8f",
                              cursor: "pointer",
                              fontSize: 14,
                              fontWeight: 900,
                              minHeight: 48,
                            }}
                          >
                            + Add custom habit
                          </button>
                        )}
                      </div>

                      <button
                        onClick={completeDay}
                        disabled={doneCount < requiredTasks.length || dayDone}
                        className={`reward-action-btn${
                          doneCount >= requiredTasks.length && !dayDone
                            ? ""
                            : " walk-incomplete"
                        }`}
                        style={{
                          width: "100%",
                          marginTop: 12,
                          background:
                            doneCount >= requiredTasks.length && !dayDone
                              ? GOLD_GRAD
                              : "rgba(255,253,246,.84)",
                          border: `1.5px solid ${
                            doneCount >= requiredTasks.length && !dayDone
                              ? C.gold
                              : C.cardBorder
                          }`,
                          borderRadius: 14,
                          padding: "12px 14px",
                          fontSize: 14,
                          whiteSpace: "nowrap",
                          color:
                            doneCount >= requiredTasks.length && !dayDone
                              ? "#FFFDF8"
                              : C.textFaint,
                          fontWeight: 900,
                          cursor:
                            doneCount >= requiredTasks.length && !dayDone
                              ? "pointer"
                              : "default",
                          minHeight: 48,
                        }}
                      >
                        {dayDone
                          ? "✓ Done!"
                          : doneCount >= requiredTasks.length
                          ? "Let My Spirit Shine"
                          : "Complete Day"}
                      </button>
                    </Card>
                  </div>

                </div>
              )}

              {/* ════ PRAYERS ════ */}
              {tab === "prayers" && (
                <PrayersPage
                  prayers={prayerFriends}
                  today={today}
                  dailyPrayerFocus={dailyPrayerFocus}
                  spiritName={spiritName}
                  onAddPrayer={addPrayerFriend}
                  onPrayForPerson={prayForFriend}
                  onMarkAnswered={markPrayerAnswered}
                  onDeletePrayer={deletePrayer}
                  onTogglePublicShare={togglePrayerPublicShare}
                  onUpdatePrayer={updatePrayer}
                  onSubmitPublicTestimony={submitPublicTestimony}
                  currentUser={cloudUser}
                  friendProfile={friendProfile}
                  spiritFriends={spiritFriends}
                  incomingFriendRequests={incomingFriendRequests}
                  outgoingFriendRequests={outgoingFriendRequests}
                  sharedPrayers={sharedPrayers}
                  receivedPrayerSignals={receivedPrayerSignals}
                  accountabilityPartnerUid={accountabilityPartnerUid}
                  accountabilityPartnerUids={accountabilityPartnerUids}
                  friendStatus={friendStatus}
                  onSignIn={handleGoogleSignIn}
                  onSendFriendRequest={sendFriendRequest}
                  onRespondFriendRequest={respondFriendRequest}
                  onCancelFriendRequest={cancelFriendRequest}
                  onSendAccountabilityRequest={sendAccountabilityRequest}
                  onRespondAccountabilityRequest={respondAccountabilityRequest}
                  onRemoveAccountabilityPartner={removeAccountabilityPartner}
                  onDeleteSpiritFriend={deleteSpiritFriend}
                  onCompleteAccountabilityFocus={completeAccountabilityFocus}
                  onSendPrayedForFriend={sendPrayedForFriend}
                  onSendBlessingToFriend={sendBlessingToFriend}
                  onSharePrayerWithFriends={sharePrayerWithFriends}
                />
              )}

              {/* ════ SPIRIT FRIENDS ════ */}
              {tab === "friends" && (
                <PrayersPage
                  prayers={prayerFriends}
                  today={today}
                  dailyPrayerFocus={dailyPrayerFocus}
                  spiritName={spiritName}
                  friendsOnly
                  onAddPrayer={addPrayerFriend}
                  onPrayForPerson={prayForFriend}
                  onMarkAnswered={markPrayerAnswered}
                  onDeletePrayer={deletePrayer}
                  onTogglePublicShare={togglePrayerPublicShare}
                  onUpdatePrayer={updatePrayer}
                  onSubmitPublicTestimony={submitPublicTestimony}
                  currentUser={cloudUser}
                  friendProfile={friendProfile}
                  spiritFriends={spiritFriends}
                  incomingFriendRequests={incomingFriendRequests}
                  outgoingFriendRequests={outgoingFriendRequests}
                  sharedPrayers={sharedPrayers}
                  receivedPrayerSignals={receivedPrayerSignals}
                  accountabilityPartnerUid={accountabilityPartnerUid}
                  accountabilityPartnerUids={accountabilityPartnerUids}
                  friendStatus={friendStatus}
                  onSignIn={handleGoogleSignIn}
                  onSendFriendRequest={sendFriendRequest}
                  onRespondFriendRequest={respondFriendRequest}
                  onCancelFriendRequest={cancelFriendRequest}
                  onSendAccountabilityRequest={sendAccountabilityRequest}
                  onRespondAccountabilityRequest={respondAccountabilityRequest}
                  onRemoveAccountabilityPartner={removeAccountabilityPartner}
                  onDeleteSpiritFriend={deleteSpiritFriend}
                  onCompleteAccountabilityFocus={completeAccountabilityFocus}
                  onSendPrayedForFriend={sendPrayedForFriend}
                  onSendBlessingToFriend={sendBlessingToFriend}
                  onSharePrayerWithFriends={sharePrayerWithFriends}
                />
              )}

              {/* ════ TESTIMONY WALL ════ */}
              {tab === "wall" && (
                <TestimonyWallPage
                  currentUser={cloudUser}
                  onAdminSignIn={handleGoogleSignIn}
                  onApprovedCountChange={setApprovedTestimonyCount}
                  onSaveDraft={saveTestimonyDraftToBook}
                  testimonyBookItems={testimonyBook}
                  onEditTestimonyEntry={editTestimonyEntry}
                  onDeleteTestimonyEntry={deleteTestimonyEntry}
                />
              )}

              {/* ════ JOURNAL ════ */}
              {tab === "journal" && (
                <div
                  className="journal-page premium-journal-page"
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  {viewingJournalEntry && (
                    <div className="journal-viewer-page">
                      <button
                        type="button"
                        className="journal-back-btn"
                        onClick={() => setViewingJournalEntry(null)}
                      >
                        ← Back to Journal
                      </button>
                      <Card className="journal-view-entry-card">
                        <SectionLabel>Full Reflection</SectionLabel>
                        <h2 className="serif">{viewingJournalEntry.date}</h2>
                        <div className="journal-view-full-text">
                          {viewingJournalEntry.text}
                        </div>
                        <div className="journal-view-actions">
                          <button
                            type="button"
                            className="journal-view-btn"
                            onClick={() => {
                              setEditingEntry(viewingJournalEntry.date);
                              setEditText(viewingJournalEntry.text);
                              setViewingJournalEntry(null);
                            }}
                          >
                            Edit Reflection
                          </button>
                          <button
                            type="button"
                            className="journal-delete-btn journal-view-delete-btn"
                            aria-label={`Delete reflection from ${viewingJournalEntry.date}`}
                            onClick={() => {
                              if (!window.confirm("Delete this reflection?")) return;
                              const entryDate = viewingJournalEntry.date;
                              setEntries((p) => p.filter((e) => e.date !== entryDate));
                              if (entryDate === today) setJournal("");
                              setViewingJournalEntry(null);
                              showToast("Reflection deleted");
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </Card>
                    </div>
                  )}

                  <Card className="journal-write-card" style={viewingJournalEntry ? { display: "none" } : undefined}>
                    <h2
                      className="serif"
                      style={{
                        margin: "0 0 4px",
                        fontSize: 18,
                        color: C.textDark,
                      }}
                    >
                      Daily Reflection
                    </h2>
                    <p
                      style={{
                        margin: "0 0 12px",
                        fontSize: 13,
                        color: C.textSoft,
                        lineHeight: 1.5,
                      }}
                    >
                      {today} · How did today go?
                    </p>
                    <textarea
                      value={journal}
                      onChange={(e) => setJournal(e.target.value)}
                      placeholder={
                        "How did today go?\n\nWhat did I notice God doing?\n\nWhat am I thankful for?"
                      }
                      className="sg-textarea"
                      style={{ minHeight: 200 }}
                    />
                    <GoldBtn
                      onClick={saveJournal}
                      style={{
                        marginTop: 12,
                        width: "100%",
                        background: journalSaved ? C.greenPale : GOLD_GRAD,
                        color: journalSaved ? C.green : C.textDark,
                        boxShadow: journalSaved ? "none" : undefined,
                      }}
                    >
                      {journalSaved
                        ? "✓ Reflection Saved"
                        : "Save Reflection"}
                    </GoldBtn>
                  </Card>

                  <Card className="journal-history-card" style={viewingJournalEntry ? { display: "none" } : undefined}>
                    <h2
                      className="serif"
                      style={{
                        margin: "0 0 14px",
                        fontSize: 18,
                        color: C.textDark,
                      }}
                    >
                      Past Reflections
                    </h2>
                    {entries.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-state-icon"><JournalIcon size={34} /></div>
                        <p className="empty-state-title">No reflections yet.</p>
                        <p className="empty-state-copy">
                          Begin with one honest sentence about what God is teaching you today.
                        </p>
                      </div>
                    ) : (
                      <div className="month-scroll-list">
                        {journalMonthGroups.map((group) => {
                          const open = openJournalMonths[group.key] !== false;
                          return (
                            <div key={group.key} className="month-group">
                              <button
                                type="button"
                                className="month-toggle"
                                onClick={() => toggleJournalMonth(group.key)}
                                aria-expanded={open}
                              >
                                <span>{group.label}</span>
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <span className="placed-chip">
                                    {group.items.length}
                                  </span>
                                  <span
                                    className={
                                      "testimony-stone-chevron" +
                                      (open ? " open" : "")
                                    }
                                  >
                                    ⌄
                                  </span>
                                </span>
                              </button>
                              {open && (
                                <div className="month-items">
                                  {group.items.map((entry) => (
                                    <div
                                      key={entry.date}
                                      className="journal-entry-card"
                                      style={{
                                        background: "rgba(255,253,240,.6)",
                                        borderRadius: 14,
                                        padding: 14,
                                        border: `1px solid ${C.cardBorder}`,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          marginBottom: 8,
                                        }}
                                      >
                                        <div
                                          className="journal-entry-date"
                                          style={{
                                            fontSize: 12,
                                            color: C.textFaint,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {entry.date}
                                        </div>
                                        {editingEntry === entry.date ? (
                                          <div
                                            style={{ display: "flex", gap: 6 }}
                                          >
                                            <GoldBtn
                                              onClick={() => {
                                                const t = editText.trim();
                                                if (!t) return;
                                                setEntries((p) =>
                                                  p.map((e) =>
                                                    e.date === entry.date
                                                      ? { ...e, text: t }
                                                      : e
                                                  )
                                                );
                                                setEditingEntry(null);
                                                showToast("Entry updated ✓");
                                              }}
                                              style={{
                                                padding: "8px 14px",
                                                fontSize: 13,
                                              }}
                                            >
                                              Save
                                            </GoldBtn>
                                            <button
                                              onClick={() => {
                                                setEditingEntry(null);
                                                setEditText("");
                                              }}
                                              style={{
                                                background:
                                                  "rgba(255,253,240,.7)",
                                                border: `1px solid ${C.cardBorder}`,
                                                borderRadius: 10,
                                                padding: "8px 12px",
                                                color: C.textFaint,
                                                cursor: "pointer",
                                                fontSize: 13,
                                              }}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        ) : (
                                          <div style={{ display: "flex", gap: 6 }}>
                                            <button
                                              className="journal-view-btn"
                                              onClick={() => setViewingJournalEntry(entry)}
                                              style={{
                                                background:
                                                  "rgba(241,249,255,.72)",
                                                border: `1px solid rgba(91,143,184,.24)`,
                                                borderRadius: 10,
                                                padding: "8px 12px",
                                                color: "#4f6e88",
                                                cursor: "pointer",
                                                fontSize: 13,
                                                fontWeight: 700,
                                              }}
                                            >
                                              View
                                            </button>
                                            <button
                                              className="journal-edit-btn"
                                              onClick={() => {
                                                setEditingEntry(entry.date);
                                                setEditText(entry.text);
                                              }}
                                              style={{
                                                background:
                                                  "rgba(255,253,240,.7)",
                                                border: `1px solid ${C.gold}60`,
                                                borderRadius: 10,
                                                padding: "8px 12px",
                                                color: C.goldDeep,
                                                cursor: "pointer",
                                                fontSize: 13,
                                                fontWeight: 700,
                                              }}
                                            >
                                              ✏️
                                            </button>
                                            <button
                                              className="journal-delete-btn"
                                              aria-label={`Delete reflection from ${entry.date}`}
                                              onClick={() => {
                                                if (!window.confirm("Delete this reflection?")) return;
                                                setEntries((p) =>
                                                  p.filter((e) => e.date !== entry.date)
                                                );
                                                if (entry.date === today) setJournal("");
                                                showToast("Reflection deleted");
                                              }}
                                              style={{
                                                background:
                                                  "rgba(255,253,240,.7)",
                                                border: `1px solid ${C.cardBorder}`,
                                                borderRadius: 10,
                                                width: 36,
                                                minHeight: 36,
                                                color: C.textFaint,
                                                cursor: "pointer",
                                                fontSize: 18,
                                                fontWeight: 900,
                                                lineHeight: 1,
                                              }}
                                            >
                                              ×
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      {editingEntry === entry.date ? (
                                        <textarea
                                          value={editText}
                                          onChange={(e) =>
                                            setEditText(e.target.value)
                                          }
                                          className="sg-textarea"
                                          style={{
                                            minHeight: 100,
                                            fontSize: 13,
                                          }}
                                          autoFocus
                                        />
                                      ) : (
                                        <div
                                          className="serif journal-entry-text"
                                          style={{
                                            fontSize: 13,
                                            color: C.textMid,
                                            lineHeight: 1.7,
                                            fontStyle: "normal",
                                            whiteSpace: "pre-wrap",
                                          }}
                                        >
                                          {journalEntryPreview(entry)}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                </div>
              )}

              {/* ════ HOME ════ */}
              {tab === "home" && (
	                <HeavenlyHomePage
	                  righteousness={righteousness}
	                  ownedHomeItems={ownedHomeItems}
                  placedHomeItems={placedHomeItems}
                  homeBlooms={previewHomeBlooms}
                  spiritVisitors={previewSpiritVisitors}
                  expandedHomeItem={expandedHomeItem}
                  setExpandedHomeItem={setExpandedHomeItem}
                  onBuy={tryBuyHomeItem}
                  onPlace={placeHomeItem}
                  onRemovePlaced={removeHomeItem}
                  neglectDarkness={neglectDarkness}
                  homeRestored={homeRestored}
                  homeBlessed={homeBlessed}
                  homeTransforming={homeTransforming}
                  quietMode={quietMode}
                  homeCareState={homeCareState}
                  faithFlightProgress={faithFlightProgress}
                  faithFlightUnlocked={effectiveFaithFlightUnlocked}
                  faithFlightActive={faithFlightActive}
                  onBlessHome={blessHomeToday}
                  onFaithFlight={letSpiritFly}
                  onGoToday={() => {
                    setTab("today");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  onGoPrayer={() => {
                    setTab("prayers");
                    window.setTimeout(() => {
                      document
                        .getElementById("prayer-page-top")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 80);
                  }}
                  onGoJournal={() => {
                    setTab("journal");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  spirit={{
                    stageKey: stage.key,
                    xp: xpInStage,
                    xpMax: stageRange,
                    stageAura: stage.aura,
                    stageGlow: stage.glow,
                    animated: true,
                    equipped: equippedObjs,
                    gender,
                    skinTone,
                    hairColor,
                  }}
                />
              )}

              {/* ════ SHOP ════ */}
              {tab === "shop" && (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  <button
                    onClick={() => setTab("growth")}
                    style={{
                      alignSelf: "flex-start",
                      background: "rgba(255,253,246,.82)",
                      border: `1px solid ${C.cardBorder}`,
                      borderRadius: 999,
                      padding: "9px 13px",
                      color: C.goldDeep,
                      fontSize: 13,
                      fontWeight: 900,
                      cursor: "pointer",
                    }}
                  >
                    ← Back to Growth
                  </button>
                  <Card
                    style={{
                      background: `linear-gradient(135deg,rgba(255,253,246,.98),rgba(255,248,223,.74))`,
                      border: "none",
                      outline: "none",
                      boxShadow: "0 10px 28px rgba(82,55,15,.08)",
                      padding: 16,
                    }}
                  >
	                    <div
	                      className="armor-shop-hero"
	                      style={{
	                        display: "flex",
	                        justifyContent: "space-between",
	                        alignItems: "center",
	                        gap: 10,
	                      }}
	                    >
	                      <div style={{ minWidth: 0 }}>
                        <h2
                          className="serif"
                          style={{
                            margin: "0 0 2px",
                            fontSize: 18,
                            color: C.textDark,
                          }}
                        >
                          🛡️ Armor Closet
                        </h2>
                        <p
                          style={{ margin: 0, fontSize: 13, color: C.textMid }}
                        >
                          Equip truth, peace, faith, salvation, and the Word.
                        </p>
                      </div>
	                      <div className="armor-righteousness-count" style={{ textAlign: "right", minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 22,
                            lineHeight: 1,
                            fontWeight: 900,
                            color: C.textDark,
                            whiteSpace: "nowrap",
                          }}
                        >
                          ✨ {righteousness}
                        </div>
                      </div>
                    </div>
                  </Card>

                  <button
                    onClick={openArmorCloset}
                    style={{
                      background: "rgba(255,252,210,.9)",
                      border: `2px solid ${C.gold}`,
                      borderRadius: 16,
                      padding: "16px 18px",
                      cursor: "pointer",
                      color: C.textDark,
                      fontWeight: 800,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      boxShadow: `0 4px 18px rgba(180,130,20,.18)`,
                      fontSize: 15,
                      minHeight: 56,
                      width: "100%",
                    }}
                  >
                    <span style={{ fontSize: 26 }}>🛡️</span>
                    <span style={{ flex: 1, textAlign: "left" }}>
                      Open Armor Closet
                    </span>
                    <span
                      style={{
                        background: GOLD_GRAD,
                        borderRadius: 999,
                        padding: "4px 12px",
                        fontSize: 13,
                        color: C.textDark,
                        fontWeight: 800,
                      }}
                    >
                      {closetOwnedIds.length} owned
                    </span>
                  </button>

                  <Card
                    className="sticky-preview-card"
                    style={{
                      padding: 14,
                      background:
                        "linear-gradient(135deg,rgba(255,253,246,.96),rgba(255,248,223,.55))",
                      borderColor: `${C.gold}40`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <div>
                        <h3
                          className="serif"
                          style={{ margin: 0, fontSize: 16, color: C.textDark }}
                        >
                          Spirit Preview
                        </h3>
                        <p
                          style={{
                            margin: "2px 0 0",
                            fontSize: 13.5,
                            color: C.textSoft,
                          }}
	                        >
	                          {previewClothingItem
	                            ? `Previewing ${previewClothingItem.name}.`
	                            : "Your current spirit look."}
	                        </p>
	                      </div>
	                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
	                        <span className="sticky-preview-label">
	                          {previewClothingItem ? "Preview" : "Current look"}
	                        </span>
	                        {previewClothingItem && (
	                          <button
	                            type="button"
	                            onClick={() => setPreviewClothingItemId(null)}
	                            className="soft-action-btn muted"
	                            style={{
	                              minHeight: 30,
	                              padding: "5px 9px",
	                              fontSize: 12,
	                            }}
	                          >
	                            Clear
	                          </button>
	                        )}
	                      </div>
	                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 14 }}
                    >
                      <div
                        style={{
                          flexShrink: 0,
                          animation: "float 3.5s ease-in-out infinite",
                        }}
                      >
                        <SpiritAvatar
                          stageKey={stage.key}
                          xp={xpInStage}
                          xpMax={stageRange}
                          stageAura={stage.aura}
	                          stageGlow={stage.glow}
	                          animated
	                          equipped={previewEquippedObjs}
	                          gender={gender}
                          skinTone={skinTone}
                          hairColor={hairColor}
                          size={112}
                          showBar={false}
                        />
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          color: C.textSoft,
                          lineHeight: 1.55,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        Open the Armor Closet to equip your spirit. You can also
                        sell pieces you no longer want for 50% back.
                      </p>
                    </div>
                  </Card>

                  {(() => {
                    const shopSlots = SLOT_ORDER.filter((slot) =>
                      CLOTHES.some((c) => c.slot === slot)
                    );
                    const currentSlot: Slot = shopSlots.includes(activeShopSlot)
                      ? activeShopSlot
                      : shopSlots[0];
                    const currentItems = CLOTHES.filter(
                      (c) => c.slot === currentSlot
                    );
                    const slotIcon: Record<Slot, string> = {
                      set: "✦",
                      hat: "🪖",
                      halo: "⭕",
                      crown: "👑",
                      wings: "🪽",
                      robe: "✦",
                      hand: "⚔️",
                      shield: "🛡️",
                      belt: "💛",
                      sash: "🌈",
                      feet: "👟",
                    };

                    return (
                      <>
                        <Card
                          style={{
                            padding: 16,
                            background: "rgba(255,253,246,.9)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 10,
                              marginBottom: 14,
                            }}
                          >
                            <div>
                              <h3
                                className="serif"
                                style={{
                                  margin: 0,
                                  fontSize: 17,
                                  color: C.textDark,
                                }}
                              >
                                Closet Pieces
                              </h3>
                              <p
                                style={{
                                  margin: "3px 0 0",
                                  fontSize: 13.5,
                                  color: C.textFaint,
                                }}
                              >
                                Earn righteousness through habits & quests to
                                unlock more.
                              </p>
                            </div>
                            <span
                              style={{
                                background: "rgba(255,248,223,.78)",
                                border: `1px solid ${C.goldPale}`,
                                borderRadius: 999,
                                padding: "5px 10px",
                                fontSize: 13.5,
                                color: C.goldDeep,
                                fontWeight: 900,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {closetOwnedIds.length}/{CLOTHES.length}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 10,
                              overflowX: "auto",
                              padding: "2px 2px 8px",
                            }}
                          >
                            {shopSlots.map((slot) => {
                              const total = CLOTHES.filter(
                                (c) => c.slot === slot
                              ).length;
                              const ownedInSlot = CLOTHES.filter(
                                (c) =>
                                  c.slot === slot && closetOwnedIds.includes(c.id)
                              ).length;
                              const active = currentSlot === slot;
                              return (
                                <button
                                  key={slot}
	                                  onClick={() => {
	                                    setActiveShopSlot(slot);
	                                    setExpandedShopItem(null);
	                                    setPreviewClothingItemId(null);
	                                  }}
                                  className={`shop-category-card${
                                    active ? " active" : ""
                                  }`}
                                >
                                  <div className="shop-category-icon">
                                    {slotIcon[slot]}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 900,
                                      lineHeight: 1.18,
                                      position: "relative",
                                      zIndex: 1,
                                    }}
                                  >
                                    {SLOT_LABELS[slot]}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      color: active ? C.textMid : C.textFaint,
                                      fontWeight: 800,
                                      marginTop: 5,
                                      position: "relative",
                                      zIndex: 1,
                                    }}
                                  >
                                    {ownedInSlot}/{total}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </Card>

                        <div>
                          <SectionDivider label={SLOT_LABELS[currentSlot]} />
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                              gap: 10,
                            }}
                          >
                            {currentItems.map((item) => {
                              const owned = closetOwnedIds.includes(item.id),
                                equipped = equippedItems.includes(item.id);
                              const expanded = expandedShopItem === item.id;
                              const canAfford =
                                ARMOR_CLOSET_TEST_MODE ||
                                righteousness >= item.cost;
                              return (
                                <div
	                                  key={item.id}
	                                  className={`shop-card${
	                                    owned ? " owned" : ""
	                                  }${expanded ? " expanded" : ""} armor-shop-card`}
                                  onClick={() =>
                                    setExpandedShopItem(
                                      expanded ? null : item.id
                                    )
                                  }
                                  style={{
                                    background: owned
                                      ? `linear-gradient(160deg,rgba(255,253,246,.98),${item.color}74)`
                                      : `linear-gradient(160deg,rgba(255,253,246,.92),${item.color}38)`,
                                    minHeight: expanded ? 196 : 150,
                                    cursor: "pointer",
                                  }}
                                >
                                  <div
                                    style={{
                                      position: "relative",
                                      zIndex: 1,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      flex: 1,
                                    }}
                                  >
                                    <ShopGameIcon item={item} />
	                                    <div
	                                      className="armor-item-name"
	                                      style={{
	                                        fontSize: 13.5,
	                                        fontWeight: 900,
	                                        color: C.textDark,
                                        marginBottom: 4,
                                        lineHeight: 1.25,
                                        textAlign: "center",
                                      }}
                                    >
                                      {item.name}
                                    </div>
                                    <span
                                      className="meaning-chip"
                                      style={{ marginBottom: 4 }}
                                    >
                                      {clothingMeaning(item)}
                                    </span>
                                    {!ARMOR_CLOSET_TEST_MODE &&
                                      !canAfford &&
                                      !owned && (
                                        <div
                                          style={{
                                            fontSize: 13,
                                            color: C.red,
                                            fontWeight: 700,
                                            marginBottom: 4,
                                          }}
                                        >
                                          Need ✨{item.cost - righteousness}{" "}
                                          more
                                        </div>
                                      )}
                                    <div
                                      style={{
                                        fontSize: 13,
                                        color: C.textFaint,
                                        textAlign: "center",
                                        marginBottom: expanded ? 8 : 10,
                                        fontWeight: 800,
                                      }}
                                    >
                                      {expanded
                                        ? "Tap again to close"
                                        : "Tap for details"}
                                    </div>
                                    {expanded && (
                                      <div
                                        style={{
                                          fontSize: 13.5,
                                          color: C.textSoft,
                                          marginBottom: 10,
                                          lineHeight: 1.5,
                                          flex: 1,
                                          textAlign: "center",
                                        }}
                                      >
                                        {item.desc}
                                      </div>
                                    )}
                                  </div>
		                                  {!owned ? (
		                                    <div
		                                      className="armor-action-row"
		                                      style={{
		                                        display: "flex",
		                                        gap: 6,
		                                        position: "relative",
	                                        zIndex: 1,
	                                      }}
	                                    >
	                                      <button
	                                        type="button"
	                                        onClick={(e) => {
	                                          e.stopPropagation();
	                                          setPreviewClothingItemId((current) =>
	                                            current === item.id ? null : item.id
	                                          );
	                                          window.setTimeout(() => {
	                                            document
	                                              .querySelector(
	                                                ".shop-card, .sticky-preview-card"
	                                              )
	                                              ?.scrollIntoView({
	                                                behavior: "smooth",
	                                                block: "center",
	                                              });
	                                          }, 60);
	                                        }}
	                                        className={`soft-action-btn${
	                                          previewClothingItemId === item.id
	                                            ? ""
	                                            : " muted"
	                                        }`}
	                                        style={{
	                                          flex: 0.85,
	                                          minHeight: 40,
	                                          padding: "10px 0",
	                                          fontSize: 13,
	                                        }}
	                                      >
	                                        {previewClothingItemId === item.id
	                                          ? "View"
	                                          : "Preview"}
	                                      </button>
	                                    <button
	                                      onClick={(e) => {
	                                        e.stopPropagation();
	                                        tryBuyClothes(item);
                                      }}
                                      style={{
                                        position: "relative",
                                        zIndex: 1,
                                        width: "100%",
                                        background: canAfford
                                          ? GOLD_GRAD
                                          : `${C.goldPale}40`,
                                        border: `1px solid ${
                                          canAfford ? C.gold : C.goldPale
                                        }`,
                                        borderRadius: 12,
                                        padding: "10px 0",
                                        color: canAfford
                                          ? C.textDark
                                          : C.textFaint,
                                        fontWeight: 900,
                                        cursor: canAfford
                                          ? "pointer"
                                          : "default",
                                        fontSize: 13,
                                        minHeight: 40,
	                                      }}
		                                    >
		                                      {ARMOR_CLOSET_TEST_MODE
		                                        ? "Add"
		                                        : `✨ ${item.cost} · Buy`}
		                                    </button>
		                                    </div>
		                                  ) : (
		                                    <div
		                                      className="armor-action-row owned"
		                                      style={{
		                                        display: "flex",
		                                        gap: 6,
                                        position: "relative",
                                        zIndex: 1,
	                                      }}
	                                    >
	                                      <button
	                                        type="button"
	                                        onClick={(e) => {
	                                          e.stopPropagation();
	                                          setPreviewClothingItemId((current) =>
	                                            current === item.id ? null : item.id
	                                          );
	                                        }}
	                                        className={`soft-action-btn${
	                                          previewClothingItemId === item.id
	                                            ? ""
	                                            : " muted"
	                                        }`}
	                                        style={{
	                                          flex: 0.9,
	                                          minHeight: 40,
	                                          padding: "10px 0",
	                                          fontSize: 13,
	                                        }}
	                                      >
	                                        {previewClothingItemId === item.id
	                                          ? "Viewing"
	                                          : "Preview"}
	                                      </button>
	                                      <button
	                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openArmorCloset();
                                        }}
                                        style={{
                                          flex: 2,
                                          background: equipped
                                            ? GOLD_GRAD
                                            : `${C.goldPale}50`,
                                          border: `1px solid ${C.gold}`,
                                          borderRadius: 12,
                                          padding: "10px 0",
                                          color: C.textDark,
                                          fontWeight: 900,
                                          cursor: "pointer",
                                          fontSize: 13,
                                          minHeight: 40,
                                        }}
                                      >
	                                        {equipped
	                                          ? "Equipped"
	                                          : "Closet"}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const item_ = CLOTHES.find(
                                            (c) => c.id === item.id
                                          )!;
                                          setShowCloset(false);
                                          sellClothes(item_);
                                        }}
                                        style={{
                                          flex: 1,
                                          background: "rgba(255,240,220,.7)",
                                          border: `1px solid ${C.cardBorder}`,
                                          borderRadius: 12,
                                          padding: "10px 0",
                                          color: C.textFaint,
                                          fontWeight: 800,
                                          cursor: "pointer",
                                          fontSize: 13.5,
                                          minHeight: 40,
                                        }}
                                      >
                                        Sell
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    );
                  })()}

                </div>
              )}

              {/* ════ JOURNEY ════ */}
              {tab === "growth" && (
                <div
                  className="growth-page-calm"
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  <Card
                    className="gold-outline-card growth-account-card"
                    style={{
                      padding: 15,
                      outline: `2px solid ${C.gold}`,
                      outlineOffset: -3,
                    }}
                  >
                    <SectionLabel>Account</SectionLabel>
                    <p style={{ margin: "0 0 12px", fontSize: 13.5, color: C.textSoft, lineHeight: 1.55 }}>
                      {cloudUser?.email
                        ? `Signed in as ${cloudDisplayName}. Your garden is saved across devices.`
                        : "Sign in to save your garden across devices."}
                    </p>
                    <div
                      style={{
                        background: cloudUser?.email
                          ? "rgba(236,253,245,.72)"
                          : "rgba(246,250,255,.62)",
                        border: `1px solid ${
                          cloudUser?.email
                            ? "rgba(134,190,155,.24)"
                            : "rgba(125,174,212,.18)"
                        }`,
                        borderRadius: 13,
                        padding: "9px 11px",
                        marginBottom: 10,
                      }}
                    >
                      <p style={{ margin: 0, fontSize: 12.5, color: C.textMid, lineHeight: 1.45, fontWeight: 800 }}>
                        {cloudStatus === "Saving..."
                          ? "Saving..."
                          : cloudUser?.email && lastSavedText
                          ? `Saved · ${lastSavedText}`
                          : cloudUser?.email
                          ? cloudStatus || "Ready to save"
                          : "Not signed in yet"}
                      </p>
                      {cloudUser?.email && (
                        <p style={{ margin: "3px 0 0", fontSize: 12, color: C.textFaint, lineHeight: 1.35 }}>
                          {cloudUser.email}
                        </p>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: cloudUser?.email ? "1fr 1fr" : "1fr", gap: 8 }}>
                      {!cloudUser?.email ? (
                        <GoldBtn onClick={handleGoogleSignIn} style={{ width: "100%" }}>
                          Save with Google
                        </GoldBtn>
                      ) : (
                        <>
                          <GoldBtn onClick={() => saveProgressToCloud()} style={{ width: "100%" }}>
                            Save Now
                          </GoldBtn>
                          <button
                            onClick={handleSignOut}
                            style={{
                              background: "rgba(255,253,246,.82)",
                              border: `1px solid ${C.cardBorder}`,
                              borderRadius: 14,
                              padding: "11px 10px",
                              color: C.textFaint,
                              fontWeight: 900,
                              cursor: "pointer",
                              fontSize: 13,
                              minHeight: 44,
                            }}
                          >
                            Sign Out
                          </button>
                        </>
                      )}
                    </div>
                    {cloudUser?.email && (
                      <div
                        style={{
                          marginTop: 10,
                          background: "rgba(246,250,255,.62)",
                          border: "1px solid rgba(125,174,212,.20)",
                          borderRadius: 13,
                          padding: "10px 11px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <p
                              style={{
                                margin: 0,
                                fontSize: 13,
                                color: C.textDark,
                                fontWeight: 900,
                              }}
                            >
                              Notifications
                            </p>

                          </div>
                          <button
                            onClick={enablePushNotifications}
                            disabled={pushBusy}
                            style={{
                              flexShrink: 0,
                              background: "rgba(255,253,246,.86)",
                              border: `1.5px solid ${C.gold}`,
                              borderRadius: 999,
                              padding: "8px 10px",
                              color: C.goldDeep,
                              fontWeight: 900,
                              cursor: pushBusy ? "wait" : "pointer",
                              fontSize: 12.5,
                            }}
                          >
                            {pushBusy ? "Opening..." : "Enable"}
                          </button>
                        </div>
                        {pushStatus && (
                          <p
                            style={{
                              margin: "8px 0 0",
                              fontSize: 12,
                              color: C.textFaint,
                              lineHeight: 1.35,
                            }}
                          >
                            {pushStatus}
                          </p>
                        )}
                      </div>
                    )}
                    {!cloudUser?.email && (
                      <p style={{ margin: "9px 0 0", fontSize: 12.5, color: C.textFaint, lineHeight: 1.45 }}>
                        {cloudStatus || "Private on this device until you sign in."}
                      </p>
                    )}
                  </Card>

                  <Card className="growth-summary-card growth-stage-hero" style={{ padding: 16 }}>
                    <div className="growth-summary-orb growth-stage-orb">
                      <SpiritAvatar
                        stageKey={stage.key}
                        xp={stagePreviewXP}
                        xpMax={nextStage ? nextStage.xpMin : stage.xpMax}
                        stageAura={stage.aura}
                        stageGlow={stage.glow}
                        animated={true}
                        equipped={equippedObjs}
                        gender={gender}
                        skinTone={skinTone}
                        hairColor={hairColor}
                        size={112}
                        showBar={false}
                      />
                    </div>
                    <div className="growth-summary-copy growth-stage-copy">
                      <SectionLabel>Current Stage</SectionLabel>
                      <h3 className="serif" style={{ margin: "0 0 5px", fontSize: 23, color: C.textDark }}>
                        {stage.name}
                      </h3>
                      <p style={{ margin: 0, fontSize: 13.5, color: C.textSoft, lineHeight: 1.5 }}>
                        {nextStage
                          ? xpToNext + " righteousness until " + nextStage.name + "."
                          : "Your spirit is radiant. Keep shining each day."}
                      </p>
                      <div className="growth-stage-progress" aria-label="Spirit growth progress">
                        <div className="growth-stage-progress-top">
                          <span>Righteousness grows through faithful daily steps.</span>
                          <b>{totalXP}</b>
                        </div>
                        <div className="growth-stage-progress-track">
                          <div
                            className="growth-stage-progress-fill"
                            style={{ width: stage.xpMax === Infinity ? "100%" : stageProgressPercent + "%" }}
                          >
                            <span className="growth-stage-progress-label">
                              {stage.xpMax === Infinity ? "100%" : Math.round(stageProgressPercent) + "%"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>

                  <Card className="growth-hero-card" style={{ padding: 14 }}>
                    <div className="testimony-stone-toggle">
                      <div>
                        <h3
                          className="serif"
                          style={{ margin: 0, fontSize: 17, color: C.textDark }}
                        >
                          Spirit's Growth
                        </h3>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexShrink: 0,
                        }}
                      >
                        <span className="placed-chip">
                          {SPIRIT_STAGES.findIndex((s) => s.key === stage.key) +
                            1}
                          /{SPIRIT_STAGES.length}
                        </span>
                      </div>
                    </div>
                    <div className="growth-path-list">
                      {SPIRIT_STAGES.map((s) => {
                        const unlocked = totalXP >= s.xpMin;
                        const current = stage === s;
                        const completed = totalXP > s.xpMax;
                        return (
                          <div
                            key={s.name}
                            className={`growth-path-step${current ? " current" : ""}${unlocked ? " unlocked" : " locked"}`}
                          >
                            <div className="growth-path-avatar">
                              <SpiritAvatar
                                stageKey={s.key}
                                xp={0}
                                xpMax={0}
                                stageAura={s.aura}
                                stageGlow={s.glow}
                                animated={current}
                                equipped={current ? equippedObjs : []}
                                gender={gender}
                                skinTone={skinTone}
                                hairColor={hairColor}
                                size={64}
                                showBar={false}
                              />
                            </div>
                            <div className="growth-path-copy">
                              <div className="growth-path-name">{s.name}</div>
                              <div className="growth-path-range">
                                {completed
                                  ? "Completed"
                                  : current
                                  ? "Current stage"
                                  : unlocked
                                  ? "Unlocked"
                                  : s.xpMin + " righteousness"}
                              </div>
                            </div>
                            <span className="growth-path-status" aria-hidden="true">
                              {completed ? "✓" : current ? "✦" : unlocked ? "•" : "○"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div
                      className="growth-customize-panel"
                      style={{
                        marginTop: 14,
                        paddingTop: 14,
                        borderTop: `1px solid ${C.cardBorder}`,
                      }}
                    >
                    <button
                      className="testimony-stone-toggle"
                      onClick={() => setShowCustomizeGrowth((v) => !v)}
                      aria-expanded={showCustomizeGrowth}
                    >
                      <div>
                        <h3
                          className="serif"
                          style={{ margin: 0, fontSize: 17, color: C.textDark }}
                        >
                          Customize Spirit
                        </h3>
                      </div>
                      <span
                        className={`testimony-stone-chevron${
                          showCustomizeGrowth ? " open" : ""
                        }`}
                      >
                        ⌄
                      </span>
                    </button>
                    {showCustomizeGrowth && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 14,
                          marginTop: 14,
                        }}
	                      >
	                        <div>
	                          <SectionLabel>Spirit Name</SectionLabel>
	                          <div
	                            style={{
	                              background: "rgba(255,253,246,.72)",
	                              border: `1px solid ${C.cardBorder}`,
	                              borderRadius: 14,
	                              padding: 12,
	                              display: "flex",
	                              flexDirection: "column",
	                              gap: 10,
	                            }}
	                          >
	                            {editingSpiritName ? (
	                              <>
	                                <input
	                                  value={spiritNameDraft}
	                                  onChange={(e) =>
	                                    setSpiritNameDraft(
	                                      formatSpiritName(e.target.value)
	                                    )
	                                  }
	                                  onKeyDown={(e) =>
	                                    e.key === "Enter" && saveSpiritNameEdit()
	                                  }
	                                  placeholder="Your spirit name"
	                                  maxLength={24}
	                                  className="sg-input"
	                                  autoFocus
	                                />
	                                <div
	                                  style={{
	                                    display: "grid",
	                                    gridTemplateColumns: "1fr 1fr",
	                                    gap: 8,
	                                  }}
	                                >
	                                  <GoldBtn
	                                    onClick={saveSpiritNameEdit}
	                                    disabled={!spiritNameDraft.trim()}
	                                    style={{ minHeight: 40 }}
	                                  >
	                                    Save Name
	                                  </GoldBtn>
	                                  <button
	                                    onClick={() => {
	                                      setSpiritNameDraft(spiritName);
	                                      setEditingSpiritName(false);
	                                    }}
	                                    style={{
	                                      background: "rgba(255,253,246,.82)",
	                                      border: `1px solid ${C.cardBorder}`,
	                                      borderRadius: 12,
	                                      color: C.textFaint,
	                                      fontWeight: 900,
	                                      cursor: "pointer",
	                                      minHeight: 40,
	                                    }}
	                                  >
	                                    Cancel
	                                  </button>
	                                </div>
	                              </>
	                            ) : (
	                              <div
	                                style={{
	                                  display: "flex",
	                                  alignItems: "center",
	                                  justifyContent: "space-between",
	                                  gap: 10,
	                                }}
	                              >
	                                <div style={{ minWidth: 0 }}>
	                                  <div
	                                    className="serif"
	                                    style={{
	                                      fontSize: 18,
	                                      color: C.goldDeep,
	                                      lineHeight: 1.2,
	                                      overflow: "hidden",
	                                      textOverflow: "ellipsis",
	                                      whiteSpace: "nowrap",
	                                    }}
	                                  >
	                                    {spiritName || "Daily Walk"}
	                                  </div>
	                                </div>
	                                <button
	                                  onClick={() => {
	                                    setSpiritNameDraft(spiritName);
	                                    setEditingSpiritName(true);
	                                  }}
	                                  className="soft-action-btn"
	                                  style={{ flexShrink: 0 }}
	                                >
	                                  Edit
	                                </button>
	                              </div>
	                            )}
	                          </div>
	                        </div>
	                        <div>
	                          <SectionLabel>Spirit</SectionLabel>
                          <div style={{ display: "flex", gap: 10 }}>
                            {(["female", "male"] as Gender[]).map((g) => (
                              <button
                                key={g}
                                onClick={() => {
                                  setGender(g);
                                  showToast(
                                    g === "female"
                                      ? "Her Spirit 🌸"
                                      : "His Spirit 🌿"
                                  );
                                }}
                                style={{
                                  flex: 1,
                                  background:
                                    gender === g
                                      ? GOLD_GRAD
                                      : "rgba(255,253,240,.6)",
                                  border: `1.5px solid ${
                                    gender === g ? C.gold : C.cardBorder
                                  }`,
                                  borderRadius: 12,
                                  padding: "12px 16px",
                                  color: gender === g ? C.textDark : C.textMid,
                                  fontWeight: gender === g ? 800 : 500,
                                  cursor: "pointer",
                                  fontSize: 14,
                                  minHeight: 44,
                                }}
                              >
                                {g === "female"
                                  ? "🌸 Her Spirit"
                                  : "🌿 His Spirit"}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    </div>
                  </Card>

                  <Card
                    className="growth-closet-card"
                    style={{
                      padding: 15,
                      background:
                        "linear-gradient(160deg,rgba(255,253,246,.96),rgba(246,250,255,.45))",
                      borderColor: "rgba(125,174,212,.18)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                      <div style={{ minWidth: 0 }}>
                        <SectionLabel>Armor Closet</SectionLabel>
                        <p className="growth-closet-copy">Equip what reflects your walk.</p>
                      </div>
                    </div>
                    {babySpiritUnlocked ? (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
                        <GoldBtn onClick={openArmorCloset} style={{ width: "100%", padding: "11px 10px" }}>
                          Open Closet
                        </GoldBtn>
                        <button
                          onClick={openArmorShop}
                          style={{
                            background: "rgba(255,253,246,.82)",
                            border: `1px solid ${C.cardBorder}`,
                            borderRadius: 14,
                            padding: "11px 10px",
                            color: C.goldDeep,
                            fontWeight: 900,
                            cursor: "pointer",
                            fontSize: 13,
                            minHeight: 44,
                          }}
                        >
                          Armor Shop
                        </button>
                      </div>
                    ) : (
                      <div className="unlock-note" style={{ marginTop: 12 }}>
                        Unlocks at Baby Spirit ({babySpiritXp} XP).
                      </div>
                    )}
                  </Card>

                  <Card
                    className="growth-stones-card"
                    style={{
                      padding: 16,
                      background:
                        "linear-gradient(135deg,rgba(255,253,246,.96),rgba(232,246,255,.38))",
                      borderColor: "rgba(125,211,252,.22)",
                    }}
                  >
                    <button
                      className="testimony-stone-toggle"
                      onClick={() => setShowTestimonyStones((v) => !v)}
                      aria-expanded={showTestimonyStones}
                    >
                      <div>
                        <h3
                          className="serif"
                          style={{ margin: 0, fontSize: 17, color: C.goldDeep }}
                        >
                          Heavenly Stones
                        </h3>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexShrink: 0,
                        }}
                      >
                        <span className="placed-chip">
                          {testimonyStones.length}/9 stones
                        </span>
                        <span
                          className={`testimony-stone-chevron${
                            showTestimonyStones ? " open" : ""
                          }`}
                        >
                          ⌄
                        </span>
                      </div>
                    </button>

                    {showTestimonyStones && (
                      <>
                        <div className="testimony-stone-grid">
                          {(
                            Object.keys(
                              TESTIMONY_STONE_LIBRARY
                            ) as TestimonyStoneKind[]
                          ).map((kind) => {
                            const ownedStone = testimonyStones.find(
                              (stone) => stone.kind === kind
                            );
                            const base = TESTIMONY_STONE_LIBRARY[kind];
                            const owned = Boolean(ownedStone);
                            const [gemA, gemB, gemC] = testimonyGemColors(kind);
                            return (
                              <div
                                key={kind}
                                className={`testimony-stone-card${
                                  owned ? "" : " locked"
                                }`}
                              >
                                <div className="testimony-gem-wrap">
                                  <div className="testimony-gem-shadow" />
                                  <div
                                    className={`testimony-gem${
                                      kind === "white"
                                        ? " testimony-gem-white"
                                        : ""
                                    }${owned ? "" : " testimony-gem-locked"}`}
                                    style={
                                      {
                                        "--gem-a": gemA,
                                        "--gem-b": gemB,
                                        "--gem-c": gemC,
                                      } as React.CSSProperties
                                    }
                                  >
                                    <span className="testimony-gem-core" />
                                  </div>
                                </div>
                                <div className="testimony-gem-name">
                                  {base.name}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11.5,
                                    color: C.textFaint,
                                    marginTop: 4,
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {ownedStone
                                    ? ownedStone.source
                                    : kind === "white"
                                    ? "Collect every major stone"
                                    : "Hidden testimony"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p
                          style={{
                            margin: "12px 0 0",
                            fontSize: 13,
                            color: C.textSoft,
                            lineHeight: 1.55,
                          }}
                        >
                          Stones appear as your walk grows.
                        </p>
                      </>
                    )}
                  </Card>

                  <Card
                    className="growth-book-card"
                    style={{
                      padding: 16,
                      background:
                        "linear-gradient(135deg,rgba(255,253,246,.96),rgba(255,241,242,.32))",
                      borderColor: "rgba(249,168,212,.18)",
                    }}
                  >
                    <button
                      className="testimony-stone-toggle"
                      onClick={() => setShowTestimonyBook((v) => !v)}
                      aria-expanded={showTestimonyBook}
                    >
                      <div>
                        <h3
                          className="serif"
                          style={{ margin: 0, fontSize: 17, color: C.goldDeep }}
                        >
                          Testimony Book
                        </h3>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexShrink: 0,
                        }}
                      >
                        <span className="placed-chip">
                          {testimonyBook.length} saved
                        </span>
                        <span
                          className={
                            "testimony-stone-chevron" +
                            (showTestimonyBook ? " open" : "")
                          }
                        >
                          ⌄
                        </span>
                      </div>
                    </button>
                    {showTestimonyBook &&
                      (testimonyBook.length === 0 ? (
                        <p
                          style={{
                            margin: "14px 0 0",
                            fontSize: 13,
                            color: C.textFaint,
                            lineHeight: 1.6,
                            textAlign: "center",
                            padding: "12px 8px",
                          }}
                        >
                          No answered prayers yet.
                        </p>
                      ) : (
                        <div
                          className="month-scroll-list testimony-book-scroll"
                          style={{ marginTop: 12 }}
                        >
                          {testimonyMonthGroups.map((group) => {
                            const open =
                              openTestimonyMonths[group.key] !== false;
                            return (
                              <div key={group.key} className="month-group">
                                <button
                                  type="button"
                                  className="month-toggle"
                                  onClick={() =>
                                    toggleTestimonyMonth(group.key)
                                  }
                                  aria-expanded={open}
                                >
                                  <span>{group.label}</span>
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 8,
                                    }}
                                  >
                                    <span className="placed-chip">
                                      {group.items.length}
                                    </span>
                                    <span
                                      className={
                                        "testimony-stone-chevron" +
                                        (open ? " open" : "")
                                      }
                                    >
                                      ⌄
                                    </span>
                                  </span>
                                </button>
                                {open && (
                                  <div className="month-items">
                                    {group.items.map((item) => (
                                      <div
                                        key={item.id}
                                        style={{
                                          display: "flex",
                                          gap: 10,
                                          alignItems: "flex-start",
                                          background: "rgba(255,253,246,.68)",
                                          border: "1px solid " + C.cardBorder,
                                          borderRadius: 14,
                                          padding: "10px 12px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: 34,
                                            height: 34,
                                            borderRadius: "50%",
                                            background:
                                              "linear-gradient(135deg,rgba(255,255,255,.92)," +
                                              item.accent +
                                              "55)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                            fontSize: 18,
                                          }}
                                        >
                                          {item.icon}
                                        </div>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              gap: 8,
                                              alignItems: "flex-start",
                                            }}
                                          >
                                            <div style={{ minWidth: 0 }}>
                                              <h4
                                                style={{
                                                  margin: 0,
                                                  fontSize: 13.5,
                                                  color: C.textDark,
                                                  fontWeight: 900,
                                                }}
                                              >
                                                {item.title}
                                              </h4>
                                              {item.date && (
                                                <span
                                                  style={{
                                                    fontSize: 13,
                                                    color: C.textFaint,
                                                  }}
                                                >
                                                  {item.date}
                                                </span>
                                              )}
                                            </div>
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: 6,
                                                flexShrink: 0,
                                              }}
                                            >
                                              <button
                                                aria-label={
                                                  "Edit " + item.title
                                                }
                                                onClick={() =>
                                                  editTestimonyEntry(
                                                    item.prayerId
                                                  )
                                                }
                                                style={{
                                                  background:
                                                    "rgba(255,253,246,.78)",
                                                  border:
                                                    "1px solid " + C.cardBorder,
                                                  borderRadius: 10,
                                                  padding: "6px 9px",
                                                  color: C.goldDeep,
                                                  cursor: "pointer",
                                                  fontSize: 13,
                                                  fontWeight: 900,
                                                }}
                                              >
                                                Edit
                                              </button>
                                              <button
                                                aria-label={
                                                  "Remove " +
                                                  item.title +
                                                  " from testimony book"
                                                }
                                                onClick={() =>
                                                  deleteTestimonyEntry(
                                                    item.prayerId
                                                  )
                                                }
                                                style={{
                                                  background:
                                                    "rgba(255,253,246,.78)",
                                                  border:
                                                    "1px solid " + C.cardBorder,
                                                  borderRadius: 10,
                                                  padding: "6px 9px",
                                                  color: C.textFaint,
                                                  cursor: "pointer",
                                                  fontSize: 13,
                                                  fontWeight: 900,
                                                }}
                                              >
                                                Delete
                                              </button>
                                            </div>
                                          </div>
                                          <p
                                            style={{
                                              margin: "3px 0 0",
                                              fontSize: 13.5,
                                              color: C.textSoft,
                                              lineHeight: 1.45,
                                            }}
                                          >
                                            {item.detail}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                  </Card>

                  {/* Neglect status card */}
                  {neglectDarkness > 0 && (
                    <Card
                      style={{
                        padding: 14,
                        background:
                          "linear-gradient(135deg,rgba(40,20,20,.06),rgba(80,40,20,.04))",
                        borderColor: "rgba(150,60,60,.25)",
                      }}
                    >
                      <h3
                        className="serif"
                        style={{
                          margin: "0 0 8px",
                          fontSize: 16,
                          color: "#8b3030",
                        }}
                      >
                        🌑 Home Neglect Warning
                      </h3>
                      <p
                        style={{
                          margin: "0 0 10px",
                          fontSize: 13,
                          color: "#4F3510",
                          lineHeight: 1.6,
                        }}
                      >
                        Your home grows darker the longer you stay away. Return
                        to your daily habits to restore its golden light.
                      </p>
                      <div
                        style={{
                          background: "rgba(255,248,223,.4)",
                          borderRadius: 12,
                          padding: "10px 14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 13,
                            color: C.textMid,
                            marginBottom: 4,
                          }}
                        >
                          <span>Darkness level</span>
                          <span
                            style={{
                              fontWeight: 800,
                              color:
                                neglectDarkness > 0.6 ? "#b71c1c" : "#c99012",
                            }}
                          >
                            {neglectDarkness > 0.8
                              ? "Very Dark"
                              : neglectDarkness > 0.5
                              ? "Dark"
                              : neglectDarkness > 0.3
                              ? "Dimming"
                              : "Slightly dim"}
                          </span>
                        </div>
                        <div
                          style={{
                            height: 6,
                            borderRadius: 999,
                            background: "rgba(180,130,20,.15)",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: `${Math.round(neglectDarkness * 100)}%`,
                              background:
                                "linear-gradient(135deg,#c99012,#8b3030)",
                              borderRadius: 999,
                            }}
                          />
                        </div>
                      </div>
                    </Card>
                  )}

                </div>
              )}
            </>
          )}
        </div>
      </div>

      <nav className="bottom-tabs">
        {visibleTabs.map(([id, icon, label]) => {
          const locked = !isTabUnlocked(id);
          return (
            <button
              key={id}
              aria-label={locked ? `${label} locked` : `Open ${label}`}
              className={`tab-btn${tab === id ? " active" : ""}`}
              data-tab-id={id}
              onClick={() => setTab(id)}
              style={locked ? { opacity: 0.62 } : undefined}
            >
              <span className="tab-icon-wrap">
                <span style={{ fontSize: 22 }}>{id === "home" ? <HomeIcon size={24} /> : id === "journal" ? <JournalIcon size={24} /> : icon}</span>
                {id === "prayers" && hasPrayerNotice && (
                  <span className="tab-heart-dot" aria-label="New prayer activity">♥</span>
                )}
                {id === "home" && hasHomeNotice && (
                  <span className="tab-heart-dot" aria-label="New home blessing">♥</span>
                )}
                {id === "wall" && hasTestimonyNotice && (
                  <span className="tab-heart-dot" aria-label="New testimony activity">♥</span>
                )}
                {locked && <span className="tab-lock-dot">🔒</span>}
              </span>
              <span className="tab-label">{label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
